/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { isChunkLoadError, lazyWithRetry, reloadOnceForChunkError } from './lazyWithRetry';
import {
  classifyLicenceSideFromImage,
  clientExtractLicenceBackFromImage,
  clientExtractLicenceFrontFromImage,
} from './licenceClientOcr';
import { buildLicenceSideSwapPatch } from './licenceTextExtract';
import { createPortal, flushSync } from 'react-dom';
import { 
  LayoutDashboard, 
  Users, 
  Search, 
  AlertTriangle, 
  CheckCircle2,
  Check,
  CheckCheck, 
  Clock, 
  User, 
  FileText, 
  Stethoscope, 
  ArrowLeft,
  Upload,
  ShieldCheck,
  ChevronRight,
  Menu,
  X,
  Trash2,
  LogOut,
  LogIn,
  BarChart3,
  UserPlus,
  Shield,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  ArrowLeftRight,
  Camera,
  Edit2,
  Trash,
  FileWarning,
  ExclamationTriangle,
  History,
  TrainFront,
  MapIcon,
  FileCheck,
  Activity,
  Settings,
  ChevronLeft,
  Plus,
  GripVertical,
  Paperclip,
  Mail,
  Phone,
  HelpCircle,
  Info,
  GraduationCap,
  Folder,
  FolderFile,
  FolderPlus,
  Grid,
  List,
  AlertCircle,
  Archive,
  Award,
  IdCard,
  Calendar,
  Video,
  Play,
  Eye,
  EyeOff,
  PauseCircle,
  PlayCircle,
  Wrench,
  Printer,
  Package,
  Briefcase,
  KeyRound,
  Lock,
  Unlock,
  RotateCcw,
  MessageCircle,
  Heart,
  HeartPulse,
  Bell,
  ClipboardList,
  Sun,
  Moon,
  Flag,
  Connect,
  ExternalLink,
  RefreshCw,
} from './icons';
import { formatUnitDisplayTitle } from './formatUnitDisplayTitle';
import {
  DEFAULT_REPORTING_CALENDAR,
  calendarConfigFromCommencementYmd,
  formatFinancialYearLabel,
  formatTrendBucketLabel,
  formatYmdUk,
  formatYmdShort,
  formatYmdRangeShort,
  selectedReportingDateRange,
  serializeReportingCalendarState,
  startOfWeekOnOrAfter,
  suggestedP1CommencementYmd,
  trendBucketMergeKey,
  weekStartsOnLabel,
  type ReportingCalendarConfig,
  type ReportingCalendarState,
  type WeekStartsOn,
} from './reportingCalendar';
import { flattenCriteria } from './flattenCriteria';
import type { DerogationMode, DerogationSubmission, SuggestedAssessor } from './AssessorDerogationDialog';
import type { LateAssessmentSubmission } from './LateAssessmentReasonModal';
import { normalizeOrgChart, resolveTier, compareSeniority, isMoreSenior, type OrgChart, type ResolvedTier } from './seniority';
import { fetchCompanyStorageSuspended } from './storageQuotaEvidence';
import {
  gradeForAssessmentSelectDisplay,
  getGuidedCriterionChipVisual,
  hasAssessmentMethodSatisfied,
  isFinalEventAcceptableGrade,
  isGradeAssessed,
  isNotApplicableGradeValue,
  gradeOptionPillClasses,
} from './assessmentCriteriaGates';
import {
  AssessmentPillSelect,
  buildAlphabeticalGradeOptions,
  sortAssessmentMethodsAlphabetical,
} from './AssessmentPillSelect';
import { GuidedAssessmentCriterionChipContent } from './GuidedAssessmentCriterionChip';
import { getCycleById } from './cycles';
import { formatCycleDurationLabel, resolveDisplayedCycleMonths } from './cycle-builder/formatCycleDuration';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from './LanguageSelector';
import { LoginMfaPanel } from './LoginMfaPanel';
import { TwoFactorSection } from './TwoFactorSection';
import { useUiLocale } from './useUiLocale';
import {
  applyEnabledLanguagesPolicy,
  ensureEnabledLanguagesLoaded,
  resolveLanguageAgainstAllowlist,
} from './useEnabledLanguages';

import { applyPreferredLocaleFromProfile, persistPreferredLocale } from './i18n/persistLocale';
import { isAppLanguageCode, readStoredLanguage, LOCALE_STORAGE_KEY } from './i18n/languages';
import i18n, { translateUi } from './i18n';
import { setSessionAuth, clearSessionAuth, getSessionToken, getSessionEmail } from './apiAuth';
import { CompanyDashboardBody } from './CompanyDashboardBody';
import { SystemAdminPerformancePanes } from './SystemAdminPerformancePanes';
import {
  DashboardGradientSection,
  DashboardGradientPaneGlows,
  DASHBOARD_GRADIENT_PANE_SHELL,
  DASHBOARD_GLASS_TILE,
  DASHBOARD_GLASS_TILE_INTERACTIVE,
} from './dashboardGradientPane';
import { GbrModalBackdrop } from './GbrModalBackdrop';
import { ReportingQaComplianceModal } from './ReportingQaComplianceModal';
import { useDeviceType, getInitialSidebarOpen } from './useDeviceType';
import { isRouteCompetency, isTractionCompetency } from './competencyCategory';
import { CompanyDoc } from "./companyOnboarding";
import { NEW_COMPANY_DARK_STYLES } from "./onboardingDarkMode";
import { isSystemAdminCompanyCode } from "../server/systemAdminCompany";
import { getOnboardingAddons } from "./addonCatalog";
import { nominatedAdminWelcomeStorageKey } from "./nominatedAdminWelcomeStorageKey";
import {
  nominatedAdminNavTourStorageKey,
  type NomineeNavTourStep,
} from "./nominatedAdminNavTourTypes";
import { buildTermsAgreementRecordHtml, openTermsAgreementPdfDownload } from "./termsAgreementRecordHtml";
import type { VerificationItem } from "./QAVerificationReport";
import { EmployeeProfileMegaMenu, type EmployeeProfileNavTab } from "./EmployeeProfileMegaMenu";
import {
  EmployeeProfileLockBanner,
  EmployeeProfileLockButton,
  readEmployeeLockFromRecord,
  type EmployeeLockState,
} from "./EmployeeProfileLockControls";
import { EmployeeProfileBrochureButton } from "./EmployeeProfileBrochureButton";
import { formatUkDdMmYy } from "./formatUkDdMmYy";
type SessionUser = { email: string; uid: string; displayName?: string };

/** Lazy page chunks — keep shell/dashboard/login eager for first paint. */
const CycleStructureView = lazyWithRetry(() =>
  import('./CycleStructureView').then((m) => ({ default: m.CycleStructureView }))
);
const AssessableCycleStructureView = lazyWithRetry(() =>
  import('./AssessableCycleStructureView').then((m) => ({ default: m.AssessableCycleStructureView }))
);
const StorageQuotaOpenAssessmentDialog = lazyWithRetry(() =>
  import('./StorageQuotaOpenAssessmentDialog').then((m) => ({ default: m.StorageQuotaOpenAssessmentDialog }))
);
const StorageQuotaStartAssessmentDialog = lazyWithRetry(() =>
  import('./StorageQuotaStartAssessmentDialog').then((m) => ({ default: m.StorageQuotaStartAssessmentDialog }))
);
const StorageQuotaUploadDialog = lazyWithRetry(() =>
  import('./StorageQuotaUploadDialog').then((m) => ({ default: m.StorageQuotaUploadDialog }))
);
const AssessorDerogationDialog = lazyWithRetry(() =>
  import('./AssessorDerogationDialog').then((m) => ({ default: m.AssessorDerogationDialog }))
);
const LateAssessmentReasonModal = lazyWithRetry(() =>
  import('./LateAssessmentReasonModal').then((m) => ({ default: m.LateAssessmentReasonModal }))
);
const SetupWizard = lazyWithRetry(() => import("./SetupWizard").then((m) => ({ default: m.SetupWizard })));
import {
  ensureRequiredOnboardingJobRoles,
  isRequiredOnboardingJobRole,
  shouldLockInstructorJobRoles,
} from "./SetupWizard";
import { InstructorSelectDropdown, getInstructorAssignmentPeriod, InstructorAssignmentAuditCard } from "./InstructorSelectDropdown";
const NominatedAdminWelcomeScreen = lazyWithRetry(() =>
  import("./NominatedAdminWelcomeScreen").then((m) => ({ default: m.NominatedAdminWelcomeScreen }))
);
const NominatedAdminNavTour = lazyWithRetry(() =>
  import("./NominatedAdminNavTour").then((m) => ({ default: m.NominatedAdminNavTour }))
);
const GradingScalePage = lazyWithRetry(() => import("./GradingScalePage").then((m) => ({ default: m.GradingScalePage })));
const RolePermissionsPage = lazyWithRetry(() =>
  import("./RolePermissionsPage").then((m) => ({ default: m.RolePermissionsPage }))
);
const CompanyLogoPage = lazyWithRetry(() => import("./CompanyLogoPage").then((m) => ({ default: m.CompanyLogoPage })));
const CompanyBillingDirectoryPage = lazyWithRetry(() =>
  import("./CompanyBillingDirectoryPage").then((m) => ({ default: m.CompanyBillingDirectoryPage }))
);
const CompanyStandardsPage = lazyWithRetry(() =>
  import("./CompanyStandardsPage").then((m) => ({ default: m.CompanyStandardsPage }))
);
const CompanyTractionRoutesPage = lazyWithRetry(() =>
  import("./CompanyTractionRoutesPage").then((m) => ({ default: m.CompanyTractionRoutesPage }))
);
const CompanyOrgStructurePage = lazyWithRetry(() =>
  import("./CompanyOrgStructurePage").then((m) => ({ default: m.CompanyOrgStructurePage }))
);
const EmailTemplatePage = lazyWithRetry(() =>
  import("./EmailTemplatePage").then((m) => ({ default: m.EmailTemplatePage }))
);
const OnboardingAdminPage = lazyWithRetry(() =>
  import("./OnboardingAdminPage").then((m) => ({ default: m.OnboardingAdminPage }))
);
const AddonsManagementPage = lazyWithRetry(() =>
  import("./AddonsManagementPage").then((m) => ({ default: m.AddonsManagementPage }))
);
const SupportTicketsPage = lazyWithRetry(() =>
  import("./SupportTicketsPage").then((m) => ({ default: m.SupportTicketsPage }))
);
const SupportDeskPage = lazyWithRetry(() => import("./SupportDeskPage").then((m) => ({ default: m.SupportDeskPage })));
const InvestigationsApiManagementPage = lazyWithRetry(() =>
  import("./InvestigationsApiManagementPage").then((m) => ({ default: m.InvestigationsApiManagementPage }))
);
const AnnouncementsPage = lazyWithRetry(() =>
  import("./AnnouncementsPage").then((m) => ({ default: m.AnnouncementsPage }))
);
const MaintenanceDowntimeView = lazyWithRetry(() =>
  import("./MaintenanceDowntimeView").then((m) => ({ default: m.MaintenanceDowntimeView }))
);
const MaintenanceActiveBanner = lazyWithRetry(() =>
  import("./MaintenanceDowntimeView").then((m) => ({ default: m.MaintenanceActiveBanner }))
);
const TasksPage = lazyWithRetry(() => import("./TasksPage").then((m) => ({ default: m.TasksPage })));
const EmployeeTasksSection = lazyWithRetry(() =>
  import("./TasksPage").then((m) => ({ default: m.EmployeeTasksSection }))
);
const TaskModuleSettingsPage = lazyWithRetry(() =>
  import("./TaskModuleSettingsPage").then((m) => ({ default: m.TaskModuleSettingsPage }))
);
const SafetyBriefsPage = lazyWithRetry(() =>
  import("./SafetyBriefsPage").then((m) => ({ default: m.SafetyBriefsPage }))
);
const LicencingPage = lazyWithRetry(() =>
  import("./LicencingPage").then((m) => ({ default: m.LicencingPage }))
);
const DriverReportsPanel = lazyWithRetry(() =>
  import("./DriverReportsPanel").then((m) => ({ default: m.DriverReportsPanel }))
);
const LeaveAbsencePage = lazyWithRetry(() =>
  import("./LeaveAbsencePage").then((m) => ({ default: m.LeaveAbsencePage }))
);
const LeaveAbsenceEmployeePanel = lazyWithRetry(() =>
  import("./LeaveAbsencePage").then((m) => ({ default: m.LeaveAbsenceEmployeePanel }))
);
const MedicationChecksPanel = lazyWithRetry(() =>
  import("./MedicationChecksPanel").then((m) => ({ default: m.MedicationChecksPanel }))
);
const PerformanceSupportPlans = lazyWithRetry(() =>
  import("./PerformanceSupportPlans").then((m) => ({ default: m.PerformanceSupportPlans }))
);
const TraineeDriverPage = lazyWithRetry(() =>
  import("./TraineeDriverPage").then((m) => ({ default: m.TraineeDriverPage }))
);
const TraineeDriverGlobalTemplatesPage = lazyWithRetry(() =>
  import("./TraineeDriverGlobalTemplatesPage").then((m) => ({ default: m.TraineeDriverGlobalTemplatesPage }))
);
const VerificationFlowModal = lazyWithRetry(() =>
  import("./QAVerificationReport").then((m) => ({ default: m.VerificationFlowModal }))
);
const QAVerificationsHistoryPage = lazyWithRetry(() =>
  import("./QAVerificationReport").then((m) => ({ default: m.QAVerificationsHistoryPage }))
);
const QAVerificationCheckSettingsPage = lazyWithRetry(() =>
  import("./QAVerificationCheckSettingsPage").then((m) => ({ default: m.QAVerificationCheckSettingsPage }))
);
const LicenceCardScanAnimation = lazyWithRetry(() =>
  import("./LicenceCardScanAnimation").then((m) => ({ default: m.LicenceCardScanAnimation }))
);
const TrainSourceSettingsPage = lazyWithRetry(() =>
  import("./TrainSourceSettingsPage").then((m) => ({ default: m.TrainSourceSettingsPage }))
);
const AppLanguagesSettingsPage = lazyWithRetry(() =>
  import("./AppLanguagesSettingsPage").then((m) => ({ default: m.AppLanguagesSettingsPage }))
);
const MigrationCenterPage = lazyWithRetry(() =>
  import("./MigrationCenterPage").then((m) => ({ default: m.MigrationCenterPage }))
);
const SecurityPage = lazyWithRetry(() => import("./SecurityPage").then((m) => ({ default: m.SecurityPage })));
const CertificateTab = lazyWithRetry(() => import("./CertificateTab").then((m) => ({ default: m.CertificateTab })));
const CabPassesTab = lazyWithRetry(() => import("./CabPassesTab").then((m) => ({ default: m.CabPassesTab })));
const CycleWizardPage = lazyWithRetry(() =>
  import("./cycle-builder/CycleWizardPage").then((m) => ({ default: m.CycleWizardPage }))
);

// Utility for tailwind classes
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Emergency display-name fallback for known system-admin login aliases. */
function forcedNameForKnownAdminEmail(email: string | null | undefined): { full: string; first: string } | null {
  const em = String(email ?? "").trim().toLowerCase();
  const local = em.split("@")[0] ?? "";
  if (local === "andyuk38") return { full: "Andy Hill", first: "Andy" };
  return null;
}

/** Header / avatar: saved profile full name, then Azure display name, then email local part. */
function chromeDisplayName(
  profile: { full_name?: string | null; email?: string | null } | null | undefined,
  authUser: SessionUser | null | undefined
): string {
  const forced = forcedNameForKnownAdminEmail(profile?.email ?? authUser?.email);
  if (forced) return forced.full;
  const fromProfile = profile?.full_name?.trim();
  if (fromProfile) return fromProfile;
  const fromAuth = authUser?.displayName?.trim();
  if (fromAuth) return fromAuth;
  const em = profile?.email?.trim() || authUser?.email?.trim() || "";
  const local = em.split("@")[0]?.trim();
  return local || "User";
}

/** Dashboard welcome line: given name only (first whitespace-delimited word), never surname on trailing words. */
function welcomeFirstNameOnly(
  profile: { full_name?: string | null; email?: string | null } | null | undefined,
  authUser: SessionUser | null | undefined
): string | null {
  const forced = forcedNameForKnownAdminEmail(profile?.email ?? authUser?.email);
  if (forced) return forced.first;
  const full = profile?.full_name?.trim();
  const disp = authUser?.displayName?.trim();
  const named = full || disp;
  if (named) {
    const parts = named.split(/\s+/).filter(Boolean);
    const first = parts[0];
    return first || named;
  }
  const em = profile?.email?.trim() || authUser?.email?.trim() || "";
  const local = em.split("@")[0]?.trim();
  return local || null;
}

/** System access translateUi("Read Only") blocks creating/updating/deleting operational data (employees, incidents, medicals, etc.). */
function allowsOperationalMutations(userProfile?: { role?: string } | null): boolean {
  return (userProfile?.role ?? '').trim() !== 'Read Only';
}

function normalizeAccessError(message: string | null | undefined, fallback: string): string {
  const raw = String(message || "").trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  /** A locked profile is not a permission problem — surface the server's explanation verbatim. */
  if (lower.includes("locked for evidential purposes")) return raw;
  if (
    lower.includes("permission") ||
    lower.includes("access denied") ||
    lower.includes("only system administrators") ||
    lower.includes("only company administrators")
  ) {
    return "Access restricted: your role does not have permission for this action in this company.";
  }
  return raw;
}

/** "You have arrived" once per browser tab session; cleared when auth user becomes null */
const SESSION_DASHBOARD_ARRIVAL_KEY = 'rv_session_dashboard_you_have_arrived_done';

function hasSessionDashboardArrivalPlayed(): boolean {
  try {
    return sessionStorage.getItem(SESSION_DASHBOARD_ARRIVAL_KEY) === '1';
  } catch {
    return false;
  }
}

function markSessionDashboardArrivalPlayed(): void {
  try {
    sessionStorage.setItem(SESSION_DASHBOARD_ARRIVAL_KEY, '1');
  } catch {
    /* ignore */
  }
}

function clearSessionDashboardArrivalFlag(): void {
  try {
    sessionStorage.removeItem(SESSION_DASHBOARD_ARRIVAL_KEY);
  } catch {
    /* ignore */
  }
}

// Licence image: load file, auto portrait→landscape, optional crop/rotate for tablet
const LICENCE_HOLDER_ASPECT = 1.4;
const LICENCE_CROP_MIN = 0.05; // min width/height as fraction (5%)
const LICENCE_CROP_INITIAL_INSET = 0.12; // initial crop inset from each edge (12%) so box is clearly inside image
const LICENCE_UPLOAD_MAX_EDGE = 1800; // px
const LICENCE_UPLOAD_TARGET_MAX_BYTES = 450 * 1024; // ~450KB
/** Minimum short-edge pixels for a usable licence photo (mobile suitability gate). */
const LICENCE_PHOTO_MIN_SHORT_EDGE = 640;
/** Laplacian variance below this ≈ too blurry for OCR. */
const LICENCE_PHOTO_MIN_SHARPNESS = 45;
/** Mean luma outside this range ≈ too dark / washed out. */
const LICENCE_PHOTO_LUMA_MIN = 38;
const LICENCE_PHOTO_LUMA_MAX = 230;

type LicencePhotoSuitability = {
  ok: boolean;
  issues: string[];
  width: number;
  height: number;
  sharpness: number;
  meanLuma: number;
};

/** Client-side check that a licence photo is sharp/bright enough before OCR (esp. mobile). */
async function assessLicencePhotoSuitability(dataUrl: string): Promise<LicencePhotoSuitability> {
  const img = await loadImage(dataUrl);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const issues: string[] = [];
  if (!width || !height) {
    return { ok: false, issues: ["Could not read the photo dimensions."], width: 0, height: 0, sharpness: 0, meanLuma: 0 };
  }
  const shortEdge = Math.min(width, height);
  if (shortEdge < LICENCE_PHOTO_MIN_SHORT_EDGE) {
    issues.push("Photo resolution is too low — move closer or use a higher-quality camera setting.");
  }
  const aspect = width / height;
  if (aspect < 1.05 || aspect > 2.2) {
    issues.push("Frame the licence so it fills most of the photo in landscape orientation.");
  }

  const sampleW = Math.min(480, width);
  const sampleH = Math.max(1, Math.round((sampleW / width) * height));
  const canvas = document.createElement("canvas");
  canvas.width = sampleW;
  canvas.height = sampleH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    return { ok: issues.length === 0, issues, width, height, sharpness: 0, meanLuma: 0 };
  }
  ctx.drawImage(img, 0, 0, sampleW, sampleH);
  const { data } = ctx.getImageData(0, 0, sampleW, sampleH);
  const gray = new Float32Array(sampleW * sampleH);
  let sum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    gray[p] = y;
    sum += y;
  }
  const meanLuma = sum / gray.length;
  if (meanLuma < LICENCE_PHOTO_LUMA_MIN) {
    issues.push("Photo is too dark — improve lighting and avoid shadows over the text.");
  } else if (meanLuma > LICENCE_PHOTO_LUMA_MAX) {
    issues.push("Photo is overexposed / washed out — reduce glare and bright reflections.");
  }

  // Laplacian variance (blur metric) on interior pixels.
  let lapSum = 0;
  let lapSumSq = 0;
  let lapCount = 0;
  for (let y = 1; y < sampleH - 1; y++) {
    for (let x = 1; x < sampleW - 1; x++) {
      const i = y * sampleW + x;
      const lap =
        -4 * gray[i] +
        gray[i - 1] +
        gray[i + 1] +
        gray[i - sampleW] +
        gray[i + sampleW];
      lapSum += lap;
      lapSumSq += lap * lap;
      lapCount += 1;
    }
  }
  const lapMean = lapCount ? lapSum / lapCount : 0;
  const sharpness = lapCount ? lapSumSq / lapCount - lapMean * lapMean : 0;
  if (sharpness < LICENCE_PHOTO_MIN_SHARPNESS) {
    issues.push("Photo looks blurry — hold steady, tap to focus, and retake.");
  }

  // Glare: large bright near-white clusters.
  let hot = 0;
  for (let i = 0; i < gray.length; i++) {
    if (gray[i] >= 248) hot += 1;
  }
  if (hot / gray.length > 0.12) {
    issues.push("Too much glare or reflection — tilt the licence slightly and retake.");
  }

  return { ok: issues.length === 0, issues, width, height, sharpness, meanLuma };
}
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}
function rotateImageDataUrl(dataUrl: string, degrees: number): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const d = degrees % 360;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const swap = d === 90 || d === 270;
    const cw = swap ? h : w;
    const ch = swap ? w : h;
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate((d * Math.PI) / 180);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.92);
  });
}
function compressImageDataUrl(dataUrl: string): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    if (!srcW || !srcH) return dataUrl;
    const scale = Math.min(1, LICENCE_UPLOAD_MAX_EDGE / Math.max(srcW, srcH));
    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, outW, outH);
    const approxBytes = (url: string) => Math.round((url.length - url.indexOf(',') - 1) * 0.75);
    let quality = 0.86;
    let best = canvas.toDataURL('image/jpeg', quality);
    while (approxBytes(best) > LICENCE_UPLOAD_TARGET_MAX_BYTES && quality > 0.5) {
      quality -= 0.08;
      best = canvas.toDataURL('image/jpeg', quality);
    }
    return best;
  });
}
function cropImageToAspect(dataUrl: string, aspect: number): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const imgAspect = w / h;
    let sw = w;
    let sh = h;
    let sx = 0;
    let sy = 0;
    if (imgAspect > aspect) {
      sw = Math.round(h * aspect);
      sx = Math.round((w - sw) / 2);
    } else {
      sh = Math.round(w / aspect);
      sy = Math.round((h - sh) / 2);
    }
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    return canvas.toDataURL('image/jpeg', 0.92);
  });
}
/** Fit full image into target aspect by padding empty space (no content crop). */
function fitImageToAspect(dataUrl: string, aspect: number): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return dataUrl;
    const imgAspect = w / h;
    let outW = w;
    let outH = h;
    if (imgAspect > aspect) {
      outH = Math.max(1, Math.round(w / aspect));
    } else if (imgAspect < aspect) {
      outW = Math.max(1, Math.round(h * aspect));
    }
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    const dx = Math.round((outW - w) / 2);
    const dy = Math.round((outH - h) / 2);
    ctx.drawImage(img, dx, dy, w, h);
    return canvas.toDataURL('image/jpeg', 0.92);
  });
}
/** Crop image to a region given in normalized 0–1 coordinates (xNorm, yNorm = top-left; wNorm, hNorm = size). */
function cropImageToRegion(dataUrl: string, xNorm: number, yNorm: number, wNorm: number, hNorm: number): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const sx = Math.max(0, Math.round(xNorm * w));
    const sy = Math.max(0, Math.round(yNorm * h));
    const sw = Math.min(w - sx, Math.round(wNorm * w));
    const sh = Math.min(h - sy, Math.round(hNorm * h));
    if (sw <= 0 || sh <= 0) return dataUrl;
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    return canvas.toDataURL('image/jpeg', 0.92);
  });
}
function processLicenceFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      loadImage(dataUrl).then((img) => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (h > w) {
          rotateImageDataUrl(dataUrl, 90)
            .then((rotated) => compressImageDataUrl(rotated))
            .then(resolve)
            .catch(reject);
        } else {
          compressImageDataUrl(dataUrl).then(resolve).catch(reject);
        }
      }).catch(reject);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Accept Finder / desktop drag-drops that often use empty or octet-stream MIME types. */
function isLikelyLicenceImageFile(file: File | null | undefined): file is File {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "");
  if (type.startsWith("image/")) return true;
  const looksNamed =
    /\.(jpe?g|png|gif|webp|heic|heif|bmp|tif?f)$/i.test(name);
  if (
    looksNamed &&
    (!type ||
      type === "application/octet-stream" ||
      type === "binary/octet-stream" ||
      type === "application/x-download")
  ) {
    return true;
  }
  return false;
}

type EmployeeCycleEventSummary = {
  id: number | string;
  event_type?: 'assessment' | 'monitoring' | string;
  cdp_plan_id?: string | null;
  event_name: string;
  monitoring_method?: "Practical" | "Simulation" | "Briefing" | "OTDR Download" | "Other" | string | null;
  monitoring_description?: string | null;
  window_open: string;
  window_close: string;
  offset_months_open?: number;
  offset_months_close?: number;
  status: string;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
  total_criteria?: number;
  total_criteria_in_completed_elements?: number;
  assessed_count?: number;
  advice_count?: number;
  review_count?: number;
  not_applicable_count?: number;
  unassessed_count?: number;
  assessment_completed_at?: string | null;
  assessment_completed_by?: string | null;
  assessment_completed_by_name?: string | null;
  assessment_started_at?: string | null;
  assessment_started_by?: string | null;
  assessment_started_by_name?: string | null;
  assessment_opened_at?: string | null;
  assessment_opened_by?: string | null;
  assessment_opened_by_name?: string | null;
  assessment_started_element_keys?: string[] | null;
  assessment_started_criterion_keys?: string[] | null;
  amended_at?: string | null;
  amended_by?: string | null;
  amended_by_name?: string | null;
  amendment_notes?: string | null;
  amendment_snapshot?: string | null;
  assessment_duration_seconds?: number | null;
  early_assessment_reasons?: string | null;
  late_assessment_reasons?: string | null;
  /** Optional closing summary saved before or after marking the event complete (assessment events only). */
  final_assessment_feedback?: {
    general_observations?: string;
    personal_attributes?: string;
    candidate_feedback?: string;
    updated_at?: string;
    /** Audit: user pasted into this section at least once (sticky on the event once saved). */
    general_observations_pasted?: boolean;
    personal_attributes_pasted?: boolean;
    candidate_feedback_pasted?: boolean;
  } | null;
};

type CustomCycleCriterionRow = {
  /** SQLite numeric id or RTDB criteria row key (string). */
  id: number | string;
  unit_no: string;
  element_no: string;
  element: string;
  criteria: string;
  status: string;
};

type EmployeeCriterionAssessment = {
  grade: string | null;
  notes: string | null;
  assessed_at: string | null;
  assessed_by: string | null;
  employee_cycle_event_id?: number | string | null;
  assessment_method: string | null;
};

const ASSESSMENT_RESUME_STORAGE_KEY = "rv_assessment_resume_v1";

const EXCLUSIVE_ASSESSMENT_FALLBACK_ALERT =
  "This assessment is already in progress by another assessor. It cannot be opened or edited until they mark the event as complete.";

async function alertIfExclusiveAssessmentDenied(res: Response): Promise<void> {
  if (res.status !== 403) return;
  try {
    const j = (await res.json()) as { error?: string };
    window.alert(j.error?.trim() || translateUi(EXCLUSIVE_ASSESSMENT_FALLBACK_ALERT));
  } catch {
    window.alert(translateUi(EXCLUSIVE_ASSESSMENT_FALLBACK_ALERT));
  }
}

/** Surface API errors for assessment mutations (including non-403 failures that used to fail silently). */
async function alertAssessmentMutationFailed(res: Response, fallback: string): Promise<void> {
  try {
    const j = (await res.json()) as { error?: string };
    const msg = j.error?.trim();
    if (res.status === 403) {
      window.alert(msg || translateUi(EXCLUSIVE_ASSESSMENT_FALLBACK_ALERT));
      return;
    }
    window.alert(msg || fallback);
  } catch {
    window.alert(res.status === 403 ? translateUi(EXCLUSIVE_ASSESSMENT_FALLBACK_ALERT) : fallback);
  }
}

const ASSESSMENT_METHOD_OPTIONS = [
  'observed',
  'questioning',
  'simulation',
  'written',
  'OTDR',
  'professional discussion',
  'testimony',
  'not applicable',
  'other',
] as const;

function toMethodLabel(value: string): string {
  const english =
    value.trim().toLowerCase() === 'not applicable'
      ? 'Not applicable'
      : value
          .split(" ")
          .map((word) => (word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1)))
          .join(" ");
  return translateUi(english);
}

function isImageFile(name: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name);
}

function isPdfFile(name: string): boolean {
  return /\.pdf$/i.test(name);
}

function inferAttachmentMimeType(name: string): string {
  if (isPdfFile(name)) return 'application/pdf';
  if (/\.png$/i.test(name)) return 'image/png';
  if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
  if (/\.gif$/i.test(name)) return 'image/gif';
  if (/\.webp$/i.test(name)) return 'image/webp';
  if (/\.bmp$/i.test(name)) return 'image/bmp';
  if (/\.heic$/i.test(name)) return 'image/heic';
  if (/\.heif$/i.test(name)) return 'image/heif';
  return 'application/octet-stream';
}

function toAttachmentDataUrl(content: unknown, name: string): string | null {
  if (typeof content !== 'string') return null;
  const trimmed = content.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed === 'string') return toAttachmentDataUrl(parsed, name);
      if (parsed && typeof parsed === 'object' && 'content' in parsed) {
        return toAttachmentDataUrl((parsed as { content?: unknown }).content, name);
      }
    } catch {
      // not JSON; treat as raw base64 below
    }
  }
  return `data:${inferAttachmentMimeType(name)};base64,${trimmed}`;
}

function bytesLookLikePdf(bytes: Uint8Array): boolean {
  if (bytes.length < 5) return false;
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
}

/** PDF blob MIME must be application/pdf for Chrome's built-in viewer with blob: URLs. */
function dataUrlToPreviewObjectUrl(dataUrl: string, fileName?: string): { url: string; revoke: () => void } {
  const trimmed = dataUrl.trim();
  const commaIdx = trimmed.indexOf(',');
  if (trimmed.startsWith('data:') && commaIdx !== -1) {
    const header = trimmed.slice(5, commaIdx);
    const body = trimmed.slice(commaIdx + 1);
    let mime = /^([^;]+)/.exec(header)?.[1]?.trim() || 'application/octet-stream';
    const isB64 = /;base64/i.test(header);
    let bytes: Uint8Array;
    if (isB64) {
      const bin = atob(body.replace(/\s/g, ''));
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(body.replace(/\+/g, ' ')));
    }
    const generic =
      mime === 'application/octet-stream' ||
      mime === 'binary/octet-stream' ||
      mime === 'text/plain' ||
      mime === '';
    if (bytesLookLikePdf(bytes) || (isPdfFile(fileName ?? '') && generic)) {
      mime = 'application/pdf';
    } else if (generic && isImageFile(fileName ?? '')) {
      mime = inferAttachmentMimeType(fileName ?? '');
    }
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    return { url, revoke: () => URL.revokeObjectURL(url) };
  }
  const bin = atob(trimmed.replace(/\s/g, ''));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  let fallbackMime = 'application/octet-stream';
  if (bytesLookLikePdf(bytes) || isPdfFile(fileName ?? '')) fallbackMime = 'application/pdf';
  else if (isImageFile(fileName ?? '')) fallbackMime = inferAttachmentMimeType(fileName ?? '');
  const blob = new Blob([bytes], { type: fallbackMime });
  const url = URL.createObjectURL(blob);
  return { url, revoke: () => URL.revokeObjectURL(url) };
}

function customCriterionKey(criterionId: number | string) {
  return `custom:${String(criterionId)}`;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Employee / team-user Additional Roles (stored on employees.work_status — English keys). */
const ADDITIONAL_ROLE_OPTIONS = [
  'Assessor',
  'Instructor',
  'Driving Instructor',
  'Senior Driver Instructor',
  'Assurance Manager',
  'TOLO',
  'DCP',
  'Mentor',
  'Verifier',
  'Training Delivery',
] as const;

/** Normalize for role matching (case + Arabic diacritics / presentation forms). */
function normalizeRoleCompareKey(value: string): string {
  return value
    .trim()
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
    .toLowerCase();
}

function parseAdditionalRoles(workStatus?: string | null): string[] {
  return (workStatus || '').split(',').map((v) => v.trim()).filter(Boolean);
}

/** All known labels for an English role across UI locales (fixes roles saved while UI was Arabic). */
function additionalRoleVariants(englishRole: string): string[] {
  const labels = new Set<string>([englishRole]);
  for (const lng of ['en', 'es', 'fr', 'cy', 'it', 'de', 'ar']) {
    const bundle = i18n.getResourceBundle(lng, 'ui') as Record<string, string> | undefined;
    const translated = String(bundle?.[englishRole] ?? '').trim();
    if (translated) labels.add(translated);
  }
  const live = translateUi(englishRole).trim();
  if (live) labels.add(live);
  return [...labels];
}

function canonicalizeAdditionalRole(role: string): string {
  const raw = role.trim();
  if (!raw) return raw;
  const needle = normalizeRoleCompareKey(raw);
  for (const english of ADDITIONAL_ROLE_OPTIONS) {
    if (normalizeRoleCompareKey(english) === needle) return english;
    if (additionalRoleVariants(english).some((v) => normalizeRoleCompareKey(v) === needle)) {
      return english;
    }
  }
  return raw;
}

function rolesMatch(stored: string, englishRole: string): boolean {
  return normalizeRoleCompareKey(canonicalizeAdditionalRole(stored)) === normalizeRoleCompareKey(englishRole);
}

function hasAdditionalRole(workStatus: string | null | undefined, role: string): boolean {
  return parseAdditionalRoles(workStatus).some((r) => rolesMatch(r, role));
}

/** Display label in the active UI language; never leave a frozen Arabic/other-locale value on screen. */
function formatAdditionalRoleLabel(role: string): string {
  return translateUi(canonicalizeAdditionalRole(role));
}

/** Persist English role keys only (repair rows corrupted by locale-at-module-load). */
function normalizeAdditionalRolesCsv(workStatus?: string | null): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const role of parseAdditionalRoles(workStatus)) {
    const english = canonicalizeAdditionalRole(role);
    const key = normalizeRoleCompareKey(english);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(english);
  }
  return out.join(',');
}

function userHasAssessorAdditionalRole(user: { work_status?: string | null }): boolean {
  return hasAdditionalRole(user.work_status, 'Assessor');
}

function isSamePersonName(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = (a || '').trim().toLowerCase();
  const right = (b || '').trim().toLowerCase();
  return Boolean(left && right && left === right);
}

function formatMonitoringStatusLabel(label: string): string {
  if (!label) return label;
  const raw = label.trim();
  if (raw === 'no issues') return translateUi('No Issues');
  const known = [
    'Off Track',
    'Awaiting Med Check',
    'OH follow-up due',
    'On additional support',
    'Relieve end of journey unless SPAD',
    'On additional monitoring',
    'Relieve upon incident',
    'no issues',
    'No Issues',
  ];
  const needle = normalizeRoleCompareKey(raw);
  for (const english of known) {
    if (normalizeRoleCompareKey(english) === needle) {
      return english === 'no issues' || english === 'No Issues'
        ? translateUi('No Issues')
        : translateUi(english);
    }
    for (const lng of ['en', 'es', 'fr', 'cy', 'it', 'de', 'ar']) {
      const bundle = i18n.getResourceBundle(lng, 'ui') as Record<string, string> | undefined;
      const translated = String(bundle?.[english] ?? '').trim();
      if (translated && normalizeRoleCompareKey(translated) === needle) {
        return english === 'no issues' || english === 'No Issues'
          ? translateUi('No Issues')
          : translateUi(english);
      }
    }
  }
  return translateUi(raw);
}

function formatEmployeeStatusLabel(status: string | null | undefined): string {
  const value = String(status || '').trim();
  if (!value) return translateUi('Unknown');
  return translateUi(value);
}

type AssessorOption = { id: string | number; full_name: string };

function buildAssessorOptions(
  teamUsers: Array<{ id?: string | number; full_name?: string; work_status?: string | null }>,
  employees: Array<{ id?: string; full_name?: string; work_status?: string | null }>,
  excludeName?: string | null,
): AssessorOption[] {
  const seen = new Map<string, AssessorOption>();
  for (const user of teamUsers) {
    if (!userHasAssessorAdditionalRole(user)) continue;
    const name = String(user.full_name ?? '').trim();
    if (!name || isSamePersonName(name, excludeName)) continue;
    seen.set(name.toLowerCase(), { id: user.id ?? name, full_name: name });
  }
  for (const employee of employees) {
    if (!hasAdditionalRole(employee.work_status, 'Assessor')) continue;
    const name = String(employee.full_name ?? '').trim();
    if (!name || isSamePersonName(name, excludeName)) continue;
    if (!seen.has(name.toLowerCase())) {
      seen.set(name.toLowerCase(), { id: employee.id ?? name, full_name: name });
    }
  }
  return [...seen.values()].sort((a, b) => a.full_name.localeCompare(b.full_name, undefined, { sensitivity: 'base' }));
}

function getAssessmentGradeBadgeClasses(grade: string | null | undefined, isDarkMode = false) {
  return gradeOptionPillClasses(grade, isDarkMode);
}

/** Shown after OCR fills licence fields; user must tick confirmation before Save. */
const LICENCE_SCAN_REVIEW_PREAMBLE =
  'Please review that the licence details are correct (whether scanned or entered manually), then tick below to confirm before saving.';

/** Proactive guidance — OCR auto-fill is UK train driving licence layout only. */
const LICENCE_INTERNATIONAL_HELPER_TITLE = 'International licences';
const LICENCE_INTERNATIONAL_HELPER_BODY =
  'Automatic scanning only supports UK train driving licences (for example UK7120 followed by six digits). International licences often use a different layout and field labels, so upload photos if needed, then enter the licence number, dates, language and restrictions manually and confirm before saving.';
const LICENCE_INTERNATIONAL_SCAN_FAIL_HINT =
  ' If this is an international licence, enter the details manually — automatic scanning supports UK train driving licences only.';

const LICENCE_HOLDER_UPLOAD_HINT = 'Drag and drop or click to upload';
const LICENCE_FRONT_SPECIMEN_SRC = '/uk-train-driving-licence-specimen.jpg';
const LICENCE_BACK_SPECIMEN_SRC = '/uk-train-driving-licence-back-specimen.jpg';

/** True when licence content exists but confirmation stamps are not yet recorded. */
function employeeNeedsLicenceDetailsConfirmation(emp: {
  licence_image_url?: string | null;
  licence_back_image_url?: string | null;
  licence_number?: string | null;
  licence_issue_date?: string | null;
  licence_expiry?: string | null;
  licence_details_confirmed_at?: string | null;
  licence_details_confirmed_by?: string | null;
} | null | undefined): boolean {
  if (!emp) return false;
  const confirmed = Boolean(
    String(emp.licence_details_confirmed_at ?? "").trim() &&
      String(emp.licence_details_confirmed_by ?? "").trim(),
  );
  if (confirmed) return false;
  const hasMedia = Boolean(
    String(emp.licence_image_url ?? "").trim() || String(emp.licence_back_image_url ?? "").trim(),
  );
  const hasDetails = Boolean(
    String(emp.licence_number ?? "").trim() ||
      String(emp.licence_issue_date ?? "").trim() ||
      String(emp.licence_expiry ?? "").trim(),
  );
  return hasMedia || hasDetails;
}

const LICENCE_UPLOAD_NEED_EDIT_MSG =
  'Please select Edit Record first (at the top of the employee profile) before uploading licence images.';

const LICENCE_EXPIRY_YELLOW_DAYS = 90;
const LICENCE_EXPIRY_RED_DAYS = 42;

function classifyEmployeeLicenceExpiry(licenceExpiry: string | null | undefined): {
  urgency: "ok" | "yellow" | "red" | "expired" | "missing";
  days: number | null;
  expiryYmd: string | null;
} {
  const raw = String(licenceExpiry ?? "").trim();
  if (!raw) return { urgency: "missing", days: null, expiryYmd: null };
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  const expiryYmd = iso
    ? iso[1]
    : (() => {
        const d = new Date(raw);
        return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : null;
      })();
  if (!expiryYmd) return { urgency: "missing", days: null, expiryYmd: null };
  const today = new Date().toISOString().slice(0, 10);
  const days = Math.round(
    (new Date(`${expiryYmd}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime()) /
      86400000,
  );
  if (days < 0) return { urgency: "expired", days, expiryYmd };
  if (days <= LICENCE_EXPIRY_RED_DAYS) return { urgency: "red", days, expiryYmd };
  if (days <= LICENCE_EXPIRY_YELLOW_DAYS) return { urgency: "yellow", days, expiryYmd };
  return { urgency: "ok", days, expiryYmd };
}

function formatLicenceExpiryDisplay(expiryYmd: string): string {
  const d = new Date(`${expiryYmd}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return expiryYmd;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

type LicenceFieldHighlightKey =
  | 'licence_number'
  | 'licence_issue_date'
  | 'licence_expiry'
  | 'licence_language'
  | 'licence_restrictions';

function formatDateUK(value: string | Date | null | undefined): string {
  if (!value) return '—';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      return `${d}-${m}-${y}`;
    }
  }
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

/** Inclusive end date (YYYY-MM-DD): period is start through end, length = duration in the given unit. */
function calculateCdpEndDateInclusive(
  startYYYYMMDD: string,
  value: number,
  unit: 'days' | 'months' | 'years'
): string | null {
  const datePart = startYYYYMMDD.split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  const start = new Date(y, m - 1, d);
  if (Number.isNaN(start.getTime())) return null;
  const v = Math.floor(value);
  if (v < 1) return null;
  const anniversary = new Date(start);
  if (unit === 'days') anniversary.setDate(anniversary.getDate() + v);
  else if (unit === 'months') anniversary.setMonth(anniversary.getMonth() + v);
  else anniversary.setFullYear(anniversary.getFullYear() + v);
  const end = new Date(anniversary);
  end.setDate(end.getDate() - 1);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

function formatDateTimeUK(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return `${formatDateUK(d)} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function formatDateTimeUKShort(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatStorageBytes(bytes: number | null | undefined): string {
  const n = Number(bytes ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  const TB_AT_1000_GB = GB * 1000;
  if (n >= TB_AT_1000_GB) {
    const tbValue = n / TB_AT_1000_GB;
    const precision = tbValue >= 100 ? 0 : tbValue >= 10 ? 1 : 2;
    return `${tbValue.toFixed(precision)} TB`;
  }
  if (n >= GB) {
    const gbValue = n / GB;
    const precision = gbValue >= 100 ? 0 : gbValue >= 10 ? 1 : 2;
    return `${gbValue.toFixed(precision)} GB`;
  }
  if (n >= MB) {
    const mbValue = n / MB;
    const precision = mbValue >= 100 ? 0 : mbValue >= 10 ? 1 : 2;
    return `${mbValue.toFixed(precision)} MB`;
  }
  if (n >= KB) {
    const kbValue = n / KB;
    const precision = kbValue >= 100 ? 0 : kbValue >= 10 ? 1 : 2;
    return `${kbValue.toFixed(precision)} KB`;
  }
  return `${Math.round(n)} B`;
}

/** Matches server `noteReadsEmailKey` — used for read receipt map keys. */
function noteReadsEmailKey(email: string): string {
  return email.toLowerCase().trim().replace(/[.#$\[\]]/g, "_");
}

/** Soft per-author bubble colours — muted pastels, still clearly different. */
function noteAuthorPaletteByIndex(index: number, dark: boolean): {
  avatar: string;
  name: string;
  fill: string;
  bubbleText: string;
} {
  const palettes = dark
    ? [
        { avatar: "bg-sky-700", name: "text-sky-300", fill: "#0c4a6e", bubbleText: "text-sky-100" },
        { avatar: "bg-amber-700", name: "text-amber-200", fill: "#78350f", bubbleText: "text-amber-50" },
        { avatar: "bg-rose-700", name: "text-rose-200", fill: "#9f1239", bubbleText: "text-rose-50" },
        { avatar: "bg-teal-700", name: "text-teal-200", fill: "#115e59", bubbleText: "text-teal-50" },
        { avatar: "bg-violet-700", name: "text-violet-200", fill: "#5b21b6", bubbleText: "text-violet-50" },
        { avatar: "bg-orange-700", name: "text-orange-200", fill: "#9a3412", bubbleText: "text-orange-50" },
        { avatar: "bg-cyan-700", name: "text-cyan-200", fill: "#155e75", bubbleText: "text-cyan-50" },
        { avatar: "bg-fuchsia-700", name: "text-fuchsia-200", fill: "#86198f", bubbleText: "text-fuchsia-50" },
      ]
    : [
        { avatar: "bg-sky-400", name: "text-sky-700", fill: "#e0f2fe", bubbleText: "text-slate-800" },
        { avatar: "bg-amber-400", name: "text-amber-800", fill: "#fef3c7", bubbleText: "text-slate-800" },
        { avatar: "bg-rose-400", name: "text-rose-700", fill: "#ffe4e6", bubbleText: "text-slate-800" },
        { avatar: "bg-teal-500", name: "text-teal-800", fill: "#ccfbf1", bubbleText: "text-slate-800" },
        { avatar: "bg-violet-400", name: "text-violet-700", fill: "#ede9fe", bubbleText: "text-slate-800" },
        { avatar: "bg-orange-400", name: "text-orange-800", fill: "#ffedd5", bubbleText: "text-slate-800" },
        { avatar: "bg-cyan-500", name: "text-cyan-800", fill: "#cffafe", bubbleText: "text-slate-800" },
        { avatar: "bg-fuchsia-400", name: "text-fuchsia-700", fill: "#fae8ff", bubbleText: "text-slate-800" },
      ];
  const i = Number.isFinite(index) ? Math.abs(Math.floor(index)) : 0;
  return palettes[i % palettes.length];
}

function formatHeaderDateUK(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const lang = (i18n.language || 'en').split('-')[0];
  const locale = lang === 'en' ? 'en-GB' : lang;
  const day = d.getDate();
  const suffix = (n: number) => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    if (n % 10 === 1) return 'st';
    if (n % 10 === 2) return 'nd';
    if (n % 10 === 3) return 'rd';
    return 'th';
  };
  const weekday = d.toLocaleDateString(locale, { weekday: 'long' });
  const month = d.toLocaleDateString(locale, { month: 'short' });
  if (lang === 'en') {
    return `${weekday} ${day}${suffix(day)} ${month} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatAssessmentMeta(value: string | null | undefined) {
  return formatDateTimeUK(value);
}

function profileTitleIsSystemAdministrator(title?: string | null): boolean {
  const t = String(title ?? '').trim().toLowerCase();
  return t.includes('system') && (t.includes('administrator') || t.includes('admin'));
}

function emailIsBuiltinSystemAdmin(email?: string | null): boolean {
  const em = String(email ?? '').trim().toLowerCase();
  return (
    em === 'admin@railvault.com' ||
    em === 'admin@railintel.co.uk' ||
    em === 'admin@cms.railintel.co.uk' ||
    em === 'andyuk38@gmail.com'
  );
}

/** Clean unit label for display: "u1" -> "Unit 1", etc. */
function cleanUnitDisplay(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "—";
  const m = s.match(/^u(\d+)$/i);
  if (m) return `Unit ${m[1]}`;
  return s;
}

class PageErrorBoundary extends React.Component<{ children: React.ReactNode; name?: string }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[${this.props.name || 'Page'}] render error:`, error, info);
    if (isChunkLoadError(error)) reloadOnceForChunkError();
  }
  render() {
    if (this.state.error) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="bg-white p-8 rounded-2xl border border-red-200 shadow-sm">
          <h3 className="text-lg font-bold text-red-700 mb-2">{translateUi("Something went wrong")}</h3>
          <p className="text-sm text-slate-600 mb-2">
            {chunkError
              ? translateUi("This page is still starting or was updated. Refresh to load it.")
              : `${this.props.name || 'Page'} encountered an error:`}
          </p>
          {!chunkError ? (
            <pre className="text-xs bg-red-50 text-red-800 p-3 rounded-lg overflow-auto max-h-40">{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (chunkError) {
                window.location.reload();
                return;
              }
              this.setState({ error: null });
            }}
            className="mt-3 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg"
          >
            {translateUi("Try again")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function LazyPageShell({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <PageErrorBoundary name={name}>
      <Suspense
        fallback={
          <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-500 shadow-sm">
            {translateUi("Loading…")}
          </div>
        }
      >
        {children}
      </Suspense>
    </PageErrorBoundary>
  );
}

// Types
interface Competency {
  id: number | string;
  type: string;
  date_assessed: string;
  expiry_date: string;
  result: string;
  assessed_by: string;
  notes?: string;
  certificate_url?: string;
  /** Stored in Azure JSON store: distinguishes rolling stock vs route vs rules rows */
  competency_category?: 'traction' | 'route' | 'rules';
  rtdb_key?: string;
}

interface Medical {
  id: number;
  type: string;
  date_completed: string;
  expiry_date: string;
  status: string;
  restrictions: string;
  is_latest?: number | string | boolean;
  medical_standard_assessed_against?: string;
  pre_orr?: boolean | number | string;
  psychological_pre_orr?: boolean | number | string;
  psychological_exam_passed?: boolean | number | string;
  psychologist?: string;
  doctor?: string;
  glasses?: boolean | number | string;
  contact_lenses?: boolean | number | string;
  hearing_aid?: boolean | number | string;
  comms_device?: boolean | number | string;
  last_updated_by?: string;
  certificate_name?: string;
  certificate_content?: string;
}

interface Employee {
  id: string;
  full_name: string;
  role: string;
  depot: string;
  status: string;
  email: string;
  phone: string;
  work_phone?: string;
  phone_country_code?: string;
  manager: string;
  start_date: string;
  dob: string;
  place_of_birth?: string;
  /** UK National Insurance number */
  ni_number?: string;
  image_url?: string;
  work_status?: string;
  monitoring_status?: string;
  licence_image_url?: string;
  licence_back_image_url?: string;
  licence_number?: string;
  licence_issue_date?: string;
  licence_expiry?: string;
  licence_language?: string;
  licence_restrictions?: string;
  licence_categories?: string;
  signature_url?: string;
  /** ISO timestamp when user confirmed scanned licence details */
  licence_details_confirmed_at?: string;
  /** Display name of user who confirmed */
  licence_details_confirmed_by?: string;
  /** ISO timestamp when licence details were first added */
  licence_details_added_at?: string;
  /** Display name of user who first added licence details */
  licence_details_added_by?: string;
  licence_details_deleted_at?: string;
  licence_details_deleted_by?: string;
  company_code?: string;
  company_name?: string;
  company_logo_url?: string;
  address?: string;
  postcode?: string;
  gender_types?: string;
  emergency_contact_name?: string;
  emergency_contact_country_code?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  competencies?: Competency[];
  medicals?: Medical[];
  incidents?: any[];
  notes?: any[];
  /** Grouped note threads (active + archived) with start/end dates. */
  note_conversations?: Array<{
    id: string;
    employee_id?: string;
    title?: string | null;
    status?: string;
    started_at?: string;
    ended_at?: string | null;
    created_by_email?: string | null;
    created_by_name?: string | null;
    locked?: number | boolean | null;
    locked_at?: string | null;
    locked_by_email?: string | null;
    locked_by_name?: string | null;
    /** ISO date when unlocked archived threads are auto-purged; null when locked. */
    retention_expires_at?: string | null;
  }>;
  company_admin_competency_removals?: Array<{
    kind?: string;
    value?: string;
    removed_at?: string;
    removed_by?: string;
  }>;
  medication_declarations?: any[];
  training_records?: any[];
  qualifications?: any[];
  safety_briefs?: any[];
  experience_records_json?: string;
  /** JSON array of cab pass records; see CabPassesTab. */
  cab_passes_json?: string;
}

type PhoneCountryOption = {
  dialCode: string;
  flag: string;
  label: string;
};

type GenderTypeOption = {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeClassName: string;
};

const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
  { dialCode: "+1", flag: "🇺🇸", label: "United States / Canada / Caribbean territories" },
  { dialCode: "+7", flag: "🇷🇺", label: "Russia / Kazakhstan" },
  { dialCode: "+20", flag: "🇪🇬", label: "Egypt" },
  { dialCode: "+27", flag: "🇿🇦", label: "South Africa" },
  { dialCode: "+30", flag: "🇬🇷", label: "Greece" },
  { dialCode: "+31", flag: "🇳🇱", label: "Netherlands" },
  { dialCode: "+32", flag: "🇧🇪", label: "Belgium" },
  { dialCode: "+33", flag: "🇫🇷", label: "France" },
  { dialCode: "+34", flag: "🇪🇸", label: "Spain" },
  { dialCode: "+36", flag: "🇭🇺", label: "Hungary" },
  { dialCode: "+39", flag: "🇮🇹", label: "Italy / Vatican City State" },
  { dialCode: "+40", flag: "🇷🇴", label: "Romania" },
  { dialCode: "+41", flag: "🇨🇭", label: "Switzerland" },
  { dialCode: "+43", flag: "🇦🇹", label: "Austria" },
  { dialCode: "+44", flag: "🇬🇧", label: "United Kingdom" },
  { dialCode: "+45", flag: "🇩🇰", label: "Denmark" },
  { dialCode: "+46", flag: "🇸🇪", label: "Sweden" },
  { dialCode: "+47", flag: "🇳🇴", label: "Norway" },
  { dialCode: "+48", flag: "🇵🇱", label: "Poland" },
  { dialCode: "+49", flag: "🇩🇪", label: "Germany" },
  { dialCode: "+51", flag: "🇵🇪", label: "Peru" },
  { dialCode: "+52", flag: "🇲🇽", label: "Mexico" },
  { dialCode: "+53", flag: "🇨🇺", label: "Cuba" },
  { dialCode: "+54", flag: "🇦🇷", label: "Argentina" },
  { dialCode: "+55", flag: "🇧🇷", label: "Brazil" },
  { dialCode: "+56", flag: "🇨🇱", label: "Chile" },
  { dialCode: "+57", flag: "🇨🇴", label: "Colombia" },
  { dialCode: "+58", flag: "🇻🇪", label: "Venezuela" },
  { dialCode: "+60", flag: "🇲🇾", label: "Malaysia" },
  { dialCode: "+61", flag: "🇦🇺", label: "Australia" },
  { dialCode: "+62", flag: "🇮🇩", label: "Indonesia" },
  { dialCode: "+63", flag: "🇵🇭", label: "Philippines" },
  { dialCode: "+64", flag: "🇳🇿", label: "New Zealand" },
  { dialCode: "+65", flag: "🇸🇬", label: "Singapore" },
  { dialCode: "+66", flag: "🇹🇭", label: "Thailand" },
  { dialCode: "+81", flag: "🇯🇵", label: "Japan" },
  { dialCode: "+82", flag: "🇰🇷", label: "South Korea" },
  { dialCode: "+84", flag: "🇻🇳", label: "Vietnam" },
  { dialCode: "+86", flag: "🇨🇳", label: "China" },
  { dialCode: "+90", flag: "🇹🇷", label: "Turkey" },
  { dialCode: "+91", flag: "🇮🇳", label: "India" },
  { dialCode: "+92", flag: "🇵🇰", label: "Pakistan" },
  { dialCode: "+93", flag: "🇦🇫", label: "Afghanistan" },
  { dialCode: "+94", flag: "🇱🇰", label: "Sri Lanka" },
  { dialCode: "+95", flag: "🇲🇲", label: "Myanmar" },
  { dialCode: "+98", flag: "🇮🇷", label: "Iran" },
  { dialCode: "+220", flag: "🇬🇲", label: "Gambia" },
  { dialCode: "+221", flag: "🇸🇳", label: "Senegal" },
  { dialCode: "+222", flag: "🇲🇷", label: "Mauritania" },
  { dialCode: "+223", flag: "🇲🇱", label: "Mali" },
  { dialCode: "+224", flag: "🇬🇳", label: "Guinea" },
  { dialCode: "+225", flag: "🇨🇮", label: "Cote d'Ivoire" },
  { dialCode: "+226", flag: "🇧🇫", label: "Burkina Faso" },
  { dialCode: "+227", flag: "🇳🇪", label: "Niger" },
  { dialCode: "+228", flag: "🇹🇬", label: "Togo" },
  { dialCode: "+229", flag: "🇧🇯", label: "Benin" },
  { dialCode: "+230", flag: "🇲🇺", label: "Mauritius" },
  { dialCode: "+231", flag: "🇱🇷", label: "Liberia" },
  { dialCode: "+232", flag: "🇸🇱", label: "Sierra Leone" },
  { dialCode: "+233", flag: "🇬🇭", label: "Ghana" },
  { dialCode: "+234", flag: "🇳🇬", label: "Nigeria" },
  { dialCode: "+235", flag: "🇹🇩", label: "Chad" },
  { dialCode: "+236", flag: "🇨🇫", label: "Central African Republic" },
  { dialCode: "+237", flag: "🇨🇲", label: "Cameroon" },
  { dialCode: "+238", flag: "🇨🇻", label: "Cabo Verde" },
  { dialCode: "+239", flag: "🇸🇹", label: "Sao Tome and Principe" },
  { dialCode: "+240", flag: "🇬🇶", label: "Equatorial Guinea" },
  { dialCode: "+241", flag: "🇬🇦", label: "Gabon" },
  { dialCode: "+242", flag: "🇨🇬", label: "Congo, Republic of the" },
  { dialCode: "+243", flag: "🇨🇩", label: "Congo, Democratic Republic of the" },
  { dialCode: "+244", flag: "🇦🇴", label: "Angola" },
  { dialCode: "+245", flag: "🇬🇼", label: "Guinea-Bissau" },
  { dialCode: "+246", flag: "🇮🇴", label: "Diego Garcia" },
  { dialCode: "+247", flag: "🇦🇨", label: "Ascension" },
  { dialCode: "+248", flag: "🇸🇨", label: "Seychelles" },
  { dialCode: "+249", flag: "🇸🇩", label: "Sudan" },
  { dialCode: "+250", flag: "🇷🇼", label: "Rwanda" },
  { dialCode: "+251", flag: "🇪🇹", label: "Ethiopia" },
  { dialCode: "+252", flag: "🇸🇴", label: "Somalia" },
  { dialCode: "+253", flag: "🇩🇯", label: "Djibouti" },
  { dialCode: "+254", flag: "🇰🇪", label: "Kenya" },
  { dialCode: "+255", flag: "🇹🇿", label: "Tanzania" },
  { dialCode: "+256", flag: "🇺🇬", label: "Uganda" },
  { dialCode: "+257", flag: "🇧🇮", label: "Burundi" },
  { dialCode: "+258", flag: "🇲🇿", label: "Mozambique" },
  { dialCode: "+260", flag: "🇿🇲", label: "Zambia" },
  { dialCode: "+261", flag: "🇲🇬", label: "Madagascar" },
  { dialCode: "+262", flag: "🇷🇪", label: "Reunion / Mayotte" },
  { dialCode: "+263", flag: "🇿🇼", label: "Zimbabwe" },
  { dialCode: "+264", flag: "🇳🇦", label: "Namibia" },
  { dialCode: "+265", flag: "🇲🇼", label: "Malawi" },
  { dialCode: "+266", flag: "🇱🇸", label: "Lesotho" },
  { dialCode: "+267", flag: "🇧🇼", label: "Botswana" },
  { dialCode: "+268", flag: "🇸🇿", label: "Eswatini" },
  { dialCode: "+269", flag: "🇰🇲", label: "Comoros" },
  { dialCode: "+290", flag: "🇸🇭", label: "Saint Helena / Tristan da Cunha" },
  { dialCode: "+291", flag: "🇪🇷", label: "Eritrea" },
  { dialCode: "+297", flag: "🇦🇼", label: "Aruba" },
  { dialCode: "+298", flag: "🇫🇴", label: "Faroe Islands" },
  { dialCode: "+299", flag: "🇬🇱", label: "Greenland" },
  { dialCode: "+350", flag: "🇬🇮", label: "Gibraltar" },
  { dialCode: "+351", flag: "🇵🇹", label: "Portugal" },
  { dialCode: "+352", flag: "🇱🇺", label: "Luxembourg" },
  { dialCode: "+353", flag: "🇮🇪", label: "Ireland (Eire)" },
  { dialCode: "+354", flag: "🇮🇸", label: "Iceland" },
  { dialCode: "+355", flag: "🇦🇱", label: "Albania" },
  { dialCode: "+356", flag: "🇲🇹", label: "Malta" },
  { dialCode: "+357", flag: "🇨🇾", label: "Cyprus" },
  { dialCode: "+358", flag: "🇫🇮", label: "Finland" },
  { dialCode: "+359", flag: "🇧🇬", label: "Bulgaria" },
  { dialCode: "+370", flag: "🇱🇹", label: "Lithuania" },
  { dialCode: "+371", flag: "🇱🇻", label: "Latvia" },
  { dialCode: "+372", flag: "🇪🇪", label: "Estonia" },
  { dialCode: "+373", flag: "🇲🇩", label: "Moldova" },
  { dialCode: "+374", flag: "🇦🇲", label: "Armenia" },
  { dialCode: "+375", flag: "🇧🇾", label: "Belarus" },
  { dialCode: "+376", flag: "🇦🇩", label: "Andorra" },
  { dialCode: "+377", flag: "🇲🇨", label: "Monaco" },
  { dialCode: "+378", flag: "🇸🇲", label: "San Marino" },
  { dialCode: "+381", flag: "🇷🇸", label: "Serbia" },
  { dialCode: "+382", flag: "🇲🇪", label: "Montenegro" },
  { dialCode: "+383", flag: "🇽🇰", label: "Kosovo" },
  { dialCode: "+385", flag: "🇭🇷", label: "Croatia" },
  { dialCode: "+386", flag: "🇸🇮", label: "Slovenia" },
  { dialCode: "+387", flag: "🇧🇦", label: "Bosnia and Herzegovina" },
  { dialCode: "+389", flag: "🇲🇰", label: "North Macedonia" },
  { dialCode: "+420", flag: "🇨🇿", label: "Czech Republic" },
  { dialCode: "+421", flag: "🇸🇰", label: "Slovakia" },
  { dialCode: "+423", flag: "🇱🇮", label: "Liechtenstein" },
  { dialCode: "+500", flag: "🇫🇰", label: "Falkland Islands" },
  { dialCode: "+501", flag: "🇧🇿", label: "Belize" },
  { dialCode: "+502", flag: "🇬🇹", label: "Guatemala" },
  { dialCode: "+503", flag: "🇸🇻", label: "El Salvador" },
  { dialCode: "+504", flag: "🇭🇳", label: "Honduras" },
  { dialCode: "+505", flag: "🇳🇮", label: "Nicaragua" },
  { dialCode: "+506", flag: "🇨🇷", label: "Costa Rica" },
  { dialCode: "+507", flag: "🇵🇦", label: "Panama" },
  { dialCode: "+508", flag: "🇵🇲", label: "Saint Pierre and Miquelon" },
  { dialCode: "+509", flag: "🇭🇹", label: "Haiti" },
  { dialCode: "+590", flag: "🇫🇷", label: "French overseas territories" },
  { dialCode: "+591", flag: "🇧🇴", label: "Bolivia" },
  { dialCode: "+592", flag: "🇬🇾", label: "Guyana" },
  { dialCode: "+593", flag: "🇪🇨", label: "Ecuador" },
  { dialCode: "+594", flag: "🇬🇫", label: "French Guiana" },
  { dialCode: "+595", flag: "🇵🇾", label: "Paraguay" },
  { dialCode: "+596", flag: "🇲🇶", label: "Martinique" },
  { dialCode: "+597", flag: "🇸🇷", label: "Suriname" },
  { dialCode: "+598", flag: "🇺🇾", label: "Uruguay" },
  { dialCode: "+599", flag: "🇨🇼", label: "Curaçao / Caribbean Netherlands" },
  { dialCode: "+670", flag: "🇹🇱", label: "Timor-Leste" },
  { dialCode: "+672", flag: "🇦🇺", label: "Antarctica / Norfolk Island / Australian bases" },
  { dialCode: "+673", flag: "🇧🇳", label: "Brunei" },
  { dialCode: "+674", flag: "🇳🇷", label: "Nauru" },
  { dialCode: "+675", flag: "🇵🇬", label: "Papua New Guinea" },
  { dialCode: "+676", flag: "🇹🇴", label: "Tonga" },
  { dialCode: "+677", flag: "🇸🇧", label: "Solomon Islands" },
  { dialCode: "+678", flag: "🇻🇺", label: "Vanuatu" },
  { dialCode: "+679", flag: "🇫🇯", label: "Fiji" },
  { dialCode: "+680", flag: "🇵🇼", label: "Palau" },
  { dialCode: "+681", flag: "🇼🇫", label: "Wallis and Futuna" },
  { dialCode: "+682", flag: "🇨🇰", label: "Cook Islands" },
  { dialCode: "+683", flag: "🇳🇺", label: "Niue" },
  { dialCode: "+685", flag: "🇼🇸", label: "Samoa" },
  { dialCode: "+686", flag: "🇰🇮", label: "Kiribati" },
  { dialCode: "+687", flag: "🇳🇨", label: "New Caledonia" },
  { dialCode: "+688", flag: "🇹🇻", label: "Tuvalu" },
  { dialCode: "+689", flag: "🇵🇫", label: "French Polynesia" },
  { dialCode: "+690", flag: "🇹🇰", label: "Tokelau" },
  { dialCode: "+691", flag: "🇫🇲", label: "Micronesia" },
  { dialCode: "+692", flag: "🇲🇭", label: "Marshall Islands" },
  { dialCode: "+850", flag: "🇰🇵", label: "North Korea" },
  { dialCode: "+852", flag: "🇭🇰", label: "Hong Kong" },
  { dialCode: "+853", flag: "🇲🇴", label: "Macau" },
  { dialCode: "+855", flag: "🇰🇭", label: "Cambodia" },
  { dialCode: "+856", flag: "🇱🇦", label: "Laos" },
  { dialCode: "+880", flag: "🇧🇩", label: "Bangladesh" },
  { dialCode: "+886", flag: "🇹🇼", label: "Taiwan" },
  { dialCode: "+961", flag: "🇱🇧", label: "Lebanon" },
  { dialCode: "+962", flag: "🇯🇴", label: "Jordan" },
  { dialCode: "+963", flag: "🇸🇾", label: "Syria" },
  { dialCode: "+964", flag: "🇮🇶", label: "Iraq" },
  { dialCode: "+965", flag: "🇰🇼", label: "Kuwait" },
  { dialCode: "+966", flag: "🇸🇦", label: "Saudi Arabia" },
  { dialCode: "+967", flag: "🇾🇪", label: "Yemen" },
  { dialCode: "+968", flag: "🇴🇲", label: "Oman" },
  { dialCode: "+970", flag: "🇵🇸", label: "Palestine" },
  { dialCode: "+971", flag: "🇦🇪", label: "United Arab Emirates" },
  { dialCode: "+972", flag: "🇮🇱", label: "Israel" },
  { dialCode: "+974", flag: "🇶🇦", label: "Qatar" },
  { dialCode: "+975", flag: "🇧🇹", label: "Bhutan" },
  { dialCode: "+977", flag: "🇳🇵", label: "Nepal" },
  { dialCode: "+992", flag: "🇹🇯", label: "Tajikistan" },
  { dialCode: "+993", flag: "🇹🇲", label: "Turkmenistan" },
  { dialCode: "+994", flag: "🇦🇿", label: "Azerbaijan" },
  { dialCode: "+995", flag: "🇬🇪", label: "Georgia" },
  { dialCode: "+996", flag: "🇰🇬", label: "Kyrgyzstan" },
  { dialCode: "+998", flag: "🇺🇿", label: "Uzbekistan" },
];

const SORTED_PHONE_COUNTRY_OPTIONS = [...PHONE_COUNTRY_OPTIONS].sort((a, b) =>
  a.label.localeCompare(b.label, "en", { sensitivity: "base" })
);

const GENDER_TYPE_OPTIONS: GenderTypeOption[] = [
  { value: "Female", label: "Female", icon: Heart, badgeClassName: "bg-pink-50 text-pink-700 border-pink-100" },
  { value: "Male", label: "Male", icon: User, badgeClassName: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  { value: "Transgender", label: "Transgender", icon: ArrowLeftRight, badgeClassName: "bg-sky-50 text-sky-700 border-sky-100" },
  { value: "Other", label: "Other", icon: HelpCircle, badgeClassName: "bg-slate-50 text-slate-700 border-slate-200" },
  { value: "Prefer not to say", label: "Prefer not to say", icon: HelpCircle, badgeClassName: "bg-slate-50 text-slate-700 border-slate-200" },
];

const EMERGENCY_RELATIONSHIP_OPTIONS = [
  "Spouse / Partner",
  "Parent",
  "Child",
  "Sibling",
  "Grandparent",
  "Relative",
  "Guardian",
  "Next of kin",
  "Friend",
  "Other",
];

const normalizeUkPostcode = (value: string) => value.trim().toUpperCase().replace(/\s+/g, " ");

const normalizeNiNumber = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);

function parseGenderTypes(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed.map((v) => String(v)).filter(Boolean) : [];
  } catch {
    return trimmed.split(",").map((v) => v.trim()).filter(Boolean);
  }
}

interface Stats {
  totalActive: number;
  totalEmployees?: number;
  overdueComp: number;
  dueSoonComp: number;
  expiredMed: number;
  dueSoonMed: number;
  verificationsCount: number;
  complianceRate: number;
  offTrackCount: number;
}

interface SystemUser {
  id: number;
  full_name: string;
  email: string;
  role: string;
  title: string;
  company_code?: string;
  company_name?: string;
  /** RTDB: set during onboarding for the appointed company admin. */
  nominated_company_admin?: boolean;
  /** Optional per-company staffing ID (unique within the company when set). */
  employee_id?: string | null;
  user_status?: string;
  profile_image_url?: string;
  /** UI language preference (en/es/fr/cy/it/de/ar). */
  preferred_locale?: string | null;
  added_date?: string;
  created_at: string;
  updated_at?: string;
  /** Linked employee additional roles (comma-separated), when available. */
  work_status?: string | null;
}

interface NewCompanyFormState {
  companyCode: string;
  name: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

interface CompanyRow {
  id: string;
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  status: string;
  createdAt?: string;
  setupWizardStep?: string;
}

/** Postgres may return lowercased aliases (contactname / createdat); accept any casing. */
function pickCompanyField(raw: Record<string, unknown>, ...keys: string[]): string {
  const wanted = new Set(keys.map((k) => k.toLowerCase()));
  for (const [k, v] of Object.entries(raw)) {
    if (!wanted.has(k.toLowerCase()) || v == null) continue;
    const s = v instanceof Date ? v.toISOString() : String(v).trim();
    if (s) return s;
  }
  return "";
}

function normalizeCompanyListRow(raw: unknown): CompanyRow {
  const rec = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: pickCompanyField(rec, "id", "company_code"),
    name: pickCompanyField(rec, "name"),
    contactName: pickCompanyField(rec, "contactName", "contact_name"),
    contactEmail: pickCompanyField(rec, "contactEmail", "contact_email"),
    contactPhone: pickCompanyField(rec, "contactPhone", "contact_phone"),
    status: pickCompanyField(rec, "status") || "PendingSetup",
    createdAt: pickCompanyField(rec, "createdAt", "created_at") || undefined,
    setupWizardStep: pickCompanyField(rec, "setupWizardStep", "setup_wizard_step") || undefined,
  };
}

interface CompanyDetails extends CompanyRow {
  address?: string;
  billingPlan?: string;
  billingNextDate?: string;
  billingAmount?: string;
}

function generateFiveDigitCode(): string {
  return String(Math.floor(10000 + Math.random() * 90000));
}

const NewCompanyPage: React.FC<{
  onCreated?: () => void;
  isSystemAdmin?: boolean;
  currentUserEmail?: string | null;
  currentUserId?: string | null;
  isDarkMode?: boolean;
}> = ({ onCreated, isSystemAdmin, currentUserEmail, currentUserId, isDarkMode = false }) => {
  const [form, setForm] = useState<NewCompanyFormState>({
    companyCode: generateFiveDigitCode(),
    name: "",
    address: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
  });
  const [enabledAddons, setEnabledAddons] = useState<Record<string, boolean>>({});
  const [onboardingAddonChoices, setOnboardingAddonChoices] = useState(() =>
    getOnboardingAddons().map((m) => ({
      id: m.id,
      name: m.name,
      shortDescription: m.shortDescription,
    }))
  );
  const [addonsCatalogLoading, setAddonsCatalogLoading] = useState(false);
  const [registerFormOpen, setRegisterFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<string | null>(null);
  const [testEmailFrom, setTestEmailFrom] = useState<string | null>(null);
  const [smtpStatus, setSmtpStatus] = useState<{
    configured: boolean;
    missing: string[];
    host: string | null;
    passSet: boolean;
  } | null>(null);
  const [viewModalCode, setViewModalCode] = useState<string | null>(null);
  const [viewModalCompany, setViewModalCompany] = useState<CompanyDetails | null>(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  /** Included (admin-granted) modules for the company details modal — not free-offer. */
  const [viewModalIncludedAddons, setViewModalIncludedAddons] = useState<Record<string, boolean>>({});
  const [viewModalAddonsLoading, setViewModalAddonsLoading] = useState(false);
  const [viewModalAddonsSaving, setViewModalAddonsSaving] = useState(false);
  const [viewModalAddonsMessage, setViewModalAddonsMessage] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [wizardEditCode, setWizardEditCode] = useState<string | null>(null);
  const [wizardEditStep, setWizardEditStep] = useState<string>("welcome");
  const [wizardEditCompany, setWizardEditCompany] = useState<CompanyDetails | null>(null);
  const [companyStatusFilter, setCompanyStatusFilter] = useState<"all" | "Active" | "Suspended" | "PendingSetup">("all");
  const [confirmState, setConfirmState] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void } | null>(null);
  const [termsAgreementModalCode, setTermsAgreementModalCode] = useState<string | null>(null);
  const [termsAgreementHtml, setTermsAgreementHtml] = useState<string | null>(null);
  const [termsAgreementLoading, setTermsAgreementLoading] = useState(false);
  const [termsAgreementError, setTermsAgreementError] = useState<string | null>(null);
  const validWizardSteps = useMemo(() => ([
    "welcome",
    "terms-and-conditions",
    "company-details",
    "job-roles",
    "addons",
    "change-password",
    "framework",
    "company-admin",
    "review",
  ]), []);

  const checkApi = useCallback(async () => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    try {
      const r = await fetch("/api/health", { signal: ctrl.signal });
      clearTimeout(t);
      setApiReachable(r.ok);
    } catch {
      clearTimeout(t);
      setApiReachable(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await fetch("/api/companies", { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        setCompanies(Array.isArray(data) ? data.map(normalizeCompanyListRow) : []);
      }
    } catch (_) {
      setCompanies([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    checkApi();
  }, [checkApi]);

  useEffect(() => {
    if (!currentUserEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/smtp-status", {
          headers: { "X-User-Email": currentUserEmail },
          cache: "no-store",
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          configured?: boolean;
          missing?: string[];
          host?: string | null;
          passSet?: boolean;
        };
        if (!cancelled) {
          setSmtpStatus({
            configured: data.configured === true,
            missing: Array.isArray(data.missing) ? data.missing : [],
            host: data.host ?? null,
            passSet: data.passSet === true,
          });
        }
      } catch {
        if (!cancelled) setSmtpStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserEmail]);

  useEffect(() => {
    if (apiReachable) loadCompanies();
    else setLoadingList(false);
  }, [apiReachable, loadCompanies]);

  const platformCompany = useMemo(
    () => companies.find((c) => isSystemAdminCompanyCode(c.id)) ?? null,
    [companies]
  );
  const customerCompanies = useMemo(
    () => companies.filter((c) => !isSystemAdminCompanyCode(c.id)),
    [companies]
  );

  const handleChange = (field: keyof NewCompanyFormState, value: string) => {
    if (field === "companyCode") return;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openViewModal = useCallback(async (code: string) => {
    setViewModalCode(code);
    setViewModalLoading(true);
    setViewModalCompany(null);
    setViewModalIncludedAddons({});
    setViewModalAddonsMessage(null);
    setViewModalAddonsLoading(true);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`);
      if (res.ok) {
        const data = await res.json();
        setViewModalCompany(data as CompanyDetails);
      } else {
        setViewModalCompany(null);
      }
    } catch (_) {
      setViewModalCompany(null);
    } finally {
      setViewModalLoading(false);
    }
    try {
      const headers: Record<string, string> = { "X-Company-Code": code };
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      const addonsRes = await fetch(`/api/addons?companyCode=${encodeURIComponent(code)}&_=${Date.now()}`, {
        headers,
        cache: "no-store",
      });
      const addonsData = await addonsRes.json().catch(() => ({}));
      const active = new Set(
        Array.isArray(addonsData.addons)
          ? (addonsData.addons as { addonId?: string }[]).map((a) => String(a.addonId || "").trim()).filter(Boolean)
          : []
      );
      const next: Record<string, boolean> = {};
      for (const mod of getOnboardingAddons()) {
        next[mod.id] = active.has(mod.id);
      }
      setViewModalIncludedAddons(next);
    } catch {
      setViewModalIncludedAddons({});
    } finally {
      setViewModalAddonsLoading(false);
    }
  }, [currentUserEmail]);

  const closeViewModal = useCallback(() => {
    setViewModalCode(null);
    setViewModalCompany(null);
    setViewModalIncludedAddons({});
    setViewModalAddonsMessage(null);
  }, []);

  const saveViewModalIncludedAddons = useCallback(async () => {
    if (!viewModalCode || !currentUserEmail) return;
    const addonIds = Object.entries(viewModalIncludedAddons)
      .filter(([, on]) => on)
      .map(([id]) => id);
    setViewModalAddonsSaving(true);
    setViewModalAddonsMessage(null);
    try {
      if (addonIds.length === 0) {
        setViewModalAddonsMessage(
          "No modules selected. Company can still start trials or purchase from Add-ons."
        );
        return;
      }
      const res = await fetch("/api/admin/addons/grant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          "X-Company-Code": viewModalCode,
        },
        body: JSON.stringify({ companyCode: viewModalCode, addonIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      setViewModalAddonsMessage(
        `Included modules saved (${addonIds.length}). They show as Activated on Add-ons — no free offer required.`
      );
    } catch (err: any) {
      setViewModalAddonsMessage(err?.message || "Could not save included modules.");
    } finally {
      setViewModalAddonsSaving(false);
    }
  }, [viewModalCode, currentUserEmail, viewModalIncludedAddons]);

  const openTermsAgreementModal = useCallback(
    async (code: string) => {
      if (isSystemAdminCompanyCode(code)) {
        setError("Terms agreement records are not available for the Rail Intel system administrator company.");
        return;
      }
      if (!currentUserEmail) return;
      setTermsAgreementModalCode(code);
      setTermsAgreementHtml(null);
      setTermsAgreementError(null);
      setTermsAgreementLoading(true);
      try {
        const res = await fetch(`/api/companies/${encodeURIComponent(code)}/terms-agreement-record`, {
          headers: { "X-User-Email": currentUserEmail },
          cache: "no-store",
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          hasRecord?: boolean;
          companyCode?: string;
          companyName?: string;
          termsAcceptedAt?: string | null;
          termsAcceptedBy?: string;
          signerName?: string;
          signerJobTitle?: string;
          signatureDataUrl?: string | null;
          markdown?: string;
          termsAcceptedVersionLabel?: string;
        };
        if (!res.ok) {
          setTermsAgreementError(data.error || `Request failed (${res.status})`);
          return;
        }
        if (!data.hasRecord) {
          setTermsAgreementError("No stored terms acceptance record for this company yet.");
          return;
        }
        const html = buildTermsAgreementRecordHtml({
          signerName: data.signerName || "—",
          signerJobTitle: data.signerJobTitle || "—",
          signerEmail: data.termsAcceptedBy || "—",
          signatureDataUrl: data.signatureDataUrl || "",
          companyName: data.companyName || code,
          companyCode: data.companyCode || code,
          externalIp: "—",
          termsAgreementStampedAt: data.termsAcceptedAt ?? null,
          termsAccepted: !!data.termsAcceptedAt,
          termsAcceptedAt: data.termsAcceptedAt ?? null,
          markdown: typeof data.markdown === "string" ? data.markdown : undefined,
          versionLabel: typeof data.termsAcceptedVersionLabel === "string" ? data.termsAcceptedVersionLabel : undefined,
        });
        setTermsAgreementHtml(html);
      } catch {
        setTermsAgreementError("Failed to load terms agreement.");
      } finally {
        setTermsAgreementLoading(false);
      }
    },
    [currentUserEmail]
  );

  const closeTermsAgreementModal = useCallback(() => {
    setTermsAgreementModalCode(null);
    setTermsAgreementHtml(null);
    setTermsAgreementError(null);
  }, []);

  const printStoredTermsAgreement = useCallback(() => {
    if (!termsAgreementHtml) return;
    const code = String(termsAgreementModalCode || "record").trim() || "record";
    openTermsAgreementPdfDownload(termsAgreementHtml, `terms-agreement-${code}`);
  }, [termsAgreementHtml, termsAgreementModalCode]);

  const adminHeaders = useMemo(() => {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (currentUserEmail) h["X-User-Email"] = currentUserEmail;
    return h;
  }, [currentUserEmail]);

  /** Keep New Company checkboxes in sync with the live bolt-on catalog (new modules appear automatically). */
  useEffect(() => {
    if (!currentUserEmail) return;
    let cancelled = false;
    setAddonsCatalogLoading(true);
    fetch("/api/addons/catalog?onboardingOnly=1", {
      headers: { "X-User-Email": currentUserEmail },
      cache: "no-store",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { modules?: Array<{ id?: string; name?: string; shortDescription?: string }> } | null) => {
        if (cancelled) return;
        // Client catalog is the source of truth for which modules exist; API only refreshes copy when present.
        const local = getOnboardingAddons();
        const remoteById = new Map(
          (Array.isArray(data?.modules) ? data!.modules : [])
            .filter((m) => typeof m.id === "string" && String(m.id).trim())
            .map((m) => [String(m.id).trim(), m] as const)
        );
        setOnboardingAddonChoices(
          local.map((base) => {
            const remote = remoteById.get(base.id);
            return {
              id: base.id,
              name: String(remote?.name || base.name).trim() || base.name,
              shortDescription: String(remote?.shortDescription || base.shortDescription || "").trim(),
            };
          })
        );
      })
      .catch(() => {
        /* keep bundled catalog fallback */
      })
      .finally(() => {
        if (!cancelled) setAddonsCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserEmail]);

  const handleSuspend = useCallback(async (code: string) => {
    if (isSystemAdminCompanyCode(code)) {
      setError("The Rail Intel system administrator company cannot be suspended.");
      return;
    }
    if (!isSystemAdmin || !currentUserEmail) {
      setError("You must be signed in as a System Administrator to suspend a company.");
      return;
    }
    setActionLoading(code);
    setError(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`, {
        method: "PATCH",
        headers: adminHeaders,
        body: JSON.stringify({ status: "Suspended", userEmail: currentUserEmail }),
      });
      if (res.ok) {
        loadCompanies();
        setViewModalCompany((c) => (c?.id === code ? { ...c, status: "Suspended" } : c));
      } else {
        const text = await res.text();
        let msg = translateUi("Failed to suspend");
        try {
          const data = text ? JSON.parse(text) : {};
          if (typeof (data as { error?: string }).error === "string") msg = (data as { error: string }).error;
        } catch { /* use msg */ }
        setError(msg);
      }
    } catch (_) {
      setError("Failed to suspend company");
    } finally {
      setActionLoading(null);
    }
  }, [isSystemAdmin, currentUserEmail, adminHeaders, loadCompanies]);

  const handleActivate = useCallback(async (code: string) => {
    if (!isSystemAdmin || !currentUserEmail) return;
    setActionLoading(code);
    setError(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`, {
        method: "PATCH",
        headers: adminHeaders,
        body: JSON.stringify({ status: "Active", userEmail: currentUserEmail }),
      });
      if (res.ok) {
        loadCompanies();
        setViewModalCompany((c) => (c?.id === code ? { ...c, status: "Active" } : c));
      } else {
        const text = await res.text();
        let msg = translateUi("Failed to activate");
        try {
          const data = text ? JSON.parse(text) : {};
          if (typeof (data as { error?: string }).error === "string") msg = (data as { error: string }).error;
        } catch { /* use msg */ }
        setError(msg);
      }
    } catch (_) {
      setError("Failed to activate company");
    } finally {
      setActionLoading(null);
    }
  }, [isSystemAdmin, currentUserEmail, adminHeaders, loadCompanies]);

  const handleReactivate = useCallback(async (code: string) => {
    if (!isSystemAdmin || !currentUserEmail) return;
    setActionLoading(code);
    setError(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`, {
        method: "PATCH",
        headers: adminHeaders,
        body: JSON.stringify({ status: "Active", userEmail: currentUserEmail }),
      });
      if (res.ok) {
        loadCompanies();
        setViewModalCompany((c) => (c?.id === code ? { ...c, status: "Active" } : c));
      } else {
        const text = await res.text();
        let msg = translateUi("Failed to reactivate");
        try {
          const data = text ? JSON.parse(text) : {};
          if (typeof (data as { error?: string }).error === "string") msg = (data as { error: string }).error;
        } catch { /* use msg */ }
        setError(msg);
      }
    } catch (_) {
      setError("Failed to reactivate company");
    } finally {
      setActionLoading(null);
    }
  }, [isSystemAdmin, currentUserEmail, adminHeaders, loadCompanies]);

  const handleDeleteCompany = useCallback(async (code: string) => {
    if (isSystemAdminCompanyCode(code)) {
      setError("The Rail Intel system administrator company cannot be deleted.");
      return;
    }
    if (!isSystemAdmin || !currentUserEmail) return;
    setActionLoading(code);
    setError(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`, {
        method: "DELETE",
        headers: adminHeaders,
        body: JSON.stringify({ userEmail: currentUserEmail }),
      });
      if (res.ok) {
        setViewModalCode(null);
        setViewModalCompany(null);
        loadCompanies();
      } else {
        const text = await res.text();
        let msg = translateUi("Failed to delete company");
        try {
          const data = text ? JSON.parse(text) : {};
          if (typeof (data as { error?: string }).error === "string") msg = (data as { error: string }).error;
        } catch { /* use msg */ }
        setError(msg);
      }
    } catch (_) {
      setError("Failed to delete company");
    } finally {
      setActionLoading(null);
    }
  }, [isSystemAdmin, currentUserEmail, loadCompanies]);

  const confirmDeleteCompany = useCallback((code: string) => {
    if (isSystemAdminCompanyCode(code)) {
      setError("The Rail Intel system administrator company cannot be deleted.");
      return;
    }
    setConfirmState({
      isOpen: true,
      title: "Delete Company",
      message: `Are you sure you want to delete company ${code}?\n\nThis permanently deletes all employees, users, documents, qualifications, medication records, cycles, and related data for that company, and removes all of its files from Azure Blob Storage. This cannot be undone.`,
      onConfirm: async () => {
        await handleDeleteCompany(code);
        setConfirmState(null);
      },
    });
  }, [handleDeleteCompany]);

  const openWizardEditModal = useCallback(async (code: string, fallbackStep?: string) => {
    if (!isSystemAdmin) return;
    setWizardEditCode(code);
    setWizardEditStep(validWizardSteps.includes(String(fallbackStep || "")) ? String(fallbackStep) : "welcome");
    setWizardEditCompany(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(code)}`);
      const data = res.ok ? await res.json() : null;
      const apiStep = typeof data?.setupWizardStep === "string" ? data.setupWizardStep : "";
      if (validWizardSteps.includes(apiStep)) setWizardEditStep(apiStep);
      if (data && typeof data === "object") setWizardEditCompany(data as CompanyDetails);
    } catch {
      // Keep fallback/default value.
    }
  }, [isSystemAdmin, currentUserEmail, validWizardSteps]);

  const closeWizardEditModal = useCallback(() => {
    setWizardEditCode(null);
    setWizardEditStep("welcome");
    setWizardEditCompany(null);
  }, []);

  const persistWizardStep = useCallback(async (step: string) => {
    if (!wizardEditCode || !isSystemAdmin || !currentUserEmail) return;
    if (!validWizardSteps.includes(step)) return;
    setWizardEditStep(step);
    try {
      await fetch(`/api/companies/${encodeURIComponent(wizardEditCode)}/setup-wizard-step`, {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ step, userEmail: currentUserEmail }),
      });
    } catch (_) {
      // Best-effort persistence; the step editor still updates other RTDB-backed resources.
    }
  }, [wizardEditCode, isSystemAdmin, currentUserEmail, validWizardSteps, adminHeaders]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    (async () => {
      try {
        const selectedAddons = onboardingAddonChoices
          .map((m) => m.id)
          .filter((id) => enabledAddons[id]);
        const res = await fetch("/api/new-company", {
          method: "POST",
          headers: adminHeaders,
          body: JSON.stringify({ ...form, enabledAddons: selectedAddons, userEmail: currentUserEmail }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const text = await res.text();
        if (!res.ok) {
          let data: { error?: string } = {};
          try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text ? `${text.slice(0, 200)}` : `Request failed (${res.status})` }; }
          throw new Error(data.error || `Request failed (${res.status}). Run the app with "npm run dev".`);
        }
        let data: {
          companyCode?: string;
          emailSent?: boolean;
          emailSentTo?: string;
          emailBccTo?: string;
          emailError?: string;
          enabledAddons?: string[];
        } = {};
        try { data = text ? JSON.parse(text) : {}; } catch { }
        const newCode = data.companyCode || form.companyCode;
        const activatedNames = onboardingAddonChoices
          .filter((m) => (data.enabledAddons || selectedAddons).includes(m.id))
          .map((m) => m.name);
        const addonsNote =
          activatedNames.length > 0
            ? ` Enabled add-ons (shown as activated in onboarding): ${activatedNames.join(", ")}.`
            : "";
        if (data.emailSent) {
          setError(null);
          const bccNote = data.emailBccTo ? ` A copy was also sent to ${data.emailBccTo}.` : "";
          setSuccess(
            `Company code: ${newCode}. Welcome email sent to ${data.emailSentTo || form.contactEmail || "the contact"}.${bccNote}${addonsNote}`
          );
        } else {
          setSuccess(null);
          setError(
            `Company ${newCode} was created, but the welcome email was not sent${
              data.emailError ? ` (${data.emailError})` : ""
            }. Use Send test welcome email, or check Azure Log Stream for [New Company] lines.${addonsNote}`
          );
        }
        setForm({
          companyCode: generateFiveDigitCode(),
          name: "",
          address: "",
          contactName: "",
          contactEmail: "",
          contactPhone: "",
        });
        setEnabledAddons({});
        setRegisterFormOpen(false);
        loadCompanies();
        if (onCreated) onCreated();
      } catch (err: any) {
        if (err?.name === "AbortError") setError("Request timed out. Check that the server is running (npm run dev).");
        else setError(err?.message ?? "Failed to create company");
      } finally {
        clearTimeout(timeoutId);
        setSubmitting(false);
      }
    })();
  };

  return (
    <div className={cn("space-y-6", isDarkMode && "rv-new-company--dark")}>
      {isDarkMode && <style>{NEW_COMPANY_DARK_STYLES}</style>}
      {confirmState && (
        <ConfirmDialog
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      {(apiReachable === false || (typeof window !== "undefined" && window.location.hostname === "localhost" && window.location.port !== "" && window.location.port !== "3000")) && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800">
          <p className="font-semibold"> {translateUi("API not reachable (404) – wrong port")} </p>
          <p className="text-sm mt-1">
            You're on <strong>{typeof window !== "undefined" ? window.location.origin : ""}</strong>. The API runs only when you use the combined server on port 3000.
          </p>
          <p className="text-sm mt-2">
            1. Stop any running server (Ctrl+C).<br />
            2. In the project folder run: <code className="bg-amber-100 px-1 rounded">npm run dev</code><br />
            3. Open <a href="http://localhost:3000" className="underline font-semibold">http://localhost:3000</a> in this browser (not 5173 or another port).
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{translateUi("Company registration")}</h2>
          <p className="text-slate-500 text-sm">
            Register new companies and review the audit list of companies created from this page.
          </p>
        </div>
      </div>

      {smtpStatus && !smtpStatus.configured && (
        <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-900">
          <p className="font-semibold">Welcome emails cannot send — email provider is not configured</p>
          <p className="text-sm mt-1">
            Missing: <code className="bg-red-100 px-1 rounded">{smtpStatus.missing.join(", ") || "SMTP settings"}</code>.
            Open <strong>{translateUi("Email templates")}</strong> → <strong>Email provider settings</strong> and enter the SMTP details.
          </p>
        </div>
      )}
      {smtpStatus?.configured && (
        <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
          Email provider ready{smtpStatus.host ? ` (${smtpStatus.host})` : ""}. New company welcome emails should send.
        </div>
      )}

      {(error || success || testEmailResult) && !registerFormOpen && (
        <div className="space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm">
              {success}
            </div>
          )}
          {testEmailResult && (
            <div className={`p-3 rounded-xl text-sm space-y-1 ${testEmailResult.includes("sent") ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
              <p>{testEmailResult}</p>
              {testEmailFrom && (
                <p className="text-xs opacity-90 font-mono">Sender: {testEmailFrom}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setRegisterFormOpen((open) => !open)}
          aria-expanded={registerFormOpen}
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 text-left hover:bg-slate-50 transition-colors"
        >
          <span className="min-w-0">
            <span className="block text-base font-bold text-slate-900">Register new company</span>
            <span className="block text-sm text-slate-500 mt-0.5">
              {registerFormOpen
                ? "Enter company details and optional bolt-on modules, then send the invite."
                : "Open the registration form to create a company and optionally enable bolt-on modules."}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 shrink-0">
            {!registerFormOpen && (
              <span className="hidden sm:inline-flex items-center px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm">
                Register new company
              </span>
            )}
            <ChevronDown
              className={cn(
                "w-5 h-5 text-slate-500 transition-transform",
                registerFormOpen && "rotate-180"
              )}
            />
          </span>
        </button>

        {registerFormOpen && (
      <form
        onSubmit={handleSubmit}
        className="border-t border-slate-200 p-4 sm:p-6 lg:p-8 space-y-6"
      >
        {error && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm">
            {success}
          </div>
        )}
        {testEmailResult && (
          <div className={`p-3 rounded-xl text-sm space-y-1 ${testEmailResult.includes("sent") ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
            <p>{testEmailResult}</p>
            {testEmailFrom && (
              <p className="text-xs opacity-90 font-mono">Sender: {testEmailFrom}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
              {translateUi("Company code")}
            </label>
            <input
              type="text"
              readOnly
              className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm font-mono text-slate-700 cursor-not-allowed"
              value={form.companyCode}
              title={translateUi("Allocated 5-digit code (locked)")}
            />
            <p className="text-xs text-slate-500">Allocated 5-digit code (locked)</p>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
              {translateUi("Company name")}
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Rail Intel Ltd"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
              Primary contact name
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              value={form.contactName}
              onChange={(e) => handleChange("contactName", e.target.value)}
              placeholder="e.g. Jane Smith"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
              Primary contact email
            </label>
            <input
              type="email"
              required
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              value={form.contactEmail}
              onChange={(e) => handleChange("contactEmail", e.target.value)}
              placeholder="admin@customer.co.uk"
            />
            <p className="text-xs text-slate-500 mt-1">
              A welcome email with login details will be sent to this address.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
              Primary contact telephone
            </label>
            <input
              type="tel"
              required
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              value={form.contactPhone}
              onChange={(e) => handleChange("contactPhone", e.target.value)}
              placeholder="07700 900000"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
            Registered address
          </label>
          <textarea
            required
            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm min-h-[100px]"
            value={form.address}
            onChange={(e) => handleChange("address", e.target.value)}
            placeholder="Street, city, postcode"
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5 space-y-3">
          <div className="space-y-1">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Bolt-on modules</p>
            <p className="text-sm text-slate-600">
              Optionally include modules for this company now. Included modules show as already Activated (no trial).
              Modules left unchecked stay available for the company to start a 14-day trial or purchase.
              This is separate from system-admin Free offers.
            </p>
            {addonsCatalogLoading && (
              <p className="text-xs text-slate-500">Refreshing module catalogue…</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {onboardingAddonChoices.map((mod) => {
              const checked = !!enabledAddons[mod.id];
              return (
                <label
                  key={mod.id}
                  className={clsx(
                    "flex items-start gap-3 rounded-xl border bg-white p-3 cursor-pointer transition-colors",
                    checked ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={checked}
                    onChange={(e) =>
                      setEnabledAddons((prev) => ({
                        ...prev,
                        [mod.id]: e.target.checked,
                      }))
                    }
                  />
                  <span className="min-w-0 space-y-0.5">
                    <span className="block text-sm font-semibold text-slate-900">{mod.name}</span>
                    <span className="block text-xs text-slate-600 leading-relaxed">{mod.shortDescription}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 flex-wrap">
          <button
            type="button"
            onClick={() => setRegisterFormOpen(false)}
            className="px-6 py-2 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50"
          > Cancel </button>
          <button
            type="button"
            onClick={async () => {
              setTestEmailResult(null);
              setTestEmailFrom(null);
              setTestEmailLoading(true);
              const to = (form.contactEmail && form.contactEmail.trim()) || currentUserEmail || "";
              try {
                const res = await fetch("/api/test-welcome-email", {
                  method: "POST",
                  headers: adminHeaders,
                  body: JSON.stringify({
                    to: to.trim() || undefined,
                    contactName: form.contactName?.trim() || "Test Contact",
                    companyName: form.name?.trim() || "Test Company",
                    companyCode: form.companyCode?.trim() || "12345",
                    tempPassword: "TempPass123",
                    userEmail: currentUserEmail,
                  }),
                });
                const data = await res.json().catch(() => ({}));
                setTestEmailFrom(data.from || null);
                if (res.ok && data.sent) {
                  setTestEmailResult(`Welcome email sent to ${data.to}. Check inbox (and spam).`);
                } else {
                  setTestEmailResult(data.error || "Welcome email could not be sent. Check SMTP settings (SMTP_HOST, SMTP_USER, SMTP_PASS).");
                }
              } catch (_) {
                setTestEmailResult("Could not reach server.");
              } finally {
                setTestEmailLoading(false);
              }
            }}
            disabled={testEmailLoading || !currentUserEmail}
            className="px-6 py-2 border border-indigo-300 text-indigo-800 font-medium rounded-xl hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2"
            title={translateUi("Send the actual welcome email to the contact address (or your email) to test delivery and content")}
          >
            <Mail className="w-4 h-4" />
            {testEmailLoading ? translateUi("Sending...") : translateUi("Send test welcome email")}
          </button>
          <button
            type="button"
            onClick={async () => {
              setPreviewLoading(true);
              setPreviewOpen(false);
              try {
                const res = await fetch("/api/new-company/preview", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(form),
                });
                if (res.ok) {
                  const { subject, html } = await res.json();
                  setPreviewSubject(subject);
                  setPreviewHtml(html);
                  setPreviewOpen(true);
                }
              } catch (_) {
                setError("Could not load email preview.");
              } finally {
                setPreviewLoading(false);
              }
            }}
            disabled={previewLoading || !form.name || !form.contactName}
            className="px-6 py-2 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {previewLoading ? translateUi("Loading...") : translateUi("Preview email")}
          </button>
          <button
            type="submit"
            disabled={submitting || apiReachable === false || (typeof window !== "undefined" && window.location.hostname === "localhost" && window.location.port !== "" && window.location.port !== "3000")}
            className="px-8 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-60"
          >
            {submitting ? translateUi("Creating...") : translateUi("Create company & send invite")}
          </button>
        </div>
      </form>
        )}
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setPreviewOpen(false)}>
          <div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-900">Email preview</span>
              <button type="button" onClick={() => setPreviewOpen(false)} className="p-2 text-slate-500 hover:text-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Subject</p>
              <p className="text-sm font-medium text-slate-900 mt-1">{previewSubject}</p>
            </div>
            <div className="flex-1 overflow-auto p-4 min-h-[200px]">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden text-left">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} className="prose prose-sm max-w-none p-6" style={{ fontFamily: "system-ui, sans-serif" }} />
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-200 text-xs text-slate-500">
              Placeholders like [Will be assigned] will be replaced with real values when you create the company.
            </div>
          </div>
        </div>
      )}

      {viewModalCode !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={closeViewModal}>
          <div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-900">Company details & billing</span>
              <button type="button" onClick={closeViewModal} className="p-2 text-slate-500 hover:text-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {viewModalLoading ? (
                <div className="py-12 text-center text-slate-500">Loading...</div>
              ) : viewModalCompany ? (
                <>
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Company details</h4>
                    <dl className="grid grid-cols-1 gap-3 text-sm">
                      <div><dt className="text-slate-500">{translateUi("Company code")}</dt><dd className="font-mono font-medium text-slate-900 mt-0.5">{viewModalCompany.id}</dd></div>
                      <div><dt className="text-slate-500">Name</dt><dd className="mt-0.5">{viewModalCompany.name || "—"}</dd></div>
                      <div><dt className="text-slate-500"> Address </dt><dd className="mt-0.5 whitespace-pre-wrap">{viewModalCompany.address || "—"}</dd></div>
                      <div><dt className="text-slate-500">Contact</dt><dd className="mt-0.5">{viewModalCompany.contactName || "—"}</dd></div>
                      <div><dt className="text-slate-500"> Email </dt><dd className="mt-0.5">{viewModalCompany.contactEmail || "—"}</dd></div>
                      <div><dt className="text-slate-500"> Phone </dt><dd className="mt-0.5">{viewModalCompany.contactPhone || "—"}</dd></div>
                      <div><dt className="text-slate-500"> Status </dt><dd className="mt-0.5"><span className={viewModalCompany.status === "Active" ? "text-emerald-600" : viewModalCompany.status === "Suspended" ? "text-red-600" : "text-amber-600"}>{viewModalCompany.status}</span></dd></div>
                      {viewModalCompany.createdAt && <div><dt className="text-slate-500">Created</dt><dd className="mt-0.5">{formatDateUK(viewModalCompany.createdAt)}</dd></div>}
                    </dl>
                  </section>
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3"> Billing </h4>
                    <dl className="grid grid-cols-1 gap-3 text-sm">
                      <div><dt className="text-slate-500"> Plan </dt><dd className="mt-0.5">{viewModalCompany.billingPlan || "—"}</dd></div>
                      <div><dt className="text-slate-500">Next billing date</dt><dd className="mt-0.5">{viewModalCompany.billingNextDate || "—"}</dd></div>
                      <div><dt className="text-slate-500">Amount</dt><dd className="mt-0.5">{viewModalCompany.billingAmount || "—"}</dd></div>
                    </dl>
                  </section>
                  {isSystemAdmin && (
                    <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Included modules</h4>
                        <p className="text-sm text-slate-600">
                          Grant modules to this company as Activated (included). Do not use Free for this —
                          Free is only for temporary free offers. Unchecked modules keep the normal trial / purchase path.
                        </p>
                      </div>
                      {viewModalAddonsLoading ? (
                        <p className="text-sm text-slate-500">Loading modules…</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {onboardingAddonChoices.map((mod) => {
                            const checked = !!viewModalIncludedAddons[mod.id];
                            return (
                              <label
                                key={mod.id}
                                className={clsx(
                                  "flex items-start gap-3 rounded-xl border bg-white p-3 cursor-pointer transition-colors",
                                  checked ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300"
                                )}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                  checked={checked}
                                  onChange={(e) =>
                                    setViewModalIncludedAddons((prev) => ({
                                      ...prev,
                                      [mod.id]: e.target.checked,
                                    }))
                                  }
                                />
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-slate-900">{mod.name}</span>
                                  <span className="block text-xs text-slate-600 leading-relaxed">{mod.shortDescription}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => void saveViewModalIncludedAddons()}
                          disabled={viewModalAddonsSaving || viewModalAddonsLoading || !currentUserEmail}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {viewModalAddonsSaving ? translateUi("Saving…") : translateUi("Save included modules")}
                        </button>
                        {viewModalAddonsMessage && (
                          <p className="text-sm text-slate-700">{viewModalAddonsMessage}</p>
                        )}
                      </div>
                    </section>
                  )}
                  {isSystemAdmin && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void openWizardEditModal(viewModalCompany.id, viewModalCompany.setupWizardStep);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                        title={translateUi("Edit company onboarding step")}
                      >
                        Edit onboarding step
                      </button>
                      {!isSystemAdminCompanyCode(viewModalCompany.id) && (
                        <>
                          {viewModalCompany.status === "Suspended" ? (
                            <button type="button" onClick={() => handleReactivate(viewModalCompany.id)} disabled={actionLoading === viewModalCompany.id} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">Reinstate</button>
                          ) : (
                            <button type="button" onClick={() => handleSuspend(viewModalCompany.id)} disabled={actionLoading === viewModalCompany.id} className="px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 disabled:opacity-50">Suspend</button>
                          )}
                          <button type="button" onClick={() => confirmDeleteCompany(viewModalCompany.id)} disabled={actionLoading === viewModalCompany.id} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50">Delete company</button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void openTermsAgreementModal(viewModalCompany.id);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-800 rounded-xl text-sm font-medium hover:bg-slate-50"
                            title={translateUi("View stored terms agreement")}
                          >
                            <FileText className="w-4 h-4 shrink-0" />
                            View stored agreement
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="py-12 text-center text-slate-500">Company not found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {termsAgreementModalCode !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4"
          onClick={closeTermsAgreementModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 shrink-0">
              <span className="font-semibold text-slate-900 min-w-0">
                Stored terms agreement — <span className="font-mono">{termsAgreementModalCode}</span>
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {termsAgreementHtml && (
                  <button
                    type="button"
                    onClick={printStoredTermsAgreement}
                    title={translateUi("Opens your browser print dialog — choose Save as PDF if you prefer")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 shadow-sm"
                  >
                    <Printer className="w-4 h-4 shrink-0 text-indigo-600" aria-hidden />
                    {translateUi("Print / Save as PDF")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeTermsAgreementModal}
                  className="p-2 text-slate-500 hover:text-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden p-4">
              {termsAgreementLoading ? (
                <div className="py-16 text-center text-slate-500">{translateUi("Loading…")}</div>
              ) : termsAgreementError ? (
                <div className="py-8 px-4 text-center text-rose-600 text-sm">{termsAgreementError}</div>
              ) : termsAgreementHtml ? (
                <iframe
                  title={translateUi("Terms agreement")}
                  className="w-full h-[min(70vh,800px)] rounded-xl border border-slate-200 bg-white"
                  srcDoc={termsAgreementHtml}
                  sandbox="allow-same-origin"
                />
              ) : null}
            </div>
            {termsAgreementHtml && (
              <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end shrink-0">
                <button
                  type="button"
                  onClick={printStoredTermsAgreement}
                  title={translateUi("Opens your browser print dialog — choose Save as PDF if you prefer")}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 shadow-sm"
                >
                  <Printer className="w-4 h-4 shrink-0 text-indigo-600" aria-hidden />
                  {translateUi("Print / Save as PDF")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {wizardEditCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={closeWizardEditModal}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-[min(1600px,calc(100vw-2rem))] h-[85vh] min-h-0 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900">Edit onboarding steps</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Company: <span className="font-mono">{wizardEditCode}</span>
                </p>
              </div>
              <button type="button" onClick={closeWizardEditModal} className="p-2 text-slate-500 hover:text-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <label className="block text-sm font-medium text-slate-700 mb-2">Current onboarding step</label>
              <select
                value={wizardEditStep}
                onChange={(e) => persistWizardStep(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {validWizardSteps.map((step) => (
                  <option key={step} value={step}>{step}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-auto">
              {wizardEditCompany ? (
                <SetupWizard
                  key={`wizard-edit-${wizardEditCode}`}
                  company={({
                    id: wizardEditCompany.id,
                    name: wizardEditCompany.name || wizardEditCompany.id,
                    companyCode: wizardEditCompany.id,
                    status: (wizardEditCompany.status as any) || "PendingSetup",
                    createdAt: wizardEditCompany.createdAt || new Date().toISOString(),
                    createdBy: currentUserId || currentUserEmail || "system",
                    updatedAt: wizardEditCompany.createdAt || new Date().toISOString(),
                    updatedBy: currentUserId || currentUserEmail || "system",
                    setupCompletedAt: null,
                    setupCompletedBy: null,
                  } as any)}
                  currentUserId={String(currentUserId || currentUserEmail || "system")}
                  currentUserEmail={currentUserEmail || undefined}
                  onClose={closeWizardEditModal}
                  onActivated={() => {
                    closeWizardEditModal();
                    loadCompanies();
                    if (viewModalCompany && viewModalCompany.id === wizardEditCode) {
                      setViewModalCompany({ ...viewModalCompany, status: "Active" });
                    }
                  }}
                  activeStep={wizardEditStep as any}
                  onStepChange={(step) => {
                    void persistWizardStep(String(step));
                  }}
                  hideBackToDashboard={true}
                  lockCompanyDetails={false}
                  isMobileOrTablet={true}
                  azureOnly={true}
                  editorMode={true}
                />
              ) : (
                <div className="p-6 text-center text-slate-500">Loading step editor…</div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loadingList && platformCompany && (
        <div className="bg-white rounded-3xl border border-indigo-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-indigo-100 bg-indigo-50/70">
            <h3 className="text-lg font-bold text-slate-900">Rail Intel (system administrator)</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Platform company for the main system administrator account. It cannot be deleted, suspended, or have terms records viewed.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                  <th className="px-6 py-3">{translateUi("Company code")}</th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Contact</th>
                  <th className="px-6 py-3"> Email </th>
                  <th className="px-6 py-3"> Phone </th>
                  <th className="px-6 py-3"> Status </th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3 text-center"> Actions </th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono font-medium text-slate-900">{platformCompany.id}</td>
                  <td className="px-6 py-3 text-slate-800">{platformCompany.name}</td>
                  <td className="px-6 py-3 text-slate-600">{platformCompany.contactName || "—"}</td>
                  <td className="px-6 py-3 text-slate-600">{platformCompany.contactEmail || "—"}</td>
                  <td className="px-6 py-3 text-slate-600">{platformCompany.contactPhone || "—"}</td>
                  <td className="px-6 py-3">
                    <span className={platformCompany.status === "Active" ? "text-emerald-600 font-medium" : platformCompany.status === "Suspended" ? "text-red-600 font-medium" : "text-amber-600"}>
                      {platformCompany.status === "Active" ? "Activated" : platformCompany.status === "PendingSetup" ? "Pending setup" : platformCompany.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-500">
                    {platformCompany.createdAt ? formatDateUK(platformCompany.createdAt) : "—"}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {isSystemAdmin && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void openWizardEditModal(platformCompany.id, platformCompany.setupWizardStep);
                          }}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title={translateUi("Edit onboarding step")}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openViewModal(platformCompany.id)}
                        className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        title={translateUi("View company details & billing")}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="md:col-span-2 lg:col-span-2">
            <h3 className="text-lg font-bold text-slate-900">Created companies (audit)</h3>
            <p className="text-xs text-slate-500">Customer companies created from this page. Filter by status to see suspended companies.</p>
          </div>
          {!loadingList && customerCompanies.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all" as const, label: "All" },
                { value: "Active" as const, label: translateUi("Active") },
                { value: "Suspended" as const, label: `${translateUi("Suspended")} (${customerCompanies.filter((c) => c.status === "Suspended").length})` },
                { value: "PendingSetup" as const, label: "Pending setup" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCompanyStatusFilter(value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                    companyStatusFilter === value
                      ? "bg-indigo-600 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {loadingList ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading...</div>
        ) : customerCompanies.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No companies created yet.</div>
        ) : (() => {
          const filtered = companyStatusFilter === "all" ? customerCompanies : customerCompanies.filter((c) => c.status === companyStatusFilter);
          return filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No companies with status &quot;{companyStatusFilter === "PendingSetup" ? "Pending setup" : companyStatusFilter}&quot;.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                  <th className="px-6 py-3">{translateUi("Company code")}</th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Contact</th>
                  <th className="px-6 py-3"> Email </th>
                  <th className="px-6 py-3"> Phone </th>
                  <th className="px-6 py-3"> Status </th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3 text-center"> Actions </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className={cn("hover:bg-slate-50", c.status === "Suspended" && "bg-red-50/50")}>
                    <td className="px-6 py-3 font-mono font-medium text-slate-900">{c.id}</td>
                    <td className="px-6 py-3 text-slate-800">{c.name}</td>
                    <td className="px-6 py-3 text-slate-600">{c.contactName || "—"}</td>
                    <td className="px-6 py-3 text-slate-600">{c.contactEmail || "—"}</td>
                    <td className="px-6 py-3 text-slate-600">{c.contactPhone || "—"}</td>
                    <td className="px-6 py-3">
                      <span className={c.status === "Active" ? "text-emerald-600 font-medium" : c.status === "Suspended" ? "text-red-600 font-medium" : "text-amber-600"}>
                        {c.status === "Active" ? "Activated" : c.status === "PendingSetup" ? "Pending setup" : c.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-500">
                      {c.createdAt ? formatDateUK(c.createdAt) : "—"}
                    </td>
                    <td className="px-6 py-3 text-center">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          {isSystemAdmin && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void openWizardEditModal(c.id, c.setupWizardStep);
                              }}
                              className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title={translateUi("Edit onboarding step")}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openViewModal(c.id)}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title={translateUi("View company details & billing")}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isSystemAdmin && !isSystemAdminCompanyCode(c.id) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void openTermsAgreementModal(c.id);
                              }}
                              className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title={translateUi("View stored terms agreement")}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                          {!isSystemAdminCompanyCode(c.id) && (
                            <>
                              {c.status === "Suspended" ? (
                                <button
                                  type="button"
                                  onClick={() => handleReactivate(c.id)}
                                  disabled={actionLoading === c.id}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-50 text-sm font-medium"
                                  title={translateUi("Reinstate company")}
                                >
                                  <PlayCircle className="w-4 h-4 shrink-0" />
                                  Reinstate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleSuspend(c.id)}
                                  disabled={actionLoading === c.id}
                                  className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-50"
                                  title={translateUi("Suspend company (no records deleted)")}
                                >
                                  <PauseCircle className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => confirmDeleteCompany(c.id)}
                                disabled={actionLoading === c.id}
                                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                                title={translateUi("Delete company")}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>
    </div>
  );
};

const EmployeeForm = ({ 
  onClose, 
  onSave, 
  editingEmployee,
  companyCode,
  currentUserEmail,
  isMobileOrTablet = false,
  isDarkMode = false,
}: { 
  onClose: () => void, 
  onSave: (emp: Employee) => void,
  editingEmployee: Employee | null,
  companyCode?: string,
  currentUserEmail?: string,
  isMobileOrTablet?: boolean,
  isDarkMode?: boolean,
}) => {
  const [formData, setFormData] = useState({
    id: '',
    full_name: '',
    company_code: '',
    role: '',
    depot: '',
    manager: '',
    email: '',
    phone: '',
    phone_country_code: '+44',
    dob: '',
    place_of_birth: '',
    ni_number: '',
    gender_types: '[]',
    start_date: '',
    status: "Active",
    work_status: '',
    monitoring_status: 'no issues',
    address: '',
    postcode: '',
    emergency_contact_name: '',
    emergency_contact_country_code: '+44',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    signature_url: '' as string | undefined
  });
  const [error, setError] = useState<string | null>(null);
  const [companyJobRoles, setCompanyJobRoles] = useState<string[]>([]);
  const [roleIsOther, setRoleIsOther] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; name?: string }[]>([]);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [teamUsers, setTeamUsers] = useState<SystemUser[]>([]);
  const [teamUsersLoading, setTeamUsersLoading] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState<Array<{ id: string; full_name: string; work_status?: string }>>([]);
  const [companyDepots, setCompanyDepots] = useState<string[]>([]);
  const [submittingEmployee, setSubmittingEmployee] = useState(false);
  const [enableUserAccount, setEnableUserAccount] = useState(false);

  const effectiveCompanyForTeam =
    (companyCode || '').trim() ||
    (formData.company_code || '').trim() ||
    (editingEmployee?.company_code || '').trim() ||
    '';

  useEffect(() => {
    if (!effectiveCompanyForTeam) {
      setTeamUsers([]);
      setTeamUsersLoading(false);
      return;
    }
    setTeamUsersLoading(true);
    setTeamUsers([]);
    fetch(`/api/users?companyCode=${encodeURIComponent(effectiveCompanyForTeam)}`)
      .then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
      .then((data: SystemUser[]) => {
        setTeamUsers(data);
      })
      .catch(() => setTeamUsers([]))
      .finally(() => setTeamUsersLoading(false));
  }, [effectiveCompanyForTeam]);

  useEffect(() => {
    if (!effectiveCompanyForTeam) {
      setCompanyEmployees([]);
      return;
    }
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/employees?companyCode=${encodeURIComponent(effectiveCompanyForTeam)}`, { cache: 'no-store', headers })
      .then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
      .then((rows: Array<{ id?: string; full_name?: string; work_status?: string }>) => {
        setCompanyEmployees(
          rows
            .map((row) => ({
              id: String(row.id ?? '').trim(),
              full_name: String(row.full_name ?? '').trim(),
              work_status: row.work_status,
            }))
            .filter((row) => row.id && row.full_name),
        );
      })
      .catch(() => setCompanyEmployees([]));
  }, [effectiveCompanyForTeam, currentUserEmail]);

  useEffect(() => {
    if (companyCode) {
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`)
        .then(res => (res.ok ? res.json() : []))
        .then((roles: { name: string }[]) => {
          setCompanyJobRoles(roles.map(r => r.name));
        })
        .catch(() => {});
    } else {
      setCompanyJobRoles([]);
    }
  }, [companyCode]);

  useEffect(() => {
    if (!effectiveCompanyForTeam) {
      setCompanyDepots([]);
      return;
    }
    let cancelled = false;
    const normalize = (raw: unknown): string[] => {
      if (!Array.isArray(raw)) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const row of raw) {
        const value = String(row ?? "").trim();
        if (!value) continue;
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(value);
      }
      return out;
    };
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(effectiveCompanyForTeam)}/traction-routes`, {
      headers,
    })
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json().catch(() => null)) as { depots?: unknown } | null;
      })
      .then((data) => {
        if (cancelled) return;
        setCompanyDepots(normalize(data?.depots));
      })
      .catch(() => {
        if (!cancelled) setCompanyDepots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyForTeam, currentUserEmail]);

  useEffect(() => {
    if (!companyCode && !editingEmployee) {
      fetch('/api/companies')
        .then(res => (res.ok ? res.json() : []))
        .then((data: { id: string; name?: string }[]) => setCompanies(Array.isArray(data) ? data : []))
        .catch(() => setCompanies([]));
    } else {
      setCompanies([]);
    }
  }, [companyCode, editingEmployee]);

  useEffect(() => {
    if (editingEmployee) {
      setFormData({
        id: editingEmployee.id,
        full_name: editingEmployee.full_name,
        company_code: editingEmployee.company_code || '',
        role: editingEmployee.role,
        depot: editingEmployee.depot,
        manager: editingEmployee.manager || '',
        email: editingEmployee.email || '',
        phone: editingEmployee.phone || '',
        phone_country_code: editingEmployee.phone_country_code || '+44',
        dob: editingEmployee.dob || '',
        place_of_birth: editingEmployee.place_of_birth || '',
        ni_number: editingEmployee.ni_number || '',
        gender_types: editingEmployee.gender_types || '[]',
        start_date: editingEmployee.start_date || '',
        status: editingEmployee.status || "Active",
        work_status: editingEmployee.work_status || '',
        monitoring_status: editingEmployee.monitoring_status || 'no issues',
        address: editingEmployee.address || '',
        postcode: editingEmployee.postcode || '',
        emergency_contact_name: editingEmployee.emergency_contact_name || '',
        emergency_contact_country_code: editingEmployee.emergency_contact_country_code || '+44',
        emergency_contact_phone: editingEmployee.emergency_contact_phone || '',
        emergency_contact_relationship: editingEmployee.emergency_contact_relationship || '',
        signature_url: editingEmployee.signature_url || ''
      });
    } else {
      setFormData({
        id: '',
        full_name: '',
        company_code: companyCode || '',
        role: '',
        depot: '',
        manager: '',
        email: '',
        phone: '',
        phone_country_code: '+44',
        dob: '',
        place_of_birth: '',
        ni_number: '',
        gender_types: '[]',
        start_date: '',
        status: "Active",
        work_status: '',
        monitoring_status: 'no issues',
        address: '',
        postcode: '',
        emergency_contact_name: '',
        emergency_contact_country_code: '+44',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        signature_url: ''
      });
    }
    if (editingEmployee && companyJobRoles.length > 0) {
      setRoleIsOther(!companyJobRoles.includes(editingEmployee.role));
    } else {
      setRoleIsOther(false);
    }
    setEnableUserAccount(false);
  }, [editingEmployee, companyJobRoles, companyCode]);

  const assessorTeamUsers = useMemo(
    () => buildAssessorOptions(teamUsers, companyEmployees, formData.full_name),
    [teamUsers, companyEmployees, formData.full_name],
  );

  const managerNamesSorted = useMemo(() => {
    return assessorTeamUsers.map((u) => u.full_name);
  }, [assessorTeamUsers]);

  const assessingManagerSelfSelected = isSamePersonName(formData.manager, formData.full_name);

  const managerTrim = (formData.manager || '').trim();
  const managerCanonical =
    !managerTrim
      ? ''
      : managerNamesSorted.find((n) => n.toLowerCase() === managerTrim.toLowerCase()) ?? null;
  const managerSelectValue =
    !managerTrim ? '' : managerCanonical ?? (editingEmployee ? '__other__' : '');
  const depotTrim = (formData.depot || '').trim();
  const depotOptions = useMemo(() => {
    const sorted = [...companyDepots].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    if (!depotTrim) return sorted;
    const inList = sorted.some((row) => row.toLowerCase() === depotTrim.toLowerCase());
    if (inList) return sorted;
    return [depotTrim, ...sorted];
  }, [companyDepots, depotTrim]);
  const workStatusOptions = [...ADDITIONAL_ROLE_OPTIONS];
  const selectedWorkStatuses = useMemo(
    () => parseAdditionalRoles(formData.work_status),
    [formData.work_status]
  );
  const [additionalRolesOpen, setAdditionalRolesOpen] = useState(false);
  const additionalRolesDropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleWorkStatus = useCallback((role: string) => {
    setFormData((prev) => {
      const englishRole = canonicalizeAdditionalRole(role);
      const current = parseAdditionalRoles(prev.work_status);
      const isActive = current.some((r) => rolesMatch(r, englishRole));
      const next = isActive
        ? current.filter((r) => !rolesMatch(r, englishRole))
        : [...current.filter((r) => !rolesMatch(r, englishRole)), englishRole];
      return { ...prev, work_status: normalizeAdditionalRolesCsv(next.join(',')) };
    });
  }, []);

  useEffect(() => {
    if (!managerCanonical || managerCanonical === formData.manager) return;
    setFormData((f) => ({ ...f, manager: managerCanonical }));
  }, [managerCanonical, formData.manager]);

  useEffect(() => {
    if (editingEmployee || teamUsersLoading) return;
    const m = (formData.manager || '').trim();
    if (!m) return;
    const inList = managerNamesSorted.some((n) => n.toLowerCase() === m.toLowerCase());
    if (!inList) setFormData((f) => ({ ...f, manager: '' }));
  }, [editingEmployee, teamUsersLoading, managerNamesSorted, formData.manager]);

  useEffect(() => {
    if (!additionalRolesOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!additionalRolesDropdownRef.current?.contains(event.target as Node)) {
        setAdditionalRolesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [additionalRolesOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const effectiveCode = companyCode || editingEmployee?.company_code || '';
      const addCompanyCode = effectiveCode || formData.company_code;
      if (!editingEmployee && !addCompanyCode?.trim()) {
        setError('Company code is required when adding an employee.');
        return;
      }
      if (!editingEmployee && !String(formData.depot || '').trim()) {
        setError('Please select a depot from the company depot lookup list.');
        return;
      }
      if ((formData.manager || '').trim() && assessingManagerSelfSelected) {
        setError('An employee cannot be their own assessing manager.');
        return;
      }
      const managerName = (formData.manager || '').trim();
      if (managerName && !managerNamesSorted.some((n) => n.toLowerCase() === managerName.toLowerCase())) {
        setError('Assessing manager must be a team member with the Assessor additional role.');
        return;
      }
      const cc = effectiveCode ? `?companyCode=${encodeURIComponent(effectiveCode)}` : '';
      const url = editingEmployee ? `/api/employees/${editingEmployee.id}${cc}` : '/api/employees';
      const method = editingEmployee ? "PATCH" : "POST";
      const { company_code: _cc, ...restFormData } = formData;
      const payload = editingEmployee
        ? { ...restFormData, sync_linked_user: true }
        : { ...formData, company_code: addCompanyCode, enable_user_account: enableUserAccount, sync_linked_user: true };
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (effectiveCode) (headers as Record<string, string>)['X-Company-Code'] = effectiveCode;
      if (currentUserEmail) (headers as Record<string, string>)['X-User-Email'] = currentUserEmail;
      setSubmittingEmployee(true);

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save employee');

      if (editingEmployee && enableUserAccount) {
        const provisionRes = await fetch(`/api/employees/${encodeURIComponent(editingEmployee.id)}/provision-user`, {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        });
        const provisionData = await provisionRes.json().catch(() => ({}));
        if (!provisionRes.ok) {
          throw new Error(provisionData?.error || 'Employee updated, but failed to enable system user access.');
        }
      }

      try {
        const teamBc = new BroadcastChannel('rail-vault-team-users');
        teamBc.postMessage('refresh');
        teamBc.close();
      } catch {
        /* ignore */
      }
      
      onSave(data);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmittingEmployee(false);
    }
  };

  const efLabel = cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-400");
  const efInput = cn(
    "w-full px-4 py-2 rounded-xl border focus:ring-2 focus:ring-indigo-500 outline-none transition-all",
    isDarkMode
      ? "bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500"
      : "bg-slate-50 border-slate-200"
  );
  const efInputDisabled = cn(
    "w-full px-4 py-2 rounded-xl border cursor-not-allowed",
    isDarkMode ? "bg-slate-800/80 border-slate-600 text-slate-400" : "bg-slate-100 border-slate-200 text-slate-600"
  );
  const efInset = cn(
    "rounded-2xl border p-4 sm:p-5 space-y-3",
    isDarkMode ? "border-slate-600 bg-slate-800/70" : "border-slate-200 bg-slate-50/80"
  );
  const efBody = cn("text-xs leading-relaxed", isDarkMode ? "text-slate-400" : "text-slate-500");
  const efHeading = cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500");

  return (
    <>
    <div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={cn(
        "employee-form-shell bg-white rounded-3xl shadow-sm border border-slate-200 w-full overflow-hidden mb-6",
        isDarkMode && "bg-slate-900 border-slate-700"
      )}
    >
      <div className={cn("p-6 border-b flex items-center justify-between bg-indigo-600 text-white", isDarkMode ? "border-slate-700" : "border-slate-100")}>
        <h3 className="text-xl font-bold">{editingEmployee ? translateUi("Edit Employee") : translateUi("Add New Employee")}</h3>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className={cn("p-8 space-y-6", isDarkMode ? "text-slate-100" : "")}>
        {error && (
          <div className={cn("p-3 rounded-xl text-sm border", isDarkMode ? "bg-red-950/40 border-red-500/40 text-red-200" : "bg-red-50 border-red-100 text-red-600")}>
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className={efLabel}>{translateUi("Employee ID")}</label>
            <input 
              type="text" required
              disabled={!!editingEmployee}
              className={cn(efInput, "disabled:opacity-50")}
              placeholder="e.g. DRV123"
              value={formData.id}
              onChange={e => setFormData({ ...formData, id: e.target.value })}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-2">
            <label className={efLabel}>
              {translateUi("Company Code")}
              {(editingEmployee || companyCode) ? (
                <span className={cn("ml-2 font-normal whitespace-nowrap", isDarkMode ? "text-amber-300" : "text-amber-600")}> {translateUi("(locked — only changes on transfer)")} </span>
              ) : (
                <span className="ml-2 text-slate-400 font-normal">{translateUi("(required)")}</span>
              )}
            </label>
            {editingEmployee ? (
              <input
                type="text"
                disabled
                readOnly
                className={efInputDisabled}
                value={formData.company_code || '—'}
              />
            ) : companyCode ? (
              <input
                type="text"
                disabled
                readOnly
                className={efInputDisabled}
                value={formData.company_code || companyCode}
              />
            ) : companies.length > 0 ? (
              <select
                required
                className={efInput}
                value={formData.company_code}
                onChange={e => setFormData({ ...formData, company_code: e.target.value })}
              >
                <option value="">{translateUi("— Select company —")}</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.id}{c.name ? ` — ${c.name}` : ''}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                required
                placeholder="e.g. RV-001"
                className={efInput}
                value={formData.company_code}
                onChange={e => setFormData({ ...formData, company_code: e.target.value })}
              />
            )}
          </div>
          <div className="md:col-start-1 lg:col-start-1">
            <label className={efLabel}>{translateUi("Full Name")}</label>
            <input 
              type="text" required
              className={efInput}
              placeholder="e.g. John Doe"
              value={formData.full_name}
              onChange={e => setFormData({ ...formData, full_name: e.target.value })}
            />
          </div>
          <div className="md:col-start-2 lg:col-start-2">
            <label className={efLabel}>{translateUi("Role")}</label>
            {companyJobRoles.length > 0 ? (
              <>
                <select
                  value={roleIsOther ? '__other__' : formData.role}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '__other__') {
                      setRoleIsOther(true);
                      setFormData(f => ({ ...f, role: '' }));
                    } else {
                      setRoleIsOther(false);
                      setFormData(f => ({ ...f, role: v }));
                    }
                  }}
                  required
                  className={efInput}
                >
                  <option value="">{translateUi("Please select")}</option>
                  {companyJobRoles.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                  {editingEmployee && <option value="__other__">{translateUi("Other (enter custom)")}</option>}
                </select>
                {roleIsOther && (
                  <input
                    type="text"
                    required
                    placeholder="Enter role name"
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                    className={cn(efInput, "mt-2")}
                  />
                )}
              </>
            ) : (
              <input 
                type="text" required
                className={efInput}
                placeholder="e.g. Train Driver"
                value={formData.role}
                onChange={e => setFormData({ ...formData, role: e.target.value })}
              />
            )}
          </div>
          <div className="md:col-start-1 lg:col-start-3">
            <label className={efLabel}>{translateUi("Depot")}</label>
            <select
              required
              className={cn(efInput, "disabled:opacity-60")}
              value={formData.depot}
              disabled={!effectiveCompanyForTeam || depotOptions.length === 0}
              onChange={e => setFormData({ ...formData, depot: e.target.value })}
            >
              <option value="">
                {!effectiveCompanyForTeam
                  ? translateUi("Select company code first")
                  : depotOptions.length === 0
                    ? translateUi("No depots configured")
                    : translateUi("Please select")}
              </option>
              {depotOptions.map((depotOption) => (
                <option key={depotOption} value={depotOption}>
                  {depotOption}
                </option>
              ))}
            </select>
          </div>
          <div className={cn("md:col-span-2 lg:col-span-3", efInset)}>
            <div>
              <h4 className={efHeading}>{translateUi("Assessing manager")}</h4>
              <p className={cn(efBody, "mt-1.5")}>
                {translateUi("Choose an assessing manager from Team Management who has the Assessor additional role. Phone numbers are not stored on the employee record for the manager — use Team Management or the email below when you need to reach them.")}
              </p>
            </div>
            {!effectiveCompanyForTeam ? (
              <p className={cn("text-sm py-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Select a company code first to choose a manager.")}</p>
            ) : (
              <>
                <select
                  className={cn(efInput, "disabled:opacity-60")}
                  disabled={teamUsersLoading}
                  value={managerSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || v === '__none__') {
                      setFormData((f) => ({ ...f, manager: '' }));
                    } else if (v === '__other__') {
                      setFormData((f) => ({ ...f, manager: '' }));
                    } else {
                      setFormData((f) => ({ ...f, manager: v }));
                    }
                  }}
                >
                  <option value="">{teamUsersLoading ? translateUi("Loading team users…") : translateUi("— No assessing manager —")}</option>
                  {!teamUsersLoading &&
                    managerNamesSorted.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  {editingEmployee && <option value="__other__">{translateUi("Other (custom name)")}</option>}
                </select>
                {!teamUsersLoading && assessorTeamUsers.length === 0 && (
                  <p className={cn("mt-2 text-xs rounded-lg border px-3 py-2", isDarkMode ? "border-amber-500/40 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900")}>
                    {translateUi("No team members with the Assessor additional role are available. Add the Assessor role to a team member's employee profile first.")}
                  </p>
                )}
                {assessingManagerSelfSelected && (
                  <p className={cn("mt-2 text-xs rounded-lg border px-3 py-2", isDarkMode ? "border-amber-500/40 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900")}>
                    {translateUi("An employee cannot be their own assessing manager. Choose a different assessor.")}
                  </p>
                )}
                {editingEmployee && managerSelectValue === '__other__' && (
                  <input
                    type="text"
                    className={efInput}
                    placeholder="Manager name (not in team list)"
                    value={formData.manager}
                    onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  />
                )}
                {(() => {
                  const m = (formData.manager || '').trim();
                  if (!m) return null;
                  const u = teamUsers.find((tu) => (tu.full_name || '').trim().toLowerCase() === m.toLowerCase());
                  const em = u?.email?.trim();
                  if (!em) return null;
                  return (
                    <p className={cn("text-xs rounded-lg border px-3 py-2", isDarkMode ? "border-slate-600 bg-slate-900/60 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                      <span className={cn("font-semibold", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Manager work email")}</span>{' '}
                      <span className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-800")}>{em}</span>
                    </p>
                  );
                })()}
              </>
            )}
          </div>
          <div className="md:col-span-2 lg:col-span-2">
            <label className={efLabel}>{translateUi("Work email")}</label>
            <input 
              type="email"
              className={cn(efInput, "text-sm")}
              placeholder="Employee's work email (not the manager's)."
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div>
            <label className={efLabel}>{translateUi("Start Date")}</label>
            <input 
              type="date"
              className={efInput}
              value={formData.start_date}
              onChange={e => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div>
            <label className={efLabel}>{translateUi("Status")}</label>
            <select 
              className={efInput}
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="Active">{translateUi("Active")}</option>
              <option value="Inactive">{translateUi("Inactive")}</option>
              <option value="On Leave">{translateUi("On Leave")}</option>
              <option value="Suspended">{translateUi("Suspended")}</option>
            </select>
          </div>
          <div>
            <label className={efLabel}>{translateUi("Additional Roles")}</label>
            <div className="relative" ref={additionalRolesDropdownRef}>
              <button
                type="button"
                className={cn(efInput, "text-left flex items-center justify-between")}
                onClick={() => setAdditionalRolesOpen((prev) => !prev)}
                aria-expanded={additionalRolesOpen}
              >
                <span className={cn("truncate", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                  {selectedWorkStatuses.length > 0
                    ? selectedWorkStatuses.map((role) => formatAdditionalRoleLabel(role)).join(', ')
                    : translateUi("Select additional roles")}
                </span>
                <ChevronDown className={cn('w-4 h-4 transition-transform', isDarkMode ? "text-slate-400" : "text-slate-500", additionalRolesOpen && 'rotate-180')} />
              </button>
              {additionalRolesOpen && (
                <div className={cn("absolute z-30 mt-2 w-full border rounded-xl shadow-lg p-2 max-h-56 overflow-y-auto", isDarkMode ? "bg-slate-800 border-slate-600" : "bg-white border-slate-200")}>
                  {workStatusOptions.map((opt) => {
                    const checked = selectedWorkStatuses.some((role) => rolesMatch(role, opt));
                    return (
                      <label
                        key={opt}
                        className={cn("flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer text-sm", isDarkMode ? "hover:bg-slate-700 text-slate-200" : "hover:bg-slate-50 text-slate-700")}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleWorkStatus(opt)}
                          className={cn("h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500", isDarkMode ? "border-slate-500 bg-slate-700" : "border-slate-300")}
                        />
                        <span>{formatAdditionalRoleLabel(opt)}</span>
                      </label>
                    );
                  })}
                  <div className={cn("pt-2 mt-1 border-t", isDarkMode ? "border-slate-600" : "border-slate-100")}>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, work_status: '' }))}
                      className={cn("text-xs font-medium", isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")}
                    >
                      {translateUi("Clear additional roles")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className={efLabel}>{translateUi("Monitoring Status")}</label>
            <select 
              className={efInput}
              value={formData.monitoring_status}
              onChange={e => setFormData({ ...formData, monitoring_status: e.target.value })}
            >
              <option value="no issues">{translateUi("No Issues")}</option>
              <option value="Off Track">{translateUi("Off Track")}</option>
              <option value="Awaiting Med Check">{translateUi("Awaiting Med Check")}</option>
              <option value="OH follow-up due">{translateUi("OH follow-up due")}</option>
              <option value="On additional support">{translateUi("On additional support")}</option>
              <option value="Relieve end of journey unless SPAD">{translateUi("Relieve end of journey unless SPAD")}</option>
              <option value="On additional monitoring">{translateUi("On additional monitoring")}</option>
              <option value="Relieve upon incident">{translateUi("Relieve upon incident")}</option>
            </select>
          </div>
        </div>

        <EmployeeAddressAndEmergencyPanels
          values={formData}
          isEditing
          isDarkMode={isDarkMode}
          onChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
        />

        {/* Signature — draw on add; draw or upload image when editing an existing employee */}
        <div className={cn("pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
          <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-400")}>{translateUi("Signature")}</label>
          <p className={cn("text-xs mb-3", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Used on the complementary certificate and records.")}</p>
          {formData.signature_url ? (
            <div className="relative inline-block signature-force-light rounded-lg border border-slate-200 p-2">
              <img
                src={formData.signature_url}
                alt={translateUi("Signature")}
                className="max-h-24 w-auto object-contain"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, signature_url: '' })}
                className="absolute -top-2 -right-2 p-1.5 bg-red-100 border border-red-200 text-red-600 rounded-full shadow-sm hover:bg-red-200 transition-colors z-10"
                title={translateUi("Delete signature")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : null}
          {!formData.signature_url ? (
            <div
              className={
                editingEmployee
                  ? "grid grid-cols-1 min-[480px]:grid-cols-2 gap-3 w-full min-w-0"
                  : "w-full min-w-0"
              }
            >
              <button
                type="button"
                onClick={() => setSignatureModalOpen(true)}
                className={cn(
                  "signature-drop-zone w-full min-w-0 min-h-[120px] h-32 flex flex-col items-center justify-center px-2 border-2 border-dashed rounded-xl transition-colors",
                  isDarkMode ? "border-slate-600 bg-slate-800 hover:bg-slate-700" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                )}
              >
                <Edit2 className={cn("w-7 h-7 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                <span className={cn("text-sm font-medium text-center", isDarkMode ? "text-slate-200" : "text-slate-600")}>
                  {isMobileOrTablet ? translateUi("Tap to sign") : translateUi("Draw signature")}
                </span>
                {!isMobileOrTablet ? (
                  <span className={cn("text-xs mt-0.5 sm:mt-1 text-center", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Mouse or trackpad")}</span>
                ) : null}
              </button>
              {editingEmployee ? (
                <label
                  className={cn(
                    "signature-drop-zone flex flex-col items-center justify-center w-full min-w-0 min-h-[120px] h-32 px-2 border-2 border-dashed rounded-xl cursor-pointer transition-colors",
                    isDarkMode ? "border-slate-600 bg-slate-800 hover:bg-slate-700" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                  )}
                >
                  <Upload className={cn("w-6 h-6 sm:w-7 sm:h-7 mb-1.5 sm:mb-2 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                  <span className={cn("text-sm font-medium text-center", isDarkMode ? "text-slate-200" : "text-slate-600")}>{translateUi("Upload image")}</span>
                  <span className={cn("text-xs mt-0.5 sm:mt-1 text-center", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("PNG, JPG · 5MB max")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => setFormData({ ...formData, signature_url: reader.result as string });
                        reader.readAsDataURL(file);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className={cn("rounded-xl border px-4 py-3", isDarkMode ? "border-slate-600 bg-slate-800/60" : "border-slate-200 bg-slate-50")}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enableUserAccount}
              onChange={(e) => setEnableUserAccount(e.target.checked)}
              className={cn("mt-0.5 h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500", isDarkMode ? "border-slate-500 bg-slate-700" : "border-slate-300")}
            />
            <span className={cn("text-sm", isDarkMode ? "text-slate-200" : "text-slate-700")}>
              <span className={cn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Enable this employee as a user")}</span>
              <span className={cn("block mt-0.5 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                {translateUi("Adds this employee to users list and applies role-based permissions configured by the company administrator.")}
              </span>
              {editingEmployee ? (
                <span className={cn("block mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("Tick this when editing to provision or re-enable user login access for this employee.")}
                </span>
              ) : (
                <span className={cn("block mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("Leave unticked to keep this as an employee record only. The employee will not be able to sign in or access this record.")}
                </span>
              )}
            </span>
          </label>
        </div>

        <div className={cn("flex justify-end space-x-4 pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
          <button 
            type="button" 
            onClick={onClose}
            className={cn("px-6 py-2 font-medium rounded-xl transition-colors", isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100")}
          >{translateUi("Cancel")}</button>
          <button 
            type="submit" 
            disabled={submittingEmployee}
            className={cn(
              "inline-flex items-center gap-2 px-8 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed",
              isDarkMode ? "shadow-lg shadow-indigo-950/40" : "shadow-lg shadow-indigo-200"
            )}
          >
            {submittingEmployee && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            )}
            {submittingEmployee
              ? (editingEmployee ? translateUi("Updating…") : translateUi("Adding…"))
              : (editingEmployee ? translateUi("Update Employee") : translateUi("Add Employee"))}
          </button>
        </div>
      </form>
    </div>

    {signatureModalOpen && (
      <div
        className="fixed inset-0 z-[110] bg-slate-900/60 flex items-center justify-center p-4"
        onClick={() => setSignatureModalOpen(false)}
      >
        <div
          className={cn(
            "rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden",
            isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={cn("px-4 py-3 border-b shrink-0", isDarkMode ? "border-slate-700" : "border-slate-200")}>
            <h3 className={cn("font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Sign here")}</h3>
            <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Draw with your mouse, trackpad, or finger")}</p>
          </div>
          <div className="p-4 flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-[280px] sm:min-h-[320px] signature-force-light">
              <SignaturePad
                onSave={(dataUrl) => {
                  setFormData((prev) => ({ ...prev, signature_url: dataUrl }));
                  setSignatureModalOpen(false);
                }}
                isDarkMode={isDarkMode}
                className="w-full h-full min-h-[280px] sm:min-h-[320px] border-2 border-dashed border-slate-300 rounded-xl bg-white touch-none"
                saveButtonLabel={translateUi("Apply")}
              />
            </div>
          </div>
          <div className={cn("px-4 pb-4 pt-2 shrink-0 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
            <button
              type="button"
              onClick={() => setSignatureModalOpen(false)}
              className={cn(
                "w-full px-4 py-3 text-sm font-medium rounded-xl transition-colors",
                isDarkMode ? "text-slate-200 bg-slate-800 hover:bg-slate-700" : "text-slate-600 bg-slate-100 hover:bg-slate-200"
              )}
            > Cancel </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

const PlatformClock = ({ isSidebarOpen }: { isSidebarOpen: boolean }) => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  if (!isSidebarOpen) return null;

  const timeString = time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const chars = timeString.split('');

  return (
    <div className="bg-slate-900 p-3 rounded-xl border-2 border-slate-700 flex flex-col items-center justify-center mb-6 shadow-inner mx-4">
      <div className="flex space-x-1">
        {chars.map((char, i) => (
          char === ':' ? (
            <div key={i} className="text-white font-mono text-2xl font-bold flex items-center justify-center animate-pulse">
              :
            </div>
          ) : (
            <div key={i} className="relative bg-black text-white font-mono text-2xl font-bold w-6 h-8 flex items-center justify-center rounded overflow-hidden shadow-md border border-slate-800">
              <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-slate-800 z-10"></div>
              <span className="relative z-0">{char}</span>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

// Components
const StatCard = ({ title, value, icon: Icon, color, onClick, active }: { 
  title: string, value: string | number, icon: any, color: string, onClick?: () => void, active?: boolean 
}) => (
  <div 
    whileHover={{ y: -4 }}
    onClick={onClick}
    className={cn(
      "p-8 rounded-2xl bg-white border border-slate-200 shadow-sm cursor-pointer transition-all",
      active && "ring-2 ring-indigo-500 border-transparent",
      "hover:shadow-md flex flex-col justify-between h-full"
    )}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={cn("p-4 rounded-2xl", color)}>
        <Icon className="w-8 h-8 text-white" />
      </div>
      <div className="text-right">
        <h3 className="text-4xl font-bold text-slate-900 tracking-tight">{value}</h3>
      </div>
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{title}</p>
    </div>
  </div>
);

const StatusBadge = ({ expiryDate, status }: { expiryDate: string, status?: string }) => {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0 || status === 'Unfit') return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">{status === 'Unfit' ? translateUi("Unfit") : translateUi("Expired")}</span>;
  if (diffDays <= 30) return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">{translateUi("Due Soon")}</span>;
  
  const normalizedStatus = status?.toLowerCase();
  if (normalizedStatus === 'f1' || normalizedStatus === 'fit' || normalizedStatus === 'valid' || normalizedStatus === 'valid and fit') 
    return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 status-chip-positive">F1</span>;
  
  if (normalizedStatus === 'f2') 
    return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">F2</span>;

  return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 status-chip-positive"> Valid </span>;
};

type EmployeeDetailTab = "personal" | "address" | "emergency";

function EmployeeAddressAndEmergencyPanels({
  values,
  isEditing,
  isDarkMode = false,
  onChange,
}: {
  values: Partial<Employee>;
  isEditing: boolean;
  isDarkMode?: boolean;
  onChange: (updates: Partial<Employee>) => void;
}) {
  const [detailTab, setDetailTab] = useState<EmployeeDetailTab>("personal");
  const employeeCountryCode = PHONE_COUNTRY_OPTIONS.find((opt) => opt.dialCode === values.phone_country_code)?.dialCode ?? "+44";
  const employeeCountry = PHONE_COUNTRY_OPTIONS.find((opt) => opt.dialCode === employeeCountryCode) ?? PHONE_COUNTRY_OPTIONS[0];
  const emergencyCountryCode = PHONE_COUNTRY_OPTIONS.find((opt) => opt.dialCode === values.emergency_contact_country_code)?.dialCode ?? "+44";
  const emergencyCountry = PHONE_COUNTRY_OPTIONS.find((opt) => opt.dialCode === emergencyCountryCode) ?? PHONE_COUNTRY_OPTIONS[0];
  const selectedGenderType = parseGenderTypes(values.gender_types)[0] ?? "";

  const panelClass = cn(
    "rounded-2xl border shadow-sm overflow-hidden flex min-h-0 w-full flex-col min-w-0 lg:h-full lg:min-h-0",
    isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
  );
  const tabBarClass = cn(
    "flex flex-wrap gap-1 p-2 sm:p-3 border-b shrink-0",
    isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-100 bg-slate-50/70"
  );
  const sectionLabelClass = cn(
    "block text-xs font-semibold uppercase tracking-wider mb-2",
    isDarkMode ? "text-slate-300" : "text-slate-500"
  );
  const inputClass = cn(
    "w-full min-w-0 px-3 py-2 sm:px-3.5 sm:py-2.5 border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm",
    isDarkMode ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-slate-50 border-slate-200 text-slate-900"
  );
  const valueClass = cn("text-sm", isDarkMode ? "text-slate-100" : "text-slate-900");

  const tabs: { id: EmployeeDetailTab; label: string; icon: React.ReactNode }[] = [
    {
      id: "personal",
      label: translateUi("Personal details"),
      icon: <User className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-indigo-300" : "text-indigo-600")} />,
    },
    {
      id: "address",
      label: translateUi("Address details"),
      icon: <MapIcon className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-emerald-300" : "text-emerald-600")} />,
    },
    {
      id: "emergency",
      label: translateUi("Emergency contact"),
      icon: <Phone className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-violet-300" : "text-indigo-600")} />,
    },
  ];

  const tabBtnClass = (active: boolean) =>
    cn(
      "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold transition-colors border",
      active
        ? isDarkMode
          ? "border-indigo-500/60 bg-indigo-500/20 text-indigo-200"
          : "border-indigo-200 bg-indigo-50 text-indigo-900"
        : isDarkMode
          ? "border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          : "border-transparent text-slate-600 hover:bg-white hover:text-slate-900"
    );

  return (
    <div className={panelClass}>
      <div className={tabBarClass} role="tablist" aria-label={translateUi("Employee details sections")}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={detailTab === t.id}
            id={`employee-detail-tab-${t.id}`}
            aria-controls={`employee-detail-panel-${t.id}`}
            onClick={() => setDetailTab(t.id)}
            className={tabBtnClass(detailTab === t.id)}
          >
            {t.icon}
            <span className="leading-tight text-left">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-3 sm:p-4 lg:min-h-0">
        {detailTab === "personal" && (
          <div
            id="employee-detail-panel-personal"
            role="tabpanel"
            aria-labelledby="employee-detail-tab-personal"
            className="space-y-3"
          >
            <div className={cn("pb-3 border-b", isDarkMode ? "border-slate-700" : "border-slate-100")}>
              <label className={sectionLabelClass}>{translateUi("Contact phone (employee)")}</label>
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                  <select
                    value={employeeCountryCode}
                    onChange={(e) => onChange({ phone_country_code: e.target.value })}
                    className={inputClass}
                  >
                    {SORTED_PHONE_COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.dialCode} value={opt.dialCode}>
                        {opt.flag} {translateUi(opt.label)} ({opt.dialCode})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={values.phone || ""}
                    onChange={(e) => onChange({ phone: e.target.value })}
                    className={inputClass}
                    placeholder="7700 900000"
                  />
                </div>
              ) : (
                <div className={cn("flex flex-wrap items-center gap-2 mt-1", valueClass)}>
                  <span className="text-base">{employeeCountry.flag}</span>
                  <span className="font-medium">{employeeCountryCode}</span>
                  <span>{values.phone?.trim() ? values.phone : "—"}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={sectionLabelClass}>{translateUi("Date of birth")}</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={values.dob || ""}
                    onChange={(e) => onChange({ dob: e.target.value })}
                    className={inputClass}
                  />
                ) : (
                  <div className={valueClass}>{values.dob ? formatDateUK(values.dob) : "—"}</div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className={sectionLabelClass}>{translateUi("Place of birth")}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={values.place_of_birth || ""}
                      onChange={(e) => onChange({ place_of_birth: e.target.value })}
                      className={inputClass}
                      placeholder="Town / city / country"
                    />
                  ) : (
                    <div className={valueClass}>{values.place_of_birth || "—"}</div>
                  )}
                </div>
                <div>
                  <label className={sectionLabelClass}>{translateUi("NI No.")}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={values.ni_number || ""}
                      onChange={(e) => onChange({ ni_number: normalizeNiNumber(e.target.value) })}
                      className={cn(inputClass, "font-mono uppercase")}
                      placeholder="e.g. AB123456C"
                      maxLength={9}
                      autoComplete="off"
                    />
                  ) : (
                    <div className={cn(valueClass, "font-mono uppercase")}>{values.ni_number?.trim() ? values.ni_number : "—"}</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className={sectionLabelClass}>{translateUi("Gender type")}</label>
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GENDER_TYPE_OPTIONS.map((opt) => {
                    const checked = selectedGenderType === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors text-sm",
                          checked
                            ? (isDarkMode ? "border-indigo-500/70 bg-indigo-500/20" : "border-indigo-300 bg-indigo-50")
                            : (isDarkMode ? "border-slate-700 bg-slate-800 hover:bg-slate-700" : "border-slate-200 bg-slate-50 hover:bg-slate-100")
                        )}
                      >
                        <input
                          type="radio"
                          name="gender_type"
                          checked={checked}
                          onChange={() => onChange({ gender_types: JSON.stringify([opt.value]) })}
                          className="w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className={cn("text-sm font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi(opt.label)}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                selectedGenderType ? (
                  <span
                    className={cn(
                      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border",
                      GENDER_TYPE_OPTIONS.find((opt) => opt.value === selectedGenderType)?.badgeClassName ?? "bg-indigo-50 text-indigo-700 border-indigo-100"
                    )}
                  >
                    {(() => {
                      const opt = GENDER_TYPE_OPTIONS.find((item) => item.value === selectedGenderType);
                      const Icon = opt?.icon;
                      return Icon ? <Icon className="w-4 h-4" /> : null;
                    })()}
                    {translateUi(selectedGenderType)}
                  </span>
                ) : (
                  <div className={valueClass}>—</div>
                )
              )}
            </div>
          </div>
        )}

        {detailTab === "address" && (
          <div
            id="employee-detail-panel-address"
            role="tabpanel"
            aria-labelledby="employee-detail-tab-address"
            className="space-y-3"
          >
            {isEditing ? (
              <>
                <div>
                  <label className={sectionLabelClass}> Address </label>
                  <textarea
                    value={values.address || ""}
                    onChange={(e) => onChange({ address: e.target.value })}
                    className={cn(inputClass, "min-h-[110px] resize-y py-3")}
                    placeholder="House name / number, street, town or city"
                  />
                </div>
                <div>
                  <label className={sectionLabelClass}> Postcode </label>
                  <input
                    type="text"
                    value={values.postcode || ""}
                    onChange={(e) => onChange({ postcode: normalizeUkPostcode(e.target.value) })}
                    className={cn(inputClass, "font-mono uppercase max-w-md")}
                    placeholder="SW1A 1AA"
                  />
                </div>
              </>
            ) : (
              <div className={cn("space-y-3 text-sm", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                <div>
                  <div className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-300" : "text-slate-500")}> Address </div>
                  <div className="whitespace-pre-wrap">{values.address || "—"}</div>
                </div>
                <div>
                  <div className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-300" : "text-slate-500")}> Postcode </div>
                  <div className="font-mono">{values.postcode || "—"}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {detailTab === "emergency" && (
          <div
            id="employee-detail-panel-emergency"
            role="tabpanel"
            aria-labelledby="employee-detail-tab-emergency"
            className="space-y-3"
          >
            <div>
              <label className={sectionLabelClass}> Name </label>
              {isEditing ? (
                <input
                  type="text"
                  value={values.emergency_contact_name || ""}
                  onChange={(e) => onChange({ emergency_contact_name: e.target.value })}
                  className={inputClass}
                  placeholder="Emergency contact name"
                />
              ) : (
                <div className={valueClass}>{values.emergency_contact_name || "—"}</div>
              )}
            </div>

            <div>
              {isEditing ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end">
                  <div className="min-w-0">
                    <label className={sectionLabelClass}>{translateUi("Country code")}</label>
                    <select
                      value={emergencyCountryCode}
                      onChange={(e) => onChange({ emergency_contact_country_code: e.target.value })}
                      className={cn(inputClass, "mt-1 w-full")}
                    >
                      {SORTED_PHONE_COUNTRY_OPTIONS.map((opt) => (
                        <option key={opt.dialCode} value={opt.dialCode}>
                          {opt.flag} {translateUi(opt.label)} ({opt.dialCode})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className={sectionLabelClass}>{translateUi("Phone number")}</label>
                    <input
                      type="tel"
                      value={values.emergency_contact_phone || ""}
                      onChange={(e) => onChange({ emergency_contact_phone: e.target.value })}
                      className={cn(inputClass, "mt-1 w-full")}
                      placeholder="7700 900000"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={sectionLabelClass}>{translateUi("Relationship to employee")}</label>
                    <select
                      value={values.emergency_contact_relationship || ""}
                      onChange={(e) => onChange({ emergency_contact_relationship: e.target.value })}
                      className={cn(inputClass, "mt-1 w-full")}
                    >
                      <option value="">{translateUi("Select relationship")}</option>
                      {EMERGENCY_RELATIONSHIP_OPTIONS.map((relationship) => (
                        <option key={relationship} value={relationship}>{translateUi(relationship)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div
                  className={cn(
                    "flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm",
                    isDarkMode ? "text-slate-100" : "text-slate-900"
                  )}
                >
                  <div>
                    <div className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                      Contact number
                    </div>
                    <div className="flex flex-wrap items-center gap-2 font-medium">
                      <span className="text-base">{emergencyCountry.flag}</span>
                      <span>{emergencyCountryCode}</span>
                      <span>{values.emergency_contact_phone || "—"}</span>
                    </div>
                  </div>
                  <div>
                    <div className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                      {translateUi("Relationship to employee")}
                    </div>
                    <div className={valueClass}>{values.emergency_contact_relationship || "—"}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const DashboardDetailsPage = ({ title, type, onBack, onSelectEmployee, companyCode, manager }: { title: string, type: string, onBack: () => void, onSelectEmployee: (id: string, companyCode?: string) => void, companyCode?: string; manager?: string }) => {
  const [details, setDetails] = useState<any[]>([]);
  // Fail-open on hard refresh: render shell immediately, then let auth listener
  // settle user/session state without trapping the UI behind a perpetual spinner.
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>("All");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ type });
    if (companyCode) params.set('companyCode', companyCode);
    if (manager) params.set('manager', manager);
    fetch(`/api/dashboard-details?${params}`)
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setDetails(arr);
        const members = Array.from(new Set(arr.map((item: any) => item.manager).filter(Boolean))) as string[];
        setTeamMembers(members);
        setLoading(false);
      });
  }, [type, companyCode, manager]);

  const filteredDetails = selectedTeamMember === 'All' 
    ? details 
    : details.filter(item => item.manager === selectedTeamMember);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
            <p className="text-slate-500 text-sm">{translateUi("Detailed view of records")}</p>
          </div>
        </div>
        <div>
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium text-slate-700"
            value={selectedTeamMember}
            onChange={(e) => setSelectedTeamMember(e.target.value)}
          >
            <option value="All">
              {type === 'overdueComp' || type === 'dueSoonComp'
                ? translateUi("All Assessing Managers")
                : translateUi("All Owning Managers")}
            </option>
            {teamMembers.map(member => (
              <option key={member} value={member}>{member}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : filteredDetails.length === 0 ? (
            <div className="text-center py-12 text-slate-500">{translateUi("No records found.")}</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 font-semibold"> Employee </th>
                  <th className="px-6 py-4 font-semibold"> Role </th>
                  <th className="px-6 py-4 font-semibold"> Detail </th>
                  <th className="px-6 py-4 font-semibold">{translateUi("Expiry Date")}</th>
                  <th className="px-6 py-4 font-semibold">{translateUi("Assessing Manager")}</th>
                  <th className="px-6 py-4 font-semibold text-right"> Action </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDetails.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{item.full_name}</p>
                          <p className="text-xs text-slate-500">{item.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.role}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <div>{item.detail}</div>
                      {item.block_reasons ? (
                        <p className="mt-1 text-xs text-rose-700 max-w-md">{item.block_reasons}</p>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDateUK(item.expiry_date)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.manager || "Unassigned"}</td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => onSelectEmployee(item.id, (item as { company_code?: string }).company_code)}
                        className="p-2 hover:bg-indigo-50 text-indigo-600 rounded-lg transition-colors"
                      >
                        <Search className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const railIntelOperatorNameHint = (name: string) => {
  const n = String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return n.includes('rail intel') || n.includes('railintel') || n.includes('railintel.co.uk');
};

/** Same rules as App `cmsProviderCompanyCode` — CMS / Rail Intel operator tenant id from company rows. */
const detectCmsProviderCompanyCodeFromCompanyList = (companies: readonly { id: string; name: string }[]): string => {
  const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');
  for (const company of companies) {
    const id = normalize(String(company.id || ''));
    const name = normalize(String(company.name || ''));
    const combined = `${name} ${id}`;
    const isPlatform =
      name === 'rail intel' ||
      name === 'railintel' ||
      name === 'railintel.co.uk' ||
      id === 'rail intel' ||
      id === 'railintel' ||
      id === 'railintel.co.uk' ||
      combined.includes('rail intel') ||
      combined.includes('railintel');
    if (isPlatform) return String(company.id || '').trim();
  }
  return '';
};

const TeamManagement = ({
  currentUserTitle,
  currentUserEmail,
  companyCode,
  isSystemAdmin,
  isCompanyActive,
  isNominatedCompanyAdmin,
  onLoginAsUser,
  systemAdminHomeCompanyName,
  platformOperatorCompanyCode,
  onLinkedEmployeeChanged,
}: {
  currentUserTitle?: string;
  currentUserEmail?: string;
  companyCode?: string;
  isSystemAdmin?: boolean;
  isCompanyActive?: boolean;
  isNominatedCompanyAdmin?: boolean;
  onLoginAsUser?: (user: SystemUser) => void;
  systemAdminHomeCompanyName?: string;
  /** CMS / Rail Intel operator tenant (same as employee-directory fallback). Used to attach company-less system admins to the org pane. */
  platformOperatorCompanyCode?: string;
  /** Called after a team-user save that may have updated/created a linked employee (role, name, additional roles). */
  onLinkedEmployeeChanged?: (info: {
    employeeId?: string | null;
    email?: string;
    fullName?: string;
    roleTitle?: string;
    workStatus?: string;
    employeeCreated?: boolean;
  }) => void;
}) => {
  const isAdmin = currentUserTitle?.toLowerCase()?.includes('admin') || currentUserEmail?.toLowerCase() === 'admin@railvault.com' || currentUserEmail?.toLowerCase() === 'andyuk38@gmail.com';
  // System admins can manage users across all companies; company admins require a company context.
  const canManageTeamUsers = isAdmin && (isSystemAdmin || !!companyCode);
  const normalizeCompanyAdminTitle = useCallback((title?: string | null) => {
    const value = String(title ?? '').trim();
    return value.toLowerCase() === 'administrator' ? 'Company Administrator' : value;
  }, []);
  const normalizeUserRow = useCallback(
    (u: SystemUser): SystemUser => ({
      ...u,
      title: normalizeCompanyAdminTitle(u.title) || u.title,
    }),
    [normalizeCompanyAdminTitle]
  );
  const displayTeamUserTitle = useCallback(
    (u: SystemUser) => {
      const r = u as SystemUser & { nominated_company_admin?: boolean };
      if (r.nominated_company_admin === true) return "Company Administrator";
      return normalizeCompanyAdminTitle(u.title) || u.title || "-";
    },
    [normalizeCompanyAdminTitle]
  );
  const titleLooksLikeCompanyAdministrator = useCallback((title?: string | null) => {
    const normalized = normalizeCompanyAdminTitle(title) || String(title ?? '').trim();
    const raw = normalized.toLowerCase();
    if (!raw) return false;
    if (raw.includes('system') && (raw.includes('administrator') || raw.includes('admin'))) return false;
    return raw.includes('administrator') || raw.includes('admin');
  }, [normalizeCompanyAdminTitle]);
  const countCompanyAdministratorsInCompany = useCallback(
    (cc: string | null | undefined, list: SystemUser[]) => {
      const norm = String(cc ?? '').trim().toLowerCase();
      if (!norm) return 0;
      return list.filter(
        (u) =>
          String(u.company_code ?? '').trim().toLowerCase() === norm && titleLooksLikeCompanyAdministrator(u.title)
      ).length;
    },
    [titleLooksLikeCompanyAdministrator]
  );
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<{ email: string; company_code: string | null; full_name: string | null; last_seen: string }[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [setPasswordUser, setSetPasswordUser] = useState<SystemUser | null>(null);
  const [setPasswordValue, setSetPasswordValue] = useState('');
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  const [resetMfaUser, setResetMfaUser] = useState<SystemUser | null>(null);
  const [resetMfaLoading, setResetMfaLoading] = useState(false);
  const [resetMfaError, setResetMfaError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    employee_id: '',
    role: "Read Only",
    title: "Other",
    company_code: companyCode || '',
    work_status: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [adminSeatSummary, setAdminSeatSummary] = useState<{
    maxSeats: number;
    currentAdmins: number;
    canAddAdmin: boolean;
  } | null>(null);
  const [nomineeForm, setNomineeForm] = useState({ adminEmail: "", adminFullName: "", adminJobTitle: "" });
  const [nomineeMeta, setNomineeMeta] = useState<{
    deferred: boolean;
    hasNomination: boolean;
    welcomeEmailSucceeded: boolean;
    adminEmail: string | null;
  } | null>(null);
  const [nomineeLoading, setNomineeLoading] = useState(false);
  const [nomineeSaving, setNomineeSaving] = useState(false);
  const [nomineeMessage, setNomineeMessage] = useState<string | null>(null);
  const [nomineeError, setNomineeError] = useState<string | null>(null);
  const [teamAdditionalRolesOpen, setTeamAdditionalRolesOpen] = useState(false);
  const teamAdditionalRolesDropdownRef = useRef<HTMLDivElement | null>(null);
  const [companyJobRoles, setCompanyJobRoles] = useState<Array<{ name: string; requiresCompetency: boolean }>>([]);
  const [companyDepots, setCompanyDepots] = useState<string[]>([]);
  const [createEmployeeRecord, setCreateEmployeeRecord] = useState(false);
  const [employeeDepot, setEmployeeDepot] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const selectedTeamAdditionalRoles = useMemo(
    () => parseAdditionalRoles(formData.work_status),
    [formData.work_status]
  );
  const toggleTeamAdditionalRole = useCallback((role: string) => {
    setFormData((prev) => {
      const englishRole = canonicalizeAdditionalRole(role);
      const current = parseAdditionalRoles(prev.work_status);
      const isActive = current.some((r) => rolesMatch(r, englishRole));
      const next = isActive
        ? current.filter((r) => !rolesMatch(r, englishRole))
        : [...current.filter((r) => !rolesMatch(r, englishRole)), englishRole];
      return { ...prev, work_status: normalizeAdditionalRolesCsv(next.join(',')) };
    });
  }, []);
  const effectiveTeamCompanyCode = String(formData.company_code || companyCode || '').trim();
  const selectedTitleRequiresCompetency = useMemo(() => {
    const title = String(formData.title ?? '').trim().toLowerCase();
    if (!title) return false;
    return companyJobRoles.some((r) => r.requiresCompetency && r.name.trim().toLowerCase() === title);
  }, [companyJobRoles, formData.title]);
  /** Assessor etc. live on the employee record — offer create/link even when role is not competency-tracked. */
  const needsEmployeeForAdditionalRoles = selectedTeamAdditionalRoles.length > 0;
  const showCreateEmployeeOption = !editingUser && (selectedTitleRequiresCompetency || needsEmployeeForAdditionalRoles);
  const teamTitleSelectOptions = useMemo(() => {
    const base = teamRoleTitleOptions(editingUser?.title);
    const seen = new Set(base.map((t) => t.toLowerCase()));
    const merged = [...base];
    for (const role of companyJobRoles) {
      const name = String(role.name ?? '').trim();
      if (!name || seen.has(name.toLowerCase())) continue;
      merged.push(name);
      seen.add(name.toLowerCase());
    }
    return merged;
  }, [editingUser?.title, companyJobRoles]);
  const depotSelectOptions = useMemo(() => {
    const sorted = [...companyDepots].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const current = employeeDepot.trim();
    if (!current) return sorted;
    if (sorted.some((d) => d.toLowerCase() === current.toLowerCase())) return sorted;
    return [current, ...sorted];
  }, [companyDepots, employeeDepot]);

  useEffect(() => {
    if (!teamAdditionalRolesOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!teamAdditionalRolesDropdownRef.current?.contains(event.target as Node)) {
        setTeamAdditionalRolesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [teamAdditionalRolesOpen]);

  useEffect(() => {
    if (!effectiveTeamCompanyCode) {
      setCompanyJobRoles([]);
      setCompanyDepots([]);
      return;
    }
    let cancelled = false;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(effectiveTeamCompanyCode)}/job-roles`, { headers, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : []))
      .then((rows: unknown) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setCompanyJobRoles(
          list
            .map((r) => ({
              name: String((r as { name?: unknown }).name ?? '').trim(),
              requiresCompetency: !!(r as { requiresCompetency?: unknown }).requiresCompetency,
            }))
            .filter((r) => r.name)
        );
      })
      .catch(() => {
        if (!cancelled) setCompanyJobRoles([]);
      });
    const normalizeDepots = (raw: unknown): string[] => {
      if (!Array.isArray(raw)) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const row of raw) {
        const value = String(row ?? '').trim();
        if (!value) continue;
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(value);
      }
      return out;
    };
    fetch(`/api/companies/${encodeURIComponent(effectiveTeamCompanyCode)}/traction-routes`, { headers, cache: 'no-store' })
      .then(async (res) => (res.ok ? ((await res.json().catch(() => null)) as { depots?: unknown } | null) : null))
      .then((data) => {
        if (cancelled) return;
        setCompanyDepots(normalizeDepots(data?.depots));
      })
      .catch(() => {
        if (!cancelled) setCompanyDepots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveTeamCompanyCode, currentUserEmail]);

  useEffect(() => {
    if (!showCreateEmployeeOption && createEmployeeRecord) {
      setCreateEmployeeRecord(false);
      setEmployeeDepot('');
    }
  }, [showCreateEmployeeOption, createEmployeeRecord]);

  const loadNomineeStatus = useCallback(async () => {
    const cc = String(companyCode || "").trim();
    if (!cc || !currentUserEmail || !canManageTeamUsers || !isCompanyActive) {
      setNomineeMeta(null);
      return;
    }
    setNomineeLoading(true);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(cc)}/nominate-company-admin`, {
        headers: { "X-User-Email": currentUserEmail },
        cache: "no-store",
      });
      const data = (await res.json().catch(() => ({}))) as {
        deferred?: boolean;
        hasNomination?: boolean;
        welcomeEmailSucceeded?: boolean;
        adminEmail?: string | null;
        adminFullName?: string | null;
        adminJobTitle?: string | null;
        error?: string;
      };
      if (!res.ok) {
        setNomineeMeta(null);
        return;
      }
      setNomineeMeta({
        deferred: data.deferred === true,
        hasNomination: data.hasNomination === true,
        welcomeEmailSucceeded: data.welcomeEmailSucceeded === true,
        adminEmail: typeof data.adminEmail === "string" ? data.adminEmail : null,
      });
      if (data.hasNomination) {
        setNomineeForm({
          adminEmail: String(data.adminEmail || ""),
          adminFullName: String(data.adminFullName || ""),
          adminJobTitle: String(data.adminJobTitle || ""),
        });
      }
    } catch {
      setNomineeMeta(null);
    } finally {
      setNomineeLoading(false);
    }
  }, [companyCode, currentUserEmail, canManageTeamUsers, isCompanyActive]);

  useEffect(() => {
    void loadNomineeStatus();
  }, [loadNomineeStatus]);

  const showNominateAdminPanel =
    !!isCompanyActive &&
    !!companyCode &&
    canManageTeamUsers &&
    !!nomineeMeta &&
    (nomineeMeta.deferred || !nomineeMeta.hasNomination || !nomineeMeta.welcomeEmailSucceeded);

  useEffect(() => {
    const cc = String(companyCode || formData.company_code || '').trim();
    if (!cc || !currentUserEmail || !canManageTeamUsers) {
      setAdminSeatSummary(null);
      return;
    }
    fetch(`/api/company-admin-licences/summary?companyCode=${encodeURIComponent(cc)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': cc },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setAdminSeatSummary(null);
          return;
        }
        setAdminSeatSummary({
          maxSeats: Number(data.maxSeats ?? 2),
          currentAdmins: Number(data.currentAdmins ?? 0),
          canAddAdmin: !!data.canAddAdmin,
        });
      })
      .catch(() => setAdminSeatSummary(null));
  }, [companyCode, formData.company_code, currentUserEmail, canManageTeamUsers, users.length]);

  const adminSeatLimitReachedForSelection = useMemo(() => {
    if (!adminSeatSummary || !titleLooksLikeCompanyAdministrator(formData.title)) return false;
    if (editingUser && titleLooksLikeCompanyAdministrator(editingUser.title)) return false;
    return !adminSeatSummary.canAddAdmin;
  }, [adminSeatSummary, formData.title, editingUser, titleLooksLikeCompanyAdministrator]);

  const isAddTeamUserFormComplete = useMemo(() => {
    const fullName = formData.full_name.trim();
    const email = formData.email.trim();
    const cc = String(formData.company_code || companyCode || '').trim();
    if (!fullName || !email || !cc) return false;
    if (!formData.role.trim()) return false;
    if (!String(formData.title ?? '').trim()) return false;
    if (createEmployeeRecord) {
      if (!showCreateEmployeeOption) return false;
      if (!formData.employee_id.trim()) return false;
      if (!employeeDepot.trim()) return false;
    }
    return true;
  }, [formData, companyCode, createEmployeeRecord, showCreateEmployeeOption, employeeDepot]);

  const isTeamUserSubmitDisabled = useMemo(() => {
    if (adminSeatLimitReachedForSelection) return true;
    if (editingUser) {
      return !formData.full_name.trim() || !formData.email.trim();
    }
    return !isAddTeamUserFormComplete;
  }, [adminSeatLimitReachedForSelection, editingUser, formData.full_name, formData.email, isAddTeamUserFormComplete]);

  useEffect(() => {
    if (editingUser) {
      const storedTitle = normalizeCompanyAdminTitle(editingUser.title) || editingUser.title || "Other";
      setFormData({
        full_name: editingUser.full_name,
        email: editingUser.email,
        employee_id: editingUser.employee_id != null && String(editingUser.employee_id).trim() ? String(editingUser.employee_id).trim() : '',
        role: editingUser.role,
        title: storedTitle,
        company_code: editingUser.company_code || companyCode || '',
        work_status: editingUser.work_status || '',
      });
      setIsModalOpen(true);
      setTeamAdditionalRolesOpen(false);
      setCreateEmployeeRecord(false);
      setEmployeeDepot('');
    } else {
      setFormData({
        full_name: '',
        email: '',
        employee_id: '',
        role: "Read Only",
        title: "Other",
        company_code: companyCode || '',
        work_status: '',
      });
      setTeamAdditionalRolesOpen(false);
      setCreateEmployeeRecord(false);
      setEmployeeDepot('');
    }
  }, [editingUser, companyCode, normalizeCompanyAdminTitle]);

  useEffect(() => {
    if (!editingUser || !isModalOpen) return;
    const cc = String(editingUser.company_code || companyCode || '').trim();
    if (!cc) return;
    if (editingUser.employee_id != null && String(editingUser.employee_id).trim()) return;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    let cancelled = false;
    fetch(`/api/employees?companyCode=${encodeURIComponent(cc)}`, { headers, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : []))
      .then((list: unknown) => {
        if (cancelled || !Array.isArray(list)) return;
        const em = String(editingUser.email ?? '').trim().toLowerCase();
        const match = list.find(
          (e) => String((e as { email?: string }).email ?? '').trim().toLowerCase() === em
        ) as { id?: string } | undefined;
        if (!match?.id) return;
        setFormData((prev) =>
          prev.employee_id.trim() ? prev : { ...prev, employee_id: String(match.id).trim() }
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editingUser, isModalOpen, companyCode, currentUserEmail]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
    const opts: RequestInit = { cache: 'no-store' };
    if (!companyCode && currentUserEmail) opts.headers = { 'X-User-Email': currentUserEmail };
    fetch(`/api/users${params}`, opts)
      .then(async res => {
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
      .then(data => {
        const normalized = (Array.isArray(data) ? data : []).map((u) => normalizeUserRow(u));
        setUsers(normalized);
        setEditingUser((prev) => {
          if (!prev) return null;
          const fresh = normalized.find((u) => u.id === prev.id);
          return fresh ?? prev;
        });
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching users:", err);
        setUsers([]);
        setLoading(false);
      });
  }, [companyCode, currentUserEmail, normalizeUserRow]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  useEffect(() => {
    try {
      const bc = new BroadcastChannel('rail-vault-team-users');
      bc.onmessage = () => fetchUsers();
      return () => bc.close();
    } catch {
      return undefined;
    }
  }, [fetchUsers]);
  useEffect(() => {
    if (!isSystemAdmin) return;
    fetch('/api/companies')
      .then((r) => r.json())
      .then((data: unknown) => setCompanies(Array.isArray(data) ? data : []));
  }, [isSystemAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!canManageTeamUsers) {
      setError('Only company administrators can edit team users and permissions from Team Management.');
      return;
    }
    if (isTeamUserSubmitDisabled) {
      if (adminSeatLimitReachedForSelection) {
        setError('You have reached your company administrator limit. Purchase additional Company Admin Licences from Administration → Add-ons.');
      }
      return;
    }
    try {
      if (editingUser) {
        // Update existing user - company_code is locked, never sent
        const { company_code: _cc, ...patchData } = formData;
        const res = await fetch(`/api/users/${editingUser.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patchData)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to update user');
        
        const normalizedData = normalizeUserRow(data);
        setUsers((Array.isArray(users) ? users : []).map(u => u.id === editingUser.id ? normalizedData : u));
        if (isSystemAdmin) setAllTeamMembers(prev => (Array.isArray(prev) ? prev : []).map(u => u.id === editingUser.id ? normalizedData : u));
        const linkedEmployeeId =
          String((data as { employee_id?: unknown }).employee_id ?? formData.employee_id ?? editingUser.employee_id ?? '').trim() || null;
        onLinkedEmployeeChanged?.({
          employeeId: linkedEmployeeId,
          email: String(formData.email || editingUser.email || '').trim(),
          fullName: String(formData.full_name || '').trim(),
          roleTitle: String(formData.title || '').trim(),
          workStatus: String(formData.work_status ?? '').trim(),
        });
        setSuccess(`User ${formData.full_name} updated successfully.`);
        setIsModalOpen(false);
        setEditingUser(null);
        return;
      }

      // Create user in Azure SQL (password set via admin Set Password or invite email)
      // 1. Add to local database - company_code comes from current company (locked for company users)
      const payload = {
        ...formData,
        company_code: formData.company_code || companyCode || '',
        create_employee: createEmployeeRecord && showCreateEmployeeOption,
        ...(createEmployeeRecord && showCreateEmployeeOption ? { depot: employeeDepot.trim() } : {}),
      };
      if (!payload.company_code) {
        setError('Company code is required when adding a user.');
        return;
      }
      if (payload.create_employee) {
        if (!formData.employee_id.trim()) {
          setError('Employee ID is required when adding this user to the employee list.');
          return;
        }
        if (!employeeDepot.trim()) {
          setError('Depot is required when adding this user to the employee list.');
          return;
        }
      }
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned an unexpected response format");
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add user to database');
      
      setUsers([normalizeUserRow(data), ...(Array.isArray(users) ? users : [])]);
      const employeeCreated = !!(data as { employee_created?: boolean }).employee_created;
      const inviteSent = !!(data as { invite_email_sent?: boolean }).invite_email_sent;
      const inviteError = String((data as { invite_email_error?: string }).invite_email_error ?? '').trim();
      const linkedEmployeeId =
        String((data as { employee_id?: unknown }).employee_id ?? formData.employee_id ?? '').trim() || null;
      if (employeeCreated || linkedEmployeeId || String(formData.work_status ?? '').trim()) {
        onLinkedEmployeeChanged?.({
          employeeId: linkedEmployeeId,
          email: String(formData.email || '').trim(),
          fullName: String(formData.full_name || '').trim(),
          roleTitle: String(formData.title || '').trim(),
          workStatus: String(formData.work_status ?? '').trim(),
          employeeCreated,
        });
      }
      const baseMsg = employeeCreated
        ? `User ${formData.full_name} added and created on the employee list.`
        : `User ${formData.full_name} added successfully.`;
      setSuccess(
        inviteSent
          ? `${baseMsg} An invite email with a temporary password has been sent.`
          : `${baseMsg} The invite email could not be sent${inviteError ? ` (${inviteError})` : ''}. Use Set Password, or ask them to use Forgot password.`
      );
      setIsModalOpen(false);
      setFormData({
        full_name: '',
        email: '',
        employee_id: '',
        role: "Read Only",
        title: "Other",
        company_code: companyCode || '',
        work_status: '',
      });
      setTeamAdditionalRolesOpen(false);
      setCreateEmployeeRecord(false);
      setEmployeeDepot('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const [allTeamMembers, setAllTeamMembers] = useState<SystemUser[]>([]);
  const [allTeamLoading, setAllTeamLoading] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const [allTeamSearch, setAllTeamSearch] = useState('');
  const [allTeamCompanyFilter, setAllTeamCompanyFilter] = useState<string>('all');
  const [allTeamRoleFilter, setAllTeamRoleFilter] = useState<string>('all');
  useEffect(() => {
    if (!isSystemAdmin || !currentUserEmail) return;
    setAllTeamLoading(true);
    fetch('/api/users', { headers: { 'X-User-Email': currentUserEmail } })
      .then(res => res.json())
      .then(data => { setAllTeamMembers((Array.isArray(data) ? data : []).map((u) => normalizeUserRow(u))); setAllTeamLoading(false); })
      .catch(() => { setAllTeamMembers([]); setAllTeamLoading(false); });
  }, [isSystemAdmin, currentUserEmail, users.length, normalizeUserRow]);

  useEffect(() => {
    if (!currentUserEmail) return;
    const load = () => {
      const params = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      fetch(`/api/presence/online${params}`, { headers: { 'X-User-Email': currentUserEmail } })
        .then(r => r.json())
        .then(data => setOnlineUsers(Array.isArray(data) ? data : []))
        .catch(() => setOnlineUsers([]));
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [currentUserEmail, companyCode]);

  const safeAllTeamMembers = Array.isArray(allTeamMembers) ? allTeamMembers : [];
  const safeUsers = Array.isArray(users) ? users : [];
  const safeCompanies = Array.isArray(companies) ? companies : [];

  const companyCodeToName = new Map<string, string>();
  safeAllTeamMembers.forEach(u => {
    if (u.company_code && !companyCodeToName.has(u.company_code)) {
      companyCodeToName.set(u.company_code, u.company_name || u.company_code);
    }
  });
  const uniqueCompaniesForFilter = [...companyCodeToName.entries()]
    .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

  const inferredOwnOrgCompanyCode = useMemo(() => {
    if (!isSystemAdmin) return '';
    const distinct = new Set<string>();
    for (const u of safeAllTeamMembers) {
      const cc = String(u.company_code ?? '').trim();
      if (cc) distinct.add(cc);
    }
    if (distinct.size !== 1) return '';
    return [...distinct][0] ?? '';
  }, [isSystemAdmin, safeAllTeamMembers]);

  /** Session/CMS company from parent, or when the directory is single-tenant infer that code so the pane still appears for system users without company_code. */
  const teamPaneCompanyCode = (String(companyCode ?? '').trim() || inferredOwnOrgCompanyCode).trim();
  const ownCompanyCodeNorm = teamPaneCompanyCode.toLowerCase();
  /** Home company scope from parent (`employeesApiCompanyCode`); may match pane even when platform heuristics miss. */
  const homeScopeNorm = String(companyCode ?? '').trim().toLowerCase();
  const showOwnCompanyTeamPane = isSystemAdmin && !!ownCompanyCodeNorm;

  const titleLooksLikeSystemAdministratorUser = (u: SystemUser) => {
    const raw = String(u.title ?? '')
      .trim()
      .toLowerCase();
    if (raw.includes('system') && (raw.includes('administrator') || raw.includes('admin'))) return true;
    const em = (u.email || '').trim().toLowerCase();
    return em === 'admin@railvault.com' || em === 'andyuk38@gmail.com';
  };

  const companyDisplayNameForPaneTenant = useMemo(() => {
    const r = safeAllTeamMembers.find(
      (u) => String(u.company_code ?? '').trim().toLowerCase() === ownCompanyCodeNorm
    );
    return String(r?.company_name ?? '').trim();
  }, [safeAllTeamMembers, ownCompanyCodeNorm]);

  const platformOperatorNormFromParent = String(platformOperatorCompanyCode ?? '').trim().toLowerCase();
  const platformOperatorNormFromList = useMemo(
    () => detectCmsProviderCompanyCodeFromCompanyList(safeCompanies).trim().toLowerCase(),
    [safeCompanies]
  );
  const vitePlatformOperatorNorm =
    typeof import.meta !== 'undefined'
      ? String((import.meta as { env?: Record<string, string> }).env?.VITE_PLATFORM_OPERATOR_COMPANY_CODE ?? '')
          .trim()
          .toLowerCase()
      : '';
  const effectivePlatformNorm =
    platformOperatorNormFromParent || platformOperatorNormFromList || vitePlatformOperatorNorm;

  const paneIsPlatformOperatorTenant =
    (!!effectivePlatformNorm && ownCompanyCodeNorm === effectivePlatformNorm) ||
    (!!ownCompanyCodeNorm && !effectivePlatformNorm && railIntelOperatorNameHint(companyDisplayNameForPaneTenant));

  const ownCompanyTeamMembers = showOwnCompanyTeamPane
    ? safeAllTeamMembers.filter((u) => {
        const cc = String(u.company_code ?? '').trim().toLowerCase();
        if (cc && cc === ownCompanyCodeNorm) return true;
        const noCompany = !String(u.company_code ?? '').trim();
        if (!noCompany || !titleLooksLikeSystemAdministratorUser(u)) return false;
        if (paneIsPlatformOperatorTenant) return true;
        if (!!homeScopeNorm && ownCompanyCodeNorm === homeScopeNorm) return true;
        return false;
      })
    : [];
  const ownCompanyLabel =
    (systemAdminHomeCompanyName && systemAdminHomeCompanyName.trim()) ||
    ownCompanyTeamMembers.find((u) => String(u.company_code ?? '').trim().toLowerCase() === ownCompanyCodeNorm)?.company_name ||
    companyDisplayNameForPaneTenant ||
    teamPaneCompanyCode ||
    '';

  /** Shown company / code for system admins with no DB company (e.g. platform operator). */
  const getDisplayTeamUserCompany = (u: SystemUser): { name: string; code: string } => {
    const rawCode = String(u.company_code ?? '').trim();
    if (rawCode) {
      return {
        name: String(u.company_name ?? '').trim() || '—',
        code: rawCode,
      };
    }
    if (!isSystemAdmin || !titleLooksLikeSystemAdministratorUser(u)) {
      return { name: '—', code: '—' };
    }
    const code = String(companyCode ?? '').trim() || effectivePlatformNorm || teamPaneCompanyCode || '';
    const norm = code.trim().toLowerCase();
    const fromList = safeCompanies.find((c) => String(c.id ?? '').trim().toLowerCase() === norm);
    const name =
      (fromList?.name && String(fromList.name).trim()) ||
      (systemAdminHomeCompanyName && systemAdminHomeCompanyName.trim()) ||
      (code || '—');
    return { name: name || '—', code: code || '—' };
  };

  const filteredAllTeamMembers = safeAllTeamMembers.filter((u) => {
    const q = allTeamSearch.trim().toLowerCase();
    if (q) {
      const match = (s: string) => (s || '').toLowerCase().includes(q);
      const eid = u.employee_id != null ? String(u.employee_id) : '';
      const disp = getDisplayTeamUserCompany(u);
      if (
        !match(u.full_name) &&
        !match(u.email) &&
        !match(eid) &&
        !match(u.company_name || '') &&
        !match(u.company_code || '') &&
        !(disp.code && disp.code !== '—' && match(disp.code)) &&
        !(disp.name && disp.name !== '—' && match(disp.name)) &&
        !match(u.title || '')
      )
        return false;
    }
    if (allTeamCompanyFilter !== 'all') {
      const filterNorm = allTeamCompanyFilter.toLowerCase();
      const ucc = String(u.company_code ?? '').trim().toLowerCase();
      const dispCode = getDisplayTeamUserCompany(u).code.trim().toLowerCase();
      if (ucc !== filterNorm && dispCode !== filterNorm) return false;
    }
    if (allTeamRoleFilter !== 'all' && u.role !== allTeamRoleFilter) return false;
    return true;
  });

  const filteredAllUsersForMainGrid = useMemo(() => {
    if (!isSystemAdmin) return filteredAllTeamMembers;
    return filteredAllTeamMembers.filter((u) => {
      if (String(u.company_code ?? '').trim()) return true;
      if (!titleLooksLikeSystemAdministratorUser(u)) return true;
      if (paneIsPlatformOperatorTenant) return false;
      if (!!homeScopeNorm && ownCompanyCodeNorm === homeScopeNorm) return false;
      return true;
    });
  }, [isSystemAdmin, filteredAllTeamMembers, paneIsPlatformOperatorTenant, homeScopeNorm, ownCompanyCodeNorm]);

  const displayUsers = isSystemAdmin ? filteredAllUsersForMainGrid : safeUsers;
  const displayLoading = isSystemAdmin ? allTeamLoading : loading;
  const tableColCount = isSystemAdmin ? 7 : 4;
  const systemAdminFiltersActive =
    isSystemAdmin &&
    (allTeamSearch.trim().length > 0 || allTeamCompanyFilter !== 'all' || allTeamRoleFilter !== 'all');
  const visibleUserCount = displayUsers.length;
  const totalUserCount = isSystemAdmin ? safeAllTeamMembers.length : safeUsers.length;

  const performDeleteUser = useCallback(async (id: number | string) => {
    try {
      const headers: HeadersInit = {};
      if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
      const res = await fetch(`/api/users/${id}`, { method: 'DELETE', headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to delete user');
      }
      setUsers((prev) => (Array.isArray(prev) ? prev : []).filter((u) => String(u.id) !== String(id)));
      if (isSystemAdmin) {
        setAllTeamMembers((prev) => (Array.isArray(prev) ? prev : []).filter((u) => String(u.id) !== String(id)));
      }
      setSuccess('User removed. Assessment history and write-ups on employee records were kept for audit.');
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : translateUi('Failed to delete user'));
    }
  }, [currentUserEmail, isSystemAdmin]);

  const confirmDeleteUser = useCallback((id: number | string, email?: string) => {
    if (!canManageTeamUsers) {
      setError('Only company administrators can delete team users from Team Management.');
      return;
    }
    const requesterEmail = (currentUserEmail || '').toLowerCase().trim();
    const targetEmail = (email || '').toLowerCase().trim();
    if (requesterEmail && targetEmail && requesterEmail === targetEmail) {
      setError('You cannot delete your own system user account.');
      return;
    }
    const pool = isSystemAdmin ? safeAllTeamMembers : safeUsers;
    const target = pool.find((u) => String(u.id) === String(id));
    const targetIsOtherCompanyAdmin =
      target &&
      titleLooksLikeCompanyAdministrator(target.title) &&
      (target.email || '').toLowerCase() !== requesterEmail;
    if (targetIsOtherCompanyAdmin && !isSystemAdmin && isNominatedCompanyAdmin) {
      setError('Nominated company administrators cannot remove other company administrators.');
      return;
    }
    if (targetIsOtherCompanyAdmin && !isSystemAdmin && countCompanyAdministratorsInCompany(target.company_code, pool) > 1) {
      setError(
        'Only System Administrators can remove another company administrator while more than one company administrator exists for that company.'
      );
      return;
    }
    const displayName = String(target?.full_name || email || 'this user').trim();
    const additionalRoles = parseAdditionalRoles(target?.work_status);
    const hasAssessorRole = additionalRoles.some((r) => r.toLowerCase() === 'assessor');
    const rolesNote =
      additionalRoles.length > 0
        ? `\n\nAdditional roles on this account: ${additionalRoles.join(', ')}.`
        : '';
    const auditNote = hasAssessorRole
      ? '\n\nImportant: Any assessments, grades, notes, and write-ups this person recorded on other employees will remain unchanged for audit purposes. Removing them only revokes login/system access.'
      : '\n\nEmployee competency records and any assessment history linked by email remain unchanged for audit purposes. This only removes their login/system access.';
    setError(null);
    setDeleteConfirm({
      isOpen: true,
      title: 'Remove team user?',
      message: `Are you sure you want to remove ${displayName} from Team Management?${rolesNote}${auditNote}\n\nThis cannot be undone.`,
      onConfirm: () => {
        setDeleteConfirm(null);
        void performDeleteUser(id);
      },
    });
  }, [
    canManageTeamUsers,
    currentUserEmail,
    isSystemAdmin,
    isNominatedCompanyAdmin,
    safeAllTeamMembers,
    safeUsers,
    titleLooksLikeCompanyAdministrator,
    countCompanyAdministratorsInCompany,
    performDeleteUser,
  ]);

  const renderUserTableRows = (userList: SystemUser[], showCompanyColumns: boolean) =>
    userList.map((user) => {
      const rowUserId = String(user.id ?? '').trim();
      const isCompanyContactRow = rowUserId === '' || rowUserId === '0';
      const canManageThisRow = canManageTeamUsers && !isCompanyContactRow;
      const poolForAdminCount = isSystemAdmin ? safeAllTeamMembers : safeUsers;
      const peerCompanyAdminCount = countCompanyAdministratorsInCompany(user.company_code, poolForAdminCount);
      const isOtherCompanyAdminRow =
        titleLooksLikeCompanyAdministrator(user.title) &&
        (user.email || '').toLowerCase() !== (currentUserEmail || '').toLowerCase();
      const deleteCompanyAdminBlocked =
        !isSystemAdmin &&
        isOtherCompanyAdminRow &&
        (isNominatedCompanyAdmin || peerCompanyAdminCount > 1);
      return (
        <tr
          key={`${String(user.company_code ?? '').toLowerCase()}::${String(user.id ?? '')}::${String(user.email ?? '').toLowerCase()}`}
          className="text-sm hover:bg-slate-50 transition-colors"
        >
          <td className="px-6 py-4">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center mr-3 shrink-0">
                <User className="w-4 h-4 text-slate-400" />
              </div>
              <div className="min-w-0">
                <span className="font-medium text-slate-900 block truncate">{user.full_name}</span>
                {user.employee_id != null && String(user.employee_id).trim() ? (
                  <span
                    className="text-xs text-slate-500 font-mono block truncate"
                    title={translateUi('Employee ID: {{id}}', { id: String(user.employee_id).trim() })}
                  >
                    ID: {String(user.employee_id).trim()}
                  </span>
                ) : null}
              </div>
            </div>
          </td>
          <td className="px-6 py-4 text-slate-600 font-medium">{displayTeamUserTitle(user)}</td>
          {showCompanyColumns && isSystemAdmin && (
            <>
              <td className="px-6 py-4 text-slate-600">
                {(() => {
                  const d = getDisplayTeamUserCompany(user);
                  return d.name;
                })()}
              </td>
              <td className="px-6 py-4 text-slate-600 font-mono">
                {(() => {
                  const d = getDisplayTeamUserCompany(user);
                  return d.code;
                })()}
              </td>
            </>
          )}
          <td className="px-6 py-4 text-slate-600">{user.email}</td>
          {isSystemAdmin && (
            <td className="px-6 py-4 text-slate-600 text-xs">
              {user.role === 'Edit' ? translateUi("Can modify records & managers") : translateUi("View records only")}
            </td>
          )}
          <td className="px-6 py-4 w-72">
            <div className="flex items-center justify-start gap-1 whitespace-nowrap">
              {isSystemAdmin && onLoginAsUser && user.email?.toLowerCase() !== currentUserEmail?.toLowerCase() && (
                <button
                  onClick={() => onLoginAsUser(user)}
                  className="p-2 bg-violet-100 hover:bg-violet-200 rounded-lg transition-all border border-violet-300"
                  title={translateUi("Login as this user")}
                  aria-label={translateUi('Login as {{name}}', { name: user.full_name })}
                >
                  <LogIn className="w-4 h-4 shrink-0 text-violet-800" />
                </button>
              )}
              {canManageThisRow && (
                <button
                  type="button"
                  onClick={() => setEditingUser(user)}
                  className="p-2 bg-amber-100 hover:bg-amber-200 rounded-lg transition-all border border-amber-300"
                  title={translateUi("Edit user")}
                  aria-label={translateUi('Edit user {{name}}', { name: user.full_name })}
                >
                  <Edit2 className="w-4 h-4 shrink-0 text-amber-800" />
                </button>
              )}
              {isSystemAdmin && (
                <button
                  onClick={() => {
                    setSetPasswordUser(user);
                    setSetPasswordValue('');
                    setSetPasswordError(null);
                  }}
                  className="p-2 bg-yellow-200 hover:bg-yellow-300 rounded-lg transition-all border border-yellow-400"
                  title={translateUi("Set password for this user")}
                >
                  <KeyRound className="w-4 h-4 shrink-0 text-yellow-800" />
                </button>
              )}
              {(isSystemAdmin || canManageThisRow) && (
                <button
                  type="button"
                  onClick={() => { setResetMfaUser(user); setResetMfaError(null); }}
                  className="p-2 bg-sky-100 hover:bg-sky-200 rounded-lg transition-all border border-sky-300"
                  title={translateUi("Reset authenticator")}
                >
                  <ShieldCheck className="w-4 h-4 shrink-0 text-sky-800" />
                </button>
              )}
              {canManageThisRow && (user.email || '').toLowerCase() !== (currentUserEmail || '').toLowerCase() && (
                <button
                  type="button"
                  disabled={deleteCompanyAdminBlocked}
                  onClick={() => confirmDeleteUser(user.id, user.email)}
                  className={`p-2 rounded-lg transition-all border ${
                    deleteCompanyAdminBlocked
                      ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-rose-100 hover:bg-rose-200 border-rose-300"
                  }`}
                  title={
                    deleteCompanyAdminBlocked
                      ? isNominatedCompanyAdmin
                        ? 'Nominated company administrators cannot remove other company administrators.'
                        : 'Only a System Administrator can remove another company administrator while more than one exists.'
                      : 'Delete this user'
                  }
                  aria-label={translateUi('Delete user {{name}}', { name: user.full_name })}
                >
                  <Trash2 className={`w-4 h-4 shrink-0 ${deleteCompanyAdminBlocked ? "text-slate-400" : "text-rose-700"}`} />
                </button>
              )}
            </div>
          </td>
        </tr>
      );
    });

  return (
    <div className="space-y-6">
      {deleteConfirm && (
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={translateUi("Remove user")}
          cancelLabel={translateUi("Cancel")}
          onConfirm={deleteConfirm.onConfirm}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      {showNominateAdminPanel && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 sm:p-6 space-y-4 shadow-sm">
          <div className="space-y-1">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" aria-hidden />
              Nominate company administrator
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {nomineeMeta?.deferred
                ? "Onboarding skipped this step. Enter the administrator details below — we will create their login and send a welcome invite email with a temporary password."
                : nomineeMeta?.hasNomination && !nomineeMeta.welcomeEmailSucceeded
                  ? "A nomination is saved but the welcome invite has not been confirmed as sent. Save again to resend the invite email."
                  : "Invite a company administrator by email. They receive a welcome message with the company code and a temporary password."}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-semibold text-slate-600">Work email</span>
              <input
                type="email"
                value={nomineeForm.adminEmail}
                onChange={(e) => setNomineeForm((prev) => ({ ...prev, adminEmail: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="admin@company.com"
                disabled={nomineeSaving || nomineeLoading}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-slate-600">{translateUi("Full name")}</span>
              <input
                type="text"
                value={nomineeForm.adminFullName}
                onChange={(e) => setNomineeForm((prev) => ({ ...prev, adminFullName: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Jane Smith"
                disabled={nomineeSaving || nomineeLoading}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-slate-600">{translateUi("Job title")}</span>
              <input
                type="text"
                value={nomineeForm.adminJobTitle}
                onChange={(e) => setNomineeForm((prev) => ({ ...prev, adminJobTitle: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Operations Manager"
                disabled={nomineeSaving || nomineeLoading}
              />
            </label>
          </div>
          {nomineeError && <p className="text-sm text-rose-600">{nomineeError}</p>}
          {nomineeMessage && <p className="text-sm text-emerald-700 font-medium">{nomineeMessage}</p>}
          <button
            type="button"
            disabled={nomineeSaving || nomineeLoading || !currentUserEmail}
            onClick={async () => {
              if (!companyCode || !currentUserEmail) return;
              setNomineeError(null);
              setNomineeMessage(null);
              setNomineeSaving(true);
              try {
                const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/nominate-company-admin`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "X-User-Email": currentUserEmail,
                  },
                  body: JSON.stringify({
                    adminEmail: nomineeForm.adminEmail.trim(),
                    adminFullName: nomineeForm.adminFullName.trim(),
                    adminJobTitle: nomineeForm.adminJobTitle.trim(),
                  }),
                });
                const data = (await res.json().catch(() => ({}))) as {
                  error?: string;
                  message?: string;
                  inviteSent?: boolean;
                };
                if (!res.ok) throw new Error(data.error || "Could not nominate administrator");
                setNomineeMessage(
                  data.message ||
                    (data.inviteSent
                      ? translateUi("Invite email sent to the nominated administrator.")
                      : translateUi("Nomination saved."))
                );
                await loadNomineeStatus();
                fetchUsers();
              } catch (err: any) {
                setNomineeError(err?.message || "Could not nominate administrator");
              } finally {
                setNomineeSaving(false);
              }
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            <Mail className="w-4 h-4" aria-hidden />
            {nomineeSaving ? translateUi("Sending invite…") : translateUi("Save & send invite email")}
          </button>
        </div>
      )}
      {onlineUsers.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-emerald-800">
            {onlineUsers.length} user{onlineUsers.length !== 1 ? 's' : ''} currently online
            {companyCode ? ' in this company' : ''}
          </span>
        </div>
      )}
      {showOwnCompanyTeamPane && (
        <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden ring-1 ring-indigo-50/80">
          <div className="px-6 py-4 border-b border-indigo-100/80 bg-indigo-50/50">
            <h3 className="font-bold text-slate-900">Your organisation</h3>
            <p className="text-slate-600 text-sm mt-1">
              <span className="font-medium text-slate-800">{ownCompanyLabel}</span>
              <span className="text-slate-400 mx-1.5">·</span>
              <span className="font-mono text-slate-700">{teamPaneCompanyCode}</span>
            </p>
            <p className="text-slate-500 text-xs mt-1.5">
              Team users for your system administrator company. The section below lists users across every company.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 font-semibold">Name</th>
                  <th className="px-6 py-4 font-semibold"> Title </th>
                  <th className="px-6 py-4 font-semibold"> Email </th>
                  <th className="px-6 py-4 font-semibold"> Permissions </th>
                  <th className="px-6 py-4 w-72">
                    <div className="flex justify-start">
                      <span className="font-semibold"> Actions </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
                    </td>
                  </tr>
                ) : ownCompanyTeamMembers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-400 italic">
                      {safeAllTeamMembers.length === 0 ? translateUi("No users loaded yet") : translateUi("No team users for this company yet")}
                    </td>
                  </tr>
                ) : (
                  renderUserTableRows(ownCompanyTeamMembers, false)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-900">All Users</h3>
              <p className="text-slate-500 text-sm mt-0.5">
                {isSystemAdmin ? translateUi("All system users across all companies") : translateUi("Manage system access and permissions")}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="rounded-xl border border-amber-300 bg-amber-100 px-3 py-2 text-xs text-amber-900">
                <p className="font-semibold whitespace-nowrap">
                  Users: {visibleUserCount}
                  {systemAdminFiltersActive ? ` / ${totalUserCount}` : ''}
                  {` · ${systemAdminFiltersActive ? translateUi("Filtered view") : translateUi("Current view")}`}
                </p>
              </div>
              {isCompanyActive && companyCode && isAdmin && (
                <>
                  <a
                    href={`data:text/csv;charset=utf-8,${encodeURIComponent('# role = Read Only (view only) or Edit (can edit). title = job title. employee_id optional; unique per company when set.\nfull_name,email,role,title,employee_id\nJohn Smith,john.smith@example.com,Read Only,Manager,EMP001\nJane Doe,jane.doe@example.com,Edit,Driver,')}`}
                    download="rail-intel-users-template.csv"
                    className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors border border-slate-200"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Download template
                  </a>
                  <button
                    onClick={() => { setBulkUploadOpen(true); setBulkUploadFile(null); setBulkUploadResult(null); }}
                    className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors border border-slate-200"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Bulk upload
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setError(null);
                  setSuccess(null);
                }}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add User
              </button>
            </div>
          </div>
          {isSystemAdmin && (
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name, email, employee ID, company..."
                  value={allTeamSearch}
                  onChange={e => setAllTeamSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <select
                value={allTeamCompanyFilter}
                onChange={e => setAllTeamCompanyFilter(e.target.value)}
                className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="all">{translateUi("All companies")}</option>
                {uniqueCompaniesForFilter.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
              <select
                value={allTeamRoleFilter}
                onChange={e => setAllTeamRoleFilter(e.target.value)}
                className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="all">{translateUi("All roles")}</option>
                <option value="Read Only">{translateUi("Read Only")}</option>
                <option value="Edit"> Edit </option>
              </select>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold">Name</th>
                <th className="px-6 py-4 font-semibold"> Title </th>
                {isSystemAdmin && (
                  <>
                    <th className="px-6 py-4 font-semibold"> Company </th>
                    <th className="px-6 py-4 font-semibold">Company Number</th>
                  </>
                )}
                <th className="px-6 py-4 font-semibold"> Email </th>
                {isSystemAdmin && <th className="px-6 py-4 font-semibold"> Permissions </th>}
                <th className="px-6 py-4 w-72">
                  <div className="flex justify-start">
                    <span className="font-semibold"> Actions </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayLoading ? (
                <tr><td colSpan={tableColCount} className="px-6 py-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></td></tr>
              ) : displayUsers.length === 0 ? (
                <tr><td colSpan={tableColCount} className="px-6 py-8 text-center text-slate-400 italic">
                  {isSystemAdmin ? (safeAllTeamMembers.length === 0 ? translateUi("No users found") : translateUi("No matching users")) : 'No users found'}
                </td></tr>
              ) : (
                renderUserTableRows(displayUsers, true)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-emerald-700 text-sm flex items-center">
          <CheckCircle2 className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      <>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">{editingUser ? translateUi("Edit User") : translateUi("Add User")}</h3>
                <button onClick={() => { setIsModalOpen(false); setEditingUser(null); setTeamAdditionalRolesOpen(false); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}
                {editingUser && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                    <p><span className="font-semibold text-slate-700">Added:</span> {formatDateTimeUKShort(editingUser.added_date || editingUser.created_at)}</p>
                    <p className="mt-1"><span className="font-semibold text-slate-700">Last updated:</span> {formatDateTimeUKShort(editingUser.updated_at || editingUser.created_at)}</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Full Name")}</label>
                  <input 
                    type="text" 
                    required
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="e.g. Sarah Jenkins"
                    value={formData.full_name}
                    onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Email Address")}</label>
                  <input 
                    type="email" 
                    required
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="s.jenkins@rail.co.uk"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                  {!editingUser && (
                    <p className="mt-2 text-xs text-slate-500">
                      Team users can be created without an employee record. This grants system access only based on selected permissions.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Employee ID{' '}
                    <span className="font-normal normal-case text-slate-400">
                      {createEmployeeRecord ? translateUi("(required for employee list)") : translateUi("(optional)")}
                    </span>
                  </label>
                  <input
                    type="text"
                    required={createEmployeeRecord}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-mono text-sm"
                    placeholder="e.g. EMP-10492"
                    value={formData.employee_id}
                    onChange={e => setFormData({ ...formData, employee_id: e.target.value })}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Additional Roles")}</label>
                  <div className="relative" ref={teamAdditionalRolesDropdownRef}>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-left flex items-center justify-between"
                      onClick={() => setTeamAdditionalRolesOpen((prev) => !prev)}
                      aria-expanded={teamAdditionalRolesOpen}
                    >
                      <span className="truncate text-slate-700">
                        {selectedTeamAdditionalRoles.length > 0
                          ? selectedTeamAdditionalRoles.map((role) => formatAdditionalRoleLabel(role)).join(', ')
                          : translateUi("Select additional roles")}
                      </span>
                      <ChevronDown className={cn('w-4 h-4 text-slate-500 transition-transform', teamAdditionalRolesOpen && 'rotate-180')} />
                    </button>
                    {teamAdditionalRolesOpen && (
                      <div className="absolute z-30 mt-2 w-full border border-slate-200 rounded-xl shadow-lg p-2 max-h-56 overflow-y-auto bg-white">
                        {ADDITIONAL_ROLE_OPTIONS.map((opt) => {
                          const checked = selectedTeamAdditionalRoles.some((role) => rolesMatch(role, opt));
                          return (
                            <label
                              key={opt}
                              className="flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer text-sm hover:bg-slate-50 text-slate-700"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleTeamAdditionalRole(opt)}
                                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span>{formatAdditionalRoleLabel(opt)}</span>
                            </label>
                          );
                        })}
                        <div className="pt-2 mt-1 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, work_status: '' }))}
                            className="text-xs font-medium text-slate-500 hover:text-slate-700"
                          >
                            Clear additional roles
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Tick Assessor to appear in assessing manager lists on employee profiles. Requires a linked employee (Employee ID or matching email).
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Role Title</label>
                  <select 
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  >
                    {teamTitleSelectOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {adminSeatSummary && (
                    <p className="text-xs text-slate-500 mt-1">
                      Company administrators: {adminSeatSummary.currentAdmins} of {adminSeatSummary.maxSeats} seats used (2 included with your company).
                    </p>
                  )}
                  {adminSeatLimitReachedForSelection && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                      You have reached your company administrator limit. Purchase additional Company Admin Licences from Administration → Add-ons.
                    </p>
                  )}
                </div>
                {showCreateEmployeeOption && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createEmployeeRecord}
                        onChange={(e) => {
                          setCreateEmployeeRecord(e.target.checked);
                          if (!e.target.checked) setEmployeeDepot('');
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-slate-800">
                        <span className="font-semibold block">Also add to employee list</span>
                        <span className="text-xs text-slate-600 mt-0.5 block">
                          {needsEmployeeForAdditionalRoles && !selectedTitleRequiresCompetency
                            ? 'Additional roles like Assessor are stored on an employee record. Tick this if they are not already on the employee list, then choose a Depot.'
                            : 'This role requires competency tracking. Tick to create their employee/competency record automatically.'}
                        </span>
                      </span>
                    </label>
                    {createEmployeeRecord && (
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> Depot </label>
                        <select
                          required
                          className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none disabled:opacity-60"
                          value={employeeDepot}
                          disabled={depotSelectOptions.length === 0}
                          onChange={(e) => setEmployeeDepot(e.target.value)}
                        >
                          <option value="">
                            {depotSelectOptions.length === 0 ? translateUi("No depots configured") : translateUi("Select depot…")}
                          </option>
                          {depotSelectOptions.map((depot) => (
                            <option key={depot} value={depot}>{depot}</option>
                          ))}
                        </select>
                        {depotSelectOptions.length === 0 && (
                          <p className="mt-1 text-xs text-amber-800">
                            Configure depots under Traction &amp; Routes before creating an employee record.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> Company </label>
                  {companyCode || editingUser || !isSystemAdmin ? (
                    <div className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 font-mono cursor-not-allowed">
                      {formData.company_code || companyCode || '—'}
                    </div>
                  ) : (
                    <select
                      required
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                      value={formData.company_code}
                      onChange={e => setFormData({ ...formData, company_code: e.target.value })}
                    >
                      <option value="">Select company…</option>
                      {safeCompanies.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-slate-400 mt-1">Company code is set at onboarding and cannot be changed</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">System Access Level</label>
                  {!editingUser && !showCreateEmployeeOption && (
                    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-xs text-slate-600">
                        <span className="block font-semibold text-slate-800">System access only</span>
                        <span className="mt-1 block text-slate-500">
                          This role title is not marked for competency tracking and no Additional Roles are selected, so an employee list record is not offered here.
                        </span>
                      </div>
                    </div>
                  )}
                  {!editingUser && showCreateEmployeeOption && !createEmployeeRecord && (
                    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-xs text-slate-600">
                        <span className="block font-semibold text-slate-800">
                          {needsEmployeeForAdditionalRoles ? translateUi("Employee record required for Additional Roles") : translateUi("Employee competency record optional")}
                        </span>
                        <span className="mt-1 block text-slate-500">
                          {needsEmployeeForAdditionalRoles
                            ? 'Tick "Also add to employee list" above if they are not already an employee, or enter an existing Employee ID / matching email to link.'
                            : 'Tick "Also add to employee list" above, or create their employee record later in Employee Records and link it here.'}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-3 text-xs text-indigo-900">
                    <p className="font-semibold mb-1">Permission guide</p>
                    <p><span className="font-semibold">Read Only:</span> Can view records and dashboards but cannot edit employee, medical, incident, or team data.</p>
                    <p className="mt-1"><span className="font-semibold">Edit:</span> Can add and update operational records, including employee and medical updates, based on company access.</p>
                    <p className="mt-1 text-indigo-700">Tip: Use Read Only for users who only need visibility.</p>
                  </div>
                  <select 
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="Read Only">{translateUi("Read Only (View records only)")}</option>
                    <option value="Edit">{translateUi("Edit (Can modify records & managers)")}</option>
                  </select>
                </div>
                <div className="pt-4">
                  <button 
                    type="submit"
                    disabled={isTeamUserSubmitDisabled}
                    className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600 disabled:shadow-none"
                  >
                    {editingUser ? translateUi("Update User") : translateUi("Add User & Notify")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {setPasswordUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">{translateUi("Set password")}</h3>
                <button onClick={() => { setSetPasswordUser(null); setSetPasswordValue(''); setSetPasswordError(null); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-slate-600 text-sm">Override password for <span className="font-medium text-slate-900">{setPasswordUser.full_name}</span> ({setPasswordUser.email})</p>
                {setPasswordError && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{setPasswordError}</div>
                )}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("New password")}</label>
                  <input
                    type="password"
                    minLength={6}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="Min 6 characters"
                    value={setPasswordValue}
                    onChange={e => { setSetPasswordValue(e.target.value); setSetPasswordError(null); }}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setSetPasswordUser(null); setSetPasswordValue(''); setSetPasswordError(null); }}
                    className="flex-1 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl font-medium transition-colors"
                  > Cancel </button>
                  <button
                    type="button"
                    disabled={setPasswordValue.length < 6 || setPasswordLoading}
                    onClick={async () => {
                      if (setPasswordValue.length < 6) return;
                      setSetPasswordLoading(true);
                      setSetPasswordError(null);
                      try {
                        const res = await fetch('/api/users/set-password', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail || '' },
                          body: JSON.stringify({ email: setPasswordUser.email, newPassword: setPasswordValue }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (res.ok) {
                          setSetPasswordUser(null);
                          setSetPasswordValue('');
                          setSuccess('Password updated successfully.');
                        } else {
                          setSetPasswordError(data.error || 'Failed to set password');
                        }
                      } catch (err: any) {
                        setSetPasswordError(err?.message || 'Failed to set password');
                      } finally {
                        setSetPasswordLoading(false);
                      }
                    }}
                    className="flex-1 py-2.5 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {setPasswordLoading ? 'Updating…' : translateUi("Set password")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {resetMfaUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">{translateUi("Reset authenticator")}</h3>
                <button onClick={() => { setResetMfaUser(null); setResetMfaError(null); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-slate-600 text-sm">
                  {translateUi("This clears the authenticator for")} <span className="font-medium text-slate-900">{resetMfaUser.full_name}</span> ({resetMfaUser.email}). {translateUi("They will set up a new app on next sign-in if 2FA is required.")}
                </p>
                {resetMfaError && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{resetMfaError}</div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setResetMfaUser(null); setResetMfaError(null); }}
                    className="flex-1 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl font-medium"
                  >{translateUi("Cancel")}</button>
                  <button
                    type="button"
                    disabled={resetMfaLoading}
                    onClick={async () => {
                      setResetMfaLoading(true);
                      setResetMfaError(null);
                      try {
                        const res = await fetch(`/api/users/${resetMfaUser.id}/reset-mfa`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}) },
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.error || translateUi("Could not reset authenticator"));
                        setResetMfaUser(null);
                      } catch (err: unknown) {
                        setResetMfaError(err instanceof Error ? err.message : translateUi("Could not reset authenticator"));
                      } finally {
                        setResetMfaLoading(false);
                      }
                    }}
                    className="flex-1 py-2.5 bg-sky-700 text-white font-medium rounded-xl hover:bg-sky-800 disabled:opacity-50"
                  >
                    {resetMfaLoading ? translateUi("Resetting…") : translateUi("Reset authenticator")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {bulkUploadOpen && companyCode && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">Bulk upload users</h3>
                <button onClick={() => { setBulkUploadOpen(false); setBulkUploadFile(null); setBulkUploadResult(null); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="text-sm text-slate-600 space-y-2">
                  <p className="font-medium">CSV format</p>
                  <p>Required columns (first row = header): <code className="bg-slate-100 px-1 rounded">full_name</code>, <code className="bg-slate-100 px-1 rounded">email</code>, <code className="bg-slate-100 px-1 rounded">role</code>, <code className="bg-slate-100 px-1 rounded">title</code></p>
                  <div className="text-xs text-slate-500 space-y-1 mt-2">
                    <p><strong>role</strong> — Permissions: <code className="bg-slate-50 px-1 rounded">{translateUi("Read Only")}</code> (view only) or <code className="bg-slate-50 px-1 rounded"> Edit </code> (can edit records)</p>
                    <p><strong>title</strong> — Job title or position, e.g. Company Administrator, Manager, Driver, Trainer</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">CSV file</label>
                  <input
                    type="file"
                    accept=".csv"
                    className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-medium hover:file:bg-indigo-100"
                    onChange={(e) => { setBulkUploadFile(e.target.files?.[0] || null); setBulkUploadResult(null); }}
                  />
                </div>
                {bulkUploadResult && (
                  <div className={cn(
                    "p-4 rounded-xl text-sm",
                    bulkUploadResult.failed > 0 ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"
                  )}>
                    <p className="font-medium">Imported {bulkUploadResult.success} user{bulkUploadResult.success !== 1 ? 's' : ''}{bulkUploadResult.failed > 0 ? `, ${bulkUploadResult.failed} failed` : ''}.</p>
                    {bulkUploadResult.success > 0 && (
                      <p className="text-xs mt-1 opacity-90">New users can use &quot;Forgot password&quot; on the login page to set their password.</p>
                    )}
                    {bulkUploadResult.errors.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs max-h-24 overflow-y-auto">
                        {bulkUploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setBulkUploadOpen(false); setBulkUploadFile(null); setBulkUploadResult(null); }}
                    className="flex-1 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl font-medium transition-colors"
                  > Cancel </button>
                  <button
                    type="button"
                    disabled={!bulkUploadFile || bulkUploadLoading}
                    onClick={async () => {
                      if (!bulkUploadFile || !currentUserEmail) return;
                      setBulkUploadLoading(true);
                      setBulkUploadResult(null);
                      try {
                        const text = await bulkUploadFile.text();
                        const lines = text.split(/\r?\n/).filter(Boolean);
                        const dataLines = lines.filter(l => !l.trim().startsWith('#'));
                        if (dataLines.length < 2) {
                          setBulkUploadResult({ success: 0, failed: 0, errors: ['CSV must have header row and at least one data row'] });
                          setBulkUploadLoading(false);
                          return;
                        }
                        const header = dataLines[0].toLowerCase().replace(/\s/g, '_');
                        const cols = header.split(',').map(c => c.trim());
                        const fnIdx = cols.findIndex(c => c === 'full_name' || c === 'fullname');
                        const emIdx = cols.findIndex(c => c === 'email');
                        const roleIdx = cols.findIndex(c => c === 'role');
                        const titleIdx = cols.findIndex(c => c === 'title');
                        if (fnIdx < 0 || emIdx < 0) {
                          setBulkUploadResult({ success: 0, failed: 0, errors: ['CSV must have full_name and email columns'] });
                          setBulkUploadLoading(false);
                          return;
                        }
                        const csvRows = dataLines.slice(1).map(line => {
                          const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                          return {
                            full_name: (vals[fnIdx] ?? '').trim(),
                            email: (vals[emIdx] ?? '').trim(),
                            role: roleIdx >= 0 ? (vals[roleIdx] ?? "Read Only").trim() || "Read Only" : "Read Only",
                            title: titleIdx >= 0 ? (vals[titleIdx] ?? "Company Administrator").trim() || "Company Administrator" : "Company Administrator",
                          };
                        }).filter(r => r.full_name || r.email);
                        const res = await fetch('/api/users/bulk-upload', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
                          body: JSON.stringify({ csvRows, companyCode }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setBulkUploadResult({ success: 0, failed: csvRows.length, errors: [data.error || "Upload failed"] });
                        } else {
                          setBulkUploadResult(data);
                          if (data.success > 0) {
                            fetchUsers();
                            if (isSystemAdmin) {
                              fetch('/api/users', { headers: { 'X-User-Email': currentUserEmail } })
                                .then(r => r.json())
                                .then(arr => setAllTeamMembers((Array.isArray(arr) ? arr : []).map((u) => normalizeUserRow(u))))
                                .catch(() => {});
                            }
                          }
                        }
                      } catch (err: any) {
                        setBulkUploadResult({ success: 0, failed: 0, errors: [err?.message || "Upload failed"] });
                      } finally {
                        setBulkUploadLoading(false);
                      }
                    }}
                    className="flex-1 py-2.5 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {bulkUploadLoading ? 'Uploading…' : translateUi("Upload")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
};

const ActionDropdown = ({ onSelect, onEdit, onDelete, canEdit, canDelete, center }: { onSelect: () => void, onEdit?: () => void, onDelete: () => void, canEdit: boolean, canDelete: boolean; center?: boolean }) => {
  return (
    <div className={cn("flex items-center space-x-1", center ? "justify-center" : "justify-end")}>
      <button 
        onClick={onSelect}
        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
        title={translateUi("View Record")}
      >
        <Search className="w-4 h-4" />
      </button>
      {canEdit && onEdit && (
        <button 
          onClick={onEdit}
          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
          title={translateUi("Edit Record")}
        >
          <Edit2 className="w-4 h-4" />
        </button>
      )}
      {canDelete && (
        <button 
          onClick={onDelete}
          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
          title={translateUi("Delete Record")}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const SystemLogsPage = ({ currentUserEmail, isMobileOrTablet, onBack }: { currentUserEmail?: string; isMobileOrTablet?: boolean; onBack?: () => void }) => {
  const [auditLogs, setAuditLogs] = useState<{ id: number; action: string; user: string; details: string | null; timestamp: string; company_code?: string | null; company_name?: string | null }[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name?: string; contactName?: string; contactEmail?: string; contactPhone?: string; status: string; createdAt?: string }[]>([]);
  const [auditActionFilter, setAuditActionFilter] = useState<string>('all');
  const [auditUserFilter, setAuditUserFilter] = useState<string>('all');
  const [auditCompanyFilter, setAuditCompanyFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [auditLogsRefreshTrigger, setAuditLogsRefreshTrigger] = useState(0);
  const [auditLogsClearing, setAuditLogsClearing] = useState(false);

  useEffect(() => {
    if (!currentUserEmail) return;
    const headers: HeadersInit = { 'X-User-Email': currentUserEmail };
    const load = async () => {
      try {
        const [logsRes, compsRes] = await Promise.allSettled([
          fetch('/api/audit-logs?limit=300', { headers, cache: 'no-store' }).then(r => r.json()),
          fetch('/api/companies', { headers, cache: 'no-store' }).then(r => r.json()),
        ]);
        if (logsRes.status === 'fulfilled') setAuditLogs(Array.isArray(logsRes.value) ? logsRes.value : []);
        if (compsRes.status === 'fulfilled') setCompanies(Array.isArray(compsRes.value) ? compsRes.value : []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUserEmail, auditLogsRefreshTrigger]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", isMobileOrTablet && "space-y-6 px-1")}>
      <div className={cn("flex items-start justify-between gap-3", isMobileOrTablet && "flex-col items-stretch")}>
        <div>
          <h1 className={cn("font-bold text-slate-900 tracking-tight", isMobileOrTablet ? "text-xl sm:text-2xl" : "text-3xl")}>{translateUi("System Logs")}</h1>
          <p className={cn("text-slate-500 mt-1", isMobileOrTablet && "text-sm")}>{translateUi("Recent system activity for diagnosing access issues.")}</p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors",
              isMobileOrTablet && "w-full justify-center min-h-[44px]"
            )}
          >
            <ArrowLeft className="w-4 h-4" />
            {translateUi("Back to Dashboard")}
          </button>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className={cn("border-b border-slate-200 bg-slate-50", isMobileOrTablet ? "px-4 py-3" : "px-6 py-4")}>
          <div className={cn("flex flex-col gap-3", !isMobileOrTablet && "sm:flex-row sm:items-start sm:justify-between sm:gap-4")}>
            <div>
              <h3 className="font-bold text-slate-900">Access Records</h3>
              <p className="text-xs text-slate-500 mt-1">Filter and review authentication and access activity.</p>
            </div>
            {auditLogs.length > 0 && (
              <div className={cn("flex flex-wrap gap-2 shrink-0 items-center", isMobileOrTablet ? "flex-col" : "sm:justify-end")}>
                <button
                  onClick={async () => {
                    if (!confirm(translateUi('Clear all access records? This cannot be undone.'))) return;
                    setAuditLogsClearing(true);
                    try {
                      const headers: HeadersInit = { 'X-User-Email': currentUserEmail || '' };
                      const r = await fetch('/api/audit-logs', { method: 'DELETE', headers });
                      if (r.ok) {
                        setAuditLogs([]);
                        setAuditLogsRefreshTrigger(t => t + 1);
                      } else {
                        const data = await r.json().catch(() => ({}));
                        alert(data.error || translateUi('Failed to clear access records.'));
                      }
                    } finally {
                      setAuditLogsClearing(false);
                    }
                  }}
                  disabled={auditLogsClearing}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  {auditLogsClearing ? translateUi("Clearing…") : translateUi("Clear records")}
                </button>
                <select
                  value={auditActionFilter}
                  onChange={e => setAuditActionFilter(e.target.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none",
                    isMobileOrTablet && "w-full min-h-[44px]"
                  )}
                >
                  <option value="all">{translateUi("All actions")}</option>
                  {[...new Set(auditLogs.map(l => l.action))].sort().map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <select
                  value={auditUserFilter}
                  onChange={e => setAuditUserFilter(e.target.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none",
                    isMobileOrTablet && "w-full min-h-[44px]"
                  )}
                >
                  <option value="all">{translateUi("All users")}</option>
                  {[...new Set(auditLogs.map(l => l.user))].sort().map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <select
                  value={auditCompanyFilter}
                  onChange={e => setAuditCompanyFilter(e.target.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none",
                    isMobileOrTablet && "w-full min-h-[44px]"
                  )}
                >
                  <option value="all">{translateUi("All companies")}</option>
                  {companies
                    .slice()
                    .sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''))
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name ? `${c.name} (${c.id})` : `Company ${c.id}`}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        </div>
        {(() => {
          const filteredLogs = auditLogs.filter(log => {
            if (auditActionFilter !== 'all' && log.action !== auditActionFilter) return false;
            if (auditUserFilter !== 'all' && log.user !== auditUserFilter) return false;
            if (auditCompanyFilter !== 'all' && log.company_code !== auditCompanyFilter) return false;
            return true;
          });
          return isMobileOrTablet ? (
          <div className="divide-y divide-slate-100 max-h-[65vh] overflow-y-auto">
            {filteredLogs.length === 0 ? (
              <div className="px-4 py-12 text-center text-slate-400 text-sm">
                {auditLogs.length === 0 ? translateUi("No audit records yet.") : translateUi("No records match the selected filters.")}
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div key={log.id} className="px-4 py-3">
                  <p className="font-medium text-slate-800">{log.user}</p>
                  <p className="text-xs text-slate-500 font-mono">{log.timestamp}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{log.action}</span>
                  <p className="text-xs text-slate-600 mt-1">
                    {log.company_code && <span className="font-mono">{log.company_code}</span>}
                    {log.company_name && <span className="ml-1">· {log.company_name}</span>}
                    {!log.company_code && !log.company_name && '—'}
                  </p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 z-[1]">
                <tr className="border-b border-slate-200 text-slate-600 font-semibold">
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">Action</th>
                  <th className="px-6 py-3"> User </th>
                  <th className="px-6 py-3">Company No.</th>
                  <th className="px-6 py-3">{translateUi("Company Name")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                    {auditLogs.length === 0 ? translateUi("No audit records yet.") : translateUi("No records match the selected filters.")}
                  </td></tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-6 py-3 text-slate-600 font-mono text-xs">{log.timestamp}</td>
                      <td className="px-6 py-3"><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{log.action}</span></td>
                      <td className="px-6 py-3 text-slate-800">{log.user}</td>
                      <td className="px-6 py-3 text-slate-600 font-mono">{log.company_code || '—'}</td>
                      <td className="px-6 py-3 text-slate-600">{log.company_name || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
        })()}
      </div>
    </div>
  );
};

const SystemAdminDashboard = ({
  currentUserEmail,
  isMobileOrTablet,
  isDarkMode = false,
  onOpenLogs,
  welcomeName,
}: {
  currentUserEmail?: string;
  isMobileOrTablet?: boolean;
  isDarkMode?: boolean;
  onOpenLogs?: () => void;
  welcomeName?: string | null;
}) => {
  const [overview, setOverview] = useState<{
    totalCompanies: number;
    totalUsers: number;
    totalEmployees: number;
    companiesByStatus: Record<string, number>;
    recentLogins7d: number;
  } | null>(null);
  const [auditLogs, setAuditLogs] = useState<{ id: number; action: string; user: string; details: string | null; timestamp: string; company_code?: string | null; company_name?: string | null }[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<{ email: string; company_code: string | null; full_name: string | null; last_seen: string }[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name?: string; contactName?: string; contactEmail?: string; contactPhone?: string; status: string; createdAt?: string }[]>([]);
  const [statsCompanyFilter, setStatsCompanyFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [titleShowsArrival, setTitleShowsArrival] = useState(() => !hasSessionDashboardArrivalPlayed());
  const companyNamesById = useMemo(
    () => new Map(companies.map((c) => [c.id, c.name?.trim() || ''])),
    [companies]
  );
  const platformCompanyCode = useMemo(
    () =>
      detectCmsProviderCompanyCodeFromCompanyList(
        companies.map((c) => ({ id: c.id, name: c.name?.trim() || '' }))
      ),
    [companies]
  );
  const excludePlatformCompanyFromStats = statsCompanyFilter === 'all' && Boolean(platformCompanyCode);
  const isPlatformCompanyCode = (code: string | null | undefined) => {
    if (!excludePlatformCompanyFromStats) return false;
    return String(code || '').trim().toLowerCase() === platformCompanyCode.trim().toLowerCase();
  };
  const filteredOnlineUsers = useMemo(
    () =>
      excludePlatformCompanyFromStats
        ? onlineUsers.filter((u) => !isPlatformCompanyCode(u.company_code))
        : onlineUsers,
    [onlineUsers, excludePlatformCompanyFromStats, platformCompanyCode]
  );
  const computedOverview = useMemo(() => {
    if (overview) {
      return {
        totalCompanies: overview.totalCompanies,
        totalUsers: overview.totalUsers,
        totalEmployees: overview.totalEmployees,
        recentLogins7d: overview.recentLogins7d,
      };
    }
    const selectedCompany = statsCompanyFilter === 'all' ? null : statsCompanyFilter;
    const normalize = (v: string | null | undefined) => String(v || '').trim().toLowerCase();
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const recentLogins7d = auditLogs.filter((log) => {
      if (log.action !== 'LOGIN_SUCCESS') return false;
      const ts = new Date(log.timestamp).getTime();
      if (!Number.isFinite(ts) || now - ts > sevenDaysMs) return false;
      if (excludePlatformCompanyFromStats && isPlatformCompanyCode(log.company_code)) return false;
      if (!selectedCompany) return true;
      return normalize(log.company_code) === normalize(selectedCompany);
    }).length;
    const customerCompanies = excludePlatformCompanyFromStats
      ? companies.filter((c) => !isPlatformCompanyCode(c.id))
      : companies;
    return {
      totalCompanies: selectedCompany
        ? (companies.some((c) => normalize(c.id) === normalize(selectedCompany)) ? 1 : 0)
        : customerCompanies.length,
      totalUsers: 0,
      totalEmployees: 0,
      recentLogins7d,
    };
  }, [overview, statsCompanyFilter, companies, auditLogs, excludePlatformCompanyFromStats, platformCompanyCode]);
  const lastLoggedIn = useMemo(
    () =>
      auditLogs
        .filter((log) => log.action === 'LOGIN_SUCCESS')
        .sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || '')))
        .slice(0, 10),
    [auditLogs]
  );

  useEffect(() => {
    if (!currentUserEmail) return;
    const headers: HeadersInit = { 'X-User-Email': currentUserEmail };
    const load = async () => {
      const overviewUrlBase = statsCompanyFilter === 'all' ? '/api/system-overview' : `/api/system-overview?companyCode=${encodeURIComponent(statsCompanyFilter)}`;
      const overviewUrl = `${overviewUrlBase}${overviewUrlBase.includes('?') ? '&' : '?'}_=${Date.now()}`;
      try {
        const [ovRes, logsRes, onlineRes, compsRes] = await Promise.allSettled([
          fetch(overviewUrl, { headers, cache: 'no-store' }).then(async (r) => {
            if (!r.ok) throw new Error(`system-overview ${r.status}`);
            return r.json();
          }),
          fetch('/api/audit-logs?limit=100', { headers }).then(r => r.json()),
          fetch(
            statsCompanyFilter === 'all'
              ? '/api/presence/online'
              : `/api/presence/online?companyCode=${encodeURIComponent(statsCompanyFilter)}`,
            { headers }
          ).then(r => r.json()),
          fetch('/api/companies', { headers }).then(r => r.json()),
        ]);
        if (ovRes.status === 'fulfilled') setOverview(ovRes.value);
        if (logsRes.status === 'fulfilled') setAuditLogs(Array.isArray(logsRes.value) ? logsRes.value : []);
        if (onlineRes.status === 'fulfilled') setOnlineUsers(Array.isArray(onlineRes.value) ? onlineRes.value : []);
        if (compsRes.status === 'fulfilled') setCompanies(Array.isArray(compsRes.value) ? compsRes.value : []);
        if (ovRes.status === 'rejected' || logsRes.status === 'rejected' || onlineRes.status === 'rejected' || compsRes.status === 'rejected') {
          console.warn('System admin dashboard partial load failure', { ovRes, logsRes, onlineRes, compsRes });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [currentUserEmail, statsCompanyFilter]);

  useEffect(() => {
    if (loading) return;
    if (!titleShowsArrival) return;
    const id = window.setTimeout(() => {
      setTitleShowsArrival(false);
      markSessionDashboardArrivalPlayed();
    }, 5000);
    return () => window.clearTimeout(id);
  }, [loading, titleShowsArrival]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const trimmedAdminWelcome = welcomeName?.trim();
  const systemAdminTitleLine = trimmedAdminWelcome
    ? translateUi("Welcome {{name}} to your System Administrator Dashboard", { name: trimmedAdminWelcome })
    : translateUi("Welcome to your System Administrator Dashboard");

  const sd = !!isDarkMode;
  const glassTile = cn(DASHBOARD_GLASS_TILE, isMobileOrTablet ? "p-4" : "p-5");
  const glassBtn = cn(DASHBOARD_GLASS_TILE_INTERACTIVE, "text-left w-full", isMobileOrTablet ? "p-4" : "p-4 sm:p-5");

  return (
    <div className={cn("space-y-5 sm:space-y-6", isMobileOrTablet && "px-1")}>
      <div>
        <h1
          className={cn(
            "font-bold tracking-tight",
            sd ? "text-slate-50" : "text-slate-900",
            isMobileOrTablet ? "text-xl sm:text-2xl" : "text-3xl"
          )}
        >
          {titleShowsArrival ? translateUi("You have arrived") : systemAdminTitleLine}
        </h1>
        <p className={cn("mt-1", sd ? "text-slate-400" : "text-slate-500", isMobileOrTablet && "text-sm")}>
          {translateUi("System-wide overview for diagnosing access and system information.")}
        </p>
      </div>

      <div className={cn("flex flex-wrap items-center gap-3", isMobileOrTablet && "flex-col items-stretch")}>
        <span className={cn("text-sm font-medium", sd ? "text-slate-300" : "text-slate-600")}>{translateUi("Filter stats by company:")}</span>
        <select
          value={statsCompanyFilter}
          onChange={e => setStatsCompanyFilter(e.target.value)}
          className={cn(
            "px-4 py-2 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none border",
            sd
              ? "bg-slate-900 border-slate-600 text-slate-100"
              : "bg-white border-slate-200 text-slate-700 focus:border-indigo-500",
            isMobileOrTablet && "w-full min-h-[44px]"
          )}
        >
          <option value="all">{translateUi("All companies")}</option>
          {companies
            .slice()
            .sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''))
            .map(c => (
              <option key={c.id} value={c.id}>
                {c.name ? `${c.name} (${c.id})` : `Company ${c.id}`}
              </option>
            ))}
        </select>
      </div>

      <DashboardGradientSection
        title={translateUi("System overview")}
        subtitle={translateUi("Live platform counts")}
        icon={Shield}
      >
        <div
          className={cn(
            "grid gap-3 sm:gap-4",
            isMobileOrTablet ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 xl:grid-cols-5"
          )}
        >
          {[
            {
              label: translateUi("Companies"),
              value: computedOverview.totalCompanies,
              icon: Briefcase,
              iconWrap: "bg-indigo-500/30 text-indigo-100",
            },
            {
              label: translateUi("Total Users"),
              value: computedOverview.totalUsers,
              icon: Users,
              iconWrap: "bg-slate-500/35 text-slate-100",
            },
            {
              label: translateUi("Total Employees"),
              value: computedOverview.totalEmployees,
              icon: User,
              iconWrap: "bg-emerald-500/30 text-emerald-100",
            },
            {
              label: translateUi("Logins (7 days)"),
              value: computedOverview.recentLogins7d,
              icon: LogIn,
              iconWrap: "bg-amber-500/30 text-amber-100",
            },
            {
              label: translateUi("Users Online"),
              value: filteredOnlineUsers.length,
              icon: null as React.ComponentType<{ className?: string }> | null,
              iconWrap: "bg-emerald-500/30",
              pulse: true,
            },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className={glassTile}>
                <div className="relative z-10 flex items-center gap-3">
                  <div className={cn("rounded-xl p-2.5 shadow-sm ring-1 ring-white/15", stat.iconWrap)}>
                    {stat.pulse ? (
                      <div className="w-5 h-5 rounded-full bg-emerald-400 animate-pulse" title={translateUi("Online")} />
                    ) : Icon ? (
                      <Icon className="w-5 h-5" />
                    ) : null}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">{stat.label}</p>
                    <p className={cn("font-bold text-white tabular-nums", isMobileOrTablet ? "text-xl" : "text-2xl")}>
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={cn("mt-4 flex flex-wrap gap-3", isMobileOrTablet && "flex-col")}>
          <button
            type="button"
            onClick={() => onOpenLogs?.()}
            className={cn(glassBtn, !isMobileOrTablet && "min-w-[200px] max-w-xs")}
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-sky-500/30 p-2.5 text-sky-100 ring-1 ring-white/15">
                <FileText className="w-5 h-5" />
              </div>
              <p className="font-bold text-white uppercase tracking-wider text-sm sm:text-base">{translateUi("Sys Logs")}</p>
            </div>
          </button>
        </div>
      </DashboardGradientSection>

      <SystemAdminPerformancePanes
        key={statsCompanyFilter}
        currentUserEmail={currentUserEmail}
        isMobileOrTablet={isMobileOrTablet}
        companyCode={statsCompanyFilter === "all" ? undefined : statsCompanyFilter}
        companyName={
          statsCompanyFilter === "all"
            ? undefined
            : companyNamesById.get(statsCompanyFilter) || undefined
        }
      />

      {filteredOnlineUsers.length > 0 && (
        <DashboardGradientSection
          title={translateUi("Current Users Online")}
          subtitle={translateUi("Users active in the last 2 minutes. Refreshes every 15 seconds.")}
          icon={Users}
        >
          {isMobileOrTablet ? (
            <div className="divide-y divide-white/10 rounded-xl border border-white/10 overflow-hidden">
              {filteredOnlineUsers.map((u) => (
                <div key={u.email} className="px-4 py-3 bg-white/[0.04]">
                  <p className="font-medium text-white">{u.full_name || u.email}</p>
                  <p className="text-xs text-slate-300 font-mono truncate">{u.email}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {(u.company_code && (companyNamesById.get(u.company_code) || u.company_code)) || '—'} · {formatDateTimeUKShort(u.last_seen)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-white/[0.06] border-b border-white/10 text-slate-300 font-semibold">
                    <th className="px-5 py-3">{translateUi("User")}</th>
                    <th className="px-5 py-3">{translateUi("Email")}</th>
                    <th className="px-5 py-3">{translateUi("Company")}</th>
                    <th className="px-5 py-3">{translateUi("Last seen")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredOnlineUsers.map((u) => (
                    <tr key={u.email} className="hover:bg-white/[0.05]">
                      <td className="px-5 py-3 text-slate-100">{u.full_name || u.email}</td>
                      <td className="px-5 py-3 text-slate-300 font-mono text-xs">{u.email}</td>
                      <td className="px-5 py-3 text-slate-300">{(u.company_code && (companyNamesById.get(u.company_code) || u.company_code)) || '—'}</td>
                      <td className="px-5 py-3 text-slate-400 text-xs">{formatDateTimeUKShort(u.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardGradientSection>
      )}
    </div>
  );
};

const DashboardHeader = ({
  totalEmployees,
  isMobileOrTablet,
  welcomeName,
  isDarkMode,
}: {
  totalEmployees: number;
  isMobileOrTablet?: boolean;
  welcomeName?: string | null;
  isDarkMode: boolean;
}) => {
  const [titleShowsArrival, setTitleShowsArrival] = useState(() => !hasSessionDashboardArrivalPlayed());

  useEffect(() => {
    if (!titleShowsArrival) return;
    const id = window.setTimeout(() => {
      setTitleShowsArrival(false);
      markSessionDashboardArrivalPlayed();
    }, 5000);
    return () => window.clearTimeout(id);
  }, [titleShowsArrival]);

  const trimmedWelcome = welcomeName?.trim();
  const welcomeLine = trimmedWelcome
    ? translateUi("Welcome {{name}} to your Rail Intel Dashboard", { name: trimmedWelcome })
    : translateUi("Welcome to your Rail Intel Dashboard");

  return (
    <div className={cn(
      "flex flex-col gap-5 mb-8",
      isMobileOrTablet ? "mb-6 sm:mb-8" : ""
    )}>
      <div className={cn(
        "flex justify-between gap-4",
        isMobileOrTablet ? "flex-col md:flex-row md:items-end" : "flex-row items-end"
      )}>
        <div>
          <h1
            className={cn(
              "font-bold tracking-tight",
              isMobileOrTablet ? "text-2xl sm:text-3xl" : "text-3xl",
              isDarkMode ? "text-slate-50" : "text-slate-900"
            )}
          >
            {titleShowsArrival ? translateUi("You have arrived") : welcomeLine}
          </h1>
          <p
            className={cn(
              isMobileOrTablet ? "text-sm sm:text-base" : "",
              isDarkMode ? "text-slate-300" : "text-slate-500"
            )}
          >
            {translateUi("Real-time compliance monitoring for safety-critical staff.")}
          </p>
        </div>
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl py-3 flex items-center shrink-0",
            isMobileOrTablet ? "px-4 sm:px-5 gap-3" : "px-5 space-x-3",
            isDarkMode
              ? "border border-slate-500 bg-slate-800/95 shadow-lg shadow-black/40 ring-1 ring-slate-500/50"
              : "border-2 border-white/65 bg-white/45 shadow-[0_20px_48px_rgba(15,23,42,0.08)] ring-1 ring-sky-100/35 backdrop-blur-md"
          )}
        >
          {!isDarkMode && (
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-sky-100/12"
              aria-hidden
            />
          )}
          <div
            className={cn(
              "relative z-[1] p-2.5 rounded-xl",
              isDarkMode ? "bg-indigo-500/35" : "bg-indigo-500/15"
            )}
          >
            <Users className={cn("w-5 h-5", isDarkMode ? "text-indigo-200" : "text-indigo-600")} />
          </div>
          <div className="relative z-[1]">
            <p
              className={cn(
                "text-[10px] font-bold uppercase tracking-wider",
                isDarkMode ? "text-slate-300" : "text-slate-500"
              )}
            >
              Total Employees
            </p>
            <p className={cn("text-xl font-bold tabular-nums", isDarkMode ? "text-white" : "text-slate-900")}>
              {Number(totalEmployees) || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = translateUi("Confirm"),
  cancelLabel = translateUi("Cancel"),
  confirmButtonClassName = 'px-4 py-2 bg-rose-600 text-white hover:bg-rose-700 rounded-lg font-medium transition-colors',
}: {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonClassName?: string;
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl p-4 sm:p-6 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 mb-6 whitespace-pre-line">{message}</p>
        <div className="flex justify-end space-x-3">
          <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors">
            {cancelLabel}
          </button>
          <button onClick={onConfirm} className={confirmButtonClassName}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/** True if this employee already has a live row for the same cycle template (cycle_id). */
function employeeHasLiveCycleForTemplate(
  liveCycles: ReadonlyArray<{ cycle_id?: number | string | null }>,
  templateCycleId: number | string | null | undefined
): boolean {
  if (templateCycleId == null || String(templateCycleId).trim() === '') return false;
  const want = String(templateCycleId);
  return liveCycles.some((ec) => String(ec.cycle_id ?? '') === want);
}

const CloseEmployeeCycleDialog = ({
  isOpen,
  cycleTitle,
  confirming,
  onCancel,
  onConfirm,
  variant = 'manual',
}: {
  isOpen: boolean;
  cycleTitle: string;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** post_assessment: shown after finishing the last assessment event */
  variant?: 'manual' | 'post_assessment';
}) => {
  if (!isOpen) return null;
  const post = variant === 'post_assessment';
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4"
      onClick={() => !confirming && onCancel()}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rv-close-cycle-title"
      >
        <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 px-6 py-5 text-white">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-white/15 p-2.5 shrink-0 border border-white/20">
              <Archive className="w-6 h-6" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/85">
                {post ? translateUi("Last assessment complete") : 'Move to closed cycles'}
              </p>
              <h2 id="rv-close-cycle-title" className="text-lg font-bold leading-snug mt-0.5 truncate">
                {cycleTitle}
              </h2>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm text-slate-700 leading-relaxed">
          {post ? (
            <p>
              All assessment events on this period are finished. Do you want to <strong>close this cycle now</strong> and
              move it to <strong>Closed Cycles</strong>?
            </p>
          ) : (
            <p>
              This live assignment will be <strong>archived</strong> and listed under <strong>Closed Cycles</strong>. Assessment
              events, grades, and evidence stay on record for read-only review.
            </p>
          )}
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
            <p className="font-semibold text-amber-900 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
              {post ? translateUi("You will need to assign a new cycle manually") : translateUi("Plan the next competency period")}
            </p>
            <p className="mt-1.5 text-sm text-amber-900/90">
              {post ? (
                <>
                  This assignment is not continuous, so it will not renew automatically. If the role still needs an active
                  competency cycle, assign a <strong>new dated period</strong> from Live Cycles after closing.
                </>
              ) : (
                <>
                  If this role still needs an active competency cycle, assign a <strong>new dated period</strong> from Live Cycles
                  when you are ready. Only one <em>live</em> cycle of the same is allowed at a time — after this cycle is
                  closed you can assign the same cycle again.
                </>
              )}
            </p>
          </div>
          {!post ? (
            <ul className="list-none space-y-2 text-slate-600">
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                <span>Closing is only available when every assessment event on this cycle is marked complete.</span>
              </li>
              <li className="flex gap-2">
                <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" aria-hidden />
                <span>
                  Use <strong> Remove </strong> only if you need to delete a mistaken assignment before any work is recorded — it
                  is not a substitute for a proper close when assessments are finished.
                </span>
              </li>
            </ul>
          ) : (
            <p className="text-slate-600">
              Choose <strong>{translateUi("Not now")}</strong> to leave this completed cycle live — you can close it later from Live Cycles.
            </p>
          )}
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button
            type="button"
            disabled={confirming}
            onClick={onCancel}
            className="px-4 py-2.5 rounded-xl font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 min-h-[44px]"
          >
            {post ? translateUi("Not now") : translateUi("Cancel")}
          </button>
          <button
            type="button"
            disabled={confirming}
            onClick={onConfirm}
            className="px-4 py-2.5 rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 min-h-[44px] shadow-sm"
          >
            {confirming ? 'Closing…' : translateUi("Close cycle")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContinueEmployeeCycleDialog = ({
  isOpen,
  cycleTitle,
  confirming,
  onCancel,
  onConfirm,
  variant = 'manual',
  startFromToday = false,
}: {
  isOpen: boolean;
  cycleTitle: string;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** post_assessment: shown after finishing the last assessment event */
  variant?: 'manual' | 'post_assessment';
  /** When true, the new period starts today (not the day after current expiry). */
  startFromToday?: boolean;
}) => {
  if (!isOpen) return null;
  const post = variant === 'post_assessment';
  const todayLabel = new Date().toLocaleDateString('en-GB');
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4"
      onClick={() => !confirming && onCancel()}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rv-continue-cycle-title"
      >
        <div className="bg-gradient-to-br from-sky-600 via-cyan-600 to-teal-700 px-6 py-5 text-white">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-white/15 p-2.5 shrink-0 border border-white/20">
              <RotateCcw className="w-6 h-6" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/85">
                {post ? translateUi("Last assessment complete") : 'Continue continuous cycle'}
              </p>
              <h2 id="rv-continue-cycle-title" className="text-lg font-bold leading-snug mt-0.5 truncate">
                {cycleTitle}
              </h2>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm text-slate-700 leading-relaxed">
          {post ? (
            <p>
              This assignment is <strong>continuous</strong>. Do you want to <strong>renew the cycle from today</strong>
              {startFromToday ? ` (${todayLabel})` : ''}? The current period will move to <strong>Closed Cycles</strong>, and a
              new live period will start with the same assessment events.
            </p>
          ) : (
            <p>
              This period will be <strong>closed</strong> and listed under <strong>Closed Cycles</strong>. A new live period
              will start with the same assessment schedule
              {startFromToday ? (
                <>
                  {' '}
                  from <strong>today ({todayLabel})</strong>
                </>
              ) : (
                ' (from the day after the current expiry)'
              )}
              .
            </p>
          )}
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sky-950">
            <p className="font-semibold text-sky-900 flex items-center gap-2 text-sm">
              <Info className="w-4 h-4 shrink-0" aria-hidden />
              CDP monitoring carries over
            </p>
            <p className="mt-1.5 text-sm text-sky-900/90">
              Unfinished CDP monitoring events move into the new period. They do not block renewal.
            </p>
          </div>
          <ul className="list-none space-y-2 text-slate-600">
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
              <span>A live F1/F2 medical is required to renew.</span>
            </li>
            <li className="flex gap-2">
              <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" aria-hidden />
              <span>
                {post
                  ? 'Choose Not now to leave this completed cycle live — you can renew later from Live Cycles.'
                  : 'Assessment evidence and grades for the current period stay available from Closed Cycles.'}
              </span>
            </li>
          </ul>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button
            type="button"
            disabled={confirming}
            onClick={onCancel}
            className="px-4 py-2.5 rounded-xl font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 min-h-[44px]"
          >
            {post ? translateUi("Not now") : translateUi("Cancel")}
          </button>
          <button
            type="button"
            disabled={confirming}
            onClick={onConfirm}
            className="px-4 py-2.5 rounded-xl font-semibold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 min-h-[44px] shadow-sm"
          >
            {confirming ? 'Renewing…' : post ? 'Renew from today' : translateUi("Continue cycle")}
          </button>
        </div>
      </div>
    </div>
  );
};

const titles = [
  "Company Administrator",
  "System Administrator",
  "Tech Support Agent",
  "Train Driver",
  "Trainee Train Driver",
  "Assurance Manager",
  "Instructor",
  "Driver Instructor",
  "Driving Instructor",
  "Driver Manager",
  "Head of Drivers",
  "Operations Trainer",
  "Other",
  "Senior Driver Instructor",
];

function teamRoleTitleOptions(storedTitle?: string | null): string[] {
  const value = String(storedTitle ?? "").trim();
  if (!value || titles.includes(value)) return titles;
  return [value, ...titles];
}

/** Profile picture: display, upload, delete. */
const ProfilePictureSection = ({
  profileImageUrl,
  userEmail,
  onUpdated,
}: {
  profileImageUrl?: string;
  userEmail: string;
  onUpdated: (newUrl: string | null) => void;
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setImageLoadFailed(false);
  }, [profileImageUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    e.target.value = "";
    setError(null);
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      const res = await fetch("/api/users/me/avatar", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Email": userEmail },
        body: JSON.stringify({ image: dataUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Upload failed");
      const newUrl = data.profile_image_url ?? null;
      onUpdated(newUrl);
    } catch (err: any) {
      setError(err?.message ?? translateUi("Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      const res = await fetch("/api/users/me/avatar", {
        method: "DELETE",
        headers: { "X-User-Email": userEmail, "Content-Type": "application/json" },
        body: JSON.stringify({ userEmail }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Delete failed");
      }
      onUpdated(null);
    } catch (err: any) {
      setError(err?.message ?? translateUi("Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="w-28 h-28 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
        {profileImageUrl && !imageLoadFailed ? (
          <img src={profileImageUrl} alt={translateUi("Profile")} className="w-full h-full object-cover" onError={() => setImageLoadFailed(true)} />
        ) : (
          <User className="w-14 h-14 text-slate-400" />
        )}
      </div>
      {error && <p className="text-red-600 text-sm text-center">{error}</p>}
      <div className="flex flex-wrap justify-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 disabled:opacity-50 flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading…" : translateUi("Upload")}
        </button>
        {profileImageUrl && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 disabled:opacity-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? translateUi("Deleting…") : translateUi("Delete picture")}
          </button>
        )}
      </div>
    </div>
  );
};

type CompanyActivationSuccessState = {
  companyName: string;
  companyCode: string;
  nominatedAdminName: string;
  nominatedAdminEmail: string;
  nominatedAdminJobTitle: string;
  emailSent: boolean;
  emailWarning?: string;
  /** Nominee is the existing company contact — no new login/welcome email. */
  usedExistingContactLogin?: boolean;
};

const CompanyActivationSuccessPanel = ({
  detail,
  isMobileOrTablet,
  isDarkMode = false,
  onContinue,
  onSignOut,
  onResendWelcomeEmail,
  resendEmailLoading = false,
  resendEmailMessage,
}: {
  detail: CompanyActivationSuccessState;
  isMobileOrTablet?: boolean;
  isDarkMode?: boolean;
  onContinue: () => void;
  onSignOut: () => void;
  onResendWelcomeEmail?: () => void;
  resendEmailLoading?: boolean;
  resendEmailMessage?: string | null;
}) => (
  <div
    key="company-activation-success"
    className={cn("mx-auto w-full max-w-2xl relative z-10", isMobileOrTablet ? "px-1" : "")}
  >
    <div
      className={cn(
        "rounded-3xl border shadow-2xl ring-1 overflow-hidden",
        isDarkMode ? "border-slate-700 bg-slate-900 ring-slate-800/80" : "border-slate-200 bg-white ring-slate-900/5"
      )}
    >
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 px-6 py-8 sm:px-8 sm:py-10 text-center text-white">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/40">
          <CheckCircle2 className="h-9 w-9" aria-hidden />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Company activated</h2>
        <p className="mt-2 text-sm sm:text-base text-emerald-50/95 leading-relaxed">
          <strong className="font-semibold text-white">{detail.companyName}</strong>
          {detail.companyCode ? (
            <span className="text-emerald-100/90"> ({detail.companyCode})</span>
          ) : null}{" "}
          is now active on Rail Intel.
        </p>
      </div>
      <div className="px-6 py-6 sm:px-8 sm:py-8 space-y-5">
        <p className={cn("text-base sm:text-lg leading-relaxed text-center", isDarkMode ? "text-slate-200" : "text-slate-800")}>
          Thank you for activating your company. We are pleased to have you onboard and welcome you to{" "}
          <strong className={cn("font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>{translateUi("Rail Intel")}</strong> — the most advanced rail competency
          management system.
        </p>
        <div className={cn("rounded-2xl border p-4 sm:p-5 space-y-3", isDarkMode ? "border-slate-600 bg-slate-800/80" : "border-slate-200 bg-slate-50")}>
          <p className={cn("text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>What happens next</p>
          <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-700")}>
            <strong>{detail.nominatedAdminName}</strong>
            {detail.nominatedAdminJobTitle ? (
              <span className={isDarkMode ? "text-slate-400" : "text-slate-600"}> ({detail.nominatedAdminJobTitle})</span>
            ) : null}{" "}
            will sign in as company administrator and complete operational setup in{" "}
            <strong> Administration </strong> — competency frameworks, grading scale, standards, and related
            configuration before teams rely on assessments and records.
          </p>
          <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-600")}>
            Nominated administrator:{" "}
            <a href={`mailto:${detail.nominatedAdminEmail}`} className={cn("font-medium", isDarkMode ? "text-indigo-300 hover:text-indigo-200" : "text-indigo-600 hover:text-indigo-800")}>
              {detail.nominatedAdminEmail}
            </a>
          </p>
        </div>
        {detail.usedExistingContactLogin ? (
          <div className={cn("rounded-2xl border px-4 py-3 text-sm leading-relaxed", isDarkMode ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-100" : "border-emerald-200 bg-emerald-50 text-emerald-900")}>
            The company contact was assigned as company administrator. They already had login details from the company invite, so no new welcome email or temporary password was sent.
          </div>
        ) : detail.emailSent ? (
          <div className={cn("rounded-2xl border px-4 py-3 text-sm leading-relaxed", isDarkMode ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-100" : "border-emerald-200 bg-emerald-50 text-emerald-900")}>
            A welcome email with company code and temporary password has been sent to the nominated administrator. They
            must set a new password on first sign-in.
          </div>
        ) : (
          <div className={cn("rounded-2xl border px-4 py-3 text-sm leading-relaxed space-y-3", isDarkMode ? "border-amber-500/40 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-950")}>
            <div>
              <p className="font-semibold">Welcome email could not be sent automatically.</p>
              <p className="mt-1">
                {detail.emailWarning ||
                  "The welcome email could not be delivered. Use Resend welcome email below, or share the company code and temporary password securely with the nominated administrator."}
              </p>
              <p className={cn("mt-2 text-xs", isDarkMode ? "text-amber-200/90" : "text-amber-900/90")}>
                Until email works, share the company code and a temporary password with the nominated administrator
                securely. Their login was still created when possible.
              </p>
            </div>
            {onResendWelcomeEmail && (
              <button
                type="button"
                disabled={resendEmailLoading}
                onClick={() => void onResendWelcomeEmail()}
                className={cn("w-full py-2.5 rounded-xl border text-sm font-semibold disabled:opacity-60", isDarkMode ? "border-amber-500/50 bg-slate-800 text-amber-50 hover:bg-slate-700" : "border-amber-300 bg-white text-amber-950 hover:bg-amber-100/80")}
              >
                {resendEmailLoading ? translateUi("Sending welcome email…") : translateUi("Resend welcome email")}
              </button>
            )}
            {resendEmailMessage && <p className={cn("text-xs font-medium", isDarkMode ? "text-amber-200" : "text-amber-900")}>{resendEmailMessage}</p>}
          </div>
        )}
        <div className={cn("rounded-2xl border px-4 py-4 text-sm leading-relaxed", isDarkMode ? "border-indigo-500/35 bg-indigo-950/40 text-indigo-100" : "border-indigo-100 bg-indigo-50/80 text-indigo-950")}>
          <p className={cn("font-semibold", isDarkMode ? "text-indigo-200" : "text-indigo-900")}>Your onboarding is complete</p>
          <p className="mt-1">
            Continue to your dashboard, or sign out and leave operational configuration with your nominated company administrator.
          </p>
        </div>
        <div className="flex flex-row gap-3">
          <button
            type="button"
            onClick={onContinue}
            className="flex-1 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-200/50 transition-colors"
          >
            Continue to dashboard
          </button>
          <button
            type="button"
            onClick={() => void onSignOut()}
            className={cn(
              "flex-1 py-3.5 rounded-xl border font-semibold transition-colors",
              isDarkMode
                ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                : "border-slate-200 text-slate-700 hover:bg-slate-50"
            )}
          >
            {translateUi("Sign out")}
          </button>
        </div>
      </div>
    </div>
  </div>
);


/** Change password form for Account profile page (Azure SQL password_hash). */
const ChangePasswordSection = ({ userEmail, noCard = false }: { userEmail: string; noCard?: boolean }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }
    if (!userEmail) {
      setError("Not signed in");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/users/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Email": userEmail },
        body: JSON.stringify({ email: userEmail, currentPassword, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to update password");
      try {
        await fetch("/api/users/me/password-changed", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-User-Email": userEmail },
        });
      } catch (_) {}
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to update password";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <>
      <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
        <KeyRound className="w-6 h-6 text-indigo-600" />
        {translateUi("Change password")}
      </h2>
      <p className="text-slate-600 text-sm mb-6">Set a new password for your account. You’ll need to enter your current password.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{error}</div>
        )}
        {success && (
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            Password updated. Use your new password next time you sign in.
          </div>
        )}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Current password")}</label>
          <input
            type="password"
            required
            autoComplete="current-password"
            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
            placeholder="Enter your current password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("New password")}</label>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
            placeholder="At least 8 characters"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Confirm new password</label>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
            placeholder="Repeat new password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
        </div>
        <div className="pt-2">
          <button type="submit" disabled={loading} className="px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all text-sm">
            {loading ? translateUi("Updating…") : translateUi("Update password")}
          </button>
        </div>
      </form>
    </>
  );
  if (noCard) return <div className="min-w-0">{formContent}</div>;
  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 w-full">
      <div className="max-w-2xl">{formContent}</div>
    </div>
  );
};

/** Shared profile form used in both modal and account profile page. System Access Level is only editable when canEditSystemAccessLevel is true (company admins). */
const AccountProfilePage = ({
  profile,
  companyName,
  companyCode,
  canEditSystemAccessLevel = false,
  onSave,
}: {
  profile: SystemUser;
  companyName?: string;
  companyCode?: string;
  canEditSystemAccessLevel?: boolean;
  onSave: (updates: { full_name: string; email: string; role: string; title: string }) => void | Promise<void>;
}) => {
  const [formData, setFormData] = useState({
    full_name: profile.full_name,
    email: profile.email,
    role: profile.role,
    title: profile.title || "Company Administrator",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const updates = canEditSystemAccessLevel ? formData : { ...formData, role: profile.role };
      await onSave(updates);
      setSaved(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Full Name")}</label>
        <input
          type="text"
          required
          className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
          value={formData.full_name}
          onChange={e => setFormData({ ...formData, full_name: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Email Address")}</label>
        <input
          type="email"
          required
          className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Role Title")}</label>
        <select
          className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
          value={formData.title}
          onChange={e => setFormData({ ...formData, title: e.target.value })}
        >
          {titles.map(t => <option key={t} value={t}>{translateUi(t)}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("System Access Level")}</label>
        {canEditSystemAccessLevel ? (
          <select
            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
            value={formData.role}
            onChange={e => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="Read Only">{translateUi("Read Only (View records only)")}</option>
            <option value="Edit">{translateUi("Edit (Can modify records & managers)")}</option>
          </select>
        ) : (
          <>
            <p className="text-slate-700">
              {formData.role === 'Edit' ? translateUi("Edit (Can modify records & managers)") : translateUi("Read Only (View records only)")}
            </p>
            <p className="text-xs text-slate-400 mt-1">Set by your company administrator</p>
          </>
        )}
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Company Name")}</label>
        <div className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 cursor-not-allowed">
          {companyName || '—'}
        </div>
        <p className="text-xs text-slate-400 mt-1">Set during company onboarding and cannot be changed</p>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Company Code")}</label>
        <div className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 font-mono cursor-not-allowed">
          {companyCode || '—'}
        </div>
        <p className="text-xs text-slate-400 mt-1">Set during company onboarding and cannot be changed</p>
      </div>
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-sm">{error}</div>
      )}
      {saved && (
        <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          Profile updated.
        </div>
      )}
      <div className="pt-4">
        <button type="submit" disabled={saving} className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all">
          {saving ? 'Saving…' : translateUi("Save Changes")}
        </button>
      </div>
    </form>
  );
};

const MyProfileModal = ({
  profile,
  companyName,
  companyCode,
  canEditSystemAccessLevel = false,
  onClose,
  onSave,
}: {
  profile: SystemUser;
  companyName?: string;
  companyCode?: string;
  canEditSystemAccessLevel?: boolean;
  onClose: () => void;
  onSave: (updates: { full_name: string; email: string; role: string; title: string }) => void | Promise<void>;
}) => {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[100]" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900">My Profile</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <AccountProfilePage profile={profile} companyName={companyName} companyCode={companyCode} canEditSystemAccessLevel={canEditSystemAccessLevel} onSave={async (updates) => { await onSave(updates); onClose(); }} />
      </div>
    </div>
  );
};

const EarlyAssessmentReasonModal = ({
  event,
  onProceed,
  onCancel,
  isMobileOrTablet = false,
  isDarkMode = false,
}: {
  event: EmployeeCycleEventSummary;
  onProceed: (reasons: string) => Promise<void>;
  onCancel: () => void;
  isMobileOrTablet?: boolean;
  isDarkMode?: boolean;
}) => {
  const [reasons, setReasons] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const canProceed = reasons.trim().length > 0;
  const handleProceed = async () => {
    if (!canProceed || submitting) return;
    setSubmitting(true);
    try {
      await onProceed(reasons.trim());
    } finally {
      setSubmitting(false);
    }
  };
  const panelBg = isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const headerBg = isDarkMode ? 'bg-amber-950/40 border-slate-700' : 'bg-amber-50 border-slate-200';
  const headerTitle = isDarkMode ? "text-amber-200" : "text-amber-900";
  const headerBody = isDarkMode ? 'text-amber-100/90' : 'text-amber-800';
  const labelText = isDarkMode ? "text-slate-200" : "text-slate-700";
  const footerBg = isDarkMode ? 'bg-slate-950/80 border-slate-700' : 'bg-slate-50 border-slate-200';
  const inputCls = cn(
    'w-full rounded-xl border outline-none transition-colors focus:ring-2 touch-manipulation resize-none',
    isMobileOrTablet ? "px-4 py-3.5 text-base min-h-[120px]" : "px-4 py-3 text-sm",
    isDarkMode
      ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/40'
      : 'border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-amber-500 focus:border-amber-500',
  );
  return (
    <div
      className={cn(
        'fixed inset-0 z-[200] flex bg-slate-900/60 backdrop-blur-sm',
        isMobileOrTablet ? "items-stretch p-0" : "items-center justify-center p-4",
      )}
      onClick={submitting ? undefined : onCancel}
    >
      <div
        className={cn(
          'w-full overflow-hidden border shadow-2xl flex flex-col',
          panelBg,
          isMobileOrTablet ? "h-full max-w-none rounded-none" : "max-w-md max-h-[92vh] rounded-2xl",
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="early-assessment-title"
      >
        <div className={cn('border-b shrink-0', headerBg, isMobileOrTablet ? "px-4 py-4 sm:px-6" : "px-6 py-4")}>
          <h3 id="early-assessment-title" className={cn('font-bold', headerTitle, isMobileOrTablet ? "text-base sm:text-lg" : "text-lg")}>
            Assessment not yet due
          </h3>
          <p className={cn('mt-1 leading-relaxed', headerBody, isMobileOrTablet ? "text-sm" : "text-sm")}>
            This assessment window opens on <strong>{formatDateUK(event.window_open)}</strong>. The assessment is not due until then.
          </p>
          <p className={cn('mt-2 leading-relaxed', headerBody, isMobileOrTablet ? "text-sm" : "text-sm")}>
            If you need to proceed with this assessment early, please state your reasons below. You must provide reasons before continuing.
          </p>
        </div>
        <div className={cn('flex-1 overflow-y-auto overscroll-contain', isMobileOrTablet ? "px-4 py-5 sm:px-6" : "p-6")}>
          <label htmlFor="early-assessment-reasons" className={cn('block font-medium mb-2', labelText, isMobileOrTablet ? "text-base" : "text-sm")}>
            Reasons for early assessment (required)
          </label>
          <textarea
            id="early-assessment-reasons"
            value={reasons}
            onChange={(e) => setReasons(e.target.value)}
            placeholder="e.g. Employee requested early assessment due to upcoming leave..."
            rows={isMobileOrTablet ? 5 : 4}
            className={inputCls}
          />
        </div>
        <div
          className={cn(
            'border-t shrink-0 flex gap-3',
            footerBg,
            isMobileOrTablet
              ? 'flex-col-reverse px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6'
              : 'flex-row justify-end px-6 py-4',
          )}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={cn(
              'font-medium rounded-xl border transition-colors disabled:opacity-50 touch-manipulation',
              isMobileOrTablet ? "min-h-[48px] px-5 py-3 text-base w-full sm:w-auto" : "px-4 py-2 text-sm",
              isDarkMode
                ? "border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
                : "border-transparent bg-transparent text-slate-600 hover:text-slate-800",
            )}
          > Cancel </button>
          <button
            type="button"
            onClick={handleProceed}
            disabled={!canProceed || submitting}
            className={cn(
              'font-medium text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation',
              isMobileOrTablet ? "min-h-[48px] px-5 py-3 text-base w-full sm:w-auto" : "px-4 py-2 text-sm",
              'bg-amber-600 hover:bg-amber-700',
            )}
          >
            {submitting ? translateUi("Saving…") : translateUi("Proceed with assessment")}
          </button>
        </div>
      </div>
    </div>
  );
};

function formatDurationSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  if (m > 0) return `${m}:${String(s).padStart(2, '0')}`;
  return `0:${String(s).padStart(2, '0')}`;
}

/** Saved on the assessment event row (RTDB) when criteria are ready for close-out summary. */
type AssessmentFinalFeedbackPayload = EmployeeCycleEventSummary['final_assessment_feedback'];

function isFinalAssessmentFeedbackSufficient(fb?: AssessmentFinalFeedbackPayload | null): boolean {
  const g = typeof fb?.general_observations === 'string' ? fb.general_observations.trim() : '';
  const p = typeof fb?.personal_attributes === 'string' ? fb.personal_attributes.trim() : '';
  const c = typeof fb?.candidate_feedback === 'string' ? fb.candidate_feedback.trim() : '';
  return g.length > 0 && p.length > 0 && c.length > 0;
}

function AssessmentFinalFeedbackPanel({
  eventId,
  eventStatus,
  eventType,
  initialFeedback,
  eligibilityComplete,
  canMutate,
  currentUserEmail,
  patchLocalEvent,
  onFinalizeAssessment,
  showUpdateButton = false,
  onRecordAmendment,
  isUpdatingAmendment = false,
}: {
  eventId: number | string;
  eventStatus: string;
  eventType?: string | null;
  initialFeedback?: AssessmentFinalFeedbackPayload | null;
  eligibilityComplete: boolean;
  canMutate: boolean;
  currentUserEmail?: string | null;
  patchLocalEvent: (fb: NonNullable<AssessmentFinalFeedbackPayload>) => void;
  /** When set for an incomplete event, invoked after feedback is saved successfully to mark the event complete. */
  onFinalizeAssessment?: () => Promise<void> | void;
  /** Completed assessments: record grade/notes amendments (same action as the former header Update). */
  showUpdateButton?: boolean;
  onRecordAmendment?: () => Promise<void> | void;
  isUpdatingAmendment?: boolean;
}) {
  const isMonitoring = String(eventType ?? 'assessment').trim().toLowerCase() === 'monitoring';
  const [general, setGeneral] = useState('');
  const [personal, setPersonal] = useState('');
  const [candidate, setCandidate] = useState('');
  const [pastedGeneral, setPastedGeneral] = useState(false);
  const [pastedPersonal, setPastedPersonal] = useState(false);
  const [pastedCandidate, setPastedCandidate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState<string | null>(null);
  const [localLastSavedAt, setLocalLastSavedAt] = useState<string | null>(null);
  const saveFlashClearTimerRef = useRef<number | null>(null);

  // Reset confirmation only when switching events — not when parent feedback is patched after save.
  useEffect(() => {
    setSaveFlash(null);
    if (saveFlashClearTimerRef.current != null) {
      window.clearTimeout(saveFlashClearTimerRef.current);
      saveFlashClearTimerRef.current = null;
    }
  }, [eventId]);

  useEffect(() => {
    const i = initialFeedback;
    setGeneral(typeof i?.general_observations === 'string' ? i.general_observations : '');
    setPersonal(typeof i?.personal_attributes === 'string' ? i.personal_attributes : '');
    setCandidate(typeof i?.candidate_feedback === 'string' ? i.candidate_feedback : '');
    setPastedGeneral(!!i?.general_observations_pasted);
    setPastedPersonal(!!i?.personal_attributes_pasted);
    setPastedCandidate(!!i?.candidate_feedback_pasted);
    const at = typeof i?.updated_at === 'string' && i.updated_at ? i.updated_at : null;
    if (at) {
      setLocalLastSavedAt((prev) => (prev && prev >= at ? prev : at));
    }
  }, [eventId, initialFeedback]);

  useEffect(() => {
    return () => {
      if (saveFlashClearTimerRef.current != null) {
        window.clearTimeout(saveFlashClearTimerRef.current);
      }
    };
  }, []);

  const completed = eventStatus === 'completed';
  /** Offer when all criteria qualify for completion, or when reviewing a completed assessment. */
  const showSection = !isMonitoring && ((!completed && eligibilityComplete) || completed);
  const canEdit = canMutate && ((!completed && eligibilityComplete) || completed);
  const lastSavedAt = localLastSavedAt || (typeof initialFeedback?.updated_at === 'string' ? initialFeedback.updated_at : null);

  const markFeedbackSaved = (updatedAt: string) => {
    setLocalLastSavedAt(updatedAt);
    setSaveFlash('Feedback saved');
    if (saveFlashClearTimerRef.current != null) {
      window.clearTimeout(saveFlashClearTimerRef.current);
    }
    saveFlashClearTimerRef.current = window.setTimeout(() => {
      setSaveFlash(null);
      saveFlashClearTimerRef.current = null;
    }, 3500);
  };

  const persistFeedback = async (): Promise<boolean> => {
    if (!canEdit) {
      window.alert(translateUi("You cannot edit final assessment feedback for this event."));
      return false;
    }
    const payload: Record<string, unknown> = {
      general_observations: general.trim(),
      personal_attributes: personal.trim(),
      candidate_feedback: candidate.trim(),
    };
    if (pastedGeneral) payload.general_observations_pasted = true;
    if (pastedPersonal) payload.personal_attributes_pasted = true;
    if (pastedCandidate) payload.candidate_feedback_pasted = true;
    try {
      const res = await fetch(`/api/employee-cycle-events/${encodeURIComponent(String(eventId))}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
        },
        body: JSON.stringify({
          userEmail: currentUserEmail,
          final_assessment_feedback: payload,
        }),
      });
      if (!res.ok) {
        await alertAssessmentMutationFailed(res, translateUi("Could not save final assessment feedback. Please try again."));
        return false;
      }
      const merged = {
        general_observations: String(payload.general_observations ?? ''),
        personal_attributes: String(payload.personal_attributes ?? ''),
        candidate_feedback: String(payload.candidate_feedback ?? ''),
        updated_at: new Date().toISOString(),
      } as NonNullable<AssessmentFinalFeedbackPayload>;
      if (pastedGeneral || initialFeedback?.general_observations_pasted) merged.general_observations_pasted = true;
      if (pastedPersonal || initialFeedback?.personal_attributes_pasted) merged.personal_attributes_pasted = true;
      if (pastedCandidate || initialFeedback?.candidate_feedback_pasted) merged.candidate_feedback_pasted = true;
      markFeedbackSaved(merged.updated_at ?? new Date().toISOString());
      patchLocalEvent(merged);
      return true;
    } catch (e) {
      console.warn(e);
      window.alert(translateUi("Could not save final assessment feedback. Please try again."));
      return false;
    }
  };

  const handlePrimaryAction = async () => {
    if (!canEdit) {
      window.alert(translateUi("You cannot edit final assessment feedback for this event."));
      return;
    }
    const payloadForCheck = {
      general_observations: general.trim(),
      personal_attributes: personal.trim(),
      candidate_feedback: candidate.trim(),
    };
    if (!completed && onFinalizeAssessment) {
      if (!eligibilityComplete || !isFinalAssessmentFeedbackSufficient(payloadForCheck)) {
        window.alert(translateUi("Fill General observations, Personal attributes, and Candidate feedback before completing."));
        return;
      }
      setSaving(true);
      try {
        const ok = await persistFeedback();
        if (!ok) return;
        await onFinalizeAssessment();
      } catch (e) {
        console.warn(e);
        window.alert(translateUi("Could not complete assessment. Please try again."));
      } finally {
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    try {
      await persistFeedback();
    } finally {
      setSaving(false);
    }
  };

  if (!showSection) return null;

  const showUpdate = completed && showUpdateButton && !!onRecordAmendment;

  return (
    <div className={cn('w-full min-w-0 px-4 py-4 sm:px-6 space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))]')}>
      <div>
        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Final assessment feedback</h4>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          {completed
            ? showUpdate
              ? 'Edit the three sections if needed, then tap Save feedback. Tap Update to record criterion grade/notes amendments and finish.'
              : 'Edit the three sections if needed, then tap Save feedback to store your narrative on the server.'
            : 'Finish every criterion first, then complete all three sections below. Tap Complete Assessment when you are ready to save feedback and finish this assessment event.'}
        </p>
        <div
          role="status"
          className="mt-2 w-full min-w-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 leading-snug dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-50"
        >
          <p className="min-w-0">
            An <span className="font-semibold">ENTRY PASTED</span> alert appears when wording was inserted with paste; it stays on record after save for audit.
            {' '}
            Any text pasted into these sections will be flagged by the verification module for review.
            {' '}
            This helps ensure feedback remains appropriately individualised for each employee and allows repeated or identical wording across assessments to be identified.
            {' '}
            Pasted wording is shown on the QA report only when a reuse trend is detected (near-identical text used by the same assessor for more than one employee).
          </p>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              General observations
            </label>
            {pastedGeneral ? (
              <span
                className="inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-950/80 dark:text-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-red-200/90 dark:border-red-700"
                title={translateUi("Paste is stored on save and flagged in QA. Wording appears on the QA report only when a reuse trend is detected.")}
              >
                Entry pasted
              </span>
            ) : null}
          </div>
          <textarea
            value={general}
            onChange={(e) => setGeneral(e.target.value)}
            onPaste={() => setPastedGeneral(true)}
            disabled={!canEdit}
            rows={3}
            className={cn(
              'w-full rounded-xl border px-3 py-2 text-sm min-h-[52px]',
              canEdit
                ? 'border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500'
                : 'border-slate-100 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300'
            )}
            placeholder={canEdit ? 'Overall observations from this assessment…' : '—'}
          />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              Personal attributes
            </label>
            {pastedPersonal ? (
              <span
                className="inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-950/80 dark:text-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-red-200/90 dark:border-red-700"
                title={translateUi("Paste is stored on save and flagged in QA. Wording appears on the QA report only when a reuse trend is detected.")}
              >
                Entry pasted
              </span>
            ) : null}
          </div>
          <textarea
            value={personal}
            onChange={(e) => setPersonal(e.target.value)}
            onPaste={() => setPastedPersonal(true)}
            disabled={!canEdit}
            rows={4}
            className={cn(
              'w-full rounded-xl border px-3 py-2 text-sm min-h-[88px]',
              canEdit
                ? 'border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500'
                : 'border-slate-100 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300'
            )}
            placeholder={canEdit ? 'e.g. communication, teamwork, diligence…' : '—'}
          />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              Candidate feedback
            </label>
            {pastedCandidate ? (
              <span
                className="inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-950/80 dark:text-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-red-200/90 dark:border-red-700"
                title={translateUi("Paste is stored on save and flagged in QA. Wording appears on the QA report only when a reuse trend is detected.")}
              >
                Entry pasted
              </span>
            ) : null}
          </div>
          <textarea
            value={candidate}
            onChange={(e) => setCandidate(e.target.value)}
            onPaste={() => setPastedCandidate(true)}
            disabled={!canEdit}
            rows={4}
            className={cn(
              'w-full rounded-xl border px-3 py-2 text-sm min-h-[88px]',
              canEdit
                ? 'border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500'
                : 'border-slate-100 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300'
            )}
            placeholder={canEdit ? 'Feedback suitable to share or record for the candidate…' : '—'}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 text-[11px] text-slate-500 dark:text-slate-400">
          {saveFlash ? (
            <p className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-semibold text-xs" role="status">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
              {saveFlash}
              {lastSavedAt ? <span className="font-normal text-emerald-800/80 dark:text-emerald-300/80">· {formatDateTimeUK(lastSavedAt)}</span> : null}
            </p>
          ) : lastSavedAt ? (
            <p>Last saved {formatDateTimeUK(lastSavedAt)}</p>
          ) : null}
        </div>
        {(canEdit || showUpdate) && (
          <div className="flex flex-wrap justify-end gap-2">
            {showUpdate ? (
              <button
                type="button"
                disabled={isUpdatingAmendment || saving}
                onClick={() => void onRecordAmendment?.()}
                className={cn(
                  'px-4 py-2 min-h-[44px] text-sm font-semibold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400 disabled:opacity-50 touch-manipulation sm:min-h-0 sm:py-2',
                  !isUpdatingAmendment && 'animate-pulse',
                )}
                title={translateUi("Save criterion grade/notes changes and record an amendment")}
              >
                {isUpdatingAmendment ? 'Updating…' : translateUi("Update")}
              </button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                disabled={
                  saving ||
                  isUpdatingAmendment ||
                  (!completed &&
                    !!onFinalizeAssessment &&
                    (!eligibilityComplete ||
                      !isFinalAssessmentFeedbackSufficient({
                        general_observations: general.trim(),
                        personal_attributes: personal.trim(),
                        candidate_feedback: candidate.trim(),
                      })))
                }
                onClick={() => void handlePrimaryAction()}
                className={cn(
                  'px-4 py-2 min-h-[44px] text-sm font-medium rounded-xl text-white disabled:opacity-50 touch-manipulation sm:min-h-0 sm:py-2',
                  saveFlash
                    ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
                    : !completed && onFinalizeAssessment
                      ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
                      : 'bg-emerald-700 hover:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500',
                )}
              >
                {saving
                  ? 'Saving…'
                  : saveFlash
                    ? 'Saved ✓'
                    : completed || !onFinalizeAssessment
                      ? 'Save feedback'
                      : translateUi("Complete Assessment")}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

const AssessmentEventModalFrame = ({
  event,
  title,
  subtitle,
  showUpdateButton = false,
  onRecordAmendment,
  onClose,
  children,
  mobileAppMode = false,
  isSubmittingAssessment = false,
  isUpdatingAmendment = false,
  amendmentSaved = false,
  assessmentFooter = null,
  isDarkMode = false,
  onToggleDarkMode,
}: {
  event: EmployeeCycleEventSummary;
  title: string;
  subtitle: string;
  showUpdateButton?: boolean;
  onRecordAmendment?: (amendmentNotes?: string) => Promise<void> | void;
  onClose: () => void;
  children: React.ReactNode;
  mobileAppMode?: boolean;
  isSubmittingAssessment?: boolean;
  isUpdatingAmendment?: boolean;
  amendmentSaved?: boolean;
  assessmentFooter?: React.ReactNode;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}) => {
  const [sessionStartedAt] = useState<Date>(() => new Date());
  const [timerTick, setTimerTick] = useState<number>(() => Date.now());
  const [showAmendmentPopup, setShowAmendmentPopup] = useState(false);
  const [brightness, setBrightness] = useState(() => {
    if (!mobileAppMode) return 1;
    try {
      const raw = sessionStorage.getItem('rv_assess_brightness');
      const parsed = raw != null ? Number.parseFloat(raw) : NaN;
      if (Number.isFinite(parsed) && parsed >= 0.4 && parsed <= 1.4) return parsed;
    } catch {
      /* ignore */
    }
    return 1;
  });
  const isCompleted = event.status === 'completed';
  const startedAt = event.assessment_started_at;
  const [liveElapsed, setLiveElapsed] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTimerTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (!mobileAppMode) return;
    try {
      sessionStorage.setItem('rv_assess_brightness', String(brightness));
    } catch {
      /* ignore */
    }
  }, [mobileAppMode, brightness]);
  /** Immersive mobile/tablet assessment: lock page scroll behind the assessment shell. */
  useEffect(() => {
    if (!mobileAppMode) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'manipulation';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [mobileAppMode]);
  useEffect(() => {
    if (isCompleted || !startedAt) return;
    const compute = () => Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    setLiveElapsed(compute());
    const interval = setInterval(() => setLiveElapsed(compute()), 1000);
    return () => clearInterval(interval);
  }, [isCompleted, startedAt]);

  const durationDisplay = isCompleted
    ? (event.assessment_duration_seconds != null && event.assessment_duration_seconds >= 0
      ? formatDurationSeconds(event.assessment_duration_seconds)
      : null)
    : (startedAt ? formatDurationSeconds(liveElapsed) : null);
  const finishTime = isCompleted && (event.completed_at || event.assessment_completed_at)
    ? formatDateTimeUK(event.completed_at || event.assessment_completed_at)
    : null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-slate-900/60",
        mobileAppMode ? "p-0" : "flex items-center justify-center p-4",
        isDarkMode && "rv-dark"
      )}
      onClick={onClose}
      style={mobileAppMode ? { height: '100dvh' } : undefined}
    >
      <div
        className={cn(
          "w-full overflow-hidden border shadow-2xl flex flex-col relative",
          mobileAppMode
            ? "h-full max-w-none max-h-none rounded-none border-0"
            : "max-w-7xl max-h-[92vh] rounded-2xl border-slate-200",
          isDarkMode ? "bg-slate-900 border-slate-700 rv-dark" : "bg-slate-50 border-slate-200"
        )}
        style={
          mobileAppMode
            ? {
                height: '100dvh',
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                filter: brightness !== 1 ? `brightness(${brightness})` : undefined,
              }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        <GbrModalBackdrop opacity={isDarkMode ? 0.14 : 0.12} />
        {(isSubmittingAssessment || isUpdatingAmendment || amendmentSaved) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 px-6 py-4 rounded-xl bg-white border border-slate-200 shadow-lg">
              {amendmentSaved ? (
                <>
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  <p className="text-sm font-medium text-slate-700">Amendments saved</p>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-slate-700">
                    {isSubmittingAssessment ? translateUi("Submitting assessment…") : translateUi("Updating Amendments…")}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        <div className={cn("relative z-10 border-b flex items-start justify-between gap-4", mobileAppMode ? "px-4 py-3 sm:px-5" : "px-6 py-4", isDarkMode ? "border-slate-700 bg-slate-900/55" : "border-slate-200 bg-slate-50/70")}>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-indigo-600">Assessment event</p>
            <h3 className={cn("font-bold text-slate-900 truncate", mobileAppMode ? "text-base sm:text-lg" : "text-lg")}>{title}</h3>
            <p className={cn("text-slate-500", mobileAppMode ? "text-xs sm:text-sm" : "text-sm")}>{subtitle}</p>
            <p className="text-xs text-slate-500 mt-1">
              Window: {formatDateUK(event.window_open)} to {formatDateUK(event.window_close)}
            </p>
            {isCompleted && (event.assessment_completed_at || event.completed_at) && (
              <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-600">
                <span>
                  <span className="text-slate-500">Date & Time completed:</span>{' '}
                  <span className="font-medium text-slate-800">{finishTime}</span>
                </span>
                {durationDisplay != null && (
                  <span className="tabular-nums">
                    <span className="text-slate-500">Duration:</span>{' '}
                    <span className="font-medium text-slate-800">{durationDisplay}</span>
                  </span>
                )}
                {(event.assessment_completed_by_name || event.assessment_completed_by || event.completed_by) && (
                  <span>
                    <span className="text-slate-500">Assessment completed by:</span>{' '}
                    <span className="font-medium text-slate-800">{event.assessment_completed_by_name || event.assessment_completed_by || event.completed_by}</span>
                  </span>
                )}
                {(event.amended_by_name || event.amended_by) && (
                  <span className="inline-flex items-center gap-1 relative">
                    <span>
                      <span className="text-slate-500">Amended by:</span>{' '}
                      <span className="font-medium text-slate-800">{event.amended_by_name || event.amended_by}</span>
                      {event.amended_at && (
                        <span className="text-slate-600"> ({formatDateTimeUK(event.amended_at)})</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAmendmentPopup((p) => !p)}
                      className="p-0.5 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50 transition-colors shrink-0"
                      title={translateUi("View amendment details")}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                    {showAmendmentPopup && (
                      <>
                        <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setShowAmendmentPopup(false); }} aria-hidden />
                        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] min-w-[280px] max-w-[min(420px,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto p-4 bg-slate-800 text-white text-sm rounded-xl shadow-2xl border border-slate-600">
                          {(() => {
                            let snapshot: { criterion_key: string; unit: string; element: string; criteria: string; grade: string | null; notes: string | null; previous_grade?: string; amended_at?: string }[] | null = null;
                            try {
                              if (event.amendment_snapshot?.trim()) snapshot = JSON.parse(event.amendment_snapshot);
                            } catch {}
                            if (Array.isArray(snapshot) && snapshot.length > 0) {
                              return (
                                <div className="space-y-0">
                                  <div className="mb-3 pb-3 border-b border-slate-600 space-y-1.5">
                                    <p className="text-slate-200">
                                      <span className="text-slate-400">Amended by:</span> {event.amended_by_name || event.amended_by || '—'}
                                    </p>
                                  </div>
                                  {snapshot.map((s, i) => (
                                    <div key={s.criterion_key || i}>
                                      {i > 0 && <hr className="border-slate-600 my-3" />}
                                      <div className="space-y-1.5">
                                        <p className="text-slate-300 font-medium">{cleanUnitDisplay(s.unit)} · {s.element}</p>
                                        <p className="text-slate-200">{s.criteria}</p>
                                        {(s.grade || s.previous_grade) && (
                                          <p>
                                            <span className="text-slate-400">Grade:</span>{' '}
                                            {s.previous_grade && s.grade
                                              ? `${s.previous_grade} → ${s.grade}`
                                              : s.grade || s.previous_grade}
                                          </p>
                                        )}
                                        {s.notes && <p><span className="text-slate-400">Notes:</span> {s.notes}</p>}
                                        {(s.amended_at ?? event.amended_at) && (
                                          <p className="text-slate-400 text-xs mt-1">
                                            {formatDateTimeUK(s.amended_at ?? event.amended_at)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-2 text-slate-200">
                                <p>{event.amendment_notes?.trim() || 'Assessment criteria (grades, notes)'}</p>
                                <div className="pt-2 border-t border-slate-600 space-y-1.5">
                                  <p className="text-slate-200">
                                    <span className="text-slate-400">Date & time:</span> {event.amended_at ? formatDateTimeUK(event.amended_at) : '—'}
                                  </p>
                                  <p className="text-slate-200">
                                    <span className="text-slate-400">Amended by:</span> {event.amended_by_name || event.amended_by || '—'}
                                  </p>
                                </div>
                              </div>
                            );
                          })()}
                          <button type="button" onClick={(e) => { e.stopPropagation(); setShowAmendmentPopup(false); }} className="mt-3 text-indigo-300 hover:text-white text-xs font-medium"> Close </button>
                        </div>
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
            {!isCompleted && durationDisplay != null && (
              <p className="text-xs font-medium text-slate-700 mt-1 tabular-nums">
                Duration: {durationDisplay}
                <span className="text-slate-500 font-normal ml-1">(in progress)</span>
              </p>
            )}
            {!isCompleted && finishTime != null && (
              <p className="text-xs text-slate-600 mt-1">
                Finish time: <span className="font-medium text-slate-800">{finishTime}</span>
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <div className="flex items-center gap-2">
              {onToggleDarkMode && (
                <button
                  type="button"
                  onClick={onToggleDarkMode}
                  className={cn(
                    "inline-flex items-center gap-1.5 font-medium border rounded-lg",
                    isDarkMode
                      ? "border-slate-600 bg-slate-800 text-amber-300 hover:bg-slate-700"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                    mobileAppMode ? "px-3 py-2 text-sm min-h-[44px]" : "px-3 py-1.5 text-xs"
                  )}
                  title={isDarkMode ? translateUi("Switch to light mode") : translateUi("Switch to dark mode — reduces windscreen glare in tunnels")}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? <Sun className="w-4 h-4" aria-hidden /> : <Moon className="w-4 h-4" aria-hidden />}
                  <span>{isDarkMode ? translateUi("Light") : translateUi("Dark")}</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  "text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100",
                  mobileAppMode ? "p-2.5 min-h-[44px] min-w-[44px]" : "p-2"
                )}
                title={translateUi("Close assessment")}
                aria-label={translateUi("Close assessment")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {durationDisplay != null && (
            <p className="text-xs text-slate-600">
              Duration: <span className="font-semibold text-slate-800">{durationDisplay}</span>
              {!isCompleted && <span className="text-slate-500 font-normal ml-1">(in progress)</span>}
            </p>
            )}
          </div>
        </div>
        {mobileAppMode ? (
          <div
            className={cn(
              "relative z-10 px-4 py-2 border-b flex items-center gap-2",
              isDarkMode ? "border-slate-700 bg-slate-900/55" : "border-slate-200 bg-slate-50/70"
            )}
          >
            <Moon
              className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-500")}
              aria-hidden
            />
            <input
              type="range"
              min={0.4}
              max={1.35}
              step={0.05}
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
              className="flex-1 min-w-0 h-2 accent-indigo-600 touch-manipulation"
              aria-label={translateUi("Screen brightness")}
              title={translateUi("Adjust assessment screen brightness")}
            />
            <Sun
              className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-amber-300" : "text-amber-500")}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => setBrightness(1)}
              className={cn(
                "shrink-0 text-[11px] font-medium px-2 py-1 rounded-md min-h-[32px]",
                isDarkMode
                  ? "text-slate-300 hover:bg-slate-800"
                  : "text-slate-600 hover:bg-slate-200/80"
              )}
              title={translateUi("Reset brightness")}
            >
              {Math.round(brightness * 100)}%
            </button>
          </div>
        ) : null}
        <div className="relative z-10 flex-1 overflow-hidden flex flex-col min-h-0">
          <div className={cn("flex-1 min-h-0", mobileAppMode ? "overflow-y-auto flex flex-col overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch]" : "overflow-auto")}>{children}</div>
          {assessmentFooter != null && assessmentFooter !== false ? (
            <div className={cn("shrink-0 border-t", isDarkMode ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70")}>
              {assessmentFooter}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

function CycleCriteriaProgressDial({
  covered,
  total,
  isDarkMode = false,
}: {
  covered: number;
  total: number;
  isDarkMode?: boolean;
}) {
  const safeTotal = Math.max(0, total);
  const safeCovered = Math.min(Math.max(0, covered), safeTotal > 0 ? safeTotal : covered);
  const remaining = Math.max(0, safeTotal - safeCovered);
  const pct = safeTotal > 0 ? Math.min(1, safeCovered / safeTotal) : 0;
  const pctLabel = safeTotal > 0 ? Math.round(pct * 100) : 0;

  const width = 200;
  const height = 96;
  const cx = width / 2;
  const cy = height - 4;
  const radius = 70;
  const stroke = 11;
  const coveredColor = isDarkMode ? "#34d399" : "#059669";
  const remainingColor = isDarkMode ? "#fb7185" : "#e11d48";
  const segmentGap = pct > 0 && pct < 1 ? 3 : 0;

  const polar = (angleDeg: number, r: number) => {
    const rad = ((angleDeg - 180) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const arcPath = (startDeg: number, endDeg: number, r: number) => {
    if (endDeg <= startDeg + 0.05) return "";
    const start = polar(startDeg, r);
    const end = polar(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
  };

  const coveredEnd = pct * 180;
  const coveredD = pct > 0 ? arcPath(0, Math.max(0.5, coveredEnd - segmentGap / 2), radius) : "";
  const remainingD =
    pct < 1 ? arcPath(Math.min(179.5, coveredEnd + segmentGap / 2), 180, radius) : "";

  return (
    <div
      className={cn(
        "w-full max-w-[220px] mx-auto rounded-xl border px-3 pt-2 pb-2",
        isDarkMode ? "border-slate-700 bg-slate-900/80" : "border-slate-200 bg-white"
      )}
      role="img"
      aria-label={translateUi("Overall cycle criteria: {{covered}} of {{total}} covered, {{remaining}} remaining ({{pct}}%)", { covered: safeCovered, total: safeTotal, remaining, pct: pctLabel })}
    >
      <p className={cn("text-xs font-semibold text-center leading-none", isDarkMode ? "text-slate-200" : "text-slate-800")}>
        Overall Cycle Criteria
      </p>

      <div className="relative mx-auto mt-0.5" style={{ width, height }}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" aria-hidden>
          {remainingD ? (
            <path
              d={remainingD}
              fill="none"
              stroke={remainingColor}
              strokeWidth={stroke}
              strokeLinecap="round"
            />
          ) : null}
          {coveredD ? (
            <path
              d={coveredD}
              fill="none"
              stroke={coveredColor}
              strokeWidth={stroke}
              strokeLinecap="round"
            />
          ) : null}
        </svg>

        <div
          className="absolute inset-x-0 flex flex-col items-center pointer-events-none"
          style={{ top: cy - radius * 0.58 }}
        >
          <p className={cn("text-2xl font-bold tabular-nums leading-none", isDarkMode ? "text-white" : "text-slate-900")}>
            {pctLabel}%
          </p>
          <p className="text-[10px] font-medium tabular-nums mt-1 flex items-center gap-1.5">
            <span style={{ color: coveredColor }}>{safeCovered} covered</span>
            <span className={isDarkMode ? "text-slate-600" : "text-slate-300"}>·</span>
            <span style={{ color: remainingColor }}>{remaining} left</span>
          </p>
        </div>
      </div>
    </div>
  );
}

type ClosedCycleArchiveSummary = {
  assessed_criteria_count: number;
  attachment_count: number;
  grade_buckets: { review: number; advice: number; not_applicable: number; satisfactory: number };
};

function isCriterionGradeRecorded(g: string | null | undefined): boolean {
  const s = (g ?? '').trim();
  if (!s) return false;
  return !/^not\s+yet\s+assessed$/i.test(s);
}

function criterionGradeNeedsAttention(g: string | null | undefined): boolean {
  const s = (g ?? '').trim().toLowerCase();
  if (!s || /^not\s+yet\s+assessed$/.test(s)) return false;
  return /\breview\b/.test(s) || /\bfail\b/.test(s) || /\bunsafe\b/.test(s) || /\bunsatisfactory\b/.test(s);
}

/** Aligns with server buildArchiveSummaryForSourceEc — every recorded grade maps to one of four UI buckets. */
function classifyGradeForArchiveBucket(grade: string): 'review' | 'advice' | 'not_applicable' | 'satisfactory' {
  const n = grade.trim().toLowerCase();
  if (!n || /^not\s+yet\s+assessed$/.test(n)) return 'satisfactory';
  if (n.includes('not applicable')) return 'not_applicable';
  if (n.includes('review')) return 'review';
  if (n.includes('fail') || n.includes('unsafe') || n.includes('unsatisfactory')) return 'review';
  if (n.includes('advis') || n.includes('advice')) return 'advice';
  return 'satisfactory';
}

type ClosedCycleCriterionAssessmentSnapshot = {
  grade: string | null;
  employee_cycle_event_id?: string | number | null;
};

/** Full-width summary for archived cycle assessment record (grades + evidence). */
function ClosedCycleArchiveBreakdownPanel({
  summary,
  gradeRows,
  gradeBreakdownStatus,
  attentionItems,
  onJumpToCriterion,
}: {
  summary: ClosedCycleArchiveSummary | null;
  gradeRows: { label: string; count: number }[];
  gradeBreakdownStatus: 'idle' | 'loading' | 'done' | 'error';
  attentionItems: {
    criterionKey: string;
    gradeLabel: string;
    criterionLabel: string;
    eventId: number | string | null;
  }[];
  onJumpToCriterion?: (eventId: number | string, criterionKey: string) => void;
}) {
  const att = summary?.attachment_count ?? 0;
  const parts = React.useMemo(() => {
    const defs: { key: 'sat' | 'adv' | 'rev' | 'na'; c: string; label: string; title: string }[] = [
      { key: 'sat', c: '#10b981', label: translateUi("Competent"), title: 'Satisfactory / positive outcomes (shown under Competent with advisory items)' },
      { key: 'adv', c: '#d97706', label: 'Advice / further evidence', title: 'Advice or further evidence required' },
      { key: 'rev', c: '#e11d48', label: translateUi("Review"), title: translateUi("Review required") },
      { key: 'na', c: '#0284c7', label: translateUi("Not applicable"), title: translateUi("Marked not applicable") },
    ];
    const bk = summary?.grade_buckets;
    if (bk) {
      return defs.map((d) => ({
        ...d,
        n: d.key === 'sat' ? bk.satisfactory : d.key === 'adv' ? bk.advice : d.key === 'rev' ? bk.review : bk.not_applicable,
      }));
    }
    const map = { sat: 0, adv: 0, rev: 0, na: 0 };
    for (const row of gradeRows) {
      const bucket = classifyGradeForArchiveBucket(row.label);
      const pk: keyof typeof map =
        bucket === 'satisfactory' ? 'sat' : bucket === 'advice' ? 'adv' : bucket === 'review' ? 'rev' : 'na';
      map[pk] += row.count;
    }
    return defs.map((d) => ({ ...d, n: map[d.key] }));
  }, [summary?.grade_buckets, gradeRows]);
  const mixTotal = parts.reduce((s, p) => s + p.n, 0);
  const adviceColor = '#d97706';
  const donutSlices = React.useMemo(() => {
    const s = parts.find((p) => p.key === 'sat')?.n ?? 0;
    const a = parts.find((p) => p.key === 'adv')?.n ?? 0;
    const r = parts.find((p) => p.key === 'rev');
    const na = parts.find((p) => p.key === 'na');
    return [
      {
        key: 'comp',
        c: '#10b981',
        n: s + a,
        label: translateUi("Competent"),
        title: 'Satisfactory outcomes and advisory / further evidence (combined)',
      },
      { key: 'rev', c: '#e11d48', n: r?.n ?? 0, label: translateUi("Review"), title: r?.title ?? translateUi("Review required") },
      { key: 'na', c: '#0284c7', n: na?.n ?? 0, label: translateUi("Not applicable"), title: na?.title ?? translateUi("Marked not applicable") },
    ];
  }, [parts]);
  const competentTotal = donutSlices[0]?.n ?? 0;
  const nonZeroDonut = donutSlices.filter((p) => p.n > 0);
  const gradeTotal = nonZeroDonut.reduce((s, p) => s + p.n, 0);
  let gradient = '';
  /** Mid-angle (deg, clockwise from top) and % for labels on the donut ring. */
  const donutArcLabels: { key: string; midDeg: number; sweepDeg: number; pct: number }[] = [];
  if (gradeTotal > 0) {
    let pct = 0;
    const stops: string[] = [];
    let cursorDeg = 0;
    for (const p of nonZeroDonut) {
      const start = pct;
      pct += (p.n / gradeTotal) * 100;
      stops.push(`${p.c} ${start}% ${pct}%`);
      const sweepDeg = (p.n / gradeTotal) * 360;
      const midDeg = cursorDeg + sweepDeg / 2;
      cursorDeg += sweepDeg;
      donutArcLabels.push({
        key: p.key,
        midDeg,
        sweepDeg,
        pct: Math.round((p.n / gradeTotal) * 100),
      });
    }
    gradient = `conic-gradient(${stops.join(', ')})`;
  }
  /** h-52 w-52 → outer radius 104px; inset for wide ring */
  const donutOuterPx = 104;
  const donutHoleInsetPx = 34;
  const donutLabelRadiusPx = donutOuterPx - donutHoleInsetPx / 2;

  const pctDenom = mixTotal > 0 ? mixTotal : 1;

  const labelsByPartKey = React.useMemo(() => {
    const map: Record<'sat' | 'adv' | 'rev' | 'na', { label: string; count: number }[]> = {
      sat: [],
      adv: [],
      rev: [],
      na: [],
    };
    for (const row of gradeRows) {
      const k = classifyGradeForArchiveBucket(row.label);
      const pk: 'sat' | 'adv' | 'rev' | 'na' =
        k === 'satisfactory' ? 'sat' : k === 'advice' ? 'adv' : k === 'review' ? 'rev' : 'na';
      map[pk].push(row);
    }
    (['sat', 'adv', 'rev', 'na'] as const).forEach((pk) => {
      map[pk].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    });
    return map;
  }, [gradeRows]);

  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50/80 to-white overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-white/90 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center">
          <BarChart3 className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-900 tracking-tight">Cycle summary — grades &amp; evidence</h3>
          <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            Totals for this closed cycle across all saved criterion assessments and assessment evidence files. Open each event above to see notes, methods, and attachments in context.
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-6">
        {attentionItems.length > 0 && (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="status"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 shrink-0 text-amber-700 mt-0.5" aria-hidden />
              <div className="min-w-0 space-y-2">
                <p className="font-semibold text-amber-950">
                  This cycle has {attentionItems.length} criterion grade{attentionItems.length === 1 ? '' : 's'} marked review, fail, unsafe, or unsatisfactory.
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-amber-900/95">
                  {attentionItems.map((it) => (
                    <li key={it.criterionKey} className="leading-snug">
                      <span className="font-medium">{it.gradeLabel}</span>
                      {' — '}
                      <span className="text-amber-950/90">{it.criterionLabel}</span>
                      {it.eventId != null && onJumpToCriterion ? (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => onJumpToCriterion(it.eventId!, it.criterionKey)}
                            className="text-amber-900 underline font-medium hover:text-amber-950"
                          >
                            Open in assessment
                          </button>
                        </>
                      ) : it.eventId == null ? (
                        <span className="text-amber-800/80"> (link unavailable — open the relevant event manually)</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        {!summary && (
          <p className="text-sm text-slate-600 mb-4">
            Summary totals are not available for this record (older data or template mismatch). Grade groups below are derived from saved criterion grades.
          </p>
        )}
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            {nonZeroDonut.map((p) => (
              <span
                key={p.key}
                className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                style={{ backgroundColor: p.c }}
                title={translateUi("{{title}} — {{pct}}% of graded mix", { title: p.title, pct: gradeTotal > 0 ? Math.round((p.n / gradeTotal) * 100) : 0 })}
              >
                {p.label} · {p.n}
                {gradeTotal > 0 ? (
                  <span className="opacity-90 font-bold tabular-nums">({Math.round((p.n / gradeTotal) * 100)}%)</span>
                ) : null}
              </span>
            ))}
            <span className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-full bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-800">
              <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {att} file{att === 1 ? '' : 's'}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="flex justify-start lg:justify-start">
              <div
                className="relative h-52 w-52 shrink-0 rounded-full border border-slate-200 bg-slate-100 overflow-hidden shadow-inner"
                title={att === 1
                  ? translateUi("{{count}} graded criteria · {{att}} attachment", { count: mixTotal, att })
                  : translateUi("{{count}} graded criteria · {{att}} attachments", { count: mixTotal, att })}
              >
                {gradeTotal > 0 ? (
                  <>
                    <div className="absolute inset-0" style={{ background: gradient }} />
                    {donutArcLabels.map((seg) => {
                      const showPct = seg.sweepDeg >= 22 || donutArcLabels.length === 1;
                      if (!showPct) return null;
                      return (
                        <span
                          key={seg.key}
                          className="absolute left-1/2 top-1/2 z-[1] text-xs font-bold tabular-nums text-white leading-none pointer-events-none whitespace-nowrap"
                          style={{
                            textShadow: '0 0 3px rgba(0,0,0,.65), 0 1px 3px rgba(0,0,0,.5)',
                            transform: `translate(-50%, -50%) rotate(${seg.midDeg}deg) translateY(-${donutLabelRadiusPx}px) rotate(-${seg.midDeg}deg)`,
                          }}
                          aria-hidden
                        >
                          {seg.pct}%
                        </span>
                      );
                    })}
                    <div
                      className="absolute rounded-full bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.35)] flex items-center justify-center"
                      style={{ inset: donutHoleInsetPx }}
                    >
                      <span className="text-2xl font-bold text-slate-800 tabular-nums">{mixTotal}</span>
                    </div>
                  </>
                ) : (
                  <div
                    className="absolute rounded-full bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.35)] flex items-center justify-center"
                    style={{ inset: donutHoleInsetPx }}
                  >
                    <span className="text-lg font-semibold text-slate-500 tabular-nums">0</span>
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 space-y-3">
              <div>
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Grade mix (grouped)</h4>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Competent includes satisfactory grades and advisory items (yellow band). Exact saved labels are listed under each group.
                </p>
                {gradeBreakdownStatus === 'loading' && (
                  <p className="text-xs text-slate-500 mt-1.5">Loading exact grade labels…</p>
                )}
                {gradeBreakdownStatus === 'error' && (
                  <p className="text-xs text-amber-800 mt-1.5">Could not load exact grade labels; category totals still apply.</p>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white max-h-[min(480px,62vh)] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 z-[1] shadow-[0_1px_0_0_rgb(226,232,240)]">
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                      <th className="px-3 py-2 font-semibold">Category / grade as recorded</th>
                      <th className="px-3 py-2 font-semibold text-right w-24">Count</th>
                      <th className="px-3 py-2 font-semibold text-right w-28">Of graded</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <React.Fragment key="competent-block">
                      <tr className="text-slate-800 bg-white">
                        <td className="px-3 py-2 font-medium">
                          <span className="inline-block w-2.5 h-2.5 rounded-full align-middle mr-2 bg-emerald-500" aria-hidden /> Competent </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{competentTotal}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                          {mixTotal > 0 ? `${Math.round((competentTotal / mixTotal) * 100)}%` : '—'}
                        </td>
                      </tr>
                      {gradeBreakdownStatus === 'done' &&
                        (labelsByPartKey.sat ?? []).map((row, idx) => (
                          <tr
                            key={`sat-lbl-${row.label}::${idx}`}
                            className="text-slate-700 bg-slate-50/70 text-xs border-l-[3px]"
                            style={{ borderLeftColor: '#10b981' }}
                          >
                            <td className="px-3 py-1.5 pl-8">{row.label}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">{row.count}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                              {pctDenom > 0 ? `${Math.round((row.count / pctDenom) * 100)}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      {gradeBreakdownStatus === 'done' &&
                        (labelsByPartKey.adv ?? []).map((row, idx) => (
                          <tr
                            key={`adv-lbl-${row.label}::${idx}`}
                            className="text-slate-700 bg-amber-50/80 text-xs border-l-[3px]"
                            style={{ borderLeftColor: adviceColor }}
                          >
                            <td className="px-3 py-1.5 pl-8">{row.label}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">{row.count}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                              {pctDenom > 0 ? `${Math.round((row.count / pctDenom) * 100)}%` : '—'}
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                    {(['rev', 'na'] as const).map((pk) => {
                      const p = parts.find((x) => x.key === pk);
                      if (!p) return null;
                      return (
                        <React.Fragment key={p.key}>
                          <tr className="text-slate-800 bg-white">
                            <td className="px-3 py-2 font-medium">
                              <span className="inline-block w-2.5 h-2.5 rounded-full align-middle mr-2" style={{ backgroundColor: p.c }} aria-hidden />
                              {p.label}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">{p.n}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                              {mixTotal > 0 ? `${Math.round((p.n / mixTotal) * 100)}%` : '—'}
                            </td>
                          </tr>
                          {gradeBreakdownStatus === 'done' &&
                            (labelsByPartKey[pk] ?? []).map((row, idx) => (
                              <tr
                                key={`${p.key}-lbl-${row.label}::${idx}`}
                                className="text-slate-700 bg-slate-50/70 text-xs border-l-[3px]"
                                style={{ borderLeftColor: p.c }}
                              >
                                <td className="px-3 py-1.5 pl-8">{row.label}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{row.count}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                                  {pctDenom > 0 ? `${Math.round((row.count / pctDenom) * 100)}%` : '—'}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2.5 text-xs text-indigo-900">
                <span className="font-semibold">Evidence files:</span>{' '}
                {att === 0
                  ? 'No assessment evidence files were indexed for this cycle path.'
                  : `${att} file${att === 1 ? '' : 's'} stored under this cycle’s assessment evidence paths (photos, PDFs, documents). View or download them from within each assessment event.`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CustomCycleAssessmentModal = ({
  employeeId,
  employeeCycleId,
  evidenceStorageEmployeeCycleId,
  companyCode,
  currentUserEmail,
  canAssess,
  readOnlyEvidence = false,
  event,
  criteria,
  onClose,
  onMarkCompleted,
  onRecordAmendment,
  onStartAssessment,
  showAssessedOnly = false,
  isMobileOrTablet = false,
  isLastEvent = false,
  priorEventIds = [],
  onClosedCycleSummaryInvalidate,
  onAssessmentScheduleInvalidate,
  onAssessmentProgressPatch,
  requestFocusCriterionKey,
  onRequestFocusCriterionHandled,
  patchEmployeeCycleEvent,
  isDarkMode = false,
  onToggleDarkMode,
}: {
  employeeId: string;
  employeeCycleId: number | string;
  evidenceStorageEmployeeCycleId?: number | string;
  companyCode: string;
  currentUserEmail?: string;
  canAssess: boolean;
  /** When true, hide upload / delete evidence (e.g. closed-cycle archive view) */
  readOnlyEvidence?: boolean;
  event: EmployeeCycleEventSummary;
  criteria: CustomCycleCriterionRow[];
  onClose: () => void;
  onMarkCompleted: () => Promise<void> | void;
  onRecordAmendment?: (amendmentNotes?: string) => Promise<void> | void;
  onStartAssessment?: (criterionKeys: string[], elementKeys?: string[]) => void;
  /** When true, show only assessed criteria (grade + notes) instead of remaining */
  showAssessedOnly?: boolean;
  isMobileOrTablet?: boolean;
  isLastEvent?: boolean;
  priorEventIds?: Array<number | string>;
  onClosedCycleSummaryInvalidate?: () => void;
  /** Refetch cycle-event summary counts (schedule row) — e.g. after bulk save before final feedback */
  onAssessmentScheduleInvalidate?: (opts?: { immediate?: boolean }) => void;
  /** Instant local schedule/dial patch after a grade change (no network). */
  onAssessmentProgressPatch?: (args: {
    eventId: number | string;
    previousGrade: string | null | undefined;
    nextGrade: string | null | undefined;
  }) => void;
  requestFocusCriterionKey?: string | null;
  onRequestFocusCriterionHandled?: () => void;
  patchEmployeeCycleEvent?: (partial: Partial<EmployeeCycleEventSummary>) => void;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}) => {
  const uiLocale = useUiLocale();
  const cc = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
  /** Stable string for effect deps — parent often passes a fresh `[]` each render. */
  const priorEventIdsKey = priorEventIds.map((id) => String(id)).join(',');
  const companyHeaders = useMemo(
    () => (companyCode ? ({ 'X-Company-Code': companyCode } as Record<string, string>) : undefined),
    [companyCode]
  );
  const storageEcId = evidenceStorageEmployeeCycleId ?? employeeCycleId;
  const canMutateEvidence = canAssess && !readOnlyEvidence;
  const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);
  const [isUpdatingAmendment, setIsUpdatingAmendment] = useState(false);
  const [amendmentSaved, setAmendmentSaved] = useState(false);
  const [gradingScale, setGradingScale] = useState<{ name: string; description: string }[]>([]);
  const gradeSelectOptions = useMemo(
    () => buildAlphabeticalGradeOptions(gradingScale.map((g) => g.name)),
    [gradingScale]
  );
  const methodSelectOptions = useMemo(
    () => [
      { value: '', label: translateUi("Select method") },
      ...sortAssessmentMethodsAlphabetical(ASSESSMENT_METHOD_OPTIONS, toMethodLabel).map((m) => ({
        value: m,
        label: toMethodLabel(m),
      })),
    ],
    [uiLocale]
  );
  const bulkGradeSelectOptions = useMemo(
    () => [
      { value: '', label: translateUi('Choose grade for selected') },
      ...buildAlphabeticalGradeOptions(gradingScale.map((g) => g.name), { includeNotApplicable: false })
        .filter((o) => o.value)
        .map((o) => ({ value: o.value, label: o.label })),
    ],
    [gradingScale, uiLocale]
  );
  const [assessments, setAssessments] = useState<Record<string, EmployeeCriterionAssessment>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [evidenceByCriterion, setEvidenceByCriterion] = useState<Record<string, { id: string | number; name: string }[]>>({});
  const [uploadingEvidenceDocument, setUploadingEvidenceDocument] = useState<Record<string, boolean>>({});
  const [uploadingEvidencePhoto, setUploadingEvidencePhoto] = useState<Record<string, boolean>>({});
  const [storageQuotaUploadDialogOpen, setStorageQuotaUploadDialogOpen] = useState(false);
  const [storageQuotaStartDialogOpen, setStorageQuotaStartDialogOpen] = useState(false);
  const pendingStartAssessmentRef = useRef<(() => void) | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const [expandedElements, setExpandedElements] = useState<Record<string, boolean>>({});
  const [selectedCriteria, setSelectedCriteria] = useState<Record<string, boolean>>({});
  const [elementBulkGrades, setElementBulkGrades] = useState<Record<string, string>>({});
  const [manualGradeOverrides, setManualGradeOverrides] = useState<Record<string, boolean>>({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [activeMobileElementKey, setActiveMobileElementKey] = useState<string | null>(null);
  const [mobilePhase, setMobilePhase] = useState<'select' | 'assess' | 'feedback'>(() =>
    event.assessment_started_at ? 'assess' : 'select'
  );
  const [mobileSelectedCriteria, setMobileSelectedCriteria] = useState<Record<string, boolean>>({});
  const [mobileActiveCriterionKey, setMobileActiveCriterionKey] = useState<string | null>(null);
  const [mobileElementCriteriaOpen, setMobileElementCriteriaOpen] = useState<Record<string, boolean>>({});
  const [mobileAssessmentStarted, setMobileAssessmentStarted] = useState(() => !!event.assessment_started_at);
  const [mobileAttachmentPreview, setMobileAttachmentPreview] = useState<{ name: string; url: string } | null>(null);
  const [mobileAttachmentPreviewLoadingId, setMobileAttachmentPreviewLoadingId] = useState<string | number | null>(null);
  const mobileAttachmentPreviewRevokeRef = useRef<(() => void) | null>(null);
  const [persistingAssessmentsToFeedback, setPersistingAssessmentsToFeedback] = useState(false);
  const [priorEventAssessedCriterionKeys, setPriorEventAssessedCriterionKeys] = useState<Set<string>>(new Set());
  const [assessedCountAcrossCycle, setAssessedCountAcrossCycle] = useState<number | null>(null);
  const useGuidedAssessmentFlow = true;
  /** Ignore stale GET responses when multiple cycle-assessment fetches overlap (e.g. save + mount). */
  const fetchAssessmentsSeqRef = useRef(0);
  const onStartAssessmentRef = useRef(onStartAssessment);
  onStartAssessmentRef.current = onStartAssessment;
  /** Only force mobile "assess" phase once per event + assessment_started_at; avoids undoing "Select units" on parent re-renders. */
  const mobileForcedAssessKeyRef = useRef<string | null>(null);

  const groupedCriteria = useMemo(() => {
    const unitMap: Record<string, { key: string; label: string; elements: Record<string, { key: string; label: string; rows: CustomCycleCriterionRow[] }> }> = {};
    for (const row of criteria) {
      const unitKey = row.unit_no?.trim() || 'no-unit';
      const rawUnitNo = row.unit_no?.trim() || '';
      const cleanedUnitNo = rawUnitNo.replace(/^u/i, '');
      const unitLabel = cleanedUnitNo ? `Unit ${cleanedUnitNo}` : 'Additional criteria';
      if (!unitMap[unitKey]) unitMap[unitKey] = { key: unitKey, label: unitLabel, elements: {} };
      const unit = unitMap[unitKey];
      const elementKey = `${unitKey}::${row.element_no?.trim() || row.element}`;
      const elementNo = row.element_no?.trim() || '';
      const cleanedElementNo = elementNo.replace(/^e/i, '');
      const rawElementName = row.element?.trim() || '';
      const cleanedElementName = cleanedElementNo
        ? rawElementName.replace(new RegExp(`^(?:element\\s*)?${escapeRegExp(cleanedElementNo)}\\s*[-.:)]?\\s*`, 'i'), '').trim()
        : rawElementName;
      const elementLabel = [cleanedElementNo || null, cleanedElementName || rawElementName].filter(Boolean).join(' · ');
      if (!unit.elements[elementKey]) unit.elements[elementKey] = { key: elementKey, label: elementLabel, rows: [] };
      unit.elements[elementKey].rows.push(row);
    }
    return Object.values(unitMap).map((unit) => ({
      key: unit.key,
      label: unit.label,
      elements: Object.values(unit.elements),
    }));
  }, [criteria]);

  const criterionKeyToLocation = useMemo(() => {
    const m = new Map<string, { unitKey: string; elementKey: string }>();
    for (const u of groupedCriteria) {
      for (const el of u.elements) {
        for (const row of el.rows) {
          m.set(customCriterionKey(row.id), { unitKey: u.key, elementKey: el.key });
        }
      }
    }
    return m;
  }, [groupedCriteria]);

  useLayoutEffect(() => {
    if (!requestFocusCriterionKey) return;
    const loc = criterionKeyToLocation.get(requestFocusCriterionKey);
    if (!loc) {
      onRequestFocusCriterionHandled?.();
      return;
    }
    const domId = `rv-custom-criterion-${requestFocusCriterionKey.replace(/[^a-zA-Z0-9_-]+/g, '_')}`;
    setExpandedUnits((prev) => ({ ...prev, [loc.unitKey]: true }));
    setExpandedElements((prev) => ({ ...prev, [loc.elementKey]: true }));
    if (isMobileOrTablet) {
      flushSync(() => {
        setMobileSelectedCriteria((prev) => ({ ...prev, [requestFocusCriterionKey]: true }));
        setMobileAssessmentStarted(true);
        setMobilePhase('assess');
        setMobileActiveCriterionKey(requestFocusCriterionKey);
      });
    }
    requestAnimationFrame(() => {
      document.getElementById(domId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onRequestFocusCriterionHandled?.();
    });
  }, [requestFocusCriterionKey, criterionKeyToLocation, isMobileOrTablet, onRequestFocusCriterionHandled]);

  const groupedCriteriaForDisplay = useMemo(() => {
    if (!isLastEvent || priorEventIds.length === 0) {
      return groupedCriteria;
    }
    return groupedCriteria.map((unit) => ({
      ...unit,
      elements: unit.elements
        .map((element) => ({
          ...element,
          rows: element.rows.filter((row) => !priorEventAssessedCriterionKeys.has(customCriterionKey(row.id))),
        }))
        .filter((element) => element.rows.length > 0),
    })).filter((unit) => unit.elements.length > 0);
  }, [groupedCriteria, isLastEvent, priorEventIds, priorEventAssessedCriterionKeys]);

  const groupedCriteriaOutstanding = useMemo(() => {
    return groupedCriteriaForDisplay.map((unit) => ({
      ...unit,
      elements: unit.elements.filter((element) => {
        const allAssessed = element.rows.every((row) => {
          const grade = assessments[customCriterionKey(row.id)]?.grade;
          return isGradeAssessed(grade);
        });
        return !allAssessed;
      }),
    })).filter((unit) => unit.elements.length > 0);
  }, [groupedCriteriaForDisplay, assessments]);

  const groupedCriteriaAssessed = useMemo(() => {
    return groupedCriteriaForDisplay.map((unit) => ({
      ...unit,
      elements: unit.elements
        .map((element) => ({
          ...element,
          rows: element.rows.filter((row) => {
            const grade = assessments[customCriterionKey(row.id)]?.grade;
            return isGradeAssessed(grade);
          }),
        }))
        .filter((element) => element.rows.length > 0),
    })).filter((unit) => unit.elements.length > 0);
  }, [groupedCriteriaForDisplay, assessments]);

  const displayGroupedCriteria = showAssessedOnly ? groupedCriteriaAssessed : groupedCriteriaOutstanding;
  const displayGroupedCriteriaRef = useRef(displayGroupedCriteria);
  displayGroupedCriteriaRef.current = displayGroupedCriteria;
  /** Which rows appear in the modal tree (ids only). Changes when structure changes, not when only a grade updates. */
  const assessmentModalLayoutKey = useMemo(() => {
    try {
      return JSON.stringify(
        displayGroupedCriteria.map((u) => ({
          uk: u.key,
          es: u.elements.map((e) => ({
            ek: e.key,
            rids: e.rows.map((r) => String(r.id)).sort(),
          })),
        }))
      );
    } catch {
      return '';
    }
  }, [displayGroupedCriteria]);
  /**
   * In assess phase every criterion can have a lenient "recorded" grade while outstanding is empty, which clears
   * `displayGroupedCriteria`. Keep showing the full milestone tree so Previous/Next grading still works.
   */
  const groupedCriteriaForMobileNavigator = useMemo(() => {
    if (
      useGuidedAssessmentFlow &&
      mobilePhase === 'assess' &&
      displayGroupedCriteria.length === 0 &&
      groupedCriteriaForDisplay.length > 0
    ) {
      return groupedCriteriaForDisplay;
    }
    return displayGroupedCriteria;
  }, [useGuidedAssessmentFlow, mobilePhase, displayGroupedCriteria, groupedCriteriaForDisplay]);
  const mobileAllElements = useMemo(
    () =>
      groupedCriteriaForMobileNavigator.flatMap((unit) =>
        unit.elements.map((element) => ({
          ...element,
          unitLabel: unit.label,
        }))
      ),
    [groupedCriteriaForMobileNavigator]
  );
  const mobileElementSteps = useMemo(
    () =>
      groupedCriteriaForMobileNavigator.flatMap((unit) =>
        unit.elements.map((element) => ({
          ...element,
          unitLabel: unit.label,
        }))
      ),
    [groupedCriteriaForMobileNavigator]
  );
  const mobileUnassessedByElement = useMemo(() => {
    const map: Record<string, string[]> = {};
    mobileAllElements.forEach((element) => {
      map[element.key] = element.rows
        .filter((row) => !isGradeAssessed(assessments[customCriterionKey(row.id)]?.grade))
        .map((row) => customCriterionKey(row.id));
    });
    return map;
  }, [mobileAllElements, assessments]);
  const carryForwardElementKeys = useMemo(() => {
    if (priorEventIds.length === 0) return [];
    return mobileAllElements
      .filter((element) => {
        const touchedInPrior = element.rows.some((row) => {
          const a = assessments[customCriterionKey(row.id)];
          return (
            isGradeAssessed(a?.grade)
            && a?.employee_cycle_event_id != null
            && priorEventIds.some((pid) => String(pid) === String(a.employee_cycle_event_id))
          );
        });
        const hasUnassessed = element.rows.some((row) => !isGradeAssessed(assessments[customCriterionKey(row.id)]?.grade));
        return touchedInPrior && hasUnassessed;
      })
      .map((element) => element.key);
  }, [mobileAllElements, priorEventIds, assessments]);
  const mobileSelectedElements = useMemo(
    () =>
      mobileAllElements.reduce<Record<string, boolean>>((acc, element) => {
        acc[element.key] = element.rows.some((row) => !!mobileSelectedCriteria[customCriterionKey(row.id)]);
        return acc;
      }, {}),
    [mobileAllElements, mobileSelectedCriteria]
  );
  const mobileSelectedRows = useMemo(
    () =>
      mobileAllElements.flatMap((element) =>
        element.rows.filter((row) => !!mobileSelectedCriteria[customCriterionKey(row.id)])
      ),
    [mobileAllElements, mobileSelectedCriteria]
  );
  const mobileSelectedRowIdsForEvidence = useMemo(
    () => mobileSelectedRows.map((row) => row.id),
    [mobileSelectedRows]
  );
  const mobileActiveRow = useMemo(
    () => mobileSelectedRows.find((row) => customCriterionKey(row.id) === mobileActiveCriterionKey) ?? null,
    [mobileSelectedRows, mobileActiveCriterionKey]
  );
  const mobileActiveIndex = mobileActiveRow ? mobileSelectedRows.findIndex((row) => row.id === mobileActiveRow.id) : -1;
  const mobileCriterionNavAnchorRef = useRef<HTMLDivElement>(null);
  const prevMobileCriterionKeyForCueRef = useRef<string | null>(null);
  const [mobileCriterionNavCue, setMobileCriterionNavCue] = useState(false);

  useLayoutEffect(() => {
    if (mobilePhase !== 'assess' || !mobileActiveCriterionKey || !mobileActiveRow) {
      if (mobilePhase !== 'assess') prevMobileCriterionKeyForCueRef.current = null;
      return;
    }
    const prev = prevMobileCriterionKeyForCueRef.current;
    const navigated = prev !== null && prev !== mobileActiveCriterionKey;
    prevMobileCriterionKeyForCueRef.current = mobileActiveCriterionKey;
    if (navigated) {
      requestAnimationFrame(() => {
        mobileCriterionNavAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      setMobileCriterionNavCue(true);
      const t = window.setTimeout(() => setMobileCriterionNavCue(false), 900);
      return () => clearTimeout(t);
    }
  }, [mobileActiveCriterionKey, mobilePhase, mobileActiveRow]);

  const selectedCriteriaRows = useMemo(
    () => criteria.filter((row) => !!selectedCriteria[customCriterionKey(row.id)]),
    [criteria, selectedCriteria]
  );
  const eventScopeCriteriaRows = useMemo(() => {
    if (useGuidedAssessmentFlow && mobileSelectedRows.length > 0) return mobileSelectedRows;
    if (useGuidedAssessmentFlow && event.assessment_started_at && Array.isArray(event.assessment_started_criterion_keys) && event.assessment_started_criterion_keys.length > 0) {
      const keys = new Set(event.assessment_started_criterion_keys);
      return criteria.filter((row) => keys.has(customCriterionKey(row.id)));
    }
    if (useGuidedAssessmentFlow && event.assessment_started_at && Array.isArray(event.assessment_started_element_keys) && event.assessment_started_element_keys.length > 0) {
      const keys = new Set(event.assessment_started_element_keys);
      return groupedCriteria.flatMap((unit) =>
        unit.elements
          .filter((el) => keys.has(el.key))
          .flatMap((el) => el.rows)
      );
    }
    return selectedCriteriaRows;
  }, [useGuidedAssessmentFlow, event.assessment_started_at, event.assessment_started_criterion_keys, event.assessment_started_element_keys, groupedCriteria, criteria, mobileSelectedRows, selectedCriteriaRows]);
  /**
   * When every criterion is graded, `groupedCriteriaOutstanding` is empty so `mobileAllElements` / `mobileSelectedRows`
   * become empty and `eventScopeCriteriaRows` can drop to `[]` even though the event is done. Fall back to rows already
   * saved against this event id so completion + inline messaging stay consistent with "N of N assessed".
   */
  const criteriaRowsForCompletionCheck = useMemo(() => {
    if (eventScopeCriteriaRows.length > 0) return eventScopeCriteriaRows;
    return criteria.filter(
      (row) => String(assessments[customCriterionKey(row.id)]?.employee_cycle_event_id ?? '') === String(event.id)
    );
  }, [eventScopeCriteriaRows, criteria, assessments, event.id]);

  /** Outstanding criteria for the last event (excludes keys already final-graded on earlier events). */
  const lastEventOutstandingRows = useMemo(() => {
    if (!isLastEvent) return [] as CustomCycleCriterionRow[];
    return groupedCriteriaForDisplay.flatMap((unit) => unit.elements.flatMap((el) => el.rows));
  }, [isLastEvent, groupedCriteriaForDisplay]);

  /**
   * Criteria-only eligibility.
   * Last event: only require grades/methods for criteria still outstanding on THIS milestone.
   * If earlier events already covered every criterion acceptably, this event is feedback-only — do not
   * block on `assessment_started_criterion_keys` that point at prior-graded rows missing from this event’s map.
   */
  const eventEligibleToComplete = useMemo(() => {
    const checkRow = (row: CustomCycleCriterionRow) => {
      const a = assessments[customCriterionKey(row.id)];
      if (isLastEvent) {
        if (!isFinalEventAcceptableGrade(a?.grade)) return false;
        return hasAssessmentMethodSatisfied(a?.grade, a?.assessment_method);
      }
      if (!isGradeAssessed(a?.grade)) return false;
      return hasAssessmentMethodSatisfied(a?.grade, a?.assessment_method);
    };

    if (isLastEvent) {
      if (criteria.length === 0) return false;
      if (lastEventOutstandingRows.length === 0) return true;
      const scopeOutstanding = criteriaRowsForCompletionCheck.filter((row) =>
        lastEventOutstandingRows.some((m) => String(m.id) === String(row.id))
      );
      const toCheck = scopeOutstanding.length > 0 ? scopeOutstanding : lastEventOutstandingRows;
      return toCheck.every(checkRow);
    }

    const scope = criteriaRowsForCompletionCheck;
    if (scope.length > 0) return scope.every(checkRow);
    const milestoneFlat = groupedCriteriaForDisplay.flatMap((unit) =>
      unit.elements.flatMap((el) => el.rows)
    );
    if (milestoneFlat.length > 0) return milestoneFlat.every(checkRow);
    return false;
  }, [
    criteriaRowsForCompletionCheck,
    groupedCriteriaForDisplay,
    lastEventOutstandingRows,
    assessments,
    isLastEvent,
    criteria.length,
  ]);

  const isMonitoringAssessmentEvent = String(event.event_type ?? 'assessment').toLowerCase() === 'monitoring';
  const canMarkAssessmentEventComplete =
    eventEligibleToComplete &&
    (isMonitoringAssessmentEvent || isFinalAssessmentFeedbackSufficient(event.final_assessment_feedback));
  const markCompleteDisabledReason =
    !eventEligibleToComplete
      ? isLastEvent
        ? 'Final assessment: every outstanding criterion needs a substantive grade and an assessment method. Not applicable and Not yet Assessed are not allowed.'
        : 'Each criterion in this event needs a grade and, where applicable, an assessment method (Not applicable fixes method to N/A).'
      : !isMonitoringAssessmentEvent && !isFinalAssessmentFeedbackSufficient(event.final_assessment_feedback)
        ? 'Fill General observations, Personal attributes, and Candidate feedback on the final feedback step, then tap Complete Assessment.'
        : undefined;

  const runMarkEventCompleted = useCallback(async () => {
    setIsSubmittingAssessment(true);
    try {
      await onMarkCompleted?.();
    } finally {
      setIsSubmittingAssessment(false);
    }
  }, [onMarkCompleted]);

  const fetchAssessments = useCallback(() => {
    const mySeq = ++fetchAssessmentsSeqRef.current;
    const eventParam =
      event?.id != null
        ? `${cc ? "&" : "?"}eventId=${encodeURIComponent(String(event.id))}`
        : "";
    return fetch(
      `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(employeeCycleId))}${cc}${eventParam}`
    )
      .then((res) => (res.ok ? res.json() : {}))
      .then((data: Record<string, EmployeeCriterionAssessment>) => {
        if (mySeq !== fetchAssessmentsSeqRef.current) return;
        const normalized: Record<string, EmployeeCriterionAssessment> = {};
        for (const k of Object.keys(data || {})) {
          const v = data[k];
          normalized[k] = {
            grade: v?.grade ?? null,
            notes: v?.notes ?? null,
            assessed_at: v?.assessed_at ?? null,
            assessed_by: v?.assessed_by ?? null,
            assessment_method: v?.assessment_method ?? null,
            employee_cycle_event_id: v?.employee_cycle_event_id ?? null,
          };
        }
        setAssessments(normalized);
      });
  }, [employeeId, employeeCycleId, cc, event?.id]);

  const fetchPriorAssessments = useCallback(() => {
    if (!priorEventIdsKey) return Promise.resolve({});
    const priorParam = `${cc ? "&" : "?"}priorEventIds=${priorEventIdsKey.split(",").map((pid) => encodeURIComponent(pid)).join(",")}`;
    return fetch(
      `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(employeeCycleId))}${cc}${priorParam}`
    )
      .then((res) => (res.ok ? res.json() : {}))
      .then((data: Record<string, EmployeeCriterionAssessment>) => data || {});
  }, [employeeId, employeeCycleId, cc, priorEventIdsKey]);

  const fetchAllCycleAssessments = useCallback(() => {
    return fetch(
      `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(employeeCycleId))}${cc}`
    )
      .then((res) => (res.ok ? res.json() : {}))
      .then((data: Record<string, EmployeeCriterionAssessment>) => {
        let count = 0;
        for (const k of Object.keys(data || {})) {
          if (isGradeAssessed(data[k]?.grade)) count++;
        }
        return count;
      });
  }, [employeeId, employeeCycleId, cc]);

  const fetchEvidenceForCriterion = useCallback(
    (criterionId: number | string) => {
      const criterionKey = customCriterionKey(criterionId);
      const path = `assessments/${String(storageEcId).trim()}/${criterionKey.replace(/:/g, "_")}`;
      return fetch(
        `/api/employees/${encodeURIComponent(employeeId)}/documents?path=${encodeURIComponent(path)}${companyCode ? "&companyCode=" + encodeURIComponent(companyCode) : ""}`,
        companyHeaders ? { headers: companyHeaders } : undefined
      )
        .then(async (res) => {
          if (!res.ok) {
            const hint = await res.text().catch(() => '');
            console.warn('[evidence] list fetch failed', res.status, hint.slice(0, 300));
            return [];
          }
          return res.json();
        })
        .then((docs: { id: string | number; name: string; is_folder?: number }[]) => {
          const files = (Array.isArray(docs) ? docs : []).filter((d) => Number(d.is_folder ?? 0) === 0);
          setEvidenceByCriterion((prev) => ({ ...prev, [criterionKey]: files }));
        });
    },
    [employeeId, storageEcId, companyCode, companyHeaders]
  );

  const uploadEvidenceFile = (criterionId: number | string, file: File, slot: 'document' | 'photo') => {
    if (!canMutateEvidence) return;
    const criterionKey = customCriterionKey(criterionId);
    const path = `assessments/${String(storageEcId).trim()}/${criterionKey.replace(/:/g, "_")}`;
    const setUploading = slot === 'document' ? setUploadingEvidenceDocument : setUploadingEvidencePhoto;
    setUploading((u) => ({ ...u, [criterionKey]: true }));
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const content = reader.result as string;
      fetch(`/api/employees/${encodeURIComponent(employeeId)}/documents${cc}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(companyHeaders ?? {}),
        },
        body: JSON.stringify({ name: file.name, path, content, is_folder: 0 }),
      })
        .then(async (res) => {
          if (res.ok) {
            onClosedCycleSummaryInvalidate?.();
            try {
              const parsed = (await res.clone().json()) as { id?: string | number };
              if (parsed?.id != null) {
                const newId = parsed.id;
                setEvidenceByCriterion((prev) => {
                  const cur = prev[criterionKey] ?? [];
                  if (cur.some((x) => String(x.id) === String(newId))) return prev;
                  return { ...prev, [criterionKey]: [...cur, { id: newId, name: file.name }] };
                });
              }
            } catch {
              /* refetch will populate */
            }
            fetchEvidenceForCriterion(criterionId);
            window.setTimeout(() => fetchEvidenceForCriterion(criterionId), 600);
          } else {
            let parsed: { code?: string } = {};
            try {
              parsed = (await res.json()) as { code?: string };
            } catch {
              /* ignore */
            }
            const storageQuotaBlock = res.status === 402 || parsed?.code === 'STORAGE_SUSPENDED';
            if (storageQuotaBlock) {
              setStorageQuotaUploadDialogOpen(true);
            } else {
              console.warn('[evidence] upload failed', res.status, parsed);
            }
          }
        })
        .catch((err) => {
          console.warn('[evidence] upload network error', err);
        })
        .finally(() => setUploading((u) => ({ ...u, [criterionKey]: false })));
    };
  };

  const deleteEvidenceFile = (docId: string | number, criterionId: number | string) => {
    if (!canMutateEvidence) return;
    fetch(`/api/documents/${encodeURIComponent(String(docId))}${companyCode ? "?companyCode=" + encodeURIComponent(companyCode) : ""}`, {
      method: 'DELETE',
      headers: companyHeaders,
    })
      .then((res) => {
        if (res.ok) {
          onClosedCycleSummaryInvalidate?.();
          fetchEvidenceForCriterion(criterionId);
        }
      });
  };

  const closeMobileAttachmentPreview = useCallback(() => {
    mobileAttachmentPreviewRevokeRef.current?.();
    mobileAttachmentPreviewRevokeRef.current = null;
    setMobileAttachmentPreview(null);
  }, []);

  useEffect(
    () => () => {
      mobileAttachmentPreviewRevokeRef.current?.();
      mobileAttachmentPreviewRevokeRef.current = null;
    },
    []
  );

  const openMobileAttachmentPreview = useCallback(
    async (docId: string | number, name: string) => {
      setMobileAttachmentPreviewLoadingId(docId);
      try {
        const res = await fetch(`/api/documents/${encodeURIComponent(String(docId))}/download${cc}`, {
          headers: companyHeaders,
        });
        if (!res.ok) throw new Error(`Preview request failed (${res.status})`);
        const text = await res.text();
        let payload: unknown = {};
        try {
          payload = text ? JSON.parse(text) : {};
        } catch {
          payload = text;
        }
        const rawContent = payload && typeof payload === 'object' && 'content' in payload
          ? (payload as { content?: unknown }).content
          : payload;
        const dataUrl = toAttachmentDataUrl(rawContent, name);
        if (!dataUrl) throw new Error('Missing attachment content');
        mobileAttachmentPreviewRevokeRef.current?.();
        const { url, revoke } = dataUrlToPreviewObjectUrl(dataUrl, name);
        mobileAttachmentPreviewRevokeRef.current = revoke;
        setMobileAttachmentPreview({ name, url });
      } catch (error) {
        console.error('Unable to preview attachment:', error);
        window.alert(translateUi("Unable to preview this attachment. Please try downloading it."));
      } finally {
        setMobileAttachmentPreviewLoadingId((current) => (current === docId ? null : current));
      }
    },
    [cc, companyHeaders]
  );

  useEffect(() => {
    const dgc = displayGroupedCriteriaRef.current;
    if (dgc.length === 0) return;
    const nextUnits: Record<string, boolean> = {};
    const nextElements: Record<string, boolean> = {};
    dgc.forEach((unit) => {
      nextUnits[unit.key] = true;
      unit.elements.forEach((element) => {
        nextElements[element.key] = false;
      });
    });
    setExpandedUnits(nextUnits);
    setExpandedElements(nextElements);
    setSelectedCriteria({});
    setElementBulkGrades({});
    setManualGradeOverrides({});
    setMobileElementCriteriaOpen({});
    const mobileEls = dgc.flatMap((unit) =>
      unit.elements.map((element) => ({
        ...element,
        unitLabel: unit.label,
      }))
    );
    if (event.assessment_started_at) {
      setMobileAssessmentStarted(true);
      const forceKey = `${event.id}|${String(event.assessment_started_at)}`;
      if (mobileForcedAssessKeyRef.current !== forceKey) {
        mobileForcedAssessKeyRef.current = forceKey;
        // Last event with nothing left to grade → open feedback (not empty assess pane).
        setMobilePhase(isLastEvent && mobileEls.length === 0 ? 'feedback' : 'assess');
      }
    } else if (isLastEvent && mobileEls.length === 0) {
      // All criteria already final-graded earlier: start the event and go straight to feedback/notes.
      mobileForcedAssessKeyRef.current = null;
      setMobileAssessmentStarted(true);
      setMobilePhase('feedback');
      setMobileSelectedCriteria({});
      onStartAssessmentRef.current?.([], []);
    } else if (isLastEvent && mobileEls.length > 0) {
      mobileForcedAssessKeyRef.current = null;
      setMobileAssessmentStarted(true);
      setMobilePhase('assess');
      setMobileSelectedCriteria((prev) => {
        const next = { ...prev };
        mobileEls.forEach((element) => {
          element.rows.forEach((row) => { next[customCriterionKey(row.id)] = true; });
        });
        return next;
      });
      const criterionKeys = mobileEls.flatMap((el) => el.rows.map((r) => customCriterionKey(r.id)));
      const elementKeys = mobileEls.map((el) => el.key);
      onStartAssessmentRef.current?.(criterionKeys, elementKeys);
    } else {
      mobileForcedAssessKeyRef.current = null;
      setMobileAssessmentStarted(false);
      setMobilePhase('select');
      setMobileSelectedCriteria({});
    }
  }, [assessmentModalLayoutKey, event.id, event.assessment_started_at, isLastEvent]);

  /**
   * After criteria are fully satisfied (same rules as Continue to feedback), reopen on final feedback.
   * Runs once per modal mount so Back to criteria is respected until the modal closes.
   */
  const didRestoreFinalFeedbackStepRef = useRef(false);
  useEffect(() => {
    didRestoreFinalFeedbackStepRef.current = false;
  }, [event.id]);
  useEffect(() => {
    if (!useGuidedAssessmentFlow || isMonitoringAssessmentEvent || event.status === 'completed') return;
    if (!eventEligibleToComplete) return;
    if (didRestoreFinalFeedbackStepRef.current) return;
    didRestoreFinalFeedbackStepRef.current = true;
    setMobilePhase('feedback');
  }, [useGuidedAssessmentFlow, isMonitoringAssessmentEvent, event.status, eventEligibleToComplete]);

  useEffect(() => {
    if (mobileElementSteps.length === 0) {
      setActiveMobileElementKey(null);
      return;
    }
    if (!activeMobileElementKey || !mobileElementSteps.some((el) => el.key === activeMobileElementKey)) {
      setActiveMobileElementKey(mobileElementSteps[0].key);
    }
  }, [mobileElementSteps, activeMobileElementKey]);

  useEffect(() => {
    if (!useGuidedAssessmentFlow) return;
    const hasExistingSelection = mobileAllElements.some((element) =>
      element.rows.some((row) => !!mobileSelectedCriteria[customCriterionKey(row.id)])
    );
    if (!(event.assessment_started_at && mobilePhase === 'assess' && !hasExistingSelection)) {
      return;
    }
    const storedCriterionKeys = event.assessment_started_criterion_keys;
    if (Array.isArray(storedCriterionKeys) && storedCriterionKeys.length > 0) {
      const nextCriteriaSelection = mobileAllElements.reduce<Record<string, boolean>>((acc, element) => {
        element.rows.forEach((row) => {
          acc[customCriterionKey(row.id)] = storedCriterionKeys.includes(customCriterionKey(row.id));
        });
        return acc;
      }, {});
      const hasChanged = Object.keys(nextCriteriaSelection).some(
        (k) => !!nextCriteriaSelection[k] !== !!mobileSelectedCriteria[k]
      ) || Object.keys(mobileSelectedCriteria).some(
        (k) => !!nextCriteriaSelection[k] !== !!mobileSelectedCriteria[k]
      );
      if (hasChanged) setMobileSelectedCriteria(nextCriteriaSelection);
      return;
    }
    const nextElementSelection = mobileAllElements.reduce<Record<string, boolean>>((acc, element) => {
      acc[element.key] = false;
      return acc;
    }, {});
    if (isLastEvent) {
      Object.keys(nextElementSelection).forEach((key) => {
        if ((mobileUnassessedByElement[key]?.length ?? 0) > 0) nextElementSelection[key] = true;
      });
    }
    const storedElementKeys = event.assessment_started_element_keys;
    if (Array.isArray(storedElementKeys) && storedElementKeys.length > 0) {
      storedElementKeys.forEach((key) => { if (key in nextElementSelection) nextElementSelection[key] = true; });
    } else {
      Object.keys(nextElementSelection).forEach((key) => { nextElementSelection[key] = true; });
    }
    const nextCriteriaSelection = mobileAllElements.reduce<Record<string, boolean>>((acc, element) => {
      const selected = !!nextElementSelection[element.key];
      element.rows.forEach((row) => { acc[customCriterionKey(row.id)] = selected; });
      return acc;
    }, {});
    const hasChanged = Object.keys(nextCriteriaSelection).some(
      (k) => !!nextCriteriaSelection[k] !== !!mobileSelectedCriteria[k]
    ) || Object.keys(mobileSelectedCriteria).some(
      (k) => !!nextCriteriaSelection[k] !== !!mobileSelectedCriteria[k]
    );
    if (hasChanged) setMobileSelectedCriteria(nextCriteriaSelection);
  }, [useGuidedAssessmentFlow, isLastEvent, event.assessment_started_at, event.assessment_started_criterion_keys, event.assessment_started_element_keys, mobilePhase, mobileAllElements, mobileSelectedCriteria, mobileUnassessedByElement]);

  useEffect(() => {
    if (!useGuidedAssessmentFlow || mobilePhase !== 'select' || isLastEvent || carryForwardElementKeys.length === 0) return;
    const hasAnyCarryExcluded = carryForwardElementKeys.some((key) => !mobileSelectedElements[key]);
    if (!hasAnyCarryExcluded) return;
    const include = window.confirm(translateUi("Some previously assessed elements still contain unassessed criteria. Include those unassessed criteria in this event?"));
    if (!include) return;
    setMobileSelectedCriteria((prev) => {
      const next = { ...prev };
      carryForwardElementKeys.forEach((key) => {
        const element = mobileAllElements.find((el) => el.key === key);
        element?.rows.forEach((row) => { next[customCriterionKey(row.id)] = true; });
      });
      return next;
    });
  }, [useGuidedAssessmentFlow, mobilePhase, isLastEvent, carryForwardElementKeys, mobileSelectedElements, mobileAllElements]);

  useEffect(() => {
    if (!useGuidedAssessmentFlow || mobilePhase !== 'assess') return;
    if (mobileSelectedRows.length === 0) {
      setMobileActiveCriterionKey(null);
      return;
    }
    if (!mobileActiveCriterionKey || !mobileSelectedRows.some((row) => customCriterionKey(row.id) === mobileActiveCriterionKey)) {
      setMobileActiveCriterionKey(customCriterionKey(mobileSelectedRows[0].id));
    }
  }, [useGuidedAssessmentFlow, mobilePhase, mobileSelectedRows, mobileActiveCriterionKey]);

  /** Guided chip strip shows paperclips for attachments on any numbered criterion — prefetch lists for all selected rows. */
  useEffect(() => {
    if (!useGuidedAssessmentFlow || mobilePhase !== 'assess') return;
    if (!storageEcId || String(storageEcId).trim() === '') return;
    if (mobileSelectedRowIdsForEvidence.length === 0) return;
    for (const id of mobileSelectedRowIdsForEvidence) {
      void fetchEvidenceForCriterion(id);
    }
  }, [useGuidedAssessmentFlow, mobilePhase, storageEcId, mobileSelectedRowIdsForEvidence, fetchEvidenceForCriterion]);

  useEffect(() => {
    let cancelled = false;
    if (companyCode) {
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/grading-scales`)
        .then((res) => (res.ok ? res.json() : []))
        .then((grades) => {
          if (!cancelled) setGradingScale(Array.isArray(grades) ? grades : []);
        })
        .catch(() => {
          if (!cancelled) setGradingScale([]);
        });
    } else {
      setGradingScale([]);
    }
    fetchAssessments().catch(() => {
      if (!cancelled) setAssessments({});
    });
    if (isLastEvent && priorEventIdsKey) {
      fetchPriorAssessments().then((prior) => {
        if (cancelled) return;
        const keys = new Set<string>();
        for (const k of Object.keys(prior || {})) {
          const v = prior[k];
          if (isFinalEventAcceptableGrade(v?.grade)) keys.add(k);
        }
        setPriorEventAssessedCriterionKeys(keys);
      });
    } else {
      setPriorEventAssessedCriterionKeys(new Set());
    }
    if (isLastEvent) {
      fetchAllCycleAssessments().then((count) => {
        if (!cancelled) setAssessedCountAcrossCycle(count);
      });
    } else {
      setAssessedCountAcrossCycle(null);
    }
    return () => {
      cancelled = true;
    };
  }, [companyCode, fetchAssessments, fetchPriorAssessments, fetchAllCycleAssessments, event.id, isLastEvent, priorEventIdsKey]);

  const saveAssessment = useCallback(
    async (
      criterion: CustomCycleCriterionRow,
      next: { grade: string | null; notes: string | null; assessment_method?: string | null },
      opts?: { refetch?: boolean; previousGrade?: string | null }
    ) => {
      const key = customCriterionKey(criterion.id);
      const gradeToSave = next.grade && isGradeAssessed(next.grade) ? next.grade : null;
      const previousGrade =
        opts?.previousGrade !== undefined
          ? opts.previousGrade
          : (assessments[key]?.grade ?? null);
      const shouldRefetch = opts?.refetch === true;
      try {
        const res = await fetch(
          `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(employeeCycleId))}/criteria/${encodeURIComponent(key)}${cc}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail ?? '' },
            body: JSON.stringify({
              grade: gradeToSave,
              notes: next.notes,
              assessment_method: next.assessment_method ?? null,
              userEmail: currentUserEmail,
              employee_cycle_event_id: event.id,
            }),
          }
        );
        if (!res.ok) {
          await alertIfExclusiveAssessmentDenied(res);
          await fetchAssessments();
          return false;
        }
        onClosedCycleSummaryInvalidate?.();
        if (String(previousGrade ?? '') !== String(gradeToSave ?? '')) {
          onAssessmentProgressPatch?.({
            eventId: event.id,
            previousGrade,
            nextGrade: gradeToSave,
          });
        }
        if (shouldRefetch) await fetchAssessments();
        if (isLastEvent) {
          const was = isGradeAssessed(previousGrade);
          const now = isGradeAssessed(gradeToSave);
          if (was !== now) {
            setAssessedCountAcrossCycle((prev) => {
              if (prev == null) return prev;
              return Math.max(0, prev + (now ? 1 : -1));
            });
          }
        }
        return true;
      } catch (error) {
        console.error(error);
        await fetchAssessments();
        return false;
      }
    },
    [
      assessments,
      cc,
      currentUserEmail,
      employeeCycleId,
      employeeId,
      event.id,
      fetchAssessments,
      isLastEvent,
      onClosedCycleSummaryInvalidate,
      onAssessmentProgressPatch,
    ]
  );

  const saveAssessmentInBackground = useCallback(
    (
      criterion: CustomCycleCriterionRow,
      next: { grade: string | null; notes: string | null; assessment_method?: string | null },
      opts?: { previousGrade?: string | null }
    ) => {
      void saveAssessment(criterion, next, { ...opts, refetch: false });
    },
    [saveAssessment]
  );

  const assessedCount = useMemo(() => {
    if (isLastEvent && assessedCountAcrossCycle != null) return assessedCountAcrossCycle;
    return criteria.filter((row) => {
      const grade = assessments[customCriterionKey(row.id)]?.grade;
      return isGradeAssessed(grade);
    }).length;
  }, [criteria, assessments, isLastEvent, assessedCountAcrossCycle]);

  const finalMilestoneCriterionTotal = useMemo(
    () =>
      groupedCriteriaForDisplay.reduce(
        (n, u) => n + u.elements.reduce((m, el) => m + el.rows.length, 0),
        0
      ),
    [groupedCriteriaForDisplay]
  );
  const finalMilestoneStrictReadyCount = useMemo(() => {
    if (!isLastEvent) return null;
    let n = 0;
    for (const u of groupedCriteriaForDisplay) {
      for (const el of u.elements) {
        for (const row of el.rows) {
          const a = assessments[customCriterionKey(row.id)];
          if (
            isFinalEventAcceptableGrade(a?.grade) &&
            hasAssessmentMethodSatisfied(a?.grade, a?.assessment_method)
          ) {
            n++;
          }
        }
      }
    }
    return n;
  }, [isLastEvent, groupedCriteriaForDisplay, assessments]);
  const assessmentEventModalSubtitle = useMemo(() => {
    if (isLastEvent && finalMilestoneStrictReadyCount != null) {
      if (finalMilestoneCriterionTotal === 0 && assessedCount > 0) {
        return `Final assessment: all ${assessedCount} criteria already covered earlier — complete feedback to finish`;
      }
      return `Final assessment: ${finalMilestoneStrictReadyCount} of ${finalMilestoneCriterionTotal} criteria meet requirements · ${assessedCount} of ${criteria.length} with any recorded grade`;
    }
    return `${assessedCount} of ${criteria.length} criteria assessed`;
  }, [
    isLastEvent,
    finalMilestoneStrictReadyCount,
    finalMilestoneCriterionTotal,
    assessedCount,
    criteria.length,
  ]);

  const toggleCriterionSelection = (criterionId: number | string) => {
    const key = customCriterionKey(criterionId);
    setSelectedCriteria((prev) => ({ ...prev, [key]: !prev[key] }));
    setManualGradeOverrides((prev) => ({ ...prev, [key]: false }));
  };

  const toggleElementSelection = (rows: CustomCycleCriterionRow[]) => {
    const keys = rows.map((row) => customCriterionKey(row.id));
    const allSelected = keys.every((key) => !!selectedCriteria[key]);
    setSelectedCriteria((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = !allSelected;
      });
      return next;
    });
    setManualGradeOverrides((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = false;
      });
      return next;
    });
  };

  const getElementSelectedCount = (rows: CustomCycleCriterionRow[]) =>
    rows.filter((row) => !!selectedCriteria[customCriterionKey(row.id)]).length;

  const getElementBulkEligibleRows = (rows: CustomCycleCriterionRow[]) =>
    rows.filter((row) => {
      const key = customCriterionKey(row.id);
      return !!selectedCriteria[key] && !manualGradeOverrides[key];
    });

  const getElementManualOverrideCount = (rows: CustomCycleCriterionRow[]) =>
    rows.filter((row) => {
      const key = customCriterionKey(row.id);
      return !!selectedCriteria[key] && !!manualGradeOverrides[key];
    }).length;

  const applyGradeToElementSelection = async (elementKey: string, rows: CustomCycleCriterionRow[]) => {
    const bulkGrade = elementBulkGrades[elementKey];
    if (!bulkGrade) return;
    const selectedRows = getElementBulkEligibleRows(rows);
    if (selectedRows.length === 0) return;
    const selectedKeys = selectedRows.map((row) => customCriterionKey(row.id));
    const now = new Date().toISOString();
    const bulkIsNotApplicableGrade = bulkGrade.trim().toLowerCase() === 'not applicable';
    const nextAssessments = { ...assessments };
    selectedRows.forEach((row) => {
      const key = customCriterionKey(row.id);
      const existingMethod = (nextAssessments[key]?.assessment_method ?? '').trim();
      const nextMethod = bulkIsNotApplicableGrade
        ? 'not applicable'
        : existingMethod.toLowerCase() === 'not applicable'
          ? null
          : (existingMethod || null);
      nextAssessments[key] = {
        ...(nextAssessments[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null }),
        grade: bulkGrade,
        assessment_method: nextMethod,
        assessed_at: now,
        assessed_by: currentUserEmail || null,
        employee_cycle_event_id: event.id,
      };
    });

    // Force the selected rows to reflect the chosen grade immediately.
    flushSync(() => {
      setAssessments(nextAssessments);
    });

    setBulkSaving(true);
    try {
      const results = await Promise.all(
        selectedRows.map(async (row) => {
          const key = customCriterionKey(row.id);
          const previousGrade = assessments[key]?.grade ?? null;
          const assessment = nextAssessments[key];
          const res = await fetch(
            `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(employeeCycleId))}/criteria/${encodeURIComponent(key)}${cc}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail ?? '' },
              body: JSON.stringify({
                grade: bulkGrade,
                notes: assessment?.notes ?? null,
                assessment_method: assessment?.assessment_method ?? null,
                userEmail: currentUserEmail,
                employee_cycle_event_id: event.id,
              }),
            }
          );
          if (res.ok) {
            onAssessmentProgressPatch?.({
              eventId: event.id,
              previousGrade,
              nextGrade: bulkGrade,
            });
          }
          return res.ok;
        })
      );
      if (results.every(Boolean)) onClosedCycleSummaryInvalidate?.();
      setSelectedCriteria((prev) => {
        const next = { ...prev };
        selectedKeys.forEach((key) => {
          delete next[key];
        });
        return next;
      });
      setManualGradeOverrides((prev) => {
        const next = { ...prev };
        selectedKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
      setElementBulkGrades((prev) => ({ ...prev, [elementKey]: '' }));
    } finally {
      setBulkSaving(false);
    }
  };

  const activeMobileElementIndex = activeMobileElementKey
    ? mobileElementSteps.findIndex((el) => el.key === activeMobileElementKey)
    : -1;
  const activeMobileElement = activeMobileElementIndex >= 0 ? mobileElementSteps[activeMobileElementIndex] : null;

  const goMobilePrevElement = () => {
    if (activeMobileElementIndex > 0) setActiveMobileElementKey(mobileElementSteps[activeMobileElementIndex - 1].key);
  };
  const goMobileNextElement = () => {
    if (activeMobileElementIndex >= 0 && activeMobileElementIndex < mobileElementSteps.length - 1) {
      setActiveMobileElementKey(mobileElementSteps[activeMobileElementIndex + 1].key);
    }
  };
  const goMobilePrevCriterion = () => {
    if (mobileActiveIndex > 0) setMobileActiveCriterionKey(customCriterionKey(mobileSelectedRows[mobileActiveIndex - 1].id));
  };
  const mobileActiveCriterionKeyComputed = mobileActiveRow ? customCriterionKey(mobileActiveRow.id) : '';
  const mobileCurrentRowMethodOk =
    !!mobileActiveRow &&
    hasAssessmentMethodSatisfied(
      assessments[mobileActiveCriterionKeyComputed]?.grade,
      assessments[mobileActiveCriterionKeyComputed]?.assessment_method
    );
  const mobileIsLastSelectedCriterion =
    mobileSelectedRows.length > 0 && mobileActiveIndex === mobileSelectedRows.length - 1 && mobileActiveIndex >= 0;
  const mobileNextDisabled =
    persistingAssessmentsToFeedback ||
    mobileActiveIndex < 0 ||
    !mobileActiveRow ||
    !mobileCurrentRowMethodOk ||
    (mobileIsLastSelectedCriterion && !eventEligibleToComplete);
  const goMobileNextCriterion = async () => {
    if (mobileActiveIndex < 0 || !mobileActiveRow || mobileNextDisabled) return;
    if (mobileIsLastSelectedCriterion && eventEligibleToComplete) {
      if (!canAssess) {
        setMobilePhase('feedback');
        return;
      }
      setPersistingAssessmentsToFeedback(true);
      try {
        const results = await Promise.all(
          mobileSelectedRows.map((row) => {
            const key = customCriterionKey(row.id);
            return saveAssessment(
              row,
              {
                grade: assessments[key]?.grade ?? null,
                notes: assessments[key]?.notes ?? null,
                assessment_method: assessments[key]?.assessment_method ?? null,
              },
              { refetch: false }
            );
          })
        );
        if (results.every(Boolean)) {
          await fetchAssessments();
          onAssessmentScheduleInvalidate?.({ immediate: true });
          setMobilePhase('feedback');
        } else {
          window.alert(translateUi("Unable to save every criterion to the server. Check your connection and try Continue to feedback again. Nothing was lost in this app session."));
        }
      } finally {
        setPersistingAssessmentsToFeedback(false);
      }
      return;
    }
    if (canAssess) {
      const key = customCriterionKey(mobileActiveRow.id);
      saveAssessmentInBackground(
        mobileActiveRow,
        {
          grade: assessments[key]?.grade ?? null,
          notes: assessments[key]?.notes ?? null,
          assessment_method: assessments[key]?.assessment_method ?? null,
        }
      );
    }
    if (mobileActiveIndex < mobileSelectedRows.length - 1) {
      setMobileActiveCriterionKey(customCriterionKey(mobileSelectedRows[mobileActiveIndex + 1].id));
    }
  };
  const mobileNextHelperText =
    mobileActiveRow && mobileSelectedRows.length > 0 && mobileActiveIndex >= 0
      ? !mobileCurrentRowMethodOk
        ? 'Pick an assessment method for this grade, or set the grade to Not applicable or Not yet Assessed before continuing.'
        : mobileIsLastSelectedCriterion && !eventEligibleToComplete
          ? isLastEvent
            ? 'Final assessment: every criterion needs a substantive grade and an assessment method. Not applicable and Not yet Assessed are not allowed. Use Previous to review other criteria.'
            : 'Every criterion in this event needs a grade and, where applicable, an assessment method. Use Previous to review other criteria.'
          : null
      : null;
  const mobileActiveEvidence = mobileActiveRow ? evidenceByCriterion[customCriterionKey(mobileActiveRow.id)] ?? [] : [];
  const mobileKey = mobileActiveRow ? customCriterionKey(mobileActiveRow.id) : '';
  const mobileUploadingDocument = mobileActiveRow ? !!uploadingEvidenceDocument[mobileKey] : false;
  const mobileUploadingPhoto = mobileActiveRow ? !!uploadingEvidencePhoto[mobileKey] : false;

  const renderElementRows = (element: { key: string; rows: CustomCycleCriterionRow[] }) => (
    <div className="border-t border-slate-200 divide-y divide-slate-100">
      {canAssess && !showAssessedOnly && (
        <div className="px-4 py-3 bg-white flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500 space-y-1">
            <p>{getElementSelectedCount(element.rows)} selected in this element</p>
            {getElementManualOverrideCount(element.rows) > 0 && (
              <p className="text-amber-700">
                {getElementManualOverrideCount(element.rows)} manually adjusted row{getElementManualOverrideCount(element.rows) !== 1 ? translateUi("s are") : translateUi(" is")} excluded from bulk apply
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <AssessmentPillSelect
              aria-label={translateUi("Bulk grade for selected criteria")}
              value={elementBulkGrades[element.key] ?? ''}
              options={bulkGradeSelectOptions}
              onChange={(v) => setElementBulkGrades((prev) => ({ ...prev, [element.key]: v }))}
              colourByGrade
              isDarkMode={isDarkMode}
              placeholder="Choose grade for selected"
              className="min-w-[180px]"
            />
            <button
              type="button"
              onClick={() => applyGradeToElementSelection(element.key, element.rows)}
              disabled={!elementBulkGrades[element.key] || getElementBulkEligibleRows(element.rows).length === 0 || bulkSaving}
              className={cn(
                "font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50",
                isMobileOrTablet ? "px-4 py-2.5 text-sm min-h-[44px]" : "px-4 py-2 text-sm"
              )}
            >
              {bulkSaving ? 'Applying…' : `Apply to selected (${getElementBulkEligibleRows(element.rows).length})`}
            </button>
          </div>
        </div>
      )}
      {element.rows.map((row) => {
        const key = customCriterionKey(row.id);
        const assessment = assessments[key];
        const previewGrade = selectedCriteria[key] && elementBulkGrades[element.key]
          && !manualGradeOverrides[key]
          ? elementBulkGrades[element.key]
          : '';
        const grade = previewGrade || gradeForAssessmentSelectDisplay(assessment?.grade);
        const notes = assessment?.notes ?? '';
        const assessmentMethod = assessment?.assessment_method ?? '';
        const lockedMethodNa = isNotApplicableGradeValue(grade);
        const isSaving = !!saving[key];
        const isAssessed = isGradeAssessed(assessment?.grade);
        const rowDomId = `rv-custom-criterion-${key.replace(/[^a-zA-Z0-9_-]+/g, '_')}`;
        return (
          <div key={row.id} id={rowDomId} className="p-4 bg-slate-50/50 space-y-3 scroll-mt-24">
            <div className="flex items-start gap-3">
              {canAssess && (
                <input
                  type="checkbox"
                  checked={!!selectedCriteria[key]}
                  onChange={() => toggleCriterionSelection(row.id)}
                  className={cn(
                    "mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500",
                    isMobileOrTablet ? "h-5 w-5" : "h-4 w-4"
                  )}
                />
              )}
              <div className="flex-1">
                <div className="flex items-start gap-2">
                  <p className="text-base text-slate-800 flex-1">{row.criteria}</p>
                  {isAssessed && assessment?.grade && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${getAssessmentGradeBadgeClasses(assessment?.grade, isDarkMode)}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {assessment.grade}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div
                className={cn(
                  "rounded-xl border p-3",
                  isDarkMode ? "border-slate-600 bg-slate-800/80" : "border-slate-200 bg-slate-50/60"
                )}
              >
                <label
                  className={cn(
                    "block text-sm font-medium mb-1.5",
                    isDarkMode ? "text-slate-200" : "text-slate-600"
                  )}
                > Grade </label>
                <AssessmentPillSelect
                  aria-label={translateUi("Grade")}
                  value={grade}
                  disabled={!canAssess || isSaving}
                  options={gradeSelectOptions}
                  colourByGrade
                  isDarkMode={isDarkMode}
                  placeholder={translateUi("Not yet Assessed")}
                  onChange={(nextRaw) => {
                    const nextGrade = nextRaw || null;
                    const previousGrade = assessments[key]?.grade ?? null;
                    const isNotApplicableGrade = isNotApplicableGradeValue(nextGrade);
                    const currentMethodIsNotApplicable = (assessmentMethod ?? '').trim().toLowerCase() === 'not applicable';
                    const nextAssessmentMethod = isNotApplicableGrade
                      ? 'not applicable'
                      : currentMethodIsNotApplicable
                        ? null
                        : (assessmentMethod || null);
                    const now = new Date().toISOString();
                    setManualGradeOverrides((prev) => ({ ...prev, [key]: true }));
                    flushSync(() => {
                      setAssessments((prev) => ({
                        ...prev,
                        [key]: {
                          ...(prev[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null }),
                          grade: nextGrade,
                          assessment_method: nextAssessmentMethod,
                          assessed_at: now,
                          assessed_by: currentUserEmail || null,
                          employee_cycle_event_id: event.id,
                        },
                      }));
                    });
                    saveAssessmentInBackground(
                      row,
                      { grade: nextGrade, notes: notes || null, assessment_method: nextAssessmentMethod },
                      { previousGrade }
                    );
                  }}
                />
                {previewGrade && (
                  <p className={cn("mt-1 text-xs", isDarkMode ? "text-indigo-300" : "text-indigo-600")}>
                    Pending bulk apply
                  </p>
                )}
                {selectedCriteria[key] && manualGradeOverrides[key] && (
                  <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                    Manual override: bulk apply will not change this row
                  </p>
                )}
              </div>
              <div
                className={cn(
                  "rounded-xl border p-3",
                  isDarkMode ? "border-slate-600 bg-slate-800/80" : "border-slate-200 bg-slate-50/60"
                )}
              >
                <label
                  className={cn(
                    "block text-sm font-medium mb-1.5",
                    isDarkMode ? "text-slate-200" : "text-slate-600"
                  )}
                >
                  {translateUi("Assessment method")}
                </label>
                <AssessmentPillSelect
                  aria-label={translateUi("Assessment method")}
                  value={lockedMethodNa ? 'not applicable' : assessmentMethod}
                  disabled={!canAssess || isSaving || lockedMethodNa}
                  options={methodSelectOptions}
                  isDarkMode={isDarkMode}
                  placeholder={translateUi("Select method")}
                  onChange={(nextRaw) => {
                    const nextMethod = nextRaw || null;
                    const now = new Date().toISOString();
                    let merged: EmployeeCriterionAssessment;
                    flushSync(() => {
                      setAssessments((prev) => {
                        const cur = prev[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null };
                        merged = {
                          ...cur,
                          assessment_method: nextMethod,
                          assessed_at: now,
                          assessed_by: currentUserEmail || null,
                          employee_cycle_event_id: event.id,
                        };
                        return { ...prev, [key]: merged };
                      });
                    });
                    const gradeForSave = previewGrade || merged!.grade || '';
                    saveAssessmentInBackground(row, { grade: gradeForSave || null, notes: merged!.notes ?? null, assessment_method: nextMethod });
                  }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5"> Notes </label>
              <textarea
                value={notes}
                disabled={!canAssess || isSaving}
                onChange={(e) =>
                  setAssessments((prev) => ({
                    ...prev,
                    [key]: {
                      ...(prev[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null }),
                      notes: e.target.value,
                      assessed_at: new Date().toISOString(),
                      assessed_by: currentUserEmail || null,
                      employee_cycle_event_id: event.id,
                    },
                  }))
                }
                onBlur={(e) =>
                  saveAssessmentInBackground(
                    row,
                    {
                      grade: grade || null,
                      notes: e.target.value.trim() === '' ? null : e.target.value,
                      assessment_method: lockedMethodNa ? 'not applicable' : assessmentMethod || null,
                    }
                  )}
                rows={2}
                placeholder="Optional assessor notes"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white text-base text-slate-800 resize-none"
              />
            </div>
            {assessment?.assessed_at && (
              <p className="text-sm text-slate-500">
                Last updated {formatDateTimeUK(assessment.assessed_at)}
                {assessment.assessed_by ? ` by ${assessment.assessed_by}` : ''}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  const runRecordAmendment = async (notes?: string) => {
    setIsUpdatingAmendment(true);
    setAmendmentSaved(false);
    try {
      const rowsToSave = showAssessedOnly
        ? displayGroupedCriteria.flatMap((u) => u.elements.flatMap((e) => e.rows))
        : eventScopeCriteriaRows;
      await Promise.all(
        rowsToSave.map((row) =>
          saveAssessment(row, {
            grade: assessments[customCriterionKey(row.id)]?.grade ?? null,
            notes: assessments[customCriterionKey(row.id)]?.notes ?? null,
            assessment_method: assessments[customCriterionKey(row.id)]?.assessment_method ?? null,
          }),
        ),
      );
      await onRecordAmendment?.(notes);
      setAmendmentSaved(true);
      await new Promise((r) => setTimeout(r, 1500));
      onClose();
    } finally {
      setIsUpdatingAmendment(false);
    }
  };

  const completedFeedbackUpdateProps =
    canAssess && event.status === 'completed'
      ? {
          showUpdateButton: true as const,
          onRecordAmendment: () => runRecordAmendment(),
          isUpdatingAmendment,
        }
      : {};

  return (
    <>
    <AssessmentEventModalFrame
      event={event}
      title={event.event_name}
      subtitle={assessmentEventModalSubtitle}
      showUpdateButton={canAssess && event.status === 'completed'}
      onRecordAmendment={runRecordAmendment}
      onClose={onClose}
      mobileAppMode={isMobileOrTablet}
      isDarkMode={isDarkMode}
      onToggleDarkMode={onToggleDarkMode}
      isSubmittingAssessment={isSubmittingAssessment}
      isUpdatingAmendment={isUpdatingAmendment}
      amendmentSaved={amendmentSaved}
      assessmentFooter={
        String(event.event_type ?? 'assessment').toLowerCase() !== 'monitoring'
          ? useGuidedAssessmentFlow
            ? undefined /* Final feedback is shown on its own pane in guided flow (Criteria → Continue to feedback). */
            : (
                <div id="rv-final-assessment-feedback" className="scroll-mt-4">
                  <AssessmentFinalFeedbackPanel
                    eventId={event.id}
                    eventStatus={event.status}
                    eventType={event.event_type}
                    initialFeedback={event.final_assessment_feedback}
                    eligibilityComplete={eventEligibleToComplete}
                    canMutate={canAssess}
                    currentUserEmail={currentUserEmail}
                    patchLocalEvent={(fb) => patchEmployeeCycleEvent?.({ final_assessment_feedback: fb })}
                    onFinalizeAssessment={
                      event.status !== 'completed' && !isMonitoringAssessmentEvent ? runMarkEventCompleted : undefined
                    }
                    {...completedFeedbackUpdateProps}
                  />
                </div>
              )
          : undefined
      }
    >
      <div className={cn("min-w-0 space-y-4", isMobileOrTablet ? "p-3 sm:p-4" : "p-6")}>
        {!showAssessedOnly && gradingScale.length === 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No company grading scale is configured yet.
          </div>
        )}
        {displayGroupedCriteria.length === 0 && !useGuidedAssessmentFlow && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
            {showAssessedOnly ? translateUi("No criteria have been assessed yet for this event.") : translateUi("All elements have been assessed. No outstanding criteria for this event.")}
          </div>
        )}
        {useGuidedAssessmentFlow ? (
          <div className="min-w-0 space-y-3">
            {mobilePhase === 'feedback' && !isMonitoringAssessmentEvent ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4 dark:border-slate-700 dark:bg-slate-950">
                <div
                  role="status"
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 leading-snug dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-50"
                >
                  All criterion grades and notes were saved on the server before this step—you can pause and finish feedback later from any device.
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setMobilePhase('assess')}
                    className="shrink-0 inline-flex items-center gap-2 min-h-[44px] px-3 rounded-xl border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden /> Back to criteria
                  </button>
                  <p className="text-xs text-slate-500 leading-snug dark:text-slate-400 min-w-0 flex-1 sm:text-right">
                    {event.status === 'completed'
                      ? 'Edit feedback if needed, then Save feedback. Use Update to record criterion amendments.'
                      : 'Fill all three sections, then tap Complete Assessment to save your feedback and finish this event.'}
                  </p>
                </div>
                <div id="rv-final-assessment-feedback" className="scroll-mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                  <AssessmentFinalFeedbackPanel
                    eventId={event.id}
                    eventStatus={event.status}
                    eventType={event.event_type}
                    initialFeedback={event.final_assessment_feedback}
                    eligibilityComplete={eventEligibleToComplete}
                    canMutate={canAssess}
                    currentUserEmail={currentUserEmail}
                    patchLocalEvent={(fb) => patchEmployeeCycleEvent?.({ final_assessment_feedback: fb })}
                    onFinalizeAssessment={
                      event.status !== 'completed' && !isMonitoringAssessmentEvent ? runMarkEventCompleted : undefined
                    }
                    {...completedFeedbackUpdateProps}
                  />
                </div>
              </div>
            ) : (
              <>
            {canAssess && showAssessedOnly && !isMonitoringAssessmentEvent && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-indigo-900">
                  To save narrative feedback or record criterion amendments, open the feedback step.
                </p>
                <button
                  type="button"
                  onClick={() => setMobilePhase('feedback')}
                  className={cn(
                    'shrink-0 px-4 py-2 min-h-[44px] rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700',
                    'animate-pulse',
                  )}
                >
                  Open feedback
                </button>
              </div>
            )}
            {!isLastEvent && (
              <div className="rounded-2xl border border-slate-200 bg-white p-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobilePhase('select')}
                  className={cn("px-3 min-h-[40px] rounded-lg text-xs font-medium", mobilePhase === 'select' ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600")}
                >
                  Select elements
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (mobileSelectedRows.length === 0) return;
                    const criterionKeys = mobileSelectedRows.map((r) => customCriterionKey(r.id));
                    const elementKeys = Object.keys(mobileSelectedElements).filter((k) => !!mobileSelectedElements[k]);
                    const runStart = () => {
                      onStartAssessment?.(criterionKeys, elementKeys);
                      setMobileAssessmentStarted(true);
                      setMobilePhase('assess');
                    };
                    if (companyCode && currentUserEmail) {
                      const suspended = await fetchCompanyStorageSuspended(companyCode, currentUserEmail);
                      if (suspended) {
                        pendingStartAssessmentRef.current = runStart;
                        setStorageQuotaStartDialogOpen(true);
                        return;
                      }
                    }
                    runStart();
                  }}
                  disabled={mobileSelectedRows.length === 0}
                  className={cn(
                    "px-3 min-h-[40px] rounded-lg text-xs font-medium transition-colors",
                    mobilePhase === 'assess' || mobilePhase === 'feedback' ? 'bg-indigo-100 text-indigo-700' :
                    mobileSelectedRows.length > 0
                      ? "bg-amber-400 hover:bg-amber-500 text-white font-semibold animate-pulsate"
                      : "bg-slate-100 text-slate-600 opacity-50"
                  )}
                >
                  {mobileAssessmentStarted || mobilePhase === 'assess' || mobilePhase === 'feedback' ? translateUi("Return to Assessment") : translateUi("Start assessment")}
                </button>
                <div className="ml-auto text-xs text-slate-500">
                  {mobileSelectedRows.length} selected
                </div>
              </div>
            )}
            {isLastEvent && mobileAllElements.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                Final assessment event: all {mobileAllElements.flatMap((el) => el.rows).length} remaining criteria are automatically included.
              </div>
            )}

            {(mobilePhase === 'select' && !isLastEvent) ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-2 space-y-2">
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                  Select the required elements for this assessment event. You can return during assessment to add more elements if you observe criteria in another element.
                </div>
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs text-slate-500">{mobileAllElements.length} elements available</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileSelectedCriteria((prev) => {
                          const next = { ...prev };
                          mobileAllElements.forEach((element) =>
                            element.rows.forEach((row) => { next[customCriterionKey(row.id)] = true; })
                          );
                          return next;
                        });
                      }}
                      className="text-xs text-indigo-700"
                    >
                      {translateUi("Select all")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMobileSelectedCriteria((prev) => {
                          const next = { ...prev };
                          mobileAllElements.forEach((element) =>
                            element.rows.forEach((row) => { next[customCriterionKey(row.id)] = false; })
                          );
                          return next;
                        });
                      }}
                      className="text-xs text-slate-600"
                      disabled={isLastEvent}
                    > Clear </button>
                  </div>
                </div>
                {isLastEvent && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Final assessment event: all remaining unassessed criteria are automatically included.
                  </div>
                )}
                <div className="space-y-2 max-h-[58vh] overflow-y-auto">
                  {mobileAllElements.map((element, idx) => {
                    const key = element.key;
                    const checked = !!mobileSelectedElements[key];
                    const unassessedCount = mobileUnassessedByElement[key]?.length ?? 0;
                    const locked = isLastEvent && unassessedCount > 0;
                    const showCriteria = !!mobileElementCriteriaOpen[key];
                    return (
                      <div
                        key={key}
                        className={cn("w-full rounded-xl border overflow-hidden", checked ? "border-indigo-300 bg-indigo-50/30" : "border-slate-200 bg-white")}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (locked) return;
                            const allSelected = element.rows.every((row) => !!mobileSelectedCriteria[customCriterionKey(row.id)]);
                            setMobileSelectedCriteria((prev) => {
                              const next = { ...prev };
                              element.rows.forEach((row) => { next[customCriterionKey(row.id)] = !allSelected; });
                              return next;
                            });
                          }}
                          className="w-full text-left px-3 py-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs text-slate-500">{element.unitLabel}</p>
                              <p className="text-xs text-slate-800 mt-1">{element.unitLabel} · {element.label}</p>
                              <p className="text-xs text-slate-600 mt-1">{element.rows.length} criteria · {unassessedCount} unassessed</p>
                            </div>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setMobileElementCriteriaOpen((prev) => ({ ...prev, [key]: !prev[key] }));
                              }}
                              className="shrink-0 text-[11px] text-indigo-700 underline cursor-pointer"
                            >
                              {showCriteria ? translateUi("Hide criteria") : translateUi("Show criteria")}
                            </span>
                          </div>
                        </button>
                        {showCriteria && (
                          <div className="border-t border-slate-200 px-3 pt-2 pb-3 space-y-1 bg-white">
                            {element.rows.map((row) => {
                              const rowKey = customCriterionKey(row.id);
                              const rowChecked = !!mobileSelectedCriteria[rowKey];
                              return (
                                <label
                                  key={row.id}
                                  className="flex items-start gap-2 cursor-pointer hover:bg-slate-50/80 rounded p-1.5 -mx-1.5"
                                >
                                  <input
                                    type="checkbox"
                                    checked={rowChecked}
                                    onChange={() => setMobileSelectedCriteria((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                    className="mt-0.5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <span className="text-[11px] text-slate-600">{row.criteria}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                ref={mobileCriterionNavAnchorRef}
                className={`min-w-0 w-full rounded-2xl border border-slate-200 bg-white transition-[background-color] duration-300 ${
                  mobileCriterionNavCue ? 'ring-2 ring-indigo-400 ring-offset-2 bg-indigo-50/60' : ''
                }`}
              >
                {mobileSelectedRows.length > 0 && mobileActiveRow ? (
                  <div className="h-1.5 w-full bg-slate-100 shrink-0" aria-hidden>
                    <div
                      className="h-full bg-indigo-500 transition-[width] duration-300 ease-out"
                      style={{
                        width: `${((mobileActiveIndex + 1) / mobileSelectedRows.length) * 100}%`,
                      }}
                    />
                  </div>
                ) : null}
                {mobileSelectedRows.length > 0 ? (
                  <div className="min-w-0 px-4 pt-4 pb-3 border-b border-slate-100">
                    <p className="text-[11px] text-slate-500 text-center mb-2">Criteria Compliance — tap a number to open it</p>
                    <div className="flex w-full min-w-0 max-w-full flex-wrap justify-center content-start gap-x-2 gap-y-2 md:gap-x-2.5 md:gap-y-2 py-1.5 px-1">
                      {mobileSelectedRows.map((chipRow, chipIdx) => {
                        const chipKey = customCriterionKey(chipRow.id);
                        const chipA = assessments[chipKey];
                        const vis = getGuidedCriterionChipVisual(chipA?.grade, chipA?.assessment_method, { isLastEvent });
                        const chipActive = chipKey === mobileActiveCriterionKey;
                        const chipHasEvidence = (evidenceByCriterion[chipKey]?.length ?? 0) > 0;
                        return (
                          <button
                            key={chipKey}
                            type="button"
                            onClick={() => setMobileActiveCriterionKey(chipKey)}
                            className={cn(
                              "relative flex min-h-[2.625rem] min-w-[2.625rem] shrink-0 items-center justify-center overflow-visible rounded-full text-xs font-bold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2",
                              chipActive && "z-10 ring-2 ring-inset ring-white/90 brightness-[1.02] shadow-inner",
                              vis === 'complete' && "bg-emerald-500 text-white",
                              vis === 'not_applicable' && "bg-slate-300 text-slate-800 ring-2 ring-slate-400",
                              vis === 'attention' && "bg-red-500 text-white ring-2 ring-red-700"
                            )}
                            aria-current={chipActive ? 'true' : undefined}
                            aria-label={translateUi("Criterion {{n}}{{status}}{{evidence}}", {
                              n: chipIdx + 1,
                              status: vis === 'complete' ? translateUi(", complete") : vis === 'not_applicable' ? translateUi(", not applicable") : translateUi(", needs attention"),
                              evidence: chipHasEvidence ? translateUi(", evidence attached") : "",
                            })}
                          >
                            <GuidedAssessmentCriterionChipContent visual={vis} index={chipIdx} hasAttachments={chipHasEvidence} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {mobileActiveRow ? (
                  <>
                    <div
                      key={mobileActiveCriterionKey ?? 'criterion'}
                      role="status"
                      aria-live="polite"
                      aria-atomic="true"
                      className="sr-only"
                    >
                      Criterion {mobileActiveIndex + 1} of {mobileSelectedRows.length}. {mobileActiveRow.criteria.slice(0, 220)}
                    </div>
                    <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-indigo-900">Criterion {mobileActiveIndex + 1} of {mobileSelectedRows.length}</p>
                        <p className="text-base text-slate-800 mt-1 leading-relaxed">{mobileActiveRow.criteria}</p>
                        <p className="text-sm text-slate-500 mt-1">Previous / Next changes criterion; the bar above and highlight show where you are.</p>
                      </div>
                      {!isLastEvent && (
                        <button
                          type="button"
                          onClick={() => setMobilePhase('select')}
                          className="shrink-0 whitespace-nowrap min-h-[36px] px-3 rounded-lg border border-indigo-200 bg-indigo-50 text-xs text-indigo-700 hover:bg-indigo-100"
                        >
                          Select units
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5"> Assessment </label>
                      {(() => {
                        const key = customCriterionKey(mobileActiveRow.id);
                        const grade = gradeForAssessmentSelectDisplay(assessments[key]?.grade);
                        const assessmentMethod = assessments[key]?.assessment_method ?? '';
                        const lockedMethodNa = isNotApplicableGradeValue(grade);
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div
                              className={cn(
                                "rounded-xl border p-2",
                                isDarkMode ? "border-slate-600 bg-slate-800/80" : "border-slate-200 bg-slate-50/60"
                              )}
                            >
                              <label
                                className={cn(
                                  "block text-sm font-medium mb-1.5",
                                  isDarkMode ? "text-slate-200" : "text-slate-600"
                                )}
                              > Grade </label>
                              <AssessmentPillSelect
                                aria-label={translateUi("Grade")}
                                value={grade}
                                disabled={!canAssess}
                                options={gradeSelectOptions}
                                colourByGrade
                                isDarkMode={isDarkMode}
                                placeholder={translateUi("Not yet Assessed")}
                                onChange={(nextRaw) => {
                                  const nextGrade = nextRaw || null;
                                  const previousGrade = assessments[key]?.grade ?? null;
                                  const isNotApplicableGrade = isNotApplicableGradeValue(nextGrade);
                                  const currentMethodIsNotApplicable = (assessmentMethod ?? '').trim().toLowerCase() === 'not applicable';
                                  const nextAssessmentMethod = isNotApplicableGrade
                                    ? 'not applicable'
                                    : currentMethodIsNotApplicable
                                      ? null
                                      : (assessmentMethod || null);
                                  const now = new Date().toISOString();
                                  let merged: EmployeeCriterionAssessment;
                                  flushSync(() => {
                                    setAssessments((prev) => {
                                      const cur = prev[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null };
                                      merged = {
                                        ...cur,
                                        grade: nextGrade,
                                        assessment_method: nextAssessmentMethod,
                                        assessed_at: now,
                                        assessed_by: currentUserEmail || null,
                                        employee_cycle_event_id: event.id,
                                      };
                                      return { ...prev, [key]: merged };
                                    });
                                  });
                                  saveAssessmentInBackground(
                                    mobileActiveRow,
                                    {
                                      grade: nextGrade,
                                      notes: merged!.notes ?? null,
                                      assessment_method: merged!.assessment_method ?? null,
                                    },
                                    { previousGrade }
                                  );
                                }}
                              />
                            </div>
                            <div
                              className={cn(
                                "rounded-xl border p-2",
                                isDarkMode ? "border-slate-600 bg-slate-800/80" : "border-slate-200 bg-slate-50/60"
                              )}
                            >
                              <label
                                className={cn(
                                  "block text-sm font-medium mb-1.5",
                                  isDarkMode ? "text-slate-200" : "text-slate-600"
                                )}
                              >
                                {translateUi("Assessment method")}
                              </label>
                              <AssessmentPillSelect
                                aria-label={translateUi("Assessment method")}
                                value={lockedMethodNa ? 'not applicable' : assessmentMethod}
                                disabled={!canAssess || lockedMethodNa}
                                options={methodSelectOptions}
                                isDarkMode={isDarkMode}
                                placeholder={translateUi("Select method")}
                                onChange={(nextRaw) => {
                                  const nextMethod = nextRaw || null;
                                  const now = new Date().toISOString();
                                  let merged: EmployeeCriterionAssessment;
                                  flushSync(() => {
                                    setAssessments((prev) => {
                                      const cur = prev[key] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null };
                                      merged = {
                                        ...cur,
                                        assessment_method: nextMethod,
                                        assessed_at: now,
                                        assessed_by: currentUserEmail || null,
                                        employee_cycle_event_id: event.id,
                                      };
                                      return { ...prev, [key]: merged };
                                    });
                                  });
                                  saveAssessmentInBackground(mobileActiveRow, {
                                    grade: merged!.grade ?? null,
                                    notes: merged!.notes ?? null,
                                    assessment_method: nextMethod,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5"> Notes </label>
                      <textarea
                        value={assessments[customCriterionKey(mobileActiveRow.id)]?.notes ?? ''}
                        disabled={!canAssess}
                        onChange={(e) => setAssessments((prev) => ({ ...prev, [customCriterionKey(mobileActiveRow.id)]: { ...(prev[customCriterionKey(mobileActiveRow.id)] || { grade: null, notes: null, assessed_at: null, assessed_by: null, employee_cycle_event_id: null, assessment_method: null }), notes: e.target.value, assessed_at: new Date().toISOString(), assessed_by: currentUserEmail || null, employee_cycle_event_id: event.id } }))}
                        onBlur={(e) => {
                          const mk = customCriterionKey(mobileActiveRow.id);
                          const cur = assessments[mk];
                          const na = isNotApplicableGradeValue(cur?.grade);
                          saveAssessmentInBackground(
                            mobileActiveRow,
                            {
                              grade: cur?.grade ?? null,
                              notes: e.target.value.trim() === '' ? null : e.target.value,
                              assessment_method: na ? 'not applicable' : (cur?.assessment_method ?? null),
                            }
                          );
                        }}
                        rows={4}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white text-base text-slate-800 resize-none disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2"> Evidence </label>
                      {canMutateEvidence ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label className="flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 border border-slate-200 rounded-xl text-slate-700 bg-white">
                          <Camera className="w-4 h-4" />
                          {mobileUploadingPhoto ? 'Uploading...' : 'Take/Upload Photo'}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadEvidenceFile(mobileActiveRow.id, file, 'photo');
                              e.currentTarget.value = '';
                            }}
                            disabled={mobileUploadingPhoto}
                          />
                        </label>
                        <label className="flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 border border-slate-200 rounded-xl text-slate-700 bg-white">
                          <Upload className="w-4 h-4" />
                          {mobileUploadingDocument ? translateUi("Uploading...") : translateUi("Upload document")}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadEvidenceFile(mobileActiveRow.id, file, 'document');
                              e.currentTarget.value = '';
                            }}
                            disabled={mobileUploadingDocument}
                          />
                        </label>
                      </div>
                      ) : (
                        <p className="text-xs text-slate-500 mb-1">This cycle is closed — evidence is read-only.</p>
                      )}
                      {mobileActiveEvidence.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {mobileActiveEvidence.map((f) => {
                            const isPreviewLoading = mobileAttachmentPreviewLoadingId === f.id;
                            return (
                              <li key={f.id} className="text-sm py-2 px-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openMobileAttachmentPreview(f.id, f.name)}
                                    disabled={isPreviewLoading}
                                    className="text-indigo-600 hover:underline truncate flex-1 text-left"
                                  >
                                    {isPreviewLoading ? (
                                      <span className="inline-flex items-center gap-1">
                                        <RotateCcw className="w-3 h-3 animate-spin" />
                                        {`${f.name} (Loading preview...)`}
                                      </span>
                                    ) : (
                                      f.name
                                    )}
                                  </button>
                                  <div className="ml-2 shrink-0 flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openMobileAttachmentPreview(f.id, f.name)}
                                      disabled={isPreviewLoading}
                                      className="text-indigo-600 hover:underline text-xs"
                                    >
                                      {isPreviewLoading ? (
                                        <span className="inline-flex items-center gap-1">
                                          <RotateCcw className="w-3 h-3 animate-spin" />
                                          Loading...
                                        </span>
                                      ) : (
                                        translateUi("View")
                                      )}
                                    </button>
                                    {canMutateEvidence ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const confirmed = window.confirm(translateUi('Are you sure you want to remove the attached evidence?'));
                                        if (!confirmed) return;
                                        deleteEvidenceFile(f.id, mobileActiveRow.id);
                                      }}
                                      className="text-slate-400 hover:text-red-600 p-1"
                                      title={translateUi("Remove attached evidence")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    ) : null}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <button type="button" onClick={goMobilePrevCriterion} disabled={mobileActiveIndex <= 0} className="px-4 py-2 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 disabled:opacity-50 inline-flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Previous </button>
                      <span className="inline-flex min-w-[5.5rem] justify-center px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-sm font-semibold text-indigo-900 tabular-nums">
                        {mobileActiveIndex + 1} / {mobileSelectedRows.length}
                      </span>
                      <button
                        type="button"
                        onClick={goMobileNextCriterion}
                        disabled={mobileNextDisabled}
                        aria-busy={persistingAssessmentsToFeedback}
                        aria-describedby={mobileNextHelperText ? 'rv-mobile-next-helper' : undefined}
                        className="px-4 py-2 min-h-[44px] min-w-[44px] rounded-xl bg-indigo-600 text-white disabled:opacity-50 inline-flex items-center justify-center gap-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 touch-manipulation active:opacity-95"
                      >
                        {persistingAssessmentsToFeedback ? (
                          <>Saving criteria…</>
                        ) : (
                          <>
                            {mobileIsLastSelectedCriterion && eventEligibleToComplete
                              ? 'Continue to feedback'
                              : translateUi("Next")}{' '}
                            <ChevronRight className="w-4 h-4 shrink-0" aria-hidden />
                          </>
                        )}
                      </button>
                    </div>
                    {mobileNextHelperText ? (
                      <p id="rv-mobile-next-helper" className="mt-2 text-xs text-amber-800 text-center leading-snug px-1">
                        {mobileNextHelperText}
                      </p>
                    ) : null}
                    </div>
                  </>
                ) : eventEligibleToComplete ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800 space-y-3">
                    <div>
                      <p className="font-medium">
                        {isLastEvent && lastEventOutstandingRows.length === 0
                          ? translateUi("All criteria already covered earlier")
                          : translateUi("All criteria assessed")}
                      </p>
                      {canMarkAssessmentEventComplete ? (
                        <p className="mt-1 text-emerald-700">
                          {isMonitoringAssessmentEvent
                            ? 'Use Complete Assessment at the bottom of this screen when you are done.'
                            : 'Open the feedback step, fill all sections, then tap Complete Assessment to finish this event.'}
                        </p>
                      ) : (
                        <p className="mt-1 text-amber-800">
                          {isLastEvent && lastEventOutstandingRows.length === 0
                            ? 'Continue to feedback to add General observations, Personal attributes, and Candidate feedback, then complete this event.'
                            : 'Tap Continue to feedback on the last criterion (or the button below), fill all sections, then tap Complete Assessment.'}
                        </p>
                      )}
                    </div>
                    {!isMonitoringAssessmentEvent && (
                      <button
                        type="button"
                        onClick={() => setMobilePhase('feedback')}
                        className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                      >
                        Continue to feedback
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {!event.assessment_started_at
                      ? 'Select at least one criteria item to begin assessment.'
                      : !isMonitoringAssessmentEvent &&
                          eventEligibleToComplete &&
                          !isFinalAssessmentFeedbackSufficient(event.final_assessment_feedback)
                        ? 'When criteria are complete, use Continue to feedback on the last criterion, fill all sections, then tap Complete Assessment.'
                        : event.assessment_started_at && !eventEligibleToComplete && isLastEvent
                          ? 'Use Previous/Next above (when shown) to fix any criterion that still fails final-event rules — details appear as a short note under the Next button.'
                          : 'If you do not see criterion fields, reopen this assessment or refresh the page. On non-final events, try Select elements.'}
                  </div>
                )}
                {mobileAttachmentPreview && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeMobileAttachmentPreview}>
                    <div className="w-full max-w-3xl max-h-[85vh] rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{mobileAttachmentPreview.name}</p>
                        <button type="button" onClick={closeMobileAttachmentPreview} className="p-1 text-slate-500 hover:text-slate-800 shrink-0">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-3 bg-slate-50">
                        {isImageFile(mobileAttachmentPreview.name) ? (
                          <img src={mobileAttachmentPreview.url} alt={mobileAttachmentPreview.name} className="w-full max-h-[70vh] object-contain rounded-lg border border-slate-200 bg-white" />
                        ) : isPdfFile(mobileAttachmentPreview.name) ? (
                          <embed
                            key={mobileAttachmentPreview.url}
                            src={mobileAttachmentPreview.url}
                            type="application/pdf"
                            title={mobileAttachmentPreview.name}
                            className="w-full h-[70vh] rounded-lg border border-slate-200 bg-white"
                          />
                        ) : (
                          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                            Preview unavailable for this file type.
                            <a href={mobileAttachmentPreview.url} target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-600 hover:underline">Open file</a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {displayGroupedCriteria.map((unit) => (
              <div key={unit.key} className="rounded-2xl border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedUnits((prev) => ({ ...prev, [unit.key]: !prev[unit.key] }))}
                  className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-slate-800">{unit.label}</span>
                  {expandedUnits[unit.key] ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                </button>
                {expandedUnits[unit.key] && (
                  <div className="p-4 space-y-3">
                    {unit.elements.map((element) => (
                      <div key={element.key} className="rounded-xl border border-slate-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedElements((prev) => ({ ...prev, [element.key]: !prev[element.key] }))}
                          className="w-full px-4 py-3 bg-white flex items-center justify-between text-left"
                        >
                          <div>
                            <p className="font-medium text-slate-800">{element.label}</p>
                            <p className="text-xs text-slate-500">
                              {element.rows.length} criteria. {expandedElements[element.key] ? translateUi("Click to collapse") : translateUi("Click to expand")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {canAssess && !showAssessedOnly && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleElementSelection(element.rows);
                                }}
                                className={cn(
                                  "border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50",
                                  isMobileOrTablet ? "px-3 py-2 text-sm min-h-[40px]" : "px-2 py-1 text-xs"
                                )}
                              >
                                {element.rows.every((row) => !!selectedCriteria[customCriterionKey(row.id)]) ? translateUi("Deselect all") : translateUi("Select all")}
                              </button>
                            )}
                            {expandedElements[element.key] ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                          </div>
                        </button>
                        {expandedElements[element.key] && renderElementRows(element)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {isMonitoringAssessmentEvent && event.status !== 'completed' && canAssess && (
          <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              {!canMarkAssessmentEventComplete
                ? markCompleteDisabledReason ?? 'Finish every criterion above before you can complete this event.'
                : 'All required criteria are assessed. Complete this monitoring event when you are done.'}
            </p>
            <button
              type="button"
              onClick={() => runMarkEventCompleted()}
              disabled={!canMarkAssessmentEventComplete || isSubmittingAssessment}
              className="shrink-0 px-4 py-2 min-h-[44px] text-sm font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-emerald-600 dark:hover:bg-emerald-500 touch-manipulation sm:min-h-0 sm:py-2"
            >
              {isSubmittingAssessment ? 'Completing…' : translateUi("Complete Assessment")}
            </button>
          </div>
        )}
      </div>
    </AssessmentEventModalFrame>
    <StorageQuotaUploadDialog open={storageQuotaUploadDialogOpen} onClose={() => setStorageQuotaUploadDialogOpen(false)} />
    <StorageQuotaStartAssessmentDialog
      open={storageQuotaStartDialogOpen}
      onCancel={() => {
        pendingStartAssessmentRef.current = null;
        setStorageQuotaStartDialogOpen(false);
      }}
      onContinue={() => {
        pendingStartAssessmentRef.current?.();
        pendingStartAssessmentRef.current = null;
        setStorageQuotaStartDialogOpen(false);
      }}
    />
    </>
  );
};

/** Canvas-based signature pad — pointer events for mouse, trackpad, touch, and pen */
function SignaturePad({ onSave, className, saveButtonLabel = 'Save signature', isDarkMode = false }: { onSave: (dataUrl: string) => void; className?: string; saveButtonLabel?: string; isDarkMode?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const activePointerIdRef = useRef<number | null>(null);

  const getPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    // CSS pixel coords; ctx is scaled by dpr in useEffect
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const draw = useCallback((pos: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const last = lastPosRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    lastPosRef.current = pos;
  }, []);

  const startDraw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture can throw in edge cases */
      }
      activePointerIdRef.current = e.pointerId;
    }
    const pos = getPos(e);
    if (pos) {
      isDrawingRef.current = true;
      lastPosRef.current = pos;
    }
  }, [getPos]);

  const moveDraw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;
    const pos = getPos(e);
    if (pos) draw(pos);
  }, [getPos, draw]);

  const endDraw = useCallback((e?: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas && e && activePointerIdRef.current === e.pointerId) {
      try {
        if (canvas.hasPointerCapture(e.pointerId)) {
          canvas.releasePointerCapture(e.pointerId);
        }
      } catch {
        /* releasePointerCapture can throw if not captured */
      }
    }
    activePointerIdRef.current = null;
    isDrawingRef.current = false;
    lastPosRef.current = null;
  }, []);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dataUrl = canvas.toDataURL('image/png');
    if (dataUrl && dataUrl.length > 100) onSave(dataUrl);
  }, [onSave]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const init = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    init();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={cn('relative flex flex-col min-h-0', className)}>
      <div className="flex-1 min-h-[200px] overflow-hidden">
        <canvas
          ref={canvasRef}
          onPointerDown={startDraw}
          onPointerMove={moveDraw}
          onPointerUp={endDraw}
          onPointerCancel={endDraw}
          onPointerLeave={(e) => {
            if (isDrawingRef.current && e.buttons === 0) endDraw(e);
          }}
          className="w-full h-full rounded-xl block touch-none"
          style={{ touchAction: 'none' }}
        />
      </div>
      <div className="flex gap-2 mt-3 shrink-0">
        <button
          onClick={handleClear}
          type="button"
          className={cn(
            "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
            isDarkMode ? "text-slate-200 bg-slate-800 hover:bg-slate-700" : "text-slate-600 bg-slate-100 hover:bg-slate-200"
          )}
        > Clear </button>
        <button onClick={handleSave} type="button" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
          {saveButtonLabel}
        </button>
      </div>
    </div>
  );
}

const EMPLOYEE_PROFILE_TAB_OPTIONS = ['Main', 'Licence', 'Cab Passes', 'Certificate', 'Trains & Routes', 'Experience Records', 'Cycles', 'Training', 'Monitoring', 'Medical', 'Medication Checks', 'Safety Briefs', 'Driver Reports', 'Leave & Absence', 'Documents', 'Notes'] as const;
type EmployeeProfileTab = (typeof EMPLOYEE_PROFILE_TAB_OPTIONS)[number] | "Tasks";
const EMPLOYEE_PROFILE_UI_STYLES = `
  .employee-profile-shell {
    border-radius: 1rem;
    min-width: 0;
    max-width: 100%;
    overflow-x: hidden;
  }
  .employee-profile-nav-shell {
    border: 1px solid #dbe3ee;
    border-radius: 1.15rem;
    background:
      radial-gradient(120% 140% at 0% 0%, rgba(56, 189, 248, 0.08), transparent 42%),
      radial-gradient(100% 120% at 100% 0%, rgba(99, 102, 241, 0.07), transparent 40%),
      linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
    padding: 0.55rem;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.8) inset,
      0 18px 40px -28px rgba(15, 23, 42, 0.35);
    position: relative;
    z-index: 30;
  }
  .employee-mega-menu {
    position: relative;
  }
  .employee-mega-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    padding: 0.15rem;
  }
  .employee-mega-trigger-wrap {
    position: relative;
  }
  .employee-mega-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2.55rem;
    padding: 0.45rem 0.78rem;
    border: 1px solid transparent;
    border-radius: 0.85rem;
    background: transparent;
    color: #475569;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
  }
  .employee-mega-trigger:hover,
  .employee-mega-trigger.open {
    background: rgba(255, 255, 255, 0.88);
    border-color: #d7e0ea;
    color: #0f172a;
    box-shadow: 0 8px 18px -16px rgba(15, 23, 42, 0.45);
  }
  .employee-mega-trigger.active {
    background: linear-gradient(180deg, #ffffff 0%, #eef4ff 100%);
    border-color: #bfd0fe;
    color: #1e3a8a;
    box-shadow: 0 10px 22px -18px rgba(37, 99, 235, 0.55);
  }
  .employee-mega-trigger-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.55rem;
    height: 1.55rem;
    border-radius: 0.55rem;
  }
  .employee-mega-trigger-underline {
    position: absolute;
    left: 0.85rem;
    right: 0.85rem;
    bottom: 0.22rem;
    height: 2px;
    border-radius: 999px;
  }
  .employee-mega-current {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.05rem;
    padding: 0.25rem 0.65rem;
    min-width: 0;
  }
  .employee-mega-current-kicker {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .employee-mega-current-value {
    font-size: 0.78rem;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 14rem;
  }
  .employee-mega-panel {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    transform: translateY(-6px);
    pointer-events: none;
    transition: grid-template-rows 220ms ease, opacity 180ms ease, transform 180ms ease;
  }
  .employee-mega-panel.open {
    grid-template-rows: 1fr;
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
    margin-top: 0.45rem;
  }
  .employee-mega-panel-inner {
    overflow: hidden;
    border: 1px solid #dbe3ee;
    border-radius: 1rem;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(248, 250, 252, 0.94) 100%);
    box-shadow:
      0 24px 48px -32px rgba(15, 23, 42, 0.45),
      0 1px 0 rgba(255, 255, 255, 0.85) inset;
    padding: 0.95rem;
    position: relative;
    isolation: isolate;
  }
  .employee-mega-panel-inner::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: inherit;
    background-image: url("/GBR_BG.png");
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
    opacity: 0.06;
    pointer-events: none;
  }
  .employee-mega-panel-inner > * {
    position: relative;
    z-index: 1;
  }
  .employee-mega-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.85rem;
    padding: 0 0.15rem;
  }
  .employee-mega-panel-eyebrow {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #64748b;
  }
  .employee-mega-panel-blurb {
    margin: 0.2rem 0 0;
    font-size: 0.86rem;
    color: #475569;
    max-width: 36rem;
  }
  .employee-mega-panel-count {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.28rem 0.65rem;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .employee-mega-grid {
    display: grid;
    gap: 0.55rem;
  }
  .employee-mega-grid.cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .employee-mega-grid.cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .employee-mega-grid.cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .employee-mega-card {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    text-align: left;
    min-height: 5.1rem;
    padding: 0.85rem 0.9rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.95rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    color: #334155;
    overflow: hidden;
    transform: translateY(4px);
    opacity: 0;
    animation: employeeMegaCardIn 280ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    transition: border-color 170ms ease, background-color 170ms ease, box-shadow 170ms ease, transform 170ms ease;
  }
  .employee-mega-card:hover {
    border-color: #c7d2fe;
    background: #ffffff;
    box-shadow: 0 14px 28px -22px rgba(37, 99, 235, 0.55);
    transform: translateY(-1px);
  }
  .employee-mega-card.active {
    border-color: #93c5fd;
    background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
    box-shadow: 0 16px 30px -22px rgba(37, 99, 235, 0.6);
  }
  .employee-mega-card-copy {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
    min-width: 0;
    flex: 1;
  }
  .employee-mega-card-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.015em;
  }
  .employee-mega-card-title-row,
  .employee-mega-link-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }
  .employee-mega-card-desc {
    font-size: 0.75rem;
    line-height: 1.35;
    color: #64748b;
  }
  .employee-mega-card-arrow {
    margin-top: 0.15rem;
    color: #94a3b8;
    opacity: 0;
    transform: translateX(-4px);
    transition: opacity 160ms ease, transform 160ms ease, color 160ms ease;
  }
  .employee-mega-card:hover .employee-mega-card-arrow,
  .employee-mega-card.active .employee-mega-card-arrow {
    opacity: 1;
    transform: translateX(0);
    color: #3b82f6;
  }
  .employee-mega-card-active-bar {
    position: absolute;
    left: 0;
    top: 0.7rem;
    bottom: 0.7rem;
    width: 3px;
    border-radius: 999px;
  }
  .employee-mega-item-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.7rem;
    flex-shrink: 0;
  }
  .employee-mega-item-icon--lg {
    width: 2.45rem;
    height: 2.45rem;
    border-radius: 0.8rem;
  }
  .employee-mega-active-dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .employee-mega-mobile-trigger {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 3.1rem;
    padding: 0.7rem 0.85rem;
    border: 1px solid #d8e0ea;
    border-radius: 0.95rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    color: #1e293b;
    box-shadow: 0 10px 22px -20px rgba(15, 23, 42, 0.35);
  }
  .employee-mega-mobile-trigger-main {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
  }
  .employee-mega-mobile-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 0.75rem;
    flex-shrink: 0;
  }
  .employee-mega-mobile-copy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  }
  .employee-mega-mobile-kicker {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .employee-mega-mobile-title {
    font-size: 0.92rem;
    font-weight: 700;
    color: #0f172a;
  }
  .employee-mega-mobile-panel {
    display: grid;
    gap: 0.45rem;
    max-height: 0;
    opacity: 0;
    transform: translateY(-4px);
    pointer-events: none;
    overflow: hidden;
    transition: max-height 240ms ease, opacity 180ms ease, transform 180ms ease, margin 180ms ease;
  }
  .employee-mega-mobile-panel.open {
    max-height: min(80vh, 48rem);
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
    margin-top: 0.55rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  .employee-mega-mobile-group {
    border: 1px solid #e2e8f0;
    border-radius: 0.95rem;
    background: #ffffff;
    overflow: hidden;
  }
  .employee-mega-mobile-group.is-active {
    border-color: #bfdbfe;
    box-shadow: 0 10px 24px -20px rgba(37, 99, 235, 0.45);
  }
  .employee-mega-mobile-group-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.75rem 0.8rem;
    text-align: left;
    background: transparent;
    color: #334155;
  }
  .employee-mega-mobile-group-copy {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1;
  }
  .employee-mega-mobile-group-label {
    font-size: 0.86rem;
    font-weight: 700;
    color: #0f172a;
  }
  .employee-mega-mobile-group-blurb {
    font-size: 0.72rem;
    color: #64748b;
    line-height: 1.3;
  }
  .employee-mega-mobile-items {
    display: grid;
    gap: 0.3rem;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 220ms ease, opacity 160ms ease, padding 160ms ease;
  }
  .employee-mega-mobile-items.open {
    max-height: none;
    opacity: 1;
    padding: 0 0.45rem 0.55rem;
    overflow: visible;
  }
  .employee-mega-menu--tablet .employee-mega-mobile-items.open {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .employee-mega-link {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.65rem;
    text-align: left;
    padding: 0.65rem 0.55rem;
    border-radius: 0.75rem;
    border: 1px solid transparent;
    background: transparent;
    color: #334155;
    transition: background-color 160ms ease, border-color 160ms ease;
  }
  .employee-mega-link:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
  }
  .employee-mega-link.active {
    background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
    border-color: #93c5fd;
  }
  .employee-mega-link-copy {
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
    min-width: 0;
    flex: 1;
  }
  .employee-mega-link-title {
    font-size: 0.84rem;
    font-weight: 700;
    color: #0f172a;
  }
  .employee-mega-link-desc {
    font-size: 0.7rem;
    color: #64748b;
    line-height: 1.3;
  }
  @keyframes employeeMegaCardIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (max-width: 1100px) {
    .employee-mega-grid.cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .employee-mega-current { display: none; }
  }
  @media (max-width: 900px) {
    .employee-mega-grid.cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  .employee-profile-shell--dark .employee-profile-nav-shell {
    border-color: #334155;
    background:
      radial-gradient(120% 140% at 0% 0%, rgba(56, 189, 248, 0.08), transparent 42%),
      radial-gradient(100% 120% at 100% 0%, rgba(99, 102, 241, 0.1), transparent 40%),
      linear-gradient(180deg, #0b1220 0%, #0f172a 100%);
    box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.09);
  }
  .employee-profile-shell--dark .employee-mega-trigger {
    color: #cbd5e1;
  }
  .employee-profile-shell--dark .employee-mega-trigger:hover,
  .employee-profile-shell--dark .employee-mega-trigger.open {
    background: rgba(30, 41, 59, 0.85);
    border-color: #475569;
    color: #e2e8f0;
  }
  .employee-profile-shell--dark .employee-mega-trigger.active {
    background: linear-gradient(180deg, rgba(30, 58, 138, 0.45) 0%, rgba(30, 64, 175, 0.32) 100%);
    border-color: #3b82f6;
    color: #dbeafe;
  }
  .employee-profile-shell--dark .employee-mega-current-kicker {
    color: #64748b;
  }
  .employee-profile-shell--dark .employee-mega-current-value {
    color: #e2e8f0;
  }
  .employee-profile-shell--dark .employee-mega-panel-inner {
    border-color: #334155;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.94) 0%, rgba(11, 18, 32, 0.96) 100%);
    box-shadow: 0 24px 48px -28px rgba(0, 0, 0, 0.65);
  }
  .employee-profile-shell--dark .employee-mega-panel-inner::before {
    opacity: 0.08;
  }
  .employee-profile-shell--dark .employee-mega-panel-eyebrow {
    color: #94a3b8;
  }
  .employee-profile-shell--dark .employee-mega-panel-blurb {
    color: #94a3b8;
  }
  .employee-profile-shell--dark .employee-mega-card {
    border-color: #334155;
    background: linear-gradient(180deg, #111827 0%, #0f172a 100%);
    color: #cbd5e1;
  }
  .employee-profile-shell--dark .employee-mega-card:hover {
    border-color: #475569;
    background: #1e293b;
  }
  .employee-profile-shell--dark .employee-mega-card.active {
    border-color: #3b82f6;
    background: linear-gradient(180deg, rgba(30, 58, 138, 0.45) 0%, rgba(30, 64, 175, 0.3) 100%);
  }
  .employee-profile-shell--dark .employee-mega-card-title {
    color: #f8fafc;
  }
  .employee-profile-shell--dark .employee-mega-card-desc {
    color: #94a3b8;
  }
  .employee-profile-shell--dark .employee-mega-mobile-trigger {
    border-color: #334155;
    background: linear-gradient(180deg, #111827 0%, #0f172a 100%);
    color: #e2e8f0;
  }
  .employee-profile-shell--dark .employee-mega-mobile-title {
    color: #f8fafc;
  }
  .employee-profile-shell--dark .employee-mega-mobile-group {
    border-color: #334155;
    background: #111827;
  }
  .employee-profile-shell--dark .employee-mega-mobile-group.is-active {
    border-color: #3b82f6;
  }
  .employee-profile-shell--dark .employee-mega-mobile-group-label {
    color: #f8fafc;
  }
  .employee-profile-shell--dark .employee-mega-mobile-group-blurb,
  .employee-profile-shell--dark .employee-mega-link-desc {
    color: #94a3b8;
  }
  .employee-profile-shell--dark .employee-mega-link:hover {
    background: #1e293b;
    border-color: #334155;
  }
  .employee-profile-shell--dark .employee-mega-link.active {
    background: linear-gradient(180deg, rgba(30, 58, 138, 0.45) 0%, rgba(30, 64, 175, 0.3) 100%);
    border-color: #3b82f6;
  }
  .employee-profile-shell--dark .employee-mega-link-title {
    color: #f8fafc;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) {
    background: #0b1220;
    border-color: #334155;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50 {
    background: #0b1220 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-100 {
    background: #111827 !important;
    border-color: #334155 !important;
    box-shadow: inset 0 0 0 1px rgba(100, 116, 139, 0.4);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-900 {
    color: #f8fafc !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-700 {
    color: #e2e8f0 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-600,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-500 {
    color: #cbd5e1 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-400 {
    color: #94a3b8 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .border-slate-100,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .border-slate-200 {
    border-color: #334155 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .divide-slate-100 > :not([hidden]) ~ :not([hidden]) {
    border-color: #334155 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .hover\\:bg-slate-50:hover {
    background: #1e293b !important;
  }
  /* Cycles tab — assessment schedule: neutral text + tinted panels + type pills missed by flipped bg-white/bg-slate-50 alone */
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-slate-800 {
    color: #f1f5f9 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50\\/70 {
    background-color: rgba(51, 65, 85, 0.5) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50\\/50 {
    background-color: rgba(30, 41, 59, 0.82) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/70,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/90 {
    background-color: rgba(30, 41, 59, 0.92) !important;
    box-shadow: inset 0 0 0 1px rgba(51, 65, 85, 0.9);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-indigo-100 {
    background-color: rgba(79, 70, 229, 0.38) !important;
    box-shadow: inset 0 0 0 1px rgba(165, 180, 252, 0.5);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-indigo-700 {
    color: #e0e7ff !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-indigo-800 {
    color: #eef2ff !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-indigo-50 {
    background-color: rgba(30, 58, 138, 0.42) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .border-indigo-200 {
    border-color: rgba(129, 140, 248, 0.5) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .hover\\:bg-indigo-50:hover {
    background-color: rgba(30, 58, 138, 0.55) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .hover\\:bg-indigo-100:hover {
    background-color: rgba(55, 48, 163, 0.45) !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-cyan-100 {
    background-color: rgba(6, 182, 212, 0.26) !important;
    box-shadow: inset 0 0 0 1px rgba(103, 232, 249, 0.45);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-cyan-700 {
    color: #a5f3fc !important;
  }
  /* Status / tally chips — keep hue, lift text slightly on tinted fills */
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-emerald-100\\/90 {
    background-color: rgba(16, 185, 129, 0.22) !important;
    box-shadow: inset 0 0 0 1px rgba(52, 211, 153, 0.45);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-red-100\\/90 {
    background-color: rgba(248, 113, 113, 0.2) !important;
    box-shadow: inset 0 0 0 1px rgba(252, 165, 165, 0.45);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-amber-100\\/90 {
    background-color: rgba(245, 158, 11, 0.2) !important;
    box-shadow: inset 0 0 0 1px rgba(251, 191, 36, 0.42);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-rose-100\\/90 {
    background-color: rgba(244, 63, 94, 0.2) !important;
    box-shadow: inset 0 0 0 1px rgba(253, 164, 175, 0.42);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-sky-100\\/90 {
    background-color: rgba(14, 165, 233, 0.2) !important;
    box-shadow: inset 0 0 0 1px rgba(125, 211, 252, 0.42);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-200\\/90 {
    background-color: rgba(71, 85, 105, 0.55) !important;
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.35);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .text-indigo-600 {
    color: #a5b4fc !important;
  }
  /* High-contrast pills (type + status) — avoid pastel-on-pastel once dark shell is active */
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-indigo-100.text-indigo-700 {
    background-color: #4338ca !important;
    color: #eef2ff !important;
    box-shadow: inset 0 0 0 1px rgba(196, 181, 253, 0.45);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-cyan-100.text-cyan-700 {
    background-color: #0e7490 !important;
    color: #ecfeff !important;
    box-shadow: inset 0 0 0 1px rgba(103, 232, 249, 0.4);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-emerald-100\\/90.text-emerald-800 {
    background-color: rgba(6, 95, 70, 0.92) !important;
    color: #d1fae5 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-amber-100\\/90.text-amber-800 {
    background-color: rgba(146, 64, 14, 0.9) !important;
    color: #fef3c7 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-red-100\\/90.text-red-800 {
    background-color: rgba(153, 27, 27, 0.88) !important;
    color: #fee2e2 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-rose-100\\/90.text-rose-800 {
    background-color: rgba(157, 23, 77, 0.9) !important;
    color: #ffe4e6 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-sky-100\\/90.text-sky-800 {
    background-color: rgba(3, 105, 161, 0.9) !important;
    color: #e0f2fe !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-200\\/90.text-slate-700 {
    background-color: #475569 !important;
    color: #f8fafc !important;
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.35);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50\\/70 .text-slate-600,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50\\/70 .text-slate-500 {
    color: #e2e8f0 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-100.text-slate-600 {
    background-color: #334155 !important;
    color: #e2e8f0 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-100.text-slate-500 {
    background-color: #334155 !important;
    color: #cbd5e1 !important;
  }
  /* Criteria x/y chip: was white/70 + slate text */
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/70.text-slate-700 {
    background-color: rgba(15, 23, 42, 0.95) !important;
    color: #f1f5f9 !important;
    box-shadow: inset 0 0 0 1px rgba(71, 85, 105, 0.9);
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/70 .text-emerald-600 {
    color: #6ee7b7 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/70 .text-slate-400 {
    color: #94a3b8 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-white\\/70 .text-slate-500 {
    color: #cbd5e1 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-force-light,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-force-light.bg-white,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-force-light .bg-white,
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-force-light img {
    background: #ffffff !important;
    background-color: #ffffff !important;
    border-color: #cbd5e1 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .border-slate-300 {
    border-color: #64748b !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-drop-zone {
    background-color: #1e293b !important;
    border-color: #64748b !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .signature-drop-zone:hover {
    background-color: #334155 !important;
    border-color: #94a3b8 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-slate-50.text-slate-600.border-slate-200 {
    background-color: #334155 !important;
    color: #e2e8f0 !important;
    border-color: #475569 !important;
  }
  .employee-profile-shell--dark :is(.employee-pane-surface, .viewing-cycle-pane) .bg-indigo-50.text-indigo-700.border-indigo-100 {
    background-color: #4338ca !important;
    color: #eef2ff !important;
    border-color: #6366f1 !important;
  }
`;

const EXPERIENCE_COUNTERS = [
  { key: 'spad_with_authority', label: 'Passing signal at danger with authority' },
  { key: 'stopping_at_danger_signal', label: 'Stopping at danger signals' },
  { key: 'single_line_working', label: 'Single line working' },
  { key: 'extended_block_working', label: 'Extended block working' },
  { key: 'train_faults', label: 'Train faults' },
  { key: 'train_failure', label: 'Train failure' },
  { key: 'low_rail_adhesion', label: 'Low rail adhesion driving' },
  { key: 'prepare_cab', label: 'Prepare cabs' },
  { key: 'terminate_cab', label: 'Terminate cabs' },
  { key: 'coupling', label: 'Coupling' },
  { key: 'uncoupling', label: 'Uncoupling' },
  { key: 'approaching_buffer_stops', label: 'Approaching buffer stops' },
  { key: 'unit_prep', label: 'Unit prep' },
  { key: 'safety_critical_communication', label: 'Safety critical communication' },
  { key: 'shunt_driving', label: 'Shunt driving' },
  { key: 'adverse_weather_driving', label: 'Driving in adverse weather' },
  { key: 'permissive_working', label: 'Permissive working' },
] as const;

const EMERGENCY_COUNTERS = [
  { key: 'emergency_brake_applied', label: 'Emergency brake applied' },
  { key: 'resetting_trip_cock', label: 'Resetting trip cock' },
  { key: 'lock_out_process_station', label: 'Lock out process in station' },
  { key: 'emergency_evacuation', label: 'Emergency evacuation' },
] as const;

type ExperienceCounterKey = (typeof EXPERIENCE_COUNTERS)[number]['key'];
type EmergencyCounterKey = (typeof EMERGENCY_COUNTERS)[number]['key'];
type ExperienceCountKey = ExperienceCounterKey | EmergencyCounterKey;

const EXPERIENCE_COUNTER_MOBILE_LABELS: Partial<Record<ExperienceCountKey, string>> = {
  spad_with_authority: 'SPAD w/ authority',
  stopping_at_danger_signal: 'Stop at danger',
  single_line_working: 'Single line',
  extended_block_working: 'Ext. block',
  train_faults: 'Train faults',
  train_failure: 'Train failure',
  low_rail_adhesion: 'Low adhesion',
  prepare_cab: 'Prepare cab',
  terminate_cab: 'Terminate cab',
  coupling: 'Coupling',
  uncoupling: 'Uncoupling',
  approaching_buffer_stops: 'Buffer stops',
  unit_prep: 'Unit prep',
  safety_critical_communication: 'Safety comms',
  shunt_driving: 'Shunt driving',
  adverse_weather_driving: 'Adverse weather',
  permissive_working: 'Permissive',
  emergency_brake_applied: 'E-brake applied',
  resetting_trip_cock: 'Reset trip cock',
  lock_out_process_station: 'Lock out process',
  emergency_evacuation: 'Emergency evac',
};

type ExperienceDrivingSession = {
  id: string;
  date: string;
  traction: string;
  route?: string;
  notes?: string;
  feedback?: string;
  start_hour: number;
  start_minute: number;
  finish_hour: number;
  finish_minute: number;
  duration_minutes: number;
  /** Portion of this session counted as darktime driving (minutes); must not exceed duration_minutes. */
  dark_duration_minutes?: number;
  train_source?: 'manual' | 'feed';
  train_service_ref?: string;
  encounters?: Array<{
    key: string;
    label: string;
    count: number;
  }>;
};

type ExperienceRouteRow = {
  id: string;
  name: string;
  trips: number;
};

type ExperienceCustomRow = {
  id: string;
  name: string;
  count: number;
};

type ExperienceAllocatedInstructor = {
  employee_id: string | null;
  name: string | null;
  updated_at?: string;
};

type ExperienceInstructorAssignmentEntry = {
  id: string;
  action: "assigned" | "reassigned" | "cleared" | string;
  instructor_employee_id: string | null;
  instructor_name: string | null;
  previous_instructor_employee_id: string | null;
  previous_instructor_name: string | null;
  assigned_at: string;
  assigned_by: string;
  assigned_by_name?: string;
};

type ExperienceRecordsState = {
  driving_sessions: ExperienceDrivingSession[];
  /** Manager / qualified driver hours — same session shape; stored separately from trainee `driving_sessions`. */
  manager_driving_sessions: ExperienceDrivingSession[];
  experience_counts: Partial<Record<ExperienceCountKey, number>>;
  custom_experience: ExperienceCustomRow[];
  routes: ExperienceRouteRow[];
  capture_cadence?: 'daily' | 'weekly';
  updated_at?: string;
  /** Synced from Trainee Driver enrolment — current allocated driving instructor. */
  allocated_instructor?: ExperienceAllocatedInstructor | null;
  /** Synced from Trainee Driver enrolment — assignment / reassignment audit trail. */
  instructor_assignment_history?: ExperienceInstructorAssignmentEntry[];
};

const EMPTY_EXPERIENCE_RECORDS: ExperienceRecordsState = {
  driving_sessions: [],
  manager_driving_sessions: [],
  experience_counts: {},
  custom_experience: [],
  routes: [],
  allocated_instructor: null,
  instructor_assignment_history: [],
};

function coerceNonNegativeInt(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.round(value));
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.round(n));
  }
  return 0;
}

function parseExperienceDrivingSessionsArray(raw: unknown): ExperienceDrivingSession[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row: any): ExperienceDrivingSession => ({
      id: String(row?.id || `${Date.now()}_${Math.random()}`),
      date: typeof row?.date === 'string' ? row.date : '',
      traction: typeof row?.traction === 'string' ? row.traction : '',
      route: typeof row?.route === 'string' ? row.route : '',
      notes: typeof row?.notes === 'string' ? row.notes : '',
      feedback: typeof row?.feedback === 'string' ? row.feedback : '',
      start_hour: coerceNonNegativeInt(row?.start_hour) % 24,
      start_minute: coerceNonNegativeInt(row?.start_minute) % 60,
      finish_hour: coerceNonNegativeInt(row?.finish_hour) % 24,
      finish_minute: coerceNonNegativeInt(row?.finish_minute) % 60,
      duration_minutes: coerceNonNegativeInt(row?.duration_minutes),
      dark_duration_minutes: (() => {
        const dur = coerceNonNegativeInt(row?.duration_minutes);
        let d = coerceNonNegativeInt(row?.dark_duration_minutes);
        if (d > dur) d = dur;
        return d;
      })(),
      train_source: row?.train_source === 'feed' ? 'feed' : 'manual',
      train_service_ref: typeof row?.train_service_ref === 'string' ? row.train_service_ref : '',
      encounters: Array.isArray(row?.encounters)
        ? row.encounters
            .map((enc: any) => ({
              key: typeof enc?.key === 'string' ? enc.key : '',
              label: typeof enc?.label === 'string' ? enc.label : '',
              count: Math.max(1, coerceNonNegativeInt(enc?.count || 1)),
            }))
            .filter((enc: { key: string; label: string; count: number }) => enc.label.trim().length > 0)
        : [],
    }))
    .filter((row) => row.duration_minutes >= 0);
}

function parseExperienceRecords(value: unknown): ExperienceRecordsState {
  if (!value || typeof value !== 'string') return EMPTY_EXPERIENCE_RECORDS;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return EMPTY_EXPERIENCE_RECORDS;
    const driving_sessions = parseExperienceDrivingSessionsArray((parsed as any).driving_sessions);
    const manager_driving_sessions = parseExperienceDrivingSessionsArray((parsed as any).manager_driving_sessions);
    const routes = Array.isArray((parsed as any).routes)
      ? (parsed as any).routes
          .map((row: any) => ({
            id: String(row?.id || `${Date.now()}_${Math.random()}`),
            name: typeof row?.name === 'string' ? row.name : '',
            trips: Math.max(1, coerceNonNegativeInt(row?.trips || 1)),
          }))
          .filter((row: ExperienceRouteRow) => row.name.trim().length > 0)
      : [];
    const custom_experience = Array.isArray((parsed as any).custom_experience)
      ? (parsed as any).custom_experience
          .map((row: any) => ({
            id: String(row?.id || `exp-custom-${Date.now()}_${Math.random()}`),
            name: typeof row?.name === 'string' ? row.name.trim() : '',
            count: Math.max(1, coerceNonNegativeInt(row?.count || 1)),
          }))
          .filter((row: ExperienceCustomRow) => row.name.length > 0)
      : [];
    const counts: Partial<Record<ExperienceCountKey, number>> = {};
    const rawCounts = (parsed as any).experience_counts;
    if (rawCounts && typeof rawCounts === 'object') {
      [...EXPERIENCE_COUNTERS, ...EMERGENCY_COUNTERS].forEach(({ key }) => {
        counts[key] = coerceNonNegativeInt((rawCounts as Record<string, unknown>)[key]);
      });
    }
    const updated_at = typeof (parsed as any).updated_at === 'string' ? (parsed as any).updated_at : undefined;
    const capture_cadence = (parsed as any).capture_cadence === 'weekly' ? 'weekly' : 'daily';
    const allocatedRaw = (parsed as any).allocated_instructor;
    const allocated_instructor =
      allocatedRaw && typeof allocatedRaw === 'object'
        ? {
            employee_id: String((allocatedRaw as any).employee_id ?? '').trim() || null,
            name: String((allocatedRaw as any).name ?? '').trim() || null,
            updated_at: typeof (allocatedRaw as any).updated_at === 'string' ? (allocatedRaw as any).updated_at : undefined,
          }
        : null;
    const instructor_assignment_history = Array.isArray((parsed as any).instructor_assignment_history)
      ? ((parsed as any).instructor_assignment_history as any[])
          .map((row: any): ExperienceInstructorAssignmentEntry => ({
            id: String(row?.id || `ia-${Date.now()}`),
            action: String(row?.action || 'assigned'),
            instructor_employee_id: String(row?.instructor_employee_id ?? '').trim() || null,
            instructor_name: String(row?.instructor_name ?? '').trim() || null,
            previous_instructor_employee_id: String(row?.previous_instructor_employee_id ?? '').trim() || null,
            previous_instructor_name: String(row?.previous_instructor_name ?? '').trim() || null,
            assigned_at: String(row?.assigned_at ?? ''),
            assigned_by: String(row?.assigned_by ?? ''),
            assigned_by_name: typeof row?.assigned_by_name === 'string' ? row.assigned_by_name : undefined,
          }))
          .filter((row: ExperienceInstructorAssignmentEntry) => !!row.assigned_at)
      : [];
    return {
      driving_sessions,
      manager_driving_sessions,
      experience_counts: counts,
      custom_experience,
      routes,
      capture_cadence,
      updated_at,
      allocated_instructor,
      instructor_assignment_history,
    };
  } catch {
    return EMPTY_EXPERIENCE_RECORDS;
  }
}

function calculateDrivingDurationMinutes(
  startHour: number,
  startMinute: number,
  finishHour: number,
  finishMinute: number
): number {
  const start = Math.max(0, Math.min(23, startHour)) * 60 + Math.max(0, Math.min(59, startMinute));
  const finish = Math.max(0, Math.min(23, finishHour)) * 60 + Math.max(0, Math.min(59, finishMinute));
  const raw = finish - start;
  if (raw >= 0) return raw;
  return 24 * 60 + raw;
}

function formatHoursMinutes(totalMinutes: number): string {
  const mins = Math.max(0, coerceNonNegativeInt(totalMinutes));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function parseTimeValue(value: string): { hour: number; minute: number } {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return { hour: 0, minute: 0 };
  return {
    hour: Math.max(0, Math.min(23, coerceNonNegativeInt(match[1]))),
    minute: Math.max(0, Math.min(59, coerceNonNegativeInt(match[2]))),
  };
}

function getTodayDateInputValue(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local calendar date from `YYYY-MM-DD`; returns null if invalid. */
function parseCalendarDateYmd(ymd: string): Date | null {
  const m = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

/** Elapsed calendar months and days from `start` (inclusive) to `end` (inclusive), both at local midnight. */
function monthsAndDaysBetweenCalendarStartEnd(start: Date, end: Date): { months: number; days: number } {
  if (end.getTime() < start.getTime()) return { months: 0, days: 0 };
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  let days = end.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
  }
  return { months: Math.max(0, months), days: Math.max(0, days) };
}

function formatMonthsAndDaysLabel(m: { months: number; days: number }): string {
  const parts: string[] = [];
  if (m.months > 0) parts.push(`${m.months} month${m.months === 1 ? '' : 's'}`);
  parts.push(`${m.days} day${m.days === 1 ? '' : 's'}`);
  return parts.join(', ');
}

/**
 * Trainee hours apply only when the job role text includes "trainee" and is not
 * a manager/managing-style role (e.g. "Trainee Manager" remains excluded).
 */
function employeeRoleExcludesTraineeHours(role: string | undefined | null): boolean {
  const r = String(role ?? '').toLowerCase();
  if (!r.includes('trainee')) return true;
  return r.includes('manager') || r.includes('managing');
}

/**
 * Manager competence hours apply to manager-type job roles (text contains
 * "manager" or "managing"). Other roles (e.g. translateUi("Trainee Driver"), "Driver") see a greyed pane.
 */
function employeeRoleExcludesManagerHours(role: string | undefined | null): boolean {
  const r = String(role ?? '').toLowerCase();
  return !r.includes('manager') && !r.includes('managing');
}

/** Aligns with Company standards → Trainee driving (daylight + darktime minimums). */
function parseTraineeDrivingStandardsMinutes(data: Record<string, unknown>): {
  dayMinutes: number;
  darkMinutes: number;
} {
  const finite = (v: unknown): number | null => {
    if (v == null) return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  let dayM = finite(data.traineeMinimumDaylightDrivingMinutes);
  if (dayM == null) {
    const h = finite(data.traineeMinimumDaylightDrivingHours);
    if (h != null) dayM = Math.floor(h * 60);
  }
  if (dayM == null) {
    const h = finite(data.traineeMinimumDrivingHours);
    if (h != null) dayM = Math.floor(h * 60);
  }
  let darkM = finite(data.traineeMinimumDarkDrivingMinutes);
  if (darkM == null) {
    const h = finite(data.traineeMinimumDarkDrivingHours);
    if (h != null) darkM = Math.floor(h * 60);
  }
  return {
    dayMinutes: Math.max(0, dayM ?? 0),
    darkMinutes: Math.max(0, darkM ?? 0),
  };
}

/** Aligns with Company standards → Maintain driver competence (non-driver minimum + optional rolling period). */
function parseManagerCompetenceStandardsFromApi(data: Record<string, unknown>): {
  minimumMinutes: number;
  periodMonths: number | null;
} {
  const finite = (v: unknown): number | null => {
    if (v == null) return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  let minM = finite(data.nonDriverCompetenceMinimumDrivingMinutes);
  if (minM == null) {
    const h = finite(data.nonDriverCompetenceMinimumDrivingHours);
    if (h != null) minM = Math.floor(h * 60);
  }
  const perRaw = data.nonDriverCompetenceMaintenancePeriodMonths;
  let periodMonths: number | null = null;
  if (perRaw != null) {
    const n = typeof perRaw === 'number' ? perRaw : Number(perRaw);
    if (Number.isFinite(n)) {
      const floored = Math.floor(Number(n));
      if (floored > 0) periodMonths = floored;
    }
  }
  return {
    minimumMinutes: Math.max(0, minM ?? 0),
    periodMonths,
  };
}

/** `YYYY-MM-DD` for local `date` at local midnight. */
function formatCalendarDateYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function ExperienceTraineeHoursDonut({
  recordedMinutes,
  requiredMinutes,
  isDarkMode,
  size = 228,
  centerLabel = 'Logged',
  variant = 'indigo',
  indigoOverExcessLabel = 'combined minimum',
}: {
  recordedMinutes: number;
  requiredMinutes: number;
  isDarkMode: boolean;
  size?: number;
  centerLabel?: string;
  variant?: 'indigo' | 'violet';
  /** Shown after “above …” when logged exceeds required (indigo variant only). */
  indigoOverExcessLabel?: string;
}) {
  const stroke = Math.max(12, Math.round(size * 0.083));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const completedPortion = requiredMinutes > 0 ? Math.min(1, recordedMinutes / requiredMinutes) : 0;
  const dashProgress = circumference * completedPortion;
  const trackClass = isDarkMode ? "text-slate-700" : "text-slate-200";
  const progressClass =
    variant === 'violet'
      ? isDarkMode
        ? "text-violet-400"
        : "text-violet-600"
      : isDarkMode
        ? "text-indigo-400"
        : "text-indigo-600";
  const overBy = Math.max(0, recordedMinutes - requiredMinutes);
  const remaining = Math.max(0, requiredMinutes - Math.min(recordedMinutes, requiredMinutes));
  const doneClass =
    variant === 'violet'
      ? isDarkMode
        ? "text-emerald-400"
        : "text-emerald-700"
      : isDarkMode
        ? "text-emerald-400"
        : "text-emerald-700";
  const overClass =
    variant === 'violet'
      ? isDarkMode
        ? "text-violet-300"
        : "text-violet-700"
      : isDarkMode
        ? "text-indigo-300"
        : "text-indigo-700";

  return (
    <div className="flex flex-col items-center gap-2 shrink-0">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]" aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className={trackClass}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dashProgress} ${circumference}`}
            className={progressClass}
          />
        </svg>
        <div
          className={cn(
            'pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center px-2',
            isDarkMode ? "text-slate-100" : "text-slate-800"
          )}
        >
          <span className="text-xs font-semibold uppercase tracking-wider opacity-80">{centerLabel}</span>
          <span
            className={cn(
              'font-bold tabular-nums leading-tight',
              size >= 210 ? "text-xl" : "text-lg"
            )}
          >
            {formatHoursMinutes(recordedMinutes)}
          </span>
          <span className={cn('text-[10px] mt-0.5 tabular-nums', isDarkMode ? "text-slate-400" : "text-slate-500")}>
            {variant === 'violet' ? (
              <>
                minimum {formatHoursMinutes(requiredMinutes)}
              </>
            ) : (
              <>
                of {formatHoursMinutes(requiredMinutes)}
              </>
            )}
          </span>
        </div>
      </div>
      <div className={cn('w-full text-[11px] space-y-1', isDarkMode ? "text-slate-300" : "text-slate-600")}>
        <div className="flex justify-between gap-2">
          <span className={cn('font-medium', doneClass)}>
            {variant === 'violet' ? translateUi("Toward minimum") : translateUi("Completed toward target")}
          </span>
          <span className="tabular-nums font-semibold">{formatHoursMinutes(Math.min(recordedMinutes, requiredMinutes))}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="font-medium">{variant === 'violet' ? translateUi("Remaining to minimum") : translateUi("Remaining")}</span>
          <span className="tabular-nums font-semibold">{formatHoursMinutes(remaining)}</span>
        </div>
        {overBy > 0 && (
          <p
            className={cn(
              'text-[10px] pt-0.5',
              variant === 'violet'
                ? isDarkMode
                  ? 'text-emerald-300/95'
                  : 'text-emerald-700'
                : overClass
            )}
          >
            {variant === 'violet'
              ? `${formatHoursMinutes(overBy)} beyond minimum — requirement met`
              : `${formatHoursMinutes(overBy)} above ${indigoOverExcessLabel}`}
          </p>
        )}
      </div>
    </div>
  );
}

const ENCOUNTER_PIE_COLORS_LIGHT = [
  '#7c3aed',
  '#4f46e5',
  '#0d9488',
  '#d97706',
  '#e11d48',
  '#2563eb',
  '#059669',
  '#db2777',
  '#65a30d',
  '#ea580c',
];
const ENCOUNTER_PIE_COLORS_DARK = [
  '#a78bfa',
  '#818cf8',
  '#2dd4bf',
  '#fbbf24',
  '#fb7185',
  '#60a5fa',
  '#34d399',
  '#f472b6',
  '#a3e635',
  '#fb923c',
];

function ExperienceEncountersDistributionBar({
  rows,
  isDarkMode,
}: {
  rows: Array<{ key: string; label: string; count: number }>;
  isDarkMode: boolean;
}) {
  const total = rows.reduce((s, r) => s + Math.max(0, r.count), 0);
  const [selectedSegmentKey, setSelectedSegmentKey] = useState<string | null>(null);

  const segments = useMemo(() => {
    if (total <= 0) return [] as Array<{ key: string; label: string; count: number; pct: number; color: string }>;
    const pal = isDarkMode ? ENCOUNTER_PIE_COLORS_DARK : ENCOUNTER_PIE_COLORS_LIGHT;
    return rows.map((row, i) => ({
      ...row,
      count: Math.max(0, row.count),
      pct: (Math.max(0, row.count) / total) * 100,
      color: pal[i % pal.length],
    }));
  }, [rows, total, isDarkMode]);
  const selectedSegment = useMemo(
    () => segments.find((seg) => seg.key === selectedSegmentKey) ?? null,
    [segments, selectedSegmentKey]
  );

  useEffect(() => {
    if (!selectedSegmentKey) return;
    if (segments.some((seg) => seg.key === selectedSegmentKey)) return;
    setSelectedSegmentKey(null);
  }, [segments, selectedSegmentKey]);

  if (total <= 0) return null;

  const label = `Encounters across driving entries: ${total} total, ${rows.length} categories`;

  return (
    <div className="min-w-0 space-y-3">
      <p
        className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-indigo-200/90' : 'text-indigo-800')}
      >
        Encounters achieved
      </p>
      <p className={cn('text-xs leading-relaxed', isDarkMode ? "text-slate-500" : "text-slate-600")}>
        Totals from all driving entries below — bar width shows each type’s share.
      </p>
      <p className={cn('text-[11px]', isDarkMode ? "text-slate-400" : "text-slate-500")}>
        Click a bar segment to view its detailed stats.
      </p>
      <div
        className={cn(
          'relative flex h-14 w-full overflow-hidden rounded-xl shadow-inner ring-1 ring-inset',
          isDarkMode ? 'ring-slate-600/80 bg-slate-950/50' : 'ring-slate-200 bg-slate-100/80'
        )}
        role="img"
        aria-label={label}
      >
        {segments.map((seg) =>
          seg.count > 0 ? (
            <button
              type="button"
              key={seg.key}
              onClick={() => setSelectedSegmentKey(seg.key)}
              className={cn(
                "h-full min-w-[3px] border-r border-white/25 last:border-r-0 dark:border-slate-950/40 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-indigo-500",
                selectedSegmentKey === seg.key && "brightness-110"
              )}
              style={{ width: `${seg.pct}%`, backgroundColor: seg.color }}
              title={`${seg.label}: ${seg.count} (${Math.round(seg.pct * 10) / 10}%)`}
              aria-label={translateUi("{{label}}: {{count}} encounters, {{pct}}% of total", { label: seg.label, count: seg.count, pct: Math.round(seg.pct * 10) / 10 })}
              aria-pressed={selectedSegmentKey === seg.key}
            />
          ) : null
        )}
      </div>
      {selectedSegment && (
        <div
          className={cn(
            "rounded-xl border p-3.5 sm:p-4",
            isDarkMode ? "border-indigo-400/40 bg-indigo-950/35" : "border-indigo-200 bg-indigo-50/80"
          )}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-200" : "text-indigo-700")}>
                Encounter stats
              </p>
              <p className={cn("mt-1 text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                {selectedSegment.label}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedSegmentKey(null)}
              className={cn(
                "rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors",
                isDarkMode
                  ? "border-slate-500 text-slate-200 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-white"
              )}
            > Close </button>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
            <div className={cn("rounded-lg px-2.5 py-2", isDarkMode ? "bg-slate-900/60 text-slate-200" : "bg-white text-slate-700")}>
              <span className={cn("block text-[10px] uppercase tracking-wide", isDarkMode ? "text-slate-400" : "text-slate-500")}>Count</span>
              <span className="font-semibold tabular-nums">{selectedSegment.count}</span>
            </div>
            <div className={cn("rounded-lg px-2.5 py-2", isDarkMode ? "bg-slate-900/60 text-slate-200" : "bg-white text-slate-700")}>
              <span className={cn("block text-[10px] uppercase tracking-wide", isDarkMode ? "text-slate-400" : "text-slate-500")}> Share </span>
              <span className="font-semibold tabular-nums">{Math.round(selectedSegment.pct * 10) / 10}%</span>
            </div>
            <div className={cn("rounded-lg px-2.5 py-2", isDarkMode ? "bg-slate-900/60 text-slate-200" : "bg-white text-slate-700")}>
              <span className={cn("block text-[10px] uppercase tracking-wide", isDarkMode ? "text-slate-400" : "text-slate-500")}>Total encounters</span>
              <span className="font-semibold tabular-nums">{total}</span>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={cn('text-2xl font-bold tabular-nums tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}>
          {total}
        </span>
        <span className={cn('text-xs font-medium', isDarkMode ? "text-slate-400" : "text-slate-600")}>encounters logged</span>
      </div>
    </div>
  );
}

function ExperienceEncountersPieLegend({
  rows,
  isDarkMode,
}: {
  rows: Array<{ key: string; label: string; count: number }>;
  isDarkMode: boolean;
}) {
  const total = rows.reduce((s, r) => s + Math.max(0, r.count), 0);
  const palette = isDarkMode ? ENCOUNTER_PIE_COLORS_DARK : ENCOUNTER_PIE_COLORS_LIGHT;

  if (total <= 0) return null;

  return (
    <ul
      className={cn(
        'grid grid-cols-1 gap-x-8 gap-y-2.5 text-xs sm:grid-cols-2 sm:text-[13px] lg:grid-cols-3 xl:grid-cols-4'
      )}
      aria-label={translateUi("Encounter legend")}
    >
      {rows.map((row, i) => {
        const pct = total > 0 ? Math.round((row.count / total) * 1000) / 10 : 0;
        const color = palette[i % palette.length];
        return (
          <li key={row.key} className="flex gap-2.5 items-start">
            <span
              className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/10 dark:ring-white/20"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <span className={cn('font-medium leading-snug block', isDarkMode ? "text-slate-200" : "text-slate-800")}>
                {row.label}
              </span>
              <span className={cn('tabular-nums text-[11px]', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                {row.count} ({pct}%)
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const ExperienceRecordsTab = ({
  experienceJson,
  tractionOptions,
  companyCode,
  currentUserEmail,
  employeeId,
  canAddExperienceRecords = false,
  canEdit,
  isDarkMode = false,
  onSave,
  onRefreshEmployee,
  employeeRole,
}: {
  experienceJson?: string;
  tractionOptions: string[];
  companyCode?: string;
  currentUserEmail?: string;
  employeeId?: string;
  canAddExperienceRecords?: boolean;
  canEdit: boolean;
  isDarkMode?: boolean;
  onSave: (nextJson: string) => Promise<boolean>;
  /** Refresh parent employee after instructor reassignment (syncs Experience Records JSON). */
  onRefreshEmployee?: () => unknown | Promise<unknown>;
  /** Employee role; used to grey out Trainee or Manager hours by role. */
  employeeRole?: string;
}) => {
  const records = useMemo(() => parseExperienceRecords(experienceJson), [experienceJson]);
  const canAddOnlyExperienceRecords = canAddExperienceRecords;
  const canEditExperienceEntryForms =
    (canEdit || canAddExperienceRecords) &&
    (!canAddOnlyExperienceRecords || canAddExperienceRecords);
  const canDeleteExperienceEntries = canEdit && !canAddOnlyExperienceRecords;
  const traineeHoursNotApplicable = useMemo(() => employeeRoleExcludesTraineeHours(employeeRole), [employeeRole]);
  const managerHoursNotApplicable = useMemo(() => employeeRoleExcludesManagerHours(employeeRole), [employeeRole]);
  const [drivingDate, setDrivingDate] = useState(() => getTodayDateInputValue());
  const [startTime, setStartTime] = useState('00:00');
  const [finishTime, setFinishTime] = useState('00:00');
  const [traction, setTraction] = useState('');
  const [drivingRouteFrom, setDrivingRouteFrom] = useState('');
  const [drivingRouteTo, setDrivingRouteTo] = useState('');
  const [drivingNotes, setDrivingNotes] = useState('');
  const [drivingFeedback, setDrivingFeedback] = useState('');
  const [trainSource, setTrainSource] = useState<'manual' | 'feed'>('manual');
  const [trainServiceRef, setTrainServiceRef] = useState('');
  const [encounterCustomLabel, setEncounterCustomLabel] = useState('');
  const [draftEncounters, setDraftEncounters] = useState<Array<{ key: string; label: string; count: number }>>([]);
  const [drivingHoursExpanded, setDrivingHoursExpanded] = useState(false);
  const [instructorPaneExpanded, setInstructorPaneExpanded] = useState(false);
  const [instructorEnrollmentState, setInstructorEnrollmentState] = useState<
    'idle' | 'loading' | 'enrolled' | 'not_enrolled' | 'error'
  >('idle');
  const [canManageTraineeInstructor, setCanManageTraineeInstructor] = useState(false);
  const [canDeleteInstructorAudit, setCanDeleteInstructorAudit] = useState(false);
  const [deletingInstructorAuditId, setDeletingInstructorAuditId] = useState<string | null>(null);
  const [instructorOptions, setInstructorOptions] = useState<Array<{ id: string; full_name?: string; role?: string }>>([]);
  const [instructorDraft, setInstructorDraft] = useState('');
  const [savingInstructor, setSavingInstructor] = useState(false);
  const [instructorError, setInstructorError] = useState<string | null>(null);
  const [liveInstructorHistory, setLiveInstructorHistory] = useState<ExperienceInstructorAssignmentEntry[] | null>(null);
  const [liveAllocatedInstructor, setLiveAllocatedInstructor] = useState<ExperienceAllocatedInstructor | null | undefined>(undefined);
  const [encountersExpanded, setEncountersExpanded] = useState(false);
  const [encounterCountModal, setEncounterCountModal] = useState<
    null | { mode: 'predefined'; key: string; label: string } | { mode: 'custom'; label: string }
  >(null);
  const [encounterModalCount, setEncounterModalCount] = useState('1');
  const [showAddDrivingConfirmModal, setShowAddDrivingConfirmModal] = useState(false);
  const [encounterPickerQuery, setEncounterPickerQuery] = useState('');
  const [showFeedLookupOption, setShowFeedLookupOption] = useState(false);
  const [saving, setSaving] = useState(false);
  const [traineeStandardsMinutes, setTraineeStandardsMinutes] = useState<{ dayMinutes: number; darkMinutes: number } | null>(null);
  const [managerCompetenceStandards, setManagerCompetenceStandards] = useState<{
    minimumMinutes: number;
    periodMonths: number | null;
  } | null>(null);
  const [traineeStandardsLoad, setTraineeStandardsLoad] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [darkDrivingHours, setDarkDrivingHours] = useState('0');
  const [darkDrivingMinutes, setDarkDrivingMinutes] = useState('0');
  const [showTraineeReadOnlyRecordsModal, setShowTraineeReadOnlyRecordsModal] = useState(false);
  const [expandedTraineeArchiveEntryId, setExpandedTraineeArchiveEntryId] = useState<string | null>(null);
  const [showManagerReadOnlyRecordsModal, setShowManagerReadOnlyRecordsModal] = useState(false);
  const [printTraineePageOneHtml, setPrintTraineePageOneHtml] = useState('');
  const [printTraineePageTwoHtml, setPrintTraineePageTwoHtml] = useState('');
  const [traineeDonutInfoPinned, setTraineeDonutInfoPinned] = useState(false);
  const [traineeDonutInfoHover, setTraineeDonutInfoHover] = useState(false);
  const traineeDonutInfoRef = useRef<HTMLDivElement>(null);
  const traineeExpandedContentRef = useRef<HTMLDivElement>(null);
  const traineeStatsPaneRef = useRef<HTMLDivElement>(null);
  const traineeFormPaneRef = useRef<HTMLDivElement>(null);
  const traineeEntriesPaneRef = useRef<HTMLDivElement>(null);
  const drivingHoursEntryFormRef = useRef<HTMLDivElement>(null);
  const [viewDrivingEntry, setViewDrivingEntry] = useState<ExperienceDrivingSession | null>(null);
  const [managerHoursExpanded, setManagerHoursExpanded] = useState(false);
  const managerDrivingHoursEntryFormRef = useRef<HTMLDivElement>(null);
  const [mgrDrivingDate, setMgrDrivingDate] = useState(() => getTodayDateInputValue());
  const [mgrStartTime, setMgrStartTime] = useState('00:00');
  const [mgrFinishTime, setMgrFinishTime] = useState('00:00');
  const [mgrTraction, setMgrTraction] = useState('');
  const [mgrDrivingRouteFrom, setMgrDrivingRouteFrom] = useState('');
  const [mgrDrivingRouteTo, setMgrDrivingRouteTo] = useState('');
  const [mgrDrivingNotes, setMgrDrivingNotes] = useState('');
  const [mgrTrainSource, setMgrTrainSource] = useState<'manual' | 'feed'>('manual');
  const [mgrTrainServiceRef, setMgrTrainServiceRef] = useState('');
  const [mgrDarkDrivingHours, setMgrDarkDrivingHours] = useState('0');
  const [mgrDarkDrivingMinutes, setMgrDarkDrivingMinutes] = useState('0');
  const [showAddManagerDrivingConfirmModal, setShowAddManagerDrivingConfirmModal] = useState(false);
  const [viewManagerDrivingEntry, setViewManagerDrivingEntry] = useState<ExperienceDrivingSession | null>(null);
  const [managerDonutInfoPinned, setManagerDonutInfoPinned] = useState(false);
  const [managerDonutInfoHover, setManagerDonutInfoHover] = useState(false);
  const managerDonutInfoRef = useRef<HTMLDivElement>(null);
  const showTraineeDonutInfo = traineeDonutInfoPinned || traineeDonutInfoHover;
  const showManagerDonutInfo = managerDonutInfoPinned || managerDonutInfoHover;

  const displayAllocatedInstructor =
    liveAllocatedInstructor !== undefined ? liveAllocatedInstructor : records.allocated_instructor ?? null;
  const displayInstructorHistory = useMemo(() => {
    const rows = Array.isArray(liveInstructorHistory)
      ? liveInstructorHistory
      : Array.isArray(records.instructor_assignment_history)
        ? records.instructor_assignment_history
        : [];
    return [...rows].sort((a, b) => String(b.assigned_at || '').localeCompare(String(a.assigned_at || '')));
  }, [liveInstructorHistory, records.instructor_assignment_history]);

  useEffect(() => {
    // Parent refresh after reassignment replaces experienceJson — prefer that as source of truth.
    setLiveAllocatedInstructor(undefined);
    setLiveInstructorHistory(null);
  }, [experienceJson]);

  const traineeDriverApiHeaders = useMemo(() => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (companyCode) headers['X-Company-Code'] = companyCode;
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    return headers;
  }, [companyCode, currentUserEmail]);

  const loadInstructorPaneData = useCallback(async () => {
    if (!companyCode || !employeeId || !currentUserEmail) {
      setInstructorEnrollmentState('idle');
      setCanManageTraineeInstructor(false);
      setCanDeleteInstructorAudit(false);
      return;
    }
    setInstructorEnrollmentState('loading');
    setInstructorError(null);
    const cc = `?companyCode=${encodeURIComponent(companyCode)}`;
    try {
      const [enrollRes, candidatesRes] = await Promise.all([
        fetch(`/api/trainee-driver/enrollments/${encodeURIComponent(employeeId)}${cc}`, {
          headers: traineeDriverApiHeaders,
          cache: 'no-store',
        }),
        fetch(`/api/trainee-driver/enrolment-candidates${cc}`, {
          headers: traineeDriverApiHeaders,
          cache: 'no-store',
        }),
      ]);

      if (candidatesRes.ok) {
        const cand = (await candidatesRes.json().catch(() => ({}))) as {
          instructors?: Array<{ id?: string; full_name?: string; role?: string }>;
        };
        const list = Array.isArray(cand.instructors) ? cand.instructors : [];
        setCanManageTraineeInstructor(true);
        setInstructorOptions(
          list
            .map((e) => ({
              id: String(e.id || '').trim(),
              full_name: e.full_name,
              role: e.role,
            }))
            .filter((e) => e.id && e.id !== employeeId),
        );
      } else {
        setCanManageTraineeInstructor(false);
        setInstructorOptions([]);
      }

      if (enrollRes.status === 404) {
        setInstructorEnrollmentState('not_enrolled');
        setInstructorDraft('');
        return;
      }
      if (!enrollRes.ok) {
        const errBody = (await enrollRes.json().catch(() => ({}))) as { error?: string };
        setInstructorEnrollmentState('error');
        setInstructorError(errBody.error || translateUi('Failed to load trainee enrolment.'));
        return;
      }
      const data = (await enrollRes.json().catch(() => ({}))) as {
        enrollment?: {
          instructor_employee_id?: string | null;
          instructor_name?: string | null;
          instructor_assignment_history?: ExperienceInstructorAssignmentEntry[];
        };
        can_delete_instructor_audit?: boolean;
      };
      const enrollment = data.enrollment;
      setCanDeleteInstructorAudit(data.can_delete_instructor_audit === true);
      const insId = String(enrollment?.instructor_employee_id ?? '').trim();
      const insName = String(enrollment?.instructor_name ?? '').trim() || null;
      setInstructorDraft(insId);
      setLiveAllocatedInstructor({
        employee_id: insId || null,
        name: insName,
        updated_at: new Date().toISOString(),
      });
      if (Array.isArray(enrollment?.instructor_assignment_history)) {
        setLiveInstructorHistory(enrollment.instructor_assignment_history);
      }
      setInstructorEnrollmentState('enrolled');
    } catch (err: any) {
      setInstructorEnrollmentState('error');
      setInstructorError(err?.message || translateUi('Failed to load instructor details.'));
    }
  }, [companyCode, currentUserEmail, employeeId, traineeDriverApiHeaders]);

  useEffect(() => {
    if (!instructorPaneExpanded) return;
    void loadInstructorPaneData();
  }, [instructorPaneExpanded, loadInstructorPaneData]);

  const deleteInstructorAuditEntry = useCallback(
    async (entryId: string) => {
      if (!companyCode || !employeeId || !canDeleteInstructorAudit || !entryId) return;
      const confirmed = window.confirm(
        translateUi('Delete this assignment audit entry? This cannot be undone.'),
      );
      if (!confirmed) return;
      setDeletingInstructorAuditId(entryId);
      setInstructorError(null);
      try {
        const cc = `?companyCode=${encodeURIComponent(companyCode)}`;
        const res = await fetch(
          `/api/trainee-driver/enrollments/${encodeURIComponent(employeeId)}/instructor-assignment-history/${encodeURIComponent(entryId)}${cc}`,
          {
            method: 'DELETE',
            headers: traineeDriverApiHeaders,
          },
        );
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          enrollment?: { instructor_assignment_history?: ExperienceInstructorAssignmentEntry[] };
        };
        if (!res.ok) throw new Error(body.error || translateUi('Failed to delete audit entry.'));
        if (Array.isArray(body.enrollment?.instructor_assignment_history)) {
          setLiveInstructorHistory(body.enrollment.instructor_assignment_history);
        }
        await onRefreshEmployee?.();
      } catch (err: any) {
        setInstructorError(err?.message || translateUi('Failed to delete audit entry.'));
      } finally {
        setDeletingInstructorAuditId(null);
      }
    },
    [canDeleteInstructorAudit, companyCode, employeeId, onRefreshEmployee, traineeDriverApiHeaders],
  );

  const saveInstructorAllocation = useCallback(async () => {
    if (!companyCode || !employeeId || !canManageTraineeInstructor) return;
    setSavingInstructor(true);
    setInstructorError(null);
    try {
      const cc = `?companyCode=${encodeURIComponent(companyCode)}`;
      const res = await fetch(`/api/trainee-driver/enrollments/${encodeURIComponent(employeeId)}${cc}`, {
        method: 'PATCH',
        headers: traineeDriverApiHeaders,
        body: JSON.stringify({
          companyCode,
          instructorEmployeeId: instructorDraft || null,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error || translateUi('Failed to save instructor allocation.'));
      await onRefreshEmployee?.();
      await loadInstructorPaneData();
    } catch (err: any) {
      setInstructorError(err?.message || translateUi('Failed to save instructor allocation.'));
    } finally {
      setSavingInstructor(false);
    }
  }, [
    canManageTraineeInstructor,
    companyCode,
    employeeId,
    instructorDraft,
    loadInstructorPaneData,
    onRefreshEmployee,
    traineeDriverApiHeaders,
  ]);

  const { filteredExperienceCounters, filteredEmergencyCounters } = useMemo(() => {
    const q = encounterPickerQuery.trim().toLowerCase();
    const match = (label: string, key: string) => {
      if (!q) return true;
      if (label.toLowerCase().includes(q)) return true;
      const short = EXPERIENCE_COUNTER_MOBILE_LABELS[key as ExperienceCountKey];
      return short ? short.toLowerCase().includes(q) : false;
    };
    return {
      filteredExperienceCounters: EXPERIENCE_COUNTERS.filter((o) => match(o.label, o.key)),
      filteredEmergencyCounters: EMERGENCY_COUNTERS.filter((o) => match(o.label, o.key)),
    };
  }, [encounterPickerQuery]);

  useEffect(() => {
    if (tractionOptions.length === 0) {
      if (traction) setTraction('');
      return;
    }
    if (traction && !tractionOptions.includes(traction)) setTraction('');
  }, [traction, tractionOptions]);

  useEffect(() => {
    if (tractionOptions.length === 0) {
      if (mgrTraction) setMgrTraction('');
      return;
    }
    if (mgrTraction && !tractionOptions.includes(mgrTraction)) setMgrTraction('');
  }, [mgrTraction, tractionOptions]);

  useEffect(() => {
    if (!companyCode || !currentUserEmail) {
      setShowFeedLookupOption(false);
      return;
    }
    const loadTrainSourceOptions = async () => {
      try {
        const cc = `?companyCode=${encodeURIComponent(companyCode)}`;
        const res = await fetch(`/api/train-source/options${cc}`, {
          headers: {
            'X-Company-Code': companyCode,
            'X-User-Email': currentUserEmail,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Train source config request failed (${res.status})`);
        setShowFeedLookupOption(data.feedLookupVisible === true);
      } catch (err) {
        setShowFeedLookupOption(false);
      }
    };
    void loadTrainSourceOptions();
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    if (!showFeedLookupOption && trainSource === 'feed') setTrainSource('manual');
  }, [showFeedLookupOption, trainSource]);

  useEffect(() => {
    if (!showFeedLookupOption && mgrTrainSource === 'feed') setMgrTrainSource('manual');
  }, [showFeedLookupOption, mgrTrainSource]);

  const durationPreview = useMemo(() => {
    const start = parseTimeValue(startTime);
    const finish = parseTimeValue(finishTime);
    return calculateDrivingDurationMinutes(
      start.hour,
      start.minute,
      finish.hour,
      finish.minute
    );
  }, [startTime, finishTime]);

  const mgrDurationPreview = useMemo(() => {
    const start = parseTimeValue(mgrStartTime);
    const finish = parseTimeValue(mgrFinishTime);
    return calculateDrivingDurationMinutes(
      start.hour,
      start.minute,
      finish.hour,
      finish.minute
    );
  }, [mgrStartTime, mgrFinishTime]);

  const darkInputTotalMinutes = useMemo(
    () => coerceNonNegativeInt(darkDrivingHours) * 60 + Math.min(59, coerceNonNegativeInt(darkDrivingMinutes === '' ? '0' : darkDrivingMinutes)),
    [darkDrivingHours, darkDrivingMinutes]
  );
  const mgrDarkInputTotalMinutes = useMemo(
    () => coerceNonNegativeInt(mgrDarkDrivingHours) * 60 + Math.min(59, coerceNonNegativeInt(mgrDarkDrivingMinutes === '' ? '0' : mgrDarkDrivingMinutes)),
    [mgrDarkDrivingHours, mgrDarkDrivingMinutes]
  );
  const darktimeExceedsDuration = darkInputTotalMinutes > durationPreview;
  const managerDarktimeExceedsDuration = mgrDarkInputTotalMinutes > mgrDurationPreview;

  const clampDarktimeInputToDuration = useCallback(
    (hoursRaw: string, minutesRaw: string, maxMinutes: number) => {
      const max = Math.max(0, coerceNonNegativeInt(maxMinutes));
      const h = Math.max(0, coerceNonNegativeInt(hoursRaw));
      const m = Math.min(59, Math.max(0, coerceNonNegativeInt(minutesRaw)));
      const total = h * 60 + m;
      const capped = Math.min(max, total);
      return {
        hours: String(Math.floor(capped / 60)),
        minutes: String(capped % 60),
      };
    },
    []
  );

  const runningTotalMinutes = useMemo(
    () => records.driving_sessions.reduce((sum, row) => sum + coerceNonNegativeInt(row.duration_minutes), 0),
    [records.driving_sessions]
  );

  const runningDarkTotalMinutes = useMemo(
    () =>
      records.driving_sessions.reduce(
        (sum, row) => sum + coerceNonNegativeInt(row.dark_duration_minutes ?? 0),
        0
      ),
    [records.driving_sessions]
  );

  const managerLatestSessionDateYmd = useMemo(() => {
    const rows = records.manager_driving_sessions;
    if (!rows.length) return null;
    let best = '';
    for (const r of rows) {
      const d = r.date?.trim();
      if (!d) continue;
      if (d > best) best = d;
    }
    return best || null;
  }, [records.manager_driving_sessions]);

  const managerMonthsDaysSinceLast = useMemo(() => {
    if (!managerLatestSessionDateYmd) return null;
    const start = parseCalendarDateYmd(managerLatestSessionDateYmd);
    if (!start) return null;
    const t = new Date();
    const end = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    return monthsAndDaysBetweenCalendarStartEnd(start, end);
  }, [managerLatestSessionDateYmd]);

  const traineeRequiredTotalMinutes = useMemo(() => {
    if (!traineeStandardsMinutes) return 0;
    return Math.max(0, traineeStandardsMinutes.dayMinutes + traineeStandardsMinutes.darkMinutes);
  }, [traineeStandardsMinutes]);

  const managerCompetenceRequiredMinutes = useMemo(() => {
    if (!managerCompetenceStandards) return 0;
    return Math.max(0, managerCompetenceStandards.minimumMinutes);
  }, [managerCompetenceStandards]);

  const managerStandardsWindowStartYmd = useMemo(() => {
    const p = managerCompetenceStandards?.periodMonths;
    if (p == null || p <= 0) return null;
    const t = new Date();
    const start = new Date(t.getFullYear(), t.getMonth() - p, t.getDate());
    return formatCalendarDateYmd(start);
  }, [managerCompetenceStandards]);

  const managerSessionsInStandardsWindow = useMemo(() => {
    const rows = records.manager_driving_sessions;
    const start = managerStandardsWindowStartYmd;
    if (!start) return rows;
    return rows.filter((r) => {
      const d = r.date?.trim();
      if (!d) return false;
      return d >= start;
    });
  }, [records.manager_driving_sessions, managerStandardsWindowStartYmd]);

  const managerProgressRecordedMinutes = useMemo(
    () =>
      managerSessionsInStandardsWindow.reduce((sum, row) => sum + coerceNonNegativeInt(row.duration_minutes), 0),
    [managerSessionsInStandardsWindow]
  );

  const managerProgressDarkRecordedMinutes = useMemo(
    () =>
      managerSessionsInStandardsWindow.reduce(
        (sum, row) => sum + coerceNonNegativeInt(row.dark_duration_minutes ?? 0),
        0
      ),
    [managerSessionsInStandardsWindow]
  );

  const encounterTotalsAcrossSessions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number }>();
    for (const session of records.driving_sessions) {
      for (const enc of session.encounters ?? []) {
        const key = enc.key?.trim() || `lbl:${enc.label}`;
        const prev = map.get(key);
        const c = Math.max(0, coerceNonNegativeInt(enc.count));
        if (prev) prev.count += c;
        else map.set(key, { key, label: enc.label || key, count: c });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [records.driving_sessions]);

  const sortedTraineeReadOnlySessions = useMemo(() => {
    const rank = (s: ExperienceDrivingSession) => {
      const d = (s.date ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      return '0000-00-00';
    };
    return [...records.driving_sessions].sort((a, b) => {
      const da = rank(a);
      const db = rank(b);
      if (db !== da) return db.localeCompare(da);
      return String(b.id ?? '').localeCompare(String(a.id ?? ''));
    });
  }, [records.driving_sessions]);

  const traineeArchiveStats = useMemo(() => {
    const sessions = records.driving_sessions;
    let earliest = '';
    let latest = '';
    const tractionMap = new Map<string, { minutes: number; count: number }>();
    const routeMap = new Map<string, { minutes: number; count: number }>();
    let encounterEvents = 0;
    let withFeedback = 0;
    let withNotes = 0;
    for (const s of sessions) {
      const d = (s.date ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
        if (!earliest || d < earliest) earliest = d;
        if (!latest || d > latest) latest = d;
      }
      const t = (s.traction || "Unknown").trim() || "Unknown";
      const tm = tractionMap.get(t) || { minutes: 0, count: 0 };
      tm.minutes += coerceNonNegativeInt(s.duration_minutes);
      tm.count += 1;
      tractionMap.set(t, tm);
      const route = (s.route || '').trim();
      if (route) {
        const rm = routeMap.get(route) || { minutes: 0, count: 0 };
        rm.minutes += coerceNonNegativeInt(s.duration_minutes);
        rm.count += 1;
        routeMap.set(route, rm);
      }
      for (const enc of s.encounters ?? []) {
        encounterEvents += Math.max(0, coerceNonNegativeInt(enc.count));
      }
      if (s.feedback?.trim()) withFeedback += 1;
      if (s.notes?.trim()) withNotes += 1;
    }
    const byMinutesDesc = (a: { minutes: number; label: string }, b: { minutes: number; label: string }) =>
      b.minutes - a.minutes || a.label.localeCompare(b.label);
    return {
      entryCount: sessions.length,
      earliest,
      latest,
      tractionRows: [...tractionMap.entries()]
        .map(([label, v]) => ({ label, ...v }))
        .sort(byMinutesDesc),
      routeRows: [...routeMap.entries()]
        .map(([label, v]) => ({ label, ...v }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      encounterEvents,
      withFeedback,
      withNotes,
    };
  }, [records.driving_sessions]);

  useEffect(() => {
    if (!showTraineeReadOnlyRecordsModal) setExpandedTraineeArchiveEntryId(null);
  }, [showTraineeReadOnlyRecordsModal]);

  const sortedManagerSessions = useMemo(() => {
    const rank = (s: ExperienceDrivingSession) => {
      const d = (s.date ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      return '0000-00-00';
    };
    return [...records.manager_driving_sessions].sort((a, b) => {
      const da = rank(a);
      const db = rank(b);
      if (db !== da) return db.localeCompare(da);
      return String(b.id ?? '').localeCompare(String(a.id ?? ''));
    });
  }, [records.manager_driving_sessions]);

  const encounterModalShowsRemove = useMemo(() => {
    if (!encounterCountModal) return false;
    if (encounterCountModal.mode === 'predefined') {
      return draftEncounters.some(
        (e) => e.key === encounterCountModal.key && e.label === encounterCountModal.label
      );
    }
    const L = encounterCountModal.label.trim();
    const K = `custom:${L.toLowerCase().replace(/\s+/g, '_')}`;
    return draftEncounters.some((e) => e.key === K && e.label === L);
  }, [encounterCountModal, draftEncounters]);

  useEffect(() => {
    if (!companyCode || !currentUserEmail) {
      setTraineeStandardsMinutes(null);
      setManagerCompetenceStandards(null);
      setTraineeStandardsLoad('idle');
      return;
    }
    setTraineeStandardsLoad('loading');
    const ac = new AbortController();
    fetch(`/api/companies/${encodeURIComponent(companyCode)}/company-standards`, {
      signal: ac.signal,
      headers: { 'X-User-Email': currentUserEmail },
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string };
        if (!res.ok) {
          setTraineeStandardsMinutes(null);
          setManagerCompetenceStandards(null);
          setTraineeStandardsLoad('error');
          return;
        }
        setTraineeStandardsMinutes(parseTraineeDrivingStandardsMinutes(data));
        setManagerCompetenceStandards(parseManagerCompetenceStandardsFromApi(data));
        setTraineeStandardsLoad('loaded');
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setTraineeStandardsMinutes(null);
        setManagerCompetenceStandards(null);
        setTraineeStandardsLoad('error');
      });
    return () => ac.abort();
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    if (!traineeDonutInfoPinned) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (traineeDonutInfoRef.current && !traineeDonutInfoRef.current.contains(e.target as Node)) {
        setTraineeDonutInfoPinned(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [traineeDonutInfoPinned]);

  useEffect(() => {
    if (!managerDonutInfoPinned) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (managerDonutInfoRef.current && !managerDonutInfoRef.current.contains(e.target as Node)) {
        setManagerDonutInfoPinned(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [managerDonutInfoPinned]);

  const saveRecords = useCallback(
    async (next: ExperienceRecordsState) => {
      if (!canEditExperienceEntryForms && !canDeleteExperienceEntries) return false;
      setSaving(true);
      try {
        const payload = JSON.stringify({ ...next, updated_at: new Date().toISOString() });
        const ok = await onSave(payload);
        return ok;
      } finally {
        setSaving(false);
      }
    },
    [canDeleteExperienceEntries, canEditExperienceEntryForms, onSave]
  );

  const persistDrivingSession = async () => {
    if (!canEditExperienceEntryForms) return;
    if (!traction.trim() || !tractionOptions.includes(traction)) {
      alert(translateUi("Select traction from the employee traction list before adding driving time."));
      return;
    }
    if (!drivingRouteFrom.trim() || !drivingRouteTo.trim()) {
      alert(translateUi("Enter both route From and To before adding driving time."));
      return;
    }
    const start = parseTimeValue(startTime);
    const finish = parseTimeValue(finishTime);
    const darkH = coerceNonNegativeInt(darkDrivingHours);
    const darkM = Math.min(59, coerceNonNegativeInt(darkDrivingMinutes));
    const darkTotal = darkH * 60 + darkM;
    if (darkTotal > durationPreview) {
      alert(translateUi("Darktime cannot exceed the calculated driving duration for this entry."));
      return;
    }
    const darkCapped = Math.min(durationPreview, darkTotal);
    const nextSession: ExperienceDrivingSession = {
      id: `exp-drive-${Date.now()}`,
      date: drivingDate || getTodayDateInputValue(),
      traction: traction.trim(),
      route: `${drivingRouteFrom.trim()} - ${drivingRouteTo.trim()}`,
      notes: drivingNotes.trim(),
      feedback: drivingFeedback.trim(),
      start_hour: start.hour,
      start_minute: start.minute,
      finish_hour: finish.hour,
      finish_minute: finish.minute,
      duration_minutes: durationPreview,
      dark_duration_minutes: darkCapped,
      train_source: trainSource,
      train_service_ref: trainServiceRef.trim(),
      encounters: draftEncounters.map((enc) => ({ ...enc, count: Math.max(1, coerceNonNegativeInt(enc.count)) })),
    };
    const next: ExperienceRecordsState = {
      ...records,
      driving_sessions: [nextSession, ...records.driving_sessions],
    };
    const saved = await saveRecords(next);
    if (!saved) return;
    setShowAddDrivingConfirmModal(false);
    setDrivingRouteFrom('');
    setDrivingRouteTo('');
    setDrivingNotes('');
    setDrivingFeedback('');
    setTrainServiceRef('');
    setEncounterCustomLabel('');
    setDraftEncounters([]);
    setEncountersExpanded(false);
    setDrivingHoursExpanded(false);
    setDarkDrivingHours('0');
    setDarkDrivingMinutes('0');
  };

  const addDrivingSession = () => {
    if (!canEditExperienceEntryForms) return;
    if (!traction.trim() || !tractionOptions.includes(traction)) {
      alert(translateUi("Select traction from the employee traction list before adding driving time."));
      return;
    }
    if (!drivingRouteFrom.trim() || !drivingRouteTo.trim()) {
      alert(translateUi("Enter both route From and To before adding driving time."));
      return;
    }
    setShowAddDrivingConfirmModal(true);
  };

  const openPredefinedEncounterModal = (key: string, label: string) => {
    if (!canEditExperienceEntryForms || saving) return;
    const existing = draftEncounters.find((e) => e.key === key);
    setEncounterModalCount(String(Math.max(1, existing?.count ?? 1)));
    setEncounterCountModal({ mode: 'predefined', key, label });
  };

  const openCustomEncounterModal = () => {
    if (!canEditExperienceEntryForms || saving) return;
    const customLabel = encounterCustomLabel.trim();
    if (!customLabel) {
      alert(translateUi("Enter a short description for the custom encounter first."));
      return;
    }
    const slug = customLabel.toLowerCase().replace(/\s+/g, '_');
    const key = `custom:${slug}`;
    const existing = draftEncounters.find((e) => e.key === key && e.label === customLabel);
    setEncounterModalCount(String(Math.max(1, existing?.count ?? 1)));
    setEncounterCountModal({ mode: 'custom', label: customLabel });
  };

  const removeDraftEncounterFromModal = () => {
    if (!canEditExperienceEntryForms) return;
    if (!encounterCountModal) return;
    if (encounterCountModal.mode === 'predefined') {
      const { key, label } = encounterCountModal;
      setDraftEncounters((prev) => prev.filter((row) => !(row.key === key && row.label === label)));
    } else {
      const label = encounterCountModal.label.trim();
      const key = `custom:${label.toLowerCase().replace(/\s+/g, '_')}`;
      setDraftEncounters((prev) => prev.filter((row) => !(row.key === key && row.label === label)));
    }
    setEncounterCountModal(null);
  };

  const applyEncounterCountFromModal = () => {
    if (!canEditExperienceEntryForms) return;
    if (!encounterCountModal) return;
    const count = Math.max(1, coerceNonNegativeInt(encounterModalCount));
    if (encounterCountModal.mode === 'predefined') {
      const { key, label } = encounterCountModal;
      setDraftEncounters((prev) => {
        const idx = prev.findIndex((row) => row.key === key && row.label === label);
        if (idx >= 0) {
          return prev.map((row, i) => (i === idx ? { ...row, count } : row));
        }
        return [...prev, { key, label, count }];
      });
    } else {
      const label = encounterCountModal.label.trim();
      const key = `custom:${label.toLowerCase().replace(/\s+/g, '_')}`;
      setDraftEncounters((prev) => {
        const idx = prev.findIndex((row) => row.key === key && row.label === label);
        if (idx >= 0) {
          return prev.map((row, i) => (i === idx ? { ...row, count } : row));
        }
        return [...prev, { key, label, count }];
      });
      setEncounterCustomLabel('');
    }
    setEncounterCountModal(null);
  };

  const removeDrivingSession = async (id: string) => {
    if (!canDeleteExperienceEntries) return;
    const next: ExperienceRecordsState = {
      ...records,
      driving_sessions: records.driving_sessions.filter((row) => row.id !== id),
    };
    await saveRecords(next);
  };

  const persistManagerDrivingSession = async () => {
    if (!canEditExperienceEntryForms) return;
    if (!mgrTraction.trim() || !tractionOptions.includes(mgrTraction)) {
      alert(translateUi("Select traction from the employee traction list before adding driving time."));
      return;
    }
    if (!mgrDrivingRouteFrom.trim() || !mgrDrivingRouteTo.trim()) {
      alert(translateUi("Enter both route From and To before adding driving time."));
      return;
    }
    const start = parseTimeValue(mgrStartTime);
    const finish = parseTimeValue(mgrFinishTime);
    const darkH = coerceNonNegativeInt(mgrDarkDrivingHours);
    const darkM = Math.min(59, coerceNonNegativeInt(mgrDarkDrivingMinutes));
    const darkTotal = darkH * 60 + darkM;
    if (darkTotal > mgrDurationPreview) {
      alert(translateUi("Darktime cannot exceed the calculated driving duration for this entry."));
      return;
    }
    const darkCapped = Math.min(mgrDurationPreview, darkTotal);
    const nextSession: ExperienceDrivingSession = {
      id: `exp-manager-drive-${Date.now()}`,
      date: mgrDrivingDate || getTodayDateInputValue(),
      traction: mgrTraction.trim(),
      route: `${mgrDrivingRouteFrom.trim()} - ${mgrDrivingRouteTo.trim()}`,
      notes: mgrDrivingNotes.trim(),
      feedback: '',
      start_hour: start.hour,
      start_minute: start.minute,
      finish_hour: finish.hour,
      finish_minute: finish.minute,
      duration_minutes: mgrDurationPreview,
      dark_duration_minutes: darkCapped,
      train_source: mgrTrainSource,
      train_service_ref: mgrTrainServiceRef.trim(),
      encounters: [],
    };
    const next: ExperienceRecordsState = {
      ...records,
      manager_driving_sessions: [nextSession, ...records.manager_driving_sessions],
    };
    const saved = await saveRecords(next);
    if (!saved) return;
    setShowAddManagerDrivingConfirmModal(false);
    setMgrDrivingRouteFrom('');
    setMgrDrivingRouteTo('');
    setMgrDrivingNotes('');
    setMgrTrainServiceRef('');
    setMgrDarkDrivingHours('0');
    setMgrDarkDrivingMinutes('0');
    setManagerHoursExpanded(false);
  };

  const addManagerDrivingSession = () => {
    if (!canEditExperienceEntryForms) return;
    if (!mgrTraction.trim() || !tractionOptions.includes(mgrTraction)) {
      alert(translateUi("Select traction from the employee traction list before adding driving time."));
      return;
    }
    if (!mgrDrivingRouteFrom.trim() || !mgrDrivingRouteTo.trim()) {
      alert(translateUi("Enter both route From and To before adding driving time."));
      return;
    }
    setShowAddManagerDrivingConfirmModal(true);
  };

  const removeManagerDrivingSession = async (id: string) => {
    if (!canDeleteExperienceEntries) return;
    const next: ExperienceRecordsState = {
      ...records,
      manager_driving_sessions: records.manager_driving_sessions.filter((row) => row.id !== id),
    };
    await saveRecords(next);
  };

  const expandDrivingHoursForNewEntry = () => {
    setManagerHoursExpanded(false);
    setDrivingDate(getTodayDateInputValue());
    setStartTime('00:00');
    setFinishTime('00:00');
    setTraction('');
    setDrivingRouteFrom('');
    setDrivingRouteTo('');
    setDrivingNotes('');
    setDrivingFeedback('');
    setTrainSource('manual');
    setTrainServiceRef('');
    setEncounterCustomLabel('');
    setDraftEncounters([]);
    setEncountersExpanded(false);
    setEncounterPickerQuery('');
    setShowAddDrivingConfirmModal(false);
    setEncounterCountModal(null);
    setDarkDrivingHours('0');
    setDarkDrivingMinutes('0');
    setDrivingHoursExpanded(true);
  };

  const handlePrintTraineeHours = useCallback(() => {
    if (!drivingHoursExpanded && !showTraineeReadOnlyRecordsModal) return;
    flushSync(() => {
      setPrintTraineePageOneHtml('ready');
      setPrintTraineePageTwoHtml('ready');
    });
    document.body.classList.add('print-trainee-active');
    window.setTimeout(() => {
      window.print();
    }, 220);
  }, [drivingHoursExpanded, showTraineeReadOnlyRecordsModal]);

  useEffect(() => {
    const onAfterPrint = () => {
      document.body.classList.remove('print-trainee-active');
      setPrintTraineePageOneHtml('');
      setPrintTraineePageTwoHtml('');
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  const expandManagerHoursForNewEntry = () => {
    setDrivingHoursExpanded(false);
    setMgrDrivingDate(getTodayDateInputValue());
    setMgrStartTime('00:00');
    setMgrFinishTime('00:00');
    setMgrTraction('');
    setMgrDrivingRouteFrom('');
    setMgrDrivingRouteTo('');
    setMgrDrivingNotes('');
    setMgrTrainSource('manual');
    setMgrTrainServiceRef('');
    setShowAddManagerDrivingConfirmModal(false);
    setMgrDarkDrivingHours('0');
    setMgrDarkDrivingMinutes('0');
    setManagerHoursExpanded(true);
  };

  const printShowCombined = traineeStandardsLoad === 'loaded' && traineeRequiredTotalMinutes > 0;
  const printDarkMinutesStandard = traineeStandardsMinutes?.darkMinutes ?? 0;
  const printShowDarkDonut = printShowCombined && printDarkMinutesStandard > 0;
  const printShowDarkCallout = printShowCombined && !printShowDarkDonut && runningDarkTotalMinutes > 0;
  const printHasEncounters = encounterTotalsAcrossSessions.length > 0;

  return (
    <div className="space-y-4 sm:space-y-5">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body.print-trainee-active > * { display: none !important; }
          body.print-trainee-active > .trainee-print-root-portal { display: block !important; }
          body.print-trainee-active .trainee-print-root-portal {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            color: #111827 !important;
            background: #fff !important;
            font-size: 12px !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-page {
            margin: 0 !important;
            padding: 0 !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-page--entries {
            page-break-before: always !important;
            break-before: page !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-stack {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-stack > .trainee-print-pane {
            width: 100% !important;
          }
          body.print-trainee-active .trainee-print-root-portal .print-title {
            margin: 0 0 10px !important;
            font-size: 20px !important;
            font-weight: 700 !important;
          }
          body.print-trainee-active .trainee-print-root-portal .trainee-print-pane {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            box-shadow: none !important;
            margin-top: 0 !important;
          }
          body.print-trainee-active .trainee-print-root-portal .trainee-print-pane-entries {
            break-inside: auto !important;
            page-break-inside: auto !important;
          }
          body.print-trainee-active .trainee-print-root-portal .trainee-print-pane-entries > * {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          body.print-trainee-active .no-print { display: none !important; }
        }
      `}</style>
      {(printTraineePageOneHtml || printTraineePageTwoHtml) && typeof document !== 'undefined'
        ? createPortal(
            <div className="trainee-print-root-portal">
              <section className="print-page print-page--summary">
                <h1 className="print-title">Trainee Driver Hours</h1>
                <div
                  className={cn(
                    "rounded-2xl border p-4 sm:p-5 shadow-sm",
                    isDarkMode
                      ? "border-indigo-500/35 bg-indigo-500/10 ring-1 ring-indigo-500/20"
                      : "border-indigo-200 bg-indigo-50/70 ring-1 ring-indigo-100"
                  )}
                >
                  <div className="mb-3">
                    <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-200" : "text-indigo-700")}>
                      Overall Progress Across All Experiences
                    </p>
                  </div>
                  {printShowCombined ? (
                    <div
                      className={cn(
                        'flex min-w-0 flex-col overflow-hidden rounded-2xl border text-left shadow-sm lg:flex-row',
                        isDarkMode ? 'border-slate-600 bg-slate-900/80 divide-slate-600/90' : 'border-slate-200 bg-white divide-slate-200',
                        (printShowDarkDonut || printShowDarkCallout) && 'divide-y lg:divide-y-0 lg:divide-x'
                      )}
                    >
                      <div className="flex min-w-0 flex-1 basis-0 flex-col items-center p-4 sm:p-5">
                        <div className="relative mx-auto w-full max-w-[16.5rem]">
                          <p className={cn('mb-3 text-center text-[11px] font-semibold uppercase tracking-wide', isDarkMode ? 'text-indigo-200/90' : 'text-indigo-800')}>
                            {translateUi("Combined (daylight + darktime)")}
                          </p>
                          <div className="flex justify-center">
                            <ExperienceTraineeHoursDonut
                              recordedMinutes={runningTotalMinutes}
                              requiredMinutes={traineeRequiredTotalMinutes}
                              isDarkMode={!!isDarkMode}
                            />
                          </div>
                        </div>
                      </div>
                      {(printShowDarkDonut || printShowDarkCallout) ? (
                        <div
                          className={cn(
                            'flex min-w-0 flex-1 basis-0 flex-col items-center p-4 sm:p-5',
                            printShowDarkDonut && (isDarkMode ? 'bg-violet-950/15 lg:bg-transparent' : 'bg-violet-50/40 lg:bg-transparent')
                          )}
                        >
                          {printShowDarkDonut ? (
                            <div className="mx-auto w-full max-w-[16.5rem]">
                              <p className={cn('mb-3 text-center text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-violet-200/95' : 'text-violet-900')}>
                                Darktime vs minimum
                              </p>
                              <div className="flex justify-center">
                                <ExperienceTraineeHoursDonut
                                  recordedMinutes={runningDarkTotalMinutes}
                                  requiredMinutes={printDarkMinutesStandard}
                                  isDarkMode={!!isDarkMode}
                                  centerLabel={translateUi("Darktime")}
                                  variant="violet"
                                />
                              </div>
                            </div>
                          ) : (
                            <div
                              className={cn(
                                'flex min-h-[8rem] w-full max-w-lg items-center justify-center rounded-xl border px-4 py-4 text-center text-sm leading-relaxed sm:px-5',
                                isDarkMode ? 'border-violet-500/30 bg-violet-950/20 text-slate-300' : 'border-violet-200 bg-violet-50/80 text-slate-700'
                              )}
                            >
                              <p>
                                Darktime logged across entries:{' '}
                                <span className={cn('font-semibold tabular-nums', isDarkMode ? "text-violet-300" : "text-violet-600")}>
                                  {formatHoursMinutes(runningDarkTotalMinutes)}
                                </span>
                                .
                              </p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      Company standards are required to print progress charts.
                    </p>
                  )}
                </div>
              </section>
              <section className="print-page print-page--entries">
                <h1 className="print-title">Encounters and Recorded Entries</h1>
                {printHasEncounters ? (
                  <div
                    className={cn(
                      "mb-4 rounded-2xl border p-4 sm:p-5 shadow-sm",
                      isDarkMode
                        ? "border-slate-700 bg-slate-900/35"
                        : "border-slate-200 bg-white/90"
                    )}
                  >
                    <p className={cn("mb-3 text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                      Encounters Achieved
                    </p>
                    <ExperienceEncountersDistributionBar rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
                    <div className="mt-3">
                      <ExperienceEncountersPieLegend rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
                    </div>
                  </div>
                ) : null}
                <p className={cn("mb-3 text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-sky-200" : "text-sky-700")}>
                  {translateUi("Recorded Entries")}
                </p>
                {records.driving_sessions.length === 0 ? (
                  <p>No recorded entries found.</p>
                ) : (
                  <div className="space-y-2">
                    {sortedTraineeReadOnlySessions.map((row) => (
                      <div
                        key={`print-${row.id}`}
                        className={cn(
                          "rounded-xl border px-3 py-2",
                          isDarkMode ? "border-slate-700 bg-slate-950/40" : "border-slate-200 bg-slate-50"
                        )}
                      >
                        <p className={cn("text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.traction}</p>
                        <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                          {String(row.start_hour).padStart(2, '0')}:{String(row.start_minute).padStart(2, '0')} - {String(row.finish_hour).padStart(2, '0')}:{String(row.finish_minute).padStart(2, '0')} ({formatHoursMinutes(row.duration_minutes)})
                          {coerceNonNegativeInt(row.dark_duration_minutes) > 0 ? ` · Darktime ${formatHoursMinutes(row.dark_duration_minutes ?? 0)}` : ''}
                        </p>
                        {(row.route || row.train_service_ref) ? (
                          <p className={cn("text-[11px]", isDarkMode ? "text-slate-500" : "text-slate-600")}>
                            {row.route ? `Route: ${row.route}` : ''}{row.route && row.train_service_ref ? ' · ' : ''}{row.train_service_ref ? `Train ref: ${row.train_service_ref}` : ''}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>,
            document.body
          )
        : null}
      <div className="relative trainee-print-section">
        <div
          className={cn(
            "rounded-xl sm:rounded-2xl border p-3 sm:p-5",
            isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white",
            traineeHoursNotApplicable && "pointer-events-none select-none opacity-[0.45]"
          )}
          aria-hidden={traineeHoursNotApplicable}
        >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3 sm:mb-4">
          <div className="min-w-0 flex-1">
            <h3 className={cn("text-base sm:text-lg font-bold flex items-center gap-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>
              <Clock className="w-5 h-5 text-indigo-500" />
              Trainee Driver Hours
            </h3>
            <p className={cn("text-xs sm:text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>Create a driving entry and include any encounters that happened during that specific run.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className={cn("rounded-xl border px-3 py-2 text-sm font-semibold", isDarkMode ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-200" : "border-indigo-200 bg-indigo-50 text-indigo-700")}>
              <span className="block">Running total: {formatHoursMinutes(runningTotalMinutes)}</span>
              {runningDarkTotalMinutes > 0 ? (
                <span className={cn('block text-xs font-medium mt-0.5', isDarkMode ? 'text-violet-200/90' : 'text-violet-800')}>
                  Darktime logged: {formatHoursMinutes(runningDarkTotalMinutes)}
                </span>
              ) : null}
            </div>
            {drivingHoursExpanded ? (
              <button
                type="button"
                className={cn(
                  "no-print rounded-lg p-0.5 -m-0.5 border-0 bg-transparent",
                  isDarkMode ? "text-slate-200 hover:text-white" : "text-slate-600 hover:text-slate-900"
                )}
                onClick={handlePrintTraineeHours}
                aria-label={translateUi("Print trainee driver hours")}
                title={translateUi("Print trainee driver hours")}
              >
                <Printer className="h-7 w-7 cursor-pointer" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <div
            className={cn(
              'rounded-xl border overflow-hidden',
              isDarkMode ? "border-slate-700" : "border-slate-200"
            )}
          >
            <button
              type="button"
              onClick={() => {
                if (drivingHoursExpanded) {
                  setDrivingHoursExpanded(false);
                  setEncountersExpanded(false);
                } else {
                  setManagerHoursExpanded(false);
                  expandDrivingHoursForNewEntry();
                }
              }}
              aria-expanded={drivingHoursExpanded}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors sm:px-5 sm:py-3.5',
                isDarkMode
                  ? 'bg-slate-900/90 hover:bg-slate-800/90 border-b border-slate-700'
                  : 'bg-slate-100/90 hover:bg-slate-100 border-b border-slate-200'
              )}
            >
              <div className="min-w-0">
                <p className={cn('text-xs sm:text-sm font-bold', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                  Trainee driving details
                </p>
                <p className={cn('mt-0.5 text-[11px] leading-snug', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  {drivingHoursExpanded
                    ? translateUi("Tap to hide progress charts, new entry form, and recent entries.")
                    : translateUi("Tap to show progress charts, add a driving entry, and recent entries.")}
                </p>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 transition-transform',
                  isDarkMode ? "text-slate-400" : "text-slate-500",
                  drivingHoursExpanded && 'rotate-180'
                )}
                aria-hidden
              />
            </button>
            {drivingHoursExpanded ? (
              <>
              <div
                ref={traineeExpandedContentRef}
                className={cn(
                  'p-4 sm:p-6',
                  isDarkMode ? 'bg-slate-950/40' : 'bg-slate-50/90'
                )}
              >
            <div className="space-y-5">
              <div
                ref={traineeStatsPaneRef}
                className={cn(
                  "rounded-2xl border p-4 sm:p-5 shadow-sm trainee-print-pane",
                  isDarkMode
                    ? "border-indigo-500/35 bg-indigo-500/10 ring-1 ring-indigo-500/20"
                    : "border-indigo-200 bg-indigo-50/70 ring-1 ring-indigo-100"
                )}
              >
              <div className="mb-3">
                <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-200" : "text-indigo-700")}>
                  Overall Progress Across All Experiences
                </p>
                <p className={cn("mt-1 text-xs leading-relaxed", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  These totals and charts include all recorded trainee experience entries and hours, not just this new form entry.
                </p>
              </div>
              <div className="min-w-0 space-y-4 text-left sm:space-y-5">
                <div>
                  <p className={cn('text-xs leading-relaxed', isDarkMode ? "text-slate-500" : "text-slate-600")}>
                    Targets come from <span className="font-semibold">Company standards</span> (daylight + darktime minimums).
                  </p>
                </div>
                {traineeStandardsLoad === 'loading' && (
                  <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>Loading company standards…</p>
                )}
                {traineeStandardsLoad === 'error' && (
                  <p className={cn('text-sm', isDarkMode ? 'text-amber-200/90' : 'text-amber-800')}>
                    Could not load company standards. Progress chart is unavailable.
                  </p>
                )}
                {traineeStandardsLoad === 'loaded' && traineeRequiredTotalMinutes <= 0 && (
                  <p className={cn('text-sm leading-relaxed', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    Trainee daylight and darktime minimums are not set (or are zero). Configure them under{' '}
                    <span className="font-semibold">Company standards</span> to see completed vs remaining hours here.
                  </p>
                )}
                {!companyCode && traineeStandardsLoad === 'idle' && (
                  <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    Company context is required to load standards.
                  </p>
                )}
              </div>

            {(() => {
              const showCombined = traineeStandardsLoad === 'loaded' && traineeRequiredTotalMinutes > 0;
              const darkMinutesStandard = traineeStandardsMinutes?.darkMinutes ?? 0;
              const showDarkDonut = showCombined && darkMinutesStandard > 0;
              const showDarkCallout = showCombined && !showDarkDonut && runningDarkTotalMinutes > 0;
              const showEncounters = encounterTotalsAcrossSessions.length > 0;

              if (!showCombined && !showEncounters) return null;

              const paneCount =
                (showCombined ? 1 : 0) + (showCombined && (showDarkDonut || showDarkCallout) ? 1 : 0) + (showEncounters ? 1 : 0);

              return (
                <div className="min-w-0 space-y-2">
                  <div
                    className={cn(
                      'flex min-w-0 flex-col overflow-hidden rounded-2xl border text-left shadow-sm lg:flex-row',
                      isDarkMode
                        ? 'border-slate-600 bg-slate-900/80 divide-slate-600/90'
                        : 'border-slate-200 bg-white divide-slate-200',
                      paneCount > 1 && 'divide-y lg:divide-y-0 lg:divide-x'
                    )}
                  >
                    {showCombined && (
                      <div
                        ref={traineeDonutInfoRef}
                        className="flex min-w-0 flex-1 basis-0 flex-col items-center p-4 sm:p-5"
                      >
                        <div
                          className="relative mx-auto w-full max-w-[16.5rem]"
                          role="img"
                          aria-label={translateUi("Driving hours logged {{logged}} of combined trainee minimum {{minimum}}", { logged: formatHoursMinutes(runningTotalMinutes), minimum: formatHoursMinutes(traineeRequiredTotalMinutes) })}
                        >
                          <div
                            className="relative z-20 mb-3"
                            onMouseEnter={() => setTraineeDonutInfoHover(true)}
                            onMouseLeave={() => setTraineeDonutInfoHover(false)}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <p
                                className={cn(
                                  'whitespace-nowrap text-center text-[11px] font-semibold uppercase tracking-wide',
                                  isDarkMode ? 'text-indigo-200/90' : 'text-indigo-800'
                                )}
                              >
                                {translateUi("Combined (daylight + darktime)")}
                              </p>
                              <button
                                type="button"
                                className={cn(
                                  'shrink-0 rounded-full p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                                  isDarkMode
                                    ? "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                )}
                                aria-expanded={showTraineeDonutInfo}
                                aria-label={translateUi("How this progress chart works. Hover for a summary, or click to keep the panel open.")}
                                onClick={() => setTraineeDonutInfoPinned((p) => !p)}
                              >
                                <Info className="h-4 w-4" aria-hidden />
                              </button>
                            </div>
                            {showTraineeDonutInfo ? (
                              <div
                                className={cn(
                                  'absolute left-0 right-0 top-full z-30 mt-2 w-full rounded-xl border p-3 text-left text-xs leading-relaxed shadow-xl',
                                  isDarkMode ? "border-slate-600 bg-slate-900 text-slate-200" : "border-slate-200 bg-white text-slate-700"
                                )}
                                role="tooltip"
                              >
                                <p>
                                  Target is the sum of <span className="font-semibold">minimum daylight</span> and{' '}
                                  <span className="font-semibold">darktime</span> driving from company standards
                                  {traineeStandardsMinutes ? (
                                    <>
                                      {' '}
                                      ({formatHoursMinutes(traineeStandardsMinutes.dayMinutes)} daylight +{' '}
                                      {formatHoursMinutes(traineeStandardsMinutes.darkMinutes)} darktime).
                                    </>
                                  ) : null}
                                </p>
                                <p className={cn('mt-2', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                  Logged hours from entries below are total time and are not split into daylight vs darktime. Use{' '}
                                  <span className="font-semibold">{translateUi("Darktime driving (this entry)")}</span> when recording hours to count toward the darktime chart. Company figures are{' '}
                                  <span className="font-semibold">minimums</span>; logged darktime may exceed the darktime minimum.
                                </p>
                              </div>
                            ) : null}
                          </div>
                          <div className="flex justify-center">
                            <ExperienceTraineeHoursDonut
                              recordedMinutes={runningTotalMinutes}
                              requiredMinutes={traineeRequiredTotalMinutes}
                              isDarkMode={!!isDarkMode}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {showCombined && (showDarkDonut || showDarkCallout) && (
                      <div
                        className={cn(
                          'flex min-w-0 flex-1 basis-0 flex-col items-center p-4 sm:p-5',
                          showDarkDonut &&
                            (isDarkMode ? 'bg-violet-950/15 lg:bg-transparent' : 'bg-violet-50/40 lg:bg-transparent')
                        )}
                      >
                        {showDarkDonut ? (
                          <div
                            className="mx-auto w-full max-w-[16.5rem]"
                            role="img"
                            aria-label={translateUi("Darktime driving logged {{logged}}; company darktime minimum {{minimum}}", { logged: formatHoursMinutes(runningDarkTotalMinutes), minimum: formatHoursMinutes(darkMinutesStandard) })}
                          >
                            <p
                              className={cn(
                                'mb-3 text-center text-[11px] font-semibold uppercase tracking-wider',
                                isDarkMode ? 'text-violet-200/95' : 'text-violet-900'
                              )}
                            >
                              Darktime vs minimum
                            </p>
                            <div className="flex justify-center">
                              <ExperienceTraineeHoursDonut
                                recordedMinutes={runningDarkTotalMinutes}
                                requiredMinutes={darkMinutesStandard}
                                isDarkMode={!!isDarkMode}
                                centerLabel={translateUi("Darktime")}
                                variant="violet"
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'flex min-h-[8rem] w-full max-w-lg items-center justify-center rounded-xl border px-4 py-4 text-center text-sm leading-relaxed sm:px-5',
                              isDarkMode ? 'border-violet-500/30 bg-violet-950/20 text-slate-300' : 'border-violet-200 bg-violet-50/80 text-slate-700'
                            )}
                          >
                            <p>
                              Darktime logged across entries:{' '}
                              <span
                                className={cn(
                                  'font-semibold tabular-nums',
                                  isDarkMode ? "text-violet-300" : "text-violet-600"
                                )}
                              >
                                {formatHoursMinutes(runningDarkTotalMinutes)}
                              </span>
                              . Set a <span className="font-semibold">darktime</span> minimum under Company standards to see a progress ring for darktime.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {showEncounters && (
                      <div className="min-w-0 flex-1 basis-0 p-4 sm:p-5">
                        <ExperienceEncountersDistributionBar rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
                      </div>
                    )}
                  </div>
                  {showCombined && !showEncounters ? (
                    <p className={cn('text-sm italic', isDarkMode ? "text-slate-500" : "text-slate-500")}>
                      No encounters recorded on driving entries yet.
                    </p>
                  ) : null}
                </div>
              );
            })()}

            {encounterTotalsAcrossSessions.length > 0 ? (
              <div
                className={cn(
                  'rounded-xl border p-4 sm:p-5',
                  isDarkMode ? 'border-slate-700 bg-slate-900/35' : 'border-slate-200 bg-white/90'
                )}
              >
                <p
                  className={cn(
                    'mb-3 text-left text-[11px] font-semibold uppercase tracking-wider',
                    isDarkMode ? "text-slate-400" : "text-slate-500"
                  )}
                >
                  {translateUi("Encounter legend")}
                </p>
                <ExperienceEncountersPieLegend rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
              </div>
            ) : null}
            </div>
              </div>
              </div>

        <div
          ref={traineeFormPaneRef}
          className={cn(
            "rounded-2xl border p-4 sm:p-5 shadow-sm trainee-print-pane",
            isDarkMode
              ? "border-emerald-500/35 bg-emerald-500/10 ring-1 ring-emerald-500/20"
              : "border-emerald-200 bg-emerald-50/70 ring-1 ring-emerald-100"
          )}
        >
        <div className="mb-3">
          <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-emerald-200" : "text-emerald-700")}>
            Add New Experience Entry
          </p>
          <p className={cn("mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
            Complete the form below to add a new trainee driving experience record.
          </p>
        </div>
        <div
          ref={drivingHoursEntryFormRef}
          className="scroll-mt-20 outline-none"
          tabIndex={-1}
          aria-label={translateUi("Driving entry form")}
        >
        {tractionOptions.length === 0 && (
          <div className={cn("mb-3 rounded-xl border px-3 py-2 text-xs sm:text-sm", isDarkMode ? "border-amber-400/30 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800")}>
            No traction records found for this employee yet. Add traction in `Trains & Routes` first, then return here.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 sm:gap-3">
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Date</label>
            <input
              type="date"
              disabled={!canEditExperienceEntryForms || saving}
              value={drivingDate}
              onChange={(e) => setDrivingDate(e.target.value)}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Driving start time (HH:MM)</label>
            <input
              type="time"
              step={60}
              disabled={!canEditExperienceEntryForms || saving}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value || '00:00')}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Driving finish time (HH:MM)</label>
            <input
              type="time"
              step={60}
              disabled={!canEditExperienceEntryForms || saving}
              value={finishTime}
              onChange={(e) => setFinishTime(e.target.value || '00:00')}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}> Traction </label>
            <select
              disabled={!canEditExperienceEntryForms || saving || tractionOptions.length === 0}
              value={traction}
              onChange={(e) => setTraction(e.target.value)}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            >
              <option value="">{tractionOptions.length === 0 ? translateUi("No traction available") : translateUi("Please select traction type")}</option>
              {tractionOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Route from</label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={drivingRouteFrom}
              onChange={(e) => setDrivingRouteFrom(e.target.value)}
              placeholder="e.g. Bicester South Jct"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Route to</label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={drivingRouteTo}
              onChange={(e) => setDrivingRouteTo(e.target.value)}
              placeholder="e.g. Oxford"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Train source</label>
            <select
              disabled={!canEditExperienceEntryForms || saving}
              value={trainSource}
              onChange={(e) => setTrainSource(e.target.value === 'feed' ? 'feed' : 'manual')}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            >
              <option value="manual">Manual entry</option>
              {showFeedLookupOption && <option value="feed">Feed lookup</option>}
            </select>
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              {trainSource === 'feed' ? translateUi("Train service reference") : translateUi("Train reference (optional)")}
            </label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={trainServiceRef}
              onChange={(e) => setTrainServiceRef(e.target.value)}
              placeholder={trainSource === 'feed' ? translateUi("e.g. 1A23@KGX or KGX") : translateUi("e.g. 1A23")}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Calculated</label>
            <div className={cn("h-[38px] rounded-xl border px-2.5 py-2 text-sm font-semibold flex items-center justify-center sm:justify-start", isDarkMode ? "border-slate-600 bg-slate-950 text-slate-200" : "border-slate-300 bg-slate-50 text-slate-700")}>
              {formatHoursMinutes(durationPreview)}
            </div>
          </div>
        </div>
        <div
          className={cn(
            'mt-2 rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 space-y-2',
            isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50/90'
          )}
        >
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              {translateUi("Darktime driving (this entry)")}
            </label>
            <p className={cn('text-[10px] leading-snug mb-2', isDarkMode ? "text-slate-500" : "text-slate-600")}>
              Of the calculated duration above, how much was darktime driving.
            </p>
            <div className="flex flex-wrap items-end gap-2 sm:gap-3">
              <div className="min-w-[5rem]">
                <label className={cn('mb-0.5 block text-[10px] font-medium', isDarkMode ? "text-slate-400" : "text-slate-500")}> Hours </label>
                <input
                  type="number"
                  min={0}
                  disabled={!canEditExperienceEntryForms || saving}
                  value={darkDrivingHours}
                  onChange={(e) => {
                    setDarkDrivingHours(e.target.value);
                  }}
                  className={cn(
                    'w-full min-w-0 rounded-xl border px-2.5 py-2 text-sm tabular-nums',
                    isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                  )}
                />
              </div>
              <div className="min-w-[5rem]">
                <label className={cn('mb-0.5 block text-[10px] font-medium', isDarkMode ? "text-slate-400" : "text-slate-500")}> Minutes </label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  disabled={!canEditExperienceEntryForms || saving}
                  value={darkDrivingMinutes}
                  onChange={(e) => {
                    setDarkDrivingMinutes(e.target.value);
                  }}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v === '') return;
                    const m = Math.max(0, Math.min(59, coerceNonNegativeInt(v)));
                    setDarkDrivingMinutes(String(m));
                  }}
                  className={cn(
                    'w-full min-w-0 rounded-xl border px-2.5 py-2 text-sm tabular-nums',
                    isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                  )}
                />
              </div>
              <div className="pb-2 text-xs">
                <span className={cn('tabular-nums font-semibold', isDarkMode ? "text-violet-300" : "text-violet-700")}>
                  = {formatHoursMinutes(darkInputTotalMinutes)}
                </span>
              </div>
            </div>
            <p className={cn('mt-2 text-[11px]', darktimeExceedsDuration ? (isDarkMode ? "text-rose-300" : "text-rose-700") : (isDarkMode ? "text-emerald-300" : "text-emerald-700"))}>
              {darktimeExceedsDuration
                ? `Darktime cannot exceed calculated duration (${formatHoursMinutes(durationPreview)}).`
                : `Darktime is within calculated duration (${formatHoursMinutes(durationPreview)}).`}
            </p>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}> Notes </label>
            <textarea
              disabled={!canEditExperienceEntryForms || saving}
              value={drivingNotes}
              onChange={(e) => setDrivingNotes(e.target.value)}
              rows={3}
              placeholder="Notes for this driving entry"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0 resize-y", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}> Feedback </label>
            <textarea
              disabled={!canEditExperienceEntryForms || saving}
              value={drivingFeedback}
              onChange={(e) => setDrivingFeedback(e.target.value)}
              rows={3}
              placeholder="Feedback for this driving entry"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0 resize-y", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
        </div>
        <div className={cn("mt-3 rounded-xl border overflow-hidden", isDarkMode ? "border-slate-700 bg-slate-950/40" : "border-slate-200 bg-slate-50")}>
          <div className={cn("flex flex-wrap items-center justify-between gap-2 px-3 py-2.5", isDarkMode ? "bg-slate-900/80 border-b border-slate-700" : "bg-slate-100 border-b border-slate-200")}>
            <div className="min-w-0 flex-1">
              <p className={cn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Encounters within this driving entry</p>
              {!encountersExpanded && (
                <p className={cn("text-xs mt-0.5", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  {draftEncounters.length > 0
                    ? `${draftEncounters.length} encounter type${draftEncounters.length === 1 ? '' : 's'} on this entry — expand to change counts`
                    : 'Expand and tap an encounter to set how many occurred'}
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={!canEditExperienceEntryForms || saving}
              onClick={() => setEncountersExpanded((v) => !v)}
              className={cn(
                "rounded-lg border px-2.5 py-1.5 text-xs font-semibold shrink-0",
                isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
                (!canEditExperienceEntryForms || saving) && "opacity-50 cursor-not-allowed"
              )}
            >
              {encountersExpanded ? translateUi("Collapse") : 'Select Encounters'}
            </button>
          </div>
          {encountersExpanded && (
            <div className="p-3 sm:p-4 space-y-4">
              <div className="relative">
                <Search
                  className={cn(
                    'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
                    isDarkMode ? "text-slate-500" : "text-slate-400"
                  )}
                  aria-hidden
                />
                <input
                  type="search"
                  disabled={!canEditExperienceEntryForms || saving}
                  value={encounterPickerQuery}
                  onChange={(e) => setEncounterPickerQuery(e.target.value)}
                  placeholder="Search encounter types…"
                  autoComplete="off"
                  className={cn(
                    'w-full rounded-xl border py-2.5 pl-10 pr-3 text-sm min-w-0 outline-none focus:ring-2 focus:ring-indigo-500/30',
                    isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400"
                  )}
                />
              </div>
              <p className={cn('text-xs', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                Tap a card to enter or change how many occurred. The count appears on the card. Tap again to update.
              </p>
              {filteredExperienceCounters.length === 0 && filteredEmergencyCounters.length === 0 ? (
                <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  No encounter types match &ldquo;{encounterPickerQuery.trim()}&rdquo;. Try another search or clear the filter.
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredExperienceCounters.length > 0 && (
                    <div>
                      <p
                        className={cn(
                          'mb-2 text-[11px] font-semibold uppercase tracking-wider',
                          isDarkMode ? "text-slate-400" : "text-slate-500"
                        )}
                      >
                        Driving experience
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                        {filteredExperienceCounters.map((opt) => {
                          const onEntry = draftEncounters.find((e) => e.key === opt.key);
                          const countOnCard = onEntry?.count ?? 0;
                          const short =
                            EXPERIENCE_COUNTER_MOBILE_LABELS[opt.key as ExperienceCountKey] ?? opt.label;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              disabled={!canEditExperienceEntryForms || saving}
                              aria-pressed={countOnCard > 0}
                              title={translateUi(opt.label)}
                              onClick={() => openPredefinedEncounterModal(opt.key, opt.label)}
                              className={cn(
                                'relative rounded-2xl border px-3 py-3 pr-11 text-left text-sm transition-all min-h-[3.25rem] flex items-start gap-3 w-full shadow-sm',
                                countOnCard > 0
                                  ? isDarkMode
                                    ? 'border-indigo-400/70 bg-indigo-500/15 text-slate-100 ring-2 ring-indigo-400/35 shadow-indigo-950/20'
                                    : 'border-indigo-400 bg-indigo-50/90 text-indigo-950 ring-2 ring-indigo-200 shadow-indigo-100/80'
                                  : isDarkMode
                                    ? 'border-slate-600/80 bg-slate-900 text-slate-200 hover:border-slate-500 hover:bg-slate-800/60'
                                    : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50',
                                (!canEditExperienceEntryForms || saving) && 'opacity-50 cursor-not-allowed'
                              )}
                            >
                              {countOnCard > 0 ? (
                                <CheckCircle2
                                  className={cn(
                                    'w-5 h-5 shrink-0 mt-0.5',
                                    isDarkMode ? "text-indigo-300" : "text-indigo-600"
                                  )}
                                  aria-hidden
                                />
                              ) : (
                                <span
                                  className={cn(
                                    'w-5 h-5 shrink-0 mt-0.5 rounded-lg border-2 border-dashed opacity-50',
                                    isDarkMode ? "border-slate-500" : "border-slate-300"
                                  )}
                                  aria-hidden
                                />
                              )}
                              <span className="min-w-0 leading-snug">
                                <span className="sm:hidden">{translateUi(short)}</span>
                                <span className="hidden sm:inline">{translateUi(opt.label)}</span>
                              </span>
                              {countOnCard > 0 ? (
                                <span
                                  className={cn(
                                    'absolute top-2 right-2 flex h-7 min-w-[1.75rem] items-center justify-center rounded-lg px-1.5 text-xs font-bold tabular-nums',
                                    isDarkMode
                                      ? 'bg-indigo-500/40 text-indigo-50 ring-1 ring-indigo-400/40'
                                      : 'bg-indigo-600 text-white shadow-sm'
                                  )}
                                  aria-label={translateUi("{{count}} recorded", { count: countOnCard })}
                                >
                                  {countOnCard}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {filteredEmergencyCounters.length > 0 && (
                    <div
                      className={cn(
                        'rounded-2xl border p-3 sm:p-4',
                        isDarkMode ? 'border-amber-500/30 bg-amber-500/[0.07]' : 'border-amber-200/90 bg-amber-50/50'
                      )}
                    >
                      <p
                        className={cn(
                          'mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider',
                          isDarkMode ? 'text-amber-200/95' : 'text-amber-900'
                        )}
                      >
                        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                        Emergency
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                        {filteredEmergencyCounters.map((opt) => {
                          const onEntry = draftEncounters.find((e) => e.key === opt.key);
                          const countOnCard = onEntry?.count ?? 0;
                          const short =
                            EXPERIENCE_COUNTER_MOBILE_LABELS[opt.key as ExperienceCountKey] ?? opt.label;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              disabled={!canEditExperienceEntryForms || saving}
                              aria-pressed={countOnCard > 0}
                              title={translateUi(opt.label)}
                              onClick={() => openPredefinedEncounterModal(opt.key, opt.label)}
                              className={cn(
                                'relative rounded-2xl border px-3 py-3 pr-11 text-left text-sm transition-all min-h-[3.25rem] flex items-start gap-3 w-full shadow-sm',
                                countOnCard > 0
                                  ? isDarkMode
                                    ? 'border-amber-400/80 bg-amber-500/20 text-amber-50 ring-2 ring-amber-400/30'
                                    : 'border-amber-500 bg-amber-100/95 text-amber-950 ring-2 ring-amber-300/80'
                                  : isDarkMode
                                    ? 'border-slate-600/80 bg-slate-900/90 text-slate-200 hover:border-slate-500'
                                    : 'border-slate-200 bg-white text-slate-800 hover:border-amber-200',
                                (!canEditExperienceEntryForms || saving) && 'opacity-50 cursor-not-allowed'
                              )}
                            >
                              {countOnCard > 0 ? (
                                <CheckCircle2
                                  className={cn(
                                    'w-5 h-5 shrink-0 mt-0.5',
                                    isDarkMode ? "text-amber-300" : "text-amber-700"
                                  )}
                                  aria-hidden
                                />
                              ) : (
                                <span
                                  className={cn(
                                    'w-5 h-5 shrink-0 mt-0.5 rounded-lg border-2 border-dashed opacity-50',
                                    isDarkMode ? "border-slate-500" : "border-slate-300"
                                  )}
                                  aria-hidden
                                />
                              )}
                              <span className="min-w-0 leading-snug">
                                <span className="sm:hidden">{translateUi(short)}</span>
                                <span className="hidden sm:inline">{translateUi(opt.label)}</span>
                              </span>
                              {countOnCard > 0 ? (
                                <span
                                  className={cn(
                                    'absolute top-2 right-2 flex h-7 min-w-[1.75rem] items-center justify-center rounded-lg px-1.5 text-xs font-bold tabular-nums',
                                    isDarkMode
                                      ? 'bg-amber-500/45 text-amber-50 ring-1 ring-amber-400/45'
                                      : 'bg-amber-700 text-white shadow-sm'
                                  )}
                                  aria-label={translateUi("{{count}} recorded", { count: countOnCard })}
                                >
                                  {countOnCard}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div
                className={cn(
                  'flex flex-col gap-3 pt-2 border-t sm:flex-row sm:items-end',
                  isDarkMode ? "border-slate-700" : "border-slate-200"
                )}
              >
                <div className="min-w-0 flex-1">
                  <label
                    className={cn(
                      'mb-1 block text-[11px] font-semibold uppercase tracking-wider',
                      isDarkMode ? "text-slate-400" : "text-slate-500"
                    )}
                  >
                    Custom (optional)
                  </label>
                  <input
                    type="text"
                    disabled={!canEditExperienceEntryForms || saving}
                    value={encounterCustomLabel}
                    onChange={(e) => setEncounterCustomLabel(e.target.value)}
                    placeholder="Not listed above? Describe it here"
                    className={cn(
                      'w-full rounded-xl border px-3 py-2.5 text-sm min-w-0',
                      isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                    )}
                  />
                </div>
                <button
                  type="button"
                  disabled={!canEditExperienceEntryForms || saving || !encounterCustomLabel.trim()}
                  onClick={openCustomEncounterModal}
                  className={cn(
                    'shrink-0 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors',
                    isDarkMode
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
                    (!canEditExperienceEntryForms || saving || !encounterCustomLabel.trim()) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  Set count…
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={!canEditExperienceEntryForms || saving || tractionOptions.length === 0}
            onClick={() => void addDrivingSession()}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white min-w-[8rem]",
              saving ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700",
              (!canEditExperienceEntryForms || saving || tractionOptions.length === 0) && "opacity-60 cursor-not-allowed"
            )}
          >
            <Plus className="w-4 h-4" /> Add </button>
        </div>
        </div>
        </div>

        <div
          ref={traineeEntriesPaneRef}
          className={cn(
            "mt-3 sm:mt-4 rounded-2xl border p-4 sm:p-5 shadow-sm trainee-print-pane trainee-print-pane-entries",
            isDarkMode
              ? "border-sky-500/35 bg-sky-500/10 ring-1 ring-sky-500/20"
              : "border-sky-200 bg-sky-50/70 ring-1 ring-sky-100"
          )}
        >
          <div className="mb-3">
            <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-sky-200" : "text-sky-700")}>
              {translateUi("Recorded Entries")}
            </p>
            <p className={cn("mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
              Latest trainee experience entries are listed here for quick visibility and review.
            </p>
          </div>
        <div className="space-y-2">
          {records.driving_sessions.length === 0 ? (
            <p className={cn("text-sm italic", isDarkMode ? "text-slate-400" : "text-slate-500")}>No driving entries yet.</p>
          ) : (
            sortedTraineeReadOnlySessions.slice(0, 12).map((row) => (
              <div
                key={row.id}
                className={cn("rounded-xl border px-2.5 sm:px-3 py-2 flex items-center justify-between gap-2 sm:gap-3", isDarkMode ? "border-slate-700 bg-slate-950/40" : "border-slate-200 bg-slate-50")}
              >
                <div className="min-w-0">
                  <p className={cn("text-sm font-semibold truncate", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.traction}</p>
                  <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {String(row.start_hour).padStart(2, '0')}:{String(row.start_minute).padStart(2, '0')} - {String(row.finish_hour).padStart(2, '0')}:{String(row.finish_minute).padStart(2, '0')} ({formatHoursMinutes(row.duration_minutes)})
                    {coerceNonNegativeInt(row.dark_duration_minutes) > 0 ? (
                      <>
                        {' '}
                        ·{' '}
                        <span className={cn('font-medium', isDarkMode ? 'text-violet-300/90' : 'text-violet-700')}>
                          Darktime {formatHoursMinutes(row.dark_duration_minutes ?? 0)}
                        </span>
                      </>
                    ) : null}
                  </p>
                  {(row.route || row.train_service_ref) && (
                    <p className={cn("text-[11px]", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                      {row.route ? `Route: ${row.route}` : ''}{row.route && row.train_service_ref ? ' · ' : ''}{row.train_service_ref ? `Train ref: ${row.train_service_ref}` : ''}
                    </p>
                  )}
                  {Array.isArray(row.encounters) && row.encounters.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {row.encounters.map((enc) => (
                        <span
                          key={`${row.id}-${enc.key}-${enc.label}`}
                          className={cn(
                            'inline-flex items-center max-w-full rounded-full border px-2 py-0.5 text-[10px] sm:text-[11px] font-medium leading-tight',
                            isDarkMode
                              ? 'border-indigo-500/35 bg-indigo-500/15 text-indigo-100'
                              : 'border-indigo-200 bg-indigo-50 text-indigo-900'
                          )}
                          title={`${enc.label} × ${enc.count}`}
                        >
                          <span className="truncate">{enc.label}</span>
                          <span className={cn('ml-1 shrink-0 tabular-nums', isDarkMode ? 'text-indigo-200/90' : 'text-indigo-700')}>
                            ×{enc.count}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => setViewDrivingEntry(row)}
                    className={cn(
                      'p-2 rounded-lg',
                      isDarkMode ? 'text-sky-300 hover:bg-sky-500/10' : 'text-sky-600 hover:bg-sky-50'
                    )}
                    title={translateUi("View record entry")}
                    aria-label={translateUi("View driving record entry: {{traction}}", { traction: row.traction })}
                  >
                    <Search className="w-4 h-4" aria-hidden />
                  </button>
                  {canDeleteExperienceEntries && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void removeDrivingSession(row.id)}
                      className={cn("p-2 rounded-lg", isDarkMode ? "text-rose-300 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50")}
                      title={translateUi("Remove driving entry")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        </div>
              </>
        ) : null}
          </div>
        </div>

        <div className="mt-3 sm:mt-4">
          <div
            className={cn(
              'rounded-xl border overflow-hidden',
              isDarkMode ? 'border-slate-700' : 'border-slate-200'
            )}
          >
            <button
              type="button"
              onClick={() => setInstructorPaneExpanded((v) => !v)}
              aria-expanded={instructorPaneExpanded}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors sm:px-5 sm:py-3.5',
                isDarkMode
                  ? 'bg-slate-900/90 hover:bg-slate-800/90 border-b border-slate-700'
                  : 'bg-slate-100/90 hover:bg-slate-100 border-b border-slate-200'
              )}
            >
              <div className="min-w-0">
                <p className={cn('text-xs sm:text-sm font-bold flex items-center gap-2', isDarkMode ? 'text-slate-100' : 'text-slate-900')}>
                  <History className={cn('w-4 h-4 shrink-0', isDarkMode ? 'text-sky-300' : 'text-sky-600')} />
                  {translateUi('Instructor details & audit')}
                </p>
                <p className={cn('mt-0.5 text-[11px] leading-snug', isDarkMode ? 'text-slate-400' : 'text-slate-600')}>
                  {displayAllocatedInstructor?.name
                    ? `${translateUi('Allocated')}: ${displayAllocatedInstructor.name}`
                    : translateUi('Tap to view allocation, reassign, and audit trail.')}
                </p>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 transition-transform',
                  isDarkMode ? 'text-slate-400' : 'text-slate-500',
                  instructorPaneExpanded && 'rotate-180'
                )}
                aria-hidden
              />
            </button>
            {instructorPaneExpanded ? (
              <div className={cn('p-4 sm:p-5 space-y-4', isDarkMode ? 'bg-slate-950/40' : 'bg-slate-50/90')}>
                <div
                  className={cn(
                    'rounded-2xl border p-4',
                    isDarkMode ? 'border-sky-500/30 bg-sky-500/10' : 'border-sky-200 bg-sky-50/80'
                  )}
                >
                  <p className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-sky-300' : 'text-sky-700')}>
                    {translateUi('Current allocation')}
                  </p>
                  <p className={cn('mt-1 text-base font-bold', isDarkMode ? 'text-slate-100' : 'text-slate-900')}>
                    {displayAllocatedInstructor?.name || translateUi('Not allocated')}
                  </p>
                  <p className={cn('mt-1 text-xs', isDarkMode ? 'text-slate-400' : 'text-slate-500')}>
                    {translateUi('Synced with the Trainee module. Reassign from either place — both stay up to date.')}
                  </p>
                </div>

                {instructorEnrollmentState === 'loading' ? (
                  <p className={cn('text-sm', isDarkMode ? 'text-slate-400' : 'text-slate-500')}>
                    {translateUi('Loading instructor details…')}
                  </p>
                ) : null}

                {instructorEnrollmentState === 'not_enrolled' ? (
                  <p
                    className={cn(
                      'rounded-xl border px-4 py-3 text-sm',
                      isDarkMode ? 'border-amber-800/50 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'
                    )}
                  >
                    {translateUi(
                      'This employee is not enrolled in the Trainee module yet. Enrol them there first to allocate an instructor.',
                    )}
                  </p>
                ) : null}

                {instructorError ? (
                  <p
                    className={cn(
                      'rounded-xl border px-4 py-3 text-sm',
                      isDarkMode ? 'border-red-900/50 text-red-300' : 'border-red-200 text-red-700'
                    )}
                  >
                    {instructorError}
                  </p>
                ) : null}

                {instructorEnrollmentState === 'enrolled' && canManageTraineeInstructor ? (
                  <div
                    className={cn(
                      'rounded-xl border p-4 space-y-3',
                      isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-white'
                    )}
                  >
                    <p className={cn('text-sm font-semibold', isDarkMode ? 'text-slate-200' : 'text-slate-800')}>
                      {translateUi('Assign or reassign instructor')}
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[12rem]">
                        <label
                          className={cn(
                            'block text-xs font-semibold mb-1',
                            isDarkMode ? 'text-slate-300' : 'text-slate-600'
                          )}
                        >
                          {translateUi('Driving instructor')}
                        </label>
                        <InstructorSelectDropdown
                          value={instructorDraft}
                          options={instructorOptions}
                          onChange={setInstructorDraft}
                          dark={isDarkMode}
                          disabled={savingInstructor}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={
                          savingInstructor ||
                          instructorDraft === String(displayAllocatedInstructor?.employee_id || '')
                        }
                        onClick={() => void saveInstructorAllocation()}
                        className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {savingInstructor ? translateUi('Saving…') : translateUi('Save allocation')}
                      </button>
                    </div>
                  </div>
                ) : null}

                <div>
                  <h4 className={cn('text-sm font-bold mb-3', isDarkMode ? 'text-slate-200' : 'text-slate-800')}>
                    {translateUi('Assignment audit trail')}
                  </h4>
                  {displayInstructorHistory.length === 0 ? (
                    <div
                      className={cn(
                        'rounded-xl border border-dashed px-4 py-8 text-center text-sm',
                        isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'
                      )}
                    >
                      {displayAllocatedInstructor?.name
                        ? translateUi(
                            'No assignment history recorded yet. Future reassignments will appear here with date and timestamp.',
                          )
                        : translateUi('No instructor has been allocated yet.')}
                    </div>
                  ) : (
                    <ol
                      className="relative space-y-0 border-l-2 ml-2 pl-5"
                      style={{ borderColor: isDarkMode ? '#334155' : '#e2e8f0' }}
                    >
                      {displayInstructorHistory.map((entry, index) => {
                        const period = getInstructorAssignmentPeriod(displayInstructorHistory, index);
                        const entryId = String(entry.id || '').trim();
                        return (
                          <li key={entryId || entry.assigned_at} className="relative pb-5 last:pb-0">
                            <span
                              className={cn(
                                'absolute -left-[1.4rem] top-1.5 w-2.5 h-2.5 rounded-full ring-4',
                                entry.action === 'cleared' || !entry.instructor_name
                                  ? isDarkMode
                                    ? 'bg-slate-500 ring-slate-900'
                                    : 'bg-slate-400 ring-white'
                                  : isDarkMode
                                    ? 'bg-sky-400 ring-slate-900'
                                    : 'bg-sky-500 ring-white'
                              )}
                            />
                            <InstructorAssignmentAuditCard
                              entry={entry}
                              fromAt={period.fromAt}
                              toAt={period.toAt}
                              dark={isDarkMode}
                              canDelete={canDeleteInstructorAudit && Boolean(entryId)}
                              deleting={deletingInstructorAuditId === entryId}
                              onDelete={
                                entryId
                                  ? () => {
                                      void deleteInstructorAuditEntry(entryId);
                                    }
                                  : undefined
                              }
                            />
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        </div>
        {traineeHoursNotApplicable && (
          <div
            className={cn(
              "absolute inset-0 z-10 flex items-center justify-center rounded-xl sm:rounded-2xl p-4 sm:p-5",
              isDarkMode ? "bg-slate-950/50" : "bg-slate-200/50"
            )}
            role="status"
            aria-live="polite"
          >
            <div className="flex max-w-sm flex-col items-center gap-3 text-center">
              <p
                className={cn(
                  "text-sm font-semibold",
                  isDarkMode ? "text-slate-100" : "text-slate-800"
                )}
              >
                Not applicable to your role
              </p>
              <button
                type="button"
                onClick={() => setShowTraineeReadOnlyRecordsModal(true)}
                disabled={records.driving_sessions.length === 0}
                className={cn(
                  'rounded-xl border px-3.5 py-2 text-xs font-semibold transition-colors',
                  isDarkMode
                    ? 'border-slate-500 bg-slate-900/80 text-slate-100 hover:bg-slate-800'
                    : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
                  records.driving_sessions.length === 0 && 'opacity-50 cursor-not-allowed'
                )}
              >
                View trainee records (read only)
              </button>
              <p className={cn('text-[11px]', isDarkMode ? "text-slate-300" : "text-slate-700")}>
                {records.driving_sessions.length > 0
                  ? `${records.driving_sessions.length} entr${records.driving_sessions.length === 1 ? 'y' : 'ies'} · ${formatHoursMinutes(runningTotalMinutes)} logged${runningDarkTotalMinutes > 0 ? ` · ${formatHoursMinutes(runningDarkTotalMinutes)} darktime` : ''}`
                  : 'No trainee driving entries recorded yet'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <div
          className={cn(
            "rounded-xl sm:rounded-2xl border p-3 sm:p-5",
            isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white",
            managerHoursNotApplicable && "pointer-events-none select-none opacity-[0.45]"
          )}
          aria-hidden={managerHoursNotApplicable}
        >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3 sm:mb-4">
          <div className="min-w-0 flex-1">
            <h3 className={cn("text-base sm:text-lg font-bold flex items-center gap-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>
              <Briefcase className="w-5 h-5 text-teal-500" />
              Managers hours
            </h3>
            <p className={cn("text-xs sm:text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              Log manager driving time against the <span className="font-semibold">Maintain driver competence</span> minimum from Company standards (not trainee daylight or darktime targets). Encounters and feedback are not included on these entries.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto shrink-0">
            <div className={cn("rounded-xl border px-3 py-2 text-sm font-semibold", isDarkMode ? "border-teal-400/40 bg-teal-500/10 text-teal-100" : "border-teal-200 bg-teal-50 text-teal-900")}>
              <span className="block">Running total: {formatHoursMinutes(managerProgressRecordedMinutes)}</span>
              {managerStandardsWindowStartYmd && managerCompetenceStandards?.periodMonths ? (
                <span className={cn('block text-[10px] font-medium mt-0.5', isDarkMode ? 'text-teal-200/80' : 'text-teal-800/90')}>
                  Counts entries on or after {formatDateUK(managerStandardsWindowStartYmd)} ({managerCompetenceStandards.periodMonths}-month maintenance period).
                </span>
              ) : null}
              {managerProgressDarkRecordedMinutes > 0 ? (
                <span className={cn('block text-xs font-medium mt-0.5', isDarkMode ? 'text-violet-200/90' : 'text-violet-800')}>
                  Darktime logged: {formatHoursMinutes(managerProgressDarkRecordedMinutes)}
                </span>
              ) : null}
              {managerLatestSessionDateYmd ? (
                <>
                  <span className={cn('block text-xs font-medium mt-1', isDarkMode ? "text-slate-300" : "text-slate-600")}>
                    Last entry: {formatDateUK(managerLatestSessionDateYmd)}
                  </span>
                  {managerMonthsDaysSinceLast ? (
                    <span className={cn('block text-xs mt-0.5', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      Since then: {formatMonthsAndDaysLabel(managerMonthsDaysSinceLast)}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className={cn('block text-xs font-medium mt-1', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  No manager hours recorded yet.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <div
            className={cn(
              'rounded-xl border overflow-hidden',
              isDarkMode ? "border-slate-700" : "border-slate-200"
            )}
          >
            <button
              type="button"
              onClick={() => {
                if (managerHoursExpanded) {
                  setManagerHoursExpanded(false);
                } else {
                  setDrivingHoursExpanded(false);
                  expandManagerHoursForNewEntry();
                }
              }}
              aria-expanded={managerHoursExpanded}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors sm:px-5 sm:py-3.5',
                isDarkMode
                  ? 'bg-slate-900/90 hover:bg-slate-800/90 border-b border-slate-700'
                  : 'bg-slate-100/90 hover:bg-slate-100 border-b border-slate-200'
              )}
            >
              <div className="min-w-0">
                <p className={cn('text-xs sm:text-sm font-bold', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                  Manager driving details
                </p>
                <p className={cn('mt-0.5 text-[11px] leading-snug', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  {managerHoursExpanded
                    ? translateUi("Tap to hide progress charts, new entry form, and recent entries.")
                    : translateUi("Tap to show progress charts, add a manager entry, and recent entries.")}
                </p>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 transition-transform',
                  isDarkMode ? "text-slate-400" : "text-slate-500",
                  managerHoursExpanded && 'rotate-180'
                )}
                aria-hidden
              />
            </button>
            {managerHoursExpanded ? (
              <>
              <div
                className={cn(
                  'p-4 sm:p-6',
                  isDarkMode ? 'bg-slate-950/40' : 'bg-slate-50/90'
                )}
              >
            <div className="space-y-6">
              <div className="min-w-0 space-y-4 text-left sm:space-y-5">
                <div>
                  <p className={cn('text-xs leading-relaxed', isDarkMode ? "text-slate-500" : "text-slate-600")}>
                    Progress uses the <span className="font-semibold">Maintain driver competence</span> driving minimum from{' '}
                    <span className="font-semibold">Company standards</span>. When a maintenance period is set, totals only include manager entries on or after the start of that window.
                  </p>
                </div>
                {traineeStandardsLoad === 'loading' && (
                  <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>Loading company standards…</p>
                )}
                {traineeStandardsLoad === 'error' && (
                  <p className={cn('text-sm', isDarkMode ? 'text-amber-200/90' : 'text-amber-800')}>
                    Could not load company standards. Progress chart is unavailable.
                  </p>
                )}
                {traineeStandardsLoad === 'loaded' && managerCompetenceRequiredMinutes <= 0 && (
                  <p className={cn('text-sm leading-relaxed', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    The manager competence driving minimum is not set (or is zero). Configure <span className="font-semibold">Maintain driver competence</span> under{' '}
                    <span className="font-semibold">Company standards</span> to see completed vs remaining hours here.
                  </p>
                )}
                {!companyCode && traineeStandardsLoad === 'idle' && (
                  <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    Company context is required to load standards.
                  </p>
                )}
              </div>

            {(() => {
              const showManagerProgress =
                traineeStandardsLoad === 'loaded' && managerCompetenceRequiredMinutes > 0;
              const showDarkInfoPane = showManagerProgress && managerProgressDarkRecordedMinutes > 0;
              if (!showManagerProgress) return null;
              const paneCount = showDarkInfoPane ? 2 : 1;
              const maintMonths = managerCompetenceStandards?.periodMonths;
              return (
                <div className="min-w-0 space-y-2">
                  <div
                    className={cn(
                      'flex min-w-0 flex-col overflow-hidden rounded-2xl border text-left shadow-sm lg:flex-row',
                      isDarkMode
                        ? 'border-slate-600 bg-slate-900/80 divide-slate-600/90'
                        : 'border-slate-200 bg-white divide-slate-200',
                      paneCount > 1 && 'divide-y lg:divide-y-0 lg:divide-x'
                    )}
                  >
                    <div
                      ref={managerDonutInfoRef}
                      className="flex min-w-0 flex-1 basis-0 flex-col items-center p-4 sm:p-5"
                    >
                      <div
                        className="relative mx-auto w-full max-w-[16.5rem]"
                        role="img"
                        aria-label={translateUi("Manager driving hours logged {{logged}} of company competence minimum {{minimum}}", { logged: formatHoursMinutes(managerProgressRecordedMinutes), minimum: formatHoursMinutes(managerCompetenceRequiredMinutes) })}
                      >
                        <div
                          className="relative z-20 mb-3"
                          onMouseEnter={() => setManagerDonutInfoHover(true)}
                          onMouseLeave={() => setManagerDonutInfoHover(false)}
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <p
                              className={cn(
                                'whitespace-nowrap text-center text-[11px] font-semibold uppercase tracking-wide',
                                isDarkMode ? 'text-indigo-200/90' : 'text-indigo-800'
                              )}
                            >
                              Competence driving minimum
                            </p>
                            <button
                              type="button"
                              className={cn(
                                'shrink-0 rounded-full p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                                isDarkMode
                                  ? "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                              )}
                              aria-expanded={showManagerDonutInfo}
                              aria-label={translateUi("How this progress chart works. Hover for a summary, or click to keep the panel open.")}
                              onClick={() => setManagerDonutInfoPinned((p) => !p)}
                            >
                              <Info className="h-4 w-4" aria-hidden />
                            </button>
                          </div>
                          {showManagerDonutInfo ? (
                            <div
                              className={cn(
                                'absolute left-0 right-0 top-full z-30 mt-2 w-full rounded-xl border p-3 text-left text-xs leading-relaxed shadow-xl',
                                isDarkMode ? "border-slate-600 bg-slate-900 text-slate-200" : "border-slate-200 bg-white text-slate-700"
                              )}
                              role="tooltip"
                            >
                              <p>
                                Target is the <span className="font-semibold">Maintain driver competence</span> driving minimum (
                                {formatHoursMinutes(managerCompetenceRequiredMinutes)}).
                              </p>
                              <p className={cn('mt-2', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                {managerStandardsWindowStartYmd && maintMonths ? (
                                  <>
                                    Logged time counts only manager entries dated on or after{' '}
                                    <span className="font-semibold">{formatDateUK(managerStandardsWindowStartYmd)}</span> (company{' '}
                                    {maintMonths}-month maintenance period). Totals in the header match this chart.
                                  </>
                                ) : (
                                  <>
                                    Logged time counts all manager entries. Set a maintenance period under Company standards if totals should roll over a fixed window.
                                  </>
                                )}
                              </p>
                              <p className={cn('mt-2', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                <span className="font-semibold">{translateUi("Darktime driving (this entry)")}</span> is optional detail only; it does not change the competence minimum (unlike trainee darktime targets).
                              </p>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex justify-center">
                          <ExperienceTraineeHoursDonut
                            recordedMinutes={managerProgressRecordedMinutes}
                            requiredMinutes={managerCompetenceRequiredMinutes}
                            isDarkMode={!!isDarkMode}
                            indigoOverExcessLabel="the competence minimum"
                          />
                        </div>
                      </div>
                    </div>
                    {showDarkInfoPane ? (
                      <div
                        className={cn(
                          'flex min-w-0 flex-1 basis-0 flex-col items-center justify-center p-4 sm:p-5',
                          isDarkMode ? 'bg-violet-950/15 lg:bg-transparent' : 'bg-violet-50/40 lg:bg-transparent'
                        )}
                      >
                        <div
                          className={cn(
                            'flex min-h-[8rem] w-full max-w-lg flex-col justify-center rounded-xl border px-4 py-4 text-center text-sm leading-relaxed sm:px-5',
                            isDarkMode ? 'border-violet-500/30 bg-violet-950/20 text-slate-300' : 'border-violet-200 bg-violet-50/80 text-slate-700'
                          )}
                          role="region"
                          aria-label={translateUi("Darktime driving logged on manager entries in scope: {{logged}}", { logged: formatHoursMinutes(managerProgressDarkRecordedMinutes) })}
                        >
                          <p
                            className={cn(
                              'mb-2 text-center text-[11px] font-semibold uppercase tracking-wider',
                              isDarkMode ? 'text-violet-200/95' : 'text-violet-900'
                            )}
                          >
                            Darktime in scope
                          </p>
                          <p>
                            Darktime recorded on manager entries in this scope:{' '}
                            <span
                              className={cn(
                                'font-semibold tabular-nums',
                                isDarkMode ? "text-violet-300" : "text-violet-600"
                              )}
                            >
                              {formatHoursMinutes(managerProgressDarkRecordedMinutes)}
                            </span>
                            . This is for your records only and is not compared to trainee darktime minimums.
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}
            </div>
              </div>

        <div
          ref={managerDrivingHoursEntryFormRef}
          className="scroll-mt-20 outline-none"
          tabIndex={-1}
          aria-label={translateUi("Manager driving entry form")}
        >
        {tractionOptions.length === 0 && (
          <div className={cn("mb-3 rounded-xl border px-3 py-2 text-xs sm:text-sm", isDarkMode ? "border-amber-400/30 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800")}>
            No traction records found for this employee yet. Add traction in `Trains & Routes` first, then return here.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 sm:gap-3">
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Date</label>
            <input
              type="date"
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrDrivingDate}
              onChange={(e) => setMgrDrivingDate(e.target.value)}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Driving start time (HH:MM)</label>
            <input
              type="time"
              step={60}
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrStartTime}
              onChange={(e) => setMgrStartTime(e.target.value || '00:00')}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Driving finish time (HH:MM)</label>
            <input
              type="time"
              step={60}
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrFinishTime}
              onChange={(e) => setMgrFinishTime(e.target.value || '00:00')}
              className={cn("w-full rounded-xl border px-2 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}> Traction </label>
            <select
              disabled={!canEditExperienceEntryForms || saving || tractionOptions.length === 0}
              value={mgrTraction}
              onChange={(e) => setMgrTraction(e.target.value)}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            >
              <option value="">{tractionOptions.length === 0 ? translateUi("No traction available") : translateUi("Please select traction type")}</option>
              {tractionOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Route from</label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrDrivingRouteFrom}
              onChange={(e) => setMgrDrivingRouteFrom(e.target.value)}
              placeholder="e.g. Bicester South Jct"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Route to</label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrDrivingRouteTo}
              onChange={(e) => setMgrDrivingRouteTo(e.target.value)}
              placeholder="e.g. Oxford"
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Train source</label>
            <select
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrTrainSource}
              onChange={(e) => setMgrTrainSource(e.target.value === 'feed' ? 'feed' : 'manual')}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            >
              <option value="manual">Manual entry</option>
              {showFeedLookupOption && <option value="feed">Feed lookup</option>}
            </select>
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              {mgrTrainSource === 'feed' ? translateUi("Train service reference") : translateUi("Train reference (optional)")}
            </label>
            <input
              type="text"
              disabled={!canEditExperienceEntryForms || saving}
              value={mgrTrainServiceRef}
              onChange={(e) => setMgrTrainServiceRef(e.target.value)}
              placeholder={mgrTrainSource === 'feed' ? translateUi("e.g. 1A23@KGX or KGX") : translateUi("e.g. 1A23")}
              className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
            />
          </div>
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>Calculated</label>
            <div className={cn("h-[38px] rounded-xl border px-2.5 py-2 text-sm font-semibold flex items-center justify-center sm:justify-start", isDarkMode ? "border-slate-600 bg-slate-950 text-slate-200" : "border-slate-300 bg-slate-50 text-slate-700")}>
              {formatHoursMinutes(mgrDurationPreview)}
            </div>
          </div>
        </div>
        <div
          className={cn(
            'mt-2 rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 space-y-2',
            isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50/90'
          )}
        >
          <div>
            <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              {translateUi("Darktime driving (this entry)")}
            </label>
            <p className={cn('text-[10px] leading-snug mb-2', isDarkMode ? "text-slate-500" : "text-slate-600")}>
              Of the calculated duration above, how much was darktime driving.
            </p>
            <div className="flex flex-wrap items-end gap-2 sm:gap-3">
              <div className="min-w-[5rem]">
                <label className={cn('mb-0.5 block text-[10px] font-medium', isDarkMode ? "text-slate-400" : "text-slate-500")}> Hours </label>
                <input
                  type="number"
                  min={0}
                  disabled={!canEditExperienceEntryForms || saving}
                  value={mgrDarkDrivingHours}
                  onChange={(e) => {
                    setMgrDarkDrivingHours(e.target.value);
                  }}
                  className={cn(
                    'w-full min-w-0 rounded-xl border px-2.5 py-2 text-sm tabular-nums',
                    isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                  )}
                />
              </div>
              <div className="min-w-[5rem]">
                <label className={cn('mb-0.5 block text-[10px] font-medium', isDarkMode ? "text-slate-400" : "text-slate-500")}> Minutes </label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  disabled={!canEditExperienceEntryForms || saving}
                  value={mgrDarkDrivingMinutes}
                  onChange={(e) => {
                    setMgrDarkDrivingMinutes(e.target.value);
                  }}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v === '') return;
                    const m = Math.max(0, Math.min(59, coerceNonNegativeInt(v)));
                    setMgrDarkDrivingMinutes(String(m));
                  }}
                  className={cn(
                    'w-full min-w-0 rounded-xl border px-2.5 py-2 text-sm tabular-nums',
                    isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                  )}
                />
              </div>
              <div className="pb-2 text-xs">
                <span className={cn('tabular-nums font-semibold', isDarkMode ? "text-violet-300" : "text-violet-700")}>
                  = {formatHoursMinutes(mgrDarkInputTotalMinutes)}
                </span>
              </div>
            </div>
            <p className={cn('mt-2 text-[11px]', managerDarktimeExceedsDuration ? (isDarkMode ? "text-rose-300" : "text-rose-700") : (isDarkMode ? "text-emerald-300" : "text-emerald-700"))}>
              {managerDarktimeExceedsDuration
                ? `Darktime cannot exceed calculated duration (${formatHoursMinutes(mgrDurationPreview)}).`
                : `Darktime is within calculated duration (${formatHoursMinutes(mgrDurationPreview)}).`}
            </p>
          </div>
        </div>
        <div className="mt-2">
          <label className={cn("mb-1 block text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}> Notes </label>
          <textarea
            disabled={!canEditExperienceEntryForms || saving}
            value={mgrDrivingNotes}
            onChange={(e) => setMgrDrivingNotes(e.target.value)}
            rows={3}
            placeholder="Notes for this manager driving entry"
            className={cn("w-full rounded-xl border px-2.5 py-2 text-sm min-w-0 resize-y", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={!canEditExperienceEntryForms || saving || tractionOptions.length === 0}
            onClick={() => void addManagerDrivingSession()}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white min-w-[8rem]",
              saving ? "bg-teal-400" : "bg-teal-600 hover:bg-teal-700",
              (!canEditExperienceEntryForms || saving || tractionOptions.length === 0) && "opacity-60 cursor-not-allowed"
            )}
          >
            <Plus className="w-4 h-4" /> Add </button>
        </div>
        </div>

        <div className="mt-3 sm:mt-4 space-y-2">
          {records.manager_driving_sessions.length === 0 ? (
            <p className={cn("text-sm italic", isDarkMode ? "text-slate-400" : "text-slate-500")}>No manager driving entries yet.</p>
          ) : (
            sortedManagerSessions.slice(0, 12).map((row) => (
              <div
                key={row.id}
                className={cn("rounded-xl border px-2.5 sm:px-3 py-2 flex items-center justify-between gap-2 sm:gap-3", isDarkMode ? "border-slate-700 bg-slate-950/40" : "border-slate-200 bg-slate-50")}
              >
                <div className="min-w-0">
                  <p className={cn("text-sm font-semibold truncate", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.traction}</p>
                  <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {String(row.start_hour).padStart(2, '0')}:{String(row.start_minute).padStart(2, '0')} - {String(row.finish_hour).padStart(2, '0')}:{String(row.finish_minute).padStart(2, '0')} ({formatHoursMinutes(row.duration_minutes)})
                    {coerceNonNegativeInt(row.dark_duration_minutes) > 0 ? (
                      <>
                        {' '}
                        ·{' '}
                        <span className={cn('font-medium', isDarkMode ? 'text-violet-300/90' : 'text-violet-700')}>
                          Darktime {formatHoursMinutes(row.dark_duration_minutes ?? 0)}
                        </span>
                      </>
                    ) : null}
                  </p>
                  {(row.route || row.train_service_ref) && (
                    <p className={cn("text-[11px]", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                      {row.route ? `Route: ${row.route}` : ''}{row.route && row.train_service_ref ? ' · ' : ''}{row.train_service_ref ? `Train ref: ${row.train_service_ref}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => setViewManagerDrivingEntry(row)}
                    className={cn(
                      'p-2 rounded-lg',
                      isDarkMode ? 'text-sky-300 hover:bg-sky-500/10' : 'text-sky-600 hover:bg-sky-50'
                    )}
                    title={translateUi("View record entry")}
                    aria-label={translateUi("View manager driving record: {{traction}}", { traction: row.traction })}
                  >
                    <Search className="w-4 h-4" aria-hidden />
                  </button>
                  {canDeleteExperienceEntries && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void removeManagerDrivingSession(row.id)}
                      className={cn("p-2 rounded-lg", isDarkMode ? "text-rose-300 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50")}
                      title={translateUi("Remove driving entry")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
              </>
        ) : null}
          </div>
        </div>
        </div>
        {managerHoursNotApplicable && (
          <div
            className={cn(
              "absolute inset-0 z-10 flex items-center justify-center rounded-xl sm:rounded-2xl p-4 sm:p-5",
              isDarkMode ? "bg-slate-950/50" : "bg-slate-200/50"
            )}
            role="status"
            aria-live="polite"
          >
            <div className="flex max-w-sm flex-col items-center gap-3 text-center">
              <p
                className={cn(
                  "text-sm font-semibold",
                  isDarkMode ? "text-slate-100" : "text-slate-800"
                )}
              >
                Not applicable to your role
              </p>
              <button
                type="button"
                onClick={() => setShowManagerReadOnlyRecordsModal(true)}
                disabled={records.manager_driving_sessions.length === 0}
                className={cn(
                  'rounded-xl border px-3.5 py-2 text-xs font-semibold transition-colors',
                  isDarkMode
                    ? 'border-slate-500 bg-slate-900/80 text-slate-100 hover:bg-slate-800'
                    : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
                  records.manager_driving_sessions.length === 0 && 'opacity-50 cursor-not-allowed'
                )}
              >
                View all records
              </button>
            </div>
          </div>
        )}
      </div>

      {viewDrivingEntry && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setViewDrivingEntry(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="driving-entry-view-title"
        >
          <div
            className={cn(
              'w-full max-w-4xl max-h-[min(92vh,44rem)] overflow-y-auto rounded-2xl shadow-2xl border',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6 border-b', isDarkMode ? "border-slate-700" : "border-slate-100")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="driving-entry-view-title"
                    className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
                  >
                    Driving entry
                  </h3>
                  <p className={cn('mt-1 text-sm font-medium', isDarkMode ? "text-slate-300" : "text-slate-700")}>
                    {viewDrivingEntry.traction}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setViewDrivingEntry(null)}
                  className={cn(
                    'rounded-lg p-2 shrink-0',
                    isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                  )}
                  title={translateUi("Close")}
                  aria-label={translateUi("Close driving entry")}
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>
            </div>
            <div className={cn('p-5 sm:p-6 space-y-4 text-sm', isDarkMode ? "text-slate-200" : "text-slate-700")}>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/50' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Date</span>
                  <p className="mt-0.5">{viewDrivingEntry.date ? formatDateUK(viewDrivingEntry.date) : '—'}</p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/50' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Duration </span>
                  <p className="mt-0.5 tabular-nums">{formatHoursMinutes(viewDrivingEntry.duration_minutes)}</p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-violet-500/30 bg-violet-950/20' : 'border-violet-200 bg-violet-50/70')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-violet-300/80' : 'text-violet-700')}> Darktime </span>
                  <p className="mt-0.5 tabular-nums">{formatHoursMinutes(coerceNonNegativeInt(viewDrivingEntry.dark_duration_minutes))}</p>
                </div>
              </div>

              <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Driving time</span>
                <p className="mt-1 tabular-nums">
                  {String(viewDrivingEntry.start_hour).padStart(2, '0')}:{String(viewDrivingEntry.start_minute).padStart(2, '0')} - {String(viewDrivingEntry.finish_hour).padStart(2, '0')}:{String(viewDrivingEntry.finish_minute).padStart(2, '0')}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Route </span>
                  <p className={cn('mt-1 whitespace-pre-wrap', !viewDrivingEntry.route && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                    {viewDrivingEntry.route || '—'}
                  </p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Train</span>
                  <p className="mt-1">
                    {viewDrivingEntry.train_source === 'feed' ? translateUi("Feed lookup") : translateUi("Manual")}
                    {viewDrivingEntry.train_service_ref?.trim() ? ` · ${viewDrivingEntry.train_service_ref.trim()}` : ''}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Notes </span>
                  <p className={cn('mt-1 whitespace-pre-wrap', !viewDrivingEntry.notes?.trim() && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                    {viewDrivingEntry.notes?.trim() ? viewDrivingEntry.notes.trim() : '—'}
                  </p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Feedback </span>
                  <p className={cn('mt-1 whitespace-pre-wrap', !viewDrivingEntry.feedback?.trim() && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                    {viewDrivingEntry.feedback?.trim() ? viewDrivingEntry.feedback.trim() : '—'}
                  </p>
                </div>
              </div>

              <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Encounters</span>
                {Array.isArray(viewDrivingEntry.encounters) && viewDrivingEntry.encounters.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {viewDrivingEntry.encounters.map((enc) => (
                      <span
                        key={`${viewDrivingEntry.id}-${enc.key}-${enc.label}`}
                        className={cn(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          isDarkMode ? 'border-indigo-500/35 bg-indigo-500/15 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-900'
                        )}
                      >
                        {enc.label} <span className="ml-1 tabular-nums">×{enc.count}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className={cn('mt-1', isDarkMode ? "text-slate-500" : "text-slate-400")}> None </p>
                )}
              </div>
            </div>
            <div
              className={cn(
                'flex justify-end border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                onClick={() => setViewDrivingEntry(null)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold',
                  isDarkMode ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"
                )}
              > Close </button>
            </div>
          </div>
        </div>
      )}

      {showTraineeReadOnlyRecordsModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setShowTraineeReadOnlyRecordsModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="trainee-readonly-records-title"
        >
          <div
            className={cn(
              'w-full max-w-5xl max-h-[min(94vh,52rem)] flex flex-col overflow-hidden rounded-2xl shadow-2xl border',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('shrink-0 p-4 sm:p-5 border-b', isDarkMode ? "border-slate-700" : "border-slate-100")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="trainee-readonly-records-title"
                    className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
                  >
                    Trainee records (read only)
                  </h3>
                  <p className={cn('mt-1 text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    Historic trainee driving hours kept after promotion to train driver
                    {traineeArchiveStats.entryCount > 0
                      ? ` · ${traineeArchiveStats.entryCount} entr${traineeArchiveStats.entryCount === 1 ? 'y' : 'ies'}`
                      : ''}
                    {traineeArchiveStats.earliest && traineeArchiveStats.latest
                      ? traineeArchiveStats.earliest === traineeArchiveStats.latest
                        ? ` · ${formatDateUK(traineeArchiveStats.earliest)}`
                        : ` · ${formatDateUK(traineeArchiveStats.earliest)} – ${formatDateUK(traineeArchiveStats.latest)}`
                      : ''}
                    .
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {records.driving_sessions.length > 0 ? (
                    <button
                      type="button"
                      onClick={handlePrintTraineeHours}
                      className={cn(
                        'rounded-lg p-2',
                        isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                      )}
                      title={translateUi("Print trainee records")}
                      aria-label={translateUi("Print trainee records")}
                    >
                      <Printer className="w-4 h-4" aria-hidden />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setShowTraineeReadOnlyRecordsModal(false)}
                    className={cn(
                      'rounded-lg p-2',
                      isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                    )}
                    title={translateUi("Close")}
                    aria-label={translateUi("Close trainee records popup")}
                  >
                    <X className="w-4 h-4" aria-hidden />
                  </button>
                </div>
              </div>
            </div>

            <div className={cn('min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-4', isDarkMode ? "text-slate-200" : "text-slate-700")}>
              {records.driving_sessions.length === 0 ? (
                <p className={cn('text-sm italic', isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  No trainee driving entries recorded.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: 'Entries', value: String(traineeArchiveStats.entryCount) },
                      { label: translateUi("Total hours"), value: formatHoursMinutes(runningTotalMinutes) },
                      { label: translateUi("Darktime"), value: formatHoursMinutes(runningDarkTotalMinutes) },
                      { label: 'Encounters', value: String(traineeArchiveStats.encounterEvents) },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className={cn(
                          'rounded-xl border px-3 py-2.5',
                          isDarkMode ? 'border-slate-700 bg-slate-950/50' : 'border-slate-200 bg-slate-50'
                        )}
                      >
                        <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>
                          {stat.label}
                        </p>
                        <p className={cn('mt-0.5 text-sm font-bold tabular-nums', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                          {stat.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  {(() => {
                    const showCombined = traineeStandardsLoad === 'loaded' && traineeRequiredTotalMinutes > 0;
                    const darkMinutesStandard = traineeStandardsMinutes?.darkMinutes ?? 0;
                    const showDarkDonut = showCombined && darkMinutesStandard > 0;
                    const showDarkCallout = showCombined && !showDarkDonut && runningDarkTotalMinutes > 0;
                    if (!showCombined && encounterTotalsAcrossSessions.length === 0) return null;
                    return (
                      <div
                        className={cn(
                          'rounded-xl border p-3 sm:p-4 space-y-4',
                          isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
                        )}
                      >
                        {showCombined ? (
                          <div>
                            <p
                              className={cn(
                                'mb-3 text-[11px] font-semibold uppercase tracking-wider',
                                isDarkMode ? 'text-indigo-200/90' : 'text-indigo-700'
                              )}
                            >
                              Progress against company trainee minimums
                            </p>
                            <div
                              className={cn(
                                'flex min-w-0 flex-col overflow-hidden rounded-xl border lg:flex-row',
                                isDarkMode ? 'border-slate-600 bg-slate-900/70 divide-slate-600/90' : 'border-slate-200 bg-white divide-slate-200',
                                (showDarkDonut || showDarkCallout) && 'divide-y lg:divide-y-0 lg:divide-x'
                              )}
                            >
                              <div className="flex min-w-0 flex-1 basis-0 flex-col items-center p-3 sm:p-4">
                                <p className={cn('mb-2 text-center text-[10px] font-semibold uppercase tracking-wide', isDarkMode ? 'text-indigo-200/90' : 'text-indigo-800')}>
                                  {translateUi("Combined (daylight + darktime)")}
                                </p>
                                <ExperienceTraineeHoursDonut
                                  recordedMinutes={runningTotalMinutes}
                                  requiredMinutes={traineeRequiredTotalMinutes}
                                  isDarkMode={!!isDarkMode}
                                  size={148}
                                />
                              </div>
                              {showDarkDonut || showDarkCallout ? (
                                <div
                                  className={cn(
                                    'flex min-w-0 flex-1 basis-0 flex-col items-center justify-center p-3 sm:p-4',
                                    showDarkDonut && (isDarkMode ? 'bg-violet-950/15 lg:bg-transparent' : 'bg-violet-50/40 lg:bg-transparent')
                                  )}
                                >
                                  {showDarkDonut ? (
                                    <>
                                      <p className={cn('mb-2 text-center text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-violet-200/95' : 'text-violet-900')}>
                                        Darktime vs minimum
                                      </p>
                                      <ExperienceTraineeHoursDonut
                                        recordedMinutes={runningDarkTotalMinutes}
                                        requiredMinutes={darkMinutesStandard}
                                        isDarkMode={!!isDarkMode}
                                        centerLabel={translateUi("Darktime")}
                                        variant="violet"
                                        size={148}
                                      />
                                    </>
                                  ) : (
                                    <p className={cn('text-sm text-center leading-relaxed', isDarkMode ? "text-slate-300" : "text-slate-700")}>
                                      Darktime logged:{' '}
                                      <span className={cn('font-semibold tabular-nums', isDarkMode ? "text-violet-300" : "text-violet-700")}>
                                        {formatHoursMinutes(runningDarkTotalMinutes)}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              ) : null}
                            </div>
                            {(traineeArchiveStats.withFeedback > 0 || traineeArchiveStats.withNotes > 0) && (
                              <p className={cn('mt-2 text-xs', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                {traineeArchiveStats.withFeedback > 0
                                  ? `${traineeArchiveStats.withFeedback} entr${traineeArchiveStats.withFeedback === 1 ? 'y' : 'ies'} with feedback`
                                  : null}
                                {traineeArchiveStats.withFeedback > 0 && traineeArchiveStats.withNotes > 0 ? ' · ' : null}
                                {traineeArchiveStats.withNotes > 0
                                  ? `${traineeArchiveStats.withNotes} with notes`
                                  : null}
                              </p>
                            )}
                          </div>
                        ) : null}

                        {encounterTotalsAcrossSessions.length > 0 ? (
                          <div>
                            <p
                              className={cn(
                                'mb-2 text-[11px] font-semibold uppercase tracking-wider',
                                isDarkMode ? "text-slate-400" : "text-slate-500"
                              )}
                            >
                              Encounters across all entries
                            </p>
                            <ExperienceEncountersDistributionBar rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
                            <div className="mt-2.5">
                              <ExperienceEncountersPieLegend rows={encounterTotalsAcrossSessions} isDarkMode={!!isDarkMode} />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}

                  {(traineeArchiveStats.tractionRows.length > 0 || traineeArchiveStats.routeRows.length > 0) && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {traineeArchiveStats.tractionRows.length > 0 ? (
                        <div
                          className={cn(
                            'rounded-xl border p-3',
                            isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
                          )}
                        >
                          <p className={cn('mb-2 text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-400" : "text-slate-500")}>
                            Traction covered
                          </p>
                          <ul className="space-y-1.5">
                            {traineeArchiveStats.tractionRows.map((row) => (
                              <li key={`arch-trac-${row.label}`} className="flex items-baseline justify-between gap-2 text-sm">
                                <span className={cn('min-w-0 truncate font-medium', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                                  {row.label}
                                </span>
                                <span className={cn('shrink-0 text-xs tabular-nums', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                  {row.count}× · {formatHoursMinutes(row.minutes)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {traineeArchiveStats.routeRows.length > 0 ? (
                        <div
                          className={cn(
                            'rounded-xl border p-3',
                            isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
                          )}
                        >
                          <p className={cn('mb-2 text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-400" : "text-slate-500")}>
                            Routes covered
                          </p>
                          <ul className="space-y-1.5">
                            {traineeArchiveStats.routeRows.map((row) => (
                              <li key={`arch-route-${row.label}`} className="flex items-baseline justify-between gap-2 text-sm">
                                <span className={cn('min-w-0 truncate font-medium', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                                  {row.label}
                                </span>
                                <span className={cn('shrink-0 text-xs tabular-nums', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                  {row.count}× · {formatHoursMinutes(row.minutes)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <div>
                    <div className="mb-2 flex items-end justify-between gap-2">
                      <div>
                        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-sky-200" : "text-sky-700")}>
                          Recorded entries
                        </p>
                        <p className={cn('mt-0.5 text-xs', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                          Newest first. Expand a row for notes, feedback, and full detail.
                        </p>
                      </div>
                      {sortedTraineeReadOnlySessions.length > 50 ? (
                        <p className={cn('text-[11px] shrink-0', isDarkMode ? "text-slate-500" : "text-slate-500")}>
                          Showing 50 of {sortedTraineeReadOnlySessions.length}
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      {sortedTraineeReadOnlySessions.slice(0, 50).map((row) => {
                        const expanded = expandedTraineeArchiveEntryId === row.id;
                        const timeLabel = `${String(row.start_hour).padStart(2, '0')}:${String(row.start_minute).padStart(2, '0')} – ${String(row.finish_hour).padStart(2, '0')}:${String(row.finish_minute).padStart(2, '0')}`;
                        return (
                          <div
                            key={`readonly-${row.id}`}
                            className={cn(
                              'rounded-xl border overflow-hidden',
                              isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
                            )}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedTraineeArchiveEntryId((prev) => (prev === row.id ? null : row.id))
                              }
                              className={cn(
                                'w-full text-left px-3 py-2.5 flex items-start justify-between gap-2 sm:gap-3 transition-colors',
                                isDarkMode ? 'hover:bg-slate-900/60' : 'hover:bg-white/70'
                              )}
                              aria-expanded={expanded}
                              aria-label={expanded
                                ? translateUi("Collapse trainee entry: {{traction}}", { traction: row.traction })
                                : translateUi("Expand trainee entry: {{traction}}", { traction: row.traction })}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                  <p className={cn('text-sm font-semibold', isDarkMode ? "text-slate-100" : "text-slate-900")}>
                                    {row.traction || '—'}
                                  </p>
                                  <p className={cn('text-[11px] tabular-nums', isDarkMode ? "text-slate-400" : "text-slate-500")}>
                                    {row.date ? formatDateUK(row.date) : '—'}
                                  </p>
                                </div>
                                <p className={cn('mt-0.5 text-xs', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                  <span className="tabular-nums">{timeLabel}</span>
                                  {' · '}
                                  <span className="font-medium tabular-nums">{formatHoursMinutes(row.duration_minutes)}</span>
                                  {coerceNonNegativeInt(row.dark_duration_minutes) > 0 ? (
                                    <>
                                      {' · '}
                                      <span className={cn('font-medium', isDarkMode ? 'text-violet-300/90' : 'text-violet-700')}>
                                        Darktime {formatHoursMinutes(row.dark_duration_minutes ?? 0)}
                                      </span>
                                    </>
                                  ) : null}
                                </p>
                                {(row.route || row.train_service_ref) && (
                                  <p className={cn('mt-0.5 text-[11px]', isDarkMode ? "text-slate-500" : "text-slate-500")}>
                                    {row.route ? `Route: ${row.route}` : ''}
                                    {row.route && row.train_service_ref ? ' · ' : ''}
                                    {row.train_service_ref
                                      ? `Train: ${row.train_source === 'feed' ? translateUi("Feed") : translateUi("Manual")} · ${row.train_service_ref}`
                                      : ''}
                                  </p>
                                )}
                                {!expanded && Array.isArray(row.encounters) && row.encounters.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {row.encounters.map((enc) => (
                                      <span
                                        key={`readonly-${row.id}-${enc.key}-${enc.label}`}
                                        className={cn(
                                          'inline-flex items-center max-w-full rounded-full border px-2 py-0.5 text-[10px] sm:text-[11px] font-medium leading-tight',
                                          isDarkMode
                                            ? 'border-indigo-500/35 bg-indigo-500/15 text-indigo-100'
                                            : 'border-indigo-200 bg-indigo-50 text-indigo-900'
                                        )}
                                      >
                                        <span className="truncate">{enc.label}</span>
                                        <span className={cn('ml-1 shrink-0 tabular-nums', isDarkMode ? 'text-indigo-200/90' : 'text-indigo-700')}>
                                          ×{enc.count}
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {!expanded && (row.feedback?.trim() || row.notes?.trim()) ? (
                                  <p className={cn('mt-1.5 text-[11px] line-clamp-1', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                                    {row.feedback?.trim()
                                      ? `Feedback: ${row.feedback.trim()}`
                                      : `Notes: ${row.notes!.trim()}`}
                                  </p>
                                ) : null}
                              </div>
                              <ChevronRight
                                className={cn(
                                  'w-4 h-4 shrink-0 mt-1 transition-transform',
                                  expanded && 'rotate-90',
                                  isDarkMode ? "text-slate-400" : "text-slate-500"
                                )}
                                aria-hidden
                              />
                            </button>
                            {expanded ? (
                              <div
                                className={cn(
                                  'border-t px-3 py-3 space-y-3 text-sm',
                                  isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-white'
                                )}
                              >
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                  <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? "border-slate-700" : "border-slate-200")}>
                                    <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Duration </p>
                                    <p className="mt-0.5 tabular-nums font-medium">{formatHoursMinutes(row.duration_minutes)}</p>
                                  </div>
                                  <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? 'border-violet-500/30 bg-violet-950/20' : 'border-violet-200 bg-violet-50/70')}>
                                    <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-violet-300/80' : 'text-violet-700')}> Darktime </p>
                                    <p className="mt-0.5 tabular-nums font-medium">{formatHoursMinutes(coerceNonNegativeInt(row.dark_duration_minutes))}</p>
                                  </div>
                                  <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? "border-slate-700" : "border-slate-200")}>
                                    <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Train</p>
                                    <p className="mt-0.5">
                                      {row.train_source === 'feed' ? translateUi("Feed lookup") : translateUi("Manual")}
                                      {row.train_service_ref?.trim() ? ` · ${row.train_service_ref.trim()}` : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                  <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? "border-slate-700" : "border-slate-200")}>
                                    <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Notes </p>
                                    <p className={cn('mt-1 whitespace-pre-wrap text-xs leading-relaxed', !row.notes?.trim() && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                                      {row.notes?.trim() || '—'}
                                    </p>
                                  </div>
                                  <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? "border-slate-700" : "border-slate-200")}>
                                    <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Feedback </p>
                                    <p className={cn('mt-1 whitespace-pre-wrap text-xs leading-relaxed', !row.feedback?.trim() && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                                      {row.feedback?.trim() || '—'}
                                    </p>
                                  </div>
                                </div>
                                <div className={cn('rounded-lg border px-2.5 py-2', isDarkMode ? "border-slate-700" : "border-slate-200")}>
                                  <p className={cn('text-[10px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Encounters</p>
                                  {Array.isArray(row.encounters) && row.encounters.length > 0 ? (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                      {row.encounters.map((enc) => (
                                        <span
                                          key={`readonly-exp-${row.id}-${enc.key}-${enc.label}`}
                                          className={cn(
                                            'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                                            isDarkMode
                                              ? 'border-indigo-500/35 bg-indigo-500/15 text-indigo-100'
                                              : 'border-indigo-200 bg-indigo-50 text-indigo-900'
                                          )}
                                        >
                                          {enc.label} <span className="ml-1 tabular-nums">×{enc.count}</span>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className={cn('mt-1 text-xs', isDarkMode ? "text-slate-500" : "text-slate-400")}> None </p>
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div
              className={cn(
                'shrink-0 flex justify-end border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                onClick={() => setShowTraineeReadOnlyRecordsModal(false)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold',
                  isDarkMode ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"
                )}
              > Close </button>
            </div>
          </div>
        </div>
      )}

      {showManagerReadOnlyRecordsModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setShowManagerReadOnlyRecordsModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manager-readonly-records-title"
        >
          <div
            className={cn(
              'w-full max-w-4xl max-h-[min(92vh,44rem)] overflow-y-auto rounded-2xl shadow-2xl border',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6 border-b', isDarkMode ? "border-slate-700" : "border-slate-100")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="manager-readonly-records-title"
                    className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
                  >
                    Manager driving records
                  </h3>
                  <p className={cn('mt-1 text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    View-only list of all manager driving entries.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowManagerReadOnlyRecordsModal(false)}
                  className={cn(
                    'rounded-lg p-2 shrink-0',
                    isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                  )}
                  title={translateUi("Close")}
                  aria-label={translateUi("Close manager driving records")}
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>
            </div>
            <div className="p-5 sm:p-6 space-y-2">
              {records.manager_driving_sessions.length === 0 ? (
                <p className={cn("text-sm italic", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  No manager driving entries yet.
                </p>
              ) : (
                sortedManagerSessions.slice(0, 50).map((row) => (
                  <div
                    key={`readonly-manager-${row.id}`}
                    className={cn(
                      "rounded-xl border px-2.5 sm:px-3 py-2 flex items-center justify-between gap-2 sm:gap-3",
                      isDarkMode ? "border-slate-700 bg-slate-950/40" : "border-slate-200 bg-slate-50"
                    )}
                  >
                    <div className="min-w-0">
                      <p className={cn("text-sm font-semibold truncate", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.traction}</p>
                      <p className={cn("text-[11px]", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                        Date: {row.date ? formatDateUK(row.date) : '—'}
                      </p>
                      <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        {String(row.start_hour).padStart(2, '0')}:{String(row.start_minute).padStart(2, '0')} - {String(row.finish_hour).padStart(2, '0')}:{String(row.finish_minute).padStart(2, '0')} ({formatHoursMinutes(row.duration_minutes)})
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowManagerReadOnlyRecordsModal(false);
                        setViewManagerDrivingEntry(row);
                      }}
                      className={cn(
                        'rounded-lg p-2 shrink-0',
                        isDarkMode ? 'text-sky-300 hover:bg-sky-500/10' : 'text-sky-600 hover:bg-sky-50'
                      )}
                      title={translateUi("View record entry")}
                      aria-label={translateUi("View manager record entry: {{traction}}", { traction: row.traction })}
                    >
                      <Search className="w-4 h-4" aria-hidden />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div
              className={cn(
                'flex justify-end border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                onClick={() => setShowManagerReadOnlyRecordsModal(false)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold',
                  isDarkMode ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"
                )}
              > Close </button>
            </div>
          </div>
        </div>
      )}

      {viewManagerDrivingEntry && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setViewManagerDrivingEntry(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manager-driving-entry-view-title"
        >
          <div
            className={cn(
              'w-full max-w-4xl max-h-[min(92vh,44rem)] overflow-y-auto rounded-2xl shadow-2xl border',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6 border-b', isDarkMode ? "border-slate-700" : "border-slate-100")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="manager-driving-entry-view-title"
                    className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
                  >
                    Manager driving entry
                  </h3>
                  <p className={cn('mt-1 text-sm font-medium', isDarkMode ? "text-slate-300" : "text-slate-700")}>
                    {viewManagerDrivingEntry.traction}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setViewManagerDrivingEntry(null)}
                  className={cn(
                    'rounded-lg p-2 shrink-0',
                    isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                  )}
                  title={translateUi("Close")}
                  aria-label={translateUi("Close manager driving entry")}
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>
            </div>
            <div className={cn('p-5 sm:p-6 space-y-4 text-sm', isDarkMode ? "text-slate-200" : "text-slate-700")}>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/50' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Date</span>
                  <p className="mt-0.5">{viewManagerDrivingEntry.date ? formatDateUK(viewManagerDrivingEntry.date) : '—'}</p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/50' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Duration </span>
                  <p className="mt-0.5 tabular-nums">{formatHoursMinutes(viewManagerDrivingEntry.duration_minutes)}</p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-violet-500/30 bg-violet-950/20' : 'border-violet-200 bg-violet-50/70')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-violet-300/80' : 'text-violet-700')}> Darktime </span>
                  <p className="mt-0.5 tabular-nums">{formatHoursMinutes(coerceNonNegativeInt(viewManagerDrivingEntry.dark_duration_minutes))}</p>
                </div>
              </div>

              <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Driving time</span>
                <p className="mt-1 tabular-nums">
                  {String(viewManagerDrivingEntry.start_hour).padStart(2, '0')}:{String(viewManagerDrivingEntry.start_minute).padStart(2, '0')} – {String(viewManagerDrivingEntry.finish_hour).padStart(2, '0')}:{String(viewManagerDrivingEntry.finish_minute).padStart(2, '0')}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Route </span>
                  <p className={cn('mt-1 whitespace-pre-wrap', !viewManagerDrivingEntry.route && (isDarkMode ? "text-slate-500" : "text-slate-400"))}>
                    {viewManagerDrivingEntry.route || '—'}
                  </p>
                </div>
                <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                  <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}>Train</span>
                  <p className="mt-1">
                    {viewManagerDrivingEntry.train_source === 'feed' ? translateUi("Feed lookup") : translateUi("Manual")}
                    {viewManagerDrivingEntry.train_service_ref?.trim() ? ` · ${viewManagerDrivingEntry.train_service_ref.trim()}` : ''}
                  </p>
                </div>
              </div>

              <div className={cn('rounded-xl border px-3 py-2.5', isDarkMode ? 'border-slate-700 bg-slate-950/40' : 'border-slate-200 bg-slate-50')}>
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? "text-slate-500" : "text-slate-500")}> Notes </span>
                <p
                  className={cn(
                    'mt-1 whitespace-pre-wrap',
                    !viewManagerDrivingEntry.notes?.trim() && (isDarkMode ? "text-slate-500" : "text-slate-400")
                  )}
                >
                  {viewManagerDrivingEntry.notes?.trim() ? viewManagerDrivingEntry.notes.trim() : '—'}
                </p>
              </div>
            </div>
            <div
              className={cn(
                'flex justify-end border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                onClick={() => setViewManagerDrivingEntry(null)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold',
                  isDarkMode ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"
                )}
              > Close </button>
            </div>
          </div>
        </div>
      )}

      {encounterCountModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => !saving && setEncounterCountModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="encounter-count-modal-title"
        >
          <div
            className={cn(
              'w-full max-w-md rounded-2xl shadow-2xl border overflow-hidden',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6', isDarkMode ? "bg-slate-900" : "bg-white")}>
              <h3
                id="encounter-count-modal-title"
                className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
              >
                {encounterCountModal.label}
              </h3>
              <p className={cn('mt-1 text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                How many times did this occur on this driving entry?
              </p>
              <label
                className={cn(
                  'mt-4 mb-1 block text-[11px] font-semibold uppercase tracking-wider',
                  isDarkMode ? "text-slate-400" : "text-slate-500"
                )}
              >
                Count
              </label>
              <select
                disabled={!canEditExperienceEntryForms || saving}
                value={encounterModalCount}
                onChange={(e) => setEncounterModalCount(e.target.value)}
                className={cn(
                  'w-full rounded-xl border px-3 py-2.5 text-sm',
                  isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"
                )}
              >
                {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div
              className={cn(
                'flex flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <div className="min-h-[40px] flex items-center">
                {encounterModalShowsRemove ? (
                  <button
                    type="button"
                    disabled={!canEditExperienceEntryForms || saving}
                    onClick={removeDraftEncounterFromModal}
                    className={cn(
                      'rounded-xl px-4 py-2 text-sm font-semibold border transition-colors',
                      isDarkMode
                        ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'
                        : 'border-rose-200 text-rose-700 hover:bg-rose-50',
                      (!canEditExperienceEntryForms || saving) && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    Remove from entry
                  </button>
                ) : null}
              </div>
              <div className="flex flex-row flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setEncounterCountModal(null)}
                  className={cn(
                    'rounded-xl px-5 py-2.5 text-sm font-semibold border transition-colors',
                    isDarkMode
                      ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                      : "border-slate-300 text-slate-800 hover:bg-slate-100",
                    saving && 'opacity-50 cursor-not-allowed'
                  )}
                > Cancel </button>
                <button
                  type="button"
                  disabled={!canEditExperienceEntryForms || saving}
                  onClick={applyEncounterCountFromModal}
                  className={cn(
                    'rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors',
                    saving ? 'bg-indigo-400' : isDarkMode ? "bg-indigo-500 hover:bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700",
                    (!canEditExperienceEntryForms || saving) && 'opacity-50 cursor-not-allowed'
                  )}
                > Save </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddDrivingConfirmModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => !saving && setShowAddDrivingConfirmModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-driving-confirm-title"
        >
          <div
            className={cn(
              'w-full max-w-lg rounded-2xl shadow-2xl border overflow-hidden',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6', isDarkMode ? "bg-slate-900" : "bg-white")}>
              <h3
                id="add-driving-confirm-title"
                className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
              >
                Add this driving entry?
              </h3>
              <p className={cn('mt-1 text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                Check the summary below, then confirm to save it to the record.
              </p>
              {draftEncounters.length === 0 && (
                <div
                  className={cn(
                    'mt-4 rounded-xl border px-3 py-2.5 text-sm flex gap-2',
                    isDarkMode ? 'border-amber-500/35 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-950'
                  )}
                >
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
                  <span>
                    No encounters are listed for this entry. You can still add the entry if that is correct.
                  </span>
                </div>
              )}
              <ul
                className={cn(
                  'mt-4 space-y-1.5 text-sm list-disc pl-5',
                  isDarkMode ? "text-slate-300" : "text-slate-700"
                )}
              >
                <li>
                  <span className="font-semibold">Date:</span>{' '}
                  {formatDateUK(drivingDate || getTodayDateInputValue())}
                </li>
                <li>
                  <span className="font-semibold">Driving time:</span> {startTime} – {finishTime} (
                  {formatHoursMinutes(durationPreview)})
                </li>
                <li>
                  <span className="font-semibold">Darktime (this entry):</span>{' '}
                  {formatHoursMinutes(
                    Math.min(
                      durationPreview,
                      coerceNonNegativeInt(darkDrivingHours) * 60 +
                        Math.min(59, coerceNonNegativeInt(darkDrivingMinutes === '' ? '0' : darkDrivingMinutes))
                    )
                  )}
                </li>
                <li>
                  <span className="font-semibold">Traction:</span> {traction}
                </li>
                <li>
                  <span className="font-semibold">Route:</span> {drivingRouteFrom.trim()} — {drivingRouteTo.trim()}
                </li>
                <li>
                  <span className="font-semibold">Train:</span>{' '}
                  {trainSource === 'feed' ? translateUi("Feed lookup") : translateUi("Manual")}
                  {trainServiceRef.trim() ? ` · ${trainServiceRef.trim()}` : ''}
                </li>
                {drivingNotes.trim() ? (
                  <li>
                    <span className="font-semibold">Notes:</span> {drivingNotes.trim()}
                  </li>
                ) : null}
                {drivingFeedback.trim() ? (
                  <li>
                    <span className="font-semibold">Feedback:</span> {drivingFeedback.trim()}
                  </li>
                ) : null}
                <li>
                  <span className="font-semibold">Encounters:</span>{' '}
                  {draftEncounters.length === 0
                    ? translateUi("None")
                    : draftEncounters.map((e) => `${e.label} (×${e.count})`).join('; ')}
                </li>
              </ul>
            </div>
            <div
              className={cn(
                'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowAddDrivingConfirmModal(false)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold border transition-colors',
                  isDarkMode
                    ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                    : "border-slate-300 text-slate-800 hover:bg-slate-100",
                  saving && 'opacity-50 cursor-not-allowed'
                )}
              > Cancel </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void persistDrivingSession()}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors',
                  saving ? 'bg-indigo-400' : isDarkMode ? "bg-indigo-500 hover:bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700",
                  saving && 'cursor-wait'
                )}
              >
                {saving ? 'Saving…' : translateUi("Confirm and add")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddManagerDrivingConfirmModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => !saving && setShowAddManagerDrivingConfirmModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-manager-driving-confirm-title"
        >
          <div
            className={cn(
              'w-full max-w-[min(100%,32rem)] mx-auto rounded-2xl shadow-2xl border overflow-hidden',
              isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200/90 bg-white'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('p-5 sm:p-6', isDarkMode ? "bg-slate-900" : "bg-white")}>
              <h3
                id="add-manager-driving-confirm-title"
                className={cn('text-lg font-bold tracking-tight', isDarkMode ? "text-slate-100" : "text-slate-900")}
              >
                Add this manager driving entry?
              </h3>
              <p className={cn('mt-1 text-sm', isDarkMode ? "text-slate-400" : "text-slate-600")}>
                Check the summary below, then confirm to save it to the record.
              </p>
              <ul
                className={cn(
                  'mt-4 space-y-1.5 text-sm list-disc pl-5',
                  isDarkMode ? "text-slate-300" : "text-slate-700"
                )}
              >
                <li>
                  <span className="font-semibold">Date:</span>{' '}
                  {formatDateUK(mgrDrivingDate || getTodayDateInputValue())}
                </li>
                <li>
                  <span className="font-semibold">Driving time:</span> {mgrStartTime} – {mgrFinishTime} (
                  {formatHoursMinutes(mgrDurationPreview)})
                </li>
                <li>
                  <span className="font-semibold">Darktime (this entry):</span>{' '}
                  {formatHoursMinutes(
                    Math.min(
                      mgrDurationPreview,
                      coerceNonNegativeInt(mgrDarkDrivingHours) * 60 +
                        Math.min(59, coerceNonNegativeInt(mgrDarkDrivingMinutes === '' ? '0' : mgrDarkDrivingMinutes))
                    )
                  )}
                </li>
                <li>
                  <span className="font-semibold">Traction:</span> {mgrTraction}
                </li>
                <li>
                  <span className="font-semibold">Route:</span> {mgrDrivingRouteFrom.trim()} — {mgrDrivingRouteTo.trim()}
                </li>
                <li>
                  <span className="font-semibold">Train:</span>{' '}
                  {mgrTrainSource === 'feed' ? translateUi("Feed lookup") : translateUi("Manual")}
                  {mgrTrainServiceRef.trim() ? ` · ${mgrTrainServiceRef.trim()}` : ''}
                </li>
                {mgrDrivingNotes.trim() ? (
                  <li>
                    <span className="font-semibold">Notes:</span> {mgrDrivingNotes.trim()}
                  </li>
                ) : null}
              </ul>
            </div>
            <div
              className={cn(
                'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t px-4 py-3',
                isDarkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-100 bg-slate-50'
              )}
            >
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowAddManagerDrivingConfirmModal(false)}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold border transition-colors',
                  isDarkMode
                    ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                    : "border-slate-300 text-slate-800 hover:bg-slate-100",
                  saving && 'opacity-50 cursor-not-allowed'
                )}
              > Cancel </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void persistManagerDrivingSession()}
                className={cn(
                  'rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors',
                  saving ? 'bg-teal-400' : isDarkMode ? "bg-teal-500 hover:bg-teal-400" : "bg-teal-600 hover:bg-teal-700",
                  saving && 'cursor-wait'
                )}
              >
                {saving ? 'Saving…' : translateUi("Confirm and add")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const EmployeeProfile = ({
  employeeId, 
  initialEmployee,
  onBack, 
  onEdit, 
  onDelete, 
  currentUserTitle, 
  currentUserEmail,
  realActorEmail,
  currentUserProfile,
  companyName,
  companyAddress,
  companyLogoUrl,
  companyCode,
  isSystemAdmin,
  isVerificationModuleActive = false,
  canRunQAVerifications = false,
  isTaskModuleActive = false,
  isSafetyBriefsModuleActive = false,
  canManageSafetyBriefs = false,
  isDriverReportsModuleActive = false,
  canManageDriverReports = false,
  isLeaveAbsenceModuleActive = false,
  isMedicationChecksModuleActive = false,
  canEditMedicationChecks = false,
  canDeleteMedicationChecks = false,
  canSubmitMedicationChecks = false,
  isTraineeDriverModuleActive = false,
  canManageTraineeDriver = false,
  isDarkMode = false,
  onToggleDarkMode,
  onTransferComplete,
  onEmployeeIdChanged,
  isMobileOrTablet = false,
  isMobile = false,
  isTablet = false,
  onAssessmentSessionRestored,
  initialProfileTab,
  onProfileTabChange,
  onEmployeeNotFound,
  onEmployeeNotePosted,
  onEmployeePatched,
  canOverrideStuckAssessment = false,
  canAdminResetOpenAssessment = false,
  systemAdminClosedCycleEditWhileImpersonating = false,
  assessmentTenantImpersonationOk = false,
  canEditEmployeeRecord = false,
  canManageCompetencyAssessments = false,
  canManageContinuousCycles = false,
  canManageCompanyTasks = false,
  canAddExperienceRecords = false,
  canAccessCabPassesTab = false,
  cabPassesManagePermission = false,
  cabPassPermissionEdit = false,
  cabPassPermissionDelete = false,
  cabPassPermissionRevoke = false,
  cabPassPermissionRenew = false,
  cabPassCompanyAdminActions = false,
  canLockEmployeeProfile = false,
}: { 
  employeeId: string, 
  initialEmployee?: Employee | null,
  onBack: () => void, 
  onEdit: (emp: Employee) => void, 
  onDelete: (id: string) => void, 
  currentUserTitle?: string, 
  currentUserEmail?: string,
  /** Real signed-in user email — use for system-admin API actions while impersonating another user. */
  realActorEmail?: string,
  currentUserProfile?: SystemUser | null,
  companyName?: string;
  companyAddress?: string;
  companyLogoUrl?: string;
  companyCode?: string;
  isSystemAdmin?: boolean;
  onTransferComplete?: (newEmployee?: { id: string; company_code?: string }) => void;
  onEmployeeIdChanged?: (newId: string) => void;
  isMobileOrTablet?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
  onAssessmentSessionRestored?: () => void;
  initialProfileTab?: string;
  onProfileTabChange?: (tab: string) => void;
  onEmployeeNotFound?: () => void;
  /** Called after a successful employee PATCH so parent lists (e.g. Licencing) can update without a full reload. */
  onEmployeePatched?: (employee: Employee) => void;
  /** Called after a note is successfully posted (e.g. refresh notification bell). */
  onEmployeeNotePosted?: () => void;
  isVerificationModuleActive?: boolean;
  /** Permission gate for running employee verifications (qa-verifications.run). */
  canRunQAVerifications?: boolean;
  isTaskModuleActive?: boolean;
  isSafetyBriefsModuleActive?: boolean;
  /** Permission gate for adding/editing/deleting safety brief records (safety-briefs.manage). View-own does not need this. */
  canManageSafetyBriefs?: boolean;
  /** Driver Reports add-on active for the company (Operations tab). */
  isDriverReportsModuleActive?: boolean;
  /** Permission gate for submitting/editing driver reports. */
  canManageDriverReports?: boolean;
  /** Leave & Absence add-on active for the company. */
  isLeaveAbsenceModuleActive?: boolean;
  /** Medication Checks add-on active for the company. */
  isMedicationChecksModuleActive?: boolean;
  /** Permission gate for adding/editing medication checks (medication-checks.edit). */
  canEditMedicationChecks?: boolean;
  /** Permission gate for deleting medication checks (medication-checks.delete). */
  canDeleteMedicationChecks?: boolean;
  /** Permission gate for submitting own medication checks (medication-checks.submit). */
  canSubmitMedicationChecks?: boolean;
  /** Trainee Driver module active for the company (shows Performance & Support plans on Monitoring tab). */
  isTraineeDriverModuleActive?: boolean;
  /** Company admin, system admin, or trainee-driver.manage — can create/edit support plans. */
  canManageTraineeDriver?: boolean;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
  /** Real system admin or company administrator — can clear stuck “in progress” assessment lock */
  canOverrideStuckAssessment?: boolean;
  /** System or company admin: wipe open assessment activity back to not started. */
  canAdminResetOpenAssessment?: boolean;
  systemAdminClosedCycleEditWhileImpersonating?: boolean;
  /**
   * True when “View as” a company (not another user): real system admin or company admin may record assessments
   * for that tenant even if RBAC titles alone would be ambiguous.
   */
  assessmentTenantImpersonationOk?: boolean;
  canEditEmployeeRecord?: boolean;
  /** When true, user may record grades and complete assessment events (assessments.manage). */
  canManageCompetencyAssessments?: boolean;
  /** Role permission cycles.continuous — assign/activate/deactivate/continue continuous cycles. */
  canManageContinuousCycles?: boolean;
  /** Company admin, system admin, or tasks.manage — matches server rules for task reassignment. */
  canManageCompanyTasks?: boolean;
  /** Allows add-only roles to append experience records. */
  canAddExperienceRecords?: boolean;
  /** Combined: cab baseline + granular mutations — show Cab Passes tab. */
  canAccessCabPassesTab?: boolean;
  /** Role permission cab-passes.manage (baseline view / legacy assessing renew). */
  cabPassesManagePermission?: boolean;
  /** Granular role permissions — see Role permissions page. */
  cabPassPermissionEdit?: boolean;
  cabPassPermissionDelete?: boolean;
  cabPassPermissionRevoke?: boolean;
  cabPassPermissionRenew?: boolean;
  /** Company administrator or system administrator — full cab-pass actions without granular checks. */
  cabPassCompanyAdminActions?: boolean;
  /** Administrators, or a role granted employees.lock — may lock/unlock the profile for evidential purposes. */
  canLockEmployeeProfile?: boolean;
}) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  /** Effective company code: prefer prop, then initialEmployee (from list), then loaded employee. useMemo avoids TDZ. */
  const effectiveCompanyCode = useMemo(
    () => (companyCode || initialEmployee?.company_code || employee?.company_code) ?? '',
    [companyCode, initialEmployee?.company_code, employee?.company_code]
  );
  const cc = effectiveCompanyCode ? `?companyCode=${encodeURIComponent(effectiveCompanyCode)}` : '';
  const rc = effectiveCompanyCode ? `?companyCode=${encodeURIComponent(effectiveCompanyCode)}` : '';
  const companyHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    if (effectiveCompanyCode) headers['X-Company-Code'] = effectiveCompanyCode;
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    return headers;
  }, [effectiveCompanyCode, currentUserEmail]);
  const adminActorEmail = (realActorEmail || currentUserEmail || '').trim() || undefined;
  const cabPassGranularAny = Boolean(
    cabPassPermissionEdit ||
      cabPassPermissionDelete ||
      cabPassPermissionRevoke ||
      cabPassPermissionRenew
  );
  const cabPassMayPersistToServer = useMemo(() => {
    if (!canAccessCabPassesTab) return false;
    const mgr = String(employee?.manager ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const self = String(currentUserProfile?.full_name ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const assessingOk = mgr.length > 0 && self.length > 0 && mgr === self;
    return Boolean(cabPassCompanyAdminActions || assessingOk || cabPassGranularAny);
  }, [
    cabPassCompanyAdminActions,
    cabPassGranularAny,
    canAccessCabPassesTab,
    currentUserProfile?.full_name,
    employee?.manager,
  ]);
  const [loading, setLoading] = useState(true);
  /** Open OH recommendations with a due date — shown as amber companion to monitoring status. */
  const [hasOhFollowUpDueBadge, setHasOhFollowUpDueBadge] = useState(false);
  /** Set when API returns 403 or other error — do not call onEmployeeNotFound (avoids flashing back to list). */
  const [profileAccessError, setProfileAccessError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  /** When true the QA Verifications scan modal opens and runs against this employee. */
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [licenceExtracting, setLicenceExtracting] = useState(false);
  const [licenceExtractNotice, setLicenceExtractNotice] = useState<string | null>(null);
  /** Shown when the system moves/swaps a photo into the correct front/back holder. */
  const [licenceSideCorrectionNotice, setLicenceSideCorrectionNotice] = useState<string | null>(null);
  const [licenceCategoryInfo, setLicenceCategoryInfo] = useState<{ id: string; label: string; description: string } | null>(null);
  const [licenceScanVisualState, setLicenceScanVisualState] = useState<'scanning' | 'success' | 'partial'>('scanning');
  const [showLicenceScanningModal, setShowLicenceScanningModal] = useState(false);
  const [showLicenceComplianceModal, setShowLicenceComplianceModal] = useState(false);
  const [licencePhotoSuitability, setLicencePhotoSuitability] = useState<{
    side: "front" | "back";
    dataUrl: string;
    issues: string[];
    /** When set, user already cropped — "Use anyway" should submit this image. */
    croppedDataUrl?: string;
    field?: "licence_image_url" | "licence_back_image_url";
  } | null>(null);
  const [licenceScanSide, setLicenceScanSide] = useState<'front' | 'back'>('front');
  const [licenceComplianceSide, setLicenceComplianceSide] = useState<'front' | 'back'>('front');
  const [lastLicenceExtractionMissing, setLastLicenceExtractionMissing] = useState<string[]>([]);
  const [licenceScanRequiresConfirmation, setLicenceScanRequiresConfirmation] = useState(false);
  const [licenceScanDetailsConfirmed, setLicenceScanDetailsConfirmed] = useState(false);
  const [licenceFieldHighlight, setLicenceFieldHighlight] = useState<
    Partial<Record<LicenceFieldHighlightKey, 'success' | 'error'>>
  >({});
  const [licenceImageEdit, setLicenceImageEdit] = useState<{ dataUrl: string; field: 'licence_image_url' | 'licence_back_image_url'; rotation: number } | null>(null);
  const [licenceDropTarget, setLicenceDropTarget] = useState<'front' | 'back' | null>(null);
  const [licenceCropImageSize, setLicenceCropImageSize] = useState<{ w: number; h: number } | null>(null);
  const [licenceCropLeft, setLicenceCropLeft] = useState(0);
  const [licenceCropTop, setLicenceCropTop] = useState(0);
  const [licenceCropWidth, setLicenceCropWidth] = useState(1);
  const [licenceCropHeight, setLicenceCropHeight] = useState(1);
  const [licenceCropDragging, setLicenceCropDragging] = useState(false);
  type CropEdge = 'left' | 'right' | 'top' | 'bottom';
  const licenceCropDragRef = useRef<{
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    startWidth: number;
    startHeight: number;
    containerRect: DOMRect;
    edge: CropEdge;
  } | null>(null);
  const licenceCropContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!licenceCropDragging) return;
    const getXY = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
      if ('touches' in e && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if ('changedTouches' in e && e.changedTouches.length) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      const m = e as MouseEvent;
      return { x: m.clientX, y: m.clientY };
    };
    const onMove = (e: MouseEvent | TouchEvent) => {
      if ('touches' in e) e.preventDefault(); // keep page from scrolling while dragging crop
      const r = licenceCropDragRef.current;
      if (!r) return;
      const { x, y } = getXY(e);
      const dx = (x - r.startX) / r.containerRect.width;
      const dy = (y - r.startY) / r.containerRect.height;
      const min = LICENCE_CROP_MIN;
      switch (r.edge) {
        case 'left': {
          const newLeft = Math.max(0, Math.min(r.startLeft + r.startWidth - min, r.startLeft + dx));
          const newWidth = r.startLeft + r.startWidth - newLeft;
          setLicenceCropLeft(newLeft);
          setLicenceCropWidth(newWidth);
          break;
        }
        case 'right': {
          const newWidth = Math.max(min, Math.min(1 - r.startLeft, r.startWidth + dx));
          setLicenceCropWidth(newWidth);
          break;
        }
        case 'top': {
          const newTop = Math.max(0, Math.min(r.startTop + r.startHeight - min, r.startTop + dy));
          const newHeight = r.startTop + r.startHeight - newTop;
          setLicenceCropTop(newTop);
          setLicenceCropHeight(newHeight);
          break;
        }
        case 'bottom': {
          const newHeight = Math.max(min, Math.min(1 - r.startTop, r.startHeight + dy));
          setLicenceCropHeight(newHeight);
          break;
        }
      }
    };
    const onEnd = () => {
      setLicenceCropDragging(false);
      licenceCropDragRef.current = null;
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [licenceCropDragging]);
  const [activeTab, setActiveTab] = useState<EmployeeProfileTab>(() => {
    if (typeof window === 'undefined' || !employeeId) return 'Main';
    try {
      const key = `rv_employee_tab_${employeeId}`;
      const stored = sessionStorage.getItem(key);
      if (stored === 'Tasks') return 'Tasks';
      if (stored && (EMPLOYEE_PROFILE_TAB_OPTIONS as unknown as string[]).includes(stored)) return stored as EmployeeProfileTab;
    } catch {
      // Ignore
    }
    if (initialProfileTab === 'Tasks') return 'Tasks';
    if (initialProfileTab && (EMPLOYEE_PROFILE_TAB_OPTIONS as unknown as string[]).includes(initialProfileTab)) {
      return initialProfileTab as EmployeeProfileTab;
    }
    return 'Main';
  });
  const [mobileProfileNavOpen, setMobileProfileNavOpen] = useState(false);
  const [confirmState, setConfirmState] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  /** Conversation filter: active | all | specific conversation id */
  const [notesConversationFilter, setNotesConversationFilter] = useState<string>('active');
  const [noteConversationBusy, setNoteConversationBusy] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savingMainChanges, setSavingMainChanges] = useState(false);
  const [profileImageAction, setProfileImageAction] = useState<'uploading' | 'deleting' | null>(null);
  const [editForm, setEditForm] = useState<Partial<Employee>>({});
  /** Count of in-flight cab_passes_json PATCHes so we do not clobber optimistic edits when syncing from employee. */
  const pendingCabPassPatchRef = useRef(0);
  /** Last cab_passes_json we successfully sent in a PATCH — used to detect local edits that landed after that send. */
  const lastCommittedCabPassesJsonRef = useRef<string | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [assessingManagers, setAssessingManagers] = useState<any[]>([]);
  const [companyEmployeesForAssessors, setCompanyEmployeesForAssessors] = useState<Array<{ id: string; full_name: string; work_status?: string }>>([]);
  /** Company org chart (seniority tiers) used to gate assessment start with the derogation rule. */
  const [orgChart, setOrgChart] = useState<OrgChart | null>(null);
  /**
   * When the assessor's tier is the same as (or more junior than) the assessee's, we stage the
   * pending start here and surface the derogation dialog before actually calling PATCH start_assessment.
   */
  const [derogationDialog, setDerogationDialog] = useState<{
    mode: DerogationMode;
    eventId: number | string;
    eventName: string;
    selectedCriterionKeys?: string[];
    selectedElementKeys?: string[];
    assessorName: string;
    assessorRole: string;
    assessorTier: ResolvedTier | null;
    assesseeRole: string;
    assesseeTier: ResolvedTier | null;
    suggestedAssessors: SuggestedAssessor[];
  } | null>(null);
  const [derogationSubmitting, setDerogationSubmitting] = useState(false);
  const [derogationError, setDerogationError] = useState<string | null>(null);
  const [editingCompetencyId, setEditingCompetencyId] = useState<number | string | null>(null);
  const [competencyForm, setCompetencyForm] = useState<Partial<Competency>>({});
  const [isAddingTraction, setIsAddingTraction] = useState(false);
  const [isAddingRoute, setIsAddingRoute] = useState(false);
  const [newTractionForm, setNewTractionForm] = useState<Partial<Competency>>({});
  const [newRouteForm, setNewRouteForm] = useState<Partial<Competency>>({});
  const [isAddTractionModalOpen, setIsAddTractionModalOpen] = useState(false);
  const [isAddRouteModalOpen, setIsAddRouteModalOpen] = useState(false);
  const [companyTractionTypes, setCompanyTractionTypes] = useState<string[]>([]);
  const [companyRouteTypes, setCompanyRouteTypes] = useState<string[]>([]);
  const [companyDepots, setCompanyDepots] = useState<string[]>([]);
  const [companyAddressForProfile, setCompanyAddressForProfile] = useState("");
  const [companyJobRoles, setCompanyJobRoles] = useState<string[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [safetyBriefs, setSafetyBriefs] = useState<any[]>([]);
  const [safetyBriefsError, setSafetyBriefsError] = useState<string | null>(null);
  const [loadingSafetyBriefs, setLoadingSafetyBriefs] = useState(false);
  const [savingSafetyBrief, setSavingSafetyBrief] = useState(false);
  const [editingSafetyBriefId, setEditingSafetyBriefId] = useState<string | null>(null);
  const [safetyBriefTypes, setSafetyBriefTypes] = useState<
    Array<{ id: string; name: string; window_start: string; window_end: string; windowStatus?: string }>
  >([]);
  const [safetyBriefForm, setSafetyBriefForm] = useState({
    brief_type_id: '',
    brief_name: '',
    date: new Date().toISOString().slice(0, 10),
    delivery_type: 'in_person',
    provided_by: '',
    remote_method: '',
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [docView, setDocView] = useState<'grid' | 'list'>('grid');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isAddingTraining, setIsAddingTraining] = useState(false);
  const [trainingForm, setTrainingForm] = useState({ training_name: '', type: "Training", date_attended: '', expiry_date: '', provider: '', status: "Completed" });
  const [submittingTraining, setSubmittingTraining] = useState(false);
  const [viewingTrainingRecord, setViewingTrainingRecord] = useState<any | null>(null);
  const [editingTrainingRecord, setEditingTrainingRecord] = useState<any | null>(null);
  const [trainingCertUploadingId, setTrainingCertUploadingId] = useState<number | null>(null);
  const trainingCertInputRef = React.useRef<HTMLInputElement>(null);
  const trainingCertRecordIdRef = React.useRef<number | null>(null);
  const [isAddingQualification, setIsAddingQualification] = useState(false);
  const [editingQualification, setEditingQualification] = useState<any | null>(null);
  const [qualificationForm, setQualificationForm] = useState({ name: '', certificate_number: '', qualification_type: '', date_obtained: '', expiry_date: '', related_roles: [] as string[] });
  const [qualificationCertFile, setQualificationCertFile] = useState<File | null>(null);
  const [submittingQualification, setSubmittingQualification] = useState(false);
  const [viewingQualification, setViewingQualification] = useState<any | null>(null);
  const [qualificationCertUploadingId, setQualificationCertUploadingId] = useState<number | string | null>(null);
  const [qualificationCertViewingId, setQualificationCertViewingId] = useState<number | string | null>(null);
  const qualificationCertInputRef = React.useRef<HTMLInputElement>(null);
  const qualificationCertRecordIdRef = React.useRef<number | string | null>(null);
  const qualificationModalCertInputRef = React.useRef<HTMLInputElement>(null);
  const openQualificationCertificate = useCallback(async (docId: string | number) => {
    setQualificationCertViewingId(docId);
    try {
      const res = await fetch(`/api/documents/${encodeURIComponent(String(docId))}/download${rc}`, { headers: companyHeaders });
      const data = await res.json().catch(() => ({} as { error?: string; content?: string; name?: string }));
      if (!res.ok) throw new Error((data as { error?: string }).error || `Failed to open certificate (${res.status})`);
      const fileName = String((data as { name?: string }).name || 'certificate.pdf');
      const dataUrl = toAttachmentDataUrl((data as { content?: string }).content, fileName);
      if (!dataUrl) throw new Error('Certificate content was empty');
      const { url } = dataUrlToPreviewObjectUrl(dataUrl, fileName);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    } catch (err: any) {
      console.error('Unable to open qualification certificate:', err);
      alert(err?.message || translateUi('Unable to open this certificate.'));
    } finally {
      setQualificationCertViewingId(null);
    }
  }, [companyHeaders, rc]);

  const resetQualificationForm = useCallback(() => {
    setQualificationForm({ name: '', certificate_number: '', qualification_type: '', date_obtained: '', expiry_date: '', related_roles: [] });
    setQualificationCertFile(null);
  }, []);

  const [employeeCycles, setEmployeeCycles] = useState<
    {
      id: number | string;
      cycle_id: number | string;
      cycle_title: string;
      start_date: string;
      expiry_date: string;
      expiry_years: number | null;
      framework_cycle_id?: string | null;
      is_continuous?: boolean;
      renewal_count?: number;
      last_renewed_at?: string | null;
      end_reason?: string | null;
      ended_at?: string | null;
      closed_by?: string | null;
      closed_by_name?: string | null;
      source_employee_cycle_id?: string | null;
      archive_summary?: {
        assessed_criteria_count: number;
        attachment_count: number;
        grade_buckets: { review: number; advice: number; not_applicable: number; satisfactory: number };
      } | null;
      // Live-only fields (derived from employee_cycle_events in RTDB)
      completed?: boolean;
      completed_at?: string | null;
    }[]
  >([]);
  const [availableCycles, setAvailableCycles] = useState<{ id: number | string; title: string; expiry_years: number; expiry_months?: number | null; company_code?: string | null; framework_cycle_id?: string | null }[]>([]);
  const [selectedCycleToAssign, setSelectedCycleToAssign] = useState<number | string | null>(null);
  const [assignStartDate, setAssignStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [assignContinuous, setAssignContinuous] = useState(false);
  const [assigningCycle, setAssigningCycle] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferTargetCode, setTransferTargetCode] = useState('');
  const [transferNewEmail, setTransferNewEmail] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [closingEmployeeCycleId, setClosingEmployeeCycleId] = useState<number | string | null>(null);
  const [closeCycleDialog, setCloseCycleDialog] = useState<{
    ecId: number | string;
    cycleTitle: string;
    variant?: 'manual' | 'post_assessment';
  } | null>(null);
  const [continuingEmployeeCycleId, setContinuingEmployeeCycleId] = useState<number | string | null>(null);
  const [continueCycleDialog, setContinueCycleDialog] = useState<{
    ecId: number | string;
    cycleTitle: string;
    variant?: 'manual' | 'post_assessment';
    startFromToday?: boolean;
  } | null>(null);
  const [assignCycleFeedback, setAssignCycleFeedback] = useState<string | null>(null);
  const [deletingClosedCycleId, setDeletingClosedCycleId] = useState<number | string | null>(null);
  const [viewingCycle, setViewingCycle] = useState<{
    id: number | string;
    cycle_id: number | string;
    cycle_title: string;
    start_date: string;
    expiry_date: string;
    framework_cycle_id?: string | null;
    /** Live continuous assignment — after last assessment, offer renew from today. */
    is_continuous?: boolean;
    /** Original live cycle id for Azure evidence paths when viewing closed-cycle history */
    source_employee_cycle_id?: string | number | null;
    is_read_only_history?: boolean;
    /** Snapshot from cycles list API for archived row; used for summary pane on assessment record */
    archive_summary?: ClosedCycleArchiveSummary | null;
  } | null>(() => {
    if (typeof window === 'undefined' || !employeeId) return null;
    try {
      const raw = sessionStorage.getItem(`rv_employee_viewing_cycle_${employeeId}`);
      if (!raw) return null;
      const saved = JSON.parse(raw) as {
        id?: number | string;
        cycle_id?: number | string;
        cycle_title?: string;
        start_date?: string;
        expiry_date?: string;
        framework_cycle_id?: string | null;
        is_continuous?: boolean;
        source_employee_cycle_id?: string | number | null;
        is_read_only_history?: boolean;
        archive_summary?: ClosedCycleArchiveSummary | null;
      };
      if (!saved?.id || !saved?.cycle_id || !saved?.cycle_title || !saved?.start_date || !saved?.expiry_date) return null;
      return {
        id: saved.id,
        cycle_id: saved.cycle_id,
        cycle_title: saved.cycle_title,
        start_date: saved.start_date,
        expiry_date: saved.expiry_date,
        framework_cycle_id: saved.framework_cycle_id,
        is_continuous: !!saved.is_continuous,
        source_employee_cycle_id: saved.source_employee_cycle_id,
        is_read_only_history: saved.is_read_only_history,
        archive_summary: saved.archive_summary,
      };
    } catch {
      return null;
    }
  });
  const [cycleDetailCriteria, setCycleDetailCriteria] = useState<CustomCycleCriterionRow[] | null>(null);
  const [employeeCycleEvents, setEmployeeCycleEvents] = useState<EmployeeCycleEventSummary[]>([]);
  const [employeeCycleEventsLoadStatus, setEmployeeCycleEventsLoadStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [openingAssessmentEventId, setOpeningAssessmentEventId] = useState<number | string | null>(null);
  const cycleEventsFetchSeqRef = useRef(0);
  const prevViewingCycleIdRef = useRef<number | string | null>(null);
  const [cycleCriteriaCoverage, setCycleCriteriaCoverage] = useState<{ covered: number; total: number } | null>(null);
  const [closedCycleCriterionAssessments, setClosedCycleCriterionAssessments] = useState<Record<
    string,
    ClosedCycleCriterionAssessmentSnapshot
  > | null>(null);
  const [closedCycleSummaryRefreshVersion, setClosedCycleSummaryRefreshVersion] = useState(0);
  const [pendingClosedCycleCriterionFocusKey, setPendingClosedCycleCriterionFocusKey] = useState<string | null>(null);
  const [closedCycleCriterionAssessmentsStatus, setClosedCycleCriterionAssessmentsStatus] = useState<
    'idle' | 'loading' | 'done' | 'error'
  >('idle');
  const [activeEventId, setActiveEventId] = useState<number | string | null>(null);
  const [eventAssessmentModalId, setEventAssessmentModalId] = useState<number | string | null>(null);
  /** Avoid circular hook deps: openAssessmentModalForEvent may start the assessment when opened. */
  const canMutateAssessmentsRef = useRef(false);
  const startAssessmentForEventRef = useRef<(eventId: number | string, selectedCriterionKeys?: string[], selectedElementKeys?: string[]) => void>(() => {});
  const invalidateAssessmentScheduleRef = useRef<() => void>(() => {});
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);
  const [monitoringEventNotesSaving, setMonitoringEventNotesSaving] = useState(false);
  const [monitoringCloseNowByEventId, setMonitoringCloseNowByEventId] = useState<Record<string, boolean>>({});
  const [monitoringCompletedDateByEventId, setMonitoringCompletedDateByEventId] = useState<Record<string, string>>({});
  const [amendmentUpdating, setAmendmentUpdating] = useState(false);
  const [amendmentSaved, setAmendmentSaved] = useState(false);
  const [frameworkAssessmentEligibleToComplete, setFrameworkAssessmentEligibleToComplete] = useState(false);
  const [editRestrictionInfoEventId, setEditRestrictionInfoEventId] = useState<string | number | null>(null);
  const [assessmentLockedByUser, setAssessmentLockedByUser] = useState<{ eventName: string; startedBy: string } | null>(null);
  const [assessmentBlockedPriorIncomplete, setAssessmentBlockedPriorIncomplete] = useState<{ eventName: string; priorEventName: string } | null>(null);
  const [storageQuotaOpenAssessmentDialogOpen, setStorageQuotaOpenAssessmentDialogOpen] = useState(false);
  const [pendingOpenAssessmentEventId, setPendingOpenAssessmentEventId] = useState<number | string | null>(null);
  const [amendmentInfoEventId, setAmendmentInfoEventId] = useState<string | number | null>(null);
  const frameworkSaveBeforeAmendmentRef = React.useRef<(() => Promise<void>) | null>(null);
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);
  const [criteriaPopupExpanded, setCriteriaPopupExpanded] = useState<Record<string, boolean>>({});
  const [pendingRestoreAssessmentEventId, setPendingRestoreAssessmentEventId] = useState<number | string | null>(null);

  // When switching to a different employee, restore that employee's last tab from storage
  const prevEmployeeIdRef = React.useRef(employeeId);
  useEffect(() => {
    if (prevEmployeeIdRef.current === employeeId) return;
    prevEmployeeIdRef.current = employeeId;
    if (!employeeId) return;
    try {
      const key = `rv_employee_tab_${employeeId}`;
      const stored = sessionStorage.getItem(key);
      if (stored === 'Tasks') {
        setActiveTab('Tasks');
      } else if (stored && (EMPLOYEE_PROFILE_TAB_OPTIONS as unknown as string[]).includes(stored)) {
        setActiveTab(stored as EmployeeProfileTab);
      } else {
        setActiveTab('Main');
      }
    } catch {
      setActiveTab('Main');
    }
  }, [employeeId]);

  // Persist current tab and notify App so refresh keeps user on same tab (e.g. Cycles)
  useEffect(() => {
    if (!employeeId) return;
    try {
      sessionStorage.setItem(`rv_employee_tab_${employeeId}`, activeTab);
    } catch {
      // Ignore storage errors
    }
    onProfileTabChange?.(activeTab);
  }, [employeeId, activeTab, onProfileTabChange]);

  // Scroll to a specific note when opened from notification (rv_highlight_note_<employeeId> in sessionStorage)
  useEffect(() => {
    if (activeTab !== "Notes" || !employeeId) return;
    const notes = employee?.notes;
    if (!notes?.length) return;
    let ref: string | null = null;
    try {
      ref = sessionStorage.getItem(`rv_highlight_note_${employeeId}`);
    } catch {
      return;
    }
    if (!ref) return;
    const hasNote = notes.some((n: { id?: unknown }) => String(n.id) === String(ref));
    if (!hasNote) return;
    try {
      sessionStorage.removeItem(`rv_highlight_note_${employeeId}`);
    } catch {
      /* ignore */
    }
    const t = window.setTimeout(() => {
      const el = document.getElementById(`rv-employee-note-${ref}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.classList.add("ring-2", "ring-indigo-400", "ring-offset-2");
      window.setTimeout(() => el?.classList.remove("ring-2", "ring-indigo-400", "ring-offset-2"), 2500);
    }, 150);
    return () => clearTimeout(t);
  }, [activeTab, employeeId, employee?.notes]);

  // Persist currently opened cycle detail (assessment events schedule view) per employee
  useEffect(() => {
    if (!employeeId) return;
    try {
      const key = `rv_employee_viewing_cycle_${employeeId}`;
      if (viewingCycle) {
        sessionStorage.setItem(key, JSON.stringify(viewingCycle));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore storage errors
    }
  }, [employeeId, viewingCycle]);

  const isAssessmentEditRestricted = useCallback((ev: EmployeeCycleEventSummary) => {
    if (ev.status !== 'completed') return false;
    const completedBy = (ev.assessment_completed_by || ev.completed_by || '').trim().toLowerCase();
    if (!completedBy) return false;
    const currentEmail = (currentUserEmail || '').trim().toLowerCase();
    const isCompleter = completedBy && currentEmail && completedBy === currentEmail;
    const userIsAdmin = currentUserTitle?.toLowerCase()?.includes('admin') || currentUserEmail?.toLowerCase() === 'admin@railvault.com' || currentUserEmail?.toLowerCase() === 'andyuk38@gmail.com';
    return !isCompleter && !userIsAdmin;
  }, [currentUserEmail, currentUserTitle]);

  const exitAssessmentFullscreen = useCallback(() => {
    try {
      const doc = document as Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => Promise<void> | void };
      if (doc.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
        return;
      }
      if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) doc.webkitExitFullscreen();
    } catch {
      // Best effort only.
    }
  }, []);

  const openAssessmentModalForEvent = useCallback((eventId: number | string) => {
    const idStr = String(eventId).trim();
    if (!idStr || idStr === 'undefined' || idStr === 'null') return;
    const evIndex = employeeCycleEvents.findIndex((e) => String(e.id).trim() === idStr);
    const ev = evIndex >= 0 ? employeeCycleEvents[evIndex] : undefined;
    if (ev && ev.status !== 'completed' && evIndex > 0) {
      const priorEv = employeeCycleEvents[evIndex - 1];
      if (priorEv && priorEv.status !== 'completed') {
        setAssessmentBlockedPriorIncomplete({ eventName: ev.event_name, priorEventName: priorEv.event_name });
        return;
      }
    }
    setAssessmentBlockedPriorIncomplete(null);
    if (ev && ev.assessment_started_at && ev.status !== 'completed') {
      const starterEmail = (ev.assessment_started_by || '').trim().toLowerCase();
      const currentEmail = (currentUserEmail || '').trim().toLowerCase();
      if (starterEmail) {
        if (!currentEmail || starterEmail !== currentEmail) {
          setAssessmentLockedByUser({
            eventName: ev.event_name,
            startedBy: (ev.assessment_started_by_name || '').trim() || ev.assessment_started_by || 'Another assessor',
          });
          return;
        }
      }
    }
    setAssessmentLockedByUser(null);
    /** Preserve Azure / RTDB string ids (e.g. -Nx…); Number() would be NaN and blocked the modal. */
    const modalId: number | string = ev?.id != null && String(ev.id).trim() !== '' ? ev.id : idStr;
    const canMutateOpen = canMutateAssessmentsRef.current;
    const isMonitoringOpen =
      String(ev?.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
    const todayYmd = new Date().toISOString().slice(0, 10);
    const needsEarlyReasons =
      !!ev &&
      !isMonitoringOpen &&
      String(ev.window_open ?? '') > todayYmd &&
      !String(ev.early_assessment_reasons ?? '').trim();
    const needsLateReasons =
      !!ev &&
      !isMonitoringOpen &&
      String(ev.window_close ?? '') < todayYmd &&
      ev.status !== 'completed' &&
      !String(ev.late_assessment_reasons ?? '').trim();
    // Early/late reason prompts must not open or start the assessment — Cancel should leave it untouched.
    const canCommitOpenAndStart = !needsEarlyReasons && !needsLateReasons;
    if (
      canMutateOpen &&
      canCommitOpenAndStart &&
      ev &&
      ev.status !== 'completed' &&
      !isMonitoringOpen &&
      !ev.assessment_opened_at &&
      !ev.assessment_started_at
    ) {
      const openedAt = new Date().toISOString();
      const openerName = String(currentUserProfile?.full_name ?? currentUserTitle ?? '').trim();
      setEmployeeCycleEvents((prev) =>
        prev.map((event) =>
          String(event.id) === idStr
            ? {
                ...event,
                assessment_opened_at: openedAt,
                assessment_opened_by: currentUserEmail || null,
                assessment_opened_by_name: openerName || null,
              }
            : event
        )
      );
      void fetch(`/api/employee-cycle-events/${modalId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}) },
        body: JSON.stringify({ record_assessment_open: true, userEmail: currentUserEmail }),
      }).catch(() => {});
    }
    setActiveEventId(modalId);
    setFrameworkAssessmentEligibleToComplete(false);
    setEventAssessmentModalId(modalId);
    // Desktop (and mobile): marking open alone is not "in progress" — start now so the schedule
    // shows STILL IN PROGRESS. Seniority rules may prompt for a derogation before start commits.
    // Skip when early/late reason gates are showing (start after Proceed instead).
    if (
      canMutateOpen &&
      canCommitOpenAndStart &&
      ev &&
      ev.status !== 'completed' &&
      !isMonitoringOpen &&
      !ev.assessment_started_at
    ) {
      startAssessmentForEventRef.current(modalId);
    }
    try {
      if (viewingCycle) {
        sessionStorage.setItem(
          ASSESSMENT_RESUME_STORAGE_KEY,
          JSON.stringify({
            employeeId,
            companyCode: effectiveCompanyCode || null,
            viewingCycle,
            eventId: modalId,
            savedAt: new Date().toISOString(),
          })
        );
      }
    } catch {}
  }, [viewingCycle, employeeId, effectiveCompanyCode, employeeCycleEvents, currentUserEmail, currentUserProfile?.full_name, currentUserTitle]);

  const closeAssessmentModalForEvent = useCallback((eventId: number | string) => {
    setFrameworkAssessmentEligibleToComplete(false);
    setEventAssessmentModalId(null);
    setPendingClosedCycleCriterionFocusKey(null);
    exitAssessmentFullscreen();
    invalidateAssessmentScheduleRef.current();
    try { sessionStorage.removeItem(ASSESSMENT_RESUME_STORAGE_KEY); } catch {}
  }, [exitAssessmentFullscreen]);

  const jumpToClosedCycleAssessmentCriterion = useCallback((eventId: number | string, criterionKey: string) => {
    flushSync(() => {
      setPendingClosedCycleCriterionFocusKey(criterionKey);
    });
    openAssessmentModalForEvent(eventId);
  }, [openAssessmentModalForEvent]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ASSESSMENT_RESUME_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        employeeId?: string;
        viewingCycle?: { id: number; cycle_id: number; cycle_title: string; start_date: string; expiry_date: string; framework_cycle_id?: string };
        eventId?: number;
      };
      if (!saved || saved.employeeId !== employeeId || !saved.viewingCycle || !saved.eventId) return;
      setActiveTab('Cycles');
      setViewingCycle(saved.viewingCycle);
      setPendingRestoreAssessmentEventId(saved.eventId);
    } catch {}
  }, [employeeId]);

  useEffect(() => {
    if (!pendingRestoreAssessmentEventId || !viewingCycle || employeeCycleEvents.length === 0 || eventAssessmentModalId) return;
    const exists = employeeCycleEvents.some((ev) => String(ev.id) === String(pendingRestoreAssessmentEventId));
    if (!exists) {
      try { sessionStorage.removeItem(ASSESSMENT_RESUME_STORAGE_KEY); } catch {}
      setPendingRestoreAssessmentEventId(null);
      onAssessmentSessionRestored?.();
      return;
    }
    openAssessmentModalForEvent(pendingRestoreAssessmentEventId);
    setPendingRestoreAssessmentEventId(null);
    onAssessmentSessionRestored?.();
  }, [pendingRestoreAssessmentEventId, viewingCycle, employeeCycleEvents, eventAssessmentModalId, openAssessmentModalForEvent, onAssessmentSessionRestored]);

  useEffect(() => {
    if (eventAssessmentModalId == null) return;
    const modalEvent = employeeCycleEvents.find((event) => String(event.id) === String(eventAssessmentModalId));
    if (!modalEvent) return;
    const isMonitoringEvent = String(modalEvent.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
    if (!isMonitoringEvent || modalEvent.status === 'completed') return;
    const key = String(modalEvent.id);
    setMonitoringCloseNowByEventId((prev) => (key in prev ? prev : { ...prev, [key]: true }));
    setMonitoringCompletedDateByEventId((prev) =>
      key in prev ? prev : { ...prev, [key]: new Date().toISOString().slice(0, 10) }
    );
  }, [eventAssessmentModalId, employeeCycleEvents]);

  const tabs: EmployeeProfileTab[] = useMemo(() => {
    const base = [...EMPLOYEE_PROFILE_TAB_OPTIONS] as EmployeeProfileTab[];
    if (!canAccessCabPassesTab) {
      const idx = base.indexOf(translateUi("Cab Passes"));
      if (idx >= 0) base.splice(idx, 1);
    }
    if (!isSafetyBriefsModuleActive) {
      const idx = base.indexOf(translateUi("Safety Briefs"));
      if (idx >= 0) base.splice(idx, 1);
    }
    if (!isDriverReportsModuleActive) {
      const idx = base.indexOf(translateUi("Driver Reports"));
      if (idx >= 0) base.splice(idx, 1);
    }
    if (!isLeaveAbsenceModuleActive) {
      const idx = base.indexOf(translateUi("Leave & Absence"));
      if (idx >= 0) base.splice(idx, 1);
    }
    if (!isMedicationChecksModuleActive) {
      const idx = base.indexOf(translateUi("Medication Checks"));
      if (idx >= 0) base.splice(idx, 1);
    }
    if (isTaskModuleActive) {
      const idx = base.indexOf(translateUi("Notes"));
      base.splice(idx, 0, translateUi("Tasks"));
    }
    return base;
  }, [canAccessCabPassesTab, isTaskModuleActive, isSafetyBriefsModuleActive, isDriverReportsModuleActive, isLeaveAbsenceModuleActive, isMedicationChecksModuleActive]);

  useEffect(() => {
    if (activeTab === "Cab Passes" && !canAccessCabPassesTab) setActiveTab("Main");
  }, [activeTab, canAccessCabPassesTab]);
  const handleProfileTabSelect = useCallback((tab: EmployeeProfileTab, closeMobileMenu = false) => {
    setActiveTab(tab);
    if (closeMobileMenu) setMobileProfileNavOpen(false);
    try {
      sessionStorage.setItem(`rv_employee_tab_${employeeId}`, tab);
    } catch {}
    requestAnimationFrame(() => {
      document.querySelectorAll<HTMLElement>(".rv-main-content").forEach((el) => {
        el.scrollLeft = 0;
      });
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    });
  }, [employeeId]);

  useEffect(() => {
    requestAnimationFrame(() => {
      document.querySelectorAll<HTMLElement>(".rv-main-content").forEach((el) => {
        el.scrollLeft = 0;
      });
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'Tasks' && !isTaskModuleActive) setActiveTab('Main');
  }, [isTaskModuleActive, activeTab]);

  useEffect(() => {
    if (activeTab === 'Safety Briefs' && !isSafetyBriefsModuleActive) setActiveTab('Main');
  }, [isSafetyBriefsModuleActive, activeTab]);
  useEffect(() => {
    if (activeTab === 'Driver Reports' && !isDriverReportsModuleActive) setActiveTab('Main');
  }, [isDriverReportsModuleActive, activeTab]);
  useEffect(() => {
    if (activeTab === 'Leave & Absence' && !isLeaveAbsenceModuleActive) setActiveTab('Main');
  }, [isLeaveAbsenceModuleActive, activeTab]);
  useEffect(() => {
    if (activeTab === 'Medication Checks' && !isMedicationChecksModuleActive) setActiveTab('Main');
  }, [isMedicationChecksModuleActive, activeTab]);
  useEffect(() => {
    if (!isMobileOrTablet && mobileProfileNavOpen) {
      setMobileProfileNavOpen(false);
    }
  }, [isMobileOrTablet, mobileProfileNavOpen]);

  const workStatusOptions = [...ADDITIONAL_ROLE_OPTIONS];
  const qualificationRoleOptions = ['Assessing', 'Verification', 'Mentoring', 'Training Delivery'] as const;
  const monitoringStatusOptions = [
    { label: 'Off Track', class: 'bg-red-100 text-red-800' },
    { label: 'Awaiting Med Check', class: 'bg-violet-100 text-violet-700' },
    { label: 'OH follow-up due', class: 'bg-amber-100 text-amber-800' },
    { label: 'no issues', class: 'bg-emerald-100 text-emerald-700 status-chip-positive' },
    { label: 'On additional support', class: 'bg-blue-100 text-blue-700' },
    { label: 'Relieve end of journey unless SPAD', class: 'bg-amber-100 text-amber-700' },
    { label: 'On additional monitoring', class: 'bg-sky-100 text-sky-800' },
    { label: 'Relieve upon incident', class: 'bg-red-600 text-white' },
  ];

  const assessorEligibleManagers = useMemo(
    () => buildAssessorOptions(assessingManagers, companyEmployeesForAssessors, employee?.full_name),
    [assessingManagers, companyEmployeesForAssessors, employee?.full_name],
  );

  const displayedMonitoringStatus = isEditing
    ? (editForm.monitoring_status ?? employee?.monitoring_status ?? 'no issues')
    : (employee?.monitoring_status ?? 'no issues');

  // Companion amber badge when open OH recommendations have a due date (alongside restored base status).
  useEffect(() => {
    if (!isMedicationChecksModuleActive || !employeeId) {
      setHasOhFollowUpDueBadge(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const params = new URLSearchParams();
        if (effectiveCompanyCode) params.set('companyCode', effectiveCompanyCode);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(`/api/employees/${employeeId}/medication_oh_recommendations${qs}`, {
          headers: {
            ...(effectiveCompanyCode ? { 'X-Company-Code': effectiveCompanyCode } : {}),
            ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;
        const rows = Array.isArray(data.recommendations) ? data.recommendations : [];
        const hasDue = rows.some((r: { status?: string; due_date?: string | null }) => {
          const s = String(r.status || 'open').toLowerCase();
          if (s !== 'open' && s !== 'in_progress') return false;
          return !!String(r.due_date || '').trim();
        });
        setHasOhFollowUpDueBadge(hasDue);
      } catch {
        if (!cancelled) setHasOhFollowUpDueBadge(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    employeeId,
    effectiveCompanyCode,
    currentUserEmail,
    isMedicationChecksModuleActive,
    employee?.monitoring_status,
    employee?.medication_declarations,
  ]);

  const showOhFollowUpDueCompanion =
    hasOhFollowUpDueBadge && displayedMonitoringStatus !== 'OH follow-up due';

  const selectedAssessingManager = (editForm.manager ?? employee?.manager ?? '').trim();
  const assessingManagerSelfSelected = Boolean(
    employee?.full_name && isSamePersonName(selectedAssessingManager, employee.full_name),
  );

  const isAdmin = currentUserTitle?.toLowerCase()?.includes('admin') || currentUserEmail?.toLowerCase() === 'admin@railvault.com' || currentUserEmail?.toLowerCase() === 'andyuk38@gmail.com';
  const titleLowerForDocs = (currentUserTitle || '').trim().toLowerCase();
  const isManager =
    titleLowerForDocs.includes('manager') ||
    titleLowerForDocs.includes('supervisor') ||
    titleLowerForDocs.includes('team lead') ||
    titleLowerForDocs.endsWith(' lead') ||
    titleLowerForDocs.includes(' line lead');
  const roleOk = allowsOperationalMutations(currentUserProfile);
  const legacyCanEdit = isAdmin || isManager;
  /**
   * Evidential lock. Held locally so the header reflects a lock/unlock immediately; the server is the
   * authority and rejects writes to a locked profile with HTTP 423 regardless of this flag.
   */
  const [profileLockOverride, setProfileLockOverride] = useState<EmployeeLockState | null>(null);
  const profileLock = useMemo(
    () => profileLockOverride ?? readEmployeeLockFromRecord(employee),
    [profileLockOverride, employee],
  );
  useEffect(() => {
    setProfileLockOverride(null);
  }, [employeeId]);
  const profileLocked = profileLock.locked;
  const canEdit = roleOk && (canEditEmployeeRecord || legacyCanEdit) && !profileLocked;
  /** Document upload/delete: managers & admins even when system access is Read Only. Everyone else can view/download only. */
  const canUploadDocuments =
    !profileLocked && (Boolean(isSystemAdmin) || isAdmin || isManager || canEditEmployeeRecord);
  const employeeCycleRowForView = React.useMemo(
    () =>
      viewingCycle ? employeeCycles.find((ec) => String(ec.id) === String(viewingCycle.id)) : undefined,
    [employeeCycles, viewingCycle]
  );
  const viewingCycleIsContinuous = !!(
    viewingCycle?.is_continuous ?? employeeCycleRowForView?.is_continuous
  );
  const viewingCycleRenewalCount = Number(employeeCycleRowForView?.renewal_count ?? 0);
  /** Client-side checklist mirroring server/cycleContinuation.ts gates (for assessment-record helper). */
  const cycleContinuationChecklist = React.useMemo(() => {
    if (!viewingCycle || viewingCycle.is_read_only_history || employeeCycleRowForView?.end_reason) {
      return null;
    }
    const todayYmd = new Date().toISOString().slice(0, 10);
    const assessmentEvents = employeeCycleEvents.filter(
      (ev) => String(ev.event_type ?? "assessment").trim().toLowerCase() !== "monitoring",
    );
    const assessmentTotal = assessmentEvents.length;
    const assessmentCompleted = assessmentEvents.filter(
      (ev) => String(ev.status ?? "").trim().toLowerCase() === "completed",
    ).length;
    const assessmentsDone = assessmentTotal > 0 && assessmentCompleted === assessmentTotal;
    const openMonitoringCount = employeeCycleEvents.filter((ev) => {
      const et = String(ev.event_type ?? "").trim().toLowerCase();
      return et === "monitoring" && String(ev.status ?? "").trim().toLowerCase() !== "completed";
    }).length;

    const medicals = employee?.medicals ?? [];
    const toBoolLatest = (v: unknown) => v === 1 || v === "1" || v === true;
    const latestMed =
      medicals.find((m) => toBoolLatest(m?.is_latest)) ??
      [...medicals].sort((a, b) =>
        String(b.date_completed ?? "").localeCompare(String(a.date_completed ?? "")),
      )[0] ??
      null;
    const medStatus = String(latestMed?.status ?? "").trim();
    const medExpiry = String(latestMed?.expiry_date ?? "").trim();
    const medIsF1F2 = /^F[12]$/i.test(medStatus);
    const medicalLive = !!latestMed && medIsF1F2 && !!medExpiry && medExpiry >= todayYmd;

    let medicalLabel = translateUi("Live medical at Fit (F1) or Fit with restrictions (F2)");
    if (!latestMed) {
      medicalLabel = translateUi("Record a medical certificate (Fit (F1) or Fit with restrictions (F2))");
    } else if (!medIsF1F2) {
      medicalLabel = translateUi("Latest medical must be Fit (F1) or Fit with restrictions (F2)");
    } else if (!medExpiry || medExpiry < todayYmd) {
      medicalLabel = translateUi("Medical certificate must not be expired");
    } else {
      medicalLabel = translateUi("Live medical at Fit (F1) or Fit with restrictions (F2)");
    }

    const items: { id: string; ok: boolean; label: string }[] = [
      {
        id: "continuous",
        ok: viewingCycleIsContinuous,
        label: viewingCycleIsContinuous
          ? translateUi("Assessment event is marked continuous (set to continue)")
          : translateUi("Mark the assessment event as continuous (set to continue)"),
      },
      {
        id: "assessments",
        ok: assessmentsDone,
        label:
          assessmentTotal === 0
            ? translateUi("Cycle needs at least one assessment event")
            : assessmentsDone
              ? translateUi(`All assessment events completed (${assessmentCompleted}/${assessmentTotal})`)
              : translateUi(
                  `Complete every assessment event (${assessmentCompleted}/${assessmentTotal} done)`,
                ),
      },
      {
        id: "medical",
        ok: medicalLive,
        label: medicalLabel,
      },
    ];

    return {
      items,
      eligible: items.every((i) => i.ok),
      openMonitoringCount,
    };
  }, [
    viewingCycle,
    employeeCycleRowForView?.end_reason,
    employeeCycleEvents,
    employee?.medicals,
    viewingCycleIsContinuous,
  ]);
  const isViewingClosedCycleHistory =
    viewingCycle != null &&
    (viewingCycle.is_read_only_history === true || !!employeeCycleRowForView?.end_reason);
  /** Company impersonation path, or global system-admin employee view (not company-only Administrator). */
  const closedCycleSystemAdminOverride =
    !!systemAdminClosedCycleEditWhileImpersonating || !!isSystemAdmin;
  const displayArchiveSummary = useMemo((): ClosedCycleArchiveSummary | null => {
    return employeeCycleRowForView?.archive_summary ?? viewingCycle?.archive_summary ?? null;
  }, [viewingCycle?.archive_summary, employeeCycleRowForView?.archive_summary]);
  /** Prefer live criterion map for grade mix so fail/unsafe/custom labels match "recorded grades by label". */
  const closedCycleDisplayArchiveSummary = useMemo((): ClosedCycleArchiveSummary | null => {
    const base = displayArchiveSummary;
    if (!isViewingClosedCycleHistory || !closedCycleCriterionAssessments || closedCycleCriterionAssessmentsStatus !== 'done') {
      return base;
    }
    const buckets = { review: 0, advice: 0, not_applicable: 0, satisfactory: 0 };
    let assessed = 0;
    for (const v of Object.values(closedCycleCriterionAssessments)) {
      const g = v?.grade;
      if (!isCriterionGradeRecorded(g)) continue;
      assessed++;
      buckets[classifyGradeForArchiveBucket(String(g).trim())]++;
    }
    return {
      assessed_criteria_count: assessed,
      grade_buckets: buckets,
      attachment_count: base?.attachment_count ?? 0,
    };
  }, [
    isViewingClosedCycleHistory,
    closedCycleCriterionAssessments,
    closedCycleCriterionAssessmentsStatus,
    displayArchiveSummary,
  ]);
  const closedCycleGradeRows = useMemo(() => {
    if (!closedCycleCriterionAssessments) return [];
    const counts = new Map<string, number>();
    for (const v of Object.values(closedCycleCriterionAssessments)) {
      const g = v?.grade;
      if (!isCriterionGradeRecorded(g)) continue;
      const label = (g as string).trim();
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [closedCycleCriterionAssessments]);
  const closedCycleAttentionItems = useMemo(() => {
    if (!closedCycleCriterionAssessments || !viewingCycle) return [];
    const bundled = viewingCycle.framework_cycle_id ? getCycleById(viewingCycle.framework_cycle_id) : undefined;
    const frameworkFlat = bundled ? flattenCriteria(bundled) : [];
    const customRows = cycleDetailCriteria ?? [];
    const items: {
      criterionKey: string;
      gradeLabel: string;
      criterionLabel: string;
      eventId: number | string | null;
    }[] = [];
    for (const [criterionKey, v] of Object.entries(closedCycleCriterionAssessments)) {
      if (!criterionGradeNeedsAttention(v?.grade)) continue;
      const eventRaw = v?.employee_cycle_event_id;
      let eventId: number | string | null = null;
      if (eventRaw != null && String(eventRaw).trim() !== '') {
        eventId = typeof eventRaw === 'number' && Number.isFinite(eventRaw) ? eventRaw : String(eventRaw).trim();
      }
      let criterionLabel = criterionKey;
      const customMatch = customRows.find((row) => customCriterionKey(row.id) === criterionKey);
      if (customMatch?.criteria?.trim()) {
        criterionLabel = customMatch.criteria.trim();
      } else {
        const fw = frameworkFlat.find((c) => c.key === criterionKey);
        if (fw) criterionLabel = fw.text;
      }
      items.push({
        criterionKey,
        gradeLabel: (v?.grade ?? '').trim(),
        criterionLabel,
        eventId,
      });
    }
    items.sort((a, b) => a.criterionLabel.localeCompare(b.criterionLabel, undefined, { sensitivity: 'base' }));
    return items;
  }, [closedCycleCriterionAssessments, viewingCycle, cycleDetailCriteria]);
  /** Real system admin “View as” company, or company admin in the same mode — plus normal RBAC assessors. */
  const canRecordCompetencyAssessments =
    !profileLocked &&
    (!!systemAdminClosedCycleEditWhileImpersonating ||
    (roleOk &&
      (assessmentTenantImpersonationOk ||
        canEditEmployeeRecord ||
        legacyCanEdit ||
        canManageCompetencyAssessments)));
  const canMutateAssessmentsInViewingCycle = !viewingCycle
    ? canEdit
    : canRecordCompetencyAssessments && (!isViewingClosedCycleHistory || closedCycleSystemAdminOverride);
  canMutateAssessmentsRef.current = canMutateAssessmentsInViewingCycle;
  const canDelete = roleOk && isAdmin && !profileLocked;
  const licenceDetailsUserLabel = currentUserProfile?.full_name || currentUserTitle || currentUserEmail || "User";

  const refetchEmployee = useCallback(async (): Promise<Employee | null> => {
    if (!effectiveCompanyCode) return null;
    const cacheBust = `${cc ? '&' : '?'}_=${Date.now()}`;
    try {
      const res = await fetch(`/api/employees/${employeeId}${cc}${cacheBust}`, { cache: 'no-store', headers: companyHeaders });
      if (res.ok) {
        const data = await res.json();
        if (data && !data.error) {
          setEmployee(data);
          return data as Employee;
        }
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [employeeId, cc, effectiveCompanyCode, companyHeaders]);

  /** Other tabs (same browser): refetch when this profile is PATCHed elsewhere. */
  useEffect(() => {
    if (!employeeId) return;
    const name = `rail-vault-employee-${employeeId}`;
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(name);
    } catch {
      return;
    }
    bc.onmessage = (ev: MessageEvent) => {
      if (ev?.data === "employee-patch") void refetchEmployee();
    };
    return () => {
      try {
        bc?.close();
      } catch {
        /* ignore */
      }
    };
  }, [employeeId, refetchEmployee]);

  /** Phones / other devices: poll server while this profile is open; refetch when tab regains focus or visibility. */
  useEffect(() => {
    if (!effectiveCompanyCode || !employeeId) return;
    const POLL_MS = 4000;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void refetchEmployee();
    };
    const id = window.setInterval(tick, POLL_MS);
    const onResume = () => {
      if (document.visibilityState === "visible") void refetchEmployee();
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
    };
  }, [effectiveCompanyCode, employeeId, refetchEmployee]);

  const notesSorted = useMemo(() => {
    const arr = employee?.notes ?? [];
    return [...arr].sort((a, b) => String((b as { timestamp?: string }).timestamp ?? "").localeCompare(String((a as { timestamp?: string }).timestamp ?? "")));
  }, [employee?.notes]);

  const noteConversations = useMemo(() => {
    const list = Array.isArray(employee?.note_conversations) ? employee!.note_conversations! : [];
    return [...list].sort((a, b) => {
      const aActive = String(a.status || "").toLowerCase() === "active" ? 0 : 1;
      const bActive = String(b.status || "").toLowerCase() === "active" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return String(b.started_at || "").localeCompare(String(a.started_at || ""));
    });
  }, [employee?.note_conversations]);

  const activeNoteConversation = useMemo(
    () => noteConversations.find((c) => String(c.status || "").toLowerCase() === "active") || null,
    [noteConversations]
  );

  const archivedNoteConversations = useMemo(
    () => noteConversations.filter((c) => String(c.status || "").toLowerCase() === "archived"),
    [noteConversations]
  );

  const filteredNotesForView = useMemo(() => {
    const filter = notesConversationFilter;
    let list = notesSorted;
    if (filter === "all") {
      /* keep all */
    } else if (filter === "active") {
      const activeId = activeNoteConversation?.id;
      if (activeId) {
        list = notesSorted.filter(
          (n: any) =>
            String(n.conversation_id || "") === activeId ||
            !String(n.conversation_id || "").trim()
        );
      }
    } else {
      list = notesSorted.filter((n: any) => String(n.conversation_id || "") === filter);
    }
    // Conversation reading order: oldest first (newest near composer).
    return [...list].sort((a: any, b: any) =>
      String(a.timestamp || "").localeCompare(String(b.timestamp || ""))
    );
  }, [notesSorted, notesConversationFilter, activeNoteConversation?.id]);

  const canComposeNotes =
    notesConversationFilter === "active" ||
    notesConversationFilter === "all" ||
    (!!activeNoteConversation && notesConversationFilter === activeNoteConversation.id);

  const viewingArchivedNoteConversation = useMemo(() => {
    if (notesConversationFilter === "active" || notesConversationFilter === "all") return null;
    return (
      archivedNoteConversations.find((c) => c.id === notesConversationFilter) || null
    );
  }, [notesConversationFilter, archivedNoteConversations]);

  useEffect(() => {
    if (activeTab !== "Notes" || !employee?.notes?.length || !currentUserEmail || !effectiveCompanyCode) return;
    const viewer = currentUserEmail.toLowerCase();
    const key = noteReadsEmailKey(currentUserEmail);
    const unreadIds: string[] = [];
    for (const note of employee.notes as { id?: string | number; author_email?: string; reads?: Record<string, unknown> }[]) {
      if (note.id == null || note.id === "") continue;
      const authorE = (note.author_email || "").toLowerCase();
      if (authorE && authorE === viewer) continue;
      const reads = note.reads && typeof note.reads === "object" ? note.reads : {};
      if (reads[key]) continue;
      unreadIds.push(String(note.id));
    }
    if (unreadIds.length === 0) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      fetch(`/api/employees/${employeeId}/notes/mark-read${cc}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          ...companyHeaders,
        },
        body: JSON.stringify({
          note_ids: unreadIds,
          reader_name: currentUserProfile?.full_name || currentUserTitle || "",
        }),
      }).then((res) => {
        if (res.ok && !cancelled) refetchEmployee();
      });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [activeTab, employee?.notes, employeeId, currentUserEmail, effectiveCompanyCode, cc, companyHeaders, currentUserProfile?.full_name, currentUserTitle, refetchEmployee]);

  useEffect(() => {
    if (!isSystemAdmin) return;
    if (!transferModalOpen && availableCycles.length === 0) return;
    let cancelled = false;
    fetch('/api/companies')
      .then((r) => r.json())
      .then((data: unknown) => {
        if (!cancelled) setCompanies(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [transferModalOpen, isSystemAdmin, availableCycles.length]);

  useEffect(() => {
    let cancelled = false;
    setEmployee(null);
    setProfileAccessError(null);
    setLoading(true);
    const useInitial = initialEmployee && initialEmployee.id === employeeId;
    if (useInitial) {
      setEmployee(initialEmployee);
      setLoading(false);
    }
    if (!effectiveCompanyCode) {
      setLoading(false);
      return () => { cancelled = true; };
    }
    const cacheBust = `${cc ? '&' : '?'}_=${Date.now()}`;
    fetch(`/api/employees/${employeeId}${cc}${cacheBust}`, { cache: 'no-store', headers: companyHeaders })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Employee & { error?: string };
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 403) {
            setProfileAccessError(
              typeof data?.error === "string" && data.error.trim()
                ? data.error
                : "You do not have permission to view this employee record."
            );
            setEmployee(null);
            setLoading(false);
            return;
          }
          if (res.status === 404) {
            onEmployeeNotFound?.();
            setLoading(false);
            return;
          }
          setProfileAccessError(
            typeof data?.error === "string" && data.error.trim() ? data.error : `Could not load employee (${res.status}).`
          );
          setEmployee(null);
          setLoading(false);
          return;
        }
        if (data?.error) {
          onEmployeeNotFound?.();
          setLoading(false);
          return;
        }
        if (useInitial && data) {
          setEmployee({ ...data, id: initialEmployee!.id, manager: initialEmployee!.manager ?? null });
        } else {
          setEmployee(data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          onEmployeeNotFound?.();
          setLoading(false);
        }
      });
    const userHeaders: Record<string, string> = { 'Cache-Control': 'no-store', ...companyHeaders };
    if (!effectiveCompanyCode && currentUserEmail) userHeaders['X-User-Email'] = currentUserEmail;
    fetch(`/api/users${effectiveCompanyCode ? '?companyCode=' + encodeURIComponent(effectiveCompanyCode) : ''}`, { cache: 'no-store', headers: userHeaders })
      .then(res => {
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) return [];
        return res.json();
      })
      .then(data => { if (!cancelled) setAssessingManagers(Array.isArray(data) ? data : []); });
    return () => { cancelled = true; };
  }, [employeeId, cc, effectiveCompanyCode, companyHeaders, companyCode, initialEmployee, currentUserEmail, onEmployeeNotFound]);

  useEffect(() => {
    if (!employee?.company_code) return;
    let cancelled = false;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/users?companyCode=${encodeURIComponent(employee.company_code)}`, { cache: 'no-store', headers })
      .then(res => {
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) return [];
        return res.json();
      })
      .then(data => {
        if (!cancelled) setAssessingManagers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAssessingManagers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [employee?.company_code, currentUserEmail]);

  useEffect(() => {
    const code = String(effectiveCompanyCode || employee?.company_code || '').trim();
    if (!code) {
      setCompanyEmployeesForAssessors([]);
      return;
    }
    let cancelled = false;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/employees?companyCode=${encodeURIComponent(code)}`, { cache: 'no-store', headers })
      .then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
      .then((rows: Array<{ id?: string; full_name?: string; work_status?: string }>) => {
        if (cancelled) return;
        setCompanyEmployeesForAssessors(
          rows
            .map((row) => ({
              id: String(row.id ?? '').trim(),
              full_name: String(row.full_name ?? '').trim(),
              work_status: row.work_status,
            }))
            .filter((row) => row.id && row.full_name),
        );
      })
      .catch(() => {
        if (!cancelled) setCompanyEmployeesForAssessors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCode, employee?.company_code, currentUserEmail]);

  // Load the company org chart (seniority tiers) so we can apply the assessor derogation rule.
  useEffect(() => {
    const code = String(effectiveCompanyCode || employee?.company_code || '').trim();
    if (!code) {
      setOrgChart(null);
      return;
    }
    let cancelled = false;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(code)}/org-chart`, { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (!cancelled) setOrgChart(data ? normalizeOrgChart(data) : { levels: [] });
      })
      .catch(() => {
        if (!cancelled) setOrgChart({ levels: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCode, employee?.company_code, currentUserEmail]);

  useEffect(() => {
    const code = String(effectiveCompanyCode || "").trim();
    if (!code) {
      setCompanyAddressForProfile("");
      return;
    }
    let cancelled = false;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(code)}`, { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { postalAddress?: string; address?: string } | null) => {
        if (cancelled) return;
        setCompanyAddressForProfile(String(data?.postalAddress ?? data?.address ?? "").trim());
      })
      .catch(() => {
        if (!cancelled) setCompanyAddressForProfile("");
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCode, currentUserEmail]);

  useEffect(() => {
    if (!effectiveCompanyCode) {
      setCompanyTractionTypes([]);
      setCompanyRouteTypes([]);
      setCompanyDepots([]);
      return;
    }
    let cancelled = false;
    const normalize = (raw: unknown): string[] => {
      if (!Array.isArray(raw)) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const row of raw) {
        const value = String(row ?? '').trim();
        if (!value) continue;
        const lower = value.toLowerCase();
        if (seen.has(lower)) continue;
        seen.add(lower);
        out.push(value);
      }
      return out;
    };
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(effectiveCompanyCode)}/traction-routes`, { headers })
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json().catch(() => null)) as { tractionTypes?: unknown; routes?: unknown; depots?: unknown } | null;
      })
      .then((data) => {
        if (cancelled || !data) return;
        setCompanyTractionTypes(normalize(data.tractionTypes));
        setCompanyRouteTypes(normalize(data.routes));
        setCompanyDepots(normalize(data.depots));
      })
      .catch(() => {
        if (!cancelled) {
          setCompanyTractionTypes([]);
          setCompanyRouteTypes([]);
          setCompanyDepots([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCode, currentUserEmail]);

  useEffect(() => {
    if (!effectiveCompanyCode) {
      setCompanyJobRoles([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/companies/${encodeURIComponent(effectiveCompanyCode)}/job-roles`)
      .then((res) => (res.ok ? res.json() : []))
      .then((roles: { name: string }[]) => {
        if (!cancelled) setCompanyJobRoles(Array.isArray(roles) ? roles.map((r) => r.name) : []);
      })
      .catch(() => {
        if (!cancelled) setCompanyJobRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCode]);

  const fetchDocuments = async (path: string = currentPath) => {
    if (!employeeId || !effectiveCompanyCode) return;
    try {
      const res = await fetch(
        `/api/employees/${employeeId}/documents?path=${encodeURIComponent(path)}&companyCode=${encodeURIComponent(effectiveCompanyCode)}`,
        { headers: companyHeaders }
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        // List failures are not upload errors — avoid the Upload Error banner when browsing.
        console.warn(
          "Documents list failed:",
          (payload as { error?: string })?.error || res.status
        );
        setFolders([]);
        setDocuments([]);
        return;
      }
      const data = Array.isArray(payload) ? payload : [];

      const folderList = data
        .filter((d: any) => Number(d.is_folder) === 1 || d.is_folder === true)
        .map((d: any) => ({
        id: d.id,
        name: d.name,
        path: path ? `${path}/${d.name}` : d.name
      }));

      const docs = data
        .filter((d: any) => Number(d.is_folder) !== 1 && d.is_folder !== true)
        .map((d: any) => ({
        id: d.id,
        name: d.name,
        // We don't load the full content here, just a placeholder URL or we can fetch it when clicked
        url: '#',
      }));

      setFolders(folderList);
      setDocuments(docs);
    } catch (error: any) {
      console.warn("Error fetching documents:", error);
      setFolders([]);
      setDocuments([]);
    }
  };

  useEffect(() => {
    if (employeeId && effectiveCompanyCode && !transferring) {
      fetchDocuments(currentPath);
    }
  }, [currentPath, employeeId, effectiveCompanyCode, transferring]);

  const fetchSafetyBriefs = useCallback(async () => {
    if (!employeeId || !effectiveCompanyCode || !isSafetyBriefsModuleActive) return;
    setLoadingSafetyBriefs(true);
    setSafetyBriefsError(null);
    try {
      const headers: Record<string, string> = { ...companyHeaders };
      if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
      const [res, typesRes] = await Promise.all([
        fetch(`/api/employees/${employeeId}/safety-briefs${cc}`, { headers }),
        canManageSafetyBriefs
          ? fetch(`/api/safety-briefs/types${cc}`, { headers, cache: "no-store" })
          : Promise.resolve(null),
      ]);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error((data as { error?: string })?.error || `Failed (${res.status})`);
      setSafetyBriefs(Array.isArray(data) ? data : []);
      if (typesRes) {
        const typesData = await typesRes.json().catch(() => []);
        if (typesRes.ok && Array.isArray(typesData)) setSafetyBriefTypes(typesData);
        else setSafetyBriefTypes([]);
      } else {
        setSafetyBriefTypes([]);
      }
    } catch (err: any) {
      setSafetyBriefs([]);
      setSafetyBriefsError(normalizeAccessError(err?.message, "Failed to load safety briefs."));
    } finally {
      setLoadingSafetyBriefs(false);
    }
  }, [employeeId, effectiveCompanyCode, isSafetyBriefsModuleActive, canManageSafetyBriefs, cc, companyHeaders, currentUserEmail]);

  useEffect(() => {
    if (activeTab === 'Safety Briefs') {
      fetchSafetyBriefs();
    }
  }, [activeTab, fetchSafetyBriefs]);

  const resetSafetyBriefForm = useCallback(() => {
    setEditingSafetyBriefId(null);
    setSafetyBriefForm({
      brief_type_id: '',
      brief_name: '',
      date: new Date().toISOString().slice(0, 10),
      delivery_type: 'in_person',
      provided_by: '',
      remote_method: '',
    });
  }, []);

  const submitSafetyBrief = useCallback(async () => {
    if (!employeeId || !effectiveCompanyCode || !isSafetyBriefsModuleActive || !canManageSafetyBriefs) return;
    const selectedType = safetyBriefTypes.find((t) => t.id === safetyBriefForm.brief_type_id);
    const payload = {
      brief_type_id: safetyBriefForm.brief_type_id.trim(),
      brief_name: (selectedType?.name || safetyBriefForm.brief_name).trim(),
      date: safetyBriefForm.date,
      delivery_type: safetyBriefForm.delivery_type,
      provided_by: safetyBriefForm.delivery_type === 'in_person' ? safetyBriefForm.provided_by.trim() : '',
      remote_method: safetyBriefForm.delivery_type === 'remote' ? safetyBriefForm.remote_method.trim() : '',
    };
    if ((!payload.brief_type_id && !payload.brief_name) || !payload.date) return;
    setSavingSafetyBrief(true);
    try {
      const url = editingSafetyBriefId
        ? `/api/safety-briefs/${encodeURIComponent(editingSafetyBriefId)}${cc}`
        : `/api/employees/${employeeId}/safety-briefs${cc}`;
      const res = await fetch(url, {
        method: editingSafetyBriefId ? "PATCH" : "POST",
        headers: { 'Content-Type': 'application/json', ...companyHeaders },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      resetSafetyBriefForm();
      await fetchSafetyBriefs();
    } catch (err: any) {
      alert(err?.message || translateUi('Failed to save safety brief'));
    } finally {
      setSavingSafetyBrief(false);
    }
  }, [employeeId, effectiveCompanyCode, isSafetyBriefsModuleActive, canManageSafetyBriefs, safetyBriefForm, safetyBriefTypes, editingSafetyBriefId, cc, companyHeaders, fetchSafetyBriefs, resetSafetyBriefForm]);

  const deleteSafetyBrief = useCallback(async (briefId: string) => {
    if (!briefId || !effectiveCompanyCode || !canManageSafetyBriefs) return;
    if (!confirm(translateUi('Delete this safety brief record?'))) return;
    try {
      const res = await fetch(`/api/safety-briefs/${encodeURIComponent(briefId)}${cc}`, {
        method: 'DELETE',
        headers: companyHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      await fetchSafetyBriefs();
    } catch (err: any) {
      alert(err?.message || translateUi('Failed to delete safety brief'));
    }
  }, [effectiveCompanyCode, canManageSafetyBriefs, cc, companyHeaders, fetchSafetyBriefs]);

  const fetchEmployeeCycles = useCallback(() => {
    if (!employeeId) return;
    fetch(`/api/employees/${employeeId}/cycles${rc}`, {
      cache: 'no-store',
      headers: companyHeaders,
    })
      .then(res => res.ok ? res.json() : [])
      .then((data) => setEmployeeCycles(Array.isArray(data) ? data : []))
      .catch(() => setEmployeeCycles([]));
  }, [employeeId, rc, companyHeaders]);

  const refreshClosedCycleAssessmentSummary = useCallback(() => {
    fetchEmployeeCycles();
    setClosedCycleSummaryRefreshVersion((v) => v + 1);
  }, [fetchEmployeeCycles]);

  const effectiveCycleCompanyCode = effectiveCompanyCode || '';

  const fetchAvailableCycles = useCallback(() => {
    const bust = `v=${Date.now()}`;
    const url = effectiveCycleCompanyCode
      ? `/api/cycles?companyCode=${encodeURIComponent(effectiveCycleCompanyCode)}&assignableOnly=1&${bust}`
      : `/api/cycles?assignableOnly=1&${bust}`;
    fetch(url, { cache: "no-store", headers: companyHeaders })
      .then(res => res.ok ? res.json() : [])
      .then((data) => setAvailableCycles(Array.isArray(data) ? data : []))
      .catch(() => setAvailableCycles([]));
  }, [effectiveCycleCompanyCode, companyHeaders]);

  useEffect(() => {
    if (!employeeId) return;
    fetchEmployeeCycles();
    fetchAvailableCycles();
  }, [employeeId, fetchEmployeeCycles, fetchAvailableCycles]);

  useEffect(() => {
    if (!employeeId || employeeCycles.length === 0) return;
    let pending: { employeeCycleId?: string; cycleId?: string } | null = null;
    try {
      const raw = sessionStorage.getItem(`rv_employee_open_cycle_${employeeId}`);
      if (!raw) return;
      sessionStorage.removeItem(`rv_employee_open_cycle_${employeeId}`);
      pending = JSON.parse(raw) as { employeeCycleId?: string; cycleId?: string };
    } catch {
      return;
    }
    const wantEcId = String(pending?.employeeCycleId || "").trim();
    const wantCycleId = String(pending?.cycleId || "").trim();
    if (!wantEcId && !wantCycleId) return;
    const live = employeeCycles.filter((ec) => !ec.end_reason);
    const pool = live.length > 0 ? live : employeeCycles;
    const hit =
      (wantEcId ? pool.find((ec) => String(ec.id) === wantEcId) : undefined) ||
      (wantCycleId ? pool.find((ec) => String(ec.cycle_id) === wantCycleId) : undefined);
    if (!hit) return;
    setViewingCycle({
      id: hit.id,
      cycle_id: hit.cycle_id,
      cycle_title: hit.cycle_title,
      start_date: hit.start_date,
      expiry_date: hit.expiry_date,
      framework_cycle_id: hit.framework_cycle_id,
      is_continuous: !!hit.is_continuous,
      is_read_only_history: !!hit.end_reason,
      source_employee_cycle_id: hit.source_employee_cycle_id,
      archive_summary: hit.archive_summary ?? null,
    });
  }, [employeeId, employeeCycles]);

  const refetchCycleEvents = useCallback(() => {
    if (!viewingCycle || !employeeId) return;
    if (!effectiveCompanyCode) {
      setEmployeeCycleEventsLoadStatus('loading');
      return;
    }
    const seq = ++cycleEventsFetchSeqRef.current;
    setEmployeeCycleEventsLoadStatus('loading');
    const params = new URLSearchParams();
    params.set('includeMonitoring', '1');
    params.set('companyCode', effectiveCompanyCode);
    fetch(`/api/employees/${employeeId}/cycle-events/${viewingCycle.id}?${params.toString()}`, {
      headers: companyHeaders,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`cycle-events ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (seq !== cycleEventsFetchSeqRef.current) return;
        const rows = Array.isArray(data) ? (data as EmployeeCycleEventSummary[]) : [];
        setEmployeeCycleEvents(rows);
        setEmployeeCycleEventsLoadStatus('loaded');
      })
      .catch(() => {
        if (seq !== cycleEventsFetchSeqRef.current) return;
        setEmployeeCycleEvents((prev) => {
          if (prev.length > 0) {
            setEmployeeCycleEventsLoadStatus('loaded');
            return prev;
          }
          setEmployeeCycleEventsLoadStatus('error');
          return [];
        });
      });
  }, [viewingCycle, employeeId, effectiveCompanyCode, companyHeaders]);

  /** Cycle-wide assessment criteria coverage (excludes CDP monitoring events from the dial narrative). */
  const employeeCycleEventsRef = useRef(employeeCycleEvents);
  employeeCycleEventsRef.current = employeeCycleEvents;
  const refetchCycleCriteriaCoverage = useCallback(() => {
    if (!viewingCycle || !employeeId) {
      setCycleCriteriaCoverage(null);
      return;
    }
    const assessmentEvents = employeeCycleEventsRef.current.filter(
      (ev) => String(ev.event_type ?? 'assessment').trim().toLowerCase() !== 'monitoring'
    );
    const framework = viewingCycle.framework_cycle_id ? getCycleById(viewingCycle.framework_cycle_id) : undefined;
    const frameworkTotal = framework ? flattenCriteria(framework).length : 0;
    const customTotal = Array.isArray(cycleDetailCriteria) ? cycleDetailCriteria.length : 0;
    const eventTotal = assessmentEvents.reduce((max, ev) => Math.max(max, ev.total_criteria ?? 0), 0);
    const totalFallback = frameworkTotal || customTotal || eventTotal;

    fetch(
      `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(viewingCycle.id))}${rc}`,
      { headers: companyHeaders }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        let covered = 0;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          for (const row of Object.values(data as Record<string, { grade?: string | null }>)) {
            if (isGradeAssessed(row?.grade)) covered += 1;
          }
        } else {
          covered = assessmentEvents.reduce((max, ev) => Math.max(max, ev.assessed_count ?? 0), 0);
        }
        const total = totalFallback > 0 ? totalFallback : Math.max(covered, eventTotal);
        setCycleCriteriaCoverage({ covered: Math.min(covered, total || covered), total });
      })
      .catch(() => {
        const covered = assessmentEvents.reduce((max, ev) => Math.max(max, ev.assessed_count ?? 0), 0);
        setCycleCriteriaCoverage({ covered, total: totalFallback || covered });
      });
  }, [viewingCycle, employeeId, rc, companyHeaders, cycleDetailCriteria]);

  useEffect(() => {
    refetchCycleCriteriaCoverage();
  }, [refetchCycleCriteriaCoverage]);

  /**
   * Refresh schedule row counts + overall dial. Debounced during grading so each
   * criterion save is not blocked by two heavy GETs (cycle-events + all assessments).
   */
  const scheduleInvalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidateAssessmentScheduleNow = useCallback(() => {
    if (scheduleInvalidateTimerRef.current) {
      clearTimeout(scheduleInvalidateTimerRef.current);
      scheduleInvalidateTimerRef.current = null;
    }
    refetchCycleEvents();
    refetchCycleCriteriaCoverage();
  }, [refetchCycleEvents, refetchCycleCriteriaCoverage]);
  const invalidateAssessmentSchedule = useCallback(
    (opts?: { immediate?: boolean }) => {
      if (opts?.immediate) {
        invalidateAssessmentScheduleNow();
        return;
      }
      if (scheduleInvalidateTimerRef.current) clearTimeout(scheduleInvalidateTimerRef.current);
      scheduleInvalidateTimerRef.current = setTimeout(() => {
        scheduleInvalidateTimerRef.current = null;
        invalidateAssessmentScheduleNow();
      }, 1800);
    },
    [invalidateAssessmentScheduleNow]
  );
  invalidateAssessmentScheduleRef.current = () => invalidateAssessmentScheduleNow();

  /** Instant local schedule/dial update after a grade change — no network wait. */
  const patchAssessmentScheduleProgress = useCallback(
    (args: {
      eventId: number | string;
      previousGrade: string | null | undefined;
      nextGrade: string | null | undefined;
    }) => {
      const prevAssessed = isGradeAssessed(args.previousGrade);
      const nextAssessed = isGradeAssessed(args.nextGrade);
      const assessedDelta = (nextAssessed ? 1 : 0) - (prevAssessed ? 1 : 0);

      const bucket = (g: string | null | undefined): 'advice' | 'review' | 'na' | null => {
        if (!isGradeAssessed(g)) return null;
        const n = String(g).trim().toLowerCase();
        if (n.includes('review')) return 'review';
        if (n.includes('not applicable')) return 'na';
        if (n.includes('advis') || n.includes('advice')) return 'advice';
        return null;
      };
      const prevBucket = bucket(args.previousGrade);
      const nextBucket = bucket(args.nextGrade);

      if (assessedDelta !== 0 || prevBucket !== nextBucket) {
        setEmployeeCycleEvents((prev) =>
          prev.map((ev) => {
            if (String(ev.id) !== String(args.eventId)) return ev;
            const assessed = Math.max(0, (ev.assessed_count ?? 0) + assessedDelta);
            let advice = ev.advice_count ?? 0;
            let review = ev.review_count ?? 0;
            let na = ev.not_applicable_count ?? 0;
            if (prevBucket === 'advice') advice = Math.max(0, advice - 1);
            if (prevBucket === 'review') review = Math.max(0, review - 1);
            if (prevBucket === 'na') na = Math.max(0, na - 1);
            if (nextBucket === 'advice') advice += 1;
            if (nextBucket === 'review') review += 1;
            if (nextBucket === 'na') na += 1;
            return {
              ...ev,
              assessed_count: assessed,
              advice_count: advice,
              review_count: review,
              not_applicable_count: na,
            };
          })
        );
      }
      if (assessedDelta !== 0) {
        setCycleCriteriaCoverage((prev) => {
          if (!prev || prev.total <= 0) return prev;
          const covered = Math.max(0, Math.min(prev.total, prev.covered + assessedDelta));
          return { covered, total: prev.total };
        });
      }
      invalidateAssessmentSchedule();
    },
    [invalidateAssessmentSchedule]
  );

  /** Full cycle criterion count — schedule cards must use this, not started-selection scope sizes. */
  const scheduleCycleCriteriaTotal = useMemo(() => {
    if (!viewingCycle) return 0;
    const framework = viewingCycle.framework_cycle_id ? getCycleById(viewingCycle.framework_cycle_id) : undefined;
    const frameworkTotal = framework ? flattenCriteria(framework).length : 0;
    const customTotal = Array.isArray(cycleDetailCriteria) ? cycleDetailCriteria.length : 0;
    return frameworkTotal || customTotal || 0;
  }, [viewingCycle, cycleDetailCriteria]);

  useEffect(() => {
    if (!viewingCycle) {
      prevViewingCycleIdRef.current = null;
      setCycleDetailCriteria(null);
      setEmployeeCycleEvents([]);
      setEmployeeCycleEventsLoadStatus('idle');
      setActiveEventId(null);
      setEventAssessmentModalId(null);
      return;
    }

    const cycleChanged = prevViewingCycleIdRef.current !== viewingCycle.id;
    prevViewingCycleIdRef.current = viewingCycle.id;

    if (cycleChanged) {
      setActiveEventId(null);
      setEventAssessmentModalId(null);
      setCycleDetailCriteria(null);
      setEmployeeCycleEvents([]);
      setEmployeeCycleEventsLoadStatus('loading');
      fetch(`/api/cycles/${viewingCycle.cycle_id}`, { headers: companyHeaders })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => (data?.criteria ? setCycleDetailCriteria(data.criteria) : setCycleDetailCriteria([])))
        .catch(() => setCycleDetailCriteria([]));
    }

    if (!effectiveCompanyCode) {
      setEmployeeCycleEventsLoadStatus('loading');
      return;
    }

    refetchCycleEvents();
  }, [viewingCycle, employeeId, effectiveCompanyCode, companyHeaders, refetchCycleEvents]);

  useEffect(() => {
    setClosedCycleSummaryRefreshVersion(0);
  }, [viewingCycle?.id]);

  useEffect(() => {
    if (!viewingCycle || !isViewingClosedCycleHistory || !employeeId) {
      setClosedCycleCriterionAssessments(null);
      setClosedCycleCriterionAssessmentsStatus('idle');
      return;
    }
    let cancelled = false;
    const isRefetch = closedCycleSummaryRefreshVersion > 0;
    if (!isRefetch) {
      setClosedCycleCriterionAssessments(null);
    }
    setClosedCycleCriterionAssessmentsStatus('loading');
    fetch(
      `/api/employees/${encodeURIComponent(employeeId)}/cycle-assessments/${encodeURIComponent(String(viewingCycle.id))}${rc}`,
      { headers: companyHeaders }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const raw = data as Record<string, Record<string, unknown>>;
          const normalized: Record<string, ClosedCycleCriterionAssessmentSnapshot> = {};
          for (const k of Object.keys(raw)) {
            const row = raw[k];
            const eceRaw = row?.employee_cycle_event_id;
            const eceNorm: string | number | null =
              eceRaw == null ? null : typeof eceRaw === "string" || typeof eceRaw === "number" ? eceRaw : String(eceRaw);
            normalized[k] = {
              grade: (row?.grade as string | null | undefined) ?? null,
              employee_cycle_event_id: eceNorm,
            };
          }
          setClosedCycleCriterionAssessments(normalized);
          setClosedCycleCriterionAssessmentsStatus('done');
        } else {
          setClosedCycleCriterionAssessments(null);
          setClosedCycleCriterionAssessmentsStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClosedCycleCriterionAssessments(null);
          setClosedCycleCriterionAssessmentsStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    viewingCycle,
    isViewingClosedCycleHistory,
    employeeId,
    rc,
    companyHeaders,
    closedCycleSummaryRefreshVersion,
  ]);

  const performStartAssessment = useCallback((eventId: number | string, selectedCriterionKeys?: string[], selectedElementKeys?: string[]) => {
    fetch(`/api/employee-cycle-events/${eventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...companyHeaders },
      body: JSON.stringify({
        start_assessment: true,
        userEmail: currentUserEmail,
        ...(Array.isArray(selectedCriterionKeys) && selectedCriterionKeys.length > 0 ? { selected_criterion_keys: selectedCriterionKeys } : {}),
        ...(Array.isArray(selectedElementKeys) && selectedElementKeys.length > 0 ? { selected_element_keys: selectedElementKeys } : {}),
      }),
    }).then((r) => {
      if (r.ok) {
        const startedAt = new Date().toISOString();
        const openerName = String(currentUserProfile?.full_name ?? currentUserTitle ?? '').trim();
        setEmployeeCycleEvents((prev) =>
          prev.map((event) =>
            String(event.id) === String(eventId)
              ? {
                  ...event,
                  assessment_started_at: startedAt,
                  assessment_started_by: currentUserEmail || null,
                  assessment_started_by_name: openerName || null,
                  ...(Array.isArray(selectedCriterionKeys) && selectedCriterionKeys.length > 0
                    ? { assessment_started_criterion_keys: selectedCriterionKeys }
                    : {}),
                  ...(Array.isArray(selectedElementKeys) && selectedElementKeys.length > 0
                    ? { assessment_started_element_keys: selectedElementKeys }
                    : {}),
                }
              : event
          )
        );
        refetchCycleEvents();
      }
    });
  }, [currentUserEmail, currentUserProfile?.full_name, currentUserTitle, refetchCycleEvents, companyHeaders]);

  /**
   * Assessor seniority rule: before starting an assessment we compare the assessor's job-role tier
   * (the logged-in user's profile title) against the assessee's role tier in the org chart.
   * - Assessor more senior (or tier unknown): start immediately, unchanged behaviour.
   * - Same tier: a derogation must be captured (mode 'same_tier').
   * - Assessor more junior: warn + advise a higher-seniority assessor, but still allow a derogation.
   */
  const startAssessmentForEvent = useCallback((eventId: number | string, selectedCriterionKeys?: string[], selectedElementKeys?: string[]) => {
    const ev = employeeCycleEvents.find((e) => String(e.id) === String(eventId));
    if (!ev || ev.status === 'completed' || ev.assessment_started_at) return;

    const assessorRole = String(currentUserProfile?.title ?? currentUserTitle ?? '').trim();
    const assessorName = String(currentUserProfile?.full_name ?? currentUserTitle ?? currentUserEmail ?? '').trim();
    const assesseeRole = String(employee?.role ?? '').trim();
    const assessorTier = resolveTier(orgChart, assessorRole);
    const assesseeTier = resolveTier(orgChart, assesseeRole);
    const relation = compareSeniority(assessorTier, assesseeTier);

    if (relation === 'senior' || relation === 'unknown') {
      performStartAssessment(eventId, selectedCriterionKeys, selectedElementKeys);
      return;
    }

    const suggestedAssessors: SuggestedAssessor[] =
      relation === 'junior'
        ? (assessingManagers || [])
            .filter((u) => userHasAssessorAdditionalRole(u))
            .map((u) => {
              const role = String(u?.title ?? '').trim();
              return { name: String(u?.full_name ?? '').trim(), role, tier: resolveTier(orgChart, role) };
            })
            .filter((s) => s.name && !isSamePersonName(s.name, assessorName) && isMoreSenior(s.tier, assesseeTier))
            .map((s) => ({ name: s.name, role: s.role || null, tierTitle: s.tier?.title || null }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        : [];

    setDerogationError(null);
    setDerogationSubmitting(false);
    setDerogationDialog({
      mode: relation === 'junior' ? 'assessor_junior' : 'same_tier',
      eventId,
      eventName: String(ev.event_name ?? '').trim(),
      selectedCriterionKeys,
      selectedElementKeys,
      assessorName,
      assessorRole,
      assessorTier,
      assesseeRole,
      assesseeTier,
      suggestedAssessors,
    });
  }, [employeeCycleEvents, currentUserProfile?.title, currentUserProfile?.full_name, currentUserTitle, currentUserEmail, employee?.role, orgChart, assessingManagers, performStartAssessment]);
  startAssessmentForEventRef.current = startAssessmentForEvent;

  /** Persist the derogation captured in the dialog, then continue starting the staged assessment. */
  const submitAssessorDerogation = useCallback(async (submission: DerogationSubmission) => {
    const d = derogationDialog;
    if (!d) return;
    setDerogationSubmitting(true);
    setDerogationError(null);
    try {
      const res = await fetch(`/api/employees/${employeeId}/assessment-derogations${cc}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...companyHeaders },
        body: JSON.stringify({
          employee_cycle_event_id: d.eventId,
          event_name: d.eventName,
          assessor_email: currentUserEmail,
          assessor_name: d.assessorName,
          assessor_role: d.assessorRole,
          assessor_tier_index: d.assessorTier?.index ?? null,
          assessor_tier_title: d.assessorTier?.title ?? null,
          assessee_role: d.assesseeRole,
          assessee_tier_index: d.assesseeTier?.index ?? null,
          assessee_tier_title: d.assesseeTier?.title ?? null,
          relationship: d.mode,
          reason: submission.reason,
          approved_by: submission.approved_by,
          filled_in_by_name: currentUserProfile?.full_name || currentUserTitle || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }));
        setDerogationError(err.error || 'Failed to record derogation.');
        setDerogationSubmitting(false);
        return;
      }
      performStartAssessment(d.eventId, d.selectedCriterionKeys, d.selectedElementKeys);
      setDerogationDialog(null);
      setDerogationSubmitting(false);
    } catch {
      setDerogationError('Failed to record derogation. Please try again.');
      setDerogationSubmitting(false);
    }
  }, [derogationDialog, employeeId, cc, companyHeaders, currentUserEmail, currentUserProfile?.full_name, currentUserTitle, performStartAssessment]);

  const markEmployeeCycleEventCompleted = useCallback(async (eventId: number | string, completedDate?: string) => {
    const res = await fetch(`/api/employee-cycle-events/${eventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}) },
      body: JSON.stringify({
        status: 'completed',
        userEmail: currentUserEmail,
        ...(completedDate && /^\d{4}-\d{2}-\d{2}$/.test(completedDate) ? { completed_date: completedDate } : {}),
      }),
    });
    if (!res.ok) {
      await alertAssessmentMutationFailed(res, translateUi("Could not complete assessment. Please try again."));
      return false;
    }
    const completedAt =
      completedDate && /^\d{4}-\d{2}-\d{2}$/.test(completedDate)
        ? new Date(`${completedDate}T12:00:00`).toISOString()
        : new Date().toISOString();
    let allAssessmentsComplete = false;
    let completedIsAssessment = true;
    setEmployeeCycleEvents((prev) => {
      const next = prev.map((event) => {
        if (String(event.id) !== String(eventId)) return event;
        const startedAt = event.assessment_started_at;
        const durationSeconds = startedAt
          ? Math.max(0, Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000))
          : null;
        return {
          ...event,
          status: 'completed',
          completed_at: completedAt,
          completed_by: currentUserEmail || null,
          assessment_duration_seconds: durationSeconds ?? undefined,
        };
      });
      const completedEv = next.find((e) => String(e.id) === String(eventId));
      completedIsAssessment =
        String(completedEv?.event_type ?? 'assessment')
          .trim()
          .toLowerCase() !== 'monitoring';
      const assessments = next.filter(
        (e) =>
          String(e.event_type ?? 'assessment')
            .trim()
            .toLowerCase() !== 'monitoring',
      );
      allAssessmentsComplete =
        assessments.length > 0 &&
        assessments.every((e) => String(e.status ?? '').trim().toLowerCase() === 'completed');
      return next;
    });
    refetchCycleEvents();
    fetchEmployeeCycles();
        if (completedIsAssessment && allAssessmentsComplete) {
      // Let callers close the assessment modal first, then offer renew/close.
      window.setTimeout(() => {
        const live = viewingCycle;
        if (!live || live.is_read_only_history) return;
        const fromList = employeeCycles.find((ec) => String(ec.id) === String(live.id) && !ec.end_reason);
        const isContinuous = !!(live.is_continuous ?? fromList?.is_continuous);
        const title = live.cycle_title || fromList?.cycle_title || "Competency cycle";
        if (isContinuous) {
          setContinueCycleDialog({
            ecId: live.id,
            cycleTitle: title,
            variant: 'post_assessment',
            startFromToday: true,
          });
        } else {
          setCloseCycleDialog({
            ecId: live.id,
            cycleTitle: title,
            variant: 'post_assessment',
          });
        }
      }, 0);
    }
    return true;
  }, [currentUserEmail, refetchCycleEvents, fetchEmployeeCycles, viewingCycle, employeeCycles]);

  const adminClearStuckAssessmentStart = useCallback(
    (eventId: number | string, eventName: string) => {
      setConfirmState({
        isOpen: true,
        title: 'Clear in-progress lock?',
        message: `Reset “${eventName}” so another assessor can open it? Grades and notes already saved for this event stay on record. Use only if the assessment was left open by mistake.`,
        onConfirm: () => {
          setConfirmState(null);
          void (async () => {
            const res = await fetch(`/api/employee-cycle-events/${eventId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', ...(adminActorEmail ? { 'X-User-Email': adminActorEmail } : {}) },
              body: JSON.stringify({ admin_clear_assessment_start: true, userEmail: adminActorEmail }),
            });
            if (res.ok) {
              setEmployeeCycleEvents((prev) =>
                prev.map((event) =>
                  String(event.id) === String(eventId)
                    ? {
                        ...event,
                        assessment_started_at: undefined,
                        assessment_started_by: undefined,
                        assessment_started_by_name: undefined,
                        assessment_started_criterion_keys: undefined,
                        assessment_started_element_keys: undefined,
                      }
                    : event
                )
              );
              refetchCycleEvents();
            } else {
              try {
                const j = (await res.json()) as { error?: string };
                window.alert(j.error?.trim() || translateUi("Could not clear assessment lock."));
              } catch {
                window.alert(translateUi("Could not clear assessment lock."));
              }
            }
          })();
        },
      });
    },
    [adminActorEmail, refetchCycleEvents]
  );

  const adminResetOpenAssessmentEvent = useCallback(
    (eventId: number | string, eventName: string) => {
      setConfirmState({
        isOpen: true,
        title: 'Reset this assessment?',
        message: `This will permanently erase all activity on “${eventName}”, including grades, notes, methods, feedback, open/start state, and any evidence attachments for criteria assessed on this event (database records and Azure storage files). The event will return to an unopened, not-started assessment. This cannot be undone.`,
        onConfirm: () => {
          setConfirmState(null);
          void (async () => {
            const res = await fetch(`/api/employee-cycle-events/${eventId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', ...(adminActorEmail ? { 'X-User-Email': adminActorEmail } : {}) },
              body: JSON.stringify({ admin_reset_assessment_event: true, userEmail: adminActorEmail }),
            });
            if (res.ok) {
              if (String(eventAssessmentModalId) === String(eventId)) {
                closeAssessmentModalForEvent(eventId);
              }
              setEmployeeCycleEvents((prev) =>
                prev.map((event) =>
                  String(event.id) === String(eventId)
                    ? {
                        ...event,
                        assessment_started_at: undefined,
                        assessment_started_by: undefined,
                        assessment_started_by_name: undefined,
                        assessment_opened_at: undefined,
                        assessment_opened_by: undefined,
                        assessment_opened_by_name: undefined,
                        assessment_started_criterion_keys: undefined,
                        assessment_started_element_keys: undefined,
                        assessed_count: 0,
                        advice_count: 0,
                        review_count: 0,
                        not_applicable_count: 0,
                        unassessed_count: event.total_criteria ?? null,
                        early_assessment_reasons: undefined,
                        late_assessment_reasons: undefined,
                        final_assessment_feedback: undefined,
                        assessment_duration_seconds: undefined,
                      }
                    : event
                )
              );
              refetchCycleEvents();
              setClosedCycleSummaryRefreshVersion((v) => v + 1);
            } else {
              try {
                const j = (await res.json()) as { error?: string };
                window.alert(j.error?.trim() || translateUi("Could not reset assessment."));
              } catch {
                window.alert(translateUi("Could not reset assessment."));
              }
            }
          })();
        },
      });
    },
    [adminActorEmail, refetchCycleEvents, eventAssessmentModalId, closeAssessmentModalForEvent]
  );

  const recordAmendmentForEvent = useCallback(async (eventId: number | string, amendmentNotes?: string) => {
    const res = await fetch(`/api/employee-cycle-events/${eventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}) },
      body: JSON.stringify({
        record_amendment: true,
        userEmail: currentUserEmail,
        ...(amendmentNotes?.trim() ? { amendment_notes: amendmentNotes.trim() } : {}),
      }),
    });
    if (res.ok) refetchCycleEvents();
    return res.ok;
  }, [currentUserEmail, refetchCycleEvents]);

  const saveMonitoringEventNotes = useCallback(
    async (eventId: number | string, notes: string) => {
      setMonitoringEventNotesSaving(true);
      try {
        const res = await fetch(`/api/employee-cycle-events/${eventId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}) },
          body: JSON.stringify({ notes, userEmail: currentUserEmail }),
        });
        if (!res.ok) return false;
        refetchCycleEvents();
        return true;
      } finally {
        setMonitoringEventNotesSaving(false);
      }
    },
    [currentUserEmail, refetchCycleEvents]
  );

  const handleAssignCycle = async () => {
    if (!employeeId || !selectedCycleToAssign) return;
    const code = employee?.company_code || companyCode;
    if (!code) {
      alert(translateUi("Company context is required. Please refresh and try again."));
      return;
    }
    if (assignContinuous && !canManageContinuousCycles) {
      alert(
        translateUi('You do not have permission to activate continuous cycles. Ask a company administrator to grant “Activate continuous cycles” in Role permissions.'),
      );
      return;
    }
    setAssignCycleFeedback(null);
    setAssigningCycle(true);
    try {
      const res = await fetch(`/api/employees/${employeeId}/cycles?companyCode=${encodeURIComponent(code)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
        },
        body: JSON.stringify({
          cycle_id: selectedCycleToAssign,
          start_date: assignStartDate,
          is_continuous: assignContinuous && canManageContinuousCycles,
          companyCode: code,
          userEmail: currentUserEmail,
        }),
      });
      if (res.ok) {
        setSelectedCycleToAssign(null);
        setAssignStartDate(new Date().toISOString().slice(0, 10));
        setAssignContinuous(false);
        fetchEmployeeCycles();
      } else {
        let msg = translateUi("Failed to assign cycle");
        try {
          const err = (await res.json()) as { error?: string };
          msg = (err.error || msg).trim();
        } catch {
          if (res.status === 409) {
            msg =
              translateUi('This competency cycle is already a live assignment for this employee. Close it into Closed Cycles first, or remove the live row, then assign the same cycle again.');
          }
        }
        if (res.status === 409) {
          setAssignCycleFeedback(msg);
        } else {
          alert(msg);
        }
      }
    } catch (err) {
      alert(translateUi("Failed to assign cycle"));
    } finally {
      setAssigningCycle(false);
    }
  };

  const handleToggleContinuous = async (ecId: number | string, next: boolean) => {
    if (!employeeId) return;
    if (
      !next &&
      !confirm(
        translateUi('Deactivate continuous for this cycle? It will no longer auto-renew and must be closed normally when assessments are complete.'),
      )
    ) {
      return;
    }
    try {
      const res = await fetch(`/api/employees/${employeeId}/employee-cycles/${encodeURIComponent(String(ecId))}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
        },
        body: JSON.stringify({ is_continuous: next }),
      });
      if (!res.ok) {
        let msg = translateUi("Failed to update continuous flag");
        try {
          const j = (await res.json()) as { error?: string };
          msg = (j.error || msg).trim();
        } catch {
          /* ignore */
        }
        alert(msg);
        return;
      }
      fetchEmployeeCycles();
    } catch {
      alert(translateUi("Failed to update continuous flag"));
    }
  };

  const performContinueEmployeeCycle = useCallback(
    async (ecId: number | string, opts?: { startFromToday?: boolean }) => {
      if (!employeeId) return;
      setContinuingEmployeeCycleId(ecId);
      try {
        const start_date = opts?.startFromToday ? new Date().toISOString().slice(0, 10) : undefined;
        const res = await fetch(`/api/employees/${employeeId}/employee-cycles/${encodeURIComponent(String(ecId))}/continue`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
          },
          body: JSON.stringify({
            userEmail: currentUserEmail,
            ...(start_date ? { start_date } : {}),
          }),
        });
        if (!res.ok) {
          let msg = translateUi("Failed to continue cycle");
          try {
            const j = (await res.json()) as { error?: string; reasons?: string[] };
            msg = (j.error || msg).trim();
          } catch {
            /* ignore */
          }
          alert(msg);
          return;
        }
        setContinueCycleDialog(null);
        fetchEmployeeCycles();
        setViewingCycle(null);
      } catch {
        alert(translateUi("Failed to continue cycle"));
      } finally {
        setContinuingEmployeeCycleId(null);
      }
    },
    [employeeId, currentUserEmail, fetchEmployeeCycles],
  );

  const handleUnassignCycle = async (ecId: number | string) => {
    if (!confirm(translateUi('Remove this cycle assignment?'))) return;
    try {
      const res = await fetch(`/api/employee-cycles/${ecId}`, { method: 'DELETE' });
      if (res.ok) fetchEmployeeCycles();
    } catch (err) {
      alert(translateUi("Failed to remove"));
    }
  };

  const performCloseEmployeeCycle = useCallback(
    async (ecId: number | string) => {
      if (!employeeId) return;
      setClosingEmployeeCycleId(ecId);
      try {
        const res = await fetch(
          `/api/employees/${employeeId}/employee-cycles/${encodeURIComponent(String(ecId))}/close${rc}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
            },
          }
        );
        if (!res.ok) {
          let errMsg = translateUi("Failed to close cycle");
          try {
            const j = (await res.json()) as { error?: string };
            errMsg = (j.error || errMsg).trim();
          } catch {
            errMsg = errMsg;
          }
          alert(errMsg);
          return;
        }
        setCloseCycleDialog(null);
        fetchEmployeeCycles();
      } catch {
        alert(translateUi("Failed to close cycle"));
      } finally {
        setClosingEmployeeCycleId(null);
      }
    },
    [employeeId, rc, currentUserEmail, fetchEmployeeCycles]
  );

  const handleDeleteClosedCycle = useCallback(
    async (historyId: number | string) => {
      if (!employeeId) return;
      if (!confirm(translateUi('Delete this closed cycle record?'))) return;
      setDeletingClosedCycleId(historyId);
      try {
        const res = await fetch(
          `/api/employees/${employeeId}/cycle-history/${encodeURIComponent(String(historyId))}${rc}`,
          {
            method: 'DELETE',
            headers: { ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}) },
          }
        );
        if (!res.ok) {
          let msg = translateUi("Failed to delete closed cycle");
          try {
            const j = (await res.json()) as { error?: string };
            msg = j.error || msg;
          } catch {}
          alert(msg);
          return;
        }
        fetchEmployeeCycles();
      } catch {
        alert(translateUi("Failed to delete closed cycle"));
      } finally {
        setDeletingClosedCycleId(null);
      }
    },
    [employeeId, rc, currentUserEmail, fetchEmployeeCycles]
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const file = target.files?.[0];
    if (!file) return;
    if (!canUploadDocuments) {
      setUploadError(
        "Only managers, company administrators, and system administrators can upload documents. You can view files stored here."
      );
      target.value = '';
      return;
    }

    setUploadingDoc(true);
    setUploadError(null);
    try {
      console.log("Starting upload for file:", file.name);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      await new Promise((resolve, reject) => {
        reader.onload = resolve;
        reader.onerror = reject;
      });
      
      const content = reader.result as string;
      
      const res = await fetch(`/api/employees/${employeeId}/documents${cc}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...companyHeaders,
        },
        body: JSON.stringify({
          name: file.name,
          path: currentPath,
          content: content,
          is_folder: 0
        })
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as { error?: string })?.error || `Failed to upload document (${res.status})`);
      }

      console.log("Upload success, fetching documents...");
      await fetchDocuments(currentPath);
    } catch (error: any) {
      console.error("Error uploading document:", error);
      setUploadError(`Failed to upload document: ${error.message}`);
    } finally {
      setUploadingDoc(false);
      target.value = '';
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName || !newFolderName.trim()) return;
    if (!canUploadDocuments) {
      setUploadError(
        "Only managers, company administrators, and system administrators can create folders. You can view files stored here."
      );
      return;
    }
    
    setUploadingDoc(true);
    setUploadError(null);
    try {
      console.log("Creating folder:", newFolderName);
      
      const res = await fetch(`/api/employees/${employeeId}/documents${cc}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...companyHeaders,
        },
        body: JSON.stringify({
          name: newFolderName.trim(),
          path: currentPath,
          content: '',
          is_folder: 1
        })
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as { error?: string })?.error || `Failed to create folder (${res.status})`);
      }
      
      console.log("Folder created, fetching documents...");
      await fetchDocuments(currentPath);
      setIsCreatingFolder(false);
      setNewFolderName('');
    } catch (error: any) {
      console.error("Error creating folder:", error);
      setUploadError(`Failed to create folder: ${error.message}`);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (doc: any, isFolder: boolean = false) => {
    if (!canUploadDocuments) {
      alert(translateUi("Only managers, company administrators, and system administrators can delete documents. You can view files stored here."));
      return;
    }
    setConfirmState({
      isOpen: true,
      title: `Delete ${isFolder ? translateUi("Folder") : translateUi("Document")}`,
      message: `Are you sure you want to delete ${doc.name}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/documents/${doc.id}${rc}`, {
            method: 'DELETE',
            headers: companyHeaders,
          });
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((payload as { error?: string })?.error || `Failed to delete (${res.status})`);
          }
          await fetchDocuments(currentPath);
        } catch (error: any) {
          console.error("Error deleting document:", error);
          alert(error?.message || translateUi("Failed to delete document."));
        }
        setConfirmState(null);
      }
    });
  };

  const handleDownloadDocument = async (doc: any) => {
    try {
      const res = await fetch(`/api/documents/${doc.id}/download${rc}`, { headers: companyHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string })?.error || `Failed to download (${res.status})`);
      }
      const content = String((data as { content?: string }).content ?? "");
      if (!content) throw new Error("Document content was empty");

      // Create a temporary link to download the base64 content
      const a = document.createElement("a");
      a.href = content.startsWith("data:") ? content : `data:application/octet-stream;base64,${content}`;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {
      console.error("Error downloading document:", error);
      alert(error?.message || translateUi("Failed to download document."));
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteContent.trim() || noteSubmitting || !canComposeNotes) return;
    setNoteError(null);
    setNoteSubmitting(true);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...companyHeaders,
        ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
      };
      const postRes = await fetch(`/api/employees/${employeeId}/notes${cc}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          author_name: currentUserProfile?.full_name || currentUserTitle || "User",
          author_email: currentUserEmail || undefined,
          content: noteContent,
          conversation_id: activeNoteConversation?.id || undefined,
        })
      });
      const postBody = await postRes.json().catch(() => ({}));
      if (!postRes.ok) {
        throw new Error((postBody as { error?: string }).error || 'Failed to add note');
      }
      setNoteContent('');
      setNotesConversationFilter('active');
      // Refresh employee data to show new note
      const res = await fetch(`/api/employees/${employeeId}${cc}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error((data as { error?: string }).error || 'Note saved, but failed to refresh the list');
      }
      setEmployee(data);
      onEmployeeNotePosted?.();
    } catch (err) {
      console.error(err);
      setNoteError(err instanceof Error ? err.message : translateUi('Failed to add note'));
    } finally {
      setNoteSubmitting(false);
    }
  };

  const canManageNoteConversationRetention =
    Boolean(isSystemAdmin) || isAdmin || isManager || canEditEmployeeRecord;

  const handleStartNewNoteConversation = async () => {
    if (noteConversationBusy) return;
    if (
      !window.confirm(translateUi("Archive the current conversation and start a new one? Existing notes stay available under Archived conversations for 12 months unless locked."))
    ) {
      return;
    }
    setNoteConversationBusy(true);
    try {
      const res = await fetch(`/api/employees/${employeeId}/note-conversations${cc}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...companyHeaders,
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({
          created_by_name: currentUserProfile?.full_name || currentUserTitle || "",
        }),
      });
      const body = await res.json().catch(() => ({})) as {
        error?: string;
        conversations?: NonNullable<typeof employee>["note_conversations"];
      };
      if (!res.ok) throw new Error(body.error || translateUi("Failed to start conversation"));
      if (Array.isArray(body.conversations)) {
        setEmployee((prev) => (prev ? { ...prev, note_conversations: body.conversations } : prev));
      }
      setNotesConversationFilter("active");
      await refetchEmployee();
    } catch (err) {
      alert(err instanceof Error ? err.message : translateUi("Failed to start conversation"));
    } finally {
      setNoteConversationBusy(false);
    }
  };

  const handleLockNoteConversation = async (conversationId: string) => {
    if (noteConversationBusy) return;
    if (
      !window.confirm(translateUi("Lock this archived conversation? It will be kept indefinitely until a manager deletes it."))
    ) {
      return;
    }
    setNoteConversationBusy(true);
    try {
      const res = await fetch(
        `/api/employees/${employeeId}/note-conversations/${encodeURIComponent(conversationId)}/lock${cc}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...companyHeaders,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
          body: JSON.stringify({
            locked_by_name: currentUserProfile?.full_name || currentUserTitle || "",
          }),
        }
      );
      const body = await res.json().catch(() => ({})) as {
        error?: string;
        conversations?: NonNullable<typeof employee>["note_conversations"];
      };
      if (!res.ok) throw new Error(body.error || translateUi("Failed to lock conversation"));
      if (Array.isArray(body.conversations)) {
        setEmployee((prev) => (prev ? { ...prev, note_conversations: body.conversations } : prev));
      }
      await refetchEmployee();
    } catch (err) {
      alert(err instanceof Error ? err.message : translateUi("Failed to lock conversation"));
    } finally {
      setNoteConversationBusy(false);
    }
  };

  const handleUnlockNoteConversation = async (conversationId: string) => {
    if (noteConversationBusy || !canManageNoteConversationRetention) return;
    if (
      !window.confirm(translateUi("Unlock this conversation? It will be deleted automatically 12 months after it was archived (if still unlocked)."))
    ) {
      return;
    }
    setNoteConversationBusy(true);
    try {
      const res = await fetch(
        `/api/employees/${employeeId}/note-conversations/${encodeURIComponent(conversationId)}/unlock${cc}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...companyHeaders,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
          body: JSON.stringify({}),
        }
      );
      const body = await res.json().catch(() => ({})) as {
        error?: string;
        conversations?: NonNullable<typeof employee>["note_conversations"];
      };
      if (!res.ok) throw new Error(body.error || translateUi("Failed to unlock conversation"));
      if (Array.isArray(body.conversations)) {
        setEmployee((prev) => (prev ? { ...prev, note_conversations: body.conversations } : prev));
      }
      await refetchEmployee();
    } catch (err) {
      alert(err instanceof Error ? err.message : translateUi("Failed to unlock conversation"));
    } finally {
      setNoteConversationBusy(false);
    }
  };

  const handleDeleteNoteConversation = async (conversationId: string) => {
    if (noteConversationBusy || !canManageNoteConversationRetention) return;
    if (
      !window.confirm(translateUi("Permanently delete this archived conversation and all of its notes? This cannot be undone."))
    ) {
      return;
    }
    setNoteConversationBusy(true);
    try {
      const res = await fetch(
        `/api/employees/${employeeId}/note-conversations/${encodeURIComponent(conversationId)}${cc}`,
        {
          method: "DELETE",
          headers: {
            ...companyHeaders,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
        }
      );
      const body = await res.json().catch(() => ({})) as {
        error?: string;
        conversations?: NonNullable<typeof employee>["note_conversations"];
      };
      if (!res.ok) throw new Error(body.error || translateUi("Failed to delete conversation"));
      if (Array.isArray(body.conversations)) {
        setEmployee((prev) => (prev ? { ...prev, note_conversations: body.conversations } : prev));
      }
      if (notesConversationFilter === conversationId) setNotesConversationFilter("active");
      await refetchEmployee();
    } catch (err) {
      alert(err instanceof Error ? err.message : translateUi("Failed to delete conversation"));
    } finally {
      setNoteConversationBusy(false);
    }
  };

  const handlePrintNotes = () => {
    const empName = String(employee?.full_name || "Employee")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const filterLabel =
      notesConversationFilter === "all"
        ? "All conversations"
        : notesConversationFilter === "active"
          ? activeNoteConversation?.title || "Current conversation"
          : noteConversations.find((c) => c.id === notesConversationFilter)?.title || "Conversation";
    const rows = filteredNotesForView
      .map((note: any) => {
        const author = String(note.author_name || "Unknown")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        const when = formatDateTimeUK(note.timestamp);
        const content = String(note.content || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        return `<div class="note"><div class="meta"><strong>${author}</strong> · ${when}</div><div class="body">${content}</div></div>`;
      })
      .join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Employee Messaging & Notes — ${empName}</title>
<style>
  body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif; color: #0f172a; padding: 28px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .sub { color: #64748b; font-size: 13px; margin-bottom: 20px; }
  .note { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; }
  .meta { font-size: 12px; color: #64748b; margin-bottom: 6px; }
  .body { font-size: 14px; white-space: pre-wrap; line-height: 1.45; }
  @media print { body { padding: 12px; } }
</style></head><body>
  <h1>Employee Messaging &amp; Notes — ${empName}</h1>
  <div class="sub">${String(filterLabel).replace(/&/g, "&amp;").replace(/</g, "&lt;")} · Printed ${formatDateTimeUK(new Date().toISOString())} · ${filteredNotesForView.length} note${filteredNotesForView.length === 1 ? "" : "s"}</div>
  ${rows || "<p>No notes in this conversation.</p>"}
</body></html>`;
    // iframe print avoids blank pages from window.open(..., "noopener")
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Print notes");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      alert(translateUi("Unable to open print preview."));
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    window.setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        alert(translateUi("Unable to print notes."));
      } finally {
        window.setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch {
            /* ignore */
          }
        }, 1000);
      }
    }, 300);
  };

  const handleDeleteNote = async (noteId: string | number) => {
    if (!window.confirm(translateUi('Delete this note? This cannot be undone.'))) return;
    try {
      const headers: Record<string, string> = {
        ...companyHeaders,
        ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
      };
      const res = await fetch(`/api/employees/${employeeId}/notes/${encodeURIComponent(String(noteId))}${cc}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to delete note');
      }
      await refetchEmployee();
    } catch (err) {
      console.error(err);
      alert((err as Error)?.message || translateUi('Failed to delete note.'));
    }
  };

  const EMPLOYEE_PATCH_KEYS = ["id", "full_name", "dob", "place_of_birth", "ni_number", "gender_types", "role", "depot", "status", "start_date", "manager", "email", "phone", "work_phone", "phone_country_code", "address", "postcode", "emergency_contact_name", "emergency_contact_country_code", "emergency_contact_phone", "emergency_contact_relationship", "image_url", "work_status", "monitoring_status", "licence_image_url", "licence_back_image_url", "licence_number", "licence_language", "licence_issue_date", "licence_expiry", "licence_restrictions", "licence_categories", "signature_url", "licence_details_added_at", "licence_details_added_by", "licence_details_confirmed_at", "licence_details_confirmed_by", "licence_details_deleted_at", "licence_details_deleted_by", "experience_records_json", "cab_passes_json"] as const;
  const buildEmployeePatchFromEditForm = (baseline: Employee, form: Partial<Employee> ): Partial <Employee> => {
    const out: Partial<Employee> = {};
    for (const k of EMPLOYEE_PATCH_KEYS) {
      if (k === "id") continue;
      const key = k as keyof Employee;
      if (!Object.prototype.hasOwnProperty.call(form, key)) continue;
      const next = form[key];
      const prev = baseline[key];
      if (next === prev) continue;
      if (next !== null && typeof next === "object" && prev !== null && typeof prev === "object") {
        if (JSON.stringify(next) === JSON.stringify(prev)) continue;
      }
      (out as Record<string, unknown>)[k] = next;
    }
    return out;
  };
  const handleUpdate = async (
    updates: Partial<Employee>,
    options?: { quiet?: boolean },
  ): Promise<boolean> => {
    const filtered = Object.fromEntries(
      Object.entries(updates).filter(([k]) => EMPLOYEE_PATCH_KEYS.includes(k as any))
    ) as Partial<Employee>;
    // Team-user role alerts are only relevant when identity/role fields change (or an explicit
    // sync-only save). Incidental patches (e.g. monitoring_status from medication checks) must stay quiet.
    const linkedUserSyncNotifyKeys = new Set(["role", "full_name", "email"]);
    const filteredKeys = Object.keys(filtered);
    const notifyLinkedUserSync =
      filteredKeys.length === 0 || filteredKeys.some((k) => linkedUserSyncNotifyKeys.has(k));
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (effectiveCompanyCode) (headers as Record<string, string>)['X-Company-Code'] = effectiveCompanyCode;
      if (currentUserEmail) (headers as Record<string, string>)['X-User-Email'] = currentUserEmail;
      const body = {
        ...filtered,
        sync_linked_user: true,
        ...(effectiveCompanyCode ? { companyCode: effectiveCompanyCode } : {}),
      };
      const res = await fetch(`/api/employees/${employeeId}${cc}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const raw = String((data as { error?: string }).error || "").trim();
        const isBooting = /^starting$/i.test(raw) || /still starting|upstream timeout/i.test(raw);
        if (!options?.quiet) {
          alert(
            isBooting
              ? translateUi("The server is still starting. Wait a few seconds and try again.")
              : raw || translateUi("Update failed"),
          );
        }
        return false;
      }
      const newId = (data as { newId?: string }).newId;
      if (newId) onEmployeeIdChanged?.(newId);
      // Prefer server-materialized image URL (Azure SAS) over the local data URL before refetch.
      const patchedImageUrl = (data as { image_url?: unknown }).image_url;
      const patchedLicenceFront = (data as { licence_image_url?: unknown }).licence_image_url;
      const patchedLicenceBack = (data as { licence_back_image_url?: unknown }).licence_back_image_url;
      if (
        (typeof patchedImageUrl === "string" && Object.prototype.hasOwnProperty.call(filtered, "image_url")) ||
        (typeof patchedLicenceFront === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_image_url")) ||
        (typeof patchedLicenceBack === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_back_image_url"))
      ) {
        setEmployee((prev) =>
          prev
            ? {
                ...prev,
                ...(typeof patchedImageUrl === "string" &&
                Object.prototype.hasOwnProperty.call(filtered, "image_url")
                  ? { image_url: patchedImageUrl }
                  : {}),
                ...(typeof patchedLicenceFront === "string" &&
                Object.prototype.hasOwnProperty.call(filtered, "licence_image_url")
                  ? { licence_image_url: patchedLicenceFront }
                  : {}),
                ...(typeof patchedLicenceBack === "string" &&
                Object.prototype.hasOwnProperty.call(filtered, "licence_back_image_url")
                  ? { licence_back_image_url: patchedLicenceBack }
                  : {}),
              }
            : prev,
        );
      }
      const refreshed = await refetchEmployee();
      setEditForm((prev) => {
        const next = { ...prev, ...filtered };
        if (typeof patchedImageUrl === "string" && Object.prototype.hasOwnProperty.call(filtered, "image_url")) {
          next.image_url = patchedImageUrl;
        }
        if (
          typeof patchedLicenceFront === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_image_url")
        ) {
          next.licence_image_url = patchedLicenceFront;
        }
        if (
          typeof patchedLicenceBack === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_back_image_url")
        ) {
          next.licence_back_image_url = patchedLicenceBack;
        }
        return next;
      });
      if (refreshed) {
        onEmployeePatched?.(refreshed);
      } else if (employee) {
        onEmployeePatched?.({
          ...employee,
          ...filtered,
          ...(typeof patchedImageUrl === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "image_url")
            ? { image_url: patchedImageUrl }
            : {}),
          ...(typeof patchedLicenceFront === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_image_url")
            ? { licence_image_url: patchedLicenceFront }
            : {}),
          ...(typeof patchedLicenceBack === "string" &&
          Object.prototype.hasOwnProperty.call(filtered, "licence_back_image_url")
            ? { licence_back_image_url: patchedLicenceBack }
            : {}),
        });
      }
      try {
        const bc = new BroadcastChannel(`rail-vault-employee-${employeeId}`);
        bc.postMessage("employee-patch");
        bc.close();
      } catch {
        /* ignore */
      }
      try {
        const teamBc = new BroadcastChannel('rail-vault-team-users');
        teamBc.postMessage('refresh');
        teamBc.close();
      } catch {
        /* ignore */
      }
      if (notifyLinkedUserSync) {
        const linkedUserSync = (data as {
          linkedUserSync?: {
            synced?: boolean;
            reason?: string;
            employeeRole?: string;
            userTitle?: string;
            updatedUserIds?: string[];
          };
        }).linkedUserSync;
        if (!linkedUserSync) {
          console.warn('[employee save] server did not return linkedUserSync — restart the Node server after deploy');
        } else if (linkedUserSync.synced) {
          alert(translateUi('Team user role updated to "{{role}}".', { role: linkedUserSync.employeeRole || translateUi('employee role') }));
        } else if (linkedUserSync.reason === 'user_not_found') {
          alert(translateUi("Employee record saved, but no matching team user was found. Ensure the team user has the same Employee ID (EMP001), email, or full name as this employee record."));
        } else if (linkedUserSync.reason === 'nominated_company_admin') {
          alert(translateUi("Employee record saved. Role title was not changed because this user is a nominated company administrator."));
        } else if (linkedUserSync.reason === 'already_up_to_date') {
          alert(
            translateUi(
              'Employee record saved. Team user title already matches "{{role}}". Refresh Team Management if it still shows "{{oldTitle}}".',
              {
                role: linkedUserSync.employeeRole || translateUi('employee role'),
                oldTitle: linkedUserSync.userTitle || translateUi('the old title'),
              },
            ),
          );
        } else if (linkedUserSync.reason === 'no_backend' || linkedUserSync.reason === 'not_rtdb') {
          alert(translateUi("Employee record saved, but team user sync is unavailable on this server configuration."));
        }
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleUpdateRef = useRef(handleUpdate);
  handleUpdateRef.current = handleUpdate;

  const commitCabPassesNowWithPending = useCallback(async (json: string) => {
    pendingCabPassPatchRef.current += 1;
    lastCommittedCabPassesJsonRef.current = json;
    try {
      return await handleUpdateRef.current({ cab_passes_json: json });
    } finally {
      pendingCabPassPatchRef.current = Math.max(0, pendingCabPassPatchRef.current - 1);
    }
  }, []);

  const onCabPassesJsonChangeStable = useCallback((json: string) => {
    setEditForm((prev) => (prev.cab_passes_json === json ? prev : { ...prev, cab_passes_json: json }));
  }, []);

  /** After server employee updates (e.g. cab passes edited on another device), mirror cab_passes_json into editForm so the Cab Passes tab keeps showing server truth. */
  useEffect(() => {
    if (pendingCabPassPatchRef.current > 0) return;
    const v = employee?.cab_passes_json;
    if (v === undefined) return;
    setEditForm((prev) => {
      if (prev.cab_passes_json === v) return prev;
      const local = prev.cab_passes_json;
      const committed = lastCommittedCabPassesJsonRef.current;
      // A PATCH completed with an older snapshot while the user kept typing — keep local edits.
      if (
        typeof local === "string" &&
        committed != null &&
        local !== committed &&
        local !== v
      ) {
        return prev;
      }
      return { ...prev, cab_passes_json: v };
    });
  }, [employee?.cab_passes_json]);

  const LICENCE_DELETION_AUDIT_CLEAR: Pick<Employee, 'licence_details_deleted_at' | 'licence_details_deleted_by'> = {
    licence_details_deleted_at: '',
    licence_details_deleted_by: '',
  };

  /** Require confirmation when editing unconfirmed licence details (scan or manual). */
  const requireLicenceDetailsConfirmationIfNeeded = () => {
    const alreadyConfirmed = Boolean(
      String(employee?.licence_details_confirmed_at ?? '').trim() &&
        String(employee?.licence_details_confirmed_by ?? '').trim(),
    );
    if (alreadyConfirmed) return;
    setLicenceScanRequiresConfirmation(true);
    setLicenceScanDetailsConfirmed(false);
  };

  /**
   * Full profile save triggered from licence confirmation — same as top Save Changes,
   * then exits editing mode so confirmed details are locked in.
   */
  const saveLicenceDetailsSection = async (opts?: {
    forceConfirmed?: boolean;
  }): Promise<boolean> => {
    if (!employee || !isEditing || savingMainChanges || licenceExtracting) return false;
    const confirmed = Boolean(opts?.forceConfirmed || licenceScanDetailsConfirmed);
    const fieldErrors = Object.values(licenceFieldHighlight).some((v) => v === "error");
    if (fieldErrors) {
      alert(translateUi("Manual entry is required for fields highlighted in red before confirming licence details."));
      return false;
    }
    if (licenceScanRequiresConfirmation && !confirmed) {
      alert(translateUi("Please confirm the licence details are correct (open the Licence tab and tick the confirmation box) before saving."));
      return false;
    }
    const pendingManager = (editForm.manager ?? employee.manager ?? "").trim();
    if (pendingManager && isSamePersonName(pendingManager, employee.full_name)) {
      alert(translateUi("An employee cannot be their own assessing manager. Choose a different assessor with the Assessor additional role."));
      return false;
    }
    if (
      pendingManager &&
      !assessorEligibleManagers.some((m) => isSamePersonName(m.full_name, pendingManager))
    ) {
      alert(translateUi("Assessing manager must be a team member with the Assessor additional role."));
      return false;
    }

    let allowedUpdates = buildEmployeePatchFromEditForm(employee, editForm);
    if (typeof allowedUpdates.manager === "string") {
      allowedUpdates.manager = allowedUpdates.manager.trim();
    }
    const saveStamp = new Date().toISOString();
    const hasAddedAuditPersisted =
      String(employee.licence_details_added_at ?? "").trim() &&
      String(employee.licence_details_added_by ?? "").trim();
    if (confirmed) {
      allowedUpdates = {
        ...allowedUpdates,
        licence_details_confirmed_at: saveStamp,
        licence_details_confirmed_by: licenceDetailsUserLabel,
        ...(!hasAddedAuditPersisted
          ? { licence_details_added_at: saveStamp, licence_details_added_by: licenceDetailsUserLabel }
          : {}),
      };
    }
    const nextFrontUrl = Object.prototype.hasOwnProperty.call(allowedUpdates, "licence_image_url")
      ? String((allowedUpdates as { licence_image_url?: string }).licence_image_url ?? "").trim()
      : String(employee.licence_image_url ?? "").trim();
    const nextBackUrl = Object.prototype.hasOwnProperty.call(allowedUpdates, "licence_back_image_url")
      ? String((allowedUpdates as { licence_back_image_url?: string }).licence_back_image_url ?? "").trim()
      : String(employee.licence_back_image_url ?? "").trim();
    const willHaveLicenceMedia = Boolean(nextFrontUrl || nextBackUrl);
    if (willHaveLicenceMedia && !hasAddedAuditPersisted) {
      allowedUpdates = {
        ...allowedUpdates,
        licence_details_added_at: saveStamp,
        licence_details_added_by: licenceDetailsUserLabel,
      };
    }

    setSavingMainChanges(true);
    try {
      const ok = await handleUpdate(
        Object.keys(allowedUpdates).length === 0 ? {} : allowedUpdates,
      );
      if (ok) {
        setEditForm({});
        setIsEditing(false);
        setLicenceScanRequiresConfirmation(false);
        setLicenceScanDetailsConfirmed(false);
        setLicenceFieldHighlight({});
        setLicenceExtractNotice(null);
        setLicenceSideCorrectionNotice(null);
      }
      return ok;
    } finally {
      setSavingMainChanges(false);
    }
  };

  const withLicenceAddedAudit = (updates: Partial<Employee> ): Partial <Employee> => {
    const touchesLicenceImage =
      Object.prototype.hasOwnProperty.call(updates, 'licence_image_url') ||
      Object.prototype.hasOwnProperty.call(updates, 'licence_back_image_url');
    if (!touchesLicenceImage) return updates;
    const nextFront = typeof updates.licence_image_url === 'string' ? updates.licence_image_url : employee.licence_image_url;
    const nextBack = typeof updates.licence_back_image_url === 'string' ? updates.licence_back_image_url : employee.licence_back_image_url;
    const hadAny = Boolean((employee.licence_image_url || '').trim() || (employee.licence_back_image_url || '').trim());
    const willHaveAny = Boolean((nextFront || '').trim() || (nextBack || '').trim());
    const hasAddedAudit = Boolean((employee.licence_details_added_at || '').trim() && (employee.licence_details_added_by || '').trim());
    if (!willHaveAny) return { ...updates, licence_details_added_at: '', licence_details_added_by: '' };
    if (!hadAny || !hasAddedAudit) {
      return {
        ...updates,
        licence_details_added_at: new Date().toISOString(),
        licence_details_added_by: licenceDetailsUserLabel,
      };
    }
    return updates;
  };

  /** Remove one licence photo and the fields typically read from that side only. */
  const applyLicenceSideRemoval = async (side: 'front' | 'back'): Promise<boolean> => {
    const patch: Partial<Employee> =
      side === 'front'
        ? {
            licence_image_url: '',
            licence_number: '',
            licence_issue_date: '',
            licence_expiry: '',
            licence_details_confirmed_at: '',
            licence_details_confirmed_by: '',
            ...LICENCE_DELETION_AUDIT_CLEAR,
          }
        : {
            licence_back_image_url: '',
            licence_language: '',
            licence_restrictions: '',
            licence_details_confirmed_at: '',
            licence_details_confirmed_by: '',
            ...LICENCE_DELETION_AUDIT_CLEAR,
          };
    const ok = await handleUpdate(patch);
    if (ok) {
      setLicenceScanRequiresConfirmation(false);
      setLicenceScanDetailsConfirmed(false);
      setLicenceExtractNotice(null);
      setLicenceFieldHighlight((prev) => {
        const next = { ...prev };
        if (side === 'front') {
          delete next.licence_number;
          delete next.licence_issue_date;
          delete next.licence_expiry;
        } else {
          delete next.licence_language;
          delete next.licence_restrictions;
        }
        return next;
      });
    }
    return ok;
  };

  const pickDroppedImageFile = (dt: DataTransfer | null): File | null => {
    if (!dt) return null;
    const fromFiles = Array.from(dt.files || []).find(isLikelyLicenceImageFile);
    if (fromFiles) return fromFiles;
    for (const item of Array.from(dt.items || [])) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (isLikelyLicenceImageFile(file)) return file;
    }
    return null;
  };

  const ingestLicenceImageFile = async (file: File, side: "front" | "back"): Promise<void> => {
    if (!canEdit || !isEditing) return;
    if (!isLikelyLicenceImageFile(file)) {
      alert(translateUi("Please drop a licence photo (JPEG, PNG, WebP, or HEIC)."));
      return;
    }
    try {
      const dataUrl = await processLicenceFile(file);
      if (isMobileOrTablet) {
        const suitability = await assessLicencePhotoSuitability(dataUrl);
        if (!suitability.ok) {
          setLicencePhotoSuitability({ side, dataUrl, issues: suitability.issues });
          return;
        }
        setLicenceImageEdit({
          dataUrl,
          field: side === "front" ? "licence_image_url" : "licence_back_image_url",
          rotation: 0,
        });
        return;
      }
      setLicenceScanSide(side);
      setLicenceScanVisualState("scanning");
      setShowLicenceScanningModal(true);
      try {
        await commitLicenceImageUpload(dataUrl, side);
      } finally {
        window.setTimeout(() => {
          setShowLicenceScanningModal(false);
          setLicenceScanVisualState("scanning");
        }, 850);
      }
    } catch (err) {
      console.error(err);
      alert(translateUi("Could not read that image. Try a JPEG or PNG export of the licence photo."));
    }
  };

  const commitLicenceImageUpload = async (
    rawDataUrl: string,
    uploadedToSide: "front" | "back",
  ): Promise<void> => {
    const fit = await fitImageToAspect(rawDataUrl, LICENCE_HOLDER_ASPECT);
    let correctSide = uploadedToSide;
    let userAcceptedCorrection = false;

    try {
      const classified = await classifyLicenceSideFromImage(rawDataUrl);
      if (classified.side !== "unknown" && classified.side !== uploadedToSide) {
        if (classified.confidence === "high") {
          correctSide = classified.side;
          userAcceptedCorrection = true;
        } else if (classified.confidence === "medium") {
          const confirmMsg =
            classified.side === "front"
              ? translateUi(
                  "This photo looks like the front of the licence, not the back. Move it to the Front holder and scan again?",
                )
              : translateUi(
                  "This photo looks like the back of the licence, not the front. Move it to the Back holder and scan again?",
                );
          if (window.confirm(confirmMsg)) {
            correctSide = classified.side;
            userAcceptedCorrection = true;
          }
        }
      }
    } catch (err) {
      console.warn("[licence-ocr] side detection failed", err);
    }

    const currentFront = String(employee?.licence_image_url ?? editForm.licence_image_url ?? "").trim();
    const currentBack = String(employee?.licence_back_image_url ?? editForm.licence_back_image_url ?? "").trim();
    const { licence_image_url, licence_back_image_url, swapped } = buildLicenceSideSwapPatch({
      imageDataUrl: fit,
      uploadedToSide,
      correctSide,
      currentFrontUrl: currentFront,
      currentBackUrl: currentBack,
    });

    const patch: Partial<Employee> = { ...LICENCE_DELETION_AUDIT_CLEAR };
    if (licence_image_url !== undefined) patch.licence_image_url = licence_image_url;
    if (licence_back_image_url !== undefined) patch.licence_back_image_url = licence_back_image_url;

    const ok = await handleUpdate(withLicenceAddedAudit(patch), { quiet: true });
    if (!ok) return;

    if (userAcceptedCorrection && swapped) {
      const exchangedBoth =
        (correctSide === "front" && Boolean(currentFront)) ||
        (correctSide === "back" && Boolean(currentBack));
      const correctionNotice = exchangedBoth
        ? correctSide === "front"
          ? translateUi(
              "Corrected: this photo is the front of the licence, so we swapped the holders — Front now shows this photo, and the previous Front photo moved to Back. We then scanned both sides.",
            )
          : translateUi(
              "Corrected: this photo is the back of the licence, so we swapped the holders — Back now shows this photo, and the previous Back photo moved to Front. We then scanned both sides.",
            )
        : correctSide === "front"
          ? translateUi(
              "Corrected: this photo looks like the front of the licence, so we moved it to the Front holder and scanned it there.",
            )
          : translateUi(
              "Corrected: this photo looks like the back of the licence, so we moved it to the Back holder and scanned it there.",
            );
      setLicenceSideCorrectionNotice(correctionNotice);
    } else if (!swapped) {
      setLicenceSideCorrectionNotice(null);
    }

    setLicenceScanSide(correctSide);

    if (correctSide === "front") {
      await runLicenceFrontExtraction(rawDataUrl);
      if (swapped && licence_back_image_url) {
        await runLicenceBackExtraction(licence_back_image_url);
      }
    } else {
      await runLicenceBackExtraction(rawDataUrl);
      if (swapped && licence_image_url) {
        await runLicenceFrontExtraction(licence_image_url);
      }
    }
  };

  /** Always preventDefault on dragover/drop so the browser never navigates to the file. */
  const licenceDropHandlers = (side: "front" | "back") => ({
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canEdit) return;
      setLicenceDropTarget(side);
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        e.dataTransfer.dropEffect = canEdit ? "copy" : "none";
      } catch {
        /* ignore */
      }
      if (!canEdit) return;
      setLicenceDropTarget(side);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const related = e.relatedTarget as Node | null;
      if (related && e.currentTarget.contains(related)) return;
      setLicenceDropTarget((prev) => (prev === side ? null : prev));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setLicenceDropTarget(null);
      if (!canEdit) return;
      if (!isEditing) {
        alert(translateUi(LICENCE_UPLOAD_NEED_EDIT_MSG));
        return;
      }
      const file = pickDroppedImageFile(e.dataTransfer);
      if (!file) {
        alert(translateUi("Please drop a licence photo (JPEG, PNG, WebP, or HEIC)."));
        return;
      }
      void ingestLicenceImageFile(file, side);
    },
  });

  const licenceDropZoneClass = (side: "front" | "back") =>
    cn(
      "relative w-full bg-white rounded-xl border-2 border-dashed overflow-hidden group transition-colors",
      licenceDropTarget === side
        ? isEditing
          ? "border-sky-400 bg-sky-50 ring-2 ring-sky-300/60"
          : "border-amber-500 bg-amber-50 ring-2 ring-amber-300/60"
        : "border-white/40",
    );

  const renderLicenceSpecimenPlaceholder = (specimenSrc: string, emptyViewLabel: string) => (
    <div className="absolute inset-0 pointer-events-none">
      <img
        src={specimenSrc}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-contain p-2 opacity-95"
      />
      <div className="absolute inset-x-0 bottom-0 flex justify-center p-2.5 bg-gradient-to-t from-slate-100/95 via-slate-100/80 to-transparent">
        <p className="text-xs sm:text-sm text-slate-600 font-medium text-center px-2 py-1 rounded-lg bg-white/80 border border-slate-200/80 shadow-sm">
          {canEdit && isEditing
            ? translateUi(LICENCE_HOLDER_UPLOAD_HINT)
            : translateUi(emptyViewLabel)}
        </p>
      </div>
    </div>
  );

  const handleSaveCompetency = async () => {
    try {
      const assessedBy = (competencyForm.assessed_by || '').trim();
      if (assessedBy && isSamePersonName(assessedBy, employee?.full_name)) {
        alert(translateUi("An employee cannot assess themselves. Choose a different assessor with the Assessor additional role."));
        return;
      }
      if (assessedBy && !assessorEligibleManagers.some((m) => isSamePersonName(m.full_name, assessedBy))) {
        alert(translateUi("Assessed by must be a team member with the Assessor additional role."));
        return;
      }
      if (editingCompetencyId) {
        const res = await fetch(`/api/competencies/${editingCompetencyId}${rc}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(competencyForm)
        });
        if (!res.ok) throw new Error((await res.text()) || 'Failed to save competency');
      }
      const editedId = String(editingCompetencyId ?? '');
      if (editedId) {
        setEmployee(prev => {
          if (!prev) return prev;
          const nextCompetencies = (prev.competencies ?? []).map((c) => {
            const rowId = String(c.id ?? '');
            const rowKey = String((c as { rtdb_key?: string }).rtdb_key ?? '');
            if (rowId !== editedId && rowKey !== editedId) return c;
            return { ...c, ...competencyForm };
          });
          return { ...prev, competencies: nextCompetencies };
        });
      }
      window.setTimeout(() => { void refetchEmployee(); }, 900);
      setEditingCompetencyId(null);
      setCompetencyForm({});
      setIsAddingTraction(false);
      setIsAddingRoute(false);
    } catch (err) {
      console.error(err);
      alert((err as Error)?.message || translateUi('Failed to save competency.'));
    }
  };

  const handleSaveNewTraction = async () => {
    const selectedTraction = String(newTractionForm.type ?? '').trim();
    if (!selectedTraction || !companyTractionTypes.includes(selectedTraction)) {
      alert(translateUi("Select traction from Company Configuration > Traction, Routes & Depots before saving."));
      return;
    }
    const assessedBy = (newTractionForm.assessed_by || '').trim();
    if (assessedBy && isSamePersonName(assessedBy, employee?.full_name)) {
      alert(translateUi("An employee cannot assess themselves. Choose a different assessor with the Assessor additional role."));
      return;
    }
    if (assessedBy && !assessorEligibleManagers.some((m) => isSamePersonName(m.full_name, assessedBy))) {
      alert(translateUi("Assessed by must be a team member with the Assessor additional role."));
      return;
    }
    try {
      const res = await fetch(`/api/employees/${employeeId}/competencies${cc}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newTractionForm, type: selectedTraction, competency_category: 'traction' as const })
      });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to add traction');
      const created = await res.json().catch(() => ({} as { id?: number | string }));
      const createdId = created?.id != null ? String(created.id) : '';
      const newCompetency: Partial<Competency> = {
        ...(newTractionForm || {}),
        id: createdId || `tmp_traction_${Date.now()}`,
        competency_category: 'traction',
      };
      setEmployee(prev => prev ? { ...prev, competencies: [...(prev.competencies ?? []), newCompetency as Competency] } : prev);
      window.setTimeout(() => { void refetchEmployee(); }, 900);
      setNewTractionForm({});
      setIsAddingTraction(false);
      setIsAddTractionModalOpen(false);
    } catch (err) {
      console.error(err);
      alert((err as Error)?.message || translateUi('Failed to add traction.'));
    }
  };

  const handleSaveNewRoute = async () => {
    const selectedRoute = String(newRouteForm.type ?? '').trim();
    if (!selectedRoute || !companyRouteTypes.includes(selectedRoute)) {
      alert(translateUi("Select route from Company Configuration > Traction, Routes & Depots before saving."));
      return;
    }
    const assessedBy = (newRouteForm.assessed_by || '').trim();
    if (assessedBy && isSamePersonName(assessedBy, employee?.full_name)) {
      alert(translateUi("An employee cannot assess themselves. Choose a different assessor with the Assessor additional role."));
      return;
    }
    if (assessedBy && !assessorEligibleManagers.some((m) => isSamePersonName(m.full_name, assessedBy))) {
      alert(translateUi("Assessed by must be a team member with the Assessor additional role."));
      return;
    }
    try {
      const res = await fetch(`/api/employees/${employeeId}/competencies${cc}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newRouteForm, type: selectedRoute, competency_category: 'route' as const })
      });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to add route');
      const created = await res.json().catch(() => ({} as { id?: number | string }));
      const createdId = created?.id != null ? String(created.id) : '';
      const newCompetency: Partial<Competency> = {
        ...(newRouteForm || {}),
        id: createdId || `tmp_route_${Date.now()}`,
        competency_category: 'route',
      };
      setEmployee(prev => prev ? { ...prev, competencies: [...(prev.competencies ?? []), newCompetency as Competency] } : prev);
      window.setTimeout(() => { void refetchEmployee(); }, 900);
      setNewRouteForm({});
      setIsAddingRoute(false);
      setIsAddRouteModalOpen(false);
    } catch (err) {
      console.error(err);
      alert((err as Error)?.message || translateUi('Failed to add route.'));
    }
  };

  const handleDeleteCompetency = async (id?: number | string) => {
    try {
      if (id == null) throw new Error('Missing competency id');
      const res = await fetch(`/api/competencies/${id}${rc}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to delete competency');
      const deletedId = String(id);
      setEmployee(prev => {
        if (!prev) return prev;
        const nextCompetencies = (prev.competencies ?? []).filter((c) => {
          const rowId = String(c.id ?? '');
          const rowKey = String((c as { rtdb_key?: string }).rtdb_key ?? '');
          return rowId !== deletedId && rowKey !== deletedId;
        });
        return { ...prev, competencies: nextCompetencies };
      });
      window.setTimeout(() => { void refetchEmployee(); }, 900);
    } catch (err) {
      console.error(err);
      alert((err as Error)?.message || translateUi('Failed to delete competency.'));
    } finally {
      setConfirmState(null);
    }
  };

  const handleCompetencyUpload = async (id: number | string, file: File) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const res = await fetch(`/api/competencies/${id}${rc}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ certificate_url: reader.result as string })
        });
        if (!res.ok) throw new Error((await res.text()) || "Upload failed");
        const targetId = String(id);
        setEmployee(prev => {
          if (!prev) return prev;
          const nextCompetencies = (prev.competencies ?? []).map((c) => {
            const rowId = String(c.id ?? '');
            const rowKey = String((c as { rtdb_key?: string }).rtdb_key ?? '');
            if (rowId !== targetId && rowKey !== targetId) return c;
            return { ...c, certificate_url: reader.result as string };
          });
          return { ...prev, competencies: nextCompetencies };
        });
        window.setTimeout(() => { void refetchEmployee(); }, 900);
      } catch (err) {
        console.error(err);
        alert((err as Error)?.message || translateUi('Failed to upload competency attachment.'));
      }
    };
    reader.readAsDataURL(file);
  };

  const runAnalysis = () => {
    if (!isVerificationModuleActive) {
      setAnalysis('Please purchase the QA Verifications module via your company admin.');
      return;
    }
    setAnalysis(null);
    setVerificationOpen(true);
  };

  const isVerifiedTrainDrivingLicenceNumber = (raw: string): boolean => {
    const compact = raw.replace(/\s+/g, '').toUpperCase();
    return /^UK7120\d{6}$/.test(compact);
  };

  const runLicenceFrontExtraction = async (frontImageDataUrl: string): Promise<void> => {
    if (!isEditing) return;
    setLicenceScanSide('front');
    setLicenceExtractNotice(null);
    setLicenceExtracting(true);
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (effectiveCompanyCode) (headers as Record<string, string>)['X-Company-Code'] = effectiveCompanyCode;
      const body = effectiveCompanyCode
        ? { image: frontImageDataUrl, companyCode: effectiveCompanyCode }
        : { image: frontImageDataUrl };
      const res = await fetch(`/api/employees/${employeeId}/extract-licence-front${cc}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      let data = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        data = {};
      }
      let licenceNo = typeof data.licence_number === 'string' ? data.licence_number.trim() : '';
      let issueDate = typeof data.licence_issue_date === 'string' ? data.licence_issue_date.trim() : '';
      let expiryDate = typeof data.licence_expiry === 'string' ? data.licence_expiry.trim() : '';
      if (!licenceNo || !issueDate || !expiryDate) {
        try {
          const local = await clientExtractLicenceFrontFromImage(frontImageDataUrl);
          if (!licenceNo && local.licence_number) licenceNo = local.licence_number.trim();
          if (!issueDate && local.licence_issue_date) issueDate = local.licence_issue_date.trim();
          if (!expiryDate && local.licence_expiry) expiryDate = local.licence_expiry.trim();
        } catch (clientErr) {
          console.warn('[licence-ocr] browser fallback failed', clientErr);
        }
      }
      if (!licenceNo && !issueDate && !expiryDate) {
        let sideHint = "";
        try {
          const classified = await classifyLicenceSideFromImage(frontImageDataUrl);
          if (classified.side === "back" && classified.confidence !== "low") {
            sideHint = translateUi(
              " This photo looks like the back of the licence — try uploading it to the back slot instead (or remove and re-add both photos).",
            );
          }
        } catch {
          /* ignore */
        }
        setLicenceExtractNotice(
          ((data as { error?: string }).error || "Could not auto-read licence fields.") +
            sideHint +
            translateUi(LICENCE_INTERNATIONAL_SCAN_FAIL_HINT),
        );
        setLicenceScanRequiresConfirmation(true);
        setLicenceScanDetailsConfirmed(false);
        setLicenceScanVisualState('partial');
        setLicenceFieldHighlight((prev) => ({
          ...prev,
          licence_number: 'error',
          licence_issue_date: 'error',
          licence_expiry: 'error',
        }));
        return;
      }
      const suggested: Partial<Employee> = {};
      const formOnly: Partial<Employee> = {};
      const loaded: string[] = [];
      const missing: string[] = [];
      if (licenceNo) {
        if (isVerifiedTrainDrivingLicenceNumber(licenceNo)) {
          suggested.licence_number = licenceNo.replace(/\s+/g, '').toUpperCase();
          loaded.push('licence number');
        } else {
          formOnly.licence_number = licenceNo;
          missing.push('licence number');
        }
      } else missing.push('licence number');
      if (issueDate) {
        suggested.licence_issue_date = issueDate;
        loaded.push('issue date');
      } else missing.push('issue date');
      if (expiryDate) {
        suggested.licence_expiry = expiryDate;
        loaded.push('expiry date');
      } else missing.push('expiry date');
      setLastLicenceExtractionMissing(missing);
      if (loaded.length === 0) {
        const unverifiedMsg =
          licenceNo && !isVerifiedTrainDrivingLicenceNumber(licenceNo)
            ? ' The licence number could not be verified: train numbers must start with UK7120 followed by six digits (e.g. UK7120123456). Check the value in the field and correct it manually if needed.'
            : '';
        setEditForm(prev => ({ ...prev, ...formOnly }));
        setLicenceScanRequiresConfirmation(true);
        setLicenceScanDetailsConfirmed(false);
        setLicenceExtractNotice(
          `${LICENCE_SCAN_REVIEW_PREAMBLE} No licence fields were confidently detected. Please enter them manually, then confirm they are correct before saving.${unverifiedMsg}${translateUi(LICENCE_INTERNATIONAL_SCAN_FAIL_HINT)}`
        );
        setLicenceScanVisualState('partial');
        setLicenceFieldHighlight((prev) => ({
          ...prev,
          licence_number: 'error',
          licence_issue_date: issueDate ? 'success' : 'error',
          licence_expiry: expiryDate ? 'success' : 'error',
        }));
        if (isMobileOrTablet) {
          setLicenceComplianceSide('front');
          setShowLicenceComplianceModal(true);
        }
        return;
      }
      setEditForm(prev => ({ ...prev, ...suggested, ...formOnly }));
      // Persist extracted values immediately so they survive refresh across devices.
      // Quiet: do not block scan UX with browser alerts (e.g. Azure boot "starting").
      await handleUpdate({ ...suggested, ...LICENCE_DELETION_AUDIT_CLEAR }, { quiet: true });
      const licenceUnverified = Boolean(licenceNo && !isVerifiedTrainDrivingLicenceNumber(licenceNo));
      let sideHint = "";
      if (licenceUnverified) {
        try {
          const classified = await classifyLicenceSideFromImage(frontImageDataUrl);
          if (classified.side === "back" && classified.confidence !== "low") {
            sideHint = translateUi(
              " This photo may be the back of the licence in the front slot — remove both photos and upload front and back to the correct holders.",
            );
          }
        } catch {
          /* ignore */
        }
      }
      const baseNotice = `Auto-filled: ${loaded.join(', ')}. Please review before saving.`;
      setLicenceScanRequiresConfirmation(true);
      setLicenceScanDetailsConfirmed(false);
      setLicenceExtractNotice(
        `${LICENCE_SCAN_REVIEW_PREAMBLE} ${
          licenceUnverified
            ? `${baseNotice} Licence number could not be verified: train driving numbers must start with UK7120 followed by six digits. The scanner read a value that does not match; please check the field and enter the number manually if needed.${sideHint}`
            : baseNotice
        }`
      );
      const scanComplete = loaded.length >= 3 && !licenceUnverified;
      setLicenceScanVisualState(scanComplete ? 'success' : 'partial');
      if (isMobileOrTablet && (!scanComplete || licenceUnverified)) {
        setLicenceComplianceSide('front');
        setShowLicenceComplianceModal(true);
      }
      setLicenceFieldHighlight((prev) => ({
        ...prev,
        licence_number: licenceUnverified ? 'error' : 'success',
        licence_issue_date: issueDate ? 'success' : 'error',
        licence_expiry: expiryDate ? 'success' : 'error',
      }));
    } catch (err) {
      console.error(err);
      setLicenceExtractNotice(
        translateUi("Could not auto-read licence fields.") + translateUi(LICENCE_INTERNATIONAL_SCAN_FAIL_HINT),
      );
      setLicenceScanRequiresConfirmation(true);
      setLicenceScanDetailsConfirmed(false);
      setLicenceScanVisualState('partial');
      setLicenceFieldHighlight((prev) => ({
        ...prev,
        licence_number: 'error',
        licence_issue_date: 'error',
        licence_expiry: 'error',
      }));
      if (isMobileOrTablet) {
        setLicenceComplianceSide('front');
        setShowLicenceComplianceModal(true);
      }
    } finally {
      setLicenceExtracting(false);
    }
  };

  const runLicenceBackExtraction = async (backImageDataUrl: string): Promise<void> => {
    if (!isEditing) return;
    setLicenceScanSide('back');
    setLicenceExtractNotice(null);
    setLicenceExtracting(true);
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (effectiveCompanyCode) (headers as Record<string, string>)['X-Company-Code'] = effectiveCompanyCode;
      const body = effectiveCompanyCode
        ? { image: backImageDataUrl, companyCode: effectiveCompanyCode }
        : { image: backImageDataUrl };
      const res = await fetch(`/api/employees/${employeeId}/extract-licence-back${cc}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      let data = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        data = {};
      }
      let language = typeof data.licence_language === 'string' ? data.licence_language.trim() : '';
      let restrictionsDetected =
        data.licence_restrictions_detected === true
        || data.licence_restrictions === 'Yes'
        || data.licence_restrictions === 'No';
      let restrictionsValue = typeof data.licence_restrictions === 'string' ? data.licence_restrictions : '';
      if (!language || !restrictionsDetected) {
        try {
          const local = await clientExtractLicenceBackFromImage(backImageDataUrl);
          if (!language && local.licence_language) language = local.licence_language.trim();
          if (!restrictionsDetected && local.licence_restrictions_detected && local.licence_restrictions) {
            restrictionsDetected = true;
            restrictionsValue = local.licence_restrictions;
          }
        } catch (clientErr) {
          console.warn('[licence-ocr] browser back fallback failed', clientErr);
        }
      }
      const suggested: Partial<Employee> = {};
      const loaded: string[] = [];
      const missing: string[] = [];
      if (language) {
        suggested.licence_language = language;
        loaded.push('language');
      } else {
        missing.push('language');
      }
      if (restrictionsDetected && restrictionsValue) {
        if (restrictionsValue === 'Yes') {
          suggested.licence_restrictions = translateUi("Yes");
          loaded.push('medical restriction (Yes)');
        } else {
          suggested.licence_restrictions = translateUi("No");
          loaded.push('medical restriction (No)');
        }
      } else {
        missing.push('medical restriction');
      }
      setLastLicenceExtractionMissing(missing);
      if (loaded.length === 0) {
        let sideHint = "";
        try {
          const classified = await classifyLicenceSideFromImage(backImageDataUrl);
          if (classified.side === "front" && classified.confidence !== "low") {
            sideHint = translateUi(
              " This photo looks like the front of the licence — try uploading it to the front slot instead (or remove and re-add both photos).",
            );
          }
        } catch {
          /* ignore */
        }
        setLicenceScanRequiresConfirmation(true);
        setLicenceScanDetailsConfirmed(false);
        setLicenceExtractNotice(
          `${LICENCE_SCAN_REVIEW_PREAMBLE} No back-of-licence fields were confidently detected. Please enter language and restrictions manually if needed, then confirm before saving.${sideHint}${translateUi(LICENCE_INTERNATIONAL_SCAN_FAIL_HINT)}`,
        );
        setLicenceScanVisualState('partial');
        setLicenceFieldHighlight((prev) => ({
          ...prev,
          licence_language: 'error',
          licence_restrictions: 'error',
        }));
        if (isMobileOrTablet) {
          setLicenceComplianceSide('back');
          setShowLicenceComplianceModal(true);
        }
        return;
      }
      setEditForm(prev => ({ ...prev, ...suggested }));
      await handleUpdate({ ...suggested, ...LICENCE_DELETION_AUDIT_CLEAR }, { quiet: true });
      setLicenceScanRequiresConfirmation(true);
      setLicenceScanDetailsConfirmed(false);
      setLicenceExtractNotice(
        `${LICENCE_SCAN_REVIEW_PREAMBLE} Auto-filled from back: ${loaded.join(', ')}. Please review before saving.`
      );
      setLicenceScanVisualState(missing.length === 0 ? 'success' : 'partial');
      if (isMobileOrTablet && missing.length > 0) {
        setLicenceComplianceSide('back');
        setShowLicenceComplianceModal(true);
      }
      const restrictionsRecognised = restrictionsDetected;
      setLicenceFieldHighlight((prev) => ({
        ...prev,
        licence_language: language ? 'success' : 'error',
        licence_restrictions: restrictionsRecognised ? 'success' : 'error',
      }));
    } catch (err) {
      console.error(err);
      setLicenceExtractNotice(
        translateUi("Could not auto-read back of licence.") + translateUi(LICENCE_INTERNATIONAL_SCAN_FAIL_HINT),
      );
      setLicenceScanRequiresConfirmation(true);
      setLicenceScanDetailsConfirmed(false);
      setLicenceScanVisualState('partial');
      setLicenceFieldHighlight((prev) => ({
        ...prev,
        licence_language: 'error',
        licence_restrictions: 'error',
      }));
      if (isMobileOrTablet) {
        setLicenceComplianceSide('back');
        setShowLicenceComplianceModal(true);
      }
    } finally {
      setLicenceExtracting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  if (profileAccessError) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={onBack} className="flex items-center text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> {translateUi("Back to list")}
        </button>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">{profileAccessError}</div>
      </div>
    );
  }
  if (!employee) return <div>{translateUi("Employee not found")}</div>;

  const isMedicallyUnfit = employee.medicals?.some(m => (m.is_latest === 1 || m.is_latest === '1' || m.is_latest === true) && (new Date(m.expiry_date) < new Date() || m.status === 'Unfit'));
  const isNotCompetent = employee.competencies?.some(c => new Date(c.expiry_date) < new Date());
  const licenceConfirmationBlocksSave =
    isEditing && licenceScanRequiresConfirmation && !licenceScanDetailsConfirmed;
  const hasLicenceScanFieldErrors = Object.values(licenceFieldHighlight).some((v) => v === 'error');

  const licenceFieldTileClass = (key: LicenceFieldHighlightKey) => {
    const h = licenceFieldHighlight[key];
    const base = cn(
      DASHBOARD_GLASS_TILE,
      'px-3 sm:px-4 py-3 min-w-0 max-w-full transition-colors duration-200',
      !isEditing && 'flex-1 flex flex-col justify-center',
    );
    if (h === 'success') return cn(base, 'border-emerald-400/50 bg-emerald-500/15');
    if (h === 'error') return cn(base, 'border-red-400/50 bg-red-500/15');
    return base;
  };

  return (
    <div className="space-y-6">
      <style>{EMPLOYEE_PROFILE_UI_STYLES}</style>
      {confirmState && (
        <ConfirmDialog 
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      {closeCycleDialog && (
        <CloseEmployeeCycleDialog
          isOpen
          cycleTitle={closeCycleDialog.cycleTitle}
          variant={closeCycleDialog.variant ?? 'manual'}
          confirming={
            closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(closeCycleDialog.ecId)
          }
          onCancel={() => {
            const busy =
              closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(closeCycleDialog.ecId);
            if (!busy) setCloseCycleDialog(null);
          }}
          onConfirm={() => performCloseEmployeeCycle(closeCycleDialog.ecId)}
        />
      )}
      {continueCycleDialog && (
        <ContinueEmployeeCycleDialog
          isOpen
          cycleTitle={continueCycleDialog.cycleTitle}
          variant={continueCycleDialog.variant ?? 'manual'}
          startFromToday={!!continueCycleDialog.startFromToday}
          confirming={
            continuingEmployeeCycleId != null &&
            String(continuingEmployeeCycleId) === String(continueCycleDialog.ecId)
          }
          onCancel={() => {
            const busy =
              continuingEmployeeCycleId != null &&
              String(continuingEmployeeCycleId) === String(continueCycleDialog.ecId);
            if (!busy) setContinueCycleDialog(null);
          }}
          onConfirm={() =>
            performContinueEmployeeCycle(continueCycleDialog.ecId, {
              startFromToday: !!continueCycleDialog.startFromToday,
            })
          }
        />
      )}
      {transferModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setTransferModalOpen(false)}>
          <div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{translateUi("Transfer employee to another company")}</h3>
              <button type="button" onClick={() => setTransferModalOpen(false)} className="p-2 text-slate-500 hover:text-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4"> {translateUi("Transfer")} <strong>{employee.full_name}</strong> to a different company. Their company code will be updated to the new company and remain locked. Their current email will be removed and a new email is required.
            </p>
            <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"> {translateUi("Verify personal information (unchanged by transfer)")} </h4>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <dt className="text-slate-500">{translateUi("Full name")}</dt>
                <dd className="font-medium text-slate-900">{employee.full_name}</dd>
                <dt className="text-slate-500">{translateUi("Date of birth")}</dt>
                <dd className="font-medium text-slate-900">{employee.dob || '—'}</dd>
                <dt className="text-slate-500">{translateUi("Current email")}</dt>
                <dd className="font-medium text-slate-600">{employee.email || '—'} <span className="text-amber-600 text-xs"> {translateUi("(will be removed)")} </span></dd>
              </dl>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">{translateUi("Target company")}</label>
              <select
                value={transferTargetCode}
                onChange={e => setTransferTargetCode(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{translateUi("Select company...")}</option>
                {companies.filter(c => c.id !== (employee.company_code || '')).map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2"> {translateUi("New email (required)")} </label>
              <input
                type="email"
                value={transferNewEmail}
                onChange={e => setTransferNewEmail(e.target.value)}
                placeholder="employee@newcompany.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-slate-500 mt-1">{translateUi("The employee's previous company email will be removed.")}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTransferModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-900"
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                disabled={!transferTargetCode || !transferNewEmail.trim() || transferring}
                onClick={async () => {
                  if (!transferTargetCode || !transferNewEmail.trim()) return;
                  if (!currentUserEmail) {
                    alert(translateUi("Your session email could not be determined. Please sign out and sign in again."));
                    return;
                  }
                  setTransferring(true);
                  try {
                    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail };
                    const res = await fetch(`/api/employees/${encodeURIComponent(employeeId)}/transfer-company`, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({ companyCode: transferTargetCode, newEmail: transferNewEmail.trim(), userEmail: currentUserEmail }),
                    });
                    if (res.ok) {
                      const newEmployee = await res.json();
                      setTransferModalOpen(false);
                      onTransferComplete?.(newEmployee);
                    } else {
                      const ct = res.headers.get('content-type');
                      let err: { error?: string } = {};
                      if (ct?.includes('json')) {
                        try { err = await res.json(); } catch { err = {}; }
                      } else {
                        const text = await res.text();
                        err = { error: text || res.statusText || `Transfer failed (${res.status})` };
                      }
                      let msg = err.error || res.statusText || `Transfer failed (${res.status})`;
                      if (res.status === 404 && !ct?.includes('json')) {
                        msg = 'API not reachable (404). Ensure you run "npm run dev" so the app and API are both served.';
                      }
                      alert(msg);
                    }
                  } catch (e) {
                    alert(translateUi('Transfer failed: {{error}}', { error: e instanceof Error ? e.message : String(e) }));
                  } finally {
                    setTransferring(false);
                  }
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 disabled:opacity-50"
              >
                {transferring ? translateUi("Transferring...") : translateUi("Transfer")}
              </button>
            </div>
          </div>
        </div>
      )}
      {viewingCycle ? (
        <div className={cn('space-y-6', isDarkMode && 'employee-profile-shell--dark')}>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewingCycle(null)}
              className={cn(
                'flex items-center transition-colors',
                isDarkMode ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900"
              )}
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> {translateUi("Back to Cycles")}
            </button>
          </div>
          {isViewingClosedCycleHistory && (
            <div
              className={`rounded-2xl border px-5 py-4 sm:px-6 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4 ${
                closedCycleSystemAdminOverride
                  ? 'border-amber-200/90 bg-gradient-to-br from-amber-50 via-white to-orange-50/40'
                  : 'border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50'
              }`}
            >
              <div
                className={`shrink-0 w-12 h-12 rounded-xl text-white flex items-center justify-center shadow-md ${
                  closedCycleSystemAdminOverride ? "bg-amber-700" : "bg-slate-900"
                }`}
              >
                <Archive className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 tracking-tight">
                  {closedCycleSystemAdminOverride
                    ? translateUi("Archived cycle — system administrator edit mode")
                    : translateUi("Archived cycle (read-only)")}
                </p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  {closedCycleSystemAdminOverride ? (
                    <>
                      You are viewing as a <span className="font-semibold text-slate-800">{translateUi("System Administrator")}</span> under company
                      impersonation. You may open each assessment event to change grades, notes, methods, and evidence. Changes are written to
                      the stored assessment record; use only for legitimate corrections.
                    </>
                  ) : (
                    <>
                      Open each assessment event to review grades, notes, methods, and evidence attachments exactly as recorded. Editing and
                      completion actions are not available.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
          <div className="viewing-cycle-pane bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                  <Award className="w-6 h-6 text-indigo-600" />
                  {viewingCycle.cycle_title}
                  <span className="text-slate-400 font-semibold">·</span>
                  <span className="text-slate-600 font-semibold text-lg">{translateUi("Assessment record")}</span>
                  {viewingCycleIsContinuous && !isViewingClosedCycleHistory ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-900 border border-sky-200"
                      title={translateUi("This cycle will renew when continuation requirements are met")}
                    >
                      <RotateCcw className="w-3 h-3" aria-hidden />
                      {translateUi("Set to continue")}
                      {viewingCycleRenewalCount > 0 ? ` · R${viewingCycleRenewalCount}` : ""}
                    </span>
                  ) : null}
                </h2>
                <p className="text-sm text-slate-500 mt-1">Assigned: {formatDateUK(viewingCycle.start_date)} → Expiry: {formatDateUK(viewingCycle.expiry_date)}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCriteriaPopup(true)}
                className="shrink-0 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                {translateUi("View criteria & elements")}
              </button>
            </div>

            {/* Assessment Event Timeline */}
            {employeeCycleEvents.length > 0 && (
              <div className="px-4 sm:px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">{translateUi("Assessment Event Schedule")}</h3>
                  {employeeCycleEventsLoadStatus === 'loading' && (
                    <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" aria-hidden />
                      {translateUi("Refreshing…")}
                    </span>
                  )}
                </div>
                <div
                  className={cn(
                    "mb-4 rounded-xl border p-3 sm:p-4",
                    isDarkMode
                      ? "border-slate-600 bg-slate-900/70"
                      : "border-slate-200 bg-slate-50/70"
                  )}
                >
                  <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-xs font-semibold uppercase tracking-wider mb-2",
                          isDarkMode ? "text-slate-300" : "text-slate-600"
                        )}
                      >
                        {translateUi("Cycle event breakdown")}
                      </p>
                      <div className="space-y-1.5">
                        {employeeCycleEvents.map((ev, idx) => {
                          const isMonitoringEvent = String(ev.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
                          return (
                            <div key={`breakdown-${String(ev.id)}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                              <span className={cn("font-medium", isDarkMode ? "text-slate-400" : "text-slate-500")}>{idx + 1}.</span>
                              <span className={cn(
                                "inline-flex rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider",
                                isMonitoringEvent
                                  ? (isDarkMode ? "bg-cyan-950 text-cyan-300 border border-cyan-700/50" : "bg-cyan-100 text-cyan-700")
                                  : (isDarkMode ? "bg-indigo-950 text-indigo-300 border border-indigo-700/50" : "bg-indigo-100 text-indigo-700")
                              )}>
                                {isMonitoringEvent ? translateUi("CDP Monitoring") : translateUi("Assessment")}
                              </span>
                              <span className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-800")}>{ev.event_name}</span>
                              <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>{formatDateUK(ev.window_open)} to {formatDateUK(ev.window_close)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {cycleCriteriaCoverage && cycleCriteriaCoverage.total > 0 ? (
                      <div className="lg:w-[248px] shrink-0 flex items-center justify-center lg:justify-end self-center">
                        <CycleCriteriaProgressDial
                          covered={cycleCriteriaCoverage.covered}
                          total={cycleCriteriaCoverage.total}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col items-stretch">
                  {employeeCycleEvents.map((ev, idx) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isMonitoringEvent = String(ev.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
                    const isOpen = ev.window_open <= today && ev.window_close >= today && ev.status !== 'completed';
                    const isOverdue = ev.window_close < today && ev.status !== 'completed';
                    const isCompleted = ev.status === 'completed';
                    const isUpcoming = ev.window_open > today;
                    const isActive = String(activeEventId) === String(ev.id);
                    const hasAdvice = (ev.advice_count || 0) > 0;
                    const hasReview = (ev.review_count || 0) > 0;
                    const hasNotApplicable = (ev.not_applicable_count || 0) > 0;
                    const assessed = ev.assessed_count ?? 0;
                    const totalAll = ev.total_criteria ?? 0;
                    const totalInCompletedElements = ev.total_criteria_in_completed_elements ?? 0;
                    const total =
                      !isMonitoringEvent && scheduleCycleCriteriaTotal > 0
                        ? scheduleCycleCriteriaTotal
                        : totalInCompletedElements > 0
                          ? totalInCompletedElements
                          : totalAll;
                    const remainingLeft = Math.max(0, total - assessed);
                    const hasUnassessed = remainingLeft > 0;
                    const isStartedNotCompleted = !isCompleted && !!ev.assessment_started_at;
                    /** Window not open yet on the calendar, but assessor already started — don't show translateUi("Upcoming") next to "still in progress". */
                    const hideWindowStatusBadge = isStartedNotCompleted && isUpcoming && !isOpen && !isOverdue;

                    const allCriteriaCompleted = total > 0 && assessed === total;
                    const awaitingFinalFeedback =
                      isStartedNotCompleted &&
                      !isMonitoringEvent &&
                      allCriteriaCompleted &&
                      !isFinalAssessmentFeedbackSufficient(ev.final_assessment_feedback);
                    const isEditRestricted = isCompleted && canMutateAssessmentsInViewingCycle && isAssessmentEditRestricted(ev);
                    const showReturnToAssessment = !isCompleted && !!ev.assessment_started_at;
                    const priorAssessmentEvent =
                      !isMonitoringEvent
                        ? [...employeeCycleEvents.slice(0, idx)]
                            .reverse()
                            .find(
                              (candidate) =>
                                String(candidate.event_type ?? 'assessment').trim().toLowerCase() !== 'monitoring'
                            ) ?? null
                        : null;
                    const priorEventIncomplete =
                      !isMonitoringEvent &&
                      !isCompleted &&
                      priorAssessmentEvent != null &&
                      priorAssessmentEvent.status !== 'completed';
                    const starterEmailLower = (ev.assessment_started_by || '').trim().toLowerCase();
                    const currentEmailLower = (currentUserEmail || '').trim().toLowerCase();
                    const exclusiveLockByOther =
                      !isMonitoringEvent &&
                      !isCompleted &&
                      !!ev.assessment_started_at &&
                      !!starterEmailLower &&
                      (!currentEmailLower || starterEmailLower !== currentEmailLower);
                    const scheduleAssessmentButtonLabel = exclusiveLockByOther
                      ? 'In progress (locked)'
                      : isCompleted
                        ? (canMutateAssessmentsInViewingCycle ? (isMonitoringEvent ? translateUi("Edit event") : translateUi("Edit assessment")) : (isMonitoringEvent ? translateUi("View event") : translateUi("View assessment")))
                        : (canMutateAssessmentsInViewingCycle
                            ? (isMonitoringEvent ? 'Open event' : (showReturnToAssessment ? translateUi("Return to assessment") : translateUi("Open assessment")))
                            : (isMonitoringEvent ? translateUi("View event") : translateUi("View assessment")));
                    const hasOpenAssessmentCapture =
                      !!ev.assessment_started_at ||
                      !!ev.assessment_opened_at ||
                      assessed > 0 ||
                      !!(ev.early_assessment_reasons || '').trim() ||
                      !!(ev.late_assessment_reasons || '').trim();
                    const showResetOpenAssessment =
                      canAdminResetOpenAssessment &&
                      !viewingCycle.is_read_only_history &&
                      !isCompleted &&
                      !isMonitoringEvent &&
                      hasOpenAssessmentCapture;
                    const assessmentOpenedByDisplay =
                      (ev.assessment_started_by_name || '').trim() ||
                      (ev.assessment_started_by || '').trim() ||
                      (ev.assessment_opened_by_name || '').trim() ||
                      (ev.assessment_opened_by || '').trim() ||
                      null;
                    const assessmentOpenedAtDisplay = ev.assessment_started_at || ev.assessment_opened_at || null;

                    return (
                      <React.Fragment key={ev.id}>
                        <div
                          className={`w-full flex flex-col gap-2 sm:gap-3 px-3 sm:px-4 py-3 rounded-xl border transition-all ${
                            isStartedNotCompleted ? (isActive ? "border-amber-400 bg-amber-50 ring-2 ring-amber-200" : "border-amber-300 bg-amber-50") :
                            isActive ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200' :
                            isCompleted ? 'border-emerald-300 bg-emerald-50' :
                            isOverdue ? 'border-red-300 bg-red-50' :
                            isOpen ? 'border-amber-300 bg-amber-50' :
                            'border-slate-200 bg-slate-50/50'
                          }`}
                        >
                          <div className={(isCompleted || isStartedNotCompleted)
                            ? "grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-2"
                            : "flex flex-wrap items-center gap-x-3 gap-y-2"
                          }>
                            <button
                              type="button"
                              onClick={() => setActiveEventId(isActive ? null : ev.id)}
                              className={`flex items-center gap-2 sm:gap-2.5 text-left min-w-0 ${isCompleted ? '' : 'flex-1'}`}
                              title={isCompleted ? translateUi("Event completed") : isOverdue ? translateUi("Event window has passed") : isOpen ? translateUi("Event window is currently open") : translateUi("Event window has not yet opened")}
                            >
                              <div
                                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  isCompleted ? 'bg-emerald-500' : isOverdue ? 'bg-red-500' : isOpen ? "bg-amber-500 animate-pulse" : "bg-slate-300"
                                }`}
                                title={isCompleted ? translateUi("Event completed") : isOverdue ? translateUi("Event window has passed") : isOpen ? translateUi("Event window is currently open") : translateUi("Event window has not yet opened")}
                                aria-hidden
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 truncate">{ev.event_name}</p>
                                  <span className={cn(
                                    "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0",
                                    isMonitoringEvent ? "bg-cyan-100 text-cyan-700" : "bg-indigo-100 text-indigo-700"
                                  )}>
                                    {isMonitoringEvent ? translateUi("CDP Monitoring") : translateUi("Assessment")}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{formatDateUK(ev.window_open)} → {formatDateUK(ev.window_close)}</p>
                                {!isCompleted && !isMonitoringEvent && assessmentOpenedByDisplay && (
                                  <p className="text-xs font-medium text-indigo-800 mt-0.5">
                                    {translateUi("Opened by")} <span className="font-semibold">{assessmentOpenedByDisplay}</span>
                                    {assessmentOpenedAtDisplay ? (
                                      <span className="text-slate-500 font-normal"> · {formatAssessmentMeta(assessmentOpenedAtDisplay)}</span>
                                    ) : null}
                                  </p>
                                )}
                                {isStartedNotCompleted && ev.assessment_started_at && assessmentOpenedByDisplay && (
                                  <div className="mt-1 flex flex-col gap-0.5 text-[11px] sm:text-xs text-slate-600 sm:hidden">
                                    <span>
                                      <span className="text-slate-500">{translateUi("In progress since")}</span>{' '}
                                      <span className="text-slate-800 font-medium">{formatAssessmentMeta(ev.assessment_started_at)}</span>
                                    </span>
                                  </div>
                                )}
                                {isCompleted && (ev.assessment_completed_at || ev.completed_at) && (
                                  <div className="mt-1 flex flex-col gap-0.5 text-[11px] sm:text-xs text-slate-600">
                                    <span>
                                      <span className="text-slate-500">{translateUi("Date & Time completed:")}</span>{' '}
                                      <span className="text-slate-800 font-medium">{formatAssessmentMeta(ev.assessment_completed_at || ev.completed_at)}</span>
                                    </span>
                                    {typeof ev.assessment_duration_seconds === 'number' && (
                                      <span>
                                        <span className="text-slate-500">{translateUi("Duration:")}</span>{' '}
                                        <span className="text-slate-800 font-medium">{formatDuration(ev.assessment_duration_seconds)}</span>
                                      </span>
                                    )}
                                    {(ev.assessment_completed_by_name || ev.assessment_completed_by || ev.completed_by) && (
                                      <span>
                                        <span className="text-slate-500">{translateUi("Assessment completed by:")}</span>{' '}
                                        <span className="text-slate-800 font-medium">{ev.assessment_completed_by_name || ev.assessment_completed_by || ev.completed_by}</span>
                                      </span>
                                    )}
                                    {(ev.amended_by_name || ev.amended_by) && (
                                      <span className="inline-flex items-center gap-1 relative">
                                        <span>
                                          <span className="text-slate-500">{translateUi("Amended by:")}</span>{' '}
                                          <span className="text-slate-800 font-medium">{ev.amended_by_name || ev.amended_by}</span>
                                          {ev.amended_at && (
                                            <span className="text-slate-600"> ({formatAssessmentMeta(ev.amended_at)})</span>
                                          )}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setAmendmentInfoEventId(amendmentInfoEventId === ev.id ? null : ev.id); }}
                                          className="p-0.5 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50 transition-colors shrink-0"
                                          title={translateUi("View amendment details")}
                                        >
                                          <HelpCircle className="w-3.5 h-3.5" />
                                        </button>
                                        {amendmentInfoEventId === ev.id && (
                                          <>
                                            <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setAmendmentInfoEventId(null); }} aria-hidden />
                                            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] min-w-[280px] max-w-[min(420px,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto p-4 bg-slate-800 text-white text-sm rounded-xl shadow-2xl border border-slate-600">
                                              {(() => {
                                                let snapshot: { criterion_key: string; unit: string; element: string; criteria: string; grade: string | null; notes: string | null; previous_grade?: string; amended_at?: string }[] | null = null;
                                                try {
                                                  if (ev.amendment_snapshot?.trim()) snapshot = JSON.parse(ev.amendment_snapshot);
                                                } catch {}
                                                if (Array.isArray(snapshot) && snapshot.length > 0) {
                                                  return (
                                                    <div className="space-y-0">
                                                      <div className="mb-3 pb-3 border-b border-slate-600 space-y-1.5">
                                                        <p className="text-slate-200">
                                                          <span className="text-slate-400">{translateUi("Amended by:")}</span> {ev.amended_by_name || ev.amended_by || '—'}
                                                        </p>
                                                      </div>
                                                      {snapshot.map((s, i) => (
                                                        <div key={s.criterion_key || i}>
                                                          {i > 0 && <hr className="border-slate-600 my-3" />}
                                                          <div className="space-y-1.5">
                                                            <p className="text-slate-300 font-medium">{cleanUnitDisplay(s.unit)} · {s.element}</p>
                                                            <p className="text-slate-200">{s.criteria}</p>
                                                            {(s.grade || s.previous_grade) && (
                                                              <p>
                                                                <span className="text-slate-400">{translateUi("Grade:")}</span>{' '}
                                                                {s.previous_grade && s.grade
                                                                  ? `${s.previous_grade} → ${s.grade}`
                                                                  : s.grade || s.previous_grade}
                                                              </p>
                                                            )}
                                                            {s.notes && <p><span className="text-slate-400">{translateUi("Notes:")}</span> {s.notes}</p>}
                                                            {(s.amended_at ?? ev.amended_at) && (
                                                              <p className="text-slate-400 text-xs mt-1">
                                                                {formatAssessmentMeta(s.amended_at ?? ev.amended_at)}
                                                              </p>
                                                            )}
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <div className="space-y-2 text-slate-200">
                                                    <p>{ev.amendment_notes?.trim() || 'Assessment criteria (grades, notes)'}</p>
                                                    <div className="pt-2 border-t border-slate-600 space-y-1.5">
                                                      <p className="text-slate-200">
                                                        <span className="text-slate-400">{translateUi("Date & time:")}</span> {ev.amended_at ? formatAssessmentMeta(ev.amended_at) : '—'}
                                                      </p>
                                                      <p className="text-slate-200">
                                                        <span className="text-slate-400">{translateUi("Amended by:")}</span> {ev.amended_by_name || ev.amended_by || '—'}
                                                      </p>
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                              <button type="button" onClick={(e) => { e.stopPropagation(); setAmendmentInfoEventId(null); }} className="mt-3 text-indigo-300 hover:text-white text-xs font-medium"> {translateUi("Close")} </button>
                                            </div>
                                          </>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </button>
                            {(isCompleted || isStartedNotCompleted) && (
                              <span
                                className={cn(
                                  "flex flex-col items-center justify-center text-center gap-0.5 font-bold tracking-wide",
                                  isCompleted ? "text-lg sm:text-xl text-emerald-800" : "text-lg sm:text-xl text-amber-800"
                                )}
                              >
                                {isCompleted ? (
                                  "COMPLETED"
                                ) : (
                                  <>
                                    <span>
                                      {isMobileOrTablet ? (
                                        <>
                                          STILL IN
                                          <br /> {translateUi("PROGRESS")} </>
                                      ) : (
                                        "STILL IN PROGRESS"
                                      )}
                                    </span>
                                    {awaitingFinalFeedback && (
                                      <span className="text-[11px] sm:text-xs font-semibold tracking-normal text-amber-800/90 leading-tight">
                                        {translateUi("Awaiting feedback")}
                                      </span>
                                    )}
                                  </>
                                )}
                              </span>
                            )}
                            <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${isCompleted ? 'justify-end' : ''}`}>
                              {!isMonitoringEvent && assessmentOpenedByDisplay && (
                                <span
                                  className={cn(
                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shrink-0 max-w-full",
                                    isDarkMode
                                      ? "bg-indigo-950/80 border border-indigo-700/60 text-indigo-100"
                                      : "bg-indigo-100/90 text-indigo-900"
                                  )}
                                  title={
                                    assessmentOpenedAtDisplay
                                      ? `Assessment opened by ${assessmentOpenedByDisplay} on ${formatAssessmentMeta(assessmentOpenedAtDisplay)}`
                                      : `Assessment opened by ${assessmentOpenedByDisplay}`
                                  }
                                >
                                  <User
                                    className={cn(
                                      "h-3.5 w-3.5 shrink-0",
                                      isDarkMode ? "text-indigo-300" : "text-indigo-700"
                                    )}
                                    aria-hidden
                                  />
                                  <span className="truncate">
                                    {translateUi("Opened by")} <span className="font-semibold">{assessmentOpenedByDisplay}</span>
                                  </span>
                                </span>
                              )}
                              {!hideWindowStatusBadge && (
                              <span
                                className={`text-xs font-semibold px-2.5 py-1 rounded-lg shrink-0 ${
                                  isCompleted ? 'bg-emerald-100/90 text-emerald-800' :
                                  isOverdue ? 'bg-red-100/90 text-red-800' :
                                  isOpen ? 'bg-amber-100/90 text-amber-800' :
                                  isUpcoming ? "bg-slate-100 text-slate-600" :
                                  "bg-slate-100 text-slate-500"
                                }`}
                                title={isCompleted ? 'This assessment event has been marked as completed' : isOverdue ? 'The assessment window has passed without completion' : isOpen ? translateUi("The assessment window is currently open for submissions") : translateUi("The assessment window has not yet opened")}
                              >
                                {isCompleted ? translateUi("Completed") : isOverdue ? translateUi("Overdue") : isOpen ? translateUi("Open") : translateUi("Upcoming")}
                              </span>
                              )}
                              {!isMonitoringEvent && total > 0 && (
                                <span
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/70 text-xs font-medium text-slate-700 shrink-0"
                                  title={assessed === total
                                    ? translateUi("{{assessed}} of {{total}} criteria assessed. All criteria have been assessed.", { assessed, total })
                                    : translateUi("{{assessed}} of {{total}} criteria assessed. {{n}} criteria remaining.", { assessed, total, n: remainingLeft })}
                                >
                                  <span className={assessed === total ? "text-emerald-600" : "text-slate-600"}>{assessed}</span>
                                  <span className="text-slate-400">/</span>
                                  <span>{total}</span>
                                  <span className="text-slate-500 font-normal">criteria</span>
                                </span>
                              )}
                              {!isMonitoringEvent && (hasAdvice || hasReview || hasNotApplicable || hasUnassessed) && (
                                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                                  {hasAdvice && (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-md bg-amber-100/90 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                                      title={translateUi("Criteria graded with advice or development needed")}
                                    >
                                      <AlertCircle className="h-3 w-3 shrink-0" />{ev.advice_count}
                                    </span>
                                  )}
                                  {hasReview && (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-md bg-rose-100/90 px-2 py-0.5 text-[11px] font-medium text-rose-800"
                                      title={translateUi("Criteria requiring review or follow-up")}
                                    >
                                      <AlertTriangle className="h-3 w-3 shrink-0" />{ev.review_count}
                                    </span>
                                  )}
                                  {hasNotApplicable && (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-md bg-sky-100/90 px-2 py-0.5 text-[11px] font-medium text-sky-800"
                                      title={translateUi("Criteria marked as not applicable")}
                                    >
                                      <PauseCircle className="h-3 w-3 shrink-0" />{ev.not_applicable_count}
                                    </span>
                                  )}
                                  {hasUnassessed && (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-md bg-slate-200/90 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                                      title={translateUi("Criteria not yet assessed")}
                                    >
                                      <AlertCircle className="h-3 w-3 shrink-0" />{remainingLeft} left
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {showResetOpenAssessment && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  adminResetOpenAssessmentEvent(ev.id, ev.event_name);
                                }}
                                className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] p-2 rounded-lg border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 transition-colors shrink-0"
                                title={translateUi("Administrator: reset this assessment — erase all activity and return to unopened / not started")}
                                aria-label={translateUi("Reset assessment")}
                              >
                                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            )}
                            {canOverrideStuckAssessment && !viewingCycle.is_read_only_history && showReturnToAssessment && !isCompleted && !!ev.assessment_started_at && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  adminClearStuckAssessmentStart(ev.id, ev.event_name);
                                }}
                                className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] p-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors shrink-0"
                                title={translateUi("Administrator: clear in-progress lock if the assessor left this open by mistake")}
                                aria-label={translateUi("Clear stuck assessment lock")}
                              >
                                <Unlock className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            )}
                            {isCompleted && canMutateAssessmentsInViewingCycle && (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setEditRestrictionInfoEventId(editRestrictionInfoEventId === ev.id ? null : ev.id)}
                                  className="p-1.5 text-indigo-500 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                                  title={translateUi("Assessment editing policy")}
                                >
                                  <HelpCircle className="w-4 h-4" />
                                </button>
                                {editRestrictionInfoEventId === ev.id && (
                                  <>
                                    <div className="fixed inset-0 z-40" onClick={() => setEditRestrictionInfoEventId(null)} aria-hidden />
                                    <div className="absolute right-0 bottom-full mb-1 z-50 w-64 sm:w-72 max-w-[calc(100vw-2rem)] p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg">
                                      <p className="font-medium mb-1">{isEditRestricted ? translateUi("Editing restricted") : translateUi("Assessment editing policy")}</p>
                                      <p>{translateUi("Assessment events cannot be edited more than 14 days after the date completed. This ensures assessment records remain auditable.")}</p>
                                      <button type="button" onClick={() => setEditRestrictionInfoEventId(null)} className="mt-2 text-indigo-300 hover:text-white text-xs font-medium"> {translateUi("Close")} </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                if (isEditRestricted || priorEventIncomplete || exclusiveLockByOther || String(openingAssessmentEventId) === String(ev.id)) return;
                                setOpeningAssessmentEventId(ev.id);
                                try {
                                  if (showReturnToAssessment) {
                                    openAssessmentModalForEvent(ev.id);
                                    return;
                                  }
                                  if (canMutateAssessmentsInViewingCycle && effectiveCompanyCode && currentUserEmail) {
                                    const suspended = await fetchCompanyStorageSuspended(effectiveCompanyCode, currentUserEmail);
                                    if (suspended) {
                                      setPendingOpenAssessmentEventId(ev.id);
                                      setStorageQuotaOpenAssessmentDialogOpen(true);
                                      return;
                                    }
                                  }
                                  openAssessmentModalForEvent(ev.id);
                                } finally {
                                  setOpeningAssessmentEventId((current) =>
                                    String(current) === String(ev.id) ? null : current
                                  );
                                }
                              }}
                              disabled={
                                isEditRestricted ||
                                priorEventIncomplete ||
                                exclusiveLockByOther ||
                                String(openingAssessmentEventId) === String(ev.id)
                              }
                              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors min-h-[36px] touch-manipulation ${
                                isEditRestricted || priorEventIncomplete || exclusiveLockByOther || String(openingAssessmentEventId) === String(ev.id)
                                  ? 'bg-slate-200 text-slate-500 border border-slate-300 cursor-not-allowed'
                                  : allCriteriaCompleted
                                    ? 'bg-emerald-700 hover:bg-emerald-800 text-white border border-emerald-800 shadow-sm'
                                    : 'bg-white/90 border border-slate-200/80 text-slate-700 hover:bg-white hover:border-slate-300 shadow-sm'
                              }`}
                              title={
                                exclusiveLockByOther
                                  ? 'Another assessor has started this event. It cannot be opened or edited until they mark it complete.'
                                  : isEditRestricted
                                    ? 'Editing is restricted after 14 days from completion'
                                    : priorEventIncomplete
                                      ? `Complete "${priorAssessmentEvent?.event_name ?? 'previous'}" first`
                                      : isCompleted
                                        ? (isMonitoringEvent ? translateUi("View monitoring event details and notes.") : translateUi("View criteria assessed and notes. Editable if you have permission."))
                                        : (isMonitoringEvent ? translateUi("Open monitoring event details and notes.") : translateUi("Open assessment to record grades"))
                              }
                            >
                              {String(openingAssessmentEventId) === String(ev.id) ? (
                                <>
                                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-400 border-t-transparent shrink-0" aria-hidden />
                                  {showReturnToAssessment ? translateUi("Opening assessment…") : translateUi("Opening…")}
                                </>
                              ) : (
                                scheduleAssessmentButtonLabel
                              )}
                            </button>
                          </div>
                        </div>
                        {idx < employeeCycleEvents.length - 1 && (
                          <div className="flex justify-center py-1" aria-hidden>
                            <ChevronRight className="w-5 h-5 text-slate-400 rotate-90" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                {cycleContinuationChecklist ? (
                  <div
                    className={cn(
                      "mt-4 rounded-xl border px-4 py-3",
                      cycleContinuationChecklist.eligible
                        ? "border-emerald-200 bg-emerald-50/70"
                        : "border-sky-200 bg-sky-50/70",
                    )}
                  >
                    <p
                      className={cn(
                        "text-sm font-semibold",
                        cycleContinuationChecklist.eligible ? "text-emerald-950" : "text-sky-950",
                      )}
                    >
                      {cycleContinuationChecklist.eligible
                        ? translateUi("Ready to continue — requirements met")
                        : translateUi("Required for this cycle to continue")}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {cycleContinuationChecklist.items.map((item) => (
                        <li key={item.id} className="flex items-start gap-2 text-sm text-slate-700">
                          {item.ok ? (
                            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
                          ) : (
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" aria-hidden />
                          )}
                          <span className={item.ok ? "text-slate-700" : "text-slate-800"}>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                      {cycleContinuationChecklist.openMonitoringCount > 0
                        ? translateUi(
                            "Unfinished CDP monitoring does not block continuation — open monitoring events will carry over to the next period.",
                          )
                        : translateUi(
                            "CDP monitoring incompletes do not block continuation; unfinished monitoring carries over when the cycle renews.",
                          )}
                    </p>
                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">
                      {translateUi(
                        "Emails are sent as part of this process: the assessing manager is notified when renewal is approaching (within 14 days of expiry). If renewal is blocked after expiry, the assessing manager and company administrators are emailed.",
                      )}
                    </p>
                  </div>
                ) : null}
                {activeEventId && (
                  <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                    {(() => {
                      const ev = employeeCycleEvents.find((e) => String(e.id) === String(activeEventId));
                      if (!ev) return null;
                      const isMonitoringEvent = String(ev.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
                      const evTotalAll = ev.total_criteria ?? 0;
                      const evTotalInCompletedElements = ev.total_criteria_in_completed_elements ?? 0;
                      const evTotal =
                        !isMonitoringEvent && scheduleCycleCriteriaTotal > 0
                          ? scheduleCycleCriteriaTotal
                          : evTotalInCompletedElements > 0
                            ? evTotalInCompletedElements
                            : evTotalAll;
                      const evAssessed = ev.assessed_count ?? 0;
                      const evCanMarkCompleted = isMonitoringEvent || (evTotal > 0 && evAssessed === evTotal);
                      return (
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-indigo-800">
                              {isMonitoringEvent ? translateUi("Event:") : translateUi("Assessing:")} {ev.event_name}
                            </p>
                            <p className="text-xs text-indigo-600">
                              {isMonitoringEvent
                                ? translateUi("Monitoring event notes and completion are recorded against this event window.")
                                : translateUi("Criteria assessed during this event will be linked to it.")}
                            </p>
                          </div>
                          {ev.status !== 'completed' && canMutateAssessmentsInViewingCycle && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!evCanMarkCompleted) return;
                                markEmployeeCycleEventCompleted(ev.id);
                              }}
                              disabled={!evCanMarkCompleted}
                              title={!evCanMarkCompleted ? translateUi("Assess or mark not applicable for every selected criterion before completing this event.") : undefined}
                              className="shrink-0 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {translateUi("Mark as completed")}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            {!isViewingClosedCycleHistory && employeeCycleEventsLoadStatus === 'loading' && employeeCycleEvents.length === 0 && (
              <div className="px-6 py-10 text-center border-t border-slate-100 bg-slate-50/40">
                <div className="max-w-md mx-auto space-y-3">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" aria-hidden />
                  <p className="text-sm font-semibold text-slate-800">{translateUi("Loading assessment schedule…")}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {translateUi("Fetching cycle events for this assignment. This can take a moment on slower connections.")}
                  </p>
                </div>
              </div>
            )}
            {!isViewingClosedCycleHistory && employeeCycleEventsLoadStatus === 'error' && employeeCycleEvents.length === 0 && (
              <div className="px-6 py-10 text-center border-t border-slate-100 bg-slate-50/40">
                <div className="max-w-md mx-auto space-y-3">
                  <p className="text-sm font-semibold text-slate-800">{translateUi("Assessment schedule not loaded")}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {translateUi("The server returned no events for this assignment. This often happens if the company tenant was not sent on the first request — use Retry. If it persists, confirm the cycle had assessment events when assigned.")}
                  </p>
                  {!effectiveCompanyCode && (
                    <p className="text-xs text-amber-800 font-medium">
                      {translateUi("No company code is in context for this profile; tenant headers cannot be applied until the employee record finishes loading or you open this employee from the company-scoped list.")}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => refetchCycleEvents()}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {translateUi("Retry loading schedule")}
                  </button>
                </div>
              </div>
            )}
            {!isViewingClosedCycleHistory && employeeCycleEventsLoadStatus === 'loaded' && employeeCycleEvents.length === 0 && (
              <div className="px-6 py-10 text-center border-t border-slate-100 bg-slate-50/40">
                <div className="max-w-md mx-auto space-y-3">
                  <p className="text-sm font-semibold text-slate-800">{translateUi("No assessment events on this cycle")}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {translateUi("This assignment has no scheduled assessment events. Confirm the cycle template included events when it was assigned, or contact your administrator.")}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchCycleEvents()}
                    disabled={employeeCycleEventsLoadStatus === 'loading'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {translateUi("Retry loading schedule")}
                  </button>
                </div>
              </div>
            )}
            {isViewingClosedCycleHistory && (
              <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-slate-50/40">
                <ClosedCycleArchiveBreakdownPanel
                  summary={closedCycleDisplayArchiveSummary}
                  gradeRows={closedCycleGradeRows}
                  gradeBreakdownStatus={closedCycleCriterionAssessmentsStatus}
                  attentionItems={closedCycleAttentionItems}
                  onJumpToCriterion={jumpToClosedCycleAssessmentCriterion}
                />
              </div>
            )}
            {employeeCycleEvents.length === 0 && viewingCycle.is_read_only_history && (
              <div className="px-6 py-12 text-center border-t border-slate-100 bg-gradient-to-b from-slate-50/30 to-white">
                <div className="max-w-md mx-auto space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                    <ClipboardList className="w-7 h-7" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">{translateUi("Assessment schedule could not be loaded")}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {translateUi("The system could not match this closed record to saved assessment events (for example the cycle template changed, or events were removed). Grades and evidence uploaded for this employee under this cycle may still exist in storage; contact support if you need a manual data review.")}
                  </p>
                </div>
              </div>
            )}

          </div>
          {assessmentLockedByUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setAssessmentLockedByUser(null)}>
              <div
                className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold text-slate-900 mb-2">{translateUi("Assessment in progress")}</h3>
                <p className="text-slate-600 mb-4">
                  {translateUi("Assessment progress for")} <span className="font-medium text-slate-800">&quot;{assessmentLockedByUser.eventName}&quot;</span> is underway.
                  It was started by <span className="font-medium text-slate-800">{assessmentLockedByUser.startedBy}</span>.
                  You cannot open or edit this event until they mark it as complete.
                </p>
                <button
                  type="button"
                  onClick={() => setAssessmentLockedByUser(null)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                > OK </button>
              </div>
            </div>
          )}
          {assessmentBlockedPriorIncomplete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setAssessmentBlockedPriorIncomplete(null)}>
              <div
                className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold text-slate-900 mb-2">{translateUi("Complete previous assessment first")}</h3>
                <p className="text-slate-600 mb-4">
                  {translateUi("The assessment")} <span className="font-medium text-slate-800">&quot;{assessmentBlockedPriorIncomplete.eventName}&quot;</span> cannot be started until{' '}
                  <span className="font-medium text-slate-800">&quot;{assessmentBlockedPriorIncomplete.priorEventName}&quot;</span> {translateUi("has been marked as complete.")}
                </p>
                <button
                  type="button"
                  onClick={() => setAssessmentBlockedPriorIncomplete(null)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                > OK </button>
              </div>
            </div>
          )}
          <StorageQuotaOpenAssessmentDialog
            open={storageQuotaOpenAssessmentDialogOpen}
            onCancel={() => {
              setPendingOpenAssessmentEventId(null);
              setStorageQuotaOpenAssessmentDialogOpen(false);
            }}
            onContinue={() => {
              if (pendingOpenAssessmentEventId != null) {
                openAssessmentModalForEvent(pendingOpenAssessmentEventId);
              }
              setPendingOpenAssessmentEventId(null);
              setStorageQuotaOpenAssessmentDialogOpen(false);
            }}
          />
          <AssessorDerogationDialog
            open={derogationDialog != null}
            mode={derogationDialog?.mode ?? 'same_tier'}
            assessorName={derogationDialog?.assessorName}
            assessorRole={derogationDialog?.assessorRole}
            assesseeName={employee?.full_name}
            assesseeRole={derogationDialog?.assesseeRole}
            tierTitle={derogationDialog?.assessorTier?.title ?? derogationDialog?.assesseeTier?.title}
            assesseeTierTitle={derogationDialog?.assesseeTier?.title}
            suggestedAssessors={derogationDialog?.suggestedAssessors ?? []}
            filledInByName={currentUserProfile?.full_name || currentUserTitle}
            submitting={derogationSubmitting}
            error={derogationError}
            isDarkMode={isDarkMode}
            onConfirm={submitAssessorDerogation}
            onCancel={() => {
              if (derogationSubmitting) return;
              setDerogationDialog(null);
              setDerogationError(null);
            }}
          />
          {showCriteriaPopup && viewingCycle && (() => {
            const cycle = viewingCycle.framework_cycle_id ? getCycleById(viewingCycle.framework_cycle_id) : null;
            const criteria = cycleDetailCriteria ?? [];
            const toggleElement = (key: string) => setCriteriaPopupExpanded((p) => ({ ...p, [key]: !p[key] }));
            const stripU = (s: string | undefined) => (s || '').replace(/u(\d)/gi, '$1');
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => { setShowCriteriaPopup(false); setCriteriaPopupExpanded({}); }}>
                <div
                  className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">{translateUi("Criteria & elements")}</h3>
                    <button type="button" onClick={() => { setShowCriteriaPopup(false); setCriteriaPopupExpanded({}); }} className="p-2 text-slate-500 hover:text-slate-800 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-auto p-4">
                    {cycle ? (
                      <div className="space-y-2">
                        {cycle.units.map((unit) =>
                          unit.elements.map((el) => {
                            const key = `${unit.id}:${el.id}`;
                            const open = criteriaPopupExpanded[key];
                            const count = (el.demonstrate?.length ?? 0) + (el.explain?.length ?? 0);
                            return (
                              <div key={key} className="rounded-lg border border-slate-200 overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => toggleElement(key)}
                                  className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between text-left hover:bg-slate-100 gap-4"
                                >
                                  <span className="font-medium text-slate-800 min-w-0 flex-1">{stripU(unit.title) || unit.title} · {stripU(el.title) || el.title}</span>
                                  <span className="text-xs text-slate-500 shrink-0 text-right tabular-nums">{count} criteria</span>
                                  <span className="shrink-0">{open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}</span>
                                </button>
                                {open && (
                                  <div className="border-t border-slate-200 p-4 space-y-3 bg-white">
                                    {el.demonstrate?.length ? (
                                      <div>
                                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2"> {translateUi("Demonstrate")} </p>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                          {el.demonstrate.map((c, i) => (
                                            <li key={i}>{c}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    ) : null}
                                    {el.explain?.length ? (
                                      <div>
                                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2"> {translateUi("Explain")} </p>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                          {el.explain.map((c, i) => (
                                            <li key={i}>{c}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    ) : cycleDetailCriteria === null ? (
                      <p className="text-slate-500 text-sm py-4">{translateUi("Loading criteria…")}</p>
                    ) : criteria.length === 0 ? (
                      <p className="text-slate-500 text-sm py-4">{translateUi("No criteria defined for this cycle.")}</p>
                    ) : (
                      (() => {
                        const unitMap: Record<string, { label: string; elements: Record<string, { label: string; rows: typeof criteria }> }> = {};
                        for (const row of criteria) {
                          const uKey = row.unit_no?.trim() || '_';
                          const eKey = `${uKey}::${row.element_no?.trim() || '_'}`;
                          if (!unitMap[uKey]) unitMap[uKey] = { label: `Unit ${stripU(row.unit_no) || row.unit_no || '—'}`, elements: {} };
                          if (!unitMap[uKey].elements[eKey]) unitMap[uKey].elements[eKey] = { label: row.element || '—', rows: [] };
                          unitMap[uKey].elements[eKey].rows.push(row);
                        }
                        const expanded = criteriaPopupExpanded;
                        const setExpanded = (k: string, v: boolean) => setCriteriaPopupExpanded((p) => ({ ...p, [k]: v }));
                        const partitionCriteriaForPopup = (rows: typeof criteria) => {
                          const demonstrate: typeof criteria = [];
                          const explain: typeof criteria = [];
                          const other: typeof criteria = [];
                          for (const r of rows) {
                            const head = (r.criteria || '').trim().split(/\r?\n/)[0] ?? '';
                            if (/^\d+\s*[\.)]/.test(head)) explain.push(r);
                            else if (/^[a-z]\s*[\.)]/i.test(head)) demonstrate.push(r);
                            else other.push(r);
                          }
                          return { demonstrate: [...demonstrate, ...other], explain };
                        };
                        return (
                          <div className="space-y-2">
                            {Object.entries(unitMap).flatMap(([, unit]) =>
                              Object.entries(unit.elements).map(([eKey, el]) => {
                                const open = expanded[eKey];
                                const { demonstrate: demoRows, explain: explainRows } = partitionCriteriaForPopup(el.rows);
                                return (
                                  <div key={eKey} className="rounded-lg border border-slate-200 overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => setExpanded(eKey, !open)}
                                      className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between text-left hover:bg-slate-100 gap-4"
                                    >
                                      <span className="font-medium text-slate-800 min-w-0 flex-1">{unit.label} · {stripU(el.label) || el.label}</span>
                                      <span className="text-xs text-slate-500 shrink-0 text-right tabular-nums">{el.rows.length} criteria</span>
                                      <span className="shrink-0">{open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}</span>
                                    </button>
                                    {open && (
                                      <div className="border-t border-slate-200 p-4 space-y-3 bg-white">
                                        {demoRows.length > 0 && (
                                          <div>
                                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2"> {translateUi("Demonstrate")} </p>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                              {demoRows.map((r) => (
                                                <li key={r.id}>{r.criteria}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {explainRows.length > 0 && (
                                          <div>
                                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2"> {translateUi("Explain")} </p>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                              {explainRows.map((r) => (
                                                <li key={r.id}>{r.criteria}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          {eventAssessmentModalId != null && viewingCycle && (() => {
            const modalEvent = employeeCycleEvents.find((event) => String(event.id) === String(eventAssessmentModalId));
            if (!modalEvent) return null;
            const isMonitoringEvent = String(modalEvent.event_type ?? 'assessment').trim().toLowerCase() === 'monitoring';
            const modalEventIndex = employeeCycleEvents.findIndex((event) => String(event.id) === String(modalEvent.id));
            const isLastAssessmentEvent = modalEventIndex === employeeCycleEvents.length - 1;
            const priorEventIds = modalEventIndex > 0 ? employeeCycleEvents.slice(0, modalEventIndex).map((e) => e.id) : [];
            const today = new Date().toISOString().slice(0, 10);
            const isBeforeWindow = modalEvent.window_open > today;
            const isOverdue = modalEvent.window_close < today && modalEvent.status !== 'completed';
            const hasEarlyReasons = !!(modalEvent.early_assessment_reasons?.trim());
            const hasLateReasons = !!(modalEvent.late_assessment_reasons?.trim());
            if (isMonitoringEvent) {
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => closeAssessmentModalForEvent(modalEvent.id)}>
                  <div
                    className="w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-slate-900 truncate">{translateUi("Monitoring Event")}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{viewingCycle.cycle_title}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => closeAssessmentModalForEvent(modalEvent.id)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                        aria-label={translateUi("Close monitoring event")}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="p-6 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{translateUi("Monitoring event")}</p>
                          <p className="text-slate-900 font-medium">{modalEvent.event_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{translateUi("Monitoring type")}</p>
                          <p className="text-slate-900 font-medium">{modalEvent.monitoring_method || 'Other'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"> {translateUi("Window")} </p>
                          <p className="text-slate-900 font-medium">{formatDateUK(modalEvent.window_open)} to {formatDateUK(modalEvent.window_close)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"> {translateUi("Status")} </p>
                          <p className="text-slate-900 font-medium capitalize">{modalEvent.status || 'pending'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"> {translateUi("Description")} </p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {modalEvent.monitoring_description?.trim() || 'No description provided for this monitoring event.'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{translateUi("Event notes")}</label>
                        <textarea
                          rows={4}
                          value={modalEvent.notes ?? ''}
                          onChange={(e) =>
                            setEmployeeCycleEvents((prev) =>
                              prev.map((event) =>
                                String(event.id) === String(modalEvent.id) ? { ...event, notes: e.target.value } : event
                              )
                            )
                          }
                          placeholder="Add monitoring notes for this event."
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {canMutateAssessmentsInViewingCycle && modalEvent.status !== 'completed' && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2">
                          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={monitoringCloseNowByEventId[String(modalEvent.id)] !== false}
                              onChange={(e) =>
                                setMonitoringCloseNowByEventId((prev) => ({
                                  ...prev,
                                  [String(modalEvent.id)]: e.target.checked,
                                }))
                              }
                            />
                            {translateUi("Close this monitoring event immediately if done")}
                          </label>
                          {monitoringCloseNowByEventId[String(modalEvent.id)] !== false && (
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1"> {translateUi("Completed date (optional)")} </label>
                              <input
                                type="date"
                                value={monitoringCompletedDateByEventId[String(modalEvent.id)] ?? ''}
                                onChange={(e) =>
                                  setMonitoringCompletedDateByEventId((prev) => ({
                                    ...prev,
                                    [String(modalEvent.id)]: e.target.value,
                                  }))
                                }
                                className="w-full sm:w-auto px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => closeAssessmentModalForEvent(modalEvent.id)}
                        className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-100"
                      > {translateUi("Close")} </button>
                      {canMutateAssessmentsInViewingCycle && (
                        <button
                          type="button"
                          disabled={monitoringEventNotesSaving}
                          onClick={() => saveMonitoringEventNotes(modalEvent.id, modalEvent.notes ?? '')}
                          className="px-3 py-2 text-sm font-medium rounded-lg border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
                        >
                          {monitoringEventNotesSaving ? translateUi("Saving…") : translateUi("Save notes")}
                        </button>
                      )}
                      {canMutateAssessmentsInViewingCycle &&
                        modalEvent.status !== 'completed' &&
                        monitoringCloseNowByEventId[String(modalEvent.id)] !== false && (
                        <button
                          type="button"
                          onClick={async () => {
                            setAssessmentSubmitting(true);
                            try {
                              const ok = await markEmployeeCycleEventCompleted(
                                modalEvent.id,
                                monitoringCompletedDateByEventId[String(modalEvent.id)] || undefined
                              );
                              if (ok) closeAssessmentModalForEvent(modalEvent.id);
                            } finally {
                              setAssessmentSubmitting(false);
                            }
                          }}
                          disabled={assessmentSubmitting}
                          className="px-3 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {assessmentSubmitting ? translateUi("Completing…") : translateUi("Mark as completed")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            if (isBeforeWindow && !hasEarlyReasons) {
              return (
                <EarlyAssessmentReasonModal
                  event={modalEvent}
                  isMobileOrTablet={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  onProceed={async (reasons) => {
                    const res = await fetch(`/api/employee-cycle-events/${modalEvent.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', ...companyHeaders },
                      body: JSON.stringify({ early_assessment_reasons: reasons, userEmail: currentUserEmail }),
                    });
                    if (res.ok) {
                      const openedAt = new Date().toISOString();
                      const openerName = String(currentUserProfile?.full_name ?? currentUserTitle ?? '').trim();
                      setEmployeeCycleEvents((prev) =>
                        prev.map((e) =>
                          e.id === modalEvent.id
                            ? {
                                ...e,
                                early_assessment_reasons: reasons,
                                ...(!e.assessment_opened_at
                                  ? {
                                      assessment_opened_at: openedAt,
                                      assessment_opened_by: currentUserEmail || null,
                                      assessment_opened_by_name: openerName || null,
                                    }
                                  : {}),
                              }
                            : e,
                        ),
                      );
                      if (!modalEvent.assessment_opened_at && canMutateAssessmentsRef.current) {
                        void fetch(`/api/employee-cycle-events/${modalEvent.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', ...companyHeaders },
                          body: JSON.stringify({ record_assessment_open: true, userEmail: currentUserEmail }),
                        }).catch(() => {});
                      }
                      if (canMutateAssessmentsRef.current && !modalEvent.assessment_started_at) {
                        startAssessmentForEventRef.current(modalEvent.id);
                      }
                    } else {
                      const body = await res.json().catch(() => ({}));
                      throw new Error((body as { error?: string }).error || `Failed to save reasons (${res.status})`);
                    }
                  }}
                  onCancel={() => closeAssessmentModalForEvent(modalEvent.id)}
                />
              );
            }
            if (isOverdue && !hasLateReasons) {
              const assessorDisplayName =
                currentUserProfile?.full_name || currentUserTitle || currentUserEmail || null;
              return (
                <LateAssessmentReasonModal
                  eventName={modalEvent.event_name}
                  windowClose={modalEvent.window_close}
                  formatDate={formatDateUK}
                  assessorName={assessorDisplayName}
                  isMobileOrTablet={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  onProceed={async (submission: LateAssessmentSubmission) => {
                    const res = await fetch(`/api/employee-cycle-events/${modalEvent.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', ...companyHeaders },
                      body: JSON.stringify({
                        late_assessment_reasons: submission.reasons,
                        userEmail: currentUserEmail,
                      }),
                    });
                    if (!res.ok) {
                      const body = await res.json().catch(() => ({}));
                      throw new Error(
                        (body as { error?: string }).error || `Failed to save late assessment reasons (${res.status})`,
                      );
                    }
                    if (submission.derogation) {
                      const derRes = await fetch(`/api/employees/${employeeId}/assessment-derogations${cc}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...companyHeaders },
                        body: JSON.stringify({
                          employee_cycle_event_id: modalEvent.id,
                          event_name: modalEvent.event_name,
                          assessor_email: currentUserEmail,
                          assessor_name: assessorDisplayName,
                          assessor_role: currentUserProfile?.title ?? currentUserTitle ?? null,
                          relationship: 'late_assessment',
                          reason: submission.derogation.reason,
                          approved_by: submission.derogation.approved_by,
                          filled_in_by_name: assessorDisplayName,
                        }),
                      });
                      if (!derRes.ok) {
                        const errBody = await derRes.json().catch(() => ({}));
                        throw new Error(
                          (errBody as { error?: string }).error ||
                            `Failed to record late assessment derogation (${derRes.status})`,
                        );
                      }
                    }
                    setEmployeeCycleEvents((prev) =>
                      prev.map((e) =>
                        e.id === modalEvent.id
                          ? {
                              ...e,
                              late_assessment_reasons: submission.reasons,
                              ...(!e.assessment_opened_at
                                ? {
                                    assessment_opened_at: new Date().toISOString(),
                                    assessment_opened_by: currentUserEmail || null,
                                    assessment_opened_by_name:
                                      String(currentUserProfile?.full_name ?? currentUserTitle ?? '').trim() || null,
                                  }
                                : {}),
                            }
                          : e,
                      ),
                    );
                    if (!modalEvent.assessment_opened_at && canMutateAssessmentsRef.current) {
                      void fetch(`/api/employee-cycle-events/${modalEvent.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', ...companyHeaders },
                        body: JSON.stringify({ record_assessment_open: true, userEmail: currentUserEmail }),
                      }).catch(() => {});
                    }
                    if (canMutateAssessmentsRef.current && !modalEvent.assessment_started_at) {
                      startAssessmentForEventRef.current(modalEvent.id);
                    }
                  }}
                  onCancel={() => closeAssessmentModalForEvent(modalEvent.id)}
                />
              );
            }
            const bundledFrameworkCycle = viewingCycle.framework_cycle_id
              ? getCycleById(viewingCycle.framework_cycle_id)
              : undefined;
            const frameworkAssessmentEventMonitoring =
              String(modalEvent.event_type ?? 'assessment').toLowerCase() === 'monitoring';
            const finalizeFrameworkAssessmentEvent = async () => {
              setAssessmentSubmitting(true);
              try {
                const ok = await markEmployeeCycleEventCompleted(modalEvent.id);
                if (ok) closeAssessmentModalForEvent(modalEvent.id);
              } finally {
                setAssessmentSubmitting(false);
              }
            };
            const frameworkCanAmend =
              canMutateAssessmentsInViewingCycle &&
              modalEvent.status === 'completed' &&
              !isAssessmentEditRestricted(modalEvent);
            const runFrameworkRecordAmendment = async (notes?: string) => {
              setAmendmentUpdating(true);
              setAmendmentSaved(false);
              try {
                await frameworkSaveBeforeAmendmentRef.current?.();
                await recordAmendmentForEvent(modalEvent.id, notes);
                setAmendmentSaved(true);
                await new Promise((r) => setTimeout(r, 1500));
                closeAssessmentModalForEvent(modalEvent.id);
              } finally {
                setAmendmentUpdating(false);
              }
            };
            if (bundledFrameworkCycle) {
              return (
                <AssessmentEventModalFrame
                  event={modalEvent}
                  title={translateUi("{{name}} assessment", { name: modalEvent.event_name })}
                  subtitle={viewingCycle.cycle_title}
                  showUpdateButton={frameworkCanAmend}
                  onRecordAmendment={runFrameworkRecordAmendment}
                  onClose={() => closeAssessmentModalForEvent(modalEvent.id)}
                  mobileAppMode={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  onToggleDarkMode={onToggleDarkMode}
                  isSubmittingAssessment={assessmentSubmitting}
                  isUpdatingAmendment={amendmentUpdating}
                  amendmentSaved={amendmentSaved}
                  assessmentFooter={
                    !frameworkAssessmentEventMonitoring && modalEvent.status === 'completed' ? (
                      <div id="rv-final-assessment-feedback" className="scroll-mt-4">
                        <AssessmentFinalFeedbackPanel
                          eventId={modalEvent.id}
                          eventStatus={modalEvent.status}
                          eventType={modalEvent.event_type}
                          initialFeedback={modalEvent.final_assessment_feedback}
                          eligibilityComplete={frameworkAssessmentEligibleToComplete}
                          canMutate={canMutateAssessmentsInViewingCycle && !isAssessmentEditRestricted(modalEvent)}
                          currentUserEmail={currentUserEmail}
                          patchLocalEvent={(fb) =>
                            setEmployeeCycleEvents((prev) =>
                              prev.map((e) => (String(e.id) === String(modalEvent.id) ? { ...e, final_assessment_feedback: fb } : e))
                            )
                          }
                          showUpdateButton={frameworkCanAmend}
                          onRecordAmendment={frameworkCanAmend ? () => runFrameworkRecordAmendment() : undefined}
                          isUpdatingAmendment={amendmentUpdating}
                        />
                      </div>
                    ) : undefined
                  }
                >
                  <div className={cn(isMobileOrTablet ? "p-0 h-full min-h-0 flex flex-col" : "p-6")}>
                    <AssessableCycleStructureView
                      cycle={bundledFrameworkCycle}
                      employeeId={employeeId}
                      employeeCycleId={viewingCycle.id}
                      evidenceStorageEmployeeCycleId={viewingCycle.source_employee_cycle_id ?? undefined}
                      companyCode={companyCode ?? employee?.company_code ?? ''}
                      currentUserEmail={currentUserEmail}
                      canAssess={canMutateAssessmentsInViewingCycle && !isAssessmentEditRestricted(modalEvent)}
                      readOnlyArchive={isViewingClosedCycleHistory && !closedCycleSystemAdminOverride}
                      defaultCollapsed={false}
                      fullPage
                      isMobileOrTablet={isMobileOrTablet}
                      className={isMobileOrTablet ? "h-full min-h-0" : undefined}
                      activeEventId={modalEvent.id}
                      activeEventSummary={modalEvent}
                      showAssessedOnly={modalEvent.status === 'completed'}
                      onCompletionEligibilityChange={(eligible) => {
                        setFrameworkAssessmentEligibleToComplete(eligible);
                      }}
                      finalAssessmentFeedbackSlot={
                        !frameworkAssessmentEventMonitoring && modalEvent.status !== 'completed' ? (
                          <AssessmentFinalFeedbackPanel
                            eventId={modalEvent.id}
                            eventStatus={modalEvent.status}
                            eventType={modalEvent.event_type}
                            initialFeedback={modalEvent.final_assessment_feedback}
                            eligibilityComplete={frameworkAssessmentEligibleToComplete}
                            canMutate={canMutateAssessmentsInViewingCycle && !isAssessmentEditRestricted(modalEvent)}
                            currentUserEmail={currentUserEmail}
                            patchLocalEvent={(fb) =>
                              setEmployeeCycleEvents((prev) =>
                                prev.map((e) => (String(e.id) === String(modalEvent.id) ? { ...e, final_assessment_feedback: fb } : e))
                              )
                            }
                            onFinalizeAssessment={
                              modalEvent.status !== 'completed' && !frameworkAssessmentEventMonitoring
                                ? finalizeFrameworkAssessmentEvent
                                : undefined
                            }
                          />
                        ) : undefined
                      }
                      onStartAssessment={(criterionKeys, elementKeys) => startAssessmentForEvent(modalEvent.id, criterionKeys, elementKeys)}
                      onMarkCompleted={finalizeFrameworkAssessmentEvent}
                      isLastEvent={isLastAssessmentEvent}
                      priorEventIds={priorEventIds}
                      saveAllBeforeAmendmentRef={frameworkSaveBeforeAmendmentRef}
                      onClosedCycleSummaryInvalidate={isViewingClosedCycleHistory ? refreshClosedCycleAssessmentSummary : undefined}
                      onAssessmentScheduleInvalidate={invalidateAssessmentSchedule}
                      onAssessmentProgressPatch={patchAssessmentScheduleProgress}
                      requestFocusCriterionKey={
                        String(eventAssessmentModalId) === String(modalEvent.id)
                          ? pendingClosedCycleCriterionFocusKey
                          : null
                      }
                      onRequestFocusCriterionHandled={() => setPendingClosedCycleCriterionFocusKey(null)}
                      isDarkMode={isDarkMode}
                      onEnableDarkMode={
                        !isDarkMode && onToggleDarkMode
                          ? () => onToggleDarkMode()
                          : undefined
                      }
                    />
                  </div>
                </AssessmentEventModalFrame>
              );
            }
            if (cycleDetailCriteria === null) {
              return (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
                  onClick={() => closeAssessmentModalForEvent(modalEvent.id)}
                >
                  <div
                    className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{translateUi("Loading assessment")}</h3>
                    <p className="text-slate-600 text-sm mb-4">{translateUi("Fetching criteria for this cycle…")}</p>
                    <button
                      type="button"
                      onClick={() => closeAssessmentModalForEvent(modalEvent.id)}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    > {translateUi("Cancel")} </button>
                  </div>
                </div>
              );
            }
            return (
              <CustomCycleAssessmentModal
                employeeId={employeeId}
                employeeCycleId={viewingCycle.id}
                evidenceStorageEmployeeCycleId={viewingCycle.source_employee_cycle_id ?? undefined}
                companyCode={companyCode ?? employee?.company_code ?? ''}
                currentUserEmail={currentUserEmail}
                canAssess={canMutateAssessmentsInViewingCycle && !isAssessmentEditRestricted(modalEvent)}
                readOnlyEvidence={isViewingClosedCycleHistory && !closedCycleSystemAdminOverride}
                isMobileOrTablet={isMobileOrTablet}
                event={modalEvent}
                criteria={cycleDetailCriteria ?? []}
                onClose={() => closeAssessmentModalForEvent(modalEvent.id)}
                onMarkCompleted={async () => {
                  const ok = await markEmployeeCycleEventCompleted(modalEvent.id);
                  if (ok) closeAssessmentModalForEvent(modalEvent.id);
                }}
                onRecordAmendment={async (notes) => {
                  await recordAmendmentForEvent(modalEvent.id, notes);
                }}
                onStartAssessment={(criterionKeys, elementKeys) => startAssessmentForEvent(modalEvent.id, criterionKeys, elementKeys)}
                showAssessedOnly={modalEvent.status === 'completed'}
                isLastEvent={isLastAssessmentEvent}
                priorEventIds={priorEventIds}
                isDarkMode={isDarkMode}
                onToggleDarkMode={onToggleDarkMode}
                onClosedCycleSummaryInvalidate={isViewingClosedCycleHistory ? refreshClosedCycleAssessmentSummary : undefined}
                onAssessmentScheduleInvalidate={invalidateAssessmentSchedule}
                onAssessmentProgressPatch={patchAssessmentScheduleProgress}
                requestFocusCriterionKey={
                  String(eventAssessmentModalId) === String(modalEvent.id)
                    ? pendingClosedCycleCriterionFocusKey
                    : null
                }
                onRequestFocusCriterionHandled={() => setPendingClosedCycleCriterionFocusKey(null)}
                patchEmployeeCycleEvent={(partial) =>
                  setEmployeeCycleEvents((prev) =>
                    prev.map((e) => (String(e.id) === String(modalEvent.id) ? { ...e, ...partial } : e))
                  )
                }
              />
            );
          })()}
        </div>
      ) : (
      <div className={cn("employee-profile-shell space-y-4 sm:space-y-5 min-w-0 max-w-full overflow-x-hidden", isDarkMode && "employee-profile-shell--dark")}>
      <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between")}>
        <button
          onClick={onBack}
          className={cn(
            "inline-flex items-center text-sm font-medium transition-colors",
            isDarkMode ? "text-slate-300 hover:text-slate-100" : "text-slate-500 hover:text-slate-900"
          )}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {translateUi("Back to Dashboard")}
        </button>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {canEdit && activeTab !== 'Medical' && activeTab !== 'Cab Passes' && (
            isEditing ? (
              <>
                <button 
                  onClick={async () => {
                    if (savingMainChanges) return;
                    if (licenceConfirmationBlocksSave) {
                      alert(translateUi("Please confirm the licence details are correct (open the Licence tab and tick the confirmation box) before saving."));
                      return;
                    }
                    const pendingManager = (editForm.manager ?? employee.manager ?? '').trim();
                    if (pendingManager && isSamePersonName(pendingManager, employee.full_name)) {
                      alert(translateUi("An employee cannot be their own assessing manager. Choose a different assessor with the Assessor additional role."));
                      return;
                    }
                    if (
                      pendingManager &&
                      !assessorEligibleManagers.some((m) => isSamePersonName(m.full_name, pendingManager))
                    ) {
                      alert(translateUi("Assessing manager must be a team member with the Assessor additional role."));
                      return;
                    }
                    let allowedUpdates = buildEmployeePatchFromEditForm(employee, editForm);
                    if (typeof allowedUpdates.manager === "string") {
                      allowedUpdates.manager = allowedUpdates.manager.trim();
                    }
                    const saveStamp = new Date().toISOString();
                    const hasAddedAuditPersisted =
                      String(employee?.licence_details_added_at ?? '').trim() &&
                      String(employee?.licence_details_added_by ?? '').trim();
                    if (licenceScanRequiresConfirmation && licenceScanDetailsConfirmed) {
                      /** Stamp confirmation; if added-* was never persisted (common with older flows), backfill so RTDB matches assurance. */
                      allowedUpdates = {
                        ...allowedUpdates,
                        licence_details_confirmed_at: saveStamp,
                        licence_details_confirmed_by: licenceDetailsUserLabel,
                        ...(!hasAddedAuditPersisted
                          ? { licence_details_added_at: saveStamp, licence_details_added_by: licenceDetailsUserLabel }
                          : {}),
                      };
                    }
                    /** Legacy rows: licence photos without first-capture audit (predates stamps or non-edit uploads). One save backfills. */
                    const nextFrontUrl = Object.prototype.hasOwnProperty.call(allowedUpdates, "licence_image_url")
                      ? String((allowedUpdates as { licence_image_url?: string }).licence_image_url ?? "").trim()
                      : String(employee?.licence_image_url ?? "").trim();
                    const nextBackUrl = Object.prototype.hasOwnProperty.call(allowedUpdates, "licence_back_image_url")
                      ? String((allowedUpdates as { licence_back_image_url?: string }).licence_back_image_url ?? "").trim()
                      : String(employee?.licence_back_image_url ?? "").trim();
                    const willHaveLicenceMedia = Boolean(nextFrontUrl || nextBackUrl);
                    if (willHaveLicenceMedia && !hasAddedAuditPersisted) {
                      allowedUpdates = {
                        ...allowedUpdates,
                        licence_details_added_at: saveStamp,
                        licence_details_added_by: licenceDetailsUserLabel,
                      };
                    }
                    if (Object.keys(allowedUpdates).length === 0) {
                      setSavingMainChanges(true);
                      try {
                        const ok = await handleUpdate({});
                        if (ok) {
                          setEditForm({});
                          setIsEditing(false);
                          setLicenceScanRequiresConfirmation(false);
                          setLicenceScanDetailsConfirmed(false);
                          setLicenceFieldHighlight({});
                        }
                      } finally {
                        setSavingMainChanges(false);
                      }
                      return;
                    }
                    setSavingMainChanges(true);
                    try {
                      const ok = await handleUpdate(allowedUpdates);
                      if (ok) {
                        setEditForm({});
                        setIsEditing(false);
                        setLicenceScanRequiresConfirmation(false);
                        setLicenceScanDetailsConfirmed(false);
                        setLicenceFieldHighlight({});
                      }
                    } finally {
                      setSavingMainChanges(false);
                    }
                  }}
                  disabled={licenceConfirmationBlocksSave || savingMainChanges}
                  title={licenceConfirmationBlocksSave ? translateUi("Confirm licence details on the Licence tab before saving") : undefined}
                  className={`flex items-center px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors ${(licenceConfirmationBlocksSave || savingMainChanges) ? "opacity-50 cursor-not-allowed hover:bg-emerald-600" : "hover:bg-emerald-700"}`}
                >
                  {savingMainChanges ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  {savingMainChanges ? 'Saving...' : translateUi("Save Changes")}
                </button>
                <button 
                onClick={() => {
                  setIsEditing(false);
                  setSavingMainChanges(false);
                  setEditForm({});
                  setLicenceScanRequiresConfirmation(false);
                  setLicenceScanDetailsConfirmed(false);
                  setLicenceFieldHighlight({});
                }}
                  className={cn(
                    "flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                    isDarkMode
                      ? "bg-slate-900 border border-slate-700 text-slate-200 hover:bg-slate-800"
                      : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  )}
                > {translateUi("Cancel")} </button>
              </>
            ) : (
              <button 
                onClick={() => {
                  setEditForm(employee ? { ...employee } : {});
                  setSavingMainChanges(false);
                  setLicenceScanRequiresConfirmation(employeeNeedsLicenceDetailsConfirmation(employee));
                  setLicenceScanDetailsConfirmed(false);
                  setLicenceFieldHighlight({});
                  setIsEditing(true);
                }}
                className={cn(
                  "flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                  isDarkMode
                    ? "bg-slate-900 border border-slate-700 text-slate-200 hover:bg-slate-800"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                )}
              >
                <Edit2 className={cn("w-4 h-4 mr-2", isDarkMode ? "text-slate-400" : "text-slate-400")} /> {translateUi("Edit Record")}
              </button>
            )
          )}
          {canDelete && activeTab === 'Main' && (
            <button 
              onClick={() => {
                setConfirmState({
                  isOpen: true,
                  title: translateUi("Delete Employee"),
                  message: `Are you sure you want to delete ${employee.full_name}? This action cannot be undone.`,
                  onConfirm: () => {
                    onDelete(employee.id);
                    setConfirmState(null);
                  }
                });
              }}
              className={cn(
                "flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                isDarkMode
                  ? "bg-slate-900 border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                  : "bg-white border border-red-100 text-red-600 hover:bg-red-50"
              )}
            >
              <Trash2 className={cn("w-4 h-4 mr-2", isDarkMode ? "text-rose-300" : "text-red-400")} /> {translateUi("Delete Employee")}
            </button>
          )}
          {isSystemAdmin && activeTab === 'Main' && (
            <>
            <button 
              onClick={async () => {
                if (!currentUserEmail) {
                  alert(translateUi("Session email not found. Please sign out and sign in again."));
                  return;
                }
                setCleaningUp(true);
                try {
                  const res = await fetch(`/api/employees/cleanup-transfer-data/${encodeURIComponent(employeeId)}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
                    body: JSON.stringify({ userEmail: currentUserEmail }),
                    cache: 'no-store',
                  });
                  if (res.ok) {
                    refetchEmployee();
                  } else {
                    const ct = res.headers.get('content-type');
                    const err = ct?.includes('json') ? await res.json().catch(() => ({})) : {};
                    alert((err as { error?: string }).error || res.statusText || `Cleanup failed (${res.status})`);
                  }
                } catch (e) {
                  alert(translateUi('Cleanup failed: {{error}}', { error: e instanceof Error ? e.message : String(e) }));
                } finally {
                  setCleaningUp(false);
                }
              }}
              disabled={cleaningUp}
              className={cn(
                "flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                isDarkMode
                  ? "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              )}
              title={translateUi("Remove medication checks, notes, training, competencies, documents (for employees transferred before cleanup was added)")}
            >
              {cleaningUp ? translateUi('Cleaning...') : translateUi('Clean transferred data')}
            </button>
            <button 
              onClick={() => {
                setConfirmState({
                  isOpen: true,
                  title: translateUi("Transfer employee – warning"),
                  message: translateUi("Transferring an employee will:\n\n• Assign a new employee ID (the old ID will be removed).\n• Remove: medication checks, notes, training records, trains & routes (competencies), certificate entries, and all documents.\n• Preserve: licence details and incidents.\n• Require a new email for the new company.\n• Reset the certificate to the new company (logo, address).\n\nPersonal information (name, DOB) will remain unchanged – please verify it is correct before proceeding. This action should only be used when an employee has left one company and joined another."),
                  onConfirm: () => {
                    setConfirmState(null);
                    setTransferTargetCode('');
                    setTransferNewEmail('');
                    setTransferModalOpen(true);
                  }
                });
              }}
              className={cn(
                "flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                isDarkMode
                  ? "bg-slate-900 border border-amber-400/35 text-amber-300 hover:bg-amber-500/10"
                  : "bg-white border border-amber-200 text-amber-700 hover:bg-amber-50"
              )}
            >
              <ArrowLeftRight className={cn("w-4 h-4 mr-2", isDarkMode ? "text-amber-300" : "text-amber-500")} /> {translateUi("Transfer to Company")}
            </button>
            </>
          )}
          <EmployeeProfileBrochureButton
            employee={{ ...(employee as unknown as Record<string, unknown>), ...profileLock }}
            documents={documents}
            employeeCycles={employeeCycles as unknown as Record<string, unknown>[]}
            cycleEvents={employeeCycleEvents as unknown as Record<string, unknown>[]}
            safetyBriefs={safetyBriefs}
            isDarkMode={isDarkMode}
            producedBy={currentUserProfile?.full_name || currentUserEmail}
            producerEmail={currentUserEmail}
            producerEmployeeId={currentUserProfile?.employee_id || undefined}
            companyHeaders={companyHeaders}
          />
          {canLockEmployeeProfile && (
            <EmployeeProfileLockButton
              employeeId={employeeId}
              employeeName={employee.full_name}
              lock={profileLock}
              isDarkMode={isDarkMode}
              companyHeaders={companyHeaders}
              onLockChange={(next) => {
                setProfileLockOverride(next);
                void refetchEmployee();
              }}
            />
          )}
          {activeTab !== 'Medical' && (!isVerificationModuleActive || canRunQAVerifications) && (
            <button
              onClick={runAnalysis}
              disabled={analyzing || verificationOpen}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {analyzing || verificationOpen ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <ShieldCheck className="w-4 h-4 mr-2" />
              )}
              {verificationOpen || analyzing ? translateUi('Verifying…') : translateUi('Run Employee Verification')}
            </button>
          )}
        </div>
      </div>

      <EmployeeProfileLockBanner lock={profileLock} isDarkMode={isDarkMode} />

      {/* Tabs */}
      <div className="employee-profile-nav-shell">
        <EmployeeProfileMegaMenu
          tabs={tabs as EmployeeProfileNavTab[]}
          activeTab={activeTab as EmployeeProfileNavTab}
          onSelectTab={handleProfileTabSelect}
          isDarkMode={isDarkMode}
          isMobileOrTablet={isMobileOrTablet}
          isTablet={isTablet}
          mobileOpen={mobileProfileNavOpen}
          onMobileOpenChange={setMobileProfileNavOpen}
        />
      </div>

      {analysis && (
        <div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "p-4 rounded-xl text-sm italic border",
            isDarkMode
              ? "bg-indigo-500/10 border-indigo-400/30 text-indigo-100"
              : "bg-indigo-50 border-indigo-100 text-indigo-900"
          )}
        >
          <p className={cn("font-bold text-xs uppercase tracking-wider mb-1 not-italic", isDarkMode ? "text-indigo-300" : "text-indigo-500")}>{translateUi("AUTOMATED VERIFICATION")}</p>
          "{analysis}"
        </div>
      )}

      <VerificationFlowModal
        open={verificationOpen && isVerificationModuleActive && canRunQAVerifications}
        employeeId={employeeId}
        employeeName={String(employee?.full_name ?? "")}
        companyCode={effectiveCompanyCode}
        currentUserEmail={currentUserEmail}
        currentUserName={chromeDisplayName(currentUserProfile, currentUserEmail ? { email: currentUserEmail, uid: "" } : null)}
        isDarkMode={isDarkMode}
        onClose={() => setVerificationOpen(false)}
        onNavigateToFix={(target) => {
          const tab = target.tab;
          const allowed =
            tab === "Tasks" ||
            (EMPLOYEE_PROFILE_TAB_OPTIONS as readonly string[]).includes(tab);
          if (allowed) {
            handleProfileTabSelect(tab as EmployeeProfileTab);
            setVerificationOpen(false);
          }
        }}
      />

      {(isMedicallyUnfit || isNotCompetent) && (
        <div className={cn(
          "p-4 rounded-xl flex items-center border",
          isDarkMode ? "bg-rose-500/10 border-rose-500/30 text-rose-200" : "bg-red-50 border-red-200 text-red-800"
        )}>
          <ExclamationTriangle className="w-5 h-5 mr-3 flex-shrink-0" />
          <div>
            <p className="font-bold">{translateUi("NOT SAFE TO WORK")}</p>
            <p className="text-sm">
              {isMedicallyUnfit && (employee.medicals?.some(m => (m.is_latest === 1 || m.is_latest === '1' || m.is_latest === true) && m.status === 'Unfit') ? translateUi("Medical Status: Unfit. ") : translateUi("Medical Expired. "))}
              {isNotCompetent && translateUi("Mandatory Competency Expired.")}
            </p>
          </div>
        </div>
      )}

      <div className={cn(
        "employee-pane-surface grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 rounded-2xl border p-3 sm:p-4 min-w-0 max-w-full overflow-x-hidden",
        isDarkMode ? "border-slate-700/80 bg-slate-950/40" : "border-slate-200 bg-slate-50/70"
      )}>
        {activeTab === 'Main' && (
          <div className={cn(
            "lg:col-span-12 rounded-2xl border p-5 sm:p-6",
            isDarkMode
              ? "bg-gradient-to-r from-indigo-500/20 via-slate-900 to-violet-500/20 border-slate-700"
              : "bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-indigo-100"
          )}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className={cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-indigo-300" : "text-indigo-600")}>{translateUi("Main Employee Page")}</p>
                <h2 className={cn("text-xl sm:text-2xl font-bold mt-1", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Employee Record Centre")}</h2>
                <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Personal profile, contact details, and emergency information in one workspace.")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border", isDarkMode ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-white border-slate-200 text-slate-700")}>
                  {translateUi("ID")}: {employee.id}
                </span>
                <span className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold border",
                  employee.status === 'Active'
                    ? (isDarkMode ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300" : "bg-emerald-50 border-emerald-200 text-emerald-700")
                    : (isDarkMode ? "bg-amber-500/15 border-amber-400/30 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-700")
                )}>
                  {formatEmployeeStatusLabel(employee.status)}
                </span>
                <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border", isDarkMode ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-white border-slate-200 text-slate-700")}>
                  {translateUi("Depot")}: {employee.depot || "—"}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Personal Info */}
        {activeTab === 'Main' && (
          <div className="lg:col-span-12 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-start lg:items-stretch min-h-0">
            <div className="lg:col-span-5 min-w-0">
            <div className={cn(
              "p-6 rounded-2xl border shadow-sm overflow-hidden",
              isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
            )}>
              <div className={cn(
                "mb-5 rounded-2xl border px-4 py-3",
                isDarkMode ? "bg-indigo-500/10 border-indigo-400/25" : "bg-indigo-50 border-indigo-100"
              )}>
                <p className={cn("text-[11px] uppercase tracking-wider font-bold", isDarkMode ? "text-indigo-300" : "text-indigo-600")}>{translateUi("Employee Main Profile")}</p>
                <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Core identity, status, and monitoring overview.")}</p>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative group">
                  <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center overflow-hidden border border-slate-200">
                    {employee.image_url ? (
                      <img src={employee.image_url} alt={employee.full_name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <User className="w-10 h-10 text-slate-400" />
                    )}
                    {profileImageAction && (
                      <div className="absolute inset-0 bg-slate-900/45 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  {canEdit && employee.image_url && (
                    <button 
                      type="button"
                      disabled={profileImageAction !== null}
                      onClick={(e) => {
                        if (profileImageAction) return;
                        e.stopPropagation();
                        e.preventDefault();
                        setConfirmState({
                          isOpen: true,
                          title: "Delete Picture",
                          message: "Are you sure you want to delete this picture?",
                          onConfirm: () => {
                            setProfileImageAction('deleting');
                            // Send empty string (not only null) so the PATCH always includes image_url
                            // and the server can delete the Azure cms-assets blob.
                            void handleUpdate({ image_url: "" }).finally(() => {
                              setProfileImageAction(null);
                            });
                            setConfirmState(null);
                          }
                        });
                      }}
                      className={cn(
                        "absolute -top-2 -right-2 p-1 bg-red-100 border border-red-200 text-red-600 rounded-full shadow-sm transition-opacity hover:bg-red-200 z-50 cursor-pointer",
                        profileImageAction ? "opacity-40 cursor-not-allowed" : "opacity-0 group-hover:opacity-100"
                      )}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  {canEdit && (
                    <label className={cn(
                      "absolute -bottom-2 -right-2 p-2 bg-white border border-slate-200 rounded-xl shadow-sm transition-opacity hover:bg-slate-50 z-10",
                      profileImageAction ? "opacity-40 cursor-not-allowed" : "opacity-0 group-hover:opacity-100 cursor-pointer"
                    )}>
                      <Camera className="w-4 h-4 text-slate-600" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => {
                          if (profileImageAction) return;
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setProfileImageAction('uploading');
                              void handleUpdate({ image_url: reader.result as string }).finally(() => {
                                setProfileImageAction(null);
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                          e.target.value = '';
                        }} 
                        disabled={profileImageAction !== null}
                      />
                    </label>
                  )}
                </div>
              <div>
                {isEditing ? (
                  <input 
                    type="text" 
                    value={editForm.full_name || ''} 
                    onChange={e => setEditForm({ ...editForm, full_name: e.target.value })}
                    className={cn(
                      "text-xl font-bold rounded px-2 py-1 w-full mb-1 border",
                      isDarkMode ? "text-slate-100 bg-slate-800 border-slate-700" : "text-slate-900 bg-slate-50 border-slate-200"
                    )}
                  />
                ) : (
                  <h2 className={cn("text-xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.full_name}</h2>
                )}
                {isEditing ? (
                  <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    • {editForm.id ?? employee.id}
                  </p>
                ) : (
                  <p className={cn("text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                    • {employee.id}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {canEdit && isEditing && (
                <div className="flex justify-between text-sm items-center">
                  <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Employee ID")}</span>
                  <input 
                    type="text" 
                    value={editForm.id ?? employee.id} 
                    onChange={e => setEditForm({ ...editForm, id: e.target.value.trim() })}
                    placeholder="e.g. E1A2B3C4"
                    className={cn(
                      "font-medium border rounded px-2 py-1 text-right w-40 font-mono",
                      isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200"
                    )}
                  />
                </div>
              )}
              <div className="flex justify-between text-sm items-center">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("Company Code")} <span className={cn("font-normal", isDarkMode ? "text-amber-300" : "text-amber-600")}> {translateUi("(locked — only changes on transfer)")} </span>
                </span>
                <span className={cn("font-medium font-mono", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.company_code || '—'}</span>
              </div>
              <div className="flex justify-between text-sm items-center gap-3">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Job role")}</span>
                {isEditing ? (
                  companyJobRoles.length > 0 ? (
                    <select
                      value={String(editForm.role ?? employee.role ?? '')}
                      onChange={(e) => {
                        setEditForm((f) => ({ ...f, role: e.target.value }));
                      }}
                      className={cn(
                        "font-medium border rounded px-2 py-1 w-full min-w-0 sm:max-w-md text-right",
                        isDarkMode
                          ? "text-slate-100 bg-slate-800 border-slate-700"
                          : "text-slate-900 bg-slate-50 border-slate-200"
                      )}
                    >
                      <option value="">{translateUi("Select job role…")}</option>
                      {(() => {
                        const current = String(editForm.role ?? employee.role ?? '').trim();
                        const sorted = [...companyJobRoles].sort((a, b) =>
                          a.localeCompare(b, undefined, { sensitivity: 'base' })
                        );
                        const inList = current
                          ? sorted.some((row) => row.toLowerCase() === current.toLowerCase())
                          : true;
                        const options = current && !inList ? [current, ...sorted] : sorted;
                        return options.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ));
                      })()}
                    </select>
                  ) : (
                    <div className="text-right min-w-0 sm:max-w-md space-y-1">
                      {employee.role ? (
                        <p
                          className={cn("text-sm font-medium", isDarkMode ? "text-slate-300" : "text-slate-700")}
                        >
                          {employee.role}
                        </p>
                      ) : null}
                      <p
                        className={cn(
                          "text-xs",
                          isDarkMode ? "text-amber-200/90" : "text-amber-800"
                        )}
                      >
                        No company job roles are configured. A company admin can add them under{" "}
                        <span className="font-semibold">{translateUi("Custom job roles")}</span> {translateUi("in Company administration.")}
                      </p>
                    </div>
                  )
                ) : (
                  <span
                    className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}
                  >
                    {employee.role || '—'}
                  </span>
                )}
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Depot")} </span>
                {isEditing ? (
                  <select
                    value={editForm.depot || ''}
                    onChange={e => setEditForm({ ...editForm, depot: e.target.value })}
                    className={cn(
                      "font-medium border rounded px-2 py-1 text-right w-40 sm:w-44 disabled:opacity-60",
                      isDarkMode
                        ? "text-slate-100 bg-slate-800 border-slate-700"
                        : "text-slate-900 bg-slate-50 border-slate-200"
                    )}
                    disabled={companyDepots.length === 0}
                  >
                    <option value="">
                      {companyDepots.length === 0 ? translateUi("No depots configured") : translateUi("Select depot...")}
                    </option>
                    {(() => {
                      const currentDepot = String(editForm.depot || '').trim();
                      const sortedDepots = [...companyDepots].sort((a, b) =>
                        a.localeCompare(b, undefined, { sensitivity: 'base' })
                      );
                      const inList = currentDepot
                        ? sortedDepots.some((row) => row.toLowerCase() === currentDepot.toLowerCase())
                        : true;
                      const depotOptions = currentDepot && !inList ? [currentDepot, ...sortedDepots] : sortedDepots;
                      return depotOptions.map((depotOption) => (
                        <option key={depotOption} value={depotOption}>
                          {depotOption}
                        </option>
                      ));
                    })()}
                  </select>
                ) : (
                  <span className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.depot}</span>
                )}
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Status")} </span>
                {isEditing ? (
                  <select 
                    value={editForm.status || "Active"} 
                    onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                    className={cn(
                      "font-medium border rounded px-2 py-1 text-right w-32",
                      isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200"
                    )}
                  >
                    <option value="Active"> {translateUi("Active")} </option>
                    <option value="Inactive"> {translateUi("Inactive")} </option>
                    <option value="On Leave">{translateUi("On Leave")}</option>
                    <option value="Suspended"> {translateUi("Suspended")} </option>
                  </select>
                ) : (
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-xs font-medium",
                    employee.status === 'Active' ? "bg-emerald-100 text-emerald-700 status-chip-positive" : "bg-slate-100 text-slate-700"
                  )}>{formatEmployeeStatusLabel(employee.status)}</span>
                )}
              </div>
              <div className="flex justify-between text-sm items-center gap-3">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Monitoring Status")}</span>
                {isEditing ? (
                  <select
                    value={displayedMonitoringStatus}
                    onChange={(e) => setEditForm({ ...editForm, monitoring_status: e.target.value })}
                    className={cn(
                      "font-medium border rounded px-2 py-1 text-right w-48 max-w-[12rem]",
                      isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200"
                    )}
                  >
                    {monitoringStatusOptions.map((opt) => (
                      <option key={opt.label} value={opt.label}>
                        {formatMonitoringStatusLabel(opt.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    <span className={cn(
                      "px-2 py-0.5 rounded-full text-xs font-medium",
                      monitoringStatusOptions.find((opt) => opt.label === displayedMonitoringStatus)?.class
                        ?? (isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-700")
                    )}>
                      {formatMonitoringStatusLabel(displayedMonitoringStatus)}
                    </span>
                    {showOhFollowUpDueCompanion && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {translateUi("OH follow-up due")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className={cn(isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Assessing Manager")}</span>
                {isEditing ? (
                  <div className="text-right">
                    <select 
                      value={editForm.manager ?? employee.manager ?? ''} 
                      onChange={e => setEditForm({ ...editForm, manager: e.target.value })}
                      className={cn(
                        "font-medium border rounded px-2 py-1 text-right w-48",
                        isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200",
                        assessingManagerSelfSelected && "border-amber-500"
                      )}
                    >
                      <option value="">{translateUi("Select assessor...")}</option>
                      {assessorEligibleManagers.map(m => (
                        <option key={m.id} value={m.full_name}>{m.full_name}</option>
                      ))}
                    </select>
                    {assessorEligibleManagers.length === 0 && (
                      <p className={cn("mt-1 text-[11px] max-w-xs ml-auto", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                        {translateUi("No team members with the Assessor additional role are available.")}
                      </p>
                    )}
                    {assessingManagerSelfSelected && (
                      <p className={cn("mt-1 text-[11px] max-w-xs ml-auto", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                        {translateUi("An employee cannot be their own assessing manager.")}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.manager || '—'}</span>
                )}
              </div>
              <div className={cn("pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                <p className={cn("text-xs font-bold uppercase tracking-wider mb-3", isDarkMode ? "text-slate-400" : "text-slate-400")}>{translateUi("Additional Roles")}</p>
                <div className="flex flex-wrap gap-2">
                  {workStatusOptions.map(opt => {
                    const current = (employee.work_status || '')
                      .split(',')
                      .map((value) => value.trim())
                      .filter(Boolean);
                    const isActive = current.some((role) => rolesMatch(role, opt));
                    const canChangeAdditionalRoles = canEdit && isEditing;
                    return (
                      <button
                        key={opt}
                        disabled={!canChangeAdditionalRoles}
                        onClick={canChangeAdditionalRoles ? () => {
                          const next = isActive
                            ? current.filter((c) => !rolesMatch(c, opt))
                            : [...current.filter((c) => !rolesMatch(c, opt)), opt];
                          handleUpdate({ work_status: normalizeAdditionalRolesCsv(next.join(',')) });
                        } : undefined}
                        className={cn(
                          "px-3 py-1 rounded-lg text-xs font-medium border transition-all",
                          isActive
                            ? (canChangeAdditionalRoles
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : isDarkMode
                                  ? "bg-indigo-700 text-indigo-100 border-indigo-500"
                                  : "bg-indigo-50 text-indigo-700 border-indigo-100")
                            : isDarkMode
                              ? "bg-slate-800 text-slate-300 border-slate-600 hover:border-slate-500"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300",
                          !canChangeAdditionalRoles && !isActive && "opacity-50 cursor-not-allowed hover:border-slate-200",
                          !canChangeAdditionalRoles && isActive && "cursor-not-allowed"
                        )}
                      >
                        {formatAdditionalRoleLabel(opt)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={cn("pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                <p className={cn("text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-400")}>{translateUi("Contact Work Details")}</p>
                {isEditing ? (
                  <div className="space-y-2">
                    <label className={cn("text-sm flex items-center gap-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      <Mail className={cn("w-3 h-3 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                      <input
                        type="email"
                        value={editForm.email ?? employee.email ?? ''}
                        onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                        placeholder="Work email"
                        className={cn(
                          "flex-1 min-w-0 font-medium border rounded px-2 py-1",
                          isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200"
                        )}
                      />
                    </label>
                    <label className={cn("text-sm flex items-center gap-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      <Phone className={cn("w-3 h-3 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                      <input
                        type="tel"
                        value={editForm.work_phone ?? employee.work_phone ?? employee.phone ?? ''}
                        onChange={e => setEditForm({ ...editForm, work_phone: e.target.value })}
                        placeholder="Work phone"
                        className={cn(
                          "flex-1 min-w-0 font-medium border rounded px-2 py-1",
                          isDarkMode ? "text-slate-100 bg-slate-800 border-slate-600" : "text-slate-900 bg-slate-50 border-slate-200"
                        )}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    <p className={cn("text-sm mb-1 flex items-center", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const emailAddress = employee.email;
                          window.location.href = `mailto:${emailAddress}`;
                        }}
                        className={cn(
                          "flex items-center transition-colors cursor-pointer",
                          isDarkMode ? "hover:text-indigo-300" : "hover:text-indigo-600"
                        )}
                      >
                        <Mail className={cn("w-3 h-3 mr-2", isDarkMode ? "text-slate-400" : "text-slate-400")} /> {employee.email}
                      </a>
                    </p>
                    <p className={cn("text-sm flex items-center", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      <Phone className={cn("w-3 h-3 mr-2", isDarkMode ? "text-slate-400" : "text-slate-400")} /> {employee.work_phone || employee.phone || '—'}
                    </p>
                  </>
                )}
              </div>

              {/* Signature - show when exists (under Contact Work Details); edit controls only when editing */}
              {((editForm.signature_url ?? employee.signature_url) || (canEdit && isEditing)) ? (
                <div className={cn("pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                  <p className={cn("text-xs font-bold uppercase tracking-wider mb-3", isDarkMode ? "text-slate-400" : "text-slate-400")}> {translateUi("Signature")} </p>
                  {(editForm.signature_url ?? employee.signature_url) ? (
                    <div className="relative inline-block signature-force-light rounded-lg border border-slate-200 p-2">
                      <img 
                        src={editForm.signature_url ?? employee.signature_url} 
                        alt={translateUi("Signature")} 
                        className="max-h-24 w-auto object-contain"
                      />
                      {canEdit && isEditing && (
                        <button
                          type="button"
                          onClick={() => handleUpdate({ signature_url: '' })}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-100 border border-red-200 text-red-600 rounded-full shadow-sm hover:bg-red-200 transition-colors z-10"
                          title={translateUi("Delete signature")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : null}
                  {canEdit && isEditing && (
                    <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3 mt-2 w-full min-w-0">
                      <button
                        type="button"
                        onClick={() => setSignatureModalOpen(true)}
                        className={cn(
                          "signature-drop-zone w-full min-w-0 min-h-[120px] h-32 flex flex-col items-center justify-center px-2 border-2 border-dashed rounded-xl transition-colors",
                          isDarkMode ? "border-slate-600 bg-slate-800 hover:bg-slate-700" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                        )}
                      >
                        <Edit2 className={cn("w-7 h-7 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                        <span className={cn("text-sm font-medium text-center", isDarkMode ? "text-slate-200" : "text-slate-600")}>
                          {isMobile || isTablet ? translateUi("Tap to sign") : translateUi("Draw signature")}
                        </span>
                        {!(isMobile || isTablet) ? (
                          <span className={cn("text-xs mt-0.5 sm:mt-1 text-center", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Mouse or trackpad")}</span>
                        ) : null}
                      </button>
                      <label
                        className={cn(
                          "signature-drop-zone flex flex-col items-center justify-center w-full min-w-0 min-h-[120px] h-32 px-2 border-2 border-dashed rounded-xl cursor-pointer transition-colors",
                          isDarkMode ? "border-slate-600 bg-slate-800 hover:bg-slate-700" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                        )}
                      >
                        <Upload className={cn("w-6 h-6 sm:w-7 sm:h-7 mb-1.5 sm:mb-2 shrink-0", isDarkMode ? "text-slate-400" : "text-slate-400")} />
                        <span className={cn("text-sm font-medium text-center", isDarkMode ? "text-slate-200" : "text-slate-600")}>{translateUi("Upload image")}</span>
                        <span className={cn("text-xs mt-0.5 sm:mt-1 text-center", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("PNG, JPG · 5MB max")}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => handleUpdate({ signature_url: reader.result as string });
                              reader.readAsDataURL(file);
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

              {/* Draw signature (mouse, trackpad, touch) */}
              {signatureModalOpen && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setSignatureModalOpen(false)}>
                  <div
                    className={cn(
                      "rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden",
                      isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={cn("px-4 py-3 border-b shrink-0", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                      <h3 className={cn("font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Sign here")}</h3>
                      <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Draw with your mouse, trackpad, or finger")}</p>
                    </div>
                    <div className="p-4 flex-1 min-h-0 flex flex-col">
                      <div className="flex-1 min-h-[280px] sm:min-h-[320px] signature-force-light">
                        <SignaturePad
                          onSave={(dataUrl) => {
                            handleUpdate({ signature_url: dataUrl });
                            setSignatureModalOpen(false);
                          }}
                          isDarkMode={isDarkMode}
                          className="w-full h-full min-h-[280px] sm:min-h-[320px] border-2 border-dashed border-slate-300 rounded-xl bg-white touch-none"
                          saveButtonLabel={translateUi("Apply")}
                        />
                      </div>
                    </div>
                    <div className={cn("px-4 pb-4 pt-2 shrink-0 border-t", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                      <button
                        type="button"
                        onClick={() => setSignatureModalOpen(false)}
                        className={cn(
                          "w-full px-4 py-3 text-sm font-medium rounded-xl transition-colors",
                          isDarkMode ? "text-slate-200 bg-slate-800 hover:bg-slate-700" : "text-slate-600 bg-slate-100 hover:bg-slate-200"
                        )}
                      > {translateUi("Cancel")} </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
            <div className="lg:col-span-7 flex min-h-0 min-w-0 flex-col gap-6 lg:h-full lg:min-h-0">
              <div className={cn(
                "rounded-2xl border p-4 sm:p-5 shrink-0",
                isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200 shadow-sm"
              )}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className={cn("text-[11px] uppercase tracking-wider font-bold", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Main Tab Summary")}</p>
                    <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Employee Record Overview")}</h3>
                  </div>
                  <span className={cn(
                    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold w-fit",
                    employee.status === 'Active'
                      ? (isDarkMode ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300" : "bg-emerald-50 border-emerald-200 text-emerald-700")
                      : (isDarkMode ? "bg-amber-500/15 border-amber-400/30 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-700")
                  )}>
                    {employee.status ? formatEmployeeStatusLabel(employee.status) : translateUi("Unknown status")}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  <div className={cn("rounded-xl border px-3 py-3", isDarkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200")}>
                    <p className={cn("text-[11px] uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Company Code")}</p>
                    <p className={cn("font-mono text-sm font-semibold mt-1", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.company_code || "—"}</p>
                  </div>
                  <div className={cn("rounded-xl border px-3 py-3", isDarkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200")}>
                    <p className={cn("text-[11px] uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Depot")} </p>
                    <p className={cn("text-sm font-semibold mt-1", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.depot || "—"}</p>
                  </div>
                  <div className={cn("rounded-xl border px-3 py-3", isDarkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200")}>
                    <p className={cn("text-[11px] uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Assessing Manager")}</p>
                    <p className={cn("text-sm font-semibold mt-1", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.manager || "—"}</p>
                  </div>
                  <div className={cn("rounded-xl border px-3 py-3", isDarkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200")}>
                    <p className={cn("text-[11px] uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Contact")} </p>
                    <p className={cn("text-sm font-semibold mt-1", isDarkMode ? "text-slate-100" : "text-slate-900")}>{employee.work_phone || employee.phone || "—"}</p>
                  </div>
                </div>
              </div>
              <div className={cn(
                "rounded-2xl border p-4 sm:p-5 shrink-0",
                isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200 shadow-sm"
              )}>
                <p className={cn("text-[11px] uppercase tracking-wider font-bold mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Monitoring")} </p>
                <h3 className={cn("text-base font-bold mb-4", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Monitoring status")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {monitoringStatusOptions.map((opt) => {
                    const canChangeMonitoringStatus = canEdit && isEditing;
                    const isBaseSelected = displayedMonitoringStatus === opt.label;
                    const isFollowUpCompanion =
                      opt.label === 'OH follow-up due' && showOhFollowUpDueCompanion;
                    const isHighlighted = isBaseSelected || isFollowUpCompanion;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        disabled={!canChangeMonitoringStatus}
                        onClick={
                          canChangeMonitoringStatus
                            ? () => {
                                if (isEditing) {
                                  setEditForm((prev) => ({ ...prev, monitoring_status: opt.label }));
                                } else {
                                  void handleUpdate({ monitoring_status: opt.label });
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          "px-3 py-2 rounded-xl text-xs font-medium border text-left transition-all",
                          isHighlighted
                            ? opt.class
                            : cn(
                                isDarkMode
                                  ? "bg-slate-800 text-slate-300 border-slate-600 hover:border-slate-500"
                                  : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300"
                              ),
                          !canChangeMonitoringStatus && "opacity-50 cursor-not-allowed hover:border-slate-200"
                        )}
                      >
                        {formatMonitoringStatusLabel(opt.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col lg:min-h-0">
                <EmployeeAddressAndEmergencyPanels
                  values={isEditing ? editForm : employee}
                  isEditing={isEditing}
                  isDarkMode={isDarkMode}
                  onChange={(updates) => setEditForm(prev => ({ ...prev, ...updates }))}
                />
              </div>
            </div>
          </div>
        )}

        {/* Records */}
        <div className={activeTab === 'Main' ? "hidden" : "lg:col-span-12 space-y-6 min-w-0 max-w-full overflow-x-hidden"}>
          {activeTab === 'Licence' ? (
            <>
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl border border-slate-200 shadow-sm min-w-0 max-w-full overflow-x-hidden">
              <div className="mb-4 text-xs md:text-sm text-slate-500 min-w-0 break-words">
                {(() => {
                  const hasFront = Boolean((employee.licence_image_url || '').trim());
                  const hasBack = Boolean((employee.licence_back_image_url || '').trim());
                  const showDeleted =
                    Boolean(
                      (employee.licence_details_deleted_at || '').trim() &&
                        (employee.licence_details_deleted_by || '').trim() &&
                        !hasFront &&
                        !hasBack
                    );
                  if (showDeleted) {
                    const t = new Date(employee.licence_details_deleted_at!);
                    const when = Number.isNaN(t.getTime())
                      ? String(employee.licence_details_deleted_at)
                      : formatDateTimeUKShort(t);
                    return (
                      <span className="text-slate-600">
                        Licence details deleted by{' '}
                        <span className="font-semibold text-slate-700">{employee.licence_details_deleted_by}</span>
                        {' '}on <span className="font-mono break-all">{when}</span>.
                      </span>
                    );
                  }
                  /** Only show real RTDB values — never substitute “current user / now” (that hid missing stamps from QA verification). */
                  const addedAtRaw = (employee.licence_details_added_at || '').trim();
                  const addedAt = (() => {
                    if (!addedAtRaw) return '';
                    const at = new Date(addedAtRaw);
                    return Number.isNaN(at.getTime()) ? String(addedAtRaw) : formatDateTimeUKShort(at);
                  })();
                  const addedBy = (employee.licence_details_added_by || '').trim();
                  const confirmedAtRaw = (employee.licence_details_confirmed_at || '').trim();
                  const confirmedBy = (employee.licence_details_confirmed_by || '').trim();
                  const hasConfirmedPair = Boolean(confirmedAtRaw && confirmedBy);
                  const confirmedAtDisplay = (() => {
                    if (!confirmedAtRaw) return '';
                    const ct = new Date(confirmedAtRaw);
                    return Number.isNaN(ct.getTime()) ? String(confirmedAtRaw) : formatDateTimeUKShort(ct);
                  })();
                  const fullAddedPair = Boolean(addedBy && addedAt);
                  const partialAddedOnly =
                    (addedBy && !addedAt) || (!addedBy && addedAt);
                  /** Older saves stamped confirmation without separate first-capture fields — show the same name/date here instead of a warning. */
                  const showAddedLineFromConfirmation =
                    !fullAddedPair &&
                    !partialAddedOnly &&
                    hasConfirmedPair &&
                    (hasFront || hasBack);
                  const showMissingAuditHelper =
                    !fullAddedPair && !partialAddedOnly && !showAddedLineFromConfirmation;
                  return (
                    <div className="space-y-3 w-full">
                      <p className="text-sm font-semibold text-slate-700">{translateUi("Licence details:-")}</p>
                      <div
                        className={cn(
                          "flex w-full gap-3",
                          showMissingAuditHelper
                            ? "items-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                            : "flex-wrap items-center overflow-x-auto whitespace-nowrap pb-1"
                        )}
                      >
                        <span className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                          {translateUi("Added by")}
                        </span>
                        {fullAddedPair ? (
                          <p className="text-sm text-slate-600 shrink-0">
                            <span className="font-semibold text-slate-700">{addedBy}</span>{' '}
                            on <span className="font-mono">{addedAt}</span>
                          </p>
                        ) : addedBy && !addedAt ? (
                          <p className="min-w-0 flex-1 text-sm text-slate-600 whitespace-normal">
                            <span className="font-semibold text-slate-700">{addedBy}</span>
                            {' '}(time not recorded on this record).
                          </p>
                        ) : !addedBy && addedAt ? (
                          <p className="min-w-0 flex-1 text-sm text-slate-600 whitespace-normal">
                            {translateUi("First capture logged on")} <span className="font-mono">{addedAt}</span> {translateUi("(user name not recorded).")} </p> ) : showAddedLineFromConfirmation ? ( <p className="min-w-0 flex-1 text-sm text-slate-600 whitespace-normal">
                            <span className="font-semibold text-slate-700">{confirmedBy}</span> on{' '}
                            <span className="font-mono">{confirmedAtDisplay}</span>
                          </p>
                        ) : (
                          <p className="min-w-0 flex-1 w-full text-sm text-amber-900 whitespace-normal leading-snug">
                            No audit name and date stored yet. They are saved when you upload or replace a licence photo, or when you use{' '}
                            <span className="font-semibold">{translateUi("Save changes")}</span> {translateUi("from Edit record with a licence image present.")}
                          </p>
                        )}
                        {hasConfirmedPair && !showAddedLineFromConfirmation ? (
                          <>
                            <span className="inline-flex shrink-0 items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                              {translateUi("Details confirmed by")}
                            </span>
                            <p className="text-sm text-slate-600 shrink-0">
                              <span className="font-semibold text-slate-700">{confirmedBy}</span>{' '}
                              on{' '}
                              <span className="font-mono">{confirmedAtDisplay}</span>
                            </p>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between mb-4 min-w-0">
                <h3 className="text-lg font-bold text-slate-900 flex items-center min-w-0">
                  <FileText className="w-5 h-5 mr-2 text-indigo-600 shrink-0" /> {translateUi("Train Driving Licence")}
                </h3>
              </div>

              <div
                className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 sm:px-4 py-3 text-sm text-slate-600 flex items-start gap-3 min-w-0"
                role="note"
              >
                <Info className="w-5 h-5 shrink-0 text-slate-400 mt-0.5" />
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold text-slate-700">{translateUi(LICENCE_INTERNATIONAL_HELPER_TITLE)}</p>
                  <p className="leading-snug">{translateUi(LICENCE_INTERNATIONAL_HELPER_BODY)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5 min-w-0 items-stretch">
                {/* Left: front stacked over back */}
                <section className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "flex h-full min-w-0 flex-col")}>
                  <DashboardGradientPaneGlows />
                  <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-3">
                    <h4 className="text-sm font-bold tracking-tight text-white">{translateUi("Licence photos")}</h4>
                {isEditing && licenceSideCorrectionNotice ? (
                  <div
                    role="status"
                    className="rounded-xl border border-sky-400/40 bg-sky-500/15 px-3 sm:px-4 py-3 text-sm text-sky-50 flex items-start gap-3"
                  >
                    <Info className="w-5 h-5 shrink-0 text-sky-300 mt-0.5" />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="font-semibold text-white">{translateUi("Photo holders corrected")}</p>
                      <p className="leading-snug text-sky-100/90">{licenceSideCorrectionNotice}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLicenceSideCorrectionNotice(null)}
                      className="shrink-0 p-1 rounded-lg text-sky-200 hover:bg-white/10"
                      aria-label={translateUi("Dismiss")}
                      title={translateUi("Dismiss")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : null}
                <div className="flex min-h-0 flex-1 flex-col justify-center gap-4 min-w-0">
                {/* Front image */}
                <div className="min-w-0 max-w-full">
                  <div
                    className={licenceDropZoneClass("front")}
                    style={{ aspectRatio: '1.4' }}
                    {...(canEdit ? licenceDropHandlers("front") : {})}
                  >
                    {employee.licence_image_url ? (
                      <img src={employee.licence_image_url} alt="Licence Front" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      renderLicenceSpecimenPlaceholder(
                        LICENCE_FRONT_SPECIMEN_SRC,
                        "No front image uploaded",
                      )
                    )}
                    {canEdit && !isEditing && licenceDropTarget === "front" && (
                      <div className="absolute inset-0 z-[6] flex items-center justify-center bg-amber-500/20 pointer-events-none p-4">
                        <span className="text-center text-sm font-semibold text-amber-950 bg-amber-100/95 border border-amber-300 px-4 py-2 rounded-lg shadow-sm">
                          {translateUi("Select Edit Record first to upload")}
                        </span>
                      </div>
                    )}
                    {canEdit && !isEditing && (
                      <button
                        type="button"
                        className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
                        onClick={() => alert(translateUi(LICENCE_UPLOAD_NEED_EDIT_MSG))}
                        aria-label={translateUi("Select Edit Record to upload licence front")}
                        {...licenceDropHandlers("front")}
                      />
                    )}
                    {canEdit && isEditing && employee.licence_image_url && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            window.confirm(translateUi('Remove the front licence photo and the licence number and dates read from it? The back photo and back-of-licence fields are kept. This cannot be undone.'))
                          ) {
                            void applyLicenceSideRemoval('front');
                          }
                        }}
                        className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-black/60 text-white hover:bg-red-600 transition-colors"
                        title={translateUi("Remove front image")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {canEdit && isEditing && (
                      <label
                        className={cn(
                          "absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity",
                          employee.licence_image_url
                            ? cn(
                                "bg-black/50",
                                licenceDropTarget === "front"
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100",
                              )
                            : licenceDropTarget === "front"
                              ? "bg-indigo-500/15 opacity-100"
                              : "bg-transparent opacity-100",
                        )}
                        {...licenceDropHandlers("front")}
                      >
                        {employee.licence_image_url ? (
                          <span
                            className={cn(
                              "text-white font-medium bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm",
                              licenceDropTarget === "front" ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                            )}
                          >
                            {licenceDropTarget === "front"
                              ? translateUi("Drop to upload front")
                              : translateUi("Upload Front")}
                          </span>
                        ) : (
                          <span
                            className={cn(
                              "text-slate-700 font-medium bg-white/90 px-3 py-2 rounded-lg shadow-sm border border-slate-200 text-center max-w-[92%] text-xs sm:text-sm",
                              licenceDropTarget === "front" ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                            )}
                          >
                            {licenceDropTarget === "front"
                              ? translateUi("Drop to upload front")
                              : translateUi(LICENCE_HOLDER_UPLOAD_HINT)}
                          </span>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          capture={isMobileOrTablet ? 'environment' : undefined}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            void ingestLicenceImageFile(file, "front");
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-slate-300 uppercase tracking-wide shrink-0"> {translateUi("Front")} </p>
                </div>

                {/* Back image (stacked under front) */}
                <div className="min-w-0 max-w-full">
                  <div
                    className={licenceDropZoneClass("back")}
                    style={{ aspectRatio: '1.4' }}
                    {...(canEdit ? licenceDropHandlers("back") : {})}
                  >
                    {employee.licence_back_image_url ? (
                      <img src={employee.licence_back_image_url} alt="Licence Back" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      renderLicenceSpecimenPlaceholder(
                        LICENCE_BACK_SPECIMEN_SRC,
                        "No back image uploaded",
                      )
                    )}
                    {canEdit && !isEditing && licenceDropTarget === "back" && (
                      <div className="absolute inset-0 z-[6] flex items-center justify-center bg-amber-500/20 pointer-events-none p-4">
                        <span className="text-center text-sm font-semibold text-amber-950 bg-amber-100/95 border border-amber-300 px-4 py-2 rounded-lg shadow-sm">
                          {translateUi("Select Edit Record first to upload")}
                        </span>
                      </div>
                    )}
                    {canEdit && !isEditing && (
                      <button
                        type="button"
                        className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
                        onClick={() => alert(translateUi(LICENCE_UPLOAD_NEED_EDIT_MSG))}
                        aria-label={translateUi("Select Edit Record to upload licence back")}
                        {...licenceDropHandlers("back")}
                      />
                    )}
                    {canEdit && isEditing && employee.licence_back_image_url && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            window.confirm(translateUi('Remove the back licence photo and the language and restrictions read from it? The front photo and front-of-licence fields are kept. This cannot be undone.'))
                          ) {
                            void applyLicenceSideRemoval('back');
                          }
                        }}
                        className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-black/60 text-white hover:bg-red-600 transition-colors"
                        title={translateUi("Remove back image")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {canEdit && isEditing && (
                      <label
                        className={cn(
                          "absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity",
                          employee.licence_back_image_url
                            ? cn(
                                "bg-black/50",
                                licenceDropTarget === "back"
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100",
                              )
                            : licenceDropTarget === "back"
                              ? "bg-indigo-500/15 opacity-100"
                              : "bg-transparent opacity-100",
                        )}
                        {...licenceDropHandlers("back")}
                      >
                        {employee.licence_back_image_url ? (
                          <span
                            className={cn(
                              "text-white font-medium bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm",
                              licenceDropTarget === "back" ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                            )}
                          >
                            {licenceDropTarget === "back"
                              ? translateUi("Drop to upload back")
                              : translateUi("Upload Back")}
                          </span>
                        ) : (
                          <span
                            className={cn(
                              "text-slate-700 font-medium bg-white/90 px-3 py-2 rounded-lg shadow-sm border border-slate-200 text-center max-w-[92%] text-xs sm:text-sm",
                              licenceDropTarget === "back" ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                            )}
                          >
                            {licenceDropTarget === "back"
                              ? translateUi("Drop to upload back")
                              : translateUi(LICENCE_HOLDER_UPLOAD_HINT)}
                          </span>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          capture={isMobileOrTablet ? 'environment' : undefined}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            void ingestLicenceImageFile(file, "back");
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-slate-300 uppercase tracking-wide shrink-0"> {translateUi("Back")} </p>
                </div>
                </div>

                {(() => {
                  const expirySource = isEditing
                    ? ((editForm as { licence_expiry?: string | null }).licence_expiry ??
                      employee.licence_expiry)
                    : employee.licence_expiry;
                  const classified = classifyEmployeeLicenceExpiry(expirySource);
                  if (
                    classified.urgency !== "yellow" &&
                    classified.urgency !== "red" &&
                    classified.urgency !== "expired"
                  ) {
                    return null;
                  }
                  const expiryLabel = classified.expiryYmd
                    ? formatLicenceExpiryDisplay(classified.expiryYmd)
                    : "—";
                  const days = classified.days ?? 0;
                  const isExpired = classified.urgency === "expired";
                  const isRed = classified.urgency === "red" || isExpired;
                  const title = isExpired
                    ? translateUi("Licence expired — take off track")
                    : isRed
                      ? translateUi("Licence expiry urgent")
                      : translateUi("Licence renewal due soon");
                  const detail = isExpired
                    ? translateUi(
                        "This licence expired on {{date}}. The driver must be taken off track until a valid licence is recorded.",
                        { date: expiryLabel },
                      )
                    : translateUi(
                        "This licence expires on {{date}} ({{days}} day(s) remaining). Please arrange renewal and update the licence details.",
                        { date: expiryLabel, days },
                      );
                  return (
                    <div
                      role="alert"
                      className={cn(
                        "w-full rounded-lg border px-3 py-2 flex items-start gap-2.5 min-w-0 h-auto",
                        isRed
                          ? "border-red-400/40 bg-red-500/20 text-red-50"
                          : "border-amber-400/40 bg-amber-500/20 text-amber-50",
                      )}
                    >
                      <ExclamationTriangle
                        className={cn(
                          "w-4 h-4 shrink-0 mt-0.5",
                          isRed ? "text-red-300" : "text-amber-300",
                        )}
                      />
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-semibold leading-snug text-white">{title}</p>
                        <p className="text-sm leading-snug text-slate-200/95">
                          {detail}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Mobile: Edit button under photo stack */}
                {canEdit && isMobileOrTablet && (
                  <div className="xl:hidden flex justify-end">
                    {isEditing ? (
                      <span className="text-sm text-slate-300"> {translateUi("Editing")} </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditForm(employee ? { ...employee } : {});
                          setLicenceScanRequiresConfirmation(employeeNeedsLicenceDetailsConfirmation(employee));
                          setLicenceScanDetailsConfirmed(false);
                          setLicenceFieldHighlight({});
                          setIsEditing(true);
                        }}
                        className="flex items-center px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-400 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 mr-2" /> {translateUi("Edit")} </button>
                    )}
                  </div>
                )}
                  </div>
                </section>
                {/* Middle: licence detail fields */}
                <section className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "flex h-full min-w-0 w-full max-w-full flex-col")}>
                  <DashboardGradientPaneGlows />
                  <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-3">
                    <h4 className="text-sm font-bold tracking-tight text-white">{translateUi("Licence details")}</h4>
                  <div className="grid grid-cols-1 gap-3 content-stretch flex-1 min-h-0 w-full max-w-full">
                  {isEditing && (() => {
                    const scanDetail =
                      licenceExtractNotice && licenceExtractNotice.startsWith(LICENCE_SCAN_REVIEW_PREAMBLE)
                        ? licenceExtractNotice.slice(LICENCE_SCAN_REVIEW_PREAMBLE.length).trim()
                        : licenceExtractNotice;
                    return (
                    <div
                      className={cn(
                        "rounded-xl border px-3 sm:px-4 py-3 text-sm min-w-0",
                        licenceScanRequiresConfirmation
                          ? licenceScanDetailsConfirmed
                            ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
                            : "bg-amber-500/15 border-amber-400/40 text-amber-50"
                          : licenceExtractNotice
                            ? licenceExtractNotice.toLowerCase().includes('auto-filled')
                              ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
                              : "bg-amber-500/15 border-amber-400/40 text-amber-50"
                            : "bg-white/[0.06] border-white/15 text-slate-200"
                      )}
                    >
                      {licenceExtracting ? (
                        <p>
                          {licenceScanSide === 'back'
                            ? translateUi("Reading back of licence and suggesting fields…")
                            : translateUi("Reading front of licence and suggesting fields…")}
                        </p>
                      ) : (
                        <>
                          {licenceSideCorrectionNotice ? (
                            <p className="mb-3 rounded-lg border border-sky-400/40 bg-sky-500/15 px-3 py-2 text-[13px] font-medium leading-snug text-sky-50">
                              {licenceSideCorrectionNotice}
                            </p>
                          ) : null}
                          {licenceScanRequiresConfirmation ? (
                            <p className="mb-3 font-medium leading-snug">{LICENCE_SCAN_REVIEW_PREAMBLE}</p>
                          ) : null}
                          {scanDetail ? (
                            <p className={`leading-snug ${licenceScanRequiresConfirmation ? 'mb-3 text-[13px]' : ''}`}>
                              {scanDetail}
                            </p> ) : !licenceScanRequiresConfirmation ? ( <p>{translateUi("Upload the front and back of a UK train driving licence to auto-fill the fields below. International licences must be entered manually (see note above).")}</p>
                          ) : null}
                          {licenceScanRequiresConfirmation ? (
                            <label className="flex items-start gap-3 cursor-pointer select-none font-medium">
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                checked={licenceScanDetailsConfirmed}
                                disabled={savingMainChanges || licenceExtracting}
                                onChange={(e) => {
                                  if (e.target.checked && hasLicenceScanFieldErrors) {
                                    alert(translateUi("Manual entry is required for fields highlighted in red before confirming licence details."));
                                    return;
                                  }
                                  if (!e.target.checked) {
                                    setLicenceScanDetailsConfirmed(false);
                                    return;
                                  }
                                  setLicenceScanDetailsConfirmed(true);
                                  // Full save (same as top Save Changes) and exit editing mode.
                                  void saveLicenceDetailsSection({ forceConfirmed: true }).then((ok) => {
                                    if (!ok) setLicenceScanDetailsConfirmed(false);
                                  });
                                }}
                              />
                              <span>{translateUi("I confirm the licence details shown above are correct.")}</span>
                            </label>
                          ) : null}
                        </>
                      )}
                    </div>
                    );
                  })()}
                  {isEditing ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
                      <p className="text-xs text-slate-300">
                        {translateUi("Clears licence number, dates, language and restrictions (photos are kept).")}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setEditForm((prev) => ({
                            ...prev,
                            licence_number: '',
                            licence_issue_date: '',
                            licence_expiry: '',
                            licence_language: '',
                            licence_restrictions: '',
                          }));
                          setLicenceFieldHighlight({});
                          setLicenceScanDetailsConfirmed(false);
                          setLicenceExtractNotice(null);
                          setLicenceSideCorrectionNotice(null);
                          setLicenceScanRequiresConfirmation(
                            employeeNeedsLicenceDetailsConfirmation({
                              ...employee,
                              licence_number: '',
                              licence_issue_date: '',
                              licence_expiry: '',
                              licence_language: '',
                              licence_restrictions: '',
                            }),
                          );
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/20 bg-white/10 text-sm font-medium text-white hover:bg-white/15 transition-colors shrink-0"
                        title={translateUi("Clear licence detail fields")}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {translateUi("Clear fields")}
                      </button>
                    </div>
                  ) : null}
                  <div className={licenceFieldTileClass('licence_number')}>
                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">{translateUi("Licence Number")}</label>
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editForm.licence_number || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditForm({ ...editForm, licence_number: v });
                            setLicenceFieldHighlight((prev) => ({
                              ...prev,
                              licence_number: isVerifiedTrainDrivingLicenceNumber(v)
                                ? 'success'
                                : prev.licence_number !== undefined
                                  ? 'error'
                                  : undefined,
                            }));
                            requireLicenceDetailsConfirmationIfNeeded();
                          }}
                          className={cn(
                            'w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-sky-400 outline-none transition-all font-mono text-sm text-slate-900',
                            licenceFieldHighlight.licence_number === 'error' && 'border-red-300',
                            licenceFieldHighlight.licence_number === 'success' && 'border-emerald-300',
                            !licenceFieldHighlight.licence_number && 'border-slate-200'
                          )}
                          placeholder="e.g. UK7120 ******"
                        />
                        {licenceFieldHighlight.licence_number === 'error' ? (
                          <p className="mt-1.5 text-xs font-medium text-red-200 leading-snug"> {translateUi("This is not a recognised train driving licence number. It must start with UK7120 followed by six digits (e.g. UK7120123456).")} </p>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-sm font-mono text-white">{employee.licence_number || 'Not recorded'}</p>
                    )}
                  </div>

                  <div className={licenceFieldTileClass('licence_issue_date')}>
                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">{translateUi("Issue Date")}</label>
                    {isEditing ? (
                      <input
                        type="date"
                        lang="en-GB"
                        value={editForm.licence_issue_date || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setEditForm({ ...editForm, licence_issue_date: v });
                          setLicenceFieldHighlight((prev) => ({
                            ...prev,
                            licence_issue_date: v.trim() ? 'success' : prev.licence_issue_date,
                          }));
                          requireLicenceDetailsConfirmationIfNeeded();
                        }}
                        className={cn(
                          'w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-sky-400 outline-none transition-all text-sm text-slate-900',
                          licenceFieldHighlight.licence_issue_date === 'error' && 'border-red-300',
                          licenceFieldHighlight.licence_issue_date === 'success' && 'border-emerald-300',
                          !licenceFieldHighlight.licence_issue_date && 'border-slate-200'
                        )}
                      />
                    ) : (
                      <p className="text-sm text-white">{employee.licence_issue_date ? formatUkDdMmYy(employee.licence_issue_date) || '—' : 'Not recorded'}</p>
                    )}
                  </div>

                  <div className={licenceFieldTileClass('licence_expiry')}>
                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">{translateUi("Date of Expiry")}</label>
                    {isEditing ? (
                      <input
                        type="date"
                        lang="en-GB"
                        value={editForm.licence_expiry || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setEditForm({ ...editForm, licence_expiry: v });
                          setLicenceFieldHighlight((prev) => ({
                            ...prev,
                            licence_expiry: v.trim() ? 'success' : prev.licence_expiry,
                          }));
                          requireLicenceDetailsConfirmationIfNeeded();
                        }}
                        className={cn(
                          'w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-sky-400 outline-none transition-all text-sm text-slate-900',
                          licenceFieldHighlight.licence_expiry === 'error' && 'border-red-300',
                          licenceFieldHighlight.licence_expiry === 'success' && 'border-emerald-300',
                          !licenceFieldHighlight.licence_expiry && 'border-slate-200'
                        )}
                      />
                    ) : (
                      <p className="text-sm text-white">{employee.licence_expiry ? formatUkDdMmYy(employee.licence_expiry) || '—' : 'Not recorded'}</p>
                    )}
                  </div>

                  <div className={licenceFieldTileClass('licence_language')}>
                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1"> {translateUi("Language")} </label>
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editForm.licence_language || ''}
                          onChange={e => {
                            const v = e.target.value;
                            setEditForm({ ...editForm, licence_language: v });
                            setLicenceFieldHighlight((prev) => ({
                              ...prev,
                              licence_language: v.trim() ? 'success' : prev.licence_language,
                            }));
                            requireLicenceDetailsConfirmationIfNeeded();
                          }}
                          className={cn(
                            'w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-sky-400 outline-none transition-all text-sm text-slate-900',
                            licenceFieldHighlight.licence_language === 'error' && 'border-red-300',
                            licenceFieldHighlight.licence_language === 'success' && 'border-emerald-300',
                            !licenceFieldHighlight.licence_language && 'border-slate-200'
                          )}
                          placeholder="e.g. English"
                        />
                        {licenceFieldHighlight.licence_language === 'error' ? (
                          <p className="mt-1.5 text-xs font-medium text-red-200 leading-snug">
                            {translateUi("We could not read the language from the back of the licence. Enter it manually or retake the photo.")}
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-sm text-white">{employee.licence_language || 'Not recorded'}</p>
                    )}
                  </div>

                  <div className={licenceFieldTileClass('licence_restrictions')}>
                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1"> {translateUi("Restrictions")} </label>
                    {isEditing ? (
                      <>
                        <textarea
                          value={editForm.licence_restrictions || ''}
                          onChange={e => {
                            const v = e.target.value;
                            setEditForm({ ...editForm, licence_restrictions: v });
                            setLicenceFieldHighlight((prev) => ({
                              ...prev,
                              licence_restrictions: v.trim() ? 'success' : prev.licence_restrictions,
                            }));
                            requireLicenceDetailsConfirmationIfNeeded();
                          }}
                          className={cn(
                            'w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-sky-400 outline-none transition-all min-h-[80px] text-sm resize-y text-slate-900',
                            licenceFieldHighlight.licence_restrictions === 'error' && 'border-red-300',
                            licenceFieldHighlight.licence_restrictions === 'success' && 'border-emerald-300',
                            !licenceFieldHighlight.licence_restrictions && 'border-slate-200'
                          )}
                          placeholder="Enter 9b value (e.g. Yes / No)"
                        />
                        {licenceFieldHighlight.licence_restrictions === 'error' ? (
                          <p className="mt-1.5 text-xs font-medium text-red-200 leading-snug">
                            {translateUi("We could not read restrictions from field 9b on the back of the licence. Enter them manually, set to None if there are none, or retake the photo.")}
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-sm text-white whitespace-pre-line">{employee.licence_restrictions || 'None'}</p>
                    )}
                  </div>
                  </div>
                  </div>
                </section>

                {/* Right: categories */}
                <section className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "flex h-full min-w-0 flex-col")}>
                  <DashboardGradientPaneGlows />
                  <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-3">
                  <h4 className="text-sm font-bold tracking-tight text-white">{translateUi("Categories")}</h4>
                  {(() => {
                    const raw = isEditing ? (editForm.licence_categories ?? employee.licence_categories) : employee.licence_categories;
                    let selected: string[] = [];
                    try {
                      if (typeof raw === 'string' && raw.trim()) selected = JSON.parse(raw);
                      if (!Array.isArray(selected)) selected = [];
                    } catch (_) { selected = []; }
                    const toggle = (cat: string) => {
                      if (!isEditing) return;
                      const next = selected.includes(cat) ? selected.filter(c => c !== cat) : [...selected, cat];
                      setEditForm({ ...editForm, licence_categories: JSON.stringify(next) });
                    };
                    const A_CATS: { id: string; label: string; description: string }[] = [
                      { id: 'A', label: 'A', description: 'Shunting and Maintenance. Covers moving trains within restricted areas or for maintenance. Global A encompasses all sub-activities below, including shunting locomotives and work trains.' },
                      { id: 'A1', label: 'A1', description: 'Limited to shunting locomotives.' },
                      { id: 'A2', label: 'A2', description: 'Limited to work trains.' },
                      { id: 'A3', label: 'A3', description: 'Limited to maintenance railway vehicles.' },
                      { id: 'A4', label: 'A4', description: 'Limited to all other locomotives when used specifically for shunting.' },
                      { id: 'A5', label: 'A5', description: 'Other specific services or rolling stock not covered by the previous sub-categories.' },
                    ];
                    const B_CATS: { id: string; label: string; description: string }[] = [
                      { id: 'B', label: 'B', description: 'Passenger and Goods. Mainline operations for transport of people or commercial freight. Global B encompasses both carriage of passengers and goods.' },
                      { id: 'B1', label: 'B1', description: 'Limited to the carriage of passengers.' },
                      { id: 'B2', label: 'B2', description: 'Limited to the carriage of goods (freight).' },
                    ];
                    const Tile = ({ id, label, description }: { id: string; label: string; description: string }) => {
                      const on = selected.includes(id);
                      return (
                      <div
                        className={cn(
                          DASHBOARD_GLASS_TILE,
                          "flex items-center gap-2.5 px-3 py-2.5",
                          on && "border-sky-400/50 bg-sky-500/15 ring-1 ring-sky-400/30",
                          isEditing && "cursor-pointer hover:bg-white/[0.1]",
                        )}
                        onClick={isEditing ? () => toggle(id) : undefined}
                        role={isEditing ? 'button' : undefined}
                        tabIndex={isEditing ? 0 : undefined}
                        onKeyDown={isEditing ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(id); } } : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(id)}
                          disabled={!isEditing}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 shrink-0 rounded border-2 border-white/30 text-sky-500 focus:ring-sky-400 cursor-pointer disabled:cursor-default"
                          aria-label={translateUi("Category {{id}}", { id: label })}
                        />
                        <span className="font-bold text-white flex-1 min-w-0">{label}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLicenceCategoryInfo({ id, label, description });
                          }}
                          className="shrink-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-white/15 bg-white/10 text-slate-200 hover:bg-white/15 hover:text-white"
                          aria-label={translateUi("About category {{id}}", { id: label })}
                          title={translateUi("About category {{id}}", { id: label })}
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </div>
                      );
                    };
                    return (
                      <div className="flex flex-1 flex-col justify-center gap-3 min-h-0">
                        <div className="space-y-1.5">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-300">A — {translateUi("Shunting & maintenance")}</p>
                          {A_CATS.map((c) => (
                            <Tile key={c.id} id={c.id} label={c.label} description={c.description} />
                          ))}
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">B — {translateUi("Passenger & goods")}</p>
                          {B_CATS.map((c) => (
                            <Tile key={c.id} id={c.id} label={c.label} description={c.description} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  </div>
                </section>
              </div>
            </div>

            {/* Licence image editor modal (tablet: drag crop & rotate) */}
            {licenceImageEdit && (() => {
              const closeLicenceEdit = () => {
                setLicenceImageEdit(null);
                setLicenceCropImageSize(null);
              };
              const sz = licenceCropImageSize;
              const startEdgeDrag = (edge: CropEdge) => (e: React.MouseEvent | React.TouchEvent) => {
                e.preventDefault();
                const container = licenceCropContainerRef.current;
                if (!container || !sz) return;
                const rect = container.getBoundingClientRect();
                const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
                const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
                licenceCropDragRef.current = {
                  startX: x,
                  startY: y,
                  startLeft: licenceCropLeft,
                  startTop: licenceCropTop,
                  startWidth: licenceCropWidth,
                  startHeight: licenceCropHeight,
                  containerRect: rect,
                  edge
                };
                setLicenceCropDragging(true);
              };
              const resetCropToFull = () => {
                setLicenceCropLeft(0);
                setLicenceCropTop(0);
                setLicenceCropWidth(1);
                setLicenceCropHeight(1);
              };
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="licence-edit-title">
                  <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                      <h3 id="licence-edit-title" className="font-semibold text-slate-900">{translateUi("Adjust licence image")}</h3>
                      <button type="button" onClick={closeLicenceEdit} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="px-6 py-5 overflow-auto flex-1 min-h-0 flex flex-col items-center">
                      <div
                        ref={licenceCropContainerRef}
                        className="bg-slate-200 rounded-xl overflow-hidden mb-4 relative mx-auto"
                        style={{
                          width: sz ? `min(100%, calc(45vh * ${sz.w / sz.h}))` : '100%',
                          aspectRatio: sz ? `${sz.w} / ${sz.h}` : '1.4',
                        }}
                      >
                        <img
                          src={licenceImageEdit.dataUrl}
                          alt={translateUi("Preview")}
                          className="block w-full h-full"
                          style={{ transform: `rotate(${licenceImageEdit.rotation}deg)` }}
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            const w = img.naturalWidth;
                            const h = img.naturalHeight;
                            if (!w || !h) return;
                            setLicenceCropImageSize({ w, h });
                            const inset = LICENCE_CROP_INITIAL_INSET;
                            setLicenceCropLeft(inset);
                            setLicenceCropTop(inset);
                            setLicenceCropWidth(1 - 2 * inset);
                            setLicenceCropHeight(1 - 2 * inset);
                          }}
                        />
                        {sz && (
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute left-0 right-0 top-0 bg-black/50" style={{ height: `${licenceCropTop * 100}%` }} />
                            <div className="absolute left-0 right-0 bottom-0 bg-black/50" style={{ top: `${(licenceCropTop + licenceCropHeight) * 100}%`, height: `${(1 - licenceCropTop - licenceCropHeight) * 100}%` }} />
                            <div className="absolute top-0 left-0 bg-black/50" style={{ width: `${licenceCropLeft * 100}%`, top: `${licenceCropTop * 100}%`, height: `${licenceCropHeight * 100}%` }} />
                            <div className="absolute top-0 right-0 bg-black/50" style={{ left: `${(licenceCropLeft + licenceCropWidth) * 100}%`, top: `${licenceCropTop * 100}%`, width: `${(1 - licenceCropLeft - licenceCropWidth) * 100}%`, height: `${licenceCropHeight * 100}%` }} />
                          </div>
                        )}
                        {sz && (() => {
                          const cornerInset = 0.15;
                          const r = licenceCropLeft + licenceCropWidth;
                          const b = licenceCropTop + licenceCropHeight;
                          return (
                          <div className="absolute inset-0 z-20 pointer-events-none">
                            {/* Single-pixel lines on the dark overlay edge; hit areas inset from corners; no arrows */}
                            <div
                              className="absolute flex flex-col cursor-ew-resize pointer-events-auto"
                              style={{ left: `${licenceCropLeft * 100}%`, top: `${(licenceCropTop + cornerInset * licenceCropHeight) * 100}%`, width: 32, height: `${(licenceCropHeight * (1 - 2 * cornerInset)) * 100}%` }}
                              onMouseDown={startEdgeDrag('left')}
                              onTouchStart={startEdgeDrag('left')}
                              aria-label={translateUi("Drag left edge")}
                            >
                              <div className="absolute left-0 top-0 bottom-0 w-px bg-white min-h-[24px]" style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.85)' }} />
                            </div>
                            <div
                              className="absolute flex flex-col cursor-ew-resize pointer-events-auto"
                              style={{ left: `calc(${r * 100}% - 32px)`, top: `${(licenceCropTop + cornerInset * licenceCropHeight) * 100}%`, width: 32, height: `${(licenceCropHeight * (1 - 2 * cornerInset)) * 100}%` }}
                              onMouseDown={startEdgeDrag('right')}
                              onTouchStart={startEdgeDrag('right')}
                              aria-label={translateUi("Drag right edge")}
                            >
                              <div className="absolute right-0 top-0 bottom-0 w-px bg-white min-h-[24px]" style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.85)' }} />
                            </div>
                            <div
                              className="absolute flex cursor-ns-resize pointer-events-auto"
                              style={{ left: `${(licenceCropLeft + cornerInset * licenceCropWidth) * 100}%`, top: `${licenceCropTop * 100}%`, width: `${(licenceCropWidth * (1 - 2 * cornerInset)) * 100}%`, height: 32 }}
                              onMouseDown={startEdgeDrag('top')}
                              onTouchStart={startEdgeDrag('top')}
                              aria-label={translateUi("Drag top edge")}
                            >
                              <div className="absolute top-0 left-0 right-0 h-px bg-white min-w-[24px]" style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.85)' }} />
                            </div>
                            <div
                              className="absolute flex cursor-ns-resize pointer-events-auto"
                              style={{ left: `${(licenceCropLeft + cornerInset * licenceCropWidth) * 100}%`, top: `calc(${b * 100}% - 32px)`, width: `${(licenceCropWidth * (1 - 2 * cornerInset)) * 100}%`, height: 32 }}
                              onMouseDown={startEdgeDrag('bottom')}
                              onTouchStart={startEdgeDrag('bottom')}
                              aria-label={translateUi("Drag bottom edge")}
                            >
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-white min-w-[24px]" style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.85)' }} />
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            if (!licenceImageEdit) return;
                            const rot = (licenceImageEdit.rotation - 90 + 360) % 360;
                            rotateImageDataUrl(licenceImageEdit.dataUrl, rot).then((dataUrl) => {
                              setLicenceImageEdit((prev) => prev ? { ...prev, dataUrl, rotation: 0 } : null);
                              setLicenceCropImageSize(null);
                            }).catch(() => {});
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {translateUi("Rotate left")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!licenceImageEdit) return;
                            const rot = (licenceImageEdit.rotation + 90) % 360;
                            rotateImageDataUrl(licenceImageEdit.dataUrl, rot).then((dataUrl) => {
                              setLicenceImageEdit((prev) => prev ? { ...prev, dataUrl, rotation: 0 } : null);
                              setLicenceCropImageSize(null);
                            }).catch(() => {});
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          <RotateCcw className="w-4 h-4 rotate-180" />
                          {translateUi("Rotate right")}
                        </button>
                        <button
                          type="button"
                          onClick={resetCropToFull}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          <Package className="w-4 h-4" />
                          {translateUi("Reset to full image")}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 text-center">{translateUi("Drag each edge of the white box to set the crop. Rotate if needed, then Apply.")}</p>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
                      <button type="button" onClick={closeLicenceEdit} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"> {translateUi("Cancel")} </button>
                      <button
                        type="button"
                        disabled={!sz}
                        onClick={() => {
                          const { dataUrl, field, rotation } = licenceImageEdit;
                          const showScanning = isMobileOrTablet && (field === 'licence_image_url' || field === 'licence_back_image_url');
                          if (showScanning) {
                            setLicenceScanSide(field === 'licence_back_image_url' ? 'back' : 'front');
                            setLicenceScanVisualState('scanning');
                            setShowLicenceScanningModal(true);
                          }
                          rotateImageDataUrl(dataUrl, rotation)
                            .then((rotated) => cropImageToRegion(rotated, licenceCropLeft, licenceCropTop, licenceCropWidth, licenceCropHeight))
                            .then(async (final) => {
                              if (isMobileOrTablet) {
                                const suitability = await assessLicencePhotoSuitability(final);
                                if (!suitability.ok) {
                                  if (showScanning) {
                                    setShowLicenceScanningModal(false);
                                    setLicenceScanVisualState('scanning');
                                  }
                                  setLicencePhotoSuitability({
                                    side: field === 'licence_back_image_url' ? 'back' : 'front',
                                    dataUrl: licenceImageEdit.dataUrl,
                                    issues: suitability.issues,
                                    croppedDataUrl: final,
                                    field,
                                  });
                                  return;
                                }
                              }
                              const uploadedSide = field === 'licence_back_image_url' ? 'back' : 'front';
                              await commitLicenceImageUpload(final, uploadedSide);
                              closeLicenceEdit();
                            })
                            .catch(() => {})
                            .finally(() => {
                              if (showScanning) {
                                window.setTimeout(() => {
                                  setShowLicenceScanningModal(false);
                                  setLicenceScanVisualState('scanning');
                                }, 850);
                              }
                            });
                        }}
                        className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      > {translateUi("Apply")} </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {licenceCategoryInfo ? (
              <div
                className="fixed inset-0 z-[125] bg-slate-950/60 backdrop-blur-[2px] flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="licence-category-info-title"
                onClick={() => setLicenceCategoryInfo(null)}
              >
                <div
                  className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/15 shadow-2xl p-5 sm:p-6 text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h4 id="licence-category-info-title" className="text-base font-bold text-white">
                      {translateUi("Category {{id}}", { id: licenceCategoryInfo.label })}
                    </h4>
                    <button
                      type="button"
                      onClick={() => setLicenceCategoryInfo(null)}
                      className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
                      aria-label={translateUi("Close")}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-200 leading-relaxed">{translateUi(licenceCategoryInfo.description)}</p>
                  <div className="mt-5 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setLicenceCategoryInfo(null)}
                      className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-500"
                    >
                      {translateUi("Close")}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {showLicenceScanningModal && (
              <div className="fixed inset-0 z-[120] bg-slate-950/60 backdrop-blur-[2px] flex items-center justify-center p-4">
                <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-200 p-6 text-center">
                  <LicenceCardScanAnimation
                    state={licenceScanVisualState}
                    title={
                      licenceScanVisualState === 'success'
                        ? 'Licence scanned'
                        : licenceScanVisualState === 'partial'
                          ? 'Partial scan'
                          : licenceScanSide === 'back'
                            ? translateUi("Scanning licence back")
                            : translateUi("Scanning licence")
                    }
                    description={
                      licenceScanVisualState === 'success'
                        ? 'Detected key licence details.'
                        : licenceScanVisualState === 'partial'
                          ? 'Some details need manual check.'
                          : licenceScanSide === 'back'
                            ? translateUi("Reading language (9a.1) and medical restriction (9b)...")
                            : translateUi("Reading licence number and dates...")
                    }
                  />
                </div>
              </div>
            )}
            {licencePhotoSuitability && (
              <div className="fixed inset-0 z-[122] bg-slate-950/65 backdrop-blur-[2px] flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 p-6">
                  <h4 className="text-base font-semibold text-slate-900">{translateUi("Photo not suitable yet")}</h4>
                  <p className="text-sm text-slate-600 mt-2">
                    {translateUi("Check the photo before submitting — it needs to be sharp, well lit, and framed so licence text is readable.")}
                  </p>
                  <ul className="mt-3 text-sm text-amber-800 list-disc pl-5 space-y-1.5">
                    {licencePhotoSuitability.issues.map((issue) => (
                      <li key={issue}>{translateUi(issue)}</li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const pending = licencePhotoSuitability;
                        setLicencePhotoSuitability(null);
                        if (pending.croppedDataUrl && pending.field) {
                          void (async () => {
                            const field = pending.field!;
                            const final = pending.croppedDataUrl!;
                            const showScanning = isMobileOrTablet;
                            if (showScanning) {
                              setLicenceScanSide(field === "licence_back_image_url" ? "back" : "front");
                              setLicenceScanVisualState("scanning");
                              setShowLicenceScanningModal(true);
                            }
                            try {
                              const uploadedSide = field === "licence_back_image_url" ? "back" : "front";
                              await commitLicenceImageUpload(final, uploadedSide);
                              setLicenceImageEdit(null);
                              setLicenceCropImageSize(null);
                            } finally {
                              if (showScanning) {
                                window.setTimeout(() => {
                                  setShowLicenceScanningModal(false);
                                  setLicenceScanVisualState("scanning");
                                }, 850);
                              }
                            }
                          })();
                          return;
                        }
                        setLicenceImageEdit({
                          dataUrl: pending.dataUrl,
                          field: pending.side === "back" ? "licence_back_image_url" : "licence_image_url",
                          rotation: 0,
                        });
                      }}
                      className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      {licencePhotoSuitability.croppedDataUrl
                        ? translateUi("Use anyway")
                        : translateUi("Crop anyway")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLicencePhotoSuitability(null);
                        setLicenceImageEdit(null);
                        setLicenceCropImageSize(null);
                      }}
                      className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      {translateUi("Retake photo")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showLicenceComplianceModal && (
              <div className="fixed inset-0 z-[121] bg-slate-950/65 backdrop-blur-[2px] flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 p-6">
                  <h4 className="text-base font-semibold text-slate-900">{translateUi("Licence scan compliance check")}</h4>
                  <p className="text-sm text-slate-600 mt-2">
                    {translateUi("We could not confidently capture all required fields from the photo.")}
                  </p>
                  {lastLicenceExtractionMissing.length > 0 && (
                    <p className="text-sm text-amber-700 mt-2">
                      Missing: {lastLicenceExtractionMissing.join(', ')}.
                    </p>
                  )}
                  <ul className="mt-3 text-xs text-slate-500 list-disc pl-5 space-y-1">
                    {licenceComplianceSide === 'back' ? (
                      <>
                        <li>{translateUi("Ensure fields")} <code className="text-slate-600">9a.1</code> {translateUi("(language) and")} <code className="text-slate-600">9b</code> {translateUi("(medical restriction) are fully visible. We only read the 9b Yes/No field value, not the explanatory text below it.")} </li>
                        <li> {translateUi("Field")} <code className="text-slate-600">9a.2</code> {translateUi("is not stored; it can be ignored for scanning.")}</li>
                      </>
                    ) : (
                      <>
                        <li>{translateUi("Ensure fields")} <code className="text-slate-600">5</code>, <code className="text-slate-600">4a</code>{translateUi(", and")} <code className="text-slate-600">4b</code> {translateUi("are fully visible.")}</li>
                        <li>{translateUi("Train driving licence numbers must start with")} <code className="text-slate-600">{translateUi("UK7120")}</code> {translateUi("followed by six digits (e.g.")} <code className="text-slate-600"> {translateUi("UK7120123456")} </code>).</li>
                      </>
                    )}
                    <li>{translateUi("Avoid glare and shadows over text.")}</li>
                    <li>{translateUi("Keep image sharp before applying crop.")}</li>
                  </ul>
                  <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowLicenceComplianceModal(false);
                        setLicenceScanRequiresConfirmation(true);
                        setLicenceScanDetailsConfirmed(false);
                        setLicenceExtractNotice(
                          `${LICENCE_SCAN_REVIEW_PREAMBLE} Enter the licence details manually, then confirm they are correct before saving.`
                        );
                      }}
                      className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      {translateUi("Enter manually")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLicenceComplianceModal(false);
                        setLicenceScanRequiresConfirmation(false);
                        setLicenceScanDetailsConfirmed(false);
                        const notice =
                          licenceComplianceSide === 'back'
                            ? 'Previous back photo was removed. Retake the back photo for compliance capture, then crop and apply again.'
                            : 'Previous front photo was removed. Retake the front photo for compliance capture, then crop and apply again.';
                        void (async () => {
                          const patch =
                            licenceComplianceSide === 'back'
                              ? {
                                  licence_back_image_url: '',
                                  licence_language: '',
                                  licence_restrictions: '',
                                  licence_details_confirmed_at: '',
                                  licence_details_confirmed_by: '',
                                }
                              : {
                                  licence_image_url: '',
                                  licence_number: '',
                                  licence_issue_date: '',
                                  licence_expiry: '',
                                  licence_details_confirmed_at: '',
                                  licence_details_confirmed_by: '',
                                };
                          const ok = await handleUpdate(patch);
                          if (ok) {
                            setLicenceExtractNotice(notice);
                            setLicenceFieldHighlight((prev) => {
                              const next = { ...prev };
                              if (licenceComplianceSide === 'back') {
                                delete next.licence_language;
                                delete next.licence_restrictions;
                              } else {
                                delete next.licence_number;
                                delete next.licence_issue_date;
                                delete next.licence_expiry;
                              }
                              return next;
                            });
                          }
                        })();
                      }}
                      className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      {translateUi("Retake photo")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            </>
          ) : activeTab === 'Cab Passes' ? (
            employee ? (
              <CabPassesTab
                employee={{
                  ...employee,
                  company_address: companyAddressForProfile || companyAddress,
                  company_logo_url: employee.company_logo_url ?? companyLogoUrl,
                }}
                companyCode={effectiveCompanyCode}
                cabPassesJson={editForm.cab_passes_json ?? employee.cab_passes_json}
                canInteractWithCabPassTab={canAccessCabPassesTab}
                cabPassesManagePermission={cabPassesManagePermission}
                cabPassPermissionEdit={cabPassPermissionEdit}
                cabPassPermissionDelete={cabPassPermissionDelete}
                cabPassPermissionRevoke={cabPassPermissionRevoke}
                cabPassPermissionRenew={cabPassPermissionRenew}
                cabPassCompanyAdminActions={cabPassCompanyAdminActions}
                currentUserFullName={currentUserProfile?.full_name}
                currentUserEmail={currentUserEmail}
                isDarkMode={isDarkMode}
                isMobileOrTablet={isMobileOrTablet}
                onCabPassesJsonChange={onCabPassesJsonChangeStable}
                onCommitCabPassesNow={cabPassMayPersistToServer ? commitCabPassesNowWithPending : undefined}
              />
            ) : (
              <div className={cn("p-8", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Loading…")}</div>
            )
          ) : activeTab === 'Certificate' ? (
            employee ? <CertificateTab employee={employee} companyName={employee.company_name ?? companyName} companyLogoUrl={employee.company_logo_url ?? companyLogoUrl} /> : <div className="p-8 text-slate-500">{translateUi("Loading…")}</div>
          ) : activeTab === 'Main' ? null : activeTab === 'Trains & Routes' ? (
            <div className="space-y-6">
              {Array.isArray(employee.company_admin_competency_removals) && employee.company_admin_competency_removals.length > 0 && (
                <div className={cn(
                  "rounded-2xl border p-4",
                  isDarkMode ? "border-amber-500/40 bg-amber-500/10" : "border-amber-200 bg-amber-50"
                )}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={cn("w-5 h-5 mt-0.5 shrink-0", isDarkMode ? "text-amber-300" : "text-amber-700")} />
                    <div className="min-w-0">
                      <p className={cn("text-sm font-semibold", isDarkMode ? "text-amber-100" : "text-amber-900")}>
                        {translateUi("Company-admin competency removals")}
                      </p>
                      <p className={cn("text-xs mt-0.5", isDarkMode ? "text-amber-200/90" : "text-amber-800")}>
                        {translateUi("Entries below show traction/route competencies removed centrally in Company Configuration.")}
                      </p>
                    </div>
                  </div>
                  <div className={cn("mt-3 space-y-1.5 text-xs sm:text-sm", isDarkMode ? "text-amber-100" : "text-amber-900")}>
                    {employee.company_admin_competency_removals
                      .slice()
                      .sort((a, b) => String(b?.removed_at ?? "").localeCompare(String(a?.removed_at ?? "")))
                      .slice(0, 8)
                      .map((entry, idx) => {
                        const kind = String(entry?.kind ?? "").trim().toLowerCase();
                        const label = kind === "route" ? "route" : "traction";
                        const value = String(entry?.value ?? "").trim() || "Unknown";
                        const when = entry?.removed_at ? formatDateTimeUK(entry.removed_at) : "Unknown date/time";
                        const removedBy = String(entry?.removed_by ?? "").trim();
                        return (
                          <p key={`admin-removal-${idx}`}>
                            <span className="font-semibold">{when}</span> - Company admin removed {label} <span className="font-semibold">{value}</span>{removedBy ? ` (${removedBy})` : ""}.
                          </p>
                        );
                      })}
                  </div>
                </div>
              )}
              {/* Traction Section */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between relative overflow-hidden min-h-14">
                  <div className="traction-header-train absolute z-0 max-w-[90%] pointer-events-none flex items-center" aria-hidden>
                    {/* Native sprite is engine + coaches (nose left). Append a cropped, mirrored
                        engine so the consist reads lead engine — coaches — rear engine. */}
                    <img
                      src="/TractionHeaderImage.png"
                      alt=""
                      className="block h-14 w-auto max-w-none select-none object-contain object-left"
                    />
                    <span className="block" style={{ transform: 'scaleX(-1)' }}>
                      {/* Crop to leading power car only (sprite cols 0–301 of 600). */}
                      <span
                        className="block h-14 overflow-hidden"
                        style={{ width: 'calc(3.5rem * 302 / 75)' }}
                      >
                        <img
                          src="/TractionHeaderImage.png"
                          alt=""
                          className="block h-14 w-auto max-w-none select-none object-contain object-left"
                        />
                      </span>
                    </span>
                  </div>
                  <h3 className="relative z-10 font-bold text-slate-900 flex items-center">
                    <TrainFront className="w-5 h-5 mr-2 text-indigo-600" /> {translateUi("Traction")} </h3>
                  {canEdit && (
                    <button 
                      onClick={() => {
                        setIsAddingTraction(true);
                        setNewTractionForm({
                          type: '',
                          date_assessed: new Date().toISOString().split('T')[0],
                          assessed_by: '',
                          notes: ''
                        });
                        setIsAddTractionModalOpen(true);
                      }}
                      className="add-traction-btn relative z-10 flex items-center px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-1" /> {translateUi("Add Traction")}
                    </button>
                  )}
                </div>
                <div className="max-h-[min(38rem,70vh)] overflow-auto">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Traction")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Date Found Competent")}</th>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Expiry (If Applicable)")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Assessed By")}</th>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Notes")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Written Assessment")}</th>
                        {canEdit && <th className="px-6 py-3 font-semibold text-right"> {translateUi("Action")} </th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {false && (
                        <tr className="bg-indigo-50/50">
                          <td className="px-6 py-4">
                            <input 
                              type="text" 
                              value={newTractionForm.type || ''} 
                              onChange={e => setNewTractionForm({ ...newTractionForm, type: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                              placeholder="e.g. Class 800"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input 
                              type="date" 
                              value={newTractionForm.date_assessed || ''} 
                              onChange={e => setNewTractionForm({ ...newTractionForm, date_assessed: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input 
                              type="date" 
                              value={newTractionForm.expiry_date || ''} 
                              onChange={e => setNewTractionForm({ ...newTractionForm, expiry_date: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <select 
                              value={newTractionForm.assessed_by || ''} 
                              onChange={e => setNewTractionForm({ ...newTractionForm, assessed_by: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            >
                              <option value="">{translateUi("Select Assessor...")}</option>
                              {assessorEligibleManagers.map(m => (
                                <option key={m.id} value={m.full_name}>{m.full_name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 text-slate-400 text-sm italic">{translateUi("Save first to upload")}</td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button onClick={handleSaveNewTraction} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setIsAddingTraction(false); setNewTractionForm({}); }} className="p-1 text-slate-400 hover:text-rose-600 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )}
                      {(employee.competencies ?? []).filter(c => isTractionCompetency(c)).map(comp => (
                        <tr key={comp.id} className="text-sm hover:bg-slate-50">
                          {editingCompetencyId === comp.id ? (
                            <>
                              <td className="px-6 py-4">
                                <input 
                                  type="text" 
                                  value={competencyForm.type || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, type: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input 
                                  type="date" 
                                  value={competencyForm.date_assessed || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, date_assessed: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input 
                                  type="date" 
                                  value={competencyForm.expiry_date || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, expiry_date: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <select 
                                  value={competencyForm.assessed_by || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, assessed_by: e.target.value })}
                                  className={cn(
                                    "w-full px-2 py-1 text-sm border border-slate-200 rounded",
                                    isSamePersonName(competencyForm.assessed_by, employee?.full_name) && "border-amber-500"
                                  )}
                                >
                                  <option value="">{translateUi("Select Assessor...")}</option>
                                  {assessorEligibleManagers.map(m => (
                                    <option key={m.id} value={m.full_name}>{m.full_name}</option>
                                  ))}
                                </select>
                                {isSamePersonName(competencyForm.assessed_by, employee?.full_name) && (
                                  <p className="mt-1 text-[11px] text-amber-700">{translateUi("An employee cannot assess themselves.")}</p>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <textarea
                                  value={competencyForm.notes || ''}
                                  onChange={e => setCompetencyForm({ ...competencyForm, notes: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded min-h-[64px]"
                                  placeholder="Add notes"
                                />
                              </td>
                              <td className="px-6 py-4 text-slate-400 italic">{translateUi("Save first to upload")}</td>
                              <td className="px-6 py-4 text-right space-x-2 flex items-center justify-end">
                                <button onClick={handleSaveCompetency} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                                  <CheckCircle2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setEditingCompetencyId(null); }} className="p-1 text-slate-400 hover:text-rose-600 transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-medium text-slate-900">{comp.type}</td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.date_assessed ? formatDateUK(comp.date_assessed) : ''}
                              </td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.expiry_date ? formatDateUK(comp.expiry_date) : <span className="text-slate-400 italic">{translateUi("Expiry not set")}</span>}
                              </td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.assessed_by || <span className="text-slate-400 italic">{translateUi("Not recorded")}</span>}
                              </td>
                              <td className="px-6 py-4 text-slate-600 whitespace-pre-wrap">
                                {comp.notes?.trim() ? comp.notes : <span className="text-slate-400 italic">{translateUi("No notes")}</span>}
                              </td>
                              <td className="px-6 py-4">
                                {comp.certificate_url ? (
                                  <a href={comp.certificate_url} download={`competency_${comp.id}_attachment`} className="text-indigo-600 hover:underline flex items-center">
                                    <Paperclip className="w-4 h-4 mr-1" /> {translateUi("View Attachment")}
                                  </a>
                                ) : (
                                  <label className="flex items-center text-indigo-600 hover:text-indigo-800 cursor-pointer">
                                    <Upload className="w-4 h-4 mr-1" /> {translateUi("Upload")} <input 
                                      type="file" 
                                      accept=".pdf,.doc,.docx,image/*" 
                                      className="hidden" 
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) handleCompetencyUpload(comp.id, e.target.files[0]);
                                      }} 
                                    />
                                  </label>
                                )}
                              </td>
                              {canEdit && (
                                <td className="px-6 py-4 text-right space-x-2">
                                  <div className="flex items-center justify-end space-x-2">
                                    <button 
                                      onClick={() => {
                                        setEditingCompetencyId(comp.id);
                                        setCompetencyForm(comp);
                                      }}
                                      className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setConfirmState({
                                          isOpen: true,
                                          title: "Remove Competency",
                                          message: "Are you sure you want to remove this competency?\n\nPlease ensure you reprint the complimentary certificate and reissue to the driver upon deletion.",
                                          onConfirm: () => handleDeleteCompetency(comp.id)
                                        });
                                      }}
                                      className="p-1 text-slate-400 hover:text-rose-600 transition-colors z-50 relative cursor-pointer"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      ))}
                      {((employee.competencies ?? []).filter(c => isTractionCompetency(c)).length === 0) && !isAddingTraction && (
                        <tr><td colSpan={canEdit ? 7 : 6} className="px-6 py-8 text-center text-slate-400 italic">{translateUi("No traction competencies recorded.")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Routes Section */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between relative overflow-hidden min-h-14">
                  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden>
                    <img
                      src="/RoutesHeaderImage.png"
                      alt=""
                      className="h-full w-full object-cover object-center opacity-40 select-none"
                    />
                  </div>
                  <h3 className="relative z-10 font-bold text-slate-900 flex items-center">
                    <MapIcon className="w-5 h-5 mr-2 text-emerald-600" /> {translateUi("Routes")} </h3>
                  {canEdit && (
                    <button 
                      onClick={() => {
                        setIsAddingRoute(true);
                        setNewRouteForm({
                          type: '',
                          date_assessed: new Date().toISOString().split('T')[0],
                          assessed_by: '',
                          notes: ''
                        });
                        setIsAddRouteModalOpen(true);
                      }}
                      className="add-route-btn relative z-10 flex items-center px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-1" /> {translateUi("Add Route")}
                    </button>
                  )}
                </div>
                <div className="max-h-[min(38rem,70vh)] overflow-auto">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Route")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Date Found Competent")}</th>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Expiry (If Applicable)")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Assessed By")}</th>
                        <th className="px-6 py-3 font-semibold"> {translateUi("Notes")} </th>
                        <th className="px-6 py-3 font-semibold">{translateUi("Written Assessment")}</th>
                        {canEdit && <th className="px-6 py-3 font-semibold text-right"> {translateUi("Action")} </th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {false && (
                        <tr className="bg-emerald-50/50">
                          <td className="px-6 py-4">
                            <input 
                              type="text" 
                              value={newRouteForm.type || ''} 
                              onChange={e => setNewRouteForm({ ...newRouteForm, type: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                              placeholder="e.g. London to Newcastle"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input 
                              type="date" 
                              value={newRouteForm.date_assessed || ''} 
                              onChange={e => setNewRouteForm({ ...newRouteForm, date_assessed: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input 
                              type="date" 
                              value={newRouteForm.expiry_date || ''} 
                              onChange={e => setNewRouteForm({ ...newRouteForm, expiry_date: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <select 
                              value={newRouteForm.assessed_by || ''} 
                              onChange={e => setNewRouteForm({ ...newRouteForm, assessed_by: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                            >
                              <option value="">{translateUi("Select Assessor...")}</option>
                              {assessorEligibleManagers.map(m => (
                                <option key={m.id} value={m.full_name}>{m.full_name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 text-slate-400 text-sm italic">{translateUi("Save first to upload")}</td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button onClick={handleSaveNewRoute} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setIsAddingRoute(false); setNewRouteForm({}); }} className="p-1 text-slate-400 hover:text-rose-600 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )}
                      {(employee.competencies ?? []).filter(c => isRouteCompetency(c)).map(comp => (
                        <tr key={comp.id} className="text-sm hover:bg-slate-50">
                          {editingCompetencyId === comp.id ? (
                            <>
                              <td className="px-6 py-4">
                                <input 
                                  type="text" 
                                  value={competencyForm.type || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, type: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input 
                                  type="date" 
                                  value={competencyForm.date_assessed || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, date_assessed: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input 
                                  type="date" 
                                  value={competencyForm.expiry_date || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, expiry_date: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <select 
                                  value={competencyForm.assessed_by || ''} 
                                  onChange={e => setCompetencyForm({ ...competencyForm, assessed_by: e.target.value })}
                                  className={cn(
                                    "w-full px-2 py-1 text-sm border border-slate-200 rounded",
                                    isSamePersonName(competencyForm.assessed_by, employee?.full_name) && "border-amber-500"
                                  )}
                                >
                                  <option value="">{translateUi("Select Assessor...")}</option>
                                  {assessorEligibleManagers.map(m => (
                                    <option key={m.id} value={m.full_name}>{m.full_name}</option>
                                  ))}
                                </select>
                                {isSamePersonName(competencyForm.assessed_by, employee?.full_name) && (
                                  <p className="mt-1 text-[11px] text-amber-700">{translateUi("An employee cannot assess themselves.")}</p>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <textarea
                                  value={competencyForm.notes || ''}
                                  onChange={e => setCompetencyForm({ ...competencyForm, notes: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-slate-200 rounded min-h-[64px]"
                                  placeholder="Add notes"
                                />
                              </td>
                              <td className="px-6 py-4 text-slate-400 italic">{translateUi("Save first to upload")}</td>
                              <td className="px-6 py-4 text-right space-x-2 flex items-center justify-end">
                                <button onClick={handleSaveCompetency} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                                  <CheckCircle2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setEditingCompetencyId(null); }} className="p-1 text-slate-400 hover:text-rose-600 transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 font-medium text-slate-900">{comp.type}</td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.date_assessed ? formatDateUK(comp.date_assessed) : ''}
                              </td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.expiry_date ? formatDateUK(comp.expiry_date) : <span className="text-slate-400 italic">{translateUi("Expiry not set")}</span>}
                              </td>
                              <td className="px-6 py-4 text-slate-600">
                                {comp.assessed_by || <span className="text-slate-400 italic">{translateUi("Not recorded")}</span>}
                              </td>
                              <td className="px-6 py-4 text-slate-600 whitespace-pre-wrap">
                                {comp.notes?.trim() ? comp.notes : <span className="text-slate-400 italic">{translateUi("No notes")}</span>}
                              </td>
                              <td className="px-6 py-4">
                                {comp.certificate_url ? (
                                  <a href={comp.certificate_url} download={`competency_${comp.id}_attachment`} className="text-indigo-600 hover:underline flex items-center">
                                    <Paperclip className="w-4 h-4 mr-1" /> {translateUi("View Attachment")}
                                  </a>
                                ) : (
                                  <label className="flex items-center text-indigo-600 hover:text-indigo-800 cursor-pointer">
                                    <Upload className="w-4 h-4 mr-1" /> {translateUi("Upload")} <input 
                                      type="file" 
                                      accept=".pdf,.doc,.docx,image/*" 
                                      className="hidden" 
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) handleCompetencyUpload(comp.id, e.target.files[0]);
                                      }} 
                                    />
                                  </label>
                                )}
                              </td>
                              {canEdit && (
                                <td className="px-6 py-4 text-right space-x-2">
                                  <div className="flex items-center justify-end space-x-2">
                                    <button 
                                      onClick={() => {
                                        setEditingCompetencyId(comp.id);
                                        setCompetencyForm(comp);
                                      }}
                                      className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setConfirmState({
                                          isOpen: true,
                                          title: "Remove Competency",
                                          message: "Are you sure you want to remove this competency?\n\nPlease ensure you reprint the complimentary certificate and reissue to the driver upon deletion.",
                                          onConfirm: () => handleDeleteCompetency(comp.id)
                                        });
                                      }}
                                      className="p-1 text-slate-400 hover:text-rose-600 transition-colors z-50 relative cursor-pointer"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      ))}
                      {((employee.competencies ?? []).filter(c => isRouteCompetency(c)).length === 0) && !isAddingRoute && (
                        <tr><td colSpan={canEdit ? 7 : 6} className="px-6 py-8 text-center text-slate-400 italic">{translateUi("No route competencies recorded.")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {canEdit && isAddTractionModalOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-3 sm:p-4"
                  onClick={() => { setIsAddTractionModalOpen(false); setIsAddingTraction(false); }}
                >
                  <div
                    className={cn(
                      "relative w-full max-w-2xl rounded-2xl shadow-xl px-5 py-5 sm:p-6 max-h-[88vh] overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom))]",
                      isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GbrModalBackdrop />
                    <div className="relative z-10 flex items-center justify-between mb-4">
                      <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Add Traction")}</h3>
                      <button
                        type="button"
                        onClick={() => { setIsAddTractionModalOpen(false); setIsAddingTraction(false); }}
                        className={cn(
                          "p-1 transition-colors rounded",
                          isDarkMode ? "text-slate-400 hover:text-rose-400 hover:bg-slate-800" : "text-slate-400 hover:text-rose-600 hover:bg-slate-100"
                        )}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}> {translateUi("Traction")} </label>
                        <select
                          value={newTractionForm.type || ''}
                          onChange={e => setNewTractionForm({ ...newTractionForm, type: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        >
                          <option value="">
                            {companyTractionTypes.length === 0 ? translateUi("No traction types configured") : translateUi("Select traction")}
                          </option>
                          {companyTractionTypes.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {companyTractionTypes.length === 0 && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("Configure values in Company Configuration > Traction, Routes & Depots first.")}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Date Found Competent")}</label>
                        <input
                          type="date"
                          value={newTractionForm.date_assessed || ''}
                          onChange={e => setNewTractionForm({ ...newTractionForm, date_assessed: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        />
                      </div>
                      <div>
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Expiry (If Applicable)")}</label>
                        <input
                          type="date"
                          value={newTractionForm.expiry_date || ''}
                          onChange={e => setNewTractionForm({ ...newTractionForm, expiry_date: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Assessed By")}</label>
                        <select
                          value={newTractionForm.assessed_by || ''}
                          onChange={e => setNewTractionForm({ ...newTractionForm, assessed_by: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900",
                            isSamePersonName(newTractionForm.assessed_by, employee?.full_name) && "border-amber-500"
                          )}
                        >
                          <option value="">{translateUi("Select Assessor...")}</option>
                          {assessorEligibleManagers.map(m => (
                            <option key={m.id} value={m.full_name}>{m.full_name}</option>
                          ))}
                        </select>
                        {assessorEligibleManagers.length === 0 && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("No team members with the Assessor additional role are available.")}
                          </p>
                        )}
                        {isSamePersonName(newTractionForm.assessed_by, employee?.full_name) && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("An employee cannot assess themselves.")}
                          </p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}> {translateUi("Notes")} </label>
                        <textarea
                          value={newTractionForm.notes || ''}
                          onChange={e => setNewTractionForm({ ...newTractionForm, notes: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg min-h-[96px]",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                          placeholder="Add notes for this traction entry"
                        />
                      </div>
                    </div>
                    <div className="relative z-10 mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setIsAddTractionModalOpen(false); setIsAddingTraction(false); setNewTractionForm({}); }}
                        className={cn(
                          "px-4 py-2 text-sm rounded-lg border",
                          isDarkMode ? "border-slate-600 text-slate-300 hover:bg-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        )}
                      > {translateUi("Cancel")} </button>
                      <button
                        type="button"
                        onClick={handleSaveNewTraction}
                        disabled={companyTractionTypes.length === 0 || !String(newTractionForm.type ?? '').trim()}
                        className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {translateUi("Save traction")}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {canEdit && isAddRouteModalOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-3 sm:p-4"
                  onClick={() => { setIsAddRouteModalOpen(false); setIsAddingRoute(false); }}
                >
                  <div
                    className={cn(
                      "relative w-full max-w-2xl rounded-2xl shadow-xl px-5 py-5 sm:p-6 max-h-[88vh] overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom))]",
                      isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GbrModalBackdrop />
                    <div className="relative z-10 flex items-center justify-between mb-4">
                      <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Add Route")}</h3>
                      <button
                        type="button"
                        onClick={() => { setIsAddRouteModalOpen(false); setIsAddingRoute(false); }}
                        className={cn(
                          "p-1 transition-colors rounded",
                          isDarkMode ? "text-slate-400 hover:text-rose-400 hover:bg-slate-800" : "text-slate-400 hover:text-rose-600 hover:bg-slate-100"
                        )}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}> {translateUi("Route")} </label>
                        <select
                          value={newRouteForm.type || ''}
                          onChange={e => setNewRouteForm({ ...newRouteForm, type: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        >
                          <option value="">
                            {companyRouteTypes.length === 0 ? translateUi("No routes configured") : translateUi("Select route")}
                          </option>
                          {companyRouteTypes.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {companyRouteTypes.length === 0 && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("Configure values in Company Configuration > Traction, Routes & Depots first.")}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Date Found Competent")}</label>
                        <input
                          type="date"
                          value={newRouteForm.date_assessed || ''}
                          onChange={e => setNewRouteForm({ ...newRouteForm, date_assessed: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        />
                      </div>
                      <div>
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Expiry (If Applicable)")}</label>
                        <input
                          type="date"
                          value={newRouteForm.expiry_date || ''}
                          onChange={e => setNewRouteForm({ ...newRouteForm, expiry_date: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Assessed By")}</label>
                        <select
                          value={newRouteForm.assessed_by || ''}
                          onChange={e => setNewRouteForm({ ...newRouteForm, assessed_by: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900",
                            isSamePersonName(newRouteForm.assessed_by, employee?.full_name) && "border-amber-500"
                          )}
                        >
                          <option value="">{translateUi("Select Assessor...")}</option>
                          {assessorEligibleManagers.map(m => (
                            <option key={m.id} value={m.full_name}>{m.full_name}</option>
                          ))}
                        </select>
                        {assessorEligibleManagers.length === 0 && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("No team members with the Assessor additional role are available.")}
                          </p>
                        )}
                        {isSamePersonName(newRouteForm.assessed_by, employee?.full_name) && (
                          <p className={cn("mt-1 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                            {translateUi("An employee cannot assess themselves.")}
                          </p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className={cn("block text-xs font-semibold mb-1", isDarkMode ? "text-slate-300" : "text-slate-600")}> {translateUi("Notes")} </label>
                        <textarea
                          value={newRouteForm.notes || ''}
                          onChange={e => setNewRouteForm({ ...newRouteForm, notes: e.target.value })}
                          className={cn(
                            "w-full px-3 py-2 text-sm border rounded-lg min-h-[96px]",
                            isDarkMode ? "border-slate-600 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
                          )}
                          placeholder="Add notes for this route entry"
                        />
                      </div>
                    </div>
                    <div className="relative z-10 mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setIsAddRouteModalOpen(false); setIsAddingRoute(false); setNewRouteForm({}); }}
                        className={cn(
                          "px-4 py-2 text-sm rounded-lg border",
                          isDarkMode ? "border-slate-600 text-slate-300 hover:bg-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        )}
                      > {translateUi("Cancel")} </button>
                      <button
                        type="button"
                        onClick={handleSaveNewRoute}
                        disabled={companyRouteTypes.length === 0 || !String(newRouteForm.type ?? '').trim()}
                        className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {translateUi("Save route")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'Experience Records' ? (
            <ExperienceRecordsTab
              experienceJson={employee.experience_records_json}
              tractionOptions={[...new Set((employee.competencies ?? []).filter(c => isTractionCompetency(c)).map((c) => String(c.type || '').trim()).filter(Boolean))]}
              companyCode={effectiveCompanyCode || undefined}
              currentUserEmail={currentUserEmail}
              employeeId={employeeId}
              canAddExperienceRecords={canAddExperienceRecords}
              canEdit={canEdit}
              isDarkMode={isDarkMode}
              employeeRole={employee.role}
              onSave={async (nextJson) => handleUpdate({ experience_records_json: nextJson })}
              onRefreshEmployee={refetchEmployee}
            />
          ) : activeTab === 'Training' ? (
            <>
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl border border-slate-200 shadow-sm min-w-0 max-w-full overflow-x-hidden">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center min-w-0">
                  <GraduationCap className="w-5 h-5 mr-2 shrink-0 text-indigo-600" /> {translateUi("Training Records")}
                </h3>
                {canEdit && (
                  <button 
                    onClick={() => {
                      setTrainingForm({ training_name: '', type: "Training", date_attended: '', expiry_date: '', provider: '', status: "Completed" });
                      setIsAddingTraining(true);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center shadow-sm shadow-indigo-200 shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-2" /> {translateUi("Add New Training")}
                  </button>
                )}
              </div>
              <input ref={trainingCertInputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                const recordId = trainingCertRecordIdRef.current;
                if (!file || recordId == null) return;
                setTrainingCertUploadingId(recordId);
                try {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  await new Promise<void>((resolve, reject) => { reader.onload = () => resolve(); reader.onerror = reject; });
                  const content = (reader.result as string).split(',')[1];
                  const res = await fetch(`/api/training_records/${recordId}/certificate${rc}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: file.name, content }) });
                  if (!res.ok) throw new Error((await res.text()) || "Upload failed");
                  refetchEmployee();
                } catch (err: any) {
                  alert(err.message || translateUi("Upload failed"));
                } finally {
                  setTrainingCertUploadingId(null);
                  e.target.value = '';
                }
              }} />
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Training")} </th>
                      <th className="px-6 py-3 font-semibold">{translateUi("Type")}</th>
                      <th className="px-6 py-3 font-semibold">{translateUi("Date Attended")}</th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Expiry (if applicable)")} </th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Provider")} </th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Status")} </th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Certificate")} </th>
                      {(canEdit || canDelete) ? <th className="px-6 py-3 font-semibold w-28"> {translateUi("Actions")} </th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(employee.training_records?.length ?? 0) === 0 && (
                      <tr><td colSpan={(canEdit || canDelete) ? 8 : 7} className="px-6 py-8 text-center text-slate-400 italic">{translateUi("No training records yet")}</td></tr>
                    )}
                    {employee.training_records?.map((tr: any) => (
                      <tr key={tr.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">{tr.training_name || '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{tr.type || '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{tr.date_attended ? formatDateUK(tr.date_attended) : '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{tr.expiry_date ? formatDateUK(tr.expiry_date) : '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{tr.provider || '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{tr.status || '—'}</td>
                        <td className="px-6 py-4">
                          {tr.certificate_document_id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button type="button" onClick={() => fetch(`/api/documents/${tr.certificate_document_id}/download${rc}`).then(r => r.json()).then((d: any) => { const dataUrl = d.content?.startsWith('data:') ? d.content : 'data:application/pdf;base64,' + (d.content || ''); const w = window.open(''); if (w) w.document.write('<iframe src="' + dataUrl + '" style="width:100%;height:100%;border:none"></iframe>'); })} className="text-indigo-600 hover:underline text-sm"> {translateUi("View")} </button>
                              {canEdit && (
                                <button type="button" onClick={() => { if (!confirm(translateUi('Delete this certificate?'))) return; fetch(`/api/training_records/${tr.id}/certificate${rc}`, { method: 'DELETE' }).then(r => r.ok ? refetchEmployee() : alert(translateUi("Failed to delete"))); }} className="text-red-600 hover:underline text-sm flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> {translateUi("Delete")} </button>
                              )}
                            </div>
                          ) : trainingCertUploadingId === tr.id ? (
                            <span className="text-slate-500 text-sm">{translateUi("Uploading…")}</span> ) : canEdit ? ( <button type="button" onClick={() => { trainingCertRecordIdRef.current = tr.id; trainingCertInputRef.current?.click(); }} className="text-indigo-600 hover:underline text-sm flex items-center gap-1"><Upload className="w-4 h-4" /> {translateUi("Upload")} </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              {canEdit && (
                                <>
                                  <button type="button" onClick={() => setViewingTrainingRecord(tr)} className="text-slate-600 hover:text-slate-900 p-1 rounded" title={translateUi("View")}><Search className="w-4 h-4" /></button>
                                  <button type="button" onClick={() => { setEditingTrainingRecord(tr); setTrainingForm({ training_name: tr.training_name || '', type: tr.type || 'Training', date_attended: tr.date_attended || '', expiry_date: tr.expiry_date || '', provider: tr.provider || '', status: tr.status || 'Completed' }); }} className="text-slate-600 hover:text-indigo-600 p-1 rounded" title={translateUi("Edit")}><Edit2 className="w-4 h-4" /></button>
                                </>
                              )}
                              {canDelete && (
                                <button type="button" onClick={() => { if (confirm(translateUi('Delete this training record?'))) fetch(`/api/training_records/${tr.id}${rc}`, { method: 'DELETE' }).then(() => refetchEmployee()); }} className="text-red-600 hover:text-red-800 p-1 rounded" title={translateUi("Delete")}><Trash2 className="w-4 h-4" /></button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Add New Training modal */}
              <>
                {isAddingTraining && (
                  <div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={cn("fixed inset-0 z-50 flex items-center justify-center p-4", isDarkMode ? "bg-black/60" : "bg-black/50")} onClick={() => setIsAddingTraining(false)}>
                  <div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className={cn("relative rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden", isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200/80")} onClick={e => e.stopPropagation()}>
                      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none" style={{ backgroundImage: "url('/GBR_BG.png')", opacity: isDarkMode ? 0.1 : 0.06 }} aria-hidden="true" />
                      <div className={cn("relative z-10 px-6 py-5 flex items-start justify-between border-b", isDarkMode ? "bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-slate-700" : "bg-gradient-to-r from-indigo-50 via-white to-indigo-50 border-slate-200")}>
                        <div>
                          <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Add New Training")}</h3>
                          <p className={cn("mt-1 text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Capture training details and completion status.")}</p>
                        </div>
                        <button type="button" onClick={() => setIsAddingTraining(false)} className={cn("p-2 rounded-lg", isDarkMode ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-500")}><X className="w-5 h-5" /></button>
                      </div>
                      <form className="relative z-10 p-6 space-y-5" onSubmit={async (e) => {
                        e.preventDefault();
                        if (!trainingForm.training_name?.trim()) { alert(translateUi("Training name is required.")); return; }
                        setSubmittingTraining(true);
                        try {
                          const res = await fetch(`/api/employees/${employeeId}/training_records${cc}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              training_name: trainingForm.training_name.trim(),
                              type: "Training",
                              date_attended: trainingForm.date_attended || null,
                              expiry_date: trainingForm.expiry_date || null,
                              provider: trainingForm.provider || null,
                              status: trainingForm.status
                            })
                          });
                          const text = await res.text();
                          const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
                          if (!res.ok) throw new Error((data as any).error || "Failed to save");
                          refetchEmployee();
                          setIsAddingTraining(false);
                        } catch (err: any) {
                          alert(err.message || translateUi('Failed to save training record'));
                        } finally {
                          setSubmittingTraining(false);
                        }
                      }}>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Training name *")}</label>
                          <input type="text" required value={trainingForm.training_name} onChange={e => setTrainingForm(f => ({ ...f, training_name: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. Safety Critical Communications" />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Date Attended")}</label>
                            <input type="date" value={trainingForm.date_attended} onChange={e => setTrainingForm(f => ({ ...f, date_attended: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                          </div>
                          <div className="space-y-1">
                            <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Expiry (if applicable)")}</label>
                            <input type="date" value={trainingForm.expiry_date} onChange={e => setTrainingForm(f => ({ ...f, expiry_date: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}> {translateUi("Provider")} </label>
                          <input type="text" value={trainingForm.provider} onChange={e => setTrainingForm(f => ({ ...f, provider: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. Internal" />
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}> {translateUi("Status")} </label>
                          <select value={trainingForm.status} onChange={e => setTrainingForm(f => ({ ...f, status: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")}>
                            <option value="Completed"> {translateUi("Completed")} </option>
                            <option value="Left"> {translateUi("Left")} </option>
                            <option value="Did not complete">{translateUi("Did not complete")}</option>
                            <option value="Not yet competent">{translateUi("Not yet competent")}</option>
                          </select>
                        </div>
                        <div className={cn("flex justify-end gap-3 pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                          <button type="button" onClick={() => setIsAddingTraining(false)} className={cn("px-4 py-2 rounded-xl", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100")}> {translateUi("Cancel")} </button>
                          <button type="submit" disabled={submittingTraining} className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"> {translateUi("Save")} </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </>
              {/* View Training modal */}
              <>
                {viewingTrainingRecord && (
                  <div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewingTrainingRecord(null)}>
                    <div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
                      <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Search className="w-5 h-5 text-indigo-600" /> {translateUi("View Training Record")}</h3>
                        <button type="button" onClick={() => setViewingTrainingRecord(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
                      </div>
                      <div className="p-6 space-y-3 text-sm">
                        <div><span className="text-slate-500 font-medium"> {translateUi("Training")} </span><br /><span className="text-slate-900">{viewingTrainingRecord.training_name || '—'}</span></div>
                        <div><span className="text-slate-500 font-medium">{translateUi("Type")}</span><br /><span className="text-slate-900">{viewingTrainingRecord.type || '—'}</span></div>
                        <div><span className="text-slate-500 font-medium">{translateUi("Date Attended")}</span><br /><span className="text-slate-900">{viewingTrainingRecord.date_attended ? formatDateUK(viewingTrainingRecord.date_attended) : '—'}</span></div>
                        <div><span className="text-slate-500 font-medium"> {translateUi("Expiry")} </span><br /><span className="text-slate-900">{viewingTrainingRecord.expiry_date ? formatDateUK(viewingTrainingRecord.expiry_date) : '—'}</span></div>
                        <div><span className="text-slate-500 font-medium"> {translateUi("Provider")} </span><br /><span className="text-slate-900">{viewingTrainingRecord.provider || '—'}</span></div>
                        <div><span className="text-slate-500 font-medium"> {translateUi("Status")} </span><br /><span className="text-slate-900">{viewingTrainingRecord.status || '—'}</span></div>
                      </div>
                      <div className="px-6 pb-4"><button type="button" onClick={() => setViewingTrainingRecord(null)} className="w-full py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"> {translateUi("Close")} </button></div>
                    </div>
                  </div>
                )}
              </>
              {/* Edit Training modal */}
              <>
                {editingTrainingRecord && (
                  <div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditingTrainingRecord(null)}>
                    <div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
                      <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-slate-900">{translateUi("Edit Training Record")}</h3>
                        <button type="button" onClick={() => setEditingTrainingRecord(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
                      </div>
                      <form className="p-6 space-y-4" onSubmit={async (e) => {
                        e.preventDefault();
                        if (!trainingForm.training_name?.trim()) { alert(translateUi("Training name is required.")); return; }
                        setSubmittingTraining(true);
                        try {
                          const res = await fetch(`/api/training_records/${editingTrainingRecord.id}${rc}`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              training_name: trainingForm.training_name.trim(),
                              type: trainingForm.type,
                              date_attended: trainingForm.date_attended || null,
                              expiry_date: trainingForm.expiry_date || null,
                              provider: trainingForm.provider || null,
                              status: trainingForm.status
                            })
                          });
                          if (!res.ok) throw new Error(translateUi("Failed to save"));
                          refetchEmployee();
                          setEditingTrainingRecord(null);
                        } catch (err: any) {
                          alert(err.message || translateUi("Failed to save"));
                        } finally {
                          setSubmittingTraining(false);
                        }
                      }}>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">{translateUi("Training name *")}</label>
                          <input type="text" required value={trainingForm.training_name} onChange={e => setTrainingForm(f => ({ ...f, training_name: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">{translateUi("Type")}</label>
                          <select value={trainingForm.type} onChange={e => setTrainingForm(f => ({ ...f, type: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900">
                            <option value="Training"> {translateUi("Training")} </option>
                            <option value="Qualification"> {translateUi("Qualification")} </option>
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{translateUi("Date Attended")}</label>
                            <input type="date" value={trainingForm.date_attended} onChange={e => setTrainingForm(f => ({ ...f, date_attended: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1"> {translateUi("Expiry (if applicable)")} </label>
                            <input type="date" value={trainingForm.expiry_date} onChange={e => setTrainingForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1"> {translateUi("Provider")} </label>
                          <input type="text" value={trainingForm.provider} onChange={e => setTrainingForm(f => ({ ...f, provider: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1"> {translateUi("Status")} </label>
                          <select value={trainingForm.status} onChange={e => setTrainingForm(f => ({ ...f, status: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900">
                            <option value="Completed"> {translateUi("Completed")} </option>
                            <option value="Left"> {translateUi("Left")} </option>
                            <option value="Did not complete">{translateUi("Did not complete")}</option>
                            <option value="Not yet competent">{translateUi("Not yet competent")}</option>
                          </select>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                          <button type="button" onClick={() => setEditingTrainingRecord(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl"> {translateUi("Cancel")} </button>
                          <button type="submit" disabled={submittingTraining} className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"> {translateUi("Save")} </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </>
            </div>
            {/* Qualifications pane */}
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl border border-slate-200 shadow-sm mt-8 min-w-0 max-w-full overflow-x-hidden">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center min-w-0">
                  <GraduationCap className="w-5 h-5 mr-2 shrink-0 text-indigo-600" /> {translateUi("Qualifications")} </h3>
                {canEdit && (
                  <button
                    onClick={() => {
                      resetQualificationForm();
                      setEditingQualification(null);
                      setIsAddingQualification(true);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center shadow-sm shadow-indigo-200 shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-2" /> {translateUi("Add Qualification")}
                  </button>
                )}
              </div>
              <input ref={qualificationCertInputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                const recordId = qualificationCertRecordIdRef.current;
                if (!file || recordId == null) return;
                setQualificationCertUploadingId(recordId);
                try {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  await new Promise<void>((resolve, reject) => { reader.onload = () => resolve(); reader.onerror = reject; });
                  const content = (reader.result as string).split(',')[1];
                  const res = await fetch(`/api/qualifications/${recordId}/certificate${rc}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: file.name, content }) });
                  if (!res.ok) throw new Error((await res.text()) || "Upload failed");
                  refetchEmployee();
                } catch (err: any) {
                  alert(err.message || translateUi("Upload failed"));
                } finally {
                  setQualificationCertUploadingId(null);
                  e.target.value = '';
                }
              }} />
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Qualification")} </th>
                      <th className="px-6 py-3 font-semibold">{translateUi("Date Obtained")}</th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Expiry (if applicable)")} </th>
                      <th className="px-6 py-3 font-semibold"> {translateUi("Certificate")} </th>
                      {(canEdit || canDelete) ? <th className="px-6 py-3 font-semibold w-28"> {translateUi("Actions")} </th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(employee.qualifications?.length ?? 0) === 0 && (
                      <tr><td colSpan={(canEdit || canDelete) ? 5 : 4} className="px-6 py-8 text-center text-slate-400 italic">{translateUi("No qualifications yet")}</td></tr>
                    )}
                    {employee.qualifications?.map((q: any) => (
                      <tr key={q.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          <div>{q.name || '—'}</div>
                          {Array.isArray(q.related_roles) && q.related_roles.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {q.related_roles.map((role: string) => (
                                <span key={role} className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-100">{role}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{q.date_obtained ? formatDateUK(q.date_obtained) : '—'}</td>
                        <td className="px-6 py-4 text-slate-600">{q.expiry_date ? formatDateUK(q.expiry_date) : '—'}</td>
                        <td className="px-6 py-4">
                          {q.certificate_document_id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                disabled={qualificationCertViewingId === q.certificate_document_id}
                                onClick={() => openQualificationCertificate(q.certificate_document_id)}
                                className="text-indigo-600 hover:underline text-sm flex items-center gap-1 disabled:opacity-60"
                              >
                                <Search className="w-3.5 h-3.5" />
                                {qualificationCertViewingId === q.certificate_document_id ? translateUi("Opening…") : translateUi("View")}
                              </button>
                              {canEdit && (
                                <button type="button" onClick={() => { if (!confirm(translateUi('Delete this certificate?'))) return; fetch(`/api/qualifications/${q.id}/certificate${rc}`, { method: 'DELETE' }).then(r => r.ok ? refetchEmployee() : alert(translateUi("Failed to delete"))); }} className="text-red-600 hover:underline text-sm flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> {translateUi("Delete")} </button>
                              )}
                            </div>
                          ) : qualificationCertUploadingId === q.id ? (
                            <span className="text-slate-500 text-sm">{translateUi("Uploading…")}</span> ) : canEdit ? ( <button type="button" onClick={() => { qualificationCertRecordIdRef.current = q.id; qualificationCertInputRef.current?.click(); }} className="text-indigo-600 hover:underline text-sm flex items-center gap-1"><Upload className="w-4 h-4" /> {translateUi("Upload")} </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              {canEdit && (
                                <>
                                  <button type="button" onClick={() => setViewingQualification(q)} className="text-slate-600 hover:text-slate-900 p-1 rounded" title={translateUi("View")}><Search className="w-4 h-4" /></button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setQualificationForm({
                                        name: q.name || '',
                                        certificate_number: q.certificate_number || '',
                                        qualification_type: q.qualification_type || '',
                                        date_obtained: q.date_obtained || '',
                                        expiry_date: q.expiry_date || '',
                                        related_roles: Array.isArray(q.related_roles) ? [...q.related_roles] : [],
                                      });
                                      setQualificationCertFile(null);
                                      setIsAddingQualification(false);
                                      setEditingQualification(q);
                                    }}
                                    className="text-slate-600 hover:text-indigo-600 p-1 rounded"
                                    title={translateUi("Edit")}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {canDelete && (
                                <button type="button" onClick={() => { if (confirm(translateUi('Delete this qualification?'))) fetch(`/api/qualifications/${q.id}${rc}`, { method: 'DELETE' }).then(() => refetchEmployee()); }} className="text-red-600 hover:text-red-800 p-1 rounded" title={translateUi("Delete")}><Trash2 className="w-4 h-4" /></button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Add Qualification modal */}
              {isAddingQualification && (
                <div className={cn("fixed inset-0 z-50 flex items-center justify-center p-4", isDarkMode ? "bg-black/60" : "bg-black/50")} onClick={() => { if (submittingQualification) return; setIsAddingQualification(false); setQualificationCertFile(null); }}>
                  <div className={cn("relative rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto", isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200/80")} onClick={e => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none" style={{ backgroundImage: "url('/GBR_BG.png')", opacity: isDarkMode ? 0.1 : 0.06 }} aria-hidden="true" />
                    <div className={cn("relative z-10 px-6 py-5 flex items-start justify-between border-b", isDarkMode ? "bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-slate-700" : "bg-gradient-to-r from-indigo-50 via-white to-indigo-50 border-slate-200")}>
                      <div>
                        <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Add Qualification")}</h3>
                        <p className={cn("mt-1 text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Add qualification details and optional certificate information.")}</p>
                      </div>
                      <button type="button" disabled={submittingQualification} onClick={() => { setIsAddingQualification(false); setQualificationCertFile(null); }} className={cn("p-2 rounded-lg disabled:opacity-50", isDarkMode ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-500")}><X className="w-5 h-5" /></button>
                    </div>
                    <form className="relative z-10 p-6 space-y-5" onSubmit={async (e) => {
                      e.preventDefault();
                      if (!qualificationForm.name?.trim()) { alert(translateUi("Qualification name is required.")); return; }
                      const hasRelatedRoles = (qualificationForm.related_roles?.length ?? 0) > 0;
                      if (hasRelatedRoles && !qualificationCertFile) {
                        alert(translateUi("A certificate / attachment upload is required when Assessing, Verification, Mentoring or Training Delivery is selected."));
                        return;
                      }
                      setSubmittingQualification(true);
                      try {
                        const res = await fetch(`/api/employees/${employeeId}/qualifications${cc}`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            name: qualificationForm.name.trim(),
                            certificate_number: qualificationForm.certificate_number.trim() || null,
                            qualification_type: qualificationForm.qualification_type || null,
                            date_obtained: qualificationForm.date_obtained || null,
                            expiry_date: qualificationForm.expiry_date || null,
                            related_roles: qualificationForm.related_roles || []
                          })
                        });
                        const text = await res.text();
                        const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
                        if (!res.ok) throw new Error((data as any).error || "Failed to save");
                        const newId = (data as any).id;
                        if (qualificationCertFile && newId) {
                          const reader = new FileReader();
                          reader.readAsDataURL(qualificationCertFile);
                          await new Promise<void>((resolve, reject) => { reader.onload = () => resolve(); reader.onerror = reject; });
                          const content = (reader.result as string).split(',')[1];
                          const uploadRes = await fetch(`/api/qualifications/${newId}/certificate${cc}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ name: qualificationCertFile.name, content })
                          });
                          if (!uploadRes.ok) throw new Error((await uploadRes.text()) || 'Qualification saved but certificate upload failed');
                        }
                        refetchEmployee();
                        setQualificationCertFile(null);
                        setIsAddingQualification(false);
                      } catch (err: any) {
                        alert(err.message || translateUi('Failed to save qualification'));
                      } finally {
                        setSubmittingQualification(false);
                      }
                    }}>
                      <div className="space-y-1">
                        <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Qualification name *")}</label>
                        <input type="text" required value={qualificationForm.name} onChange={e => setQualificationForm(f => ({ ...f, name: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. C&G Assessing" />
                      </div>
                      <div className="space-y-2">
                        <label className={cn("block text-sm font-medium", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Related roles")}</label>
                        <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Tick if this qualification supports an additional role. Certificate upload is required when any are selected.")}</p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {qualificationRoleOptions.map((role) => {
                            const checked = (qualificationForm.related_roles || []).includes(role);
                            return (
                              <label key={role} className={cn("flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-800")}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setQualificationForm((f) => {
                                    const current = f.related_roles || [];
                                    const next = checked ? current.filter((r) => r !== role) : [...current, role];
                                    return { ...f, related_roles: next };
                                  })}
                                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                {translateUi(role)}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                          Certificate / attachment{(qualificationForm.related_roles?.length ?? 0) > 0 ? ' *' : ''}
                        </label>
                        <p className={cn("text-xs mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                          {(qualificationForm.related_roles?.length ?? 0) > 0
                            ? translateUi("Required when a related role is selected.")
                            : translateUi("Upload the certificate or supporting document for this qualification.")}
                        </p>
                        <input ref={qualificationModalCertInputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => { setQualificationCertFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                        <div className="flex items-center gap-3 flex-wrap">
                          <button type="button" onClick={() => qualificationModalCertInputRef.current?.click()} className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")}>
                            <Upload className="w-4 h-4" /> {qualificationCertFile ? translateUi("Change file") : translateUi("Upload attachment")}
                          </button>
                          <span className={cn("text-sm", isDarkMode ? "text-slate-300" : "text-slate-600")}>{qualificationCertFile ? qualificationCertFile.name : 'No file selected'}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Certificate number (if applicable)")}</label>
                          <input type="text" value={qualificationForm.certificate_number} onChange={e => setQualificationForm(f => ({ ...f, certificate_number: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. 12345-AB" />
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Qualification type")}</label>
                          <select value={qualificationForm.qualification_type} onChange={e => setQualificationForm(f => ({ ...f, qualification_type: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")}>
                            <option value="">{translateUi("Please select")}</option>
                            <option value="BTEC">{translateUi("BTEC")}</option>
                            <option value="C&G">{translateUi("C&G")}</option>
                            <option value="Internally recognised">{translateUi("Internally recognised")}</option>
                            <option value="NVQ">NVQ</option>
                            <option value="Other"> {translateUi("Other")} </option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Date obtained")}</label>
                          <input type="date" value={qualificationForm.date_obtained} onChange={e => setQualificationForm(f => ({ ...f, date_obtained: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Expiry (if applicable)")}</label>
                          <input type="date" value={qualificationForm.expiry_date} onChange={e => setQualificationForm(f => ({ ...f, expiry_date: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                        </div>
                      </div>
                      <div className={cn("flex justify-end gap-3 pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                        <button type="button" disabled={submittingQualification} onClick={() => { setIsAddingQualification(false); setQualificationCertFile(null); }} className={cn("px-4 py-2 rounded-xl disabled:opacity-50", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100")}> {translateUi("Cancel")} </button>
                        <button type="submit" disabled={submittingQualification} className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 min-w-[7rem]">
                          {submittingQualification ? 'Saving…' : translateUi("Save")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {/* Edit Qualification modal */}
              {editingQualification && (
                <div className={cn("fixed inset-0 z-50 flex items-center justify-center p-4", isDarkMode ? "bg-black/60" : "bg-black/50")} onClick={() => { if (submittingQualification) return; setEditingQualification(null); setQualificationCertFile(null); }}>
                  <div className={cn("relative rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto", isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200/80")} onClick={e => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none" style={{ backgroundImage: "url('/GBR_BG.png')", opacity: isDarkMode ? 0.1 : 0.06 }} aria-hidden="true" />
                    <div className={cn("relative z-10 px-6 py-5 flex items-start justify-between border-b", isDarkMode ? "bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-slate-700" : "bg-gradient-to-r from-indigo-50 via-white to-indigo-50 border-slate-200")}>
                      <div>
                        <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Edit Qualification")}</h3>
                        <p className={cn("mt-1 text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Update qualification details and certificate attachment.")}</p>
                      </div>
                      <button type="button" disabled={submittingQualification} onClick={() => { setEditingQualification(null); setQualificationCertFile(null); }} className={cn("p-2 rounded-lg disabled:opacity-50", isDarkMode ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-500")}><X className="w-5 h-5" /></button>
                    </div>
                    <form className="relative z-10 p-6 space-y-5" onSubmit={async (e) => {
                      e.preventDefault();
                      if (!qualificationForm.name?.trim()) { alert(translateUi("Qualification name is required.")); return; }
                      const hasRelatedRoles = (qualificationForm.related_roles?.length ?? 0) > 0;
                      const hasExistingCert = !!editingQualification.certificate_document_id;
                      if (hasRelatedRoles && !hasExistingCert && !qualificationCertFile) {
                        alert(translateUi("A certificate / attachment upload is required when Assessing, Verification, Mentoring or Training Delivery is selected."));
                        return;
                      }
                      setSubmittingQualification(true);
                      try {
                        const res = await fetch(`/api/qualifications/${editingQualification.id}${rc}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            name: qualificationForm.name.trim(),
                            certificate_number: qualificationForm.certificate_number.trim() || null,
                            qualification_type: qualificationForm.qualification_type || null,
                            date_obtained: qualificationForm.date_obtained || null,
                            expiry_date: qualificationForm.expiry_date || null,
                            related_roles: qualificationForm.related_roles || []
                          })
                        });
                        const text = await res.text();
                        const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
                        if (!res.ok) throw new Error((data as any).error || "Failed to save");
                        if (qualificationCertFile) {
                          const reader = new FileReader();
                          reader.readAsDataURL(qualificationCertFile);
                          await new Promise<void>((resolve, reject) => { reader.onload = () => resolve(); reader.onerror = reject; });
                          const content = (reader.result as string).split(',')[1];
                          const uploadRes = await fetch(`/api/qualifications/${editingQualification.id}/certificate${rc}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ name: qualificationCertFile.name, content })
                          });
                          if (!uploadRes.ok) throw new Error((await uploadRes.text()) || 'Qualification saved but certificate upload failed');
                        }
                        refetchEmployee();
                        setQualificationCertFile(null);
                        setEditingQualification(null);
                      } catch (err: any) {
                        alert(err.message || translateUi('Failed to save qualification'));
                      } finally {
                        setSubmittingQualification(false);
                      }
                    }}>
                      <div className="space-y-1">
                        <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Qualification name *")}</label>
                        <input type="text" required value={qualificationForm.name} onChange={e => setQualificationForm(f => ({ ...f, name: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. C&G Assessing" />
                      </div>
                      <div className="space-y-2">
                        <label className={cn("block text-sm font-medium", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Related roles")}</label>
                        <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Tick if this qualification supports an additional role. Certificate upload is required when any are selected.")}</p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {qualificationRoleOptions.map((role) => {
                            const checked = (qualificationForm.related_roles || []).includes(role);
                            return (
                              <label key={role} className={cn("flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-800")}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setQualificationForm((f) => {
                                    const current = f.related_roles || [];
                                    const next = checked ? current.filter((r) => r !== role) : [...current, role];
                                    return { ...f, related_roles: next };
                                  })}
                                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                {translateUi(role)}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                          Certificate / attachment{(qualificationForm.related_roles?.length ?? 0) > 0 && !editingQualification.certificate_document_id ? ' *' : ''}
                        </label>
                        <p className={cn("text-xs mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                          {editingQualification.certificate_document_id
                            ? translateUi("A certificate is already on file. Upload a new file to replace it.")
                            : translateUi("Upload the certificate or supporting document for this qualification.")}
                        </p>
                        <input ref={qualificationModalCertInputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => { setQualificationCertFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                        <div className="flex items-center gap-3 flex-wrap">
                          <button type="button" onClick={() => qualificationModalCertInputRef.current?.click()} className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")}>
                            <Upload className="w-4 h-4" /> {qualificationCertFile ? 'Change file' : (editingQualification.certificate_document_id ? translateUi("Replace attachment") : translateUi("Upload attachment"))}
                          </button>
                          <span className={cn("text-sm", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                            {qualificationCertFile
                              ? qualificationCertFile.name
                              : editingQualification.certificate_document_id
                                ? 'Current certificate on file'
                                : translateUi("No file selected")}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Certificate number (if applicable)")}</label>
                          <input type="text" value={qualificationForm.certificate_number} onChange={e => setQualificationForm(f => ({ ...f, certificate_number: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400" : "border-slate-300 text-slate-900")} placeholder="e.g. 12345-AB" />
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Qualification type")}</label>
                          <select value={qualificationForm.qualification_type} onChange={e => setQualificationForm(f => ({ ...f, qualification_type: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")}>
                            <option value="">{translateUi("Please select")}</option>
                            <option value="BTEC">{translateUi("BTEC")}</option>
                            <option value="C&G">{translateUi("C&G")}</option>
                            <option value="Internally recognised">{translateUi("Internally recognised")}</option>
                            <option value="NVQ">NVQ</option>
                            <option value="Other"> {translateUi("Other")} </option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Date obtained")}</label>
                          <input type="date" value={qualificationForm.date_obtained} onChange={e => setQualificationForm(f => ({ ...f, date_obtained: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                        </div>
                        <div className="space-y-1">
                          <label className={cn("block text-sm font-medium mb-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Expiry (if applicable)")}</label>
                          <input type="date" value={qualificationForm.expiry_date} onChange={e => setQualificationForm(f => ({ ...f, expiry_date: e.target.value }))} className={cn("w-full rounded-xl border px-3 py-2.5 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 text-slate-900")} />
                        </div>
                      </div>
                      <div className={cn("flex justify-end gap-3 pt-4 border-t", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                        <button type="button" disabled={submittingQualification} onClick={() => { setEditingQualification(null); setQualificationCertFile(null); }} className={cn("px-4 py-2 rounded-xl disabled:opacity-50", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100")}> {translateUi("Cancel")} </button>
                        <button type="submit" disabled={submittingQualification} className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 min-w-[7rem]">
                          {submittingQualification ? 'Saving…' : translateUi("Save")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {/* View Qualification modal */}
              {viewingQualification && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewingQualification(null)}>
                  <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
                    <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Search className="w-5 h-5 text-indigo-600" /> {translateUi("View Qualification")}</h3>
                      <button type="button" onClick={() => setViewingQualification(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-6 space-y-3 text-sm">
                      <div><span className="text-slate-500 font-medium"> {translateUi("Qualification")} </span><br /><span className="text-slate-900">{viewingQualification.name || '—'}</span></div>
                      <div>
                        <span className="text-slate-500 font-medium">{translateUi("Related roles")}</span><br />
                        <span className="text-slate-900">
                          {Array.isArray(viewingQualification.related_roles) && viewingQualification.related_roles.length > 0
                            ? viewingQualification.related_roles.join(', ')
                            : '—'}
                        </span>
                      </div>
                      <div><span className="text-slate-500 font-medium">{translateUi("Certificate number")}</span><br /><span className="text-slate-900">{viewingQualification.certificate_number || '—'}</span></div>
                      <div><span className="text-slate-500 font-medium">{translateUi("Type")}</span><br /><span className="text-slate-900">{viewingQualification.qualification_type || '—'}</span></div>
                      <div><span className="text-slate-500 font-medium">{translateUi("Date obtained")}</span><br /><span className="text-slate-900">{viewingQualification.date_obtained ? formatDateUK(viewingQualification.date_obtained) : '—'}</span></div>
                      <div><span className="text-slate-500 font-medium"> {translateUi("Expiry")} </span><br /><span className="text-slate-900">{viewingQualification.expiry_date ? formatDateUK(viewingQualification.expiry_date) : '—'}</span></div>
                      <div>
                        <span className="text-slate-500 font-medium">{translateUi("Certificate / attachment")}</span><br />
                        <span className="text-slate-900">{viewingQualification.certificate_document_id ? translateUi("Uploaded") : translateUi("Not uploaded")}</span>
                      </div>
                    </div>
                    <div className="px-6 pb-4"><button type="button" onClick={() => setViewingQualification(null)} className="w-full py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"> {translateUi("Close")} </button></div>
                  </div>
                </div>
              )}
            </div>
          </>
          ) : activeTab === 'Monitoring' ? (
            <MonitoringTab
              employeeId={employee.id}
              currentUserProfile={currentUserProfile}
              currentUserEmail={currentUserEmail}
              companyCode={effectiveCompanyCode || companyCode}
              isDarkMode={isDarkMode}
              employeeEmail={employee.email}
              isTraineeDriverModuleActive={isTraineeDriverModuleActive}
              canManageTraineeDriver={canManageTraineeDriver}
              onOpenMedicationChecks={() => handleProfileTabSelect(translateUi("Medication Checks"))}
            />
          ) : activeTab === 'Safety Briefs' ? (
            <div className={cn("p-6 sm:p-8 rounded-2xl border shadow-sm space-y-6", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
              {!isSafetyBriefsModuleActive ? (
                <div className={cn("rounded-xl border p-5 text-sm", isDarkMode ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600")}>
                  {translateUi("Activate the Safety Briefs module from Addons to use this tab.")}
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Safety brief records")}</h3>
                      <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        {canManageSafetyBriefs
                          ? translateUi("Choose a company brief type and record in-person or remote attendance.")
                          : translateUi("Safety briefs you have attended, as recorded by your manager.")}
                      </p>
                    </div>
                  </div>

                  {canManageSafetyBriefs && (
                    <div className={cn("rounded-2xl border p-4 sm:p-5 space-y-4", isDarkMode ? "border-slate-700 bg-slate-800/70" : "border-slate-200 bg-slate-50/60")}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={cn("mb-1 block text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Brief type")}</label>
                          {safetyBriefTypes.length === 0 ? (
                            <p className={cn("rounded-xl border px-3 py-2 text-sm", isDarkMode ? "border-amber-500/30 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800")}>
                              {translateUi("No brief types configured yet. A company administrator must create types and delivery windows on the Safety Briefs page.")}
                            </p>
                          ) : (
                            <select
                              value={safetyBriefForm.brief_type_id}
                              onChange={(e) => {
                                const id = e.target.value;
                                const selected = safetyBriefTypes.find((t) => t.id === id);
                                setSafetyBriefForm((s) => ({ ...s, brief_type_id: id, brief_name: selected?.name || s.brief_name }));
                              }}
                              className={cn("w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                            >
                              <option value="">{translateUi("Select brief type")}</option>
                              {safetyBriefTypes.map((t) => {
                                const status =
                                  t.windowStatus === "open"
                                    ? translateUi("Open now")
                                    : t.windowStatus === "upcoming"
                                      ? translateUi("Upcoming")
                                      : t.windowStatus === "closed"
                                        ? translateUi("Closed")
                                        : "";
                                return (
                                  <option key={t.id} value={t.id}>
                                    {status ? `${t.name} (${status})` : t.name}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                          {(() => {
                            const selected = safetyBriefTypes.find((t) => t.id === safetyBriefForm.brief_type_id);
                            if (!selected) return null;
                            const fmt = (iso: string) => {
                              const d = new Date(iso);
                              if (!Number.isFinite(d.getTime())) return "—";
                              return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
                            };
                            return (
                              <p className={cn("mt-1.5 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                                {translateUi("Delivery window")}: {fmt(selected.window_start)} – {fmt(selected.window_end)}
                              </p>
                            );
                          })()}
                        </div>
                        <div>
                          <label className={cn("mb-1 block text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Brief date")}</label>
                          <input
                            type="date"
                            value={safetyBriefForm.date}
                            onChange={(e) => setSafetyBriefForm((s) => ({ ...s, date: e.target.value }))}
                            className={cn("w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        <label className={cn("inline-flex items-center gap-2 text-sm", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                          <input
                            type="radio"
                            checked={safetyBriefForm.delivery_type === 'in_person'}
                            onChange={() => setSafetyBriefForm((s) => ({ ...s, delivery_type: 'in_person' }))}
                          />
                          {translateUi("In person")}
                        </label>
                        <label className={cn("inline-flex items-center gap-2 text-sm", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                          <input
                            type="radio"
                            checked={safetyBriefForm.delivery_type === 'remote'}
                            onChange={() => setSafetyBriefForm((s) => ({ ...s, delivery_type: 'remote' }))}
                          /> {translateUi("Remote")} </label>
                      </div>

                      <div>
                        <label className={cn("mb-1 block text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                          {safetyBriefForm.delivery_type === 'in_person' ? translateUi("Provided by") : translateUi("Method used")}
                        </label>
                        <input
                          type="text"
                          value={safetyBriefForm.delivery_type === 'in_person' ? safetyBriefForm.provided_by : safetyBriefForm.remote_method}
                          onChange={(e) =>
                            setSafetyBriefForm((s) =>
                              s.delivery_type === 'in_person'
                                ? { ...s, provided_by: e.target.value }
                                : { ...s, remote_method: e.target.value },
                            )
                          }
                          placeholder={safetyBriefForm.delivery_type === 'in_person' ? translateUi("Trainer or assessor name") : translateUi("Teams, Zoom, webinar, phone, etc.")}
                          className={cn("w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                        />
                      </div>

                      <div className="flex flex-wrap justify-end gap-2">
                        {editingSafetyBriefId && (
                          <button type="button" onClick={resetSafetyBriefForm} className={cn("rounded-xl border px-4 py-2 text-sm", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")}>
                            {translateUi("Cancel edit")}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={submitSafetyBrief}
                          disabled={savingSafetyBrief || (safetyBriefTypes.length > 0 && !safetyBriefForm.brief_type_id)}
                          className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {savingSafetyBrief ? 'Saving…' : editingSafetyBriefId ? translateUi("Update brief") : translateUi("Add brief")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className={cn("rounded-2xl border overflow-hidden", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                    {loadingSafetyBriefs ? (
                      <div className="flex justify-center py-10">
                        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      </div> ) : safetyBriefsError ? ( <div
                        className={cn(
                          "m-4 rounded-xl border px-4 py-3 text-sm font-medium",
                          isDarkMode
                            ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                        )}
                      >
                        {safetyBriefsError}
                      </div>
                    ) : safetyBriefs.length === 0 ? (
                      <p className={cn("px-4 py-8 text-center text-sm", isDarkMode ? "text-slate-400 bg-slate-900/40" : "text-slate-500 bg-slate-50")}>
                        {translateUi("No safety brief records for this employee yet.")}
                      </p>
                    ) : (
                      <table className="w-full text-left text-sm">
                        <thead className={cn("text-xs uppercase tracking-wider", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-500")}>
                          <tr>
                            <th className="px-4 py-3">{translateUi("Date")}</th>
                            <th className="px-4 py-3"> {translateUi("Brief")} </th>
                            <th className="px-4 py-3">{translateUi("Delivery")}</th>
                            <th className="px-4 py-3">{translateUi("Provider / Method")}</th>
                            {canManageSafetyBriefs && <th className="px-4 py-3 text-right"> {translateUi("Actions")} </th>}
                          </tr>
                        </thead>
                        <tbody className={cn(isDarkMode ? "divide-y divide-slate-700 bg-slate-900/30" : "divide-y divide-slate-100 bg-white")}>
                          {safetyBriefs.map((row: any) => (
                            <tr key={String(row.id)}>
                              <td className={cn("px-4 py-3 whitespace-nowrap", isDarkMode ? "text-slate-200" : "text-slate-700")}>{row.date || '—'}</td>
                              <td className={cn("px-4 py-3", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.brief_name || '—'}</td>
                              <td className="px-4 py-3">
                                <span className={cn("inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold", String(row.delivery_type) === 'in_person' ? (isDarkMode ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700") : (isDarkMode ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"))}>
                                  {String(row.delivery_type) === 'in_person' ? translateUi("In person") : translateUi("Remote")}
                                </span>
                              </td>
                              <td className={cn("px-4 py-3", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                                {String(row.delivery_type) === 'in_person' ? (row.provided_by || '—') : (row.remote_method || '—')}
                              </td>
                              {canManageSafetyBriefs && (
                                <td className="px-4 py-3">
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingSafetyBriefId(String(row.id));
                                        setSafetyBriefForm({
                                          brief_type_id:
                                            String(row.brief_type_id || '') ||
                                            (safetyBriefTypes.find((t) => t.name === String(row.brief_name || ''))?.id ?? ''),
                                          brief_name: String(row.brief_name || ''),
                                          date: String(row.date || new Date().toISOString().slice(0, 10)),
                                          delivery_type: String(row.delivery_type || 'in_person') === 'remote' ? 'remote' : 'in_person',
                                          provided_by: String(row.provided_by || ''),
                                          remote_method: String(row.remote_method || ''),
                                        });
                                      }}
                                      className={cn("rounded-lg border px-2.5 py-1 text-xs font-semibold", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")}
                                    > {translateUi("Edit")} </button>
                                    <button
                                      type="button"
                                      onClick={() => deleteSafetyBrief(String(row.id))}
                                      className={cn("rounded-lg border px-2.5 py-1 text-xs font-semibold", isDarkMode ? "border-rose-400/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-300 text-rose-700 hover:bg-rose-50")}
                                    > {translateUi("Delete")} </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : activeTab === 'Driver Reports' ? (
            !isDriverReportsModuleActive ? (
              <div className={cn("p-6 sm:p-8 rounded-2xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
                <div className={cn("rounded-xl border p-5 text-sm", isDarkMode ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600")}>
                  {translateUi("Activate the Driver Reports module from Addons to use this tab.")}
                </div>
              </div>
            ) : (
              <DriverReportsPanel
                employeeId={employeeId}
                companyCode={effectiveCompanyCode || companyCode}
                currentUserEmail={currentUserEmail}
                canEdit={!!canManageDriverReports}
                isDarkMode={isDarkMode}
              />
            )
          ) : activeTab === 'Leave & Absence' ? (
            !isLeaveAbsenceModuleActive ? (
              <div className={cn("p-6 sm:p-8 rounded-2xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
                <div className={cn("rounded-xl border p-5 text-sm", isDarkMode ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600")}>
                  {translateUi("Activate the Leave & Absence module from Addons to use this tab.")}
                </div>
              </div>
            ) : (
              <LeaveAbsenceEmployeePanel
                employeeId={employeeId}
                companyCode={effectiveCompanyCode || companyCode || ""}
                employeeEmail={employee?.email}
                currentUserEmail={currentUserEmail}
                isDarkMode={isDarkMode}
                isMobileOrTablet={isMobileOrTablet}
              />
            )
          ) : activeTab === 'Medication Checks' ? (
            !isMedicationChecksModuleActive ? (
              <div className={cn("p-6 sm:p-8 rounded-2xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
                <div className={cn("rounded-xl border p-5 text-sm", isDarkMode ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600")}>
                  {translateUi("Activate the Medication Checks module from Addons to use this tab.")}
                </div>
              </div>
            ) : (
              <MedicationChecksPanel
                employeeId={employeeId}
                companyCode={effectiveCompanyCode || companyCode}
                currentUserEmail={currentUserEmail}
                declarations={employee.medication_declarations ?? []}
                canEdit={!!canEditMedicationChecks}
                canDelete={!!canDeleteMedicationChecks}
                canSubmitOwn={
                  !!canSubmitMedicationChecks &&
                  String(employee?.email || '').toLowerCase() === String(currentUserEmail || '').toLowerCase()
                }
                isSystemAdmin={!!isSystemAdmin}
                onRefresh={refetchEmployee}
                onMonitoringStatusChange={async (status) => { await handleUpdate({ monitoring_status: status }); }}
              />
            )
          ) : activeTab === 'Documents' ? (
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl border border-slate-200 shadow-sm min-w-0 max-w-full overflow-x-hidden">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:space-x-2">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center shrink-0">
                    <FolderFile className="w-5 h-5 mr-2 text-indigo-600" /> {canUploadDocuments ? translateUi("Document Upload Area") : translateUi("Documents")}
                  </h3>
                  {currentPath && (
                    <div className="flex min-w-0 flex-wrap items-center text-sm font-medium text-slate-500">
                      <span className="mx-2 hidden sm:inline">/</span>
                      <button onClick={() => setCurrentPath('')} className="hover:text-indigo-600 transition-colors"> {translateUi("Root")} </button>
                      <span className="mx-2">/</span>
                      <span className="text-slate-900 truncate">{currentPath}</span>
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button 
                      onClick={() => setDocView('grid')}
                      className={cn("p-1.5 rounded-md transition-colors", docView === 'grid' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}
                      title={translateUi("Thumbnail View")}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => setDocView('list')}
                      className={cn("p-1.5 rounded-md transition-colors", docView === 'list' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}
                      title={translateUi("List View")}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                  {canUploadDocuments && (
                    <>
                      <button 
                        onClick={() => setIsCreatingFolder(true)}
                        disabled={uploadingDoc}
                        className="px-3 sm:px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors flex items-center shadow-sm disabled:opacity-50 text-sm"
                      >
                        <FolderPlus className="w-4 h-4 mr-2 shrink-0" /> {translateUi("New Folder")}
                      </button>
                      <label className="px-3 sm:px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center shadow-sm shadow-indigo-200 cursor-pointer disabled:opacity-50 text-sm whitespace-nowrap">
                        {uploadingDoc ? (
                          <span className="flex items-center"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div> {translateUi("Processing...")} </span>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> {translateUi("Upload Document")}</>
                        )}
                        <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingDoc} />
                      </label>
                    </>
                  )}
                </div>
              </div>

              {!canUploadDocuments && (
                <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {translateUi("You can view and download files stored here. Only managers, company administrators, and system administrators can upload or delete documents.")}
                </div>
              )}
              
              {uploadError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-start">
                  <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{translateUi("Upload Error")}</h4>
                    <p className="text-sm mt-1">{uploadError}</p>
                  </div>
                </div>
              )}

              {canUploadDocuments && isCreatingFolder && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center space-x-3">
                  <FolderPlus className="w-5 h-5 text-indigo-500" />
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name..."
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateFolder();
                      if (e.key === 'Escape') {
                        setIsCreatingFolder(false);
                        setNewFolderName('');
                      }
                    }}
                  />
                  <button
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim() || uploadingDoc}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  > {translateUi("Create")} </button>
                  <button
                    onClick={() => {
                      setIsCreatingFolder(false);
                      setNewFolderName('');
                    }}
                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                  > {translateUi("Cancel")} </button>
                </div>
              )}
              
              {documents.length === 0 && folders.length === 0 && !uploadError && !(canUploadDocuments && isCreatingFolder) ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center bg-slate-50">
                  <FolderFile className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{translateUi("No documents yet")}</h3>
                  <p className="text-slate-500 text-sm">
                    {canUploadDocuments
                      ? translateUi("Create a folder or upload documents to get started.")
                      : translateUi("No files have been uploaded for this employee yet.")}
                  </p>
                </div>
              ) : (
                docView === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {currentPath && (
                      <div 
                        onClick={() => {
                          const parts = currentPath.split('/');
                          parts.pop();
                          setCurrentPath(parts.join('/'));
                        }}
                        className="border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer bg-slate-50 flex flex-col items-center justify-center"
                      >
                        <Folder className="w-12 h-12 text-indigo-300 mb-2" />
                        <p className="text-sm font-medium text-slate-700"> {translateUi(".. (Go Back)")} </p>
                      </div>
                    )}
                    {folders.map((folder, idx) => (
                      <div 
                        key={`folder-${idx}`} 
                        className="border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer bg-slate-50 flex flex-col items-center justify-center relative group"
                      >
                        <div onClick={() => setCurrentPath(folder.path)} className="flex flex-col items-center justify-center w-full h-full">
                          <Folder className="w-12 h-12 text-indigo-500 mb-2" />
                          <p className="text-sm font-medium text-slate-700 truncate w-full text-center" title={folder.name}>{folder.name}</p>
                        </div>
                        {canUploadDocuments && (
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteDocument(folder, true); }} className="p-1.5 bg-white text-red-600 rounded-md shadow-sm hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {documents.map((doc, idx) => (
                      <div key={`doc-${idx}`} className="border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all group relative bg-white">
                        <div className="aspect-square bg-slate-50 rounded-lg mb-3 flex items-center justify-center border border-slate-100 overflow-hidden">
                          {doc.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                            <FileText className="w-12 h-12 text-indigo-300" />
                          ) : (
                            <FileText className="w-12 h-12 text-slate-300" />
                          )}
                        </div>
                        <p className="text-sm font-medium text-slate-700 truncate" title={doc.name}>{doc.name}</p>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                          <button onClick={() => handleDownloadDocument(doc)} className="p-1.5 bg-white text-indigo-600 rounded-md shadow-sm hover:bg-indigo-50">
                            <Search className="w-4 h-4" />
                          </button>
                          {canUploadDocuments && (
                            <button onClick={() => handleDeleteDocument(doc)} className="p-1.5 bg-white text-red-600 rounded-md shadow-sm hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-3 font-semibold">{translateUi("Name")}</th>
                          <th className="px-6 py-3 font-semibold">{translateUi("Type")}</th>
                          <th className="px-6 py-3 font-semibold text-right"> {translateUi("Actions")} </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {currentPath ? (
                          <tr 
                            onClick={() => {
                              const parts = currentPath.split('/');
                              parts.pop();
                              setCurrentPath(parts.join('/'));
                            }}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <Folder className="w-5 h-5 text-indigo-300 mr-3" />
                                <span className="font-medium text-slate-900"> {translateUi(".. (Go Back)")} </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-500"> {translateUi("Folder")} </td>
                            <td className="px-6 py-4"></td>
                          </tr>
                        ) : null}
                        {folders.map((folder, idx) => (
                          <tr 
                            key={`folder-${idx}`} 
                            className="hover:bg-slate-50 transition-colors cursor-pointer group"
                          >
                            <td className="px-6 py-4" onClick={() => setCurrentPath(folder.path)}>
                              <div className="flex items-center">
                                <Folder className="w-5 h-5 text-indigo-500 mr-3" />
                                <span className="font-medium text-slate-900">{folder.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-500" onClick={() => setCurrentPath(folder.path)}> {translateUi("Folder")} </td>
                            <td className="px-6 py-4 text-right">
                              {canUploadDocuments && (
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                                  <button onClick={(e) => { e.stopPropagation(); handleDeleteDocument(folder, true); }} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                        {documents.map((doc, idx) => (
                          <tr key={`doc-${idx}`} className="hover:bg-slate-50 transition-colors group">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <FileText className="w-5 h-5 text-slate-400 mr-3" />
                                <span className="font-medium text-slate-900">{doc.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-500"> {translateUi("File")} </td>
                            <td className="px-6 py-4 text-right">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end space-x-2">
                                <button onClick={() => handleDownloadDocument(doc)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                                  <Search className="w-4 h-4" />
                                </button>
                                {canUploadDocuments && (
                                  <button onClick={() => handleDeleteDocument(doc)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          ) : activeTab === 'Medical' ? (
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              {/* Medical examination pane (loaded via /api/employees/:id). */}
              {(() => {
                const medicals = employee.medicals ?? [];
                const toBoolLatest = (v: any) => v === true || v === 1 || v === '1' || v === 'true';
                const latest =
                  medicals.find((m: any) => toBoolLatest(m?.is_latest)) ??
                  medicals
                    .slice()
                    .sort((a: any, b: any) => String(b?.date_completed ?? '').localeCompare(String(a?.date_completed ?? '')))[0];

                const statusClass = (status: string) => {
                  const s = (status ?? '').toLowerCase();
                  if (s === 'f1' || s === 'fit' || s === 'valid' || s === 'valid and fit' || s === 'valid&fit') return 'bg-emerald-100 text-emerald-700 status-chip-positive';
                  if (s === 'f2') return 'bg-orange-100 text-orange-700';
                  if (s === 'unfit') return 'bg-red-100 text-red-700';
                  return 'bg-slate-100 text-slate-700';
                };

                return (
                  <div className="mb-8 bg-slate-50 rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center">
                        <HeartPulse className="w-5 h-5 mr-2 text-indigo-600" /> {translateUi("Medical Records")}
                      </h3>
                    </div>
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                      <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{translateUi("Restricted: Medical information can only be edited by company admin or by Human Resources unless specific custom permissions are granted.")}</span>
                    </div>

                    {medicals.length === 0 || !latest ? (
                      <div className="text-slate-500 text-sm italic">{translateUi("No medical record found for this employee.")}</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Date passed")}</div>
                            <div className="font-medium text-slate-900">{latest.date_completed ? formatDateUK(latest.date_completed) : '—'}</div>
                          </div>
                          <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Expiry date")}</div>
                            <div className="font-medium text-slate-900">{latest.expiry_date ? formatDateUK(latest.expiry_date) : '—'}</div>
                          </div>
                          <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"> {translateUi("Status")} </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClass(latest.status)}`}>
                              {latest.status ?? '—'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-white">
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Medical standard assessed against")}</div>
                          <div className="text-sm text-slate-800">{String(latest.medical_standard_assessed_against ?? '').trim() || '—'}</div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Doctor / Psychologist")}</div>
                            <div className="text-sm text-slate-700">
                              <div><span className="font-medium text-slate-900">{translateUi("Doctor:")}</span> {latest.doctor || '—'}</div>
                              <div className="mt-1"><span className="font-medium text-slate-900">{translateUi("Psychologist:")}</span> {latest.psychologist || '—'}</div>
                            </div>
                          </div>
                          <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"> {translateUi("Restrictions")} </div>
                            <div className="text-sm text-slate-700 whitespace-pre-line">{latest.restrictions || '—'}</div>
                          </div>
                        </div>

                        {latest.certificate_content ? (
                          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                            <div className="text-sm text-slate-600">
                              {translateUi("Certificate:")} <span className="font-medium text-slate-900">{latest.certificate_name || 'Medical certificate'}</span>
                            </div>
                            <a
                              href={latest.certificate_content}
                              download={latest.certificate_name || 'medical_certificate'}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors"
                            >
                              <FileText className="w-4 h-4" /> {translateUi("View / Download")}
                            </a>
                          </div>
                        ) : (
                          <div className="mt-4 text-sm text-slate-500 italic">{translateUi("No medical certificate attached.")}</div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

            </div>
          ) : activeTab === 'Tasks' ? (
            <div className="w-full min-w-0">
              <EmployeeTasksSection
                companyCode={effectiveCompanyCode}
                employeeId={employeeId}
                currentUserEmail={currentUserEmail}
                currentUserTitle={currentUserTitle}
                profileEmployeeName={(employee || initialEmployee)?.full_name?.trim() || undefined}
                companyHeaders={companyHeaders}
                isDarkMode={isDarkMode}
                canManageCompanyTasks={canManageCompanyTasks}
              />
            </div>
          ) : activeTab === 'Notes' ? (
            <div className="space-y-6">
            <div
              className={cn(
                "relative flex min-h-[640px] flex-col overflow-hidden min-w-0 max-w-full rounded-3xl border shadow-xl",
                isDarkMode
                  ? "border-slate-700/80 bg-slate-950 shadow-black/40"
                  : "border-slate-200/80 bg-white shadow-slate-200/60"
              )}
            >
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-br",
                  isDarkMode
                    ? "from-sky-500/15 via-indigo-500/10 to-transparent"
                    : "from-sky-100/90 via-indigo-50/70 to-transparent"
                )}
              />
              <div
                className={cn(
                  "relative z-[1] space-y-3 border-b px-5 py-5 sm:px-6",
                  isDarkMode ? "border-slate-800" : "border-slate-100"
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div
                      className={cn(
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm",
                        isDarkMode
                          ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-sky-900/40"
                          : "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-indigo-200"
                      )}
                    >
                      <MessageCircle className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className={cn("text-lg font-bold tracking-tight", isDarkMode ? "text-slate-50" : "text-slate-900")}>
                        {translateUi("Employee Messaging & Notes")}
                      </h3>
                      <p className={cn("mt-0.5 text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        {translateUi("Conversations on this employee record")}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-semibold tabular-nums",
                        isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"
                      )}
                    >
                      {filteredNotesForView.length} shown
                      {filteredNotesForView.length !== notesSorted.length
                        ? ` · ${notesSorted.length} total`
                        : ""}
                    </span>
                    <button
                      type="button"
                      onClick={handlePrintNotes}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors",
                        isDarkMode
                          ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <Printer className="h-3.5 w-3.5" /> {translateUi("Print")} </button>
                    <button
                      type="button"
                      onClick={handleStartNewNoteConversation}
                      disabled={noteConversationBusy}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50",
                        isDarkMode
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                          : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      )}
                    >
                      <Archive className="h-3.5 w-3.5" />
                      {noteConversationBusy ? translateUi("Working…") : translateUi("Archive & new")}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    className={cn(
                      "text-xs font-semibold uppercase tracking-wide",
                      isDarkMode ? "text-slate-500" : "text-slate-500"
                    )}
                  >
                    {translateUi("Conversation")}
                  </label>
                  <select
                    value={notesConversationFilter}
                    onChange={(e) => setNotesConversationFilter(e.target.value)}
                    className={cn(
                      "min-w-[16rem] flex-1 rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2",
                      isDarkMode
                        ? "border-slate-700 bg-slate-900 text-slate-100 focus:ring-sky-500/30"
                        : "border-slate-200 bg-white text-slate-800 focus:ring-indigo-500/25"
                    )}
                  >
                    <option value="active">
                      Current{activeNoteConversation?.title ? ` — ${activeNoteConversation.title}` : ""}
                    </option>
                    <option value="all">{translateUi("All conversations")}</option>
                    {archivedNoteConversations.map((c) => {
                      const locked =
                        c.locked === true || c.locked === 1 || String(c.locked) === "1";
                      return (
                        <option key={c.id} value={c.id}>
                          {(locked ? "Locked — " : "") +
                            (c.title || "Archived conversation") +
                            ` (${formatDateUK(c.started_at) || "—"} → ${formatDateUK(c.ended_at) || "—"})`}
                        </option>
                      );
                    })}
                  </select>
                  {viewingArchivedNoteConversation && (
                    <button
                      type="button"
                      onClick={() => setNotesConversationFilter("active")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors",
                        isDarkMode
                          ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                      )}
                      title={translateUi("Close archived conversation and return to current")}
                    >
                      <X className="h-3.5 w-3.5" />
                      {translateUi("Close archive")}
                    </button>
                  )}
                </div>
                {viewingArchivedNoteConversation && (
                  <div
                    className={cn(
                      "flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2.5",
                      isDarkMode
                        ? "border-amber-500/30 bg-amber-500/10"
                        : "border-amber-200 bg-amber-50"
                    )}
                  >
                    <p
                      className={cn(
                        "min-w-0 text-xs sm:text-sm",
                        isDarkMode ? "text-amber-100" : "text-amber-900"
                      )}
                    >
                      Viewing archived conversation
                      {viewingArchivedNoteConversation.title
                        ? ` — ${viewingArchivedNoteConversation.title}`
                        : ""}{" "}
                      (read-only)
                    </p>
                    <button
                      type="button"
                      onClick={() => setNotesConversationFilter("active")}
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors",
                        isDarkMode
                          ? "border-amber-400/40 bg-amber-500/20 text-amber-50 hover:bg-amber-500/30"
                          : "border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                      )}
                    >
                      <X className="h-3.5 w-3.5" /> {translateUi("Close")} </button>
                  </div>
                )}
              </div>

              <div
                className={cn(
                  "relative z-[1] flex-1 min-h-[200px] min-h-0 space-y-3 overflow-y-auto px-3 py-5 sm:px-5",
                  isDarkMode
                    ? "bg-[radial-gradient(ellipse_at_top,_rgba(56,189,248,0.06),_transparent_55%)]"
                    : "bg-[radial-gradient(ellipse_at_top,_rgba(14,165,233,0.06),_transparent_50%),linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)]"
                )}
              >
                {(() => {
                  // Colour by displayed author name so John Doe ≠ Andy Hill even if emails collide.
                  const authorColourIndex = new Map<string, number>();
                  for (const n of filteredNotesForView as any[]) {
                    const label =
                      String(n.author_name || "Unknown").trim().toLowerCase() || "unknown";
                    if (!authorColourIndex.has(label)) {
                      authorColourIndex.set(label, authorColourIndex.size);
                    }
                  }
                  const viewerName = String(currentUserProfile?.full_name || currentUserTitle || "")
                    .trim()
                    .toLowerCase();
                  return filteredNotesForView.map((note: any, noteIdx: number) => {
                  const viewer = (currentUserEmail || "").toLowerCase();
                  const authorE = String(note.author_email || "").trim().toLowerCase();
                  const authorLabel = String(note.author_name || "Unknown").trim() || "Unknown";
                  const authorNameKey = authorLabel.toLowerCase() || "unknown";
                  // "You" only when this note is really from the signed-in person (name match; email if present).
                  const selfAuthored = Boolean(
                    viewerName &&
                      authorNameKey === viewerName &&
                      (!authorE || !viewer || authorE === viewer)
                  );
                  const canDeleteThisNote =
                    isAdmin ||
                    selfAuthored ||
                    Boolean(authorE && viewer && authorE === viewer);
                  const readKey = currentUserEmail ? noteReadsEmailKey(currentUserEmail) : "";
                  const myRead = currentUserEmail && note.reads?.[readKey];
                  const isUnread = !selfAuthored && Boolean(currentUserEmail) && !myRead;
                  const receiptRows =
                    note.reads && typeof note.reads === "object"
                      ? Object.entries(note.reads as Record<string, { read_at?: string; reader_name?: string }>)
                          .map(([k, v]) => ({ ...v, _k: k }))
                          .sort((a, b) => String(b.read_at || "").localeCompare(String(a.read_at || "")))
                      : [];
                  // WhatsApp-style ticks: one tick once posted, two once somebody
                  // other than the author has opened it.
                  const authorReadKey = authorE ? noteReadsEmailKey(authorE) : "";
                  const otherReceipts = receiptRows.filter((r) => r._k !== authorReadKey);
                  const readByOthers = otherReceipts.length > 0;
                  const tickTitle = readByOthers
                    ? `${translateUi("Read")} · ${otherReceipts
                        .map(
                          (r) =>
                            `${r.reader_name || r._k}${r.read_at ? ` (${formatDateTimeUK(r.read_at)})` : ""}`
                        )
                        .join(", ")}`
                    : translateUi("Sent — not read yet");
                  const initials = authorLabel
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p: string) => p[0]?.toUpperCase() || "")
                    .join("") || "?";
                  const themed = noteAuthorPaletteByIndex(
                    authorColourIndex.get(authorNameKey) ?? 0,
                    isDarkMode
                  );
                  const prev = noteIdx > 0 ? (filteredNotesForView as any[])[noteIdx - 1] : null;
                  const prevNameKey = prev
                    ? String(prev.author_name || "Unknown").trim().toLowerCase()
                    : "";
                  const authorChanged = Boolean(prev && prevNameKey !== authorNameKey);
                  return (
                    <div
                      key={note.id}
                      id={note.id != null && note.id !== "" ? `rv-employee-note-${note.id}` : undefined}
                      className={cn(
                        "group flex w-full justify-start scroll-mt-4",
                        authorChanged && "mt-2"
                      )}
                    >
                      <article className="flex max-w-[min(100%,32rem)] items-end gap-2.5">
                        <div
                          className={cn(
                            "mb-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white shadow-sm",
                            themed.avatar
                          )}
                          aria-hidden
                        >
                          {initials}
                        </div>
                        <div className="min-w-0 text-left">
                          <div className="mb-1 flex flex-wrap items-center gap-1.5 px-1">
                            {isUnread && (
                              <span
                                className={cn(
                                  "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]",
                                  isDarkMode ? "bg-sky-400/20 text-sky-200" : "bg-sky-500/15 text-sky-700"
                                )}
                              > {translateUi("New")} </span>
                            )}
                            <span className={cn("truncate text-xs font-semibold", themed.name)}>
                              {authorLabel}
                            </span>
                            {selfAuthored && (
                              <span
                                className={cn(
                                  "rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                                  isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-200/80 text-slate-600"
                                )}
                              >
                                You
                              </span>
                            )}
                            <span className={cn("text-[11px]", isDarkMode ? "text-slate-500" : "text-slate-400")}>
                              {formatDateTimeUK(note.timestamp)}
                            </span>
                          </div>
                          <div
                            className={cn(
                              "relative rounded-2xl px-3.5 py-2.5 text-left text-sm leading-relaxed shadow-sm",
                              "rounded-bl-none",
                              themed.bubbleText,
                              isUnread && (isDarkMode ? "ring-2 ring-sky-400/50" : "ring-2 ring-sky-300/70")
                            )}
                            style={{ backgroundColor: themed.fill }}
                          >
                            <p className="relative z-[1] whitespace-pre-wrap break-words">{note.content}</p>
                            {selfAuthored && (
                              <span
                                className="relative z-[1] mt-0.5 flex items-center justify-end"
                                title={tickTitle}
                                aria-label={tickTitle}
                              >
                                {readByOthers ? (
                                  <CheckCheck
                                    className={cn(
                                      "h-3.5 w-3.5",
                                      isDarkMode ? "text-sky-300" : "text-sky-600"
                                    )}
                                  />
                                ) : (
                                  <Check className="h-3.5 w-3.5 opacity-50" />
                                )}
                              </span>
                            )}
                            {canDeleteThisNote && note.id != null && note.id !== "" && (
                              <div className="relative z-[1] mt-1.5 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteNote(note.id)}
                                  className={cn(
                                    "rounded-md p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100",
                                    isDarkMode
                                      ? "text-slate-400 hover:bg-white/5 hover:text-rose-300"
                                      : "text-slate-400 hover:bg-black/5 hover:text-rose-600"
                                  )}
                                  title={translateUi("Delete note")}
                                  aria-label={translateUi("Delete note")}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                            {/* Speech-bubble tail — tucked into bottom-left corner */}
                            <span
                              aria-hidden
                              className="pointer-events-none absolute bottom-0 left-0 z-0 block h-[11px] w-[11px] -translate-x-[7px] translate-y-[5px]"
                              style={{
                                backgroundColor: themed.fill,
                                clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
                              }}
                            />
                          </div>
                          {receiptRows.length > 0 && (
                            <ul className={cn("mt-1.5 space-y-1 px-1 text-left", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                              {receiptRows.map((r) => (
                                <li key={r._k} className="inline-flex items-center gap-1 text-[11px]">
                                  <CheckCheck
                                    className={cn(
                                      "h-3.5 w-3.5 shrink-0",
                                      isDarkMode ? "text-emerald-400" : "text-emerald-500"
                                    )}
                                    aria-label={translateUi("Read")}
                                  />
                                  <span>
                                    Read {r.read_at ? formatDateTimeUK(r.read_at) : "—"}
                                    {r.reader_name ? ` · ${r.reader_name}` : ""}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </article>
                    </div>
                  );
                  });
                })()}
                {filteredNotesForView.length === 0 && (
                  <div
                    className={cn(
                      "flex h-full min-h-[200px] flex-col items-center justify-center rounded-2xl border border-dashed px-6 text-center",
                      isDarkMode ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-white/70"
                    )}
                  >
                    <div
                      className={cn(
                        "mb-4 flex h-14 w-14 items-center justify-center rounded-2xl",
                        isDarkMode ? "bg-slate-800 text-sky-300" : "bg-white text-indigo-500 shadow-sm"
                      )}
                    >
                      <MessageCircle className="h-7 w-7" />
                    </div>
                    <p className={cn("text-base font-semibold", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                      {notesSorted.length === 0 ? translateUi("No notes yet") : translateUi("No notes in this conversation")}
                    </p>
                    <p className={cn("mt-1 max-w-xs text-sm", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                      {notesSorted.length === 0
                        ? "Leave the first update for this employee — managers and colleagues will see it here."
                        : "Choose another conversation from the filter, or start a new one."}
                    </p>
                  </div>
                )}
              </div>

              <form
                onSubmit={handleAddNote}
                className={cn(
                  "relative z-[1] space-y-2 border-t px-4 py-4 sm:px-6",
                  isDarkMode
                    ? "border-slate-800 bg-slate-950/90 backdrop-blur"
                    : "border-slate-100 bg-gradient-to-t from-slate-50 to-white"
                )}
              >
                {!canComposeNotes && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                      {translateUi("Viewing an archived conversation (read-only).")}
                    </p>
                    <button
                      type="button"
                      onClick={() => setNotesConversationFilter("active")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors",
                        isDarkMode
                          ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                          : "border-slate-200 text-slate-700 hover:bg-slate-100"
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                      {translateUi("Close archive")}
                    </button>
                  </div>
                )}
                {noteError && (
                  <p
                    className={cn(
                      "rounded-xl border px-3 py-2 text-sm",
                      isDarkMode
                        ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    )}
                  >
                    {noteError}
                  </p>
                )}
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-2xl border p-1.5 shadow-inner transition-shadow focus-within:ring-2",
                    isDarkMode
                      ? "border-slate-700 bg-slate-900 focus-within:ring-sky-500/30"
                      : "border-slate-200 bg-white focus-within:ring-indigo-500/25",
                    !canComposeNotes && "opacity-60"
                  )}
                >
                  <input
                    type="text"
                    placeholder={
                      canComposeNotes
                        ? translateUi("Write a note for this employee…")
                        : translateUi("Switch to the current conversation to write a note…")
                    }
                    className={cn(
                      "min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm outline-none",
                      isDarkMode
                        ? "text-slate-100 placeholder:text-slate-500"
                        : "text-slate-900 placeholder:text-slate-400"
                    )}
                    value={noteContent}
                    onChange={(e) => {
                      setNoteContent(e.target.value);
                      if (noteError) setNoteError(null);
                    }}
                    disabled={noteSubmitting || !canComposeNotes}
                  />
                  <button
                    type="submit"
                    disabled={noteSubmitting || !noteContent.trim() || !canComposeNotes}
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-45",
                      "bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 hover:shadow-md active:scale-[0.98]"
                    )}
                  >
                    {noteSubmitting ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> {translateUi("Sending")} </span>
                    ) : (
                      <>
                        Post
                        <ChevronRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Archived conversations — separate pane below messaging */}
            <div
              className={cn(
                "relative min-w-0 max-w-full overflow-hidden rounded-3xl border shadow-xl",
                isDarkMode
                  ? "border-slate-700/80 bg-slate-950 shadow-black/40"
                  : "border-slate-200/80 bg-white shadow-slate-200/60"
              )}
            >
              <div
                className={cn(
                  "flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4 sm:px-6",
                  isDarkMode ? "border-slate-800" : "border-slate-100"
                )}
              >
                <div className="flex min-w-0 items-start gap-3">
                  <div
                    className={cn(
                      "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm",
                      isDarkMode
                        ? "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-amber-900/40"
                        : "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-amber-200"
                    )}
                  >
                    <Archive className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className={cn("text-lg font-bold tracking-tight", isDarkMode ? "text-slate-50" : "text-slate-900")}>
                      {translateUi("Archived conversations")}
                    </h3>
                    <p className={cn("mt-0.5 text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                      {translateUi("Auto-archive after 7 days idle · kept 12 months unless locked")}
                    </p>
                  </div>
                </div>
                <span
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-semibold tabular-nums",
                    isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"
                  )}
                >
                  {archivedNoteConversations.length} archived
                </span>
              </div>
              <div className="px-4 py-4 sm:px-6">
                {archivedNoteConversations.length === 0 ? (
                  <p className={cn("text-xs", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                    {translateUi("No archived conversations yet. Use Archive & new, or wait for 7 days of inactivity.")}
                  </p>
                ) : (
                  <ul
                    className={cn(
                      "max-h-72 divide-y overflow-y-auto rounded-xl border",
                      isDarkMode ? "divide-slate-700 border-slate-700" : "divide-slate-200 border-slate-200"
                    )}
                  >
                    {archivedNoteConversations.map((c) => {
                      const selected = notesConversationFilter === c.id;
                      const locked =
                        c.locked === true || c.locked === 1 || String(c.locked) === "1";
                      return (
                        <li
                          key={c.id}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 transition-colors",
                            selected
                              ? isDarkMode
                                ? "bg-sky-500/15"
                                : "bg-sky-50"
                              : isDarkMode
                                ? "bg-slate-950/60 hover:bg-slate-900"
                                : "bg-white hover:bg-slate-50"
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => setNotesConversationFilter(c.id)}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            title={translateUi("View this conversation")}
                          >
                            <Lock
                              className={cn(
                                "h-4 w-4 shrink-0",
                                locked
                                  ? isDarkMode
                                    ? "text-amber-300"
                                    : "text-amber-700"
                                  : isDarkMode
                                    ? "text-slate-400"
                                    : "text-slate-500"
                              )}
                            />
                            <span
                              className={cn(
                                "min-w-0 truncate text-xs font-semibold",
                                isDarkMode ? "text-slate-100" : "text-slate-800"
                              )}
                            >
                              {c.title || "Archived conversation"}
                            </span>
                            <span
                              className={cn(
                                "hidden shrink-0 text-[11px] tabular-nums sm:inline",
                                isDarkMode ? "text-slate-400" : "text-slate-500"
                              )}
                            >
                              {formatDateUK(c.started_at) || "—"}
                              <span className="mx-1 opacity-60">→</span>
                              {formatDateUK(c.ended_at) || "—"}
                            </span>
                            <span
                              className={cn(
                                "ml-auto hidden shrink-0 text-[10px] sm:inline",
                                locked
                                  ? isDarkMode
                                    ? "text-amber-300/90"
                                    : "text-amber-700"
                                  : isDarkMode
                                    ? "text-slate-500"
                                    : "text-slate-400"
                              )}
                            >
                              {locked
                                ? "Locked · kept until deleted"
                                : c.retention_expires_at
                                  ? `Auto-delete ${formatDateUK(c.retention_expires_at) || ""}`
                                  : "12-month retention"}
                            </span>
                          </button>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              disabled={noteConversationBusy || (locked && !canManageNoteConversationRetention)}
                              onClick={() => {
                                if (locked) {
                                  void handleUnlockNoteConversation(c.id);
                                } else {
                                  void handleLockNoteConversation(c.id);
                                }
                              }}
                              title={
                                locked
                                  ? canManageNoteConversationRetention
                                    ? translateUi("Unlock (resume 12-month retention)")
                                    : translateUi("Locked — only a manager can unlock")
                                  : "Lock to keep indefinitely"
                              }
                              className={cn(
                                "rounded-lg border p-1.5 transition-colors disabled:opacity-40",
                                locked
                                  ? isDarkMode
                                    ? "border-amber-500/40 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30"
                                    : "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200"
                                  : isDarkMode
                                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                                    : "border-slate-200 text-slate-600 hover:bg-slate-100"
                              )}
                              aria-label={locked ? translateUi("Locked conversation") : translateUi("Lock conversation")}
                            >
                              <Lock className="h-3.5 w-3.5" />
                            </button>
                            {canManageNoteConversationRetention && (
                              <button
                                type="button"
                                disabled={noteConversationBusy}
                                onClick={() => {
                                  void handleDeleteNoteConversation(c.id);
                                }}
                                title={translateUi("Delete permanently (manager)")}
                                className={cn(
                                  "rounded-lg border p-1.5 transition-colors disabled:opacity-40",
                                  isDarkMode
                                    ? "border-rose-500/30 text-rose-300 hover:bg-rose-500/15"
                                    : "border-rose-200 text-rose-600 hover:bg-rose-50"
                                )}
                                aria-label={translateUi("Delete archived conversation")}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            </div>
          ) : activeTab === 'Cycles' ? (
            (() => {
              const liveCycles = employeeCycles.filter((ec) => !ec.end_reason);
              const closedCycles = employeeCycles.filter((ec) => !!ec.end_reason);
              const selectedTemplateAlreadyLive =
                selectedCycleToAssign != null &&
                employeeHasLiveCycleForTemplate(liveCycles, selectedCycleToAssign);
              return (
                <div className="space-y-6">
                  <div className={cn("bg-white rounded-2xl border border-slate-200 shadow-sm", isMobileOrTablet ? "p-4" : "p-6")}>
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2 text-indigo-600" /> {translateUi("Live Cycles")}
                    </h3>
                    <p className="text-sm text-slate-500 mb-3">
                      {translateUi("Cycles that apply to this employee. Expiry is 2 years for train driver cycles, 1 year for PTS.")}
                    </p>
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/80 px-4 py-3 text-sm text-indigo-950 mb-5 flex gap-3">
                      <Archive className="w-5 h-5 shrink-0 text-indigo-600 mt-0.5" aria-hidden />
                      <div className="space-y-1.5 text-indigo-950/95">
                        <p>
                          <strong>{translateUi("Live vs closed:")}</strong> only <em>live</em> assignments accept new assessments. When every
                          event is finished, use <strong className="whitespace-nowrap">{translateUi("Close cycle")}</strong> — the record moves
                          to <strong className="whitespace-nowrap">{translateUi("Closed Cycles")}</strong>.
                        </p>
                        <p> {translateUi("Only")} <strong>{translateUi("one live cycle of the same")}</strong> is allowed. After closing (or removing a mistaken live
                          row), you can assign the same cycle again for a new competency period.
                        </p>
                      </div>
                    </div>
                    {assignCycleFeedback && (
                      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-3 items-start">
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-amber-900">{translateUi("Assignment blocked")}</p>
                          <p className="mt-1 text-amber-900/90">{assignCycleFeedback}</p>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 text-xs font-semibold text-amber-800 underline decoration-amber-600/70 hover:text-amber-950"
                          onClick={() => setAssignCycleFeedback(null)}
                        >
                          {translateUi("Dismiss")}
                        </button>
                      </div>
                    )}
                    <div className={cn("mb-6", isMobileOrTablet ? "flex flex-col gap-3" : "flex flex-wrap items-end gap-4")}>
                      <div className={cn("min-w-0", isMobileOrTablet ? "w-full" : "flex-1 sm:flex-none")}>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Assign cycle")}</label>
                        <select
                          value={selectedCycleToAssign != null ? String(selectedCycleToAssign) : ''}
                          onChange={(e) => {
                            setAssignCycleFeedback(null);
                            const raw = e.target.value;
                            if (!raw) {
                              setSelectedCycleToAssign(null);
                              return;
                            }
                            const asNum = Number(raw);
                            setSelectedCycleToAssign(Number.isFinite(asNum) && String(asNum) === raw ? asNum : raw);
                          }}
                          className={cn(
                            "w-full border border-slate-200 rounded-xl bg-white text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 outline-none",
                            isMobileOrTablet ? "max-w-none px-4 py-3 text-base min-h-[48px]" : "max-w-md px-4 py-2 min-w-[200px]"
                          )}
                        >
                          <option value="">{translateUi("— Choose a cycle —")}</option>
                          {availableCycles.map((c) => {
                            const liveBlocked = employeeHasLiveCycleForTemplate(liveCycles, c.id);
                            const cycleCc = String(c.company_code ?? '').trim();
                            const profileCc = String(companyCode ?? '').trim();
                            const ownerLabel = !cycleCc
                              ? translateUi("Standard")
                              : (profileCc &&
                                    cycleCc.toLowerCase() === profileCc.toLowerCase() &&
                                    String(companyName ?? '').trim())
                                ? String(companyName).trim()
                                : companies.find(
                                    (co) => String(co.id ?? '').trim().toLowerCase() === cycleCc.toLowerCase(),
                                  )?.name?.trim() || cycleCc;
                            return (
                              <option key={String(c.id)} value={String(c.id)} disabled={liveBlocked}>
                                {c.title} ({formatCycleDurationLabel(resolveDisplayedCycleMonths(c))})
                                {' · '}
                                {ownerLabel}
                                {liveBlocked ? ' — already live' : ''}
                              </option>
                            );
                          })}
                        </select>
                        {availableCycles.length === 0 && (
                          <p className="mt-2 text-xs text-slate-500 leading-snug">
                            {translateUi("No assignable cycles were found for this company. Create or apply a cycle in Cycle Builder.")}
                          </p>
                        )}
                        {selectedTemplateAlreadyLive && (
                          <p className="mt-2 text-xs text-amber-800 leading-snug max-w-md">
                            This competency cycle already has an active assignment. Close it into Closed Cycles or remove it before assigning
                            the same competency cycle again.
                          </p>
                        )}
                      </div>
                      <div className={isMobileOrTablet ? "w-full" : undefined}>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Start date")}</label>
                        <input
                          type="date"
                          value={assignStartDate}
                          onChange={(e) => setAssignStartDate(e.target.value)}
                          className={cn(
                            "border border-slate-200 rounded-xl bg-white text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 outline-none",
                            isMobileOrTablet ? "w-full px-4 py-3 text-base min-h-[48px]" : "px-4 py-2"
                          )}
                        />
                      </div>
                      {canManageContinuousCycles ? (
                      <label className={cn(
                        "inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 cursor-pointer select-none",
                        isMobileOrTablet ? "w-full min-h-[48px]" : "sm:mb-px"
                      )}>
                        <input
                          type="checkbox"
                          checked={assignContinuous}
                          onChange={(e) => setAssignContinuous(e.target.checked)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="font-medium"> {translateUi("Continuous")} </span>
                      </label>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleAssignCycle}
                        disabled={!selectedCycleToAssign || assigningCycle || selectedTemplateAlreadyLive}
                        title={
                          selectedTemplateAlreadyLive
                            ? 'Close or remove the existing live assignment for this cycle first'
                            : undefined
                        }
                        className={cn(
                          "px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50",
                          isMobileOrTablet ? "w-full min-h-[48px] text-base" : "sm:mb-px"
                        )}
                      >
                        {assigningCycle ? 'Assigning…' : translateUi("Assign")}
                      </button>
                    </div>
                    {isMobileOrTablet ? (
                      <div className="space-y-3">
                        {liveCycles.map((ec) => (
                          <div key={ec.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                            <div>
                              <button
                                type="button"
                                onClick={() => setViewingCycle({ id: ec.id, cycle_id: ec.cycle_id, cycle_title: ec.cycle_title, start_date: ec.start_date, expiry_date: ec.expiry_date, framework_cycle_id: ec.framework_cycle_id, is_continuous: !!ec.is_continuous, is_read_only_history: false })}
                                className="font-semibold text-indigo-700 hover:underline text-left text-base"
                              >
                                {ec.cycle_title}
                              </button>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {ec.is_continuous ? (
                                  <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-900 font-medium">
                                    Continuous
                                    {Number(ec.renewal_count ?? 0) > 0 ? ` · R${ec.renewal_count}` : ''}
                                  </span>
                                ) : null}
                                {ec.completed && (
                                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800 font-medium">
                                    Completed{ec.completed_at ? ` (${formatDateTimeUK(ec.completed_at)})` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-sm text-slate-600">
                                {translateUi("Start date")}: {formatDateUK(ec.start_date)}
                                <span className="mx-2 text-slate-300">·</span>
                                <span className={new Date(ec.expiry_date) < new Date() ? "text-rose-600 font-medium" : undefined}>
                                  {translateUi("Expiry date")}: {formatDateUK(ec.expiry_date)}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setViewingCycle({ id: ec.id, cycle_id: ec.cycle_id, cycle_title: ec.cycle_title, start_date: ec.start_date, expiry_date: ec.expiry_date, framework_cycle_id: ec.framework_cycle_id, is_continuous: !!ec.is_continuous, is_read_only_history: false })}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg border border-indigo-200 min-h-[44px]"
                                title={translateUi("View criteria & elements")}
                              >
                                <Search className="w-4 h-4" /> {translateUi("View")}
                              </button>
                              {canEdit && canManageContinuousCycles && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleContinuous(ec.id, !ec.is_continuous)}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200 min-h-[44px]"
                                  title={ec.is_continuous ? translateUi("Deactivate continuous (close normally when assessments done)") : translateUi("Mark as continuous")}
                                >
                                  {ec.is_continuous ? translateUi("Deactivate continuous") : translateUi("Make continuous")}
                                </button>
                              )}
                              {canEdit && canManageContinuousCycles && ec.is_continuous && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setContinueCycleDialog({
                                      ecId: ec.id,
                                      cycleTitle: ec.cycle_title || "Competency cycle",
                                    })
                                  }
                                  disabled={
                                    continuingEmployeeCycleId != null &&
                                    String(continuingEmployeeCycleId) === String(ec.id)
                                  }
                                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-sky-800 hover:bg-sky-50 rounded-lg border border-sky-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                  title={translateUi("Continue into the next period if all assessment events are completed and medical is live")}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  {continuingEmployeeCycleId != null &&
                                  String(continuingEmployeeCycleId) === String(ec.id)
                                    ? 'Continuing…'
                                    : translateUi("Continue cycle")}
                                </button>
                              )}
                              {canEdit && ec.completed && (
                                <button
                                  type="button"
                                  onClick={() => setCloseCycleDialog({ ecId: ec.id, cycleTitle: ec.cycle_title || "Competency cycle" })}
                                  disabled={closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(ec.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded-lg border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                  title={translateUi("Close cycle (moves it into Closed Cycles)")}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                  {closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(ec.id) ? translateUi("Closing…") : translateUi("Close cycle")}
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => handleUnassignCycle(ec.id)}
                                  className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
                                  title={translateUi("Remove")}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500 uppercase tracking-wider text-xs border-b border-slate-200">
                            <th className="pb-2 pr-4 font-semibold"> {translateUi("Cycle")} </th>
                            <th className="pb-2 pr-4 font-semibold">{translateUi("Start date")}</th>
                            <th className="pb-2 pr-4 font-semibold">{translateUi("Expiry date")}</th>
                            <th className="pb-2 pl-4 font-semibold text-right min-w-[180px]"> {translateUi("Action")} </th>
                          </tr>
                        </thead>
                        <tbody>
                          {liveCycles.map((ec) => (
                            <tr key={ec.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 pr-4">
                                <button
                                  type="button"
                                  onClick={() => setViewingCycle({ id: ec.id, cycle_id: ec.cycle_id, cycle_title: ec.cycle_title, start_date: ec.start_date, expiry_date: ec.expiry_date, framework_cycle_id: ec.framework_cycle_id, is_continuous: !!ec.is_continuous, is_read_only_history: false })}
                                  className="font-medium text-indigo-600 hover:underline text-left"
                                >
                                  {ec.cycle_title}
                                </button>
                                {ec.is_continuous ? (
                                  <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-900 font-medium">
                                    Continuous
                                    {Number(ec.renewal_count ?? 0) > 0 ? ` · R${ec.renewal_count}` : ''}
                                  </span>
                                ) : null}
                                {ec.completed && (
                                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800 font-medium">
                                    Completed{ec.completed_at ? ` (${formatDateTimeUK(ec.completed_at)})` : ''}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 pr-4 text-slate-700">{formatDateUK(ec.start_date)}</td>
                              <td className="py-3 pr-4">
                                <span className={new Date(ec.expiry_date) < new Date() ? "text-rose-600 font-medium" : "text-slate-700"}>
                                  {formatDateUK(ec.expiry_date)}
                                </span>
                              </td>
                              <td className="py-3 text-right">
                                <div className="flex items-center justify-end gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => setViewingCycle({ id: ec.id, cycle_id: ec.cycle_id, cycle_title: ec.cycle_title, start_date: ec.start_date, expiry_date: ec.expiry_date, framework_cycle_id: ec.framework_cycle_id, is_continuous: !!ec.is_continuous, is_read_only_history: false })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg border border-indigo-200"
                                    title={translateUi("View criteria & elements")}
                                  >
                                    <Search className="w-4 h-4" /> {translateUi("View")} </button>
                                  {canEdit && canManageContinuousCycles && (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleContinuous(ec.id, !ec.is_continuous)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200"
                                      title={ec.is_continuous ? translateUi("Deactivate continuous (close normally when assessments done)") : translateUi("Mark as continuous")}
                                    >
                                      {ec.is_continuous ? translateUi("Deactivate continuous") : translateUi("Make continuous")}
                                    </button>
                                  )}
                                  {canEdit && canManageContinuousCycles && ec.is_continuous && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setContinueCycleDialog({
                                          ecId: ec.id,
                                          cycleTitle: ec.cycle_title || "Competency cycle",
                                        })
                                      }
                                      disabled={
                                        continuingEmployeeCycleId != null &&
                                        String(continuingEmployeeCycleId) === String(ec.id)
                                      }
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-sky-800 hover:bg-sky-50 rounded-lg border border-sky-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={translateUi("Continue into the next period if all assessment events are completed and medical is live")}
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                      {continuingEmployeeCycleId != null &&
                                      String(continuingEmployeeCycleId) === String(ec.id)
                                        ? 'Continuing…'
                                        : translateUi("Continue cycle")}
                                    </button>
                                  )}
                                  {canEdit && ec.completed && (
                                    <button
                                      type="button"
                                      onClick={() => setCloseCycleDialog({ ecId: ec.id, cycleTitle: ec.cycle_title || "Competency cycle" })}
                                      disabled={closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(ec.id)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded-lg border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={translateUi("Close cycle (moves it into Closed Cycles)")}
                                    >
                                      <CheckCircle2 className="w-4 h-4" />
                                      {closingEmployeeCycleId != null && String(closingEmployeeCycleId) === String(ec.id) ? translateUi("Closing…") : translateUi("Close cycle")}
                                    </button>
                                  )}
                                  {canEdit && (
                                    <button
                                      type="button"
                                      onClick={() => handleUnassignCycle(ec.id)}
                                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                      title={translateUi("Remove")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                    {liveCycles.length === 0 && (
                      <p className="py-8 text-center text-slate-500 text-sm">{translateUi("No live cycles assigned. Use the dropdown above to assign one.")}</p>
                    )}
                  </div>

                  <div className={cn("bg-white rounded-2xl border border-slate-200 shadow-sm", isMobileOrTablet ? "p-4" : "p-6")}>
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2 text-slate-600" /> {translateUi("Closed Cycles")}
                    </h3>
                    {isMobileOrTablet ? (
                      <div className="space-y-3">
                        {closedCycles.map((ec) => (
                          <div key={ec.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                            <div>
                              <p className="font-semibold text-slate-800">{ec.cycle_title}</p>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {ec.end_reason === 'ended_by_admin' && (
                                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                                    Cycle ended by Rail Intel upon request{ec.ended_at ? ` (${formatDateTimeUK(ec.ended_at)})` : ''}
                                  </span>
                                )}
                                {(ec.end_reason === 'closed_by_admin' ||
                                  ec.end_reason === 'continuous_renewal') && (
                                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                                    Cycle closed{ec.ended_at ? ` (${formatDateTimeUK(ec.ended_at)})` : ''}
                                  </span>
                                )}
                              </div>
                              <p
                                className="mt-2 text-sm text-slate-600"
                                title={
                                  ec.closed_by && ec.closed_by_name && ec.closed_by_name !== ec.closed_by
                                    ? ec.closed_by
                                    : undefined
                                }
                              >
                                {translateUi("Closed by")}: {ec.closed_by_name || ec.closed_by || 'System'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() =>
                                  setViewingCycle({
                                    id: ec.id,
                                    cycle_id: ec.cycle_id,
                                    cycle_title: ec.cycle_title,
                                    start_date: ec.start_date,
                                    expiry_date: ec.expiry_date,
                                    framework_cycle_id: ec.framework_cycle_id,
                                    source_employee_cycle_id: ec.source_employee_cycle_id ?? undefined,
                                    is_read_only_history: true,
                                    archive_summary: ec.archive_summary ?? null,
                                  })
                                }
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg border border-indigo-200 min-h-[44px]"
                                title={translateUi("View cycle details (read-only for closed cycles)")}
                              >
                                <Search className="w-4 h-4" /> {translateUi("View")}
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClosedCycle(ec.id)}
                                  disabled={deletingClosedCycleId != null && String(deletingClosedCycleId) === String(ec.id)}
                                  className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px]"
                                  title={translateUi("Delete closed cycle record")}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500 uppercase tracking-wider text-xs border-b border-slate-200">
                            <th className="pb-2 pr-4 font-semibold"> {translateUi("Cycle")} </th>
                            <th className="pb-2 pr-4 font-semibold">{translateUi("Closed by")}</th>
                            <th className="pb-2 pl-4 font-semibold text-right min-w-[180px]"> {translateUi("Action")} </th>
                          </tr>
                        </thead>
                        <tbody>
                          {closedCycles.map((ec) => (
                            <tr key={ec.id} className="border-b border-slate-100 hover:bg-slate-50 bg-slate-50/50">
                              <td className="py-3 pr-4">
                                <span className="font-medium text-slate-600">{ec.cycle_title}</span>
                                {ec.end_reason === 'ended_by_admin' && (
                                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                                    Cycle ended by Rail Intel upon request{ec.ended_at ? ` (${formatDateTimeUK(ec.ended_at)})` : ''}
                                  </span>
                                )}
                                {(ec.end_reason === 'closed_by_admin' ||
                                  ec.end_reason === 'continuous_renewal') && (
                                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                                    Cycle closed{ec.ended_at ? ` (${formatDateTimeUK(ec.ended_at)})` : ''}
                                  </span>
                                )}
                              </td>
                              <td
                                className="py-3 pr-4 text-slate-700"
                                title={
                                  ec.closed_by && ec.closed_by_name && ec.closed_by_name !== ec.closed_by
                                    ? ec.closed_by
                                    : undefined
                                }
                              >
                                <span className="font-medium text-slate-800">
                                  {ec.closed_by_name || ec.closed_by || 'System'}
                                </span>
                              </td>
                              <td className="py-3 text-right">
                                <div className="flex items-center justify-end gap-3">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setViewingCycle({
                                        id: ec.id,
                                        cycle_id: ec.cycle_id,
                                        cycle_title: ec.cycle_title,
                                        start_date: ec.start_date,
                                        expiry_date: ec.expiry_date,
                                        framework_cycle_id: ec.framework_cycle_id,
                                        source_employee_cycle_id: ec.source_employee_cycle_id ?? undefined,
                                        is_read_only_history: true,
                                        archive_summary: ec.archive_summary ?? null,
                                      })
                                    }
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg border border-indigo-200"
                                    title={translateUi("View cycle details (read-only for closed cycles)")}
                                  >
                                    <Search className="w-4 h-4" /> {translateUi("View")} </button>
                                  {isAdmin && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteClosedCycle(ec.id)}
                                      disabled={deletingClosedCycleId != null && String(deletingClosedCycleId) === String(ec.id)}
                                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={translateUi("Delete closed cycle record")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                    {closedCycles.length === 0 && (
                      <p className="py-8 text-center text-slate-500 text-sm">{translateUi("No closed cycles yet.")}</p>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-sm text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">{activeTab} Section</h3>
              <p className="text-slate-500">{translateUi("This section is currently being populated with data.")}</p>
            </div>
          )}
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

interface ReportingAnalytics {
  incidentsByType: { name: string; count: number }[];
  incidentsBySeverity: { name: string; count: number }[];
  incidentsByStatus: { name: string; count: number }[];
  incidentsTrend: { month: string; count: number }[];
  incidentsTrendByType?: { type: string; month: string; count: number }[];
  monitoringBreakdown: { name: string; count: number }[];
  roleBreakdown: { name: string; count: number }[];
  statusBreakdown: { name: string; count: number }[];
  medicalsByType?: { name: string; count: number }[];
  medicalsByStatus?: { name: string; count: number }[];
  medicationByOutcome?: { name: string; count: number }[];
  medicationChecksTrend?: { month: string; count: number }[];
  safetyBriefsByName?: { name: string; count: number }[];
  safetyBriefsByMethod?: { name: string; count: number }[];
  safetyBriefsTrend?: { month: string; count: number }[];
  lateAssessmentsByEvent?: { name: string; count: number }[];
  lateAssessmentsTrend?: { month: string; count: number }[];
  trendSelectedYears?: number[];
  trendAvailableYears?: number[];
  trendSelectedPeriods?: number[];
  trendSelectedWeeks?: number[];
  reportingCalendar?: ReportingCalendarState;
  safetyBriefsModuleActive?: boolean;
  /** True when Leave/QA Verifications addon is active for this company. */
  qaVerificationsModuleActive?: boolean;
  /**
   * QA module compliance (C / (C+A+R+NA)), matching verification reports —
   * not competency expiry.
   */
  qaCompliance?: {
    rate: number;
    employeeCount: number;
    checkCount: number;
    source: "stored" | "live";
    counts?: { C: number; A: number; R: number; NA: number };
  } | null;
  licensing?: {
    scope?: string;
    activeEmployees?: number;
    expired?: number;
    dueSoonSixWeeks?: number;
    dueSoonThreeMonths?: number;
    valid?: number;
    missing?: number;
    recorded?: number;
    attention?: Array<{
      id: string;
      full_name?: string;
      role?: string;
      depot?: string;
      company_code?: string;
      licence_expiry?: string;
      days?: number;
      urgency?: "expired" | "dueSoonSixWeeks" | "dueSoonThreeMonths" | "valid";
    }>;
  };
  totals: {
    incidents: number;
    openIncidents: number;
    employees: number;
    activeEmployees: number;
    offTrack: number;
    expiredMedicals?: number;
    validMedicals?: number;
    totalMedicationChecks?: number;
    safetyBriefs?: number;
    employeesWithSafetyBriefs?: number;
    lateAssessments?: number;
    overdueAssessments?: number;
  };
}

const createEmptyReportingAnalytics = (year = new Date().getFullYear()): ReportingAnalytics => {
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
  return {
    incidentsByType: [],
    incidentsBySeverity: [],
    incidentsByStatus: [],
    incidentsTrend: months.map((month) => ({ month, count: 0 })),
    incidentsTrendByType: [],
    monitoringBreakdown: [],
    roleBreakdown: [],
    statusBreakdown: [],
    medicalsByType: [],
    medicalsByStatus: [],
    medicationByOutcome: [],
    medicationChecksTrend: months.map((month) => ({ month, count: 0 })),
    safetyBriefsByName: [],
    safetyBriefsByMethod: [],
    safetyBriefsTrend: months.map((month) => ({ month, count: 0 })),
    lateAssessmentsByEvent: [],
    lateAssessmentsTrend: months.map((month) => ({ month, count: 0 })),
    licensing: {
      scope: "active employees",
      activeEmployees: 0,
      expired: 0,
      dueSoonSixWeeks: 0,
      dueSoonThreeMonths: 0,
      valid: 0,
      missing: 0,
      recorded: 0,
      attention: [],
    },
    trendSelectedYears: [year],
    trendAvailableYears: [year],
    reportingCalendar: serializeReportingCalendarState(DEFAULT_REPORTING_CALENDAR, { years: [year], periods: [], weeks: [] }),
    safetyBriefsModuleActive: false,
    totals: {
      incidents: 0,
      openIncidents: 0,
      employees: 0,
      activeEmployees: 0,
      offTrack: 0,
      expiredMedicals: 0,
      validMedicals: 0,
      totalMedicationChecks: 0,
      safetyBriefs: 0,
      employeesWithSafetyBriefs: 0,
      lateAssessments: 0,
      overdueAssessments: 0,
    },
  };
};

const normalizeReportingAnalytics = (raw: unknown, fallbackYear: number): ReportingAnalytics => {
  if (!raw || typeof raw !== "object") return createEmptyReportingAnalytics(fallbackYear);
  const data = raw as Partial<ReportingAnalytics>;
  const empty = createEmptyReportingAnalytics(fallbackYear);
  const totalsRaw = data.totals && typeof data.totals === "object" ? data.totals : {};
  const licensingRaw = data.licensing && typeof data.licensing === "object" ? data.licensing : {};
  return {
    ...empty,
    ...data,
    incidentsByType: Array.isArray(data.incidentsByType) ? data.incidentsByType : empty.incidentsByType,
    incidentsBySeverity: Array.isArray(data.incidentsBySeverity) ? data.incidentsBySeverity : empty.incidentsBySeverity,
    incidentsByStatus: Array.isArray(data.incidentsByStatus) ? data.incidentsByStatus : empty.incidentsByStatus,
    incidentsTrend: Array.isArray(data.incidentsTrend) ? data.incidentsTrend : empty.incidentsTrend,
    incidentsTrendByType: Array.isArray(data.incidentsTrendByType) ? data.incidentsTrendByType : empty.incidentsTrendByType,
    monitoringBreakdown: Array.isArray(data.monitoringBreakdown) ? data.monitoringBreakdown : empty.monitoringBreakdown,
    roleBreakdown: Array.isArray(data.roleBreakdown) ? data.roleBreakdown : empty.roleBreakdown,
    statusBreakdown: Array.isArray(data.statusBreakdown) ? data.statusBreakdown : empty.statusBreakdown,
    medicalsByType: Array.isArray(data.medicalsByType) ? data.medicalsByType : empty.medicalsByType,
    medicalsByStatus: Array.isArray(data.medicalsByStatus) ? data.medicalsByStatus : empty.medicalsByStatus,
    medicationByOutcome: Array.isArray(data.medicationByOutcome) ? data.medicationByOutcome : empty.medicationByOutcome,
    medicationChecksTrend: Array.isArray(data.medicationChecksTrend) ? data.medicationChecksTrend : empty.medicationChecksTrend,
    safetyBriefsByName: Array.isArray(data.safetyBriefsByName) ? data.safetyBriefsByName : empty.safetyBriefsByName,
    safetyBriefsByMethod: Array.isArray(data.safetyBriefsByMethod) ? data.safetyBriefsByMethod : empty.safetyBriefsByMethod,
    safetyBriefsTrend: Array.isArray(data.safetyBriefsTrend) ? data.safetyBriefsTrend : empty.safetyBriefsTrend,
    lateAssessmentsByEvent: Array.isArray(data.lateAssessmentsByEvent) ? data.lateAssessmentsByEvent : empty.lateAssessmentsByEvent,
    lateAssessmentsTrend: Array.isArray(data.lateAssessmentsTrend) ? data.lateAssessmentsTrend : empty.lateAssessmentsTrend,
    licensing: {
      ...(empty.licensing ?? {}),
      ...licensingRaw,
      attention: Array.isArray(licensingRaw.attention) ? licensingRaw.attention : [],
    },
    totals: { ...empty.totals, ...totalsRaw },
  };
};

const hasReportingData = (analytics: ReportingAnalytics): boolean => {
  const t = analytics.totals;
  if ((t.employees ?? 0) > 0 || (t.incidents ?? 0) > 0 || (t.totalMedicationChecks ?? 0) > 0 || (t.safetyBriefs ?? 0) > 0 || (t.lateAssessments ?? 0) > 0) {
    return true;
  }
  const breakdowns = [
    analytics.incidentsByType,
    analytics.incidentsBySeverity,
    analytics.incidentsByStatus,
    analytics.monitoringBreakdown,
    analytics.roleBreakdown,
    analytics.statusBreakdown,
    analytics.medicalsByType,
    analytics.medicalsByStatus,
    analytics.medicationByOutcome,
    analytics.safetyBriefsByName,
    analytics.safetyBriefsByMethod,
    analytics.lateAssessmentsByEvent,
  ];
  return breakdowns.some((rows) => Array.isArray(rows) && rows.some((row) => (row?.count ?? 0) > 0));
};

type ReportingSectionId = "summary" | "team" | "licensing" | "health" | "incidentStats" | "visuals";

const DEFAULT_REPORTING_SECTION_ORDER: ReportingSectionId[] = ["summary", "team", "licensing", "health", "incidentStats", "visuals"];

const REPORTING_SECTION_LABELS: Record<ReportingSectionId, string> = {
  summary: "Headline metrics",
  team: "My Team",
  licensing: "Licensing expiry",
  health: "Compliance health",
  incidentStats: "Incident Data Statistics",
  visuals: "Charts and trends",
};

type ReportingPreferences = {
  visibleSections: ReportingSectionId[];
  sectionOrder: ReportingSectionId[];
};

const defaultReportingPreferences = (): ReportingPreferences => ({
  visibleSections: [...DEFAULT_REPORTING_SECTION_ORDER],
  sectionOrder: [...DEFAULT_REPORTING_SECTION_ORDER],
});

const normalizeReportingPreferences = (raw: unknown): ReportingPreferences => {
  const defaults = defaultReportingPreferences();
  if (!raw || typeof raw !== "object") return defaults;
  const candidate = raw as { visibleSections?: unknown; sectionOrder?: unknown };
  const valid = (value: unknown): ReportingSectionId[] =>
    Array.isArray(value)
      ? Array.from(
          new Set(
            value.filter(
              (id): id is ReportingSectionId =>
                id === "summary" || id === "team" || id === "licensing" || id === "health" || id === "incidentStats" || id === "visuals",
            ),
          ),
        )
      : [];
  const order = valid(candidate.sectionOrder);
  const visible = valid(candidate.visibleSections);
  const storedVisible = visible.length > 0 ? visible : defaults.visibleSections;
  // Preferences saved before the team section existed should opt into it once.
  // A current preference document always keeps "team" in sectionOrder, even when hidden.
  if (Array.isArray(candidate.sectionOrder) && !order.includes("team") && !storedVisible.includes("team")) {
    storedVisible.push("team");
  }
  if (!order.includes("incidentStats")) {
    const visualsIdx = order.indexOf("visuals");
    if (visualsIdx >= 0) order.splice(visualsIdx, 0, "incidentStats");
  }
  if (!storedVisible.includes("incidentStats") && storedVisible.includes("visuals")) {
    storedVisible.push("incidentStats");
  }
  return {
    sectionOrder: [...order, ...defaults.sectionOrder.filter((id) => !order.includes(id))],
    visibleSections: storedVisible,
  };
};

const ReportingPage = ({
  companyCode,
  currentUserEmail,
  currentUserName,
  isSystemAdmin = false,
  isAdmin = false,
  isDarkMode = false,
  onNavigateToAddons,
  onNavigateToQaVerifications,
}: {
  companyCode?: string;
  currentUserEmail?: string;
  currentUserName?: string;
  isSystemAdmin?: boolean;
  isAdmin?: boolean;
  isDarkMode?: boolean;
  onNavigateToAddons?: () => void;
  onNavigateToQaVerifications?: () => void;
}) => {
  const [analytics, setAnalytics] = useState<ReportingAnalytics | null>(null);
  const [stats, setStats] = useState<{
    totalActive?: number;
    overdueComp?: number;
    dueSoonComp?: number;
    expiredMed?: number;
    dueSoonMed?: number;
    verificationsCount?: number;
    complianceRate?: number | null;
    competencyInDateRate?: number | null;
    offTrackCount?: number;
    safetyBriefsCount?: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartsRefreshing, setChartsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const analyticsRef = useRef<ReportingAnalytics | null>(null);
  const reportingScopeKeyRef = useRef("");
  analyticsRef.current = analytics;
  const [selectedTrendYears, setSelectedTrendYears] = useState<number[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<number[]>([]);
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [calendarRevision, setCalendarRevision] = useState(0);
  const [calendarSaveState, setCalendarSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [companyOptions, setCompanyOptions] = useState<{ id: string; name?: string }[]>([]);
  const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<string[]>([]);
  const [teamStats, setTeamStats] = useState<{
    totalEmployees?: number;
    totalActive?: number;
    overdueComp?: number;
    dueSoonComp?: number;
    offTrackCount?: number;
    complianceRate?: number | null;
    competencyInDateRate?: number | null;
    expiredMed?: number;
    dueSoonMed?: number;
    licensing?: {
      expired?: number;
      dueSoonSixWeeks?: number;
      dueSoonThreeMonths?: number;
      missing?: number;
    };
  } | null>(null);
  const [reportingPreferences, setReportingPreferences] = useState<ReportingPreferences>(() => defaultReportingPreferences());
  const [dashboardPreferenceDocument, setDashboardPreferenceDocument] = useState<Record<string, unknown>>({});
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [customiseOpen, setCustomiseOpen] = useState(false);
  const [draggedSection, setDraggedSection] = useState<ReportingSectionId | null>(null);
  const [preferenceSaveState, setPreferenceSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [qaComplianceModal, setQaComplianceModal] = useState<null | "breakdown" | "upsell">(null);

  const normalizeYearList = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    return Array.from(
      new Set(
        value
          .map((entry) => Number.parseInt(String(entry), 10))
          .filter((year) => Number.isInteger(year) && year >= 2000 && year <= 2100),
      ),
    ).sort((a, b) => a - b);
  };

  useEffect(() => {
    if (!isSystemAdmin || companyCode) {
      setCompanyOptions([]);
      setSelectedCompanyCodes([]);
      return;
    }
    let cancelled = false;
    fetch('/api/companies', {
      headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : undefined,
      cache: 'no-store',
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: unknown) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        const cleaned = rows
          .map((row) => {
            const id = String((row as { id?: unknown }).id ?? "").trim();
            const name = String((row as { name?: unknown }).name ?? "").trim();
            return id ? { id, name } : null;
          })
          .filter((row): row is { id: string; name: string } => row != null);
        setCompanyOptions(cleaned);
      })
      .catch(() => {
        if (!cancelled) setCompanyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isSystemAdmin, companyCode, currentUserEmail]);

  useEffect(() => {
    if (companyOptions.length === 0) return;
    setSelectedCompanyCodes((prev) => prev.filter((code) => companyOptions.some((c) => c.id === code)));
  }, [companyOptions]);

  useEffect(() => {
    const defaults = defaultReportingPreferences();
    if (!companyCode || !currentUserEmail) {
      setReportingPreferences(defaults);
      setDashboardPreferenceDocument({});
      setPreferencesLoaded(true);
      setPreferenceSaveState("idle");
      return;
    }
    let cancelled = false;
    setPreferencesLoaded(false);
    setPreferenceSaveState("idle");
    const params = new URLSearchParams({ companyCode });
    fetch(`/api/dashboard-preferences?${params.toString()}`, {
      headers: {
        "X-User-Email": currentUserEmail,
        "X-Company-Code": companyCode,
      },
      cache: "no-store",
    })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data: unknown) => {
        if (cancelled) return;
        const document = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
        setDashboardPreferenceDocument(document);
        setReportingPreferences(normalizeReportingPreferences(document.reporting));
        setPreferencesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setDashboardPreferenceDocument({});
        setReportingPreferences(defaults);
        setPreferencesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    const manager = String(currentUserName ?? "").trim();
    if (!companyCode || !currentUserEmail || !manager) {
      setTeamStats(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ companyCode, manager, managerEmail: currentUserEmail });
    fetch(`/api/stats?${params.toString()}`, {
      headers: {
        "X-User-Email": currentUserEmail,
        "X-Company-Code": companyCode,
      },
      cache: "no-store",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (cancelled) return;
        setTeamStats(data && typeof data === "object" ? (data as NonNullable<typeof teamStats>) : null);
      })
      .catch(() => {
        if (!cancelled) setTeamStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, currentUserEmail, currentUserName]);

  const canCustomizeReporting = Boolean(companyCode && currentUserEmail && preferencesLoaded);
  const saveReportingPreferences = useCallback(
    async (next: ReportingPreferences) => {
      setReportingPreferences(next);
      if (!companyCode || !currentUserEmail) return;
      const nextDocument = { ...dashboardPreferenceDocument, reporting: next };
      setDashboardPreferenceDocument(nextDocument);
      setPreferenceSaveState("saving");
      try {
        const response = await fetch("/api/dashboard-preferences", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-User-Email": currentUserEmail,
            "X-Company-Code": companyCode,
          },
          body: JSON.stringify({ companyCode, preferences: nextDocument }),
        });
        if (!response.ok) throw new Error("Unable to save reporting preferences");
        setPreferenceSaveState("saved");
      } catch {
        setPreferenceSaveState("error");
      }
    },
    [companyCode, currentUserEmail, dashboardPreferenceDocument],
  );

  const toggleReportingSection = (section: ReportingSectionId) => {
    const visible = reportingPreferences.visibleSections.includes(section)
      ? reportingPreferences.visibleSections.filter((id) => id !== section)
      : [...reportingPreferences.visibleSections, section];
    void saveReportingPreferences({ ...reportingPreferences, visibleSections: visible });
  };

  const moveReportingSection = (section: ReportingSectionId, direction: -1 | 1) => {
    const currentIndex = reportingPreferences.sectionOrder.indexOf(section);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= reportingPreferences.sectionOrder.length) return;
    const nextOrder = [...reportingPreferences.sectionOrder];
    [nextOrder[currentIndex], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[currentIndex]];
    void saveReportingPreferences({ ...reportingPreferences, sectionOrder: nextOrder });
  };

  const dropReportingSection = (target: ReportingSectionId) => {
    if (!draggedSection || draggedSection === target) return;
    const fromIndex = reportingPreferences.sectionOrder.indexOf(draggedSection);
    const toIndex = reportingPreferences.sectionOrder.indexOf(target);
    if (fromIndex < 0 || toIndex < 0) return;
    const nextOrder = [...reportingPreferences.sectionOrder];
    nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, draggedSection);
    setDraggedSection(null);
    void saveReportingPreferences({ ...reportingPreferences, sectionOrder: nextOrder });
  };

  const resetReportingPreferences = () => {
    void saveReportingPreferences(defaultReportingPreferences());
  };

  useEffect(() => {
    let cancelled = false;
    const nextScope = `${companyCode ?? ""}::${selectedCompanyCodes.join(",")}`;
    const filterOnlyRefresh = analyticsRef.current != null && reportingScopeKeyRef.current === nextScope;
    reportingScopeKeyRef.current = nextScope;
    if (filterOnlyRefresh) {
      setChartsRefreshing(true);
    } else {
      setLoading(true);
    }
    setLoadError(null);

    async function parseJsonResponse(res: Response, label: string): Promise<unknown> {
      const text = await res.text();
      const trimmed = text.trim().toLowerCase();
      if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<!html') || trimmed.startsWith('<html')) {
        throw new Error(
          'Reporting API returned a page instead of data. Open the app at http://localhost:3000 (run: npm run dev) so API and app use the same server.'
        );
      }
      try {
        return text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`${label}: invalid response`);
      }
    }

    async function fetchReportingAnalytics(res: Response): Promise<ReportingAnalytics> {
      const fallbackYear = selectedTrendYears[0] ?? new Date().getFullYear();
      if (res.ok) {
        const data = await parseJsonResponse(res, translateUi("Analytics"));
        return normalizeReportingAnalytics(data, fallbackYear);
      }
      let body: { error?: string } = {};
      try {
        body = (await parseJsonResponse(res, translateUi("Analytics"))) as { error?: string };
      } catch {
        body = {};
      }
      // Unavailable or empty backend responses are shown as an empty dataset, not a failure.
      if (res.status === 404 || res.status === 204 || res.status === 503) {
        return createEmptyReportingAnalytics(fallbackYear);
      }
      const message = typeof body.error === "string" && body.error.trim() ? body.error.trim() : null;
      throw new Error(message || "Failed to load reporting data");
    }

    const analyticsParams = new URLSearchParams();
    if (companyCode) analyticsParams.set("companyCode", companyCode);
    if (!companyCode && isSystemAdmin && selectedCompanyCodes.length > 0) {
      analyticsParams.set("companyCodes", selectedCompanyCodes.join(","));
    }
    if (selectedTrendYears.length > 0) analyticsParams.set("years", selectedTrendYears.join(","));
    if (selectedPeriods.length > 0) analyticsParams.set("periods", selectedPeriods.join(","));
    if (selectedWeeks.length > 0) analyticsParams.set("weeks", selectedWeeks.join(","));
    const analyticsUrl = `/api/reporting/analytics?${analyticsParams.toString()}`;

    const analyticsRequest = fetch(analyticsUrl, {
      headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : undefined,
    }).then((r) => fetchReportingAnalytics(r));
    const statsRequest = filterOnlyRefresh
      ? Promise.resolve(null)
      : fetch(companyCode ? `/api/stats?companyCode=${encodeURIComponent(companyCode)}` : '/api/stats')
          .then(async (r) => {
            if (!r.ok) return {};
            return parseJsonResponse(r, 'Stats').catch(() => ({}));
          });

    Promise.all([analyticsRequest, statsRequest])
      .then(([analyticsData, statsData]) => {
        if (!cancelled) {
          setAnalytics(analyticsData);
          const serverSelectedYears = normalizeYearList(analyticsData.trendSelectedYears);
          if (
            serverSelectedYears.length > 0 &&
            serverSelectedYears.join(",") !== selectedTrendYears.join(",")
          ) {
            setSelectedTrendYears(serverSelectedYears);
          }
          if (statsData) {
            setStats(statsData as {
              totalActive?: number;
              overdueComp?: number;
              dueSoonComp?: number;
              expiredMed?: number;
              dueSoonMed?: number;
              verificationsCount?: number;
              complianceRate?: number | null;
              competencyInDateRate?: number | null;
              offTrackCount?: number;
              safetyBriefsCount?: number;
            });
          }
        }
      })
      .catch((err) => {
        if (!cancelled && analyticsRef.current == null) {
          setLoadError(err?.message ?? 'Failed to load reporting data');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setChartsRefreshing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, selectedTrendYears, selectedPeriods, selectedWeeks, calendarRevision, currentUserEmail, isSystemAdmin, selectedCompanyCodes]);

  if (loading && !chartsRefreshing) {
    return (
      <div className={cn("flex items-center justify-center min-h-[400px] rounded-3xl border", isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white")}>
        <div className={cn("flex items-center gap-2", isDarkMode ? "text-slate-300" : "text-slate-500")}>
          <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          {translateUi("Loading analytics…")}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className={cn("rounded-3xl border p-12 text-center", isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white")}>
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className={cn("text-xl font-bold mb-2", isDarkMode ? "text-slate-100" : "text-slate-900")}> {translateUi("Reporting")} </h2>
        <p className={cn(isDarkMode ? "text-slate-300" : "text-slate-500")}>{loadError || "Unable to load reporting data."}</p>
      </div>
    );
  }

  const reportingIsEmpty = !hasReportingData(analytics);
  const reportingCalendar = analytics.reportingCalendar;
  const fyStartMonth = reportingCalendar?.fyStartMonth ?? 1;
  const weekStartsOn = (reportingCalendar?.weekStartsOn === 0 ? 0 : 1) as WeekStartsOn;
  const canEditReportingCalendar = Boolean(companyCode && currentUserEmail && (isAdmin || isSystemAdmin));
  const saveReportingCalendar = async (next: ReportingCalendarConfig) => {
    if (!companyCode || !currentUserEmail) return;
    setCalendarSaveState("saving");
    try {
      const response = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/reporting-calendar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          "X-Company-Code": companyCode,
        },
        body: JSON.stringify(next),
      });
      if (!response.ok) throw new Error("Unable to save reporting calendar");
      setSelectedTrendYears([]);
      setSelectedPeriods([]);
      setSelectedWeeks([]);
      setCalendarRevision((value) => value + 1);
      setCalendarSaveState("saved");
    } catch {
      setCalendarSaveState("error");
    }
  };
  const calendarYearForSettings = Number((reportingCalendar?.period1Start ?? "").slice(0, 4))
    || selectedTrendYears[0]
    || new Date().getFullYear();
  const commencementInputValue = reportingCalendar?.period1Start
    ?? `${calendarYearForSettings}-${reportingCalendar?.p1CommencementMd ?? "01-01"}`;
  const applyReportingCalendarPatch = (patch: Partial<ReportingCalendarConfig>) => {
    const nextMonth = patch.fyStartMonth ?? fyStartMonth;
    const nextWeek = patch.weekStartsOn ?? weekStartsOn;
    let md = patch.p1CommencementMd;
    if (!md) {
      if (patch.fyStartMonth != null && patch.fyStartMonth !== fyStartMonth) {
        md = suggestedP1CommencementYmd(calendarYearForSettings, nextMonth, nextWeek).slice(5);
      } else if (patch.weekStartsOn != null && patch.weekStartsOn !== weekStartsOn && reportingCalendar?.period1Start) {
        md = startOfWeekOnOrAfter(reportingCalendar.period1Start, nextWeek).slice(5);
      } else {
        md = reportingCalendar?.p1CommencementMd ?? suggestedP1CommencementYmd(calendarYearForSettings, nextMonth, nextWeek).slice(5);
      }
    }
    void saveReportingCalendar({
      fyStartMonth: nextMonth,
      weekStartsOn: nextWeek,
      p1CommencementMd: md ?? DEFAULT_REPORTING_CALENDAR.p1CommencementMd,
    });
  };
  const reportingCalendarFieldClass = (onGradient: boolean) =>
    onGradient
      ? "rounded-lg border border-white/20 bg-slate-950/60 px-2.5 py-1.5 text-xs font-semibold text-slate-100 [color-scheme:dark]"
      : cn("rounded-lg border px-2.5 py-1.5 text-xs font-semibold", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-200" : "border-slate-300 bg-white text-slate-700");
  const renderReportingCalendarControls = (onGradient: boolean) => {
    if (!canEditReportingCalendar) return null;
    const labelClass = onGradient
      ? "flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400"
      : cn("flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500");
    return (
      <div className={cn("rounded-xl border p-3", onGradient ? "border-white/10 bg-white/[0.04]" : isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white")}>
        <div className="mb-2 flex items-center gap-2">
          <Calendar className={cn("h-4 w-4", onGradient ? "text-sky-200" : "text-indigo-500")} />
          <p className={cn("text-[11px] font-semibold uppercase tracking-wider", onGradient ? "text-slate-300" : isDarkMode ? "text-slate-300" : "text-slate-600")}>
            {translateUi("Period calendar")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className={labelClass}>
            {translateUi("Financial year starts")}
            <select
              value={fyStartMonth === 4 ? "4" : "1"}
              onChange={(event) => applyReportingCalendarPatch({ fyStartMonth: Number.parseInt(event.target.value, 10) })}
              className={reportingCalendarFieldClass(onGradient)}
            >
              <option value="1">{translateUi("January")}</option>
              <option value="4">{translateUi("April")}</option>
            </select>
          </label>
          <label className={labelClass}>
            {translateUi("Week starts on")}
            <select
              value={String(weekStartsOn)}
              onChange={(event) => applyReportingCalendarPatch({ weekStartsOn: Number.parseInt(event.target.value, 10) === 0 ? 0 : 1 })}
              className={reportingCalendarFieldClass(onGradient)}
            >
              <option value="1">{translateUi("Monday")}</option>
              <option value="0">{translateUi("Sunday")}</option>
            </select>
          </label>
          <label className={labelClass}>
            {translateUi("Period 1 commencement")}
            <input
              type="date"
              value={commencementInputValue}
              onChange={(event) => {
                const value = event.target.value;
                if (!value) return;
                void saveReportingCalendar(calendarConfigFromCommencementYmd(value, { fyStartMonth, weekStartsOn }));
              }}
              className={reportingCalendarFieldClass(onGradient)}
            />
          </label>
        </div>
        <p className={cn("mt-2 text-xs", onGradient ? "text-slate-300" : isDarkMode ? "text-slate-400" : "text-slate-500")}>
          {reportingCalendar?.period1Start
            ? translateUi("Period 1 starts {{date}}. Weeks begin on {{weekday}}.", {
                date: formatYmdUk(reportingCalendar.period1Start),
                weekday: weekStartsOnLabel(weekStartsOn),
              })
            : translateUi("Choose the first Sunday or Monday of the financial year, then periods run in 4-week blocks from that date.")}
          {reportingCalendar?.current
            ? ` ${translateUi("Today is P{{period}} week {{week}}.", { period: reportingCalendar.current.period, week: reportingCalendar.current.weekInPeriod })}`
            : ""}
          {calendarSaveState === "saving"
            ? ` ${translateUi("Saving…")}`
            : calendarSaveState === "saved"
              ? ` ${translateUi("Saved")}`
              : calendarSaveState === "error"
                ? ` ${translateUi("Could not save")}`
                : ""}
        </p>
      </div>
    );
  };

  if (reportingIsEmpty) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <h1 className={cn("text-2xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Reporting & Analytics")}</h1>
          <p className={cn("text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>
            {translateUi("Incidents, monitoring, safety briefs and compliance overview.")}
          </p>
        </div>
        <div
          className={cn(
            "rounded-3xl border p-12 text-center max-w-xl mx-auto",
            isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
          )}
        >
          <BarChart3 className={cn("w-12 h-12 mx-auto mb-4", isDarkMode ? "text-slate-500" : "text-slate-400")} />
          <h2 className={cn("text-xl font-bold mb-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("No data to report yet")}</h2>
          <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
            There is nothing to show in analytics for this company yet. Once you add employees and start recording
            incidents, medicals, and competency activity, charts and summaries will appear here.
          </p>
        </div>
        <div className="mx-auto max-w-xl">
          {renderReportingCalendarControls(false)}
        </div>
      </div>
    );
  }

  const { totals } = analytics;
  const availableTrendYears = normalizeYearList(analytics.trendAvailableYears);
  const yearsForControls = availableTrendYears.length > 0 ? availableTrendYears : selectedTrendYears;
  const toggleTrendYear = (year: number) => {
    setSelectedTrendYears((prev) => {
      if (prev.includes(year)) {
        return prev.length > 1 ? prev.filter((v) => v !== year) : prev;
      }
      return [...prev, year].sort((a, b) => a - b);
    });
  };
  const reportingPeriods = reportingCalendar?.periods ?? [];
  const reportingWeeks = reportingCalendar?.weeks ?? [];
  const maxWeekInPeriod = Math.max(
    4,
    ...reportingPeriods
      .filter((row) => selectedPeriods.length === 0 || selectedPeriods.includes(row.period))
      .map((row) => row.weeks),
  );
  const weekOptions = Array.from({ length: maxWeekInPeriod }, (_, idx) => {
    const weekInPeriod = idx + 1;
    const sample = reportingWeeks.find((week) =>
      (selectedPeriods.length === 0 || selectedPeriods.includes(week.period)) && week.weekInPeriod === weekInPeriod,
    );
    return {
      id: weekInPeriod,
      label: `W${weekInPeriod}`,
      title: sample
        ? selectedPeriods.length === 1
          ? `Week ${weekInPeriod}: ${formatYmdShort(sample.start)} – ${formatYmdShort(sample.end)}`
          : `Week ${weekInPeriod} of each selected period`
        : `Week ${weekInPeriod}`,
    };
  });
  const toggleReportingPeriod = (period: number) => {
    setSelectedPeriods((prev) => {
      const next = prev.includes(period) ? prev.filter((value) => value !== period) : [...prev, period].sort((a, b) => a - b);
      return next;
    });
  };
  const toggleReportingWeek = (week: number) => {
    setSelectedWeeks((prev) => {
      const next = prev.includes(week) ? prev.filter((value) => value !== week) : [...prev, week].sort((a, b) => a - b);
      return next;
    });
  };
  const chartFilterChipClass = (active: boolean) =>
    cn(
      "inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold ring-1 transition",
      active
        ? "bg-sky-400 text-slate-950 ring-sky-200 shadow-sm"
        : "bg-white/10 text-slate-200 ring-white/15 hover:bg-white/15 hover:ring-white/30",
    );
  const chartFilterSummary = [
    yearsForControls.length > 0
      ? (selectedTrendYears.length === yearsForControls.length
          ? translateUi("All years")
          : selectedTrendYears.map((year) => formatFinancialYearLabel(year, fyStartMonth)).join(", "))
      : null,
    selectedPeriods.length > 0 ? selectedPeriods.map((period) => `P${period}`).join(", ") : translateUi("All periods"),
    selectedWeeks.length > 0 ? selectedWeeks.map((week) => `W${week}`).join(", ") : translateUi("All weeks"),
  ].filter(Boolean).join(" · ");
  const chartFilterDateRange = selectedReportingDateRange(
    {
      fyStartMonth,
      weekStartsOn,
      p1CommencementMd: reportingCalendar?.p1CommencementMd ?? DEFAULT_REPORTING_CALENDAR.p1CommencementMd,
    },
    {
      years: selectedTrendYears.length > 0 ? selectedTrendYears : yearsForControls,
      periods: selectedPeriods,
      weeks: selectedWeeks,
    },
  );
  const chartFilterDateRangeLabel = chartFilterDateRange
    ? formatYmdRangeShort(chartFilterDateRange.start, chartFilterDateRange.end)
    : null;
  const showIncidentStats = reportingPreferences.visibleSections.includes("incidentStats");
  const showVisualsCharts = reportingPreferences.visibleSections.includes("visuals");
  const reportingChartFilters = (
        <div className="mb-5 rounded-2xl border border-white/15 bg-slate-950/40 p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-300">{translateUi("Chart filters")}</p>
              <p className="mt-1 text-sm text-slate-200">{chartFilterSummary}</p>
              <p className="mt-0.5 text-xs text-slate-400">{translateUi("Applies to incident statistics and trend charts. Headline, team, licensing and health stay unchanged. Select more than one period or week to compare.")}</p>
            </div>
            <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
              {chartFilterDateRangeLabel && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-sky-400/15 px-3 py-1.5 text-xs font-semibold text-sky-100 ring-1 ring-sky-300/40"
                  title={
                    chartFilterDateRange
                      ? `${formatYmdUk(chartFilterDateRange.start)} – ${formatYmdUk(chartFilterDateRange.end)}`
                      : undefined
                  }
                >
                  <Calendar className="h-3.5 w-3.5 text-sky-300" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-300/80">{translateUi("Range")}</span>
                  {chartFilterDateRangeLabel}
                </span>
              )}
              {(selectedPeriods.length > 0 || selectedWeeks.length > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPeriods([]);
                    setSelectedWeeks([]);
                  }}
                  className="rounded-lg px-2.5 py-1 text-xs font-semibold text-sky-200 ring-1 ring-white/15 hover:bg-white/10"
                >
                  {translateUi("Clear periods & weeks")}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{translateUi("Years")}</span>
              {yearsForControls.map((year) => {
                const active = selectedTrendYears.includes(year);
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => toggleTrendYear(year)}
                    className={chartFilterChipClass(active)}
                    aria-pressed={active}
                  >
                    {active ? <Check className="h-3 w-3" /> : null}
                    {formatFinancialYearLabel(year, fyStartMonth)}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setSelectedTrendYears(yearsForControls)}
                className={chartFilterChipClass(selectedTrendYears.length === yearsForControls.length && yearsForControls.length > 0)}
                aria-pressed={selectedTrendYears.length === yearsForControls.length && yearsForControls.length > 0}
              >
                {translateUi("All years")}
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{translateUi("Periods")}</span>
                <button
                  type="button"
                  onClick={() => setSelectedPeriods([])}
                  className={chartFilterChipClass(selectedPeriods.length === 0)}
                  aria-pressed={selectedPeriods.length === 0}
                >
                  {translateUi("All")}
                </button>
                {(reportingPeriods.length > 0 ? reportingPeriods : Array.from({ length: 13 }, (_, idx) => ({ period: idx + 1, weeks: 4, start: "", end: "" }))).map((period) => {
                  const active = selectedPeriods.includes(period.period);
                  const title = period.start && period.end ? `P${period.period}: ${formatYmdShort(period.start)} – ${formatYmdShort(period.end)}` : `P${period.period}`;
                  return (
                    <button
                      key={period.period}
                      type="button"
                      title={title}
                      onClick={() => toggleReportingPeriod(period.period)}
                      className={chartFilterChipClass(active)}
                      aria-pressed={active}
                    >
                      {active ? <Check className="h-3 w-3" /> : null}
                      P{period.period}
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{translateUi("Weeks")}</span>
                <button
                  type="button"
                  onClick={() => setSelectedWeeks([])}
                  className={chartFilterChipClass(selectedWeeks.length === 0)}
                  aria-pressed={selectedWeeks.length === 0}
                >
                  {translateUi("All")}
                </button>
                {weekOptions.map((week) => {
                  const active = selectedWeeks.includes(week.id);
                  return (
                    <button
                      key={week.id}
                      type="button"
                      title={week.title}
                      onClick={() => toggleReportingWeek(week.id)}
                      className={chartFilterChipClass(active)}
                      aria-pressed={active}
                    >
                      {active ? <Check className="h-3 w-3" /> : null}
                      {week.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {renderReportingCalendarControls(true)}
          </div>
        </div>
  );
  const showSafetyBriefs = analytics.safetyBriefsModuleActive !== false;
  const showQaCompliance = analytics.qaVerificationsModuleActive === true;
  const competencyInDateRate =
    stats?.competencyInDateRate != null
      ? stats.competencyInDateRate
      : stats?.complianceRate != null
        ? stats.complianceRate
        : null;
  const openQaComplianceModal = () => {
    setQaComplianceModal(showQaCompliance ? "breakdown" : "upsell");
  };
  const complianceTile = showQaCompliance
    ? {
        key: "qa-compliance",
        label: "QA compliance rate",
        value:
          analytics.qaCompliance?.rate != null ? `${analytics.qaCompliance.rate}%` : "—",
        icon: CheckCircle2,
        color: "bg-violet-500",
        text: "text-violet-600",
        onClick: openQaComplianceModal,
        clickable: true,
      }
    : {
        key: "qa-compliance-upsell",
        label: "QA compliance rate",
        value: "—",
        icon: CheckCircle2,
        color: "bg-violet-500",
        text: "text-violet-600",
        onClick: openQaComplianceModal,
        clickable: true,
        locked: true,
      };
  const competencyTile = !showQaCompliance
    ? {
        key: "competencies-in-date",
        label: "Competencies in date",
        value: competencyInDateRate != null ? `${competencyInDateRate}%` : "—",
        icon: CheckCircle2,
        color: "bg-violet-500",
        text: "text-violet-600",
      }
    : null;
  const safetyBriefCoverageRate =
    (totals.employees ?? 0) > 0
      ? Math.round((((totals.employeesWithSafetyBriefs ?? 0) as number) / (totals.employees as number)) * 100)
      : 0;
  const coverageTone =
    safetyBriefCoverageRate >= 90
      ? (isDarkMode ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
      : safetyBriefCoverageRate >= 70
        ? (isDarkMode ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700")
        : (isDarkMode ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700");
  const licensing = analytics.licensing ?? {};
  const licenceAttention = Array.isArray(licensing.attention) ? licensing.attention : [];
  const formatLicenceDate = (value?: string) => {
    if (!value) return "—";
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };
  const licenceUrgencyLabel = (urgency?: string) => {
    if (urgency === "expired") return translateUi("Expired");
    if (urgency === "dueSoonSixWeeks") return translateUi("Due within 6 weeks");
    return translateUi("Due within 3 months");
  };
  const hasAssessingTeam = (teamStats?.totalEmployees ?? 0) > 0;
  const teamComplianceRate =
    teamStats?.competencyInDateRate != null
      ? teamStats.competencyInDateRate
      : teamStats?.complianceRate != null
        ? teamStats.complianceRate
        : null;
  const teamComplianceTone =
    teamComplianceRate == null
      ? "text-slate-300"
      : teamComplianceRate >= 90
        ? "text-emerald-300"
        : teamComplianceRate >= 70
          ? "text-amber-300"
          : "text-rose-300";

  return (
    <div className="flex flex-col gap-8">
      <ReportingQaComplianceModal
        open={qaComplianceModal != null}
        mode={qaComplianceModal === "upsell" ? "upsell" : "breakdown"}
        data={analytics.qaCompliance ?? null}
        isDarkMode={isDarkMode}
        isAdmin={isAdmin || isSystemAdmin}
        onClose={() => setQaComplianceModal(null)}
        onExploreAddons={onNavigateToAddons}
        onOpenQaVerifications={showQaCompliance ? onNavigateToQaVerifications : undefined}
      />
      <div className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}>
        <DashboardGradientPaneGlows />
        <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">{translateUi("Company intelligence")}</p>
            <h1 className="text-3xl font-bold tracking-tight text-white">{translateUi("Reporting & Analytics")}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300">{translateUi("A live view of workforce readiness, assurance activity, and operational risk.")}</p>
          </div>
          <button
            type="button"
            onClick={() => setCustomiseOpen((open) => !open)}
            disabled={!canCustomizeReporting}
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Settings className="h-4 w-4" />
            {translateUi("Customise view")}
          </button>
        </div>
        {isSystemAdmin && !companyCode && (
          <details className="relative z-10 mt-4 w-full max-w-lg rounded-xl border border-white/15 bg-white/[0.06] px-3 py-2">
            <summary className="cursor-pointer select-none text-sm font-semibold text-slate-100">
              Companies: {selectedCompanyCodes.length === 0 ? "All companies" : `${selectedCompanyCodes.length} selected`}
            </summary>
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={selectedCompanyCodes.length === 0}
                  onChange={() => setSelectedCompanyCodes([])}
                />
                {translateUi("All companies")}
              </label>
              <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                {companyOptions.map((company) => {
                  const checked = selectedCompanyCodes.includes(company.id);
                  return (
                    <label key={company.id} className="flex items-center gap-2 text-sm text-slate-200">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedCompanyCodes((prev) =>
                            checked ? prev.filter((id) => id !== company.id) : [...prev, company.id]
                          )
                        }
                      />
                      <span className="truncate">{company.name || company.id}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </details>
        )}
      </div>

      {customiseOpen && (
        <section className={cn("rv-reporting-reveal rounded-2xl border p-4 shadow-sm sm:p-5", isDarkMode ? "border-slate-700 bg-slate-900" : "border-indigo-100 bg-white")}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className={cn("text-sm font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Customise your reporting view")}</h2>
              <p className={cn("mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                {translateUi("Choose which sections appear and drag them into the order that suits you.")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {preferenceSaveState !== "idle" && (
                <span className={cn("text-xs", preferenceSaveState === "error" ? "text-rose-500" : isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {preferenceSaveState === "saving"
                    ? translateUi("Saving…")
                    : preferenceSaveState === "saved"
                      ? translateUi("Saved")
                      : translateUi("Could not save")}
                </span>
              )}
              <button
                type="button"
                onClick={resetReportingPreferences}
                className={cn("inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold", isDarkMode ? "text-sky-300 hover:bg-slate-800" : "text-indigo-700 hover:bg-indigo-50")}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {translateUi("Reset")}
              </button>
              <button
                type="button"
                onClick={() => setCustomiseOpen(false)}
                className={cn("inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold", isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100")}
                aria-label={translateUi("Collapse customisation panel")}
              >
                <ChevronUp className="h-3.5 w-3.5" />
                {translateUi("Collapse")}
              </button>
            </div>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {reportingPreferences.sectionOrder.filter((section) => section !== "team" || hasAssessingTeam).map((section, index, sections) => {
              const visible = reportingPreferences.visibleSections.includes(section);
              return (
                <div
                  key={section}
                  draggable
                  onDragStart={() => setDraggedSection(section)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => dropReportingSection(section)}
                  onDragEnd={() => setDraggedSection(null)}
                  className={cn(
                    "flex items-center gap-2 rounded-xl border px-3 py-2.5 transition",
                    draggedSection === section && "opacity-50",
                    isDarkMode ? "border-slate-700 bg-slate-800/70" : "border-slate-200 bg-slate-50",
                  )}
                >
                  <GripVertical className={cn("h-4 w-4 shrink-0 cursor-grab", isDarkMode ? "text-slate-500" : "text-slate-400")} />
                  <label className={cn("flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm font-medium", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                    <input type="checkbox" checked={visible} onChange={() => toggleReportingSection(section)} className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500" />
                    <span className="truncate">{translateUi(REPORTING_SECTION_LABELS[section])}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => moveReportingSection(section, -1)}
                    disabled={index === 0}
                    className={cn("rounded-md px-1.5 py-1 text-xs disabled:opacity-30", isDarkMode ? "text-slate-300 hover:bg-slate-700" : "text-slate-500 hover:bg-white")}
                    aria-label={translateUi("Move section up")}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveReportingSection(section, 1)}
                      disabled={index === sections.length - 1}
                    className={cn("rounded-md px-1.5 py-1 text-xs disabled:opacity-30", isDarkMode ? "text-slate-300 hover:bg-slate-700" : "text-slate-500 hover:bg-white")}
                    aria-label={translateUi("Move section down")}
                  >
                    ↓
                  </button>
                </div>
              );
            })}
          </div>
          {!canCustomizeReporting && (
            <p className={cn("mt-3 text-xs", isDarkMode ? "text-amber-300" : "text-amber-700")}>{translateUi("Select a company to customise this view.")}</p>
          )}
        </section>
      )}

      {/* KPI cards */}
      {reportingPreferences.visibleSections.includes("summary") && (
      <section
        style={{ order: reportingPreferences.sectionOrder.indexOf("summary") }}
        className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
      >
        <DashboardGradientPaneGlows />
        <div className="relative z-10 mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><BarChart3 className="h-5 w-5" /></span>
          <div>
            <h2 className="text-base font-bold text-white">{translateUi("Headline metrics")}</h2>
            <p className="mt-0.5 text-xs text-slate-300">{translateUi("The current state of your workforce and assurance activity.")}</p>
          </div>
        </div>
        <div className="relative z-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {[
          { key: "total-incidents", label: 'Total incidents', value: totals.incidents, icon: AlertTriangle, color: 'bg-amber-500', text: 'text-amber-600' },
          { key: "open-incidents", label: translateUi("Open incidents"), value: totals.openIncidents, icon: FileWarning, color: 'bg-rose-500', text: 'text-rose-600' },
          { key: "total-employees", label: translateUi("Total employees"), value: totals.employees, icon: Users, color: 'bg-indigo-500', text: 'text-indigo-600' },
          { key: "active-employees", label: translateUi("Active employees"), value: totals.activeEmployees, icon: User, color: 'bg-emerald-500', text: 'text-emerald-600' },
          { key: "off-track", label: translateUi("Off track"), value: totals.offTrack, icon: Activity, color: 'bg-orange-500', text: 'text-orange-600' },
          complianceTile,
          ...(competencyTile ? [competencyTile] : []),
          ...(showSafetyBriefs
            ? [{ key: "safety-briefs", label: 'Safety briefs', value: totals.safetyBriefs ?? 0, icon: Shield, color: 'bg-emerald-600', text: 'text-emerald-600' }]
            : []),
        ].map((tile) => {
          const { key, label, value, icon: Icon, color } = tile;
          const onClick = (tile as { onClick?: () => void }).onClick;
          const clickable = Boolean((tile as { clickable?: boolean }).clickable);
          const locked = Boolean((tile as { locked?: boolean }).locked);
          const TileTag = clickable ? "button" : "div";
          return (
            <TileTag
              key={key}
              type={clickable ? "button" : undefined}
              onClick={onClick}
              className={cn(
                "rv-reporting-tile rounded-2xl border border-white/15 bg-white/[0.06] p-4 text-left shadow-sm backdrop-blur-sm transition",
                clickable ? "cursor-pointer hover:border-violet-300/40 hover:bg-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60" : "hover:bg-white/[0.1]",
              )}
              aria-label={clickable ? translateUi("View {{label}} details", { label: String(label) }) : undefined}
            >
              <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold tabular-nums text-white">{value}</p>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-300">{label}</p>
              {clickable ? (
                <p className="mt-2 text-[11px] font-medium text-violet-200/90">
                  {locked ? translateUi("Add-on · Tap to learn more") : translateUi("Tap to see how this is calculated")}
                </p>
              ) : null}
            </TileTag>
          );
        })}
        </div>
      </section>
      )}

      {reportingPreferences.visibleSections.includes("team") && hasAssessingTeam && (
        <section
          style={{ order: reportingPreferences.sectionOrder.indexOf("team") }}
          className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
        >
          <DashboardGradientPaneGlows />
          <div className="relative z-10">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><Users className="h-5 w-5" /></span>
                <div>
                  <h2 className="text-base font-bold text-white">{translateUi("My Team")}</h2>
                  <p className="mt-0.5 text-xs text-slate-300">{translateUi("Your assigned employees and their compliance position.")}</p>
                </div>
              </div>
              <span className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-semibold text-slate-200">
                {teamStats?.totalEmployees ?? 0} {translateUi("employees")}
              </span>
            </div>
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-sky-300/20 bg-sky-300/[0.08] px-3 py-2.5 text-xs text-sky-100">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-200" />
              <p><span className="font-semibold text-white">{translateUi("What does My Team mean?")}</span>{" "}{translateUi("My Team shows employees assigned to you as their assessing manager.")}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rv-reporting-tile rounded-2xl border border-white/15 bg-white/[0.06] p-4 shadow-sm backdrop-blur-sm">
                <div className="flex items-center gap-2 text-sky-200"><ShieldCheck className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">{translateUi("Compliance score")}</span></div>
                <p className={cn("mt-3 text-3xl font-bold tabular-nums", teamComplianceTone)}>{teamComplianceRate == null ? "—" : `${teamComplianceRate}%`}</p>
                <p className="mt-1 text-xs text-slate-400">{translateUi("Competencies currently in date")}</p>
              </div>
              {[
                { label: translateUi("Active"), value: teamStats?.totalActive ?? 0, tone: "text-emerald-300", icon: User },
                { label: translateUi("Overdue assessments"), value: teamStats?.overdueComp ?? 0, tone: "text-rose-300", icon: AlertTriangle },
                { label: translateUi("Assessments due soon"), value: teamStats?.dueSoonComp ?? 0, tone: "text-amber-300", icon: Clock },
                { label: translateUi("Off track"), value: teamStats?.offTrackCount ?? 0, tone: "text-orange-300", icon: Activity },
                { label: translateUi("Licences expired"), value: teamStats?.licensing?.expired ?? 0, tone: "text-rose-300", icon: IdCard },
                { label: translateUi("Licences due within 6 weeks"), value: teamStats?.licensing?.dueSoonSixWeeks ?? 0, tone: "text-red-300", icon: Clock },
                { label: translateUi("Licences due within 3 months"), value: teamStats?.licensing?.dueSoonThreeMonths ?? 0, tone: "text-amber-300", icon: Calendar },
                { label: translateUi("Medicals expired"), value: teamStats?.expiredMed ?? 0, tone: "text-rose-300", icon: Stethoscope },
                { label: translateUi("Medicals due within 30 days"), value: teamStats?.dueSoonMed ?? 0, tone: "text-amber-300", icon: Calendar },
              ].map(({ label, value, tone, icon: Icon }) => (
                <div key={label} className="rv-reporting-tile rounded-2xl border border-white/15 bg-white/[0.06] p-4 shadow-sm backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-slate-300"><Icon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">{label}</span></div>
                  <p className={cn("mt-3 text-3xl font-bold tabular-nums", tone)}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {reportingPreferences.visibleSections.includes("licensing") && (
        <section
          style={{ order: reportingPreferences.sectionOrder.indexOf("licensing") }}
          className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
        >
          <DashboardGradientPaneGlows />
          <div className="relative z-10">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><IdCard className="h-5 w-5" /></span>
                <div>
                  <h2 className="text-base font-bold text-white">{translateUi("Licencing readiness")}</h2>
                  <p className="mt-0.5 text-xs text-slate-300">{translateUi("Active staff expiry outlook, including upcoming renewals.")}</p>
                </div>
              </div>
              <span className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-semibold text-slate-200">
                {licensing.recorded ?? 0}/{licensing.activeEmployees ?? 0} {translateUi("recorded")}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {[
                { label: translateUi("Expired"), value: licensing.expired ?? 0, tone: "text-rose-300", icon: AlertTriangle },
                { label: translateUi("Due within 6 weeks"), value: licensing.dueSoonSixWeeks ?? 0, tone: "text-red-300", icon: Clock },
                { label: translateUi("Due within 3 months"), value: licensing.dueSoonThreeMonths ?? 0, tone: "text-amber-300", icon: Calendar },
                { label: translateUi("Valid"), value: licensing.valid ?? 0, tone: "text-emerald-300", icon: CheckCircle2 },
                { label: translateUi("Missing expiry"), value: licensing.missing ?? 0, tone: "text-slate-300", icon: FileWarning },
              ].map(({ label, value, tone, icon: Icon }) => (
                <div key={label} className="rv-reporting-tile rounded-xl border border-white/15 bg-white/[0.06] p-3 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-2">
                    <Icon className={cn("h-4 w-4", tone)} />
                    <span className={cn("text-2xl font-bold tabular-nums", tone)}>{value}</span>
                  </div>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-300">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-slate-950/25 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">{translateUi("Priority renewals")}</p>
                {licenceAttention.length > 0 ? <span className="text-[11px] text-slate-400">{translateUi("Showing nearest expiries")}</span> : null}
              </div>
              {licenceAttention.length === 0 ? (
                <p className="py-2 text-sm text-emerald-200">{translateUi("No active licences require attention in the next three months.")}</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {licenceAttention.map((row) => (
                    <div key={`${row.id}-${row.licence_expiry}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{row.full_name || row.id}</p>
                        <p className="truncate text-xs text-slate-400">{[row.role, row.depot].filter(Boolean).join(" · ") || translateUi("No role or depot recorded")}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={cn("text-xs font-semibold", row.urgency === "expired" ? "text-rose-300" : row.urgency === "dueSoonSixWeeks" ? "text-red-300" : "text-amber-300")}>
                          {licenceUrgencyLabel(row.urgency)}
                        </p>
                        <p className="text-[11px] text-slate-400">{formatLicenceDate(row.licence_expiry)}{row.days != null ? ` · ${row.days < 0 ? `${Math.abs(row.days)}d overdue` : `${row.days}d`}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Secondary stats */}
      {reportingPreferences.visibleSections.includes("health") && (
      <section
        style={{ order: reportingPreferences.sectionOrder.indexOf("health") }}
        className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
      >
        <DashboardGradientPaneGlows />
        <div className="relative z-10 mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><Shield className="h-5 w-5" /></span>
          <div>
            <h2 className="text-base font-bold text-white">{translateUi("Compliance health")}</h2>
            <p className="mt-0.5 text-xs text-slate-300">{translateUi("Exceptions and supporting indicators across the system.")}</p>
          </div>
        </div>
      <div className="relative z-10 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {[
          ...(showQaCompliance
            ? [
                {
                  label: "Competencies in date",
                  value: competencyInDateRate != null ? `${competencyInDateRate}%` : "—",
                },
              ]
            : []),
          { label: 'Overdue competencies', value: stats?.overdueComp ?? 0 },
          { label: 'Late assessments', value: totals.lateAssessments ?? 0 },
          { label: 'Overdue assessments', value: totals.overdueAssessments ?? 0 },
          { label: 'Due soon (competencies)', value: stats?.dueSoonComp ?? 0 },
          { label: 'Expired medicals', value: totals.expiredMedicals ?? stats?.expiredMed ?? 0 },
          { label: 'Due soon (medicals)', value: stats?.dueSoonMed ?? 0 },
          { label: 'Valid medical records', value: totals.validMedicals ?? '—' },
          { label: 'Medication checks', value: totals.totalMedicationChecks ?? '—' },
          ...(showSafetyBriefs
            ? [
                { label: 'Employees with safety briefs', value: totals.employeesWithSafetyBriefs ?? 0 },
                { label: 'Safety brief coverage', value: `${safetyBriefCoverageRate}%`, isCoverage: true },
              ]
            : []),
        ].map(({ label, value, isCoverage }) => (
          <div key={label} className="rv-reporting-tile rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 backdrop-blur-sm">
            <p className="text-lg font-semibold tabular-nums text-white">{value}</p>
            {isCoverage ? (
              <span className={cn("mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", coverageTone)}>
                {safetyBriefCoverageRate >= 90 ? translateUi("High") : safetyBriefCoverageRate >= 70 ? translateUi("Medium") : translateUi("Low")}
              </span>
            ) : null}
            <p className="text-xs text-slate-300">{label}</p>
          </div>
        ))}
      </div>
      </section>
      )}

      {/* Incident statistics and remaining charts */}
      {(showIncidentStats || showVisualsCharts) && (
      <>
      {(() => {
        const barData = (items: { name: string; count: number }[] | undefined, maxCount?: number) => {
          const list = items?.filter((x) => x.count > 0) ?? [];
          const m = maxCount ?? Math.max(1, ...list.map((x) => x.count));
          return { list, max: m };
        };
        const trendData = (items: { month: string; count: number }[] | undefined) => {
          const list = items ?? [];
          const m = Math.max(1, ...list.map((x) => x.count));
          return { list, max: m };
        };
        const monthPartFromKey = (monthKey: string): string | null => {
          return trendBucketMergeKey(monthKey);
        };
        const aggregateTrendAcrossYears = (items: { month: string; count: number }[] | undefined) => {
          const rows = items ?? [];
          if (selectedTrendYears.length <= 1) return rows;
          const monthTotals = new Map<string, number>();
          const order: string[] = [];
          for (const row of rows) {
            const monthPart = monthPartFromKey(row.month);
            if (!monthPart) continue;
            if (!monthTotals.has(monthPart)) order.push(monthPart);
            monthTotals.set(monthPart, (monthTotals.get(monthPart) ?? 0) + row.count);
          }
          return order.map((month) => ({ month, count: monthTotals.get(month) ?? 0 }));
        };
        const aggregateTrendByTypeAcrossYears = (items: { type: string; month: string; count: number }[] | undefined) => {
          const rows = items ?? [];
          if (selectedTrendYears.length <= 1) return rows;
          const grouped = new Map<string, number>();
          for (const row of rows) {
            const monthPart = monthPartFromKey(row.month);
            if (!monthPart) continue;
            const type = String(row.type ?? "").trim() || "Unknown";
            const key = `${type}::${monthPart}`;
            grouped.set(key, (grouped.get(key) ?? 0) + row.count);
          }
          return Array.from(grouped.entries()).map(([key, count]) => {
            const [type, monthPart] = key.split("::");
            return { type, month: monthPart, count };
          });
        };
        const formatTrendMonth = (monthKey: string) => formatTrendBucketLabel(monthKey);
        const renderBars = (list: { name: string; count: number }[], max: number) => (
          <div className="space-y-2">
            {list.length === 0 ? (
              <p className="text-slate-400 text-sm py-4 text-center">{translateUi("No data")}</p>
            ) : (
              list.map(({ name, count }) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="w-32 shrink-0 truncate text-sm text-slate-200" title={name}>{name}</span>
                  <div className="flex h-6 flex-1 overflow-hidden rounded bg-white/10">
                    <div className="h-full rounded bg-sky-400" style={{ width: `${(count / max) * 100}%`, minWidth: count > 0 ? '4px' : 0 }} />
                  </div>
                  <span className="w-8 text-right text-sm font-medium tabular-nums text-white">{count}</span>
                </div>
              ))
            )}
          </div>
        );
        const renderIncidentTypePie = (list: { name: string; count: number }[]) => {
          if (list.length === 0) {
            return <p className="text-slate-400 text-sm py-4 text-center">{translateUi("No data")}</p>;
          }
          const total = list.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
          const piePalette = [
            "#3b82f6",
            "#f59e0b",
            "#10b981",
            "#ef4444",
            "#8b5cf6",
            "#14b8a6",
            "#f97316",
            "#ec4899",
            "#22c55e",
            "#6366f1",
          ];
          const slices = list.map((row, idx) => {
            const count = Number(row.count) || 0;
            return {
              ...row,
              count,
              color: piePalette[idx % piePalette.length],
              pct: total > 0 ? (count / total) * 100 : 0,
            };
          });
          let runningPct = 0;
          const gradientStops = slices
            .map((slice) => {
              const start = runningPct;
              runningPct += slice.pct;
              return `${slice.color} ${start}% ${runningPct}%`;
            })
            .join(", ");
          const centerBg = "bg-slate-950";
          const centerText = "text-white";
          const subText = "text-slate-400";
          const rowText = "text-slate-200";

          return (
            <div className="h-full min-h-0">
              <div className="grid h-full min-h-0 grid-cols-1 gap-4 md:grid-cols-[auto_minmax(0,1fr)] md:items-start">
                <div className="flex justify-center md:justify-start">
                  <div className="relative h-44 w-44 rounded-full" style={{ background: `conic-gradient(${gradientStops})` }}>
                    <div className={cn("absolute inset-[24%] rounded-full border", centerBg, isDarkMode ? "border-slate-700" : "border-slate-200")} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={cn("text-xl font-bold tabular-nums", centerText)}>{total}</span>
                      <span className={cn("text-[10px] uppercase tracking-wider", subText)}>incidents</span>
                    </div>
                  </div>
                </div>
                <div className="h-full min-h-0 overflow-y-auto pr-1">
                  <div className="space-y-2">
                    {slices.map((slice) => (
                      <div key={slice.name} className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: slice.color }} aria-hidden />
                          <span className={cn("truncate text-sm", rowText)} title={slice.name}>
                            {slice.name}
                          </span>
                        </div>
                        <span className={cn("shrink-0 text-sm font-medium tabular-nums", centerText)}>
                          {slice.count} ({slice.pct.toFixed(1)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        };
        const renderTrendWithAxes = (
          list: { month: string; count: number }[],
          max: number,
          trendByType: { type: string; month: string; count: number }[] | undefined,
          typeBreakdown: { name: string; count: number }[],
        ) => {
          if (list.length === 0) {
            return <p className="text-slate-400 text-sm py-4 text-center">{translateUi("No data")}</p>;
          }

          const yTickSteps = 4;
          const axisColor = "bg-slate-600";
          const gridColor = "border-slate-700";
          const tickText = "text-slate-400";
          const monthText = "text-slate-300";
          const valueText = "text-slate-200";
          const typePalette = [
            "#f59e0b", // amber
            "#3b82f6", // blue
            "#10b981", // emerald
            "#ef4444", // red
            "#8b5cf6", // violet
            "#14b8a6", // teal
            "#f97316", // orange
            "#ec4899", // pink
          ];
          const orderedTypes = Array.from(
            new Set([
              ...typeBreakdown.map((row) => row.name).filter(Boolean),
              ...(trendByType ?? []).map((row) => row.type).filter(Boolean),
            ]),
          );
          const typeColor = new Map<string, string>(
            orderedTypes.map((type, idx) => [type, typePalette[idx % typePalette.length]]),
          );
          const monthTypeCounts = new Map<string, Map<string, number>>();
          for (const row of trendByType ?? []) {
            if (!row.month || !row.type) continue;
            const perMonth = monthTypeCounts.get(row.month) ?? new Map<string, number>();
            perMonth.set(row.type, (perMonth.get(row.type) ?? 0) + row.count);
            monthTypeCounts.set(row.month, perMonth);
          }
          const adjustedList = list.map((row) => {
            const perType = monthTypeCounts.get(row.month);
            const summed = perType ? Array.from(perType.values()).reduce((acc, n) => acc + n, 0) : 0;
            return { month: row.month, count: Math.max(row.count, summed) };
          });
          const maxFromAdjusted = Math.max(1, ...adjustedList.map((row) => row.count));
          const chartMax = Math.max(max, maxFromAdjusted);
          const tickStep = Math.max(1, Math.ceil(chartMax / yTickSteps));
          const yTicks: number[] = [];
          for (let value = 0; value <= chartMax; value += tickStep) {
            yTicks.push(value);
          }
          if (yTicks[yTicks.length - 1] !== chartMax) yTicks.push(chartMax);

          return (
            <div className="h-full px-1" style={{ minWidth: `${Math.max(620, list.length * 48)}px` }}>
              <div className="relative h-full pb-10 pl-11">
                {yTicks.map((tick) => {
                  const y = (tick / chartMax) * 100;
                  return (
                    <div
                      key={`tick-${tick}`}
                      className="absolute left-0 right-0"
                      style={{ bottom: `${y}%` }}
                    >
                      <span className={cn("absolute right-full mr-2 -translate-y-1/2 text-[11px] tabular-nums", tickText)}>
                        {tick}
                      </span>
                      <div className={cn("border-t border-dashed", gridColor)} />
                    </div>
                  );
                })}

                <div className={cn("absolute left-0 bottom-0 top-0 w-px", axisColor)} />
                <div className={cn("absolute left-0 right-0 bottom-0 h-px", axisColor)} />

                <div className="absolute inset-0 flex items-end justify-between gap-2 pt-2">
                  {adjustedList.map(({ month, count }) => {
                    const monthLabel = formatTrendMonth(month);
                    const perType = monthTypeCounts.get(month) ?? new Map<string, number>();
                    let stackedCount = 0;
                    return (
                      <div key={month} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-2">
                        <span className={cn("text-[11px] font-semibold tabular-nums", valueText)}>{count}</span>
                        <div className="relative flex-1 w-full max-w-10 min-h-[12px] overflow-hidden rounded-t-sm bg-slate-200/35">
                          {orderedTypes.length === 0 && count > 0 && chartMax > 0 ? (
                            <div
                              className="absolute left-0 right-0 bg-amber-500"
                              style={{ bottom: "0%", height: `${Math.max(1.5, (count / chartMax) * 100)}%` }}
                              title={`${monthLabel}: ${count}`}
                              aria-label={translateUi("{{month}}: {{count}} incidents", { month: monthLabel, count })}
                            />
                          ) : null}
                          {orderedTypes.map((type) => {
                            const value = perType.get(type) ?? 0;
                            if (value <= 0 || chartMax <= 0) return null;
                            const bottomPct = (stackedCount / chartMax) * 100;
                            const heightPct = Math.max(1.5, (value / chartMax) * 100);
                            stackedCount += value;
                            return (
                              <div
                                key={`${month}-${type}`}
                                className="absolute left-0 right-0"
                                style={{
                                  bottom: `${bottomPct}%`,
                                  height: `${heightPct}%`,
                                  backgroundColor: typeColor.get(type),
                                }}
                                title={`${monthLabel}: ${type} (${value})`}
                                aria-label={`${monthLabel}: ${type} (${value})`}
                              />
                            );
                          })}
                        </div>
                        <span className={cn("w-full truncate text-center text-[11px]", monthText)} title={monthLabel}>
                          {monthLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        };
        const renderTrendRows = (list: { month: string; count: number }[], max: number) => (
          <div className="space-y-2">
            {list.length === 0 ? (
              <p className="text-slate-400 text-sm py-4 text-center">{translateUi("No data")}</p>
            ) : (
              list.map(({ month, count }) => (
                <div key={month} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 truncate text-sm text-slate-200">{formatTrendMonth(month)}</span>
                  <div className="flex h-6 flex-1 overflow-hidden rounded bg-white/10">
                    <div className="h-full rounded bg-amber-400" style={{ width: `${(count / max) * 100}%`, minWidth: count > 0 ? '4px' : 0 }} />
                  </div>
                  <span className="w-8 text-right text-sm font-medium tabular-nums text-white">{count}</span>
                </div>
              ))
            )}
          </div>
        );
        const incidentPanes: { title: string; content: React.ReactNode }[] = [
          {
            title: 'Incidents over time',
            content: (() => {
              const trendRows = aggregateTrendAcrossYears(analytics.incidentsTrend);
              const byTypeRows = aggregateTrendByTypeAcrossYears(analytics.incidentsTrendByType);
              const { list, max } = trendData(trendRows);
              return renderTrendWithAxes(list, max, byTypeRows, analytics.incidentsByType);
            })(),
          },
          { title: 'Incidents by type', content: (() => { const { list } = barData(analytics.incidentsByType); return renderIncidentTypePie(list); })() },
        ];
        const otherPanes: { title: string; content: React.ReactNode }[] = [
          { title: 'Monitoring status', content: (() => { const { list, max } = barData(analytics.monitoringBreakdown); return renderBars(list, max); })() },
          { title: 'Late assessments by event', content: (() => { const { list, max } = barData(analytics.lateAssessmentsByEvent ?? []); return renderBars(list, max); })() },
          { title: 'Late assessments over time', content: (() => { const { list, max } = trendData(aggregateTrendAcrossYears(analytics.lateAssessmentsTrend ?? [])); return renderTrendRows(list, max); })() },
          { title: 'Employees by role', content: (() => { const { list, max } = barData(analytics.roleBreakdown); return renderBars(list, max); })() },
          { title: 'Medicals by status', content: (() => { const { list, max } = barData(analytics.medicalsByStatus ?? analytics.statusBreakdown); return renderBars(list, max); })() },
          { title: 'Medication checks over time', content: (() => { const { list, max } = trendData(aggregateTrendAcrossYears(analytics.medicationChecksTrend)); return renderTrendRows(list, max); })() },
          ...(showSafetyBriefs
            ? [
                { title: 'Safety briefs by name', content: (() => { const { list, max } = barData(analytics.safetyBriefsByName); return renderBars(list, max); })() },
                { title: 'Safety briefs by delivery', content: (() => { const { list, max } = barData(analytics.safetyBriefsByMethod); return renderBars(list, max); })() },
                { title: 'Safety briefs over time', content: (() => { const { list, max } = trendData(aggregateTrendAcrossYears(analytics.safetyBriefsTrend)); return renderTrendRows(list, max); })() },
              ]
            : []),
        ];
        const renderChartGrid = (panes: { title: string; content: React.ReactNode }[]) => (
          <div className="relative">
            {chartsRefreshing && (
              <div className="absolute inset-0 z-20 flex items-start justify-center rounded-2xl bg-slate-950/35 pt-16 backdrop-blur-[1px]">
                <div className="flex items-center gap-2 rounded-full bg-slate-900/95 px-3 py-1.5 text-xs font-semibold text-sky-200 ring-1 ring-white/20">
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sky-300 border-t-transparent" />
                  {translateUi("Updating charts…")}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {panes.map(({ title, content }) => (
              <div key={title} className="rv-reporting-tile rounded-2xl border border-white/15 bg-white/[0.06] p-5 shadow-sm backdrop-blur-sm">
                <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-200">{title}</h3>
                <div className="h-64 overflow-auto rounded-xl bg-slate-950/20 p-3">
                  {content}
                </div>
              </div>
            ))}
            </div>
          </div>
        );
        return (
          <>
            {showIncidentStats && (
              <section
                style={{ order: reportingPreferences.sectionOrder.indexOf("incidentStats") }}
                className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
              >
                <DashboardGradientPaneGlows />
                <div className="relative z-10">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><BarChart3 className="h-5 w-5" /></span>
                    <div>
                      <h2 className="text-base font-bold text-white">{translateUi("Incident Data Statistics")}</h2>
                      <p className="mt-0.5 text-xs text-slate-300">{translateUi("Incidents over time and by type for the selected financial year, period, and week.")}</p>
                    </div>
                  </div>
                  {reportingChartFilters}
                  {renderChartGrid(incidentPanes)}
                </div>
              </section>
            )}
            {showVisualsCharts && (
              <section
                style={{ order: reportingPreferences.sectionOrder.indexOf("visuals") }}
                className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "rv-reporting-reveal")}
              >
                <DashboardGradientPaneGlows />
                <div className="relative z-10">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-sky-200 ring-1 ring-white/20"><Activity className="h-5 w-5" /></span>
                    <div>
                      <h2 className="text-base font-bold text-white">{translateUi("Charts and trends")}</h2>
                      <p className="mt-0.5 text-xs text-slate-300">{translateUi("Workforce, assessment, medical and safety-brief patterns.")}</p>
                    </div>
                  </div>
                  {!showIncidentStats ? reportingChartFilters : null}
                  {renderChartGrid(otherPanes)}
                </div>
              </section>
            )}
          </>
        );
      })()}
      </>
      )}
    </div>
  );
};

function normalizeIncidentSeverityUi(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (s === "Level 1" || s === "Level 2" || s === "Level 3") return s;
  const lower = s.toLowerCase();
  if (lower === "low") return "Level 1";
  if (lower === "medium") return "Level 2";
  if (lower === "high") return "Level 3";
  return s || "Level 2";
}

function incidentSeverityChipClass(severity: unknown, dark = false): string {
  const s = normalizeIncidentSeverityUi(severity);
  if (s === "Level 3" || s === "High") return dark ? "bg-red-500/20 text-red-200" : "bg-red-100 text-red-700";
  if (s === "Level 2" || s === "Medium") return dark ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-700";
  return dark ? "bg-blue-500/20 text-blue-200" : "bg-blue-100 text-blue-700";
}

function incidentTrainInvolved(raw: unknown): boolean {
  if (raw === true || raw === 1) return true;
  const s = String(raw ?? "").trim().toLowerCase();
  return s === "1" || s === "yes" || s === "true";
}

function incidentEvidenceFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

const INCIDENT_SEVERITY_LEVELS = ["Level 1", "Level 2", "Level 3"] as const;
const INCIDENT_OTDR_CHOICES = ["Obtained", "Requested"] as const;

const IncidentManagement = ({ currentUserTitle, currentUserEmail, isAdmin, isManager, companyCode, operationalEditUnlocked = true, isDarkMode = false }: { currentUserTitle?: string, currentUserEmail?: string, isAdmin: boolean, isManager: boolean, companyCode?: string, operationalEditUnlocked?: boolean, isDarkMode?: boolean }) => {
  const incidentTenantCode = (row?: { company_code?: string; employee_id?: string }) => {
    const fromProp = companyCode?.trim() || "";
    const fromRow = row?.company_code != null ? String(row.company_code).trim() : "";
    const emp =
      row?.employee_id != null ? employees.find((e) => String(e.id ?? "") === String(row.employee_id)) : undefined;
    const fromEmp =
      emp && (emp as { company_code?: string }).company_code != null
        ? String((emp as { company_code?: string }).company_code).trim()
        : "";
    return (fromProp || fromRow || fromEmp || "").trim();
  };
  const incParams = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
  const incidentWriteHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
    ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
  }), [currentUserEmail, companyCode]);
  const mutationQueryAndHeaders = (row?: { company_code?: string }) => {
    const code = incidentTenantCode(row);
    const q = code ? `?companyCode=${encodeURIComponent(code)}` : "";
    return {
      query: q,
      headers: {
        'Content-Type': 'application/json',
        ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
        ...(code ? { 'X-Company-Code': code } : {}),
      } as Record<string, string>,
    };
  };
  const canMutateIncidents = operationalEditUnlocked && (isAdmin || isManager);
  const canDeleteIncidents = operationalEditUnlocked && isAdmin;
  const [incidents, setIncidents] = useState<any[]>([]);
  const [incidentError, setIncidentError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<any | null>(null);
  const [viewingIncident, setViewingIncident] = useState<any | null>(null);
  const [confirmState, setConfirmState] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const employeePickerRef = useRef<HTMLDivElement | null>(null);
  const evidenceInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingEvidence, setPendingEvidence] = useState<File[]>([]);
  const otdrFileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingOtdrFiles, setPendingOtdrFiles] = useState<File[]>([]);
  const [evidencePreviews, setEvidencePreviews] = useState<Record<string, string>>({});
  const [savingIncident, setSavingIncident] = useState(false);
  const emptyIncidentForm = () => ({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    type: "Collision",
    description: '',
    severity: "Level 2",
    action_taken: '',
    train_involved: false,
    headcode: '',
    vehicle_numbers: '',
    otdr_download: '',
    created_by: currentUserTitle || 'Admin User',
    last_updated_by: currentUserTitle || 'Admin User'
  });
  const [formData, setFormData] = useState(emptyIncidentForm);

  useEffect(() => {
    const params = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
    fetch(`/api/employees${params}`, { cache: 'no-store', headers: { ...incidentWriteHeaders } })
      .then(res => res.json())
      .then((data) => setEmployees(Array.isArray(data) ? data : []));
  }, [companyCode, incidentWriteHeaders]);

  useEffect(() => {
    if (editingIncident) {
      setFormData({
        employee_id: editingIncident.employee_id || '',
        date: editingIncident.date,
        type: editingIncident.type,
        description: editingIncident.description,
        severity: normalizeIncidentSeverityUi(editingIncident.severity),
        action_taken: editingIncident.action_taken || '',
        train_involved: incidentTrainInvolved(editingIncident.train_involved),
        headcode: editingIncident.headcode || '',
        vehicle_numbers: editingIncident.vehicle_numbers || '',
        otdr_download: editingIncident.otdr_download || '',
        created_by: editingIncident.created_by,
        last_updated_by: currentUserTitle || 'Admin User'
      });
      setPendingEvidence([]);
      setEmployeeSearch("");
      setEmployeePickerOpen(false);
      setShowForm(true);
    } else {
      setFormData(emptyIncidentForm());
      setPendingEvidence([]);
      setEmployeeSearch("");
      setEmployeePickerOpen(false);
    }
  }, [editingIncident]);

  /** English stored values — translate labels at render only. */
  const incidentTypes = [
    'Collision',
    'Derailment',
    'Dispatch',
    'Fail to Call',
    'Fatality',
    'Near Miss',
    'Other',
    'Personal Injury',
    'SPAD Type A1',
    'SPAD Type A2',
    'SPAD Type A3',
    'SPAD Type A4',
    'SPAD Type B1',
    'Speeding',
    'Station Overrun',
    'Station Stop Short',
    'TPWS Activation',
    'TPWS Intervention',
    'Wrong Route (Taken)',
    'Wrongside Door Release',
  ].sort((a, b) => a.localeCompare(b));

  const fetchIncidents = () => {
    setLoading(true);
    setIncidentError(null);
    fetch(`/api/incidents${incParams}`, { headers: incidentWriteHeaders })
      .then(async (res) => {
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          const msg = typeof data === "object" && data && "error" in data ? String((data as { error?: string }).error || "") : "";
          throw new Error(msg || `Failed (${res.status})`);
        }
        return data;
      })
      .then(data => {
        setIncidents(data);
        setLoading(false);
      })
      .catch((err: any) => {
        setIncidents([]);
        setIncidentError(normalizeAccessError(err?.message, "Failed to load incidents."));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchIncidents();
  }, [companyCode]);

  const tenantEmployees = useMemo(() => {
    const code = (companyCode || "").trim().toLowerCase();
    return (Array.isArray(employees) ? employees : [])
      .filter((emp) => {
        if (!code) return true;
        const empCode = String((emp as { company_code?: string }).company_code ?? "").trim().toLowerCase();
        return !empCode || empCode === code;
      })
      .sort((a, b) =>
        String(a.full_name || "").localeCompare(String(b.full_name || ""), undefined, { sensitivity: "base" }),
      );
  }, [employees, companyCode]);

  const selectedIncidentEmployee = useMemo(
    () => tenantEmployees.find((emp) => String(emp.id) === String(formData.employee_id)) || null,
    [tenantEmployees, formData.employee_id]
  );

  const filteredEmployeeOptions = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return tenantEmployees;
    return tenantEmployees.filter((emp) => {
      const name = String(emp.full_name || "").toLowerCase();
      const id = String(emp.id || "").toLowerCase();
      const role = String(emp.role || "").toLowerCase();
      return name.includes(q) || id.includes(q) || role.includes(q);
    });
  }, [tenantEmployees, employeeSearch]);

  useEffect(() => {
    if (!employeePickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!employeePickerRef.current?.contains(e.target as Node)) setEmployeePickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEmployeePickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [employeePickerOpen]);

  useEffect(() => {
    const source = viewingIncident || editingIncident;
    const list = Array.isArray(source?.evidence) ? source.evidence : [];
    if (!source?.id || list.length === 0) {
      setEvidencePreviews({});
      return;
    }
    let cancelled = false;
    const headers: Record<string, string> = {
      ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
      ...(companyCode ? { "X-Company-Code": companyCode } : {}),
    };
    const q = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : "";
    const wanted = new Set(list.map((ev: { id: string | number }) => String(ev.id)));
    setEvidencePreviews((prev) => {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (wanted.has(k)) next[k] = v;
      }
      return next;
    });
    (async () => {
      for (const ev of list as Array<{ id: string | number; content_type?: string }>) {
        const id = String(ev.id);
        try {
          const res = await fetch(`/api/incidents/${source.id}/evidence/${ev.id}/download${q}`, { headers });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || cancelled) continue;
          const content = String((data as { content?: string }).content || "");
          const type = String((data as { content_type?: string }).content_type || ev.content_type || "image/jpeg");
          if (content) {
            const url = `data:${type};base64,${content}`;
            setEvidencePreviews((prev) => (prev[id] ? prev : { ...prev, [id]: url }));
          }
        } catch {
          /* skip broken file */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewingIncident, editingIncident, currentUserEmail, companyCode]);

  const closeIncidentForm = () => {
    setShowForm(false);
    setEditingIncident(null);
    setPendingEvidence([]);
    setPendingOtdrFiles([]);
    setEmployeeSearch("");
    setEmployeePickerOpen(false);
  };

  const dropIncidentEvidenceFromState = (incidentId: string | number, evidenceId: string | number) => {
    const nextEvidence = (inc: any) => {
      if (!inc || String(inc.id) !== String(incidentId)) return inc;
      const evidence = Array.isArray(inc.evidence)
        ? inc.evidence.filter((ev: { id?: string | number }) => String(ev.id) !== String(evidenceId))
        : [];
      return { ...inc, evidence };
    };
    setIncidents((prev) => prev.map(nextEvidence));
    setEditingIncident((prev) => (prev ? nextEvidence(prev) : prev));
    setViewingIncident((prev) => (prev ? nextEvidence(prev) : prev));
    setEvidencePreviews((prev) => {
      const next = { ...prev };
      delete next[String(evidenceId)];
      return next;
    });
  };

  const handleDeleteIncidentEvidence = async (incident: { id?: string | number; company_code?: string }, evidenceId: string | number) => {
    if (incident?.id == null) return;
    try {
      const { query, headers } = mutationQueryAndHeaders(incident);
      const res = await fetch(`/api/incidents/${incident.id}/evidence/${evidenceId}${query || incParams}`, {
        method: "DELETE",
        headers: { ...headers },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || `Failed (${res.status})`);
      dropIncidentEvidenceFromState(incident.id, evidenceId);
    } catch (err) {
      console.error(err);
      setIncidentError(normalizeAccessError((err as Error)?.message, translateUi("Failed to delete evidence.")));
    }
  };

  const confirmDeleteIncidentEvidence = (incident: { id?: string | number; company_code?: string }, evidenceId: string | number) => {
    setConfirmState({
      isOpen: true,
      title: translateUi("Remove photo"),
      message: translateUi("Are you sure you want to remove the attached evidence?"),
      onConfirm: () => {
        setConfirmState(null);
        void handleDeleteIncidentEvidence(incident, evidenceId);
      },
    });
  };

  const uploadIncidentEvidenceFiles = async (incidentId: string | number, files: File[]) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
      ...(companyCode ? { "X-Company-Code": companyCode } : {}),
    };
    const q = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : "";
    for (const file of files) {
      const content = await incidentEvidenceFileToBase64(file);
      const res = await fetch(`/api/incidents/${incidentId}/evidence${q}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: file.name,
          content_type: file.type || "application/octet-stream",
          content,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || `Failed to upload ${file.name}`);
    }
  };

  const persistIncident = async (): Promise<{ id: string | number; company_code?: string }> => {
    if (!formData.employee_id) {
      throw new Error(translateUi("Select Employee"));
    }
    if (formData.train_involved && !formData.otdr_download) {
      throw new Error(translateUi("Select whether an OTDR download was obtained or requested."));
    }
    const payload = {
      ...formData,
      train_involved: formData.train_involved ? 1 : 0,
      headcode: formData.train_involved ? formData.headcode : "",
      vehicle_numbers: formData.train_involved ? formData.vehicle_numbers : "",
      otdr_download: formData.train_involved ? formData.otdr_download : "",
    };
    const patchCtx = editingIncident ? mutationQueryAndHeaders(editingIncident) : null;
    const url = editingIncident
      ? `/api/incidents/${editingIncident.id}${patchCtx?.query ?? incParams}`
      : "/api/incidents";
    const method = editingIncident ? "PATCH" : "POST";
    const res = await fetch(url, {
      method,
      headers: editingIncident ? (patchCtx?.headers ?? incidentWriteHeaders) : incidentWriteHeaders,
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || `Failed (${res.status})`);
    const savedId = editingIncident?.id ?? (data as { id?: string | number }).id;
    if (savedId == null) throw new Error(translateUi("Failed to save incident."));
    const uploads = [...pendingEvidence, ...pendingOtdrFiles];
    if (uploads.length > 0) {
      await uploadIncidentEvidenceFiles(savedId, uploads);
    }
    return { id: savedId, company_code: editingIncident?.company_code || companyCode };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingIncident(true);
    try {
      await persistIncident();
      closeIncidentForm();
      fetchIncidents();
    } catch (err) {
      console.error(err);
      setIncidentError(normalizeAccessError((err as Error)?.message, "Failed to save incident."));
    } finally {
      setSavingIncident(false);
    }
  };

  const handleDelete = async (id: number | string, row?: { company_code?: string }) => {
    try {
      const { query, headers } = mutationQueryAndHeaders(row);
      const res = await fetch(`/api/incidents/${id}${query || incParams}`, { method: 'DELETE', headers: { ...headers } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || `Failed (${res.status})`);
      fetchIncidents();
    } catch (err) {
      console.error(err);
      setIncidentError(normalizeAccessError((err as Error)?.message, "Failed to delete incident."));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  if (viewingIncident) {
    return (
      <div className="space-y-6">
        {incidentError && (
          <div
            className={cn(
              "rounded-xl border px-4 py-3 text-sm font-medium",
              isDarkMode ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"
            )}
          >
            {incidentError}
          </div>
        )}
        {confirmState && (
          <ConfirmDialog 
            isOpen={confirmState.isOpen}
            title={confirmState.title}
            message={confirmState.message}
            onConfirm={confirmState.onConfirm}
            onCancel={() => setConfirmState(null)}
          />
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setViewingIncident(null)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{translateUi("Incident Details")}</h2>
              <p className="text-slate-500">{translateUi("View complete incident report")}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {canMutateIncidents && (
              <button 
                onClick={() => {
                  setEditingIncident(viewingIncident);
                  setViewingIncident(null);
                }}
                className="flex items-center px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                <Edit2 className="w-4 h-4 mr-2 text-slate-400" /> {translateUi("Edit")} </button>
            )}
            {canDeleteIncidents && (
              <button 
                onClick={() => {
                  setConfirmState({
                    isOpen: true,
                    title: translateUi("Delete Incident"),
                    message: translateUi("Are you sure you want to delete this incident record? This action cannot be undone."),
                    onConfirm: () => {
                      handleDelete(viewingIncident.id, viewingIncident);
                      setViewingIncident(null);
                      setConfirmState(null);
                    }
                  });
                }}
                className="flex items-center px-4 py-2 bg-white border border-red-100 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2 text-red-400" /> {translateUi("Delete")} </button>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Employee")}</h3>
                <p className="text-slate-900 font-medium">{viewingIncident.employee_name || viewingIncident.employee_id}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Employee ID")}</h3>
                <p className="text-slate-900 font-medium">{viewingIncident.employee_id}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Date")}</h3>
                <p className="text-slate-900 font-medium">
                  {formatDateUK(viewingIncident.date)}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Type")}</h3>
                <p className="text-slate-900 font-medium">{translateUi(String(viewingIncident.type || '—'))}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1"> {translateUi("Severity")} </h3>
                <span className={cn(
                  "px-2.5 py-0.5 rounded-full text-xs font-medium inline-block",
                  incidentSeverityChipClass(viewingIncident.severity)
                )}>{translateUi(normalizeIncidentSeverityUi(viewingIncident.severity))}</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1"> {translateUi("Status")} </h3>
                <p className="text-slate-900 font-medium">{translateUi(String(viewingIncident.status || '—'))}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Created By")}</h3>
                <p className="text-slate-900 font-medium">{viewingIncident.created_by}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Train involved")}</h3>
                <p className="text-slate-900 font-medium">{incidentTrainInvolved(viewingIncident.train_involved) ? translateUi("Yes") : translateUi("No")}</p>
              </div>
              {incidentTrainInvolved(viewingIncident.train_involved) && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Headcode")}</h3>
                    <p className="text-slate-900 font-medium">{viewingIncident.headcode || "—"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Vehicle number(s)")}</h3>
                    <p className="text-slate-900 font-medium">{viewingIncident.vehicle_numbers || "—"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{translateUi("OTDR download")}</h3>
                    <p className="text-slate-900 font-medium">{viewingIncident.otdr_download ? translateUi(String(viewingIncident.otdr_download)) : "—"}</p>
                  </div>
                </>
              )}
            </div>
            
            <div className="pt-6 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2"> {translateUi("Description")} </h3>
              <p className="text-slate-900 whitespace-pre-wrap bg-slate-50 p-4 rounded-xl border border-slate-100">{viewingIncident.description}</p>
            </div>
            
            {viewingIncident.action_taken && (
              <div className="pt-6 border-t border-slate-100">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Action Taken")}</h3>
                <p className="text-slate-900 whitespace-pre-wrap bg-slate-50 p-4 rounded-xl border border-slate-100">{viewingIncident.action_taken}</p>
              </div>
            )}
            <div className="pt-6 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{translateUi("Evidence photos")}</h3>
              {Array.isArray(viewingIncident.evidence) && viewingIncident.evidence.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {viewingIncident.evidence.map((ev: { id: string | number; file_name?: string; content_type?: string }) => (
                    <div key={String(ev.id)} className="relative rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                      {evidencePreviews[String(ev.id)] ? (
                        <img src={evidencePreviews[String(ev.id)]} alt={ev.file_name || translateUi("Evidence")} className="h-32 w-full object-cover" />
                      ) : (
                        <div className="h-32 flex items-center justify-center text-slate-400">
                          <Paperclip className="w-5 h-5" />
                        </div>
                      )}
                      <p className="px-2 py-1.5 text-xs text-slate-600 truncate">{ev.file_name || translateUi("Evidence")}</p>
                      {canMutateIncidents && (
                        <button
                          type="button"
                          onClick={() => confirmDeleteIncidentEvidence(viewingIncident, ev.id)}
                          className="absolute top-1.5 right-1.5 rounded-lg bg-white/95 p-1 text-rose-600 shadow-sm border border-slate-200 hover:bg-rose-50"
                          aria-label={translateUi("Remove photo")}
                          title={translateUi("Remove photo")}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">{translateUi("No evidence attached")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {confirmState && (
        <ConfirmDialog 
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      {incidentError && (
        <div
          className={cn(
            "rounded-xl border px-4 py-3 text-sm font-medium",
            isDarkMode ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"
          )}
        >
          {incidentError}
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{translateUi("Incident Management")}</h2>
          <p className="text-slate-500 text-sm">{translateUi("Track and manage safety incidents")}</p>
        </div>
        {canMutateIncidents && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center px-4 py-2 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700 transition-colors"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> {translateUi("Record Incident")}
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="px-8 py-6 bg-rose-600 text-white flex items-center justify-between">
            <h3 className="text-xl font-bold">{editingIncident ? translateUi("Edit Incident") : translateUi("Record New Incident")}</h3>
            <button onClick={closeIncidentForm} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
            <form onSubmit={handleSubmit} className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 max-w-sm" ref={employeePickerRef}>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> {translateUi("Employee")} </label>
                {selectedIncidentEmployee ? (
                  <div className="flex items-center justify-between gap-3 mb-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                    <span className="min-w-0 truncate text-sm font-medium text-slate-800">
                      {selectedIncidentEmployee.full_name} ({selectedIncidentEmployee.id})
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, employee_id: "" });
                        setEmployeeSearch("");
                        setEmployeePickerOpen(true);
                      }}
                      className="shrink-0 text-xs font-semibold text-slate-500 hover:text-slate-800"
                    >
                      {translateUi("Change")}
                    </button>
                  </div>
                ) : null}
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={employeeSearch}
                    onChange={(e) => {
                      setEmployeeSearch(e.target.value);
                      setEmployeePickerOpen(true);
                    }}
                    onFocus={() => setEmployeePickerOpen(true)}
                    placeholder={translateUi("Search employee")}
                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                    aria-label={translateUi("Search employee")}
                    autoComplete="off"
                    required={!formData.employee_id}
                  />
                </div>
                {employeePickerOpen ? (
                  <ul
                    role="listbox"
                    className="relative z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                  >
                    {tenantEmployees.length === 0 ? (
                      <li className="px-3 py-2.5 text-sm text-slate-500">{translateUi("No employees found.")}</li>
                    ) : filteredEmployeeOptions.length === 0 ? (
                      <li className="px-3 py-2.5 text-sm text-slate-500">{translateUi("No matching employees")}</li>
                    ) : (
                      filteredEmployeeOptions.map((emp) => (
                        <li key={emp.id} role="option" aria-selected={String(emp.id) === String(formData.employee_id)}>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, employee_id: String(emp.id) });
                              setEmployeeSearch("");
                              setEmployeePickerOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors",
                              String(emp.id) === String(formData.employee_id)
                                ? "bg-rose-50 text-rose-900"
                                : "hover:bg-slate-50 text-slate-800"
                            )}
                          >
                            <span className="min-w-0 truncate">{emp.full_name} ({emp.id})</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Date of Incident")}</label>
                <input 
                  type="date" 
                  required
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Incident Type")}</label>
                <select 
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                >
                  {incidentTypes.map((typeKey) => (
                    <option key={typeKey} value={typeKey}>
                      {translateUi(typeKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> {translateUi("Severity")} </label>
                <select 
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.severity}
                  onChange={e => setFormData({ ...formData, severity: e.target.value })}
                >
                  {INCIDENT_SEVERITY_LEVELS.map((level) => (
                    <option key={level} value={level}>{translateUi(level)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Was a train involved?")}</label>
                <select
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.train_involved ? "yes" : "no"}
                  onChange={(e) => {
                    const involved = e.target.value === "yes";
                    if (!involved) setPendingOtdrFiles([]);
                    setFormData({
                      ...formData,
                      train_involved: involved,
                      ...(involved ? {} : { headcode: "", vehicle_numbers: "", otdr_download: "" }),
                    });
                  }}
                >
                  <option value="no">{translateUi("No")}</option>
                  <option value="yes">{translateUi("Yes")}</option>
                </select>
              </div>
              {formData.train_involved && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Headcode")}</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                      value={formData.headcode}
                      onChange={(e) => setFormData({ ...formData, headcode: e.target.value })}
                      placeholder="1A23"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Vehicle number(s)")}</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                      value={formData.vehicle_numbers}
                      onChange={(e) => setFormData({ ...formData, vehicle_numbers: e.target.value })}
                      placeholder={translateUi("Unit or coach numbers")}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("OTDR download")}</label>
                    <select
                      required
                      className="inline-block w-max max-w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                      value={formData.otdr_download}
                      onChange={(e) => {
                        if (e.target.value !== "Obtained") setPendingOtdrFiles([]);
                        setFormData({ ...formData, otdr_download: e.target.value });
                      }}
                    >
                      <option value="">{translateUi("Select")}</option>
                      {INCIDENT_OTDR_CHOICES.map((choice) => (
                        <option key={choice} value={choice}>{translateUi(choice)}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Incident Summary")}</label>
                <textarea 
                  required
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Action Taken / Notes")}</label>
                <textarea 
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                  value={formData.action_taken}
                  onChange={e => setFormData({ ...formData, action_taken: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Attach evidence")}</label>
                <p className="text-xs text-slate-500 mb-2">{translateUi("Photos are stored in this company’s Azure storage.")}</p>
                <input
                  ref={evidenceInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(f.name));
                    setPendingEvidence((prev) => [...prev, ...files].slice(0, 10));
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => evidenceInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="w-4 h-4 mr-2 text-slate-400" /> {translateUi("Choose photos")}
                </button>
                {editingIncident && Array.isArray(editingIncident.evidence) && editingIncident.evidence.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {editingIncident.evidence.map((ev: { id: string | number; file_name?: string; content_type?: string }) => (
                      <div key={String(ev.id)} className="relative rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                        {evidencePreviews[String(ev.id)] ? (
                          <img src={evidencePreviews[String(ev.id)]} alt={ev.file_name || translateUi("Evidence")} className="h-28 w-full object-cover" />
                        ) : (
                          <div className="h-28 flex items-center justify-center text-slate-400">
                            <Paperclip className="w-5 h-5" />
                          </div>
                        )}
                        <p className="px-2 py-1.5 text-xs text-slate-600 truncate">{ev.file_name || translateUi("Evidence")}</p>
                        <button
                          type="button"
                          onClick={() => confirmDeleteIncidentEvidence(editingIncident, ev.id)}
                          className="absolute top-1.5 right-1.5 rounded-lg bg-white/95 p-1 text-rose-600 shadow-sm border border-slate-200 hover:bg-rose-50"
                          aria-label={translateUi("Remove photo")}
                          title={translateUi("Remove photo")}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {pendingEvidence.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {pendingEvidence.map((file, idx) => (
                      <li key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5">
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setPendingEvidence((prev) => prev.filter((_, i) => i !== idx))}
                          className="ml-3 text-xs font-semibold text-rose-600 hover:text-rose-700"
                        >
                          {translateUi("Remove")}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {formData.train_involved && formData.otdr_download === "Obtained" && (
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("OTDR Download")}</label>
                <p className="text-xs text-slate-500 mb-2">{translateUi("Upload the OTDR data file for this incident.")}</p>
                <input
                  ref={otdrFileInputRef}
                  type="file"
                  accept=".pdf,.csv,.txt,.zip,.xls,.xlsx,.doc,.docx"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setPendingOtdrFiles((prev) => [...prev, ...files].slice(0, 5));
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => otdrFileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="w-4 h-4 mr-2 text-slate-400" /> {translateUi("Choose file")}
                </button>
                {pendingOtdrFiles.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {pendingOtdrFiles.map((file, idx) => (
                      <li key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5">
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setPendingOtdrFiles((prev) => prev.filter((_, i) => i !== idx))}
                          className="ml-3 text-xs font-semibold text-rose-600 hover:text-rose-700"
                        >
                          {translateUi("Remove")}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              )}
              <div className="flex flex-wrap justify-end md:col-span-2 gap-3 mt-4">
                <button 
                  type="button"
                  onClick={closeIncidentForm}
                  className="px-6 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-xl transition-colors"
                > {translateUi("Cancel")} </button>
                <button 
                  type="submit"
                  disabled={savingIncident}
                  className="px-8 py-2 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-200 disabled:opacity-60"
                >
                  {savingIncident
                    ? translateUi("Saving…")
                    : editingIncident ? translateUi("Update Incident") : translateUi("Record Incident")}
                </button>
              </div>
            </form>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold">{translateUi("Date")}</th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Employee")} </th>
                <th className="px-6 py-4 font-semibold">{translateUi("Type")}</th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Severity")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Status")} </th>
                <th className="px-6 py-4 font-semibold text-right"> {translateUi("Action")} </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {incidents.map(incident => (
                <tr key={incident.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                    {formatDateUK(incident.date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{incident.employee_name}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{translateUi(String(incident.type || '—'))}</td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-2.5 py-0.5 rounded-full text-xs font-medium",
                      incidentSeverityChipClass(incident.severity)
                    )}>{translateUi(normalizeIncidentSeverityUi(incident.severity))}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{translateUi(String(incident.status || '—'))}</td>
                  <td className="px-6 py-4 text-right">
                    <ActionDropdown 
                      onSelect={() => setViewingIncident(incident)}
                      onEdit={() => setEditingIncident(incident)}
                      onDelete={() => {
                        setConfirmState({
                          isOpen: true,
                          title: translateUi("Delete Incident"),
                          message: translateUi("Are you sure you want to delete this incident record? This action cannot be undone."),
                          onConfirm: () => {
                            handleDelete(incident.id, incident);
                            setConfirmState(null);
                          }
                        });
                      }}
                      canEdit={canMutateIncidents}
                      canDelete={canDeleteIncidents}
                    />
                  </td>
                </tr>
              ))}
              {incidents.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400 italic">{translateUi("No incidents reported")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/** Match employee record to logged-in / impersonated user (email or users.employee_id link). */
function employeeMatchesActorProfile(
  emp: Employee,
  profile?: SystemUser | null,
  fallbackEmail?: string | null
): boolean {
  const actorEmail = String(profile?.email || fallbackEmail || '').trim().toLowerCase();
  const linkedId = String(profile?.employee_id ?? '').trim();
  const empId = String(emp.id ?? '').trim();
  if (linkedId && empId && linkedId === empId) return true;
  const empEmail = String(emp.email ?? '').trim().toLowerCase();
  return !!(actorEmail && empEmail && empEmail === actorEmail);
}

function findOwnEmployeeForProfile(
  employees: Employee[],
  profile?: SystemUser | null,
  fallbackEmail?: string | null
): Employee | undefined {
  return employees.find((emp) => employeeMatchesActorProfile(emp, profile, fallbackEmail));
}

const EmployeeList = ({ employees, onSelectEmployee, onAddEmployee, onEditEmployee, onEmployeeDeleted, currentUserTitle, currentUserEmail, currentFilter, onClearFilter, isSystemAdmin, systemAdminProviderCompanyCode, isMobileOrTablet = false, operationalEditUnlocked = true, onLoginAsEmployee }: {
  employees: Employee[], 
  onSelectEmployee: (id: string, companyCode?: string) => void,
  onAddEmployee: () => void,
  onEditEmployee: (emp: Employee) => void,
  onEmployeeDeleted: (id: string) => void,
  currentUserTitle?: string,
  currentUserEmail?: string,
  currentFilter?: string | null,
  onClearFilter?: () => void,
  isSystemAdmin?: boolean,
  /** When set, a real system admin is managing the CMS provider tenant roster (same as a normal company). */
  systemAdminProviderCompanyCode?: string,
  isMobileOrTablet?: boolean,
  operationalEditUnlocked?: boolean,
  /** System admin only: log in as the team user linked to this employee record. */
  onLoginAsEmployee?: (emp: Employee) => void,
}) => {
  const [confirmState, setConfirmState] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  const isAdmin = currentUserTitle?.toLowerCase()?.includes('admin') || currentUserEmail?.toLowerCase() === 'admin@railvault.com' || currentUserEmail?.toLowerCase() === 'andyuk38@gmail.com';
  const isManager = currentUserTitle?.toLowerCase()?.includes('manager');
  const roleOk = operationalEditUnlocked;
  const canEdit = roleOk && (isAdmin || isManager);
  const canDelete = roleOk && isAdmin;
  const systemAdminManagingProviderRoster = !!(isSystemAdmin && String(systemAdminProviderCompanyCode ?? '').trim());

  const handleDelete = async (id: string, companyCode?: string) => {
    try {
      const params = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      const res = await fetch(`/api/employees/${id}${params}`, { method: 'DELETE' });
      if (res.ok) {
        onEmployeeDeleted(id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {confirmState && (
        <ConfirmDialog 
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      <div className={cn(
        "flex justify-between gap-4",
        isMobileOrTablet ? "flex-col sm:flex-row sm:items-center sm:justify-between" : "items-center"
      )}>
        <div>
          <h2 className={cn("font-bold text-slate-900", isMobileOrTablet ? "text-xl sm:text-2xl" : "text-2xl")}>{translateUi("Employee Records")}</h2>
          <p className="text-slate-500 text-sm">
            {currentFilter
              ? translateUi("Showing {{filter}} employees", {
                  filter: translateUi(
                    currentFilter === "off-track"
                      ? "Off Track"
                      : currentFilter === "active"
                        ? "Active"
                        : currentFilter,
                  ),
                })
              : translateUi("Full list of safety-critical personnel")}
          </p>
        </div>
        <div className={cn("flex items-center", isMobileOrTablet ? "gap-2 sm:gap-3 flex-wrap" : "space-x-3")}>
          {currentFilter && (
            <button 
              onClick={onClearFilter}
              className="px-4 py-2 text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors min-h-[44px]"
            >
              {translateUi("Clear Filter")}
            </button>
          )}
          {canEdit && (!isSystemAdmin || systemAdminManagingProviderRoster) && (
          <button 
            onClick={onAddEmployee}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors min-h-[44px]"
          >
            <UserPlus className="w-4 h-4 mr-2" /> {translateUi("Add Employee")}
          </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Mobile/tablet card layout */}
        {isMobileOrTablet && (
        <div className="divide-y divide-slate-100">
          {employees.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400">
              {isSystemAdmin
                ? systemAdminManagingProviderRoster
                  ? 'No employees in the CMS yet. Use Add Employee to create internal staff records.'
                  : 'Use View as in the header to choose a company. Employee records for that company appear here.'
                : translateUi("No employees found.")}
            </div>
          ) : (
            employees.map(emp => (
              <div key={emp.id} className="px-4 py-4 flex items-center justify-between gap-3">
                <button
                  onClick={() => onSelectEmployee(emp.id, emp.company_code)}
                  className="flex-1 flex items-center min-w-0 text-left"
                >
                  <div className="w-10 h-10 shrink-0 mr-3 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center ring-1 ring-slate-200/80">
                    {String(emp.image_url || "").trim() ? (
                      <img
                        src={String(emp.image_url).trim()}
                        alt=""
                        className="h-full w-full object-cover object-center"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <User
                      className={cn(
                        "w-5 h-5 text-slate-400",
                        String(emp.image_url || "").trim() ? "hidden" : undefined
                      )}
                      aria-hidden
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{emp.full_name}</p>
                    <p className="text-xs text-slate-500">{emp.role}{emp.depot ? ` · ${emp.depot}` : ''}</p>
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={cn(
                    "px-2.5 py-0.5 rounded-full text-xs font-medium",
                    emp.status === 'Active' ? "bg-emerald-100 text-emerald-700 status-chip-positive" : "bg-slate-100 text-slate-700"
                  )}>{translateUi(emp.status || "Active")}</span>
                  {onLoginAsEmployee && (
                    <button
                      type="button"
                      onClick={() => onLoginAsEmployee(emp)}
                      className="p-2 bg-violet-100 hover:bg-violet-200 rounded-lg transition-all border border-violet-300"
                      title={translateUi("Login as this user")}
                      aria-label={translateUi('Login as {{name}}', { name: emp.full_name })}
                    >
                      <LogIn className="w-4 h-4 shrink-0 text-violet-800" />
                    </button>
                  )}
                  <ActionDropdown 
                    onSelect={() => onSelectEmployee(emp.id, emp.company_code)}
                    onEdit={() => onEditEmployee(emp)}
                    onDelete={() => {
                      setConfirmState({
                        isOpen: true,
                        title: translateUi("Delete Employee"),
                        message: "Are you sure you want to delete this employee record? This action cannot be undone.",
                        onConfirm: () => {
                          handleDelete(emp.id, emp.company_code);
                          setConfirmState(null);
                        }
                      });
                    }}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                </div>
              </div>
            ))
          )}
        </div>
        )}
        {/* Desktop table layout */}
        <div className={isMobileOrTablet ? "hidden" : "overflow-x-auto"}>
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold"> {translateUi("Employee")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Role")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Depot")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Status")} </th>
                {isSystemAdmin ? (
                  <>
                    <th className="px-6 py-4 font-semibold"> {translateUi("Company")} </th>
                    <th className="px-6 py-4 font-semibold">{translateUi("Company Number")}</th>
                  </>
                ) : (
                  <th className="px-6 py-4 font-semibold"> {translateUi("Manager")} </th>
                )}
                <th className="px-6 py-4 font-semibold text-right"> {translateUi("Action")} </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map(emp => (
                <tr key={emp.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 shrink-0 mr-3 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center ring-1 ring-slate-200/80">
                        {String(emp.image_url || "").trim() ? (
                          <img
                            src={String(emp.image_url).trim()}
                            alt=""
                            className="h-full w-full object-cover object-center"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                              if (fallback) fallback.classList.remove("hidden");
                            }}
                          />
                        ) : null}
                        <User
                          className={cn(
                            "w-4 h-4 text-slate-400",
                            String(emp.image_url || "").trim() ? "hidden" : undefined
                          )}
                          aria-hidden
                        />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{emp.full_name}</p>
                        <p className="text-xs text-slate-500">{emp.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{emp.role}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{emp.depot}</td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-2.5 py-0.5 rounded-full text-xs font-medium",
                      emp.status === 'Active' ? "bg-emerald-100 text-emerald-700 status-chip-positive" : "bg-slate-100 text-slate-700"
                    )}>{translateUi(emp.status || "Active")}</span>
                  </td>
                  {isSystemAdmin ? (
                    <>
                      <td className="px-6 py-4 text-sm text-slate-600">{emp.company_name || '—'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-slate-600">{emp.company_code || '—'}</td>
                    </>
                  ) : (
                    <td className="px-6 py-4 text-sm text-slate-600">{emp.manager}</td>
                  )}
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onLoginAsEmployee && (
                        <button
                          type="button"
                          onClick={() => onLoginAsEmployee(emp)}
                          className="p-2 bg-violet-100 hover:bg-violet-200 rounded-lg transition-all border border-violet-300"
                          title={translateUi("Login as this user")}
                          aria-label={translateUi('Login as {{name}}', { name: emp.full_name })}
                        >
                          <LogIn className="w-4 h-4 shrink-0 text-violet-800" />
                        </button>
                      )}
                      <ActionDropdown 
                        onSelect={() => onSelectEmployee(emp.id, emp.company_code)}
                        onEdit={() => onEditEmployee(emp)}
                        onDelete={() => {
                          setConfirmState({
                            isOpen: true,
                            title: translateUi("Delete Employee"),
                            message: "Are you sure you want to delete this employee record? This action cannot be undone.",
                            onConfirm: () => {
                              handleDelete(emp.id, emp.company_code);
                              setConfirmState(null);
                            }
                          });
                        }}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={isSystemAdmin ? 7 : 6} className="px-6 py-12 text-center text-slate-400">
                  {isSystemAdmin
                    ? systemAdminManagingProviderRoster
                      ? 'No employees in the CMS yet. Use Add Employee to create internal staff records.'
                      : 'Use View as in the header to choose a company. Employee records for that company appear here.'
                    : translateUi("No employees found.")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/** Matches server: system administrator or company administrator (title), plus known admin emails. */
function userCanDeleteCdpPlan(profile?: SystemUser | null): boolean {
  const email = (profile?.email || '').toLowerCase().trim();
  if (email === 'admin@railvault.com' || email === 'andyuk38@gmail.com') return true;
  const t = (profile?.title || '').toLowerCase();
  if (t.includes('system') && (t.includes('administrator') || t.includes('admin'))) return true;
  return t.includes('administrator') || t.includes('admin');
}

const MonitoringTab = ({
  employeeId,
  currentUserProfile,
  currentUserEmail,
  companyCode,
  isDarkMode = false,
  employeeEmail,
  isTraineeDriverModuleActive = false,
  canManageTraineeDriver = false,
  onOpenMedicationChecks,
}: {
  employeeId: string;
  currentUserProfile?: SystemUser | null;
  currentUserEmail?: string;
  companyCode?: string;
  isDarkMode?: boolean;
  employeeEmail?: string;
  isTraineeDriverModuleActive?: boolean;
  canManageTraineeDriver?: boolean;
  onOpenMedicationChecks?: () => void;
}) => {
  type MonitoringEventDraft = {
    draft_id: string;
    event_name: string;
    monitoring_method: "Practical" | "Simulation" | "Briefing" | "OTDR Download" | "Other";
    monitoring_description: string;
    offset_months_open: number;
    offset_months_close: number;
    selected_for_live_cycle: boolean;
  };
  const monitoringDraftSeqRef = useRef(0);
  const nextMonitoringDraftId = useCallback(() => {
    monitoringDraftSeqRef.current += 1;
    return `m-draft-${Date.now()}-${monitoringDraftSeqRef.current}`;
  }, []);
  const [incidents, setIncidents] = useState<any[]>([]);
  const [viewingAssociatedIncident, setViewingAssociatedIncident] = useState<any | null>(null);
  const [cdpPlans, setCdpPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCdpForm, setShowCdpForm] = useState(false);
  const [selectedCdpPlanId, setSelectedCdpPlanId] = useState<string | null>(null);
  const [monitoringEventsDraft, setMonitoringEventsDraft] = useState<MonitoringEventDraft[]>([]);
  const [savingMonitoringEvents, setSavingMonitoringEvents] = useState(false);
  const [monitoringMessage, setMonitoringMessage] = useState<string | null>(null);
  const [addToLiveCycleConfirmIndex, setAddToLiveCycleConfirmIndex] = useState<number | null>(null);
  const [saveWithoutAddConfirmOpen, setSaveWithoutAddConfirmOpen] = useState(false);
  const [liveCycles, setLiveCycles] = useState<Array<{
    id: number | string;
    cycle_title: string;
    start_date: string;
    expiry_date: string;
  }>>([]);
  const [selectedLiveCycleId, setSelectedLiveCycleId] = useState<string>('');
  const [currentLiveMonitoringEvents, setCurrentLiveMonitoringEvents] = useState<EmployeeCycleEventSummary[]>([]);
  const [cdpFormData, setCdpFormData] = useState({
    plan_name: "Reactive" as "Reactive" | "Proactive",
    start_date: new Date().toISOString().split('T')[0],
    status: "Active",
    notes: '',
    incident_id: '',
    duration_value: '1',
    duration_unit: 'months' as 'days' | 'months' | 'years',
  });
  const [showActivePlanDecisionModal, setShowActivePlanDecisionModal] = useState(false);
  const [activePlanDecision, setActivePlanDecision] = useState<'keep' | 'close'>('keep');
  const [activePlanToCloseId, setActivePlanToCloseId] = useState<string>('');
  const [loadingOutstandingEvents, setLoadingOutstandingEvents] = useState(false);
  const [outstandingMonitoringEvents, setOutstandingMonitoringEvents] = useState<Array<{
    event_id: string;
    source_plan_id: string;
    source_plan_name: string;
    source_employee_cycle_id: string;
    source_cycle_label: string;
    event_name: string;
    monitoring_method: string;
    monitoring_description: string;
    offset_months_open: number;
    offset_months_close: number;
    status: string;
  }>>([]);
  const [selectedOutstandingEventIds, setSelectedOutstandingEventIds] = useState<string[]>([]);
  const [transferOutstandingEvents, setTransferOutstandingEvents] = useState(true);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [cdpPlanView, setCdpPlanView] = useState<'live' | 'closed'>('live');
  const [ohRecommendations, setOhRecommendations] = useState<Array<{
    id: number | string;
    date_checked?: string;
    recommendation_type?: string;
    details?: string;
    due_date?: string | null;
    status?: string;
    overdue?: boolean;
  }>>([]);

  const cdpComputedEndDate = useMemo(() => {
    const dur = parseInt(String(cdpFormData.duration_value).trim(), 10);
    if (!Number.isFinite(dur) || dur < 1 || !cdpFormData.start_date) return '';
    return calculateCdpEndDateInclusive(cdpFormData.start_date, dur, cdpFormData.duration_unit) ?? '';
  }, [cdpFormData.start_date, cdpFormData.duration_value, cdpFormData.duration_unit]);

  const existingCdpForFormIncident = useMemo(() => {
    const iid = cdpFormData.incident_id.trim();
    if (!iid) return null;
    return cdpPlans.find((p) => String(p.incident_id ?? '').trim() === iid) ?? null;
  }, [cdpFormData.incident_id, cdpPlans]);
  const activeCdpPlans = useMemo(
    () =>
      cdpPlans.filter(
        (plan) => String(plan.status ?? '').trim().toLowerCase() === 'active'
      ),
    [cdpPlans]
  );
  const closedCdpPlans = useMemo(
    () =>
      cdpPlans.filter(
        (plan) => String(plan.status ?? '').trim().toLowerCase() !== 'active'
      ),
    [cdpPlans]
  );
  const visibleCdpPlans = useMemo(
    () => (cdpPlanView === 'live' ? activeCdpPlans : closedCdpPlans),
    [cdpPlanView, activeCdpPlans, closedCdpPlans]
  );

  const isAdmin = currentUserProfile?.title?.toLowerCase()?.includes('admin') || currentUserProfile?.email?.toLowerCase() === 'andyuk38@gmail.com';
  const canDeleteCdp = userCanDeleteCdpPlan(currentUserProfile);
  const selectedCdpPlan = useMemo(
    () => cdpPlans.find((plan) => String(plan.id) === String(selectedCdpPlanId)),
    [cdpPlans, selectedCdpPlanId]
  );
  const currentLiveCycle = useMemo(
    () => liveCycles.find((cycle) => String(cycle.id) === selectedLiveCycleId) ?? null,
    [liveCycles, selectedLiveCycleId]
  );

  const normalizeMonitoringEventDrafts = useCallback((raw: unknown): MonitoringEventDraft[] => {
    if (!Array.isArray(raw)) return [];
    const out: MonitoringEventDraft[] = [];
    for (const row of raw) {
      const event = row as Record<string, unknown>;
      const eventName = String(event?.event_name ?? '').trim();
      const methodRaw = String(event?.monitoring_method ?? '').trim();
      const method: MonitoringEventDraft['monitoring_method'] =
        methodRaw === 'Practical' || methodRaw === 'Simulation' || methodRaw === 'Briefing' || methodRaw === 'OTDR Download'
          ? methodRaw
          : translateUi("Other");
      const description = String(event?.monitoring_description ?? '').trim();
      const openM = Number(event?.offset_months_open ?? 0);
      const closeM = Number(event?.offset_months_close ?? 0);
      if (!eventName || !Number.isFinite(openM) || !Number.isFinite(closeM) || openM < 0 || closeM <= openM) continue;
      out.push({
        draft_id: nextMonitoringDraftId(),
        event_name: eventName,
        monitoring_method: method,
        monitoring_description: description,
        offset_months_open: openM,
        offset_months_close: closeM,
        selected_for_live_cycle: event?.selected_for_live_cycle === true,
      });
    }
    return out;
  }, [nextMonitoringDraftId]);

  const fetchCurrentLiveMonitoringEvents = useCallback(async (liveCycleId: number | string) => {
    const params = new URLSearchParams();
    params.set('includeMonitoring', '1');
    params.set('eventType', 'monitoring');
    if (companyCode) params.set('companyCode', companyCode);
    const res = await fetch(`/api/employees/${employeeId}/cycle-events/${encodeURIComponent(String(liveCycleId))}?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? (data as EmployeeCycleEventSummary[]) : [];
  }, [employeeId, companyCode]);

  const loadOutstandingMonitoringEventsForPlan = useCallback(async (planId: string) => {
    const normalizedPlanId = String(planId ?? '').trim();
    if (!normalizedPlanId || liveCycles.length === 0) {
      setOutstandingMonitoringEvents([]);
      setSelectedOutstandingEventIds([]);
      return;
    }
    setLoadingOutstandingEvents(true);
    try {
      const rowsByCycle = await Promise.all(
        liveCycles.map(async (cycle) => {
          const cycleRows = await fetchCurrentLiveMonitoringEvents(cycle.id);
          return cycleRows
            .filter((ev) => {
              const eventType = String(ev.event_type ?? '').trim().toLowerCase();
              const eventPlanId = String(ev.cdp_plan_id ?? '').trim();
              const status = String(ev.status ?? '').trim().toLowerCase();
              return eventType === 'monitoring' && eventPlanId === normalizedPlanId && status !== 'completed';
            })
            .map((ev) => ({
              event_id: String(ev.id ?? ''),
              source_plan_id: normalizedPlanId,
              source_plan_name:
                String(activeCdpPlans.find((p) => String(p.id) === normalizedPlanId)?.plan_name ?? 'CDP Plan') || 'CDP Plan',
              source_employee_cycle_id: String(cycle.id),
              source_cycle_label: `${cycle.cycle_title} (${formatDateUK(cycle.start_date)} to ${formatDateUK(cycle.expiry_date)})`,
              event_name: String(ev.event_name ?? '').trim(),
              monitoring_method: String(ev.monitoring_method ?? "Other").trim() || "Other",
              monitoring_description: String(ev.monitoring_description ?? '').trim(),
              offset_months_open: Number(ev.offset_months_open ?? 0),
              offset_months_close: Number(ev.offset_months_close ?? 0),
              status: String(ev.status ?? '').trim() || 'pending',
            }))
            .filter((ev) => ev.event_id && ev.event_name);
        })
      );
      const merged = rowsByCycle.flat();
      setOutstandingMonitoringEvents(merged);
      setSelectedOutstandingEventIds(merged.map((row) => row.event_id));
    } finally {
      setLoadingOutstandingEvents(false);
    }
  }, [liveCycles, fetchCurrentLiveMonitoringEvents, activeCdpPlans]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const incParams = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      const cdpParams = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      const cycleParams = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      const [incRes, cdpRes, cycleRes] = await Promise.all([
        fetch(`/api/incidents${incParams}`, {
          headers: currentUserEmail
            ? { 'X-User-Email': currentUserEmail, ...(companyCode ? { 'X-Company-Code': companyCode } : {}) }
            : (companyCode ? { 'X-Company-Code': companyCode } : undefined),
        }),
        fetch(`/api/employees/${employeeId}/cdp${cdpParams}`),
        fetch(`/api/employees/${employeeId}/cycles${cycleParams}`),
      ]);
      const incData = await incRes.json();
      const cdpData = await cdpRes.json();
      const cyclesData = await cycleRes.json().catch(() => []);
      setIncidents(incData.filter((i: any) => i.employee_id === employeeId));
      setCdpPlans(Array.isArray(cdpData) ? cdpData : []);
      const liveCyclesRaw = Array.isArray(cyclesData)
        ? cyclesData
            .filter((row: any) => !row?.end_reason)
            .sort((a: any, b: any) => String(a.start_date ?? '').localeCompare(String(b.start_date ?? '')))
        : [];
      const normalizedLiveCycles = liveCyclesRaw.map((cycle: any) => ({
        id: cycle.id,
        cycle_title: String(cycle.cycle_title ?? 'Current live cycle'),
        start_date: String(cycle.start_date ?? ''),
        expiry_date: String(cycle.expiry_date ?? ''),
      }));
      setLiveCycles(normalizedLiveCycles);
      setSelectedLiveCycleId((prev) => {
        if (prev && normalizedLiveCycles.some((cycle) => String(cycle.id) === prev)) return prev;
        if (normalizedLiveCycles.length === 0) return '';
        return String(normalizedLiveCycles[normalizedLiveCycles.length - 1].id);
      });
      if (normalizedLiveCycles.length === 0) {
        setCurrentLiveMonitoringEvents([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyCode, employeeId, fetchCurrentLiveMonitoringEvents]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const params = new URLSearchParams();
        if (companyCode) params.set('companyCode', companyCode);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(`/api/employees/${employeeId}/medication_oh_recommendations${qs}`, {
          headers: {
            ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
            ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;
        const rows = Array.isArray(data.recommendations) ? data.recommendations : [];
        setOhRecommendations(
          rows.filter((r: { status?: string }) => {
            const s = String(r.status || 'open').toLowerCase();
            return s === 'open' || s === 'in_progress';
          })
        );
      } catch {
        if (!cancelled) setOhRecommendations([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, companyCode, currentUserEmail]);

  const openOhRecommendations = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...ohRecommendations].sort((a, b) => {
      const aOver = !!(a.overdue || (a.due_date && String(a.due_date) < today));
      const bOver = !!(b.overdue || (b.due_date && String(b.due_date) < today));
      if (aOver !== bOver) return aOver ? -1 : 1;
      return String(a.due_date || '9999').localeCompare(String(b.due_date || '9999'));
    });
  }, [ohRecommendations]);

  useEffect(() => {
    if (!selectedCdpPlan) {
      setMonitoringEventsDraft([]);
      return;
    }
    const drafts = normalizeMonitoringEventDrafts((selectedCdpPlan as Record<string, unknown>).monitoring_events);
    setMonitoringEventsDraft(drafts);
  }, [selectedCdpPlan, normalizeMonitoringEventDrafts]);

  useEffect(() => {
    if (!selectedLiveCycleId) {
      setCurrentLiveMonitoringEvents([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const events = await fetchCurrentLiveMonitoringEvents(selectedLiveCycleId);
      if (!cancelled) setCurrentLiveMonitoringEvents(events);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLiveCycleId, fetchCurrentLiveMonitoringEvents]);

  useEffect(() => {
    if (!showActivePlanDecisionModal || activePlanDecision !== 'close') return;
    const planId = String(activePlanToCloseId ?? '').trim();
    if (!planId) {
      setOutstandingMonitoringEvents([]);
      setSelectedOutstandingEventIds([]);
      return;
    }
    void loadOutstandingMonitoringEventsForPlan(planId);
  }, [showActivePlanDecisionModal, activePlanDecision, activePlanToCloseId, loadOutstandingMonitoringEventsForPlan]);

  const handleDeleteCdp = async (plan: any) => {
    const uid = currentUserEmail?.trim();
    if (!uid) {
      alert(translateUi("You must be signed in to delete a plan."));
      return;
    }
    if (!window.confirm(translateUi('Delete this competence development plan? This cannot be undone.'))) return;
    try {
      const params = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
      const res = await fetch(`/api/cdp-plans/${encodeURIComponent(String(plan.id))}${params}`, {
        method: 'DELETE',
        headers: {
          'X-User-Email': uid,
          ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete plan');
      setSelectedCdpPlanId((prev) => (String(prev ?? '') === String(plan.id) ? null : prev));
      void fetchData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : translateUi('Failed to delete plan'));
    }
  };

  const handleCreatePlan = useCallback(
    async (options?: {
      existing_plan_action?: 'keep' | 'close';
      existing_plan_id?: string;
      transfer_event_ids?: string[];
    }) => {
      const dur = parseInt(String(cdpFormData.duration_value).trim(), 10);
      if (!Number.isFinite(dur) || dur < 1) return;
      if (!cdpComputedEndDate) return;
      const incidentId = cdpFormData.incident_id.trim();
      if (!incidentId) return;
      setCreatingPlan(true);
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (companyCode) headers['X-Company-Code'] = companyCode;
        const payload = {
          employee_id: employeeId,
          plan_name: cdpFormData.plan_name,
          start_date: cdpFormData.start_date,
          end_date: cdpComputedEndDate,
          status: cdpFormData.status,
          notes: cdpFormData.notes,
          incident_id: incidentId,
          duration_value: dur,
          duration_unit: cdpFormData.duration_unit,
          ...(options?.existing_plan_action ? { existing_plan_action: options.existing_plan_action } : {}),
          ...(options?.existing_plan_id ? { existing_plan_id: options.existing_plan_id } : {}),
          ...(options?.transfer_event_ids ? { transfer_monitoring_event_ids: options.transfer_event_ids } : {}),
        };
        const res = await fetch('/api/cdp-plans', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        const created = await res.json().catch(() => ({}));
        if (res.ok) {
          const transferredEventsRaw = Array.isArray((created as { transferred_monitoring_events?: unknown[] }).transferred_monitoring_events)
            ? (created as { transferred_monitoring_events: unknown[] }).transferred_monitoring_events
            : [];
          const transferredDrafts = normalizeMonitoringEventDrafts(transferredEventsRaw);
          setShowCdpForm(false);
          setShowActivePlanDecisionModal(false);
          setMonitoringEventsDraft(transferredDrafts);
          setMonitoringMessage(
            transferredDrafts.length > 0
              ? `Transferred ${transferredDrafts.length} outstanding monitoring event${transferredDrafts.length === 1 ? '' : 's'} into this new plan. You can add more before Save & Apply.`
              : null
          );
          const createdPlanId = String((created as { id?: number | string }).id ?? '').trim();
          if (createdPlanId) setSelectedCdpPlanId(createdPlanId);
          setCdpFormData({
            plan_name: "Reactive",
            start_date: new Date().toISOString().split('T')[0],
            status: "Active",
            notes: '',
            incident_id: '',
            duration_value: '1',
            duration_unit: 'months',
          });
          setOutstandingMonitoringEvents([]);
          setSelectedOutstandingEventIds([]);
          await fetchData();
        } else {
          alert((created as { error?: string }).error || translateUi('Failed to create plan'));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setCreatingPlan(false);
      }
    },
    [cdpComputedEndDate, cdpFormData, companyCode, employeeId, fetchData, normalizeMonitoringEventDrafts]
  );

  const handleCdpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeCdpPlans.length === 0) {
      await handleCreatePlan();
      return;
    }
    const defaultPlanId = String(activeCdpPlans[0]?.id ?? '').trim();
    setActivePlanDecision('keep');
    setTransferOutstandingEvents(true);
    setActivePlanToCloseId(defaultPlanId);
    setSelectedOutstandingEventIds([]);
    setOutstandingMonitoringEvents([]);
    setShowActivePlanDecisionModal(true);
  };

  const performSaveMonitoringEvents = useCallback(async () => {
    if (!selectedCdpPlan) return;
    setSavingMonitoringEvents(true);
    setMonitoringMessage(null);
    try {
      const params = new URLSearchParams();
      if (companyCode) params.set('companyCode', companyCode);
      const payloadEvents = monitoringEventsDraft.map(({ draft_id: _draftId, ...event }) => ({
        ...event,
        monitoring_description: event.monitoring_description.trim(),
      }));
      const res = await fetch(`/api/cdp-plans/${encodeURIComponent(String(selectedCdpPlan.id))}/monitoring-events?${params.toString()}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
          ...(currentUserEmail ? { 'X-User-Email': currentUserEmail } : {}),
        },
        body: JSON.stringify({
          events: payloadEvents,
          apply_to_live_cycle: true,
          employee_cycle_id: selectedLiveCycleId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to save monitoring events');
      const applied = Number((data as { applied_to_current_cycle?: unknown }).applied_to_current_cycle ?? 0);
      const carried = Number((data as { carried_forward?: unknown }).carried_forward ?? 0);
      setMonitoringMessage(`Saved. Added ${applied} monitoring event${applied === 1 ? '' : 's'} to live cycle${carried > 0 ? `, ${carried} carried forward` : ''}.`);
      await fetchData();
    } catch (err: unknown) {
      setMonitoringMessage(err instanceof Error ? err.message : translateUi('Failed to save monitoring events'));
    } finally {
      setSavingMonitoringEvents(false);
    }
  }, [selectedCdpPlan, companyCode, currentUserEmail, monitoringEventsDraft, selectedLiveCycleId, fetchData]);

  const handleSaveMonitoringEventsClick = useCallback(() => {
    if (!selectedCdpPlan || !selectedLiveCycleId) return;
    if (!monitoringEventsDraft.some((e) => e.selected_for_live_cycle)) {
      setSaveWithoutAddConfirmOpen(true);
      return;
    }
    void performSaveMonitoringEvents();
  }, [selectedCdpPlan, selectedLiveCycleId, monitoringEventsDraft, performSaveMonitoringEvents]);

  const applyToLiveCycleLabel = useMemo(() => {
    if (currentLiveCycle) {
      return `${currentLiveCycle.cycle_title} (${formatDateUK(currentLiveCycle.start_date)} to ${formatDateUK(currentLiveCycle.expiry_date)})`;
    }
    if (selectedLiveCycleId) return `Cycle ID: ${String(selectedLiveCycleId)}`;
    return 'No live cycle selected. Choose one under Apply to live cycle before saving.';
  }, [currentLiveCycle, selectedLiveCycleId]);
  const selectedActivePlanToClose = useMemo(
    () => activeCdpPlans.find((plan) => String(plan.id) === String(activePlanToCloseId)),
    [activeCdpPlans, activePlanToCloseId]
  );

  const handleConfirmCreateWithDecision = useCallback(async () => {
    if (activePlanDecision === 'keep') {
      await handleCreatePlan({ existing_plan_action: 'keep' });
      return;
    }
    const closePlanId = String(activePlanToCloseId ?? '').trim();
    if (!closePlanId) return;
    const transferIds = transferOutstandingEvents ? selectedOutstandingEventIds : [];
    await handleCreatePlan({
      existing_plan_action: 'close',
      existing_plan_id: closePlanId,
      transfer_event_ids: transferIds,
    });
  }, [activePlanDecision, activePlanToCloseId, handleCreatePlan, selectedOutstandingEventIds, transferOutstandingEvents]);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-8">
      {openOhRecommendations.length > 0 && (
        <div
          className={cn(
            "rounded-2xl border p-5 sm:p-6 shadow-sm",
            isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h3 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                {translateUi("OH recommendations (Medication Checks)")}
              </h3>
              <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                {translateUi("Open Occupational Health follow-ups that must be adhered to.")}
              </p>
            </div>
            {onOpenMedicationChecks && (
              <button
                type="button"
                onClick={onOpenMedicationChecks}
                className={cn(
                  "text-sm font-medium px-3 py-1.5 rounded-xl border transition-colors",
                  isDarkMode
                    ? "border-slate-600 text-indigo-300 hover:bg-slate-800"
                    : "border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                )}
              >
                {translateUi("Open Medication Checks")}
              </button>
            )}
          </div>
          <ul className="space-y-2">
            {openOhRecommendations.map((r) => {
              const today = new Date().toISOString().slice(0, 10);
              const overdue = !!(r.overdue || (r.due_date && String(r.due_date) < today));
              const typeLabel =
                r.recommendation_type === 'review'
                  ? 'Review within timescale'
                  : r.recommendation_type === 'workplace_adjustment'
                    ? 'Workplace adjustment'
                    : r.recommendation_type === 'other'
                      ? 'Other recommendation'
                      : String(r.recommendation_type || 'Recommendation');
              return (
                <li
                  key={String(r.id)}
                  className={cn(
                    "rounded-xl border px-3 py-2.5 text-sm",
                    overdue
                      ? isDarkMode
                        ? "border-red-500/40 bg-red-500/10"
                        : "border-red-200 bg-red-50"
                      : isDarkMode
                        ? "border-slate-700 bg-slate-800/50"
                        : "border-slate-200 bg-slate-50"
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {typeLabel}
                    </span>
                    {overdue && (
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 px-2 py-0.5 rounded-full"> {translateUi("Overdue")} </span>
                    )}
                    <span className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                      Check {r.date_checked ? formatDateUK(r.date_checked) : '—'}
                      {r.due_date ? ` · Due ${formatDateUK(r.due_date)}` : ''}
                      {r.status ? ` · ${String(r.status).replace(/_/g, ' ')}` : ''}
                    </span>
                  </div>
                  {r.details ? (
                    <p className={cn("mt-1 whitespace-pre-wrap", isDarkMode ? "text-slate-300" : "text-slate-700")}>
                      {r.details}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {isTraineeDriverModuleActive && (
        <PerformanceSupportPlans
          employeeId={employeeId}
          companyCode={companyCode}
          currentUserEmail={currentUserEmail}
          isDarkMode={isDarkMode}
          canManage={canManageTraineeDriver}
          isOwnRecord={
            !!employeeEmail &&
            !!currentUserEmail &&
            employeeEmail.trim().toLowerCase() === currentUserEmail.trim().toLowerCase()
          }
        />
      )}
      {showActivePlanDecisionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => {
            if (creatingPlan) return;
            setShowActivePlanDecisionModal(false);
          }}
        >
          <div
            className={cn("w-full max-w-4xl rounded-2xl border shadow-2xl max-h-[90vh] overflow-hidden", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}
            onClick={(evt) => evt.stopPropagation()}
          >
            <div className={cn("px-6 py-4 border-b", isDarkMode ? "border-slate-700" : "border-slate-200")}>
              <h4 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Existing Active CDP Found")}</h4>
              <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                This employee already has {activeCdpPlans.length} active CDP plan{activeCdpPlans.length === 1 ? '' : 's'}. Choose how to continue before creating the new plan.
              </p>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[65vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setActivePlanDecision('keep')}
                  className={cn(
                    "text-left rounded-xl border px-4 py-3 transition-colors",
                    activePlanDecision === 'keep'
                      ? isDarkMode ? "border-indigo-400 bg-indigo-500/15 text-indigo-100" : "border-indigo-500 bg-indigo-50 text-indigo-900"
                      : isDarkMode ? "border-slate-700 text-slate-200 hover:border-slate-500" : "border-slate-200 text-slate-700 hover:border-slate-300"
                  )}
                >
                  <p className="font-semibold">{translateUi("Keep existing monitoring in place")}</p>
                  <p className={cn("text-xs mt-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Create a new CDP and leave current active plan monitoring untouched.")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setActivePlanDecision('close')}
                  className={cn(
                    "text-left rounded-xl border px-4 py-3 transition-colors",
                    activePlanDecision === 'close'
                      ? isDarkMode ? "border-indigo-400 bg-indigo-500/15 text-indigo-100" : "border-indigo-500 bg-indigo-50 text-indigo-900"
                      : isDarkMode ? "border-slate-700 text-slate-200 hover:border-slate-500" : "border-slate-200 text-slate-700 hover:border-slate-300"
                  )}
                >
                  <p className="font-semibold">{translateUi("Close an existing active CDP")}</p>
                  <p className={cn("text-xs mt-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Close one active plan, then optionally transfer outstanding live monitoring events into the new CDP.")}</p>
                </button>
              </div>

              {activePlanDecision === 'close' && (
                <div className={cn("rounded-xl border p-4 space-y-3", isDarkMode ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50")}>
                  <div>
                    <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Plan to close")}</label>
                    <select
                      value={activePlanToCloseId}
                      onChange={(e) => setActivePlanToCloseId(e.target.value)}
                      className={cn("w-full h-10 px-3 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-indigo-500", isDarkMode ? "bg-slate-900 border-slate-600 text-slate-100" : "bg-white border-slate-300 text-slate-900")}
                    >
                      {activeCdpPlans.map((plan) => (
                        <option key={String(plan.id)} value={String(plan.id)}>
                          {plan.plan_name || "CDP Plan"} ({formatDateUK(plan.start_date)} to {plan.end_date ? formatDateUK(plan.end_date) : 'Ongoing'})
                        </option>
                      ))}
                    </select>
                    {selectedActivePlanToClose && (
                      <p className={cn("mt-2 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        {translateUi("Closing")} <span className={cn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-700")}>{selectedActivePlanToClose.plan_name || 'CDP Plan'}</span> {translateUi("will mark it as closed.")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={cn("inline-flex items-center gap-2 text-sm", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                      <input
                        type="checkbox"
                        checked={transferOutstandingEvents}
                        onChange={(e) => setTransferOutstandingEvents(e.target.checked)}
                      />
                      {translateUi("Transfer outstanding live monitoring events into the new CDP")}
                    </label>
                  </div>
                  {transferOutstandingEvents && (
                    <div className={cn("rounded-lg border", isDarkMode ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white")}>
                      <div className={cn("px-3 py-2 border-b text-xs font-semibold uppercase tracking-wider", isDarkMode ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-500")}>
                        {translateUi("Outstanding live monitoring events")}
                      </div>
                      <div className="p-3 space-y-2">
                        {loadingOutstandingEvents ? (
                          <p className={cn("text-sm", isDarkMode ? "text-slate-300" : "text-slate-600")}>{translateUi("Loading outstanding events…")}</p>
                        ) : outstandingMonitoringEvents.length === 0 ? (
                          <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("No outstanding live monitoring events found for the selected active plan.")}</p>
                        ) : (
                          <>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedOutstandingEventIds(outstandingMonitoringEvents.map((row) => row.event_id))}
                                className={cn("px-2.5 py-1 rounded border text-xs font-medium", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")}
                              >
                                {translateUi("Select all")}
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedOutstandingEventIds([])}
                                className={cn("px-2.5 py-1 rounded border text-xs font-medium", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")}
                              > {translateUi("Clear")} </button>
                            </div>
                            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                              {outstandingMonitoringEvents.map((row) => {
                                const checked = selectedOutstandingEventIds.includes(row.event_id);
                                return (
                                  <label
                                    key={row.event_id}
                                    className={cn("flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer", checked ? (isDarkMode ? "border-indigo-400/70 bg-indigo-500/10" : "border-indigo-300 bg-indigo-50") : (isDarkMode ? "border-slate-700 hover:border-slate-500" : "border-slate-200 hover:border-slate-300"))}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) =>
                                        setSelectedOutstandingEventIds((prev) =>
                                          e.target.checked
                                            ? (prev.includes(row.event_id) ? prev : [...prev, row.event_id])
                                            : prev.filter((id) => id !== row.event_id)
                                        )
                                      }
                                    />
                                    <span className="min-w-0">
                                      <span className={cn("block text-sm font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{row.event_name}</span>
                                      <span className={cn("block text-xs", isDarkMode ? "text-slate-300" : "text-slate-600")}>{row.monitoring_method} • {row.source_cycle_label}</span>
                                      {row.monitoring_description && (
                                        <span className={cn("block text-xs mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{row.monitoring_description}</span>
                                      )}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={cn("px-6 py-4 border-t flex items-center justify-end gap-3", isDarkMode ? "border-slate-700" : "border-slate-200")}>
              <button
                type="button"
                onClick={() => setShowActivePlanDecisionModal(false)}
                disabled={creatingPlan}
                className={cn("px-4 py-2 rounded-lg text-sm", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100")}
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                onClick={() => void handleConfirmCreateWithDecision()}
                disabled={creatingPlan || (activePlanDecision === 'close' && !activePlanToCloseId)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {creatingPlan ? 'Creating…' : translateUi("Create Plan")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={addToLiveCycleConfirmIndex !== null}
        title={translateUi("Add to live cycle?")}
        message={
          addToLiveCycleConfirmIndex === null
            ? ''
            : [
                "When you click Save & Apply, any monitoring events with Add ticked will be added automatically to the employee's chosen live cycle.",
                '',
                'Ticking Add only marks your intent — nothing is added to the live cycle until you save.',
                '',
                'Are you sure you want to tick Add for this event?',
                '',
                `Apply to live cycle:\n${applyToLiveCycleLabel}`,
              ].join('\n')
        }
        onCancel={() => setAddToLiveCycleConfirmIndex(null)}
        onConfirm={() => {
          const idx = addToLiveCycleConfirmIndex;
          setAddToLiveCycleConfirmIndex(null);
          if (idx === null) return;
          setMonitoringEventsDraft((prev) =>
            prev.map((row, i) => (i === idx ? { ...row, selected_for_live_cycle: true } : row))
          );
        }}
        confirmLabel="Yes, tick Add"
        cancelLabel={translateUi("Cancel")}
        confirmButtonClassName="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg font-medium transition-colors"
      />
      <ConfirmDialog
        isOpen={saveWithoutAddConfirmOpen}
        title={translateUi("Nothing ticked for live cycle")}
        message={[
          "You have not ticked Add on any monitoring event, so no events will be added to the employee's live cycle when you save.",
          '',
          'If you meant to add events to the live cycle, tick Add on the rows you want included, then click Save & Apply again.',
          '',
          `Apply to live cycle (for when you do tick Add):\n${applyToLiveCycleLabel}`,
          '',
          'Do you still want to save your plan changes without adding any monitoring events to the live cycle?',
        ].join('\n')}
        onCancel={() => setSaveWithoutAddConfirmOpen(false)}
        onConfirm={() => {
          setSaveWithoutAddConfirmOpen(false);
          void performSaveMonitoringEvents();
        }}
        confirmLabel="Save anyway"
        cancelLabel={translateUi("Go back")}
        confirmButtonClassName="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg font-medium transition-colors"
      />
      <div className={cn("p-8 rounded-2xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
        <h3 className={cn("text-lg font-bold flex items-center mb-6", isDarkMode ? "text-slate-100" : "text-slate-900")}>
          <AlertCircle className={cn("w-5 h-5 mr-2", isDarkMode ? "text-red-300" : "text-red-600")} /> {translateUi("Incidents allocated to this employee")}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className={cn("text-xs uppercase tracking-wider", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-500")}>
              <tr>
                <th className="px-6 py-3 font-semibold">{translateUi("Date")}</th>
                <th className="px-6 py-3 font-semibold">{translateUi("Type")}</th>
                <th className="px-6 py-3 font-semibold"> {translateUi("Severity")} </th>
                <th className="px-6 py-3 font-semibold">{translateUi("Action Taken")}</th>
                <th className="px-6 py-3 font-semibold text-right"> {translateUi("Options")} </th>
              </tr>
            </thead>
            <tbody className={cn("divide-y", isDarkMode ? translateUi("divide-slate-700") : translateUi("divide-slate-100"))}>
              {incidents.length === 0 ? (
                <tr><td colSpan={5} className={cn("px-6 py-8 text-center italic", isDarkMode ? "text-slate-400" : "text-slate-400")}>{translateUi("No incidents recorded for this employee")}</td></tr>
              ) : incidents.map(inc => (
                <tr key={inc.id} className="text-sm">
                  <td className={cn("px-6 py-4", isDarkMode ? "text-slate-300" : "text-slate-600")}>{formatDateUK(inc.date)}</td>
                  <td className={cn("px-6 py-4 font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                    {inc.type}
                  </td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-2 py-0.5 rounded-full text-xs font-medium",
                      incidentSeverityChipClass(inc.severity, isDarkMode)
                    )}>
                      {translateUi(normalizeIncidentSeverityUi(inc.severity))}
                    </span>
                  </td>
                  <td className={cn("px-6 py-4 truncate max-w-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{inc.action_taken || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => setViewingAssociatedIncident(inc)}
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors",
                        isDarkMode
                          ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:border-indigo-400"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-indigo-300"
                      )}
                    >
                      <Eye className="w-3.5 h-3.5" /> {translateUi("View")} </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {viewingAssociatedIncident && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setViewingAssociatedIncident(null)}>
            <div
              className={cn("w-full max-w-3xl rounded-2xl border shadow-xl", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={cn("flex items-center justify-between px-6 py-4 border-b", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                <div>
                  <h4 className={cn("text-lg font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Incident Details")}</h4>
                  <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Viewing linked incident for this employee.")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingAssociatedIncident(null)}
                  className={cn("p-2 rounded-lg transition-colors", isDarkMode ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-500")}
                  aria-label={translateUi("Close incident details")}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Date")}</p>
                    <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{formatDateUK(viewingAssociatedIncident.date)}</p>
                  </div>
                  <div>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Type")}</p>
                    <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{viewingAssociatedIncident.type || '—'}</p>
                  </div>
                  <div>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Severity")} </p>
                    <span className={cn(
                      "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                      incidentSeverityChipClass(viewingAssociatedIncident.severity, isDarkMode)
                    )}>
                      {translateUi(normalizeIncidentSeverityUi(viewingAssociatedIncident.severity || '—'))}
                    </span>
                  </div>
                  <div>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Status")} </p>
                    <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{viewingAssociatedIncident.status || '—'}</p>
                  </div>
                  <div>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Train involved")}</p>
                    <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {incidentTrainInvolved(viewingAssociatedIncident.train_involved) ? translateUi("Yes") : translateUi("No")}
                    </p>
                  </div>
                  {incidentTrainInvolved(viewingAssociatedIncident.train_involved) && (
                    <>
                      <div>
                        <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Headcode")}</p>
                        <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{viewingAssociatedIncident.headcode || '—'}</p>
                      </div>
                      <div>
                        <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Vehicle number(s)")}</p>
                        <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>{viewingAssociatedIncident.vehicle_numbers || '—'}</p>
                      </div>
                      <div>
                        <p className={cn("text-xs font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("OTDR download")}</p>
                        <p className={cn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                          {viewingAssociatedIncident.otdr_download ? translateUi(String(viewingAssociatedIncident.otdr_download)) : '—'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className={cn("mt-5 pt-5 border-t", isDarkMode ? "border-slate-700" : "border-slate-200")}>
                  <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Description")}</p>
                  <p className={cn("whitespace-pre-wrap rounded-xl border p-3 text-sm", isDarkMode ? "border-slate-700 bg-slate-800/70 text-slate-100" : "border-slate-100 bg-slate-50 text-slate-900")}>
                    {viewingAssociatedIncident.description || '—'}
                  </p>
                </div>
                <div className={cn("mt-4", isDarkMode ? "text-slate-200" : "text-slate-900")}>
                  <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Action Taken")}</p>
                  <p className={cn("whitespace-pre-wrap rounded-xl border p-3 text-sm", isDarkMode ? "border-slate-700 bg-slate-800/70 text-slate-100" : "border-slate-100 bg-slate-50 text-slate-900")}>
                    {viewingAssociatedIncident.action_taken || '—'}
                  </p>
                </div>
                {Array.isArray(viewingAssociatedIncident.evidence) && viewingAssociatedIncident.evidence.length > 0 && (
                  <div className={cn("mt-4", isDarkMode ? "text-slate-200" : "text-slate-900")}>
                    <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Evidence photos")}</p>
                    <ul className="text-sm space-y-1">
                      {viewingAssociatedIncident.evidence.map((ev: { id: string | number; file_name?: string }) => (
                        <li key={String(ev.id)} className={cn("truncate", isDarkMode ? "text-slate-300" : "text-slate-700")}>
                          {ev.file_name || translateUi("Evidence")}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={cn("p-8 rounded-2xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h3 className={cn("text-lg font-bold flex items-center", isDarkMode ? "text-slate-100" : "text-slate-900")}>
            <FileText className={cn("w-5 h-5 mr-2", isDarkMode ? "text-indigo-300" : "text-indigo-600")} /> {translateUi("Competence Development Plans (CDP)")} </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={cn(
                "inline-flex items-center rounded-xl border p-1",
                isDarkMode ? "border-slate-600 bg-slate-800" : "border-slate-200 bg-slate-50"
              )}
              role="tablist"
              aria-label={translateUi("Switch CDP plan view")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={cdpPlanView === 'live'}
                onClick={() => setCdpPlanView('live')}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
                  cdpPlanView === 'live'
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-slate-200 hover:bg-slate-700"
                      : "text-slate-700 hover:bg-slate-100"
                )}
              >
                Live plans ({activeCdpPlans.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={cdpPlanView === 'closed'}
                onClick={() => setCdpPlanView('closed')}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
                  cdpPlanView === 'closed'
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-slate-200 hover:bg-slate-700"
                      : "text-slate-700 hover:bg-slate-100"
                )}
              >
                Closed plans ({closedCdpPlans.length})
              </button>
            </div>
            {isAdmin && (
              <button 
                onClick={() => {
                  setShowCdpForm(true);
                  setSelectedCdpPlanId(null);
                  setMonitoringEventsDraft([]);
                  setMonitoringMessage(null);
                  setShowActivePlanDecisionModal(false);
                  setActivePlanDecision('keep');
                  setOutstandingMonitoringEvents([]);
                  setSelectedOutstandingEventIds([]);
                }}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors flex items-center"
              >
                <Plus className="w-3 h-3 mr-1" /> {translateUi("New CDP")}
              </button>
            )}
          </div>
        </div>

        {selectedCdpPlan && (
          <div className={cn("mb-6 rounded-xl border p-4 space-y-4", isDarkMode ? "border-slate-700 bg-slate-800/70" : "border-slate-200 bg-slate-50/60")}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className={cn("font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{selectedCdpPlan.plan_name} CDP Plan Page</h4>
                <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("Dedicated CDP page: define monitoring events, tick events to add to the current live cycle, and carry-forward overflow to the next cycle.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCdpPlanId(null)}
                className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-100")}
              >
                {translateUi("Back to list")}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 text-sm">
              <div><span className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Start")} </span><div className={cn(isDarkMode ? "text-slate-100" : "text-slate-900")}>{selectedCdpPlan.start_date ? formatDateUK(selectedCdpPlan.start_date) : '—'}</div></div>
              <div><span className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("End")}</span><div className={cn(isDarkMode ? "text-slate-100" : "text-slate-900")}>{selectedCdpPlan.end_date ? formatDateUK(selectedCdpPlan.end_date) : '—'}</div></div>
              <div><span className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}> {translateUi("Status")} </span><div className={cn(isDarkMode ? "text-slate-100" : "text-slate-900")}>{selectedCdpPlan.status || '—'}</div></div>
              <div className="xl:col-span-2">
                <span className={cn("text-xs whitespace-nowrap", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Apply to live cycle")}</span>
                {liveCycles.length <= 1 ? (
                  <div className={cn(isDarkMode ? "text-slate-100" : "text-slate-900")}>
                    {currentLiveCycle ? `${currentLiveCycle.cycle_title} (${formatDateUK(currentLiveCycle.start_date)} to ${formatDateUK(currentLiveCycle.expiry_date)})` : 'No live cycle'}
                  </div>
                ) : (
                  <select
                    value={selectedLiveCycleId}
                    onChange={(e) => setSelectedLiveCycleId(e.target.value)}
                    className={cn("mt-1 w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900")}
                  >
                    {liveCycles.map((cycle) => (
                      <option key={String(cycle.id)} value={String(cycle.id)}>
                        {cycle.cycle_title} ({formatDateUK(cycle.start_date)} to {formatDateUK(cycle.expiry_date)})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("Monitoring events (assessment-style schedule)")}</p>
              <div className={cn("hidden xl:grid xl:grid-cols-[0.5fr_3.5fr_2fr_2fr_2fr_1fr_1fr] gap-2 text-[11px] font-semibold uppercase tracking-wider px-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                <span aria-hidden="true"></span>
                <span>{translateUi("Monitoring event name")}</span>
                <span> {translateUi("Method")} </span>
                <span>{translateUi("Open month")}</span>
                <span>{translateUi("Close month")}</span>
                <span> {translateUi("Add")} </span>
                <span> {translateUi("Action")} </span>
              </div>
              <div className="space-y-2">
                {monitoringEventsDraft.map((event, index) => (
                  <div key={event.draft_id} className={cn("grid grid-cols-1 xl:grid-cols-[0.5fr_3.5fr_2fr_2fr_2fr_1fr_1fr] gap-2 p-2 rounded-lg border", isDarkMode ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white")}>
                    <div className="flex items-center">
                      <span className={cn(
                        "inline-flex h-6 min-w-5 items-center justify-center rounded-md px-1 text-xs font-bold",
                        isDarkMode ? "bg-indigo-500/25 text-indigo-100" : "bg-indigo-100 text-indigo-700"
                      )}>
                        {index + 1}
                      </span>
                    </div>
                    <input
                      value={event.event_name}
                      onChange={(e) => setMonitoringEventsDraft((prev) => prev.map((row, i) => (i === index ? { ...row, event_name: e.target.value } : row)))}
                      className={cn("px-3 py-2 rounded-lg border text-sm outline-none", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                      placeholder="Monitoring event name"
                    />
                    <select
                      value={event.monitoring_method}
                      onChange={(e) =>
                        setMonitoringEventsDraft((prev) =>
                          prev.map((row, i) =>
                            i === index
                              ? {
                                  ...row,
                                  monitoring_method: (e.target.value as MonitoringEventDraft['monitoring_method']) || "Other",
                                }
                              : row
                          )
                        )
                      }
                      className={cn("px-3 py-2 rounded-lg border text-sm outline-none", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                      aria-label={translateUi("Monitoring method")}
                    >
                      <option value="Practical"> {translateUi("Practical")} </option>
                      <option value="Simulation"> {translateUi("Simulation")} </option>
                      <option value="Briefing"> {translateUi("Briefing")} </option>
                      <option value="OTDR Download">{translateUi("OTDR Download")}</option>
                      <option value="Other"> {translateUi("Other")} </option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={event.offset_months_open}
                      onChange={(e) => setMonitoringEventsDraft((prev) => prev.map((row, i) => (i === index ? { ...row, offset_months_open: Number(e.target.value) || 0 } : row)))}
                      className={cn("px-3 py-2 rounded-lg border text-sm outline-none", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                      aria-label={translateUi("Open month offset")}
                    />
                    <input
                      type="number"
                      min={1}
                      value={event.offset_months_close}
                      onChange={(e) => setMonitoringEventsDraft((prev) => prev.map((row, i) => (i === index ? { ...row, offset_months_close: Number(e.target.value) || 1 } : row)))}
                      className={cn("px-3 py-2 rounded-lg border text-sm outline-none", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                      aria-label={translateUi("Close month offset")}
                    />
                    <label className={cn("inline-flex items-center gap-2 text-xs font-medium", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                      <input
                        type="checkbox"
                        checked={event.selected_for_live_cycle}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAddToLiveCycleConfirmIndex(index);
                          } else {
                            setMonitoringEventsDraft((prev) =>
                              prev.map((row, i) => (i === index ? { ...row, selected_for_live_cycle: false } : row))
                            );
                          }
                        }}
                      /> {translateUi("Add")} </label>
                    <button
                      type="button"
                      onClick={() => setMonitoringEventsDraft((prev) => prev.filter((_, i) => i !== index))}
                      className={cn("px-2 py-1 rounded-lg border text-xs font-medium", isDarkMode ? "border-rose-400/50 text-rose-300 hover:bg-rose-500/10" : "border-rose-200 text-rose-700 hover:bg-rose-50")}
                    > {translateUi("Remove")} </button>
                    <div className="md:col-span-7">
                      <label className={cn("block text-[11px] font-semibold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                        {translateUi("Description")}
                      </label>
                      <textarea
                        value={event.monitoring_description}
                        onChange={(e) =>
                          setMonitoringEventsDraft((prev) =>
                            prev.map((row, i) => (i === index ? { ...row, monitoring_description: e.target.value } : row))
                          )
                        }
                        rows={2}
                        placeholder="Describe what this monitoring event covers."
                        className={cn("w-full px-3 py-2 rounded-lg border text-sm outline-none resize-y", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-indigo-400" : "border-slate-300 bg-white text-slate-900 focus:border-indigo-500")}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setMonitoringEventsDraft((prev) => [
                      ...prev,
                      {
                        draft_id: nextMonitoringDraftId(),
                        event_name: 'Monitoring Review',
                        monitoring_method: "Practical",
                        monitoring_description: '',
                        offset_months_open: 0,
                        offset_months_close: 1,
                        selected_for_live_cycle: false
                      },
                    ])
                  }
                  className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium", isDarkMode ? "border-indigo-400/50 text-indigo-200 hover:bg-indigo-500/20" : "border-indigo-200 text-indigo-700 hover:bg-indigo-50")}
                >
                  {translateUi("+ Add monitoring event")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveMonitoringEventsClick}
                  disabled={savingMonitoringEvents || !selectedLiveCycleId}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingMonitoringEvents ? translateUi("Saving…") : translateUi("Save & Apply")}
                </button>
              </div>
              {monitoringMessage && (
                <p className={cn("text-xs", monitoringMessage.toLowerCase().includes('failed') || monitoringMessage.toLowerCase().includes('error') ? "text-rose-600" : isDarkMode ? "text-emerald-300" : "text-emerald-700")}>
                  {monitoringMessage}
                </p>
              )}
            </div>

            <div className={cn("rounded-lg border p-3", isDarkMode ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-white")}>
              <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Monitoring events currently in selected live cycle")}
              </p>
              {currentLiveCycle && (
                <p className={cn("text-xs mb-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                  {currentLiveCycle.cycle_title} ({formatDateUK(currentLiveCycle.start_date)} to {formatDateUK(currentLiveCycle.expiry_date)})
                </p>
              )}
              {currentLiveMonitoringEvents.length === 0 ? (
                <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("No CDP monitoring events added to this live cycle yet.")}</p>
              ) : (
                <div className="space-y-1">
                  {currentLiveMonitoringEvents.map((ev) => (
                    <div key={String(ev.id)} className={cn("text-sm flex flex-wrap items-center gap-x-2 gap-y-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                      <span className="font-medium">{ev.event_name}</span>
                      {ev.monitoring_method && (
                        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700")}>
                          {ev.monitoring_method}
                        </span>
                      )}
                      <span className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{formatDateUK(ev.window_open)} to {formatDateUK(ev.window_close)}</span>
                      <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase", ev.status === 'completed' ? (isDarkMode ? "bg-emerald-500/20 text-emerald-200" : "bg-emerald-100 text-emerald-700") : (isDarkMode ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-700"))}>
                        {ev.status || 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {showCdpForm && (
          <form onSubmit={handleCdpSubmit} className={cn("mb-8 p-6 rounded-xl border space-y-4", isDarkMode ? "bg-slate-800/60 border-slate-700" : "bg-slate-50 border-slate-200")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-start">
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}>{translateUi("Plan type")}</label>
                <select
                  required
                  className={cn("w-full h-10 px-4 py-0 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200")}
                  value={cdpFormData.plan_name}
                  onChange={(e) =>
                    setCdpFormData({
                      ...cdpFormData,
                      plan_name: e.target.value as "Reactive" | "Proactive",
                    })
                  }
                >
                  <option value="Reactive"> {translateUi("Reactive")} </option>
                  <option value="Proactive"> {translateUi("Proactive")} </option>
                </select>
              </div>
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}>{translateUi("Start date")}</label>
                <input
                  type="date"
                  required
                  className={cn("w-full h-10 px-4 py-0 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200")}
                  value={cdpFormData.start_date}
                  onChange={(e) => setCdpFormData({ ...cdpFormData, start_date: e.target.value })}
                />
              </div>
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}> {translateUi("Duration")} </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  required
                  className={cn("w-full h-10 px-4 py-0 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200")}
                  value={cdpFormData.duration_value}
                  onChange={(e) => setCdpFormData({ ...cdpFormData, duration_value: e.target.value })}
                />
              </div>
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}> {translateUi("Unit")} </label>
                <select
                  required
                  className={cn("w-full h-10 px-4 py-0 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200")}
                  value={cdpFormData.duration_unit}
                  onChange={(e) =>
                    setCdpFormData({
                      ...cdpFormData,
                      duration_unit: e.target.value as 'days' | 'months' | 'years',
                    })
                  }
                >
                  <option value="days"> {translateUi("Days")} </option>
                  <option value="months">{translateUi("Months")}</option>
                  <option value="years"> {translateUi("Years")} </option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}>{translateUi("End date")}</label>
                <div
                  className={cn("w-full h-10 px-4 border rounded-xl text-sm flex items-center shrink-0", isDarkMode ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-slate-100 border-slate-200 text-slate-800")}
                  aria-live="polite"
                >
                  {cdpComputedEndDate ? formatDateUK(cdpComputedEndDate) : '—'}
                </div>
                <p className={cn("mt-1 text-[10px] leading-snug", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("From start date and duration (inclusive).")}</p>
              </div>
            </div>
            <div>
              <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}>{translateUi("Related incident")}</label>
              <select
                required
                disabled={incidents.length === 0}
                className={cn("w-full h-10 px-4 py-0 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm disabled:opacity-60", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200 disabled:bg-slate-100 disabled:text-slate-500")}
                value={cdpFormData.incident_id}
                onChange={(e) => setCdpFormData({ ...cdpFormData, incident_id: e.target.value })}
              >
                <option value="" disabled>
                  {incidents.length === 0 ? translateUi("No incidents — add one above first") : translateUi("Select an incident")}
                </option>
                {incidents.map((inc) => (
                  <option key={String(inc.id)} value={String(inc.id)}>
                    {inc.date ? formatDateUK(inc.date) : '—'} — {inc.type ?? translateUi("Incident")}
                    {inc.severity ? ` (${inc.severity})` : ''}
                  </option>
                ))}
              </select>
              {existingCdpForFormIncident && (
                <p
                  role="alert"
                  className={cn(
                    'mt-2 text-sm rounded-lg border px-3 py-2',
                    isDarkMode ? 'border-amber-500/40 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900',
                  )}
                >
                  A CDP plan is already linked to this incident
                  {existingCdpForFormIncident.plan_name ? ` (${existingCdpForFormIncident.plan_name})` : ''}. You cannot assign another plan to the same incident. To use a new plan for this incident, a company or system administrator must remove the existing plan first.
                </p>
              )}
              {incidents.length === 0 && (
                <p className={cn("mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Record at least one incident for this employee before creating a CDP.")}</p>
              )}
            </div>
            <div>
              <label className={cn("block text-xs font-bold uppercase tracking-wider mb-2", isDarkMode ? "text-slate-300" : "text-slate-400")}>{translateUi("Notes / Objectives")}</label>
              <textarea 
                className={cn("w-full px-4 py-2 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500", isDarkMode ? "bg-slate-900 border border-slate-600 text-slate-100" : "bg-white border border-slate-200")}
                rows={2}
                value={cdpFormData.notes}
                onChange={e => setCdpFormData({...cdpFormData, notes: e.target.value})}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCdpForm(false);
                  setShowActivePlanDecisionModal(false);
                }}
                className={cn("px-4 py-2 rounded-xl transition-colors text-sm", isDarkMode ? "text-slate-200 hover:bg-slate-700" : "text-slate-600 hover:bg-slate-200")}
              > {translateUi("Cancel")} </button>
              <button
                type="submit"
                disabled={
                  creatingPlan ||
                  !cdpComputedEndDate ||
                  incidents.length === 0 ||
                  !cdpFormData.incident_id.trim() ||
                  !!existingCdpForFormIncident
                }
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:pointer-events-none"
              >
                {creatingPlan ? 'Creating…' : translateUi("Create Plan")}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {visibleCdpPlans.length === 0 ? (
            <div className={cn("text-center py-12 italic border border-dashed rounded-xl", isDarkMode ? "text-slate-400 border-slate-700" : "text-slate-400 border-slate-200")}>
              {cdpPlanView === 'live' ? translateUi("No live development plans recorded") : translateUi("No closed development plans recorded")}
            </div>
          ) : visibleCdpPlans.map(plan => {
            const dv = plan.duration_value != null ? Number(plan.duration_value) : NaN;
            const du = String(plan.duration_unit ?? '');
            const statusNorm = String(plan.status ?? '').trim().toLowerCase();
            const closedWithTransferred =
              statusNorm === 'closed' &&
              (
                plan.closed_remaining_events_transferred === true ||
                Number(plan.closed_transferred_live_event_count ?? 0) > 0
              );
            const statusLabel = closedWithTransferred ? 'Closed - Remaining Events Transferred' : String(plan.status ?? '');
            const transferredCount = Number(plan.closed_transferred_live_event_count ?? 0);
            const transferredEventNames = Array.isArray(plan.closed_transferred_event_names)
              ? plan.closed_transferred_event_names
                  .map((name: unknown) => String(name ?? '').trim())
                  .filter((name: string) => name.length > 0)
              : [];
            const transferredToPlanId = String(plan.closed_by_new_plan_id ?? '').trim();
            const transferredToPlan = transferredToPlanId
              ? cdpPlans.find((p) => String(p.id) === transferredToPlanId)
              : null;
            const fallbackTransferredEventNames = (() => {
              if (!transferredToPlan) return [] as string[];
              const raw = (transferredToPlan as Record<string, unknown>).monitoring_events;
              const parsed = Array.isArray(raw)
                ? raw
                : typeof raw === 'string'
                  ? (() => {
                      try {
                        const decoded = JSON.parse(raw) as unknown;
                        return Array.isArray(decoded) ? decoded : [];
                      } catch {
                        return [];
                      }
                    })()
                  : [];
              const names = parsed
                .map((row) => String((row as Record<string, unknown>)?.event_name ?? '').trim())
                .filter((name) => name.length > 0);
              const unique = Array.from(new Set(names));
              if (transferredCount > 0) return unique.slice(0, transferredCount);
              return unique;
            })();
            const displayedTransferredEventNames =
              transferredEventNames.length > 0 ? transferredEventNames : fallbackTransferredEventNames;
            const transferredToIncidentType = transferredToPlan?.incident_id != null && String(transferredToPlan.incident_id).trim() !== ''
              ? String(
                  incidents.find((i) => String(i.id) === String(transferredToPlan.incident_id))?.type ?? ''
                ).trim()
              : '';
            const transferredToPlanLabel = transferredToPlan
              ? `${transferredToPlan.plan_name || "CDP Plan"} (${formatDateUK(transferredToPlan.start_date)})`
              : (transferredToPlanId ? `Plan ${transferredToPlanId}` : 'new plan');
            const unitLabel =
              du === 'days' ? (dv === 1 ? 'day' : 'days') : du === 'months' ? (dv === 1 ? 'month' : 'months') : du === 'years' ? (dv === 1 ? 'year' : 'years') : du;
            const durationLine =
              Number.isFinite(dv) && dv >= 1 && unitLabel ? `${dv} ${unitLabel}` : null;
            const monitoringEventCount = (() => {
              const raw = (plan as Record<string, unknown>).monitoring_events;
              if (Array.isArray(raw)) return raw.length;
              if (typeof raw === 'string') {
                try {
                  const parsed = JSON.parse(raw) as unknown;
                  return Array.isArray(parsed) ? parsed.length : 0;
                } catch {
                  return 0;
                }
              }
              return 0;
            })();
            const linkedInc = plan.incident_id != null && String(plan.incident_id) !== ''
              ? incidents.find((i) => String(i.id) === String(plan.incident_id))
              : null;
            return (
            <div key={plan.id} className={cn("p-4 border rounded-xl transition-all", isDarkMode ? "border-slate-700 hover:border-indigo-400/60 bg-slate-900/40" : "border-slate-200 hover:border-indigo-300")}>
              {closedWithTransferred && (
                <div className={cn("mb-3 rounded-xl border px-3 py-2 text-center", isDarkMode ? "border-indigo-400/40 bg-indigo-500/10" : "border-indigo-200 bg-indigo-50")}>
                  <p className={cn("font-bold text-base whitespace-nowrap", isDarkMode ? "text-indigo-100" : "text-indigo-900")}>
                    {translateUi("Closed - Remaining Events Transferred")}
                  </p>
                  <p className={cn("text-xs mt-1", isDarkMode ? "text-indigo-200" : "text-indigo-700")}>
                    {transferredCount > 0 ? transferredCount : 'Some'} event{transferredCount === 1 ? '' : 's'} transferred to {transferredToPlanLabel}
                    {` for ${(transferredToIncidentType || 'related')} incident.`}
                  </p>
                  {displayedTransferredEventNames.length > 0 ? (
                    <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                      {displayedTransferredEventNames.map((eventName) => (
                        <span
                          key={eventName}
                          className={cn(
                            "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
                            isDarkMode
                              ? "border-indigo-300/40 bg-indigo-400/10 text-indigo-100"
                              : "border-indigo-200 bg-white text-indigo-800"
                          )}
                        >
                          {eventName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className={cn("text-[11px] mt-2 italic", isDarkMode ? "text-indigo-200/80" : "text-indigo-700/80")}>
                      {translateUi("Event names were not captured for this transfer.")}
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h4 className={cn("font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{plan.plan_name}</h4>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                    statusNorm === 'active'
                      ? (isDarkMode ? "bg-emerald-500/20 text-emerald-200" : "bg-emerald-100 text-emerald-700 status-chip-positive")
                      : (isDarkMode ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-600")
                  )}>
                    {closedWithTransferred ? translateUi("Closed") : statusLabel}
                  </span>
                  <div className={cn(
                    "inline-flex items-center gap-2 rounded-lg border px-2 py-1",
                    isDarkMode ? "border-indigo-400/40 bg-indigo-500/10" : "border-indigo-200 bg-indigo-50"
                  )}>
                    <span className={cn("text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-200" : "text-indigo-700")}>
                      {translateUi("Monitoring events")}
                    </span>
                    <span className={cn(
                      "inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-[11px] font-bold",
                      isDarkMode ? "bg-indigo-300 text-slate-900" : "bg-indigo-600 text-white"
                    )}>
                      {monitoringEventCount}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMonitoringEventsDraft([]);
                      setSelectedCdpPlanId(String(plan.id));
                      setMonitoringMessage(null);
                    }}
                    className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:border-indigo-400" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-indigo-300")}
                  >
                    <Eye className="w-3.5 h-3.5" /> {translateUi("Open plan page")}
                  </button>
                  {canDeleteCdp && (
                    <button
                      type="button"
                      onClick={() => handleDeleteCdp(plan)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-rose-200 bg-white text-xs font-medium text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> {translateUi("Delete")} </button>
                  )}
                </div>
              </div>
              <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-3", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                <span className="flex items-center"><Calendar className="w-3 h-3 mr-1 shrink-0" /> {formatDateUK(plan.start_date)} {plan.end_date ? `to ${formatDateUK(plan.end_date)}` : '(Ongoing)'}</span>
                {durationLine && (
                  <span className={cn("flex items-center font-medium", isDarkMode ? "text-slate-200" : "text-slate-600")}>Duration: {durationLine}</span>
                )}
              </div>
              {linkedInc && (
                <p className={cn("text-xs mb-2", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                  <span className={cn("font-semibold", isDarkMode ? "text-slate-200" : "text-slate-700")}>{translateUi("Linked incident:")}</span>{' '}
                  {linkedInc.date ? formatDateUK(linkedInc.date) : '—'} — {linkedInc.type ?? translateUi("Incident")}
                  {linkedInc.severity ? ` (${linkedInc.severity})` : ''}
                </p>
              )}
              {!linkedInc && plan.incident_id != null && String(plan.incident_id) !== '' && (
                <p className="text-xs text-amber-700 mb-2">Linked incident no longer in list (id: {String(plan.incident_id)})</p>
              )}
              {plan.notes && <p className={cn("text-sm p-3 rounded-lg border", isDarkMode ? "text-slate-200 bg-slate-800 border-slate-700" : "text-slate-600 bg-slate-50 border-slate-100")}>{plan.notes}</p>}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** Bytes used by one company storage row (prefers total_bytes, else db+attachments). */
function companyStorageRowUsedBytes(row: any): number {
  const total = Number(row?.usage?.total_bytes);
  if (Number.isFinite(total) && total >= 0) return total;
  const db = Math.max(0, Number(row?.usage?.database_bytes ?? 0));
  const att = Math.max(0, Number(row?.usage?.attachments_bytes ?? 0));
  return db + att;
}

/**
 * Aggregate usage across every company row for the system-admin storage donut.
 * Dedupes by company code so the center total matches the sum of table Used values.
 */
function aggregateCompanyStorageUsage(rows: any[]) {
  const list = Array.isArray(rows) ? rows : [];
  const byCode = new Map<string, any>();
  list.forEach((row, index) => {
    const code = String(row?.company_code || row?.companyCode || `row-${index}`).trim().toLowerCase();
    if (!byCode.has(code)) byCode.set(code, row);
  });
  const uniqueRows = Array.from(byCode.values());
  let totalDatabaseBytes = 0;
  let totalAttachmentsBytes = 0;
  let totalUsedBytes = 0;
  let totalLimitBytes = 0;
  for (const row of uniqueRows) {
    const db = Math.max(0, Number(row?.usage?.database_bytes ?? 0));
    const att = Math.max(0, Number(row?.usage?.attachments_bytes ?? 0));
    totalDatabaseBytes += db;
    totalAttachmentsBytes += att;
    // Prefer explicit total_bytes when present; otherwise db+attachments.
    // Never exceed db+att when both category fields are present, so the donut
    // center stays aligned with the Database/Attachments summary lines.
    const listedTotal = companyStorageRowUsedBytes(row);
    const categoryTotal = db + att;
    totalUsedBytes += categoryTotal > 0 ? categoryTotal : listedTotal;
    totalLimitBytes += Math.max(0, Number(row?.limits?.limit_bytes ?? 0));
  }
  return {
    companyCount: uniqueRows.length,
    totalDatabaseBytes,
    totalAttachmentsBytes,
    totalUsedBytes,
    totalLimitBytes,
  };
}

const StorageUsageDonut = ({
  percentUsed,
  databaseBytes,
  attachmentsBytes,
  isDarkMode = false,
  centerContent,
  showCategoryPercentLegend = true,
  variant = "default",
}: {
  percentUsed: number;
  databaseBytes: number;
  attachmentsBytes: number;
  isDarkMode?: boolean;
  centerContent?: string;
  showCategoryPercentLegend?: boolean;
  /** Large ring and type for the company storage widget; default is compact (e.g. system admin). */
  variant?: "default" | "large";
}) => {
  const p = Math.max(0, Math.min(100, Number.isFinite(percentUsed) ? percentUsed : 0));
  const db = Math.max(0, Number(databaseBytes) || 0);
  const att = Math.max(0, Number(attachmentsBytes) || 0);
  const totalUsedBytes = db + att;
  const dbRatio = totalUsedBytes > 0 ? db / totalUsedBytes : 0;
  const dbArcEnd = p * dbRatio;
  const dbPctOfUsed = totalUsedBytes > 0 ? (db / totalUsedBytes) * 100 : 0;
  const attPctOfUsed = totalUsedBytes > 0 ? (att / totalUsedBytes) * 100 : 0;

  const track = isDarkMode ? "#334155" : "#e2e8f0";
  const donutBackground =
    p <= 0
      ? `conic-gradient(${track} 0% 100%)`
      : totalUsedBytes <= 0
        ? `conic-gradient(#4f46e5 0% ${p}%, ${track} ${p}% 100%)`
        : dbArcEnd <= 0.05
          ? `conic-gradient(#0891b2 0% ${p}%, ${track} ${p}% 100%)`
          : dbArcEnd >= p - 0.05
            ? `conic-gradient(#4f46e5 0% ${p}%, ${track} ${p}% 100%)`
            : `conic-gradient(#4f46e5 0% ${dbArcEnd}%, #0891b2 ${dbArcEnd}% ${p}%, ${track} ${p}% 100%)`;

  const ariaParts =
    totalUsedBytes > 0
      ? `Storage usage ${p.toFixed(1)} percent of quota; of used space, ${dbPctOfUsed.toFixed(0)} percent database, ${attPctOfUsed.toFixed(0)} percent attachments`
      : `Storage usage ${p.toFixed(1)} percent`;

  const isLarge = variant === "large";
  return (
    <div className={cn("flex flex-col items-center", isLarge ? "gap-3" : "gap-2")}>
      <div
        className={cn(
          "relative shrink-0 rounded-full",
          isLarge ? "h-64 w-64" : "h-32 w-32"
        )}
        style={{ background: donutBackground }}
        aria-label={ariaParts}
        role="img"
      >
        <div
          className={cn(
            "absolute rounded-full flex items-center justify-center",
            isLarge ? "inset-5 px-1.5" : "inset-3 px-1",
            isDarkMode ? 'bg-slate-900 ring-1 ring-slate-500/50' : 'bg-white'
          )}
        >
          <span
            className={cn(
              "font-bold text-center leading-tight",
              isLarge ? "text-3xl sm:text-4xl" : "text-lg",
              isDarkMode ? "text-slate-50" : "text-slate-900"
            )}
          >
            {centerContent || `${p.toFixed(0)}%`}
          </span>
        </div>
      </div>
      {showCategoryPercentLegend && totalUsedBytes > 0 && p > 0 && (
        <div
          className={cn(
            "flex flex-row flex-nowrap items-center justify-center gap-x-4",
            isLarge ? "text-xs sm:text-sm" : "text-[10px]",
            isDarkMode ? "text-slate-300" : "text-slate-600"
          )}
        >
          <span className="inline-flex items-center gap-1.5 shrink-0">
            <span
              className={cn("shrink-0 rounded-full bg-indigo-600", isLarge ? "h-2.5 w-2.5" : "h-2 w-2")}
              aria-hidden
            />
            Database {dbPctOfUsed.toFixed(0)}%
          </span>
          <span className="inline-flex items-center gap-1.5 shrink-0">
            <span
              className={cn("shrink-0 rounded-full bg-cyan-600", isLarge ? "h-2.5 w-2.5" : "h-2 w-2")}
              aria-hidden
            />
            Attachments {attPctOfUsed.toFixed(0)}%
          </span>
        </div>
      )}
    </div>
  );
};

const CompanyStorageWidget = ({
  companyCode,
  currentUserEmail,
  isCompanyAdmin,
  isDarkMode = false,
  checkoutReturnTab = "addons-storage",
}: {
  companyCode?: string;
  currentUserEmail?: string;
  isCompanyAdmin?: boolean;
  isDarkMode?: boolean;
  /** `tab` query after Stripe storage checkout; must match a valid `activeTab` id. */
  checkoutReturnTab?: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [storage, setStorage] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [purchasedAddonIds, setPurchasedAddonIds] = useState<Set<string>>(new Set());
  const [purchasingPlanId, setPurchasingPlanId] = useState<string | null>(null);
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const plansTrackRef = useRef<HTMLDivElement | null>(null);
  const [planCardWidth, setPlanCardWidth] = useState(260);
  const [offlinePlanId, setOfflinePlanId] = useState<string | null>(null);
  const [offlineMethod, setOfflineMethod] = useState<'bacs' | 'po'>('bacs');
  const [offlineNote, setOfflineNote] = useState('');
  const [offlinePoFileName, setOfflinePoFileName] = useState('');
  const [offlinePoFileBase64, setOfflinePoFileBase64] = useState('');
  const [submittingOffline, setSubmittingOffline] = useState(false);
  const [bankDetails, setBankDetails] = useState<{ accountName: string; accountNumber: string; sortCode: string }>({ accountName: '', accountNumber: '', sortCode: '' });
  const [plansOverflowing, setPlansOverflowing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback((force = false) => {
    if (!companyCode || !currentUserEmail) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`/api/storage/company/${encodeURIComponent(companyCode)}${force ? '?recalculate=1' : ''}`, {
        headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
      }).then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Storage request failed (${res.status})`);
        return data;
      }),
      fetch('/api/storage/plans').then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Plans request failed (${res.status})`);
        return Array.isArray(data.plans) ? data.plans : [];
      }),
      fetch(`/api/addons?companyCode=${encodeURIComponent(companyCode)}`).then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return [];
        return Array.isArray(data.addons) ? data.addons : [];
      }),
      fetch('/api/storage/settings').then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { accountName: '', accountNumber: '', sortCode: '' };
        return {
          accountName: String(data?.bankDetails?.accountName || ''),
          accountNumber: String(data?.bankDetails?.accountNumber || ''),
          sortCode: String(data?.bankDetails?.sortCode || ''),
        };
      }),
    ])
      .then(([storageData, planRows, addons, bank]) => {
        setStorage(storageData);
        setPlans(planRows);
        setPurchasedAddonIds(new Set(addons.map((a: any) => String(a?.addonId || ''))));
        setBankDetails(bank);
      })
      .catch((err) => setError(err.message || 'Failed to load storage usage and plans'))
      .finally(() => setLoading(false));
  }, [companyCode, currentUserEmail]);

  // Always recalculate so Azure/CMS usage is not stuck on a stale empty snapshot.
  useEffect(() => { load(true); }, [load]);
  useEffect(() => {
    setActivePlanIndex(0);
  }, [plans.length]);
  const normalizedActivePlanIndex = plans.length > 0 ? activePlanIndex % plans.length : 0;
  useEffect(() => {
    const el = plansTrackRef.current;
    if (!el) return;
    const checkOverflow = () => {
      setPlansOverflowing(el.scrollWidth > el.clientWidth + 2);
      // Show one tile on small phones, exactly two in the track on larger viewports; additional plans scroll horizontally.
      const isMobileViewport = window.matchMedia("(max-width: 640px)").matches;
      const cardsPerView = isMobileViewport ? 1 : 2;
      const gapPx = 12;
      const totalGaps = gapPx * Math.max(0, cardsPerView - 1);
      const raw = Math.floor((el.clientWidth - totalGaps) / cardsPerView);
      const width = isMobileViewport ? Math.max(140, raw) : Math.max(200, raw);
      setPlanCardWidth(width);
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    const timer = window.setTimeout(checkOverflow, 100);
    return () => {
      window.removeEventListener('resize', checkOverflow);
      window.clearTimeout(timer);
    };
  }, [plans.length]);
  useEffect(() => {
    if (plans.length <= 1 || !plansOverflowing) return;
    const id = window.setInterval(() => {
      setActivePlanIndex((prev) => (prev + 1) % plans.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [plans.length, plansOverflowing]);
  useLayoutEffect(() => {
    const track = plansTrackRef.current;
    if (!track || plans.length === 0) return;
    const card = track.querySelector<HTMLElement>(`[data-carousel-index="${activePlanIndex}"]`);
    if (!card) return;
    // offsetLeft is relative to offsetParent (inner flex), not the scrollport — use geometry so the row actually moves.
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const delta = cardRect.left - trackRect.left;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const nextLeft = Math.max(0, Math.min(track.scrollLeft + delta, max));
    track.scrollTo({ left: nextLeft, behavior: "smooth" });
  }, [activePlanIndex, planCardWidth, plans.length]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');
    const urlCompanyCode = params.get('companyCode');
    const addonId = params.get('addonId') || '';
    if (success === '1' && sessionId && urlCompanyCode && companyCode === urlCompanyCode && addonId) {
      fetch('/api/addons/verify-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, companyCode: urlCompanyCode, addonId }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(data.error || `Verification failed (${res.status})`);
          setMessage({ type: 'success', text: 'Storage plan activated successfully.' });
          load(true);
        })
        .catch((err) => setMessage({ type: 'error', text: err.message || 'Storage plan verification failed' }))
        .finally(() => {
          const tab = params.get('tab');
          window.history.replaceState({}, '', window.location.pathname + (tab ? `?tab=${tab}` : ''));
        });
    }
  }, [companyCode, load]);

  if (!companyCode) return null;

  const usage = storage?.usage || {};
  const limits = storage?.limits || {};
  const status = storage?.status || {};
  const percentUsed = Number(usage.percent_used ?? 0);
  const storageOverrideActive =
    limits.override_suspended === true || status.override_unlimited === true;
  const purchasablePlans = plans.filter((p) => !purchasedAddonIds.has(String(p.id || '')));

  const handlePurchasePlan = async (planId: string) => {
    if (!companyCode || !isCompanyAdmin) return;
    setPurchasingPlanId(planId);
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: planId, companyCode, returnTab: checkoutReturnTab }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setPurchasingPlanId(null);
    }
  };

  const submitOfflineRequest = async () => {
    if (!companyCode || !offlinePlanId) return;
    setSubmittingOffline(true);
    setMessage(null);
    try {
      const res = await fetch('/api/storage/payment-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode, 'X-User-Email': currentUserEmail || '' },
        body: JSON.stringify({
          companyCode,
          planId: offlinePlanId,
          method: offlineMethod,
          note: offlineNote,
          poFileName: offlinePoFileName,
          poFileContentBase64: offlinePoFileBase64,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setMessage({ type: 'success', text: 'Your subscription request has been submitted. Upon review and receipt of payment, Rail Intel Ltd will upgrade your storage capacity.' });
      setOfflinePlanId(null);
      setOfflineMethod('bacs');
      setOfflineNote('');
      setOfflinePoFileName('');
      setOfflinePoFileBase64('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to submit request' });
    } finally {
      setSubmittingOffline(false);
    }
  };

  return (
    <div
      className={cn(
        'w-full min-w-0 max-w-full rounded-2xl border shadow-sm p-5 sm:p-6 lg:p-8',
        isDarkMode
          ? 'bg-slate-900/90 border-slate-500 ring-1 ring-slate-400/20'
          : 'bg-white border-slate-200'
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 min-w-0">
        <div>
          <h3 className={cn('text-lg font-bold', isDarkMode ? "text-slate-50" : "text-slate-900")}>
            {translateUi("Company storage")}
          </h3>
          <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-500")}>
            {translateUi("Usage from CMS records and Azure attachment files.")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-lg border',
            isDarkMode
              ? "border-slate-500 text-slate-200 hover:bg-slate-800"
              : "border-slate-200 text-slate-600 hover:bg-slate-50"
          )}
        > {translateUi("Refresh")} </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600" /></div> ) : error ? ( <p className={cn('text-sm mt-4', isDarkMode ? "text-rose-400" : "text-rose-600")}>{error}</p>
      ) : (
        <div className="mt-5 space-y-5 w-full min-w-0 max-w-full">
          <div className="grid min-h-0 w-full min-w-0 grid-cols-1 items-stretch gap-5 min-h-[28rem] sm:min-h-[32rem] lg:min-h-[40rem] lg:grid-cols-12">
            <div className="flex w-full min-w-0 flex-col items-center justify-center gap-5 self-stretch py-2 sm:flex-row sm:items-start sm:gap-7 sm:py-4 lg:col-span-5 lg:flex-col lg:items-center xl:col-span-4">
              <StorageUsageDonut
                percentUsed={percentUsed}
                databaseBytes={Number(usage.database_bytes ?? 0)}
                attachmentsBytes={Number(usage.attachments_bytes ?? 0)}
                isDarkMode={isDarkMode}
                variant="large"
              />
              <div className="space-y-2.5 text-sm sm:text-base w-full max-w-sm lg:max-w-none text-center sm:text-left">
                <p className={isDarkMode ? "text-slate-200" : "text-slate-700"}>
                  <span className="font-semibold">{translateUi("Used:")}</span> {formatStorageBytes(usage.total_bytes)}
                </p>
                <p className={isDarkMode ? "text-slate-200" : "text-slate-700"}>
                  <span className="font-semibold">{translateUi("Available:")}</span>{' '}
                  {storageOverrideActive
                    ? 'Temporary Override by System Admin'
                    : formatStorageBytes(limits.limit_bytes)}
                </p>
                {storageOverrideActive && (
                  <div
                    className={cn(
                      'rounded-xl border px-3 py-3 text-left text-sm',
                      isDarkMode
                        ? 'border-indigo-400/40 bg-indigo-500/10 text-indigo-100'
                        : 'border-indigo-200 bg-indigo-50 text-indigo-900'
                    )}
                  >
                    <p className="font-semibold">{translateUi("Temporary unlimited storage allowance")}</p>
                    <p className={cn('mt-1 text-xs leading-relaxed', isDarkMode ? 'text-indigo-200/80' : 'text-indigo-700')}>
                      Your configured storage limit is {formatStorageBytes(limits.limit_bytes)}.
                      While the System Admin override is active, your current allowance is unlimited.
                      When the override is switched off, your allowance will automatically return to your
                      original plan, any enhanced purchased storage plan, or the plan set by Rail Intel.
                    </p>
                  </div>
                )}
                <p className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
                  CMS records: {formatStorageBytes(usage.database_bytes)} · Attachments:{' '}
                  {formatStorageBytes(usage.attachments_bytes)}
                </p>
                {Number(storage?.diagnostics?.employee_count ?? 0) > 0 ? (
                  <p className={cn('text-xs', isDarkMode ? "text-slate-500" : "text-slate-400")}>
                    {Number(storage.diagnostics.employee_count)} employees measured
                    {storage?.diagnostics?.attachments?.azure_configured === false
                      ? ' · Azure Blob not configured on server'
                      : ''}
                  </p>
                ) : (
                  <p className={cn('text-xs', isDarkMode ? 'text-amber-300/80' : 'text-amber-700')}>
                    No employees matched this company code for storage measurement.
                    {storage?.diagnostics?.attachments?.azure_configured === false
                      ? ' Azure Blob is also not configured.'
                      : ''}
                  </p>
                )}
              </div>
            </div>
            <div
              className={cn(
                "lg:col-span-7 xl:col-span-8 flex h-full min-h-0 w-full min-w-0 max-w-full flex-col rounded-2xl border p-3 sm:p-4",
                isDarkMode
                  ? "border-slate-400 bg-slate-950/85 ring-2 ring-slate-500/40 shadow-lg shadow-black/30"
                  : "border-slate-200 bg-slate-50/70"
              )}
            >
              <div className="mb-3 flex min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <div>
                  <p className={cn("text-sm font-semibold", isDarkMode ? "text-slate-50" : "text-slate-900")}>
                    {translateUi("Storage plans")}
                  </p>
                  <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Two plan cards in view on larger screens; scroll the row to see the rest, or use the dots to jump.")}
                  </p>
                </div>
              </div>
              <div
                ref={plansTrackRef}
                className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden scroll-pl-0.5 pb-1 pt-0.5 no-scrollbar snap-x snap-mandatory"
              >
                <div className="flex h-full min-h-[23rem] min-w-max items-stretch gap-3 sm:min-h-[27rem]">
                  {plans.map((plan, displayIdx) => {
                    const planId = String(plan?.id || '');
                    const isPurchased = purchasedAddonIds.has(planId);
                    const hasStripePriceId = String(plan?.stripe_price_id || '').trim().length > 0;
                    return (
                      <div
                        key={`${planId}-${displayIdx}`}
                        data-carousel-index={displayIdx}
                        className="flex h-full min-h-0 shrink-0 snap-start"
                        style={{ width: `${planCardWidth}px` }}
                      >
                        <div
                          className={cn(
                            "relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border p-0",
                            isDarkMode
                              ? "border-slate-400 bg-slate-900/90 ring-1 ring-slate-400/35 shadow-md shadow-black/40"
                              : "border-slate-200 bg-white"
                          )}
                        >
                          <div
                            className="pointer-events-none relative h-20 w-full shrink-0 overflow-hidden bg-zinc-100 sm:h-24"
                            aria-hidden
                          >
                            <div
                              className="absolute inset-0 opacity-50"
                              style={{ backgroundImage: "url('/GBR_BG.png')", backgroundSize: "cover", backgroundPosition: "top center" }}
                            />
                          </div>
                          {isDarkMode && (
                            <div
                              className="pointer-events-none absolute left-0 right-0 top-0 z-[5] h-20 sm:h-24 bg-slate-950/55"
                              aria-hidden
                            />
                          )}
                          {plan?.popular === true && (
                            <div className="pointer-events-none absolute right-0 top-0 z-20 h-20 w-20 overflow-hidden">
                              <div className="absolute right-[-34px] top-[14px] w-[145px] rotate-45 bg-amber-500 py-1 text-center shadow-sm">
                                <span className="whitespace-nowrap text-[9px] font-bold uppercase tracking-wide text-white">{translateUi("Most popular")}</span>
                              </div>
                            </div>
                          )}
                          <div className="relative z-10 flex h-full min-h-0 min-w-0 flex-1 flex-col p-3 sm:p-4">
                            <div className="shrink-0 pr-12 pb-0 sm:pr-24">
                              <p
                                className={cn(
                                  "font-semibold break-words text-sm sm:text-base",
                                  isDarkMode ? "text-slate-50" : "text-slate-900"
                                )}
                              >
                                {plan?.name || planId}
                              </p>
                              <p
                                className={cn("mt-0.5 text-xs", isDarkMode ? "text-slate-300" : "text-slate-500")}
                              >
                                +{Number(plan?.storage_gb || 0)} GB · {plan?.price_display || "Price on request"}
                              </p>
                              {!hasStripePriceId && !isPurchased && (
                                <p
                                  className={cn("mt-2 text-[11px]", isDarkMode ? "text-amber-300" : "text-amber-700")}
                                >
                                  {translateUi("Stripe price ID is not configured for this plan yet.")}
                                </p>
                              )}
                            </div>
                            <div
                              className={cn(
                                "relative z-10 -mx-3 mt-2 flex min-h-0 w-[calc(100%+1.5rem)] min-w-0 max-w-[calc(100%+1.5rem)] flex-1 flex-col",
                                "min-h-[9rem] pb-1.5 sm:-mx-4 sm:min-h-[11rem] sm:w-[calc(100%+2rem)] sm:max-w-[calc(100%+2rem)] sm:pb-2 lg:min-h-[13rem]"
                              )}
                            >
                              <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
                                <img
                                  src="/storage-plan-illustration.png"
                                  alt=""
                                  className="block h-full w-full min-h-0 min-w-0 max-w-none object-contain object-center"
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                            </div>
                            <div className="mt-auto w-full min-w-0 shrink-0 pr-12 sm:pr-24">
                              {isPurchased ? (
                                <div className="flex min-h-[2rem] items-center">
                                  <span
                                    className={cn(
                                      "inline-flex items-center gap-1 text-xs font-medium",
                                      isDarkMode ? "text-emerald-400" : "text-emerald-700"
                                    )}
                                  >
                                    <CheckCircle2 className="h-4 w-4" /> {translateUi("Purchased")} </span>
                                </div>
                              ) : (
                                <div className="w-full min-w-0">
                                  <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      disabled={!isCompanyAdmin || purchasingPlanId === planId || !hasStripePriceId}
                                      onClick={() => handlePurchasePlan(planId)}
                                      className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-left text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {purchasingPlanId === planId
                                        ? "Redirecting…"
                                        : hasStripePriceId
                                          ? translateUi("*Subscribe (card)")
                                          : translateUi("Card unavailable")}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!isCompanyAdmin}
                                      onClick={() => setOfflinePlanId(planId)}
                                      className={cn(
                                        "shrink-0 rounded-lg border px-3 py-1.5 text-xs font-bold shadow-sm disabled:opacity-50",
                                        isDarkMode
                                          ? "border-amber-500/80 bg-amber-950/80 text-amber-100 hover:bg-amber-900/90"
                                          : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                                      )}
                                    >
                                      {translateUi("Pay by BACS / PO (Offline)")}
                                    </button>
                                  </div>
                                  {hasStripePriceId && purchasingPlanId !== planId && (
                                    <p
                                      className={cn(
                                        "mt-1.5 w-full pl-0.5 text-[9px] leading-snug sm:text-[10px]",
                                        isDarkMode ? "text-slate-400" : "text-slate-500"
                                      )}
                                    >
                                      {translateUi("* Additional transaction fees may apply.")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {plans.length === 0 && (
                    <div className="shrink-0 snap-start" style={{ width: `${planCardWidth}px` }}>
                      <p className={cn('text-sm', isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        {translateUi("No storage plans are currently available. Contact support.")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {plans.length > 1 && plansOverflowing && (
                <div className="mt-3 flex items-center justify-center gap-1.5">
                  {plans.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActivePlanIndex(idx)}
                      className={cn(
                        'h-1.5 rounded-full transition-all',
                        idx === normalizedActivePlanIndex
                          ? isDarkMode
                            ? "w-5 bg-indigo-400 shadow-[0_0_10px_rgba(129,140,248,0.55)]"
                            : "w-5 bg-indigo-600"
                          : isDarkMode
                            ? "w-2 bg-slate-500 hover:bg-slate-400"
                            : "w-2 bg-slate-300"
                      )}
                      aria-label={translateUi("View plan {{n}}", { n: idx + 1 })}
                    />
                  ))}
                </div>
              )}
              <p
                className={cn(
                  'mt-3 text-sm leading-relaxed',
                  isDarkMode ? "text-slate-300" : "text-slate-600"
                )}
              >
                Our storage plans are powered by high-performance SSDs to ensure lightning-fast data access and
                seamless reliability.
              </p>
            </div>
          </div>
          {message && (
            <div
              className={cn(
                'p-3 rounded-xl text-sm border',
                message.type === 'success'
                  ? isDarkMode
                    ? 'bg-emerald-950/70 text-emerald-200 border-emerald-600/50'
                    : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : isDarkMode
                    ? 'bg-rose-950/70 text-rose-200 border-rose-600/50'
                    : 'bg-rose-50 text-rose-800 border-rose-200'
              )}
            >
              {message.text}
            </div>
          )}
          {status.suspended && (
            <div
              className={cn(
                'p-3 rounded-xl text-sm border',
                isDarkMode
                  ? 'border-rose-500/60 bg-rose-950/60 text-rose-100'
                  : 'border-rose-200 bg-rose-50 text-rose-800'
              )}
            >
              Storage is currently suspended because your company has reached its limit. Uploads are blocked until a
              storage plan is purchased, or a system administrator applies an override.
            </div>
          )}
          {!isCompanyAdmin && purchasablePlans.length > 0 && (
            <p className={cn('text-xs', isDarkMode ? "text-slate-400" : "text-slate-500")}>
              {translateUi("Company administrator access is required to purchase storage plans.")}
            </p>
          )}
          {offlinePlanId && (
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4">
              <div className="bg-white rounded-2xl p-5 max-w-lg w-full shadow-xl">
                <h4 className="text-lg font-bold text-slate-900">{translateUi("Request subscription via BACS / PO")}</h4>
                <p className="text-sm text-slate-500 mt-1">{translateUi("Submit an offline recurring subscription request for admin approval.")}</p>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{translateUi("Payment method")}</label>
                    <select value={offlineMethod} onChange={(e) => setOfflineMethod(e.target.value as 'bacs' | 'po')} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                      <option value="bacs">{translateUi("BACS transfer")}</option>
                      <option value="po"> {translateUi("Purchase order (PO)")} </option>
                    </select>
                  </div>
                  {offlineMethod === 'bacs' && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                      <p className="font-semibold text-amber-900 mb-1">{translateUi("BACS payment details")}</p>
                      <p className="text-amber-900">{translateUi("Account name:")} <span className="font-mono">{bankDetails.accountName || 'Not configured'}</span></p>
                      <p className="text-amber-900">{translateUi("Account number:")} <span className="font-mono">{bankDetails.accountNumber || 'Not configured'}</span></p>
                      <p className="text-amber-900">{translateUi("Sort code:")} <span className="font-mono">{bankDetails.sortCode || 'Not configured'}</span></p>
                    </div>
                  )}
                  {offlineMethod === 'po' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{translateUi("Upload PO document")}</label>
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <input
                          id="offline-po-upload"
                          type="file"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const r = new FileReader();
                            r.onload = () => {
                              const s = String(r.result || '');
                              const base64 = s.includes(',') ? s.split(',')[1] : s;
                              setOfflinePoFileName(f.name);
                              setOfflinePoFileBase64(base64);
                            };
                            r.readAsDataURL(f);
                          }}
                          className="sr-only"
                        />
                        <label htmlFor="offline-po-upload" className="inline-flex items-center gap-2 cursor-pointer">
                          <span className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700">{translateUi("Choose file")}</span>
                          <span className="text-xs font-medium text-slate-700">{offlinePoFileName || 'No file chosen'}</span>
                        </label>
                        <p className="text-[11px] text-amber-800 mt-2">{translateUi("A PO file is required before you can submit this request.")}</p>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1"> {translateUi("Notes (optional)")} </label>
                    <textarea value={offlineNote} onChange={e => setOfflineNote(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Reference, invoice notes, or internal purchase details" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setOfflinePlanId(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700"> {translateUi("Cancel")} </button>
                  <button
                    onClick={submitOfflineRequest}
                    disabled={submittingOffline || (offlineMethod === 'po' && !offlinePoFileBase64)}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50"
                  >
                    {submittingOffline ? translateUi("Submitting…") : translateUi("Submit request")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompanyStorageManagementPage = ({ currentUserEmail, isDarkMode = false }: { currentUserEmail?: string; isDarkMode?: boolean }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [storagePlans, setStoragePlans] = useState<any[]>([]);
  const [paymentRequests, setPaymentRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState<string | null>(null);
  const [limitGb, setLimitGb] = useState<string>('5');
  const [limitUnit, setLimitUnit] = useState<'MB' | 'GB' | 'TB'>('GB');
  const [warningPercent, setWarningPercent] = useState<string>('80');
  const [upgradeOfferPercent, setUpgradeOfferPercent] = useState<string>('90');
  const [bankDetails, setBankDetails] = useState<{ accountName: string; accountNumber: string; sortCode: string }>({ accountName: '', accountNumber: '', sortCode: '' });

  const parseStorageLimitInput = (raw: string): number | null => {
    const normalized = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/(mb|gb|tb)\b/g, '')
      .replace(',', '.')
      .trim();
    const n = Number(normalized);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  };

  const load = useCallback((force = false) => {
    if (!currentUserEmail) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`/api/storage/companies${force ? '?recalculate=1' : ''}`, { headers: { 'X-User-Email': currentUserEmail } })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
          return Array.isArray(data.companies) ? data.companies : [];
        }),
      fetch('/api/storage/plans?includeInactive=1', { headers: { 'X-User-Email': currentUserEmail } })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
          return Array.isArray(data.plans) ? data.plans : [];
        }),
      fetch('/api/storage/payment-requests', { headers: { 'X-User-Email': currentUserEmail } })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
          return Array.isArray(data.requests) ? data.requests : [];
        }),
      fetch('/api/storage/settings?admin=1', { headers: { 'X-User-Email': currentUserEmail } })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
          return {
            accountName: String(data?.bankDetails?.accountName || ''),
            accountNumber: String(data?.bankDetails?.accountNumber || ''),
            sortCode: String(data?.bankDetails?.sortCode || ''),
          };
        }),
    ])
      .then(([companyRows, plans, requests, bank]) => {
        setRows(companyRows);
        setStoragePlans(plans);
        setPaymentRequests(requests);
        setBankDetails(bank);
      })
      .catch((err) => setError(err.message || 'Unable to load storage usage'))
      .finally(() => setLoading(false));
  }, [currentUserEmail]);

  useEffect(() => { load(true); }, [load]);

  const beginEdit = (row: any) => {
    setEditingCompany(String(row.company_code || row.companyCode || ''));
    const limitBytes = Number(row?.limits?.limit_bytes || 0);
    const mb = limitBytes / (1024 * 1024);
    const gb = limitBytes / (1024 * 1024 * 1024);
    if (gb >= 1000) {
      setLimitUnit('TB');
      setLimitGb(String((gb / 1000).toFixed(2).replace(/\.00$/, '')));
    } else if (gb >= 1) {
      setLimitUnit('GB');
      setLimitGb(String(Math.max(1, Math.round(gb || 1))));
    } else {
      setLimitUnit('MB');
      setLimitGb(String(Math.max(0.01, Number((mb || 1).toFixed(2)))));
    }
    setWarningPercent(String(Math.round(Number(row?.limits?.warning_percent ?? 80))));
    setUpgradeOfferPercent(String(Math.round(Number(row?.limits?.upgrade_offer_percent ?? 90))));
  };

  const save = async () => {
    if (!editingCompany || !currentUserEmail) return;
    const parsedLimit = parseStorageLimitInput(limitGb);
    if (parsedLimit == null) {
      setError('Enter a valid storage limit value (for example 1.07 with MB selected).');
      return;
    }
    const entered = Math.max(0.01, parsedLimit);
    const gbValue = limitUnit === 'TB' ? entered * 1000 : limitUnit === 'MB' ? entered / 1024 : entered;
    const limitBytes = Math.max(1, gbValue * 1024 * 1024 * 1024);
    const res = await fetch(`/api/storage/company/${encodeURIComponent(editingCompany)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
      body: JSON.stringify({
        limitBytes,
        warningPercent: Number(warningPercent),
        upgradeOfferPercent: Number(upgradeOfferPercent),
      }),
    });
    if (res.ok) {
      setEditingCompany(null);
      load(true);
    } else {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Failed (${res.status})`);
    }
  };

  const toggleOverride = async (companyCode: string, nextValue: boolean) => {
    if (!currentUserEmail) return;
    const res = await fetch(`/api/storage/company/${encodeURIComponent(companyCode)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
      body: JSON.stringify({ overrideSuspended: nextValue }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Failed (${res.status})`);
      return;
    }
    setError(null);
    load(true);
  };

  const savePlans = async () => {
    if (!currentUserEmail) return;
    const normalized = storagePlans.map((p, index) => ({
      id: String(p?.id || '').trim(),
      name: String(p?.name || '').trim(),
      storage_gb: Number(p?.storage_gb || 0),
      price_display: String(p?.price_display || '').trim(),
      price_pence: Number(p?.price_pence || 0),
      stripe_price_id: String(p?.stripe_price_id || '').trim(),
      active: p?.active !== false,
      popular: p?.popular === true,
      sort_order: Number(p?.sort_order ?? index),
    }));
    const invalid = normalized.some((p) => !p.id || !p.name || !Number.isFinite(p.storage_gb) || p.storage_gb <= 0 || !p.price_display);
    if (invalid) {
      setError('Each storage plan needs id, name, storage GB, and display price.');
      return;
    }
    const res = await fetch('/api/storage/plans', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
      body: JSON.stringify({ plans: normalized }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Failed (${res.status})`);
      return;
    }
    setError(null);
    load(false);
  };

  const saveBankDetails = async () => {
    if (!currentUserEmail) return;
    const res = await fetch('/api/storage/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
      body: JSON.stringify({ bankDetails }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Failed (${res.status})`);
      return;
    }
    setError(null);
    load(false);
  };

  const updatePaymentRequest = async (id: string, action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'delete') => {
    if (!currentUserEmail) return;
    const res = await fetch(`/api/storage/payment-requests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
      body: JSON.stringify({ action }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Failed (${res.status})`);
      return;
    }
    setError(null);
    load(true);
  };

  const viewPoAttachment = async (id: string) => {
    if (!currentUserEmail) return;
    const res = await fetch(`/api/storage/payment-requests/${encodeURIComponent(id)}/po-download`, {
      headers: { 'X-User-Email': currentUserEmail },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || `Failed (${res.status})`);
      return;
    }
    const fileName = String(data?.fileName || 'purchase-order');
    const content = String(data?.content || '');
    const a = document.createElement('a');
    a.href = `data:application/octet-stream;base64,${content}`;
    a.download = fileName;
    a.click();
  };

  const {
    companyCount,
    totalDatabaseBytes,
    totalAttachmentsBytes,
    totalUsedBytes,
    totalLimitBytes,
  } = aggregateCompanyStorageUsage(rows);
  const donutStandardMaxBytes = 1024 * 1024 * 1024 * 1024; // 1 TB platform baseline
  const overallPercentOfCombinedLimits =
    totalLimitBytes > 0 ? Math.min(100, Math.max(0, (totalUsedBytes / totalLimitBytes) * 100)) : 0;
  const overallPercentOfPlatform =
    Math.min(100, Math.max(0, (totalUsedBytes / donutStandardMaxBytes) * 100));
  // Ring fill is the real combined usage % (vs allocated company limits, else 1 TB).
  // Center label is the summed byte total across every company in the table.
  const donutPercentUsed =
    totalLimitBytes > 0 ? overallPercentOfCombinedLimits : overallPercentOfPlatform;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "rounded-2xl border shadow-sm px-4 py-3 shrink-0",
              isDarkMode ? "bg-slate-900 border-slate-600" : "bg-white border-slate-200"
            )}
          >
            <StorageUsageDonut
              percentUsed={donutPercentUsed}
              databaseBytes={totalDatabaseBytes}
              attachmentsBytes={totalAttachmentsBytes}
              centerContent={formatStorageBytes(totalUsedBytes)}
              showCategoryPercentLegend={false}
              isDarkMode={isDarkMode}
            />
          </div>
          <div className="max-w-xl">
            <h2 className={cn("text-2xl font-bold", isDarkMode ? "text-slate-50" : "text-slate-900")}>
              {translateUi("Company storage management")}
            </h2>
            <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>
              {translateUi("Monitor usage and control per-company storage limits.")}
            </p>
            <p className={cn("text-xs mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>
              Total across {companyCount} {companyCount === 1 ? "company" : "companies"}:{" "}
              {formatStorageBytes(totalUsedBytes)}
              {totalLimitBytes > 0
                ? ` · ${overallPercentOfCombinedLimits.toFixed(1)}% of combined limits (${formatStorageBytes(totalLimitBytes)})`
                : null}
            </p>
            <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
              Platform baseline 1 TB: {formatStorageBytes(totalUsedBytes)} of{" "}
              {formatStorageBytes(donutStandardMaxBytes)} ({overallPercentOfPlatform.toFixed(2)}%)
            </p>
            <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
              Database used: {formatStorageBytes(totalDatabaseBytes)} · Attachments used:{" "}
              {formatStorageBytes(totalAttachmentsBytes)}
            </p>
          </div>
        </div>
        <button type="button" onClick={() => load(true)} className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">{translateUi("Recalculate all")}</button>
      </div>
      {error && <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm">{error}</div>}
      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left"> {translateUi("Company")} </th>
                <th className="px-4 py-3 text-left"> {translateUi("Used")} </th>
                <th className="px-4 py-3 text-left"> {translateUi("Limit")} </th>
                <th className="px-4 py-3 text-left"> {translateUi("Usage")} </th>
                <th className="px-4 py-3 text-left"> {translateUi("Status")} </th>
                <th className="px-4 py-3 text-right"> {translateUi("Actions")} </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const companyCode = String(row.company_code || row.companyCode || '');
                const isEditing = editingCompany === companyCode;
                return (
                  <tr key={companyCode} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{row.companyName || companyCode}</p>
                      <p className="text-xs text-slate-500 font-mono">{companyCode}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{formatStorageBytes(row?.usage?.total_bytes)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatStorageBytes(row?.limits?.limit_bytes)}</td>
                    <td className="px-4 py-3 text-slate-700">{Number(row?.usage?.percent_used ?? 0).toFixed(1)}%</td>
                    <td className="px-4 py-3">
                      <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium border",
                        row?.limits?.override_suspended === true
                          ? (isDarkMode ? "bg-indigo-500/25 text-indigo-100 border-indigo-300/50" : "bg-indigo-100 text-indigo-700 border-indigo-200")
                          : row?.status?.over_limit
                            ? (isDarkMode ? "bg-rose-500/25 text-rose-100 border-rose-300/50" : "bg-rose-100 text-rose-700 border-rose-200")
                            : row?.status?.warning
                              ? (isDarkMode ? "bg-amber-500/25 text-amber-100 border-amber-300/50" : "bg-amber-100 text-amber-700 border-amber-200")
                              : (isDarkMode ? "bg-emerald-500/25 text-emerald-100 border-emerald-300/50" : "bg-emerald-100 text-emerald-700 border-emerald-200")
                      )}>
                        {row?.limits?.override_suspended === true
                          ? 'Override (Unlimited)'
                          : row?.status?.over_limit
                            ? 'Over limit'
                            : row?.status?.warning
                              ? translateUi("Warning")
                              : 'Healthy'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex flex-wrap justify-end items-end gap-2">
                            <label className="flex flex-col items-start gap-0.5 text-left">
                              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500"> {translateUi("Limit")} </span>
                              <div className="flex gap-1">
                                <input value={limitGb} onChange={e => setLimitGb(e.target.value)} className="w-20 px-2 py-1 border border-slate-200 rounded-lg" title={translateUi("Storage limit")} aria-label={translateUi("Storage limit")} />
                                <select value={limitUnit} onChange={e => setLimitUnit(e.target.value as 'MB' | 'GB' | 'TB')} className="w-16 px-2 py-1 border border-slate-200 rounded-lg bg-white" title={translateUi("Limit unit")} aria-label={translateUi("Limit unit")}>
                                  <option value="MB">MB</option>
                                  <option value="GB">GB</option>
                                  <option value="TB">TB</option>
                                </select>
                              </div>
                            </label>
                            <label className="flex flex-col items-start gap-0.5 text-left" title={translateUi("Show a storage warning when usage reaches this percentage of the limit.")}>
                              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{translateUi("Warning %")}</span>
                              <input value={warningPercent} onChange={e => setWarningPercent(e.target.value)} className="w-16 px-2 py-1 border border-slate-200 rounded-lg" aria-label={translateUi("Warning percentage")} />
                            </label>
                            <label className="flex flex-col items-start gap-0.5 text-left" title={translateUi("Offer a storage upgrade when usage reaches this percentage of the limit.")}>
                              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{translateUi("Offer %")}</span>
                              <input value={upgradeOfferPercent} onChange={e => setUpgradeOfferPercent(e.target.value)} className="w-16 px-2 py-1 border border-slate-200 rounded-lg" aria-label={translateUi("Upgrade offer percentage")} />
                            </label>
                            <button onClick={save} className="px-3 py-1 bg-indigo-600 text-white rounded-lg self-end"> {translateUi("Save")} </button>
                            <button onClick={() => setEditingCompany(null)} className="px-3 py-1 border border-slate-200 rounded-lg self-end"> {translateUi("Cancel")} </button>
                          </div>
                          <p className="max-w-md text-left text-[11px] leading-snug text-slate-500">
                            <span className="font-medium text-slate-600">{translateUi("Warning %")}</span> shows an alert when usage reaches this share of the limit.
                            {' '}<span className="font-medium text-slate-600">{translateUi("Offer %")}</span> {translateUi("prompts the company to upgrade storage at this higher threshold.")}
                          </p>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => beginEdit(row)} className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-100"> {translateUi("Manage")} </button>
                          <button
                            onClick={() => toggleOverride(companyCode, !(row?.limits?.override_suspended === true))}
                            className={cn(
                              "px-3 py-1.5 rounded-lg text-xs font-medium border",
                              row?.limits?.override_suspended === true
                                ? "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                                : "border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100"
                            )}
                          >
                            {row?.limits?.override_suspended === true ? translateUi("Override On") : translateUi("Override Off")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">{translateUi("No company storage data available.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{translateUi("Storage plans")}</h3>
            <p className="text-sm text-slate-500">{translateUi("Control company upgrade plans and pricing shown on company dashboards.")}</p>
            <p className="text-xs text-slate-500 mt-1"> {translateUi("Field guide: Plan ID (unique key like")} <span className="font-mono">{translateUi("storage-plus-250gb")}</span>), Name (customer label), Storage GB (extra limit), Price display (e.g. £19/month), Stripe price ID (optional until Stripe setup), Active (visible to companies), Popular (shows "Most popular" badge on company dashboard).</p>
          </div>
          <button type="button" onClick={savePlans} className="self-start sm:self-auto shrink-0 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">{translateUi("Save plans")}</button>
        </div>
        <div className="space-y-3">
          {storagePlans.map((plan, idx) => (
            <div key={String(plan?.id || idx)} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-2 items-center border border-slate-200 rounded-xl p-3">
              <input value={String(plan?.id || '')} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, id: e.target.value } : p))} className="w-full min-w-0 xl:col-span-2 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Plan ID (unique)" title={translateUi("Unique internal key, e.g. storage-plus-250gb")} />
              <input value={String(plan?.name || '')} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))} className="w-full min-w-0 xl:col-span-4 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Plan name shown to companies" title={translateUi("Customer-facing plan name")} />
              <input value={String(plan?.storage_gb ?? '')} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, storage_gb: Number(e.target.value || 0) } : p))} className="w-full min-w-0 xl:col-span-2 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="GB" title={translateUi("Extra storage this plan adds in gigabytes")} />
              <input value={String(plan?.price_display || '')} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, price_display: e.target.value } : p))} className="w-full min-w-0 xl:col-span-4 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Price display (e.g. £19/month)" title={translateUi("Displayed price text on company dashboard")} />
              <input value={String(plan?.stripe_price_id || '')} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, stripe_price_id: e.target.value } : p))} className="w-full min-w-0 sm:col-span-2 xl:col-span-8 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Stripe price id (optional for now)" title={translateUi("Required only when Stripe checkout is enabled")} />
              <div className="sm:col-span-2 xl:col-span-4 flex flex-wrap items-center justify-start xl:justify-end gap-x-4 gap-y-2 pt-1">
                <label className="text-xs text-slate-600 flex items-center gap-1.5 whitespace-nowrap"><input type="checkbox" checked={plan?.active !== false} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, active: e.target.checked } : p))} /> {translateUi("Active")} </label>
                <label className="text-xs text-slate-600 flex items-center gap-1.5 whitespace-nowrap"><input type="checkbox" checked={plan?.popular === true} onChange={e => setStoragePlans(prev => prev.map((p, i) => i === idx ? { ...p, popular: e.target.checked } : p))} /> {translateUi("Popular")} </label>
                <button type="button" onClick={() => setStoragePlans(prev => prev.filter((_, i) => i !== idx))} className="text-xs px-2 py-1 rounded border border-slate-200 text-rose-600 hover:bg-rose-50"> {translateUi("Remove")} </button>
              </div>
            </div>
          ))}
          {storagePlans.length === 0 && <p className="text-sm text-slate-500">{translateUi("No plans yet.")}</p>}
          <button
            type="button"
            onClick={() => setStoragePlans(prev => [...prev, { id: `storage-plan-${prev.length + 1}`, name: '', storage_gb: 100, price_display: '', price_pence: 0, stripe_price_id: '', active: true, popular: false, sort_order: prev.length }])}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
          >
            {translateUi("Add plan")}
          </button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{translateUi("BACS bank details")}</h3>
            <p className="text-sm text-slate-500">{translateUi("Shown to company admins when they choose BACS for storage subscriptions.")}</p>
          </div>
          <button type="button" onClick={saveBankDetails} className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">{translateUi("Save bank details")}</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={bankDetails.accountName} onChange={e => setBankDetails(prev => ({ ...prev, accountName: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Account name" />
          <input value={bankDetails.accountNumber} onChange={e => setBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Account number" />
          <input value={bankDetails.sortCode} onChange={e => setBankDetails(prev => ({ ...prev, sortCode: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Sort code (e.g. 12-34-56)" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900"> {translateUi("Offline subscription requests (BACS / PO)")} </h3>
          <p className="text-sm text-slate-500">{translateUi("Approve to activate a recurring storage subscription for the company.")}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left"> {translateUi("Company")} </th>
                <th className="px-3 py-2 text-left"> {translateUi("Plan")} </th>
                <th className="px-3 py-2 text-left"> {translateUi("Method")} </th>
                <th className="px-3 py-2 text-left"> {translateUi("Status")} </th>
                <th className="px-3 py-2 text-left"> {translateUi("Requested")} </th>
                <th className="px-3 py-2 text-left">{translateUi("PO attachment")}</th>
                <th className="px-3 py-2 text-right"> {translateUi("Actions")} </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paymentRequests.map((r) => (
                <tr key={String(r?.id || '')}>
                  <td className="px-3 py-2 text-slate-800 font-mono">{String(r?.company_code || '—')}</td>
                  <td className="px-3 py-2 text-slate-700">{String(r?.plan_name || r?.plan_id || '—')}</td>
                  <td className="px-3 py-2 text-slate-700 uppercase">{String(r?.payment_method || '—')}</td>
                  <td className="px-3 py-2">
                    <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium border",
                      r?.status === 'approved'
                        ? (isDarkMode ? 'bg-emerald-500/25 text-emerald-100 border-emerald-300/50' : 'bg-emerald-100 text-emerald-700 border-emerald-200')
                        : r?.status === 'rejected'
                          ? (isDarkMode ? 'bg-rose-500/25 text-rose-100 border-rose-300/50' : 'bg-rose-100 text-rose-700 border-rose-200')
                          : r?.status === 'suspended'
                            ? (isDarkMode ? 'bg-amber-500/25 text-amber-100 border-amber-300/50' : 'bg-amber-100 text-amber-700 border-amber-200')
                            : r?.status === 'deleted'
                              ? (isDarkMode ? 'bg-slate-500/25 text-slate-100 border-slate-300/50' : 'bg-slate-200 text-slate-700 border-slate-300')
                              : (isDarkMode ? 'bg-amber-500/25 text-amber-100 border-amber-300/50' : 'bg-amber-100 text-amber-700 border-amber-200')
                    )}>{String(r?.status || 'pending')}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{formatDateTimeUKShort(String(r?.requested_at || ''))}</td>
                  <td className="px-3 py-2">
                    {String(r?.payment_method || '') === 'po' && String(r?.po_storage_path || '').trim() ? (
                      <button onClick={() => viewPoAttachment(String(r.id))} className="px-2.5 py-1 rounded-md border border-slate-200 text-xs text-slate-700 hover:bg-slate-100">{translateUi("View PO")}</button>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {String(r?.status || '') === 'pending' ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => updatePaymentRequest(String(r.id), 'approve')} className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs"> {translateUi("Approve")} </button>
                        <button onClick={() => updatePaymentRequest(String(r.id), 'suspend')} className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs">{translateUi("Suspend")}</button>
                        <button onClick={() => updatePaymentRequest(String(r.id), 'reject')} className="px-2.5 py-1 rounded-md bg-rose-600 text-white text-xs"> {translateUi("Reject")} </button>
                        <button onClick={() => updatePaymentRequest(String(r.id), 'delete')} className="px-2.5 py-1 rounded-md bg-slate-700 text-white text-xs"> {translateUi("Delete")} </button>
                      </div>
                    ) : String(r?.status || '') === 'approved' ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => updatePaymentRequest(String(r.id), 'suspend')} className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs">{translateUi("Suspend")}</button>
                        <button onClick={() => updatePaymentRequest(String(r.id), 'delete')} className="px-2.5 py-1 rounded-md bg-slate-700 text-white text-xs"> {translateUi("Delete")} </button>
                      </div>
                    ) : String(r?.status || '') === 'suspended' ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => updatePaymentRequest(String(r.id), 'reactivate')} className="px-2.5 py-1 rounded-md bg-indigo-600 text-white text-xs"> {translateUi("Reactivate")} </button>
                        <button onClick={() => updatePaymentRequest(String(r.id), 'delete')} className="px-2.5 py-1 rounded-md bg-slate-700 text-white text-xs"> {translateUi("Delete")} </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400"> {translateUi("Processed")} </span>
                    )}
                  </td>
                </tr>
              ))}
              {paymentRequests.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">{translateUi("No offline requests.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AddonsPage = ({
  isAdmin,
  companyCode,
  currentUserEmail,
  isDarkMode = false,
  onAddonPurchased,
  finishSetupFlow = false,
  onFinishSetup,
  knownActiveAddonIds = [],
}: {
  isAdmin: boolean;
  companyCode?: string;
  currentUserEmail?: string;
  isDarkMode?: boolean;
  onAddonPurchased: () => void;
  /** Shown when the nominated admin reaches add-ons during finish company setup. */
  finishSetupFlow?: boolean;
  onFinishSetup?: () => void;
  /** Parent-loaded active addon ids (same /api/addons source) so tiles stay in sync with the app shell. */
  knownActiveAddonIds?: string[];
}) => {
  const dark = !!isDarkMode;
  const [addons, setAddons] = useState<{ addonId: string; purchasedAt: string }[]>([]);
  /**
   * Addons activated during this session for the current company. Server reads
   * (especially under impersonation) can be briefly eventually-consistent and
   * omit a just-written row, which would otherwise flip the tile back to
   * translateUi("Activate"). Unioning this set into the derived flags keeps the tile showing
   * "Activated" once activation has succeeded. Reset whenever the company changes.
   */
  const [activatedAddonIds, setActivatedAddonIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  /** Which addon tile is running checkout/activation so other tiles stay idle. */
  const [addonActionKey, setAddonActionKey] = useState<'qa-verifications' | 'task-assignment' | 'safety-briefs' | 'trainee-driver' | 'driver-reports' | 'leave-absence' | 'medication-checks' | 'company-admin-licences' | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [taskOptions, setTaskOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [qaOptions, setQaOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [traineeDriverOptions, setTraineeDriverOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [driverReportsOptions, setDriverReportsOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [leaveAbsenceOptions, setLeaveAbsenceOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [medicationChecksOptions, setMedicationChecksOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [adminLicencesOptions, setAdminLicencesOptions] = useState<{
    isFree: boolean;
    stripeReady: boolean;
    nextRefreshAt?: string | null;
  } | null>(null);
  const [adminSeatSummary, setAdminSeatSummary] = useState<{
    includedSeats: number;
    purchasedSeats: number;
    maxSeats: number;
    currentAdmins: number;
    canAddAdmin: boolean;
  } | null>(null);
  const [adminLicencesPurchaseQty, setAdminLicencesPurchaseQty] = useState(1);

  /** Plain GET of addon rows (no React state). Used for merges and for aborted loads. */
  const fetchAddonsList = useCallback(
    async (opts?: { signal?: AbortSignal }): Promise<{ addonId: string; purchasedAt: string }[]> => {
      if (!companyCode) return [];
      const headers: Record<string, string> = {};
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      headers["X-Company-Code"] = companyCode;
      const res = await fetch(
        `/api/addons?companyCode=${encodeURIComponent(companyCode)}&_=${Date.now()}`,
        { signal: opts?.signal, headers, cache: "no-store" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.warn("[AddonsPage] /api/addons failed:", res.status, (data as { error?: string }).error);
        return [];
      }
      return Array.isArray(data.addons) ? (data.addons as { addonId: string; purchasedAt: string }[]) : [];
    },
    [companyCode, currentUserEmail],
  );

  const addonsLoadAbortRef = useRef<AbortController | null>(null);
  const addonsFetchSeqRef = useRef(0);

  const loadAddons = useCallback((): Promise<{ addonId: string; purchasedAt: string }[]> => {
    if (!companyCode) return Promise.resolve([]);
    addonsLoadAbortRef.current?.abort();
    const ac = new AbortController();
    addonsLoadAbortRef.current = ac;
    const seq = ++addonsFetchSeqRef.current;
    return fetchAddonsList({ signal: ac.signal })
      .then((list) => {
        if (seq !== addonsFetchSeqRef.current) return list;
        setAddons(list);
        return list;
      })
      .catch((e: unknown) => {
        const aborted =
          (e as { name?: string })?.name === "AbortError" ||
          (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError");
        if (aborted) {
          /* Superseded by a newer load — keep current UI state. */
        } else if (seq === addonsFetchSeqRef.current) {
          setAddons([]);
        }
        return [];
      })
      .finally(() => {
        if (seq === addonsFetchSeqRef.current) setLoading(false);
      });
  }, [companyCode, fetchAddonsList]);

  /** After a successful activation, union two reads so a fast follow-up fetch never drops sibling modules. */
  const hydrateAddonsFromServer = useCallback(async () => {
    addonsLoadAbortRef.current?.abort();
    addonsFetchSeqRef.current += 1;
    const union = new Map<string, string>();
    for (let round = 0; round < 2; round++) {
      if (round > 0) await new Promise((r) => setTimeout(r, 200));
      const rows = await fetchAddonsList();
      for (const a of rows) {
        union.set(a.addonId, a.purchasedAt || union.get(a.addonId) || new Date().toISOString());
      }
    }
    const next = Array.from(union, ([addonId, purchasedAt]) => ({ addonId, purchasedAt }));
    setAddons(next);
  }, [fetchAddonsList]);

  useEffect(() => { loadAddons(); }, [loadAddons]);

  // Switching company (e.g. changing which company is impersonated) must not
  // carry session activations across tenants.
  useEffect(() => {
    setActivatedAddonIds(new Set());
  }, [companyCode]);

  const loadTaskOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setTaskOptions(null);
      return;
    }
    fetch(`/api/task-module/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setTaskOptions(null);
          return;
        }
        setTaskOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setTaskOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadTaskOptions();
  }, [loadTaskOptions]);

  const loadQaOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setQaOptions(null);
      return;
    }
    fetch(`/api/qa-verifications/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setQaOptions(null);
          return;
        }
        setQaOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setQaOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadQaOptions();
  }, [loadQaOptions]);

  const loadTraineeDriverOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setTraineeDriverOptions(null);
      return;
    }
    fetch(`/api/trainee-driver/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setTraineeDriverOptions(null);
          return;
        }
        setTraineeDriverOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setTraineeDriverOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadTraineeDriverOptions();
  }, [loadTraineeDriverOptions]);

  const loadDriverReportsOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setDriverReportsOptions(null);
      return;
    }
    fetch(`/api/driver-reports/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setDriverReportsOptions(null);
          return;
        }
        setDriverReportsOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setDriverReportsOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadDriverReportsOptions();
  }, [loadDriverReportsOptions]);

  const loadLeaveAbsenceOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setLeaveAbsenceOptions(null);
      return;
    }
    fetch(`/api/leave-absence/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setLeaveAbsenceOptions(null);
          return;
        }
        setLeaveAbsenceOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setLeaveAbsenceOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadLeaveAbsenceOptions();
  }, [loadLeaveAbsenceOptions]);

  const loadMedicationChecksOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setMedicationChecksOptions(null);
      return;
    }
    fetch(`/api/medication-checks/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setMedicationChecksOptions(null);
          return;
        }
        setMedicationChecksOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setMedicationChecksOptions(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadMedicationChecksOptions();
  }, [loadMedicationChecksOptions]);

  const loadAdminLicencesOptions = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setAdminLicencesOptions(null);
      return;
    }
    fetch(`/api/company-admin-licences/options?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = text
          ? (() => {
              try {
                return JSON.parse(text) as Record<string, unknown>;
              } catch {
                return {};
              }
            })()
          : {};
        if (!res.ok) {
          setAdminLicencesOptions(null);
          return;
        }
        setAdminLicencesOptions({
          isFree: !!data.isFree,
          stripeReady: !!data.stripeReady,
          nextRefreshAt: (data.nextRefreshAt as string | null | undefined) ?? null,
        });
      })
      .catch(() => setAdminLicencesOptions(null));
  }, [companyCode, currentUserEmail]);

  const loadAdminSeatSummary = useCallback(() => {
    if (!companyCode || !currentUserEmail) {
      setAdminSeatSummary(null);
      return;
    }
    fetch(`/api/company-admin-licences/summary?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setAdminSeatSummary(null);
          return;
        }
        setAdminSeatSummary({
          includedSeats: Number(data.includedSeats ?? 2),
          purchasedSeats: Number(data.purchasedSeats ?? 0),
          maxSeats: Number(data.maxSeats ?? 2),
          currentAdmins: Number(data.currentAdmins ?? 0),
          canAddAdmin: !!data.canAddAdmin,
        });
      })
      .catch(() => setAdminSeatSummary(null));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    loadAdminLicencesOptions();
    loadAdminSeatSummary();
  }, [loadAdminLicencesOptions, loadAdminSeatSummary]);

  /** Refetch addons/options when free-offer window or free-trial expiry passes (server tells us when). */
  useEffect(() => {
    const iso = taskOptions?.nextRefreshAt;
    if (!iso || !companyCode || !currentUserEmail) return;
    const at = Date.parse(iso);
    if (!Number.isFinite(at)) return;
    const ms = at - Date.now() + 800;
    const bump = () => {
      loadTaskOptions();
      loadAddons();
      onAddonPurchased();
    };
    if (ms <= 0) {
      bump();
      return;
    }
    const MAX_SCHEDULE_MS = 2147483647;
    const id = window.setTimeout(bump, Math.min(ms, MAX_SCHEDULE_MS));
    return () => clearTimeout(id);
  }, [taskOptions?.nextRefreshAt, companyCode, currentUserEmail, loadTaskOptions, loadAddons, onAddonPurchased]);

  /** Same refresh-on-expiry pattern but for QA Verifications free-offer / trial. */
  useEffect(() => {
    const iso = qaOptions?.nextRefreshAt;
    if (!iso || !companyCode || !currentUserEmail) return;
    const at = Date.parse(iso);
    if (!Number.isFinite(at)) return;
    const ms = at - Date.now() + 800;
    const bump = () => {
      loadQaOptions();
      loadAddons();
      onAddonPurchased();
    };
    if (ms <= 0) {
      bump();
      return;
    }
    const MAX_SCHEDULE_MS = 2147483647;
    const id = window.setTimeout(bump, Math.min(ms, MAX_SCHEDULE_MS));
    return () => clearTimeout(id);
  }, [qaOptions?.nextRefreshAt, companyCode, currentUserEmail, loadQaOptions, loadAddons, onAddonPurchased]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');
    const urlCompanyCode = params.get('companyCode');
    const addonId = params.get('addonId') || 'qa-verifications';
    if (success === '1' && sessionId && urlCompanyCode && companyCode === urlCompanyCode) {
      fetch('/api/addons/verify-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, companyCode: urlCompanyCode, addonId }),
      })
        .then(res => res.json())
        .then(async (data) => {
          if (data.success) {
            const aid = String(addonId || '');
            const successText = aid.startsWith('storage-')
              ? 'Storage addon activated successfully.'
              : aid === 'task-assignment'
                ? 'Task assignment module activated successfully.'
                : aid === 'safety-briefs'
                  ? 'Safety Briefs module activated successfully.'
                : aid === 'qa-verifications'
                  ? 'QA Verifications Module activated successfully.'
                : aid === 'trainee-driver'
                  ? 'Trainee module activated successfully.'
                : aid === 'driver-reports'
                  ? 'Driver Reports module activated successfully.'
                : aid === 'leave-absence'
                  ? 'Leave & Absence module activated successfully.'
                : aid === 'medication-checks'
                  ? 'Medication Checks module activated successfully.'
                : aid === 'company-admin-licences'
                  ? translateUi("Company Admin Licences purchased successfully.")
                  : translateUi("Addon activated successfully.");
            setMessage({ type: 'success', text: successText });
            if (aid && !aid.startsWith('storage-')) {
              setActivatedAddonIds(prev => new Set(prev).add(aid));
            }
            await hydrateAddonsFromServer();
            loadAdminSeatSummary();
            onAddonPurchased();
          } else {
            setMessage({ type: 'error', text: data.error || "Verification failed" });
          }
        })
        .catch(() => setMessage({ type: 'error', text: translateUi("Verification failed") }))
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname + (params.get('tab') ? '?tab=' + params.get('tab') : ''));
        });
    }
  }, [companyCode, hydrateAddonsFromServer, loadAdminSeatSummary, onAddonPurchased]);

  const hasQAVerifications =
    addons.some((a) => a.addonId === "qa-verifications") ||
    activatedAddonIds.has("qa-verifications") ||
    knownActiveAddonIds.includes("qa-verifications");
  const hasTaskAssignment =
    addons.some((a) => a.addonId === "task-assignment") ||
    activatedAddonIds.has("task-assignment") ||
    knownActiveAddonIds.includes("task-assignment");
  const hasSafetyBriefs =
    addons.some((a) => a.addonId === "safety-briefs") ||
    activatedAddonIds.has("safety-briefs") ||
    knownActiveAddonIds.includes("safety-briefs");
  const hasTraineeDriver =
    addons.some((a) => a.addonId === "trainee-driver") ||
    activatedAddonIds.has("trainee-driver") ||
    knownActiveAddonIds.includes("trainee-driver");
  const hasDriverReports =
    addons.some((a) => a.addonId === "driver-reports") ||
    activatedAddonIds.has("driver-reports") ||
    knownActiveAddonIds.includes("driver-reports");
  const hasLeaveAbsence =
    addons.some((a) => a.addonId === "leave-absence") ||
    activatedAddonIds.has("leave-absence") ||
    knownActiveAddonIds.includes("leave-absence");
  const hasMedicationChecks =
    addons.some((a) => a.addonId === "medication-checks") ||
    activatedAddonIds.has("medication-checks") ||
    knownActiveAddonIds.includes("medication-checks");

  const handlePurchaseTaskAssignment = async () => {
    if (!companyCode || hasTaskAssignment || taskOptions?.isFree) return;
    setAddonActionKey('task-assignment');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'task-assignment', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateTaskFree = async () => {
    if (!companyCode || !currentUserEmail || hasTaskAssignment || !taskOptions?.isFree) return;
    setAddonActionKey('task-assignment');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'task-assignment' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Task assignment module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('task-assignment'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseSafetyBriefs = async () => {
    if (!companyCode || hasSafetyBriefs || taskOptions?.isFree) return;
    setAddonActionKey('safety-briefs');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'safety-briefs', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateSafetyBriefsFree = async () => {
    if (!companyCode || !currentUserEmail || hasSafetyBriefs || !taskOptions?.isFree) return;
    setAddonActionKey('safety-briefs');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'safety-briefs' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Safety Briefs module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('safety-briefs'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseQA = async () => {
    if (!companyCode || hasQAVerifications || qaOptions?.isFree) return;
    setAddonActionKey('qa-verifications');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'qa-verifications', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateQAFree = async () => {
    if (!companyCode || !currentUserEmail || hasQAVerifications || !qaOptions?.isFree) return;
    setAddonActionKey('qa-verifications');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'qa-verifications' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'QA Verifications module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('qa-verifications'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseTraineeDriver = async () => {
    if (!companyCode || hasTraineeDriver || traineeDriverOptions?.isFree) return;
    setAddonActionKey('trainee-driver');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'trainee-driver', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateTraineeDriverFree = async () => {
    if (!companyCode || !currentUserEmail || hasTraineeDriver || !traineeDriverOptions?.isFree) return;
    setAddonActionKey('trainee-driver');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'trainee-driver' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Trainee module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('trainee-driver'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseDriverReports = async () => {
    if (!companyCode || hasDriverReports || driverReportsOptions?.isFree) return;
    setAddonActionKey('driver-reports');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'driver-reports', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateDriverReportsFree = async () => {
    if (!companyCode || !currentUserEmail || hasDriverReports || !driverReportsOptions?.isFree) return;
    setAddonActionKey('driver-reports');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'driver-reports' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Driver Reports module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('driver-reports'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseLeaveAbsence = async () => {
    if (!companyCode || hasLeaveAbsence || leaveAbsenceOptions?.isFree) return;
    setAddonActionKey('leave-absence');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'leave-absence', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Checkout failed" });
      setAddonActionKey(null);
    }
  };

  const handleActivateLeaveAbsenceFree = async () => {
    if (!companyCode || !currentUserEmail || hasLeaveAbsence || !leaveAbsenceOptions?.isFree) return;
    setAddonActionKey('leave-absence');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'leave-absence' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Leave & Absence module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('leave-absence'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseMedicationChecks = async () => {
    if (!companyCode || hasMedicationChecks || medicationChecksOptions?.isFree) return;
    setAddonActionKey('medication-checks');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'medication-checks', companyCode, returnTab: 'addons' }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Checkout failed" });
      setAddonActionKey(null);
    }
  };

  const handleActivateMedicationChecksFree = async () => {
    if (!companyCode || !currentUserEmail || hasMedicationChecks || !medicationChecksOptions?.isFree) return;
    setAddonActionKey('medication-checks');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'medication-checks' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: 'Medication Checks module activated for your company.' });
      setActivatedAddonIds(prev => new Set(prev).add('medication-checks'));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  /** Start a local 14-day trial (or Stripe subscription trial when checkout is configured). */
  const handleStartAddonTrial = async (
    addonId:
      | 'qa-verifications'
      | 'task-assignment'
      | 'safety-briefs'
      | 'trainee-driver'
      | 'driver-reports'
      | 'leave-absence'
      | 'medication-checks',
    label: string,
    opts: { isFree?: boolean; stripeReady?: boolean } | null | undefined,
  ) => {
    if (!companyCode || !currentUserEmail || opts?.isFree) return;
    setAddonActionKey(addonId);
    setMessage(null);
    try {
      if (opts?.stripeReady) {
        const res = await fetch('/api/addons/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
          body: JSON.stringify({ addonId, companyCode, returnTab: 'addons', startTrial: true }),
        });
        const text = await res.text();
        const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
        if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
        if (data.url) window.location.href = data.url;
        else throw new Error('No checkout URL returned');
        return;
      }
      const res = await fetch('/api/addons/start-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': currentUserEmail,
          'X-Company-Code': companyCode,
        },
        body: JSON.stringify({ companyCode, addonId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not start trial (${res.status})`);
      const days = Number(data.trialDays) || 14;
      setMessage({ type: 'success', text: `${label} ${days}-day trial started for your company.` });
      setActivatedAddonIds((prev) => new Set(prev).add(addonId));
      await hydrateAddonsFromServer();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Could not start trial' });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handlePurchaseAdminLicences = async () => {
    if (!companyCode || adminLicencesOptions?.isFree) return;
    const qty = Math.max(1, Math.floor(adminLicencesPurchaseQty || 1));
    setAddonActionKey('company-admin-licences');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Company-Code': companyCode },
        body: JSON.stringify({ addonId: 'company-admin-licences', companyCode, returnTab: 'addons', quantity: qty }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'No checkout URL returned');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || translateUi('Failed to start checkout') });
    } finally {
      setAddonActionKey(null);
    }
  };

  const handleActivateAdminLicencesFree = async () => {
    if (!companyCode || !currentUserEmail || !adminLicencesOptions?.isFree) return;
    const qty = Math.max(1, Math.floor(adminLicencesPurchaseQty || 1));
    setAddonActionKey('company-admin-licences');
    setMessage(null);
    try {
      const res = await fetch('/api/addons/activate-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail, 'X-Company-Code': companyCode },
        body: JSON.stringify({ companyCode, addonId: 'company-admin-licences', seatCount: qty }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Activation failed (${res.status})`);
      setMessage({ type: 'success', text: `${qty} additional company administrator seat${qty === 1 ? '' : 's'} activated for your company.` });
      setActivatedAddonIds(prev => new Set(prev).add('company-admin-licences'));
      await hydrateAddonsFromServer();
      loadAdminSeatSummary();
      onAddonPurchased();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || "Activation failed" });
    } finally {
      setAddonActionKey(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className={cn("p-12 rounded-3xl border shadow-sm text-center", dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
        <p className={cn(dark ? "text-slate-300" : "text-slate-600")}>{translateUi("Administrator access required to purchase addons.")}</p>
      </div>
    );
  }

  if (!companyCode) {
    return (
      <div className={cn("p-12 rounded-3xl border shadow-sm text-center", dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
        <p className={cn(dark ? "text-slate-300" : "text-slate-600")}>{translateUi("Select a company to view addons.")}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div
          className={cn(
            "animate-spin rounded-full h-8 w-8 border-b-2",
            dark ? "border-indigo-400" : "border-indigo-600"
          )}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {finishSetupFlow ? (
        <div
          className={cn(
            "rounded-2xl border px-4 py-4 sm:px-5 sm:py-5",
            dark ? "border-violet-500/40 bg-violet-950/40" : "border-violet-200 bg-violet-50"
          )}
          role="note"
        >
          <p className={cn("text-sm font-semibold", dark ? "text-violet-100" : "text-violet-950")}>
            {translateUi("Optional: activate add-ons")}
          </p>
          <p className={cn("text-sm mt-1.5 leading-relaxed", dark ? "text-violet-100/90" : "text-violet-900")}>
            Core company setup is complete. As the nominated company administrator, you can turn on bolt-on modules now
            (Tasks, Safety Briefs, QA Verifications) or leave them for later. They stay available from Add-ons in the
            left navigation menu whenever you are ready.
          </p>
          {onFinishSetup ? (
            <button
              type="button"
              onClick={onFinishSetup}
              className={cn(
                "mt-3 inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold border transition-colors",
                dark
                  ? "border-violet-400/50 text-violet-100 hover:bg-violet-900/60"
                  : "border-violet-300 bg-white text-violet-900 hover:bg-violet-100"
              )}
            >
              {translateUi("Finish setup")}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={cn("text-2xl font-bold", dark ? "text-slate-50" : "text-slate-900")}> {translateUi("Addons")} </h2>
          <p className={cn("text-sm", dark ? "text-slate-400" : "text-slate-500")}>{translateUi("Bolt-on modules for your company")}</p>
        </div>
      </div>
      {message && (
        <div
          className={cn(
            "p-4 rounded-xl border",
            message.type === "success"
              ? dark
                ? "bg-emerald-950/45 text-emerald-100 border-emerald-500/35"
                : "bg-emerald-50 text-emerald-800 border-emerald-200"
              : dark
                ? "bg-red-950/45 text-red-100 border-red-500/35"
                : "bg-red-50 text-red-800 border-red-200"
          )}
        >
          {message.text}
        </div>
      )}
      <p className={cn("text-sm max-w-2xl", dark ? "text-slate-300" : "text-slate-600")}>
        Storage capacity upgrades are managed from the{" "}
        <span className={cn("font-semibold", dark ? "text-slate-100" : "text-slate-800")}> {translateUi("Dashboard")} </span> {translateUi("(Company storage section). This page lists other bolt-on modules only.")} </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-indigo-500/15 text-indigo-300" : "bg-indigo-50 text-indigo-600")}>
              <FileCheck className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("QA Verifications Module")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Bolt on the QA Verifications module for your company only. Run automated employee verification checks and produce a printable / downloadable compliance report.")}
              </p>
              {qaOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {qaOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : qaOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete QA Verifications module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasQAVerifications ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("QA Verifications module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-indigo-500/40 bg-indigo-950/55 text-indigo-100"
                  : "border-indigo-200 bg-indigo-50 text-indigo-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-indigo-300" : "text-indigo-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : qaOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateQAFree}
              disabled={addonActionKey === 'qa-verifications' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'qa-verifications' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('qa-verifications', translateUi("QA Verifications"), qaOptions)}
                disabled={addonActionKey === 'qa-verifications' || !currentUserEmail}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'qa-verifications'
                  ? qaOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {qaOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseQA}
                  disabled={addonActionKey === 'qa-verifications'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-600")}>
              <ClipboardList className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Task assignment")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                Assign tasks to users, optionally link them to an employee record, and track status. Shown in the sidebar as{" "}
                <strong className={dark ? "text-slate-200" : undefined}> {translateUi("Tasks")} </strong> {translateUi("when active.")}
              </p>
              {taskOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {taskOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : taskOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Task module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasTaskAssignment ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Task assignment module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-violet-500/40 bg-violet-950/55 text-violet-100"
                  : "border-violet-200 bg-violet-50 text-violet-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-violet-300" : "text-violet-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : taskOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateTaskFree}
              disabled={addonActionKey === 'task-assignment' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'task-assignment' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('task-assignment', 'Task assignment', taskOptions)}
                disabled={addonActionKey === 'task-assignment' || !currentUserEmail}
                className="w-full px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'task-assignment'
                  ? taskOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {taskOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseTaskAssignment}
                  disabled={addonActionKey === 'task-assignment'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600")}>
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Safety Briefs")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Capture employee safety brief attendance, including in-person provider details or remote method used. Includes dashboard and reporting visuals.")}
              </p>
              {taskOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {taskOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : taskOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Task module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasSafetyBriefs ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Safety Briefs module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-emerald-500/40 bg-emerald-950/55 text-emerald-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-emerald-300" : "text-emerald-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : taskOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateSafetyBriefsFree}
              disabled={addonActionKey === 'safety-briefs' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'safety-briefs' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('safety-briefs', translateUi("Safety Briefs"), taskOptions)}
                disabled={addonActionKey === 'safety-briefs' || !currentUserEmail}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'safety-briefs'
                  ? taskOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {taskOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseSafetyBriefs}
                  disabled={addonActionKey === 'safety-briefs'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-indigo-500/15 text-indigo-300" : "bg-indigo-50 text-indigo-600")}>
              <GraduationCap className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Trainee Module")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Build driver training schedules, enrol trainees, track attendance, train logs, hours and dual-signed feedback, auto-issue route learning passes and manage performance & support plans.")}
              </p>
              {traineeDriverOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {traineeDriverOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : traineeDriverOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Trainee module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasTraineeDriver ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Trainee module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-emerald-500/40 bg-emerald-950/55 text-emerald-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-emerald-300" : "text-emerald-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : traineeDriverOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateTraineeDriverFree}
              disabled={addonActionKey === 'trainee-driver' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'trainee-driver' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('trainee-driver', translateUi("Trainee"), traineeDriverOptions)}
                disabled={addonActionKey === 'trainee-driver' || !currentUserEmail}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'trainee-driver'
                  ? traineeDriverOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {traineeDriverOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseTraineeDriver}
                  disabled={addonActionKey === 'trainee-driver'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-cyan-500/15 text-cyan-300" : "bg-cyan-50 text-cyan-700")}>
              <ClipboardList className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Driver Reports")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Capture signed operational driver reports (turn, headcode, conditions, incident narrative). Syncs across the company and is ready to export into Investigations when that connector is active.")}
              </p>
              {driverReportsOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {driverReportsOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : driverReportsOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Driver Reports module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasDriverReports ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Driver Reports module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-emerald-500/40 bg-emerald-950/55 text-emerald-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-emerald-300" : "text-emerald-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : driverReportsOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateDriverReportsFree}
              disabled={addonActionKey === 'driver-reports' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-cyan-600 text-white rounded-xl text-sm font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'driver-reports' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('driver-reports', translateUi("Driver Reports"), driverReportsOptions)}
                disabled={addonActionKey === 'driver-reports' || !currentUserEmail}
                className="w-full px-4 py-2 bg-cyan-600 text-white rounded-xl text-sm font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'driver-reports'
                  ? driverReportsOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {driverReportsOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseDriverReports}
                  disabled={addonActionKey === 'driver-reports'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-700")}>
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Leave & Absence")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Track annual leave entitlements, sickness, and other absences with assessing-manager approval, email notifications, reporting, and a full audit trail on each employee record.")}
              </p>
              {leaveAbsenceOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {leaveAbsenceOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : leaveAbsenceOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Leave & Absence module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasLeaveAbsence ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Leave & Absence module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-emerald-500/40 bg-emerald-950/55 text-emerald-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-emerald-300" : "text-emerald-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : leaveAbsenceOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateLeaveAbsenceFree}
              disabled={addonActionKey === 'leave-absence' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'leave-absence' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('leave-absence', translateUi("Leave & Absence"), leaveAbsenceOptions)}
                disabled={addonActionKey === 'leave-absence' || !currentUserEmail}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'leave-absence'
                  ? leaveAbsenceOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {leaveAbsenceOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseLeaveAbsence}
                  disabled={addonActionKey === 'leave-absence'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")}>
              <Stethoscope className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Medication Checks")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Record medication declarations and occupational health med checks on each employee record, with optional employee self-submit for manager review.")}
              </p>
              {medicationChecksOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {medicationChecksOptions.isFree
                    ? 'Your company can activate this module at no charge while the current free offer is active.'
                    : medicationChecksOptions.stripeReady
                      ? 'Paid activation via Stripe subscription.'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Medication Checks module settings.'}
                </p>
              )}
            </div>
          </div>
          {hasMedicationChecks ? (
            <button
              type="button"
              disabled
              aria-label={translateUi("Medication Checks module is activated")}
              className={cn(
                "mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border cursor-default disabled:opacity-100",
                dark
                  ? "border-emerald-500/40 bg-emerald-950/55 text-emerald-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              )}
            >
              <CheckCircle2 className={cn("w-5 h-5 shrink-0", dark ? "text-emerald-300" : "text-emerald-600")} aria-hidden />
              {translateUi("Activated")}
            </button> ) : medicationChecksOptions?.isFree ? ( <button
              type="button"
              onClick={handleActivateMedicationChecksFree}
              disabled={addonActionKey === 'medication-checks' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'medication-checks' ? translateUi("Activating…") : translateUi("Activate (free)")}
            </button>
          ) : (
            <div className="mt-auto space-y-2">
              <button
                type="button"
                onClick={() => void handleStartAddonTrial('medication-checks', translateUi("Medication Checks"), medicationChecksOptions)}
                disabled={addonActionKey === 'medication-checks' || !currentUserEmail}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addonActionKey === 'medication-checks'
                  ? medicationChecksOptions?.stripeReady
                    ? translateUi("Opening checkout…")
                    : translateUi("Starting trial…")
                  : translateUi("Start 14-day trial")}
              </button>
              {medicationChecksOptions?.stripeReady ? (
                <button
                  type="button"
                  onClick={handlePurchaseMedicationChecks}
                  disabled={addonActionKey === 'medication-checks'}
                  className={cn(
                    "w-full px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50",
                    dark
                      ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Subscribe (yearly)")}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={cn(
            "p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col",
            dark ? "bg-slate-900/95 border-slate-600 ring-1 ring-slate-500/35 shadow-black/25" : "bg-white border-slate-200"
          )}
        >
          <div className="flex items-start space-x-4 mb-4">
            <div className={cn("p-3 rounded-xl", dark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-700")}>
              <Users className="w-8 h-8" />
            </div>
            <div>
              <h3 className={cn("text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>{translateUi("Company Admin Licences")}</h3>
              <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-500")}>
                {translateUi("Each company includes 2 company administrator seats. Purchase additional seats when you need more administrators in Team Management.")}
              </p>
              {adminSeatSummary && (
                <p className={cn("text-xs mt-2 font-medium", dark ? "text-amber-200/90" : "text-amber-900")}>
                  {adminSeatSummary.currentAdmins} of {adminSeatSummary.maxSeats} administrator seat{adminSeatSummary.maxSeats === 1 ? '' : 's'} in use
                  {adminSeatSummary.purchasedSeats > 0 ? ` (${adminSeatSummary.purchasedSeats} purchased)` : ''}.
                </p>
              )}
              {adminLicencesOptions && (
                <p className={cn("text-xs mt-2", dark ? "text-slate-500" : "text-slate-400")}>
                  {adminLicencesOptions.isFree
                    ? 'Your company can activate additional seats at no charge while the current free offer is active.'
                    : adminLicencesOptions.stripeReady
                      ? 'Paid per additional seat via Stripe subscription (quantity × price).'
                      : 'Stripe is not fully configured yet; ask your system administrator to complete Company Admin Licences settings.'}
                </p>
              )}
            </div>
          </div>
          <div className="mb-3">
            <label className={cn("block text-xs font-semibold mb-1.5", dark ? "text-slate-300" : "text-slate-600")}>
              {translateUi("Additional seats to purchase")}
            </label>
            <input
              type="number"
              min={1}
              max={99}
              value={adminLicencesPurchaseQty}
              onChange={(e) => setAdminLicencesPurchaseQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
              className={cn(
                "w-full rounded-xl border px-3 py-2 text-sm",
                dark ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-200 bg-white text-slate-900"
              )}
            />
          </div>
          {adminLicencesOptions?.isFree ? (
            <button
              type="button"
              onClick={handleActivateAdminLicencesFree}
              disabled={addonActionKey === 'company-admin-licences' || !currentUserEmail}
              className="mt-auto px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'company-admin-licences' ? translateUi("Activating…") : translateUi("Activate additional seats (free)")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePurchaseAdminLicences}
              disabled={addonActionKey === 'company-admin-licences' || !adminLicencesOptions?.stripeReady}
              className="mt-auto px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonActionKey === 'company-admin-licences'
                ? 'Redirecting to checkout…'
                : adminLicencesPurchaseQty > 1
                  ? `Purchase ${adminLicencesPurchaseQty} seats (yearly)`
                  : 'Purchase 1 additional seat (yearly)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomJobRolesPage = ({ companyCode, currentUserEmail }: { companyCode?: string; currentUserEmail?: string }) => {
  const [roles, setRoles] = useState<{ name: string; requiresCompetency: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [lockInstructorRoles, setLockInstructorRoles] = useState(true);

  const loadRoles = useCallback(() => {
    if (!companyCode) return;
    setLoading(true);
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers['X-User-Email'] = currentUserEmail;
    const companyUrl = currentUserEmail
      ? `/api/companies/${encodeURIComponent(companyCode)}?userEmail=${encodeURIComponent(currentUserEmail)}`
      : `/api/companies/${encodeURIComponent(companyCode)}`;
    Promise.all([
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, { headers }).then((res) =>
        res.ok ? res.json() : [],
      ),
      fetch(companyUrl, { headers })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ])
      .then(([data, company]: [{ name: string; requiresCompetency?: boolean }[], any]) => {
        const lock = shouldLockInstructorJobRoles(
          company?.railServiceType,
          company?.trainsNewDrivers === true || company?.trainsNewDrivers === 1
            ? true
            : company?.trainsNewDrivers === false || company?.trainsNewDrivers === 0
              ? false
              : null,
        );
        setLockInstructorRoles(lock);
        setRoles(
          ensureRequiredOnboardingJobRoles(
            (Array.isArray(data) ? data : []).map((r) => ({
              name: r.name,
              requiresCompetency: r.requiresCompetency === true,
            })),
            lock,
          ),
        );
      })
      .catch(() => {
        setLockInstructorRoles(true);
        setRoles(ensureRequiredOnboardingJobRoles([], true));
      })
      .finally(() => setLoading(false));
  }, [companyCode, currentUserEmail]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const saveRoles = async (newRoles: { name: string; requiresCompetency: boolean }[]) => {
    if (!companyCode || !currentUserEmail) return;
    setSaving(true);
    setError(null);
    const rolesToSave = ensureRequiredOnboardingJobRoles(newRoles, lockInstructorRoles);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': currentUserEmail },
        body: JSON.stringify({ roles: rolesToSave }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setRoles(rolesToSave);
        setNewRoleName('');
        setEditingName(null);
      } else {
        setError(data.error || "Failed to save");
      }
    } catch {
      setError(translateUi("Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    const name = newRoleName.trim();
    if (!name || roles.some(r => r.name === name)) return;
    saveRoles([...roles, { name, requiresCompetency: false }]);
  };

  const handleDelete = (name: string) => {
    if (isRequiredOnboardingJobRole(name, lockInstructorRoles)) {
      setError(translateUi("Instructor and Senior Driver Instructor are required and cannot be removed."));
      return;
    }
    saveRoles(roles.filter(r => r.name !== name));
  };

  const handleToggleCompetency = (name: string) => {
    saveRoles(roles.map(r => r.name === name ? { ...r, requiresCompetency: !r.requiresCompetency } : r));
  };

  const handleStartEdit = (name: string) => {
    if (isRequiredOnboardingJobRole(name, lockInstructorRoles)) {
      setError(translateUi("Instructor and Senior Driver Instructor are required and cannot be renamed."));
      return;
    }
    setEditingName(name);
    setEditValue(name);
  };

  const handleSaveEdit = () => {
    if (!editingName) return;
    const newName = editValue.trim();
    if (!newName || newName === editingName) {
      setEditingName(null);
      return;
    }
    if (
      isRequiredOnboardingJobRole(editingName, lockInstructorRoles) ||
      isRequiredOnboardingJobRole(newName, lockInstructorRoles)
    ) {
      setError(translateUi("Instructor and Senior Driver Instructor are required and cannot be renamed."));
      setEditingName(null);
      return;
    }
    if (roles.some(r => r.name === newName && r.name !== editingName)) {
      setError('A role with that name already exists');
      return;
    }
    saveRoles(roles.map(r => r.name === editingName ? { ...r, name: newName } : r));
  };

  if (!companyCode) {
    return (
      <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
        <p className="text-slate-600">{translateUi("Select a company to manage job roles.")}</p>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{translateUi("Custom job roles")}</h2>
          <p className="text-slate-500 text-sm">{translateUi("Add, edit, and delete job roles for your company. These roles appear when adding employees.")}</p>
          <p className="text-sky-800 text-sm mt-1">
            {lockInstructorRoles
              ? translateUi(
                  "Instructor and Senior Driver Instructor are locked as required for Trainee instructor allocation.",
                )
              : translateUi(
                  "Instructor and Senior Driver Instructor are optional for this company based on the service type answers given at onboarding.",
                )}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Add new role (e.g. Platform Staff)"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm w-56 focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <button
            onClick={handleAdd}
            disabled={!newRoleName.trim() || saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {translateUi("Add role")}
          </button>
        </div>

        {error && <p className="px-4 py-2 text-sm text-red-600">{error}</p>}

        {roles.length === 0 ? (
          <p className="p-6 text-slate-500 text-sm">{translateUi("No job roles yet. Add one above.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700"> {translateUi("Role")} </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">{translateUi("Competency required")}</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => {
                  const required = isRequiredOnboardingJobRole(r.name, lockInstructorRoles);
                  return (
                  <tr key={r.name} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      {editingName === r.name ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') setEditingName(null);
                          }}
                          className="px-2 py-1 border border-slate-200 rounded-lg text-sm w-48 focus:ring-2 focus:ring-indigo-500 outline-none"
                          autoFocus
                        />
                      ) : (
                        <span
                          className={cn(
                            "font-medium text-slate-800",
                            required ? "cursor-default" : "cursor-pointer hover:text-indigo-600",
                          )}
                          onClick={() => handleStartEdit(r.name)}
                        >
                          {r.name}
                          {required ? (
                            <span className="ml-2 inline-flex items-center rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-800">
                              {translateUi("Required")}
                            </span>
                          ) : null}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={r.requiresCompetency}
                          onChange={() => handleToggleCompetency(r.name)}
                          disabled={saving}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                        />
                        <span className="text-slate-600">{r.requiresCompetency ? translateUi("Yes") : translateUi("No")}</span>
                      </label>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(r.name)}
                        disabled={saving || required}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          required
                            ? translateUi("Required role — cannot delete")
                            : translateUi("Delete role")
                        }
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const HelpVideos = ({ isAdmin }: { isAdmin: boolean }) => {
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [videoToDelete, setVideoToDelete] = useState<number | null>(null);

  useEffect(() => {
    fetch('/api/help-videos')
      .then(res => res.json())
      .then(data => {
        setVideos(data);
        setLoading(false);
      });
  }, []);

  const getYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleAddVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVideoUrl || !newVideoTitle) return;

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/help-videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newVideoTitle, url: newVideoUrl })
      });
      
      if (res.ok) {
        const videosRes = await fetch('/api/help-videos');
        const videosData = await videosRes.json();
        setVideos(videosData);
        setIsAddModalOpen(false);
        setNewVideoUrl('');
        setNewVideoTitle('');
      }
    } catch (err) {
      console.error("Error adding video:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!videoToDelete) return;
    try {
      await fetch(`/api/help-videos/${videoToDelete}`, { method: 'DELETE' });
      setVideos(videos.filter(v => v.id !== videoToDelete));
    } catch (err) {
      console.error("Error deleting video:", err);
    } finally {
      setVideoToDelete(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="flex justify-end">
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center"
          >
            <Video className="w-4 h-4 mr-2" />
            {translateUi("Add Video")}
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400 italic border border-dashed border-slate-200 rounded-xl">{translateUi("No video tutorials available")}</div>
        ) : videos.map(video => {
          const videoId = getYouTubeId(video.url);
          const isPlaying = playingVideoId === video.id;
          
          return (
            <div key={video.id} className="group relative bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all">
              {videoId ? (
                <div className="aspect-video bg-black relative">
                  {isPlaying ? (
                    <iframe 
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div 
                      className="w-full h-full cursor-pointer group-hover:opacity-90 transition-opacity relative"
                      onClick={() => setPlayingVideoId(video.id)}
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play className="w-5 h-5 text-indigo-600 ml-1" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-video bg-slate-200 flex items-center justify-center">
                  <Video className="w-12 h-12 text-slate-400" />
                </div>
              )}
              <div className="p-4 flex items-center justify-between">
                <h4 className="font-bold text-slate-900 truncate pr-2">{video.title}</h4>
                {isAdmin && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoToDelete(video.id);
                    }}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Video Modal */}
      <>
        {isAddModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">{translateUi("Add Video Tutorial")}</h3>
                <button 
                  onClick={() => setIsAddModalOpen(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddVideoSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{translateUi("Video Title")}</label>
                  <input 
                    type="text" 
                    required
                    value={newVideoTitle}
                    onChange={(e) => setNewVideoTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. How to use the dashboard"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{translateUi("YouTube URL")}</label>
                  <input 
                    type="url" 
                    required
                    value={newVideoUrl}
                    onChange={(e) => setNewVideoUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button 
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-xl transition-colors"
                  > {translateUi("Cancel")} </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? translateUi("Adding...") : translateUi("Add Video")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>

      {/* Delete Confirmation Modal */}
      <>
        {videoToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden p-6 text-center"
            >
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{translateUi("Delete Video?")}</h3>
              <p className="text-slate-500 mb-6">{translateUi("Are you sure you want to delete this video tutorial? This action cannot be undone.")}</p>
              <div className="flex justify-center space-x-3">
                <button 
                  onClick={() => setVideoToDelete(null)}
                  className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-xl transition-colors"
                > {translateUi("Cancel")} </button>
                <button 
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
                > {translateUi("Delete")} </button>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
};

const STATUS_OPTIONS = ["Competent", "Advisory", "Review", "Not applicable"] as const;

interface LibraryElement {
  id: string;
  title: string;
  demonstrate: string[];
  explain: string[];
}
interface LibraryUnit {
  id: string;
  title: string;
  elements: LibraryElement[];
}
interface LibraryCycle {
  id: string;
  name: string;
  units: LibraryUnit[];
}

type CycleRow = { id: number; title: string; expiry_years: number; expiry_months?: number | null; company_code: string | null; framework_cycle_id: string | null };

const PRESET_EVENT_NAMES = ['Practical Ride Assessment', 'DEM Day', 'Summary Rules', 'Knowledge Assessment', 'Cab Ride Assessment'];

const CycleBuilderPage = ({ currentUserEmail, companyCode, isSystemAdmin }: { currentUserEmail?: string | null; companyCode?: string; isSystemAdmin?: boolean }) => {
  /** Legacy cycle builder panes: system admins configure/view template events; company context enables company tab. */
  const showAssessmentEvents = Boolean(isSystemAdmin || companyCode);
  const [cycles, setCycles] = useState<CycleRow[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [cycleDetail, setCycleDetail] = useState<{ id: number; title: string; expiry_years: number; company_code: string | null; criteria: { id: number; unit_no: string; element_no: string; element: string; criteria: string; status: string }[] } | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newExpiryYears, setNewExpiryYears] = useState(2);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState({ unit_no: '', element_no: '', element: '', criteria: '', status: "Competent" as const });
  const [editingRow, setEditingRow] = useState<{ id: number; unit_no: string; element_no: string; element: string; criteria: string } | null>(null);

  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [library, setLibrary] = useState<LibraryCycle[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const [expandedElements, setExpandedElements] = useState<Record<string, boolean>>({});
  const [selectedCriteria, setSelectedCriteria] = useState<Record<string, { unit_no: string; element_no: string; element: string; criteria: string; type: 'demonstrate' | 'explain' }>>({});
  const [importing, setImporting] = useState(false);

  // Assessment events state
  type CycleEvent = { id?: number; event_name: string; offset_months_open: number; offset_months_close: number; sort_order: number };
  const [cycleEvents, setCycleEvents] = useState<CycleEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsSaving, setEventsSaving] = useState(false);

  // Cycle Builder tabs: 'cycles' | 'assessment-events'
  const [cycleBuilderTab, setCycleBuilderTab] = useState<'cycles' | 'assessment-events'>('cycles');

  // Cycle detail sub-tabs: 'criteria' | 'events' – so Assessment Events aren't buried at bottom
  const [cycleDetailTab, setCycleDetailTab] = useState<'criteria' | 'events'>('criteria');

  // Edit cycle name (inline in header)
  const [editingCycleName, setEditingCycleName] = useState(false);
  const [cycleNameDraft, setCycleNameDraft] = useState('');
  const [cycleNameSaving, setCycleNameSaving] = useState(false);

  // Save success feedback (cleared after 3s)
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!saveSuccessMessage) return;
    const t = setTimeout(() => setSaveSuccessMessage(null), 3000);
    return () => clearTimeout(t);
  }, [saveSuccessMessage]);

  // Collapsible criteria: which unit_no groups are expanded (default: first unit expanded if few, else all collapsed)
  const [criteriaUnitsExpanded, setCriteriaUnitsExpanded] = useState<Record<string, boolean>>({});

  const adminHeaders: Record<string, string> = useMemo(() => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (currentUserEmail) h['X-User-Email'] = currentUserEmail;
    return h;
  }, [currentUserEmail]);

  /** Strip leading "u" from unit number for display (e.g. u1 → 1, u12 → 12). */
  const stripUnitNo = useCallback((s: string | undefined): string => {
    const t = (s || '').trim();
    const m = t.match(/^u(\d+)$/i);
    return m ? m[1] : t;
  }, []);

  const fetchCycles = useCallback(async () => {
    try {
      const url = companyCode ? `/api/cycles?companyCode=${encodeURIComponent(companyCode)}` : '/api/cycles';
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setCycles(data);
      }
    } catch (err) {
      console.error('Failed to fetch cycles:', err);
    } finally {
      setLoading(false);
    }
  }, [companyCode]);

  const fetchCycleDetail = useCallback(async (id: number) => {
    try {
      const res = await fetch(`/api/cycles/${id}`);
      if (res.ok) {
        const data = await res.json();
        setCycleDetail(data);
      }
    } catch (err) {
      console.error('Failed to fetch cycle detail:', err);
    }
  }, []);

  const fetchLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const res = await fetch('/api/cycle-library');
      if (res.ok) setLibrary(await res.json());
    } catch (err) {
      console.error('Failed to fetch library:', err);
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  const fetchCycleEvents = useCallback(async (cycleId: number) => {
    setEventsLoading(true);
    try {
      const res = await fetch(`/api/cycles/${cycleId}/events`);
      if (res.ok) setCycleEvents(await res.json());
    } catch (err) {
      console.error('Failed to fetch cycle events:', err);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const saveCycleEvents = useCallback(async (cycleId: number, events: CycleEvent[]) => {
    setEventsSaving(true);
    setSaveSuccessMessage(null);
    try {
      const res = await fetch(`/api/cycles/${cycleId}/events/bulk`, {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify({ events, companyCode }),
      });
      if (res.ok) {
        const saved = await res.json();
        setCycleEvents(saved);
        setSaveSuccessMessage('Assessment events saved');
      } else {
        const err = await res.json();
        setSaveSuccessMessage(err.error || "Failed to save");
      }
    } catch (err) {
      console.error('Failed to save events:', err);
      setSaveSuccessMessage('Failed to save events');
    } finally {
      setEventsSaving(false);
    }
  }, [adminHeaders, companyCode]);

  useEffect(() => {
    fetchCycles();
  }, [fetchCycles]);

  useEffect(() => {
    if (!showAssessmentEvents && cycleBuilderTab === 'assessment-events') {
      setCycleBuilderTab('cycles');
    }
  }, [showAssessmentEvents, cycleBuilderTab]);

  useEffect(() => {
    if (selectedCycleId) {
      fetchCycleDetail(selectedCycleId);
      if (showAssessmentEvents) fetchCycleEvents(selectedCycleId);
      setCycleDetailTab('criteria');
      setCriteriaUnitsExpanded({});
    } else {
      setCycleDetail(null);
      setCycleEvents([]);
    }
  }, [selectedCycleId, fetchCycleDetail, fetchCycleEvents, showAssessmentEvents]);

  const handleCreateCycle = async () => {
    if (!newTitle.trim()) return;
    setIsCreating(true);
    try {
      const body: any = { title: newTitle.trim(), expiry_years: newExpiryYears };
      if (companyCode) body.company_code = companyCode;
      const res = await fetch('/api/cycles', {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        setNewTitle('');
        setNewExpiryYears(2);
        await fetchCycles();
        setSelectedCycleId(created.id);
        setSaveSuccessMessage('Cycle created');
      } else {
        const err = await res.json();
        alert(err.error || translateUi('Failed to create cycle'));
      }
    } catch (err) {
      alert(translateUi("Failed to create cycle"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddRow = async () => {
    if (!selectedCycleId || !newRow.element.trim() || !newRow.criteria.trim()) return;
    try {
      const res = await fetch(`/api/cycles/${selectedCycleId}/criteria`, {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify(newRow),
      });
      if (res.ok) {
        setNewRow({ unit_no: '', element_no: '', element: '', criteria: '', status: "Competent" });
        fetchCycleDetail(selectedCycleId);
        setSaveSuccessMessage('Criteria added');
      } else {
        const err = await res.json();
        alert(err.error || translateUi('Failed to add row'));
      }
    } catch (err) {
      alert(translateUi("Failed to add row"));
    }
  };

  const handleUpdateCriteria = async (critId: number, field: 'unit_no' | 'element_no' | 'element' | 'criteria' | 'status', value: string) => {
    if (!selectedCycleId) return;
    setEditingRow(null);
    try {
      const res = await fetch(`/api/cycle-criteria/${critId}`, {
        method: 'PATCH',
        headers: adminHeaders,
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        fetchCycleDetail(selectedCycleId);
        setSaveSuccessMessage('Criteria updated');
      }
    } catch (err) {
      console.error('Failed to update criteria:', err);
    }
  };

  const handleDeleteCriteria = async (critId: number) => {
    if (!selectedCycleId || !confirm(translateUi('Delete this row?'))) return;
    try {
      const res = await fetch(`/api/cycle-criteria/${critId}`, { method: 'DELETE', headers: adminHeaders });
      if (res.ok) {
        fetchCycleDetail(selectedCycleId);
        setSaveSuccessMessage('Criteria removed');
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleDeleteCycle = async () => {
    if (!selectedCycleId || !confirm(translateUi('Delete this cycle and all its criteria? This cannot be undone.'))) return;
    try {
      const res = await fetch(`/api/cycles/${selectedCycleId}`, { method: 'DELETE', headers: adminHeaders });
      if (res.ok) {
        setSelectedCycleId(null);
        setCycleDetail(null);
        fetchCycles();
      } else {
        const err = await res.json();
        alert(err.error || translateUi("Failed to delete"));
      }
    } catch (err) {
      alert(translateUi("Failed to delete"));
    }
  };

  const handleUpdateCycleName = async () => {
    if (!selectedCycleId || !cycleDetail) return;
    const trimmed = cycleNameDraft.trim();
    if (!trimmed || trimmed === cycleDetail.title) {
      setEditingCycleName(false);
      setCycleNameDraft('');
      return;
    }
    setCycleNameSaving(true);
    try {
      const res = await fetch(`/api/cycles/${selectedCycleId}`, {
        method: 'PATCH',
        headers: adminHeaders,
        body: JSON.stringify({ title: trimmed }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCycleDetail((prev) => (prev ? { ...prev, title: updated.title } : null));
        setCycles((prev) => prev.map((c) => (c.id === selectedCycleId ? { ...c, title: updated.title } : c)));
        setEditingCycleName(false);
        setCycleNameDraft('');
        setSaveSuccessMessage('Cycle name updated');
      } else {
        const err = await res.json();
        alert(err.error || translateUi('Failed to update name'));
      }
    } catch (err) {
      alert(translateUi("Failed to update cycle name"));
    } finally {
      setCycleNameSaving(false);
    }
  };

  const selectedCriteriaCount = Object.keys(selectedCriteria).length;

  const openLibraryModal = () => {
    if (library.length === 0) fetchLibrary();
    setSelectedCriteria({});
    setExpandedUnits({});
    setExpandedElements({});
    setShowLibraryModal(true);
  };

  const toggleUnit = (key: string) => {
    setExpandedUnits((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const toggleElement = (key: string) => {
    setExpandedElements((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const toggleCriterion = (key: string, data: { unit_no: string; element_no: string; element: string; criteria: string; type: 'demonstrate' | 'explain' }) => {
    setSelectedCriteria((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = data;
      return next;
    });
  };

  const selectAllInElement = (cycleId: string, unitId: string, el: LibraryElement, _unitTitle: string) => {
    setSelectedCriteria((prev) => {
      const next = { ...prev };
      const items: [string, { unit_no: string; element_no: string; element: string; criteria: string; type: 'demonstrate' | 'explain' }][] = [];
      el.demonstrate.forEach((d, i) => {
        items.push([`${cycleId}-${unitId}-${el.id}-d-${i}`, { unit_no: unitId, element_no: el.id, element: el.title, criteria: d, type: 'demonstrate' }]);
      });
      el.explain.forEach((ex, i) => {
        items.push([`${cycleId}-${unitId}-${el.id}-e-${i}`, { unit_no: unitId, element_no: el.id, element: el.title, criteria: ex, type: 'explain' }]);
      });
      const allSelected = items.every(([k]) => !!next[k]);
      if (allSelected) {
        items.forEach(([k]) => delete next[k]);
      } else {
        items.forEach(([k, v]) => { next[k] = v; });
      }
      return next;
    });
  };

  const selectAllInUnit = (cycleId: string, unit: LibraryUnit) => {
    setSelectedCriteria((prev) => {
      const next = { ...prev };
      const items: [string, { unit_no: string; element_no: string; element: string; criteria: string; type: 'demonstrate' | 'explain' }][] = [];
      unit.elements.forEach((el) => {
        el.demonstrate.forEach((d, i) => {
          items.push([`${cycleId}-${unit.id}-${el.id}-d-${i}`, { unit_no: unit.id, element_no: el.id, element: el.title, criteria: d, type: 'demonstrate' }]);
        });
        el.explain.forEach((ex, i) => {
          items.push([`${cycleId}-${unit.id}-${el.id}-e-${i}`, { unit_no: unit.id, element_no: el.id, element: el.title, criteria: ex, type: 'explain' }]);
        });
      });
      const allSelected = items.every(([k]) => !!next[k]);
      if (allSelected) {
        items.forEach(([k]) => delete next[k]);
      } else {
        items.forEach(([k, v]) => { next[k] = v; });
      }
      return next;
    });
  };

  const handleBulkImport = async () => {
    if (!selectedCycleId || selectedCriteriaCount === 0) return;
    setImporting(true);
    try {
      const items = Object.values(selectedCriteria).map((v) => ({
        unit_no: v.unit_no,
        element_no: v.element_no,
        element: v.element,
        criteria: v.criteria,
        status: "Competent",
      }));
      const res = await fetch(`/api/cycles/${selectedCycleId}/criteria/bulk`, {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        setShowLibraryModal(false);
        setSelectedCriteria({});
        fetchCycleDetail(selectedCycleId);
      } else {
        const err = await res.json();
        alert(err.error || translateUi("Import failed"));
      }
    } catch (err) {
      alert(translateUi("Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const isStandardCycle = (c: CycleRow) => !c.company_code;
  const canEditCycle = (c: CycleRow) => isSystemAdmin || !!c.company_code;

  const companyCycles = useMemo(() => cycles.filter((c) => c.company_code && companyCode && c.company_code.toLowerCase() === companyCode.toLowerCase()), [cycles, companyCode]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{translateUi("Cycle Builder")}</h2>
          <p className="text-slate-500 text-sm">{translateUi("Create and manage competence cycles. Administrators only.")}</p>
        </div>
      </div>

      {/* Tabs: Cycles | Assessment Events (company-only, except CR 17504) */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setCycleBuilderTab('cycles')}
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
            cycleBuilderTab === 'cycles' ? "bg-white border border-slate-200 border-b-0 -mb-px text-indigo-600" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          )}
        >
          {translateUi("Cycles & Criteria")}
        </button>
        {showAssessmentEvents && (
          <button
            onClick={() => setCycleBuilderTab('assessment-events')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2',
              cycleBuilderTab === 'assessment-events' ? "bg-white border border-slate-200 border-b-0 -mb-px text-indigo-600" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            )}
          >
            <Calendar className="w-4 h-4" />
            Assessment Events
            {companyCycles.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">{companyCycles.length}</span>
            )}
          </button>
        )}
      </div>

      {cycleBuilderTab === 'assessment-events' ? (
        /* Assessment Events wizard – company-specific only */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {saveSuccessMessage && (
            <div className={cn(
              'px-6 py-2.5 flex items-center gap-2 text-sm font-medium',
              saveSuccessMessage.includes(translateUi("Failed")) ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
            )}>
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              {saveSuccessMessage}
            </div>
          )}
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h3 className="font-bold text-slate-900">{translateUi("Company Assessment Events")}</h3>
            <p className="text-sm text-slate-500 mt-1">
              Configure assessment events for your company&apos;s cycles. Events are isolated to your company and can differ from other companies.
              Create a cycle in Cycles &amp; Criteria, or apply a published template in Cycle Builder.
            </p>
          </div>
          <div className="p-6">
            {companyCycles.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">{translateUi("No company cycles yet")}</p>
                <p className="text-sm text-slate-500 mt-1">{translateUi("Create a cycle in the Cycles & Criteria tab, or apply a published template in Cycle Builder. Company cycles will appear here for you to configure assessment events.")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {companyCycles.map((c) => {
                  const isExpanded = selectedCycleId === c.id && cycleBuilderTab === 'assessment-events';
                  return (
                    <div key={c.id} className="border border-slate-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => {
                          setSelectedCycleId(isExpanded ? null : c.id);
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ChevronRight className={cn('w-5 h-5 text-slate-400 transition-transform', isExpanded && 'rotate-90')} />
                          <span className="font-medium text-slate-900">{c.title}</span>
                          <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full"> {translateUi("Company")} </span>
                        </div>
                        <span className="text-sm text-slate-500">
                          {selectedCycleId === c.id && cycleEvents.length > 0 ? `${cycleEvents.length} events` : 'Configure events'}
                        </span>
                      </button>
                      {isExpanded && selectedCycleId === c.id && (
                        <div className="px-4 py-4 border-t border-slate-100 bg-slate-50/30 space-y-3">
                          <p className="text-xs text-slate-500">{translateUi("Add, edit, or remove assessment events. These apply only to your company.")}</p>
                          {eventsLoading ? (
                            <p className="text-sm text-slate-500">{translateUi("Loading events…")}</p>
                          ) : (
                            <>
                              <div className="space-y-2">
                                {cycleEvents.map((ev, i) => (
                                  <div key={i} className="flex flex-wrap items-center gap-2 bg-white rounded-lg p-2 border border-slate-200">
                                    <span className="text-xs text-slate-500 font-semibold w-5">{i + 1}</span>
                                    <select
                                      value={PRESET_EVENT_NAMES.includes(ev.event_name) ? ev.event_name : '__custom__'}
                                      onChange={(e) => {
                                        const next = [...cycleEvents];
                                        const val = e.target.value;
                                        next[i] = { ...next[i], event_name: val === '__custom__' ? (PRESET_EVENT_NAMES.includes(next[i].event_name) ? '' : next[i].event_name) : val };
                                        setCycleEvents(next);
                                      }}
                                      className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm min-w-[160px]"
                                    >
                                      {PRESET_EVENT_NAMES.map((n) => (
                                        <option key={n} value={n}>{translateUi(n)}</option>
                                      ))}
                                      <option value="__custom__">{translateUi("Custom…")}</option>
                                    </select>
                                    {!PRESET_EVENT_NAMES.includes(ev.event_name) && (
                                      <input
                                        type="text"
                                        placeholder="Custom event name"
                                        value={ev.event_name}
                                        onChange={(e) => {
                                          const next = [...cycleEvents];
                                          next[i] = { ...next[i], event_name: e.target.value || "Custom Event" };
                                          setCycleEvents(next);
                                        }}
                                        className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[140px]"
                                      />
                                    )}
                                    <div className="flex items-center gap-1">
                                      <label className="text-xs text-slate-500 whitespace-nowrap"> {translateUi("Open")} </label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={120}
                                        value={ev.offset_months_open}
                                        onChange={(e) => {
                                          const next = [...cycleEvents];
                                          next[i] = { ...next[i], offset_months_open: Number(e.target.value) || 0 };
                                          setCycleEvents(next);
                                        }}
                                        className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm w-14 text-center"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <label className="text-xs text-slate-500 whitespace-nowrap"> {translateUi("Close")} </label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={120}
                                        value={ev.offset_months_close}
                                        onChange={(e) => {
                                          const next = [...cycleEvents];
                                          next[i] = { ...next[i], offset_months_close: Number(e.target.value) || 0 };
                                          setCycleEvents(next);
                                        }}
                                        className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm w-14 text-center"
                                      />
                                    </div>
                                    <button
                                      onClick={() => setCycleEvents(cycleEvents.filter((_, j) => j !== i))}
                                      className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() => setCycleEvents([...cycleEvents, { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 0, offset_months_close: 2, sort_order: cycleEvents.length }])}
                                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                                >
                                  {translateUi("+ Add event")}
                                </button>
                                <button
                                  onClick={() => saveCycleEvents(c.id, cycleEvents)}
                                  disabled={eventsSaving}
                                  className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                >
                                  {eventsSaving ? 'Saving…' : translateUi("Save events")}
                                </button>
                                {cycleEvents.length === 0 && (
                                  <button
                                    onClick={() => {
                                      setCycleEvents([
                                        { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 1, offset_months_close: 3, sort_order: 0 },
                                        { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 7, offset_months_close: 9, sort_order: 1 },
                                        { event_name: translateUi("DEM Day"), offset_months_open: 10, offset_months_close: 12, sort_order: 2 },
                                        { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 13, offset_months_close: 15, sort_order: 3 },
                                        { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 19, offset_months_close: 21, sort_order: 4 },
                                        { event_name: 'Summary Rules', offset_months_open: 21, offset_months_close: 24, sort_order: 5 },
                                      ]);
                                    }}
                                    className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
                                  >
                                    {translateUi("Use 2-year template")}
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">{translateUi("Create new cycle")}</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Cycle title"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1"> {translateUi("Cycle duration (years)")} </label>
              <input
                type="number"
                min={1}
                max={10}
                step={1}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                value={newExpiryYears}
                onChange={(e) => setNewExpiryYears(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                placeholder="e.g. 1, 2, 3, 5"
              />
              <p className="text-xs text-slate-400 mt-1"> {translateUi("How many years until this cycle expires (e.g. 1 for PTS, 2 for Train Driver)")} </p>
            </div>
            {!isSystemAdmin && companyCode && (
              <p className="text-xs text-slate-400">{translateUi("This cycle will be created for your company only.")}</p>
            )}
            <button
              onClick={handleCreateCycle}
              disabled={!newTitle.trim() || isCreating}
              className="w-full py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"
            >
              {isCreating ? translateUi("Creating…") : translateUi("Create cycle")}
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">{translateUi("Saved cycles")}</h3>
          {loading ? (
            <p className="text-slate-500 text-sm">{translateUi("Loading…")}</p>
          ) : cycles.length === 0 ? (
            <p className="text-slate-500 text-sm">{translateUi("No cycles yet. Create one above.")}</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {cycles.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCycleId(c.id)}
                  className={cn(
                    'w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2',
                    selectedCycleId === c.id ? "bg-indigo-100 text-indigo-700" : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <span className="flex-1">{c.title} ({formatCycleDurationLabel(resolveDisplayedCycleMonths(c))})</span>
                  {isStandardCycle(c) && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded font-semibold uppercase tracking-wider"> {translateUi("Standard")} </span>
                  )}
                  {c.company_code && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded font-semibold uppercase tracking-wider"> {translateUi("Company")} </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
          {selectedCycleId && cycleDetail ? (() => {
            const selectedCycleRow = cycles.find((c) => c.id === selectedCycleId);
            const editable = selectedCycleRow ? canEditCycle(selectedCycleRow) : true;
            return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {saveSuccessMessage && (
                <div className={cn(
                  'px-6 py-2.5 flex items-center gap-2 text-sm font-medium',
                  saveSuccessMessage.includes(translateUi("Failed")) ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
                )}>
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  {saveSuccessMessage}
                </div>
              )}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {editingCycleName ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        type="text"
                        value={cycleNameDraft}
                        onChange={(e) => setCycleNameDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateCycleName(); if (e.key === 'Escape') { setEditingCycleName(false); setCycleNameDraft(''); } }}
                        className="flex-1 min-w-0 px-3 py-1.5 text-base font-bold text-slate-900 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder={translateUi("Cycle name")}
                        autoFocus
                        disabled={cycleNameSaving}
                      />
                      <button
                        type="button"
                        onClick={handleUpdateCycleName}
                        disabled={cycleNameSaving || !cycleNameDraft.trim()}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cycleNameSaving ? 'Saving…' : translateUi("Save")}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingCycleName(false); setCycleNameDraft(''); }}
                        disabled={cycleNameSaving}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                      > {translateUi("Cancel")} </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-bold text-slate-900 truncate">{cycleDetail.title}</h3>
                      {editable && (
                        <button
                          type="button"
                          onClick={() => { setCycleNameDraft(cycleDetail.title); setEditingCycleName(true); }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg shrink-0"
                          title={translateUi("Edit cycle name")}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                  {!editable && !editingCycleName && (
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full shrink-0"> {translateUi("Read-only (standard cycle)")} </span>
                  )}
                </div>
                {editable && !editingCycleName && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={openLibraryModal}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      {translateUi("Predefined Unit & Elements")}
                    </button>
                    <button
                      onClick={handleDeleteCycle}
                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title={translateUi("Delete cycle")}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Sub-tabs: Criteria | Assessment Events – so events aren't buried at bottom */}
              <div className="flex gap-1 px-6 pt-3 border-b border-slate-100">
                <button
                  onClick={() => setCycleDetailTab('criteria')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-t-lg -mb-px transition-colors',
                    cycleDetailTab === 'criteria' ? "bg-slate-50 border border-slate-200 border-b-0 text-indigo-600" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  Criteria ({cycleDetail.criteria?.length ?? 0})
                </button>
                {showAssessmentEvents && (
                  <button
                    onClick={() => setCycleDetailTab('events')}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-t-lg -mb-px transition-colors flex items-center gap-1.5',
                      cycleDetailTab === 'events' ? "bg-slate-50 border border-slate-200 border-b-0 text-indigo-600" : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    <Calendar className="w-4 h-4" />
                    Assessment Events ({cycleEvents.length})
                  </button>
                )}
              </div>

              <div className="p-6">
                {cycleDetailTab === 'criteria' && (
                <>
                {editable && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">{translateUi("Add custom criteria")}</p>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      placeholder={translateUi("Unit no.")}
                      className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      value={newRow.unit_no}
                      onChange={(e) => setNewRow((r) => ({ ...r, unit_no: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder="Element no."
                      className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      value={newRow.element_no}
                      onChange={(e) => setNewRow((r) => ({ ...r, element_no: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder="Element name"
                      className="flex-1 min-w-[120px] px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      value={newRow.element}
                      onChange={(e) => setNewRow((r) => ({ ...r, element: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder={translateUi("Criteria")}
                      className="flex-1 min-w-[120px] px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      value={newRow.criteria}
                      onChange={(e) => setNewRow((r) => ({ ...r, criteria: e.target.value }))}
                    />
                    <select
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm min-w-[140px]"
                      value={newRow.status}
                      onChange={(e) => setNewRow((r) => ({ ...r, status: e.target.value as typeof newRow.status }))}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{translateUi(s)}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddRow}
                      disabled={!newRow.element.trim() || !newRow.criteria.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {translateUi("Add row")}
                    </button>
                  </div>
                </div>
                )}
                {(() => {
                  const criteria = cycleDetail.criteria ?? [];
                  const byUnit = criteria.reduce((acc: Record<string, typeof criteria>, r) => {
                    const k = (r.unit_no ?? '').trim() || '_';
                    if (!acc[k]) acc[k] = [];
                    acc[k].push(r);
                    return acc;
                  }, {});
                  const unitKeys = Object.keys(byUnit).sort((a, b) => (a === '_' ? 1 : b === '_' ? -1 : a.localeCompare(b)));
                  const toggleUnit = (key: string) => setCriteriaUnitsExpanded((p) => ({ ...p, [key]: !p[key] }));
                  const expandAll = () => setCriteriaUnitsExpanded((p) => { const n = { ...p }; unitKeys.forEach((k) => { n[k] = true; }); return n; });
                  const collapseAll = () => setCriteriaUnitsExpanded({});
                  return (
                  <div className="space-y-2">
                    {unitKeys.length > 3 && (
                      <div className="flex gap-2 mb-2">
                        <button onClick={expandAll} className="text-xs font-medium text-indigo-600 hover:underline">{translateUi("Expand all")}</button>
                        <button onClick={collapseAll} className="text-xs font-medium text-slate-500 hover:underline">{translateUi("Collapse all")}</button>
                      </div>
                    )}
                    {unitKeys.map((unitKey) => {
                      const rows = byUnit[unitKey];
                      const label = unitKey === '_' ? 'Other' : `Unit ${stripUnitNo(unitKey)}`;
                      const isOpen = criteriaUnitsExpanded[unitKey] ?? (unitKeys.length <= 3);
                      return (
                        <div key={unitKey} className="border border-slate-200 rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleUnit(unitKey)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
                          >
                            <div className="flex items-center gap-2">
                              <ChevronRight className={cn('w-4 h-4 text-slate-400 transition-transform', isOpen && 'rotate-90')} />
                              <span className="font-semibold text-slate-800">{label}</span>
                              <span className="text-xs text-slate-500">({rows.length} criteria)</span>
                            </div>
                          </button>
                          {isOpen && (
                          <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 uppercase tracking-wider text-xs border-b border-slate-200">
                        <th className="pb-2 pr-3 font-semibold w-20">{translateUi("Unit no.")}</th>
                        <th className="pb-2 pr-3 font-semibold w-24">{translateUi("Element no.")}</th>
                        <th className="pb-2 pr-4 font-semibold">{translateUi("Element name")}</th>
                        <th className="pb-2 pr-4 font-semibold"> {translateUi("Criteria")} </th>
                        <th className="pb-2 font-semibold"> {translateUi("Status")} </th>
                        {editable && <th className="pb-2 w-12"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const isEditing = editable && editingRow?.id === row.id;
                        const unitVal = isEditing ? editingRow.unit_no : (row.unit_no ?? '');
                        const unitDisplay = stripUnitNo(unitVal);
                        const elemNoVal = isEditing ? editingRow.element_no : (row.element_no ?? '');
                        const elVal = isEditing ? editingRow.element : row.element;
                        const critVal = isEditing ? editingRow.criteria : row.criteria;
                        const initEdit = () => { if (editable) setEditingRow({ id: row.id, unit_no: stripUnitNo(row.unit_no) || (row.unit_no ?? ''), element_no: row.element_no ?? '', element: row.element, criteria: row.criteria }); };
                        return (
                        <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 pr-3">
                            <input
                              type="text"
                              value={unitDisplay}
                              readOnly={!editable}
                              onFocus={initEdit}
                              onChange={(e) => setEditingRow((r) => r && r.id === row.id ? { ...r, unit_no: e.target.value } : r)}
                              onBlur={(e) => { if (editable && e.target.value.trim() !== stripUnitNo(row.unit_no ?? '')) handleUpdateCriteria(row.id, 'unit_no', e.target.value.trim()); }}
                              className={cn("w-full px-2 py-1 border border-slate-200 rounded text-slate-700", editable ? "bg-transparent" : "bg-slate-50 cursor-default")}
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <input
                              type="text"
                              value={elemNoVal}
                              readOnly={!editable}
                              onFocus={() => { if (editable) setEditingRow((r) => r?.id === row.id ? r : { id: row.id, unit_no: row.unit_no ?? '', element_no: row.element_no ?? '', element: row.element, criteria: row.criteria }); }}
                              onChange={(e) => setEditingRow((r) => r && r.id === row.id ? { ...r, element_no: e.target.value } : r)}
                              onBlur={(e) => { if (editable && e.target.value !== (row.element_no ?? '')) handleUpdateCriteria(row.id, 'element_no', e.target.value); }}
                              className={cn("w-full px-2 py-1 border border-slate-200 rounded text-slate-700", editable ? "bg-transparent" : "bg-slate-50 cursor-default")}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            <input
                              type="text"
                              value={elVal}
                              readOnly={!editable}
                              onFocus={() => { if (editable) setEditingRow((r) => r?.id === row.id ? r : { id: row.id, unit_no: row.unit_no ?? '', element_no: row.element_no ?? '', element: row.element, criteria: row.criteria }); }}
                              onChange={(e) => setEditingRow((r) => r && r.id === row.id ? { ...r, element: e.target.value } : r)}
                              onBlur={(e) => { if (editable && e.target.value !== row.element) handleUpdateCriteria(row.id, 'element', e.target.value); }}
                              className={cn("w-full px-2 py-1 border border-slate-200 rounded text-slate-700", editable ? "bg-transparent" : "bg-slate-50 cursor-default")}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            <input
                              type="text"
                              value={critVal}
                              readOnly={!editable}
                              onFocus={() => { if (editable) setEditingRow((r) => r?.id === row.id ? r : { id: row.id, unit_no: row.unit_no ?? '', element_no: row.element_no ?? '', element: row.element, criteria: row.criteria }); }}
                              onChange={(e) => setEditingRow((r) => r && r.id === row.id ? { ...r, criteria: e.target.value } : r)}
                              onBlur={(e) => { if (editable && e.target.value !== row.criteria) handleUpdateCriteria(row.id, 'criteria', e.target.value); }}
                              className={cn("w-full px-2 py-1 border border-slate-200 rounded text-slate-700", editable ? "bg-transparent" : "bg-slate-50 cursor-default")}
                            />
                          </td>
                          <td className="py-2">
                            {editable ? (
                              <select
                                value={row.status}
                                onChange={(e) => handleUpdateCriteria(row.id, 'status', e.target.value)}
                                className="px-2 py-1 border border-slate-200 rounded text-slate-700"
                              >
                                {STATUS_OPTIONS.map((s) => (
                                  <option key={s} value={s}>{translateUi(s)}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="px-2 py-1 text-slate-600">{row.status}</span>
                            )}
                          </td>
                          {editable && (
                          <td className="py-2">
                            <button
                              onClick={() => handleDeleteCriteria(row.id)}
                              className="p-1 text-rose-600 hover:bg-rose-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  );
                })()}
                {(!cycleDetail.criteria || cycleDetail.criteria.length === 0) && (
                  <p className="py-8 text-center text-slate-500 text-sm">No criteria yet. {editable ? 'Use "Predefined Unit & Elements" or add rows manually.' : ''}</p>
                )}
                </>
                )}

                {showAssessmentEvents && cycleDetailTab === 'events' && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm">{translateUi("Assessment Events Schedule")}</h4>
                  <p className="text-xs text-slate-500 mt-1">{translateUi("Define the assessment windows for this cycle. When assigned to an employee, events are generated automatically based on the cycle start date.")}</p>
                </div>

                <div className="space-y-3">
                    {eventsLoading ? (
                      <p className="text-sm text-slate-500">{translateUi("Loading events…")}</p>
                    ) : (
                      <>
                        <div className="space-y-2">
                          {cycleEvents.map((ev, i) => (
                            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                              <span className="text-xs text-slate-500 font-semibold w-5">{i + 1}</span>
                              <select
                                value={PRESET_EVENT_NAMES.includes(ev.event_name) ? ev.event_name : '__custom__'}
                                disabled={!editable}
                                onChange={(e) => {
                                  const next = [...cycleEvents];
                                  const val = e.target.value;
                                  next[i] = { ...next[i], event_name: val === '__custom__' ? (PRESET_EVENT_NAMES.includes(next[i].event_name) ? '' : next[i].event_name) : val };
                                  setCycleEvents(next);
                                }}
                                className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-0"
                              >
                                {PRESET_EVENT_NAMES.map((n) => (
                                  <option key={n} value={n}>{translateUi(n)}</option>
                                ))}
                                <option value="__custom__">{translateUi("Custom…")}</option>
                              </select>
                              {!PRESET_EVENT_NAMES.includes(ev.event_name) && editable && (
                                <input
                                  type="text"
                                  placeholder="Custom event name"
                                  value={ev.event_name}
                                  onChange={(e) => {
                                    const next = [...cycleEvents];
                                    next[i] = { ...next[i], event_name: e.target.value || "Custom Event" };
                                    setCycleEvents(next);
                                  }}
                                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm w-32"
                                />
                              )}
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-slate-500 whitespace-nowrap">{translateUi("Open month")}</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={120}
                                  value={ev.offset_months_open}
                                  disabled={!editable}
                                  onChange={(e) => {
                                    const next = [...cycleEvents];
                                    next[i] = { ...next[i], offset_months_open: Number(e.target.value) || 0 };
                                    setCycleEvents(next);
                                  }}
                                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm w-16 text-center"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-slate-500 whitespace-nowrap">{translateUi("Close month")}</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={120}
                                  value={ev.offset_months_close}
                                  disabled={!editable}
                                  onChange={(e) => {
                                    const next = [...cycleEvents];
                                    next[i] = { ...next[i], offset_months_close: Number(e.target.value) || 0 };
                                    setCycleEvents(next);
                                  }}
                                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm w-16 text-center"
                                />
                              </div>
                              {editable && (
                                <button
                                  onClick={() => setCycleEvents(cycleEvents.filter((_, j) => j !== i))}
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {editable && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCycleEvents([...cycleEvents, { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 0, offset_months_close: 2, sort_order: cycleEvents.length }])}
                              className="px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                            >
                              {translateUi("+ Add event")}
                            </button>
                            <button
                              onClick={() => saveCycleEvents(selectedCycleId!, cycleEvents)}
                              disabled={eventsSaving}
                              className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {eventsSaving ? 'Saving…' : translateUi("Save events")}
                            </button>
                            {cycleEvents.length === 0 && (
                              <button
                                onClick={() => {
                                  setCycleEvents([
                                    { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 1, offset_months_close: 3, sort_order: 0 },
                                    { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 7, offset_months_close: 9, sort_order: 1 },
                                    { event_name: translateUi("DEM Day"), offset_months_open: 10, offset_months_close: 12, sort_order: 2 },
                                    { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 13, offset_months_close: 15, sort_order: 3 },
                                    { event_name: translateUi("Practical Ride Assessment"), offset_months_open: 19, offset_months_close: 21, sort_order: 4 },
                                    { event_name: 'Summary Rules', offset_months_open: 21, offset_months_close: 24, sort_order: 5 },
                                  ]);
                                }}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
                              >
                                {translateUi("Use 2-year template")}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
            );
          })() : (
            <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-sm text-center">
              <p className="text-slate-500">{translateUi("Select a cycle to edit, or create a new one.")}</p>
            </div>
          )}
      </div>
      </>
      )}

      {showLibraryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowLibraryModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-bold text-slate-900 text-lg">{translateUi("Import from element library")}</h3>
                <p className="text-slate-500 text-sm">{translateUi("Select criteria from standard cycles to add to your cycle.")}</p>
              </div>
              <button onClick={() => setShowLibraryModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {libraryLoading ? (
                <p className="text-slate-500 text-sm text-center py-8">{translateUi("Loading library…")}</p>
              ) : library.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">{translateUi("No standard cycles available.")}</p>
              ) : (
                <div className="space-y-3">
                  {library.map((cyc) => (
                    <div key={cyc.id}>
                      <p className="text-sm font-bold text-indigo-700 mb-2">{cyc.name}</p>
                      <div className="space-y-1">
                        {cyc.units.map((unit) => {
                          const unitKey = `${cyc.id}-${unit.id}`;
                          const unitOpen = !!expandedUnits[unitKey];
                          return (
                            <div key={unit.id} className="border border-slate-200 rounded-xl overflow-hidden">
                              <button
                                onClick={() => toggleUnit(unitKey)}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                              >
                                {unitOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                                <span className="text-sm font-semibold text-slate-800 flex-1">{formatUnitDisplayTitle(unit)}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); selectAllInUnit(cyc.id, unit); }}
                                  className="text-xs text-indigo-600 hover:underline font-medium px-2"
                                >
                                  {translateUi("Toggle all")}
                                </button>
                              </button>
                              {unitOpen && (
                                <div className="border-t border-slate-100">
                                  {unit.elements.map((el) => {
                                    const elKey = `${cyc.id}-${unit.id}-${el.id}`;
                                    const elOpen = !!expandedElements[elKey];
                                    return (
                                      <div key={el.id} className="border-b border-slate-50 last:border-b-0">
                                        <button
                                          onClick={() => toggleElement(elKey)}
                                          className="w-full flex items-center gap-2 px-6 py-2 text-left hover:bg-slate-50 transition-colors"
                                        >
                                          {elOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                                          <span className="text-sm text-slate-700 flex-1">{el.title}</span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); selectAllInElement(cyc.id, unit.id, el, unit.title); }}
                                            className="text-xs text-indigo-600 hover:underline font-medium px-2"
                                          >
                                            {translateUi("Toggle all")}
                                          </button>
                                        </button>
                                        {elOpen && (
                                          <div className="pl-12 pr-4 pb-2 space-y-1">
                                            {el.demonstrate.length > 0 && (
                                              <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1"> {translateUi("Demonstrate")} </p>
                                                {el.demonstrate.map((d, i) => {
                                                  const key = `${cyc.id}-${unit.id}-${el.id}-d-${i}`;
                                                  return (
                                                    <label key={key} className="flex items-start gap-2 py-0.5 cursor-pointer hover:bg-indigo-50/50 rounded px-1">
                                                      <input
                                                        type="checkbox"
                                                        checked={!!selectedCriteria[key]}
                                                        onChange={() => toggleCriterion(key, { unit_no: unit.id, element_no: el.id, element: el.title, criteria: d, type: 'demonstrate' })}
                                                        className="mt-0.5 accent-indigo-600"
                                                      />
                                                      <span className="text-xs text-slate-600 leading-snug">{d}</span>
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                            )}
                                            {el.explain.length > 0 && (
                                              <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 mt-2"> {translateUi("Explain")} </p>
                                                {el.explain.map((ex, i) => {
                                                  const key = `${cyc.id}-${unit.id}-${el.id}-e-${i}`;
                                                  return (
                                                    <label key={key} className="flex items-start gap-2 py-0.5 cursor-pointer hover:bg-indigo-50/50 rounded px-1">
                                                      <input
                                                        type="checkbox"
                                                        checked={!!selectedCriteria[key]}
                                                        onChange={() => toggleCriterion(key, { unit_no: unit.id, element_no: el.id, element: el.title, criteria: ex, type: 'explain' })}
                                                        className="mt-0.5 accent-indigo-600"
                                                      />
                                                      <span className="text-xs text-slate-600 leading-snug">{ex}</span>
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between shrink-0 bg-slate-50 rounded-b-2xl">
              <p className="text-sm text-slate-600">{selectedCriteriaCount} criteria selected</p>
              <div className="flex gap-2">
                <button onClick={() => setShowLibraryModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"> {translateUi("Cancel")} </button>
                <button
                  onClick={handleBulkImport}
                  disabled={selectedCriteriaCount === 0 || importing}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {importing ? 'Importing…' : `Add ${selectedCriteriaCount} criteria`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ORR_REGISTERED_DOCTORS = [
  { code: 'ORRDOC2', name: 'Dr Manuel Fernandes' },
  { code: 'ORRDOC3', name: 'Dr Sallyanne Roblin' },
  { code: 'ORRDOC4', name: 'Dr Sean Ryan' },
  { code: 'ORRDOC6', name: 'Dr Harry Phoolchund' },
  { code: 'ORRDOC10', name: 'Dr Sam Farmer' },
  { code: 'ORRDOC11', name: 'Dr Paul Baker' },
  { code: 'ORRDOC13', name: 'Dr Hampapur Giridhar' },
  { code: 'ORRDOC25', name: 'Dr Steve Malleson' },
  { code: 'ORRDOC28', name: "Dr Mike O'Reilly" },
  { code: 'ORRDOC29', name: 'Dr Mark F R Hall' },
  { code: 'ORRDOC30', name: 'Dr Michael Charlson' },
  { code: 'ORRDOC35', name: 'Dr Lanre Ogunyemi' },
  { code: 'ORRDOC39', name: 'Dr David Shackleton' },
  { code: 'ORRDOC43', name: 'Dr Gary Strydom' },
  { code: 'ORRDOC45', name: 'Dr Dan Hegarty' },
  { code: 'ORRDOC47', name: 'Dr Geoff Helliwell' },
  { code: 'ORRDOC60', name: 'Dr Johann Carinus' },
  { code: 'ORRDOC62', name: 'Dr Damian Moran' },
  { code: 'ORRDOC63', name: 'Dr Sebastiano Morello' },
  { code: 'ORRDOC69', name: 'Dr Tok Hussain' },
  { code: 'ORRDOC70', name: 'Dr Shaun Austin' },
  { code: 'ORRDOC72', name: 'Dr William Jenkinson' },
  { code: 'ORRDOC73', name: 'Dr Paul Ettlinger' },
  { code: 'ORRDOC75', name: 'Dr Tony Hynes' },
  { code: 'ORRDOC78', name: 'Dr David Winfield' },
  { code: 'ORRDOC79', name: 'Dr Manon Younger' },
  { code: 'ORRDOC80', name: 'Dr Alex Wilson-Jones' },
  { code: 'ORRDOC81', name: 'Dr Rob Dallara' },
  { code: 'ORRDOC82', name: 'Dr Ray Quinlan' },
  { code: 'ORRDOC83', name: 'Dr Prassana Krishnan' },
  { code: 'ORRDOC85', name: 'Dr Dravendranath Reetoo' },
  { code: 'ORRDOC91', name: 'Dr Sayful Miah' },
  { code: 'ORRDOC92', name: 'Dr Chandrakant Mutalik' },
  { code: 'ORRDOC94', name: 'Dr Arun Chind' },
  { code: 'ORRDOC96', name: 'Dr Cheryl Liew' },
  { code: 'ORRDOC97', name: 'Dr Paul Macintyre' },
  { code: 'ORRDOC98', name: 'Dr Ravi Pandy' },
  { code: 'ORRDOC103', name: 'Dr Julia Chapman' },
  { code: 'ORRDOC105', name: 'Dr Roddy Lennox' },
  { code: 'ORRDOC107', name: 'Dr Maria Athanasiou' },
  { code: 'ORRDOC108', name: 'Dr Alexander Swan' },
  { code: 'ORRDOC109', name: 'Dr Danny Wong' },
  { code: 'ORRDOC112', name: 'Dr Syed Abbas' },
  { code: 'ORRDOC113', name: 'Dr Sheetal Chavda' },
  { code: 'ORRDOC115', name: 'Dr Maurice Mann' },
  { code: 'ORRDOC119', name: 'Dr Harbinder Sangha' },
  { code: 'ORRDOC120', name: 'Dr Momeda Deen' },
  { code: 'ORRDOC122', name: 'Dr Samantha Phillips' },
  { code: 'ORRDOC126', name: 'Dr Alan Muir' },
  { code: 'ORRDOC127', name: 'Dr Trond Eggum' },
  { code: 'ORRDOC129', name: 'Dr Philip McIlroy' },
  { code: 'ORRDOC131', name: 'Dr Sam Bennett' },
  { code: 'ORRDOC133', name: 'Dr Constantin Coltofean' },
  { code: 'ORRDOC135', name: 'Dr Denise Connolly' },
  { code: 'ORRDOC136', name: 'Dr Maryanne Robinson' },
  { code: 'ORRDOC137', name: 'Dr Omobolanle Akinosi' },
  { code: 'ORRDOC138', name: 'Dr Bosede Adejoro' },
  { code: 'ORRDOC139', name: 'Dr Mark Rooms' },
  { code: 'ORRDOC140', name: 'Dr Petar Milosevic' },
  { code: 'ORRDOC141', name: 'Dr Martin McGuire' },
  { code: 'ORRDOC142', name: 'Dr Donna Morgans' },
  { code: 'ORRDOC143', name: 'Dr Eugene Gaal' },
  { code: 'ORRDOC144', name: 'Dr Anyanate Ephraim' },
  { code: 'ORRDOC145', name: 'Dr Alan Scott' },
  { code: 'ORRDOC146', name: 'Dr Werner Stipp' },
  { code: 'ORRDOC148', name: 'Dr James Quigley' },
  { code: 'ORRDOC149', name: 'Dr Christina Coltofean' },
  { code: 'ORRDOC150', name: 'Dr Jennifer Napier' },
  { code: 'ORRDOC151', name: 'Dr Eliezer Ramirez Santana' },
  { code: 'ORRDOC152', name: 'Dr Matthew Mills' },
  { code: 'ORRDOC154', name: 'Dr Christopher Valentine' },
  { code: 'ORRDOC156', name: 'Dr Sarwar Chowdhury' },
  { code: 'ORRDOC157', name: 'Dr Nikhil Kapoor' },
  { code: 'ORRDOC158', name: 'Dr Damian Navaratnam' },
  { code: 'ORRDOC159', name: 'Dr Samina Siddiqi' },
  { code: 'ORRDOC160', name: 'Dr Mani Khan' },
  { code: 'ORRDOC161', name: 'Dr Imogen Stilz' },
  { code: 'ORRDOC162', name: 'Dr Mark Pritchard' },
  { code: 'ORRDOC163', name: 'Dr Satish Ranka' },
  { code: 'ORRDOC164', name: 'Dr Roshan Drabu' },
  { code: 'ORRDOC165', name: 'Dr Victor Olowookere' },
  { code: 'ORRDOC166', name: 'Dr Willem Johannes Theron' },
  { code: 'ORRDOC167', name: 'Dr Lucinda McWhor' },
  { code: 'ORRDOC168', name: 'Dr Shahida Islam' },
  { code: 'ORRDOC169', name: 'Dr Waseem Baig' },
  { code: 'ORRDOC170', name: 'Dr Ian Gemmell' },
  { code: 'ORRDOC171', name: 'Dr Ed Clapham' },
  { code: 'ORRDOC172', name: 'Dr Gwion Daniel' },
  { code: 'ORRDOC173', name: 'Dr Isabella Fernandes' },
  { code: 'ORRDOC174', name: 'Dr Adanma Chrys-Chikere' },
  { code: 'ORRDOC175', name: 'Dr Olimpia Topala' },
  { code: 'ORRDOC176', name: 'Dr Marjorie Greasley' },
  { code: 'ORRDOC177', name: 'Dr Sohail Ahmed' },
  { code: 'ORRDOC178', name: 'Dr Charles Joseph Miranda' },
  { code: 'ORRDOC179', name: 'Dr Duncan Parker' },
  { code: 'ORRDOC180', name: 'Dr Elizabeth Man-Li Lau' },
  { code: 'ORRDOC181', name: 'Dr Stephen Ekong' },
  { code: 'ORRDOC182', name: 'Dr Sarah Martin' },
  { code: 'ORRDOC183', name: 'Dr Kerry McNeil' },
  { code: 'ORRDOC184', name: 'Dr Zara Benjamin-Laing' },
  { code: 'ORRDOC185', name: 'Dr Leonard Stehle' },
  { code: 'ORRDOC186', name: 'Dr Anand Ramesh' },
  { code: 'ORRDOC187', name: 'Dr Charlie (Charles) Easmon' },
  { code: 'ORRDOC188', name: 'Dr Emma Robinson' },
  { code: 'ORRDOC189', name: 'Dr Ian Griffiths' }
].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

const ORR_REGISTERED_PSYCHOLOGISTS = [
  { code: 'ORRPP3', name: 'Stephen Fletcher' },
  { code: 'ORRPP6', name: 'Laura Hedley' },
  { code: 'ORRPP7', name: 'Jo Lawrence' },
  { code: 'ORRPP8', name: 'Mandy Hensby-Green' },
  { code: 'ORRPP10', name: 'Mary McColl' },
  { code: 'ORRPP12', name: 'Gayle Waldron' },
  { code: 'ORRPP14', name: 'Lisa Heyes' },
  { code: 'ORRPP16', name: 'Sonal Kanani' },
  { code: 'ORRPP18', name: 'Mandy Thomas' },
  { code: 'ORRPP24', name: 'Shanaz Ferdus' },
  { code: 'ORRPP32', name: 'Debbie Haydon' },
  { code: 'ORRPP35', name: 'Dave Rudman' },
  { code: 'ORRPP40', name: 'Paul Hayler' },
  { code: 'ORRPP52', name: 'Bethan Summers' },
  { code: 'ORRPP67', name: 'Sylvia Cranston' },
  { code: 'ORRPP75', name: 'Anand Patel' },
  { code: 'ORRPP89', name: 'Richard Lawrence' },
  { code: 'ORRPP90', name: 'Jodie Thomas' },
  { code: 'ORRPP94', name: 'Trudy-Ann Thomas' },
  { code: 'ORRPP96', name: 'Karen Fey' },
  { code: 'ORRPP98', name: 'Patricia Gillman' },
  { code: 'ORRPP100', name: 'Kurranbir Hara' },
  { code: 'ORRPP101', name: 'John Moore' },
  { code: 'ORRPP104', name: 'Michelle Jenkins' },
  { code: 'ORRPP105', name: 'Anita Ngere' },
  { code: 'ORRPP107', name: 'Daniel Mindurski' },
  { code: 'ORRPP112', name: 'Robert Boyle' },
  { code: 'ORRPP117', name: 'Shaun Kennedy' },
  { code: 'ORRPP119', name: 'Chloe Hancock' },
  { code: 'ORRPP120', name: 'Rachel Gronow' },
  { code: 'ORRPP122', name: 'Katy Simmonds' },
  { code: 'ORRPP123', name: 'Joanne Warne' },
  { code: 'ORRPP127', name: 'Cara Leek' },
  { code: 'ORRPP128', name: 'Beverley Sharp' },
  { code: 'ORRPP129', name: 'Tracey Bezuidenhout' },
  { code: 'ORRPP130', name: 'Iffath Ahmed' },
  { code: 'ORRPP131', name: 'Melanie Yang' },
  { code: 'ORRPP133', name: 'Antonio Mindurski' },
  { code: 'ORRPP139', name: 'Hannah Bullock' },
  { code: 'ORRPP140', name: 'Molly Gunn' },
  { code: 'ORRPP141', name: 'Angelika Chudzian' },
  { code: 'ORRPP142', name: 'Susanne McDermott' },
  { code: 'ORRPP143', name: 'Nathan Milligan' },
  { code: 'ORRPP144', name: 'Hayley Harris' },
  { code: 'ORRPP145', name: 'Chloe Sunnocks' },
  { code: 'ORRPP146', name: 'Charlotte Porter' },
  { code: 'ORRPP147', name: 'Zoe Gallagher' },
  { code: 'ORRPP148', name: 'Jordan Hicks-Oldham' },
  { code: 'ORRPP149', name: 'Kristina Aleliunaite' },
  { code: 'ORRPP150', name: 'Matthew Stanley' },
  { code: 'ORRPP151', name: 'Ben Grant' },
  { code: 'ORRPP152', name: 'Rebecca Griffiths' },
  { code: 'ORRPP153', name: 'Christina Williams' },
  { code: 'ORRPP156', name: 'Francesca Hartley' },
  { code: 'ORRPP157', name: 'Fraser Dawson' },
  { code: 'ORRPP158', name: 'Richard Oliver' },
  { code: 'ORRPP159', name: 'Hannah Love' },
  { code: 'ORRPP160', name: 'Harley Gale' },
  { code: 'ORRPP161', name: 'David Brooks' },
  { code: 'ORRPP162', name: 'Jessica Skinner-Jones' },
  { code: 'ORRPP163', name: 'Gary Cooper' },
  { code: 'ORRPP164', name: 'Alexandra Peplinska' },
  { code: 'ORRPP165', name: 'Nathan Ray' },
  { code: 'ORRPP166', name: 'Mattheus Lopes-Martin' },
  { code: 'ORRPP167', name: 'Wayne Heatherington' },
  { code: 'ORRPP168', name: 'Catherine Gibb' },
  { code: 'ORRPP169', name: 'Elizabeth Austin' },
  { code: 'ORRPP170', name: 'Rachel Roy' },
  { code: 'ORRPP171', name: 'Victoria Littlefair' },
  { code: 'ORRPP172', name: 'Hardeep Singh' },
  { code: 'ORRPP174', name: 'Katie Breach' },
  { code: 'ORRPP175', name: 'Jemma Foxwell' },
  { code: 'ORRPP176', name: 'Kayleigh Naylor' },
  { code: 'ORRPP177', name: 'Oyin Oyedoyin' },
  { code: 'ORRPP178', name: 'Matthew Mindurski' },
  { code: 'ORRPP179', name: 'Michaela Wernick' },
  { code: 'ORRPP180', name: 'Jamie Elwood' },
  { code: 'ORRPP181', name: 'Rob Murray' },
  { code: 'ORRPP182', name: 'Carly Munn' },
  { code: 'ORRPP183', name: 'Dru Stanford' },
  { code: 'ORRPP184', name: 'Carly Danns' },
  { code: 'ORRPP185', name: 'Rebecca Rhodes' },
  { code: 'ORRPP186', name: 'Lucy Allan' },
  { code: 'ORRPP187', name: 'Victoria Rawson' },
  { code: 'ORRPP188', name: 'Kirsty Davison' },
  { code: 'ORRPP189', name: 'Honor Taylor' },
  { code: 'ORRPP190', name: 'Chris Winch' },
  { code: 'ORRPP191', name: 'Georgina Beckett' }
].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

const MedicalsPage = ({ currentUserProfile, employees, currentUserEmail, companyCode, isDarkMode = false }: { currentUserProfile?: SystemUser | null, employees: any[], currentUserEmail?: string, companyCode?: string, isDarkMode?: boolean }) => {
  const userTitle = currentUserProfile?.title?.toLowerCase() || '';
  const userRole = currentUserProfile?.role?.toLowerCase() || '';
  const email = (currentUserProfile?.email || currentUserEmail || '').toLowerCase();
  
  const roleAllowsMutations = allowsOperationalMutations(currentUserProfile);
  const canEditMedicals =
    roleAllowsMutations &&
    (userTitle.includes('admin') ||
    userTitle.includes('system') ||
    userRole === 'admin' ||
    email === 'andyuk38@gmail.com' ||
    email === 'admin@railvault.com');
    
  const [showForm, setShowForm] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [isAddingPsychologist, setIsAddingPsychologist] = useState(false);
  const [customPsychologistName, setCustomPsychologistName] = useState('');
  const [customPsychologistRegisterNumber, setCustomPsychologistRegisterNumber] = useState('');
  const [psychologistSearch, setPsychologistSearch] = useState('');
  const [psychologistDropdownOpen, setPsychologistDropdownOpen] = useState(false);
  const [isAddingDoctor, setIsAddingDoctor] = useState(false);
  const [customDoctorName, setCustomDoctorName] = useState('');
  const [customDoctorRegisterNumber, setCustomDoctorRegisterNumber] = useState('');
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);
  const psychologistDropdownRef = useRef<HTMLDivElement | null>(null);
  const doctorDropdownRef = useRef<HTMLDivElement | null>(null);
  const [medicals, setMedicals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [confirmState, setConfirmState] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    date_passed: '',
    expiry_date: '',
    status: 'F1',
    restrictions: '',
    medical_standard_assessed_against: '',
    pre_orr: false,
    psychological_pre_orr: false,
    psychological_exam_passed: false,
    psychologist: '',
    doctor: '',
    glasses: false,
    contact_lenses: false,
    hearing_aid: false,
    comms_device: false,
    last_updated_by: currentUserProfile?.full_name || 'Admin User',
    certificate_name: '',
    certificate_content: ''
  });

  const medParams = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
  const medicalActorEmail = (currentUserProfile?.email || currentUserEmail || '').trim();

  const fetchMedicals = async () => {
    if (!companyCode) {
      setMedicals([]);
      setLoading(false);
      return;
    }
    try {
      setSaveErrorMessage('');
      const res = await fetch(`/api/medicals${medParams}`, {
        headers: medicalActorEmail ? { 'X-User-Email': medicalActorEmail } : undefined,
      });
      if (res.ok) {
        const data = await res.json();
        setMedicals(data);
      } else {
        const data = await res.json().catch(() => ({}));
        setSaveErrorMessage(normalizeAccessError((data as { error?: string }).error, `Failed to load medicals (${res.status})`));
        setMedicals([]);
      }
    } catch (err) {
      console.error("Failed to fetch medicals:", err);
      setSaveErrorMessage(normalizeAccessError((err as Error)?.message, "Failed to load medicals."));
      setMedicals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicals();
  }, [companyCode]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (psychologistDropdownRef.current && !psychologistDropdownRef.current.contains(target)) {
        setPsychologistDropdownOpen(false);
      }
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(target)) {
        setDoctorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!saveSuccessMessage) return;
    const timeout = window.setTimeout(() => setSaveSuccessMessage(''), 3000);
    return () => window.clearTimeout(timeout);
  }, [saveSuccessMessage]);

  useEffect(() => {
    if (!saveErrorMessage) return;
    const timeout = window.setTimeout(() => setSaveErrorMessage(''), 5000);
    return () => window.clearTimeout(timeout);
  }, [saveErrorMessage]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      alert(translateUi("Please upload a PDF or Word document."));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert(translateUi("File size must be less than 50MB"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setFormData({
        ...formData,
        certificate_name: file.name,
        certificate_content: base64
      });
    };
    reader.readAsDataURL(file);
  };

  const handleView = async (med: any) => {
    try {
      const res = await fetch(`/api/medicals/${med.id}${medParams}`, {
        headers: medicalActorEmail ? { 'X-User-Email': medicalActorEmail } : undefined,
      });
      if (res.ok) {
        const fullMed = await res.json();
        setFormData({
          employee_id: fullMed.employee_id,
          date_passed: fullMed.date_completed,
          expiry_date: fullMed.expiry_date || '',
          status: fullMed.status || 'F1',
          restrictions: fullMed.restrictions || '',
          medical_standard_assessed_against: fullMed.medical_standard_assessed_against || '',
          pre_orr: !!fullMed.pre_orr,
          psychological_pre_orr: !!fullMed.psychological_pre_orr,
          psychological_exam_passed: !!fullMed.psychological_exam_passed,
          psychologist: fullMed.psychologist || '',
          doctor: fullMed.doctor || '',
          glasses: !!fullMed.glasses,
          contact_lenses: !!fullMed.contact_lenses,
          hearing_aid: !!fullMed.hearing_aid,
          comms_device: !!fullMed.comms_device,
          last_updated_by: fullMed.last_updated_by || 'Admin User',
          certificate_name: fullMed.certificate_name || '',
          certificate_content: fullMed.certificate_content || ''
        });
        setEditingId(fullMed.id);
        setIsViewing(true);
        setIsAddingPsychologist(false);
        setCustomPsychologistName('');
        setCustomPsychologistRegisterNumber('');
        setIsAddingDoctor(false);
        setCustomDoctorName('');
        setCustomDoctorRegisterNumber('');
        setShowForm(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setSaveErrorMessage(normalizeAccessError((data as { error?: string }).error, `Failed to load medical record (${res.status})`));
      }
    } catch (err) {
      console.error("Error fetching full medical record:", err);
      setSaveErrorMessage(normalizeAccessError((err as Error)?.message, "Failed to load medical record."));
    }
  };

  const handleEdit = async (med: any) => {
    try {
      const res = await fetch(`/api/medicals/${med.id}${medParams}`, {
        headers: medicalActorEmail ? { 'X-User-Email': medicalActorEmail } : undefined,
      });
      if (res.ok) {
        const fullMed = await res.json();
        setFormData({
          employee_id: fullMed.employee_id,
          date_passed: fullMed.date_completed,
          expiry_date: fullMed.expiry_date || '',
          status: fullMed.status || 'F1',
          restrictions: fullMed.restrictions || '',
          medical_standard_assessed_against: fullMed.medical_standard_assessed_against || '',
          pre_orr: !!fullMed.pre_orr,
          psychological_pre_orr: !!fullMed.psychological_pre_orr,
          psychological_exam_passed: !!fullMed.psychological_exam_passed,
          psychologist: fullMed.psychologist || '',
          doctor: fullMed.doctor || '',
          glasses: !!fullMed.glasses,
          contact_lenses: !!fullMed.contact_lenses,
          hearing_aid: !!fullMed.hearing_aid,
          comms_device: !!fullMed.comms_device,
          last_updated_by: currentUserProfile?.full_name || 'Admin User',
          certificate_name: fullMed.certificate_name || '',
          certificate_content: fullMed.certificate_content || ''
        });
        setEditingId(fullMed.id);
        setIsViewing(false);
        setIsAddingPsychologist(false);
        setCustomPsychologistName('');
        setCustomPsychologistRegisterNumber('');
        setIsAddingDoctor(false);
        setCustomDoctorName('');
        setCustomDoctorRegisterNumber('');
        setShowForm(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setSaveErrorMessage(normalizeAccessError((data as { error?: string }).error, `Failed to load medical record (${res.status})`));
      }
    } catch (err) {
      console.error("Error fetching full medical record:", err);
      setSaveErrorMessage(normalizeAccessError((err as Error)?.message, "Failed to load medical record."));
    }
  };

  const handleDelete = async (id: number) => {
    if (!canEditMedicals) {
      alert(translateUi("You do not have permission to delete medical records."));
      return;
    }
    setConfirmState({
      isOpen: true,
      title: 'Delete Medical Record',
      message: 'Are you sure you want to delete this medical record? This action cannot be undone and will remove any associated files.',
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/medicals/${id}${medParams}`, {
            method: 'DELETE',
            headers: medicalActorEmail ? { 'X-User-Email': medicalActorEmail } : undefined
          });
          if (res.ok) {
            fetchMedicals();
          } else {
            alert(translateUi("Failed to delete record"));
          }
        } catch (err) {
          console.error("Error deleting medical:", err);
        }
        setConfirmState(null);
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEditMedicals) {
      setSaveErrorMessage("You do not have permission to add or edit medical records.");
      return;
    }
    if (!formData.employee_id) {
      setSaveErrorMessage("Please select an employee.");
      return;
    }

    const selectedPsychologist = isAddingPsychologist
      ? `${customPsychologistName.trim()} (${customPsychologistRegisterNumber.trim()})`
      : (formData.psychologist || '').trim();
    const selectedDoctor = isAddingDoctor
      ? `${customDoctorName.trim()} (${customDoctorRegisterNumber.trim()})`
      : (formData.doctor || '').trim();

    if (isAddingPsychologist) {
      if (!customPsychologistName.trim() || !customPsychologistRegisterNumber.trim()) {
        setSaveErrorMessage("Please enter both psychologist name and register number.");
        return;
      }
    } else if (!formData.psychologist || formData.psychologist === '__custom_psychologist__') {
      setSaveErrorMessage("Please select an ORR registered psychologist.");
      return;
    }

    if (isAddingDoctor) {
      if (!customDoctorName.trim() || !customDoctorRegisterNumber.trim()) {
        setSaveErrorMessage("Please enter both doctor name and register number.");
        return;
      }
    } else if (!formData.doctor || formData.doctor === '__custom__') {
      setSaveErrorMessage("Please select an ORR registered doctor.");
      return;
    }

    if (formData.status === 'F2' && !formData.restrictions.trim()) {
      setSaveErrorMessage("Restrictions are required for F2 status.");
      return;
    }
    
    setUploading(true);
    try {
      if (!formData.expiry_date) {
        setSaveErrorMessage("Please enter a medical expiry date.");
        return;
      }

      const url = editingId 
        ? `/api/medicals/${editingId}${medParams}`
        : `/api/employees/${formData.employee_id}/medicals${medParams}`;
        
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          'Content-Type': 'application/json',
          ...(medicalActorEmail ? { 'X-User-Email': medicalActorEmail } : {})
        },
        body: JSON.stringify({
          type: 'General Medical',
          date_completed: formData.date_passed,
          expiry_date: formData.expiry_date,
          status: formData.status,
          restrictions: formData.restrictions,
          medical_standard_assessed_against: formData.medical_standard_assessed_against,
          pre_orr: formData.pre_orr,
          psychological_pre_orr: formData.psychological_pre_orr,
          psychological_exam_passed: formData.psychological_exam_passed,
          psychologist: selectedPsychologist,
          doctor: selectedDoctor,
          glasses: formData.glasses,
          contact_lenses: formData.contact_lenses,
          hearing_aid: formData.hearing_aid,
          comms_device: formData.comms_device,
          last_updated_by: formData.last_updated_by,
          certificate_name: formData.certificate_name,
          certificate_content: formData.certificate_content,
          userEmail: medicalActorEmail || undefined
        })
      });

      if (!res.ok) {
        let serverMessage = '';
        try {
          const errorBody = await res.json();
          serverMessage = errorBody?.error || errorBody?.message || '';
        } catch {
          try {
            serverMessage = await res.text();
          } catch {
            serverMessage = '';
          }
        }
        throw new Error(serverMessage || `Failed to ${editingId ? 'update' : 'save'} medical record`);
      }
      
      setSaveSuccessMessage(`Medical record ${editingId ? 'updated' : 'saved'} successfully.`);
      setShowForm(false);
      setEditingId(null);
      setPsychologistSearch('');
      setDoctorSearch('');
      setPsychologistDropdownOpen(false);
      setDoctorDropdownOpen(false);
      setIsAddingPsychologist(false);
      setCustomPsychologistName('');
      setCustomPsychologistRegisterNumber('');
      setIsAddingDoctor(false);
      setCustomDoctorName('');
      setCustomDoctorRegisterNumber('');
      setFormData({
        employee_id: '',
        date_passed: '',
        expiry_date: '',
        status: 'F1',
        restrictions: '',
        medical_standard_assessed_against: '',
        pre_orr: false,
        psychological_pre_orr: false,
        psychological_exam_passed: false,
        psychologist: '',
        doctor: '',
        glasses: false,
        contact_lenses: false,
        hearing_aid: false,
        comms_device: false,
        last_updated_by: currentUserProfile?.full_name || 'Admin User',
        certificate_name: '',
        certificate_content: ''
      });
      fetchMedicals();
    } catch (err: any) {
      console.error("Error saving medical:", err);
      setSaveErrorMessage(normalizeAccessError(err?.message, "Failed to save medical record."));
    } finally {
      setUploading(false);
    }
  };

  const employeeIds = new Set(employees.map((e: any) => String(e.id)));
  const filteredMedicals = medicals
    .filter(med => employeeIds.has(String(med.employee_id)))
    .filter(med => {
      // SQLite might return 1/0 as numbers or strings depending on the driver
      const isLatest = med.is_latest === 1 || med.is_latest === '1' || med.is_latest === true;
      if (showArchived) {
        return !isLatest;
      } else {
        return isLatest;
      }
    });

  const filteredPsychologists = ORR_REGISTERED_PSYCHOLOGISTS.filter((psychologist) => {
    const q = psychologistSearch.trim().toLowerCase();
    if (!q) return true;
    return psychologist.name.toLowerCase().includes(q) || psychologist.code.toLowerCase().includes(q);
  });

  const filteredDoctors = ORR_REGISTERED_DOCTORS.filter((doctor) => {
    const q = doctorSearch.trim().toLowerCase();
    if (!q) return true;
    return doctor.name.toLowerCase().includes(q) || doctor.code.toLowerCase().includes(q);
  });

  const statusSelectClass = formData.status === 'F1'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900 focus:ring-emerald-500'
    : formData.status === 'F2'
      ? 'bg-amber-50 border-amber-200 text-amber-900 focus:ring-amber-500'
      : formData.status === 'Unfit'
        ? "bg-rose-50 border-rose-200 text-rose-900 focus:ring-rose-500"
        : "bg-slate-50 border-slate-200 text-slate-900 focus:ring-indigo-500";

  return (
    <div className="space-y-6">
      {saveSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 shadow-lg flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600" />
            <div className="text-sm font-medium">{saveSuccessMessage}</div>
            <button
              type="button"
              className="ml-auto text-emerald-700 hover:text-emerald-900"
              onClick={() => setSaveSuccessMessage('')}
              aria-label={translateUi("Dismiss success message")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {saveErrorMessage && (
        <div className="max-w-3xl">
          <div
            className={cn(
              "rounded-xl border px-4 py-3 shadow-sm flex items-start gap-3",
              isDarkMode ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-800"
            )}
          >
            <AlertCircle className="w-5 h-5 mt-0.5 text-rose-600" />
            <div className="text-sm font-medium">{saveErrorMessage}</div>
            <button
              type="button"
              className={cn("ml-auto", isDarkMode ? "text-rose-200 hover:text-rose-100" : "text-rose-700 hover:text-rose-900")}
              onClick={() => setSaveErrorMessage('')}
              aria-label={translateUi("Dismiss error message")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {confirmState && (
        <ConfirmDialog 
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HeartPulse className="w-7 h-7 text-indigo-600 shrink-0" aria-hidden />
            {translateUi("Medical Records")}
          </h2>
          <p className="text-slate-500 text-sm">{translateUi("Manage employee medical records and appointments")}</p>
          {!canEditMedicals && (
            <p className="mt-2 text-sm text-amber-700">
              {translateUi("Read-only access. Only company admins can add or edit records from this Medicals page.")}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={cn(
              "px-4 py-2 font-medium rounded-xl transition-colors flex items-center shadow-sm",
              showArchived 
                ? "bg-slate-800 text-white hover:bg-slate-900" 
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            )}
          >
            <Archive className="w-4 h-4 mr-2" /> 
            {showArchived ? translateUi("View Live Records") : translateUi("View Archived")}
          </button>
          {canEditMedicals && (
            <button 
              onClick={() => {
                setEditingId(null);
                setIsViewing(false);
                setPsychologistSearch('');
                setDoctorSearch('');
                setPsychologistDropdownOpen(false);
                setDoctorDropdownOpen(false);
                setIsAddingPsychologist(false);
                setCustomPsychologistName('');
                setCustomPsychologistRegisterNumber('');
                setIsAddingDoctor(false);
                setCustomDoctorName('');
                setCustomDoctorRegisterNumber('');
                setFormData({
                employee_id: '',
                date_passed: '',
                expiry_date: '',
                status: 'F1',
                restrictions: '',
                medical_standard_assessed_against: '',
                pre_orr: false,
                psychological_pre_orr: false,
                psychological_exam_passed: false,
                psychologist: '',
                doctor: '',
                glasses: false,
                contact_lenses: false,
                hearing_aid: false,
                comms_device: false,
                last_updated_by: currentUserProfile?.full_name || 'Admin User',
                certificate_name: '',
                certificate_content: ''
              });
              setShowForm(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center shadow-sm shadow-indigo-200"
          >
            <Plus className="w-4 h-4 mr-2" /> {translateUi("New Medical Record")}
          </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="px-8 py-6 bg-indigo-600 text-white flex items-center justify-between">
            <h3 className="text-xl font-bold">
              {isViewing ? 'View Medical Record' : editingId ? translateUi("Edit Medical Record") : translateUi("Record New Medical")}
            </h3>
            <button
              onClick={() => {
                setIsAddingPsychologist(false);
                setPsychologistSearch('');
                setPsychologistDropdownOpen(false);
                setCustomPsychologistName('');
                setCustomPsychologistRegisterNumber('');
                setIsAddingDoctor(false);
                setDoctorSearch('');
                setDoctorDropdownOpen(false);
                setCustomDoctorName('');
                setCustomDoctorRegisterNumber('');
                setShowForm(false);
              }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> {translateUi("Employee")} </label>
                <select 
                  required
                  disabled={isViewing}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                  value={formData.employee_id}
                  onChange={e => setFormData({ ...formData, employee_id: e.target.value })}
                >
                  <option value="" disabled>{translateUi("Select Employee")}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.id})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Medical Date Passed")}</label>
                <input 
                  type="date" 
                  required
                  disabled={isViewing}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                  value={formData.date_passed}
                  onChange={e => setFormData({ ...formData, date_passed: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Medical Expiry Date")}</label>
                <input
                  type="date"
                  required
                  disabled={isViewing}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                  value={formData.expiry_date}
                  onChange={e => setFormData({ ...formData, expiry_date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> {translateUi("Status")} </label>
                <select 
                  required
                  disabled={isViewing}
                  className={`w-full px-4 py-2 border rounded-xl focus:ring-2 outline-none transition-all disabled:opacity-70 ${statusSelectClass}`}
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="F1">{translateUi("Fit (F1)")}</option>
                  <option value="F2">{translateUi("Fit with restrictions (F2)")}</option>
                  <option value="Unfit"> {translateUi("Unfit")} </option>
                </select>
              </div>
            </div>
            {formData.status === 'F2' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"> {translateUi("Restrictions")} </label>
                <textarea
                  required={!isViewing}
                  disabled={isViewing}
                  rows={3}
                  placeholder="Enter any restrictions required for this F2 medical status..."
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                  value={formData.restrictions}
                  onChange={e => setFormData({ ...formData, restrictions: e.target.value })}
                />
              </div>
            )}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label htmlFor="medical_standard_assessed_against" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Medical standard assessed against")}</label>
                <input
                  type="text"
                  id="medical_standard_assessed_against"
                  disabled={isViewing}
                  placeholder="e.g. ORR Medical Standard…"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm disabled:opacity-70"
                  value={formData.medical_standard_assessed_against}
                  onChange={e => setFormData({ ...formData, medical_standard_assessed_against: e.target.value })}
                />
              </div>
              <div className="flex items-center space-x-3 pb-2">
                <input 
                  type="checkbox" 
                  id="pre_orr"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.pre_orr}
                  onChange={e => setFormData({ ...formData, pre_orr: e.target.checked })}
                />
                <label htmlFor="pre_orr" className="text-sm font-medium text-slate-700">{translateUi("Medical Pre ORR")}</label>
              </div>
              <div className="flex items-center space-x-3 pb-2">
                <input 
                  type="checkbox" 
                  id="psych_pre_orr"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.psychological_pre_orr}
                  onChange={e => setFormData({ ...formData, psychological_pre_orr: e.target.checked })}
                />
                <label htmlFor="psych_pre_orr" className="text-sm font-medium text-slate-700">{translateUi("Psychological Pre ORR")}</label>
              </div>
              <div className="flex items-center space-x-3 pb-2">
                <input 
                  type="checkbox" 
                  id="psych_exam_passed"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.psychological_exam_passed}
                  onChange={e => setFormData({ ...formData, psychological_exam_passed: e.target.checked })}
                />
                <label htmlFor="psych_exam_passed" className="text-sm font-medium text-slate-700">{translateUi("Psychological Exam Passed")}</label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("ORR Registered Psychologist")}</label>
              <div ref={psychologistDropdownRef} className="relative">
                <button
                  type="button"
                  disabled={isViewing}
                  onClick={() => setPsychologistDropdownOpen((prev) => !prev)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-left focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70 flex items-center justify-between"
                >
                  <span className={formData.psychologist ? "text-slate-900" : "text-slate-400"}>
                    {formData.psychologist || 'Select ORR Registered Psychologist'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
                {psychologistDropdownOpen && !isViewing && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg">
                    <div className="p-2 border-b border-slate-100">
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search psychologist by name or ORRPP..."
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        value={psychologistSearch}
                        onChange={(e) => setPsychologistSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-indigo-700"
                        onClick={() => {
                          setIsAddingPsychologist(true);
                          setFormData({ ...formData, psychologist: '' });
                          setPsychologistDropdownOpen(false);
                        }}
                      >
                        {translateUi("Add new ORR psychologist...")}
                      </button>
                      {formData.psychologist && !ORR_REGISTERED_PSYCHOLOGISTS.some((psychologist) => `${psychologist.name} (${psychologist.code})` === formData.psychologist) && (
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                          onClick={() => setPsychologistDropdownOpen(false)}
                        >
                          {formData.psychologist}
                        </button>
                      )}
                      {filteredPsychologists.map((psychologist) => {
                        const displayValue = `${psychologist.name} (${psychologist.code})`;
                        return (
                          <button
                            key={psychologist.code}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                            onClick={() => {
                              setIsAddingPsychologist(false);
                              setCustomPsychologistName('');
                              setCustomPsychologistRegisterNumber('');
                              setFormData({ ...formData, psychologist: displayValue });
                              setPsychologistDropdownOpen(false);
                            }}
                          >
                            {displayValue}
                          </button>
                        );
                      })}
                      {filteredPsychologists.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">{translateUi("No psychologists found")}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isAddingPsychologist && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    disabled={isViewing}
                    placeholder="Psychologist name (e.g. Stephen Fletcher)"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                    value={customPsychologistName}
                    onChange={(e) => setCustomPsychologistName(e.target.value)}
                  />
                  <input
                    type="text"
                    disabled={isViewing}
                    placeholder="Register number (e.g. ORRPP3)"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                    value={customPsychologistRegisterNumber}
                    onChange={(e) => setCustomPsychologistRegisterNumber(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("ORR Registered Doctor")}</label>
              <div ref={doctorDropdownRef} className="relative">
                <button
                  type="button"
                  disabled={isViewing}
                  onClick={() => setDoctorDropdownOpen((prev) => !prev)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-left focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70 flex items-center justify-between"
                >
                  <span className={formData.doctor ? "text-slate-900" : "text-slate-400"}>
                    {formData.doctor || 'Select ORR Registered Doctor'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
                {doctorDropdownOpen && !isViewing && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg">
                    <div className="p-2 border-b border-slate-100">
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search doctor by name or ORRDOC..."
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        value={doctorSearch}
                        onChange={(e) => setDoctorSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-indigo-700"
                        onClick={() => {
                          setIsAddingDoctor(true);
                          setFormData({ ...formData, doctor: '' });
                          setDoctorDropdownOpen(false);
                        }}
                      >
                        {translateUi("Add new ORR doctor...")}
                      </button>
                      {formData.doctor && !ORR_REGISTERED_DOCTORS.some((doctor) => `${doctor.name} (${doctor.code})` === formData.doctor) && (
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                          onClick={() => setDoctorDropdownOpen(false)}
                        >
                          {formData.doctor}
                        </button>
                      )}
                      {filteredDoctors.map((doctor) => {
                        const displayValue = `${doctor.name} (${doctor.code})`;
                        return (
                          <button
                            key={doctor.code}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                            onClick={() => {
                              setIsAddingDoctor(false);
                              setCustomDoctorName('');
                              setCustomDoctorRegisterNumber('');
                              setFormData({ ...formData, doctor: displayValue });
                              setDoctorDropdownOpen(false);
                            }}
                          >
                            {displayValue}
                          </button>
                        );
                      })}
                      {filteredDoctors.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">{translateUi("No doctors found")}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isAddingDoctor && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    disabled={isViewing}
                    placeholder="Doctor name (e.g. Dr Jane Doe)"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                    value={customDoctorName}
                    onChange={(e) => setCustomDoctorName(e.target.value)}
                  />
                  <input
                    type="text"
                    disabled={isViewing}
                    placeholder="Register number (e.g. ORRDOC190)"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-70"
                    value={customDoctorRegisterNumber}
                    onChange={(e) => setCustomDoctorRegisterNumber(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  id="glasses"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.glasses}
                  onChange={e => setFormData({ ...formData, glasses: e.target.checked })}
                />
                <label htmlFor="glasses" className="text-sm font-medium text-slate-700"> {translateUi("Glasses")} </label>
              </div>
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  id="contact_lenses"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.contact_lenses}
                  onChange={e => setFormData({ ...formData, contact_lenses: e.target.checked })}
                />
                <label htmlFor="contact_lenses" className="text-sm font-medium text-slate-700">{translateUi("Contact Lenses")}</label>
              </div>
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  id="hearing_aid"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.hearing_aid}
                  onChange={e => setFormData({ ...formData, hearing_aid: e.target.checked })}
                />
                <label htmlFor="hearing_aid" className="text-sm font-medium text-slate-700">{translateUi("Hearing Aid")}</label>
              </div>
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  id="comms_device"
                  disabled={isViewing}
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 disabled:opacity-70"
                  checked={formData.comms_device}
                  onChange={e => setFormData({ ...formData, comms_device: e.target.checked })}
                />
                <label htmlFor="comms_device" className="text-sm font-medium text-slate-700">{translateUi("Comms Device")}</label>
              </div>
            </div>
            <div className="md:col-span-2 pt-4 border-t border-slate-100">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Medical Certificate")}</label>
              <div className="flex items-center space-x-4">
                {!isViewing && (
                  <label className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors flex items-center shadow-sm cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" /> {translateUi("Upload Certificate")}
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFileUpload} />
                  </label>
                )}
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <FileText className="w-4 h-4" />
                  {formData.certificate_content ? (
                    <a 
                      href={formData.certificate_content} 
                      download={formData.certificate_name || 'certificate'}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {formData.certificate_name || 'Download Certificate'}
                    </a>
                  ) : (
                    <span>{formData.certificate_name || "No file chosen"}</span>
                  )}
                  {!isViewing && formData.certificate_content && (
                    <button type="button" onClick={() => setFormData({...formData, certificate_name: '', certificate_content: ''})} className="p-1 text-slate-400 hover:text-red-600 transition-colors" title={translateUi("Remove")}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="md:col-span-2 pt-4 border-t border-slate-100 text-sm text-slate-500">
              {translateUi("Last updated by:")} <span className="font-medium text-slate-700">{formData.last_updated_by}</span>
            </div>
            <div className="md:col-span-2 flex justify-end space-x-4 pt-4 border-t border-slate-100">
              <button 
                type="button" 
                onClick={() => setShowForm(false)}
                className="px-6 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-xl transition-colors"
              >
                {isViewing ? translateUi("Close") : translateUi("Cancel")}
              </button>
              {!isViewing && (
                <button 
                  type="submit" 
                  disabled={uploading}
                  className="px-8 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50"
                >
                  {uploading ? translateUi("Saving...") : translateUi("Save Record")}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-12">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredMedicals.length > 0 ? (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
                <th className="px-6 py-4 font-semibold"> {translateUi("Employee")} </th>
                <th className="px-6 py-4 font-semibold">{translateUi("Date Passed")}</th>
                <th className="px-6 py-4 font-semibold">{translateUi("Expiry Date")}</th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Doctor")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Status")} </th>
                <th className="px-6 py-4 font-semibold"> {translateUi("Certificate")} </th>
                <th className="px-6 py-4 font-semibold text-right"> {translateUi("Actions")} </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredMedicals.map((med, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{med.employee_name}</div>
                    <div className="text-xs text-slate-500">{med.employee_id}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{med.date_completed}</td>
                  <td className="px-6 py-4 text-slate-600">{formatDateUK(med.expiry_date)}</td>
                  <td className="px-6 py-4 text-slate-600">{med.doctor || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-semibold",
                      showArchived ? "bg-slate-100 text-slate-700" :
                      (med.status === 'F1' || med.status === 'Fit' || med.status === 'Valid' || med.status === 'Valid and fit') ? "bg-emerald-100 text-emerald-700 status-chip-positive" :
                      med.status === 'F2' ? "bg-orange-100 text-orange-700" : 
                      "bg-red-100 text-red-700"
                    )}>
                      {showArchived ? 'Archived' : (med.status === 'Fit' || med.status === 'Valid' || med.status === 'Valid and fit' ? 'F1' : med.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {med.certificate_name ? (
                      <button 
                        onClick={() => handleView(med)}
                        className="flex items-center text-indigo-600 hover:text-indigo-800"
                      >
                        <FileText className="w-4 h-4 mr-1" /> {translateUi("View")} </button>
                    ) : (
                      <span className="text-slate-400 text-sm">{translateUi("No file")}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <button 
                        onClick={() => handleView(med)}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        title={translateUi("View Record")}
                      >
                        <Search className="w-4 h-4" />
                      </button>
                      {canEditMedicals && (
                        <>
                          <button 
                            onClick={() => handleEdit(med)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                            title={translateUi("Edit")}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(med.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title={translateUi("Delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
          <Stethoscope className="w-12 h-12 text-slate-200 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900">No {showArchived ? translateUi("Archived") : translateUi("Live")} Medical Records</h2>
          <p className="text-slate-500">
            {showArchived ? 'There are no archived medical records.' : 'Click "New Medical Record" to add one.'}
          </p>
        </div>
      )}
    </div>
  );
};

const QAVerificationsPage = ({
  companyCode,
  currentUserEmail,
  currentUserName,
  isCompanyAdmin,
  isDarkMode,
  onNavigateToEmployeeFix,
}: {
  companyCode?: string;
  currentUserEmail?: string;
  currentUserName?: string;
  isCompanyAdmin?: boolean;
  isDarkMode?: boolean;
  onNavigateToEmployeeFix?: (opts: {
    employeeId: string;
    companyCode: string;
    tab: string;
    item: VerificationItem;
  }) => void;
}) => {
  const dark = !!isDarkMode;
  const [qaTab, setQaTab] = useState<"history" | "checks">("history");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setQaTab("history")}
          className={cn(
            "px-4 py-2 rounded-xl text-sm font-semibold transition-colors min-h-[44px]",
            qaTab === "history"
              ? dark
                ? "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/40"
                : "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200"
              : dark
                ? "text-slate-300 hover:bg-slate-800"
                : "text-slate-600 hover:bg-slate-100",
          )}
        >
          {translateUi("Run history")}
        </button>
        {isCompanyAdmin ? (
          <button
            type="button"
            onClick={() => setQaTab("checks")}
            className={cn(
              "px-4 py-2 rounded-xl text-sm font-semibold transition-colors min-h-[44px]",
              qaTab === "checks"
                ? dark
                  ? "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/40"
                  : "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200"
                : dark
                  ? "text-slate-300 hover:bg-slate-800"
                  : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {translateUi("Check configuration")}
          </button>
        ) : null}
      </div>
      {qaTab === "checks" && isCompanyAdmin ? (
        <QAVerificationCheckSettingsPage
          companyCode={companyCode}
          currentUserEmail={currentUserEmail}
          isDarkMode={isDarkMode}
        />
      ) : (
        <QAVerificationsHistoryPage
          companyCode={companyCode}
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          isCompanyAdmin={isCompanyAdmin}
          isDarkMode={isDarkMode}
          onNavigateToEmployeeFix={onNavigateToEmployeeFix}
        />
      )}
    </div>
  );
};

/** Minimum time the login train overlay stays visible (ms), even if auth finishes sooner. */
const LOGIN_OVERLAY_MIN_MS = 4000;
/** Natural size of /GBR_BG.png – used to align flyout GBR with sidebar cover. */
const GBR_BG_NATURAL_W = 960;
const GBR_BG_NATURAL_H = 640;
const ADMIN_FLYOUT_DESKTOP_WIDTH_PX = 320;
const APP_CHROME_STYLES = `
  .rv-app-chrome .rv-gbr-chrome-panel {
    position: relative;
    isolation: isolate;
    background:
      radial-gradient(720px 300px at 105% -5%, rgba(211, 17, 45, 0.28), transparent 52%),
      radial-gradient(640px 280px at -15% 15%, rgba(0, 86, 255, 0.26), transparent 50%),
      linear-gradient(180deg, #001f5c 0%, #0b1220 52%, #0f172a 100%);
  }
  .rv-app-chrome .rv-gbr-chrome-panel::before,
  .rv-administration-flyout.rv-gbr-chrome-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background-image: url("/GBR_BG.png");
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
    opacity: 0.24;
    pointer-events: none;
  }
  /* One painted GBR layer across nav + flyout; panels only show content when open */
  .rv-gbr-continuity-layer.rv-gbr-chrome-panel {
    position: fixed;
    pointer-events: none;
    z-index: 9998;
    overflow: hidden;
    border: none;
    box-shadow: none;
    animation: none;
  }
  .rv-gbr-continuity-layer.rv-gbr-chrome-panel::before {
    background-size: var(--rv-gbr-painted-w) var(--rv-gbr-painted-h);
    background-position: var(--rv-gbr-sidebar-pos-x) top;
  }
  .rv-app-chrome.rv-admin-flyout-open .rv-main-sidebar-host.rv-gbr-chrome-delegated {
    position: relative;
    z-index: 9999;
  }
  .rv-app-chrome.rv-admin-flyout-open .rv-main-sidebar.rv-gbr-chrome-delegated,
  .rv-app-chrome.rv-admin-flyout-open .rv-administration-flyout.rv-gbr-chrome-delegated {
    background: transparent !important;
    box-shadow: none;
  }
  .rv-app-chrome.rv-admin-flyout-open .rv-main-sidebar.rv-gbr-chrome-delegated::before,
  .rv-app-chrome.rv-admin-flyout-open .rv-main-sidebar.rv-gbr-chrome-delegated::after,
  .rv-app-chrome.rv-admin-flyout-open .rv-administration-flyout.rv-gbr-chrome-delegated::before,
  .rv-app-chrome.rv-admin-flyout-open .rv-administration-flyout.rv-gbr-chrome-delegated::after {
    display: none;
  }
  .rv-app-chrome .rv-gbr-chrome-panel::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
      180deg,
      rgba(11, 18, 32, 0.38) 0%,
      rgba(15, 23, 42, 0.68) 55%,
      rgba(15, 23, 42, 0.78) 100%
    );
    pointer-events: none;
  }
  .rv-app-chrome .rv-gbr-chrome-panel > * {
    position: relative;
    z-index: 1;
  }
  .rv-app-chrome .rv-main-sidebar.rv-gbr-chrome-panel {
    overflow: hidden;
    border-right: 1px solid rgba(148, 163, 184, 0.22);
  }
  /* Mobile overlay nav: zero-width flex shell so main content spans the full viewport. */
  .rv-app-chrome .rv-main-sidebar-shell {
    flex: 0 0 0;
    width: 0;
    min-width: 0;
    overflow: visible;
    border: none;
    padding: 0;
  }
  .rv-app-chrome .rv-main-sidebar.rv-main-sidebar--mobile-end {
    border-right: none;
    border-left: 1px solid rgba(148, 163, 184, 0.22);
  }
  .rv-app-chrome .rv-main-sidebar.rv-sidebar-flyout-open {
    border-right-color: transparent;
  }
  .rv-app-chrome .rv-administration-flyout.rv-gbr-chrome-panel,
  .rv-administration-flyout.rv-gbr-chrome-panel {
    background-color: #0b1220;
    border: 1px solid rgba(148, 163, 184, 0.22);
    border-left: none;
    box-shadow: 12px 0 40px -20px rgba(2, 6, 23, 0.75);
    animation: rv-administration-flyout-in 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .rv-administration-flyout.rv-gbr-chrome-panel {
    position: fixed;
    isolation: isolate;
    overflow: hidden;
    background:
      radial-gradient(720px 300px at 105% -5%, rgba(211, 17, 45, 0.28), transparent 52%),
      radial-gradient(640px 280px at -15% 15%, rgba(0, 86, 255, 0.26), transparent 50%),
      linear-gradient(180deg, #001f5c 0%, #0b1220 52%, #0f172a 100%);
  }
  .rv-administration-flyout.rv-gbr-chrome-panel::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(
      180deg,
      rgba(11, 18, 32, 0.38) 0%,
      rgba(15, 23, 42, 0.68) 55%,
      rgba(15, 23, 42, 0.78) 100%
    );
    pointer-events: none;
  }
  .rv-administration-flyout.rv-gbr-chrome-panel > * {
    position: relative;
    z-index: 1;
  }
  @keyframes rv-administration-flyout-in {
    from {
      opacity: 0;
      transform: translateX(-14px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .rv-app-chrome .rv-administration-flyout.rv-gbr-chrome-panel,
    .rv-administration-flyout.rv-gbr-chrome-panel {
      animation: none;
    }
  }
  .rv-app-chrome .rv-main-sidebar > :not(.rv-administration-flyout):not(.rv-administration-backdrop) {
    position: relative;
    z-index: 1;
  }
  .rv-app-chrome .rv-main-nav > button,
  .rv-app-chrome .rv-main-sidebar-footer > button {
    border: 1px solid transparent;
    backdrop-filter: saturate(1.1);
  }
  .rv-app-chrome .rv-main-nav > button:hover,
  .rv-app-chrome .rv-main-sidebar-footer > button:hover {
    border-color: rgba(148, 163, 184, 0.24);
    box-shadow: 0 6px 16px -14px rgba(15, 23, 42, 0.7);
  }
  .rv-app-chrome .rv-main-nav-sub {
    border-left: 1px dashed rgba(100, 116, 139, 0.35);
    margin-left: 0.85rem;
    padding-left: 0.75rem;
  }
  .rv-app-chrome .rv-main-header {
    backdrop-filter: blur(10px);
  }
  .rv-app-chrome .rv-main-pane {
    border-radius: 1rem;
  }
  .rv-app-chrome:not(.rv-dark) .rv-main-pane {
    background: linear-gradient(180deg, rgba(255,255,255,0.72), rgba(248,250,252,0.78));
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px -28px rgba(15, 23, 42, 0.35);
  }
  .rv-app-chrome.rv-dark .rv-main-pane {
    background: linear-gradient(180deg, rgba(15,23,42,0.55), rgba(2,6,23,0.48));
    border: 1px solid rgba(71, 85, 105, 0.6);
    box-shadow: 0 16px 44px -30px rgba(2, 6, 23, 0.9);
  }
  @keyframes rv-setup-step-pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
    }
  }
  .rv-setup-step-pulse {
    animation: rv-setup-step-pulse 2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .rv-setup-step-pulse {
      animation: none;
    }
  }
`;

export default function App() {
  const { t } = useTranslation();
  const uiLocale = useUiLocale();
  ensureEnabledLanguagesLoaded();
  const { isMobileOrTablet, isMobile, isTablet, isCompactDesktop } = useDeviceType();
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoadedOnce, setEmployeesLoadedOnce] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem('rv_last_employee');
      if (!raw) return null;
      const saved = JSON.parse(raw) as { id?: string };
      return saved?.id ? String(saved.id) : null;
    } catch {
      return null;
    }
  });
  const [selectedEmployeeCompanyCode, setSelectedEmployeeCompanyCode] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      const raw = window.sessionStorage.getItem('rv_last_employee');
      if (!raw) return undefined;
      const saved = JSON.parse(raw) as { companyCode?: string | null };
      return saved?.companyCode ? String(saved.companyCode) : undefined;
    } catch {
      return undefined;
    }
  });
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const hasRestoredAssessmentSession = useRef(false);
  const [restoringAssessmentNotice, setRestoringAssessmentNotice] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "dashboard"
    | "team"
    | "employees"
    | "incidents"
    | "reporting"
    | "dashboard-details"
    | "qa-verifications"
    | "tasks"
    | "safety-briefs"
    | "trainee-driver"
    | "medicals"
    | "licencing"
    | "help-support"
    | "addons"
    | "addons-storage"
    | "new-company"
    | "onboarding"
    | "addons-management"
    | "cycle-builder"
    | "custom-job-roles"
    | "grading-scale"
    | "company-email-templates"
    | "company-standards"
    | "traction-routes"
    | "org-structure"
    | "role-permissions"
    | "announcements"
    | "account-profile"
    | "security"
    | "company-billing"
    | "company-storage"
    | "task-module-settings"
    | "safety-brief-module-settings"
    | "qa-verifications-module-settings"
    | "trainee-driver-module-settings"
    | "driver-reports-module-settings"
    | "leave-absence-module-settings"
    | "medication-checks-module-settings"
    | "company-admin-licences-module-settings"
    | "trainee-driver-addon"
    | "leave-absence"
    | "train-source-settings"
    | "app-languages"
    | "migration-center"
    | "sys-logs"
    | "support-desk"
    | "email-templates"
    | "investigations-api-management"
    | "company-logo"
    | "framework"
  >(() => {
    // Default tab selection on initial load:
    // 1) Respect ?tab= URL param if present and valid
    // 2) Otherwise, restore the last tab from sessionStorage
    // 3) Fallback to dashboard
    if (typeof window === 'undefined') return 'dashboard';
    try {
      const allowedTabs = [
        'dashboard',
        'team',
        'employees',
        'incidents',
        'reporting',
        'dashboard-details',
        'qa-verifications',
        'tasks',
        'safety-briefs',
        'trainee-driver',
        'leave-absence',
        'medicals',
        'licencing',
        'help-support',
        'addons',
        'addons-storage',
        'new-company',
        'onboarding',
        'addons-management',
        'cycle-builder',
        'custom-job-roles',
        'grading-scale',
        'company-email-templates',
        'company-standards',
        'traction-routes',
        'org-structure',
        'role-permissions',
        'announcements',
        'account-profile',
        'security',
        'company-billing',
        'company-storage',
        'task-module-settings',
        'safety-brief-module-settings',
        'qa-verifications-module-settings',
        'trainee-driver-module-settings',
        'driver-reports-module-settings',
        'leave-absence-module-settings',
        'medication-checks-module-settings',
        'company-admin-licences-module-settings',
        'trainee-driver-addon',
        'train-source-settings',
        'app-languages',
        'migration-center',
        'sys-logs',
        'support-desk',
        'email-templates',
        'investigations-api-management',
        'company-logo',
        'framework',
      ] as const;

      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam && (allowedTabs as readonly string[]).includes(tabParam)) {
        return tabParam as typeof allowedTabs[number];
      }

      const stored = window.sessionStorage.getItem('rv_active_tab');
      if (stored && (allowedTabs as readonly string[]).includes(stored)) {
        return stored as typeof allowedTabs[number];
      }
    } catch {
      // Ignore errors and fall through to default
    }
    return 'dashboard';
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => getInitialSidebarOpen());
  const [isAdministrationOpen, setIsAdministrationOpen] = useState(false);
  const [isAdministrationCompanyConfigOpen, setIsAdministrationCompanyConfigOpen] = useState(false);
  const [isAdministrationStorageOpen, setIsAdministrationStorageOpen] = useState(false);
  const administrationPanelRef = useRef<HTMLDivElement | null>(null);
  const administrationToggleRef = useRef<HTMLButtonElement | null>(null);
  const mainSidebarRef = useRef<HTMLDivElement | null>(null);
  const appChromeRef = useRef<HTMLDivElement | null>(null);
  const [administrationFlyoutLayout, setAdministrationFlyoutLayout] = useState<{
    left: number;
    top: number;
    height: number;
    continuityLeft: number;
    continuityWidth: number;
  } | null>(null);

  const syncGbrContinuityLayer = useCallback(() => {
    const sidebar = mainSidebarRef.current;
    const chrome = appChromeRef.current;
    if (!sidebar || !chrome) return;
    const rect = sidebar.getBoundingClientRect();
    const sw = rect.width;
    const sh = rect.height;
    if (sw <= 0 || sh <= 0) return;
    const scale = Math.max(sw / GBR_BG_NATURAL_W, sh / GBR_BG_NATURAL_H);
    const paintedW = scale * GBR_BG_NATURAL_W;
    const paintedH = scale * GBR_BG_NATURAL_H;
    const paintedX = (sw - paintedW) / 2;
    chrome.style.setProperty("--rv-gbr-painted-w", `${paintedW}px`);
    chrome.style.setProperty("--rv-gbr-painted-h", `${paintedH}px`);
    chrome.style.setProperty("--rv-gbr-sidebar-pos-x", `${paintedX}px`);
  }, []);

  const updateAdministrationFlyoutPosition = useCallback(() => {
    const sidebar = mainSidebarRef.current;
    const toggle = administrationToggleRef.current;
    // Prefer the sidebar shell; fall back to the Admin toggle so collapsed rail still positions.
    const rect = sidebar?.getBoundingClientRect?.() ?? toggle?.getBoundingClientRect?.();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;
    const sw = Math.round(rect.width);
    // Full-height flyout from the sidebar top when we have the sidebar; otherwise near the toggle.
    const fromSidebar = Boolean(sidebar && sidebar.getBoundingClientRect().width > 0);
    const top = fromSidebar ? Math.round(rect.top) : Math.max(8, Math.round(rect.top) - 8);
    const height = fromSidebar
      ? Math.round(rect.height)
      : Math.min(Math.round(window.innerHeight - top - 8), 560);
    syncGbrContinuityLayer();
    setAdministrationFlyoutLayout({
      left: Math.max(0, Math.round(rect.right) - 1),
      top,
      height,
      continuityLeft: Math.round(rect.left),
      continuityWidth: sw + ADMIN_FLYOUT_DESKTOP_WIDTH_PX,
    });
  }, [syncGbrContinuityLayer]);

  /** Solid panel when the icon rail is collapsed — continuity GBR looks empty at ~80px width. */
  const administrationFlyoutUsesContinuity = isAdministrationOpen && !isMobileOrTablet && isSidebarOpen;
  const [employeeFilter, setEmployeeFilter] = useState<'all' | 'active' | 'off-track'>('all');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<SystemUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isResolvingCompanyContext, setIsResolvingCompanyContext] = useState(false);
  const [loginForm, setLoginForm] = useState({ companyCode: '', email: '', password: '' });
  const [loginPasswordVisible, setLoginPasswordVisible] = useState(false);
  const [loginMfa, setLoginMfa] = useState<{ mode: "verify" | "setup" | "email"; token: string; email: string; companyCode?: string } | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const loginOverlayStartedAtRef = React.useRef<number | null>(null);
  const loginOverlayEndTimerRef = React.useRef<number | null>(null);
  /** Set after validate-login succeeds, cleared after auth listener applies dashboard landing (avoids stale isLoggingIn in Azure callback). */
  const goToDashboardAfterSignInRef = React.useRef(false);
  const scheduleEndLoginOverlay = useCallback(() => {
    if (loginOverlayEndTimerRef.current) {
      clearTimeout(loginOverlayEndTimerRef.current);
      loginOverlayEndTimerRef.current = null;
    }
    const started = loginOverlayStartedAtRef.current;
    if (started == null) {
      setIsLoggingIn(false);
      return;
    }
    const wait = Math.max(0, LOGIN_OVERLAY_MIN_MS - (Date.now() - started));
    if (wait <= 0) {
      setIsLoggingIn(false);
      loginOverlayStartedAtRef.current = null;
      return;
    }
    loginOverlayEndTimerRef.current = window.setTimeout(() => {
      setIsLoggingIn(false);
      loginOverlayStartedAtRef.current = null;
      loginOverlayEndTimerRef.current = null;
    }, wait);
  }, []);
  const readRememberedCompanyScope = useCallback((email: string): string => {
    const em = email.trim().toLowerCase();
    if (!em) return "";
    try {
      return localStorage.getItem(`rv_last_company_code:${em}`)?.trim() ?? "";
    } catch {
      return "";
    }
  }, []);
  const writeRememberedCompanyScope = useCallback((email: string, companyCode: string | null | undefined) => {
    const em = email.trim().toLowerCase();
    if (!em) return;
    const cc = String(companyCode ?? "").trim();
    try {
      if (cc) localStorage.setItem(`rv_last_company_code:${em}`, cc);
      else localStorage.removeItem(`rv_last_company_code:${em}`);
    } catch {
      /* ignore storage */
    }
  }, []);
  /** Scoped /api/users/me: optional company from state (post-resolve), session (login flow), or remembered local scope (refresh/new tab). */
  const buildUsersMeUrl = useCallback((email: string, companyScope?: string | null) => {
    const em = email.trim().toLowerCase();
    const fromState = companyScope?.trim();
    let scoped = fromState ?? "";
    if (!scoped) {
      try {
        scoped = sessionStorage.getItem("rv_login_company_code")?.trim() ?? "";
      } catch {
        scoped = "";
      }
    }
    if (!scoped) scoped = readRememberedCompanyScope(em);
    if (scoped) {
      return `/api/users/me?email=${encodeURIComponent(em)}&companyCode=${encodeURIComponent(scoped)}`;
    }
    return `/api/users/me?email=${encodeURIComponent(em)}`;
  }, [readRememberedCompanyScope]);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotForm, setForgotForm] = useState({ companyCode: '', email: '' });
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [dashboardModalConfig, setDashboardModalConfig] = useState<{ isOpen: boolean, title: string, type: string }>({ isOpen: false, title: '', type: '' });
  const [currentCompany, setCurrentCompany] = useState<CompanyDoc | null>(null);
  const [impersonatedCompany, setImpersonatedCompany] = useState<CompanyDoc | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<SystemUser | null>(null);
  const transferPendingRef = React.useRef<{ id: string; companyCode: string; employee?: Employee } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = React.useRef<HTMLDivElement | null>(null);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [companiesForImpersonation, setCompaniesForImpersonation] = useState<{ id: string; name: string }[]>([]);
  const [companiesLoadingForImpersonation, setCompaniesLoadingForImpersonation] = useState(false);
  const [companiesImpersonationError, setCompaniesImpersonationError] = useState<string | null>(null);
  const companiesImpersonationFetchGenRef = useRef(0);
  const companiesForImpersonationFiltered = useMemo(() => {
    const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");
    return companiesForImpersonation.filter((company) => {
      const id = normalize(String(company.id || ""));
      const name = normalize(String(company.name || ""));
      const combined = `${name} ${id}`;
      return !(
        name === "rail intel" ||
        name === "railintel" ||
        name === "railintel.co.uk" ||
        id === "rail intel" ||
        id === "railintel" ||
        id === "railintel.co.uk" ||
        combined.includes("rail intel") ||
        combined.includes("railintel")
      );
    });
  }, [companiesForImpersonation]);
  /** Same naming rules as companies hidden from “View as” — the CMS operator / Rail Intel Ltd tenant for internal staff. */
  const cmsProviderCompanyCode = useMemo(
    () => detectCmsProviderCompanyCodeFromCompanyList(companiesForImpersonation),
    [companiesForImpersonation]
  );
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const impersonationMenuRef = React.useRef<HTMLDivElement | null>(null);
  const impersonationMenuPanelRef = React.useRef<HTMLDivElement | null>(null);
  const [impersonationMenuPosition, setImpersonationMenuPosition] = useState<{ top: number; right: number; left: number; width: number } | null>(null);
  const [enableLoginLoading, setEnableLoginLoading] = useState<string | null>(null);
  const [enableLoginMessage, setEnableLoginMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [companySwitchMessage, setCompanySwitchMessage] = useState<string | null>(null);
  const [inactivityWarningOpen, setInactivityWarningOpen] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(60);
  const [companyAddons, setCompanyAddons] = useState<string[]>([]);
  const [addonsRefreshTrigger, setAddonsRefreshTrigger] = useState(0);
  const [effectiveCompanyPermissions, setEffectiveCompanyPermissions] = useState<string[]>([]);
  const [effectiveCompanyPermissionsLoaded, setEffectiveCompanyPermissionsLoaded] = useState(false);
  const [hasOwnTraineeDriverEnrollment, setHasOwnTraineeDriverEnrollment] = useState(false);
  const [ownTraineeDriverEnrollmentLoaded, setOwnTraineeDriverEnrollmentLoaded] = useState(true);
  const [libraryDocuments, setLibraryDocuments] = useState<{ id: number; name: string; type: string | null; size_display: string | null }[]>([]);
  const [libraryDocumentsLoading, setLibraryDocumentsLoading] = useState(false);
  const [libraryDocumentUploading, setLibraryDocumentUploading] = useState(false);
  const libraryDocumentInputRef = React.useRef<HTMLInputElement>(null);
  const [canAccessSupportDesk, setCanAccessSupportDesk] = useState(false);
  const [activeAnnouncements, setActiveAnnouncements] = useState<
    {
      id: number | string;
      title: string;
      message: string;
      type: string;
      created_at?: string;
      employee_id?: string;
      note_ref?: string;
      company_code?: string;
      task_id?: string;
    }[]
  >([]);
  const [activeAnnouncementsContextKey, setActiveAnnouncementsContextKey] = useState("");
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<Set<number | string>>(() => new Set());
  const [dismissedAnnouncementsReady, setDismissedAnnouncementsReady] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] = useState<{ enabled: boolean; message: string } | null>(null);
  const [showAdminLoginDuringMaintenance, setShowAdminLoginDuringMaintenance] = useState(false);
  const [maintenanceLogoutBusy, setMaintenanceLogoutBusy] = useState(false);
  const STORAGE_KEY_PREFIX = "setupWizardStep_";
  const validSteps = ['change-password', 'welcome', 'terms-and-conditions', 'company-details', 'job-roles', 'addons', 'framework', 'company-admin', 'review'] as const;
  const [setupWizardStep, setSetupWizardStep] = useState<typeof validSteps[number]>('welcome');
  const [onboardingCompleteMessage, setOnboardingCompleteMessage] = useState<string | null>(null);
  const [companyActivationSuccess, setCompanyActivationSuccess] = useState<CompanyActivationSuccessState | null>(null);
  /** Bumped after company activation so tenant context + employee lists rebind to the activated company. */
  const [companyContextReloadToken, setCompanyContextReloadToken] = useState(0);
  const [showNominatedAdminWelcome, setShowNominatedAdminWelcome] = useState(false);
  const [nomineeNavTourActive, setNomineeNavTourActive] = useState(false);
  const [nomineeNavTourStepIndex, setNomineeNavTourStepIndex] = useState(0);
  const [pendingNomineeNavTour, setPendingNomineeNavTour] = useState(false);
  const [resendNomineeEmailLoading, setResendNomineeEmailLoading] = useState(false);
  const [resendNomineeEmailMessage, setResendNomineeEmailMessage] = useState<string | null>(null);
  /** First-login flow for nominated company admin / contact temp password — blocks the app until Azure password is updated. */
  const [mandatoryTempPasswordGate, setMandatoryTempPasswordGate] = useState(false);
  const [mandatoryPwForm, setMandatoryPwForm] = useState({ newPassword: "", confirmPassword: "" });
  const [mandatoryPwError, setMandatoryPwError] = useState<string | null>(null);
  const [mandatoryPwSaving, setMandatoryPwSaving] = useState(false);
  const [companySetupReminder, setCompanySetupReminder] = useState<{
    show: boolean;
    issues: {
      missingCycles?: boolean;
      missingCompanyStandards?: boolean;
      missingFramework?: boolean;
      missingGradingScale?: boolean;
      missingTractionAndDepots?: boolean;
      missingRoleMappings?: boolean;
    } | null;
  }>({ show: false, issues: null });
  const [companySetupAddonsPromptSkipped, setCompanySetupAddonsPromptSkipped] = useState(false);
  const [finishCompanySetupDialogOpen, setFinishCompanySetupDialogOpen] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const dark = window.localStorage.getItem('rv_theme') === 'dark';
      if (dark) document.documentElement.classList.add('rv-theme-dark');
      return dark;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('rv_theme', isDarkMode ? 'dark' : 'light');
    } catch {
      // Ignore storage errors.
    }
    const root = document.documentElement;
    if (isDarkMode) root.classList.add('rv-theme-dark');
    else root.classList.remove('rv-theme-dark');
    return () => {
      root.classList.remove('rv-theme-dark');
    };
  }, [isDarkMode]);

  useEffect(() => {
    if (!isAdministrationOpen) return;
    let handleOutsidePointer: ((event: MouseEvent | TouchEvent) => void) | null = null;
    // Defer attach so the opening click does not immediately dismiss the flyout.
    const timer = window.setTimeout(() => {
      handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
        const target = event.target as Node | null;
        if (!target) return;
        if (administrationPanelRef.current?.contains(target)) return;
        if (administrationToggleRef.current?.contains(target)) return;
        setIsAdministrationOpen(false);
      };
      document.addEventListener('mousedown', handleOutsidePointer);
      document.addEventListener('touchstart', handleOutsidePointer);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      if (handleOutsidePointer) {
        document.removeEventListener('mousedown', handleOutsidePointer);
        document.removeEventListener('touchstart', handleOutsidePointer);
      }
    };
  }, [isAdministrationOpen]);

  useLayoutEffect(() => {
    if (!isAdministrationOpen || isMobileOrTablet) return;
    updateAdministrationFlyoutPosition();
    window.addEventListener('resize', updateAdministrationFlyoutPosition);
    return () => window.removeEventListener('resize', updateAdministrationFlyoutPosition);
  }, [isAdministrationOpen, isMobileOrTablet, isSidebarOpen, updateAdministrationFlyoutPosition]);

  useEffect(() => {
    if (isAdministrationOpen) return;
    const chrome = appChromeRef.current;
    if (!chrome) return;
    chrome.style.removeProperty("--rv-gbr-painted-w");
    chrome.style.removeProperty("--rv-gbr-painted-h");
    chrome.style.removeProperty("--rv-gbr-sidebar-pos-x");
  }, [isAdministrationOpen]);

  useEffect(() => {
    if (!impersonationOpen) return;
    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (impersonationMenuRef.current?.contains(target)) return;
      if (impersonationMenuPanelRef.current?.contains(target)) return;
      setImpersonationOpen(false);
    };
    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [impersonationOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (userMenuRef.current?.contains(target)) return;
      setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [userMenuOpen]);

  const updateImpersonationMenuPosition = useCallback(() => {
    const anchor = impersonationMenuRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const padding = 12;
    const viewportW = window.innerWidth;
    // Phones/tablets: nearly full-width panel under the header control so company codes fit.
    if (isMobileOrTablet) {
      const left = padding;
      const width = Math.max(0, viewportW - padding * 2);
      setImpersonationMenuPosition({
        top: Math.round(rect.bottom + 8),
        right: padding,
        left,
        width,
      });
      return;
    }
    const left = Math.max(padding, Math.round(rect.left));
    const maxWidth = Math.max(0, viewportW - left - padding);
    const menuMinWidth = 280;
    const idealRight = Math.max(padding, Math.round(viewportW - rect.right));
    const maxRight = Math.max(padding, viewportW - menuMinWidth - padding);
    setImpersonationMenuPosition({
      top: Math.round(rect.bottom + 8),
      right: Math.min(idealRight, maxRight),
      left,
      width: Math.min(Math.max(Math.round(rect.width), 0), maxWidth || Math.round(rect.width)),
    });
  }, [isMobileOrTablet]);

  useEffect(() => {
    if (!impersonationOpen) {
      setImpersonationMenuPosition(null);
      return;
    }
    updateImpersonationMenuPosition();
    const handlePositionChange = () => updateImpersonationMenuPosition();
    window.addEventListener('resize', handlePositionChange);
    document.addEventListener('scroll', handlePositionChange, true);
    return () => {
      window.removeEventListener('resize', handlePositionChange);
      document.removeEventListener('scroll', handlePositionChange, true);
    };
  }, [impersonationOpen, updateImpersonationMenuPosition]);

  useEffect(() => {
    if (!companySwitchMessage) return;
    const timer = window.setTimeout(() => setCompanySwitchMessage(null), 3500);
    return () => window.clearTimeout(timer);
  }, [companySwitchMessage]);

  // Persist the currently active tab so browser refresh keeps you on the same section
  useEffect(() => {
    try {
      window.sessionStorage.setItem('rv_active_tab', activeTab);
    } catch {
      // Ignore storage errors
    }
  }, [activeTab]);

  // Clear top search input whenever navigation tab changes.
  useEffect(() => {
    setSearchQuery('');
  }, [activeTab]);

  const [lastEmployeeProfileTab, setLastEmployeeProfileTab] = useState<string>(() => {
    if (typeof window === 'undefined') return 'Main';
    try {
      const raw = window.sessionStorage.getItem('rv_last_employee');
      if (!raw) return 'Main';
      const saved = JSON.parse(raw) as { profileTab?: string };
      const tab = saved?.profileTab ? String(saved.profileTab) : 'Main';
      if (tab === 'Tasks') return 'Tasks';
      return (EMPLOYEE_PROFILE_TAB_OPTIONS as readonly string[]).includes(tab) ? tab : 'Main';
    } catch {
      return 'Main';
    }
  });
  const restoredEmployeeIdRef = React.useRef<string | null>(null);
  const prevSelectedEmployeeIdRef = React.useRef<string | null>(selectedEmployeeId);

  // When leaving an employee profile (back to list, etc.), drop persisted tab so reopening always lands on Main
  useEffect(() => {
    const prev = prevSelectedEmployeeIdRef.current;
    if (prev && !selectedEmployeeId) {
      try {
        sessionStorage.removeItem(`rv_employee_tab_${prev}`);
        sessionStorage.removeItem(`rv_employee_viewing_cycle_${prev}`);
        sessionStorage.removeItem(`rv_employee_open_cycle_${prev}`);
      } catch {
        // ignore
      }
      setLastEmployeeProfileTab('Main');
      restoredEmployeeIdRef.current = null;
    }
    prevSelectedEmployeeIdRef.current = selectedEmployeeId;
  }, [selectedEmployeeId]);

  // Persist the currently selected employee and their profile tab so refresh stays on same employee and tab (e.g. Cycles)
  useEffect(() => {
    try {
      if (selectedEmployeeId) {
        window.sessionStorage.setItem(
          'rv_last_employee',
          JSON.stringify({
            id: selectedEmployeeId,
            companyCode: selectedEmployeeCompanyCode ?? null,
            profileTab: lastEmployeeProfileTab,
          }),
        );
      } else {
        window.sessionStorage.removeItem('rv_last_employee');
      }
    } catch {
      // Ignore storage errors
    }
  }, [selectedEmployeeId, selectedEmployeeCompanyCode, lastEmployeeProfileTab]);

  // Restore assessment session if present (takes precedence over generic last-employee restore)
  useEffect(() => {
    if (hasRestoredAssessmentSession.current || selectedEmployeeId) return;
    hasRestoredAssessmentSession.current = true;
    try {
      const raw = sessionStorage.getItem(ASSESSMENT_RESUME_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { employeeId?: string; companyCode?: string | null };
      if (!saved?.employeeId) return;
      setSelectedEmployeeId(saved.employeeId);
      setSelectedEmployeeCompanyCode(saved.companyCode ?? undefined);
      setActiveTab('employees');
      return;
    } catch {
      // fall through to generic restore
    }

    // If no assessment session, restore the last viewed employee and their profile tab (if any)
    try {
      const rawLast = sessionStorage.getItem('rv_last_employee');
      if (!rawLast) return;
      const savedLast = JSON.parse(rawLast) as { id?: string; companyCode?: string | null; profileTab?: string };
      if (!savedLast?.id) return;
      setSelectedEmployeeId(savedLast.id);
      setSelectedEmployeeCompanyCode(savedLast.companyCode ?? undefined);
      const tab =
        savedLast.profileTab &&
        (savedLast.profileTab === 'Tasks' || (EMPLOYEE_PROFILE_TAB_OPTIONS as readonly string[]).includes(savedLast.profileTab))
          ? savedLast.profileTab
          : 'Main';
      setLastEmployeeProfileTab(tab);
      restoredEmployeeIdRef.current = savedLast.id;
      if (activeTab === 'dashboard') {
        setActiveTab('employees');
      }
    } catch {
      // Ignore parsing errors
    }
  }, [selectedEmployeeId, activeTab]);

  useEffect(() => {
    if (!restoringAssessmentNotice) return;
    const id = window.setTimeout(() => setRestoringAssessmentNotice(false), 8000);
    return () => window.clearTimeout(id);
  }, [restoringAssessmentNotice]);

  const handleAssessmentSessionRestored = useCallback(() => {
    setRestoringAssessmentNotice(false);
  }, []);

  const handleEmployeeNotFound = useCallback(() => {
    setSelectedEmployeeId(null);
    setSelectedEmployeeCompanyCode(undefined);
    setRestoringAssessmentNotice(false);
    try {
      sessionStorage.removeItem(ASSESSMENT_RESUME_STORAGE_KEY);
      sessionStorage.removeItem('rv_last_employee');
    } catch {
      /* ignore */
    }
  }, []);
  /** When impersonating a user, adopt their profile for permissions */
  const effectiveProfile = impersonatedUser ?? currentUserProfile;
  const operationalEditUnlocked = allowsOperationalMutations(effectiveProfile);
  const userEmail = (effectiveProfile?.email || user?.email || '').toLowerCase();
  useEffect(() => {
    if (!operationalEditUnlocked && isEmployeeModalOpen) {
      setIsEmployeeModalOpen(false);
      setEditingEmployee(null);
    }
  }, [operationalEditUnlocked, isEmployeeModalOpen]);
  const isNominatedCompanyAdmin =
    (() => {
      const raw = (effectiveProfile as { nominated_company_admin?: unknown } | null | undefined)?.nominated_company_admin;
      if (raw === true) return true;
      if (typeof raw === "number") return raw === 1;
      if (typeof raw === "string") {
        const v = raw.trim().toLowerCase();
        return v === "true" || v === "1" || v === "yes" || v === "y";
      }
      return false;
    })();
  const isAdmin =
    effectiveProfile?.title?.toLowerCase()?.includes('admin') ||
    isNominatedCompanyAdmin ||
    userEmail === 'andyuk38@gmail.com' ||
    userEmail === 'admin@railvault.com';
  const title = effectiveProfile?.title?.toLowerCase() ?? '';
  const isSystemAdmin = (title.includes('system') && (title.includes('administrator') || title.includes('admin'))) || userEmail === 'admin@railvault.com' || userEmail === 'andyuk38@gmail.com';
  const effectiveIsSystemAdmin = isSystemAdmin && !impersonatedCompany && !impersonatedUser;
  const maintenanceBlocksSignedInUser =
    !!maintenanceStatus?.enabled && !!user && !effectiveIsSystemAdmin;
  const loginShowsMaintenanceGate =
    !!maintenanceStatus?.enabled && !user && !showAdminLoginDuringMaintenance;
  /** When impersonating a company (no user), system admin is treated as Administrator. When impersonating a user, adopt their permissions only. */
  const effectiveIsAdmin = impersonatedUser
    ? false
    : isAdmin ||
      (!!impersonatedCompany &&
        !impersonatedUser &&
        ((currentUserProfile?.title?.toLowerCase()?.includes('system') &&
          currentUserProfile?.title?.toLowerCase()?.includes('administrator')) ||
          user?.email?.toLowerCase() === 'admin@railvault.com' ||
          user?.email?.toLowerCase() === 'andyuk38@gmail.com'));

  const effectiveCompany = impersonatedCompany ?? currentCompany;
  const displayCompanyName = isSystemAdmin && !impersonatedCompany && !impersonatedUser ? 'railintel.co.uk' : (effectiveCompany?.name ?? '');
  const isRealSystemAdmin =
    profileTitleIsSystemAdministrator(currentUserProfile?.title) ||
    emailIsBuiltinSystemAdmin(user?.email) ||
    emailIsBuiltinSystemAdmin(currentUserProfile?.email);
  /** Stuck-assessment unlock: real system admin always; or company-scoped admin (not system title). Works while impersonating. */
  const canOverrideStuckAssessment =
    isRealSystemAdmin || (!!effectiveProfile?.company_code && isAdmin && !isSystemAdmin);

  /** Real system admin viewing a company (not impersonating a user) may edit closed-cycle grades, notes, and evidence. */
  const canSystemAdminEditClosedCycleWhileImpersonatingCompany =
    isRealSystemAdmin && !!impersonatedCompany && !impersonatedUser;

  // Auto-expand Administration section when Setup Wizard is shown so Custom job roles is visible
  /**
   * PendingSetup: any signed-in company admin resumes the wizard.
   * Self-nomination sets nominated_company_admin before activation; that must not
   * block the invite contact. A different nominee only gets login after activation
   * (company becomes Active), so the old “awaiting activation” gate is unused.
   */
  const showSetupWizard =
    effectiveCompany?.status === "PendingSetup" &&
    effectiveIsAdmin &&
    !effectiveIsSystemAdmin;
  const showNomineeAwaitingActivation = false;
  /** After sign-in, keep login train overlay until company context loads (avoids dashboard flash before onboarding). */
  const postSignInContextHydrating =
    !!user && isResolvingCompanyContext && goToDashboardAfterSignInRef.current;
  /** Session refresh: hide chrome and main content until company/profile resolve. */
  const appShellBlockedForResolve = !!user && isResolvingCompanyContext;
  /** Activator always sees the thank-you screen — including when they nominated themselves. */
  const showActivationSuccessScreen = !!companyActivationSuccess;
  const useOnboardingWizardBackdrop = showSetupWizard;
  const isOnboardingLocked =
    showSetupWizard ||
    showNomineeAwaitingActivation ||
    showActivationSuccessScreen ||
    (appShellBlockedForResolve && !effectiveIsSystemAdmin);
  const showLoginShell = !user || isLoggingIn || postSignInContextHydrating;
  useEffect(() => {
    if (showSetupWizard) {
      // Do not open Administration during setup — sidebar/flyout is hidden while
      // onboarding is locked, and leaving this true can confuse layout restore later.
      setIsAdministrationOpen(false);
      setIsAdministrationCompanyConfigOpen(false);
    }
  }, [showSetupWizard]);

  /** Lock horizontal page pan while the company setup wizard is open (iOS Safari). */
  useEffect(() => {
    if (!showSetupWizard) return;
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("rv-onboarding-active");
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyWidth = body.style.width;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyBg = body.style.backgroundColor;
    const scrollY = window.scrollY || 0;
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.left = "0";
    body.style.top = `-${scrollY}px`;
    if (isDarkMode) body.style.backgroundColor = "#0f172a";

    const resetHorizontalScroll = () => {
      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
        if (root.scrollLeft) root.scrollLeft = 0;
        if (body.scrollLeft) body.scrollLeft = 0;
      }
      document.querySelectorAll<HTMLElement>(
        ".rv-app-chrome, .rv-main-content, .rv-onboarding-shell, .rv-onboarding-stack, .rv-setup-wizard, .rv-setup-wizard [class*='overflow']"
      ).forEach((el) => {
        if (el.scrollLeft) el.scrollLeft = 0;
      });
    };

    /** After focus/keyboard/layout, Safari may pan mid-frame — re-pin a few times. */
    const resetHorizontalScrollSoon = () => {
      resetHorizontalScroll();
      window.requestAnimationFrame(() => {
        resetHorizontalScroll();
        window.requestAnimationFrame(resetHorizontalScroll);
      });
      window.setTimeout(resetHorizontalScroll, 50);
      window.setTimeout(resetHorizontalScroll, 250);
      window.setTimeout(resetHorizontalScroll, 500);
    };

    let touchStartX = 0;
    let touchStartY = 0;
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const target = event.target as Element | null;
      // Allow signature pad / explicit touch-none controls to handle their own gestures.
      if (target?.closest?.("canvas, .touch-none, [data-allow-touch-pan]")) return;
      const dx = Math.abs(event.touches[0].clientX - touchStartX);
      const dy = Math.abs(event.touches[0].clientY - touchStartY);
      if (dx > dy && dx > 6) {
        event.preventDefault();
        resetHorizontalScroll();
      }
    };

    const onFocusInOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return;
      resetHorizontalScrollSoon();
    };

    resetHorizontalScrollSoon();
    const keepPinned = window.setInterval(resetHorizontalScroll, 1000);
    window.addEventListener("scroll", resetHorizontalScroll, true);
    window.addEventListener("resize", resetHorizontalScrollSoon);
    document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    document.addEventListener("focusin", onFocusInOut, true);
    document.addEventListener("focusout", onFocusInOut, true);
    window.visualViewport?.addEventListener("resize", resetHorizontalScrollSoon);
    window.visualViewport?.addEventListener("scroll", resetHorizontalScroll);

    return () => {
      root.classList.remove("rv-onboarding-active");
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.width = prevBodyWidth;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.backgroundColor = prevBodyBg;
      window.scrollTo(0, scrollY);
      window.clearInterval(keepPinned);
      window.removeEventListener("scroll", resetHorizontalScroll, true);
      window.removeEventListener("resize", resetHorizontalScrollSoon);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("focusin", onFocusInOut, true);
      document.removeEventListener("focusout", onFocusInOut, true);
      window.visualViewport?.removeEventListener("resize", resetHorizontalScrollSoon);
      window.visualViewport?.removeEventListener("scroll", resetHorizontalScroll);
    };
  }, [showSetupWizard, isDarkMode]);

  /** Lock horizontal page pan on the login shell (same fixed-document pattern as onboarding). */
  useEffect(() => {
    if (loading || !showLoginShell) return;
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("rv-login-active");
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyWidth = body.style.width;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyBg = body.style.backgroundColor;
    const scrollY = window.scrollY || 0;
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.left = "0";
    body.style.top = `-${scrollY}px`;
    body.style.backgroundColor = "#020617";

    const resetHorizontalScroll = () => {
      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
        if (root.scrollLeft) root.scrollLeft = 0;
        if (body.scrollLeft) body.scrollLeft = 0;
      }
      document.querySelectorAll<HTMLElement>(
        ".rv-login-shell, .rv-login-card, .rv-login-card-form"
      ).forEach((el) => {
        if (el.scrollLeft) el.scrollLeft = 0;
      });
    };

    const keyboardInsetPx = () => {
      const vv = window.visualViewport;
      if (!vv) return 0;
      return Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    };

    const syncLoginKeyboard = () => {
      resetHorizontalScroll();
      const inset = keyboardInsetPx();
      const keyboardOpen = inset > 80;
      root.style.setProperty("--rv-keyboard-inset", `${inset}px`);
      const shell = document.querySelector<HTMLElement>(".rv-login-shell");
      if (!shell) return;
      shell.classList.toggle("rv-login-shell--keyboard", keyboardOpen);
      if (!keyboardOpen) return;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !shell.contains(active)) return;
      const tag = active.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return;
      const target = (active.closest(".rv-otp-row") as HTMLElement | null) || active;
      const vvHeight = window.visualViewport?.height ?? window.innerHeight;
      const rect = target.getBoundingClientRect();
      const margin = 20;
      if (rect.top >= margin && rect.bottom <= vvHeight - margin) return;
      const delta = rect.top + rect.height / 2 - vvHeight * 0.36;
      shell.scrollTop = Math.max(0, shell.scrollTop + delta);
    };

    const resetHorizontalScrollSoon = () => {
      syncLoginKeyboard();
      window.requestAnimationFrame(() => {
        syncLoginKeyboard();
        window.requestAnimationFrame(syncLoginKeyboard);
      });
      window.setTimeout(syncLoginKeyboard, 50);
      window.setTimeout(syncLoginKeyboard, 250);
      window.setTimeout(syncLoginKeyboard, 500);
    };

    let touchStartX = 0;
    let touchStartY = 0;
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const target = event.target as Element | null;
      if (target?.closest?.("canvas, .touch-none, [data-allow-touch-pan]")) return;
      const dx = Math.abs(event.touches[0].clientX - touchStartX);
      const dy = Math.abs(event.touches[0].clientY - touchStartY);
      if (dx > dy && dx > 6) {
        event.preventDefault();
        resetHorizontalScroll();
      }
    };

    const onFocusInOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return;
      resetHorizontalScrollSoon();
    };

    resetHorizontalScrollSoon();
    const keepPinned = window.setInterval(syncLoginKeyboard, 1000);
    window.addEventListener("scroll", resetHorizontalScroll, true);
    window.addEventListener("resize", resetHorizontalScrollSoon);
    document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    document.addEventListener("focusin", onFocusInOut, true);
    document.addEventListener("focusout", onFocusInOut, true);
    window.visualViewport?.addEventListener("resize", resetHorizontalScrollSoon);
    window.visualViewport?.addEventListener("scroll", syncLoginKeyboard);

    return () => {
      root.classList.remove("rv-login-active");
      root.style.removeProperty("--rv-keyboard-inset");
      document.querySelector(".rv-login-shell")?.classList.remove("rv-login-shell--keyboard");
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.width = prevBodyWidth;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.backgroundColor = prevBodyBg;
      window.scrollTo(0, scrollY);
      window.clearInterval(keepPinned);
      window.removeEventListener("scroll", resetHorizontalScroll, true);
      window.removeEventListener("resize", resetHorizontalScrollSoon);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("focusin", onFocusInOut, true);
      document.removeEventListener("focusout", onFocusInOut, true);
      window.visualViewport?.removeEventListener("resize", resetHorizontalScrollSoon);
      window.visualViewport?.removeEventListener("scroll", syncLoginKeyboard);
    };
  }, [loading, showLoginShell]);

  const resolvedCompanyCodeForNominee =
    String(
      effectiveCompany?.id ||
        effectiveCompany?.companyCode ||
        effectiveProfile?.company_code ||
        currentUserProfile?.company_code ||
        ""
    ).trim();

  const dismissNominatedAdminWelcome = useCallback(() => {
    const code = resolvedCompanyCodeForNominee;
    if (code) {
      try {
        localStorage.setItem(nominatedAdminWelcomeStorageKey(code), "1");
      } catch {
        /* ignore */
      }
    }
    setShowNominatedAdminWelcome(false);
    setActiveTab("dashboard");
    try {
      sessionStorage.setItem("rv_active_tab", "dashboard");
    } catch {
      /* ignore */
    }
    setPendingNomineeNavTour(true);
  }, [resolvedCompanyCodeForNominee]);

  useEffect(() => {
    if (
      !user?.email ||
      isResolvingCompanyContext ||
      mandatoryTempPasswordGate ||
      companyActivationSuccess ||
      effectiveIsSystemAdmin ||
      effectiveCompany?.status !== "Active" ||
      !resolvedCompanyCodeForNominee
    ) {
      if (effectiveCompany?.status !== "Active") setShowNominatedAdminWelcome(false);
      return;
    }
    let cancelled = false;
    const me = user.email.trim().toLowerCase();
    const headers = { "X-User-Email": user.email };
    const applyWelcomeIfNominee = (isNominee: boolean) => {
      if (cancelled) return;
      if (!isNominee) {
        setShowNominatedAdminWelcome(false);
        return;
      }
      let welcomeSeen = false;
      let tourSeen = false;
      try {
        welcomeSeen =
          localStorage.getItem(nominatedAdminWelcomeStorageKey(resolvedCompanyCodeForNominee)) === "1";
        tourSeen = localStorage.getItem(nominatedAdminNavTourStorageKey(resolvedCompanyCodeForNominee)) === "1";
      } catch {
        setShowNominatedAdminWelcome(true);
        return;
      }
      if (!welcomeSeen) {
        setShowNominatedAdminWelcome(true);
      } else if (!tourSeen) {
        setShowNominatedAdminWelcome(false);
        setPendingNomineeNavTour(true);
      }
    };
    if (isNominatedCompanyAdmin) {
      applyWelcomeIfNominee(true);
      return;
    }
    fetch(`/api/companies/${encodeURIComponent(resolvedCompanyCodeForNominee)}/nominate-company-admin`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { adminEmail?: string } | null) => {
        const nomineeEmail = typeof data?.adminEmail === "string" ? data.adminEmail.trim().toLowerCase() : "";
        applyWelcomeIfNominee(!!nomineeEmail && nomineeEmail === me);
      })
      .catch(() => applyWelcomeIfNominee(false));
    return () => {
      cancelled = true;
    };
  }, [
    user?.email,
    isNominatedCompanyAdmin,
    effectiveCompany?.status,
    resolvedCompanyCodeForNominee,
    isResolvingCompanyContext,
    mandatoryTempPasswordGate,
    companyActivationSuccess,
    effectiveIsSystemAdmin,
  ]);

  const administrationCompanyConfigTabs = useMemo(
    () =>
      new Set<string>([
        "custom-job-roles",
        "grading-scale",
        "company-email-templates",
        "cycle-builder",
        "framework",
        "company-standards",
        "traction-routes",
        "org-structure",
        "role-permissions",
        "company-logo",
      ]),
    []
  );
  useEffect(() => {
    if (administrationCompanyConfigTabs.has(activeTab)) {
      setIsAdministrationCompanyConfigOpen(true);
    }
  }, [activeTab, administrationCompanyConfigTabs]);
  useEffect(() => {
    if (activeTab === "addons-storage") {
      setIsAdministrationStorageOpen(true);
    }
  }, [activeTab]);

  // After sign-in: open dashboard by default unless onboarding is still required (wizard + saved step apply).
  useEffect(() => {
    if (!user || isResolvingCompanyContext || !goToDashboardAfterSignInRef.current) return;
    goToDashboardAfterSignInRef.current = false;
    setSelectedEmployeeId(null);
    setSelectedEmployeeCompanyCode(undefined);
    if (showSetupWizard) return;
    setActiveTab('dashboard');
    try {
      sessionStorage.setItem('rv_active_tab', 'dashboard');
    } catch {
      /* ignore */
    }
  }, [user, isResolvingCompanyContext, showSetupWizard]);

  const persistStep = useCallback((step: typeof validSteps[number]) => {
    setSetupWizardStep(step);
    const code = effectiveCompany?.id || effectiveCompany?.companyCode;
    if (code && user?.email) {
      try { localStorage.setItem(`${STORAGE_KEY_PREFIX}${code}`, step); } catch (_) {}
      // Azure SQL only — do not write onboarding progress to SQL.
      fetch(`/api/companies/${encodeURIComponent(code)}/setup-wizard-step`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": user.email,
          "X-Company-Code": code,
        },
        body: JSON.stringify({ step, companyCode: code, userEmail: user.email }),
      }).catch(() => {});
    }
  }, [effectiveCompany?.id, effectiveCompany?.companyCode, user?.email]);

  useEffect(() => {
    if (showSetupWizard && effectiveCompany?.id && user?.email) {
      const code = effectiveCompany.id;
      const email = user.email;
      const headers = { "X-User-Email": email };
      // Restore local draft step immediately (same-device resilience).
      try {
        const localStep = localStorage.getItem(`${STORAGE_KEY_PREFIX}${code}`);
        if (localStep && validSteps.includes(localStep as any)) {
          setSetupWizardStep(localStep as typeof validSteps[number]);
        }
      } catch {
        // Ignore storage read errors.
      }
      Promise.all([
        fetch(`/api/users/me/needs-password-change?companyCode=${encodeURIComponent(code)}`, { headers })
          .then((res) => (res.ok ? res.json() : { needsChange: false }))
          .catch(() => ({ needsChange: false })),
        fetch(`/api/companies/${encodeURIComponent(code)}?userEmail=${encodeURIComponent(email)}`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null),
      ]).then(([pwData, companyData]: [{ needsChange?: boolean }, { setupWizardStep?: string } | null]) => {
        if (pwData?.needsChange) {
          setSetupWizardStep("change-password");
          return;
        }
        if (companyData?.setupWizardStep && validSteps.includes(companyData.setupWizardStep as any)) {
          setSetupWizardStep(companyData.setupWizardStep as typeof validSteps[number]);
        }
      });
    }
  }, [showSetupWizard, effectiveCompany?.id, user?.email]);

  const auth = true; // JWT session (no JWT auth)

  const fetchCompaniesForImpersonation = useCallback(() => {
    if (!isSystemAdmin) return;
    const gen = ++companiesImpersonationFetchGenRef.current;
    setCompaniesLoadingForImpersonation(true);
    setCompaniesImpersonationError(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const headers: Record<string, string> = { 'Cache-Control': 'no-store' };
    const email = String(user?.email || '').trim();
    if (email) headers['X-User-Email'] = email;
    fetch('/api/companies', { signal: controller.signal, cache: 'no-store', headers })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const msg = typeof (data as { error?: string }).error === 'string'
            ? (data as { error: string }).error
            : `Failed to load companies (${res.status})`;
          throw new Error(msg);
        }
        return res.json();
      })
      .then((data: { id: string; name: string }[]) => {
        if (gen !== companiesImpersonationFetchGenRef.current) return;
        setCompaniesForImpersonation(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (gen !== companiesImpersonationFetchGenRef.current) return;
        console.error('[Companies]', err);
        setCompaniesForImpersonation([]);
        const aborted =
          (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError') ||
          (err instanceof Error && (err.name === 'AbortError' || /aborted/i.test(err.message)));
        setCompaniesImpersonationError(
          aborted
            ? 'Server did not respond in time (often Azure 503). Retry in a moment, or check App Service health.'
            : err instanceof Error
              ? err.message
              : 'Could not load companies',
        );
      })
      .finally(() => {
        clearTimeout(timeout);
        if (gen !== companiesImpersonationFetchGenRef.current) return;
        setCompaniesLoadingForImpersonation(false);
      });
  }, [isSystemAdmin, user?.email]);

  /** Preload company directory for system admins so CMS-provider tenant scope resolves without opening "View as". */
  useEffect(() => {
    if (!isSystemAdmin) return;
    if (companiesLoadingForImpersonation) return;
    if (companiesForImpersonation.length > 0) return;
    if (companiesImpersonationError) return;
    fetchCompaniesForImpersonation();
  }, [
    isSystemAdmin,
    companiesLoadingForImpersonation,
    companiesForImpersonation.length,
    companiesImpersonationError,
    fetchCompaniesForImpersonation,
  ]);

  const handleImpersonate = useCallback((companyId: string) => {
    const code = String(companyId || '').trim();
    if (!code) return;
    setCompanySwitchMessage(null);
    setSelectedEmployeeId(null);
    try {
      sessionStorage.removeItem(ASSESSMENT_RESUME_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setActiveTab('dashboard');
    setImpersonationOpen(false);
    persistStep('job-roles');
    fetch(`/api/companies/${encodeURIComponent(code)}`)
      .then(res => {
        if (!res.ok) {
          if (res.status === 404) throw new Error('Company not found');
          throw new Error(`Failed to load company (${res.status})`);
        }
        return res.json();
      })
      .then((data: { id?: string; name?: string; status?: string; logoUrl?: string; address?: string; postalAddress?: string }) => {
        const id = String(data.id ?? code);
        setImpersonatedCompany({
          id,
          name: data.name ?? '',
          companyCode: id,
          status: (data.status as any) ?? "Active",
          address: data.address,
          postalAddress: data.postalAddress,
          logoUrl: data.logoUrl,
          setupCompletedAt: null,
          setupCompletedBy: null,
          createdAt: null as any,
          createdBy: user?.uid ?? '',
          updatedAt: null as any,
          updatedBy: user?.uid ?? '',
        });
        setCompanySwitchMessage(`Now viewing: ${data.name?.trim() || `Company ${id}`}`);
      })
      .catch(err => {
        console.error('[Impersonate]', err);
        alert(err.message || translateUi('Could not switch to that company.'));
      });
  }, [user?.uid]);

  const handleLoginAsUser = useCallback((targetUser: SystemUser, preferredEmployeeId?: string) => {
    const loginAsTitle = String(targetUser?.title ?? "").trim().toLowerCase();
    const looksLikeDirectoryRole =
      loginAsTitle.includes("manager") ||
      loginAsTitle.includes("supervisor") ||
      loginAsTitle.includes("team lead") ||
      loginAsTitle.endsWith(" lead") ||
      loginAsTitle.includes(" line lead") ||
      loginAsTitle.includes("admin");
    const shouldOpenEmployeesImmediately = loginAsTitle.includes("driver") && !looksLikeDirectoryRole;
    try {
      sessionStorage.removeItem(ASSESSMENT_RESUME_STORAGE_KEY);
      sessionStorage.removeItem('rv_last_employee');
    } catch {
      // Ignore storage errors.
    }
    setActiveAnnouncements([]);
    const code = (targetUser.company_code || '').trim();
    const linkedEmployeeId = String(preferredEmployeeId ?? targetUser?.employee_id ?? '').trim();
    if (linkedEmployeeId) {
      setSelectedEmployeeId(linkedEmployeeId);
      setSelectedEmployeeCompanyCode(code || undefined);
    } else {
      setSelectedEmployeeId(null);
      setSelectedEmployeeCompanyCode(undefined);
    }
    setActiveTab(shouldOpenEmployeesImmediately ? 'employees' : 'dashboard');
    setImpersonatedUser(targetUser);
    if (code) {
      fetch(`/api/companies/${encodeURIComponent(code)}`)
        .then(res => res.ok ? res.json() : null)
        .then((data: { id?: string; name?: string; status?: string; logoUrl?: string; address?: string; postalAddress?: string } | null) => {
          if (data) {
            setImpersonatedCompany({
              id: String(data.id ?? code),
              name: data.name ?? '',
              companyCode: code,
              status: (data.status as any) ?? "Active",
              address: data.address,
              postalAddress: data.postalAddress,
              logoUrl: data.logoUrl,
              setupCompletedAt: null,
              setupCompletedBy: null,
              createdAt: null as any,
              createdBy: user?.uid ?? '',
              updatedAt: null as any,
              updatedBy: user?.uid ?? '',
            });
          } else {
            setImpersonatedCompany(null);
          }
        })
        .catch(() => setImpersonatedCompany(null));
    } else {
      setImpersonatedCompany(null);
    }
  }, [user?.uid]);

  const handleLoginAsEmployee = useCallback(async (emp: Employee) => {
    const code = String(emp.company_code ?? '').trim();
    if (!code) {
      alert(translateUi("This employee has no company assigned."));
      return;
    }
    const headers: Record<string, string> = {};
    if (user?.email) headers['X-User-Email'] = user.email;
    try {
      const res = await fetch(`/api/users?companyCode=${encodeURIComponent(code)}`, { headers, cache: 'no-store' });
      if (!res.ok) throw new Error('Could not load users for this company.');
      const users = await res.json();
      if (!Array.isArray(users)) throw new Error('Could not load users for this company.');
      const empId = String(emp.id ?? '').trim();
      const empEmail = String(emp.email ?? '').trim().toLowerCase();
      const match = users.find((u: SystemUser) => {
        const linkedId = String(u.employee_id ?? '').trim();
        if (linkedId && empId && linkedId === empId) return true;
        const uEmail = String(u.email ?? '').trim().toLowerCase();
        return !!(empEmail && uEmail && uEmail === empEmail);
      });
      if (!match) {
        alert(translateUi("No login account found for this employee. Add them in Team Management first."));
        return;
      }
      if (match.email?.toLowerCase() === user?.email?.toLowerCase()) return;
      handleLoginAsUser(match, String(emp.id ?? '').trim() || undefined);
    } catch (err) {
      alert(err instanceof Error ? err.message : translateUi('Could not log in as this user.'));
    }
  }, [user?.email, handleLoginAsUser]);

  const handleExitUserImpersonation = useCallback(() => {
    setActiveAnnouncements([]);
    setActiveAnnouncementsContextKey("");
    setImpersonatedUser(null);
    setImpersonatedCompany(null);
    setCompanySwitchMessage(null);
  }, []);

  const handleEnableLoginFromHeader = useCallback(async (companyCode: string) => {
    if (!user?.email) return;
    setEnableLoginLoading(companyCode);
    setEnableLoginMessage(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/create-azure-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
        body: JSON.stringify({ userEmail: user.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const msg = data.message || 'Login enabled. Contact can now sign in.';
        setEnableLoginMessage({ type: 'success', text: msg });
        console.log('[Enable login]', msg);
      } else {
        const msg = data.error || 'Failed to enable login';
        setEnableLoginMessage({ type: 'error', text: msg });
        console.warn('[Enable login]', msg);
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to enable login';
      setEnableLoginMessage({ type: 'error', text: msg });
      console.warn('[Enable login]', msg);
    } finally {
      setEnableLoginLoading(null);
    }
  }, [user?.email]);

  useEffect(() => {
    if (!enableLoginMessage) return;
    const t = setTimeout(() => setEnableLoginMessage(null), 5000);
    return () => clearTimeout(t);
  }, [enableLoginMessage]);

    useEffect(() => {
    const email = getSessionEmail();
    const token = getSessionToken();
    if (email && token) {
      setUser({ email, uid: email });
      setIsResolvingCompanyContext(true);
    } else {
      setUser(null);
      setIsResolvingCompanyContext(false);
    }
    setLoading(false);
    return () => {
      if (loginOverlayEndTimerRef.current) {
        clearTimeout(loginOverlayEndTimerRef.current);
        loginOverlayEndTimerRef.current = null;
      }
    };
  }, [scheduleEndLoginOverlay]);

  // Failsafe: never keep the app on the initial full-screen spinner forever.
  useEffect(() => {
    if (!loading) return;
    const id = window.setTimeout(() => {
      setLoading(false);
    }, 15000);
    return () => window.clearTimeout(id);
  }, [loading]);

  // Failsafe: if company-context resolution stalls, release the global spinner.
  useEffect(() => {
    if (!user || !isResolvingCompanyContext || isLoggingIn) return;
    const id = window.setTimeout(() => {
      setIsResolvingCompanyContext(false);
    }, 15000);
    return () => window.clearTimeout(id);
  }, [user, isResolvingCompanyContext, isLoggingIn]);

  // Inactivity auto-logout (5 min total: 4 min warning, then 1 min countdown) and presence heartbeat
  const resetInactivityRef = React.useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!user?.email) return;
    const WARNING_AFTER_MS = 4 * 60 * 1000;
    const HEARTBEAT_MS = 30 * 1000;
    let inactivityTimer: ReturnType<typeof setTimeout>;
    let heartbeatInterval: ReturnType<typeof setInterval>;

    const showWarning = () => {
      setInactivityWarningOpen(true);
      setInactivityCountdown(60);
    };

    const resetInactivity = () => {
      clearTimeout(inactivityTimer);
      setInactivityWarningOpen(false);
      inactivityTimer = setTimeout(showWarning, WARNING_AFTER_MS);
    };
    resetInactivityRef.current = resetInactivity;

    const sendHeartbeat = () => {
      const apiCompanyCode = String(impersonatedCompany?.id || currentCompany?.id || effectiveProfile?.company_code || currentUserProfile?.company_code || '').trim();
      fetch('/api/presence/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
        body: JSON.stringify({ companyCode: apiCompanyCode || undefined }),
      }).catch(() => {});
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(ev => document.addEventListener(ev, resetInactivity));
    resetInactivity();
    sendHeartbeat();
    heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_MS);

    return () => {
      events.forEach(ev => document.removeEventListener(ev, resetInactivity));
      clearTimeout(inactivityTimer);
      clearInterval(heartbeatInterval);
      resetInactivityRef.current = null;
    };
  }, [user?.email, auth, impersonatedCompany?.id, currentCompany?.id, currentUserProfile?.company_code, impersonatedUser?.company_code]);

  useEffect(() => {
    if (!inactivityWarningOpen || !auth) return;
    let count = 60;
    const interval = setInterval(() => {
      count--;
      setInactivityCountdown(count);
      if (count <= 0) {
        clearInterval(interval);
        try {
          sessionStorage.setItem('rv_inactivity_logout', '1');
        } catch {}
        try {
          sessionStorage.removeItem('rv_login_company_code');
        } catch {}
        clearSessionAuth(); setUser(null);
        setInactivityWarningOpen(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [inactivityWarningOpen, auth]);

  // Mobile/tablet security: sign out after the app stays backgrounded, so an
  // unattended unlocked session cannot linger. Do NOT sign out on pagehide —
  // refresh/navigation fires pagehide and must keep the user on the same page.
  // Tab/browser close still drops auth via session-scoped persistence below.
  useEffect(() => {
    if (!user || !auth || !isMobileOrTablet) return;

    let logoutTimer: ReturnType<typeof setTimeout> | null = null;
    let signingOut = false;
    // Long enough that a normal refresh cannot race the timer before unload.
    const BACKGROUND_GRACE_MS = 30_000;

    const clearLogoutTimer = () => {
      if (logoutTimer != null) {
        clearTimeout(logoutTimer);
        logoutTimer = null;
      }
    };

    const performMobileSessionLogout = () => {
      if (signingOut) return;
      signingOut = true;
      clearLogoutTimer();
      try {
        sessionStorage.setItem('rv_mobile_session_logout', '1');
      } catch {
        /* ignore */
      }
      try {
        sessionStorage.removeItem('rv_login_company_code');
      } catch {
        /* ignore */
      }
      setInactivityWarningOpen(false);
      clearSessionAuth(); setUser(null); void (() => {
        signingOut = false;
      });
    };

    const scheduleLogout = (delayMs: number) => {
      clearLogoutTimer();
      logoutTimer = setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          performMobileSessionLogout();
        }
      }, delayMs);
    };

    // JWT session lives in sessionStorage (see apiAuth) — survives refresh in this tab.

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        scheduleLogout(BACKGROUND_GRACE_MS);
      } else {
        clearLogoutTimer();
      }
    };

    const onFreeze = () => {
      scheduleLogout(BACKGROUND_GRACE_MS);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    // Safari / Chrome lifecycle when the page is frozen in the background.
    document.addEventListener('freeze', onFreeze as EventListener);

    return () => {
      clearLogoutTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('freeze', onFreeze as EventListener);
    };
  }, [user, auth, isMobileOrTablet]);

  const apiCompanyCode = String(impersonatedCompany?.id || currentCompany?.id || effectiveProfile?.company_code || currentUserProfile?.company_code || '').trim();

  /** Verify Stripe checkout return even while SetupWizard is showing (AddonsPage may not mount). */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const success = params.get("success");
    const urlCompanyCode = params.get("companyCode");
    const addonId = params.get("addonId") || "";
    if (success !== "1" || !sessionId || !urlCompanyCode) return;
    if (apiCompanyCode && apiCompanyCode !== urlCompanyCode) return;
    let cancelled = false;
    fetch("/api/addons/verify-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, companyCode: urlCompanyCode, addonId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.success) setAddonsRefreshTrigger((t) => t + 1);
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        const tab = params.get("tab");
        window.history.replaceState({}, "", window.location.pathname + (tab ? `?tab=${tab}` : ""));
      });
    return () => {
      cancelled = true;
    };
  }, [apiCompanyCode]);

  /** Company scope for employee directory CRUD (includes CMS provider tenant when a real system admin has no company in context). */
  const employeesApiCompanyCode = useMemo(() => {
    if (effectiveIsSystemAdmin && !impersonatedCompany && !impersonatedUser) {
      const prov = String(cmsProviderCompanyCode ?? "").trim();
      if (prov) return prov;
      try {
        const sessionScope = String(sessionStorage.getItem("rv_login_company_code") ?? "").trim();
        if (sessionScope) return sessionScope;
      } catch {
        /* ignore storage */
      }
      const actorEmail = String(effectiveProfile?.email || user?.email || "").trim().toLowerCase();
      if (actorEmail) {
        const rememberedScope = String(readRememberedCompanyScope(actorEmail) ?? "").trim();
        if (rememberedScope) return rememberedScope;
      }
    }
    const imp = String(impersonatedCompany?.id ?? "").trim();
    if (imp) return imp;
    const cur = String(currentCompany?.id ?? "").trim();
    if (cur) return cur;
    const fromProfile = String(apiCompanyCode ?? "").trim();
    if (effectiveIsSystemAdmin && !impersonatedCompany && !impersonatedUser) {
      const prov = String(cmsProviderCompanyCode ?? "").trim();
      if (prov) return prov;
      return fromProfile;
    }
    return fromProfile;
  }, [
    impersonatedCompany?.id,
    currentCompany?.id,
    apiCompanyCode,
    effectiveIsSystemAdmin,
    impersonatedCompany,
    impersonatedUser,
    cmsProviderCompanyCode,
    effectiveProfile?.email,
    user?.email,
    readRememberedCompanyScope,
  ]);

  /** System or company admin may wipe open assessment activity back to not started. */
  const canAdminResetOpenAssessment = canOverrideStuckAssessment;

  const teamManagementHomeCompanyDisplayName = useMemo(() => {
    if (!effectiveIsSystemAdmin) return undefined;
    const code = String(employeesApiCompanyCode || "").trim();
    if (!code) return undefined;
    const curId = String(currentCompany?.id || currentCompany?.companyCode || "").trim();
    if (curId && curId === code) return currentCompany?.name?.trim() || undefined;
    const fromList = companiesForImpersonation.find((c) => String(c.id || "").trim() === code);
    return fromList?.name?.trim() || undefined;
  }, [
    effectiveIsSystemAdmin,
    employeesApiCompanyCode,
    currentCompany?.id,
    currentCompany?.companyCode,
    currentCompany?.name,
    companiesForImpersonation,
  ]);

  const companySetupAddonsPromptStorageKey = useCallback((companyCode: string, userEmail: string) => {
    const code = String(companyCode || "").trim();
    const email = String(userEmail || "").trim().toLowerCase();
    if (!code || !email) return null;
    return `rv_company_setup_addons_prompt_${code}_${email}`;
  }, []);

  const companySetupRequiredComplete = useMemo(() => {
    const iss = companySetupReminder.issues;
    if (!iss) return false;
    return (
      !iss.missingCycles &&
      !iss.missingCompanyStandards &&
      !iss.missingFramework &&
      !iss.missingGradingScale &&
      !iss.missingTractionAndDepots
    );
  }, [companySetupReminder.issues]);

  const dismissCompanySetupAddonsPrompt = useCallback(() => {
    const key = companySetupAddonsPromptStorageKey(apiCompanyCode, user?.email || "");
    if (key) {
      try {
        localStorage.setItem(key, "1");
      } catch {
        // Ignore storage errors.
      }
    }
    setCompanySetupAddonsPromptSkipped(true);
    setCompanySetupReminder({ show: false, issues: null });
  }, [apiCompanyCode, user?.email, companySetupAddonsPromptStorageKey]);

  useEffect(() => {
    const key = companySetupAddonsPromptStorageKey(apiCompanyCode, user?.email || "");
    if (!key) {
      setCompanySetupAddonsPromptSkipped(false);
      return;
    }
    try {
      setCompanySetupAddonsPromptSkipped(localStorage.getItem(key) === "1");
    } catch {
      setCompanySetupAddonsPromptSkipped(false);
    }
  }, [apiCompanyCode, user?.email, companySetupAddonsPromptStorageKey]);

  const companyInitialSetupCompleteStorageKey = useCallback((companyCode: string) => {
    const code = String(companyCode || "").trim();
    if (!code) return null;
    return `rv_company_initial_setup_complete_${code}`;
  }, []);

  const applyCompanySetupValidationResult = useCallback(
    (data: {
      isValid?: boolean;
      issues?: {
        missingCycles?: boolean;
        missingCompanyStandards?: boolean;
        missingFramework?: boolean;
        missingGradingScale?: boolean;
        missingTractionAndDepots?: boolean;
        missingRoleMappings?: boolean;
      };
    } | null) => {
      if (!data || typeof data !== "object") return;
      const issues = data.issues ?? {};
      const onlyMissingCycles =
        !!issues.missingCycles &&
        !issues.missingCompanyStandards &&
        !issues.missingFramework &&
        !issues.missingGradingScale &&
        !issues.missingTractionAndDepots;
      const companySetupKey = companyInitialSetupCompleteStorageKey(apiCompanyCode);
      let companySetupPreviouslyCompleted = false;
      if (companySetupKey) {
        try {
          companySetupPreviouslyCompleted = localStorage.getItem(companySetupKey) === "1";
        } catch {
          // Ignore storage errors.
        }
      }

      if (data.isValid === false) {
        // Initial setup is done — only cycles were cleared later. Do not re-show onboarding steps.
        if (onlyMissingCycles || companySetupPreviouslyCompleted) {
          if (onlyMissingCycles && companySetupKey) {
            try {
              localStorage.setItem(companySetupKey, "1");
            } catch {
              // Ignore storage errors.
            }
          }
          setCompanySetupReminder({ show: false, issues: null });
          return;
        }
        const addonsPromptKey = companySetupAddonsPromptStorageKey(apiCompanyCode, user?.email || "");
        let setupPreviouslyCompleted = companySetupAddonsPromptSkipped;
        if (!setupPreviouslyCompleted && addonsPromptKey) {
          try {
            setupPreviouslyCompleted = localStorage.getItem(addonsPromptKey) === "1";
          } catch {
            // Ignore storage errors.
          }
        }
        if (setupPreviouslyCompleted) {
          setCompanySetupReminder({ show: false, issues: null });
          return;
        }
        setCompanySetupReminder({ show: true, issues: data.issues ?? null });
        return;
      }
      // Core setup complete — do not keep showing the finish-setup banner on every login.
      // Optional add-ons remain available from the left navigation (Add-ons).
      if (data.isValid === true) {
        if (companySetupKey) {
          try {
            localStorage.setItem(companySetupKey, "1");
          } catch {
            // Ignore storage errors.
          }
        }
        const addonsPromptKey = companySetupAddonsPromptStorageKey(apiCompanyCode, user?.email || "");
        if (addonsPromptKey) {
          try {
            localStorage.setItem(addonsPromptKey, "1");
          } catch {
            // Ignore storage errors.
          }
        }
        setCompanySetupAddonsPromptSkipped(true);
      }
      setCompanySetupReminder({ show: false, issues: null });
    },
    [
      apiCompanyCode,
      user?.email,
      companySetupAddonsPromptStorageKey,
      companyInitialSetupCompleteStorageKey,
      companySetupAddonsPromptSkipped,
    ]
  );

  useEffect(() => {
    const actorEmail = String(effectiveProfile?.email || user?.email || '').trim().toLowerCase();
    setEffectiveCompanyPermissionsLoaded(false);
    if (!apiCompanyCode || !actorEmail) {
      setEffectiveCompanyPermissions([]);
      setEffectiveCompanyPermissionsLoaded(true);
      return;
    }
    let cancelled = false;
    fetch(`/api/companies/${encodeURIComponent(apiCompanyCode)}/role-permissions/me`, {
      headers: { 'X-User-Email': actorEmail },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { permissions?: string[] } | null) => {
        if (cancelled) return;
        setEffectiveCompanyPermissions(Array.isArray(data?.permissions) ? data!.permissions : []);
        setEffectiveCompanyPermissionsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setEffectiveCompanyPermissions([]);
          setEffectiveCompanyPermissionsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiCompanyCode, effectiveProfile?.email, user?.email]);

  useEffect(() => {
    const actorEmail = String(effectiveProfile?.email || user?.email || "").trim().toLowerCase();
    if (!apiCompanyCode || !actorEmail || !companyAddons.includes("trainee-driver")) {
      setHasOwnTraineeDriverEnrollment(false);
      setOwnTraineeDriverEnrollmentLoaded(true);
      return;
    }
    let cancelled = false;
    setOwnTraineeDriverEnrollmentLoaded(false);
    fetch(
      `/api/trainee-driver/enrollments?companyCode=${encodeURIComponent(apiCompanyCode)}`,
      { headers: { "X-User-Email": actorEmail, "X-Company-Code": apiCompanyCode } },
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { enrollments?: unknown[] } | null) => {
        if (cancelled) return;
        setHasOwnTraineeDriverEnrollment(Array.isArray(data?.enrollments) && data!.enrollments!.length > 0);
        setOwnTraineeDriverEnrollmentLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHasOwnTraineeDriverEnrollment(false);
          setOwnTraineeDriverEnrollmentLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiCompanyCode, effectiveProfile?.email, user?.email, companyAddons]);

  useEffect(() => {
    if (
      !user?.email ||
      effectiveIsSystemAdmin ||
      showSetupWizard ||
      companyActivationSuccess ||
      isResolvingCompanyContext
    ) {
      setMandatoryTempPasswordGate(false);
      return;
    }
    const cc = (apiCompanyCode || loginForm.companyCode || "").trim();
    if (!cc) {
      setMandatoryTempPasswordGate(false);
      return;
    }
    const ac = new AbortController();
    fetch(`/api/users/me/needs-password-change?companyCode=${encodeURIComponent(cc)}`, {
      headers: { "X-User-Email": user.email },
      signal: ac.signal,
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data: { needsChange?: boolean }) => setMandatoryTempPasswordGate(!!data.needsChange))
      .catch(() => setMandatoryTempPasswordGate(false));
    return () => ac.abort();
  }, [
    user?.email,
    effectiveIsSystemAdmin,
    showSetupWizard,
    companyActivationSuccess,
    isResolvingCompanyContext,
    apiCompanyCode,
    loginForm.companyCode,
  ]);

  useEffect(() => {
    if (!user?.email || !apiCompanyCode || effectiveIsSystemAdmin || mandatoryTempPasswordGate) {
      setCompanySetupReminder({ show: false, issues: null });
      return;
    }
    if (effectiveCompany?.status !== "Active" || !effectiveIsAdmin) {
      setCompanySetupReminder({ show: false, issues: null });
      return;
    }
    let disposed = false;
    let lastAc: AbortController | null = null;
    const fetchSetupValidation = () => {
      lastAc?.abort();
      const ac = new AbortController();
      lastAc = ac;
      fetch(`/api/companies/${encodeURIComponent(apiCompanyCode)}/validate-setup`, { signal: ac.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then(
          (data: {
            isValid?: boolean;
            issues?: {
              missingCycles?: boolean;
              missingCompanyStandards?: boolean;
              missingFramework?: boolean;
              missingGradingScale?: boolean;
              missingTractionAndDepots?: boolean;
              missingRoleMappings?: boolean;
            };
          } | null) => {
            if (disposed || ac.signal.aborted) return;
            applyCompanySetupValidationResult(data);
          }
        )
        .catch((err: unknown) => {
          const name = err && typeof err === "object" && "name" in err ? String((err as { name?: string }).name) : "";
          if (disposed || ac.signal.aborted || name === "AbortError") return;
          setCompanySetupReminder({ show: false, issues: null });
        });
    };

    fetchSetupValidation();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchSetupValidation();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      disposed = true;
      lastAc?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    user?.email,
    apiCompanyCode,
    effectiveCompany?.status,
    effectiveIsAdmin,
    effectiveIsSystemAdmin,
    mandatoryTempPasswordGate,
    activeTab,
    companySetupAddonsPromptSkipped,
    applyCompanySetupValidationResult,
  ]);

  const hasCompanyPermission = useCallback(
    (permissionId: string) => {
      if (impersonatedUser) return effectiveCompanyPermissions.includes(permissionId);
      return effectiveIsAdmin || effectiveIsSystemAdmin || effectiveCompanyPermissions.includes(permissionId);
    },
    [impersonatedUser, effectiveIsAdmin, effectiveIsSystemAdmin, effectiveCompanyPermissions]
  );
  const canAccessIncidents = hasCompanyPermission('incidents.manage');
  const canAccessTasks = companyAddons.includes('task-assignment') && hasCompanyPermission('tasks.manage');
  const canAccessSafetyBriefs = companyAddons.includes('safety-briefs') && hasCompanyPermission('safety-briefs.manage');
  const canAccessDriverReports = companyAddons.includes('driver-reports') && hasCompanyPermission('driver-reports.manage');
  const leaveAbsenceModuleActive = companyAddons.includes('leave-absence');
  const medicationChecksModuleActive = companyAddons.includes('medication-checks');
  // Any company member can apply for leave when the add-on is active.
  const canRequestLeave = leaveAbsenceModuleActive;
  const canApproveLeave = leaveAbsenceModuleActive && hasCompanyPermission('leave.approve');
  const canManageLeave = leaveAbsenceModuleActive && hasCompanyPermission('leave.manage');
  const canAccessLeaveAbsence = leaveAbsenceModuleActive;
  const canRunQAVerifications = companyAddons.includes('qa-verifications') && hasCompanyPermission('qa-verifications.run');
  const canViewQAVerificationsHistory = companyAddons.includes('qa-verifications') && hasCompanyPermission('qa-verifications.view-runs');
  const traineeDriverModuleActive = companyAddons.includes('trainee-driver');
  const canManageTraineeDriver = traineeDriverModuleActive && hasCompanyPermission('trainee-driver.manage');
  const canUnenrolTraineeDriver = traineeDriverModuleActive && hasCompanyPermission('trainee-driver.unenrol');
  const canManageTraineeDriverPolicies = traineeDriverModuleActive && hasCompanyPermission('trainee-driver.policies.manage');
  const canUploadTraineeDriverPolicyDocuments = traineeDriverModuleActive && hasCompanyPermission('trainee-driver.policies.upload');
  const canManageTraineeDriverEquipment = traineeDriverModuleActive && hasCompanyPermission('trainee-driver.equipment.manage');
  const cabPassesManagePermission = hasCompanyPermission("cab-passes.manage");
  const cabPassPermissionEdit = hasCompanyPermission("cab-passes.edit");
  const cabPassPermissionDelete = hasCompanyPermission("cab-passes.delete");
  const cabPassPermissionRevoke = hasCompanyPermission("cab-passes.revoke");
  const cabPassPermissionRenew = hasCompanyPermission("cab-passes.renew");
  const canAccessCabPassesSection =
    effectiveIsAdmin ||
    effectiveIsSystemAdmin ||
    cabPassesManagePermission ||
    cabPassPermissionEdit ||
    cabPassPermissionDelete ||
    cabPassPermissionRevoke ||
    cabPassPermissionRenew;
  const canAccessMedicals = hasCompanyPermission('medicals.manage');
  /** Admins implicitly, plus any role the company admin has granted employees.lock. */
  const canLockEmployeeProfiles = hasCompanyPermission('employees.lock');
  /** Align with server canViewEmployeeDirectory: employees.view/edit or title hints. Assessment flows use other routes. */
  const jobTitleForAccess = (effectiveProfile?.title || "").trim().toLowerCase();
  const notSystemTitle =
    !jobTitleForAccess ||
    !(jobTitleForAccess.includes("system") && (jobTitleForAccess.includes("administrator") || jobTitleForAccess.includes("admin")));
  const jobTitleImpliesEmployeeDirectory =
    notSystemTitle &&
    (jobTitleForAccess.includes("manager") ||
      jobTitleForAccess.includes("supervisor") ||
      jobTitleForAccess.includes("team lead") ||
      jobTitleForAccess.endsWith(" lead") ||
      jobTitleForAccess.includes(" line lead"));
  const canAccessLicencing =
    hasCompanyPermission("licencing.view") || jobTitleImpliesEmployeeDirectory;
  const jobTitleImpliesOwnRecordOnly =
    notSystemTitle &&
    !jobTitleImpliesEmployeeDirectory &&
    jobTitleForAccess.includes("driver");
  const canViewAllEmployees =
    hasCompanyPermission("employees.view") ||
    hasCompanyPermission("employees.edit") ||
    jobTitleImpliesEmployeeDirectory;
  const canEditAllEmployees = hasCompanyPermission('employees.edit');
  const canEditOwnEmployeeOnlyPermission =
    !effectiveIsAdmin &&
    !effectiveIsSystemAdmin &&
    !canEditAllEmployees &&
    effectiveCompanyPermissions.includes('employees.edit-self');
  /** Align with server resolveTraineeDriverAccess.canSelf: role permissions, own-record access, or active enrolment. */
  const canSelfServeTraineeDriver =
    traineeDriverModuleActive &&
    (
      hasCompanyPermission('trainee-driver.self') ||
      hasCompanyPermission('employees.self') ||
      canEditOwnEmployeeOnlyPermission ||
      jobTitleImpliesOwnRecordOnly ||
      hasOwnTraineeDriverEnrollment
    );
  const canAccessTraineeDriver =
    canManageTraineeDriver ||
    canSelfServeTraineeDriver ||
    canManageTraineeDriverPolicies ||
    canUploadTraineeDriverPolicyDocuments ||
    canManageTraineeDriverEquipment;
  const canViewOwnEmployeeOnly =
    !effectiveIsAdmin &&
    !effectiveIsSystemAdmin &&
    !canViewAllEmployees &&
    (effectiveCompanyPermissions.includes('employees.self') || canEditOwnEmployeeOnlyPermission || jobTitleImpliesOwnRecordOnly);
  const canManageCustomJobRoles = hasCompanyPermission('custom-job-roles.manage');
  const canManageGradingScale = hasCompanyPermission('grading-scale.manage');
  const canManageFramework = hasCompanyPermission('framework.manage');
  const canManageCompanyStandards = hasCompanyPermission('company-standards.manage');
  const canManageTractionRoutes = hasCompanyPermission('traction-routes.manage');
  const canSeeCompanyConfigurationSection =
    !!effectiveCompany &&
    !effectiveIsSystemAdmin &&
    (effectiveIsAdmin ||
      canManageCustomJobRoles ||
      canManageGradingScale ||
      canManageFramework ||
      canManageCompanyStandards ||
      canManageTractionRoutes);

  /** Paste token from Investigations module only; system admins without impersonation see all peers. */
  const canAccessInvestigationsApiManagement =
    effectiveIsSystemAdmin ||
    (!!apiCompanyCode && !impersonatedUser && effectiveIsAdmin);

  const openAdminPanelsForNomineeTour = useCallback(() => {
    setIsSidebarOpen(true);
    setIsAdministrationOpen(true);
    setIsAdministrationCompanyConfigOpen(true);
  }, []);

  const nomineeNavTourSteps = useMemo((): NomineeNavTourStep[] => {
    if (effectiveIsSystemAdmin || canViewOwnEmployeeOnly) return [];
    const steps: NomineeNavTourStep[] = [
      {
        id: "dashboard",
        title: translateUi("Dashboard"),
        body: "Your home view — competency summaries, alerts, and shortcuts for day-to-day oversight of the company.",
        onEnter: () => {
          setSelectedEmployeeId(null);
          setActiveTab("dashboard");
        },
      },
      {
        id: "employees",
        title: translateUi("Employees"),
        body: "Search and open employee records, run assessments, and manage training, medicals, and documents.",
        onEnter: () => {
          setSelectedEmployeeId(null);
          setActiveTab("employees");
        },
      },
    ];
    if (canAccessIncidents) {
      steps.push({
        id: "incidents",
        title: translateUi("Incidents"),
        body: "Record and track safety incidents, investigations, and follow-up actions.",
        onEnter: () => {
          setSelectedEmployeeId(null);
          setActiveTab("incidents");
        },
      });
    }
    steps.push({
      id: "reporting",
      title: translateUi("Reporting"),
      body: "Analyse competency and compliance data with filters and exportable reports.",
      onEnter: () => {
        setSelectedEmployeeId(null);
        setActiveTab("reporting");
      },
    });
    steps.push({
      id: "administration",
      title: translateUi("Administration"),
      body: "Opens company setup — frameworks, grading, team access, and more. Expand this menu whenever you need to configure Rail Intel.",
      onEnter: openAdminPanelsForNomineeTour,
    });
    steps.push({
      id: "admin-team",
      title: "Team management",
      body: "Invite users and control who can access your company. Nominated administrators cannot delete other company administrators.",
      onEnter: openAdminPanelsForNomineeTour,
    });
    if (canManageCustomJobRoles) {
      steps.push({
        id: "custom-job-roles",
        title: "Custom job roles",
        body: "Define job roles used across employee records and competency requirements.",
        onEnter: openAdminPanelsForNomineeTour,
      });
    }
    if (canManageCompanyStandards) {
      steps.push({
        id: "company-standards",
        title: "Company standards & timings",
        body: "Open Company Standards to set Standard Timings — trainee daylight/dark driving minimums, maintain-driver competence, and assessing session time. The Standard Timings section is expanded during company setup.",
        onEnter: () => {
          openAdminPanelsForNomineeTour();
          setSelectedEmployeeId(null);
          setActiveTab("company-standards");
        },
      });
    }
    if (canManageFramework) {
      steps.push({
        id: "framework",
        title: translateUi("Cycle Builder"),
        body: "Build competency cycles, apply Rail Vault templates, and suspend any cycle you do not want managers to assign.",
        onEnter: openAdminPanelsForNomineeTour,
      });
    }
    if (canManageGradingScale) {
      steps.push({
        id: "grading-scale",
        title: translateUi("Grading scale"),
        body: "Set the grades assessors use when marking competency (e.g. competent, review, not yet competent).",
        onEnter: openAdminPanelsForNomineeTour,
      });
    }
    return steps;
  }, [
    effectiveIsSystemAdmin,
    canViewOwnEmployeeOnly,
    canAccessIncidents,
    canManageCustomJobRoles,
    canManageCompanyStandards,
    canManageFramework,
    canManageGradingScale,
    openAdminPanelsForNomineeTour,
  ]);

  const completeNomineeNavTour = useCallback(() => {
    const code = resolvedCompanyCodeForNominee;
    if (code) {
      try {
        localStorage.setItem(nominatedAdminNavTourStorageKey(code), "1");
      } catch {
        /* ignore */
      }
    }
    setNomineeNavTourActive(false);
    setNomineeNavTourStepIndex(0);
    setSelectedEmployeeId(null);
    setIsAdministrationOpen(false);
    setIsAdministrationCompanyConfigOpen(false);
    setActiveTab("dashboard");
    try {
      sessionStorage.setItem("rv_active_tab", "dashboard");
    } catch {
      /* ignore */
    }
  }, [resolvedCompanyCodeForNominee]);

  const resetNominatedAdminTutorial = useCallback(() => {
    const code = resolvedCompanyCodeForNominee;
    if (code) {
      try {
        localStorage.removeItem(nominatedAdminWelcomeStorageKey(code));
        localStorage.removeItem(nominatedAdminNavTourStorageKey(code));
      } catch {
        /* ignore */
      }
    }
    setNomineeNavTourActive(false);
    setNomineeNavTourStepIndex(0);
    setPendingNomineeNavTour(false);
    setShowNominatedAdminWelcome(true);
    setActiveTab("dashboard");
    setSelectedEmployeeId(null);
    setIsSidebarOpen(true);
    try {
      sessionStorage.setItem("rv_active_tab", "dashboard");
    } catch {
      /* ignore */
    }
  }, [resolvedCompanyCodeForNominee]);

  const canResetNomineeTutorial = useMemo(() => {
    if (mandatoryTempPasswordGate || effectiveIsSystemAdmin) return false;
    if (effectiveCompany?.status !== "Active") return false;
    const code = resolvedCompanyCodeForNominee;
    if (!code) return false;
    if (isNominatedCompanyAdmin) return true;
    try {
      return (
        localStorage.getItem(nominatedAdminWelcomeStorageKey(code)) === "1" ||
        localStorage.getItem(nominatedAdminNavTourStorageKey(code)) === "1"
      );
    } catch {
      return false;
    }
  }, [
    mandatoryTempPasswordGate,
    effectiveIsSystemAdmin,
    effectiveCompany?.status,
    resolvedCompanyCodeForNominee,
    isNominatedCompanyAdmin,
  ]);

  useEffect(() => {
    if (!pendingNomineeNavTour || nomineeNavTourSteps.length === 0) return;
    setPendingNomineeNavTour(false);
    setNomineeNavTourStepIndex(0);
    setNomineeNavTourActive(true);
    setIsSidebarOpen(true);
    nomineeNavTourSteps[0]?.onEnter?.();
  }, [pendingNomineeNavTour, nomineeNavTourSteps]);

  const refreshCompanySetupValidation = useCallback(
    (onDone?: () => void) => {
      if (!apiCompanyCode || !user?.email) {
        onDone?.();
        return;
      }
      fetch(`/api/companies/${encodeURIComponent(apiCompanyCode)}/validate-setup`)
        .then((r) => (r.ok ? r.json() : null))
        .then(
          (data: {
            isValid?: boolean;
            issues?: {
              missingCycles?: boolean;
              missingCompanyStandards?: boolean;
              missingFramework?: boolean;
              missingGradingScale?: boolean;
              missingTractionAndDepots?: boolean;
              missingRoleMappings?: boolean;
            };
          } | null) => {
            applyCompanySetupValidationResult(data);
          }
        )
        .catch(() => {})
        .finally(() => onDone?.());
    },
    [apiCompanyCode, user?.email, applyCompanySetupValidationResult]
  );

  /** After saving a company cycle from Cycle Builder, move to the next finish-setup step when the reminder banner is visible. */
  const advanceCompanySetupAfterCycleSaved = useCallback(() => {
    if (!companySetupReminder.show) return;
    const goNext = () => {
      if (canManageCompanyStandards) setActiveTab("company-standards");
      else if (canManageGradingScale) setActiveTab("grading-scale");
      else if (canManageTractionRoutes) setActiveTab("traction-routes");
      else if (effectiveIsAdmin) setActiveTab("addons");
    };
    refreshCompanySetupValidation(goNext);
  }, [
    companySetupReminder.show,
    canManageCompanyStandards,
    canManageGradingScale,
    canManageTractionRoutes,
    effectiveIsAdmin,
    refreshCompanySetupValidation,
  ]);

  /** After saving company standards, move to Grading scale when the finish-setup reminder is visible. */
  const advanceCompanySetupAfterStandardsSaved = useCallback(() => {
    if (!companySetupReminder.show) return;
    const goNext = () => {
      if (canManageGradingScale) setActiveTab("grading-scale");
      else if (canManageTractionRoutes) setActiveTab("traction-routes");
      else if (effectiveIsAdmin) setActiveTab("addons");
    };
    refreshCompanySetupValidation(goNext);
  }, [
    companySetupReminder.show,
    canManageGradingScale,
    canManageTractionRoutes,
    effectiveIsAdmin,
    refreshCompanySetupValidation,
  ]);

  /** After applying a published template as a company cycle, re-run validate-setup and open the next step. */
  const advanceCompanySetupAfterFrameworkApplied = useCallback(() => {
    if (!companySetupReminder.show) return;
    const goNext = () => {
      if (canManageCompanyStandards) setActiveTab("company-standards");
      else if (canManageGradingScale) setActiveTab("grading-scale");
      else if (canManageTractionRoutes) setActiveTab("traction-routes");
      else if (effectiveIsAdmin) setActiveTab("addons");
    };
    refreshCompanySetupValidation(goNext);
  }, [
    companySetupReminder.show,
    canManageCompanyStandards,
    canManageGradingScale,
    canManageTractionRoutes,
    effectiveIsAdmin,
    refreshCompanySetupValidation,
  ]);

  const refreshCompanySetupAfterFrameworkRemoved = useCallback(() => {
    refreshCompanySetupValidation();
  }, [refreshCompanySetupValidation]);

  /** After saving grading scale, continue to depots & traction (required), then optional add-ons. */
  const advanceCompanySetupAfterGradingSaved = useCallback(() => {
    if (!companySetupReminder.show) return;
    setCompanySetupReminder((prev) => ({
      show: true,
      issues: {
        missingCycles: false,
        missingCompanyStandards: false,
        missingFramework: false,
        missingRoleMappings: false,
        missingTractionAndDepots: true,
        ...(prev.issues ?? {}),
        missingGradingScale: false,
      },
    }));
    if (canManageTractionRoutes) setActiveTab("traction-routes");
    else if (effectiveIsAdmin) setActiveTab("addons");
    window.setTimeout(() => refreshCompanySetupValidation(), 400);
  }, [
    companySetupReminder.show,
    canManageTractionRoutes,
    effectiveIsAdmin,
    refreshCompanySetupValidation,
  ]);

  /** After saving depots/traction, offer optional add-on activation. */
  const advanceCompanySetupAfterTractionSaved = useCallback(() => {
    if (!companySetupReminder.show) return;
    setCompanySetupReminder((prev) => ({
      show: true,
      issues: {
        missingCycles: false,
        missingCompanyStandards: false,
        missingFramework: false,
        missingGradingScale: false,
        missingRoleMappings: false,
        ...(prev.issues ?? {}),
        missingTractionAndDepots: false,
      },
    }));
    if (effectiveIsAdmin) setActiveTab("addons");
    window.setTimeout(() => refreshCompanySetupValidation(), 400);
  }, [companySetupReminder.show, effectiveIsAdmin, refreshCompanySetupValidation]);

  /** Administration flyout: Storage & plans — company admins, or system admin only when “View as” a company (not global / not user impersonation). */
  const canSeeStorageAdministrationSection =
    (isRealSystemAdmin && !!impersonatedCompany && !impersonatedUser) ||
    (!isRealSystemAdmin && effectiveIsAdmin);
  const isImpersonationPermissionLoading =
    !!impersonatedUser &&
    (!effectiveCompanyPermissionsLoaded ||
      (traineeDriverModuleActive && canViewOwnEmployeeOnly && !ownTraineeDriverEnrollmentLoaded));

  const notificationDismissStorageKey = useMemo(() => {
    const inboxEmail = String(effectiveProfile?.email || user?.email || "").trim().toLowerCase();
    if (!inboxEmail) return null;
    const companyScope = apiCompanyCode || "all";
    return `rv_dismissed_announcements:${inboxEmail}:${companyScope}`;
  }, [effectiveProfile?.email, user?.email, apiCompanyCode]);
  const announcementContextKey = useMemo(() => {
    const inboxEmail = String(effectiveProfile?.email || user?.email || "").trim().toLowerCase();
    const companyScope = String(apiCompanyCode || "all").trim().toLowerCase();
    return `${inboxEmail}|${companyScope}`;
  }, [effectiveProfile?.email, user?.email, apiCompanyCode]);
  useEffect(() => {
    setDismissedAnnouncementsReady(false);
    if (!notificationDismissStorageKey) {
      setDismissedAnnouncementIds(new Set());
      setDismissedAnnouncementsReady(true);
      return;
    }
    try {
      const stored = localStorage.getItem(notificationDismissStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDismissedAnnouncementIds(new Set(Array.isArray(parsed) ? parsed : []));
        setDismissedAnnouncementsReady(true);
        return;
      }

      // One-time migration from older session-only key.
      const legacy = sessionStorage.getItem("rv_dismissed_announcements");
      if (legacy) {
        const parsed = JSON.parse(legacy);
        const migrated = new Set<number | string>(Array.isArray(parsed) ? parsed : []);
        setDismissedAnnouncementIds(migrated);
        localStorage.setItem(notificationDismissStorageKey, JSON.stringify([...migrated]));
        sessionStorage.removeItem("rv_dismissed_announcements");
        setDismissedAnnouncementsReady(true);
        return;
      }
    } catch {
      // Ignore storage errors and fallback to empty set.
    }
    setDismissedAnnouncementIds(new Set());
    setDismissedAnnouncementsReady(true);
  }, [notificationDismissStorageKey]);


  useEffect(() => {
    if (!apiCompanyCode) {
      setCompanyAddons([]);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const scheduleTaskModuleRefresh = (iso: string | null | undefined) => {
      if (timer) clearTimeout(timer);
      timer = undefined;
      if (!iso || cancelled) return;
      const at = Date.parse(iso);
      if (!Number.isFinite(at)) return;
      const ms = at - Date.now() + 800;
      if (ms <= 0) {
        setAddonsRefreshTrigger((x) => x + 1);
        return;
      }
      const MAX_SCHEDULE_MS = 2147483647;
      timer = setTimeout(() => {
        if (!cancelled) setAddonsRefreshTrigger((x) => x + 1);
      }, Math.min(ms, MAX_SCHEDULE_MS));
    };
    const actorEmail = String(effectiveProfile?.email || user?.email || "").trim();
    fetch(`/api/addons?companyCode=${encodeURIComponent(apiCompanyCode)}&_=${Date.now()}`, {
      cache: "no-store",
      headers: {
        ...(actorEmail ? { "X-User-Email": actorEmail } : {}),
        "X-Company-Code": apiCompanyCode,
      },
    })
      .then((res) => res.json())
      .then((data: { addons?: { addonId: string }[]; taskModuleNextRefreshAt?: string | null }) => {
        if (cancelled) return;
        setCompanyAddons((data.addons || []).map((a) => a.addonId));
        scheduleTaskModuleRefresh(data.taskModuleNextRefreshAt ?? null);
      })
      .catch(() => {
        if (!cancelled) setCompanyAddons([]);
      });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [apiCompanyCode, addonsRefreshTrigger, effectiveProfile?.email, user?.email]);

  useEffect(() => {
    const goHome = () => setActiveTab(canViewOwnEmployeeOnly ? "employees" : "dashboard");

    if (canViewOwnEmployeeOnly) {
      const allowed = new Set<string>(["employees", "help-support", "account-profile"]);
      if (canSelfServeTraineeDriver) allowed.add("trainee-driver");
      if (!allowed.has(activeTab)) {
        setActiveTab("employees");
        return;
      }
    }
    if (activeTab === 'incidents' && !canAccessIncidents) {
      goHome();
      return;
    }
    if (activeTab === 'tasks' && !canAccessTasks) {
      goHome();
      return;
    }
    if (activeTab === 'safety-briefs' && !canAccessSafetyBriefs) {
      goHome();
      return;
    }
    if (activeTab === 'trainee-driver' && !canAccessTraineeDriver) {
      goHome();
      return;
    }
    if (activeTab === 'leave-absence' && !canAccessLeaveAbsence) {
      goHome();
      return;
    }
    if (activeTab === 'medicals' && !canAccessMedicals) {
      goHome();
      return;
    }
    if (activeTab === 'licencing' && !canAccessLicencing) {
      goHome();
      return;
    }
    // Company admins raise tickets via Help & Support — not the agent Support Desk.
    if (activeTab === 'support-desk' && effectiveIsAdmin && !effectiveIsSystemAdmin) {
      setActiveTab('help-support');
      return;
    }
    if (activeTab === 'security' && !effectiveIsSystemAdmin) {
      goHome();
      return;
    }
    if (activeTab === 'custom-job-roles' && !canManageCustomJobRoles) {
      goHome();
      return;
    }
    if (activeTab === 'grading-scale' && !canManageGradingScale) {
      goHome();
      return;
    }
    if (
      activeTab === "company-email-templates" &&
      (!effectiveIsAdmin || effectiveIsSystemAdmin || !apiCompanyCode)
    ) {
      goHome();
      return;
    }
    // Framework now lives inside Cycle Builder — keep old links, chips and tours working.
    if (activeTab === 'framework') {
      if (canManageFramework) setActiveTab('cycle-builder');
      else goHome();
      return;
    }
    if (activeTab === 'company-standards' && !canManageCompanyStandards) {
      goHome();
      return;
    }
    if (activeTab === 'traction-routes' && !canManageTractionRoutes) {
      goHome();
      return;
    }
    if (activeTab === 'org-structure' && (!effectiveIsAdmin || !effectiveCompany)) {
      goHome();
      return;
    }
    if (
      activeTab === "addons-storage" &&
      (canViewOwnEmployeeOnly || !apiCompanyCode || !canSeeStorageAdministrationSection)
    ) {
      goHome();
    }
    if (activeTab === "investigations-api-management" && !canAccessInvestigationsApiManagement) {
      goHome();
    }
  }, [
    activeTab,
    canAccessIncidents,
    canAccessTasks,
    canAccessSafetyBriefs,
    canAccessTraineeDriver,
    canAccessLeaveAbsence,
    canAccessMedicals,
    canAccessLicencing,
    canViewOwnEmployeeOnly,
    canSelfServeTraineeDriver,
    canManageCustomJobRoles,
    canManageGradingScale,
    effectiveIsAdmin,
    effectiveIsSystemAdmin,
    canManageFramework,
    canManageCompanyStandards,
    canManageTractionRoutes,
    apiCompanyCode,
    canSeeStorageAdministrationSection,
    canAccessInvestigationsApiManagement,
  ]);

  useEffect(() => {
    if (!canSeeStorageAdministrationSection) setIsAdministrationStorageOpen(false);
  }, [canSeeStorageAdministrationSection]);

  useEffect(() => {
    if (!canViewOwnEmployeeOnly || selectedEmployeeId || employees.length === 0) return;
    if (activeTab !== "employees") return;
    const ownEmployee = findOwnEmployeeForProfile(employees, effectiveProfile, user?.email) || employees[0];
    if (!ownEmployee?.id) return;
    setSelectedEmployeeId(ownEmployee.id);
    setSelectedEmployeeCompanyCode(ownEmployee.company_code || undefined);
    setActiveTab('employees');
  }, [canViewOwnEmployeeOnly, selectedEmployeeId, employees, effectiveProfile, user?.email, activeTab]);
  const fetchActiveAnnouncements = useCallback(() => {
    if (!user || isResolvingCompanyContext) return;
    const requestContextKey = announcementContextKey;
    /** Must match the user we are viewing as (Team Management "Login as"); not Azure user.email, or the bell shows the wrong inbox. */
    const inboxEmail = (effectiveProfile?.email || user.email || "").trim();
    fetch("/api/announcements/active", {
      cache: "no-store",
      headers: {
        ...(inboxEmail ? { "X-User-Email": inboxEmail } : {}),
        ...(apiCompanyCode ? { "X-Company-Code": apiCompanyCode } : {}),
      },
    })
      .then(res => res.ok ? res.json() : [])
      .then(
        (
          data: {
            id: number | string;
            title: string;
            message: string;
            type: string;
            created_at?: string;
            employee_id?: string;
            note_ref?: string;
            company_code?: string;
            task_id?: string;
            incident_id?: string;
          }[],
        ) => {
          setActiveAnnouncements(data);
          setActiveAnnouncementsContextKey(requestContextKey);
        },
      )
      .catch(() => {
        setActiveAnnouncements([]);
        setActiveAnnouncementsContextKey(requestContextKey);
      });
  }, [user, apiCompanyCode, effectiveProfile?.email, isResolvingCompanyContext, announcementContextKey]);

  useEffect(() => {
    if (!user || isResolvingCompanyContext) {
      setActiveAnnouncements([]);
      setActiveAnnouncementsContextKey("");
      return;
    }
    fetchActiveAnnouncements();
  }, [user, isResolvingCompanyContext, fetchActiveAnnouncements]);

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/maintenance-status");
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.enabled === "boolean") {
        setMaintenanceStatus({
          enabled: data.enabled,
          message: String(data.message || ""),
        });
      }
    } catch {
      /* unavailable — do not block access */
    }
  }, []);

  const handleMaintenanceChanged = useCallback((state: { enabled: boolean; message: string }) => {
    setMaintenanceStatus(state);
  }, []);

  useEffect(() => {
    fetchMaintenanceStatus();
    const id = window.setInterval(fetchMaintenanceStatus, 30000);
    return () => window.clearInterval(id);
  }, [fetchMaintenanceStatus]);

  useEffect(() => {
    if (!maintenanceStatus?.enabled) setShowAdminLoginDuringMaintenance(false);
  }, [maintenanceStatus?.enabled]);

  useEffect(() => {
    if (!user) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchActiveAnnouncements();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [user, fetchActiveAnnouncements]);

  /** Deep-link from employee-note email alerts: ?tab=employees&employeeId=…&profileTab=Notes&noteRef=… */
  const consumedEmployeeNoteEmailLinkRef = useRef(false);
  useEffect(() => {
    if (!user || consumedEmployeeNoteEmailLinkRef.current || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const empId = (params.get("employeeId") || "").trim();
    if (!empId) return;
    const profileTab = (params.get("profileTab") || "Notes").trim() || "Notes";
    const noteRef = (params.get("noteRef") || "").trim();
    const companyCode = (params.get("companyCode") || "").trim();
    consumedEmployeeNoteEmailLinkRef.current = true;
    try {
      sessionStorage.setItem(`rv_employee_tab_${empId}`, profileTab);
      if (noteRef) sessionStorage.setItem(`rv_highlight_note_${empId}`, noteRef);
      else sessionStorage.removeItem(`rv_highlight_note_${empId}`);
    } catch {
      /* ignore */
    }
    setLastEmployeeProfileTab(profileTab);
    restoredEmployeeIdRef.current = empId;
    if (companyCode) setSelectedEmployeeCompanyCode(companyCode);
    setSelectedEmployeeId(empId);
    setActiveTab("employees");
    params.delete("employeeId");
    params.delete("profileTab");
    params.delete("noteRef");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchActiveAnnouncements, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, fetchActiveAnnouncements]);

  useEffect(() => {
    if (activeTab === "account-profile" && user?.email) {
      fetch(buildUsersMeUrl(user.email, apiCompanyCode || undefined))
        .then((res) => (res.ok ? res.json() : null))
        .then((profile: SystemUser | null) => {
          if (profile) {
            const p = profile as unknown as Record<string, unknown>;
            const code = String(p.company_code ?? p.companyCode ?? "").trim();
            if (code) (profile as SystemUser).company_code = code;
            setCurrentUserProfile(profile);
          }
        })
        .catch(() => {});
    }
  }, [activeTab, user?.email, apiCompanyCode, buildUsersMeUrl]);

  useEffect(() => {
    if (activeTab !== 'help-support') return;
    setLibraryDocumentsLoading(true);
    fetch('/api/library-documents')
      .then(res => res.ok ? res.json() : [])
      .then((data: { id: number; name: string; type: string | null; size_display: string | null }[]) => setLibraryDocuments(data))
      .catch(() => setLibraryDocuments([]))
      .finally(() => setLibraryDocumentsLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (!user?.email) {
      setCanAccessSupportDesk(false);
      return;
    }
    let cancelled = false;
    fetch("/api/support-agents/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { canAccessDesk?: boolean } | null) => {
        if (!cancelled) setCanAccessSupportDesk(!!data?.canAccessDesk);
      })
      .catch(() => {
        if (!cancelled) setCanAccessSupportDesk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.email, effectiveIsSystemAdmin, impersonatedUser?.email]);

  const deleteLibraryDocument = useCallback(async (id: number) => {
    if (!confirm(translateUi('Remove this document from the library? This cannot be undone.'))) return;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (user?.email) headers['X-User-Email'] = user.email;
    try {
      const res = await fetch(`/api/library-documents/${id}`, { method: 'DELETE', headers, body: JSON.stringify({}) });
      if (res.ok) setLibraryDocuments(prev => prev.filter(d => d.id !== id));
      else { const err = await res.json(); alert(err.error || translateUi("Failed to delete")); }
    } catch {
      alert(translateUi("Failed to delete document"));
    }
  }, [user?.email]);

  const uploadLibraryDocument = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.email) return;
    setLibraryDocumentUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const content = (reader.result as string).split(',')[1];
        if (!content) throw new Error('Failed to read file');
        const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';
        const sizeDisplay = file.size >= 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`;
        const res = await fetch('/api/library-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
          body: JSON.stringify({ name: file.name, type: ext, size_display: sizeDisplay, content }),
        });
        if (res.ok) {
          const data = await res.json();
          setLibraryDocuments(prev => [...prev, { id: data.id, name: data.name, type: data.type, size_display: data.size_display }]);
        } else {
          const err = await res.json();
          alert(err.error || translateUi("Upload failed"));
        }
      } catch (err) {
        alert(translateUi("Upload failed"));
      } finally {
        setLibraryDocumentUploading(false);
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [user?.email]);

  const dismissAnnouncement = useCallback((id: number | string) => {
    setDismissedAnnouncementIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        if (notificationDismissStorageKey) {
          localStorage.setItem(notificationDismissStorageKey, JSON.stringify([...next]));
        } else {
          sessionStorage.setItem("rv_dismissed_announcements", JSON.stringify([...next]));
        }
      } catch {}
      return next;
    });
  }, [notificationDismissStorageKey]);

  const navigateToEmployeeFromFatalityAnniversaryNotification = useCallback(
    (a: { id: number | string; employee_id?: string; company_code?: string }) => {
      const empId = a.employee_id?.trim();
      if (!empId) return;
      const cc = (a.company_code || apiCompanyCode || "").trim();
      try {
        sessionStorage.setItem(`rv_employee_tab_${empId}`, "Main");
        sessionStorage.removeItem(`rv_highlight_note_${empId}`);
      } catch {
        /* ignore */
      }
      setLastEmployeeProfileTab("Main");
      restoredEmployeeIdRef.current = empId;
      if (cc) setSelectedEmployeeCompanyCode(cc);
      setSelectedEmployeeId(empId);
      setActiveTab("employees");
      dismissAnnouncement(a.id);
      setNotificationMenuOpen(false);
    },
    [apiCompanyCode, dismissAnnouncement],
  );

  const navigateToEmployeeLicenceFromNotification = useCallback(
    (a: { id: number | string; employee_id?: string; company_code?: string }) => {
      const empId = a.employee_id?.trim();
      if (!empId) return;
      const cc = (a.company_code || apiCompanyCode || "").trim();
      try {
        sessionStorage.setItem(`rv_employee_tab_${empId}`, "Licence");
        sessionStorage.removeItem(`rv_highlight_note_${empId}`);
      } catch {
        /* ignore */
      }
      setLastEmployeeProfileTab("Licence");
      restoredEmployeeIdRef.current = empId;
      if (cc) setSelectedEmployeeCompanyCode(cc);
      setSelectedEmployeeId(empId);
      setActiveTab("employees");
      dismissAnnouncement(a.id);
      setNotificationMenuOpen(false);
    },
    [apiCompanyCode, dismissAnnouncement],
  );

  const navigateToEmployeeNoteFromNotification = useCallback(
    (a: { id: number | string; employee_id?: string; company_code?: string; note_ref?: string }) => {
      const empId = a.employee_id?.trim();
      if (!empId) return;
      const cc = (a.company_code || apiCompanyCode || "").trim();
      const nr = a.note_ref?.trim();
      try {
        sessionStorage.setItem(`rv_employee_tab_${empId}`, "Notes");
        if (nr) sessionStorage.setItem(`rv_highlight_note_${empId}`, nr);
        else sessionStorage.removeItem(`rv_highlight_note_${empId}`);
      } catch {
        /* ignore */
      }
      setLastEmployeeProfileTab(translateUi("Notes"));
      restoredEmployeeIdRef.current = empId;
      if (cc) setSelectedEmployeeCompanyCode(cc);
      setSelectedEmployeeId(empId);
      setActiveTab("employees");
      dismissAnnouncement(a.id);
      setNotificationMenuOpen(false);
    },
    [apiCompanyCode, dismissAnnouncement],
  );

  const navigateToTaskFromNotification = useCallback(
    (a: { id: number | string; company_code?: string; task_id?: string }) => {
      const tid = a.task_id?.trim();
      if (!tid) return;
      const cc = (a.company_code || apiCompanyCode || "").trim();
      try {
        sessionStorage.setItem("rv_tasks_highlight_id", tid);
        if (cc) sessionStorage.setItem("rv_tasks_highlight_company", cc);
        else sessionStorage.removeItem("rv_tasks_highlight_company");
      } catch {
        /* ignore */
      }
      setSelectedEmployeeId(null);
      setSelectedEmployeeCompanyCode(undefined);
      setActiveTab("tasks");
      dismissAnnouncement(a.id);
      setNotificationMenuOpen(false);
    },
    [apiCompanyCode, dismissAnnouncement],
  );

  const visibleAnnouncements = dismissedAnnouncementsReady && !isResolvingCompanyContext && !isImpersonationPermissionLoading && activeAnnouncementsContextKey === announcementContextKey
    ? activeAnnouncements.filter(
        (a) => !dismissedAnnouncementIds.has(a.id) && a.type !== "employee_note_read"
      )
    : [];
  useEffect(() => {
    if (!notificationMenuOpen) return;
    const readReceiptIds = activeAnnouncements
      .filter((a) => a.type === "employee_note_read" && !dismissedAnnouncementIds.has(a.id))
      .map((a) => a.id);
    if (readReceiptIds.length === 0) return;
    for (const id of readReceiptIds) dismissAnnouncement(id);
  }, [notificationMenuOpen, activeAnnouncements, dismissedAnnouncementIds, dismissAnnouncement]);

  useEffect(() => {
    if (!user?.email) {
      setCurrentUserProfile(null);
      setCurrentCompany(null);
      setIsResolvingCompanyContext(false);
      return;
    }

    let cancelled = false;
    setIsResolvingCompanyContext(true);

    const ac = new AbortController();
    const RESOLVE_MS = 25000;
    const timeoutId = window.setTimeout(() => {
      ac.abort();
    }, RESOLVE_MS);

    const loadCompanyContext = async () => {
      try {
        const resMe = await fetch(buildUsersMeUrl(user.email!), {
          signal: ac.signal,
          cache: "no-store",
        });
        if (cancelled) return;
        const profile = resMe.ok ? ((await resMe.json()) as SystemUser) : null;
        if (cancelled) return;
        if (!profile) {
          setCurrentUserProfile(null);
          setCurrentCompany(null);
          return;
        }

        const p = profile as unknown as Record<string, unknown>;
        const code = String(p.company_code ?? p.companyCode ?? "").trim();
        if (code) (profile as SystemUser).company_code = code;
        const nameFromApi =
          (typeof p.full_name === "string" && p.full_name.trim()) ||
          (typeof p.fullName === "string" && p.fullName.trim()) ||
          (typeof p.display_name === "string" && p.display_name.trim()) ||
          (typeof p.displayName === "string" && p.displayName.trim()) ||
          "";
        if (nameFromApi) (profile as SystemUser).full_name = nameFromApi;
        writeRememberedCompanyScope(user.email!, code);
        setCurrentUserProfile(profile);
        const serverLocale = (profile as SystemUser).preferred_locale;
        // Prefer an explicit in-browser choice (rv_locale). Only apply the server
        // preferred_locale when localStorage has no saved language — otherwise a
        // stale profile value can overwrite a language the user just selected.
        // Always resolve against the platform language allowlist.
        let hasLocalLocale = false;
        try {
          const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
          hasLocalLocale = !!(stored && isAppLanguageCode(stored));
        } catch {
          hasLocalLocale = false;
        }
        if (!hasLocalLocale && serverLocale && isAppLanguageCode(String(serverLocale).trim().toLowerCase())) {
          applyPreferredLocaleFromProfile(resolveLanguageAgainstAllowlist(serverLocale));
        } else {
          const localLocale = resolveLanguageAgainstAllowlist(readStoredLanguage());
          applyEnabledLanguagesPolicy(localLocale);
          const normalizedServer = serverLocale
            ? String(serverLocale).trim().toLowerCase()
            : '';
          if (localLocale && localLocale !== normalizedServer) {
            void persistPreferredLocale(localLocale);
          }
        }

        const companyCode = profile.company_code;
        const isSysAdmin = (profile.title?.toLowerCase().includes('system') && profile.title?.toLowerCase().includes('administrator')) ?? false;
        if (companyCode) {
          // Restore local step first; API/database can still override with authoritative value.
          try {
            const localStep = localStorage.getItem(`${STORAGE_KEY_PREFIX}${companyCode}`);
            if (localStep && validSteps.includes(localStep as any)) {
              setSetupWizardStep(localStep as typeof validSteps[number]);
            }
          } catch {
            // Ignore storage read errors.
          }
          let companyData: {
            id?: string;
            name?: string;
            status?: string;
            logoUrl?: string;
            setupWizardStep?: string;
            address?: string;
            postalAddress?: string;
            contactEmail?: string;
            contact_email?: string;
            companyEmail?: string;
            company_email?: string;
          } | null = null;
          try {
            const resCo = await fetch(
              `/api/companies/${encodeURIComponent(companyCode)}?userEmail=${encodeURIComponent(user.email!)}`,
              { signal: ac.signal }
            );
            if (cancelled) return;
            companyData = resCo.ok ? await resCo.json() : null;
          } catch {
            companyData = null;
          }
          if (cancelled) return;

          const derivedCompany: CompanyDoc = {
            id: companyCode,
            name: companyData?.name || profile.company_name || companyCode,
            companyCode,
            status: (companyData?.status as any) ?? "PendingSetup",
            address: companyData?.address,
            postalAddress: companyData?.postalAddress,
            logoUrl: companyData?.logoUrl,
            contactEmail: companyData?.contactEmail || companyData?.contact_email || undefined,
            companyEmail: companyData?.companyEmail || companyData?.company_email || undefined,
            setupCompletedAt: null,
            setupCompletedBy: null,
            createdAt: null as any,
            createdBy: user.uid,
            updatedAt: null as any,
            updatedBy: user.uid,
          };
          setCurrentCompany(derivedCompany);
          if (companyData?.status === "PendingSetup" && companyData?.setupWizardStep && validSteps.includes(companyData.setupWizardStep as any)) {
            setSetupWizardStep(companyData.setupWizardStep as typeof validSteps[number]);
          }
        } else if (isSysAdmin) {
          setCurrentCompany({
            id: '',
            name: "Rail Intel",
            companyCode: '',
            status: "Active",
            setupCompletedAt: null,
            setupCompletedBy: null,
            createdAt: null as any,
            createdBy: user.uid,
            updatedAt: null as any,
            updatedBy: user.uid,
          });
        } else {
          setCurrentCompany(null);
        }
      } catch (e: unknown) {
        const isAbort = e instanceof DOMException && e.name === 'AbortError';
        if (!cancelled && !isAbort) {
          console.warn('loadCompanyContext', e);
        }
        if (!cancelled && !isAbort) {
          setCurrentUserProfile(null);
          setCurrentCompany(null);
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) {
          setIsResolvingCompanyContext(false);
        }
      }
    };

    void loadCompanyContext();
    return () => {
      cancelled = true;
      ac.abort();
      window.clearTimeout(timeoutId);
      setIsResolvingCompanyContext(false);
    };
  }, [user, buildUsersMeUrl, writeRememberedCompanyScope, companyContextReloadToken]);

  const [transferInitialEmployee, setTransferInitialEmployee] = useState<Employee | null>(null);
  const selectedEmployeeForAccess = selectedEmployeeId
    ? transferInitialEmployee && transferInitialEmployee.id === selectedEmployeeId
      ? transferInitialEmployee
      : employees.find((e) => e.id === selectedEmployeeId)
    : null;
  const actorEmailForEmployeeAccess = String(effectiveProfile?.email || user?.email || '').trim().toLowerCase();
  const canEditSelectedEmployeeRecord =
    canEditAllEmployees ||
    (canEditOwnEmployeeOnlyPermission &&
      !!selectedEmployeeForAccess &&
      employeeMatchesActorProfile(selectedEmployeeForAccess, effectiveProfile, user?.email));
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setEmployeesLoadedOnce(false);
    setEmployees([]);
    setStats(null);
    const pending = transferPendingRef.current;
    if (
      pending &&
      (pending.companyCode === apiCompanyCode || pending.companyCode === employeesApiCompanyCode)
    ) {
      setSelectedEmployeeId(pending.id);
      setTransferInitialEmployee(pending.employee ?? null);
      transferPendingRef.current = null;
    } else {
      // Keep restored/selected employee on normal refresh; only reset transfer seed.
      setTransferInitialEmployee(null);
    }
    // Stats: unchanged tenant resolution (system admin with no profile company → empty filter → dashboard zeros).
    const listCompanyCodeForStats =
      effectiveIsSystemAdmin && !impersonatedCompany && !apiCompanyCode ? '' : apiCompanyCode;
    const statsCompanyParam = listCompanyCodeForStats
      ? `?companyCode=${encodeURIComponent(listCompanyCodeForStats)}`
      : '';
    const statsCacheBust = statsCompanyParam ? `&_=${Date.now()}` : `?_=${Date.now()}`;
    const employeeListCode = String(employeesApiCompanyCode ?? '').trim();
    const employeesCompanyParam = employeeListCode
      ? `?companyCode=${encodeURIComponent(employeeListCode)}`
      : '';
    const employeesCacheBust = employeesCompanyParam ? `&_=${Date.now()}` : `?_=${Date.now()}`;
    const opts: RequestInit = {
      cache: 'no-store',
      headers: actorEmailForEmployeeAccess ? { 'X-User-Email': actorEmailForEmployeeAccess } : undefined,
    };
    fetch(`/api/stats${statsCompanyParam}${statsCacheBust}`, opts)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    fetch(`/api/employees${employeesCompanyParam}${employeesCacheBust}`, opts)
      .then(async (res) => {
        const data = await res.json();
        if (!cancelled && res.ok && Array.isArray(data)) setEmployees(data);
        if (!cancelled) setEmployeesLoadedOnce(true);
      })
      .catch(() => {
        if (!cancelled) setEmployeesLoadedOnce(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, apiCompanyCode, employeesApiCompanyCode, effectiveIsSystemAdmin, impersonatedCompany, actorEmailForEmployeeAccess, companyContextReloadToken]);

  /** Refresh / patch the in-memory employee list after Team Management updates a linked employee (role, name, additional roles). */
  const handleTeamLinkedEmployeeChanged = useCallback(
    (info: {
      employeeId?: string | null;
      email?: string;
      fullName?: string;
      roleTitle?: string;
      workStatus?: string;
      employeeCreated?: boolean;
    }) => {
      const empId = String(info.employeeId ?? '').trim();
      const emailNorm = String(info.email ?? '').trim().toLowerCase();
      const roleTitle = String(info.roleTitle ?? '').trim();
      const fullName = String(info.fullName ?? '').trim();
      const workStatus = info.workStatus !== undefined ? String(info.workStatus ?? '').trim() : undefined;

      // Optimistic update so Employees tab shows the new role without a page refresh.
      if (empId || emailNorm) {
        setEmployees((prev) =>
          (Array.isArray(prev) ? prev : []).map((e) => {
            const idMatch = empId && String(e.id ?? '').trim() === empId;
            const emailMatch =
              emailNorm && String((e as { email?: unknown }).email ?? '').trim().toLowerCase() === emailNorm;
            if (!idMatch && !emailMatch) return e;
            return {
              ...e,
              ...(roleTitle ? { role: roleTitle } : {}),
              ...(fullName ? { full_name: fullName } : {}),
              ...(workStatus !== undefined ? { work_status: workStatus || null } : {}),
            };
          }),
        );
      }

      // Refetch so creates and server-normalized fields stay accurate.
      const employeeListCode = String(employeesApiCompanyCode ?? '').trim();
      const employeesCompanyParam = employeeListCode
        ? `?companyCode=${encodeURIComponent(employeeListCode)}`
        : '';
      const employeesCacheBust = employeesCompanyParam ? `&_=${Date.now()}` : `?_=${Date.now()}`;
      const opts: RequestInit = {
        cache: 'no-store',
        headers: actorEmailForEmployeeAccess ? { 'X-User-Email': actorEmailForEmployeeAccess } : undefined,
      };
      fetch(`/api/employees${employeesCompanyParam}${employeesCacheBust}`, opts)
        .then(async (res) => {
          const data = await res.json();
          if (res.ok && Array.isArray(data)) setEmployees(data);
          setEmployeesLoadedOnce(true);
        })
        .catch(() => {
          /* keep optimistic list */
        });
    },
    [employeesApiCompanyCode, actorEmailForEmployeeAccess],
  );

  /** When leaving an employee profile for the employees tab, reload the list so it is never stuck empty after edit/close flows. */
  const prevSelectedEmployeeIdForListRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.email) return;
    const prev = prevSelectedEmployeeIdForListRef.current;
    prevSelectedEmployeeIdForListRef.current = selectedEmployeeId;

    if (activeTab !== 'employees' || selectedEmployeeId) return;
    if (!prev) return;

    const employeeListCode = String(employeesApiCompanyCode ?? '').trim();
    const employeesCompanyParam = employeeListCode
      ? `?companyCode=${encodeURIComponent(employeeListCode)}`
      : '';
    const employeesCacheBust = employeesCompanyParam ? `&_=${Date.now()}` : `?_=${Date.now()}`;
    const opts: RequestInit = {
      cache: 'no-store',
      headers: actorEmailForEmployeeAccess ? { 'X-User-Email': actorEmailForEmployeeAccess } : undefined,
    };
    fetch(`/api/employees${employeesCompanyParam}${employeesCacheBust}`, opts)
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && Array.isArray(data)) setEmployees(data);
        setEmployeesLoadedOnce(true);
      })
      .catch(() => {
        setEmployeesLoadedOnce(true);
      });
  }, [selectedEmployeeId, activeTab, user?.email, employeesApiCompanyCode, effectiveIsSystemAdmin, impersonatedCompany, actorEmailForEmployeeAccess]);

  const completeLoginSession = useCallback((token: string, emailTrimmed: string) => {
    const companyTrimmed = loginForm.companyCode.trim();
    if (companyTrimmed) {
      try {
        sessionStorage.setItem('rv_login_company_code', companyTrimmed);
      } catch {
        /* ignore storage */
      }
      writeRememberedCompanyScope(emailTrimmed, companyTrimmed);
    }
    goToDashboardAfterSignInRef.current = true;
    setSessionAuth(token, emailTrimmed);
    setUser({ email: emailTrimmed, uid: emailTrimmed });
    setIsResolvingCompanyContext(true);
    setLoginMfa(null);
    setLoginForm((f) => ({ ...f, password: "" }));
    scheduleEndLoginOverlay();
  }, [loginForm.companyCode, scheduleEndLoginOverlay]);

  const loginServerStartingMessage = t('common.serverStartingRetry', {
    defaultValue: 'The server is still starting. Wait a few seconds and try again.',
  });

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    try {
      sessionStorage.removeItem('rv_inactivity_logout');
      sessionStorage.removeItem('rv_mobile_session_logout');
      sessionStorage.removeItem('rv_login_company_code');
    } catch {}
    if (loginOverlayEndTimerRef.current) {
      clearTimeout(loginOverlayEndTimerRef.current);
      loginOverlayEndTimerRef.current = null;
    }
    loginOverlayStartedAtRef.current = Date.now();
    setIsLoggingIn(true);

    const runLoginValidation = async () => {
      const emailTrimmed = loginForm.email.trim().toLowerCase();
      const companyTrimmed = loginForm.companyCode.trim();
      const res = await fetch('/api/users/validate-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailTrimmed, companyCode: companyTrimmed, password: loginForm.password }),
      });
      const data = await res.json().catch(() => ({}));
      return { res, data, emailTrimmed, companyTrimmed };
    };

    const isLoginWarmupResponse = (res: Response, data: Record<string, unknown>) =>
      data.retry === true ||
      data.error === 'starting' ||
      (res.status === 503 && data.error === 'starting');

    const formatLoginError = (data: Record<string, unknown>) => {
      const err = typeof data.error === 'string' ? data.error.trim() : '';
      if (err === 'starting' || data.retry === true) {
        return loginServerStartingMessage;
      }
      return err || 'User not found in system or invalid company code';
    };

    try {
      let { res, data, emailTrimmed, companyTrimmed } = await runLoginValidation();
      if (isLoginWarmupResponse(res, data)) {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        ({ res, data, emailTrimmed, companyTrimmed } = await runLoginValidation());
      }

      if (data.valid && data.mfaRequired && data.mfaToken) {
        setLoginMfa({
          mode: data.mfaMethod === "email" ? "email" : "verify",
          token: String(data.mfaToken),
          email: emailTrimmed,
          companyCode: companyTrimmed,
        });
        setLoginForm((f) => ({ ...f, password: "" }));
        setIsLoggingIn(false);
        scheduleEndLoginOverlay();
        return;
      }
      if (data.valid && data.mfaSetupRequired && data.mfaToken) {
        setLoginMfa({ mode: "setup", token: String(data.mfaToken), email: emailTrimmed, companyCode: companyTrimmed });
        setLoginForm((f) => ({ ...f, password: "" }));
        setIsLoggingIn(false);
        scheduleEndLoginOverlay();
        return;
      }
      if (!data.valid || !data.token) {
        setAuthError(formatLoginError(data));
        scheduleEndLoginOverlay();
        return;
      }

      completeLoginSession(String(data.token), emailTrimmed);
    } catch (error: any) {
      goToDashboardAfterSignInRef.current = false;
      try {
        sessionStorage.removeItem('rv_login_company_code');
      } catch {}
      console.error("Auth error:", error.code);
      scheduleEndLoginOverlay();
      if (error?.message === "Failed to fetch" || error?.name === "TypeError") {
        setAuthError("Cannot reach the server. Please make sure Rail Intel API is running and try again.");
      } else {
        setAuthError(error?.message || "Email or password is incorrect");
      }
    }
  };

  const handleResendNomineeWelcomeEmail = useCallback(async () => {
    if (!user?.email || !companyActivationSuccess?.companyCode) return;
    setResendNomineeEmailLoading(true);
    setResendNomineeEmailMessage(null);
    try {
      const res = await fetch(
        `/api/companies/${encodeURIComponent(companyActivationSuccess.companyCode)}/activate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-User-Email": user.email },
          body: JSON.stringify({ userEmail: user.email }),
        }
      );
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        nominatedAdminProvisioning?: { emailed?: boolean; warning?: string };
      };
      if (!res.ok || data.success === false) {
        setResendNomineeEmailMessage(data.error || "Could not send welcome email. Check server SMTP settings.");
        return;
      }
      const p = data.nominatedAdminProvisioning;
      if (p?.emailed) {
        setCompanyActivationSuccess((prev) =>
          prev ? { ...prev, emailSent: true, emailWarning: undefined } : prev
        );
        setResendNomineeEmailMessage("Welcome email sent successfully.");
      } else {
        const warn = p?.warning || "Email was not sent. Try again or share credentials securely with the nominated administrator.";
        setCompanyActivationSuccess((prev) => (prev ? { ...prev, emailWarning: warn } : prev));
        setResendNomineeEmailMessage(warn);
      }
    } catch {
      setResendNomineeEmailMessage("Could not reach the server. Try again in a moment.");
    } finally {
      setResendNomineeEmailLoading(false);
    }
  }, [user?.email, companyActivationSuccess?.companyCode]);

  const handleLogout = async () => {
    setCompanyActivationSuccess(null);
    setOnboardingCompleteMessage(null);
    setResendNomineeEmailMessage(null);
    if (user?.email) writeRememberedCompanyScope(user.email, "");
    try {
      sessionStorage.removeItem('rv_login_company_code');
    } catch {}
    clearSessionAuth();
    setUser(null);
    setSelectedEmployeeId(null);
    setSelectedEmployeeCompanyCode(undefined);
    setEmployees([]);
    setStats(null);
    setEmployeesLoadedOnce(false);
    setCurrentCompany(null);
    setCurrentUserProfile(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSelectedEmployeeId(null);
    setSelectedEmployeeCompanyCode(undefined);
    setTransferInitialEmployee(null);
    setActiveTab('employees');
  };

  const filteredEmployees = useMemo(() => {
    let filtered = Array.isArray(employees) ? employees : [];
    if (employeeFilter === 'active') {
      filtered = filtered.filter(e => e.status === 'Active');
    } else if (employeeFilter === 'off-track') {
      filtered = filtered.filter(e => e.monitoring_status === 'Off Track');
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((e) => {
        const fullName = String(e.full_name ?? '').toLowerCase();
        const id = String(e.id ?? '').toLowerCase();
        const role = String(e.role ?? '').toLowerCase();
        const depot = String(e.depot ?? '').toLowerCase();
        return fullName.includes(q) || id.includes(q) || role.includes(q) || depot.includes(q);
      });
    }
    return filtered;
  }, [employees, employeeFilter, searchQuery]);

  // On phone/tablet the nav is an overlay — always start (and resize-enter) closed.
  useEffect(() => {
    if (isMobileOrTablet) setIsSidebarOpen(false);
  }, [isMobileOrTablet]);

  const closeMobileSidebar = useCallback(() => {
    if (isMobileOrTablet) setIsSidebarOpen(false);
  }, [isMobileOrTablet]);

  const showLoginTransitionOverlay = isLoggingIn || postSignInContextHydrating;

  // Mobile overlay is never the collapsed icon-rail — always show labels in that panel.
  const sidebarChromeExpanded = isMobileOrTablet || isSidebarOpen;

  // Spinner only while auth bootstrap is unresolved; do not block the full app on
  // company-context hydration because that fetch can stall on weak connections.
  if (loading) {
    return (
      <React.Fragment key={uiLocale}>
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </React.Fragment>
    );
  }

  if (showLoginShell) {
    const showInactivityLogoutMessage = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('rv_inactivity_logout') === '1';
    const showMobileSessionLogoutMessage =
      typeof sessionStorage !== 'undefined' && sessionStorage.getItem('rv_mobile_session_logout') === '1';
    return (
      <div key={uiLocale} dir="ltr" className={`rv-login-shell min-h-[100dvh] min-h-screen max-h-[100dvh] max-h-screen relative flex items-start justify-center md:justify-start overflow-y-auto overflow-x-hidden overscroll-x-none bg-slate-950 px-4 py-6 pb-[max(5.75rem,calc(env(safe-area-inset-bottom,0px)+4.5rem))] pt-[max(1.25rem,env(safe-area-inset-top,0px))] pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] sm:px-6 sm:py-6 md:pl-12 md:pr-10 lg:pl-16 xl:pl-20 2xl:pl-24${loginMfa ? " rv-login-shell--mfa" : ""}${loginMfa?.mode === "setup" ? " rv-login-shell--mfa-enroll" : ""}`}>
        <style>{`
            /* Landscape on touch phone/tablet widths: room for card + footer */
            @media (orientation: landscape) and (max-width: 1366px) and (pointer: coarse),
                   (orientation: landscape) and (max-width: 1366px) and (hover: none) {
              .rv-login-shell {
                align-items: center !important;
                justify-content: center !important;
                padding-top: max(1rem, env(safe-area-inset-top)) !important;
                padding-bottom: max(3.5rem, calc(env(safe-area-inset-bottom, 0px) + 2.75rem)) !important;
              }
            }
            /* Phone portrait: center card */
            @media (max-width: 767px) and (orientation: portrait) {
              .rv-login-shell {
                align-items: center !important;
                justify-content: center !important;
                padding-top: max(calc(1.25rem + 0.5in), env(safe-area-inset-top)) !important;
                padding-bottom: max(calc(1.25rem + 0.5in), env(safe-area-inset-bottom)) !important;
              }
              .rv-login-card {
                transform: none !important;
                translate: none !important;
                margin-top: 0.5rem !important;
                margin-bottom: 1rem !important;
              }
            }
            /* Phone landscape (non-short viewports): extra inset to match portrait */
            @media (max-width: 767px) and (orientation: landscape) and (min-height: 521px) {
              .rv-login-shell {
                padding-top: max(calc(1.5rem + 0.5in), env(safe-area-inset-top)) !important;
                padding-bottom: max(calc(3.25rem + 0.5in), env(safe-area-inset-bottom)) !important;
              }
            }
            /* Phone landscape: reset translate so card layout stays stable */
            @media (max-width: 767px) and (orientation: landscape) {
              .rv-login-card {
                transform: none !important;
                translate: none !important;
              }
            }
            /* Phone only (~½ in extra horizontal inset; vertical handled in blocks above / short-landscape below) */
            @media (max-width: 767px) {
              .rv-login-shell {
                padding-left: max(calc(1rem + 0.5in), env(safe-area-inset-left, 0px)) !important;
                padding-right: max(calc(1rem + 0.5in), env(safe-area-inset-right, 0px)) !important;
              }
            }
            /* Tablet portrait (touch): center card */
            @media (orientation: portrait) and (min-width: 768px) and (max-width: 1366px) and (pointer: coarse),
                   (orientation: portrait) and (min-width: 768px) and (max-width: 1366px) and (hover: none) {
              .rv-login-shell {
                align-items: center !important;
                justify-content: center !important;
                padding-top: max(1.5rem, env(safe-area-inset-top, 0px)) !important;
                padding-bottom: max(3.5rem, calc(env(safe-area-inset-bottom, 0px) + 2.75rem)) !important;
              }
              .rv-login-card {
                transform: none !important;
                translate: none !important;
                margin-top: 0.75rem !important;
                margin-bottom: 0.75rem !important;
                max-width: min(100%, 28rem) !important;
              }
            }
            /*
             * Horizontal login card: touch / no-hover devices in landscape
             * (phones + tablets). Avoids laptop trackpads (hover + fine pointer).
             */
            @media (orientation: landscape) and (max-width: 1366px) and (pointer: coarse),
                   (orientation: landscape) and (max-width: 1366px) and (hover: none) {
              .rv-login-card {
                flex-direction: row !important;
                align-items: stretch !important;
                max-width: min(92vw, 40rem) !important;
                width: min(92vw, 40rem) !important;
                max-height: min(calc(100dvh - 5.5rem), 34rem) !important;
                overflow: hidden !important;
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                transform: none !important;
                translate: none !important;
              }
              .rv-login-card-brand {
                width: clamp(8.5rem, 26vw, 11.5rem) !important;
                max-width: 11.5rem !important;
                min-height: 0 !important;
                height: auto !important;
                flex: 0 0 clamp(8.5rem, 26vw, 11.5rem) !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: center !important;
                border-bottom-width: 0 !important;
                border-right-width: 1px !important;
                border-right-color: rgba(255, 255, 255, 0.25) !important;
                padding: 0.85rem 0.65rem !important;
                background-color: rgba(2, 6, 23, 0.42) !important;
              }
              .rv-login-brand-inner {
                transform: none !important;
                translate: none !important;
              }
              .rv-login-card-brand .rv-login-logo-wrap {
                width: 4.25rem !important;
                height: 4.25rem !important;
                margin-bottom: 0.65rem !important;
              }
              .rv-login-card-brand h1.rv-login-card-title,
              .rv-login-card-brand .rv-login-brand-title {
                font-size: 1.05rem !important;
                line-height: 1.25 !important;
              }
              .rv-login-card-brand p.rv-login-card-subtitle {
                font-size: 0.72rem !important;
                margin-top: 0.2rem !important;
                line-height: 1.35 !important;
                max-width: 9.5rem !important;
              }
              .rv-login-card-form {
                flex: 1 1 auto !important;
                min-width: 0 !important;
                min-height: 0 !important;
                overflow-x: hidden !important;
                overflow-y: auto !important;
                -webkit-overflow-scrolling: touch;
                background-color: rgba(2, 6, 23, 0.28) !important;
              }
              .rv-login-card-form form {
                padding: 0.85rem 1rem !important;
              }
              .rv-login-card-form form > * + * {
                margin-top: 0.55rem !important;
              }
              .rv-login-card-form form button[type="submit"] {
                padding-top: 0.85rem !important;
                padding-bottom: 0.85rem !important;
              }
            }
            /*
             * Mobile phone landscape only (short viewport height; tablets are taller in landscape).
             * Fit card in viewport, one scroll surface to avoid nested scroll bugs.
             * Must be phone-width only — otherwise this overrides tablet landscape padding/centering below.
             */
            @media (orientation: landscape) and (max-width: 767px) and (max-height: 520px) {
              .rv-login-shell {
                overflow-y: auto !important;
                overflow-x: hidden !important;
                min-height: 100dvh !important;
                padding-top: max(0.35rem, env(safe-area-inset-top)) !important;
                padding-bottom: max(2.75rem, env(safe-area-inset-bottom)) !important;
                align-items: flex-start !important;
                justify-content: flex-start !important;
                box-sizing: border-box !important;
              }
              .rv-login-card {
                max-height: none !important;
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                flex-shrink: 1 !important;
                overflow: visible !important;
                transform: none !important;
                translate: none !important;
              }
              .rv-login-card-form {
                flex: 0 0 auto !important;
                overflow-y: visible !important;
                overscroll-behavior: auto;
                min-height: 0 !important;
              }
            }
            @media (max-width: 767px) and (orientation: landscape) and (max-height: 520px) {
              .rv-login-shell {
                padding-bottom: max(calc(2.75rem + 0.5in), env(safe-area-inset-bottom)) !important;
              }
            }
            /* Tablet landscape (touch): centered in viewport with footer clearance */
            @media (orientation: landscape) and (min-width: 768px) and (max-width: 1366px) and (pointer: coarse),
                   (orientation: landscape) and (min-width: 768px) and (max-width: 1366px) and (hover: none) {
              .rv-login-shell {
                min-height: 100dvh !important;
                min-height: 100svh !important;
                box-sizing: border-box !important;
                align-items: center !important;
                justify-content: center !important;
                padding-top: max(1rem, env(safe-area-inset-top, 0px)) !important;
                padding-bottom: max(3.75rem, calc(env(safe-area-inset-bottom, 0px) + 3rem)) !important;
                padding-left: max(1.25rem, env(safe-area-inset-left, 0px)) !important;
                padding-right: max(1.25rem, env(safe-area-inset-right, 0px)) !important;
              }
              .rv-login-card {
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                transform: none !important;
                translate: none !important;
              }
              .rv-login-card-brand {
                overflow: hidden !important;
                border-top-left-radius: 1.25rem !important;
                border-bottom-left-radius: 1.25rem !important;
                min-height: 0 !important;
              }
              .rv-login-brand-inner {
                transform: none !important;
                translate: none !important;
              }
            }
            /* Desktop / laptop: compact pane, no inner scrollbar. */
            @media (min-width: 768px) and (hover: hover) and (pointer: fine) {
              .rv-login-shell {
                justify-content: flex-start !important;
                align-items: flex-start !important;
                padding-top: max(1.25rem, env(safe-area-inset-top, 0px)) !important;
                padding-bottom: max(5.75rem, calc(env(safe-area-inset-bottom, 0px) + 4.5rem)) !important;
              }
              .rv-login-card:not(.rv-login-card--mfa-enroll) {
                transform: none !important;
                translate: none !important;
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                flex-shrink: 0 !important;
                max-height: none !important;
                overflow: hidden !important;
              }
              .rv-login-card-form {
                flex: 0 0 auto !important;
                min-height: 0 !important;
                overflow-y: visible !important;
              }
              .rv-login-card-brand {
                min-height: 0 !important;
                padding-top: 0.75rem !important;
                padding-bottom: 0.8rem !important;
              }
              .rv-login-brand-inner {
                padding-top: 0.25rem !important;
              }
              .rv-login-card-brand .rv-login-logo-wrap {
                width: 3.75rem !important;
                height: 3.75rem !important;
                margin-bottom: 0.4rem !important;
              }
              .rv-login-form {
                padding: 0.85rem 1rem 0.55rem !important;
              }
              .rv-login-form > * + * {
                margin-top: 0.625rem !important;
              }
              .rv-login-form button[type="submit"] {
                padding-top: 0.7rem !important;
                padding-bottom: 0.7rem !important;
              }
            }
            @media (min-width: 768px) and (min-height: 920px) and (hover: hover) and (pointer: fine) {
              .rv-login-card-brand {
                padding-top: 1rem !important;
                padding-bottom: 1.25rem !important;
              }
              .rv-login-brand-inner {
                padding-top: 0.75rem !important;
              }
              .rv-login-card-brand .rv-login-logo-wrap {
                width: 4.5rem !important;
                height: 4.5rem !important;
                margin-bottom: 0.65rem !important;
              }
              .rv-login-form {
                padding: 1.1rem 1.25rem 0.75rem !important;
              }
              .rv-login-form > * + * {
                margin-top: 0.875rem !important;
              }
            }
            /* Short laptop/tablet heights (e.g. 13″ Windows at 125% scaling). */
            @media (max-height: 860px) and (min-width: 768px) {
              .rv-login-card-brand {
                min-height: 0 !important;
                padding-top: 0.65rem !important;
                padding-bottom: 0.75rem !important;
              }
              .rv-login-brand-inner {
                padding-top: 0.35rem !important;
              }
              .rv-login-card-brand .rv-login-logo-wrap {
                width: 3.75rem !important;
                height: 3.75rem !important;
                margin-bottom: 0.45rem !important;
              }
            }
            @media (max-height: 720px) {
              .rv-login-card-brand {
                min-height: 0 !important;
                padding-top: 0.5rem !important;
                padding-bottom: 0.5rem !important;
              }
              .rv-login-brand-inner {
                padding-top: 0.15rem !important;
              }
              .rv-login-card-brand .rv-login-logo-wrap {
                width: 3.25rem !important;
                height: 3.25rem !important;
                margin-bottom: 0.35rem !important;
              }
              .rv-login-form {
                padding-top: 0.75rem !important;
                padding-bottom: 0.65rem !important;
              }
              .rv-login-form > * + * {
                margin-top: 0.65rem !important;
              }
              .rv-login-form button[type="submit"] {
                padding-top: 0.75rem !important;
                padding-bottom: 0.75rem !important;
              }
            }
            /* Authenticator QR enrollment only: landscape card, centered in the viewport */
            .rv-login-shell--mfa-enroll {
              justify-content: center !important;
              align-items: center !important;
              padding-left: max(1.25rem, env(safe-area-inset-left, 0px)) !important;
              padding-right: max(1.25rem, env(safe-area-inset-right, 0px)) !important;
            }
            .rv-login-card.rv-login-card--mfa-enroll {
              flex-direction: row !important;
              align-items: stretch !important;
              max-width: min(94vw, 46rem) !important;
              width: min(94vw, 46rem) !important;
              max-height: min(calc(100dvh - 3.5rem), 32rem) !important;
              margin: 0 !important;
              transform: none !important;
              translate: none !important;
            }
            .rv-login-card--mfa-enroll .rv-login-card-brand {
              width: 10.5rem !important;
              max-width: 10.5rem !important;
              min-height: 0 !important;
              height: auto !important;
              flex: 0 0 10.5rem !important;
              border-bottom-width: 0 !important;
              border-right-width: 1px !important;
              border-right-color: rgba(255, 255, 255, 0.25) !important;
              padding: 1rem 0.75rem !important;
            }
            .rv-login-card--mfa-enroll .rv-login-brand-inner {
              transform: none !important;
              translate: none !important;
            }
            .rv-login-card--mfa-enroll .rv-login-card-brand .rv-login-logo-wrap {
              width: 4.25rem !important;
              height: 4.25rem !important;
              margin-bottom: 0.5rem !important;
            }
            .rv-login-card--mfa-enroll .rv-login-card-brand h1.rv-login-card-title,
            .rv-login-card--mfa-enroll .rv-login-card-brand .rv-login-brand-title {
              font-size: 1.05rem !important;
            }
            .rv-login-card--mfa-enroll .rv-login-card-brand p.rv-login-card-subtitle {
              font-size: 0.72rem !important;
              max-width: 9.5rem !important;
            }
            .rv-login-card--mfa-enroll .rv-login-card-form {
              flex: 1 1 auto !important;
              min-width: 0 !important;
              min-height: 0 !important;
              overflow-x: hidden !important;
              overflow-y: auto !important;
            }
            @media (max-width: 767px) {
              .rv-login-card.rv-login-card--mfa-enroll {
                flex-direction: column !important;
                max-width: min(100%, 26rem) !important;
                width: 100% !important;
                max-height: none !important;
              }
              .rv-login-card--mfa-enroll .rv-login-card-brand {
                width: 100% !important;
                max-width: none !important;
                flex: 0 0 auto !important;
                border-right-width: 0 !important;
                border-bottom-width: 1px !important;
                padding: 0.75rem !important;
              }
              .rv-login-card--mfa-enroll .rv-login-card-brand .rv-login-logo-wrap {
                width: 3.5rem !important;
                height: 3.5rem !important;
                margin-bottom: 0.35rem !important;
              }
            }
          `}</style>
        <video
          className="fixed inset-0 h-full w-full object-cover object-center z-0 bg-slate-950"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src="/assets/background.mp4?v=5" type="video/mp4" />
        </video>
        <div className="fixed inset-0 z-[1] bg-black/40 pointer-events-none" aria-hidden="true" />
        <div 
          initial={{ scale: 0.98 }}
          animate={{ scale: 1 }}
          className={`rv-login-card relative z-10 flex min-h-0 w-full max-w-[min(100%,26rem)] shrink-0 flex-col overflow-hidden rounded-2xl border-2 border-white/65 bg-white/8 ring-1 ring-sky-100/35 shadow-[0_28px_72px_rgba(2,6,23,0.48),inset_0_1px_0_rgba(255,255,255,0.62)] backdrop-blur-0 sm:rounded-3xl my-3 sm:my-4 md:my-0 md:mb-2${loginMfa ? " rv-login-card--mfa" : ""}${loginMfa?.mode === "setup" ? " rv-login-card--mfa-enroll" : ""}`}
        >
          <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-white/24 via-white/6 to-sky-200/8" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-[2px] z-[1] rounded-[inherit] border border-white/30" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-24 bg-gradient-to-b from-white/42 to-transparent" aria-hidden="true" />
          <div
            className="rv-login-card-brand relative z-10 flex w-full shrink-0 flex-col items-center justify-center overflow-hidden border-b border-white/25 bg-white/6 px-4 pt-4 pb-5 text-white sm:px-5 sm:pt-5 sm:pb-6 min-h-[13.75rem] sm:min-h-[14.5rem] md:min-h-0"
          >
            <div
              className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center opacity-35"
              style={{ backgroundImage: "url('/GBR_BG.png?v=5')" }}
              aria-hidden="true"
            />
            {/* Use padding (not translate) so the tagline is never clipped by overflow-hidden on mobile. */}
            <div className="rv-login-brand-inner relative z-[1] flex flex-col items-center justify-center pt-2 sm:pt-4 md:pt-2">
              <div className="rv-login-logo-wrap h-[4.75rem] w-[4.75rem] sm:h-[5.25rem] sm:w-[5.25rem] md:h-[3.75rem] md:w-[3.75rem] flex items-center justify-center shrink-0 mb-2.5 sm:mb-3.5 md:mb-2">
                <img
                  src="/rail-intel-icon.png?v=18"
                  alt={translateUi("Rail Intel")}
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="flex flex-col items-center gap-1 sm:gap-1.5 mt-0.5 sm:mt-1 px-1">
                <p className="rv-login-brand-title text-base sm:text-lg font-bold text-white tracking-tight leading-tight">
                  {translateUi("Rail Intel")}
                </p>
                <p className="rv-login-card-subtitle text-white/85 text-sm text-center leading-snug sm:leading-relaxed text-balance max-w-[18rem] sm:max-w-none pb-0.5">
                  {t('login.tagline')}
                </p>
              </div>
            </div>
          </div>
          <div className="rv-login-card-form relative z-10 flex min-h-0 min-w-0 flex-1 flex-col bg-white/6">
          {!showLoginTransitionOverlay ? (
          loginShowsMaintenanceGate ? (
            <div className="p-6 sm:p-8">
              <MaintenanceDowntimeView
                message={
                  maintenanceStatus?.message ||
                  "Rail Intel is temporarily unavailable for scheduled maintenance. Please try again later."
                }
                showAdminHint
                onAdminSignIn={() => setShowAdminLoginDuringMaintenance(true)}
              />
            </div>
          ) : loginMfa ? (
            <LoginMfaPanel
              mode={loginMfa.mode}
              email={loginMfa.email}
              mfaToken={loginMfa.token}
              companyCode={loginMfa.companyCode}
              onSuccess={(token, em) => completeLoginSession(token, em)}
              onBack={() => { setLoginMfa(null); setAuthError(null); }}
            />
          ) : (
          <form onSubmit={handleAuth} className="rv-login-form relative space-y-3 p-4 sm:space-y-3.5 sm:p-4 md:space-y-2.5 md:p-4 lg:space-y-3 lg:p-5">
            {authError && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center">
                {authError}
              </div>
            )}
            <div className="rv-login-keyboard-hide">
              <LanguageSelector variant="login" />
            </div>
            <div>
              <label className="rv-login-field-label block text-xs font-bold text-white uppercase tracking-wider mb-2">{t('login.companyCode')}</label>
              <input 
                type="text" 
                required
                autoComplete="organization"
                enterKeyHint="next"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="w-full min-h-11 px-4 py-2.5 sm:py-3 text-base text-slate-900 caret-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                placeholder={t('login.companyCodePlaceholder')}
                value={loginForm.companyCode}
                onChange={e => {
                  try {
                    sessionStorage.removeItem('rv_inactivity_logout');
                    sessionStorage.removeItem('rv_mobile_session_logout');
                  } catch {}
                  setLoginForm({ ...loginForm, companyCode: e.target.value });
                }}
              />
            </div>
            <div>
              <label className="rv-login-field-label block text-xs font-bold text-white uppercase tracking-wider mb-2">{t('login.email')}</label>
              <input
                type="email"
                required
                autoComplete="username"
                enterKeyHint="next"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className="w-full min-h-11 px-4 py-2.5 sm:py-3 text-base text-slate-900 caret-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                placeholder={t('login.emailPlaceholder')}
                value={loginForm.email}
                onChange={e => {
                  try {
                    sessionStorage.removeItem('rv_inactivity_logout');
                    sessionStorage.removeItem('rv_mobile_session_logout');
                  } catch {}
                  setLoginForm({ ...loginForm, email: e.target.value });
                }}
              />
            </div>
            <div>
              <label className="rv-login-field-label block text-xs font-bold text-white uppercase tracking-wider mb-2">{t('login.password')}</label>
              <div className="relative">
                <input
                  type={loginPasswordVisible ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  enterKeyHint="go"
                  className="w-full min-h-11 px-4 py-2.5 sm:py-3 pr-12 text-base text-slate-900 caret-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  placeholder="••••••••"
                  value={loginForm.password}
                  onChange={e => {
                    try {
                      sessionStorage.removeItem('rv_inactivity_logout');
                      sessionStorage.removeItem('rv_mobile_session_logout');
                    } catch {}
                    setLoginForm({ ...loginForm, password: e.target.value });
                  }}
                />
                <button
                  type="button"
                  onClick={() => setLoginPasswordVisible((visible) => !visible)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 transition-colors"
                  aria-label={loginPasswordVisible ? t('login.hidePassword') : t('login.showPassword')}
                  aria-pressed={loginPasswordVisible}
                >
                  {loginPasswordVisible ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
                </button>
              </div>
            </div>
            <button 
              type="submit"
              disabled={isLoggingIn}
              className="w-full py-3.5 bg-[#243c96] text-white font-bold rounded-xl hover:bg-[#1e327f] transition-all disabled:opacity-80 disabled:cursor-not-allowed relative"
            >
              {isLoggingIn ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {t('login.loggingIn')}
                </span>
              ) : (
                t('login.signIn')
              )}
            </button>
            <div className="rv-login-form-footer rv-login-keyboard-hide">
            <button
              type="button"
              onClick={() => { setForgotPasswordOpen(true); setForgotForm({ companyCode: loginForm.companyCode, email: loginForm.email }); setForgotSent(false); setForgotError(null); setAuthError(null); }}
              className="w-full text-sm text-white hover:text-white/85"
            >
              {t('login.forgotPassword')}
            </button>
            <p className="text-center text-xs leading-snug text-white/90">
              {t('login.authorisedOnly')}
            </p>
            </div>
            {showInactivityLogoutMessage && (
              <p className="rv-login-keyboard-hide text-center text-sm text-amber-700 mt-3">
                {t('login.inactivityLogout')}
              </p>
            )}
            {showMobileSessionLogoutMessage && !showInactivityLogoutMessage && (
              <p className="rv-login-keyboard-hide text-center text-sm text-amber-700 mt-3">
                {t('login.mobileSessionLogout')}
              </p>
            )}
          </form>
          )
          ) : (
            <div
              role="status"
              aria-live="polite"
              className="relative flex min-h-[13rem] flex-1 flex-col items-center justify-center overflow-hidden px-4 py-6 sm:min-h-[15rem] sm:px-6 sm:py-8"
            >
              <div className="pointer-events-none absolute inset-0 bg-white/8 backdrop-blur-0" aria-hidden="true" />
              <div className="pointer-events-none absolute inset-0 bg-slate-950/50" aria-hidden="true" />
              <div className="relative z-10 flex w-full max-w-md flex-col items-center gap-7 px-2">
                <div className="relative flex h-24 w-full items-center overflow-hidden">
                  <div className="absolute left-0 right-0 top-[60%] h-1 -translate-y-1/2 rounded-full bg-white/40" />
                  <div
                    className="pointer-events-none absolute top-[60%] z-[2] w-max"
                    style={{ animation: 'login-train-scroll 4s linear infinite' }}
                  >
                    {/* Native sprite is engine + coach (nose left). Append a cropped, mirrored
                        engine so the consist reads rear engine — coach — leading engine. */}
                    <div className="flex items-center">
                      <img
                        src="/TractionHeaderImage.png"
                        alt=""
                        className="block h-14 w-auto max-w-none select-none object-contain object-left drop-shadow-md"
                      />
                      <span
                        className="block drop-shadow-md"
                        style={{ transform: 'scaleX(-1)' }}
                        aria-hidden="true"
                      >
                        {/* Crop to leading power car only (sprite cols 0–301 of 600). */}
                        <span
                          className="block h-14 overflow-hidden"
                          style={{ width: 'calc(3.5rem * 302 / 75)' }}
                        >
                          <img
                            src="/TractionHeaderImage.png"
                            alt=""
                            className="block h-14 w-auto max-w-none select-none object-contain object-left"
                          />
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <p className="max-w-[22rem] text-center text-balance text-lg font-semibold leading-snug text-white sm:max-w-none sm:text-xl">
                  {postSignInContextHydrating && !isLoggingIn
                    ? translateUi("Preparing your company setup…")
                    : translateUi("You are now arriving into company dashboard")}
                </p>
              </div>
              <style>{`
                @keyframes login-train-scroll {
                  0% { left: 0; transform: translateY(-100%) translateX(-100%); }
                  100% { left: 100%; transform: translateY(-100%) translateX(0); }
                }
              `}</style>
            </div>
          )}
          </div>

          {forgotPasswordOpen && (
            <div className="absolute inset-0 bg-white flex items-center justify-center p-4 sm:p-8 z-30 rounded-2xl sm:rounded-3xl overflow-y-auto">
              <div className="w-full max-w-sm space-y-4 py-2">
                <h3 className="text-lg font-bold text-slate-900">{translateUi("Reset password")}</h3>
                {forgotSent ? (
                  <>
                    <p className="text-sm text-slate-600">{translateUi("If an account exists for that email, we've sent a password reset link. Check your inbox and spam folder.")}</p>
                    <button onClick={() => setForgotPasswordOpen(false)} className="w-full py-2 bg-indigo-600 text-white rounded-xl"> {translateUi("Close")} </button>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Company Code")}</label>
                      <input
                        type="text"
                        value={forgotForm.companyCode}
                        onChange={e => setForgotForm({ ...forgotForm, companyCode: e.target.value })}
                        enterKeyHint="next"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        className="w-full min-h-11 px-4 py-2.5 sm:py-3 text-base text-slate-900 caret-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-xl"
                        placeholder="e.g. RV-123"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{translateUi("Email Address")}</label>
                      <input
                        type="email"
                        value={forgotForm.email}
                        onChange={e => setForgotForm({ ...forgotForm, email: e.target.value })}
                        enterKeyHint="go"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        className="w-full min-h-11 px-4 py-2.5 sm:py-3 text-base text-slate-900 caret-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-xl"
                        placeholder="your@email.com"
                      />
                    </div>
                    {forgotError && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                        {forgotError}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          setForgotLoading(true);
                          setForgotError(null);
                          try {
                            const res = await fetch("/api/forgot-password", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify(forgotForm),
                            });
                            const data = await res.json().catch(() => ({}));
                            if (!res.ok) {
                              setForgotError(data.error || "Could not send reset link. Please check that email is configured.");
                              setForgotLoading(false);
                              return;
                            }
                            setForgotSent(true);
                          } catch (err) {
                            setForgotError("Could not send reset link. Please try again.");
                          }
                          setForgotLoading(false);
                        }}
                        disabled={!forgotForm.email || forgotLoading}
                        className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-medium disabled:opacity-50"
                      >
                        {forgotLoading ? translateUi("Sending…") : translateUi("Send reset link")}
                      </button>
                      <button onClick={() => setForgotPasswordOpen(false)} className="px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-xl"> {translateUi("Cancel")} </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <footer className="fixed bottom-0 left-0 right-0 z-[2] border-t border-white/10 bg-slate-950/80 backdrop-blur-md">
          <div className="rv-login-footer-inner mx-auto max-w-5xl px-4 py-2 text-center pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-2">
            <p className="rv-login-footer-copy text-xs text-slate-400">
              © {new Date().getFullYear()} Rail Intel. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <React.Fragment key={uiLocale}>
      {mandatoryTempPasswordGate && user && (
        <div
          className="fixed inset-0 z-[25000] flex items-center justify-center bg-slate-900/80 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="rv-mandatory-pw-heading"
        >
          <div className={cn("w-full max-w-md rounded-3xl shadow-xl border p-6 sm:p-8 space-y-5", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
            <div>
              <h2 id="rv-mandatory-pw-heading" className={cn("text-xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                {translateUi("Set your password")}
              </h2>
              <p className={cn("text-sm mt-2 leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                You signed in with a temporary password from your welcome email. Choose a secure new password before
                using Rail Intel.
                {isNominatedCompanyAdmin
                  ? translateUi(" You will then see a short guide to your next steps as company administrator.")
                  : translateUi(" After this you can complete remaining company setup from Administration.")}
              </p>
            </div>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void (async () => {
                  if (!user?.email) return;
                  setMandatoryPwError(null);
                  const { newPassword, confirmPassword } = mandatoryPwForm;
                  if (newPassword.length < 8) {
                    setMandatoryPwError("Password must be at least 8 characters");
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    setMandatoryPwError(translateUi("Passwords do not match"));
                    return;
                  }
                  setMandatoryPwSaving(true);
                  try {
                    const tempPw = loginForm.password || "";
                    const res = await fetch("/api/users/change-password", {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "X-User-Email": user.email },
                      body: JSON.stringify({
                        email: user.email,
                        currentPassword: tempPw,
                        newPassword,
                      }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.error || "Could not update password");
                    const cc = (apiCompanyCode || loginForm.companyCode || "").trim();
                    await fetch("/api/users/me/password-changed", {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "X-User-Email": user.email },
                      body: JSON.stringify({ companyCode: cc || undefined }),
                    });
                    setMandatoryPwForm({ newPassword: "", confirmPassword: "" });
                    setMandatoryTempPasswordGate(false);
                    setActiveTab("dashboard");
                    try {
                      sessionStorage.setItem("rv_active_tab", "dashboard");
                    } catch {
                      /* ignore */
                    }
                    const ccAfterPw = (apiCompanyCode || loginForm.companyCode || "").trim();
                    const showNomineeWelcomeOrTour = () => {
                      if (!ccAfterPw) return;
                      let welcomeSeen = false;
                      let tourSeen = false;
                      try {
                        welcomeSeen = localStorage.getItem(nominatedAdminWelcomeStorageKey(ccAfterPw)) === "1";
                        tourSeen = localStorage.getItem(nominatedAdminNavTourStorageKey(ccAfterPw)) === "1";
                      } catch {
                        setShowNominatedAdminWelcome(true);
                        return;
                      }
                      if (!welcomeSeen) setShowNominatedAdminWelcome(true);
                      else if (!tourSeen) setPendingNomineeNavTour(true);
                    };
                    if (isNominatedCompanyAdmin) {
                      showNomineeWelcomeOrTour();
                    } else if (ccAfterPw && user.email) {
                      fetch(`/api/companies/${encodeURIComponent(ccAfterPw)}/nominate-company-admin`, {
                        headers: { "X-User-Email": user.email },
                      })
                        .then((r) => (r.ok ? r.json() : null))
                        .then((data: { adminEmail?: string } | null) => {
                          const ne = typeof data?.adminEmail === "string" ? data.adminEmail.trim().toLowerCase() : "";
                          if (ne && ne === user.email!.trim().toLowerCase()) showNomineeWelcomeOrTour();
                        })
                        .catch(() => {});
                    }
                  } catch (err: unknown) {
                    const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "";
                    setMandatoryPwError(
                      msg.includes("requires-recent-login")
                        ? "Session issue: sign out and sign in again with your temporary password, then set a new one."
                        : msg || "Could not update password"
                    );
                  } finally {
                    setMandatoryPwSaving(false);
                  }
                })();
              }}
            >
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("New password")}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={mandatoryPwForm.newPassword}
                  onChange={(e) => setMandatoryPwForm((p) => ({ ...p, newPassword: e.target.value }))}
                  className={cn("w-full px-4 py-2.5 rounded-xl border", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-200 text-slate-900")}
                />
              </div>
              <div>
                <label className={cn("block text-xs font-bold uppercase tracking-wider mb-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Confirm password")}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={mandatoryPwForm.confirmPassword}
                  onChange={(e) => setMandatoryPwForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                  className={cn("w-full px-4 py-2.5 rounded-xl border", isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-200 text-slate-900")}
                />
              </div>
              {mandatoryPwError && <div className={cn("text-sm", isDarkMode ? "text-red-400" : "text-red-600")}>{mandatoryPwError}</div>}
              <button
                type="submit"
                disabled={mandatoryPwSaving}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {mandatoryPwSaving ? translateUi("Updating…") : translateUi("Save password and continue")}
              </button>
            </form>
            <button type="button" onClick={() => void handleLogout()} className={cn("w-full text-sm", isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-600 hover:text-slate-900")}>
              {translateUi("Sign out instead")}
            </button>
          </div>
        </div>
      )}
      <style>{APP_CHROME_STYLES}</style>
      <div
        ref={appChromeRef}
        className={cn(
          "rv-app-chrome h-screen flex overflow-hidden relative min-w-0 w-full",
          isCompactDesktop && "rv-compact-desktop",
          administrationFlyoutUsesContinuity && "rv-admin-flyout-open",
          isDarkMode && "rv-dark",
          showActivationSuccessScreen
            ? (isDarkMode ? "bg-slate-900" : "bg-slate-100")
            : useOnboardingWizardBackdrop
              ? (isDarkMode ? "bg-slate-900" : "bg-cover bg-center")
              : isDarkMode
                ? "bg-slate-900 text-slate-100"
                : "bg-slate-50"
        )}
        style={useOnboardingWizardBackdrop && !isDarkMode ? { backgroundImage: "url('/onboarding-bg.png')" } : undefined}
      >
      {maintenanceBlocksSignedInUser && (
        <div className="fixed inset-0 z-[100] bg-slate-900 flex items-center justify-center px-4">
          <MaintenanceDowntimeView
            message={
              maintenanceStatus?.message ||
              "Rail Intel is temporarily unavailable for scheduled maintenance. Please try again later."
            }
            onLogout={async () => {
              setMaintenanceLogoutBusy(true);
              try {
                await handleLogout();
              } finally {
                setMaintenanceLogoutBusy(false);
              }
            }}
            loggingOut={maintenanceLogoutBusy}
          />
        </div>
      )}
      {effectiveIsSystemAdmin && maintenanceStatus?.enabled && !maintenanceBlocksSignedInUser && (
        <div className="absolute top-0 left-0 right-0 z-[60]">
          <MaintenanceActiveBanner onManage={() => setActiveTab("announcements")} />
        </div>
      )}
      {/* In-app background – subtle GBR map behind dashboard / chrome content */}
      {!isOnboardingLocked && !showActivationSuccessScreen && (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: "url('/GBR_BG.png')", opacity: isDarkMode ? 0.14 : 0.12 }}
          aria-hidden="true"
        />
      )}
      {useOnboardingWizardBackdrop && (
        <div className="absolute inset-0 bg-slate-900/80 pointer-events-none" aria-hidden="true" />
      )}
      {/* Mobile/tablet backdrop when sidebar overlay is open */}
      {!isOnboardingLocked && isMobileOrTablet && isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar - overlay on mobile/tablet (right side), original inline on desktop (left) */}
      {!isOnboardingLocked && !appShellBlockedForResolve && (
      <aside
        className={cn(
          isMobileOrTablet && "rv-main-sidebar-shell",
          !isMobileOrTablet && "rv-main-sidebar-host rv-main-sidebar rv-gbr-chrome-panel shrink-0 h-full",
          !isMobileOrTablet && (isSidebarOpen ? "w-64" : "w-20"),
          administrationFlyoutUsesContinuity && "rv-gbr-chrome-delegated rv-sidebar-flyout-open"
        )}
      >
        <div
          ref={mainSidebarRef}
          style={
            isMobileOrTablet
              ? !isSidebarOpen
                ? { display: 'none', transform: 'translateX(100%)' }
                : {
                    display: 'flex',
                    transform: 'translateX(0)',
                    transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }
              : undefined
          }
          className={cn(
            "rv-main-sidebar rv-gbr-chrome-panel text-white flex flex-col min-h-0",
            isMobileOrTablet
              ? "rv-main-sidebar--mobile-end fixed inset-y-0 right-0 z-[55] h-full w-64 max-w-[85vw]"
              : "h-full w-full translate-x-0 transition-all duration-300",
            administrationFlyoutUsesContinuity && "rv-gbr-chrome-delegated rv-sidebar-flyout-open"
          )}
          aria-hidden={isMobileOrTablet && !isSidebarOpen}
        >
        <div
          className={cn(
            "flex items-center shrink-0",
            sidebarChromeExpanded
              ? "p-4 lg:p-6 justify-between lg:justify-start lg:space-x-3"
              : "p-3 justify-center"
          )}
        >
          <div className={cn("flex items-center", sidebarChromeExpanded ? "space-x-3" : "justify-center")}>
            <div className="p-0 shrink-0 flex items-center justify-center">
              <img
                src="/rail-intel-icon.png?v=18"
                alt={translateUi("Rail Intel")}
                className={cn("object-contain", sidebarChromeExpanded ? "w-16 h-16" : "w-14 h-14")}
              />
            </div>
            {sidebarChromeExpanded && (
              <div className="flex flex-col">
                <span className="font-bold text-lg tracking-tight leading-none">
                  {translateUi("Rail Intel")} CMS
                </span>
                <span className="text-[10px] text-slate-400 font-medium tracking-wider uppercase mt-0.5">{translateUi("by railintel.co.uk")}</span>
              </div>
            )}
          </div>
          {isMobileOrTablet && (
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 -mr-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors" aria-label={translateUi("Close menu")}>
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <PlatformClock isSidebarOpen={sidebarChromeExpanded} />

        <nav className={cn("rv-main-nav flex-1 py-6 space-y-2 overflow-y-auto", sidebarChromeExpanded ? "px-4" : "px-2")}>
          {!canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <button
              type="button"
              data-nominee-tour="dashboard"
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('dashboard');
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'dashboard' && !selectedEmployeeId ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <LayoutDashboard className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Dashboard")}</span>}
            </button>
          )}
          
          {!canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <button
              type="button"
              data-nominee-tour="employees"
              onClick={() => {
                setSelectedEmployeeId(null);
                setEmployeeFilter('all');
                setActiveTab('employees');
                setIsAdministrationOpen(false);
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'employees' && !selectedEmployeeId ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <Users className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Employees")}</span>}
            </button>
          )}

          {canAccessMedicals && !canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <button
              type="button"
              title={translateUi("Medicals")}
              aria-label={translateUi("Medicals")}
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('medicals');
                setIsAdministrationOpen(false);
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'medicals' ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <Stethoscope className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Medicals")}</span>}
            </button>
          )}

          {canAccessLicencing && !canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <button
              type="button"
              title={translateUi("Licencing")}
              aria-label={translateUi("Licencing")}
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('licencing');
                setIsAdministrationOpen(false);
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px] text-base",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'licencing' ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <IdCard className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span className="text-base">{translateUi("Licencing")}</span>}
            </button>
          )}

          {canViewOwnEmployeeOnly && !effectiveIsSystemAdmin && !isImpersonationPermissionLoading && (
            <button
              type="button"
              onClick={() => {
                const own = findOwnEmployeeForProfile(employees, effectiveProfile, user?.email);
                if (own?.id) {
                  setSelectedEmployeeId(own.id);
                  setSelectedEmployeeCompanyCode(own.company_code || undefined);
                }
                setActiveTab("employees");
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === "employees" && selectedEmployeeId
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <User className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("My record")}</span>}
            </button>
          )}

          {canViewOwnEmployeeOnly && canSelfServeTraineeDriver && !isImpersonationPermissionLoading && (
            <button
              type="button"
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab("trainee-driver");
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === "trainee-driver"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <GraduationCap className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("My training")}</span>}
            </button>
          )}

          {canAccessIncidents && !canViewOwnEmployeeOnly && (
            <button
              type="button"
              data-nominee-tour="incidents"
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('incidents');
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'incidents' ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <ExclamationTriangle className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Incidents")}</span>}
            </button>
          )}

          {!canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <button
              type="button"
              data-nominee-tour="reporting"
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('reporting');
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === 'reporting' ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <BarChart3 className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Reporting")}</span>}
            </button>
          )}

          {!canViewOwnEmployeeOnly && !isImpersonationPermissionLoading && (
            <div className="pt-4 border-t border-slate-800/50">
              <button
                type="button"
                data-nominee-tour="administration"
                ref={administrationToggleRef}
                title={translateUi("Administration")}
                aria-label={translateUi("Administration")}
                aria-expanded={isAdministrationOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = !isAdministrationOpen;
                  if (willOpen && !isMobileOrTablet) {
                    // Flush layout before paint so the portal is not gated on a null layout.
                    flushSync(() => {
                      updateAdministrationFlyoutPosition();
                    });
                  }
                  setIsAdministrationOpen(willOpen);
                }}
                className={cn(
                  "w-full flex items-center text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors",
                  sidebarChromeExpanded ? "justify-between px-4 py-3" : "justify-center px-0 py-3",
                  isAdministrationOpen && "bg-slate-800 text-white"
                )}
              >
                <div className={cn("flex items-center", sidebarChromeExpanded ? "space-x-3" : "justify-center")}>
                  <Settings className="w-5 h-5 shrink-0" />
                  {sidebarChromeExpanded && <span>{translateUi("Administration")}</span>}
                </div>
                {sidebarChromeExpanded && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          )}
        </nav>
        {isAdministrationOpen &&
          !canViewOwnEmployeeOnly &&
          typeof document !== "undefined" &&
          createPortal(
          <>
            {administrationFlyoutUsesContinuity && administrationFlyoutLayout && (
              <div
                className="rv-gbr-continuity-layer rv-gbr-chrome-panel"
                style={{
                  left: administrationFlyoutLayout.continuityLeft,
                  top: administrationFlyoutLayout.top,
                  width: administrationFlyoutLayout.continuityWidth,
                  height: administrationFlyoutLayout.height,
                }}
                aria-hidden="true"
              />
            )}
            {(isMobileOrTablet || !isSidebarOpen) && (
              <div
                className={cn(
                  "rv-administration-backdrop fixed inset-0 z-[9998]",
                  isDarkMode ? "bg-slate-900/65" : "bg-slate-900/35"
                )}
                onClick={() => setIsAdministrationOpen(false)}
                aria-hidden="true"
              />
            )}
            <div
              ref={administrationPanelRef}
              className={cn(
                "rv-administration-flyout rv-gbr-chrome-panel fixed z-[10001]",
                administrationFlyoutUsesContinuity && "rv-gbr-chrome-delegated",
                isMobileOrTablet
                  ? "inset-x-3 top-20 bottom-3 rounded-2xl border border-slate-700/80 shadow-2xl shadow-black/40"
                  : cn(
                      "w-80",
                      isSidebarOpen
                        ? "rounded-r-2xl rounded-l-none"
                        : "rounded-2xl border border-slate-700/80 shadow-2xl shadow-black/50"
                    )
              )}
              style={
                !isMobileOrTablet
                  ? {
                      left: administrationFlyoutLayout?.left ?? 80,
                      top: administrationFlyoutLayout?.top ?? 0,
                      height: administrationFlyoutLayout?.height ?? "100vh",
                    }
                  : undefined
              }
            >
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/80">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-500">{translateUi("Administration")}</p>
                  <p className="text-sm font-semibold text-slate-200">{translateUi("Menu")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAdministrationOpen(false)}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  aria-label={translateUi("Close administration menu")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                <button
                  type="button"
                  data-nominee-tour="admin-team"
                  onClick={() => {
                    setSelectedEmployeeId(null);
                    setActiveTab('team');
                    closeMobileSidebar();
                    setIsAdministrationOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                    activeTab === 'team' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                  )}
                >
                  <Shield className="w-4 h-4 shrink-0" />
                  <span>{translateUi("Team Management")}</span>
                </button>
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('train-source-settings');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'train-source-settings' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <TrainFront className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Train source settings")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('app-languages');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'app-languages' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Flag className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Languages")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('security');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'security' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Lock className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Security")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('announcements');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'announcements' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Mail className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Announcements")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab("cycle-builder");
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === "cycle-builder"
                        ? "text-indigo-400 bg-slate-800/70"
                        : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Wrench className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Cycle Builder")}</span>
                  </button>
                )}
                {canViewQAVerificationsHistory && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('qa-verifications');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'qa-verifications' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <FileCheck className="w-4 h-4 shrink-0" />
                    <span>{translateUi("QA Verifications")}</span>
                  </button>
                )}
                {canAccessTasks && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('tasks');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'tasks' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <ClipboardList className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Tasks")}</span>
                  </button>
                )}
                {canAccessSafetyBriefs && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('safety-briefs');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'safety-briefs' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Shield className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Safety Briefs")}</span>
                  </button>
                )}
                {canAccessTraineeDriver && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('trainee-driver');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'trainee-driver' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <GraduationCap className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Trainee")}</span>
                  </button>
                )}
                {canAccessLeaveAbsence && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('leave-absence');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'leave-absence' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Leave & Absence")}</span>
                  </button>
                )}
                {canSeeStorageAdministrationSection && (
                <div className="pt-3 mt-2 border-t border-slate-800/80">
                  <button
                    type="button"
                    onClick={() => setIsAdministrationStorageOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-[11px] font-semibold tracking-widest uppercase text-slate-500 hover:bg-slate-800/50 transition-colors"
                    aria-expanded={isAdministrationStorageOpen}
                  >
                    <span>{translateUi("Storage")}</span>
                    {isAdministrationStorageOpen ? (
                      <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 shrink-0 text-slate-500" />
                    )}
                  </button>
                  {isAdministrationStorageOpen && (
                    <div className="space-y-1 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab("addons-storage");
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === "addons-storage"
                            ? "text-indigo-400 bg-slate-800/70"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Archive className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Storage & plans")}</span>
                      </button>
                    </div>
                  )}
                </div>
                )}

                {canSeeCompanyConfigurationSection && (
                  <div className="pt-3 mt-2 border-t border-slate-800/80">
                    <button
                      type="button"
                      onClick={() => setIsAdministrationCompanyConfigOpen((o) => !o)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-[11px] font-semibold tracking-widest uppercase text-slate-500 hover:bg-slate-800/50 transition-colors"
                      aria-expanded={isAdministrationCompanyConfigOpen}
                    >
                      <span>{translateUi("Company configuration")}</span>
                      {isAdministrationCompanyConfigOpen ? (
                        <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 shrink-0 text-slate-500" />
                      )}
                    </button>
                    {isAdministrationCompanyConfigOpen && (
                      <div className="space-y-1 pt-1">
                    {canManageCustomJobRoles && (
                      <button
                        type="button"
                        data-nominee-tour="custom-job-roles"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('custom-job-roles');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'custom-job-roles' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Briefcase className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Custom Job Roles")}</span>
                      </button>
                    )}
                    {canManageGradingScale && (
                      <button
                        type="button"
                        data-nominee-tour="grading-scale"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('grading-scale');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'grading-scale' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Award className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Grading Scale")}</span>
                      </button>
                    )}
                    {effectiveIsAdmin && !effectiveIsSystemAdmin && apiCompanyCode && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab("company-email-templates");
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === "company-email-templates"
                            ? "text-indigo-400 bg-slate-800/70"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Mail className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Company email templates")}</span>
                      </button>
                    )}
                    {(effectiveIsAdmin || effectiveIsSystemAdmin || canManageFramework) && (
                      <button
                        data-nominee-tour="framework"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('cycle-builder');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'cycle-builder' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Wrench className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Cycle Builder")}</span>
                      </button>
                    )}
                    {canManageCompanyStandards && (
                      <button
                        type="button"
                        data-nominee-tour="company-standards"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('company-standards');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'company-standards' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <ClipboardList className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Company Standards")}</span>
                      </button>
                    )}
                    {canManageTractionRoutes && (
                      <button
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('traction-routes');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'traction-routes' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <MapIcon className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Traction, Routes & Depots")}</span>
                      </button>
                    )}
                    {effectiveIsAdmin && effectiveCompany && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('org-structure');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'org-structure' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Users className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Organisation Structure")}</span>
                      </button>
                    )}
                    {effectiveIsAdmin && apiCompanyCode && !impersonatedUser && !effectiveIsSystemAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab("investigations-api-management");
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === "investigations-api-management"
                            ? "text-indigo-400 bg-slate-800/70"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <KeyRound className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Investigations module link")}</span>
                      </button>
                    )}
                    {effectiveIsAdmin && (
                      <button
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab('role-permissions');
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === 'role-permissions' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Shield className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Role Permissions")}</span>
                      </button>
                    )}
                    {effectiveIsAdmin && effectiveCompany && !effectiveIsSystemAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEmployeeId(null);
                          setActiveTab("company-logo");
                          closeMobileSidebar();
                          setIsAdministrationOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                          activeTab === "company-logo"
                            ? "text-indigo-400 bg-slate-800/70"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                      >
                        <Upload className="w-4 h-4 shrink-0" />
                        <span>{translateUi("Company logo")}</span>
                      </button>
                    )}
                      </div>
                    )}
                  </div>
                )}

                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('migration-center');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'migration-center' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <ArrowLeftRight className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Migration Center")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('sys-logs');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'sys-logs' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <FileText className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Sys Logs")}</span>
                  </button>
                )}
                {/* Agent/system Support Desk only — company admins use Help & Support for tickets. */}
                {(effectiveIsSystemAdmin || (canAccessSupportDesk && !effectiveIsAdmin)) && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('support-desk');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'support-desk' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <MessageCircle className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Support Desk")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('email-templates');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'email-templates' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Mail className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Email templates")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('trainee-driver-addon');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'trainee-driver-addon' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <GraduationCap className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Trainee Addon")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('investigations-api-management');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'investigations-api-management' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <KeyRound className="w-4 h-4 shrink-0" />
                    <span>{translateUi("API management")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('company-billing');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'company-billing' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Briefcase className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Company billing")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('company-storage');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'company-storage' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Archive className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Company storage")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('new-company');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'new-company' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Shield className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Company registration")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('onboarding');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'onboarding' ? "text-indigo-400 bg-slate-800/70" : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <ClipboardList className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Onboarding")}</span>
                  </button>
                )}
                {effectiveIsSystemAdmin && (
                  <button
                    onClick={() => {
                      setSelectedEmployeeId(null);
                      setActiveTab('addons-management');
                      closeMobileSidebar();
                      setIsAdministrationOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2 rounded-xl transition-colors text-sm min-h-[44px]",
                      activeTab === 'addons-management' ||
                      activeTab === 'task-module-settings' ||
                      activeTab === 'safety-brief-module-settings' ||
                      activeTab === 'qa-verifications-module-settings' ||
                      activeTab === 'trainee-driver-module-settings' ||
                      activeTab === 'driver-reports-module-settings' ||
                      activeTab === 'leave-absence-module-settings' ||
                      activeTab === 'medication-checks-module-settings' ||
                      activeTab === 'company-admin-licences-module-settings'
                        ? "text-indigo-400 bg-slate-800/70"
                        : "text-slate-300 hover:text-white hover:bg-slate-800"
                    )}
                  >
                    <Package className="w-4 h-4 shrink-0" />
                    <span>{translateUi("Addons Management")}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          </>,
          // Collapsed icon-rail: mount on body so overflow:hidden on .rv-app-chrome cannot clip the panel.
          // Expanded: keep under app chrome so shared GBR continuity CSS variables apply.
          administrationFlyoutUsesContinuity && appChromeRef.current
            ? appChromeRef.current
            : document.body
        )}

        <div className="rv-main-sidebar-footer p-4 border-t border-slate-800 flex flex-col space-y-2 shrink-0">
          <button 
            onClick={() => {
              setSelectedEmployeeId(null);
              setActiveTab('help-support');
              closeMobileSidebar();
            }}
            className={cn(
              "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
              sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
              activeTab === 'help-support' ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
            )}
          >
            <HelpCircle className="w-5 h-5 shrink-0" />
            {sidebarChromeExpanded && <span>{translateUi("Help & Support")}</span>}
          </button>
          {(effectiveIsAdmin || effectiveIsSystemAdmin) && (
            <button 
              onClick={() => {
                setSelectedEmployeeId(null);
                setActiveTab('addons');
                closeMobileSidebar();
              }}
              className={cn(
                "w-full flex items-center rounded-xl transition-colors min-h-[44px]",
                sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center",
                activeTab === "addons"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <Package className="w-5 h-5 shrink-0" />
              {sidebarChromeExpanded && <span>{translateUi("Addons")}</span>}
            </button>
          )}
          
          <button 
            onClick={() => { handleLogout(); closeMobileSidebar(); }}
            className={cn(
              "w-full flex items-center rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors min-h-[44px]",
              sidebarChromeExpanded ? "space-x-3 px-4 py-3 justify-start" : "px-0 py-3 justify-center"
            )}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {sidebarChromeExpanded && <span>{translateUi("Logout")}</span>}
          </button>
          
          {!isMobileOrTablet && (
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-all border border-slate-700/50 group mt-auto"
          >
            {isSidebarOpen && <span className="text-xs font-bold uppercase tracking-widest ml-2">{translateUi("Collapse Menu")}</span>}
            <div className={cn(
              "p-1.5 rounded-lg bg-indigo-600 text-white shadow-lg shadow-indigo-500/20 transition-transform",
              !isSidebarOpen && "mx-auto"
            )}>
              {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </div>
          </button>
          )}
        </div>
        </div>
      </aside>
      )}

      {/* Main Content */}
      <main
          className={cn(
            "rv-main-content flex-1 overflow-y-auto overflow-x-hidden min-w-0 w-full max-w-full overscroll-x-none [scrollbar-gutter:stable]",
            showSetupWizard && "touch-pan-y",
            showSetupWizard && isDarkMode && "bg-slate-900",
            showActivationSuccessScreen && (isDarkMode ? "relative z-10 bg-slate-900" : "relative z-10 bg-slate-100")
          )}
        >
        {!isOnboardingLocked && !appShellBlockedForResolve && (
        <header className={cn(
          "rv-main-header border-b sticky top-0 z-40",
          isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
          isTablet
            ? "px-4 sm:px-6 py-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 sm:gap-x-3 gap-y-3"
            : isMobileOrTablet
            ? "px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            : "px-8 py-4 flex items-center justify-between"
        )}>
          <div className={cn(
            "flex items-center",
            isTablet ? "gap-2 sm:gap-3 min-w-0" : isMobileOrTablet ? "gap-2 sm:gap-4 min-w-0 flex-1" : "space-x-4"
          )}>
            {isMobileOrTablet && (
              <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={cn("p-2 rounded-lg shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center", isDarkMode ? "hover:bg-slate-800 text-slate-200" : "hover:bg-slate-100")} aria-label={translateUi("Open menu")}>
                <Menu className="w-5 h-5" />
              </button>
            )}
            <form onSubmit={handleSearch} className={cn("relative", isMobileOrTablet ? "flex-1 min-w-0 max-w-xs sm:max-w-none" : "", isTablet && "max-w-none")}>
              <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", isDarkMode ? "text-slate-500" : "text-slate-400")} />
              <input 
                type="text" 
                placeholder={translateUi("Search name, ID, role...")} 
                className={cn(
                  "pl-10 pr-4 py-2 border-transparent focus:ring-2 focus:ring-indigo-500 rounded-xl text-sm transition-all",
                  isDarkMode ? "bg-slate-800 text-slate-100 focus:bg-slate-800" : "bg-slate-100 focus:bg-white",
                  isMobileOrTablet ? (isTablet ? "w-full" : "w-full sm:w-48") : "w-64"
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
            <div className={cn(isTablet ? "hidden" : isMobileOrTablet ? "hidden md:block shrink-0 text-sm font-medium" : "ml-4 text-sm font-medium", isDarkMode ? "text-slate-300" : "text-slate-500")}>
              {formatHeaderDateUK(new Date())}
            </div>
          </div>
          <div className={cn(
            isTablet
              ? "contents"
              : "flex items-center gap-3 shrink-0 flex-wrap justify-end"
          )}>
            {(impersonatedUser || isSystemAdmin) && (
            <div className={cn(
              isMobileOrTablet && "w-full min-w-0 basis-full order-first",
              isTablet && "col-span-2 order-last",
            )}>
            {impersonatedUser && (
              <div ref={impersonationMenuRef} className={cn("relative", isMobileOrTablet && "w-full")}>
                <button
                  onClick={() => {
                    const willOpen = !impersonationOpen;
                    setImpersonationOpen(willOpen);
                    if (willOpen) requestAnimationFrame(updateImpersonationMenuPosition);
                  }}
                  className={cn(
                    "login-as-btn flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200",
                    isMobileOrTablet && "w-full min-h-[48px] justify-between text-base",
                    isDarkMode
                      ? "shadow-lg"
                      : "bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200/80 text-violet-900 shadow-sm hover:shadow-md hover:from-violet-100 hover:to-purple-100",
                  )}
                >
                  <div
                    className={cn(
                      "login-as-btn__icon-wrap w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                      isDarkMode ? "bg-violet-500/25" : "bg-violet-200/60",
                    )}
                  >
                    <User className={cn("w-4 h-4", isDarkMode ? "text-violet-50" : "text-violet-700")} />
                  </div>
                  <span className={cn("min-w-0 truncate", isMobileOrTablet ? "flex-1 text-left" : "max-w-[100px] sm:max-w-[180px] md:max-w-none")}>{translateUi("Viewing as")} {impersonatedUser.full_name}</span>
                  <ChevronDown className={cn("login-as-btn__chevron w-4 h-4 shrink-0 transition-transform", isDarkMode ? "text-violet-100" : "text-violet-600", impersonationOpen && "rotate-180")} />
                </button>
                {impersonationOpen && impersonationMenuPosition && createPortal(
                  <div
                    ref={impersonationMenuPanelRef}
                    className={cn(
                      "fixed py-2 border rounded-xl shadow-xl z-[120] min-w-[220px] max-w-[calc(100vw-2rem)] ring-1",
                      isMobileOrTablet && "min-w-0 max-w-none",
                      isDarkMode ? "bg-slate-900 border-slate-600 ring-slate-700/50" : "bg-white border-slate-200 ring-slate-900/5",
                    )}
                    style={isMobileOrTablet
                      ? { top: `${impersonationMenuPosition.top}px`, left: `${impersonationMenuPosition.left}px`, width: `${Math.max(impersonationMenuPosition.width, 0)}px`, right: "auto" }
                      : { top: `${impersonationMenuPosition.top}px`, right: `${impersonationMenuPosition.right}px`, left: "auto" }}
                  >
                    <button
                      onClick={() => {
                        handleExitUserImpersonation();
                        setImpersonationOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                        isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-50",
                      )}
                    >
                      <LogOut className={cn("w-4 h-4", isDarkMode ? "text-slate-400" : "text-slate-500")} />
                      <span className="font-medium">{translateUi("Exit user view")}</span>
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            )}
            {isSystemAdmin && !impersonatedUser && (
              <div ref={impersonationMenuRef} className={cn("relative", isMobileOrTablet && "w-full")}>
                {impersonatedCompany ? (
                  <button
                    onClick={() => {
                      const willOpen = !impersonationOpen;
                      setImpersonationOpen(willOpen);
                      if (willOpen) {
                        fetchCompaniesForImpersonation();
                        requestAnimationFrame(updateImpersonationMenuPosition);
                      }
                    }}
                    className={cn(
                      "impersonate-company-btn impersonate-company-btn--active flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200",
                      isMobileOrTablet && "w-full min-h-[48px] justify-between text-base",
                      isDarkMode
                        ? "shadow-lg"
                        : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 text-amber-900 shadow-sm hover:shadow-md hover:from-amber-100 hover:to-orange-100",
                    )}
                  >
                    <div className="impersonate-company-btn__icon-wrap w-8 h-8 rounded-lg bg-amber-200/60 flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4 text-amber-700" />
                    </div>
                    <span className={cn("min-w-0 truncate", isMobileOrTablet ? "flex-1 text-left" : "max-w-[100px] sm:max-w-[180px] md:max-w-none")}>{translateUi("Viewing as")} {impersonatedCompany.name || impersonatedCompany.id}</span>
                    <ChevronDown className={clsx("impersonate-company-btn__chevron w-4 h-4 shrink-0 text-amber-600 transition-transform", impersonationOpen && "rotate-180")} />
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const willOpen = !impersonationOpen;
                      setImpersonationOpen(willOpen);
                      if (willOpen) {
                        fetchCompaniesForImpersonation();
                        requestAnimationFrame(updateImpersonationMenuPosition);
                      }
                    }}
                    className={cn(
                      "impersonate-company-btn flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200",
                      isMobileOrTablet && "w-full min-h-[48px] justify-between text-base",
                      isDarkMode
                        ? "shadow-md"
                        : "bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200/80 text-indigo-700 shadow-sm hover:shadow-md hover:from-indigo-100 hover:to-violet-100",
                    )}
                  >
                    <div className="impersonate-company-btn__icon-wrap w-8 h-8 rounded-lg bg-indigo-200/60 flex items-center justify-center shrink-0">
                      <ArrowLeftRight className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className={cn("min-w-0 truncate whitespace-nowrap", isMobileOrTablet && "flex-1 text-left")}>{translateUi("Login as company")}</span>
                    <ChevronDown className={clsx("impersonate-company-btn__chevron w-4 h-4 shrink-0 text-indigo-500 transition-transform", impersonationOpen && "rotate-180")} />
                  </button>
                )}
                {impersonationOpen && impersonationMenuPosition && createPortal(
                  <div
                    ref={impersonationMenuPanelRef}
                    className={cn(
                      "fixed py-2 border rounded-xl shadow-xl z-[120] min-w-[260px] max-w-[calc(100vw-2rem)] max-h-80 overflow-hidden ring-1",
                      isMobileOrTablet && "min-w-0 max-w-none",
                      isDarkMode ? "bg-slate-900 border-slate-600 ring-slate-700/50" : "bg-white border-slate-200 ring-slate-900/5",
                    )}
                    style={isMobileOrTablet
                      ? { top: `${impersonationMenuPosition.top}px`, left: `${impersonationMenuPosition.left}px`, width: `${Math.max(impersonationMenuPosition.width, 0)}px`, right: "auto" }
                      : { top: `${impersonationMenuPosition.top}px`, right: `${impersonationMenuPosition.right}px`, left: "auto" }}
                  >
                    <div className={cn("px-3 py-2.5 border-b", isDarkMode ? "bg-slate-800/80 border-slate-700" : "bg-slate-50 border-slate-100")}>
                      <p className={cn("text-xs font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-300" : "text-slate-500")}>
                        {impersonatedCompany ? translateUi("Switch company") : translateUi("Choose company")}
                      </p>
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      {impersonatedCompany && (
                        <button
                          onClick={() => {
                            setImpersonatedCompany(null);
                            setImpersonationOpen(false);
                            persistStep('job-roles');
                          }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                            isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-50",
                          )}
                        >
                          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isDarkMode ? "bg-slate-700" : "bg-slate-200")}>
                            <Shield className={cn("w-4 h-4", isDarkMode ? "text-slate-300" : "text-slate-500")} />
                          </div>
                          <span className="font-medium">{translateUi("Switch back to admin view")}</span>
                        </button>
                      )}
                      {companiesLoadingForImpersonation ? (
                        <p className={cn("px-4 py-4 text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Loading companies…")}</p> ) : companiesImpersonationError ? ( <div className="px-4 py-4 text-sm">
                          <p className={isDarkMode ? "text-red-300" : "text-red-600"}>{companiesImpersonationError}</p>
                          <button
                            type="button"
                            onClick={() => fetchCompaniesForImpersonation()}
                            className={cn("mt-2 font-medium hover:underline", isDarkMode ? "text-indigo-300" : "text-indigo-600")}
                          > {translateUi("Retry")} </button>
                        </div>
                      ) : companiesForImpersonationFiltered.length === 0 ? (
                        <p className={cn("px-4 py-4 text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("No companies found. Add a company first.")}</p>
                      ) : (
                        companiesForImpersonationFiltered.map((c) => (
                          <div key={c.id} className="flex items-center gap-1 group">
                            <button
                              onClick={() => handleImpersonate(c.id)}
                              className={clsx(
                                "flex-1 flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left",
                                impersonatedCompany?.id === c.id
                                  ? isDarkMode
                                    ? "bg-amber-900/40 text-amber-200 font-medium"
                                    : "bg-amber-50 text-amber-800 font-medium"
                                  : isDarkMode
                                    ? "text-slate-200 hover:bg-slate-800"
                                    : "text-slate-700 hover:bg-slate-50",
                              )}
                            >
                              <div className={clsx(
                                "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0",
                                impersonatedCompany?.id === c.id
                                  ? isDarkMode
                                    ? "bg-amber-800 text-amber-100"
                                    : "bg-amber-200 text-amber-800"
                                  : isDarkMode
                                    ? "bg-slate-800 text-slate-300"
                                    : "bg-slate-100 text-slate-600",
                              )}>
                                {(c.name || `C${c.id}`).slice(0, 2).toUpperCase()}
                              </div>
                              <span className="min-w-0 flex-1 truncate">
                                {c.name || `Company ${c.id}`}
                              </span>
                              {c.name && (
                                <span className="text-xs text-slate-400 ml-1 shrink-0 tabular-nums">({c.id})</span>
                              )}
                              {impersonatedCompany?.id === c.id && (
                                <CheckCircle2 className="w-4 h-4 text-amber-600 ml-auto" />
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEnableLoginFromHeader(c.id);
                              }}
                              disabled={enableLoginLoading === c.id}
                              className={cn(
                                "p-2 rounded-lg shrink-0 disabled:opacity-50",
                                isDarkMode ? "text-slate-400 hover:text-indigo-300 hover:bg-slate-800" : "text-slate-400 hover:text-indigo-600 hover:bg-indigo-50",
                              )}
                              title={translateUi("Enable login for this company")}
                            >
                              <LogIn className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    {enableLoginMessage && (
                      <div className={cn(
                        "px-3 py-2 text-sm rounded-lg",
                        enableLoginMessage.type === 'success' ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"
                      )}>
                        {enableLoginMessage.text}
                      </div>
                    )}
                    <div className="border-t border-slate-100 px-2 py-1.5">
                      <button
                        onClick={() => setImpersonationOpen(false)}
                        className="w-full px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                      > {translateUi("Cancel")} </button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
            </div>
            )}
            <div className={cn("flex items-center gap-3 shrink-0", isTablet && "justify-end")}>
          <LanguageSelector variant="header" dark={isDarkMode} persistToServer />
          <div className="relative">
            {notificationMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setNotificationMenuOpen(false)} aria-hidden="true" />
                <div className={cn(
                  "absolute right-0 top-full mt-2 py-2 rounded-xl shadow-xl z-50 min-w-[280px] max-w-[min(360px,calc(100vw-2rem))] ring-1 ring-slate-900/5",
                  isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200"
                )}>
                  <div className={cn("px-4 py-2.5 border-b text-xs font-semibold uppercase tracking-wider", isDarkMode ? "border-slate-700 text-slate-300" : "border-slate-100 text-slate-500")}> {translateUi("Notifications")} </div>
                  <div className="max-h-72 overflow-y-auto">
                    {visibleAnnouncements.length === 0 ? (
                      <p className={cn("px-4 py-4 text-sm", isDarkMode ? "text-slate-300" : "text-slate-500")}>{translateUi("No new notifications")}</p>
                    ) : (
                      visibleAnnouncements.slice(0, 8).map((a) => (
                        <div key={a.id} className={cn("px-4 py-3 border-b last:border-b-0", isDarkMode ? "border-slate-700" : "border-slate-100")}>
                          <p className={cn("text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{a.title}</p>
                          <p className={cn("text-xs mt-1", isDarkMode ? "text-slate-300" : "text-slate-600")}>{a.message}</p>
                          {(a.type === "employee_note" || a.type === "employee_note_read") && a.employee_id && (
                            <button
                              type="button"
                              onClick={() => navigateToEmployeeNoteFromNotification(a)}
                              className={cn(
                                "mt-2 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors",
                                isDarkMode
                                  ? "border-indigo-500/50 text-indigo-300 hover:bg-indigo-950/80"
                                  : "border-indigo-200 text-indigo-700 hover:bg-indigo-50",
                              )}
                            >
                              {translateUi("View note")}
                            </button>
                          )}
                          {a.type === "fatality_anniversary_7d" && a.employee_id && (
                            <button
                              type="button"
                              onClick={() => navigateToEmployeeFromFatalityAnniversaryNotification(a)}
                              className={cn(
                                "mt-2 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors",
                                isDarkMode
                                  ? "border-amber-500/50 text-amber-200 hover:bg-amber-950/50"
                                  : "border-amber-200 text-amber-800 hover:bg-amber-50",
                              )}
                            >
                              {translateUi("View employee")}
                            </button>
                          )}
                          {(a.type === "licence_expiry_3m" ||
                            a.type === "licence_expiry_6w" ||
                            a.type === "licence_expiry_expired") &&
                            a.employee_id && (
                            <button
                              type="button"
                              onClick={() => navigateToEmployeeLicenceFromNotification(a)}
                              className={cn(
                                "mt-2 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors",
                                isDarkMode
                                  ? a.type === "licence_expiry_expired"
                                    ? "border-red-500/50 text-red-200 hover:bg-red-950/50"
                                    : "border-amber-500/50 text-amber-200 hover:bg-amber-950/50"
                                  : a.type === "licence_expiry_expired"
                                    ? "border-red-200 text-red-800 hover:bg-red-50"
                                    : "border-amber-200 text-amber-800 hover:bg-amber-50",
                              )}
                            >
                              {translateUi("View licence")}
                            </button>
                          )}
                          {(a.type === "task_assigned" || a.type === "task_reassigned" || a.type === "task_reopened" || a.type === "task_note_added") && a.task_id && (
                            <button
                              type="button"
                              onClick={() => navigateToTaskFromNotification(a)}
                              className={cn(
                                "mt-2 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors",
                                isDarkMode
                                  ? "border-violet-500/50 text-violet-300 hover:bg-violet-950/80"
                                  : "border-violet-200 text-violet-700 hover:bg-violet-50",
                              )}
                            >
                              {translateUi("Open Tasks")}
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => setNotificationMenuOpen((o) => !o)}
              className={cn(
                "relative p-2 rounded-lg transition-colors",
                isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
              )}
              aria-label={translateUi("Open notifications")}
            >
              <Bell className="w-5 h-5" />
              {visibleAnnouncements.length > 0 && (
                <span className={cn("absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2", isDarkMode ? "border-slate-900" : "border-white")} />
              )}
            </button>
          </div>
          <div className="relative" ref={userMenuRef}>
            {userMenuOpen && (
              <div className={cn(
                "absolute right-0 top-full mt-2 py-2 rounded-xl shadow-xl z-50 min-w-[220px] ring-1 ring-slate-900/5",
                isDarkMode ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200"
              )}>
                {!impersonatedUser && currentUserProfile && (
                  <button
                    onClick={() => { setActiveTab('account-profile'); setUserMenuOpen(false); }}
                    className={cn("w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-50")}
                  >
                    <User className="w-4 h-4 text-slate-500 shrink-0" />
                    {translateUi("Account profile")}
                  </button>
                )}
                {!impersonatedUser && effectiveIsSystemAdmin && (
                  <button
                    onClick={() => { setActiveTab('security'); setUserMenuOpen(false); }}
                    className={cn("w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-50")}
                  >
                    <Lock className="w-4 h-4 text-slate-500 shrink-0" />
                    {translateUi("Security")}
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsDarkMode((prev) => !prev);
                    setUserMenuOpen(false);
                  }}
                  className={cn("w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-50")}
                >
                  <span className={cn("w-2.5 h-2.5 rounded-full shrink-0", isDarkMode ? "bg-emerald-400" : "bg-slate-400")} />
                  {isDarkMode ? translateUi("Dark mode on") : translateUi("Dark mode off")}
                </button>
                <button
                  onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                  className={cn("w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left", isDarkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-50")}
                >
                  <LogOut className="w-4 h-4 text-slate-500 shrink-0" />
                  {translateUi("Logout")}
                </button>
              </div>
            )}
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex items-center space-x-4 hover:opacity-90 transition-opacity"
            >
              <div className={cn("text-right", isTablet ? "hidden" : "hidden sm:block")}>
                <p className={cn("text-sm font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{chromeDisplayName(effectiveProfile, user)}</p>
                <p className={cn("text-xs", isDarkMode ? "text-slate-300" : "text-slate-500")}>{effectiveProfile?.title ? translateUi(effectiveProfile.title) : (effectiveIsSystemAdmin ? translateUi("System Administrator") : (effectiveIsAdmin ? translateUi("Company Administrator") : translateUi("User")))}{impersonatedUser ? ` ${translateUi("(viewing as)")}` : ''}</p>
              </div>
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center overflow-hidden font-bold text-sm shrink-0",
                  effectiveProfile?.profile_image_url
                    ? isDarkMode
                      ? "ring-1 ring-slate-600"
                      : "ring-1 ring-slate-200"
                    : isDarkMode
                      ? "border border-indigo-400/55 bg-indigo-950/90 text-indigo-50"
                      : "bg-indigo-100 text-indigo-800"
                )}
              >
                {effectiveProfile?.profile_image_url ? (
                  <img src={effectiveProfile.profile_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  chromeDisplayName(effectiveProfile, user)
                    .split('@')[0]
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()
                )}
              </div>
            </button>
          </div>
          </div>
          </div>
        </header>
        )}

        {isProfileModalOpen && currentUserProfile && (
          <MyProfileModal
            profile={currentUserProfile}
            companyName={displayCompanyName}
            companyCode={effectiveCompany?.id ?? currentUserProfile.company_code}
            canEditSystemAccessLevel={effectiveIsAdmin}
            onClose={() => setIsProfileModalOpen(false)}
            onSave={async (updates) => {
              const norm = (s?: string) => (s ?? '').trim().toLowerCase();
              const actorEmail = norm(currentUserProfile.email || user?.email || '');
              const payload = { ...updates };
              delete (payload as any).email;
              const res = await fetch(`/api/users/${currentUserProfile.id}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  ...(actorEmail ? { 'X-User-Email': actorEmail } : {}),
                },
                body: JSON.stringify(payload),
              });
              if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to update profile');
              }
              const updated = await res.json();
              setCurrentUserProfile((prev) => ({
                ...updated,
                profile_image_url: updated.profile_image_url ?? prev?.profile_image_url ?? undefined,
              }));
              setIsProfileModalOpen(false);
            }}
          />
        )}

        {showNominatedAdminWelcome && effectiveCompany && user?.email && !mandatoryTempPasswordGate && (
          <NominatedAdminWelcomeScreen
            companyName={effectiveCompany.name || effectiveCompany.companyCode || effectiveCompany.id}
            companyCode={String(effectiveCompany.companyCode || effectiveCompany.id || resolvedCompanyCodeForNominee).trim()}
            userEmail={user.email}
            isDarkMode={isDarkMode}
            onContinue={dismissNominatedAdminWelcome}
          />
        )}
        {nomineeNavTourActive && nomineeNavTourSteps.length > 0 && !mandatoryTempPasswordGate && (
          <NominatedAdminNavTour
            steps={nomineeNavTourSteps}
            stepIndex={nomineeNavTourStepIndex}
            onStepIndexChange={setNomineeNavTourStepIndex}
            onComplete={completeNomineeNavTour}
            onSkip={completeNomineeNavTour}
            isDarkMode={isDarkMode}
          />
        )}
        {!isOnboardingLocked && onboardingCompleteMessage && (
          <div className={cn("w-full border-b", isDarkMode ? "border-emerald-500/35 bg-emerald-950/45" : "border-emerald-200 bg-emerald-50")}>
            <div className={cn("flex items-center gap-3 w-full", isMobileOrTablet ? "px-4 py-3" : "px-8 py-3")}>
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <p className={cn("text-sm font-medium flex-1", isDarkMode ? "text-emerald-100" : "text-emerald-800")}>{onboardingCompleteMessage}</p>
              <button
                type="button"
                onClick={() => setOnboardingCompleteMessage(null)}
                className={isDarkMode ? "text-emerald-300 hover:text-emerald-100" : "text-emerald-700 hover:text-emerald-900"}
                aria-label={translateUi("Dismiss onboarding complete message")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {finishCompanySetupDialogOpen && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
            role="presentation"
            onClick={() => setFinishCompanySetupDialogOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="finish-company-setup-title"
              className={cn(
                "w-full max-w-lg rounded-2xl border p-6 shadow-2xl",
                isDarkMode ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                  isDarkMode ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"
                )}>
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h2 id="finish-company-setup-title" className="text-lg font-bold">{translateUi("Finish company setup?")}</h2>
                  <p className={cn("mt-1 text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                    Your core setup is complete. You can change competency cycles, company standards, frameworks, and
                    grading scales at any time from the Administration menu.
                  </p>
                  <p className={cn("mt-2 text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                    {translateUi("Optional add-ons remain available from Add-ons in the left navigation menu.")}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setFinishCompanySetupDialogOpen(false)}
                  className={cn(
                    "rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors",
                    isDarkMode
                      ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Continue configuring")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFinishCompanySetupDialogOpen(false);
                    dismissCompanySetupAddonsPrompt();
                    setActiveTab("dashboard");
                  }}
                  className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  {translateUi("Finish setup")}
                </button>
              </div>
            </div>
          </div>
        )}
        {!isOnboardingLocked && companySetupReminder.show && effectiveIsAdmin && (
          <div
            className={cn(
              "w-full border-b",
              isDarkMode ? "border-amber-500/35 bg-amber-950/50" : "border-amber-200 bg-amber-50"
            )}
          >
            <div className={cn("flex flex-col gap-3 w-full", isMobileOrTablet ? "px-4 py-3" : "px-8 py-4")}>
              <div className="flex items-start gap-3 min-w-0">
                <Shield className={cn("w-5 h-5 shrink-0 mt-0.5", isDarkMode ? "text-amber-300" : "text-amber-800")} />
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-bold", isDarkMode ? "text-amber-50" : "text-amber-950")}>
                    {translateUi("Finish company setup")}
                  </p>
                  <p className={cn("text-sm mt-1 leading-snug", isDarkMode ? "text-amber-100/95" : "text-amber-900")}>
                    {companySetupRequiredComplete
                      ? "Core setup is complete. You can optionally activate bolt-on modules now or finish setup — Add-ons remain available in the left navigation menu."
                      : "Complete each step in order: build a competency cycle, set company standards, add a grading scale, then configure depots and traction. You can activate add-ons after that."}
                  </p>
                  {companySetupReminder.issues &&
                    (companySetupReminder.issues.missingCycles ||
                      companySetupReminder.issues.missingCompanyStandards ||
                      companySetupReminder.issues.missingGradingScale ||
                      companySetupReminder.issues.missingTractionAndDepots) && (
                      <ul
                        className={cn(
                          "text-xs mt-2 ml-4 list-disc space-y-0.5",
                          isDarkMode ? "text-amber-100/90" : "text-amber-950"
                        )}
                      >
                        {companySetupReminder.issues.missingCycles ? (
                          <li>{translateUi("No company competency cycle yet — use Cycle Builder first")}</li>
                        ) : null}
                        {companySetupReminder.issues.missingCompanyStandards ? (
                          <li>
                            Company standards and timings not saved yet — open Company Standards and use the expanded{" "}
                            <span className="font-semibold">{translateUi("Standard Timings")}</span> {translateUi("section, then Save")}
                          </li>
                        ) : null}
                        {companySetupReminder.issues.missingGradingScale ? <li>{translateUi("Grading scale missing or incomplete")}</li> : null}
                        {companySetupReminder.issues.missingTractionAndDepots ? (
                          <li>{translateUi("Depots and traction types not configured — add at least one of each under Traction, Routes & Depots")}</li>
                        ) : null}
                      </ul>
                    )}
                  {(() => {
                    const iss = companySetupReminder.issues;
                    const cycleDone = iss ? !iss.missingCycles : false;
                    const standardsDone = iss ? !iss.missingCompanyStandards : false;
                    const gradingDone = iss ? !iss.missingGradingScale : false;
                    const tractionDone = iss ? !iss.missingTractionAndDepots : false;
                    const standardsDisabled = !cycleDone;
                    const gradingDisabled = !cycleDone || !standardsDone;
                    const tractionDisabled = !cycleDone || !standardsDone || !gradingDone;
                    // Allow Add-ons once required setup is done, or when already viewing Add-ons after traction save.
                    const addonsUnlocked =
                      companySetupRequiredComplete ||
                      (activeTab === "addons" &&
                        !!iss &&
                        !iss.missingCycles &&
                        !iss.missingCompanyStandards &&
                        !iss.missingGradingScale &&
                        !iss.missingTractionAndDepots);
                    const addonsDisabled = !addonsUnlocked;
                    type SetupStepId =
                      | "cycle-builder"
                      | "company-standards"
                      | "grading-scale"
                      | "traction-routes"
                      | "addons";
                    let currentSetupStep: SetupStepId | null = null;
                    // Prefer the page the admin is currently viewing so the pulse follows navigation.
                    if (activeTab === "cycle-builder") {
                      currentSetupStep = "cycle-builder";
                    } else if (activeTab === "company-standards" && !standardsDisabled) {
                      currentSetupStep = "company-standards";
                    } else if (activeTab === "grading-scale" && !gradingDisabled) {
                      currentSetupStep = "grading-scale";
                    } else if (activeTab === "traction-routes" && !tractionDisabled) {
                      currentSetupStep = "traction-routes";
                    } else if (activeTab === "addons" && !addonsDisabled) {
                      currentSetupStep = "addons";
                    } else if (!iss || iss.missingCycles) {
                      currentSetupStep = "cycle-builder";
                    } else if (iss.missingCompanyStandards && canManageCompanyStandards) {
                      currentSetupStep = "company-standards";
                    } else if (iss.missingGradingScale && canManageGradingScale) {
                      currentSetupStep = "grading-scale";
                    } else if (iss.missingTractionAndDepots && canManageTractionRoutes) {
                      currentSetupStep = "traction-routes";
                    } else if (effectiveIsAdmin && !companySetupAddonsPromptSkipped) {
                      currentSetupStep = "addons";
                    }
                    const stepClass = (enabled: boolean, stepId: SetupStepId) => {
                      const isCurrent = enabled && currentSetupStep === stepId;
                      return cn(
                        "text-xs font-semibold px-3 py-2 rounded-lg border inline-flex items-center gap-1.5 transition-colors",
                        isCurrent && "rv-setup-step-pulse ring-2 ring-amber-400/70",
                        enabled
                          ? isDarkMode
                            ? isCurrent
                              ? "bg-slate-800 border-amber-400 text-amber-50 hover:bg-slate-700"
                              : "bg-slate-800 border-amber-500/45 text-amber-50 hover:bg-slate-700"
                            : isCurrent
                              ? "bg-white border-amber-500 text-amber-950 hover:bg-amber-100 shadow-md shadow-amber-200/50"
                              : "bg-white border-amber-300 text-amber-950 hover:bg-amber-100"
                          : isDarkMode
                            ? "bg-slate-900/60 border-amber-700/40 text-amber-200/70 cursor-not-allowed opacity-80"
                            : "bg-amber-50/90 border-amber-200/80 text-amber-700 cursor-not-allowed opacity-70"
                      );
                    };
                    const openTab = (tab: typeof activeTab) => (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setActiveTab(tab);
                      setIsAdministrationOpen(false);
                      setIsAdministrationCompanyConfigOpen(false);
                      setIsSidebarOpen(false);
                      closeMobileSidebar();
                    };
                    const parts: React.ReactNode[] = [];
                    const addSep = () => {
                      parts.push(
                        <ChevronRight
                          key={`setup-sep-${parts.length}`}
                          className={cn("w-4 h-4 shrink-0", isDarkMode ? "text-amber-300/90" : "text-amber-600/90")}
                          aria-hidden
                        />
                      );
                    };
                    const addButton = (key: string, el: React.ReactNode) => {
                      if (parts.length > 0) addSep();
                      parts.push(el);
                    };
                    addButton(
                      "cycle-builder",
                      <button
                        key="cycle-builder"
                        type="button"
                        className={stepClass(true, "cycle-builder")}
                        onClick={openTab("cycle-builder")}
                        aria-current={currentSetupStep === "cycle-builder" ? "step" : undefined}
                      >
                        Cycle builder
                        {iss && !iss.missingCycles ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-label={translateUi("Complete")} />
                        ) : null}
                      </button>
                    );
                    if (canManageCompanyStandards) {
                      addButton(
                        "company-standards",
                        <button
                          key="company-standards"
                          type="button"
                          disabled={standardsDisabled}
                          className={stepClass(!standardsDisabled, "company-standards")}
                          onClick={standardsDisabled ? undefined : openTab("company-standards")}
                          title={standardsDisabled ? translateUi("Complete Cycle builder first") : undefined}
                          aria-current={currentSetupStep === "company-standards" ? "step" : undefined}
                        >
                          Company standards
                          {iss && !iss.missingCompanyStandards ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-label={translateUi("Complete")} />
                          ) : null}
                        </button>
                      );
                    }
                    if (canManageGradingScale) {
                      addButton(
                        "grading-scale",
                        <button
                          key="grading-scale"
                          type="button"
                          disabled={gradingDisabled}
                          className={stepClass(!gradingDisabled, "grading-scale")}
                          onClick={gradingDisabled ? undefined : openTab("grading-scale")}
                          title={
                            gradingDisabled
                              ? !cycleDone
                                ? translateUi("Complete Cycle builder first")
                                : translateUi("Complete Company standards first")
                              : undefined
                          }
                          aria-current={currentSetupStep === "grading-scale" ? "step" : undefined}
                        >
                          Grading scale
                          {iss && !iss.missingGradingScale ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-label={translateUi("Complete")} />
                          ) : null}
                        </button>
                      );
                    }
                    if (canManageTractionRoutes) {
                      addButton(
                        "traction-routes",
                        <button
                          key="traction-routes"
                          type="button"
                          disabled={tractionDisabled}
                          className={stepClass(!tractionDisabled, "traction-routes")}
                          onClick={tractionDisabled ? undefined : openTab("traction-routes")}
                          title={
                            tractionDisabled
                              ? !gradingDone
                                ? translateUi("Complete grading scale first")
                                : translateUi("Complete earlier setup steps first")
                              : undefined
                          }
                          aria-current={currentSetupStep === "traction-routes" ? "step" : undefined}
                        >
                          Depots &amp; traction
                          {iss && !iss.missingTractionAndDepots ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-label={translateUi("Complete")} />
                          ) : null}
                        </button>
                      );
                    }
                    if (effectiveIsAdmin) {
                      addButton(
                        "addons",
                        <button
                          key="addons"
                          type="button"
                          disabled={addonsDisabled}
                          className={stepClass(!addonsDisabled, "addons")}
                          onClick={addonsDisabled ? undefined : openTab("addons")}
                          title={
                            addonsDisabled
                              ? translateUi("Complete depots & traction and other required steps first")
                              : translateUi("Optional: activate bolt-on modules")
                          }
                          aria-current={currentSetupStep === "addons" ? "step" : undefined}
                        >
                          <Package className="w-3.5 h-3.5 shrink-0" aria-hidden />
                          {translateUi("Add-ons")}
                          <span className="text-[10px] font-normal opacity-80"> {translateUi("(optional)")} </span>
                        </button>
                      );
                    }
                    return (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-y-2 gap-x-0 sm:gap-x-1">{parts}</div>
                        {(companySetupRequiredComplete || activeTab === "addons") &&
                        !companySetupAddonsPromptSkipped ? (
                          <button
                            type="button"
                            onClick={() => setFinishCompanySetupDialogOpen(true)}
                            className={cn(
                              "text-xs font-semibold px-3 py-2 rounded-lg border transition-colors",
                              isDarkMode
                                ? "border-amber-500/45 text-amber-50 bg-slate-800 hover:bg-slate-700"
                                : "border-amber-300 text-amber-950 bg-white hover:bg-amber-100"
                            )}
                          >
                            {translateUi("Finish setup")}
                          </button>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (companySetupRequiredComplete && !companySetupAddonsPromptSkipped) {
                      dismissCompanySetupAddonsPrompt();
                    } else {
                      setCompanySetupReminder({ show: false, issues: null });
                    }
                  }}
                  className={cn(
                    "shrink-0",
                    isDarkMode ? "text-amber-200 hover:text-amber-50" : "text-amber-800 hover:text-amber-950"
                  )}
                  aria-label={translateUi("Dismiss finish company setup reminder")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {!isOnboardingLocked && companySwitchMessage && (
          <div className="w-full border-b border-indigo-200 bg-indigo-50">
            <div className={cn("flex items-center gap-3 w-full", isMobileOrTablet ? "px-4 py-3" : "px-8 py-3")}>
              <CheckCircle2 className="w-5 h-5 text-indigo-600 shrink-0" />
              <p className="text-sm text-indigo-900 font-medium flex-1">{companySwitchMessage}</p>
              <button
                type="button"
                onClick={() => setCompanySwitchMessage(null)}
                className="text-indigo-700 hover:text-indigo-900"
                aria-label={translateUi("Dismiss company switch message")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {!isOnboardingLocked && impersonatedUser && (
          <div
            className={cn(
              "impersonation-user-banner w-full border-b animate-[slideDown_0.35s_ease-out]",
              isDarkMode ? "border-violet-500/50 bg-violet-950/70" : "border-violet-300 bg-violet-100"
            )}
          >
            <div
              className={cn(
                "flex w-full items-center gap-3",
                isMobileOrTablet ? "px-4 py-3" : "px-8 py-3"
              )}
            >
              <LogIn
                className={cn(
                  "impersonation-user-banner__icon h-5 w-5 shrink-0",
                  isDarkMode ? "text-violet-300" : "text-violet-700"
                )}
              />
              <p
                className={cn(
                  "impersonation-user-banner__text flex-1 text-sm font-medium leading-snug",
                  isDarkMode ? "text-violet-100" : "text-violet-900"
                )}
              >
                {translateUi("You are logged in as")} <span className="font-semibold">{impersonatedUser.full_name}</span>
                {impersonatedUser.title ? ` (${impersonatedUser.title})` : ''}. Actions use this user&apos;s permissions.
              </p>
              <button
                type="button"
                onClick={handleExitUserImpersonation}
                className={cn(
                  "impersonation-user-banner__exit shrink-0 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors",
                  isDarkMode
                    ? "border-violet-400/70 bg-violet-800 text-violet-50 hover:bg-violet-700"
                    : "border-violet-400 bg-violet-200 text-violet-900 hover:bg-violet-300"
                )}
              > {translateUi("Exit")} </button>
            </div>
          </div>
        )}

        {!isOnboardingLocked && visibleAnnouncements.length > 0 && (
          <div className="w-full space-y-0">
            {visibleAnnouncements.map((a) => {
              const styles: Record<string, { bg: string; border: string; text: string; icon: string; dismiss: string }> = {
                info: { bg: "bg-blue-600", border: "border-blue-700", text: "text-white", icon: "text-blue-200", dismiss: "hover:bg-blue-700 text-blue-200 hover:text-white" },
                warning: { bg: "bg-amber-500", border: "border-amber-600", text: "text-white", icon: "text-amber-200", dismiss: "hover:bg-amber-600 text-amber-200 hover:text-white" },
                critical: { bg: "bg-red-600", border: "border-red-700", text: "text-white", icon: "text-red-200", dismiss: "hover:bg-red-700 text-red-200 hover:text-white" },
              };
              const bannerTone =
                a.type === "fatality_anniversary_7d" ||
                a.type === "licence_expiry_3m" ||
                a.type === "licence_expiry_6w"
                  ? "warning"
                  : a.type === "licence_expiry_expired"
                    ? "critical"
                    : a.type;
              const s = styles[bannerTone] || styles.info;
              return (
                <div
                  key={a.id}
                  className={`${s.bg} ${s.border} border-b ${s.text} animate-[slideDown_0.35s_ease-out]`}
                >
                  <div className={cn("flex items-center gap-3 w-full", isMobileOrTablet ? "px-4 py-3" : "px-8 py-3")}>
                    <MessageCircle className={`w-5 h-5 shrink-0 ${s.icon}`} />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm">{a.title}</span>
                      <span className="mx-2 opacity-60">—</span>
                      <span className="text-sm opacity-90">{a.message}</span>
                    </div>
                    {(a.type === "employee_note" || a.type === "employee_note_read") && a.employee_id && (
                      <button
                        type="button"
                        onClick={() => navigateToEmployeeNoteFromNotification(a)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border border-white/50 bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        {translateUi("View note")}
                      </button>
                    )}
                    {a.type === "fatality_anniversary_7d" && a.employee_id && (
                      <button
                        type="button"
                        onClick={() => navigateToEmployeeFromFatalityAnniversaryNotification(a)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border border-white/50 bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        {translateUi("View employee")}
                      </button>
                    )}
                    {(a.type === "licence_expiry_3m" ||
                      a.type === "licence_expiry_6w" ||
                      a.type === "licence_expiry_expired") &&
                      a.employee_id && (
                      <button
                        type="button"
                        onClick={() => navigateToEmployeeLicenceFromNotification(a)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border border-white/50 bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        {translateUi("View licence")}
                      </button>
                    )}
                    {(a.type === "task_assigned" || a.type === "task_reassigned" || a.type === "task_reopened" || a.type === "task_note_added") && a.task_id && (
                      <button
                        type="button"
                        onClick={() => navigateToTaskFromNotification(a)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border border-white/50 bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        {translateUi("Open Tasks")}
                      </button>
                    )}
                    <button
                      onClick={() => dismissAnnouncement(a.id)}
                      className={`p-1.5 rounded-lg transition-colors shrink-0 ${s.dismiss}`}
                      aria-label={translateUi("Dismiss announcement")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div
          className={cn(
            "rv-main-pane mx-auto w-full min-w-0 max-w-full overflow-x-hidden overscroll-x-none",
            showActivationSuccessScreen
              ? "max-w-none p-4 sm:p-6 lg:p-8 flex min-h-full items-center justify-center"
              : isOnboardingLocked
                ? "rv-onboarding-shell max-w-full w-full min-w-0 px-2 py-2 sm:p-6 lg:p-8 overflow-x-hidden overscroll-x-none"
                : "max-w-[1600px]",
            !isOnboardingLocked && !showActivationSuccessScreen && (isMobileOrTablet ? "p-4 sm:p-6" : "p-8")
          )}
        >
          <LazyPageShell name="Workspace">
          <>
            {appShellBlockedForResolve ? (
              <div
                key="company-context-loading"
                className="relative isolate flex min-h-[min(72vh,36rem)] w-full flex-col items-center justify-center overflow-hidden rounded-2xl border border-slate-800/40 shadow-xl"
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  backgroundImage: "url('/GBR_BG.png')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div
                  className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-950/88 via-slate-900/82 to-slate-950/90"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -left-16 top-1/4 h-48 w-48 rounded-full bg-sky-400/10 blur-3xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -right-10 bottom-1/4 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl"
                  aria-hidden
                />
                <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-16 text-center">
                  <div
                    className="h-11 w-11 animate-spin rounded-full border-2 border-white/25 border-t-white"
                    aria-hidden
                  />
                  <p className="text-sm font-medium tracking-wide text-white/95 sm:text-base">
                    {translateUi("Loading your workspace…")}
                  </p>
                </div>
              </div> ) : showNomineeAwaitingActivation ? ( <div
                key="nominee-awaiting-activation"
                className={cn("mx-auto w-full max-w-lg rounded-3xl border p-8 shadow-xl text-center space-y-4 relative z-10", isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white")}
              >
                <Clock className="mx-auto h-12 w-12 text-indigo-600" aria-hidden />
                <h2 className={cn("text-xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Onboarding in progress")}</h2>
                <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                  Your organisation is still completing initial onboarding. You will be able to configure Rail Intel as
                  company administrator once activation is complete. If you are the person completing company setup,
                  sign in with the original company invite email to resume where you left off.
                </p>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className={cn("w-full py-3 rounded-xl border font-semibold", isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50")}
                >
                  {translateUi("Sign out")}
                </button>
              </div> ) : companyActivationSuccess ? ( <CompanyActivationSuccessPanel
                detail={companyActivationSuccess}
                isMobileOrTablet={isMobileOrTablet}
                isDarkMode={isDarkMode}
                onContinue={() => {
                  const code = companyActivationSuccess?.companyCode || "";
                  const usedExisting = !!companyActivationSuccess?.usedExistingContactLogin;
                  setCompanyActivationSuccess(null);
                  setMandatoryTempPasswordGate(false);
                  // Invite contact who self-nominated already completed onboarding — skip nominee welcome tour.
                  if (usedExisting && code) {
                    try {
                      localStorage.setItem(nominatedAdminWelcomeStorageKey(code), "1");
                      localStorage.setItem(nominatedAdminNavTourStorageKey(code), "1");
                    } catch {
                      /* ignore */
                    }
                    setShowNominatedAdminWelcome(false);
                  }
                  // Re-bind tenant scope and drop any employee/stats payload loaded under the wrong company.
                  if (code) {
                    try {
                      sessionStorage.setItem("rv_login_company_code", code);
                    } catch {
                      /* ignore */
                    }
                    if (user?.email) writeRememberedCompanyScope(user.email, code);
                  }
                  setEmployees([]);
                  setStats(null);
                  setEmployeesLoadedOnce(false);
                  setSelectedEmployeeId(null);
                  setSelectedEmployeeCompanyCode(undefined);
                  setCompanyContextReloadToken((t) => t + 1);
                  setActiveTab("dashboard");
                  try {
                    sessionStorage.setItem("rv_active_tab", "dashboard");
                  } catch {
                    /* ignore */
                  }
                }}
                onSignOut={handleLogout}
                onResendWelcomeEmail={
                  companyActivationSuccess.emailSent || companyActivationSuccess.usedExistingContactLogin
                    ? undefined
                    : handleResendNomineeWelcomeEmail
                }
                resendEmailLoading={resendNomineeEmailLoading}
                resendEmailMessage={resendNomineeEmailMessage}
              />
            ) : showSetupWizard && effectiveCompany ? (
              <div key="company-setup" className="rv-onboarding-stack w-full min-w-0 max-w-full space-y-3 overflow-x-hidden overscroll-x-none">
                <div className={cn("rounded-2xl border backdrop-blur-[1px] shadow-sm px-4 py-4 sm:px-6 sm:py-5 w-full min-w-0 max-w-full", isDarkMode ? "border-slate-600 bg-slate-900/80" : "border-slate-200 bg-white/60")}>
                  <p className={cn("text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Company onboarding in progress")}</p>
                  <p className={cn("text-xs sm:text-sm mt-1 break-words", isDarkMode ? "text-slate-300" : "text-slate-700")}>
                    {translateUi("Complete all setup steps to unlock navigation and continue to the full dashboard.")}
                  </p>
                </div>
                <SetupWizard
                  key={effectiveCompany.id}
                  company={effectiveCompany}
                  currentUserId={user.uid}
                  currentUserEmail={user.email ?? undefined}
                  activeStep={setupWizardStep}
                  onStepChange={persistStep}
                  onClose={() => {}}
                  onLogout={handleLogout}
                  hideBackToDashboard={true}
                  isMobileOrTablet={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  azureOnly={true}
                  onAddonActivated={() => setAddonsRefreshTrigger((t) => t + 1)}
                  onActivated={(detail) => {
                    const code = effectiveCompany?.id || effectiveCompany?.companyCode;
                    if (code) try { localStorage.removeItem(`${STORAGE_KEY_PREFIX}${code}`); } catch (_) {}
                    setSelectedEmployeeId(null);
                    setSelectedEmployeeCompanyCode(undefined);
                    setEmployees([]);
                    setStats(null);
                    setEmployeesLoadedOnce(false);
                    const p = detail?.nominatedAdminProvisioning;
                    const emailSent = p?.emailed === true;
                    const usedExistingContactLogin =
                      p?.skipped === true && p?.emailed !== true && !p?.warning;
                    setOnboardingCompleteMessage(null);
                    // Password was already set during onboarding for the invite contact.
                    setMandatoryTempPasswordGate(false);
                    setShowNominatedAdminWelcome(false);
                    setCompanyActivationSuccess({
                      companyName:
                        detail?.companyName?.trim() ||
                        effectiveCompany?.name ||
                        effectiveCompany?.companyCode ||
                        code ||
                        "Your company",
                      companyCode: code || "",
                      nominatedAdminName: detail?.nominatedAdminName?.trim() || "Your nominated administrator",
                      nominatedAdminEmail: detail?.nominatedAdminEmail?.trim() || "",
                      nominatedAdminJobTitle: detail?.nominatedAdminJobTitle?.trim() || "",
                      emailSent,
                      emailWarning: p?.warning,
                      usedExistingContactLogin,
                    });
                    // Activator already completed the onboarding password step — clear any stale temp-password flag.
                    if (code && user?.email) {
                      void fetch("/api/users/me/password-changed", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "X-User-Email": user.email },
                        body: JSON.stringify({ companyCode: code }),
                      }).catch(() => {});
                    }
                    if (impersonatedCompany) {
                      setImpersonatedCompany({ ...impersonatedCompany, status: "Active" });
                    } else if (currentCompany) {
                      setCurrentCompany({ ...currentCompany, status: "Active" });
                    }
                    // Reload addons so onboarding trials appear on employee profiles immediately.
                    setAddonsRefreshTrigger((t) => t + 1);
                  }}
                  lockCompanyDetails={false}
                />
              </div>
            ) : activeTab === 'account-profile' && currentUserProfile && !impersonatedUser ? (
              <div key="account-profile" className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setActiveTab('dashboard')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {translateUi("Back to Dashboard")}
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start w-full max-w-6xl">
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 w-full min-w-0">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">{translateUi("Account profile")}</h2>

                    <div className="flex flex-col gap-8">
                      <div className="flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6 border-b border-slate-200 pb-8">
                        <ProfilePictureSection
                          profileImageUrl={currentUserProfile.profile_image_url}
                          userEmail={user?.email ?? currentUserProfile.email ?? ""}
                          onUpdated={(url) => setCurrentUserProfile((p) => (p ? { ...p, profile_image_url: url ?? undefined } : p))}
                        />
                        <div className="w-full min-w-0 text-center sm:flex-1 sm:text-left">
                          <p className="text-sm text-slate-600 mb-1">{translateUi("Profile photo")}</p>
                          {!currentUserProfile.profile_image_url && (
                            <p className="text-xs text-slate-500">{translateUi("Upload a photo to show on your account. JPEG, PNG or WebP, max 2MB.")}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">{translateUi("Your details")}</h3>
                        <AccountProfilePage
                          profile={currentUserProfile}
                          companyName={displayCompanyName}
                          companyCode={currentUserProfile.company_code ?? (currentUserProfile as { companyCode?: string }).companyCode ?? apiCompanyCode ?? effectiveCompany?.id ?? effectiveCompany?.companyCode ?? '—'}
                          canEditSystemAccessLevel={effectiveIsAdmin}
                          onSave={async (updates) => {
                            const norm = (s?: string) => (s ?? '').trim().toLowerCase();
                            const actorEmail = norm(currentUserProfile.email || user?.email || '');
                            const payload = { ...updates };
                            delete (payload as any).email;
                            const res = await fetch(`/api/users/${currentUserProfile.id}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                ...(actorEmail ? { 'X-User-Email': actorEmail } : {}),
                              },
                              body: JSON.stringify(payload),
                            });
                            if (!res.ok) {
                              const data = await res.json();
                              throw new Error(data.error || 'Failed to update profile');
                            }
                            const updated = await res.json();
                            setCurrentUserProfile((prev) => ({
                              ...updated,
                              profile_image_url: updated.profile_image_url ?? prev?.profile_image_url ?? undefined,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6 min-w-0">
                    <ChangePasswordSection userEmail={currentUserProfile.email} />
                    <TwoFactorSection userEmail={currentUserProfile.email} />
                  </div>
                </div>
              </div>
            ) : activeTab === 'custom-job-roles' && canManageCustomJobRoles ? (
              <div key="custom-job-roles" className="space-y-6">
                <CustomJobRolesPage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                />
              </div>
            ) : activeTab === 'grading-scale' && canManageGradingScale ? (
              <div key="grading-scale" className="space-y-6">
                <GradingScalePage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  onSaved={advanceCompanySetupAfterGradingSaved}
                />
              </div>
            ) : activeTab === "company-email-templates" && effectiveIsAdmin && !effectiveIsSystemAdmin && apiCompanyCode ? (
              <div key="company-email-templates" className="space-y-6">
                <EmailTemplatePage companyCode={apiCompanyCode} currentUserEmail={user?.email} />
              </div>
            ) : activeTab === 'company-standards' && canManageCompanyStandards ? (
              <div key="company-standards" className="space-y-6">
                <CompanyStandardsPage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  isDarkMode={isDarkMode}
                  finishSetupFlow={
                    !!companySetupReminder.show &&
                    !!companySetupReminder.issues?.missingCompanyStandards
                  }
                  onSaved={advanceCompanySetupAfterStandardsSaved}
                />
              </div>
            ) : activeTab === 'traction-routes' && canManageTractionRoutes ? (
              <div key="traction-routes" className="space-y-6">
                <CompanyTractionRoutesPage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  isDarkMode={isDarkMode}
                  finishSetupFlow={!!companySetupReminder.show}
                  onSaved={advanceCompanySetupAfterTractionSaved}
                />
              </div>
            ) : activeTab === 'org-structure' && effectiveIsAdmin && effectiveCompany ? (
              <div key="org-structure" className="space-y-6">
                <CompanyOrgStructurePage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  isDarkMode={isDarkMode}
                  canEdit={effectiveIsAdmin}
                />
              </div>
            ) : activeTab === 'role-permissions' && effectiveIsAdmin && effectiveCompany ? (
              <div key="role-permissions" className="space-y-6">
                <RolePermissionsPage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={user?.email || undefined}
                  isDarkMode={isDarkMode}
                />
              </div>
            ) : activeTab === 'company-logo' && effectiveIsAdmin && effectiveCompany ? (
              <div key="company-logo" className="space-y-6">
                <CompanyLogoPage
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={user?.email || undefined}
                  onLogoChanged={(logoUrl) => {
                    if (impersonatedCompany) setImpersonatedCompany({ ...impersonatedCompany, logoUrl: logoUrl ?? undefined });
                    else if (currentCompany) setCurrentCompany({ ...currentCompany, logoUrl: logoUrl ?? undefined });
                  }}
                />
              </div> ) : selectedEmployeeId ? ( <div key="profile" className="min-w-0 max-w-full overflow-x-hidden">
                <EmployeeProfile
                  key={`${selectedEmployeeId}-${effectiveIsSystemAdmin ? 'all' : (apiCompanyCode || '')}`}
                  employeeId={selectedEmployeeId}
                  initialEmployee={transferInitialEmployee && transferInitialEmployee.id === selectedEmployeeId ? transferInitialEmployee : employees.find(e => e.id === selectedEmployeeId)}
                  companyCode={
                    effectiveIsSystemAdmin
                      ? (selectedEmployeeCompanyCode || (employees.find(e => e.id === selectedEmployeeId) as Employee | undefined)?.company_code || undefined)
                      : (apiCompanyCode || selectedEmployeeCompanyCode || (employees.find(e => e.id === selectedEmployeeId) as Employee | undefined)?.company_code || undefined)
                  }
                  initialProfileTab={restoredEmployeeIdRef.current === selectedEmployeeId ? lastEmployeeProfileTab : undefined}
                  onProfileTabChange={setLastEmployeeProfileTab}
                  onBack={() => {
                    if (canViewOwnEmployeeOnly) setActiveTab("help-support");
                    setSelectedEmployeeId(null);
                    setSelectedEmployeeCompanyCode(undefined);
                    setTransferInitialEmployee(null);
                  }}
                  onEdit={(emp) => {
                    setEditingEmployee(emp);
                    setIsEmployeeModalOpen(true);
                  }}
                  onDelete={async (id) => {
                    try {
                      const targetCompanyCode =
                        employeesApiCompanyCode ||
                        apiCompanyCode ||
                        selectedEmployeeCompanyCode ||
                        (employees.find(e => e.id === id)?.company_code || '');
                      const params = targetCompanyCode ? `?companyCode=${encodeURIComponent(targetCompanyCode)}` : '';
                      const res = await fetch(`/api/employees/${id}${params}`, { method: 'DELETE' });
                      if (res.ok) {
                        setEmployees(employees.filter(e => e.id !== id));
                        setSelectedEmployeeId(null);
                        setSelectedEmployeeCompanyCode(undefined);
                        setTransferInitialEmployee(null);
                      }
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  currentUserTitle={effectiveProfile?.title || "System Administrator"}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  realActorEmail={user?.email || currentUserProfile?.email || undefined}
                  currentUserProfile={effectiveProfile}
                  companyName={employees.find(e => e.id === selectedEmployeeId)?.company_name ?? effectiveCompany?.name ?? displayCompanyName}
                  companyAddress={effectiveCompany?.postalAddress || effectiveCompany?.address}
                  companyLogoUrl={effectiveCompany?.logoUrl}
                  isSystemAdmin={effectiveIsSystemAdmin}
                  canOverrideStuckAssessment={canOverrideStuckAssessment}
                  canAdminResetOpenAssessment={canAdminResetOpenAssessment}
                  systemAdminClosedCycleEditWhileImpersonating={canSystemAdminEditClosedCycleWhileImpersonatingCompany}
                  assessmentTenantImpersonationOk={
                    !!impersonatedCompany && !impersonatedUser && (isRealSystemAdmin || effectiveIsAdmin)
                  }
                  isVerificationModuleActive={companyAddons.includes('qa-verifications')}
                  canRunQAVerifications={canRunQAVerifications}
                  isTaskModuleActive={companyAddons.includes('task-assignment')}
                  isSafetyBriefsModuleActive={companyAddons.includes('safety-briefs')}
                  canManageSafetyBriefs={canAccessSafetyBriefs}
                  isDriverReportsModuleActive={companyAddons.includes('driver-reports')}
                  canManageDriverReports={canAccessDriverReports}
                  isLeaveAbsenceModuleActive={leaveAbsenceModuleActive}
                  isMedicationChecksModuleActive={medicationChecksModuleActive}
                  canEditMedicationChecks={
                    hasCompanyPermission('medication-checks.edit') ||
                    hasCompanyPermission('medication-checks.manage') ||
                    effectiveIsSystemAdmin ||
                    effectiveIsAdmin
                  }
                  canDeleteMedicationChecks={
                    hasCompanyPermission('medication-checks.delete') ||
                    hasCompanyPermission('medication-checks.manage') ||
                    effectiveIsSystemAdmin ||
                    effectiveIsAdmin
                  }
                  canSubmitMedicationChecks={hasCompanyPermission('medication-checks.submit')}
                  isTraineeDriverModuleActive={traineeDriverModuleActive}
                  canManageTraineeDriver={canManageTraineeDriver}
                  isDarkMode={isDarkMode}
                  onToggleDarkMode={() => setIsDarkMode((v) => !v)}
                  onEmployeeIdChanged={(newId) => {
                    setSelectedEmployeeId(newId);
                    setTransferInitialEmployee(null);
                    const params = employeesApiCompanyCode ? `?companyCode=${encodeURIComponent(employeesApiCompanyCode)}` : '';
                    fetch(`/api/employees${params}`, { cache: 'no-store' })
                      .then(res => res.json())
                      .then((data) => setEmployees(Array.isArray(data) ? data : []));
                  }}
                  isMobileOrTablet={isMobileOrTablet}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  onAssessmentSessionRestored={handleAssessmentSessionRestored}
                  onEmployeeNotFound={handleEmployeeNotFound}
                  onEmployeePatched={(patched) => {
                    setEmployees((prev) => {
                      const idx = prev.findIndex((e) => e.id === patched.id);
                      if (idx < 0) return prev;
                      const next = prev.slice();
                      next[idx] = { ...next[idx], ...patched };
                      return next;
                    });
                  }}
                  onEmployeeNotePosted={fetchActiveAnnouncements}
                  canEditEmployeeRecord={canEditSelectedEmployeeRecord}
                  canManageCompetencyAssessments={hasCompanyPermission('assessments.manage')}
                  canManageContinuousCycles={hasCompanyPermission('cycles.continuous')}
                  canManageCompanyTasks={hasCompanyPermission('tasks.manage')}
                  canAddExperienceRecords={hasCompanyPermission('experience-records.add')}
                  canAccessCabPassesTab={canAccessCabPassesSection}
                  cabPassesManagePermission={cabPassesManagePermission}
                  cabPassPermissionEdit={cabPassPermissionEdit}
                  cabPassPermissionDelete={cabPassPermissionDelete}
                  cabPassPermissionRevoke={cabPassPermissionRevoke}
                  cabPassPermissionRenew={cabPassPermissionRenew}
                  cabPassCompanyAdminActions={effectiveIsAdmin || effectiveIsSystemAdmin}
                  canLockEmployeeProfile={canLockEmployeeProfiles}
                  onTransferComplete={(newEmployee) => {
                    flushSync(() => {
                      setSelectedEmployeeId(null);
                      setSelectedEmployeeCompanyCode(undefined);
                      setTransferInitialEmployee(null);
                    });
                    const params = employeesApiCompanyCode ? `?companyCode=${encodeURIComponent(employeesApiCompanyCode)}` : '';
                    fetch(`/api/employees${params}`, { cache: 'no-store' })
                      .then(res => res.json())
                      .then((data) => setEmployees(Array.isArray(data) ? data : []));
                    if (newEmployee?.id && newEmployee?.company_code && effectiveIsSystemAdmin) {
                      transferPendingRef.current = { id: newEmployee.id, companyCode: newEmployee.company_code, employee: newEmployee as Employee };
                      setActiveTab('employees');
                      fetch(`/api/companies/${encodeURIComponent(newEmployee.company_code)}`)
                        .then(res => res.ok ? res.json() : null)
                        .then((data: { id?: string; name?: string; status?: string; logoUrl?: string; address?: string; postalAddress?: string } | null) => {
                          if (data) {
                            const code = String(data.id ?? newEmployee.company_code);
                            setImpersonatedCompany({
                              id: code,
                              name: data.name ?? '',
                              companyCode: code,
                              status: (data.status as any) ?? "Active",
                              address: data.address,
                              postalAddress: data.postalAddress,
                              logoUrl: data.logoUrl,
                              setupCompletedAt: null,
                              setupCompletedBy: null,
                              createdAt: null as any,
                              createdBy: user?.uid ?? '',
                              updatedAt: null as any,
                              updatedBy: user?.uid ?? '',
                            });
                            setImpersonationOpen(false);
                          }
                        });
                    }
                  }}
                />
              </div>
            ) : activeTab === 'incidents' ? (
              <div key="incidents">
                <IncidentManagement 
                  currentUserTitle={effectiveProfile?.full_name} 
                  currentUserEmail={user?.email || undefined}
                  isAdmin={effectiveIsAdmin} 
                  isManager={effectiveProfile?.title?.toLowerCase()?.includes('manager') || false}
                  companyCode={apiCompanyCode || undefined}
                  operationalEditUnlocked={operationalEditUnlocked}
                  isDarkMode={isDarkMode}
                />
              </div>
            ) : activeTab === 'reporting' ? (
              <div key="reporting">
                <ReportingPage
                  companyCode={effectiveIsSystemAdmin ? undefined : apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  currentUserName={String(effectiveProfile?.full_name || "").trim() || undefined}
                  isSystemAdmin={effectiveIsSystemAdmin}
                  isAdmin={effectiveIsAdmin}
                  isDarkMode={isDarkMode}
                  onNavigateToAddons={() => setActiveTab('addons')}
                  onNavigateToQaVerifications={() => setActiveTab('qa-verifications')}
                />
              </div>
            ) : activeTab === 'team' ? (
              <div key="team">
                <TeamManagement
                  currentUserTitle={effectiveProfile?.title || "System Administrator"}
                  currentUserEmail={user?.email || undefined}
                  companyCode={employeesApiCompanyCode || undefined}
                  isSystemAdmin={effectiveIsSystemAdmin}
                  isCompanyActive={effectiveCompany?.status === 'Active'}
                  isNominatedCompanyAdmin={isNominatedCompanyAdmin}
                  onLoginAsUser={isRealSystemAdmin ? handleLoginAsUser : undefined}
                  systemAdminHomeCompanyName={teamManagementHomeCompanyDisplayName}
                  platformOperatorCompanyCode={cmsProviderCompanyCode || undefined}
                  onLinkedEmployeeChanged={handleTeamLinkedEmployeeChanged}
                />
              </div>
            ) : activeTab === 'employees' && isImpersonationPermissionLoading ? (
              <div
                key="employee-view-loading"
                className="flex min-h-[40vh] items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
              >
                <p className="text-slate-600">{translateUi("Loading your access…")}</p>
              </div>
            ) : activeTab === 'employees' && canViewOwnEmployeeOnly && !selectedEmployeeId ? (
              <div
                key="self-record-loading"
                className="flex min-h-[40vh] items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
              >
                <p className="text-slate-600">
                  {employeesLoadedOnce && employees.length === 0
                    ? "No employee record found for this user. Ask your company administrator to link your account to your employee record."
                    : "Loading your record…"}
                </p>
              </div>
            ) : activeTab === 'employees' && !canViewOwnEmployeeOnly ? (
              <div key="employees">
                <EmployeeList 
                  employees={filteredEmployees} 
                  onSelectEmployee={(id, companyCode) => { setSelectedEmployeeId(id); setSelectedEmployeeCompanyCode(companyCode ?? undefined); }} 
                  onAddEmployee={() => {
                    setEditingEmployee(null);
                    setIsEmployeeModalOpen(true);
                  }}
                  onEditEmployee={(emp) => {
                    setEditingEmployee(emp);
                    setIsEmployeeModalOpen(true);
                  }}
                  onEmployeeDeleted={(id) => setEmployees(employees.filter(e => e.id !== id))}
                  currentUserTitle={effectiveProfile?.title || "System Administrator"}
                  currentUserEmail={user?.email || undefined}
                  currentFilter={employeeFilter === 'all' ? null : employeeFilter}
                  onClearFilter={() => setEmployeeFilter('all')}
                  isSystemAdmin={effectiveIsSystemAdmin}
                  isMobileOrTablet={isMobileOrTablet}
                  operationalEditUnlocked={operationalEditUnlocked}
                  systemAdminProviderCompanyCode={
                    effectiveIsSystemAdmin && !impersonatedCompany && !impersonatedUser
                      ? employeesApiCompanyCode || undefined
                      : undefined
                  }
                  onLoginAsEmployee={isRealSystemAdmin ? handleLoginAsEmployee : undefined}
                />
              </div>
            ) : activeTab === 'addons' ? (
              <div key="addons" className="space-y-6">
                <AddonsPage
                  isAdmin={effectiveIsAdmin}
                  companyCode={apiCompanyCode || undefined}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  isDarkMode={isDarkMode}
                  knownActiveAddonIds={companyAddons}
                  onAddonPurchased={() => setAddonsRefreshTrigger(t => t + 1)}
                  finishSetupFlow={
                    companySetupReminder.show &&
                    !companySetupAddonsPromptSkipped &&
                    (companySetupRequiredComplete ||
                      (!!companySetupReminder.issues &&
                        !companySetupReminder.issues.missingCycles &&
                        !companySetupReminder.issues.missingCompanyStandards &&
                        !companySetupReminder.issues.missingFramework &&
                        !companySetupReminder.issues.missingGradingScale &&
                        !companySetupReminder.issues.missingTractionAndDepots))
                  }
                  onFinishSetup={() => setFinishCompanySetupDialogOpen(true)}
                />
              </div>
            ) : activeTab === "addons-storage" ? (
              <div key="addons-storage" className="w-full min-w-0 max-w-full space-y-4 sm:space-y-6">
                <div className="w-full min-w-0">
                  <h2
                    className={cn(
                      "text-xl sm:text-2xl font-bold tracking-tight",
                      isDarkMode ? "text-slate-100" : "text-slate-900"
                    )}
                  >
                    {translateUi("Storage & plans")}
                  </h2>
                  <p
                    className={cn("text-sm sm:text-base mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}
                  >
                    {translateUi("Usage and optional upgrades")}
                  </p>
                </div>
                <div className="w-full min-w-0">
                  <CompanyStorageWidget
                    companyCode={apiCompanyCode}
                    currentUserEmail={user?.email}
                    isCompanyAdmin={effectiveIsAdmin}
                    isDarkMode={isDarkMode}
                    checkoutReturnTab="addons-storage"
                  />
                </div>
              </div>
            ) : activeTab === 'help-support' ? (
              <div
                key="help-support"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className={cn("text-2xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Help & Support")}</h2>
                    <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("Support tickets, documentation and resources")}</p>
                  </div>
                </div>

                {apiCompanyCode && (effectiveProfile?.email || user?.email) && (
                  <SupportTicketsPage
                    companyCode={apiCompanyCode}
                    currentUserEmail={String(effectiveProfile?.email || user?.email || "")}
                    currentUserName={chromeDisplayName(effectiveProfile, user)}
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                  />
                )}

                <div className={cn("p-8 rounded-3xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <Video className="w-6 h-6 text-indigo-600 mr-3" />
                      <h3 className={cn("text-xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Video Tutorials")}</h3>
                    </div>
                  </div>
                  <HelpVideos isAdmin={effectiveIsAdmin || effectiveIsSystemAdmin} />
                </div>

                <div className={cn("p-8 rounded-3xl border shadow-sm", isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <Archive className="w-6 h-6 text-indigo-600 mr-3" />
                      <h3 className={cn("text-xl font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Document Library")}</h3>
                    </div>
                    {(effectiveIsAdmin || effectiveIsSystemAdmin) && (
                      <>
                        <input
                          ref={libraryDocumentInputRef}
                          type="file"
                          className="hidden"
                          accept=".pdf,.txt,.doc,.docx"
                          onChange={uploadLibraryDocument}
                          disabled={libraryDocumentUploading}
                        />
                        <button
                          type="button"
                          onClick={() => libraryDocumentInputRef.current?.click()}
                          disabled={libraryDocumentUploading}
                          className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 min-h-[44px]",
                            isDarkMode
                              ? "text-indigo-300 border border-indigo-500/40 hover:bg-slate-800"
                              : "text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
                          )}
                        >
                          {libraryDocumentUploading ? translateUi("Uploading…") : translateUi("Add document")}
                        </button>
                      </>
                    )}
                  </div>
                  {libraryDocumentsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : libraryDocuments.length === 0 ? (
                    <p className={cn("py-8 text-center text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("No documents in the library yet.")}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {libraryDocuments.map((doc) => (
                        <div key={doc.id} className={cn(
                          "p-4 border rounded-xl hover:border-indigo-300 hover:shadow-md transition-all group flex items-start space-x-4",
                          isDarkMode ? "border-slate-700" : "border-slate-200"
                        )}>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await fetch(`/api/library-documents/${doc.id}/download`);
                                if (!res.ok) {
                                  const err = await res.json().catch(() => ({}));
                                  alert(err.error || translateUi("Download failed"));
                                  return;
                                }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = doc.name;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch {
                                alert(translateUi("Download failed"));
                              }
                            }}
                            className="flex-1 min-w-0 flex items-start space-x-4 text-left cursor-pointer"
                          >
                            <div className={cn(
                              "p-3 rounded-lg transition-colors shrink-0",
                              isDarkMode ? "bg-indigo-950 text-indigo-300 group-hover:bg-indigo-600 group-hover:text-white" : "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white"
                            )}>
                              <FileText className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className={cn(
                                "font-semibold group-hover:text-indigo-600 transition-colors",
                                isDarkMode ? "text-slate-100" : "text-slate-900"
                              )}>{doc.name}</h4>
                              <p className={cn("text-xs mt-1", isDarkMode ? "text-slate-400" : "text-slate-500")}>{doc.type || 'File'} • {doc.size_display || '—'}</p>
                            </div>
                          </button>
                          {(effectiveIsAdmin || effectiveIsSystemAdmin) && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteLibraryDocument(doc.id); }}
                              className={cn(
                                "p-2 rounded-lg transition-colors shrink-0",
                                isDarkMode ? "text-slate-400 hover:text-rose-400 hover:bg-rose-950/40" : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                              )}
                              title={translateUi("Delete document")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'support-desk' ? (
              <div
                key="support-desk"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin || canAccessSupportDesk ? (
                  <SupportDeskPage
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                    isSystemAdmin={effectiveIsSystemAdmin}
                  />
                ) : (
                  <p className={cn(isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    {translateUi("Only System Administrators and Tech Support Agents can access the Support Desk.")}
                  </p>
                )}
              </div>
            ) : activeTab === 'dashboard-details' ? (
              <div
                key="dashboard-details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <DashboardDetailsPage
                  title={dashboardModalConfig.title}
                  type={dashboardModalConfig.type}
                  onBack={() => setActiveTab('dashboard')}
                  onSelectEmployee={(id, companyCode) => { setSelectedEmployeeId(id); setSelectedEmployeeCompanyCode(companyCode ?? undefined); setActiveTab('employees'); }}
                  companyCode={apiCompanyCode || undefined}
                />
              </div>
            ) : activeTab === 'qa-verifications' ? (
              <div
                key="qa-verifications"
                className={cn(isDarkMode && "min-h-full rounded-2xl bg-slate-950 p-4 sm:p-6")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {!companyAddons.includes('qa-verifications') ? (
                  <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("Activate the QA Verifications module from Addons to use this page.")}
                  </div> ) : !canViewQAVerificationsHistory ? ( <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("You do not have permission to view QA verification history. Ask your company administrator to enable the “View QA verification history” permission for your role.")}
                  </div>
                ) : (
                  <QAVerificationsPage
                    companyCode={apiCompanyCode || undefined}
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    currentUserName={chromeDisplayName(effectiveProfile, user)}
                    isCompanyAdmin={effectiveIsAdmin}
                    isDarkMode={isDarkMode}
                    onNavigateToEmployeeFix={({ employeeId, companyCode: cc, tab }) => {
                      const allowed =
                        tab === "Tasks" ||
                        (EMPLOYEE_PROFILE_TAB_OPTIONS as readonly string[]).includes(tab);
                      if (!allowed) return;
                      try {
                        sessionStorage.setItem(`rv_employee_tab_${employeeId}`, tab);
                      } catch {
                        /* ignore */
                      }
                      restoredEmployeeIdRef.current = employeeId;
                      setLastEmployeeProfileTab(tab);
                      setSelectedEmployeeId(employeeId);
                      setSelectedEmployeeCompanyCode(cc || apiCompanyCode || undefined);
                      setActiveTab("employees");
                    }}
                  />
                )}
              </div>
            ) : activeTab === 'tasks' ? (
              <div
                key="tasks"
                className={cn(isDarkMode && "min-h-full rounded-2xl bg-slate-950 p-4 sm:p-6")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {companyAddons.includes('task-assignment') ? (
                  <TasksPage
                    companyCode={apiCompanyCode || undefined}
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    currentUserTitle={effectiveProfile?.title}
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                  />
                ) : (
                  <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("Activate the Task assignment module from Addons to use this page.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'safety-briefs' ? (
              <div
                key="safety-briefs"
                className={cn(isDarkMode && "min-h-full rounded-2xl bg-slate-950 p-4 sm:p-6")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {companyAddons.includes('safety-briefs') ? (
                  <SafetyBriefsPage
                    companyCode={apiCompanyCode || undefined}
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    isDarkMode={isDarkMode}
                    canManageTypes={effectiveIsAdmin || isRealSystemAdmin}
                    onOpenEmployees={() => setActiveTab('employees')}
                  />
                ) : (
                  <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("Activate the Safety Briefs module from Addons to use this page.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'trainee-driver' ? (
              <div
                key="trainee-driver"
                className={cn(isDarkMode && "min-h-full rounded-2xl bg-slate-950 p-4 sm:p-6")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {!traineeDriverModuleActive ? (
                  <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("Activate the Trainee module from Addons to use this page.")}
                  </div> ) : !canAccessTraineeDriver ? ( <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("You do not have permission to use the Trainee module. Ask your company administrator to enable the “Manage trainee driver training”, “Trainee driver self-service”, “Manage trainee driver policies & agreements”, “Upload original trainee policy documents”, or “Manage trainee driver equipment catalogue” permission for your role.")}
                  </div>
                ) : (
                  <TraineeDriverPage
                    companyCode={apiCompanyCode || undefined}
                    companyName={displayCompanyName || effectiveCompany?.name || undefined}
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    isDarkMode={isDarkMode}
                    canManage={canManageTraineeDriver}
                    canUnenrol={canUnenrolTraineeDriver}
                    canManagePolicies={canManageTraineeDriverPolicies}
                    canUploadPolicyDocuments={canUploadTraineeDriverPolicyDocuments || canManageTraineeDriverPolicies}
                    canDeleteAnyPolicyDocument={effectiveIsAdmin || effectiveIsSystemAdmin}
                    canManageEquipment={canManageTraineeDriverEquipment}
                    canClearPolicySignatures={effectiveIsAdmin || effectiveIsSystemAdmin}
                    canResetModuleFeedback={effectiveIsAdmin || effectiveIsSystemAdmin}
                    canDeleteInstructorAudit={effectiveIsAdmin || effectiveIsSystemAdmin}
                    onOpenEmployeeCycles={({ employeeId, employeeCycleId, cycleId }) => {
                      const id = String(employeeId || "").trim();
                      if (!id) return;
                      try {
                        sessionStorage.setItem(`rv_employee_tab_${id}`, "Cycles");
                        sessionStorage.removeItem(`rv_highlight_note_${id}`);
                        sessionStorage.removeItem(`rv_employee_viewing_cycle_${id}`);
                        sessionStorage.setItem(
                          `rv_employee_open_cycle_${id}`,
                          JSON.stringify({
                            employeeCycleId: String(employeeCycleId || "").trim() || undefined,
                            cycleId: String(cycleId || "").trim() || undefined,
                          }),
                        );
                      } catch {
                        /* ignore */
                      }
                      restoredEmployeeIdRef.current = id;
                      if (apiCompanyCode) setSelectedEmployeeCompanyCode(apiCompanyCode);
                      setLastEmployeeProfileTab("Cycles");
                      setSelectedEmployeeId(id);
                      setActiveTab("employees");
                    }}
                  />
                )}
              </div>
            ) : activeTab === 'task-module-settings' ? (
              <div
                key="task-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage currentUserEmail={user?.email || undefined} isDarkMode={isDarkMode} />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage task module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'safety-brief-module-settings' ? (
              <div
                key="safety-brief-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="safety-briefs"
                    moduleTitle="Safety briefs module"
                    moduleLabel="Safety briefs module"
                    modulePluralLabel="safety briefs"
                    moduleDataLabel="safety brief data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage safety briefs module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'qa-verifications-module-settings' ? (
              <div
                key="qa-verifications-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="qa-verifications"
                    moduleTitle={translateUi("QA Verifications module")}
                    moduleLabel={translateUi("QA Verifications module")}
                    modulePluralLabel="QA verifications"
                    moduleDataLabel="verification report data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage QA Verifications module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'trainee-driver-module-settings' ? (
              <div
                key="trainee-driver-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="trainee-driver"
                    moduleTitle={translateUi("Trainee module")}
                    moduleLabel={translateUi("Trainee module")}
                    modulePluralLabel="trainee driver"
                    moduleDataLabel="trainee driver data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Trainee module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'driver-reports-module-settings' ? (
              <div
                key="driver-reports-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="driver-reports"
                    moduleTitle="Driver Reports module"
                    moduleLabel="Driver Reports module"
                    modulePluralLabel="driver reports"
                    moduleDataLabel="driver report data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Driver Reports module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'leave-absence-module-settings' ? (
              <div
                key="leave-absence-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="leave-absence"
                    moduleTitle="Leave & Absence module"
                    moduleLabel="Leave & Absence module"
                    modulePluralLabel="leave & absence"
                    moduleDataLabel="leave and absence data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Leave & Absence module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'medication-checks-module-settings' ? (
              <div
                key="medication-checks-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="medication-checks"
                    moduleTitle="Medication Checks module"
                    moduleLabel="Medication Checks module"
                    modulePluralLabel="medication checks"
                    moduleDataLabel="medication check data"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Medication Checks module billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'leave-absence' ? (
              <div
                key="leave-absence"
                className={cn(isDarkMode && "min-h-full")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {!leaveAbsenceModuleActive ? (
                  <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("Activate the Leave & Absence module from Addons to use this page.")}
                  </div> ) : !canAccessLeaveAbsence ? ( <div className={cn("rounded-3xl border p-12 text-center shadow-sm", isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600")}>
                    {translateUi("You do not have permission to use Leave & Absence. Ask your company administrator to enable Request, Approve, or Manage leave permissions for your role.")}
                  </div>
                ) : (
                  <LeaveAbsencePage
                    companyCode={apiCompanyCode || ""}
                    currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                    canRequest={canRequestLeave || canApproveLeave || canManageLeave}
                    canApprove={canApproveLeave || canManageLeave}
                    canManage={canManageLeave}
                  />
                )}
              </div>
            ) : activeTab === 'trainee-driver-addon' ? (
              <div
                key="trainee-driver-addon"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TraineeDriverGlobalTemplatesPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Trainee global templates.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'company-admin-licences-module-settings' ? (
              <div
                key="company-admin-licences-module-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <TaskModuleSettingsPage
                    currentUserEmail={user?.email || undefined}
                    isDarkMode={isDarkMode}
                    addonKey="company-admin-licences"
                    moduleTitle="Company Admin Licences"
                    moduleLabel="Company Admin Licences"
                    modulePluralLabel="company admin licences"
                    moduleDataLabel="administrator seat records"
                  />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage Company Admin Licences billing settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'train-source-settings' ? (
              <div
                key="train-source-settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <LazyPageShell name="TrainSourceSettings">
                    <TrainSourceSettingsPage currentUserEmail={user?.email || undefined} isDarkMode={isDarkMode} />
                  </LazyPageShell>
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage train source settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'app-languages' ? (
              <div
                key="app-languages"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <LazyPageShell name="AppLanguages">
                    <AppLanguagesSettingsPage currentUserEmail={user?.email || undefined} isDarkMode={isDarkMode} />
                  </LazyPageShell>
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage language settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'security' ? (
              <div
                key="security"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <LazyPageShell name="Security">
                    <SecurityPage currentUserEmail={user?.email || undefined} />
                  </LazyPageShell>
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can manage security settings.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'migration-center' ? (
              <div
                key="migration-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <LazyPageShell name="MigrationCenter">
                    <MigrationCenterPage currentUserEmail={user?.email || undefined} isDarkMode={isDarkMode} />
                  </LazyPageShell>
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 shadow-sm">
                    {translateUi("Only System Administrators can access the Migration Center.")}
                  </div>
                )}
              </div>
            ) : activeTab === 'medicals' ? (
              <div
                key="medicals"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <MedicalsPage 
                  currentUserProfile={effectiveProfile} 
                  employees={employees} 
                  currentUserEmail={user?.email}
                  companyCode={apiCompanyCode || undefined}
                  isDarkMode={isDarkMode}
                />
              </div>
            ) : activeTab === 'licencing' ? (
              <div
                key="licencing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <LicencingPage
                  employees={employees}
                  companyCode={apiCompanyCode || undefined}
                  isDarkMode={isDarkMode}
                  onOpenEmployee={(employeeId, companyCode) => {
                    try {
                      sessionStorage.setItem(`rv_employee_tab_${employeeId}`, "Licence");
                    } catch {
                      /* ignore */
                    }
                    setLastEmployeeProfileTab("Licence");
                    setSelectedEmployeeId(employeeId);
                    setSelectedEmployeeCompanyCode(companyCode ?? undefined);
                    setActiveTab("employees");
                    closeMobileSidebar();
                  }}
                />
              </div>
            ) : activeTab === 'cycle-builder' ? (
              <div
                key="cycle-builder"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {(effectiveIsAdmin || effectiveIsSystemAdmin || canManageFramework) ? (
                  <LazyPageShell name="CycleBuilder">
                    <CycleWizardPage
                      currentUserEmail={user?.email}
                      companyCode={apiCompanyCode || undefined}
                      isSystemAdmin={effectiveIsSystemAdmin}
                      isDarkMode={isDarkMode}
                      onSaved={advanceCompanySetupAfterCycleSaved}
                      onFrameworkApplied={advanceCompanySetupAfterFrameworkApplied}
                      onFrameworkRemoved={refreshCompanySetupAfterFrameworkRemoved}
                      finishSetupFlow={
                        !!companySetupReminder.show && !!companySetupReminder.issues?.missingFramework
                      }
                    />
                  </LazyPageShell>
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Administrator access required to use the Cycle Builder.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'announcements' ? (
              <div
                key="announcements"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <AnnouncementsPage
                    currentUserEmail={user?.email}
                    onMaintenanceChanged={handleMaintenanceChanged}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can manage announcements.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'sys-logs' ? (
              <div
                key="sys-logs"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <SystemLogsPage
                    currentUserEmail={user?.email}
                    isMobileOrTablet={isMobileOrTablet}
                    onBack={() => setActiveTab('dashboard')}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can view system logs.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'email-templates' ? (
              <div
                key="email-templates"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <EmailTemplatePage currentUserEmail={user?.email} />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can edit email templates.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'investigations-api-management' ? (
              <div
                key="investigations-api-management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {canAccessInvestigationsApiManagement ? (
                  <InvestigationsApiManagementPage
                    currentUserEmail={user?.email}
                    scopedCompanyCode={effectiveIsSystemAdmin ? null : apiCompanyCode || null}
                    scopedCompanyName={effectiveIsSystemAdmin ? null : (effectiveCompany?.name || null)}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">
                      Only company administrators (or system administrators) can manage the Investigations
                      module link. Open Administration while viewing your company, or use the system
                      administrator API management entry.
                    </p>
                  </div>
                )}
              </div>
            ) : activeTab === 'company-billing' ? (
              <div
                key="company-billing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <CompanyBillingDirectoryPage currentUserEmail={user?.email} />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can view company billing information.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'company-storage' ? (
              <div
                key="company-storage"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <CompanyStorageManagementPage currentUserEmail={user?.email} isDarkMode={isDarkMode} />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can manage company storage settings.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'new-company' ? (
              <div
                key="new-company"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <NewCompanyPage
                    isSystemAdmin={effectiveIsSystemAdmin}
                    currentUserEmail={user?.email}
                    currentUserId={user?.uid}
                    isDarkMode={isDarkMode}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can create new companies.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'onboarding' ? (
              <div
                key="onboarding"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <OnboardingAdminPage
                    currentUserEmail={user?.email}
                    currentUserId={user?.uid}
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can manage onboarding.")}</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'addons-management' ? (
              <div
                key="addons-management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {effectiveIsSystemAdmin ? (
                  <AddonsManagementPage
                    isDarkMode={isDarkMode}
                    isMobileOrTablet={isMobileOrTablet}
                    onOpen={(destination) => setActiveTab(destination)}
                  />
                ) : (
                  <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center">
                    <p className="text-slate-600">{translateUi("Only System Administrators can manage addons.")}</p>
                  </div>
                )}
              </div>
            ) : effectiveIsSystemAdmin ? ( <div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SystemAdminDashboard
                  currentUserEmail={user?.email}
                  isMobileOrTablet={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  onOpenLogs={() => setActiveTab('sys-logs')}
                  welcomeName={welcomeFirstNameOnly(effectiveProfile, user)}
                />
              </div>
            ) : (
              <div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex w-full flex-col gap-6 lg:min-h-[calc(100dvh-9rem)]"
              >
                <div className="shrink-0">
                <DashboardHeader
                  totalEmployees={stats?.totalEmployees ?? employees.length}
                  isMobileOrTablet={isMobileOrTablet}
                  isDarkMode={isDarkMode}
                  welcomeName={welcomeFirstNameOnly(effectiveProfile, user)}
                />
                </div>
                <div className="flex min-h-0 flex-1 flex-col">
                <CompanyDashboardBody
                  isMobileOrTablet={isMobileOrTablet}
                  isCompanyAdmin={effectiveIsAdmin}
                  isCompanyAdminUser={effectiveIsAdmin && !effectiveIsSystemAdmin}
                  companyAddons={companyAddons}
                  hasCompanyDoc={!!effectiveCompany}
                  stats={stats}
                  isDarkMode={isDarkMode}
                  onTab={(tab) => setActiveTab(tab as typeof activeTab)}
                  setEmployeeFilter={setEmployeeFilter}
                  openDashboardDetails={(title, type) => {
                    setDashboardModalConfig({ isOpen: true, title, type });
                  }}
                  companyCode={apiCompanyCode}
                  currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                  currentUserTitle={effectiveProfile?.title}
                  addonsRefreshTrigger={addonsRefreshTrigger}
                  inboxItems={visibleAnnouncements}
                  onInboxItemClick={(a) => {
                    if (
                      (a.type === "employee_note" || a.type === "employee_note_read") &&
                      a.employee_id
                    ) {
                      navigateToEmployeeNoteFromNotification(a);
                      return;
                    }
                    if (a.type === "fatality_anniversary_7d" && a.employee_id) {
                      navigateToEmployeeFromFatalityAnniversaryNotification(a);
                      return;
                    }
                    if (
                      (a.type === "licence_expiry_3m" ||
                        a.type === "licence_expiry_6w" ||
                        a.type === "licence_expiry_expired") &&
                      a.employee_id
                    ) {
                      navigateToEmployeeLicenceFromNotification(a);
                      return;
                    }
                    if (
                      (a.type === "task_assigned" ||
                        a.type === "task_reassigned" ||
                        a.type === "task_reopened") &&
                      a.task_id
                    ) {
                      navigateToTaskFromNotification(a);
                      return;
                    }
                    setNotificationMenuOpen(true);
                  }}
                  setupIssues={
                    companySetupReminder.show && effectiveIsAdmin && companySetupReminder.issues
                      ? [
                          companySetupReminder.issues.missingCycles
                            ? translateUi("Competency cycle")
                            : null,
                          companySetupReminder.issues.missingCompanyStandards
                            ? "Company standards"
                            : null,
                          companySetupReminder.issues.missingFramework ? translateUi("Framework") : null,
                          companySetupReminder.issues.missingGradingScale
                            ? translateUi("Grading scale")
                            : null,
                          companySetupReminder.issues.missingTractionAndDepots
                            ? "Depots & traction"
                            : null,
                        ].filter((x): x is string => Boolean(x))
                      : null
                  }
                  onOpenSetup={
                    companySetupReminder.show && effectiveIsAdmin
                      ? () => setFinishCompanySetupDialogOpen(true)
                      : undefined
                  }
                  onOpenEmployee={(employeeId) => {
                    const id = String(employeeId || "").trim();
                    if (!id) return;
                    try {
                      sessionStorage.setItem(`rv_employee_tab_${id}`, "Main");
                    } catch {
                      /* ignore */
                    }
                    restoredEmployeeIdRef.current = id;
                    if (apiCompanyCode) setSelectedEmployeeCompanyCode(apiCompanyCode);
                    setSelectedEmployeeId(id);
                    setActiveTab("employees");
                  }}
                  canRunQAVerifications={canRunQAVerifications}
                  currentUserName={chromeDisplayName(effectiveProfile, user)}
                  onNavigateToQaFix={(target) => {
                    const employeeId = String(target.employeeId || "").trim();
                    if (!employeeId) return;
                    const tab = target.tab;
                    const allowed =
                      tab === "Tasks" ||
                      (EMPLOYEE_PROFILE_TAB_OPTIONS as readonly string[]).includes(tab);
                    if (!allowed) return;
                    try {
                      sessionStorage.setItem(`rv_employee_tab_${employeeId}`, tab);
                    } catch {
                      /* ignore */
                    }
                    restoredEmployeeIdRef.current = employeeId;
                    setLastEmployeeProfileTab(tab);
                    setSelectedEmployeeId(employeeId);
                    setSelectedEmployeeCompanyCode(apiCompanyCode || undefined);
                    setActiveTab("employees");
                  }}
                />
                </div>
              </div>
            )}
          </>
          </LazyPageShell>
        {isEmployeeModalOpen && operationalEditUnlocked && (
          <div
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 pt-6 sm:pt-10 pb-10"
            role="dialog"
            aria-modal="true"
            aria-label={editingEmployee ? translateUi("Edit employee") : translateUi("Add employee")}
            onClick={() => {
              setIsEmployeeModalOpen(false);
              setEditingEmployee(null);
            }}
          >
            <div className="w-full max-w-5xl shrink-0" onClick={(e) => e.stopPropagation()}>
              <EmployeeForm
                companyCode={employeesApiCompanyCode || undefined}
                currentUserEmail={effectiveProfile?.email || user?.email || undefined}
                isDarkMode={isDarkMode}
                onClose={() => {
                  setIsEmployeeModalOpen(false);
                  setEditingEmployee(null);
                }}
                onSave={() => {
                  const params = employeesApiCompanyCode ? `?companyCode=${encodeURIComponent(employeesApiCompanyCode)}` : '';
                  const cacheBust = params ? `&_=${Date.now()}` : `?_=${Date.now()}`;
                  fetch(`/api/employees${params}${cacheBust}`, {
                    cache: 'no-store',
                    headers: user?.email ? { 'X-User-Email': user.email } : {},
                  })
                    .then(async (res) => {
                      const data = await res.json();
                      if (res.ok && Array.isArray(data)) setEmployees(data);
                    })
                    .catch(() => {});
                  setIsEmployeeModalOpen(false);
                  setEditingEmployee(null);
                }}
                editingEmployee={editingEmployee}
                isMobileOrTablet={isMobileOrTablet}
              />
            </div>
          </div>
        )}
        </div>
      </main>

      {canResetNomineeTutorial &&
        !showNominatedAdminWelcome &&
        !nomineeNavTourActive &&
        !showActivationSuccessScreen &&
        !isOnboardingLocked &&
        !appShellBlockedForResolve && (
          <div className="fixed bottom-0 left-0 right-0 z-[45] flex justify-center pointer-events-none px-4 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
            <button
              type="button"
              onClick={resetNominatedAdminTutorial}
              className={cn(
                "pointer-events-auto inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-sm transition-colors",
                isDarkMode
                  ? "border-slate-600/90 bg-slate-800/95 text-slate-200 hover:bg-slate-700 hover:border-indigo-500/60 hover:text-white"
                  : "border-slate-200/90 bg-white/95 text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-800 shadow-slate-900/10"
              )}
            >
              <RotateCcw className="w-4 h-4 shrink-0" aria-hidden />
              {translateUi("Reset tutorial")}
            </button>
          </div>
        )}
    </div>

    {/* Inactivity warning modal */}
    {inactivityWarningOpen && (
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{translateUi("Session expiring soon")}</h3>
              <p className="text-sm text-slate-500">{translateUi("You will be logged out due to inactivity.")}</p>
            </div>
          </div>
          <p className="text-slate-600 mb-6">
            {translateUi("Logging out in")} <span className="font-bold text-amber-600">{inactivityCountdown}</span> second{inactivityCountdown !== 1 ? 's' : ''}…
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                resetInactivityRef.current?.();
                setInactivityWarningOpen(false);
              }}
              className="flex-1 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors"
            >
              {translateUi("Stay logged in")}
            </button>
          </div>
        </div>
      </div>
    )}
    </React.Fragment>
  );
}
